// WANDER APP - COMPLETE TRAVEL PLANNING ASSISTANT
// Required fonts: Add to index.html <head>:
// <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Comfortaa:wght@400;500;600;700&display=swap" rel="stylesheet">

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { Analytics } from "@vercel/analytics/react";
import { Send, Plane, MapPin, Calendar, DollarSign, Users, Sparkles, Check, Home, Clock, ExternalLink, Star, Utensils, UtensilsCrossed, Hotel, Activity, Ship, Car, Train, Mountain, Palmtree, Coffee, Camera, Heart, Sunrise, Wine, Book, ThermometerSun, Baby, UserCheck, Compass, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Download, Share2, X, Navigation, Package, RefreshCw, MessageCircle, GripVertical, Trash2, Plus, Edit2, ArrowLeft, Wallet, CreditCard, Gem, CalendarDays, CalendarRange, Sun, Globe, Save, AlertCircle, MoreVertical, Search, Pencil, Info, Lock, Unlock, ThumbsUp, ThumbsDown, Bell, Layers, Bookmark, MessageSquare, Gift, User, Edit3, Shield, BarChart3, FileText, Trophy, Settings, Map as MapIcon, Route, Lightbulb, Crosshair, Moon } from 'lucide-react';
import { BrandHeart, BrandSparkles, BrandCompass, BrandChat, BrandMap, BrandClock, BrandCards } from './components/BrandIcons';
import ActivityIcon from './components/ActivityIcon';
import { ShareModal, Wishlist, ActivityLog, CollaboratorAvatars, InviteLanding } from './components/collaboration';
import BrowseCommunityTripsModal from './components/community/BrowseCommunityTripsModal';
import MobileAppBanner from './components/MobileAppBanner';

// Auth imports
import { useAuth } from './contexts/AuthContext';
import { readFreeTimeSuggestions, writeFreeTimeSuggestions } from './utils/freeTimeSuggestionsCache';
import { LoginModal, UserMenu, PersonalizationModal, SettingsModal, FullPageLogin } from './components/auth';
import { initAnalytics, setAnalyticsConsent, identifyUser } from './lib/analytics';
import { supabase as supabaseClient, saveItinerary, getSavedItineraries, deleteItinerary, updateItineraryTitle, saveItineraryFeedback, deleteItineraryFeedback, getItineraryFeedback, updateFeedbackReason, saveUserFeedback, getSession, subscribeToItineraryChanges, broadcastItineraryChange, onItineraryBroadcast } from './lib/supabase';
// import BugReportButton from './components/BugReportButton';
import BudgetTracker from './components/BudgetTracker';
import { getCurrencyForCity as _getCurrencyForCityShared, formatPriceTier as _formatPriceTierShared, currencySymbol as _currencySymbol, approximateToUSD as _approximateToUSD, formatDualCurrency as _formatDualCurrencyShared } from './utils/currencyUtils';
import { checkDestinationRestriction, checkCitizenshipRestriction, getDestinationSafetyReport, parsePassportCountries } from './lib/travelSafety';
import { features, tierHasFeature } from './lib/features';
import { validateTripDates, validateDestination, validateDaySchedule, validateMultiCityTrip, sanitizeTextInput } from './lib/validations';
import { getSchedulingRulesForPrompt, getRegionalMealTimes, validateAndFixDay } from './utils/itineraryValidation';
import { processUserMessage, routeIntent, classifyIntent, extractMessage, mergeTripContext, determineActions } from './utils/intentRouter';
import { getDestinationGeography, getAttractionBookingInfo, validateDayActivities, regenerateActivity } from './lib/dynamicLookups';
import './lib/testSupabase'; // Exposes window.testSupabase() for debugging
import { toTitleCase, stripEmoji, parseDurationToMinutes, getActivityEmoji, getMealEmoji, categorizeActivity } from './utils/itineraryHelpers';
import { CITY_TO_COUNTRY, UNREALISTIC_DAY_TRIP_PAIRS, DISTANT_LOCATION_PATTERNS } from './utils/itineraryConstants';
import { isHotelActivity as isHotelActivityValidator } from './utils/itineraryValidators';
import { DndContext, closestCenter, DragOverlay, useSensor, useSensors, PointerSensor } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CommunityErrorBoundary from './components/community/CommunityErrorBoundary';
import ItineraryMap from './components/itinerary/ItineraryMap';
import WeatherWidget from './components/itinerary/WeatherWidget';
import DriveFurtherPicker from './components/itinerary/DriveFurtherPicker';
import PlaceDetailModal from './components/itinerary/PlaceDetailModal';
import { collectDayPlaces, linkTipPlaces } from './utils/linkTipPlaces';
import { normalizeDay, normalizeItinerary, areSameHotel } from './utils/itineraryNormalizer';
import { isTravelDay, getStopForDay, deduplicateDay, calculateTravelStructure as _calculateTravelStructure, buildCompressedContext as _buildCompressedContext, buildItinerarySystemPrompt, buildFirstBatchUserMessage, buildPerDayUserMessage, extractAndRepairJSON, redistributeTimes, buildGeographyContext } from './utils/promptBuilder';
import { buildProfileContext as _buildProfileContext, buildFeedbackContext as _buildFeedbackContext } from './utils/profileContext';
import { buildChatModificationSystemPrompt } from './utils/chatModificationPrompt';
import { createDayTimeline as createDayTimelineShared } from '@wander/shared/utils/createDayTimeline.js';
// Canonical onboarding option lists — single source of truth across web + mobile.
// PR #298 added this schema but the Trip Builder Pace UI still had a hardcoded
// 4-option list (easy/relaxed/balanced/packed) writing non-canonical values
// like 'easy' or 'balanced' to preferredPace, which the orchestrator then
// silently ignored. Same bug as the mobile TripBuilderScreen pace issue.
import { PACE_OPTIONS as CANONICAL_PACE_OPTIONS, COMPANION_OPTIONS as CANONICAL_COMPANION_OPTIONS } from '@wander/shared/utils/onboardingSchema.js';
import { companionsToPromptString, coerceCompanionsToArray, normalizePreferences } from '@wander/shared/utils/preferenceSchema.js';
// KNOWN_TRAVEL_CITIES drives the trip-builder whitespace-split safety net
// below ("Mexico City Oaxaca" / "Auckland Wellington" pasted directly).
// Renamed locally to avoid collisions with existing variable names in this
// file.
import { KNOWN_TRAVEL_CITIES as KNOWN_TRAVEL_CITIES_WEB } from '@wander/shared/utils/extractionPostProcess.js';
// Bare-country / activity-based detection — pulled from shared so web stays
// in sync with backend (cityDisambiguation drives /api/generate/start's
// primaryCityHint injection). Previously web had its own hand-edited copies
// that drifted (missing Russia, Mozambique, Slovenia, etc.).
import {
  isBareCountry as isBareCountryShared,
  isActivityBasedLocation as isActivityBasedLocationShared,
  isAmbiguousCity as isAmbiguousCityShared,
} from '@wander/shared/utils/cityDisambiguation.js';
import { buildWebTimelineContext } from './utils/buildWebTimelineContext';
import {
  COUNTRY_TO_CONTINENT, ISLAND_LOCATION_KEYWORDS,
  isIslandCity as _isIslandCity, getContinentForCountry as _getContinentForCountry,
  getCityCountrySync as _getCityCountrySync,
  getContinentHintFromText,
} from './utils/geographyConstants';
import {
  DEPARTURE_AIRPORT_CITY_MAP, TRAIN_FRIENDLY_AIRPORT_ROUTES,
  safeAirportCity as _safeAirportCity, cleanIataCode,
  getSmartTransportMode as _getSmartTransportMode,
  MULTI_AIRPORT_CITIES, getCityFromLocation,
  CITY_TO_AIRPORT_CODE, lookupCityAirportCode,
  reverseLookupAirportCity,
  validateTransportMode, resolveStation,
} from './utils/transportConstants';
import {
  getBreakfastHotelContext, isMealBlockedByTransport,
  logisticsSortPriority, cleanOvernightArrivalDay,
  checkOvernightTransport, findMealSlot, filterPreDawnItems,
  timelineSortComparator, normalizeCityName,
  isExplicitOvernightArrival, hasMealType, isResortStay as isResortStayShared,
  injectMissingMeals, PACE_THRESHOLDS,
  extractAirportCode, validateAirportCode,
} from './utils/displayGuardrails';
import { FLIGHT_TIMES, HOME_CITY_MAP, getStaticFlightEstimate, estimateFlightDurationHeuristic, STATIC_FLIGHT_DURATIONS, estimateFlightSchedule } from './utils/flightEstimates';
import { parseTimeToMinutes, formatTime12, minutesToTime as minutesToTimeShared, calculateEndTime as calculateEndTimeShared, doesCrossMidnight as doesCrossMidnightShared, minutesBetween as minutesBetweenShared, findTransitData as findTransitDataShared } from './utils/timeUtils';
import { lookupCityAirports, getTransportModeDisplay, TRANSPORT_MODES, findAirportsWithinDriveRadius } from './utils/transportPrefetch';
import { formatDateRange as _formatDateRange, getDateForDay as _getDateForDay, parseLocalDate as _parseLocalDate, calculateDaysBetween as _calculateDaysBetween, formatDateForDisplay as _formatDateForDisplay } from './utils/dateFormatting';
import { AIRLINE_NAMES as _AIRLINE_NAMES, resolveAirlineName as _resolveAirlineName } from './utils/airlineUtils';
import { EMERGENCY_NUMBERS as _EMERGENCY_NUMBERS, getEmergencyNumber as _getEmergencyNumber } from './utils/emergencyNumbers';
import { estimateDriveHours as _estimateDriveHours, estimateTravelTime as _estimateTravelTime, calculateStartTime as _calculateStartTime, sanityCheckDriveHours as _sanityCheckDriveHours } from './utils/travelTimeEstimates';
import { validateItineraryStructure as _validateItineraryStructure } from './utils/itineraryStructureValidator';
import {
  levenshteinDistance, DESTINATION_CITIES as KNOWN_CITIES, KNOWN_COUNTRIES, KNOWN_COUNTRIES_LOWER, KNOWN_REGIONS,
  COMPOUND_PLACE_NAMES, protectCompoundNames, restoreCompoundNames,
  KNOWN_HOTELS_RESORTS, HOTEL_RESORT_KEYWORDS, HOTEL_RECOMMENDATION_PATTERNS,
  QUALITY_ADJECTIVES, GENERIC_ACCOMMODATION_WORDS, GENERIC_ACCOMMODATION_PATTERN,
  HOTEL_LOCATION_MAP, HOTEL_BRAND_PREFIXES, AIRPORT_CODES, COMMON_ABBREVIATIONS, ALL_KNOWN_LOCATIONS,
  INVALID_DESTINATION_WORDS, extractLocationFromHotelName,
  isHotelRecommendationRequest, isAmbiguousDestination, fuzzyMatchCity,
  isRealDestination, isHotelOrResort, getRecommendedMonths,
} from './utils/destinationConstants';
import {
  COUNTRY_TO_CURRENCY, SCHENGEN_COUNTRIES, VISA_REQUIREMENTS_US, BORDER_CROSSING_NOTES,
} from './utils/travelInfoConstants';
import {
  CITY_TO_IANA_TIMEZONE, COUNTRY_TO_TIMEZONE,
  getCityIANATimezone as _getCityIANATimezone,
  getCityTimezoneOffset as _getCityTimezoneOffset,
  calculateFlightArrival as _calculateFlightArrival,
  getTimezoneAbbr as _getTimezoneAbbr,
  computeArrivalDayOffset,
} from './utils/timezoneConstants';
import {
  normalizeInternational, INTERNATIONAL_CITY_ALIASES, resolveInternationalCity,
} from './utils/stringMatchingUtils';
import {
  DYNAMIC_CACHE_MAX, evictOldestFromCache,
} from './utils/cacheUtils';
import { readClimatology as readClimoCache, writeClimatology as writeClimoCache } from './utils/weatherClimatologyCache';
import { validateCityName } from './lib/validateCity';
import { createTripLog, getMyReviews, getBookmarks, deleteBookmark, createBookmark, getReferralCode, publishToCommunity } from './lib/communityApi';
import { FONTS, BTN } from './lib/theme';
// Non-lazy import of getCountryCode — used inline in the Passport stats
// computation so the country count matches TravelMap's derivation. Only a
// few bytes; no need to lazy-load a single function.
import { getCountryCode, COUNTRY_NAME_TO_ISO_NUMERIC } from './components/community/TravelMap';

// Lazy-loaded components (zero bundle cost when not used)
const MustDoStrip = React.lazy(() => import('./components/itinerary/MustDoStrip'));
const AdminAnalytics = React.lazy(() => import('./components/AdminAnalytics'));
const CommunityPage = React.lazy(() => import('./components/community/CommunityPage'));
const ShareLinkModal = React.lazy(() => import('./components/community/ShareLinkModal'));
const SharedTripViewer = React.lazy(() => import('./components/community/SharedTripViewer'));
const TripLogger = React.lazy(() => import('./components/community/TripLogger'));
const LeaderboardPage = React.lazy(() => import('./components/community/LeaderboardPage'));
const TasteProfile = React.lazy(() => import('./components/community/TasteProfile'));
const CompareModal = React.lazy(() => import('./components/community/CompareModal'));
const DiscoverGrid = React.lazy(() => import('./components/community/DiscoverGrid'));
const InnerCirclePage = React.lazy(() => import('./components/community/InnerCirclePage'));
const QuickRateFlow = React.lazy(() => import('./components/community/QuickRateFlow'));
const ActivityFeed = React.lazy(() => import('./components/community/ActivityFeed'));
const BadgeGrid = React.lazy(() => import('./components/community/BadgeGrid'));
const CompatibilityBadge = React.lazy(() => import('./components/community/CompatibilityBadge'));
const ShareableRankedList = React.lazy(() => import('./components/community/ShareableRankedList'));
const TravelersPassport = React.lazy(() => import('./components/community/TravelersPassport'));
const ReferralCard = React.lazy(() => import('./components/community/ReferralCard'));
const CommunityTravelMap = React.lazy(() => import('./components/community/TravelMap'));
const ProfileEditor = React.lazy(() => import('./components/community/ProfileEditor'));
const AdminReviewQueue = React.lazy(() => import('./components/community/AdminReviewQueue'));
const PlaceReviewForm = React.lazy(() => import('./components/community/PlaceReviewForm'));
const ShareablePassport = React.lazy(() => import('./components/community/ShareablePassport'));
const ShareableTasteCard = React.lazy(() => import('./components/community/ShareableTasteCard'));
const TasteSimilarityBadge = React.lazy(() => import('./components/community/TasteSimilarityBadge'));
const PastPlacesEditor = React.lazy(() => import('./components/community/PastPlacesEditor'));
const SkeletonTripBuilder = React.lazy(() => import('./components/community/SkeletonTripBuilder'));
const TripImporter = React.lazy(() => import('./components/community/TripImporter'));

// Simple error boundary for lazy-loaded components (catches chunk loading failures)
class ErrorBoundarySimple extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error, info) { console.error('[ErrorBoundary]', error, info); }
  render() {
    if (this.state.hasError) {
      return (
        <div className="text-center py-6 text-gray-500">
          <p className="text-sm">{this.props.fallbackMessage || 'Something went wrong. Please refresh.'}</p>
          <button onClick={() => { this.setState({ hasError: false }); window.location.reload(); }}
            className="mt-2 text-xs text-teal-600 hover:text-teal-700 underline">Refresh page</button>
        </div>
      );
    }
    return this.props.children;
  }
}

// FONTS and BTN imported from ./lib/theme

// Authenticated API headers for backend requests
// Reads token directly from localStorage to avoid getSession() hanging
// when Supabase auth endpoint is slow (especially with email/password auth)
const getApiHeaders = async () => {
  const headers = { "Content-Type": "application/json" };
  try {
    // First try localStorage (fast, no network call)
    const storageKey = Object.keys(localStorage).find(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
    if (storageKey) {
      const stored = JSON.parse(localStorage.getItem(storageKey) || '{}');
      if (stored?.access_token) {
        headers["Authorization"] = `Bearer ${stored.access_token}`;
        return headers;
      }
    }
    // Fallback to getSession() if localStorage doesn't have it
    const session = await getSession();
    if (session?.access_token) {
      headers["Authorization"] = `Bearer ${session.access_token}`;
    }
  } catch { /* proceed without auth */ }
  return headers;
};

// ─── Google Places coord fallback for stop geocoding ─────────────────────
//
// Open-Meteo's geocoder is city-focused — resort/hotel-named stops like
// "Hermitage Bay" or "The Somerset on Grace Bay" return no results, which
// leaves the weather card blank. This helper resolves any such string via
// backend `/api/resolve-place` (Google Places Text Search), normalised to
// the `{ lat, lng }` shape the weather prefetch loops already expect.
//
// Module-scoped so both the personalization-weather effect and the
// itinerary-day weather effect can share it (and its in-memory cache).
const _placesFallbackCache = new Map();
async function _placesFallbackCoords(query, opts = {}) {
  if (!query || typeof query !== 'string') return null;
  const trimmed = query.trim();
  if (!trimmed) return null;
  const cacheKey = trimmed.toLowerCase();
  if (_placesFallbackCache.has(cacheKey)) return _placesFallbackCache.get(cacheKey);
  try {
    const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
    const headers = await getApiHeaders();
    const resp = await fetch(`${API_BASE}/api/resolve-place`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ query: trimmed }),
      ...(opts.signal ? { signal: opts.signal } : {}),
    });
    if (!resp.ok) {
      _placesFallbackCache.set(cacheKey, null);
      return null;
    }
    const data = await resp.json();
    const loc = data?.location;
    if (data?.success && loc && typeof loc.lat === 'number' && typeof loc.lng === 'number') {
      const coords = { lat: loc.lat, lng: loc.lng };
      _placesFallbackCache.set(cacheKey, coords);
      // eslint-disable-next-line no-console
      console.log(`[geocodeStop] Places fallback resolved "${trimmed}" → (${loc.lat.toFixed(3)}, ${loc.lng.toFixed(3)})`);
      return coords;
    }
  } catch {
    /* non-blocking */
  }
  _placesFallbackCache.set(cacheKey, null);
  return null;
}

// ============================================
// CHAT WITH STREAMING + GRACEFUL FALLBACK
// ============================================
// Back-ported from mobile `sendChatMessageStream` (mobile/src/services/api.js).
// Attempts SSE via /api/chat-stream for long-running LLM calls (e.g. itinerary
// generation) to avoid gateway timeouts + improve perceived latency. Falls
// back transparently to the existing non-streaming /api/chat endpoint on any
// failure (endpoint missing, non-OK status, stream parse error, network error)
// so the call site sees an identical response shape either way.
//
// Response shape matches /api/chat: { content: [{ type: 'text', text }] }
const sendChatStreamWithFallback = async ({
  BACKEND_URL,
  model,
  max_tokens,
  system,
  messages,
  signal,
  onProgress,
} = {}) => {
  const headers = await getApiHeaders();
  const body = JSON.stringify({ model, max_tokens, system, messages });

  // --- Attempt streaming first ---
  try {
    const res = await fetch(`${BACKEND_URL}/api/chat-stream`, {
      method: 'POST',
      headers,
      body,
      signal,
    });

    // If backend doesn't implement streaming (404 / 501 / 405) — fall back
    if (res.status === 404 || res.status === 405 || res.status === 501) {
      throw new Error(`stream-unsupported:${res.status}`);
    }
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`stream-http-${res.status}:${text.slice(0, 200)}`);
    }

    const reader = res.body?.getReader?.();
    if (!reader) {
      // Browser lacks ReadableStream — fall back
      throw new Error('stream-no-reader');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    const textChunks = [];
    let lastProgressTime = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        try {
          const evt = JSON.parse(line.slice(6));
          if (evt.type === 'chunk') {
            textChunks.push(evt.text);
          } else if (evt.type === 'progress' && typeof onProgress === 'function') {
            const now = Date.now();
            if (now - lastProgressTime > 500) {
              lastProgressTime = now;
              try { onProgress({ daysGenerated: evt.daysGenerated }); } catch { /* noop */ }
            }
          } else if (evt.type === 'done') {
            const fullText = evt.fullText || textChunks.join('');
            return { content: [{ type: 'text', text: fullText }] };
          } else if (evt.type === 'error') {
            throw new Error(evt.message || 'stream-event-error');
          }
        } catch (parseErr) {
          if (parseErr.message?.startsWith('stream-')) throw parseErr;
          // Ignore JSON parse errors for partial chunks
        }
      }
    }

    // Stream ended without 'done' — if we got content, return it; otherwise fall back
    if (textChunks.length > 0) {
      return { content: [{ type: 'text', text: textChunks.join('') }] };
    }
    throw new Error('stream-ended-empty');
  } catch (streamErr) {
    // AbortError must propagate so callers see their own timeout
    if (streamErr?.name === 'AbortError') throw streamErr;
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[chat-stream] Falling back to /api/chat:', streamErr.message);
    }
    // --- Fallback to non-streaming /api/chat ---
    const res = await fetch(`${BACKEND_URL}/api/chat`, {
      method: 'POST',
      headers,
      body,
      signal,
    });
    if (!res.ok) {
      throw new Error(`API error: ${res.status}`);
    }
    return await res.json();
  }
};

// ============================================
// _cacheBust — render-memo invalidation for in-place edits
// ============================================
// Back-ported from mobile `_bumpCacheBust` (mobile/src/screens/ItineraryScreen.js).
// The timeline memo keys on `${dayIdx}-${schedule.len}-${meals.len}-${transitKeys.len}`
// which catches add/delete but NOT in-place edits (time change, duration change,
// cost edit, hotel swap). Bumping a monotonically-increasing `_cacheBust` on the
// mutated day closes that gap so memos invalidate on every edit without every
// call site having to know about the memo internals.
//
// Usage: `const day = bumpDayCacheBust({ ...itinerary.days[i] });`  or in a
// mutating flow, `bumpDayCacheBust(updatedItinerary.days[i]);` after the edit.
const bumpDayCacheBust = (day) => {
  if (!day || typeof day !== 'object') return day;
  day._cacheBust = (day._cacheBust || 0) + 1;
  return day;
};

// levenshteinDistance — imported from @wander/shared via ./utils/destinationConstants

// KNOWN_CITIES — imported from @wander/shared via ./utils/destinationConstants

// KNOWN_COUNTRIES — imported from @wander/shared via ./utils/destinationConstants

// KNOWN_REGIONS — imported from @wander/shared via ./utils/destinationConstants

// COMPOUND_PLACE_NAMES, protectCompoundNames, restoreCompoundNames — imported from @wander/shared via ./utils/destinationConstants

// KNOWN_HOTELS_RESORTS, HOTEL_RESORT_KEYWORDS, HOTEL_RECOMMENDATION_PATTERNS, QUALITY_ADJECTIVES, GENERIC_ACCOMMODATION_WORDS, GENERIC_ACCOMMODATION_PATTERN — imported from @wander/shared via ./utils/destinationConstants

// HOTEL_LOCATION_MAP, extractLocationFromHotelName — imported from @wander/shared via ./utils/destinationConstants


// Async hotel location lookup - uses static map first, then AI fallback
// Results are cached on the backend for 180 days
const getHotelLocationAsync = async (hotelName) => {
  if (!hotelName) return null;

  const nameLower = hotelName.toLowerCase().trim();

  // Check static map first (instant)
  if (HOTEL_LOCATION_MAP[nameLower]) {
    return { location: HOTEL_LOCATION_MAP[nameLower], source: 'static', confidence: 'high' };
  }

  // Check partial matches (with min length to avoid false positives from short names)
  for (const [hotelKey, location] of Object.entries(HOTEL_LOCATION_MAP)) {
    if (nameLower.includes(hotelKey)) {
      return { location, source: 'static', confidence: 'high' };
    }
    if (nameLower.length >= 6 && hotelKey.includes(nameLower)) {
      return { location, source: 'static', confidence: 'high' };
    }
  }

  // Try brand prefix extraction (HOTEL_BRAND_PREFIXES imported from @wander/shared)
  for (const brand of HOTEL_BRAND_PREFIXES) {
    if (nameLower.startsWith(brand)) {
      let afterBrand = hotelName.slice(brand.length).trim();
      afterBrand = afterBrand.replace(/^[,\-–—:]\s*/, '').trim();
      afterBrand = afterBrand.replace(/^(hotel|resort|inn|lodge|spa|villa|palace|suites?|mansion|house)\s+/i, '').trim();
      if (afterBrand.length > 2) {
        return { location: afterBrand, source: 'pattern', confidence: 'medium' };
      }
    }
  }

  // Fallback to AI lookup
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/hotel-location`, {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ hotelName })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success && data.location) {
        return {
          location: data.location,
          city: data.city,
          country: data.country,
          region: data.region,
          source: 'ai',
          confidence: data.confidence
        };
      }
    }
  } catch (error) {
    console.warn('Hotel location lookup failed:', error.message);
  }

  // Final fallback: return the hotel name itself
  return { location: hotelName, source: 'fallback', confidence: 'low' };
};

// Validate and auto-correct transport modes in an itinerary
// Uses Haiku API to dynamically validate all car/drive routes
const validateAndCorrectTransportModes = async (itinerary) => {
  if (!itinerary?.interCityTransport || itinerary.interCityTransport.length === 0) {
    return { itinerary, corrections: [] };
  }

  const corrections = [];
  const updatedTransport = [...itinerary.interCityTransport];

  for (let i = 0; i < updatedTransport.length; i++) {
    const transport = updatedTransport[i];
    const { from, to, mode } = transport;
    const fromCity = from?.city || from;
    const toCity = to?.city || to;

    // Skip flight mode — flights are almost always valid
    if (mode === 'flight') continue;

    // Dynamically validate train/car/drive modes via Haiku API
    // This replaces hardcoded route lists with LLM-powered feasibility checks
    console.log(`🔍 Validating transport: ${fromCity} → ${toCity} by ${mode}`);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/validate-transport-mode`, {
        method: 'POST',
        headers: await getApiHeaders(),
        body: JSON.stringify({ fromCity, toCity, proposedMode: mode })
      });

      if (response.ok) {
        const validation = await response.json();

        if (!validation.valid && validation.suggestedMode) {
          // Auto-correct invalid routes
          if (process.env.NODE_ENV === 'development') console.log(`✅ Auto-correcting: ${mode} → ${validation.suggestedMode} (${validation.reason})`);
          updatedTransport[i] = {
            ...transport,
            mode: validation.suggestedMode,
            _corrected: true,
            _originalMode: mode,
            _correctionReason: validation.reason
          };
          corrections.push({
            day: transport.day,
            from: fromCity,
            to: toCity,
            originalMode: mode,
            correctedMode: validation.suggestedMode,
            reason: validation.reason
          });
        }
      }
    } catch (error) {
      console.warn(`Transport validation API failed for ${fromCity} → ${toCity}:`, error.message);
      // On API failure, we don't auto-correct - let the user see the potentially wrong route
      // The Haiku API is our source of truth; static fallbacks would get outdated
    }
  }

  if (corrections.length > 0) {
    console.log(`🔧 Transport mode corrections applied:`, corrections);
    return {
      itinerary: { ...itinerary, interCityTransport: updatedTransport },
      corrections
    };
  }

  return { itinerary, corrections: [] };
};

// LLM-based destination validation for ambiguous cases
// Uses /api/validate-destination endpoint with Claude Haiku
const validateDestinationWithLLM = async (userInput, extractedMatch, context = null) => {
  try {
    const response = await fetch('/api/validate-destination', {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({ userInput, extractedMatch, context })
    });

    if (!response.ok) {
      if (process.env.NODE_ENV === 'development') console.warn('LLM validation failed, using rules-based result');
      return null;
    }

    const result = await response.json();
    if (process.env.NODE_ENV === 'development') console.log('🤖 LLM validation result:', result);
    return result;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.warn('LLM validation error:', error.message);
    return null;
  }
};

// isAmbiguousDestination — imported from @wander/shared via ./utils/destinationConstants

// Track destination detection feedback for improving the system
// Feedback types:
// - 'false_positive': System found a destination but user didn't mean one
// - 'false_negative': System missed a destination the user intended
// - 'wrong_destination': System found wrong destination (e.g., "Nice Hotel" vs "Nice" city)
// - 'confirmed': User confirmed the system's detection was correct
const trackDestinationFeedback = async (userInput, systemExtracted, userCorrection, feedbackType, wasLLMUsed = false) => {
  try {
    await fetch('/api/destination-feedback', {
      method: 'POST',
      headers: await getApiHeaders(),
      body: JSON.stringify({
        userInput,
        systemExtracted,
        userCorrection,
        feedbackType,
        wasLLMUsed,
        timestamp: new Date().toISOString()
      })
    });
    if (process.env.NODE_ENV === 'development') console.log(`📊 Feedback tracked: ${feedbackType} for "${userInput}"`);
  } catch (error) {
    // Don't block user flow on feedback errors
    if (process.env.NODE_ENV === 'development') console.warn('Failed to track feedback:', error.message);
  }
};

// AIRPORT_CODES, COMMON_ABBREVIATIONS, ALL_KNOWN_LOCATIONS — imported from @wander/shared via ./utils/destinationConstants

// INVALID_DESTINATION_WORDS — imported from @wander/shared via ./utils/destinationConstants

// isHotelRecommendationRequest — imported from @wander/shared via ./utils/destinationConstants

// isRealDestination, fuzzyMatchCity — imported from @wander/shared via ./utils/destinationConstants

// normalizeInternational, INTERNATIONAL_CITY_ALIASES, resolveInternationalCity — imported from @wander/shared via ./utils/stringMatchingUtils

// normalizeHotelNameForComparison, areSameHotel — deleted; use areSameHotel from @wander/shared/utils/itineraryNormalizer (now has word-overlap scoring)

// MULTI_AIRPORT_CITIES, getCityFromLocation, CITY_TO_AIRPORT_CODE, lookupCityAirportCode — imported from @wander/shared via ./utils/transportConstants

// validateCityName imported from ./lib/validateCity

// Dynamic cache for LLM-resolved city-country mappings (bounded to prevent memory leaks)
const dynamicCityCountryCache = {};
// Dynamic cache for LLM-resolved country-continent mappings
const dynamicCountryContinentCache = {};
// DYNAMIC_CACHE_MAX, evictOldestFromCache — imported from @wander/shared via ./utils/cacheUtils

// Get country for a city - uses static list first, then LLM fallback
// Also triggers continent pre-resolution for the discovered country
const getCityCountry = async (cityName) => {
  if (!cityName) return null;
  const cityLower = cityName.toLowerCase().trim();

  // Helper: pre-resolve continent for a country (fire-and-forget)
  const prefetchContinent = (country) => {
    if (country && !dynamicCountryContinentCache[country.toLowerCase().trim()]) {
      getCountryContinent(country).catch(() => {}); // Non-blocking
    }
  };

  // 1. Check static list first (instant)
  if (CITY_TO_COUNTRY[cityLower]) {
    prefetchContinent(CITY_TO_COUNTRY[cityLower]);
    return CITY_TO_COUNTRY[cityLower];
  }

  // 2. Check dynamic cache (already resolved by LLM)
  if (dynamicCityCountryCache[cityLower]) {
    prefetchContinent(dynamicCityCountryCache[cityLower]);
    return dynamicCityCountryCache[cityLower];
  }

  // 2b. Handle "City, Country" format — extract city part and retry lookups (avoids unnecessary LLM calls)
  if (cityLower.includes(',')) {
    const cityOnly = cityLower.split(',')[0].trim();
    const fromStatic = CITY_TO_COUNTRY[cityOnly];
    if (fromStatic) { prefetchContinent(fromStatic); return fromStatic; }
    const fromDynamic = dynamicCityCountryCache[cityOnly];
    if (fromDynamic) { prefetchContinent(fromDynamic); return fromDynamic; }
    // The part after the comma might BE the country (e.g., "Ericeira, Portugal")
    const possibleCountry = cityName.split(',').slice(1).join(',').trim();
    if (possibleCountry && possibleCountry.length > 1 && possibleCountry.length < 50) {
      evictOldestFromCache(dynamicCityCountryCache, DYNAMIC_CACHE_MAX);
      dynamicCityCountryCache[cityOnly] = possibleCountry;
      prefetchContinent(possibleCountry);
      return possibleCountry;
    }
  }

  // 2c. Handle accented variants — normalize and retry (e.g., "Pinhão" → "pinhao")
  const cityNormalized = cityLower.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (cityNormalized !== cityLower) {
    const fromStaticNorm = CITY_TO_COUNTRY[cityNormalized];
    if (fromStaticNorm) { prefetchContinent(fromStaticNorm); return fromStaticNorm; }
    const fromDynamicNorm = dynamicCityCountryCache[cityNormalized];
    if (fromDynamicNorm) { prefetchContinent(fromDynamicNorm); return fromDynamicNorm; }
  }

  // 3. LLM fallback - ask AI for the country
  try {
    // Use Haiku 4.5 for simple geography lookup (fast & cheap)
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
      method: "POST",
      headers: await getApiHeaders(),
      body: JSON.stringify({
        model: "claude-haiku-4-5-20251001",
        max_tokens: 50,
        system: "You are a geography expert. Given a place name (city, resort, hotel, or region), return ONLY the sovereign country name. Nothing else - just the country name. For US locations (including territories like Puerto Rico, Guam, US Virgin Islands, American Samoa), return 'USA'. For French overseas territories (Réunion, Martinique, Guadeloupe, New Caledonia, Tahiti, Bora Bora), return 'France'. For Danish territories (Faroe Islands, Greenland), return 'Denmark'. For Dutch Caribbean (Aruba, Curaçao, Bonaire), return 'Netherlands'. For UK territories (Bermuda, Gibraltar, Cayman Islands), return 'UK'. If unsure, return 'Unknown'.",
        messages: [{ role: "user", content: `What country is "${cityName}" located in?` }]
      })
    });

    if (response.ok) {
      const data = await response.json();
      const country = data.content?.[0]?.text?.trim() || null;

      if (country && country !== 'Unknown') {
        // Cache the result for future lookups
        evictOldestFromCache(dynamicCityCountryCache, DYNAMIC_CACHE_MAX);
        dynamicCityCountryCache[cityLower] = country;
        if (process.env.NODE_ENV === 'development') console.log(`🌍 LLM resolved: ${cityName} → ${country}`);
        // known_cities auto-grow happens server-side in generationOrchestrator.js
        // Also pre-resolve continent for this country
        prefetchContinent(country);
        return country;
      }
    }
  } catch (error) {
    // Silent fail in production - city resolution is non-critical
    if (process.env.NODE_ENV === 'development') console.error('LLM city-country lookup failed:', error);
  }

  return null;
};

// getCityCountrySync — thin wrapper over shared, injecting dynamicCityCountryCache
const getCityCountrySync = (cityName) => _getCityCountrySync(cityName, dynamicCityCountryCache);

// Pre-resolve multiple cities in parallel (call this when planning a trip)
// BUG 3 FIX: Also resolve continents for KNOWN cities (previously skipped entirely)
const preResolveCities = async (cityNames) => {
  const unknownCities = cityNames.filter(city => {
    if (!city) return false;
    const cityLower = city.toLowerCase().trim();
    return !CITY_TO_COUNTRY[cityLower] && !dynamicCityCountryCache[cityLower];
  });

  // Resolve unknown cities via LLM
  if (unknownCities.length > 0) {
    if (process.env.NODE_ENV === 'development') console.log(`🌍 Pre-resolving ${unknownCities.length} unknown cities via LLM...`);
    const countries = await Promise.all(unknownCities.map(city => getCityCountry(city)));
    const uniqueUnknownCountries = [...new Set(countries.filter(Boolean))];
    // Resolve continents for newly discovered countries (only ones not in static table)
    const needsContinentLookup = uniqueUnknownCountries.filter(c => !COUNTRY_TO_CONTINENT[c.toLowerCase().trim()]);
    if (needsContinentLookup.length > 0) {
      await Promise.all(needsContinentLookup.map(country => getCountryContinent(country)));
    }
  }

  // Also populate continent cache for ALL known cities (so getContinentForCountry works)
  // This ensures cross-continental flight validation has continent data for known cities too
  const allCountries = cityNames.map(city => {
    if (!city) return null;
    return getCityCountrySync(city);
  }).filter(Boolean);
  const uniqueAllCountries = [...new Set(allCountries)];
  // For any country not in static table AND not in cache, resolve via LLM
  const uncachedCountries = uniqueAllCountries.filter(c =>
    !COUNTRY_TO_CONTINENT[c.toLowerCase().trim()] && !dynamicCountryContinentCache[c.toLowerCase().trim()]
  );
  if (uncachedCountries.length > 0) {
    await Promise.all(uncachedCountries.map(country => getCountryContinent(country)));
  }
};

// Get continent for a country — async with Haiku fallback
const getCountryContinent = async (country) => {
  if (!country) return null;
  const countryLower = country.toLowerCase().trim();

  // 1. Check dynamic cache first
  if (dynamicCountryContinentCache[countryLower]) {
    return dynamicCountryContinentCache[countryLower];
  }

  // 2. LLM fallback — ask Haiku for the continent
  try {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
      method: "POST",
      headers: await getApiHeaders(),
      body: JSON.stringify({
        model: "claude-haiku-4-5-20251001",
        max_tokens: 30,
        system: "You are a geography expert. Given a country name, return ONLY the continent name. Use exactly one of: Europe, Asia, Africa, North America, South America, Oceania. Nothing else.",
        messages: [{ role: "user", content: `What continent is "${country}" on?` }]
      })
    });

    if (response.ok) {
      const data = await response.json();
      const continent = data.content?.[0]?.text?.trim() || null;
      const validContinents = ['Europe', 'Asia', 'Africa', 'North America', 'South America', 'Oceania'];
      if (continent && validContinents.includes(continent)) {
        evictOldestFromCache(dynamicCountryContinentCache, DYNAMIC_CACHE_MAX);
        dynamicCountryContinentCache[countryLower] = continent;
        if (process.env.NODE_ENV === 'development') console.log(`🌍 LLM resolved continent: ${country} → ${continent}`);
        return continent;
      }
    }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('LLM continent lookup failed:', error);
  }

  return null;
};

// COUNTRY_TO_CONTINENT — imported from @wander/shared via ./utils/geographyConstants

// cleanIataCode — imported from @wander/shared via ./utils/transportConstants

// getContinentHintFromText — imported from @wander/shared via ./utils/geographyConstants

// getContinentForCountry — thin wrapper over shared, injecting dynamicCountryContinentCache
const getContinentForCountry = (country) => _getContinentForCountry(country, dynamicCountryContinentCache);

// COUNTRY_TO_CURRENCY — imported from @wander/shared via ./utils/travelInfoConstants

// ISLAND_LOCATION_KEYWORDS — imported from @wander/shared via ./utils/geographyConstants

// Dynamic island geography cache — populated from getDestinationGeography() results
// Key: lowercase city name, Value: { isIsland, isArchipelago, noCarAccess, accessMethods }
const dynamicGeographyCache = {};

// isIslandCity — thin wrapper over shared, injecting dynamicGeographyCache
const isIslandCity = (cityName) => _isIslandCity(cityName, dynamicGeographyCache);

// SCHENGEN_COUNTRIES, VISA_REQUIREMENTS_US, BORDER_CROSSING_NOTES — imported from @wander/shared via ./utils/travelInfoConstants

// CITY_TO_IANA_TIMEZONE — imported from @wander/shared via ./utils/timezoneConstants

// Dynamic timezone cache (populated by Haiku for cities not in static table)
const dynamicTimezoneCache = {};

// COUNTRY_TO_TIMEZONE — imported from @wander/shared via ./utils/timezoneConstants

// Timezone functions — thin wrappers injecting local caches
const getCityIANATimezone = (cityName) => _getCityIANATimezone(cityName, { dynamicTimezoneCache, getCityCountrySync });
const getCityTimezoneOffset = (cityName, date = null) => _getCityTimezoneOffset(cityName, date, { dynamicTimezoneCache, getCityCountrySync });
const calculateFlightArrival = (departureTime, flightDurationHours, fromCity, toCity, travelDate = null) => _calculateFlightArrival(departureTime, flightDurationHours, fromCity, toCity, travelDate, { dynamicTimezoneCache, getCityCountrySync });
const getTimezoneAbbr = (cityName, date = null) => _getTimezoneAbbr(cityName, date, { dynamicTimezoneCache, getCityCountrySync });

// isHotelOrResort — imported from @wander/shared via ./utils/destinationConstants

// Helper to format destination with country names
// For hotels/resorts, preserve the hotel name as the destination
// Parameters:
//   - destination: The destination string (can be multi-destination with " & ")
//   - isHotelTripMode: When true, ALL destinations are treated as hotel/property names
//   - destinationTypes: Optional comma-separated string from LLM (e.g., "location, hotel, hotel")
const formatDestinationWithCountries = (destination, isHotelTripMode = false, destinationTypes = null) => {
  if (!destination) return '';

  // If in hotel trip mode, preserve ALL destination names as-is (they're property names)
  if (isHotelTripMode) {
    return destination;
  }

  // Parse destinationTypes if provided (from LLM extraction)
  const typesList = destinationTypes
    ? destinationTypes.split(',').map(t => t.trim().toLowerCase())
    : null;

  // Check if destination already has country info (e.g., "Paris, France")
  // If it contains a comma and the part after is a known country, it's already formatted
  const parts = destination.split(',').map(p => p.trim());
  if (parts.length >= 2 && !destination.includes('&')) {
    const lastPart = parts[parts.length - 1].toLowerCase();
    // KNOWN_COUNTRIES_LOWER imported from @wander/shared via destinationConstants
    if (KNOWN_COUNTRIES_LOWER.has(lastPart)) {
      return destination; // Already has country
    }
  }

  // Handle multi-destination (e.g., "Paris & Interlaken" or "Italy & Hotel Georges V")
  // But NOT hotel/resort names that contain "&" (e.g., "Waldhaus Flims Alpine Grand Hotel & Spa")
  if (destination.includes('&') && !isHotelOrResort(destination)) {
    const places = destination.split('&').map(c => c.trim());
    const formattedPlaces = places.map((place, index) => {
      // Check if place already has country
      if (place.includes(',')) {
        return place;
      }

      // Check type from LLM if available
      const placeType = typesList && typesList[index] ? typesList[index] : null;

      // If LLM says it's a hotel, preserve as-is
      if (placeType === 'hotel') {
        return place;
      }

      // If LLM says it's a location, add country
      if (placeType === 'location') {
        const country = getCityCountrySync(place);
        return (country && country.toLowerCase() !== place.toLowerCase()) ? `${place}, ${country}` : place;
      }

      // Fallback: use static detection if no LLM type available
      if (isHotelOrResort(place)) {
        return place;
      }

      // Default: try to add country for locations (guard against country === place)
      const country = getCityCountrySync(place);
      return (country && country.toLowerCase() !== place.toLowerCase()) ? `${place}, ${country}` : place;
    });
    return formattedPlaces.join(' & ');
  }

  // Single destination
  const placeType = typesList && typesList[0] ? typesList[0] : null;

  // If LLM says it's a hotel, preserve as-is
  if (placeType === 'hotel') {
    return destination;
  }

  // Fallback: use static detection
  if (isHotelOrResort(destination)) {
    return destination;
  }

  // If the destination itself IS a country, don't append country (avoids "Japan, Japan")
  // KNOWN_COUNTRIES_LOWER imported from @wander/shared via destinationConstants
  if (KNOWN_COUNTRIES_LOWER.has(destination.toLowerCase().trim())) {
    return destination;
  }

  // Single city - add country if found
  const country = getCityCountrySync(destination);
  // Also guard against country === destination (e.g., LLM resolves "Japan" → "Japan")
  if (country && country.toLowerCase() !== destination.toLowerCase()) {
    return `${destination}, ${country}`;
  }
  return destination;
};

// getRecommendedMonths — imported from @wander/shared via ./utils/destinationConstants

// Scrollable Message Content Component - handles long messages with scroll-to-top indicator
// IMPORTANT: Defined OUTSIDE the main component so React maintains a stable component identity.
// If defined inside, every parent re-render (e.g., typing in chat input) would create a new
// component type, causing React to unmount/remount all message bubbles — losing scroll position
// and causing visible text flicker.
const ScrollableMessageContent = React.memo(({ content, isUser, parseMarkdown }) => {
  const contentRef = useRef(null);
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [isLongMessage, setIsLongMessage] = useState(false);
  const maxHeight = 400; // Max height before scroll kicks in

  // useLayoutEffect prevents visible flicker: measures height BEFORE browser paints
  // (useEffect would render at full height first, then snap to 400px — causing flicker)
  useLayoutEffect(() => {
    const el = contentRef.current;
    if (el) {
      setIsLongMessage(el.scrollHeight > maxHeight);
    }
  }, [content]);

  useEffect(() => {
    const el = contentRef.current;
    if (el) {
      const handleScroll = () => {
        setShowScrollUp(el.scrollTop > 50);
      };
      el.addEventListener('scroll', handleScroll);
      return () => el.removeEventListener('scroll', handleScroll);
    }
  }, [content]);

  const bgColorClass = isUser
    ? 'text-white'
    : 'bg-white shadow-sm border border-gray-100';

  const gradientBg = isUser ? 'from-[#313143]' : 'from-white';

  return (
    <div className={`px-5 py-3.5 w-full max-w-[640px] relative ${bgColorClass}`} style={{ ...FONTS.body, borderRadius: isUser ? '22px 22px 6px 22px' : '6px 22px 22px 22px', ...(isUser ? { background: '#313143' } : {}) }}>
      {showScrollUp && (
        <button
          onClick={() => contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          className={`absolute top-2 right-2 z-10 ${isUser ? 'bg-white/30 hover:bg-white/50' : 'bg-white/90 hover:bg-white'} shadow-md rounded-full p-1.5 transition-all`}
          title="Scroll to top"
        >
          <ChevronUp className={`w-4 h-4 ${isUser ? 'text-white' : 'text-gray-600'}`} />
        </button>
      )}
      {showScrollUp && (
        <div className={`absolute top-0 left-0 right-0 h-6 bg-gradient-to-b ${gradientBg} to-transparent rounded-t-2xl pointer-events-none z-[5]`} />
      )}
      <div
        ref={contentRef}
        className="prose prose-sm max-w-none overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
        style={{ maxHeight: isLongMessage ? `${maxHeight}px` : 'none' }}
      >
        {parseMarkdown(content)}
      </div>
      {isLongMessage && (
        <div className={`absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t ${gradientBg} to-transparent rounded-b-2xl pointer-events-none`} />
      )}
    </div>
  );
});

/** Title-case a city name: "lisbon" → "Lisbon", "new york" → "New York" */
const titleCaseCity = (name) => {
  if (!name || typeof name !== 'string') return name;
  // Split on whitespace/hyphens, capitalize first letter of each part — handles accented chars (e.g. Cancún not CancúN)
  return name.replace(/(^|[\s\-])(\w)/g, (match, sep, char) => sep + char.toUpperCase());
};

const WanderAppComplete = () => {
  // ============================================
  // DEBUG MODE - Set to false for production to reduce console noise
  // Users will NOT see any console output in production
  // ============================================
  const DEBUG_MODE = process.env.NODE_ENV === 'development';
  
  // Debug logging helper - only logs when DEBUG_MODE is true
  // VERBOSE_TIMELINE: set to true only when debugging timeline issues — reduces 1500+ log lines to ~50
  const VERBOSE_TIMELINE = false;
  /* eslint-disable no-console */
  // ── Log Buffer — captures debug logs for export ──
  if (!window._wanderLogs) window._wanderLogs = [];
  const MAX_LOG_BUFFER = 5000;
  const debugLog = (...args) => {
    if (DEBUG_MODE) {
      console.log(...args);
      const timestamp = new Date().toISOString();
      const message = args.map(a => typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a)).join(' ');
      window._wanderLogs.push(`[${timestamp}] ${message}`);
      if (window._wanderLogs.length > MAX_LOG_BUFFER) window._wanderLogs.splice(0, window._wanderLogs.length - MAX_LOG_BUFFER);
    }
  };
  // Export logs to file — call from console: window.exportLogs()
  window.exportLogs = () => {
    const logs = (window._wanderLogs || []).join('\n');
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wander-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    console.log(`📋 Exported ${window._wanderLogs.length} log entries`);
  };
  // Timeline-specific logging — only logs when VERBOSE_TIMELINE is enabled
  const timelineLog = (...args) => {
    if (DEBUG_MODE && VERBOSE_TIMELINE) console.log(...args);
  };
  const debugWarn = (...args) => {
    if (DEBUG_MODE) console.warn(...args);
  };
  const debugError = (...args) => {
    // Only log errors in development mode
    if (DEBUG_MODE) {
      console.error(...args);
    }
    // In production, errors are silently ignored to avoid user confusion
  };
  /* eslint-enable no-console */

  // safeAirportCity — imported from @wander/shared via ./utils/transportConstants
  const safeAirportCity = _safeAirportCity;

  // Helper: build a geocodable airport string for Google Routes API
  // IMPORTANT: Never append the DESTINATION city to an IATA code — Google geocodes
  //   "CDG Airport, Paris" to a random Paris address (~3 km) instead of the real airport (~30 km).
  // Instead, use the airport's official name (e.g., "Paris Charles de Gaulle Airport").
  const geocodableAirportName = (airport) => {
    if (!airport) return 'Airport';
    // Prefer the airport's full name when available and meaningful
    // (LLM is prompted to return "Official Airport Name" like "Carlos Ibáñez del Campo International Airport")
    if (airport.name && !/^[A-Z]{2,4}\s+(Airport|International)$/i.test(airport.name)) {
      // Full name like "Paris Charles de Gaulle Airport" → geocodes correctly
      return airport.name;
    }
    // Generic name like "CDG Airport" — use IATA code + "International Airport"
    // IATA codes are globally unique, so Google can resolve them without a city qualifier
    if (airport.code) {
      return `${airport.code} International Airport`;
    }
    return airport.name || 'Airport';
  };

  // ============================================
  // AUTH CONTEXT
  // ============================================
  const {
    user,
    profile,
    isAuthenticated: isLoggedIn,
    loading: authLoading,
    getPreferencesForAI,
    showPersonalization,
    checkUsageLimit,
    trackUsageOnBackend,
    refreshUsage,
    checkModificationLimit,
    trackModification,
    getTierLimits,
    hasFeature,
    tier,
    isPaymentPastDue,
    refreshProfile,
  } = useAuth();

  // Settings modal state
  const [showSettings, setShowSettings] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [mustDoInput, setMustDoInput] = useState('');

  // Pre-fetch the user's referral code on auth so the Copy Invite Link
  // click handler can run synchronously (no `await` between the click and
  // the clipboard write — Safari/Brave/Chrome variations expire the
  // user-gesture token across awaits, causing both the modern Clipboard
  // API and the legacy execCommand fallback to reject. PR #261 added the
  // legacy fallback; this addition removes the await before the write so
  // both paths can succeed deterministically).
  const [referralCode, setReferralCode] = useState(null);
  useEffect(() => {
    if (!user?.id) { setReferralCode(null); return; }
    let cancelled = false;
    getReferralCode().then(code => {
      if (cancelled) return;
      setReferralCode(typeof code === 'string' ? code : code?.code || null);
    }).catch(err => {
      if (!cancelled) console.warn('[referral-prefetch] failed:', err?.message);
    });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Confirmation modal state (replaces browser confirm/alert dialogs)
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    onConfirm: null,
    onCancel: null,
    type: 'confirm', // 'confirm', 'alert', or 'upgrade'
  });
  const [toasts, setToasts] = useState([]); // Toast notification stack
  const [showUpgradeModalOpen, setShowUpgradeModalOpen] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState(''); // what triggered it
  const [billingInterval, setBillingInterval] = useState('monthly'); // 'monthly' | 'yearly'
  const [liveExchangeRates, setLiveExchangeRates] = useState(null); // Live exchange rates from API

  // Initialize analytics on mount
  useEffect(() => {
    initAnalytics();
  }, []);

  // Fetch live exchange rates once user is authenticated
  useEffect(() => {
    if (!user) return;
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/exchange-rates`, {
          headers: await getApiHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          if (data?.rates) setLiveExchangeRates(data.rates);
        }
      } catch {
        // Silent fallback — static rates will be used
      }
    })();
  }, [user]);

  // Set up analytics when user profile loads
  useEffect(() => {
    if (profile?.analytics_consent && user) {
      setAnalyticsConsent(true);
      identifyUser(user.id, profile);
    }
  }, [profile, user]);

  // Real-time sync: Supabase Realtime (cross-device) + BroadcastChannel (cross-tab)
  useEffect(() => {
    if (!user) return;

    const refreshItineraries = async () => {
      try {
        const itineraries = await getSavedItineraries(user.id);
        setSavedItinerariesList(itineraries || []);
      } catch (err) {
        console.warn('[Sync] Failed to refresh itineraries:', err.message);
      }
    };

    // Supabase Realtime: fires when another device saves/deletes
    const unsubRealtime = subscribeToItineraryChanges(user.id, (eventType) => {
      debugLog(`📡 Realtime sync: ${eventType} from another device`);
      refreshItineraries();
    });

    // BroadcastChannel: fires when another tab saves/deletes
    const unsubBroadcast = onItineraryBroadcast((eventType) => {
      debugLog(`📡 Cross-tab sync: ${eventType} from another tab`);
      refreshItineraries();
    });

    return () => {
      unsubRealtime();
      unsubBroadcast();
    };
  }, [user]);

  // App navigation stage — detect /trip/:hash URLs for shared trip viewing.
  // Also checks sessionStorage for a pending share redirect after SSO sign-in
  // (Google OAuth redirects to root, losing the /trip/:hash URL).
  // Capture referral code from /join/{code} URL before routing
  const [referralCodeFromUrl] = useState(() => {
    const path = window.location.pathname;
    const joinMatch = path.match(/^\/join\/([A-Z0-9-]+)$/i);
    if (joinMatch) {
      const code = joinMatch[1].toUpperCase();
      try {
        localStorage.setItem('arrivo_referred_by', code);
        sessionStorage.setItem('arrivo_pending_referral', code);
      } catch {}
      // Clean up URL so it doesn't persist in browser history
      window.history.replaceState({}, document.title, '/');
      return code;
    }
    // Also check ?ref= query param
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = params.get('ref');
      if (ref) {
        const code = ref.trim().toUpperCase();
        localStorage.setItem('arrivo_referred_by', code);
        sessionStorage.setItem('arrivo_pending_referral', code);
        params.delete('ref');
        const newUrl = params.toString() ? `${window.location.pathname}?${params}` : window.location.pathname;
        window.history.replaceState({}, document.title, newUrl);
        return code;
      }
    } catch {}
    return null;
  });

  const [stage, setStage] = useState(() => {
    const path = window.location.pathname;
    // Check for collaborative trip invite URL
    const inviteMatch = path.match(/^\/trip\/invite\/([a-zA-Z0-9_-]+)$/);
    if (inviteMatch) return 'invite-landing';
    const tripMatch = path.match(/^\/trip\/([a-f0-9]{16})$/);
    if (tripMatch) return 'shared-trip';
    if (path === '/pricing') return 'pricing';
    // Check for pending share redirect after SSO
    try {
      const pending = sessionStorage.getItem('wander_pending_share');
      if (pending && /^[a-f0-9]{16}$/.test(pending)) return 'shared-trip';
    } catch (e) {}
    // Check for Stripe checkout return
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') return 'checkout-success';
    // Restore itinerary view if we have a saved itinerary AND destination in sessionStorage
    try {
      const savedItinerary = sessionStorage.getItem('wander_current_itinerary');
      if (savedItinerary) {
        const parsed = JSON.parse(savedItinerary);
        if (parsed?.days?.length > 0 && parsed?.destination) return 'itinerary';
        // Has days but no destination — can reconstruct, allow it
        if (parsed?.days?.length > 0) return 'itinerary';
      }
    } catch { /* ignore */ }
    return 'landing';
  });
  const [sharedTripHash] = useState(() => {
    const path = window.location.pathname;
    const tripMatch = path.match(/^\/trip\/([a-f0-9]{16})$/);
    if (tripMatch) return tripMatch[1];
    // Check for pending share redirect after SSO
    try {
      const pending = sessionStorage.getItem('wander_pending_share');
      if (pending && /^[a-f0-9]{16}$/.test(pending)) {
        sessionStorage.removeItem('wander_pending_share');
        window.history.pushState({}, '', `/trip/${pending}`);
        return pending;
      }
    } catch (e) {}
    return null;
  });
  const [inviteToken] = useState(() => {
    const path = window.location.pathname;
    const inviteMatch = path.match(/^\/trip\/invite\/([a-zA-Z0-9_-]+)$/);
    return inviteMatch ? inviteMatch[1] : null;
  });

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [generatedTrip, setGeneratedTrip] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [loadingState, setLoadingState] = useState(null); // 'searching', 'generating', 'updating', 'optimizing'
  const [destinationOptions, setDestinationOptions] = useState([]);
  const [shownDestinations, setShownDestinations] = useState([]); // Track all destinations shown to avoid repeats
  const [transportType, setTransportType] = useState('plane');
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizStep, setQuizStep] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [pendingRegionChange, setPendingRegionChange] = useState(null);
  const [useMockMode, setUseMockMode] = useState(false);
  const messagesEndRef = useRef(null);
  const pendingHotelCitiesRef = useRef([]); // Tracks hotel cities for multi-city hotel mode (survives async gaps)
  const itineraryChatEndRef = useRef(null);
  const pendingChangesRef = useRef(null);
  const messageIntervalRef = useRef(null); // Track loading message interval for cleanup
  const generationTimeoutRef = useRef(null); // Track generation timeout for cleanup
  const generationInProgressRef = useRef(false); // Guard against duplicate generation calls
  // BUG 9 FIX: Pin hotel per stop — first day of each stop sets canonical hotel
  const pinnedHotelsRef = useRef({});

  // NEW: Dynamic airport suggestions
  const [destinationAirports, setDestinationAirports] = useState([]);
  const [lastStopAirports, setLastStopAirports] = useState([]); // For multi-stop departure airport suggestions
  const [isLoadingAirports, setIsLoadingAirports] = useState(false);

  // NEW: Airport distance warnings (for airports far from destination)
  const [airportDistances, setAirportDistances] = useState({
    arrival: null, // { duration: "3 hours 30 mins", durationMinutes: 210, isFar: true }
    departure: null
  });

  // NEW: Itinerary validation state
  const [validationIssues, setValidationIssues] = useState([]);
  const [destinationSafetyInfo, setDestinationSafetyInfo] = useState(null);
  const [showValidationPanel, setShowValidationPanel] = useState(false);
  
  // NEW: Home city banner state
  const [showHomeCityBanner, setShowHomeCityBanner] = useState(false); // Disabled — departure city set via Trip Builder sidebar
  const [landingTab, setLandingTab] = useState('home'); // 'home' | 'trips' | 'community'
  const [communitySubTab, setCommunitySubTab] = useState('feed'); // 'feed' | 'rankings' | 'discover' | 'circle'
  const [communityFilter, setCommunityFilter] = useState('All');
  const [showCompareModal, setShowCompareModal] = useState(false);
  // Place name the CompareModal should anchor pairs on. Set when the modal
  // opens from a freshly-rated place (PlaceReviewForm.onSuccess) so the
  // Beli-style binary-search insertion picks pairs that include this item
  // until placement converges. Cleared when the modal closes.
  const [compareFocalItem, setCompareFocalItem] = useState(null);
  const [showAdminQueue, setShowAdminQueue] = useState(false);
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [showSkeletonBuilder, setShowSkeletonBuilder] = useState(false);
  const [showTripImporter, setShowTripImporter] = useState(false);
  const [rankingCategory, setRankingCategory] = useState('All');
  const [sidebarReviewPlace, setSidebarReviewPlace] = useState(null);
  const [homeCityAsked, setHomeCityAsked] = useState(false);

  // ============================================
  // REQUEST CACHE SYSTEM - Persist across sessions
  // ============================================
  const apiCacheRef = useRef({
    get: (key) => {
      try {
        const stored = localStorage.getItem(`wander_cache_${key}`);
        if (!stored) return null;
        const { value, expiry } = JSON.parse(stored);
        if (Date.now() > expiry) {
          localStorage.removeItem(`wander_cache_${key}`);
          return null;
        }
        return value;
      } catch (e) {
        return null;
      }
    },
    set: (key, value, ttlMs = 10 * 60 * 1000) => {
      try {
        localStorage.setItem(`wander_cache_${key}`, JSON.stringify({
          value,
          expiry: Date.now() + ttlMs
        }));
      } catch (e) {
        // localStorage full or unavailable, ignore
      }
    },
    generateKey: (...args) => args.map(a => String(a).toLowerCase().replace(/[^a-z0-9]/g, '_')).join(':')
  });
  
  // Clean up old cache entries on mount
  useEffect(() => {
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith('wander_cache_'));
      keys.forEach(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key));
          if (data?.expiry && Date.now() > data.expiry) {
            localStorage.removeItem(key);
          }
        } catch (e) {
          localStorage.removeItem(key);
        }
      });
    } catch (e) {
      // Ignore cleanup errors
    }
  }, []);

  const [itinerary, setItineraryRaw] = useState(() => {
    // Restore itinerary from sessionStorage on page load
    try {
      const saved = sessionStorage.getItem('wander_current_itinerary');
      if (saved) return JSON.parse(saved);
    } catch { /* ignore parse errors */ }
    return null;
  });
  // Wrap setItinerary to also persist to sessionStorage
  const setItinerary = React.useCallback((valueOrUpdater) => {
    setItineraryRaw(prev => {
      const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
      try {
        if (next) {
          sessionStorage.setItem('wander_current_itinerary', JSON.stringify(next));
        } else {
          sessionStorage.removeItem('wander_current_itinerary');
        }
      } catch { /* storage full or unavailable */ }
      return next;
    });
  }, []);
  const [currentItineraryId, setCurrentItineraryId] = useState(() => {
    try { return sessionStorage.getItem('wander_current_itinerary_id') || null; } catch { return null; }
  }); // Database ID for modification tracking
  const itineraryUserModifiedRef = useRef(false); // Track if user has modified the itinerary (to prevent background enrichment from overwriting)

  // Collaboration state
  const [showShareModal, setShowShareModal] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [currentTripId, setCurrentTripId] = useState(() => {
    try { return sessionStorage.getItem('wander_current_itinerary_id') || null; } catch { return null; }
  });
  const [tripCollaborators, setTripCollaborators] = useState([]);
  const [userTripRole, setUserTripRole] = useState('owner');
  const lastSyncedHomeCityRef = useRef(null); // Track last Supabase home_city to avoid overwriting local sidebar/chat changes

  // Trip name editing state
  const [isEditingTripName, setIsEditingTripName] = useState(false);
  const [editedTripName, setEditedTripName] = useState('');
  const tripNameInputRef = useRef(null);

  // Debounce tracking for async buttons to prevent double-clicks
  const buttonDebounceRef = useRef({});
  const lastDeletedRef = useRef(null); // Stores last deleted item for undo
  const isButtonDebounced = (buttonId, debounceMs = 1000) => {
    const now = Date.now();
    const lastClick = buttonDebounceRef.current[buttonId] || 0;
    if (now - lastClick < debounceMs) {
      return true; // Still debounced
    }
    buttonDebounceRef.current[buttonId] = now;
    return false;
  };
  const [isSavingItinerary, setIsSavingItinerary] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null); // Timestamp of last save
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false); // Track unsaved modifications
  const [showSavedItineraries, setShowSavedItineraries] = useState(false);
  const [saveButtonText, setSaveButtonText] = useState(null); // Temporary text for save button ("Saved!", "Updated!", or error)
  const [showRankPrompt, setShowRankPrompt] = useState(false); // Post-save ranking prompt
  const [openTripMenuId, setOpenTripMenuId] = useState(null); // Hub trip card kebab menu — only one open at a time
  const [savedItinerariesList, setSavedItinerariesList] = useState([]);
  const [isLoadingSavedItineraries, setIsLoadingSavedItineraries] = useState(false);

  // Pre-fetch saved itineraries on landing so trip cards and stats are available immediately
  useEffect(() => {
    if (!user || stage !== 'landing') return;
    if (savedItinerariesList.length > 0) return; // already loaded
    let cancelled = false;
    (async () => {
      try {
        const itineraries = await getSavedItineraries(user.id);
        if (!cancelled) setSavedItinerariesList(itineraries || []);
      } catch (err) {
        console.warn('[Landing] Failed to pre-fetch saved itineraries:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [user, stage]);
  const [savedItinerarySearch, setSavedItinerarySearch] = useState('');
  const [savedItinerarySort, setSavedItinerarySort] = useState('newest');
  const [editingItem, setEditingItem] = useState(null); // dayIndex, itemIndex, type: 'activity' or 'meal', field: 'time' or 'duration'
  const [draggedItem, setDraggedItem] = useState(null); // For drag and drop
  const [showAddActivityModal, setShowAddActivityModal] = useState(false);
  const [showAddMealModal, setShowAddMealModal] = useState(false);
  const [showAddDayModal, setShowAddDayModal] = useState(false);
  const [addDayCustomText, setAddDayCustomText] = useState('');
  const [addDayAfterIndex, setAddDayAfterIndex] = useState(null);
  const [addActivityDay, setAddActivityDay] = useState(null);
  const [addActivityInsertIndex, setAddActivityInsertIndex] = useState(null); // null = append, number = insert at index
  const [addActivityPreFill, setAddActivityPreFill] = useState(''); // Pre-fill activity name (e.g., from wishlist)
  const [showBrowseCommunityTrips, setShowBrowseCommunityTrips] = useState(false);
  const [insertingBreak, setInsertingBreak] = useState(false); // When true, show insertion markers between timeline items
  const [selectedDestination, setSelectedDestination] = useState(() => {
    try {
      const saved = sessionStorage.getItem('wander_selected_destination');
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return null;
  });
  const [currentDay, setCurrentDay] = useState(0);
  // Persist selectedDestination to sessionStorage for refresh recovery
  React.useEffect(() => {
    try {
      if (selectedDestination) {
        sessionStorage.setItem('wander_selected_destination', JSON.stringify(selectedDestination));
      }
    } catch { /* ignore */ }
  }, [selectedDestination]);
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false);
  const [packingList, setPackingList] = useState(null);
  const [packingListGeneratedWith, setPackingListGeneratedWith] = useState(null); // { startDate, endDate } when packing list was generated
  const [showPackingList, setShowPackingList] = useState(false);
  const [checkedPackingItems, setCheckedPackingItems] = useState({});
  // Custom packing items the user added (Hope's feedback: "Can I add my own items?
  // E.g., extra batteries for Nana's hearing aids, etc.").
  // Keyed by category index → array of { item, packed } objects. Persisted in
  // localStorage so additions survive reloads/regeneration. Scoped per
  // destination so custom items don't bleed across trips.
  const [customPackingItems, setCustomPackingItems] = useState(() => {
    try {
      const stored = localStorage.getItem('arrivo_custom_packing_items');
      if (!stored) return {};
      const parsed = JSON.parse(stored);
      // Defensive validation — JSON.parse('null'), 'false', '"oops"' all
      // succeed but produce non-objects. Without this guard, the next
      // access to customPackingItems[catIdx] crashes on a primitive.
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed;
      }
      return {};
    } catch { return {}; }
  });
  const [customItemInputs, setCustomItemInputs] = useState({}); // { [catIdx]: 'partial input' }
  // Persist custom items on every change
  useEffect(() => {
    try {
      localStorage.setItem('arrivo_custom_packing_items', JSON.stringify(customPackingItems));
    } catch {}
  }, [customPackingItems]);
  const [isGeneratingPackingList, setIsGeneratingPackingList] = useState(false);
  const [showTripLogger, setShowTripLogger] = useState(false);
  const [chatWidgetOpen, setChatWidgetOpen] = useState(false);
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [currentFact, setCurrentFact] = useState('');
  const [factIndex, setFactIndex] = useState(0);
  
  // NEW: Trip type selection (destination-first vs hotel-first)
  const [tripType, setTripType] = useState(null); // 'destination' | 'hotel' | null
  // Ref mirror for tripType — avoids stale closure issues when setTripType + handleSend
  // are called in the same tick (e.g., home page input sets type then sends via setTimeout)
  const tripTypeRef = useRef(null);
  useEffect(() => { tripTypeRef.current = tripType; }, [tripType]);
  // NEW: Expanded trip type selection (shows single vs multi options)
  const [tripTypeExpanded, setTripTypeExpanded] = useState(null); // 'destination' | 'hotel' | null - which card is expanded
  // NEW: Trip scope selection (single vs multi-destination)
  const [tripScope, setTripScope] = useState(null); // 'single' | 'multi' | null
  // NEW: Selected hotel property for hotel-first trips (object with name, location)
  const [selectedHotelProperty, setSelectedHotelProperty] = useState(null);
  // NEW: Accommodation type — affects timeline cards and LLM prompts
  // 'hotel' (default) | 'cruise' | 'campervan' | 'custom'
  const [accommodationType, setAccommodationType] = useState('hotel');
  const [customAccommodationName, setCustomAccommodationName] = useState('');
  // NEW: Travel class preference for flights
  const [preferredTravelClass, setPreferredTravelClass] = useState('ECONOMY'); // ECONOMY | PREMIUM_ECONOMY | BUSINESS | FIRST

  // NEW: Unified trip plan structure for all trip types
  // Supports: single, multi-city, resort-hopping, hybrid, around-the-world
  const [tripPlan, setTripPlan] = useState(null);

  // NEW: Track selected stops when building multi-destination trips
  // Used when user is selecting from suggested stops
  const [selectedMultiStops, setSelectedMultiStops] = useState([]);
  // { name: 'Tokyo, Japan', continent: 'Asia', selected: true }

  // NEW: Trip Builder sidebar state
  const [tripBuilderStops, setTripBuilderStops] = useState([]);
  // Array of stops for the trip builder sidebar
  // { id: 'stop-1', name: 'Bora Bora, French Polynesia', continent: 'Oceania', days: 4, hotel: 'Four Seasons Bora Bora' }
  const [showTripBuilder, setShowTripBuilder] = useState(false);

  // Always show trip builder when entering chat
  useEffect(() => {
    if (stage === 'chat' && !showTripBuilder) {
      setShowTripBuilder(true);
    }
  }, [stage]);
  const [draggedStopIndex, setDraggedStopIndex] = useState(null);
  const [newStopInput, setNewStopInput] = useState(''); // Input for adding new stop in sidebar
  const [legTransports, setLegTransports] = useState({}); // { 'stopId1→stopId2': { mode, duration, durationMinutes, departureAirport, arrivalAirport, driveAvailable, isUserOverride, isLoading, source, reason, selectedFlight, time } }
  const [legFlightSearch, setLegFlightSearch] = useState({}); // { legKey: { loading, results, error, expanded } }
  const [hasOvernightOutbound, setHasOvernightOutbound] = useState(false); // True when outbound flight arrives next day → triggers Day 0 Travel Day
  const [overnightFlightInfo, setOvernightFlightInfo] = useState(null); // { departureTime, arrivalTime, from, to, airline, flightNumber, departureDate, arrivalDate }
  const [stopHotels, setStopHotels] = useState({}); // { [stopId]: { budget: 'midrange'|'budget'|'luxury', preferredHotel: '', isOverridden: true, mode: 'suggest'|'booked'|'skip', bookedHotel: '' } }
  const [addingStopType, setAddingStopType] = useState(null); // null | 'destination' | 'resort' — which add-stop input is open

  // ============================================
  // PERSISTENT TRIP CONTEXT (accumulated across messages)
  // ============================================
  const [tripContext, setTripContext] = useState({
    destinations: [],      // specific destinations added to trip
    pendingVague: [],      // [{ description, resolvedOptions, selected }]
    duration: null,        // total trip duration in days
    travelers: null,       // "couple" | "solo" | "family" | "friends"
    occasion: null,        // "honeymoon" | "anniversary" | etc
    month: null,           // "January" etc
    activities: [],        // accumulated activity interests (e.g., ["scuba diving", "hiking"])
    preferences: [],       // accumulated preferences (e.g., ["luxury", "beachfront"])
    requestedPlaces: [],   // specific places/venues user wants to visit (e.g., ["Ichiran Ramen in Shibuya"])
    hotelPreference: null, // named hotel brand
  });

  // Track last request ID for bug report correlation
  const lastRequestIdRef = useRef(null);

  // ============================================
  // PROGRESSIVE ITINERARY GENERATION STATE
  // ============================================
  // Track generation status for each day: 'pending' | 'generating' | 'complete' | 'failed'
  const [dayGenerationStatus, setDayGenerationStatus] = useState({});
  // Example: { 1: 'complete', 2: 'complete', 3: 'complete', 4: 'generating', 5: 'queued', 6: 'queued' }

  // Queue for regeneration requests made while generation is in progress
  const [regenerationQueue, setRegenerationQueue] = useState([]);
  // Example: [{ dayNumber: 2, feedback: 'More food experiences' }]

  // Track errors for failed days
  const [dayGenerationErrors, setDayGenerationErrors] = useState({});
  // Example: { 4: 'Request timed out' }

  // Track which day is currently being generated
  const [currentlyGeneratingDay, setCurrentlyGeneratingDay] = useState(null);

  // Track total days for the itinerary (so UI knows how many placeholders to show)
  const [totalItineraryDays, setTotalItineraryDays] = useState(0);

  // Track if progressive generation is active
  const [isProgressiveGenerating, setIsProgressiveGenerating] = useState(false);
  // Regenerate-day in-flight tracker. Holds the 1-indexed day number while
  // POST /api/regenerate-day runs so the failed-day banner can show a spinner.
  const [isRegeneratingDay, setIsRegeneratingDay] = useState(null);

  // Travel structure for multi-city/multi-property trips (calculated before generation)
  const [travelStructure, setTravelStructure] = useState(null);
  // Example: { stops: [...], travelDays: [{ day: 4, from: 'Paris', to: 'Rome' }] }

  /*
  tripPlan structure:
  {
    type: 'single' | 'multi-city' | 'resort-hopping' | 'hybrid' | 'around-the-world',
    stops: [
      {
        id: 'stop-1',
        type: 'city' | 'resort',
        name: 'Paris, France',
        days: 3,
        order: 1,
        hotel: { name: 'Hotel Name', address: '...' } // optional, can be AI-selected
      },
      {
        id: 'stop-2',
        type: 'city' | 'resort',
        name: 'Interlaken, Switzerland',
        days: 2,
        order: 2,
        hotel: null
      }
    ],
    totalDays: 5,
    transport: 'fly' | 'drive' | 'train' | 'mixed',
    isEditable: true // whether user can add/remove/reorder stops
  }
  */
  
  // Error handling state
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorInfo, setErrorInfo] = useState({ title: '', message: '', canRetry: true, retryAction: null });
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  // ========== TRIP PLAN HELPER FUNCTIONS ==========

  // Initialize a trip plan from a destination string (e.g., "Paris & Rome" or "Four Seasons Maldives")
  const initializeTripPlan = (destination, days, preferences = {}) => {
    // Use passed tripType if available (from LLM extraction), otherwise detect from destination format
    const hasMultipleStops = destination.includes('&') || destination.includes(',') || destination.includes(' and ');
    const isResort = /resort|hotel|four seasons|aman|ritz|peninsula|mandarin|waldorf|st\. regis|park hyatt/i.test(destination);
    const isAroundTheWorld = /around the world|world trip|multiple continents/i.test(destination);

    let type = preferences.tripType; // Use pre-detected tripType if available
    if (!type) {
      // Fallback: detect from destination name
      if (isAroundTheWorld) {
        type = 'around-the-world';
      } else if (hasMultipleStops && isResort) {
        type = 'hybrid';
      } else if (hasMultipleStops) {
        type = 'multi-city';
      } else if (isResort) {
        type = 'resort-hopping'; // Could be single resort or resort-hopping
      } else {
        type = 'single';
      }
    }

    // Universal multi-destination parser - supports virtually any format:
    // - "Paris, France & Rome, Italy" → ["Paris, France", "Rome, Italy"]
    // - "Patagonia (Chile + Argentina) & Peru" → ["Patagonia (Chile + Argentina)", "Peru"]
    // - "Paris, Rome, Barcelona" → ["Paris", "Rome", "Barcelona"]
    // - "Paris → Rome → Barcelona" → ["Paris", "Rome", "Barcelona"]
    // - "Paris - Rome - Barcelona" → ["Paris", "Rome", "Barcelona"]
    // - "Paris | Rome | Barcelona" → ["Paris", "Rome", "Barcelona"]
    // - "Paris; Rome; Barcelona" → ["Paris", "Rome", "Barcelona"]
    // - "Paris > Rome > Barcelona" → ["Paris", "Rome", "Barcelona"]
    // - "Paris then Rome then Barcelona" → ["Paris", "Rome", "Barcelona"]
    // - "Paris to Rome to Barcelona" → ["Paris", "Rome", "Barcelona"]
    // - "Paris followed by Rome" → ["Paris", "Rome"]
    // - "Start in Paris, end in Rome" → ["Paris", "Rome"]
    // - "From Paris to Rome" → ["Paris", "Rome"]

    let stopNames;

    // Dynamic country/region detection using comprehensive list + heuristics
    // This is more flexible than a rigid regex - it catches variations and edge cases
    const isCountryOrRegion = (str) => {
      if (!str || typeof str !== 'string') return false;
      const normalized = str.toLowerCase().trim();

      // Comprehensive list of countries, territories, and common geographic qualifiers
      const knownCountriesAndRegions = new Set([
        // Major countries
        'usa', 'us', 'uk', 'uae', 'united states', 'united kingdom', 'united arab emirates',
        'france', 'italy', 'spain', 'germany', 'japan', 'china', 'thailand', 'vietnam',
        'mexico', 'canada', 'australia', 'brazil', 'india', 'russia', 'south africa',
        // Europe
        'england', 'scotland', 'wales', 'northern ireland', 'ireland', 'netherlands', 'holland',
        'switzerland', 'austria', 'belgium', 'portugal', 'greece', 'turkey', 'turkiye',
        'iceland', 'norway', 'sweden', 'finland', 'denmark', 'poland', 'czech republic', 'czechia',
        'hungary', 'romania', 'bulgaria', 'croatia', 'serbia', 'slovenia', 'slovakia',
        'ukraine', 'latvia', 'lithuania', 'estonia', 'luxembourg', 'malta', 'cyprus', 'monaco',
        'liechtenstein', 'andorra', 'san marino', 'montenegro', 'albania', 'north macedonia',
        'bosnia', 'bosnia and herzegovina', 'kosovo',
        // Middle East
        'israel', 'jordan', 'lebanon', 'iran', 'iraq', 'saudi arabia', 'qatar', 'dubai',
        'oman', 'yemen', 'kuwait', 'bahrain', 'syria', 'palestine',
        // Africa
        'egypt', 'morocco', 'tunisia', 'algeria', 'libya', 'kenya', 'tanzania', 'ethiopia',
        'nigeria', 'ghana', 'senegal', 'ivory coast', 'south africa', 'namibia', 'botswana',
        'zimbabwe', 'zambia', 'mozambique', 'madagascar', 'mauritius', 'seychelles', 'reunion',
        'zanzibar', 'rwanda', 'uganda', 'malawi', 'angola', 'congo', 'cameroon',
        // Asia
        'singapore', 'malaysia', 'indonesia', 'bali', 'philippines', 'south korea', 'korea',
        'north korea', 'hong kong', 'taiwan', 'mongolia', 'pakistan', 'bangladesh', 'nepal',
        'sri lanka', 'myanmar', 'burma', 'cambodia', 'laos', 'brunei', 'timor-leste',
        // South America
        'peru', 'chile', 'argentina', 'colombia', 'ecuador', 'bolivia', 'uruguay', 'paraguay',
        'venezuela', 'guyana', 'suriname', 'french guiana',
        // Central America & Caribbean
        'panama', 'costa rica', 'nicaragua', 'honduras', 'guatemala', 'belize', 'el salvador',
        'cuba', 'jamaica', 'bahamas', 'bermuda', 'dominican republic', 'haiti', 'puerto rico',
        'st lucia', 'st. lucia', 'saint lucia', 'barbados', 'grenada', 'antigua', 'antigua and barbuda',
        'trinidad', 'tobago', 'trinidad and tobago', 'aruba', 'curacao', 'curaçao',
        'cayman islands', 'turks and caicos', 'virgin islands', 'guadeloupe', 'martinique',
        'st martin', 'st. martin', 'saint martin', 'anguilla', 'montserrat', 'dominica', 'bonaire',
        'roatan', 'cozumel', 'st kitts', 'st. kitts', 'saint kitts', 'nevis',
        // Oceania
        'fiji', 'maldives', 'new zealand', 'papua new guinea', 'samoa', 'tonga', 'vanuatu',
        'tahiti', 'french polynesia', 'bora bora', 'palau', 'guam', 'new caledonia',
        // US States (commonly used as geographic qualifiers)
        'hawaii', 'alaska', 'california', 'florida', 'texas', 'nevada', 'arizona', 'colorado',
        'utah', 'oregon', 'washington', 'new york', 'massachusetts', 'connecticut', 'vermont',
        'maine', 'new hampshire', 'rhode island', 'pennsylvania', 'new jersey', 'maryland',
        'virginia', 'north carolina', 'south carolina', 'georgia', 'tennessee', 'kentucky',
        'ohio', 'michigan', 'illinois', 'wisconsin', 'minnesota', 'iowa', 'missouri',
        'louisiana', 'mississippi', 'alabama', 'arkansas', 'oklahoma', 'kansas', 'nebraska',
        'south dakota', 'north dakota', 'montana', 'wyoming', 'idaho', 'new mexico',
        'indiana', 'west virginia', 'delaware',
        // ISO 2-letter codes
        'us', 'uk', 'gb', 'de', 'fr', 'es', 'it', 'pt', 'nl', 'be', 'at', 'ch', 'se', 'no',
        'dk', 'fi', 'pl', 'cz', 'hu', 'ro', 'hr', 'rs', 'ru', 'ua', 'gr', 'tr', 'eg', 'ma',
        'za', 'ke', 'tz', 'au', 'nz', 'jp', 'cn', 'kr', 'tw', 'hk', 'sg', 'my', 'th', 'vn',
        'ph', 'id', 'in', 'pk', 'bd', 'lk', 'np', 'mm', 'kh', 'la', 'fj', 'mv', 'mu', 'sc',
        'mg', 'hn', 'gt', 'bz', 'sv', 'ni', 'do', 'ht', 'pr', 'bb', 'gd', 'ag', 'tt', 'aw',
        'cw', 'ky', 'tc', 'vi', 'gp', 'mq', 'ai', 'ms', 'dm', 'bq', 'br', 'ar', 'cl', 'pe',
        'co', 'ec', 'bo', 'uy', 'py', 've', 'pa', 'cr', 'ni', 'cu', 'jm', 'bs', 'bm', 'mx', 'ca'
      ]);

      // Direct match
      if (knownCountriesAndRegions.has(normalized)) return true;

      // Check if it's a 2-3 letter code (likely country/state code)
      if (/^[a-z]{2,3}$/.test(normalized)) return true;

      // Heuristic: If it looks like "Bay Islands" or "Islas X" or "X Province" etc, it's geographic
      const geographicSuffixes = [
        'islands', 'island', 'islas', 'isla', 'province', 'region', 'territory',
        'prefecture', 'county', 'department', 'state', 'oblast', 'district'
      ];
      for (const suffix of geographicSuffixes) {
        if (normalized.endsWith(` ${suffix}`) || normalized.endsWith(`${suffix}s`)) {
          return true;
        }
      }

      return false;
    };

    // Smart multi-destination parser
    const parseMultiDestination = (input) => {
      if (!input || typeof input !== 'string') return [input || ''];

      // Step 1: Protect parenthesized and bracketed content
      const placeholders = [];
      let protectedInput = input.replace(/\([^)]+\)|\[[^\]]+\]/g, (match) => {
        placeholders.push(match);
        return `__PLACEHOLDER_${placeholders.length - 1}__`;
      });

      // BUG 7 FIX: Step 1b: Protect compound geographic names that use "&", "and", or "to"
      // These should NOT be split into separate destinations
      const compoundPatterns = [
        /\bnorth\s*(?:&|and)\s*south\b/gi,        // "North & South Islands"
        /\beast\s*(?:&|and)\s*west\b/gi,           // "East & West Coast"
        /\bupper\s*(?:&|and)\s*lower\b/gi,         // "Upper & Lower"
        /\binner\s*(?:&|and)\s*outer\b/gi,         // "Inner & Outer Hebrides"
        /\btrinidad\s*(?:&|and)\s*tobago\b/gi,     // "Trinidad & Tobago"
        /\bst\.?\s*kitts\s*(?:&|and)\s*nevis\b/gi, // "St. Kitts & Nevis"
        /\bantigua\s*(?:&|and)\s*barbuda\b/gi,     // "Antigua & Barbuda"
        /\bbosnia\s*(?:&|and)\s*herzegovina\b/gi,  // "Bosnia & Herzegovina"
        /\bsão\s*tomé\s*(?:&|and)\s*príncipe\b/gi, // "São Tomé & Príncipe"
        /\bturks\s*(?:&|and)\s*caicos\b/gi,        // "Turks & Caicos"
        /\bst\.?\s*vincent\s*(?:&|and)\s*(?:the\s+)?grenadines\b/gi, // "St. Vincent & the Grenadines"
        /\barts?\s*(?:&|and)\s*culture\b/gi,       // "Art & Culture"
        /\bfood\s*(?:&|and)\s*wine\b/gi,           // "Food & Wine"
        /\bsea\s*(?:&|and)\s*sand\b/gi,            // "Sea & Sand"
      ];
      protectedInput = compoundPatterns.reduce((text, pattern) => {
        return text.replace(pattern, (match) => {
          placeholders.push(match);
          return `__PLACEHOLDER_${placeholders.length - 1}__`;
        });
      }, protectedInput);

      // Step 2: Normalize various separator formats to a standard delimiter
      // Order matters - check longer patterns first
      let normalized = protectedInput
        // Arrow variations
        .replace(/\s*[→➔➜➝➞⟶]\s*/g, ' | ')
        .replace(/\s*->\s*/g, ' | ')
        .replace(/\s*=>\s*/g, ' | ')
        // Semantic separators (word boundaries)
        .replace(/\s+and\s+then\s+/gi, ' | ')
        .replace(/\s+followed\s+by\s+/gi, ' | ')
        .replace(/\s+after\s+that\s+/gi, ' | ')
        .replace(/\s+then\s+/gi, ' | ')
        .replace(/\bfrom\s+(.+?)\s+to\s+/gi, '$1 | ') // "from X to Y" → "X | Y"
        .replace(/\s+to\s+/gi, ' | ')
        .replace(/\s+and\s+/gi, ' | ')
        // Symbol separators
        .replace(/\s*&\s*/g, ' | ')
        .replace(/\s*;\s*/g, ' | ')
        .replace(/\s*>\s*/g, ' | ')
        .replace(/\s*\|\s*/g, ' | ')
        // Dash separator (but not in "City-Name" or "City - Country")
        .replace(/\s+-\s+/g, ' | ')
        // Slash separator (but careful with dates)
        .replace(/\s*\/\s*/g, ' | ')
        // Plus separator (but not in parentheses - already protected)
        .replace(/\s*\+\s*/g, ' | ');

      // Step 3: Split on the normalized delimiter
      let parts = normalized.split(/\s*\|\s*/)
        .map(s => s.trim())
        .filter(Boolean);

      // Step 4: Restore protected content
      parts = parts.map(part => {
        return part.replace(/__PLACEHOLDER_(\d+)__/g, (_, idx) => placeholders[parseInt(idx)]);
      });

      // Step 5: Handle comma-separated input (if still single part)
      if (parts.length === 1 && parts[0].includes(',')) {
        const commaParts = parts[0].split(',').map(s => s.trim()).filter(Boolean);

        // Detect "City, Country" pattern vs "City1, City2, City3" pattern
        // Rule: If exactly 2 parts and second is a known country, keep together
        if (commaParts.length === 2 && isCountryOrRegion(commaParts[1])) {
          return [parts[0]]; // "Paris, France" stays as one
        }

        // Rule: If 3+ comma parts, check if they look like landmarks/neighborhoods vs real cities
        // Landmarks/neighborhoods within a city should NOT be split into separate stops
        if (commaParts.length >= 3) {
          // Known landmarks, neighborhoods, and attractions that should never be stops
          const landmarkPatterns = [
            /colosseum/i, /vatican/i, /trastevere/i, /pantheon/i, /trevi/i, /spanish\s*steps/i,
            /eiffel/i, /louvre/i, /montmartre/i, /le\s*marais/i, /champs/i, /notre[\s-]*dame/i,
            /times\s*square/i, /central\s*park/i, /brooklyn\s*bridge/i, /wall\s*street/i,
            /big\s*ben/i, /tower\s*bridge/i, /buckingham/i, /westminster/i, /soho/i,
            /shibuya/i, /shinjuku/i, /harajuku/i, /asakusa/i, /akihabara/i, /ginza/i,
            /sagrada/i, /las\s*ramblas/i, /gothic\s*quarter/i, /park\s*güell/i,
            /acropolis/i, /parthenon/i, /plaka/i, /monastiraki/i,
            /beach/i, /market/i, /bazaar/i, /temple/i, /palace/i, /castle/i, /cathedral/i,
            /museum/i, /gallery/i, /park\b/i, /garden/i, /square/i, /bridge/i,
            /old\s*town/i, /new\s*town/i, /waterfront/i, /harbor/i, /harbour/i,
            /chinatown/i, /little\s*italy/i, /latin\s*quarter/i,
          ];
          const landmarkCount = commaParts.filter(p =>
            landmarkPatterns.some(pat => pat.test(p))
          ).length;
          // If majority look like landmarks/neighborhoods, treat entire string as one destination
          if (landmarkCount >= commaParts.length * 0.5) {
            return [parts[0]]; // Keep as single destination
          }
          return commaParts;
        }

        // Rule: If 2 parts and both look like cities (not countries), split them
        if (commaParts.length === 2 &&
            !isCountryOrRegion(commaParts[0]) &&
            !isCountryOrRegion(commaParts[1]) &&
            commaParts.every(p => p.length >= 3 && /^[A-Z]/.test(p))) {
          return commaParts;
        }
      }

      // Step 5b: Whitespace-only city lists ("Auckland Wellington Christchurch")
      // No explicit separators found. Try matching the input against known
      // cities with longest-first claiming so "Mexico City" wins over
      // "Mexico" + "City". Only split when 2+ known cities are found AND
      // nothing else is between them.
      if (parts.length === 1) {
        const lower = parts[0].toLowerCase();
        const sortedCities = [...KNOWN_TRAVEL_CITIES_WEB].sort((a, b) => b.length - a.length);
        const claimed = [];
        const matches = [];
        for (const city of sortedCities) {
          const escaped = city.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          const re = new RegExp(`\\b${escaped}\\b`, 'gi');
          let m;
          while ((m = re.exec(lower)) !== null) {
            const start = m.index;
            const end = start + city.length;
            if (claimed.some(([s, e]) => start < e && end > s)) continue;
            claimed.push([start, end]);
            matches.push({ name: city, start, end });
          }
        }
        if (matches.length >= 2) {
          matches.sort((a, b) => a.start - b.start);
          let allGapsClean = true;
          for (let i = 0; i < matches.length - 1; i++) {
            if (!/^\s*$/.test(lower.slice(matches[i].end, matches[i + 1].start))) {
              allGapsClean = false; break;
            }
          }
          const lead = lower.slice(0, matches[0].start);
          const tail = lower.slice(matches[matches.length - 1].end);
          if (allGapsClean && /^\s*$/.test(lead) && /^\s*$/.test(tail)) {
            return matches.map(m => parts[0].slice(m.start, m.end));
          }
        }
      }

      // Step 6: Handle "start in X, end in Y" patterns
      if (parts.length === 1) {
        const startEndMatch = parts[0].match(/start(?:ing)?\s+(?:in|at|from)\s+(.+?),?\s+end(?:ing)?\s+(?:in|at)\s+(.+)/i);
        if (startEndMatch) {
          return [startEndMatch[1].trim(), startEndMatch[2].trim()];
        }
      }

      // Step 7: Clean up any "starting from" or "ending in" prefixes
      parts = parts.map(part => {
        return part
          .replace(/^(?:start(?:ing)?|begin(?:ning)?)\s+(?:in|at|from)\s+/i, '')
          .replace(/^(?:end(?:ing)?|finish(?:ing)?)\s+(?:in|at)\s+/i, '')
          .replace(/^(?:visit(?:ing)?|explore|see(?:ing)?|go(?:ing)?\s+to)\s+/i, '')
          .trim();
      });

      return parts.filter(Boolean);
    };

    // Don't split hotel/resort names on "&" (e.g., "Grand Hotel & Spa")
    stopNames = isHotelOrResort(destination) ? [destination] : parseMultiDestination(destination);
    const daysPerStop = Math.max(2, Math.floor(days / stopNames.length));

    const stops = stopNames.map((name, index) => ({
      id: `stop-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
      type: isResort && name.match(/resort|hotel|four seasons|aman/i) ? 'resort' : 'city',
      name: name,
      days: index === stopNames.length - 1 ? days - (daysPerStop * index) : daysPerStop,
      order: index + 1,
      hotel: null
    }));

    return {
      type,
      stops,
      totalDays: days,
      transport: preferences.transportMode || 'fly',
      isEditable: type !== 'single' // Multi-stop trips are editable
    };
  };

  // Add a stop to the trip plan
  const addTripStop = (newStop) => {
    // Block forbidden destinations
    const stopNameCheck = newStop.name || '';
    const restriction = checkDestinationRestriction(stopNameCheck);
    if (!restriction.allowed) {
      debugLog(`🚫 BLOCKED in addTripStop: ${stopNameCheck}`);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ I can't add **${stopNameCheck}** to your trip. ${restriction.message}`
      }]);
      return;
    }
    setTripPlan(prev => {
      if (!prev) return null;
      const newId = `stop-${prev.stops.length + 1}`;
      const newOrder = prev.stops.length + 1;
      return {
        ...prev,
        stops: [...prev.stops, { ...newStop, id: newId, order: newOrder }],
        totalDays: prev.totalDays + (newStop.days || 2)
      };
    });
  };

  // Remove a stop from the trip plan
  const removeTripStop = (stopId) => {
    setTripPlan(prev => {
      if (!prev) return null;
      const stopToRemove = prev.stops.find(s => s.id === stopId);
      if (!stopToRemove) return prev;

      const newStops = prev.stops
        .filter(s => s.id !== stopId)
        .map((s, idx) => ({ ...s, order: idx + 1 }));

      return {
        ...prev,
        stops: newStops,
        totalDays: prev.totalDays - stopToRemove.days,
        type: newStops.length === 1 ? 'single' : prev.type
      };
    });
  };

  // Replace a stop in the trip plan
  const replaceTripStop = (stopId, newStopData) => {
    setTripPlan(prev => {
      if (!prev) return null;
      const daysDiff = (newStopData.days || 0) - (prev.stops.find(s => s.id === stopId)?.days || 0);
      return {
        ...prev,
        stops: prev.stops.map(s => s.id === stopId ? { ...s, ...newStopData } : s),
        totalDays: prev.totalDays + daysDiff
      };
    });
  };

  // Reorder stops in the trip plan
  const reorderTripStops = (fromIndex, toIndex) => {
    setTripPlan(prev => {
      if (!prev) return null;
      const newStops = [...prev.stops];
      const [moved] = newStops.splice(fromIndex, 1);
      newStops.splice(toIndex, 0, moved);
      return {
        ...prev,
        stops: newStops.map((s, idx) => ({ ...s, order: idx + 1 }))
      };
    });
  };

  // Get formatted destination string from trip plan
  const getTripPlanDestination = () => {
    if (!tripPlan || !tripPlan.stops || tripPlan.stops.length === 0) return '';
    return tripPlan.stops.map(s => s.name).join(' & ');
  };

  // ========== END TRIP PLAN HELPER FUNCTIONS ==========

  // ========== TRIP BUILDER SIDEBAR FUNCTIONS ==========

  // Add a stop to the trip builder
  const addTripBuilderStop = async (stop) => {
    // ── Early restriction check — block forbidden destinations before they enter the trip builder ──
    const stopNameForCheck = stop.name || '';
    const destRestriction = checkDestinationRestriction(stopNameForCheck);
    if (!destRestriction.allowed) {
      debugLog(`🚫 BLOCKED in addTripBuilderStop: ${stopNameForCheck} — ${destRestriction.message}`);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `⚠️ I can't add **${stopNameForCheck}** to your trip.\n\n${destRestriction.message}\n\nPlease choose a different destination.`
      }]);
      return; // Don't add the stop
    }

    // ── City name validation — non-blocking (optimistic add, validate in background) ──
    // Use ref for tripType to avoid stale closure from home page setTimeout
    const currentTripType = tripTypeRef.current ?? tripType;
    const stopType = stop.stopType || (currentTripType === 'hotel' ? 'hotel' : 'destination');
    const stopId = `stop-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    if (stopType === 'hotel' || stopType === 'resort') {
      // ═══════════════════════════════════════════
      // RESORT PATH: Always run hotel name extraction
      // ═══════════════════════════════════════════
      // Guard: prevent bare city/country names from being added as resorts
      const bareNameLower = (stop.name || '').toLowerCase().trim();
      const KNOWN_BARE_CITIES = ['paris', 'london', 'tokyo', 'rome', 'lisbon', 'barcelona', 'madrid', 'berlin', 'amsterdam', 'prague', 'vienna', 'budapest', 'athens', 'istanbul', 'dubai', 'singapore', 'bangkok', 'hanoi', 'seoul', 'beijing', 'shanghai', 'sydney', 'melbourne', 'toronto', 'vancouver', 'new york', 'los angeles', 'san francisco', 'chicago', 'miami', 'boston', 'seattle', 'denver', 'austin', 'nashville', 'portland', 'honolulu', 'maui', 'bali', 'cancun', 'tulum', 'mexico city', 'bogota', 'medellin', 'lima', 'buenos aires', 'santiago', 'rio de janeiro', 'sao paulo', 'cape town', 'nairobi', 'marrakech', 'cairo', 'delhi', 'mumbai', 'goa', 'colombo', 'reykjavik', 'dublin', 'edinburgh', 'florence', 'venice', 'milan', 'naples', 'nice', 'lyon', 'marseille', 'seville', 'porto', 'krakow', 'warsaw', 'helsinki', 'stockholm', 'oslo', 'copenhagen', 'zurich', 'geneva', 'salzburg', 'dubrovnik', 'split', 'santorini', 'mykonos', 'phuket', 'chiang mai', 'ho chi minh', 'siem reap', 'kyoto', 'osaka', 'manila', 'kuala lumpur', 'jakarta', 'queenstown', 'auckland'];
      const KNOWN_COUNTRIES = ['france', 'italy', 'spain', 'germany', 'portugal', 'japan', 'thailand', 'vietnam', 'south korea', 'china', 'indonesia', 'india', 'australia', 'new zealand', 'canada', 'mexico', 'brazil', 'argentina', 'colombia', 'peru', 'chile', 'egypt', 'morocco', 'kenya', 'tanzania', 'south africa', 'turkey', 'greece', 'croatia', 'czech republic', 'netherlands', 'hungary', 'poland', 'iceland', 'ireland', 'england', 'scotland', 'united kingdom', 'sweden', 'norway', 'denmark', 'finland', 'austria', 'switzerland', 'israel', 'jordan', 'singapore', 'malaysia', 'philippines', 'cambodia', 'costa rica', 'cuba', 'jamaica', 'dominican republic', 'russia', 'sri lanka'];
      if (KNOWN_BARE_CITIES.includes(bareNameLower) || KNOWN_COUNTRIES.includes(bareNameLower)) {
        // This looks like a city/country, not a resort — suggest switching to destination mode
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `**"${stop.name}"** looks like a city or country, not a hotel/resort name.\n\nIf you'd like to visit ${stop.name}, try adding it as a **destination** instead. If it is a property name, try including the full hotel name (e.g., "The Ritz ${stop.name}").`
        }]);
        return;
      }
      const syncLocation = extractLocationFromHotelName(stop.name);
      const isRecognized = syncLocation !== stop.name;

      const newStop = {
        id: stopId,
        name: titleCaseCity(stop.name),
        // Bug 2 rename: 'resort' → 'hotel'. The orchestrator accepts both
        // (isHotelStop helper) so in-flight requests don't break.
        stopType: 'hotel',
        // Bug 2 toggle (V2 will add UI). Default true preserves the
        // existing resort-style "stay on property" behavior.
        stayOnProperty: true,
        continent: stop.continent || '',
        // B1 fix: a new stop should default to 3 days — never inherit the trip's
        // total length. QA hit a case where adding stops ballooned each one to
        // 24 days (the full trip). Clamp anything > 14 to 3 as a sanity net.
        days: (stop.days && stop.days > 0 && stop.days <= 14) ? stop.days : 3,
        hotel: stop.hotel || null,
        resolvedLocation: isRecognized ? syncLocation : null,
        locationSource: isRecognized ? 'static' : null,
        needsLocation: false,
      };

      setTripBuilderStops(prev => [...prev, newStop]);
      setShowTripBuilder(true);

      // If sync extraction didn't find anything, try async
      if (!isRecognized) {
        debugLog(`🏨 Resort stop: "${stop.name}" — no sync match, trying async...`);
        getHotelLocationAsync(stop.name).then(result => {
          // Accept ANY result that has a valid location different from the hotel name itself.
          // The AI (Haiku) is accurate enough — even "low" confidence results are usually correct
          // for well-known hotels. The `location !== stop.name` check prevents circular references.
          const locationLower = (result?.location || '').toLowerCase().trim();
          const nameLower = stop.name.toLowerCase().trim();
          const isUsefulLocation = result?.location &&
            locationLower !== nameLower &&
            locationLower.length > 1 &&
            // Ensure it's not just returning the hotel name rephrased
            !nameLower.includes(locationLower);

          if (isUsefulLocation) {
            // Clean zip/postal codes from resolved location (e.g. "77400 Quintana Roo, Mexico" → "Quintana Roo, Mexico")
            const cleanLocation = result.location.replace(/\b\d{3,6}[-–]?\d{0,4}\s*/g, '').replace(/^[\s\-–,]+/, '').trim();
            setTripBuilderStops(prev => prev.map(s =>
              s.id === stopId ? { ...s, resolvedLocation: cleanLocation, locationSource: result.source, needsLocation: false } : s
            ));
            debugLog(`📍 Async resort location: ${stop.name} → ${result.location} (${result.source}, confidence: ${result.confidence})`);
          } else {
            // Still nothing — try validating as a destination (maybe it's a city, not a resort)
            debugLog(`⚠️ Resort location not found for "${stop.name}" — trying as destination fallback`);
            validateCityName(stop.name).then(cityVal => {
              if (cityVal?.valid) {
                const correctedName = cityVal.correctedName || stop.name;
                debugLog(`🔄 "${stop.name}" is actually a valid city — converting to destination`);
                setTripBuilderStops(prev => prev.map(s =>
                  s.id === stopId ? { ...s, stopType: 'destination', name: titleCaseCity(correctedName), needsLocation: false } : s
                ));
              } else {
                // Neither a resort nor a destination — mark as needing manual input
                setTripBuilderStops(prev => prev.map(s =>
                  s.id === stopId ? { ...s, needsLocation: true } : s
                ));
              }
            }).catch(() => {
              setTripBuilderStops(prev => prev.map(s =>
                s.id === stopId ? { ...s, needsLocation: true } : s
              ));
            });
          }
        }).catch(err => {
          setTripBuilderStops(prev => prev.map(s =>
            s.id === stopId ? { ...s, needsLocation: true } : s
          ));
          debugLog(`⚠️ Resort location lookup failed for ${stop.name}:`, err.message);
        });
      }

    } else {
      // ═══════════════════════════════════════════
      // DESTINATION PATH: No hotel extraction — only disambiguation/country resolution
      // ═══════════════════════════════════════════
      const newStop = {
        id: stopId,
        name: titleCaseCity(stop.name),
        stopType: 'destination',
        continent: stop.continent || '',
        // B1 fix: a new stop should default to 3 days — never inherit the trip's
        // total length. QA hit a case where adding stops ballooned each one to
        // 24 days (the full trip). Clamp anything > 14 to 3 as a sanity net.
        days: (stop.days && stop.days > 0 && stop.days <= 14) ? stop.days : 3,
        hotel: stop.hotel || null,
        resolvedLocation: null,
        locationSource: null,
      };

      setTripBuilderStops(prev => [...prev, newStop]);
      setShowTripBuilder(true);

      // ── Background city validation (non-blocking — stop already added above) ──
      if (stopType === 'destination' && stopNameForCheck) {
        validateCityName(stopNameForCheck).then(validation => {
          if (validation && !validation.valid) {
            // Before removing, check if it might be a hotel/resort name
            // (fallback for names that slipped past the HOTEL_DEST_SIGNALS check)
            const FALLBACK_HOTEL_SIGNALS = ['hotel', 'resort', 'inn', 'lodge', 'palace', 'villa',
              'manor', 'ranch', 'chateau', 'château', 'mansion', 'retreat', 'cabin', 'cottage', 'suites'];
            const FALLBACK_BRAND_PREFIXES = [
              'aman', 'soneva', 'one&only', 'six senses', 'four seasons', 'st. regis', 'st regis',
              'ritz carlton', 'ritz-carlton', 'mandarin oriental', 'rosewood', 'belmond', 'como',
              'taj ', 'oberoi', 'peninsula', 'raffles', 'banyan tree', 'anantara', 'waldorf astoria',
              'park hyatt', 'miraval', 'canyon ranch', 'singita', 'capella', 'alila', 'montage',
              'auberge', 'pendry', 'viceroy', 'faena', 'equinox hotel', 'nobu hotel',
            ];
            const nameLower = stopNameForCheck.toLowerCase();
            const looksLikeHotel = FALLBACK_HOTEL_SIGNALS.some(kw => nameLower.includes(kw)) ||
              FALLBACK_BRAND_PREFIXES.some(brand => nameLower.startsWith(brand) || nameLower.includes(brand));
            if (looksLikeHotel) {
              // Bug 2 rename: 'resort' → 'hotel'. Convert to hotel stop
              // instead of removing.
              debugLog(`🏨 City validation failed for "${stopNameForCheck}" but it looks like a hotel — converting to hotel`);
              const syncLoc = extractLocationFromHotelName(stopNameForCheck);
              const isRecognized = syncLoc !== stopNameForCheck;
              setTripBuilderStops(prev => prev.map(s =>
                s.id === stopId ? {
                  ...s,
                  stopType: 'hotel',
                  stayOnProperty: true,
                  resolvedLocation: isRecognized ? syncLoc : null,
                  locationSource: isRecognized ? 'pattern' : null,
                } : s
              ));
            } else {
              // Remove the invalid stop and show error
              setTripBuilderStops(prev => prev.filter(s => s.id !== stopId));
              showToast(`"${stopNameForCheck}" doesn't appear to be a valid destination`, 'error');
            }
          } else if (validation?.valid && validation.correctedName &&
                     validation.correctedName.toLowerCase() !== stopNameForCheck.toLowerCase()) {
            // Auto-correct the name (e.g., "pari" → "Paris")
            setTripBuilderStops(prev => prev.map(s =>
              s.id === stopId ? { ...s, name: titleCaseCity(validation.correctedName) } : s
            ));
          }
        }).catch(() => {
          debugLog(`⚠️ City validation unavailable for "${stopNameForCheck}", keeping stop`);
        });
      }

      // Run ONLY disambiguation/country resolution (NOT hotel extraction)
      if (stop.name) {
        // All three detectors come from shared (cityDisambiguation.js) so
        // web/mobile/backend stay in sync. The previous inline web copies
        // had drifted: missing 13 countries (Russia, Mozambique, Slovenia,
        // etc.) AND no aliases (USA/UK/Holland/Britain). Adding those to
        // the shared map automatically fixes web too.
        const isAmbiguousCity = isAmbiguousCityShared(stop.name);
        const isBareCountry = isBareCountryShared(stop.name);
        const isActivityBasedLocation = isActivityBasedLocationShared(stop.name);

        if (isBareCountry) {
          // Don't auto-resolve countries to a single city — the user may want
          // to visit multiple cities (e.g., "Costa Rica" → Liberia + San José + Arenal).
          // The AI prompt will handle multi-city planning within a country.
          debugLog(`🌍 Bare country detected: "${stop.name}" — keeping as-is for AI to plan across cities`);
        } else if (isAmbiguousCity) {
          const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
          const otherStops = tripBuilderStops.map(s => s.resolvedLocation || s.name).filter(n => n !== stop.name);
          fetch(`${BACKEND_URL}/api/chat`, {
            method: 'POST',
            headers: await getApiHeaders(),
            body: JSON.stringify({
              model: 'claude-haiku-4-5-20251001',
              max_tokens: 100,
              system: `You are a geography expert. A traveler is adding "${stop.name}" as a destination. ${otherStops.length > 0 ? `Their other stops are: ${otherStops.join(', ')}. ` : ''}Which specific city do they most likely mean? Return ONLY "City, Country" format (e.g., "Córdoba, Spain" or "Santiago, Chile"). Nothing else.`,
              messages: [{ role: 'user', content: `Which "${stop.name}" does this traveler mean?` }]
            })
          }).then(r => r.json()).then(data => {
            const resolved = data?.content?.[0]?.text?.trim();
            if (resolved && resolved.includes(',') && resolved !== stop.name) {
              setTripBuilderStops(prev => prev.map(s =>
                s.id === stopId ? { ...s, resolvedLocation: resolved, locationSource: 'disambiguation' } : s
              ));
              debugLog(`🔍 City disambiguation: "${stop.name}" → "${resolved}"`);
            }
          }).catch(err => {
            debugLog(`⚠️ City disambiguation failed for ${stop.name}:`, err.message);
          });
        } else if (isActivityBasedLocation) {
          // Activity-based location — resolve to a specific area (e.g., "Japan Skiing" → "Niseko, Japan")
          getHotelLocationAsync(stop.name).then(result => {
            if (result?.location && result.location !== stop.name && result.confidence !== 'low') {
              setTripBuilderStops(prev => prev.map(s =>
                s.id === stopId ? { ...s, resolvedLocation: result.location, locationSource: result.source } : s
              ));
              debugLog(`📍 Activity location resolved: ${stop.name} → ${result.location} (${result.source})`);
            }
          }).catch(err => {
            debugLog(`⚠️ Location lookup failed for ${stop.name}:`, err.message);
          });
          // Pure region names (Tuscany, Amalfi Coast, Patagonia, Lake Como, etc.)
          // are kept as-is — the AI plans across multiple towns within the region
        }
      }
    }
    return { id: stopId, name: stop.name };
  };

  // Remove a stop from the trip builder
  const removeTripBuilderStop = (stopId) => {
    setTripBuilderStops(prev => prev.filter(s => s.id !== stopId));
  };

  // Update days for a stop
  const updateTripBuilderStopDays = (stopId, days) => {
    setTripBuilderStops(prev => prev.map(s =>
      s.id === stopId ? { ...s, days: days === '' ? '' : Math.max(1, Math.min(14, days)) } : s
    ));
  };

  // Reorder stops via drag and drop
  const reorderTripBuilderStops = (fromIndex, toIndex) => {
    setTripBuilderStops(prev => {
      const newStops = [...prev];
      const [moved] = newStops.splice(fromIndex, 1);
      newStops.splice(toIndex, 0, moved);
      return newStops;
    });
  };

  // Get total days from trip builder
  const getTripBuilderTotalDays = () => {
    return tripBuilderStops.reduce((sum, stop) => sum + stop.days, 0);
  };

  // Get route string from trip builder
  // For destination stops, use resolvedLocation (e.g., "Paris, France" → "Paris")
  // For resort stops, use original name (e.g., "Four Seasons Bali")
  const getTripBuilderRoute = () => {
    return tripBuilderStops.map(s => {
      if ((s.stopType === 'hotel' || s.stopType === 'resort')) return s.name.split(',')[0];
      return (s.resolvedLocation || s.name).split(',')[0];
    }).join(' → ');
  };

  // Clear trip builder
  const clearTripBuilder = () => {
    setTripBuilderStops([]);
    setLegTransports({});
    setShowTripBuilder(false);
    // Also clear the manual "Add a Destination" / "Add a Resort" inline
    // input — without this, leftover text (e.g. "Maldives" from a failed
    // attempt) stays pre-filled across navigations and new trip sessions,
    // causing users to accidentally add wrong stops on their next trip.
    setNewStopInput('');
    setAddingStopType(null);
  };

  // Comprehensive trip state reset — used by all "Home" / "New Trip" buttons
  const resetAllTripState = () => {
    // Stage & navigation
    setStage('landing');
    // Chat & messages
    setMessages([]);
    setInput('');
    setItineraryChatMessages([]);
    // Itinerary
    setItinerary(null);
    setSelectedDestination(null);
    setCurrentDay(0);
    setTotalItineraryDays(0);
    // Destination & trip type
    setDestinationOptions([]);
    setShownDestinations([]);
    setTripType(null);
    setTripScope(null);
    setSelectedHotelProperty(null);
    // Pending changes & validation
    setPendingChanges(null);
    setPendingChangeStatuses({});
    setValidationIssues([]);
    setDestinationSafetyInfo(null);
    // Transit
    setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null;
    // Feedback
    setActivityFeedback({});
    setFeedbackReasons({});
    // Generation state
    setIsProgressiveGenerating(false);
    setCurrentlyGeneratingDay(null);
    setDayGenerationStatus({});
    setDayGenerationErrors({});
    generationInProgressRef.current = false;
    // Trip builder
    clearTripBuilder();
    // Airport state
    setDestinationAirports([]);
    setUserProfile(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        arrivalAirport: '',
        departureAirport: '',
        letAIChooseAirports: false
      }
    }));
    // Trip context
    setTripContext({});
    // Transport mode — reset so drive auto-detection works for next trip
    userHasSetTransportRef.current = false;
    setTransportationMode('fly');
    setReturnTransportMode(null);
    setCustomTransportDesc('');
    setQuickTransportResult(null);
  };

  // State for tracking route optimization
  const [isOptimizingTripRoute, setIsOptimizingTripRoute] = useState(false);

  // Optimize trip builder stops order by minimizing travel distance
  // Uses greedy nearest-neighbor algorithm starting from home city
  // Also considers the final destination (openJawDestination) if doing open-jaw routing
  const optimizeTripBuilderStops = async () => {
    if (tripBuilderStops.length < 2) {
      return; // Need at least 2 stops to optimize
    }

    setIsOptimizingTripRoute(true);

    try {
      const homeCity = userProfile?.preferences?.homeCity || tripContext?.originCity || '';
      const returnCity = userProfile?.preferences?.returnCity;
      const stops = [...tripBuilderStops];

      if (!homeCity) {
        debugLog('⚠️ No departure city set — skipping route optimization');
        return;
      }

      // Get the final destination for open-jaw (where we fly out from)
      // If returnCity is set and different from homeCity, it's an open-jaw trip
      const isOpenJaw = returnCity && returnCity !== homeCity;
      const finalDestination = isOpenJaw ? returnCity : homeCity;

      // Pre-fetch all flight times we'll need using LLM for unknown routes
      // This builds a distance matrix between all points (home, stops, and final destination)
      const allLocations = [
        homeCity,
        ...stops.map(s => extractLocationFromHotelName(s.name)),
        ...(isOpenJaw ? [extractLocationFromHotelName(returnCity)] : [])
      ];

      debugLog('🗺️ Optimizing route from:', homeCity);
      debugLog('🗺️ Final destination (fly out):', finalDestination);
      debugLog('🗺️ Stops to optimize:', stops.map(s => `${s.name} → ${extractLocationFromHotelName(s.name)}`));

      // Build distance matrix with async LLM lookups
      const distanceMatrix = {};
      const uniquePairs = [];

      // Collect all unique pairs we need to fetch
      for (let i = 0; i < allLocations.length; i++) {
        for (let j = 0; j < allLocations.length; j++) {
          if (i !== j) {
            const from = allLocations[i];
            const to = allLocations[j];
            const key = `${from}→${to}`;
            if (!uniquePairs.some(p => p.key === key)) {
              uniquePairs.push({ from, to, key });
            }
          }
        }
      }

      debugLog(`🗺️ Fetching ${uniquePairs.length} flight time estimates...`);

      // Fetch all flight times in parallel (uses cache + LLM for unknown routes)
      const flightTimePromises = uniquePairs.map(async ({ from, to, key }) => {
        // Use the async getFlightHours which falls back to LLM for unknown routes
        const time = await getFlightHours(to, from);
        return { key, time };
      });

      const flightTimeResults = await Promise.all(flightTimePromises);

      // Build the distance matrix
      for (const { key, time } of flightTimeResults) {
        distanceMatrix[key] = time;
      }

      // Helper to get flight time from the pre-computed matrix
      const getFlightTimeBetween = (from, to) => {
        const fromLocation = typeof from === 'string' && from.includes(',')
          ? extractLocationFromHotelName(from)
          : from;
        const toLocation = extractLocationFromHotelName(to);
        const key = `${fromLocation}→${toLocation}`;
        const time = distanceMatrix[key];
        if (time !== undefined) return time;
        // Fallback (shouldn't happen if matrix is complete)
        debugLog(`⚠️ Missing distance: ${key}, using default 5`);
        return 5;
      };

      // Greedy nearest-neighbor algorithm with open-jaw consideration
      // Start from home city, always pick the nearest unvisited stop
      // For open-jaw: also consider proximity to final destination when nearing the end
      const optimizedOrder = [];
      const remaining = [...stops];
      let currentLocation = homeCity;

      while (remaining.length > 0) {
        let nearestIndex = 0;
        let bestScore = Infinity;

        // Find the best next stop from current location
        // When we're down to the last few stops, factor in distance to final destination
        for (let i = 0; i < remaining.length; i++) {
          const flightTime = getFlightTimeBetween(currentLocation, remaining[i].name);

          // For open-jaw: when we have 2 or fewer stops left, consider distance to final destination
          let score = flightTime;
          if (isOpenJaw && remaining.length <= 2) {
            const toFinal = getFlightTimeBetween(remaining[i].name, finalDestination);
            // Weight the final leg more as we get closer to the end
            const weight = remaining.length === 1 ? 0.5 : 0.3;
            score = flightTime * (1 - weight) + toFinal * weight;
            debugLog(`  📍 ${currentLocation} → ${remaining[i].name}: ${flightTime}hrs (+ ${toFinal}hrs to ${finalDestination}, score: ${score.toFixed(1)})`);
          } else {
            debugLog(`  📍 ${currentLocation} → ${remaining[i].name}: ${flightTime}hrs`);
          }

          if (score < bestScore) {
            bestScore = score;
            nearestIndex = i;
          }
        }

        // Add the nearest stop to optimized order
        const nearest = remaining[nearestIndex];
        optimizedOrder.push(nearest);
        debugLog(`  ✓ Selected: ${nearest.name} (score: ${bestScore.toFixed(1)})`);
        currentLocation = nearest.name;
        remaining.splice(nearestIndex, 1);
      }

      // Check if order actually changed
      const originalOrder = stops.map(s => s.id).join(',');
      const newOrder = optimizedOrder.map(s => s.id).join(',');
      const orderChanged = originalOrder !== newOrder;

      // Update the stops with the optimized order
      setTripBuilderStops(optimizedOrder);

      if (orderChanged) {
        debugLog('✨ Trip route optimized:', optimizedOrder.map(s => s.name.split(',')[0]).join(' → '));
        // Show total travel time for the optimized route
        let totalTime = getFlightTimeBetween(homeCity, optimizedOrder[0].name);
        for (let i = 0; i < optimizedOrder.length - 1; i++) {
          totalTime += getFlightTimeBetween(optimizedOrder[i].name, optimizedOrder[i + 1].name);
        }
        totalTime += getFlightTimeBetween(optimizedOrder[optimizedOrder.length - 1].name, finalDestination);
        debugLog(`✈️ Total estimated flight time: ${totalTime.toFixed(1)} hours`);
      } else {
        debugLog('ℹ️ Route already optimal, no changes needed');
      }
    } catch (error) {
      console.error('Error optimizing trip route:', error);
    } finally {
      setIsOptimizingTripRoute(false);
    }
  };

  // ========== END TRIP BUILDER FUNCTIONS ==========

  // ========== PROGRESSIVE GENERATION HELPER FUNCTIONS ==========

  // buildCompressedContext — imported from @wander/shared via ./utils/promptBuilder
  const buildCompressedContext = _buildCompressedContext;

  // calculateTravelStructure — imported from @wander/shared via ./utils/promptBuilder
  // Wrap to pass userProfile context (shared version takes options param instead of closure)
  const calculateTravelStructure = (stops, totalDays) => _calculateTravelStructure(stops, totalDays, {
    homeCity: userProfile?.preferences?.homeCity,
    returnCity: userProfile?.preferences?.returnCity,
  });

  // isTravelDay, getStopForDay, deduplicateDay — imported from @wander/shared via ./utils/promptBuilder

  // Initialize day generation status for UI
  const initializeDayStatus = (totalDays) => {
    const status = {};
    for (let i = 1; i <= totalDays; i++) {
      status[i] = 'queued';
    }
    return status;
  };

  // Request regeneration (queues if generation in progress)
  const requestDayRegeneration = (dayNumber, feedback = null) => {
    if (isProgressiveGenerating) {
      // Queue the request
      setRegenerationQueue(prev => [...prev, { dayNumber, feedback }]);
      debugLog(`📋 Queued regeneration for Day ${dayNumber}`);
      return false; // Indicates it was queued, not executed immediately
    }
    return true; // Indicates caller should proceed with regeneration
  };

  // Process regeneration queue after generation completes
  const processRegenerationQueue = async () => {
    if (regenerationQueue.length === 0) return;

    const nextRequest = regenerationQueue[0];
    setRegenerationQueue(prev => prev.slice(1));

    debugLog(`📋 Processing queued regeneration for Day ${nextRequest.dayNumber}`);
    // The actual regeneration will be called by the component
    return nextRequest;
  };

  // ========== END PROGRESSIVE GENERATION HELPER FUNCTIONS ==========

  // ========== PROGRESSIVE GENERATION CORE FUNCTIONS ==========

  // buildFeedbackContext — imported from @wander/shared via ./utils/profileContext
  const buildFeedbackContext = _buildFeedbackContext;

  // Build community insights context for itinerary generation.
  // Injects real traveler data (top-rated places, tips) from community reviews.
  const buildCommunityContext = async (destinationName) => {
    if (!features.COMMUNITY_PLANNER_INSIGHTS) return '';
    try {
      const { getDestinationInsights } = await import('./lib/communityApi');
      const insights = await getDestinationInsights(destinationName);
      if (!insights || (!insights.top_rated && !insights.tip)) return '';

      const lines = ['\n🌍 COMMUNITY INSIGHTS (real traveler recommendations from arrivo community):'];
      if (insights.top_rated?.places?.length) {
        lines.push(`Top-rated places: ${insights.top_rated.places.slice(0, 5).map(p =>
          `${p.name} (${p.avg_rating}★${p.tags?.length ? `, ${p.tags.slice(0, 2).join('/')}` : ''})`
        ).join(', ')}`);
        lines.push('Consider including 1-2 of these community-recommended places in the itinerary.');
      }
      if (insights.tip?.tips?.length) {
        lines.push('Traveler tips:');
        insights.tip.tips.slice(0, 3).forEach(t => {
          lines.push(`- ${t.text}`);
        });
      }
      return lines.join('\n') + '\n';
    } catch (err) {
      // Community insights are optional — never block itinerary generation
      return '';
    }
  };

  // buildProfileContext — imported from @wander/shared via ./utils/profileContext
  // Wrap to pass React state (shared version takes explicit params instead of closure)
  const buildProfileContext = () => {
    const prefs = userProfile?.preferences;
    if (!prefs) return '';
    return _buildProfileContext({
      homeCity: prefs.homeCity,
      budget: prefs.budget,
      interests: prefs.interests,
      dietary: prefs.dietaryRestrictions,
      accessibility: prefs.accessibilityNeeds,
      pace: prefs.preferredPace,
      companions: prefs.travelCompanions,
      safetyConsiderations: prefs.safetyConsiderations,
      travelNotes: prefs.travelNotes,
      mustDoActivities: prefs.mustDoActivities,
      cabinClass: preferredTravelClass,
      accommodationType,
      customAccommodationName,
      likedActivities: prefs.likedActivities,
      dislikedActivities: prefs.dislikedActivities,
      likedCuisines: prefs.likedCuisines,
      dislikedCuisines: prefs.dislikedCuisines,
    });
  };

  // Generate a single day's itinerary with context
  const generateSingleDayItinerary = async (params) => {
    const {
      dayNumber,
      destination,
      priorContext,
      totalDays,
      travelInfo,
      travelStruct = null,
      stopInfo,
      userPrefs,
      transportInfo = null,
      preFetchedTransport = null,
      isFirstBatch = false,
      isLastDay = false,
      feedback = null
    } = params;

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

    // Determine hotel details for geographic constraints
    // Check per-stop hotel preferences first (from stopHotels), then global preferredHotel
    const stopHotelData = stopInfo?.id && userPrefs.stopHotels?.[stopInfo.id];
    const perStopHotel = stopHotelData?.bookedHotel || stopHotelData?.preferredHotel;
    // For resort stops, the stop name IS the hotel (e.g., "Waldhaus Flims Alpine Grand Hotel & Spa")
    const resortHotel = (stopInfo?.stopType === 'hotel' || stopInfo?.stopType === 'resort') ? stopInfo.name : null;
    const hotelName = perStopHotel || resortHotel || userPrefs.preferredHotel || userPrefs.hotelName || destination?.hotel?.name;
    const hotelAddress = stopInfo?.address || userPrefs.hotelAddress || destination?.hotel?.address;

    const prompt = buildPerDayUserMessage({
      dayNumber,
      totalDays,
      destination,
      stopInfo,
      travelInfo,
      travelStruct,
      priorContext,
      transportInfo,
      preFetchedTransport,
      effectiveReturnMode,
      transportationMode,
      tripType,
      hotelName,
      hotelAddress,
      isLastDay,
      feedback,
      userPrefs,
    });

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 150000); // 150 sec timeout per day (longer trips need more time)

    try {
      // Back-ported from mobile: prefer SSE (/api/chat-stream) to avoid gateway
      // timeouts on long Sonnet calls; transparent fallback to /api/chat.
      const data = await sendChatStreamWithFallback({
        BACKEND_URL,
        model: "claude-sonnet-4-20250514",
        max_tokens: 8000,
        messages: [{
          role: "user",
          content: prompt
        }],
        signal: controller.signal,
      });

      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }

      // Check for truncated response (LLM hit max_tokens before finishing)
      if (data.stop_reason === 'max_tokens') {
        debugLog(`⚠️ Day ${dayNumber} response was truncated (hit max_tokens). Attempting to parse partial JSON...`);
      }

      const text = data.content[0].text;
      const dayData = extractAndRepairJSON(text, {
        onRepair: (msg) => debugLog(`⚠️ ${msg}`),
      });
      if (!dayData) {
        throw new Error('No valid JSON found in response');
      }

      // Ensure day number is correct
      dayData.day = dayNumber;

      // NOTE: AM/PM time fixes, hotel consistency, address validation, and overlap resolution
      // are now handled by the unified normalizeDay() pipeline called after generation.
      // See src/utils/itineraryNormalizer.js

      // ========== DYNAMIC VALIDATION (Haiku-powered) ==========
      // Validate the generated day for geographic feasibility
      // This catches unrealistic day trips, wrong locations, etc.
      try {
        const baseCity = dayData.baseCity || stopInfo?.name || destination.name;
        const hotelLocation = dayData.hotel?.address || dayData.hotel?.name || null;

        debugLog(`🔍 Validating Day ${dayNumber} activities for ${baseCity}...`);

        const validation = await validateDayActivities(
          dayData,
          baseCity,
          hotelLocation,
          tripType,
          dayNumber,
          totalDays
        );

        if (!validation.valid && validation.issues?.length > 0) {
          debugLog(`⚠️ Day ${dayNumber} has ${validation.issues.length} issues:`, validation.issues.map(i => i.problem).join('; '));

          // Process each issue - try to fix activities with errors
          const errorIssues = validation.issues.filter(i => i.severity === 'error');

          for (const issue of errorIssues) {
            const { itemIndex, itemType, itemName, problem, suggestion } = issue;

            // Try to regenerate the problematic activity
            debugLog(`🔄 Attempting to replace "${itemName}" (${problem})`);

            const existingActivities = [
              ...(dayData.schedule || []).map(a => a.activity),
              ...(priorContext.usedActivities || [])
            ];

            const replacement = await regenerateActivity(
              itemName,
              problem,
              baseCity,
              hotelLocation,
              dayData.theme,
              existingActivities,
              itemType === 'activity' ? dayData.schedule?.[itemIndex]?.time : dayData.meals?.[itemIndex]?.time,
              itemType
            );

            if (replacement) {
              debugLog(`✅ Got replacement: "${replacement.activity || replacement.restaurant}"`);

              // Apply the replacement
              if (itemType === 'activity' && dayData.schedule?.[itemIndex]) {
                dayData.schedule[itemIndex] = {
                  ...dayData.schedule[itemIndex],
                  activity: replacement.activity,
                  location: replacement.location || dayData.schedule[itemIndex].location,
                  address: replacement.address || dayData.schedule[itemIndex].address,
                  description: replacement.description || dayData.schedule[itemIndex].description,
                  duration: replacement.duration || dayData.schedule[itemIndex].duration,
                  emoji: replacement.emoji || dayData.schedule[itemIndex].emoji,
                  _wasReplaced: true,
                  _originalIssue: problem
                };
              } else if (itemType === 'meal' && dayData.meals?.[itemIndex]) {
                dayData.meals[itemIndex] = {
                  ...dayData.meals[itemIndex],
                  restaurant: replacement.activity || replacement.restaurant || dayData.meals[itemIndex].restaurant,
                  address: replacement.address || dayData.meals[itemIndex].address,
                  _wasReplaced: true,
                  _originalIssue: problem
                };
              }
            } else {
              // If regeneration failed, mark as needing attention or insert free time
              debugLog(`⚠️ Could not regenerate "${itemName}" - marking for review`);

              if (itemType === 'activity' && dayData.schedule?.[itemIndex]) {
                // Option: Replace with free time suggestion
                dayData.schedule[itemIndex] = {
                  ...dayData.schedule[itemIndex],
                  activity: `Free time - explore ${baseCity}`,
                  description: `Originally planned: ${itemName || 'previous activity'}. ${suggestion || 'Consider an alternative local activity.'}`,
                  _hadIssue: true,
                  _originalActivity: itemName,
                  _issue: problem
                };
              }
            }
          }

          // Log warnings but don't try to fix them (they're less severe)
          const warningIssues = validation.issues.filter(i => i.severity === 'warning');
          if (warningIssues.length > 0) {
            debugLog(`📝 Day ${dayNumber} warnings (not auto-fixed):`, warningIssues.map(i => i.problem).join('; '));
          }
        } else {
          debugLog(`✅ Day ${dayNumber} validation passed`);
        }
      } catch (validationError) {
        // Don't fail the whole day generation if validation fails
        debugLog(`⚠️ Validation error for Day ${dayNumber} (continuing anyway):`, validationError.message);
      }
      // ========== END DYNAMIC VALIDATION ==========

      // NOTE: Schedule vs meals deduplication is now handled by normalizeDay() Stage 7.

      return dayData;

    } catch (error) {
      debugError(`❌ Error generating Day ${dayNumber}:`, error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Generate first batch of days (Days 1-2 or all if <= 2 days) - smaller batch for faster initial load
  const generateFirstBatch = async (params) => {
    const {
      destination,
      totalDays,
      travelStruct,
      userPrefs,
      transportInfo = {},
      preFetchedTransport = null
    } = params;

    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
    const batchSize = Math.min(2, totalDays); // Changed from 3 to 2 for faster initial load

    const prompt = buildFirstBatchUserMessage({
      destination,
      totalDays,
      batchSize,
      travelStruct,
      transportInfo,
      preFetchedTransport,
      effectiveReturnMode,
      transportationMode,
      userPrefs,
    });

    // Debug: verify departure rules are in the prompt
    if (userPrefs.departureTime) {
      debugLog(`🛫 Batch prompt departure rules: departureTime=${userPrefs.departureTime}, departureAirport=${userPrefs.departureAirport || 'not set'}, totalDays=${totalDays}`);
    } else {
      debugLog(`⚠️ Batch prompt: NO departureTime set in userPrefs — departure day rules will NOT be included`);
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 120000); // 120 sec (2 min) for 2-day batch

    try {
      // Back-ported from mobile: prefer SSE (/api/chat-stream) to avoid gateway
      // timeouts on long Sonnet calls; transparent fallback to /api/chat.
      const data = await sendChatStreamWithFallback({
        BACKEND_URL,
        model: "claude-sonnet-4-20250514",
        max_tokens: 12000,
        messages: [{
          role: "user",
          content: prompt
        }],
        signal: controller.signal,
      });

      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }

      // Check for truncated response
      if (data.stop_reason === 'max_tokens') {
        debugLog('⚠️ First batch response was truncated (hit max_tokens). Attempting to parse partial JSON...');
      }

      const text = data.content[0].text;
      const batchData = extractAndRepairJSON(text, {
        onRepair: (msg) => debugLog(`⚠️ ${msg}`),
      });
      if (!batchData) {
        throw new Error('No valid JSON found');
      }
      const allDays = batchData.days || [batchData];
      // GLM sometimes returns more days than requested — truncate to batchSize
      if (allDays.length > batchSize) {
        debugLog(`⚠️ First batch returned ${allDays.length} days but only ${batchSize} requested — truncating`);
      }
      return allDays.slice(0, batchSize);

    } catch (error) {
      debugError('❌ Error generating first batch:', error);
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  };

  // Main progressive generation orchestrator
  const generateItineraryProgressive = async (destination) => {
    // =========================================================
    // CRITICAL SAFETY CHECK - Backup check for direct calls
    // Block itinerary generation for forbidden/restricted destinations
    // =========================================================
    const destName = destination?.name || destination?.stopNames?.[0] || String(destination);
    const allStopNames = destination?.stopNames || [destName];

    for (const stopName of allStopNames) {
      const destSafetyCheck = checkDestinationRestriction(stopName);
      if (!destSafetyCheck.allowed) {
        debugLog(`🚫 BLOCKED (progressive): Cannot generate for forbidden destination: ${stopName}`);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `⚠️ I cannot create an itinerary for **${stopName}**. ${destSafetyCheck.message}`
        }]);
        setStage('chat');
        return;
      }
    }

    // Guard against duplicate calls (React StrictMode, rapid re-renders, etc.)
    if (generationInProgressRef.current) {
      debugLog('⚠️ Generation already in progress, skipping duplicate call');
      return;
    }
    generationInProgressRef.current = true;

    debugLog('🚀 Starting progressive itinerary generation for:', destination.name);

    // Get preferences
    let prefs = { ...userProfile.preferences };
    // Merge trip-level interests from destination highlights (e.g., "surfing, beach")
    // These come from what the user said in chat, stored in destination.highlights
    if (destination?.highlights?.length > 0) {
      const tripInterests = destination.highlights
        .flatMap(h => h.split(/,\s*/))
        .filter(Boolean);
      if (tripInterests.length > 0 && !(tripInterests.length === 1 && tripInterests[0] === 'Local experiences')) {
        prefs.interests = [...new Set([...(prefs.interests || []), ...tripInterests])];
        debugLog(`🎯 Merged trip interests into prefs: ${prefs.interests.join(', ')}`);
      }
    }
    // Also merge likedActivities and tripContext into interests as HIGH PRIORITY
    // This ensures activities extracted from the user's chat message (e.g., "surfing", "beach")
    // reach the LLM as trip interests, not just as lower-priority feedback context.
    // Without this, selecting a destination from recommendations loses the user's stated activities.
    const contextActivities = [
      ...(tripContext?.activities || []),
      ...(tripContext?.preferences || []),
      ...(prefs.likedActivities || [])
    ].filter(Boolean);
    if (contextActivities.length > 0) {
      const existingInterests = prefs.interests || [];
      const merged = [...new Set([...existingInterests, ...contextActivities])];
      if (merged.length > existingInterests.length) {
        prefs.interests = merged;
        debugLog(`🎯 Merged context activities/preferences into interests: ${prefs.interests.join(', ')}`);
      }
    }
    // Pass requested places from chat extraction to prefs for itinerary generation
    if (tripContext?.requestedPlaces?.length > 0) {
      prefs.requestedPlaces = tripContext.requestedPlaces;
      debugLog(`📍 Requested places from chat: ${prefs.requestedPlaces.join(', ')}`);
    }
    // Build trip context from chat — the user's original request carries rich semantic info
    // (e.g., "going for sister's wedding, want to surf after") that structured extraction loses.
    // Chat context > profile defaults (in case of conflict, chat wins).
    const tripContextLines = [];
    if (tripContext?.occasion) tripContextLines.push(`Trip occasion: ${tripContext.occasion}`);
    if (tripContext?.travelers) tripContextLines.push(`Traveling as: ${tripContext.travelers}`);
    if (tripContext?.month) tripContextLines.push(`Travel month: ${tripContext.month}`);
    // Get the user's original request from the first user message in chat
    const firstUserMessage = messages?.find(m => m.role === 'user' && m.content !== 'loading');
    if (firstUserMessage?.content) {
      tripContextLines.push(`Original request: "${firstUserMessage.content}"`);
    }
    // Store on prefs so it's available in both first-batch and single-day prompts
    prefs._tripContextBlock = tripContextLines.length > 0
      ? `\n📝 TRIP CONTEXT (from user's chat — use this to understand the trip purpose and tailor activities accordingly):\n${tripContextLines.map(l => `- ${l}`).join('\n')}\n`
      : '';
    if (prefs._tripContextBlock) {
      debugLog(`📝 Trip context block: ${tripContextLines.join(', ')}`);
    }

    // Fetch community insights for this destination (non-blocking, optional)
    try {
      const communityCtx = await buildCommunityContext(destination.name);
      if (communityCtx) {
        prefs._communityContext = communityCtx;
        debugLog('🌍 Community context injected into itinerary prompt');
      }
    } catch (_) { /* Community insights are optional */ }

    if (prefs.startDate && prefs.endDate) {
      const diffDays = calculateDaysBetween(prefs.startDate, prefs.endDate);
      if (diffDays && diffDays > 0) {
        prefs.duration = diffDays;
      }
    }

    // IMPORTANT: Enrich prefs with flight/travel times for proper scheduling
    // Uses centralized getTravelTimingPrefs() — single source of truth
    const travelTiming = getTravelTimingPrefs();
    prefs.arrivalTime = travelTiming.arrivalTime;
    prefs.departureTime = travelTiming.departureTime;
    prefs.departureAirport = prefs.departureAirport || travelTiming.departureAirport;
    debugLog(`🚀 Travel times: arrival=${prefs.arrivalTime} (${transportationMode}), departure=${prefs.departureTime} (${effectiveReturnMode}), departureAirport=${prefs.departureAirport}`);

    let totalDays = prefs.duration || 7;

    // BUG 10 FIX: If user specified days per stop that exceed totalDays, use the sum
    const stops = destination.stops || destination.stopNames || [destination.name];
    const userStopDaysSum = stops.reduce((sum, s) => sum + (typeof s === 'object' && s.days ? s.days : 0), 0);
    if (userStopDaysSum > totalDays) {
      debugLog(`📅 BUG 10 FIX: User stop days (${userStopDaysSum}) > totalDays (${totalDays}), using stop total`);
      totalDays = userStopDaysSum;
    }

    setTotalItineraryDays(totalDays);
    setIsProgressiveGenerating(true);
    pinnedHotelsRef.current = {}; // BUG 9 FIX: Reset pinned hotels for new generation
    setSelectedDestination(destination);

    // Reset modal states to prevent stale modals auto-opening on new itinerary
    setHotelAlternatives(null);
    setShowHotelDetails({});
    setExpandedFlightPrices({});

    // Calculate travel structure for multi-stop trips
    // IMPORTANT: Pass destination.stops (objects with .days) instead of stopNames (strings)
    // so calculateTravelStructure can use user-specified day allocations per stop
    const travelStruct = calculateTravelStructure(stops, totalDays);
    setTravelStructure(travelStruct);

    // Initialize day status
    const initialStatus = initializeDayStatus(totalDays);
    setDayGenerationStatus(initialStatus);
    setDayGenerationErrors({});

    // Fetch transport info for destination (car-free zones, nearest airports, etc.)
    // For multi-stop trips, fetch for each unique destination
    const uniqueStops = destination.stopNames
      ? [...new Set(destination.stopNames)]
      : [destination.name];

    const transportInfoMap = {};
    for (const stop of uniqueStops) {
      const info = await fetchDestinationTransportInfo(stop);
      if (info) {
        transportInfoMap[stop] = info;
        // Cache car-free/transport data for island detection during timeline rendering
        const stopLower = stop.toLowerCase().trim();
        if (info.carFree || info.restrictedVehicles) {
          dynamicGeographyCache[stopLower] = {
            ...(dynamicGeographyCache[stopLower] || {}),
            noCarAccess: info.carFree,
            transportNotes: info.carFreeNote || info.transportNotes,
          };
          debugLog(`🗺️ Cached transport geography for ${stop}:`, dynamicGeographyCache[stopLower]);
        }
      }
    }

    debugLog('🚂 Transport info map:', transportInfoMap);

    // Build airport info for fly mode
    // Priority: 1) User preferences, 2) Dynamic lookup from transport info, 3) AI will determine
    // Airport lookup is per-leg: arrival airport only if flying in, departure airport if flying out
    let airportInfo = {};
    let userArrivalCode = prefs.arrivalAirport;
    let userDepartureCode = prefs.departureAirport;
    const rawFirstStop = stops[0] || destination.name;
    const rawLastStop = stops[stops.length - 1] || destination.name;
    const firstStop = typeof rawFirstStop === 'object' ? (rawFirstStop.name || rawFirstStop.city || String(rawFirstStop)) : rawFirstStop;
    const lastStop = typeof rawLastStop === 'object' ? (rawLastStop.name || rawLastStop.city || String(rawLastStop)) : rawLastStop;
    const firstStopTransport = transportInfoMap[firstStop];
    const lastStopTransport = transportInfoMap[lastStop];

    // GUARDRAIL: Check if user entered their home airport as arrival/departure airport (common mistake)
    // The arrival airport should be near the DESTINATION's first stop, not the origin.
    // The departure airport should be near the DESTINATION's last stop, not the origin.
    // Parse home_airports properly — Supabase may return it as a JSON string
    const rawHomeAirports = profile?.home_airports;
    const homeAirportsList = Array.isArray(rawHomeAirports) ? rawHomeAirports
      : typeof rawHomeAirports === 'string' ? (() => { try { return JSON.parse(rawHomeAirports); } catch { return []; } })()
      : [];
    const homeAirportCode = homeAirportsList[0]?.code?.toUpperCase();
    // Also resolve home city to airport code as fallback (catches cases where profile.home_airports is empty)
    const homeCityAirportCode = lookupCityAirportCode(prefs.homeCity || profile?.home_city)?.toUpperCase();
    // Build a set of all known home airport codes
    const homeAirportCodes = new Set();
    if (homeAirportCode) homeAirportCodes.add(homeAirportCode);
    if (homeCityAirportCode) homeAirportCodes.add(homeCityAirportCode);

    if (userArrivalCode && homeAirportCodes.has(userArrivalCode.toUpperCase())) {
      debugLog(`⚠️ Airport guardrail: User entered home airport (${userArrivalCode}) as arrival airport. Using dynamic lookup instead.`);
      userArrivalCode = null; // Clear it so dynamic lookup kicks in
    }
    if (userDepartureCode && homeAirportCodes.has(userDepartureCode.toUpperCase())) {
      // Departure airport should be near the LAST STOP, not the home city
      debugLog(`⚠️ Airport guardrail: User entered home airport (${userDepartureCode}) as departure airport. Using dynamic lookup instead.`);
      userDepartureCode = null;
    }

    debugLog(`✈️ Airport lookup: firstStop="${firstStop}", lastStop="${lastStop}", arrivalMode=${transportationMode}, returnMode=${effectiveReturnMode}`);

    // Arrival airport: only needed if flying in
    if (transportationMode === 'fly') {
      if (userArrivalCode) {
        airportInfo.arrivalAirport = {
          code: userArrivalCode,
          name: `${userArrivalCode} Airport`,
          city: firstStop
        };
      } else if (firstStopTransport?.nearestAirport?.code) {
        const cleanedArrCode = cleanIataCode(firstStopTransport.nearestAirport.code);
        airportInfo.arrivalAirport = {
          code: cleanedArrCode,
          name: firstStopTransport.nearestAirport.name || `${cleanedArrCode} Airport`,
          city: firstStopTransport.nearestAirport.city || firstStop
        };
        debugLog(`✈️ Using dynamic arrival airport: ${airportInfo.arrivalAirport.code} (${airportInfo.arrivalAirport.name})`);
      }
    }

    // Departure airport: needed if flying out (even if driving in)
    if (effectiveReturnMode === 'fly') {
      if (userDepartureCode) {
        airportInfo.departureAirport = {
          code: userDepartureCode,
          name: `${userDepartureCode} Airport`,
          city: lastStop
        };
      } else if (lastStopTransport?.nearestAirport?.code) {
        const cleanedDepCode = cleanIataCode(lastStopTransport.nearestAirport.code);
        airportInfo.departureAirport = {
          code: cleanedDepCode,
          name: lastStopTransport.nearestAirport.name || `${cleanedDepCode} Airport`,
          city: lastStopTransport.nearestAirport.city || lastStop
        };
        debugLog(`✈️ Using dynamic departure airport: ${airportInfo.departureAirport.code} (${airportInfo.departureAirport.name})`);
      } else if (airportInfo.arrivalAirport) {
        // Fall back to arrival airport for round-trip (only if also flying in)
        airportInfo.departureAirport = { ...airportInfo.arrivalAirport, city: lastStop };
      }
    }

    // Pre-fetch transport modes for all inter-stop pairs + departure leg
    // Uses /api/quick-transport (Haiku, 90-day cache) to get { mode, duration, durationMinutes, reason }
    // This runs BEFORE generation so LLM prompts have real data — eliminates hallucination
    const preFetchedTransport = {};
    if (travelStruct.isMultiStop) {
      const transportPairs = [];

      // Inter-stop pairs from travelStruct
      for (const td of travelStruct.travelDays) {
        transportPairs.push({ from: td.from, to: td.to });
      }

      // Departure leg: last stop → departure airport city (for open-jaw or different-city airports)
      if (effectiveReturnMode === 'fly' && airportInfo.departureAirport?.city) {
        const lastStopName = travelStruct.stops?.[travelStruct.stops.length - 1]?.name;
        const depAirportCity = airportInfo.departureAirport.city;
        if (lastStopName && depAirportCity && lastStopName.toLowerCase() !== depAirportCity.toLowerCase()) {
          transportPairs.push({ from: lastStopName, to: depAirportCity, isDepartureLeg: true });
        }
      }

      if (transportPairs.length > 0) {
        debugLog(`🚆 Pre-fetching transport modes for ${transportPairs.length} city pairs...`);
        const BACKEND_URL_TRANSPORT = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
        await Promise.all(transportPairs.map(async (pair) => {
          try {
            const response = await fetch(`${BACKEND_URL_TRANSPORT}/api/quick-transport`, {
              method: 'POST',
              headers: await getApiHeaders(),
              body: JSON.stringify({ fromCity: pair.from, toCity: pair.to })
            });
            if (response.ok) {
              const data = await response.json();
              if (data.success) {
                const key = `${pair.from}|${pair.to}`.toLowerCase();
                preFetchedTransport[key] = data;
                // Tag departure leg for easy lookup in departure day prompt
                if (pair.isDepartureLeg) {
                  preFetchedTransport._departureLeg = data;
                }
                debugLog(`🚆 Pre-fetched: ${pair.from} → ${pair.to} = ${data.mode} (${data.duration})${pair.isDepartureLeg ? ' [departure leg]' : ''}`);
              }
            }
          } catch (e) {
            debugLog(`⚠️ Pre-fetch transport failed for ${pair.from} → ${pair.to} — will use fallback`);
          }
        }));
      }

      // Seed dynamicTransportModes state so departure timeline post-processing can use it
      if (Object.keys(preFetchedTransport).length > 0) {
        setDynamicTransportModes(prev => ({ ...prev, ...preFetchedTransport }));
      }
    }

    // Synthetic departure→destination transport for train/ferry/custom (non-fly, non-drive)
    if (transportationMode === 'train' || transportationMode === 'ferry') {
      preFetchedTransport._arrivalLeg = {
        mode: transportationMode,
        duration: quickTransportResult?.duration || '~3h',
        durationMinutes: quickTransportResult?.durationMinutes || 180,
        reason: 'user selected',
      };
    } else if (transportationMode === 'custom') {
      preFetchedTransport._arrivalLeg = {
        mode: 'custom',
        duration: 'unknown',
        reason: customTransportDesc || 'custom transport',
      };
    }

    // Initialize empty itinerary structure with transport info and airports
    const destDisplayName = destination.stops?.length > 1
      ? destination.stops.map(s => s.name).join(' & ')
      : destination.name;
    setItinerary({
      destination: destDisplayName,
      days: [],
      generatedAt: new Date().toISOString(),
      transportInfo: transportInfoMap,
      transportationMode,
      ...airportInfo
    });

    // CHANGED: Show loading screen during Days 1-2 generation
    // Then switch to itinerary view while remaining days load
    setShowLoadingScreen(true);
    setStage('loading'); // Stay on loading screen initially

    try {
      // PHASE 1: Generate first batch (Days 1-2 or all if <= 2 days)
      debugLog('📦 Phase 1: Generating first batch...');
      const batchSize = Math.min(2, totalDays); // Consistent with generateFirstBatch

      // Update status for batch
      setDayGenerationStatus(prev => {
        const updated = { ...prev };
        for (let i = 1; i <= batchSize; i++) {
          updated[i] = 'generating';
        }
        return updated;
      });
      setCurrentlyGeneratingDay(`1-${batchSize}`);

      const firstBatchDays = await generateFirstBatch({
        destination,
        totalDays,
        travelStruct,
        userPrefs: prefs,
        transportInfo: transportInfoMap,
        preFetchedTransport
      });

      // HOTEL PERSISTENCE for first batch: Ensure days at the same stop use the same hotel
      // Day 1's hotel is the "source of truth" for the entire stop
      // Normalize first batch through unified pipeline (handles hotel consistency, time fixes, etc.)
      const batchNormResult = normalizeItinerary(
        { days: firstBatchDays },
        {
          destination: destDisplayName,
          arrivalTime: prefs.arrivalTime,
          departureTime: totalDays <= firstBatchDays.length ? prefs.departureTime : null,
          preferredPace: prefs.preferredPace || prefs.tripPace || null,
          preferredStartTime: prefs.preferredStartTime || null,
          // Interest-aware pace trimming
          interests: prefs.interests || [],
          tasteProfile: prefs.tasteProfile || null,
        }
      );
      const validatedFirstBatch = batchNormResult.itinerary.days;
      if (batchNormResult.crossDayIssues.length > 0) {
        debugLog(`🔧 Batch normalization cross-day issues:`, batchNormResult.crossDayIssues);
      }

      // Update itinerary with validated first batch
      setItinerary(prev => ({
        ...prev,
        days: validatedFirstBatch
      }));

      // Update status
      setDayGenerationStatus(prev => {
        const updated = { ...prev };
        for (let i = 1; i <= batchSize; i++) {
          updated[i] = 'complete';
        }
        return updated;
      });

      // NOW switch to itinerary view - Days 1-2 are ready!
      setShowLoadingScreen(false);
      setStage('itinerary');
      debugLog('🎉 Switching to itinerary view - first batch ready');

      debugLog(`✅ First batch complete: ${firstBatchDays.length} days`);

      // If total days <= 2, we're done (first batch handles it all)
      if (totalDays <= 2) {
        debugLog('✅ Short trip - generation complete');
        setIsProgressiveGenerating(false);
        setCurrentlyGeneratingDay(null);

        // Usage now tracked server-side in /api/generate/start — no frontend increment needed

        // Post-generation quality validator (runs in all modes for multi-stop coverage)
        try {
          const { validateItinerary } = await import('./utils/postGenValidator');
          setItinerary(currentItinerary => {
            if (currentItinerary) {
              const result = validateItinerary(currentItinerary, { travelStruct, companions: prefs?.companions });
              if (result.errors.length) console.error('[Validator]', result.errors);
              if (result.warnings.length) console.warn('[Validator]', result.warnings);
              if (result.info.length) console.info('[Validator]', result.info);
              console.log(`[Validator] Quality: ${result.score}/100`);
            }
            return currentItinerary;
          });
        } catch (_e) { /* validator not available */ }
        return;
      }

      // PHASE 2: Generate remaining days one at a time (sequentially)
      // First batch = 2 days, so start at day 3
      debugLog('📦 Phase 2: Generating remaining days sequentially...');

      for (let dayNum = 3; dayNum <= totalDays; dayNum++) {
        setDayGenerationStatus(prev => ({ ...prev, [dayNum]: 'generating' }));
        setCurrentlyGeneratingDay(dayNum);

        const travelInfo = isTravelDay(dayNum, travelStruct);
        const stopInfo = getStopForDay(dayNum, travelStruct);

        try {
          // Get current days and transport info from state for context (ensures we have latest)
          const { currentDays, currentTransportInfo } = await new Promise(resolve => {
            setItinerary(prev => {
              resolve({
                currentDays: prev?.days || [],
                currentTransportInfo: prev?.transportInfo || {}
              });
              return prev; // Don't actually change state
            });
          });

          // Only use completed days for context (filter out nulls and error placeholders)
          const completedDays = currentDays.filter(d => d && !d._generationFailed);
          const priorContext = buildCompressedContext(completedDays);

          // Get transport info for this stop (car-free zones, etc.)
          const stopName = stopInfo?.name || destination.name;
          const stopTransportInfo = currentTransportInfo[stopName];

          debugLog(`🔄 Generating Day ${dayNum} with ${completedDays.length} prior days for context`);

          const newDay = await generateSingleDayItinerary({
            dayNumber: dayNum,
            destination,
            priorContext,
            totalDays,
            travelInfo,
            travelStruct,
            stopInfo,
            userPrefs: prefs,
            transportInfo: stopTransportInfo,
            preFetchedTransport,
            isLastDay: dayNum === totalDays
          });

          // Deduplicate against completed days only
          const dedupedDay = deduplicateDay(newDay, completedDays);

          // Ensure day number is correct
          dedupedDay.day = dayNum;

          // Normalize through unified pipeline (handles hotel consistency, time fixes, etc.)
          const prevDay = completedDays.find(d => d.day === dayNum - 1);
          const prevStop = getStopForDay(dayNum - 1, travelStruct);
          const isSameStop = stopInfo && prevStop && prevStop.name === stopInfo.name;

          // Enforce hotel consistency when consecutive days are at the same stop.
          // A travel day's hotel IS the arrival hotel, so Day 5 (Lyon) should match Day 4 (travel to Lyon).
          // Only skip enforcement if the CURRENT day is a travel day (it gets a new hotel for its arrival city).
          const currentDayIsTravel = !!travelInfo;
          const shouldEnforceHotel = isSameStop && !currentDayIsTravel;

          // For expectedBaseCity: use travelStruct stop as authoritative source.
          // Travel days: same-day travel → baseCity = arrival city (where they sleep).
          //              overnight travel → baseCity = departure city (where they spend the day).
          // For same-stop continuation, use the previous day's city as before.
          let travelStructExpectedCity;
          if (currentDayIsTravel) {
            let isOvernightLeg = false;
            try {
              const legEst = estimateFlightSchedule(travelInfo.from, travelInfo.to, {
                helpers: { getCityCountrySync, getContinentForCountry },
              });
              isOvernightLeg = !!(legEst?.isOvernight);
            } catch { /* ignore */ }
            travelStructExpectedCity = isOvernightLeg
              ? (travelInfo.from || stopInfo?.resolvedLocation || stopInfo?.name) // departure city for overnight
              : (travelInfo.to || stopInfo?.resolvedLocation || stopInfo?.name);  // arrival city for same-day
          } else {
            travelStructExpectedCity = stopInfo?.resolvedLocation || stopInfo?.name;
          }
          const expectedBaseCity = (shouldEnforceHotel && prevDay?.baseCity)
            ? prevDay.baseCity
            : (travelStructExpectedCity || null);

          const normResult = normalizeDay(dedupedDay, {
            dayNumber: dayNum,
            totalDays,
            destination: stopInfo?.name || destination.name,
            isFirstDay: dayNum === 1,
            isLastDay: dayNum === totalDays,
            arrivalTime: dayNum === 1 ? prefs.arrivalTime : null,
            departureTime: dayNum === totalDays ? prefs.departureTime : null,
            previousDayCity: prevDay?.baseCity || null,
            previousHotel: prevDay?.hotel?.name || null,
            previousHotelAddress: prevDay?.hotel?.address || null,
            expectedHotel: (shouldEnforceHotel && prevDay?.hotel) ? prevDay.hotel : null,
            expectedBaseCity,
            preferredPace: prefs.preferredPace || prefs.tripPace || null,
            preferredStartTime: prefs.preferredStartTime || null,
          });

          // Run scheduling validation + auto-fixes (time conflicts, meal times, duration sanity)
          let validatedDay = normResult.day;
          try {
            const validationResult = validateAndFixDay(normResult.day, {
              destination: stopInfo?.name || destination.name,
              arrivalTime: dayNum === 1 ? prefs.arrivalTime : null,
              departureTime: dayNum === totalDays ? prefs.departureTime : null,
              preferredPace: prefs.preferredPace || prefs.tripPace || 'moderate',
            });
            if (validationResult.wasFixed) {
              debugLog(`🔧 Day ${dayNum}: Applied ${validationResult.appliedFixes} scheduling fixes`);
              validatedDay = validationResult.day;
            }
          } catch (valErr) {
            debugLog(`⚠️ Day ${dayNum}: Scheduling validation skipped:`, valErr.message);
            validatedDay = normResult.day;
          }

          // Post-gen quality fix: catch generic meals ("Dinner In Ocean City"), format issues
          // This ensures the fallback client-side path has the same quality as the server-side orchestrator
          try {
            const { isGenericPlaceholder } = await import('./utils/postGenValidator');
            if (isGenericPlaceholder) {
              // Flag generic meal names for replacement
              if (validatedDay.meals) {
                validatedDay.meals = validatedDay.meals.map(meal => {
                  const name = meal.restaurant || meal.name || '';
                  if (isGenericPlaceholder(name)) {
                    debugLog(`🍽️ Day ${dayNum}: Generic meal detected: "${name}"`);
                    return { ...meal, _isGenericPlaceholder: true };
                  }
                  return meal;
                });
              }
              // Flag generic activity names
              if (validatedDay.schedule) {
                validatedDay.schedule = validatedDay.schedule.map(item => {
                  const name = item.activity || item.title || '';
                  if (isGenericPlaceholder(name)) {
                    debugLog(`🎯 Day ${dayNum}: Generic activity detected: "${name}"`);
                    return { ...item, _isGenericPlaceholder: true };
                  }
                  return item;
                });
              }
            }
          } catch (_e) { /* postGenValidator not available */ }

          // After flagging generic meals, try to resolve them via Google Places
          const genericMeals = (validatedDay.meals || []).filter(m => m._isGenericPlaceholder);
          if (genericMeals.length > 0 && validatedDay.baseCity) {
            try {
              const apiHeaders = await getApiHeaders();
              for (const meal of genericMeals) {
                const mealType = (meal.meal || meal.type || meal.mealType || '').toLowerCase();
                // Build a better search query - "best [breakfast/lunch/dinner] restaurant in [city]"
                const query = mealType ? `best ${mealType} restaurant in ${validatedDay.baseCity}` : `popular restaurant in ${validatedDay.baseCity}`;
                const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/validate-hotel`, {
                  method: 'POST',
                  headers: apiHeaders,
                  body: JSON.stringify({ hotelName: query, city: validatedDay.baseCity }),
                });
                if (res.ok) {
                  const data = await res.json();
                  // Backend returns { isValid, hotelName, address } not { validated, name }
                  const resolvedName = data.name || data.hotelName;
                  const isValid = data.validated || data.isValid || data.success;
                  if (isValid && resolvedName) {
                    meal.restaurant = resolvedName;
                    meal.name = resolvedName;
                    if (data.address) meal.address = data.address;
                    if (data.placeId) meal.placeId = data.placeId;
                    delete meal._isGenericPlaceholder;
                    debugLog(`🍽️ Day ${dayNum}: Resolved generic meal → "${resolvedName}"`);
                  }
                }
              }
            } catch (e) { /* Google Places lookup failed, keep generic name */ }
          }

          // BUG 9 FIX: Hotel pinning — first day of each stop sets canonical hotel
          if (stopInfo && validatedDay?.hotel?.name) {
            const stopKey = stopInfo.name || `stop-${stopInfo.index || dayNum}`;
            if (!pinnedHotelsRef.current[stopKey]) {
              // First day of this stop — pin its hotel as canonical
              pinnedHotelsRef.current[stopKey] = {
                name: validatedDay.hotel.name,
                address: validatedDay.hotel.address,
                pricePerNight: validatedDay.hotel.pricePerNight
              };
              debugLog(`🏨 Pinned hotel for ${stopKey}: ${validatedDay.hotel.name}`);
            } else if (!currentDayIsTravel) {
              // Subsequent day at same stop — enforce pinned hotel
              const pinned = pinnedHotelsRef.current[stopKey];
              if (validatedDay.hotel.name !== pinned.name) {
                debugLog(`🏨 Hotel consistency: Overriding ${validatedDay.hotel.name} → ${pinned.name} (pinned for ${stopKey})`);
                validatedDay = {
                  ...validatedDay,
                  hotel: {
                    ...validatedDay.hotel,
                    name: pinned.name,
                    address: pinned.address || validatedDay.hotel.address,
                    pricePerNight: pinned.pricePerNight || validatedDay.hotel.pricePerNight
                  }
                };
              }
            }
          }

          // RESTAURANT DEDUP: Check for duplicate restaurants across previous days
          if (validatedDay?.meals?.length > 0) {
            const usedRestaurants = new Set();
            // Collect restaurant names from all completed previous days
            const prevDays = await new Promise(resolve => {
              setItinerary(prev => { resolve(prev?.days || []); return prev; });
            });
            for (const pd of prevDays) {
              if (!pd?.meals) continue;
              for (const m of pd.meals) {
                const rName = (m.restaurant || '').toLowerCase().trim();
                if (rName) usedRestaurants.add(rName);
              }
            }
            // Flag duplicates (mark with _duplicate so AI context knows to suggest alternatives)
            let hasDupes = false;
            validatedDay = {
              ...validatedDay,
              meals: validatedDay.meals.map(meal => {
                const rName = (meal.restaurant || '').toLowerCase().trim();
                if (rName && usedRestaurants.has(rName)) {
                  hasDupes = true;
                  debugLog(`🍽️ Day ${dayNum}: Duplicate restaurant "${meal.restaurant}" — flagging for replacement`);
                  return { ...meal, restaurant: `${meal.restaurant} (try a different option)`, _duplicate: true };
                }
                return meal;
              })
            };
            if (hasDupes) {
              debugLog(`🍽️ Day ${dayNum}: Found duplicate restaurants from previous days`);
            }
          }

          // Update itinerary with validated day at correct index
          setItinerary(prev => {
            const updatedDays = [...(prev?.days || [])];
            // Ensure array is long enough
            while (updatedDays.length < dayNum) {
              updatedDays.push(null);
            }
            // Place validated day at correct index (dayNum - 1 for 0-based array)
            updatedDays[dayNum - 1] = validatedDay;
            return {
              ...prev,
              days: updatedDays
            };
          });

          setDayGenerationStatus(prev => ({ ...prev, [dayNum]: 'complete' }));
          debugLog(`✅ Day ${dayNum} complete and placed at index ${dayNum - 1}`);

        } catch (error) {
          // Auto-retry once on abort/timeout or network errors
          const isNetworkError = error.name === 'TypeError' &&
            (error.message.includes('Load failed') || error.message.includes('Failed to fetch') || error.message.includes('Network request failed'));
          if ((error.name === 'AbortError' || isNetworkError) && !error._retried) {
            debugLog(`⏱️ Day ${dayNum} ${isNetworkError ? 'network error' : 'timed out'} - auto-retrying once...`);
            setDayGenerationStatus(prev => ({ ...prev, [dayNum]: 'generating' }));
            try {
              const retryDays = (await new Promise(resolve => {
                setItinerary(prev => { resolve(prev?.days || []); return prev; });
              })).filter(d => d && !d._generationFailed);
              const retryContext = buildCompressedContext(retryDays);
              const retryStopTransport = (await new Promise(resolve => {
                setItinerary(prev => { resolve(prev?.transportInfo || {}); return prev; });
              }))[stopInfo?.name || destination.name];

              const retryDay = await generateSingleDayItinerary({
                dayNumber: dayNum,
                destination,
                priorContext: retryContext,
                totalDays,
                travelInfo,
                travelStruct,
                stopInfo,
                userPrefs: prefs,
                transportInfo: retryStopTransport,
                preFetchedTransport,
                isLastDay: dayNum === totalDays
              });

              const dedupedRetry = deduplicateDay(retryDay, retryDays);
              dedupedRetry.day = dayNum;

              // Normalize retry day through unified pipeline
              const retryPrevDay = retryDays.find(d => d.day === dayNum - 1);
              const retryPrevStop = getStopForDay(dayNum - 1, travelStruct);
              const retryIsSameStop = stopInfo && retryPrevStop && retryPrevStop.name === stopInfo.name;
              // Enforce hotel for same-stop days (travel day hotel = arrival hotel, so still enforce)
              // Only skip if CURRENT day is a travel day (gets new hotel for arrival city)
              const retryCurrentIsTravel = !!travelInfo;
              const retryShouldEnforceHotel = retryIsSameStop && !retryCurrentIsTravel;

              // For travel days, compute expectedBaseCity based on overnight status
              let retryExpectedBaseCity;
              if (retryCurrentIsTravel && travelInfo) {
                let retryIsOvernight = false;
                try {
                  const retryFlightEst = estimateFlightSchedule(travelInfo.from, travelInfo.to, {
                    helpers: { getCityCountrySync, getContinentForCountry },
                  });
                  retryIsOvernight = !!(retryFlightEst?.isOvernight);
                } catch { /* ignore */ }
                retryExpectedBaseCity = retryIsOvernight
                  ? (travelInfo.from || stopInfo?.resolvedLocation || stopInfo?.name)
                  : (travelInfo.to || stopInfo?.resolvedLocation || stopInfo?.name);
              } else {
                retryExpectedBaseCity = (retryShouldEnforceHotel && retryPrevDay?.baseCity)
                  ? retryPrevDay.baseCity
                  : null;
              }

              const retryNormResult = normalizeDay(dedupedRetry, {
                dayNumber: dayNum,
                totalDays,
                destination: stopInfo?.name || destination.name,
                isFirstDay: dayNum === 1,
                isLastDay: dayNum === totalDays,
                arrivalTime: dayNum === 1 ? prefs.arrivalTime : null,
                departureTime: dayNum === totalDays ? prefs.departureTime : null,
                previousDayCity: retryPrevDay?.baseCity || null,
                previousHotel: retryPrevDay?.hotel?.name || null,
                previousHotelAddress: retryPrevDay?.hotel?.address || null,
                expectedHotel: (retryShouldEnforceHotel && retryPrevDay?.hotel) ? retryPrevDay.hotel : null,
                expectedBaseCity: retryExpectedBaseCity,
                preferredPace: prefs.preferredPace || prefs.tripPace || null,
                preferredStartTime: prefs.preferredStartTime || null,
              });

              setItinerary(prev => {
                const updatedDays = [...(prev?.days || [])];
                while (updatedDays.length < dayNum) updatedDays.push(null);
                updatedDays[dayNum - 1] = retryNormResult.day;
                return { ...prev, days: updatedDays };
              });

              setDayGenerationStatus(prev => ({ ...prev, [dayNum]: 'complete' }));
              debugLog(`✅ Day ${dayNum} succeeded on retry`);
              continue; // Skip the error handling below
            } catch (retryError) {
              debugError(`❌ Day ${dayNum} retry also failed:`, retryError);
              // Fall through to normal error handling
            }
          } else {
            debugError(`❌ Day ${dayNum} failed:`, error);
          }

          setDayGenerationStatus(prev => ({ ...prev, [dayNum]: 'failed' }));
          // Provide friendly error messages for common network errors
          const friendlyError = (() => {
            const msg = error.message || '';
            if (error.name === 'TypeError' && (msg.includes('Load failed') || msg.includes('Failed to fetch') || msg.includes('Network request failed'))) {
              return 'Network connection lost. Please check your internet and retry.';
            }
            if (error.name === 'AbortError' || msg.includes('aborted') || msg.includes('timeout')) {
              return 'The request timed out. This can happen with complex itineraries — please retry.';
            }
            if (msg.includes('500') || msg.includes('Internal Server Error')) {
              return 'Our servers encountered an issue. Please retry in a moment.';
            }
            if (msg.includes('429') || msg.includes('Too Many Requests') || msg.includes('rate limit')) {
              return 'Too many requests — please wait a moment before retrying.';
            }
            return msg || 'An unexpected error occurred. Please retry.';
          })();
          setDayGenerationErrors(prev => ({ ...prev, [dayNum]: friendlyError }));

          // Store error placeholder at correct index
          setItinerary(prev => {
            const updatedDays = [...(prev?.days || [])];
            while (updatedDays.length < dayNum) {
              updatedDays.push(null);
            }
            updatedDays[dayNum - 1] = {
              day: dayNum,
              _generationFailed: true,
              _error: friendlyError,
              baseCity: stopInfo?.name || destination.name,
              schedule: [],
              meals: [],
              hotel: null
            };
            return {
              ...prev,
              days: updatedDays
            };
          });
        }
      }

      debugLog('✅ Progressive generation complete');

      // Validate and auto-correct transport modes (e.g., driving to islands)
      try {
        const currentItinerary = await new Promise(resolve => {
          setItinerary(prev => {
            resolve(prev);
            return prev;
          });
        });

        let needsUpdate = false;
        let updatedItinerary = { ...currentItinerary };

        // Validation 1: Check if arrival airport is reachable from Day 1 baseCity by ground transport
        // Valid examples: LIR airport → Four Seasons (2hr drive), DPS → Ubud (1.5hr drive)
        // Invalid examples: SIN airport → Bangkok (different country, no driving!)
        const arrivalCity = safeAirportCity(currentItinerary?.arrivalAirport).toLowerCase() || '';
        const firstDayCity = currentItinerary?.days?.[0]?.baseCity?.toLowerCase() || '';

        if (arrivalCity && firstDayCity && !arrivalCity.includes(firstDayCity) && !firstDayCity.includes(arrivalCity.split(',')[0])) {
          // Call Haiku to check if driving is possible - this handles edge cases dynamically
          debugLog(`🔍 Checking if arrival airport (${arrivalCity}) is reachable from Day 1 (${firstDayCity}) by ground transport...`);

          try {
            const validateController = new AbortController();
            const validateTimeout = setTimeout(() => validateController.abort(), 15000);
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/validate-transport-mode`, {
              method: 'POST',
              headers: await getApiHeaders(),
              body: JSON.stringify({
                fromCity: currentItinerary.arrivalAirport?.city || arrivalCity,
                toCity: firstDayCity,
                proposedMode: 'car'
              }),
              signal: validateController.signal
            });
            clearTimeout(validateTimeout);

            if (response.ok) {
              const validation = await response.json();
              if (!validation.valid) {
                // Haiku confirmed driving is not possible (different countries, water crossing, etc.)
                debugLog(`🚨 Cannot drive from ${arrivalCity} to ${firstDayCity} - ${validation.reason || 'invalid route'}`);
                // The arrival airport should be corrected to match Day 1's location
                // Look up the correct airport for Day 1's city
                const transportInfoController = new AbortController();
                const transportInfoTimeout = setTimeout(() => transportInfoController.abort(), 15000);
                const transportResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/destination-transport-info`, {
                  method: 'POST',
                  headers: await getApiHeaders(),
                  body: JSON.stringify({ destination: firstDayCity }),
                  signal: transportInfoController.signal,
                });
                clearTimeout(transportInfoTimeout);
                if (transportResponse.ok) {
                  const transportInfo = await transportResponse.json();
                  if (transportInfo.nearestAirport?.code) {
                    debugLog(`✅ Correcting arrival airport from ${currentItinerary.arrivalAirport?.code} to ${transportInfo.nearestAirport.code}`);
                    updatedItinerary.arrivalAirport = {
                      code: transportInfo.nearestAirport.code,
                      name: transportInfo.nearestAirport.name || `${transportInfo.nearestAirport.code} Airport`,
                      city: transportInfo.nearestAirport.city || firstDayCity
                    };
                    needsUpdate = true;
                  }
                }
              }
            }
          } catch (airportError) {
            debugLog('⚠️ Arrival airport validation failed:', airportError.message);
          }
        }

        // Validation 2: Check inter-city transport modes
        if (currentItinerary?.interCityTransport?.length > 0) {
          const { itinerary: validatedItinerary, corrections } = await validateAndCorrectTransportModes(currentItinerary);
          if (corrections.length > 0) {
            debugLog(`🔧 Transport mode corrections applied:`, corrections);
            updatedItinerary = { ...updatedItinerary, interCityTransport: validatedItinerary.interCityTransport };
            needsUpdate = true;
          }
        }

        // Validation 3: Cross-check flight durations dynamically
        if (updatedItinerary?.interCityTransport?.length > 0) {
          try {
            const flightSegments = updatedItinerary.interCityTransport.filter(t => t.mode === 'flight');
            if (flightSegments.length > 0) {
              const controller = new AbortController();
              const timeoutId = setTimeout(() => controller.abort(), 15000);
              const durationChecks = await Promise.all(
                flightSegments.map(async (seg) => {
                  const fromCity = seg.from?.city || seg.from;
                  const toCity = seg.to?.city || seg.to;
                  if (!fromCity || !toCity) return null;
                  try {
                    const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/validate-flight-duration`, {
                      method: 'POST',
                      headers: await getApiHeaders(),
                      body: JSON.stringify({ fromCity, toCity, proposedDuration: seg.duration }),
                      signal: controller.signal
                    });
                    if (!resp.ok) return null;
                    const data = await resp.json();
                    return { day: seg.day, fromCity, toCity, validated: data.duration, original: seg.duration };
                  } catch { return null; }
                })
              );
              clearTimeout(timeoutId);

              const parseHours = (str) => {
                if (!str) return 0;
                const h = str.match(/(\d+(?:\.\d+)?)\s*h/i);
                const m = str.match(/(\d+)\s*m/i);
                return (h ? parseFloat(h[1]) : 0) + (m ? parseInt(m[1]) / 60 : 0);
              };

              let transportCorrected = false;
              const updatedTransport = [...updatedItinerary.interCityTransport];
              for (const check of durationChecks) {
                if (!check?.validated) continue;
                // Always cache the validated duration for timeline builder to use
                const cacheKey = `${check.fromCity}-${check.toCity}`.toLowerCase().trim();
                validatedFlightDurations.current[cacheKey] = check.validated;
                debugLog(`✈️ Cached validated duration: ${cacheKey} = ${check.validated}`);

                const validatedHrs = parseHours(check.validated);
                const originalHrs = parseHours(check.original);
                // Correct if LLM is >50% off from validated duration (over OR under estimate)
                if (validatedHrs > 0 && originalHrs > 0 && (originalHrs > validatedHrs * 1.5 || originalHrs < validatedHrs * 0.5)) {
                  const idx = updatedTransport.findIndex(t => t.day === check.day);
                  if (idx >= 0) {
                    debugLog(`✈️ Duration correction (dynamic): ${check.fromCity}→${check.toCity}: ${check.original} → ${check.validated}`);
                    updatedTransport[idx] = { ...updatedTransport[idx], duration: check.validated };
                    transportCorrected = true;
                  }
                }
              }
              if (transportCorrected) {
                updatedItinerary = { ...updatedItinerary, interCityTransport: updatedTransport };
                needsUpdate = true;
              }
            }
          } catch (durationError) {
            debugLog('⚠️ Flight duration validation failed (non-critical):', durationError.message);
          }
        }

        // Validation 4: Sanity-check train durations (common European/Japan routes)
        if (updatedItinerary?.interCityTransport?.length > 0) {
          const KNOWN_TRAIN_DURATIONS = {
            // Key: "fromCity-toCity" (lowercase, sorted alphabetically), Value: typical hours
            // France
            'lyon-paris': 2, 'marseille-paris': 3.25, 'bordeaux-paris': 2.25, 'lyon-marseille': 1.75,
            // France-Italy
            'florence-lyon': 5.5, 'lyon-milan': 4.5, 'lyon-tuscany': 5.5, 'lyon-rome': 7,
            'florence-paris': 6.5, 'milan-paris': 6.5,
            // Italy
            'florence-rome': 1.5, 'florence-venice': 2, 'milan-rome': 3, 'florence-milan': 1.75,
            'milan-venice': 2.5, 'naples-rome': 1.2, 'florence-naples': 3,
            'bologna-florence': 0.6, 'florence-pisa': 1, 'florence-siena': 1.5,
            // Spain
            'barcelona-madrid': 2.5, 'madrid-seville': 2.5, 'barcelona-seville': 5.5,
            // Germany/Austria/Switzerland
            'berlin-munich': 4, 'frankfurt-munich': 3.25, 'munich-vienna': 4, 'munich-zurich': 3.5,
            'berlin-hamburg': 1.75, 'cologne-frankfurt': 1,
            // UK
            'edinburgh-london': 4.5, 'london-manchester': 2.25, 'london-paris': 2.5,
            // Japan
            'kyoto-tokyo': 2.25, 'osaka-tokyo': 2.5, 'hiroshima-tokyo': 4, 'kyoto-osaka': 0.25,
            // Benelux/Nordic
            'amsterdam-brussels': 2, 'amsterdam-paris': 3.25, 'brussels-paris': 1.5,
            'copenhagen-stockholm': 5, 'oslo-stockholm': 5.5,
          };

          const parseHoursTrain = (str) => {
            if (!str) return 0;
            const h = str.match(/(\d+(?:\.\d+)?)\s*h/i);
            const m = str.match(/(\d+)\s*m/i);
            return (h ? parseFloat(h[1]) : 0) + (m ? parseInt(m[1]) / 60 : 0);
          };

          let trainCorrected = false;
          const updatedTransportTrains = [...updatedItinerary.interCityTransport];
          for (let i = 0; i < updatedTransportTrains.length; i++) {
            const seg = updatedTransportTrains[i];
            if (seg.mode !== 'train') continue;

            const fromCity = (seg.from?.city || seg.from || '').toLowerCase().trim();
            const toCity = (seg.to?.city || seg.to || '').toLowerCase().trim();
            if (!fromCity || !toCity) continue;

            // Try both orderings of the city pair
            const key1 = [fromCity, toCity].sort().join('-');
            // Also try with region→city mapping (e.g., "tuscany" → "florence")
            const regionToCity = { tuscany: 'florence', provence: 'marseille', burgundy: 'dijon', 'cinque terre': 'pisa', amalfi: 'naples', 'lake como': 'milan' };
            const mappedFrom = regionToCity[fromCity] || fromCity;
            const mappedTo = regionToCity[toCity] || toCity;
            const key2 = [mappedFrom, mappedTo].sort().join('-');

            const knownHrs = KNOWN_TRAIN_DURATIONS[key1] || KNOWN_TRAIN_DURATIONS[key2];
            if (knownHrs) {
              const proposedHrs = parseHoursTrain(seg.duration);
              if (proposedHrs > knownHrs * 1.5 || (proposedHrs > 0 && proposedHrs < knownHrs * 0.5)) {
                const correctedH = Math.floor(knownHrs);
                const correctedM = Math.round((knownHrs - correctedH) * 60);
                const corrected = correctedM > 0 ? `${correctedH}h ${correctedM}m` : `${correctedH}h`;
                debugLog(`🚄 Train duration correction: ${fromCity}→${toCity}: ${seg.duration} → ${corrected} (known: ${knownHrs}h)`);
                updatedTransportTrains[i] = { ...seg, duration: corrected, _durationCorrected: true };
                trainCorrected = true;
              }
            }
          }
          if (trainCorrected) {
            updatedItinerary = { ...updatedItinerary, interCityTransport: updatedTransportTrains };
            needsUpdate = true;
          }
        }

        if (needsUpdate) {
          setItinerary(updatedItinerary);
        }
      } catch (validationError) {
        debugLog('⚠️ Transport validation failed (non-critical):', validationError.message);
      }

      // Usage now tracked server-side in /api/generate/start — no frontend increment needed

      // Post-generation quality validator — runs in all modes, not just dev
      // Critical for catching missing destinations in multi-stop trips
      try {
        const { validateItinerary } = await import('./utils/postGenValidator');
        setItinerary(currentItinerary => {
          if (currentItinerary) {
            const result = validateItinerary(currentItinerary, { travelStruct });
            if (result.errors.length) console.error('[Validator]', result.errors);
            if (result.warnings.length) console.warn('[Validator]', result.warnings);
            if (result.info.length) console.info('[Validator]', result.info);
            console.log(`[Validator] Quality: ${result.score}/100`);

            // Auto-fix baseCity for days where AI used wrong destination
            // If travelStruct says Day 4 should be in Sydney but AI generated Tokyo content,
            // at minimum correct the baseCity so the display layer renders the right location
            if (travelStruct?.isMultiStop && result.missingStops?.length > 0) {
              console.warn(`[Validator] ⚠️ Missing stops detected: ${result.missingStops.map(s => s.name).join(', ')}`);
              const updatedDays = [...currentItinerary.days];
              let fixed = false;
              for (let i = 0; i < updatedDays.length; i++) {
                const day = updatedDays[i];
                if (!day) continue;
                const dayNum = day.day || i + 1;
                const expectedStop = getStopForDay(dayNum, travelStruct);
                if (!expectedStop) continue;
                const expectedCity = (expectedStop.resolvedLocation || expectedStop.name || '').toLowerCase().trim();
                const actualCity = (day.baseCity || '').toLowerCase().trim();
                if (expectedCity && actualCity && !actualCity.includes(expectedCity) && !expectedCity.includes(actualCity)) {
                  console.warn(`[Validator] 🔧 Day ${dayNum}: baseCity "${day.baseCity}" → "${expectedStop.resolvedLocation || expectedStop.name}" (travelStruct expected)`);
                  updatedDays[i] = { ...day, baseCity: expectedStop.resolvedLocation || expectedStop.name };
                  fixed = true;
                }
              }
              if (fixed) {
                return { ...currentItinerary, days: updatedDays };
              }
            }
          }
          return currentItinerary;
        });
      } catch (_e) { /* validator not available */ }

    } catch (error) {
      debugError('❌ Progressive generation failed:', error);
      // Show error state
      setDayGenerationStatus(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(k => {
          if (updated[k] === 'queued' || updated[k] === 'generating') {
            updated[k] = 'failed';
          }
        });
        return updated;
      });
    } finally {
      setIsProgressiveGenerating(false);
      setCurrentlyGeneratingDay(null);
      generationInProgressRef.current = false; // Reset guard

      // BUG 5 FIX: After progressive generation completes, ensure totalItineraryDays matches actual
      // This forces re-render of the last day which triggers departure card creation
      setItinerary(prev => {
        if (prev?.days?.length > 0 && prev.days.length !== totalItineraryDays) {
          debugLog(`📅 BUG 5 FIX: Updating totalItineraryDays from ${totalItineraryDays} to ${prev.days.length}`);
          setTotalItineraryDays(prev.days.length);
        }
        return prev;
      });

      // Process any queued regeneration requests
      const nextRequest = await processRegenerationQueue();
      if (nextRequest) {
        // Trigger regeneration (will be handled by separate function)
        debugLog(`📋 Would regenerate Day ${nextRequest.dayNumber}`);
      }
    }
  };

  // Regenerate a single day
  const regenerateSingleDay = async (dayNumber, feedback = null) => {
    if (!requestDayRegeneration(dayNumber, feedback)) {
      // Request was queued
      return;
    }

    debugLog(`🔄 Regenerating Day ${dayNumber}${feedback ? ` with feedback: ${feedback}` : ''}`);

    setDayGenerationStatus(prev => ({ ...prev, [dayNumber]: 'generating' }));
    setCurrentlyGeneratingDay(dayNumber);
    setIsProgressiveGenerating(true);

    try {
      const priorDays = itinerary?.days?.slice(0, dayNumber - 1) || [];
      const priorContext = buildCompressedContext(priorDays);
      const travelInfo = isTravelDay(dayNumber, travelStructure);
      const stopInfo = getStopForDay(dayNumber, travelStructure);

      // Enrich preferences with flight/travel times for proper scheduling
      // Uses centralized getTravelTimingPrefs() — single source of truth
      let enrichedPrefs = { ...userProfile.preferences };
      const perDayTravelTiming = getTravelTimingPrefs();
      enrichedPrefs.arrivalTime = perDayTravelTiming.arrivalTime;
      enrichedPrefs.departureTime = perDayTravelTiming.departureTime;
      enrichedPrefs.departureAirport = enrichedPrefs.departureAirport || perDayTravelTiming.departureAirport;

      // Get transport info for this stop (car-free zones, etc.)
      const stopName = stopInfo?.name || selectedDestination?.name;
      const stopTransportInfo = itinerary?.transportInfo?.[stopName];

      const newDay = await generateSingleDayItinerary({
        dayNumber,
        destination: selectedDestination,
        priorContext,
        totalDays: totalItineraryDays,
        travelInfo,
        travelStruct: travelStructure,
        stopInfo,
        userPrefs: enrichedPrefs,
        transportInfo: stopTransportInfo,
        preFetchedTransport: dynamicTransportModes, // Use state from initial pre-fetch
        isLastDay: dayNumber === totalItineraryDays,
        feedback
      });

      // Deduplicate against other days (not the one being replaced)
      const otherDays = itinerary?.days?.filter((_, i) => i !== dayNumber - 1) || [];
      const dedupedDay = deduplicateDay(newDay, otherDays);

      // Normalize through unified pipeline
      const regenPrevDay = dayNumber > 1 ? itinerary?.days?.[dayNumber - 2] : null;
      const regenStopInfo = getStopForDay(dayNumber, travelStructure);
      const regenPrevStop = dayNumber > 1 ? getStopForDay(dayNumber - 1, travelStructure) : null;
      const regenIsSameStop = regenStopInfo && regenPrevStop && regenPrevStop.name === regenStopInfo.name;
      // Enforce hotel for same-stop days (travel day hotel = arrival hotel, so still enforce)
      // Only skip if CURRENT day is a travel day (gets new hotel for arrival city)
      const regenCurrentIsTravel = !!isTravelDay(dayNumber, travelStructure);
      const regenShouldEnforceHotel = regenIsSameStop && !regenCurrentIsTravel;
      const regenTravelInfo = isTravelDay(dayNumber, travelStructure);

      // For travel days, compute expectedBaseCity based on overnight status
      let regenExpectedBaseCity;
      if (regenCurrentIsTravel && regenTravelInfo) {
        let regenIsOvernight = false;
        try {
          const regenFlightEst = estimateFlightSchedule(regenTravelInfo.from, regenTravelInfo.to, {
            helpers: { getCityCountrySync, getContinentForCountry },
          });
          regenIsOvernight = !!(regenFlightEst?.isOvernight);
        } catch { /* ignore */ }
        regenExpectedBaseCity = regenIsOvernight
          ? (regenTravelInfo.from || regenStopInfo?.resolvedLocation || regenStopInfo?.name)
          : (regenTravelInfo.to || regenStopInfo?.resolvedLocation || regenStopInfo?.name);
      } else {
        regenExpectedBaseCity = (regenShouldEnforceHotel && regenPrevDay?.baseCity)
          ? regenPrevDay.baseCity
          : null;
      }

      const regenNormResult = normalizeDay(dedupedDay, {
        dayNumber,
        totalDays: totalItineraryDays,
        destination: stopInfo?.name || selectedDestination?.name || '',
        isFirstDay: dayNumber === 1,
        isLastDay: dayNumber === totalItineraryDays,
        arrivalTime: dayNumber === 1 ? enrichedPrefs.arrivalTime : null,
        departureTime: dayNumber === totalItineraryDays ? enrichedPrefs.departureTime : null,
        previousDayCity: regenPrevDay?.baseCity || null,
        expectedHotel: (regenShouldEnforceHotel && regenPrevDay?.hotel) ? regenPrevDay.hotel : null,
        expectedBaseCity: regenExpectedBaseCity,
        preferredPace: enrichedPrefs.preferredPace || enrichedPrefs.tripPace || null,
        preferredStartTime: enrichedPrefs.preferredStartTime || null,
      });

      // Update itinerary with normalized day
      setItinerary(prev => ({
        ...prev,
        days: (prev?.days || []).map((d, i) => i === dayNumber - 1 ? regenNormResult.day : d)
      }));

      setDayGenerationStatus(prev => ({ ...prev, [dayNumber]: 'complete' }));
      setDayGenerationErrors(prev => {
        const updated = { ...prev };
        delete updated[dayNumber];
        return updated;
      });

      debugLog(`✅ Day ${dayNumber} regenerated and normalized (${regenNormResult.corrections.length} corrections)`);

    } catch (error) {
      debugError(`❌ Regeneration of Day ${dayNumber} failed:`, error);
      setDayGenerationStatus(prev => ({ ...prev, [dayNumber]: 'failed' }));
      setDayGenerationErrors(prev => ({ ...prev, [dayNumber]: error.message }));
    } finally {
      setCurrentlyGeneratingDay(null);
      setIsProgressiveGenerating(false);
    }
  };

  // Retry a failed day
  const retryFailedDay = async (dayNumber) => {
    debugLog(`🔄 Retrying failed Day ${dayNumber}`);
    setDayGenerationErrors(prev => {
      const updated = { ...prev };
      delete updated[dayNumber];
      return updated;
    });
    await regenerateSingleDay(dayNumber);
  };

  // Smart itinerary generation - uses progressive for longer trips
  const generateItinerarySmart = async (destination) => {
    // =========================================================
    // CRITICAL SAFETY CHECK - Last line of defense
    // Block itinerary generation for forbidden/restricted destinations
    // =========================================================
    const destName = destination?.name || destination?.stopNames?.[0] || String(destination);
    const allStopNames = destination?.stopNames || [destName];

    // Check each stop in multi-stop trips
    for (const stopName of allStopNames) {
      const destSafetyCheck = checkDestinationRestriction(stopName);
      if (!destSafetyCheck.allowed) {
        debugLog(`🚫 BLOCKED: Cannot generate itinerary for forbidden destination: ${stopName}`);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `⚠️ **Cannot Generate Itinerary**\n\nI cannot create an itinerary for **${stopName}**.\n\n${destSafetyCheck.message}\n\nPlease choose a different destination.`
        }]);
        setStage('chat');
        return;
      }
    }

    // Check citizenship restrictions if passport country is known.
    // Both reads now resolve to canonical `preferences.passportCountry` JSONB —
    // legacy `profile.passport_country` column was dropped in the unification migration.
    const passportCountryRaw = userProfile.preferences?.passportCountry || profile?.preferences?.passportCountry;
    const passportCountries = parsePassportCountries(passportCountryRaw);
    if (passportCountries.length > 0) {
      for (const stopName of allStopNames) {
        for (const country of passportCountries) {
          const citizenCheck = checkCitizenshipRestriction(country, stopName);
          if (!citizenCheck.allowed) {
            debugLog(`🚫 BLOCKED: ${country} passport cannot travel to: ${stopName}`);
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `⚠️ **Travel Restriction**\n\nI cannot create an itinerary for **${stopName}**.\n\n${citizenCheck.message}\n\nPlease choose a different destination.`
            }]);
            setStage('chat');
            return;
          }
        }
      }
    }

    // ─── OFAC + State Dept Travel Advisory Check ───
    if (destination._advisoryConfirmed) {
      debugLog('⏭️ Advisory already confirmed by user, skipping check');
    } else try {
      const { checkTravelAdvisory, formatAdvisoryAlerts, requiresUserConfirmation } = await import('@wander/shared/utils/travelAdvisory');
      const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const advisoryHeaders = await getApiHeaders();
      const advisoryToken = advisoryHeaders['Authorization']?.replace('Bearer ', '') || null;
      const advisoryData = await checkTravelAdvisory(allStopNames, API_BASE, advisoryToken);
      const alerts = formatAdvisoryAlerts(advisoryData);

      if (alerts.length > 0) {
        const ofacAlerts = alerts.filter(a => a.type === 'ofac');
        const l4Alerts = alerts.filter(a => a.type === 'advisory_l4');
        const l3Alerts = alerts.filter(a => a.type === 'advisory_l3');

        // OFAC or Level 4: Show warning and require confirmation
        if (ofacAlerts.length > 0 || l4Alerts.length > 0) {
          const criticalAlerts = [...ofacAlerts, ...l4Alerts];
          const alertMessages = criticalAlerts.map(a => `**${a.title}**\n${a.message}`).join('\n\n');
          const l3Messages = l3Alerts.map(a => `**${a.title}**\n${a.message}`).join('\n\n');

          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `🚨 **Travel Advisory Alert**\n\n${alertMessages}${l3Messages ? `\n\n${l3Messages}` : ''}\n\n⚠️ **Do you still want to generate an itinerary?** Please be aware of the risks above. Type "yes, continue" to proceed or choose a different destination.`,
            _advisoryConfirmation: true,
            _advisoryDestination: destination,
          }]);
          setStage('chat');
          return;
        }

        // Level 3: Show warning but allow to proceed
        if (l3Alerts.length > 0) {
          const l3Messages = l3Alerts.map(a => `**${a.title}**\n${a.message}`).join('\n\n');
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `⚠️ **Travel Advisory**\n\n${l3Messages}\n\nProceeding with itinerary generation. Please review the advisory information above.`,
          }]);
          // Continue with generation — don't block for Level 3
        }

        // Level 2: Store for display in itinerary (don't show in chat)
        if (alerts.some(a => a.type === 'advisory_l2')) {
          setValidationIssues(prev => {
            // Deduplicate: remove existing travel_advisory entries before adding new ones
            const withoutAdvisories = prev.filter(i => i.type !== 'travel_advisory');
            return [...withoutAdvisories, ...alerts.filter(a => a.type === 'advisory_l2').map(a => ({
              type: 'travel_advisory',
              severity: 'info',
              message: a.message,
              global: true,
            }))];
          });
        }
      }
    } catch (advisoryErr) {
      debugWarn('[Advisory] Check failed, continuing:', advisoryErr.message);
    }

    // Guard against duplicate calls - set immediately to prevent race conditions
    if (generationInProgressRef.current) {
      debugLog('⚠️ [Smart] Generation already in progress, skipping duplicate call');
      return;
    }
    generationInProgressRef.current = true;

    try {
      // Calculate trip duration
      const prefs = userProfile.preferences;
      let tripDuration = prefs.duration || 7;
      if (prefs.startDate && prefs.endDate) {
        const diffDays = calculateDaysBetween(prefs.startDate, prefs.endDate);
        if (diffDays && diffDays > 0) tripDuration = diffDays;
      }

      // ─── Server-side generation (unified pipeline) ───
      // Routes through the same orchestrator as mobile: GLM primary, batched generation,
      // Google Places enrichment, transport validation, closed venue replacement.
      debugLog(`🚀 Smart: Using server-side generation for ${tripDuration}-day trip`);

      try {
        // Build stops array from trip builder
        const stops = (destination.stops || tripBuilderStops || []).map(s => ({
          name: (s.stopType === 'hotel' || s.stopType === 'resort') ? (s.resolvedLocation || s.name || s) : (s.resolvedLocation || s.name || s),
          days: s.days || Math.floor(tripDuration / (destination.stops?.length || 1)),
          resolvedLocation: s.resolvedLocation || s.name || s,
          stopType: s.stopType || 'destination',
          // For resort stops, preserve the original resort/hotel name so backend can enforce it
          originalName: (s.stopType === 'hotel' || s.stopType === 'resort') ? s.name : undefined,
          // Hotel name for resort mode — backend uses this to enforce the selected hotel
          hotelName: (s.stopType === 'hotel' || s.stopType === 'resort') ? s.name : undefined,
          // primaryCityHint: server-side generationRoutes.js injects this for
          // bare-country stops, but if the client already has it (e.g., from a
          // bookmark restore or chat-extracted stops), pass through so the
          // orchestrator gets it the first time.
          primaryCityHint: typeof s === 'object' ? s.primaryCityHint : undefined,
          id: typeof s === 'object' ? s.id : undefined,
        }));
        if (stops.length === 0 && destination.name) {
          stops.push({ name: destination.name, days: tripDuration, resolvedLocation: destination.name, stopType: 'destination' });
        }

        // Build preferences. B17 fix: normalize via shared schema BEFORE
        // sending — mobile already does this in api.js, but web was spreading
        // raw `prefs` which could leave `companions` as a legacy string
        // (instead of canonical array), or other fields in pre-canonical
        // shape. That payload drift caused web vs mobile generation to
        // build different prompts (different traveler context → different
        // output quality). Normalizing here closes the gap.
        const serverPrefs = {
          ...normalizePreferences(prefs),
          duration: tripDuration,
          startDate: prefs.startDate || null,
          endDate: prefs.endDate || null,
          arrivalTime: arrivalFlight?.time || '08:00',
          departureTime: departureFlight?.time || '18:00',
        };

        // Build per-leg transport overrides
        const legTransportData = {};
        if (typeof dynamicTransportModes === 'object') {
          Object.entries(dynamicTransportModes).forEach(([key, data]) => {
            legTransportData[key] = data;
          });
        }

        const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
        const headers = await getApiHeaders();

        // Start generation job
        const startRes = await fetch(`${API_BASE}/api/generate/start`, {
          method: 'POST',
          headers: { ...headers, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            stops,
            preferences: serverPrefs,
            departureCity: prefs.homeCity || userProfile?.preferences?.homeCity || '',
            returnCity: prefs.returnCity || prefs.homeCity || '',
            transportMode: transportationMode || 'fly',
            returnTransportMode: effectiveReturnMode || transportationMode || 'fly',
            customTransportDesc: customTransportDesc || '',
            quickTransportResult: quickTransportResult || null,
            legTransports: legTransportData,
            stopHotels: {},
            tripContext: {
              originalMessage: messages?.filter(m => m.role === 'user')?.pop()?.content || '',
              occasion: prefs.occasion || '',
              travelingWith: prefs.travelingWith || '',
              travelMonth: prefs.travelMonth || '',
            },
            enrichedInterests: prefs.interests || [],
          }),
        });

        if (!startRes.ok) {
          const errData = await startRes.json().catch(() => ({}));
          // Handle usage limit exceeded (429) with a user-friendly message
          if (startRes.status === 429) {
            const isExplorer = tier === 'explorer';
            const tierLimits = getTierLimits ? getTierLimits() : { itinerariesPerQuarter: 5 };
            showAlertModal(
              'Itinerary Limit Reached',
              isExplorer
                ? `You've used all ${tierLimits.itinerariesPerQuarter} free itineraries for this quarter.\n\nUpgrade to arrivo Planner for unlimited itineraries, group planning, and permanent trip history!`
                : `You've reached your quarterly itinerary limit (${tierLimits.itinerariesPerQuarter} per quarter).\n\nYour quota will reset at the beginning of next quarter.`
            );
            setIsGeneratingItinerary(false);
            setShowLoadingScreen(false);
            return;
          }
          throw new Error(errData.error || `Server generation failed: ${startRes.status}`);
        }

        const { jobId, totalDays: serverTotalDays } = await startRes.json();
        // Refresh usage cache so frontend pre-check stays in sync with backend
        if (refreshUsage) refreshUsage();
        debugLog(`🚀 Server generation started: jobId=${jobId}, totalDays=${serverTotalDays}`);

        // Ensure totalItineraryDays is set from server response so day tabs show all days upfront
        if (serverTotalDays && serverTotalDays > 0) {
          setTotalItineraryDays(serverTotalDays);
          setIsProgressiveGenerating(true);
        }

        // Show loading screen
        setShowLoadingScreen(true);
        setStage('loading');
        setLoadingMessage('Crafting your perfect itinerary...');

        // Poll for progressive results
        let lastDayCount = 0;
        let pollAttempts = 0;
        const maxPollAttempts = 600; // 10 minutes at 1s intervals

        const pollForResults = async () => {
          while (pollAttempts < maxPollAttempts) {
            // Stop polling if user navigated away from generation
            if (!generationInProgressRef.current) {
              debugLog('🛑 Poll stopped — user navigated away from generation');
              return;
            }
            pollAttempts++;
            await new Promise(r => setTimeout(r, 1500));

            try {
              const pollRes = await fetch(`${API_BASE}/api/generate/poll/${jobId}`, { headers });
              if (!pollRes.ok) continue;

              const poll = await pollRes.json();

              // Progressive rendering — show days as they complete
              debugLog(`📊 Poll #${pollAttempts}: status=${poll.status}, days=${poll.completedDays?.length || 0}, lastDayCount=${lastDayCount}, stage=${stage}`);
              if (poll.completedDays?.length > lastDayCount) {
                // Normalize meals format: GLM may return meals as object {breakfast:{},lunch:{}} instead of array
                const newDays = (poll.completedDays || []).map(day => {
                  if (day?.meals && !Array.isArray(day.meals) && typeof day.meals === 'object') {
                    return { ...day, meals: Object.entries(day.meals).filter(([, v]) => v && typeof v === 'object').map(([key, val]) => ({ ...val, type: val.type || key, meal: val.meal || key })) };
                  }
                  return day;
                });
                lastDayCount = newDays.length;
                setLoadingMessage(`Building day ${lastDayCount} of ${serverTotalDays || tripDuration}...`);

                // Update itinerary progressively
                setItinerary(prev => {
                  const meta = poll.itineraryMeta || {};
                  // Preserve multi-stop display name (e.g., "London & Paris & Bangkok")
                  // Backend meta.destination only has first city — don't overwrite
                  const multiStopName = destination.stops?.length > 1
                    ? destination.stops.map(s => s.name).join(' & ')
                    : (meta.destination || destination.name);
                  // Preserve weather data from previous state when progressive generation replaces days
                  const prevDays = prev?.days || [];
                  const mergedDays = newDays.map((day, idx) => {
                    if (!day.weather && prevDays[idx]?.weather) {
                      return { ...day, weather: prevDays[idx].weather };
                    }
                    return day;
                  });
                  return {
                    ...(prev || {}),
                    ...meta,
                    days: mergedDays,
                    destination: prev?.destination || multiStopName,
                    interCityTransport: meta.interCityTransport || prev?.interCityTransport || [],
                  };
                });

                // Show itinerary view after Day 1 (fast hand-off, matches mobile).
                // Users see their first day immediately while Days 2+ generate in background.
                if (lastDayCount >= 1 && (stage === 'loading' || stage === 'personalization')) {
                  setShowLoadingScreen(false);
                  setStage('itinerary');
                }
              }

              if (poll.status === 'completed') {
                debugLog(`✅ Server generation completed: ${poll.completedDays?.length} days`);

                // Final update with all enriched data — normalize meals format
                const finalDays = (poll.completedDays || []).map(day => {
                  if (day?.meals && !Array.isArray(day.meals) && typeof day.meals === 'object') {
                    return { ...day, meals: Object.entries(day.meals).filter(([, v]) => v && typeof v === 'object').map(([key, val]) => ({ ...val, type: val.type || key, meal: val.meal || key })) };
                  }
                  return day;
                });
                setItinerary(prev => {
                  const meta = poll.itineraryMeta || {};
                  // Preserve multi-stop display name — don't overwrite with backend's first-city
                  const multiStopName = destination.stops?.length > 1
                    ? destination.stops.map(s => s.name).join(' & ')
                    : (meta.destination || destination.name);
                  // Preserve weather data from previous state
                  const prevDays = prev?.days || [];
                  const mergedDays = finalDays.map((day, idx) => {
                    if (!day.weather && prevDays[idx]?.weather) {
                      return { ...day, weather: prevDays[idx].weather };
                    }
                    return day;
                  });
                  return {
                    ...(prev || {}),
                    ...meta,
                    days: mergedDays,
                    destination: prev?.destination || multiStopName,
                    interCityTransport: meta.interCityTransport || [],
                  };
                });
                setWeatherFetchTrigger(t => t + 1); // Trigger weather fetch for any new days from final batch

                setShowLoadingScreen(false);
                setStage('itinerary');
                setIsProgressiveGenerating(false);

                // Check for missing must-do activities and warn user
                if (finalDays?.[0]?._missingMustDo?.length > 0) {
                  const missing = finalDays[0]._missingMustDo;
                  setTimeout(() => {
                    showToast(`⚠️ Some must-do items may not have been included: ${missing.join(', ')}. You can use Ask arrivo to add them.`, 'warning', 8000);
                  }, 2000);
                }

                // Save for Planner/Admin users
                if (user && isLoggedIn) {
                  const destName = destination.stops?.length > 1
                    ? destination.stops.map(s => s.name).join(' & ')
                    : destination.name;
                  const title = `${destName} - ${tripDuration} days`;
                  try {
                    const saveResult = await saveItinerary(user.id, { days: poll.completedDays, ...(poll.itineraryMeta || {}) }, title, destName, prefs.startDate, prefs.endDate);
                    if (saveResult?.id) {
                      setCurrentItineraryId(saveResult.id);
                      setCurrentTripId(saveResult.id);
                      setLastSavedAt(new Date());
                      debugLog('✅ Itinerary saved:', saveResult.id);
                    }
                  } catch (saveErr) {
                    debugLog('⚠️ Auto-save failed:', saveErr.message);
                  }
                }

                // Run post-generation validations (safety info, etc.)
                try {
                  const { checkTravelAdvisory, formatAdvisoryAlerts } = await import('@wander/shared/utils/travelAdvisory');
                  const postGenAdvisoryHeaders = await getApiHeaders();
                  const postGenAdvisoryToken = postGenAdvisoryHeaders['Authorization']?.replace('Bearer ', '') || null;
                  const advisoryData = await checkTravelAdvisory(allStopNames, API_BASE, postGenAdvisoryToken);
                  const alerts = formatAdvisoryAlerts(advisoryData);
                  if (alerts.length > 0) {
                    setValidationIssues(prev => {
                      // Deduplicate: pre-generation may have already added the same advisories
                      const existingMessages = new Set(prev.filter(i => i.type === 'travel_advisory').map(i => i.message));
                      const newAlerts = alerts.filter(a => a.type !== 'ofac' && !existingMessages.has(a.message)).map(a => ({
                        type: 'travel_advisory', severity: a.severity, message: a.message, global: true,
                      }));
                      return newAlerts.length > 0 ? [...prev, ...newAlerts] : prev;
                    });
                    setShowValidationPanel(true);
                  }
                } catch { /* non-blocking */ }

                // Post-gen airport validator — parity with mobile
                // (ItineraryScreen.js:2180+). Catches the case where the
                // orchestrator's auto-resolver picked an airport that's
                // not actually reachable by ground transport from Day 1's
                // baseCity (e.g., picked BKK for a remote Hua Hin resort).
                // Skipped when `_userPicked` is true so deliberate user
                // choices stay locked in.
                try {
                  const finalItin = poll.itinerary || (poll.completedDays && poll.itineraryMeta
                    ? { ...poll.itineraryMeta, days: poll.completedDays } : null);
                  if (finalItin && finalItin.arrivalAirport && !finalItin.arrivalAirport._userPicked) {
                    const airportCity = (finalItin.arrivalAirport.city || '').toLowerCase();
                    const day1City = (finalItin.days?.[0]?.baseCity || '').toLowerCase();
                    const cityMismatch = airportCity && day1City
                      && !airportCity.includes(day1City)
                      && !day1City.includes(airportCity.split(',')[0]);
                    if (cityMismatch) {
                      const headers = await getApiHeaders();
                      const validateRes = await fetch(`${API_BASE}/api/validate-transport-mode`, {
                        method: 'POST', headers,
                        body: JSON.stringify({
                          fromCity: finalItin.arrivalAirport.city || airportCity,
                          toCity: day1City,
                          proposedMode: 'car',
                        }),
                      }).then(r => r.ok ? r.json() : null).catch(() => null);
                      if (validateRes && !validateRes.valid) {
                        const transportRes = await fetch(`${API_BASE}/api/destination-transport-info`, {
                          method: 'POST', headers,
                          body: JSON.stringify({ destination: day1City }),
                        }).then(r => r.ok ? r.json() : null).catch(() => null);
                        if (transportRes?.nearestAirport?.code) {
                          // Mutate the deployed itinerary state with the corrected airport.
                          // Same shape as orchestrator output so downstream consumers see no surprise.
                          finalItin.arrivalAirport = {
                            code: transportRes.nearestAirport.code,
                            name: transportRes.nearestAirport.name || `${transportRes.nearestAirport.code} Airport`,
                            city: transportRes.nearestAirport.city || day1City,
                            // Don't re-flag _userPicked — this is an auto-correction.
                          };
                          setItinerary(prev => prev ? { ...prev, arrivalAirport: finalItin.arrivalAirport } : prev);
                          console.log(`[PostGen] Corrected arrival airport → ${transportRes.nearestAirport.code}`);
                        }
                      }
                    }
                  }
                } catch (e) { console.warn('[PostGen] Airport validation failed:', e.message); }

                return;
              }

              if (poll.status === 'failed' || poll.status === 'error') {
                const errMsg = poll.error || 'Generation failed on server';
                const err = new Error(errMsg);
                err._serverFail = true;
                throw err;
              }
            } catch (pollErr) {
              if (pollErr._serverFail || pollErr.message?.includes('Generation failed')) throw pollErr;
              // Network error — keep polling
            }
          }
          throw new Error('Generation timed out after 10 minutes');
        };

        await pollForResults();

      } catch (serverGenErr) {
        debugLog(`❌ Server generation failed: ${serverGenErr.message}`);
        setShowLoadingScreen(false);
        setStage('personalization');
        setIsProgressiveGenerating(false);
        showToast(`Generation failed: ${serverGenErr.message}. Please try again.`, 'error', 6000);
      }
    } finally {
      generationInProgressRef.current = false;
      // Safety net: ensure progressive flag is cleared so export buttons are never stuck disabled
      setIsProgressiveGenerating(false);
    }
  };

  // ========== END PROGRESSIVE GENERATION CORE FUNCTIONS ==========

  // Offline detection effect
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ─── Server-side generation recovery on page load ───
  useEffect(() => {
    if (!user || !isLoggedIn) return;
    if (stage !== 'landing') return;
    const checkActiveJob = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_URL || process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
        const headers = await getApiHeaders();
        const res = await fetch(`${API_BASE}/api/generate/active`, { headers });
        if (!res.ok) return;
        const result = await res.json();
        if (!result?.active || !result?.jobId) return;
        const completedDays = typeof result.completedDays === 'string' ? JSON.parse(result.completedDays) : (result.completedDays || []);
        const itineraryMeta = typeof result.itineraryMeta === 'string' ? JSON.parse(result.itineraryMeta) : (result.itineraryMeta || {});
        const normalizeDays = (days) => days.map(day => {
          if (day?.meals && !Array.isArray(day.meals) && typeof day.meals === 'object') {
            return { ...day, meals: Object.entries(day.meals).filter(([, v]) => v && typeof v === 'object').map(([key, val]) => ({ ...val, type: val.type || key, meal: val.meal || key })) };
          }
          return day;
        });
        const normalizedDays = normalizeDays(completedDays);
        if (result.status === 'completed' && normalizedDays.length > 0) {
          debugLog('🔄 Recovery: loading completed itinerary from active job', result.jobId);
          setItinerary({ ...itineraryMeta, days: normalizedDays });
          setTotalItineraryDays(normalizedDays.length);
          setStage('itinerary');
        } else if (result.status === 'generating' || result.status === 'pending') {
          debugLog('🔄 Recovery: resuming poll for active job', result.jobId);
          if (result.totalDays > 0) setTotalItineraryDays(result.totalDays);
          setIsProgressiveGenerating(true);
          if (normalizedDays.length > 0) setItinerary({ ...itineraryMeta, days: normalizedDays });
          setShowLoadingScreen(true); setStage('loading');
          setLoadingMessage('Resuming your itinerary generation...');
          const jobId = result.jobId; let lastDayCount = normalizedDays.length;
          // B30: track last forward progress so we can detect a truly stale
          // job (no new days for >5 min) and bail out with an error instead
          // of polling silently for 15 minutes. Previously the recovery
          // loop would just exit at iteration 600 leaving the loading
          // screen stuck on screen with no error message.
          let lastProgressAt = Date.now();
          const STALL_MS = 5 * 60 * 1000;
          const pollForResults = async () => {
            for (let i = 0; i < 600; i++) {
              if (!generationInProgressRef.current) {
                debugLog('🛑 Resume poll stopped — user navigated away');
                return;
              }
              // B30: bail on prolonged stall.
              if (Date.now() - lastProgressAt > STALL_MS) {
                debugLog('⏱️ Recovery poll stalled — no progress for 5min, surfacing error');
                setShowLoadingScreen(false);
                setStage('landing');
                setIsProgressiveGenerating(false);
                if (typeof showToast === 'function') {
                  showToast('Generation stalled. Please start a fresh trip.', 'error');
                }
                return;
              }
              await new Promise(r => setTimeout(r, 1500));
              try {
                const pollRes = await fetch(`${API_BASE}/api/generate/poll/${jobId}`, { headers });
                if (!pollRes.ok) continue;
                const poll = await pollRes.json();
                if (poll.completedDays?.length > lastDayCount) {
                  lastProgressAt = Date.now(); // B30: record forward progress
                  const newDays = normalizeDays(poll.completedDays || []);
                  lastDayCount = newDays.length;
                  setLoadingMessage(`Building day ${lastDayCount} of ${result.totalDays}...`);
                  setItinerary(prev => {
                    // Preserve weather data from previous state when progressive generation replaces days
                    const prevDays = prev?.days || [];
                    const mergedDays = newDays.map((day, idx) => {
                      if (!day.weather && prevDays[idx]?.weather) {
                        return { ...day, weather: prevDays[idx].weather };
                      }
                      return day;
                    });
                    return { ...(prev || {}), ...(poll.itineraryMeta || {}), days: mergedDays };
                  });
                  if (lastDayCount >= 2) { setShowLoadingScreen(false); setStage('itinerary'); }
                }
                if (poll.status === 'completed') {
                  const finalDays = normalizeDays(poll.completedDays || []);
                  setItinerary(prev => {
                    const prevDays = prev?.days || [];
                    const mergedDays = finalDays.map((day, idx) => {
                      if (!day.weather && prevDays[idx]?.weather) {
                        return { ...day, weather: prevDays[idx].weather };
                      }
                      return day;
                    });
                    return { ...(prev || {}), ...(poll.itineraryMeta || {}), days: mergedDays };
                  });
                  setWeatherFetchTrigger(t => t + 1); // Trigger weather fetch for any new days
                  setShowLoadingScreen(false); setStage('itinerary'); setIsProgressiveGenerating(false); return;
                }
                if (poll.status === 'failed') {
                  setShowLoadingScreen(false); setStage('landing'); setIsProgressiveGenerating(false);
                  if (typeof showToast === 'function') {
                    showToast('Generation failed. Please try again.', 'error');
                  }
                  return;
                }
              } catch (err) { debugLog('⚠️ Recovery poll error:', err.message); }
            }
            // B30: loop hit the 600-iteration cap (15 min) without resolution.
            // Previously this returned silently leaving the loading screen
            // stuck. Now we surface a timeout error so the user can retry.
            debugLog('⏱️ Recovery poll exhausted 600 iterations without resolution');
            setShowLoadingScreen(false);
            setStage('landing');
            setIsProgressiveGenerating(false);
            if (typeof showToast === 'function') {
              showToast('Generation took too long. Please start a fresh trip.', 'error');
            }
          };
          pollForResults();
        }
      } catch (err) { debugLog('⚠️ Generation recovery check failed:', err.message); }
    };
    checkActiveJob();
  }, [user, isLoggedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to estimate drive time from flight time
  // Drive time estimation — imported from @wander/shared via ./utils/travelTimeEstimates
  const estimateDriveHours = _estimateDriveHours;
  
  // Cache for LLM-estimated flight times (persists during session)
  const flightTimeCache = useRef({});

  // Cache for dynamically validated inter-city flight durations (populated by post-gen validation)
  // Keys are "fromCity-toCity" (lowercase), values are duration strings like "2h 30m"
  const validatedFlightDurations = useRef({});
  
  // Check if destination has a static flight estimate available.
  // Primary source: FLIGHT_TIMES keys from shared flightEstimates.js (dynamic, comprehensive).
  // This replaces the old hardcoded KNOWN_DESTINATIONS list.
  const isKnownDestination = (destinationName) => {
    if (!destinationName) return false;
    // Use getStaticFlightEstimate — returns non-null if FLIGHT_TIMES has a match.
    // We pass 'NYC' as a dummy home city since we just care if the destination is known.
    return getStaticFlightEstimate(destinationName, 'new york') !== null;
  };
  
  // LLM-based flight time estimation (for unknown destinations)
  const estimateFlightTimeWithLLM = async (destination, homeCity) => {
    try {
      debugLog(`✈️ Asking LLM for flight time: ${homeCity} → ${destination}`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 50,
          messages: [{
            role: "user",
            content: `Estimate the direct flight time in hours from ${homeCity} to ${destination}. 
Consider the most common airport for each city.
Return ONLY a single number (e.g., "5" or "7.5"). 
If there's no direct flight, estimate total travel time with one connection.
If you're unsure, make your best estimate based on distance.`
          }]
        })
      });
      
      const data = await response.json();
      const responseText = data.content?.[0]?.text?.trim() || '';
      
      // Extract number from response (handles "5", "5 hours", "approximately 5", etc.)
      const numberMatch = responseText.match(/(\d+(?:\.\d+)?)/);
      const hours = numberMatch ? parseFloat(numberMatch[1]) : null;
      
      if (hours && hours > 0 && hours < 30) {
        debugLog(`✈️ LLM estimated: ${hours} hours`);
        return hours;
      }
      
      debugLog(`⚠️ LLM returned invalid flight time: "${responseText}", using default`);
      return 5; // Default fallback
      
    } catch (error) {
      debugError('❌ LLM flight estimation error:', error);
      return 5; // Default fallback on error
    }
  };
  
  // HYBRID: Get flight hours with static lookup, LLM fallback, and caching
  // Returns a Promise that resolves to the flight hours
  const getFlightHours = async (destination, homeCity = '') => {
    if (!destination) return 5;

    const normalizedHome = homeCity || '';
    const cacheKey = `${normalizedHome}-${destination}`.toLowerCase();
    
    // 1. Check cache first (instant)
    if (flightTimeCache.current[cacheKey]) {
      debugLog(`✈️ Cache hit: ${cacheKey} = ${flightTimeCache.current[cacheKey]}hrs`);
      return flightTimeCache.current[cacheKey];
    }
    
    // 2. Try static lookup (instant, free)
    const staticEstimate = getStaticFlightEstimate(destination, normalizedHome);
    if (staticEstimate !== null) {
      debugLog(`✈️ Static lookup: ${destination} = ${staticEstimate}hrs`);
      if (Object.keys(flightTimeCache.current).length > DYNAMIC_CACHE_MAX) {
        const keys = Object.keys(flightTimeCache.current);
        keys.slice(0, Math.floor(DYNAMIC_CACHE_MAX * 0.2)).forEach(k => delete flightTimeCache.current[k]);
      }
      flightTimeCache.current[cacheKey] = staticEstimate;
      return staticEstimate;
    }

    // 3. Unknown destination - use LLM (async, ~500ms-1s)
    debugLog(`✈️ Unknown destination "${destination}", calling LLM...`);
    const llmEstimate = await estimateFlightTimeWithLLM(destination, normalizedHome);
    if (Object.keys(flightTimeCache.current).length > DYNAMIC_CACHE_MAX) {
      const keys = Object.keys(flightTimeCache.current);
      keys.slice(0, Math.floor(DYNAMIC_CACHE_MAX * 0.2)).forEach(k => delete flightTimeCache.current[k]);
    }
    flightTimeCache.current[cacheKey] = llmEstimate;
    return llmEstimate;
  };
  
  // Synchronous version for immediate display (uses cache/static only, returns default if unknown)
  // Use this when you need an immediate value and can't await
  const getFlightHoursSync = (destination, homeCity = '') => {
    if (!destination) return null; // Return null when no departure city — callers handle this

    const normalizedHome = homeCity || '';
    const cacheKey = `${normalizedHome}-${destination}`.toLowerCase();
    
    // Check cache
    if (flightTimeCache.current[cacheKey]) {
      return flightTimeCache.current[cacheKey];
    }
    
    // Try static lookup
    const staticEstimate = getStaticFlightEstimate(destination, normalizedHome);
    if (staticEstimate !== null) {
      flightTimeCache.current[cacheKey] = staticEstimate;
      return staticEstimate;
    }
    
    // Return default for unknown (LLM call should be triggered separately)
    return 5;
  };
  
  // Legacy function name for backwards compatibility
  const estimateFlightHoursFromDestination = getFlightHoursSync;
  
  // State to track if we're fetching LLM flight estimate
  const [isFetchingFlightTime, setIsFetchingFlightTime] = useState(false);

  // NEW: Date picker and feedback states
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerValues, setDatePickerValues] = useState({ startDate: '', endDate: '' });
  
  // NEW: Transportation mode (fly, drive-own, drive-rental, train, ferry, custom)
  const [transportationMode, setTransportationMode] = useState('fly');
  const [returnTransportMode, setReturnTransportMode] = useState(null); // null = same as transportationMode
  const effectiveReturnMode = returnTransportMode || transportationMode;
  const [customTransportDesc, setCustomTransportDesc] = useState(''); // free-text for 'custom' mode
  const [quickTransportResult, setQuickTransportResult] = useState(null); // cached /api/quick-transport for departure→first-stop
  const [isFetchingQuickTransport, setIsFetchingQuickTransport] = useState(false);
  const [driveDetails, setDriveDetails] = useState({
    departureTime: '06:00',  // When to leave home
    estimatedHours: 0,       // Will be calculated
    returnDepartureTime: '10:00' // When to leave destination on last day
  });
  
  // NEW: Arrival and departure details
  const [arrivalFlight, setArrivalFlight] = useState({ 
    time: '08:00',
    flightNumber: '',
    airline: '',
    from: '',
    date: ''
  });
  const [departureFlight, setDepartureFlight] = useState({ 
    time: '18:00',
    flightNumber: '',
    airline: '',
    to: '',
    date: ''
  });
  const [editingFlight, setEditingFlight] = useState(false);
  const [showFlightModal, setShowFlightModal] = useState(false);
  const [showRouteOverview, setShowRouteOverview] = useState(false); // Route overview dropdown for multi-stop trips
  const [showBudgetTracker, setShowBudgetTracker] = useState(false); // Budget module toggle (admin-only)
  const [expandedFlightPrices, setExpandedFlightPrices] = useState({}); // { routeKey: true/false } — track which flight price cards are expanded inline
  const [showHotelModal, setShowHotelModal] = useState(false);
  const [showFreeTimeModal, setShowFreeTimeModal] = useState(false);
  const [selectedFreeTimeSlot, setSelectedFreeTimeSlot] = useState(null); // { day, time, duration, durationMinutes }
  const [freeTimeSuggestions, setFreeTimeSuggestions] = useState([]); // Dynamic AI-generated suggestions
  const [expandedFreeTimeSlots, setExpandedFreeTimeSlots] = useState(new Set()); // Track which free time cards are expanded
  const [loadingFreeTimeSuggestions, setLoadingFreeTimeSuggestions] = useState(false);
  const [activityDetailsCache, setActivityDetailsCache] = useState({}); // { 'activity|city': { whatToDo, tips, loading } }

  // Lazy-fetch activity details (whatToDo) from backend API
  const fetchActivityDetails = async (activityName, city, companion) => {
    const cacheKey = `${activityName}|${city}`;
    if (activityDetailsCache[cacheKey]?.whatToDo) return activityDetailsCache[cacheKey];
    if (activityDetailsCache[cacheKey]?.loading) return null;

    setActivityDetailsCache(prev => ({ ...prev, [cacheKey]: { loading: true } }));
    try {
      const authData = JSON.parse(localStorage.getItem('sb-ifiicplynlpcvoqdyxjf-auth-token') || '{}');
      const token = authData?.access_token;
      // companion may arrive as a string OR array (canonical multi-select). Coerce to phrase.
      const companionStr = companionsToPromptString(companion, 'solo');
      const params = new URLSearchParams({ activity: activityName, city, companion: companionStr });
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const resp = await fetch(`${BACKEND_URL}/api/activity-details?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('Failed to fetch');
      const data = await resp.json();
      const result = { whatToDo: data.whatToDo, tips: data.tips, bestTime: data.bestTime, nearbyAlternatives: data.nearbyAlternatives, loading: false };
      setActivityDetailsCache(prev => ({ ...prev, [cacheKey]: result }));
      return result;
    } catch (err) {
      setActivityDetailsCache(prev => ({ ...prev, [cacheKey]: { loading: false, error: err.message } }));
      return null;
    }
  };

  // Lazy-fetch free time suggestions from backend API.
  // Wrapped in a localStorage cache (7-day TTL) keyed on
  // (itineraryId, dayIndex, beforeTime, afterTime, city) so the same
  // gap on the same day doesn't re-fire the LLM every time the user
  // taps "Ask arrivo →" — across re-opens, day-tab switches, and
  // re-renders. Mirrors mobile cache (utils/freeTimeSuggestionsCache).
  const fetchFreeTimeSuggestions = async (city, timePeriod, companion, opts = {}) => {
    const { itineraryId, dayIndex, beforeTime, afterTime, existingActivities = [] } = opts;
    // Cache key includes existingActivities-hash so changing the itinerary
    // invalidates stale dedup-less suggestions. Without this, a Day 1 fetch
    // that suggested "MAAT" would be served from cache even after the user
    // confirmed MAAT on Day 2.
    const exclusionFingerprint = existingActivities.length
      ? existingActivities.map(s => String(s).toLowerCase().trim()).sort().join('|').slice(0, 200)
      : '';
    const cacheArgs = { itineraryId, dayIndex, beforeTime, afterTime, city, exclusionFingerprint };
    // Cache hit — skip the network round-trip.
    const cached = readFreeTimeSuggestions(cacheArgs);
    if (cached && cached.length > 0) return cached;

    try {
      const authData = JSON.parse(localStorage.getItem('sb-ifiicplynlpcvoqdyxjf-auth-token') || '{}');
      const token = authData?.access_token;
      // companion may arrive as a string OR array (canonical multi-select). Coerce to phrase.
      const companionStr = companionsToPromptString(companion, 'solo');
      const params = new URLSearchParams({ city, timePeriod: timePeriod || 'all', companion: companionStr });
      // Cross-day dedup: pass existing activity names so the backend prompt
      // can exclude them from suggestions. Bounded at 30 names to keep the
      // URL/prompt reasonable.
      if (existingActivities.length > 0) {
        params.set('exclude', existingActivities.slice(0, 30).join('|'));
      }
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const resp = await fetch(`${BACKEND_URL}/api/free-time-suggestions?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error('Failed to fetch');
      const data = await resp.json();
      // Fire-and-forget cache write — never blocks the UI.
      try { writeFreeTimeSuggestions(cacheArgs, data.suggestions); } catch {}
      return data.suggestions;
    } catch {
      return null;
    }
  };
  const [showMoreFreeTimeSuggestions, setShowMoreFreeTimeSuggestions] = useState(false);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapTarget, setSwapTarget] = useState(null); // { dayIdx, itemIdx, itemName, timeSlot, duration, city }
  const [swapSuggestions, setSwapSuggestions] = useState([]);
  const [loadingSwapSuggestions, setLoadingSwapSuggestions] = useState(false);
  const [showCustomHomeCityInput, setShowCustomHomeCityInput] = useState(false);
  const [showCustomReturnCityInput, setShowCustomReturnCityInput] = useState(false);
  const [personalizationWeather, setPersonalizationWeather] = useState(null); // single-stop: { high, low, ... } or multi-stop: [{ city, high, low, ... }, ...]
  const personalizationWeatherRef = useRef(null); // Track last fetched key to avoid duplicates
  const geocodeCacheRef = useRef({}); // Cache geocode results: { cityName: { lat, lng } }
  const [isEditingDuration, setIsEditingDuration] = useState(false);
  const [showAddCityInput, setShowAddCityInput] = useState(false);
  const [addCityName, setAddCityName] = useState('');
  const [addCityDays, setAddCityDays] = useState(2);
  const [editingHotelName, setEditingHotelName] = useState(null); // null = not editing, string = current input value
  const [hotelAlternatives, setHotelAlternatives] = useState(null); // { day: number, loading: boolean, options: [], oldHotelName: string }
  const [showHotelDetails, setShowHotelDetails] = useState({}); // toggle hotel details panel: { [dayIndex]: true/false }
  const [expandedHotelCards, setExpandedHotelCards] = useState({}); // collapsed/expanded hotel cards: { [dayIndex]: true/false }
  const [hotelRoomOptions, setHotelRoomOptions] = useState(null); // { day: number, loading: boolean, rooms: [], hotelName: string, error: string|null }
  const [amadeusHotelData, setAmadeusHotelData] = useState({}); // Per-hotel Amadeus data: { [hotelName]: { loading, rooms, rating, bestRoom, error } }
  const amadeusHotelFetchedRef = useRef(new Set()); // Track which hotels have been fetched to avoid duplicates
  const [hotelSearchFilter, setHotelSearchFilter] = useState(''); // Hotel name search filter
  const [hotelStarFilter, setHotelStarFilter] = useState(0); // 0 = all, 3/4/5 = min stars
  const [showManualHotelEntry, setShowManualHotelEntry] = useState(false); // Manual hotel/Airbnb entry
  const [manualHotelName, setManualHotelName] = useState('');
  const [manualHotelAddress, setManualHotelAddress] = useState('');
  const [showCustomActivityInput, setShowCustomActivityInput] = useState(false);
  const [customActivityName, setCustomActivityName] = useState('');
  const [customActivityDuration, setCustomActivityDuration] = useState('30 min');
  const [editingActivityIndex, setEditingActivityIndex] = useState(null);
  const [editedActivity, setEditedActivity] = useState({});
  const [editingMealIndex, setEditingMealIndex] = useState(null);
  const [editedMeal, setEditedMeal] = useState({});
  const [flightModalType, setFlightModalType] = useState(null); // 'arrival' or 'departure'
  const [lookingUpFlight, setLookingUpFlight] = useState(false);
  const [selectedHotel, setSelectedHotel] = useState(null);
  const [generatedHotelName, setGeneratedHotelName] = useState(null);
  const [transportModes, setTransportModes] = useState({}); // Track selected mode for each transit segment
  const [transitTimes, setTransitTimes] = useState({}); // Cache transit times from Google API
  const transitTimesRef = useRef({}); // Ref mirror to avoid stale closure in useEffect
  const transitInFlightRef = useRef({}); // Track in-flight fetch promises to prevent duplicate API calls
  const userHasSetTransportRef = useRef(false); // Track if user has manually chosen transport mode
  const transitCalcGuardRef = useRef(null); // Guard: tracks which day is currently being calculated
  const [dynamicTransportModes, setDynamicTransportModes] = useState({}); // Cache haiku-determined transport modes for inter-city pairs
  const [loadingTransit, setLoadingTransit] = useState({}); // Track which transits are loading
  const [interCitySelectedModes, setInterCitySelectedModes] = useState({}); // User-selected override for inter-city travel mode: 'day3-inter-city' => 'train'
  const [interCityDirectionsTimes, setInterCityDirectionsTimes] = useState({}); // Google Directions times for inter-city pairs: 'rome|florence|driving' => { duration: '3 hr 20 min', durationMinutes: 200 }
  const [showHotelOptions, setShowHotelOptions] = useState(false);
  const [hotelOptions, setHotelOptions] = useState([]);
  
  // NEW: Calculated drive time from home to destination (from Google Maps API)
  const [calculatedDriveTime, setCalculatedDriveTime] = useState(null);
  const [isCalculatingDriveTime, setIsCalculatingDriveTime] = useState(false);

  // Centralized travel timing — single source of truth for arrival/departure times
  // Eliminates duplicated, inconsistent timing logic across batch/per-day/timeline paths
  const getTravelTimingPrefs = () => {
    const timing = {};

    // === Try per-leg timing first (from unified leg transport cards) ===
    const firstLegKey = legsList?.[0]?.key;
    const lastLegKey = legsList?.[legsList.length - 1]?.key;
    const firstLegData = firstLegKey ? legTransports[firstLegKey] : null;
    const lastLegData = lastLegKey ? legTransports[lastLegKey] : null;

    // === ARRIVAL (Day 1) ===
    if (firstLegData?.time) {
      // User set time directly on leg card
      timing.arrivalTime = firstLegData.time;
    } else if (transportationMode === 'fly') {
      timing.arrivalTime = arrivalFlight.time || '12:00';
    } else if (transportationMode === 'train' || transportationMode === 'ferry') {
      const deptTime = driveDetails.departureTime || '08:00';
      const [dh, dm] = deptTime.split(':').map(Number);
      const transitMin = quickTransportResult?.durationMinutes || 180;
      const arrMin = (dh * 60 + dm) + transitMin;
      timing.arrivalTime = `${String(Math.floor(arrMin / 60) % 24).padStart(2, '0')}:${String(arrMin % 60).padStart(2, '0')}`;
    } else if (transportationMode === 'custom') {
      timing.arrivalTime = arrivalFlight.time || '12:00';
    } else {
      const deptTime = driveDetails.departureTime || '06:00';
      const [dh, dm] = deptTime.split(':').map(Number);
      const driveHrs = calculatedDriveTime?.hours || selectedDestination?.driveHours || 4;
      const arrMin = (dh * 60 + dm) + Math.round(driveHrs * 60);
      timing.arrivalTime = `${String(Math.floor(arrMin / 60) % 24).padStart(2, '0')}:${String(arrMin % 60).padStart(2, '0')}`;
    }

    // === DEPARTURE (Last day) ===
    if (lastLegData?.time) {
      // User set time directly on leg card
      timing.departureTime = lastLegData.time;
      timing.departureAirport = lastLegData.mode === 'fly'
        ? (lastLegData.departureAirport?.code || userProfile.preferences.departureAirport || '')
        : '';
    } else if (effectiveReturnMode === 'fly') {
      timing.departureTime = departureFlight.time || '18:00';
      timing.departureAirport = userProfile.preferences.departureAirport || '';
    } else if (effectiveReturnMode === 'train' || effectiveReturnMode === 'ferry' || effectiveReturnMode === 'custom') {
      timing.departureTime = driveDetails.returnDepartureTime || '14:00';
      timing.departureAirport = '';
    } else {
      timing.departureTime = driveDetails.returnDepartureTime || '14:00';
      timing.departureAirport = '';
    }

    return timing;
  };

  // NEW: Track if flight/hotel details changed (to allow regeneration)
  const [flightDetailsChanged, setFlightDetailsChanged] = useState(false);
  const [hotelDetailsChanged, setHotelDetailsChanged] = useState(false);
  
  // NEW: State for tracking if we're in the personalization phase
  // NEW: Track when we need to check if personalization is complete
  const [shouldCheckPersonalization, setShouldCheckPersonalization] = useState(false);

  const [awaitingPersonalization, setAwaitingPersonalization] = useState(false);
  const [personalizationQuestionsAsked, setPersonalizationQuestionsAsked] = useState(false);
  const [showPersonalizationPanel, setShowPersonalizationPanel] = useState(false);
  
  // NEW: Chat widget state for itinerary modifications
  const [itineraryChatMessages, setItineraryChatMessages] = useState([]);
  const [itineraryChatInput, setItineraryChatInput] = useState('');
  const [showItineraryChat, setShowItineraryChat] = useState(false);
  
  // Scroll progress for itinerary timeline
  const [scrollProgress, setScrollProgress] = useState(0);
  const dayContentRef = useRef(null);
  useEffect(() => {
    const handleScroll = () => {
      const el = dayContentRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const totalHeight = el.scrollHeight;
      const viewportH = window.innerHeight;
      // How far we've scrolled through the element (0 when top enters view, 1 when bottom leaves)
      const scrolled = -rect.top;
      const scrollable = totalHeight - viewportH;
      if (scrollable <= 0) { setScrollProgress(0); return; }
      const pct = Math.min(1, Math.max(0, scrolled / scrollable));
      setScrollProgress(pct);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // NEW: Expandable cards state
  const [expandedCards, setExpandedCards] = useState({}); // Format: 'day0-activity-0' => true
  const [overflowMenuOpen, setOverflowMenuOpen] = useState(null); // Card ID whose overflow menu is open
  const [inlineTimeEdit, setInlineTimeEdit] = useState(null); // { day, index, isActivity, currentTime }
  const [cardActiveTab, setCardActiveTab] = useState({}); // Format: 'day0-activity-0' => 'overview'
  const [roadTripStops, setRoadTripStops] = useState({}); // Format: 'day0-road-trip-0' => { loading, data, error }
  const [flightPrices, setFlightPrices] = useState({}); // Format: 'JFK-CDG-ECONOMY' => { loading, data: {price,airline,...}, error, fallbackUsed }
  const [flightCabinClass, setFlightCabinClass] = useState({}); // Per-route cabin override: 'JFK-CDG' => 'BUSINESS'
  const [flightAirlineFilter, setFlightAirlineFilter] = useState({}); // Per-route airline filter: 'JFK-CDG' => 'DL'
  const [flightStopsFilter, setFlightStopsFilter] = useState({}); // Per-route stops filter: 'JFK-CDG' => 0 (nonstop) | 1 | null (any)
  const [flightTimeFilter, setFlightTimeFilter] = useState({}); // Per-route departure time filter: 'JFK-CDG' => 'morning' | 'afternoon' | 'evening' | null (any)
  const [hotelPrices, setHotelPrices] = useState({}); // Format: 'cityCode-date' => { loading, data: [{name,price,...}], error }
  const [activityBookingInfo, setActivityBookingInfo] = useState({}); // Format: 'activity-name' => { loading, data, error }
  const [venueInfo, setVenueInfo] = useState({}); // Format: 'slugified-name:city' => { loading, data: { googleReviews, webMentions, costData }, error }

  // Place Detail modal state (Task 14) — opened from activity/meal cards and insider-tip links
  const [placeDetailModal, setPlaceDetailModal] = useState(null); // { placeName, city, country, placeCategory, placeData, item } | null
  const [quickRateOpen, setQuickRateOpen] = useState(false);
  const openPlaceDetail = useCallback((payload) => {
    if (!payload?.placeName) return;
    setPlaceDetailModal(payload);
  }, []);
  const closePlaceDetail = useCallback(() => setPlaceDetailModal(null), []);

  // NEW: Feedback system
  const [activityFeedback, setActivityFeedback] = useState({}); // Format: 'day0-activity-0' => 'up' or 'down'
  const [mealFeedback, setMealFeedback] = useState({}); // Format: 'day0-meal-0' => 'up' or 'down'
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [feedbackDialogData, setFeedbackDialogData] = useState(null); // { type, day, index, name }
  const [feedbackReason, setFeedbackReason] = useState('');
  const [feedbackReasons, setFeedbackReasons] = useState({}); // Store reasons: 'day0-activity-0' => 'Too expensive'
  const [selectedFeedbackOption, setSelectedFeedbackOption] = useState(null); // 'replace_one', 'replace_all', 'not_full_meal'
  const [showFeedbackBanner, setShowFeedbackBanner] = useState(false);
  const [feedbackCount, setFeedbackCount] = useState({ thumbsDown: 0, thumbsUp: 0 });
  
  // NEW: Track user edits for AI regeneration
  const [userEdits, setUserEdits] = useState([]);
  const [isModifyingItinerary, setIsModifyingItinerary] = useState(false);

  // Pending action tracking — stores a proposed action that awaits user confirmation
  // e.g. { type: 'modify_itinerary', description: 'Replace museum with cooking class on Day 3',
  //        handler: async () => { ... }, createdAt: Date.now() }
  const [pendingAction, setPendingAction] = useState(null);

  // Modification comparison view state
  const [pendingChanges, setPendingChanges] = useState(null); // { changes, message, originalItinerary }
  const [pendingChangeStatuses, setPendingChangeStatuses] = useState({}); // { 0: 'accepted', 1: 'rejected', ... }

  // Day rating state
  const [dayRatings, setDayRatings] = useState({}); // Format: { 0: 'love', 1: 'tweaks', 2: 'redo' }
  const [redoingDay, setRedoingDay] = useState(null); // Day index currently being regenerated via redo
  const [showGanttView, setShowGanttView] = useState(false);
  const [showMapView, setShowMapView] = useState(false);
  const [mapSelectedCardId, setMapSelectedCardId] = useState(null); // For map <-> card click sync
  const [weatherFetchTrigger, setWeatherFetchTrigger] = useState(0); // Increment to re-trigger weather fetch

  // NEW: Enhanced features state
  const [viewMode, setViewMode] = useState('cards'); // 'cards' (Things 3 style) or 'timeline' (gantt)
  const [tripPace, setTripPace] = useState('balanced'); // 'relaxed', 'balanced', 'packed'
  const [companions, setCompanions] = useState([]); // ['kids', 'elderly', 'pets', etc.]
  const [naturalLanguageInput, setNaturalLanguageInput] = useState('');
  const [smartSuggestions, setSmartSuggestions] = useState([]);
  const [showPaceModal, setShowPaceModal] = useState(false);
  const [showCompanionModal, setShowCompanionModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('pdf'); // 'pdf', 'calendar', or 'share'
  const [pdfIncludeDetails, setPdfIncludeDetails] = useState(true);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false); // Overflow menu in header
  const [keptActivities, setKeptActivities] = useState({}); // Format: 'day0-activity-2' => true
  // Legacy alias for compatibility
  const showPdfExportModal = showExportModal;
  const setShowPdfExportModal = setShowExportModal;
  // Activity type categorization
  const activityTypes = {
    'culture': { 
      color: 'purple', 
      icon: '🏛️', 
      bg: 'bg-purple-100', 
      border: 'border-purple-500',
      text: 'text-purple-700'
    },
    'adventure': { 
      color: 'red', 
      icon: '🏔️', 
      bg: 'bg-red-100', 
      border: 'border-red-500',
      text: 'text-red-700'
    },
    'food': { 
      color: 'orange', 
      icon: '🍴', 
      bg: 'bg-orange-100', 
      border: 'border-orange-500',
      text: 'text-orange-700'
    },
    'relaxation': { 
      color: 'blue', 
      icon: '🧘', 
      bg: 'bg-blue-100', 
      border: 'border-blue-500',
      text: 'text-blue-700'
    },
    'shopping': { 
      color: 'green', 
      icon: '🛍️', 
      bg: 'bg-green-100', 
      border: 'border-green-500',
      text: 'text-green-700'
    },
    'nature': { 
      color: 'teal', 
      icon: '🌳', 
      bg: 'bg-teal-100', 
      border: 'border-teal-500',
      text: 'text-teal-700'
    },
    'nightlife': {
      color: 'indigo',
      icon: '🌙',
      bg: 'bg-indigo-100',
      border: 'border-indigo-500',
      text: 'text-indigo-700'
    },
    'art': {
      color: 'pink',
      icon: '🎨',
      bg: 'bg-pink-100',
      border: 'border-pink-500',
      text: 'text-pink-700'
    }
  };
  
  // Helper functions - defined early so they can be used everywhere
  // parseTime now uses shared parseTimeToMinutes from @wander/shared/utils/timeUtils
  const parseTime = parseTimeToMinutes;

  // findTransitData now uses shared version from @wander/shared/utils/timeUtils
  const findTransitData = findTransitDataShared;

  // Helper: Fetch destination transport info (car-free status, nearest airports)
  // Used to provide intelligent transport recommendations for destinations like Zermatt
  const fetchDestinationTransportInfo = async (destinationName) => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
      const response = await fetch(`${BACKEND_URL}/api/destination-transport-info`, {
        method: 'POST',
        headers: await getApiHeaders(),
        body: JSON.stringify({ destination: destinationName })
      });

      if (!response.ok) {
        debugLog(`⚠️ Failed to fetch transport info for ${destinationName}`);
        return null;
      }

      const data = await response.json();
      debugLog(`🚂 Transport info for ${destinationName}:`, data);
      return data;
    } catch (error) {
      debugError(`Error fetching transport info for ${destinationName}:`, error);
      return null;
    }
  };

  // minutesToTime now uses shared version from @wander/shared/utils/timeUtils
  const minutesToTime = minutesToTimeShared;

  // calculateEndTime now uses shared version from @wander/shared/utils/timeUtils
  const calculateEndTime = calculateEndTimeShared;

  // doesCrossMidnight and minutesBetween now use shared versions from @wander/shared/utils/timeUtils
  const doesCrossMidnight = doesCrossMidnightShared;
  const minutesBetween = minutesBetweenShared;

  // formatTimeDisplay now uses shared formatTime12 from @wander/shared/utils/timeUtils
  const formatTimeDisplay = (time24, dayOffset = 0) => formatTime12(time24, { dayOffset });
  
  // Format time with AM/PM on separate line for itinerary display
  // Set isNextDay to true for times that rolled into the next day (after midnight)
  const formatTimeMultiLine = (time24, isNextDay = false) => {
    if (!time24) return { time: '', period: '', isNextDay: false };
    
    let hours24, minutes;
    
    const match12h = time24.match(/(\d+):(\d+)\s*(AM|PM)/i);
    if (match12h) {
      hours24 = parseInt(match12h[1]);
      minutes = parseInt(match12h[2]);
      const period = match12h[3].toUpperCase();
      if (period === 'PM' && hours24 !== 12) hours24 += 12;
      if (period === 'AM' && hours24 === 12) hours24 = 0;
    } else {
      const match24h = time24.match(/(\d+):(\d+)/);
      if (match24h) {
        hours24 = parseInt(match24h[1]);
        minutes = parseInt(match24h[2]);
      } else {
        return { time: time24, period: '', isNextDay: false };
      }
    }
    
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    
    return {
      time: `${hours12}:${String(minutes).padStart(2, '0')}`,
      period: period,
      isNextDay: isNextDay
    };
  };
  
  const [optimizingRoute, setOptimizingRoute] = useState(false);

  // Generate Google Maps URL with multiple waypoints
  const generateMapWithRoute = (day) => {
    if (!day.schedule && !day.meals) return null;
    
    const locations = [];
    
    // Collect all locations
    if (day.schedule) {
      day.schedule.forEach(act => {
        if (act.location) {
          locations.push({
            name: act.location,
            time: act.time,
            type: 'activity',
            activity: act.activity
          });
        }
      });
    }
    
    if (day.meals) {
      day.meals.forEach(meal => {
        if (meal.restaurant) {
          locations.push({
            name: meal.restaurant,
            time: meal.time,
            type: 'meal',
            activity: meal.restaurant
          });
        }
      });
    }
    
    // Sort by time
    locations.sort((a, b) => parseTime(a.time) - parseTime(b.time));
    
    if (locations.length === 0) return null;
    
    // Build Google Maps URL with waypoints
    const origin = encodeURIComponent(`${locations[0].name}, ${itinerary.destination}`);
    const destination = encodeURIComponent(`${locations[locations.length - 1].name}, ${itinerary.destination}`);
    
    let waypointsParam = '';
    if (locations.length > 2) {
      const waypoints = locations.slice(1, -1).map(loc => 
        encodeURIComponent(`${loc.name}, ${itinerary.destination}`)
      ).join('|');
      waypointsParam = `&waypoints=${waypoints}`;
    }
    
    return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}${waypointsParam}&travelmode=driving`;
  };
  
  const travelFacts = [
    "The world's shortest commercial flight lasts just 90 seconds between two Scottish islands!",
    "The Eiffel Tower can be 15cm taller during summer due to thermal expansion.",
    "There are more than 700 islands in the Bahamas, but only 30 are inhabited.",
    "You can visit four countries in one day in Europe by starting in Slovakia.",
    "Venice has more than 400 bridges connecting its 118 islands.",
    "Pizza Margherita was created to honor Queen Margherita of Italy in 1889.",
    "Japan's bullet trains are so punctual, the average delay is just 54 seconds.",
    "Australia is wider than the moon - it's 4,000km wide while the moon is 3,400km.",
    "The Louvre would take 100 days to see everything if you spent 30 seconds at each piece.",
    "Mount Everest grows about 4mm every year due to geological forces.",
    "Iceland has no mosquitoes - one of the few places on Earth without them!",
    "The Great Wall of China is not visible from space with the naked eye.",
    "Monaco is smaller than Central Park in New York City.",
    "There's a water slide in a German airport that passengers can use between terminals.",
    "The shortest international bridge connects Spain and Portugal - just 10.5 feet long!",
    "In Japan, there are more than 5.5 million vending machines - one for every 23 people.",
    "The Maldives is the flattest country on Earth, averaging just 1.5 meters above sea level.",
    "Finland has more saunas than cars - about 3.3 million saunas for 5.5 million people.",
    "The Amazon River has no bridges crossing it.",
    "Vatican City has the highest crime rate per capita... mostly pickpocketing tourists!",
    "Norway's coastline would stretch around the world twice if straightened out.",
    "In Switzerland, it's illegal to own just one guinea pig - they need companions!",
    "The oldest hotel in the world has been operating in Japan since 705 AD.",
    "Canada has more lakes than all other countries combined.",
    "The Dead Sea is so salty you can read a book while floating in it."
  ];
  
  // Shuffle facts to avoid repetition
  const [shuffledFacts] = useState(() => {
    const shuffled = [...travelFacts];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  });

  const [userProfile, setUserProfile] = useState(() => {
    // Users must be logged in - preferences come from AuthContext profile
    // Default values here are just fallbacks until profile loads
    return {
      preferences: {
        budget: null,
        duration: null,
        dates: null,
        startDate: null,
        regions: [],
        activities: [],
        vibes: [],
        travelStyle: null,
        travelCompanions: null,
        mustHaves: [],
        mustNotHaves: [],
        size: null,
        homeLocation: null,
        homeCity: "", // Empty until user sets departure city or profile loads
        returnCity: null, // null means "same as departure" (round trip)
        currency: "USD",
        maxFlightHours: null,
        dietaryRestrictions: [],
        cuisinePreferences: [],
        activityLevel: null,
        preferredPace: null,
        travelMonths: [], // For flexible month selection
        travelPeriod: null, // For backward compatibility
        companions: [], // Traveling with: kids, elderly, pets, etc.
        tripPace: 'balanced', // relaxed, balanced, packed
        hotelBudget: null, // budget, midrange, luxury - set from chat or profile
        // Profile preferences (from Settings > Travel)
        interests: [], // e.g., Museums, Hiking, Beaches
        accessibilityNeeds: [], // e.g., Wheelchair accessible, Limited mobility
        safetyConsiderations: [], // e.g., lgbtq, solo_female
        safetyNotes: '', // Free-text safety notes
        travelNotes: '', // Free-text travel notes ("I prefer morning activities...")
        mustDoActivities: [], // [{ text, timeConstraint?, dayConstraint? }]
        preferredEndTime: null, // When user wants to end their day (e.g., '20:00')
        planningStyle: 'structured', // 'structured' (fixed schedule) or 'flexible' (suggestions with swap/keep/remove)
        travelCompanions: '', // solo, couple, family, friends
        // Dream / loved / avoid (from Settings & Personalization)
        dreamDestinations: [], // Places user dreams of visiting
        lovedPlaces: [], // Places user has loved in the past
        avoidPlaces: [], // Places or things user wants to avoid
        leastFavoriteDestinations: [], // Places user does NOT enjoy
        // User feedback preferences (liked/disliked from chat)
        likedActivities: [], // Activities user explicitly liked
        dislikedActivities: [], // Activities user explicitly disliked
        likedCuisines: [], // Cuisines user prefers
        dislikedCuisines: [], // Cuisines user avoids
        preferredNeighborhoods: [], // Neighborhoods user likes
      },
      conversationHistory: [],
      rejectedDestinations: [],
      confidence: {
        budget: 0,
        duration: 0,
        vibe: 0,
        activities: 0,
        dietary: 0
      }
    };
  });

  // Flexible mode derived from planningStyle preference (set in trip builder)
  const flexibleMode = userProfile?.preferences?.planningStyle === 'flexible';

  // ─── Day 0 (Travel Day) is ONLY triggered when user selects a specific flight ───
  // Overnight detection happens in the Amadeus flight selection handler (search for setHasOvernightOutbound)
  // which compares actual departure/arrival dates. No heuristic inference — zero false positives.

  // Sync profile preferences from AuthContext to userProfile.
  // Reads from canonical `profile.preferences` JSONB (replaces the dropped
  // snake_case columns). Field mapping: legacy `travel_pace` → canonical `pace`,
  // `default_budget` → `budget`, `dietary_restrictions` → `dietary`, etc.
  // Pre-populates the itinerary personalization panel with user's saved prefs.
  useEffect(() => {
    if (profile) {
      const prefs = profile.preferences || {};
      // Only overwrite homeCity if the Supabase value actually CHANGED (e.g., user saved in Settings).
      // Otherwise, keep the local value (user may have changed it via sidebar or chat).
      const supabaseHomeCity = prefs.homeCity || '';
      const homeCityChangedInSupabase = supabaseHomeCity !== lastSyncedHomeCityRef.current;
      lastSyncedHomeCityRef.current = supabaseHomeCity;

      setUserProfile(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          homeCity: homeCityChangedInSupabase
            ? (supabaseHomeCity || prev.preferences.homeCity)
            : prev.preferences.homeCity,
          dietaryRestrictions: prefs.dietary?.length > 0
            ? prefs.dietary
            : prev.preferences.dietaryRestrictions,
          // Canonical pace
          preferredPace: prefs.pace || prev.preferences.preferredPace,
          activityLevel: prefs.pace === 'packed' ? 'high-energy'
            : prefs.pace === 'relaxed' ? 'relaxed'
            : prefs.pace === 'moderate' ? 'moderate'
            : prev.preferences.activityLevel,
          budget: prefs.budget || prev.preferences.budget,
          hotelBudget: prefs.budget || prev.preferences.hotelBudget,
          currency: prefs.currency || prev.preferences.currency || 'USD',
          interests: prefs.interests?.length > 0
            ? (Array.isArray(prefs.interests) ? prefs.interests : [])
            : prev.preferences.interests,
          accessibilityNeeds: prefs.accessibilityNeeds?.length > 0
            ? (Array.isArray(prefs.accessibilityNeeds) ? prefs.accessibilityNeeds : [])
            : prev.preferences.accessibilityNeeds,
          safetyConsiderations: (() => {
            let sc = prefs.safetyConsiderations;
            if (typeof sc === 'string') { try { sc = JSON.parse(sc); } catch {} }
            return Array.isArray(sc) && sc.length > 0 ? sc : prev.preferences.safetyConsiderations;
          })(),
          safetyNotes: prefs.safetyConsiderations || prev.preferences.safetyNotes,
          travelNotes: prefs.travelNotes || prev.preferences.travelNotes,
          // Canonical companions array (multi-select)
          companions: coerceCompanionsToArray(prefs.companions ?? prev.preferences.companions),
          // Legacy display string used by fetchActivityDetails/fetchFreeTimeSuggestions
          // and chat extractors. Derived from canonical array on every sync.
          travelCompanions: prefs.companions
            ? companionsToPromptString(prefs.companions, '')
            : prev.preferences.travelCompanions,
          // Accumulated feedback from past trips (for AI learning)
          likedActivities: prefs.likedActivities?.length > 0
            ? prefs.likedActivities : prev.preferences.likedActivities,
          dislikedActivities: prefs.dislikedActivities?.length > 0
            ? prefs.dislikedActivities : prev.preferences.dislikedActivities,
          likedCuisines: prefs.likedCuisines?.length > 0
            ? prefs.likedCuisines : prev.preferences.likedCuisines,
          dislikedCuisines: prefs.dislikedCuisines?.length > 0
            ? prefs.dislikedCuisines : prev.preferences.dislikedCuisines,
          preferredNeighborhoods: prefs.preferredNeighborhoods?.length > 0
            ? prefs.preferredNeighborhoods : prev.preferences.preferredNeighborhoods,
          // Dream / loved / avoid destinations (consolidated to likedPlaces / avoidPlaces in canonical)
          dreamDestinations: prefs.likedPlaces?.length > 0
            ? prefs.likedPlaces : prev.preferences.dreamDestinations,
          lovedPlaces: prefs.likedPlaces?.length > 0
            ? prefs.likedPlaces : prev.preferences.lovedPlaces,
          avoidPlaces: prefs.avoidPlaces?.length > 0
            ? prefs.avoidPlaces : prev.preferences.avoidPlaces,
          leastFavoriteDestinations: prefs.avoidPlaces?.length > 0
            ? prefs.avoidPlaces : prev.preferences.leastFavoriteDestinations,
        }
      }));
    }
  }, [profile]);

  // Fetch weather for personalization sidebar when destination + date/month changes
  // Supports multi-city: fetches weather for each stop in parallel
  useEffect(() => {
    const isMulti = selectedDestination?.isMultiStop && selectedDestination?.stopNames?.length > 1;
    // For resort stops, use resolvedLocation (the actual city) instead of hotel name for geocoding
    const resolveCity = (stopName, idx) => {
      const stop = selectedDestination?.stops?.[idx];
      if ((stop?.stopType === 'hotel' || stop?.stopType === 'resort') && stop.resolvedLocation) return stop.resolvedLocation;
      return stopName;
    };
    const cities = isMulti
      ? selectedDestination.stopNames.map((n, i) => resolveCity(n, i))
      : selectedDestination?.name ? [resolveCity(selectedDestination.name, 0)] : [];

    if (cities.length === 0) { setPersonalizationWeather(null); return; }

    const dateStr = userProfile.preferences.startDate;
    const travelMonths = userProfile.preferences.travelMonths || [];
    const monthForClimate = travelMonths[0] || '';
    if (!dateStr && !monthForClimate) { setPersonalizationWeather(null); return; }

    const fetchKey = cities.join('|') + '|' + (dateStr || monthForClimate);
    if (personalizationWeatherRef.current === fetchKey) return;
    personalizationWeatherRef.current = fetchKey;

    // Determine date to query
    let queryDate = dateStr;
    if (!queryDate && monthForClimate) {
      const monthIndex = ['January','February','March','April','May','June','July','August','September','October','November','December'].indexOf(monthForClimate);
      if (monthIndex >= 0) {
        const now = new Date();
        let year = now.getFullYear();
        if (monthIndex < now.getMonth()) year++;
        queryDate = `${year}-${String(monthIndex + 1).padStart(2, '0')}-15`;
      }
    }
    if (!queryDate) return;

    const controller = new AbortController();
    const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';

    // Geocode helper with client-side cache
    // Strips zip/postal codes and numeric prefixes that geocoding APIs can't resolve
    const cleanCityForGeocode = (raw) => {
      let name = raw.split(',')[0].trim();
      // Strip leading postal/zip codes like "77400 Quintana Roo" → "Quintana Roo"
      name = name.replace(/^\d{3,6}\s+/, '');
      // If result is a state/region name, try the country part too
      return name;
    };
    const geocodeCity = async (city) => {
      const cleanName = cleanCityForGeocode(city);
      const cacheKey = cleanName.toLowerCase();
      if (geocodeCacheRef.current[cacheKey]) return geocodeCacheRef.current[cacheKey];
      // Try clean name first, fall back to original if different
      const tryGeocode = async (name) => {
        const geoRes = await fetch(
          `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(name)}&count=1&language=en`,
          { signal: controller.signal }
        );
        if (!geoRes.ok) return null;
        const geoData = await geoRes.json();
        return geoData.results?.length ? { lat: geoData.results[0].latitude, lng: geoData.results[0].longitude } : null;
      };
      let coords = await tryGeocode(cleanName);
      // If cleaned name failed and original was different, try the full city string
      if (!coords && cleanName !== city.split(',')[0].trim()) {
        coords = await tryGeocode(city.split(',')[0].trim());
      }
      // Last resort: try the country/region part after the comma
      if (!coords && city.includes(',')) {
        coords = await tryGeocode(city.split(',').slice(1).join(',').trim());
      }
      // Ultimate fallback: Google Places via backend. Open-Meteo's geocoder
      // is city-focused — resort/hotel-named stops (e.g. "Hermitage Bay",
      // "The Somerset on Grace Bay") return no results. Places handles them.
      if (!coords) {
        try {
          coords = await _placesFallbackCoords(city, { signal: controller.signal });
        } catch { /* non-blocking */ }
      }
      if (coords) geocodeCacheRef.current[cacheKey] = coords;
      return coords;
    };

    (async () => {
      try {
        // Fetch weather for all cities in parallel
        const results = await Promise.all(cities.map(async (city) => {
          const coords = await geocodeCity(city);
          if (!coords) return null;
          // Serve persisted climatology for any cached (lat, lng, month) —
          // eliminates the backend round-trip for trip-planning dates.
          const cachedClimo = readClimoCache(coords.lat, coords.lng, queryDate);
          if (cachedClimo) {
            return { city: city.split(',')[0].trim(), ...cachedClimo };
          }
          const authHeaders = await getApiHeaders();
          const weatherRes = await fetch(
            `${API_BASE}/api/weather?lat=${coords.lat}&lng=${coords.lng}&date=${queryDate}`,
            { signal: controller.signal, headers: authHeaders }
          );
          if (!weatherRes.ok) return null;
          const weather = await weatherRes.json();
          writeClimoCache(coords.lat, coords.lng, queryDate, weather);
          return { city: city.split(',')[0].trim(), ...weather };
        }));

        const valid = results.filter(Boolean);
        if (valid.length === 0) return;
        // Single city: return weather object directly. Multi: return array.
        setPersonalizationWeather(valid.length === 1 ? valid[0] : valid);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('Personalization weather fetch failed:', err.message);
        }
      }
    })();
    return () => controller.abort();
  }, [selectedDestination, userProfile.preferences.startDate, userProfile.preferences.travelMonths]);

  // ─── Pre-fetch transport for each leg when stops change ──────────────────
  useEffect(() => {
    if (tripBuilderStops.length < 1) return;
    const fetchLeg = async (fromCity, toCity, legKey) => {
      // Skip if already loaded and not stale
      if (legTransports[legKey] && !legTransports[legKey].isLoading && legTransports[legKey].source) return;
      setLegTransports(prev => ({ ...prev, [legKey]: { ...prev[legKey], isLoading: true } }));
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/quick-transport`, {
          method: 'POST',
          headers: await getApiHeaders(),
          body: JSON.stringify({ fromCity, toCity }),
        });
        const data = await resp.json();
        if (data.success) {
          const modeMap = { flight: 'fly', car: 'drive', train: 'train', ferry: 'ferry', bus: 'bus' };
          const mode = modeMap[data.mode] || data.mode || 'fly';
          // Static lookup first (instant), then dynamic API fallback for unknown cities
          let depAirport = mode === 'fly' ? lookupCityAirports(fromCity) : null;
          let arrAirport = mode === 'fly' ? lookupCityAirports(toCity) : null;
          // Set initial state immediately (so UI shows mode/duration while airports load)
          setLegTransports(prev => ({
            ...prev,
            [legKey]: {
              mode, duration: data.duration, durationMinutes: data.durationMinutes,
              reason: data.reason, departureAirport: depAirport, arrivalAirport: arrAirport,
              driveAvailable: data.driveAvailable !== false,
              recommendedMode: mode,
              isUserOverride: prev[legKey]?.isUserOverride || false,
              isLoading: false, source: prev[legKey]?.isUserOverride ? 'user' : 'auto',
            },
          }));
          // Dynamic airport lookup for cities not in static map
          if (mode === 'fly' && (!depAirport || !arrAirport)) {
            const fetchDynAirport = async (city) => {
              try {
                const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/city-airports`, {
                  method: 'POST', headers: await getApiHeaders(), body: JSON.stringify({ city }),
                });
                const d = await r.json();
                if (d.success && d.airports?.length > 0) {
                  const primary = d.airports[0];
                  return { code: primary.code, name: primary.name, alternatives: d.airports.slice(1), isMultiAirport: d.airports.length > 1 };
                }
              } catch (e) { console.warn(`[DynAirport] Failed for ${city}:`, e.message); }
              return null;
            };
            const [dynDep, dynArr] = await Promise.all([
              !depAirport ? fetchDynAirport(fromCity) : Promise.resolve(depAirport),
              !arrAirport ? fetchDynAirport(toCity) : Promise.resolve(arrAirport),
            ]);
            if (dynDep || dynArr) {
              setLegTransports(prev => ({
                ...prev,
                [legKey]: {
                  ...prev[legKey],
                  departureAirport: dynDep || prev[legKey]?.departureAirport,
                  arrivalAirport: dynArr || prev[legKey]?.arrivalAirport,
                },
              }));
            }
          }
        }
      } catch (err) {
        console.warn(`[TransportPrefetch] Failed for ${legKey}:`, err.message);
        setLegTransports(prev => ({ ...prev, [legKey]: { ...prev[legKey], isLoading: false } }));
      }
    };

    const deptCity = userProfile?.preferences?.homeCity || '';
    // Leg: departure → first stop
    if (deptCity && tripBuilderStops[0]) {
      const toName = tripBuilderStops[0].resolvedLocation || tripBuilderStops[0].name;
      const key = `dept→${tripBuilderStops[0].id}`;
      if (!legTransports[key]?.isUserOverride) fetchLeg(deptCity, toName, key);
    }
    // Legs between stops
    for (let i = 0; i < tripBuilderStops.length - 1; i++) {
      const from = tripBuilderStops[i].resolvedLocation || tripBuilderStops[i].name;
      const to = tripBuilderStops[i + 1].resolvedLocation || tripBuilderStops[i + 1].name;
      const key = `${tripBuilderStops[i].id}→${tripBuilderStops[i + 1].id}`;
      if (!legTransports[key]?.isUserOverride) fetchLeg(from, to, key);
    }
    // Return leg: last stop → returnCity (or departure city)
    if (deptCity && tripBuilderStops.length > 0) {
      const lastStop = tripBuilderStops[tripBuilderStops.length - 1];
      const fromName = lastStop.resolvedLocation || lastStop.name;
      const returnDest = userProfile?.preferences?.returnCity || deptCity;
      const key = `${lastStop.id}→return`;
      if (!legTransports[key]?.isUserOverride) fetchLeg(fromName, returnDest, key);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripBuilderStops.length, tripBuilderStops.map(s => s.id).join(','), userProfile?.preferences?.homeCity]);

  // Ordered list of travel legs: departure→firstStop, inter-city legs, lastStop→return
  // Used by per-leg transport cards on the personalization page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const legsList = useMemo(() => {
    if (!tripBuilderStops.length) return [];
    const legs = [];
    const homeCity = userProfile?.preferences?.homeCity || '';

    // D8: Resolve home city label — trip-specific departure city takes priority over permanent profile airports.
    // The user explicitly sets homeCity per-trip (e.g., "London"), so that should be the label.
    // Only fall back to profile airport lookup when no trip-specific city is set.
    const homeCityTrimmed = homeCity.split(',')[0].trim();
    let homeCityLabel = homeCityTrimmed;
    if (!homeCityTrimmed) {
      // No trip-specific city — fall back to permanent profile airport reverse lookup
      const homeAirportCodeForLegs = (() => {
        const raw = profile?.home_airports;
        const list = Array.isArray(raw) ? raw : typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return []; } })() : [];
        return list[0]?.code?.toUpperCase() || '';
      })();
      homeCityLabel = reverseLookupAirportCity(homeAirportCodeForLegs) || '';
    }

    // For hotel stops, prefer the resort NAME for airport lookup. Curated
    // travel_reference rows are keyed on the resort name (`soneva jani` →
    // MLE), and getHotelLocation often resolves a hotel to a sub-region
    // ("Noonu Atoll, Maldives", "Kahuku, Hawaii") that falls into the
    // airports-table fuzzy match before reaching travel_reference and
    // returns a wrong-but-plausible nearby airport (Soneva Jani →
    // Noonu Atoll → GAN at the south of the country, instead of MLE).
    // For destination stops, the resolvedLocation is usually a cleaner
    // form of the city name and stays preferred.
    const lookupKey = (stop) => {
      const isHotel = stop?.stopType === 'hotel' || stop?.stopType === 'resort';
      return isHotel
        ? (stop.name || stop.resolvedLocation || '')
        : (stop.resolvedLocation || stop.name || '');
    };

    // Leg 0: departure → first stop
    if (homeCity && tripBuilderStops[0]) {
      legs.push({
        key: `dept→${tripBuilderStops[0].id}`,
        from: homeCity,
        fromLabel: homeCityLabel,
        to: lookupKey(tripBuilderStops[0]),
        toLabel: (tripBuilderStops[0].name || '').split(',')[0].trim(),
        type: 'departure',
        isFirst: true,
        isLast: tripBuilderStops.length === 1,
      });
    }

    // Inter-city legs
    for (let i = 0; i < tripBuilderStops.length - 1; i++) {
      const isAlsoLast = i === tripBuilderStops.length - 2;
      legs.push({
        key: `${tripBuilderStops[i].id}→${tripBuilderStops[i + 1].id}`,
        from: lookupKey(tripBuilderStops[i]),
        fromLabel: (tripBuilderStops[i].name || '').split(',')[0].trim(),
        to: lookupKey(tripBuilderStops[i + 1]),
        toLabel: (tripBuilderStops[i + 1].name || '').split(',')[0].trim(),
        type: 'inter-city',
        isFirst: false,
        isLast: false,
      });
    }

    // Return leg: last stop → returnCity (or homeCity if same)
    if (homeCity && tripBuilderStops.length > 0) {
      const last = tripBuilderStops[tripBuilderStops.length - 1];
      const returnCity = userProfile?.preferences?.returnCity || homeCity;
      const returnCityTrimmed = returnCity.split(',')[0].trim();
      const returnCityLabel = returnCity !== homeCity ? returnCityTrimmed : homeCityLabel;
      legs.push({
        key: `${last.id}→return`,
        from: lookupKey(last),
        fromLabel: (last.name || '').split(',')[0].trim(),
        to: returnCity,
        toLabel: returnCityLabel,
        type: 'return',
        isFirst: false,
        isLast: true,
      });
    }

    return legs;
  }, [tripBuilderStops, userProfile?.preferences?.homeCity, userProfile?.preferences?.returnCity]);

  // === Per-Leg Transport Handlers ===

  // Change transport mode for a specific leg
  const handleLegModeChange = (legKey, newMode, leg) => {
    userHasSetTransportRef.current = true;
    setLegTransports(prev => {
      const current = prev[legKey] || {};
      const update = {
        ...current,
        mode: newMode,
        isUserOverride: true,
        source: 'user',
      };
      // If switching to fly, ensure airports are populated (static first, dynamic fallback)
      if (newMode === 'fly') {
        if (!update.departureAirport) {
          update.departureAirport = lookupCityAirports(leg.from);
        }
        if (!update.arrivalAirport) {
          update.arrivalAirport = lookupCityAirports(leg.to);
        }
        // Dynamic fallback for cities not in static map
        if (!update.departureAirport || !update.arrivalAirport) {
          const fetchDynAirport = async (city) => {
            try {
              const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/city-airports`, {
                method: 'POST', headers: await getApiHeaders(), body: JSON.stringify({ city }),
              });
              const d = await r.json();
              if (d.success && d.airports?.length > 0) {
                return { code: d.airports[0].code, name: d.airports[0].name, alternatives: d.airports.slice(1), isMultiAirport: d.airports.length > 1 };
              }
            } catch (e) { /* silent */ }
            return null;
          };
          const needDep = !update.departureAirport;
          const needArr = !update.arrivalAirport;
          Promise.all([
            needDep ? fetchDynAirport(leg.from) : Promise.resolve(null),
            needArr ? fetchDynAirport(leg.to) : Promise.resolve(null),
          ]).then(([dynDep, dynArr]) => {
            if (dynDep || dynArr) {
              setLegTransports(prev => ({
                ...prev,
                [legKey]: {
                  ...prev[legKey],
                  ...(dynDep ? { departureAirport: dynDep } : {}),
                  ...(dynArr ? { arrivalAirport: dynArr } : {}),
                },
              }));
            }
          });
        }
      }
      // If switching away from fly, clear flight search
      if (newMode !== 'fly') {
        update.selectedFlight = null;
        setLegFlightSearch(fs => {
          const copy = { ...fs };
          delete copy[legKey];
          return copy;
        });
      }
      return { ...prev, [legKey]: update };
    });
  };

  // Change airport for a specific leg — preserves alternatives so dropdown stays visible
  const handleLegAirportChange = (legKey, which, code) => {
    setLegTransports(prev => {
      const current = prev[legKey] || {};
      const field = which === 'departure' ? 'departureAirport' : 'arrivalAirport';
      const oldAirport = current[field] || {};

      // Rebuild alternatives: old primary + old alternatives, minus the newly selected one
      const allAirports = [
        { code: oldAirport.code, name: oldAirport.name },
        ...(oldAirport.alternatives || [])
      ].filter(a => a.code && a.code !== code);

      // Find the selected airport's name from the alternatives or old primary
      const selected = oldAirport.code === code
        ? oldAirport
        : (oldAirport.alternatives || []).find(a => a.code === code);

      const airportObj = {
        code,
        name: selected?.name || code,
        alternatives: allAirports,
        isMultiAirport: oldAirport.isMultiAirport,
      };

      return {
        ...prev,
        [legKey]: {
          ...current,
          [field]: airportObj,
          isUserOverride: true,
          source: 'user',
        },
      };
    });
  };

  // Change time for first/last leg
  const handleLegTimeChange = (legKey, time) => {
    setLegTransports(prev => ({
      ...prev,
      [legKey]: { ...(prev[legKey] || {}), time, isUserOverride: true, source: 'user' },
    }));
  };

  // Per-stop hotel handlers
  const handleStopHotelBudget = (stopId, budget) => {
    setStopHotels(prev => ({
      ...prev,
      [stopId]: { ...(prev[stopId] || {}), budget, isOverridden: true },
    }));
  };

  const handleStopPreferredHotel = (stopId, hotel) => {
    setStopHotels(prev => ({
      ...prev,
      [stopId]: { ...(prev[stopId] || {}), preferredHotel: hotel.replace(/[<>]/g, '').slice(0, 150), isOverridden: true },
    }));
  };

  const handleResetStopHotel = (stopId) => {
    setStopHotels(prev => {
      const next = { ...prev };
      delete next[stopId];
      return next;
    });
  };

  const updateStopHotelMode = (stopId, mode) => {
    setStopHotels(prev => ({
      ...prev,
      [stopId]: { ...(prev[stopId] || {}), mode, isOverridden: true },
    }));
  };

  const updateStopBookedHotel = (stopId, value) => {
    setStopHotels(prev => ({
      ...prev,
      [stopId]: { ...(prev[stopId] || {}), bookedHotel: value.replace(/[<>]/g, '').slice(0, 200), isOverridden: true },
    }));
  };

  // Search flights for a specific leg using Amadeus
  const searchLegFlights = async (legKey, leg) => {
    const legData = legTransports[legKey];
    if (!legData) return;

    const depAirport = legData.departureAirport?.code || lookupCityAirports(leg.from)?.code;
    const arrAirport = legData.arrivalAirport?.code || lookupCityAirports(leg.to)?.code;

    if (!depAirport || !arrAirport) {
      setLegFlightSearch(prev => ({
        ...prev,
        [legKey]: { loading: false, error: 'Airport codes required for flight search', results: null },
      }));
      return;
    }

    setLegFlightSearch(prev => ({
      ...prev,
      [legKey]: { loading: true, results: null, error: null, expanded: false },
    }));

    try {
      const { searchFlightsWithFallback } = await import('./lib/amadeusApi');
      const departureDate = new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0];
      const cabinClass = preferredTravelClass || profile?.preferred_cabin_class || 'ECONOMY';

      const result = await withTimeout(searchFlightsWithFallback({
        origin: depAirport,
        destination: arrAirport,
        departureDate,
        travelClass: cabinClass,
        maxResults: 5,
        currencyCode: profile?.currency || userProfile?.preferences?.currency || 'USD',
      }), 15000, 'Flight search timed out');

      const offers = (result.offers || []).sort((a, b) => (a.price || 0) - (b.price || 0));
      setLegFlightSearch(prev => ({
        ...prev,
        [legKey]: {
          loading: false,
          results: offers.slice(0, 5),
          dictionaries: result.dictionaries || {},
          error: offers.length === 0 ? 'No flights found' : null,
          expanded: true,
        },
      }));
    } catch (err) {
      console.error(`[LegFlightSearch] Failed for ${legKey}:`, err);
      setLegFlightSearch(prev => ({
        ...prev,
        [legKey]: { loading: false, results: null, error: err.message, expanded: false },
      }));
    }
  };

  // Select a flight offer for a leg
  const selectLegFlight = (legKey, offer, dictionaries = {}, leg) => {
    if (!offer?.itineraries?.[0]?.segments?.length) return;

    const itin = offer.itineraries[0];
    const segs = itin.segments;
    const firstSeg = segs[0];
    const lastSeg = segs[segs.length - 1];
    const carrierCodes = [...new Set(segs.map(s => s.carrier).filter(Boolean))];
    const carrierNames = carrierCodes.map(c => resolveAirlineName(c, dictionaries));
    const flightNums = segs.map(s => `${s.carrier || ''}${s.flightNumber || ''}`).filter(Boolean);
    const arrivalTime = parseAmadeusTime(lastSeg.arrival?.time);
    const departureTime = parseAmadeusTime(firstSeg.departure?.time);

    const selectedFlight = {
      flightNumber: flightNums[0] || '',
      airline: carrierNames.join(', ') || '',
      price: offer.price,
      currency: offer.currency,
      duration: itin.duration,
      departureTime,
      arrivalTime,
      from: firstSeg.departure?.airport || '',
      to: lastSeg.arrival?.airport || '',
      segments: segs,
      bookingUrl: offer.bookingUrl || null,
      _fetchedAt: Date.now(),
    };

    // Store on legTransports
    setLegTransports(prev => ({
      ...prev,
      [legKey]: {
        ...(prev[legKey] || {}),
        selectedFlight,
        isUserOverride: true,
        source: 'user',
      },
    }));

    // For first leg → also set arrivalFlight (backward compat with generation)
    if (leg?.isFirst) {
      setArrivalFlight({
        time: arrivalTime || arrivalFlight.time,
        flightNumber: flightNums[0] || '',
        airline: carrierNames.join(', ') || '',
        from: firstSeg.departure?.airport || '',
        date: parseAmadeusDate(firstSeg.departure?.time) || '',
      });
      // Also set the time on the leg
      if (arrivalTime) {
        handleLegTimeChange(legKey, arrivalTime);
      }

      // ─── Overnight flight detection (Day 0 Travel Day) ───
      const depDateStr = parseAmadeusDate(firstSeg.departure?.time);
      const arrDateStr = parseAmadeusDate(lastSeg.arrival?.time);
      const isOvernight = depDateStr && arrDateStr && arrDateStr > depDateStr;

      if (isOvernight) {
        setHasOvernightOutbound(true);
        setOvernightFlightInfo({
          departureTime: parseAmadeusTime(firstSeg.departure?.time),
          arrivalTime: parseAmadeusTime(lastSeg.arrival?.time),
          from: firstSeg.departure?.airport || '',
          to: lastSeg.arrival?.airport || '',
          airline: carrierNames.join(', '),
          flightNumber: flightNums[0] || '',
          departureDate: depDateStr,
          arrivalDate: arrDateStr,
          departureTerminal: firstSeg.departure?.terminal || '',
          arrivalTerminal: lastSeg.arrival?.terminal || '',
          duration: itin.duration,
          segments: segs,
        });
        showToast('✈️ Overnight flight detected — a Travel Day will be added before Day 1', 'info');
      } else {
        setHasOvernightOutbound(false);
        setOvernightFlightInfo(null);
      }

      // ─── Update Day 1's arrival logistics time to match selected flight ───
      if (arrivalTime && itinerary?.days?.[0]) {
        const day1 = { ...itinerary.days[0] };
        const schedule = [...(day1.schedule || [])];
        const arrivalIdx = schedule.findIndex(item =>
          item.type === 'arrival-customs' || item.isArrivalFlightCard ||
          (item.activity || '').toLowerCase().includes('arrival')
        );
        if (arrivalIdx >= 0) {
          schedule[arrivalIdx] = { ...schedule[arrivalIdx], time: arrivalTime };
          day1.schedule = schedule;
          const updatedDays = [...itinerary.days];
          updatedDays[0] = day1;
          setItinerary(prev => ({ ...prev, days: updatedDays }));
        }
      }
    }

    // For last leg → also set departureFlight (backward compat with generation)
    if (leg?.isLast) {
      setDepartureFlight({
        time: departureTime || departureFlight.time,
        flightNumber: flightNums[0] || '',
        airline: carrierNames.join(', ') || '',
        to: lastSeg.arrival?.airport || '',
        date: parseAmadeusDate(firstSeg.departure?.time) || '',
      });
      if (departureTime) {
        handleLegTimeChange(legKey, departureTime);
      }

      // ─── Update last day's departure logistics time to match selected flight ───
      if (departureTime && itinerary?.days?.length > 0) {
        const lastIdx = itinerary.days.length - 1;
        const lastDay = { ...itinerary.days[lastIdx] };
        const schedule = [...(lastDay.schedule || [])];
        const depIdx = schedule.findIndex(item =>
          item.type === 'departure-logistics' || item.isDepartureCard ||
          (item.activity || '').toLowerCase().includes('departure') ||
          (item.activity || '').toLowerCase().includes('airport')
        );
        if (depIdx >= 0) {
          schedule[depIdx] = { ...schedule[depIdx], time: departureTime };
          lastDay.schedule = schedule;
          const updatedDays = [...itinerary.days];
          updatedDays[lastIdx] = lastDay;
          setItinerary(prev => ({ ...prev, days: updatedDays }));
        }
      }
    }

    showToast(`Flight selected: ${carrierNames[0] || flightNums[0] || 'flight'} — $${offer.price}`, 'success');

    // ─── Regeneration prompt when flight times change significantly ───
    // If itinerary already exists and times differ by >2 hours, suggest regeneration
    if (itinerary?.days?.length > 0) {
      const oldArrTime = leg?.isFirst ? arrivalFlight?.time : null;
      const oldDepTime = leg?.isLast ? departureFlight?.time : null;
      const newTime = leg?.isFirst ? arrivalTime : (leg?.isLast ? departureTime : null);

      if (oldArrTime && newTime && leg?.isFirst) {
        const [oh, om] = oldArrTime.split(':').map(Number);
        const [nh, nm] = newTime.split(':').map(Number);
        const diffMins = Math.abs((nh * 60 + nm) - (oh * 60 + om));
        if (diffMins > 120) {
          // Significant time change — flag for regeneration and suggest
          setFlightDetailsChanged(true);
          setTimeout(() => {
            showToast(
              `⏰ Arrival time changed by ${Math.round(diffMins / 60)}+ hours. Consider regenerating Day 1 for a better schedule.`,
              'info',
              8000
            );
          }, 1500);
        }
      }
      if (oldDepTime && newTime && leg?.isLast) {
        const [oh, om] = oldDepTime.split(':').map(Number);
        const [nh, nm] = newTime.split(':').map(Number);
        const diffMins = Math.abs((nh * 60 + nm) - (oh * 60 + om));
        if (diffMins > 120) {
          setFlightDetailsChanged(true);
          setTimeout(() => {
            showToast(
              `⏰ Departure time changed by ${Math.round(diffMins / 60)}+ hours. Consider regenerating the last day for a better schedule.`,
              'info',
              8000
            );
          }, 1500);
        }
      }
    }

    // Collapse the search results
    setLegFlightSearch(prev => ({
      ...prev,
      [legKey]: { ...(prev[legKey] || {}), expanded: false },
    }));
  };

  // === Sync legTransports → legacy transport state for generation pipeline ===
  // The generation pipeline reads transportationMode, returnTransportMode, arrivalFlight, departureFlight.
  // This effect keeps them in sync when user changes per-leg transport cards.
  useEffect(() => {
    if (!legsList.length) return;

    // First leg → transportationMode + arrivalFlight.time
    const firstLeg = legsList[0];
    const firstLegData = legTransports[firstLeg?.key];
    if (firstLegData?.mode && firstLegData.source) {
      setTransportationMode(firstLegData.mode);
      if (firstLegData.time) {
        setArrivalFlight(prev => ({ ...prev, time: firstLegData.time }));
      }
      // Sync drive details for drive modes
      if (firstLegData.mode?.startsWith('drive') && firstLegData.time) {
        setDriveDetails(prev => ({ ...prev, departureTime: firstLegData.time }));
      }
    }

    // Last leg → returnTransportMode + departureFlight.time
    const lastLeg = legsList[legsList.length - 1];
    const lastLegData = legTransports[lastLeg?.key];
    if (lastLeg && lastLegData?.mode && lastLegData.source) {
      // Only set returnTransportMode if different from first leg
      if (lastLegData.mode !== firstLegData?.mode) {
        setReturnTransportMode(lastLegData.mode);
      } else {
        setReturnTransportMode(null);
      }
      if (lastLegData.time) {
        if (lastLegData.mode === 'fly') {
          setDepartureFlight(prev => ({ ...prev, time: lastLegData.time }));
        } else {
          setDriveDetails(prev => ({ ...prev, returnDepartureTime: lastLegData.time }));
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legTransports, legsList.length]);

  // === Sync stopHotels → global hotelBudget (if all stops share same budget) ===
  useEffect(() => {
    if (!tripBuilderStops.length) return;
    const globalBudget = userProfile?.preferences?.hotelBudget || 'midrange';
    const budgets = tripBuilderStops.map(s => stopHotels[s.id]?.budget || globalBudget);
    const allSame = budgets.every(b => b === budgets[0]);
    if (allSame && budgets[0] !== globalBudget) {
      setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, hotelBudget: budgets[0] } }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopHotels, tripBuilderStops.length]);

  // Comprehensive currency rates (approximate, would need live API for production)
  const currencyRates = {
    // Major currencies
    USD: 1, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.52, JPY: 149.5, CNY: 7.24, INR: 83.2,
    // Europe
    CHF: 0.88, SEK: 10.5, NOK: 10.8, DKK: 6.9, PLN: 4.0, CZK: 23.5, HUF: 360, RON: 4.6,
    // Asia Pacific
    SGD: 1.34, HKD: 7.82, TWD: 31.5, KRW: 1320, THB: 35.5, MYR: 4.7, IDR: 15500, PHP: 56, VND: 24500,
    // Middle East
    AED: 3.67, SAR: 3.75, ILS: 3.7, TRY: 32,
    // Americas
    MXN: 17.2, BRL: 4.95, ARS: 870, CLP: 950, COP: 4000, PEN: 3.75,
    // Africa
    ZAR: 18.5, EGP: 31, MAD: 10, KES: 155,
    // Other
    NZD: 1.65, RUB: 92
  };
  
  // Date utilities — imported from @wander/shared via ./utils/dateFormatting
  const parseLocalDate = _parseLocalDate;
  const calculateDaysBetween = _calculateDaysBetween;
  const formatDateForDisplay = _formatDateForDisplay;

  // PDF Export function - opens a printable page with timeline format
  const exportItineraryAsPDF = async (includeDetails = true) => {
    if (!itinerary || !selectedDestination) return;

    try {

    // Pre-fetch transit times for ALL days before generating PDF
    // The live UI only fetches for the currently-viewed day, so other days may have defaults
    // PERF: Collect all needed fetches first, then run in parallel with Promise.all
    const cityName = selectedDestination?.name || itinerary.destination || '';
    const addCityContextPdf = (location) => {
      if (!location) return location;
      if (location.includes(',') || location.toLowerCase().includes(cityName.toLowerCase())) return location;
      return `${location}, ${cityName}`;
    };

    const transitFetchPromises = [];

    for (let dayIdx = 0; dayIdx < (itinerary.days?.length || 0); dayIdx++) {
      const dayData = itinerary.days[dayIdx];
      if (!dayData) continue;

      // Collect locations in order (same logic as transit useEffect)
      const pdfItems = [];
      if (dayData.hotel?.name) {
        pdfItems.push({ location: addCityContextPdf(dayData.hotel.address || dayData.hotel.name), type: 'hotel', time: '00:00', name: dayData.hotel.name });
      }
      if (dayData.schedule) {
        dayData.schedule.forEach((item, idx) => {
          const loc = item.address || item.location || item.activity;
          if (loc) pdfItems.push({ location: addCityContextPdf(loc), type: 'activity', idx, time: item.time, name: item.activity });
        });
      }
      if (dayData.meals) {
        dayData.meals.forEach((item, idx) => {
          const loc = item.address || `${item.restaurant}, ${cityName}`;
          if (item.restaurant || item.address) pdfItems.push({ location: addCityContextPdf(loc), type: 'meal', idx, time: item.time, name: item.restaurant });
        });
      }
      pdfItems.sort((a, b) => parseTime(a.time) - parseTime(b.time));

      // Queue hotel-to-first if missing
      if (pdfItems.length > 1) {
        const hotelToFirstKey = `day${dayIdx}-hotel-to-first`;
        if (!transitTimesRef.current[hotelToFirstKey]) {
          const hotelCoords = dayData.hotel?.coordinates;
          const hotelLoc = hotelCoords?.lat
            ? `${hotelCoords.lat},${hotelCoords.lng}`
            : addCityContextPdf(dayData.hotel?.address || dayData.hotel?.name || 'Hotel');
          const firstItem = pdfItems[1];
          const firstLoc = firstItem?.placeData?.location?.lat
            ? `${firstItem.placeData.location.lat},${firstItem.placeData.location.lng}`
            : firstItem?.location;
          if (firstLoc && firstLoc !== hotelLoc) {
            transitFetchPromises.push(fetchTransitTimes(hotelToFirstKey, hotelLoc, firstLoc, { type: 'same-day', expectedCity: dayData.baseCity || cityName }));
          }
        }
      }

      // Queue between-activity transit if missing
      const activitiesOnly = pdfItems.slice(1);
      let transitCounter = 0;
      for (let i = 0; i < activitiesOnly.length - 1; i++) {
        const curr = activitiesOnly[i];
        const next = activitiesOnly[i + 1];
        const currNorm = (curr.location || '').toLowerCase().replace(/^the\s+/i, '').replace(/,\s*[a-z]+$/i, '').replace(/\s+/g, ' ').trim();
        const nextNorm = (next.location || '').toLowerCase().replace(/^the\s+/i, '').replace(/,\s*[a-z]+$/i, '').replace(/\s+/g, ' ').trim();
        if (currNorm === nextNorm) { continue; }

        const transitKey = `day${dayIdx}-transit-${transitCounter}`;
        if (!transitTimesRef.current[transitKey]) {
          // Prefer coordinates over name strings for more reliable geocoding
          const currOrigin = curr.placeData?.location?.lat
            ? `${curr.placeData.location.lat},${curr.placeData.location.lng}`
            : curr.location;
          const nextDest = next.placeData?.location?.lat
            ? `${next.placeData.location.lat},${next.placeData.location.lng}`
            : next.location;
          transitFetchPromises.push(fetchTransitTimes(transitKey, currOrigin, nextDest, { type: 'same-day', expectedCity: dayData.baseCity || cityName }));
        }
        transitCounter++;
      }

      // Queue last-to-hotel if missing — skip on last day (departure day heads to airport, not hotel)
      const isLastDayPdf = dayIdx === (itinerary.days?.length || 0) - 1;
      if (!isLastDayPdf && activitiesOnly.length > 0) {
        const lastKey = `day${dayIdx}-last-to-hotel`;
        if (!transitTimesRef.current[lastKey]) {
          const lastAct = activitiesOnly[activitiesOnly.length - 1];
          const lastActLoc = lastAct.placeData?.location?.lat
            ? `${lastAct.placeData.location.lat},${lastAct.placeData.location.lng}`
            : lastAct.location;
          const hotelCoordsReturn = dayData.hotel?.coordinates;
          const hotelLocReturn = hotelCoordsReturn?.lat
            ? `${hotelCoordsReturn.lat},${hotelCoordsReturn.lng}`
            : addCityContextPdf(dayData.hotel?.address || dayData.hotel?.name || 'Hotel');
          transitFetchPromises.push(fetchTransitTimes(lastKey, lastActLoc, hotelLocReturn, { type: 'hotel-return', expectedCity: dayData.baseCity || cityName }));
        }
      }

      // Queue airport transit for first and last day
      if (dayIdx === 0 && itinerary.arrivalAirport && dayData.hotel) {
        const hotelTransitKey = `day${dayIdx}-hotel-transit`;
        if (!transitTimesRef.current[hotelTransitKey]) {
          const arrCityStr = safeAirportCity(itinerary.arrivalAirport, dayData.baseCity || cityName);
          const airportName = geocodableAirportName(itinerary.arrivalAirport);
          const hotelLoc = addCityContextPdf(dayData.hotel.address || dayData.hotel.name);
          transitFetchPromises.push(fetchTransitTimes(hotelTransitKey, airportName, hotelLoc, { type: 'airport-transfer', fromCity: arrCityStr, toCity: dayData.baseCity || cityName }));
        }
      }
      if (dayIdx === (itinerary.days.length - 1) && itinerary.departureAirport) {
        const airportKey = 'last-day-airport-transit';
        if (!transitTimesRef.current[airportKey]) {
          // For departure: use previous day's hotel if current day has no hotel
          const prevDayDataPdf = dayIdx > 0 ? itinerary.days[dayIdx - 1] : null;
          const departureHotelPdf = dayData.hotel || prevDayDataPdf?.hotel;
          if (departureHotelPdf) {
            const hotelCity = dayData.hotel ? (dayData.baseCity || cityName) : (prevDayDataPdf?.baseCity || dayData.baseCity || cityName);
            const hotelLoc = addCityContextPdf(`${departureHotelPdf.address || departureHotelPdf.name}, ${hotelCity}`);
            const depCityStr = safeAirportCity(itinerary.departureAirport, hotelCity);
            const airportName = geocodableAirportName(itinerary.departureAirport);
            transitFetchPromises.push(fetchTransitTimes(airportKey, hotelLoc, airportName, { type: 'airport-transfer', fromCity: hotelCity, toCity: depCityStr }));
          }
        }
      }
    }

    // Fire all transit fetches in parallel (was sequential — 10-30s → now 1-3s)
    if (transitFetchPromises.length > 0) {
      debugLog(`📄 PDF: Fetching ${transitFetchPromises.length} transit times in parallel...`);
      await Promise.allSettled(transitFetchPromises);
      debugLog(`📄 PDF: All transit times fetched`);
    }

    const startDateDisplay = formatDateForDisplay(userProfile?.preferences?.startDate);
    const endDateDisplay = formatDateForDisplay(userProfile?.preferences?.endDate);

    // PDF6: round odd minute values to :00 / :15 / :30 / :45 before display.
    // The timeline engine already rounds for the UI — but defensively round
    // again here in case an item slipped through (e.g., 11:12 or 9:52).
    const roundTimeForPdf = (timeStr) => {
      if (!timeStr || typeof timeStr !== 'string') return timeStr;
      // Accept "HH:MM" or "H:MM AM/PM" — only round the numeric part
      const m = timeStr.match(/^(\d{1,2}):(\d{2})(\s*[AP]M)?$/i);
      if (!m) return timeStr;
      const hh = m[1];
      let mm = parseInt(m[2], 10);
      const suf = m[3] || '';
      const rounded = Math.round(mm / 15) * 15;
      if (rounded === 60) {
        return `${(parseInt(hh, 10) + 1) % 24}:00${suf}`;
      }
      return `${hh}:${String(rounded).padStart(2, '0')}${suf}`;
    };
    // Reuse the shared formatTimeDisplay — handles both 24h and 12h AM/PM input
    const formatTimeForPdf = (t) => formatTimeDisplay(roundTimeForPdf(t));

    // ────────────────────────────────────────────────────────────
    // PDF1: Cover data prep — traveler name, stops list, world map,
    //       per-stop weather snapshot, taste profile tagline.
    // ────────────────────────────────────────────────────────────
    const travelerName = (user?.user_metadata?.full_name?.split(' ')[0])
      || (user?.email?.split('@')[0])
      || (userProfile?.preferences?.name)
      || '';
    const travelerInitial = (travelerName.charAt(0) || 'W').toUpperCase();
    const totalDays = itinerary.days?.length || 0;
    const coverDateLine = startDateDisplay && endDateDisplay
      ? `${startDateDisplay} \u2013 ${endDateDisplay} \u00b7 ${totalDays} days`
      : `${totalDays} days`;

    // Collect unique stop names for the journey line + map.
    const coverStopNames = (() => {
      const raw = tripBuilderStops?.length > 1
        ? tripBuilderStops.map(s => s.resolvedLocation || s.name)
        : selectedDestination?.stopNames?.length > 1
          ? selectedDestination.stopNames
          : selectedDestination?.stops?.length > 1
            ? selectedDestination.stops.map(s => s.resolvedLocation || s.name || s)
            : (itinerary.days || []).map(d => d?.baseCity).filter(Boolean);
      const cleaned = (raw || [])
        .map(s => (typeof s === 'string' ? s : s?.name || '').split(',')[0].trim())
        .filter(Boolean);
      const dedup = [];
      for (const s of cleaned) {
        if (!dedup.length || dedup[dedup.length - 1].toLowerCase() !== s.toLowerCase()) {
          dedup.push(s);
        }
      }
      return dedup.length > 0 ? dedup : [itinerary.destination || selectedDestination?.name || ''].filter(Boolean);
    })();

    // Pre-compute a minimal world-map SVG (d3-geo + world-atlas) on the
    // host page so the popup window — which cannot share imports — can
    // embed it inline. Silent if deps fail to load.
    //
    // CRITICAL: world-atlas country IDs are ISO 3166-1 NUMERIC ('554' for NZ,
    // '392' for Japan, etc.). The shared `CITY_TO_COUNTRY` from
    // geographyConstants returns country *names* ("New Zealand"), which never
    // matched country.id and produced an empty map (user-reported Apr 29).
    // We use the ISO-numeric map exported from TravelMap.jsx instead.
    let coverMapSvg = '';
    try {
      const [{ geoNaturalEarth1, geoPath }, topoMod, { feature }, { CITY_TO_COUNTRY: CITY_TO_ISO_NUMERIC }] = await Promise.all([
        import('d3-geo'),
        import('world-atlas/countries-110m.json'),
        import('topojson-client'),
        import('./components/community/TravelMap'),
      ]);
      const src = topoMod.default || topoMod;
      const countries = feature(src, src.objects.countries);
      const mapW = 520;
      const mapH = 200;
      const proj = geoNaturalEarth1().fitSize([mapW, mapH], countries);
      const pathGen = geoPath(proj);
      const resolveIso = (s) => {
        if (!s) return null;
        const lower = s.toLowerCase().trim();
        if (CITY_TO_ISO_NUMERIC[lower]) return CITY_TO_ISO_NUMERIC[lower];
        const cityOnly = lower.split(',')[0].trim();
        return CITY_TO_ISO_NUMERIC[cityOnly] || null;
      };
      const visited = new Set();
      for (const s of coverStopNames) {
        const code = resolveIso(s);
        if (code) visited.add(String(code));
      }
      const dots = coverStopNames.map((s) => {
        const code = resolveIso(s);
        if (!code) return null;
        const country = countries.features.find(f => String(f.id) === String(code) || String(f.id).padStart(3, '0') === String(code).padStart(3, '0'));
        if (!country) return null;
        const centroid = pathGen.centroid(country);
        if (!isFinite(centroid[0]) || !isFinite(centroid[1])) return null;
        return { x: centroid[0], y: centroid[1], name: s };
      }).filter(Boolean);

      const countryPaths = countries.features.map((f) => {
        const d = pathGen(f);
        if (!d) return '';
        const id = String(f.id || '');
        const isVisited = visited.has(id) || visited.has(id.padStart(3, '0'));
        const fill = isVisited ? '#1d8e85' : '#e8ebef';
        const stroke = isVisited ? '#166f68' : '#d3d7dd';
        return `<path d="${d}" fill="${fill}" stroke="${stroke}" stroke-width="0.4" />`;
      }).join('');

      const dotMarkers = dots.map(d => `
        <g transform="translate(${d.x},${d.y})">
          <circle r="5" fill="#CD7A4D" opacity="0.25" />
          <circle r="2.5" fill="#CD7A4D" stroke="white" stroke-width="1" />
        </g>
      `).join('');

      const arcs = dots.length > 1 ? dots.slice(1).map((b, i) => {
        const a = dots[i];
        const mx = (a.x + b.x) / 2;
        const my = (a.y + b.y) / 2 - Math.min(20, Math.abs(b.x - a.x) * 0.2);
        return `<path d="M${a.x},${a.y} Q${mx},${my} ${b.x},${b.y}" fill="none" stroke="#CD7A4D" stroke-width="1.2" stroke-dasharray="3 2" opacity="0.6" />`;
      }).join('') : '';

      coverMapSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${mapW} ${mapH}" width="100%" style="max-width:${mapW}px;display:block;margin:0 auto;">${countryPaths}${arcs}${dotMarkers}</svg>`;
    } catch (mapErr) {
      debugLog('PDF cover map unavailable:', mapErr?.message || mapErr);
      coverMapSvg = '';
    }

    // Per-stop weather snapshot. Driven by coverStopNames (authoritative
    // trip-builder stop list) instead of the days loop so a stop is never
    // dropped just because its days don't yet have cached weather. Falls
    // back to empty temps (rendered as "--") rather than omitting the cell.
    const coverWeatherByStop = (() => {
      // Build a quick lookup: first-day-per-city → weather object
      const weatherByCity = new Map();
      (itinerary.days || []).forEach((d) => {
        const city = (d?.baseCity || '').split(',')[0].trim().toLowerCase();
        if (!city || weatherByCity.has(city)) return;
        const w = d?.weather;
        if (w && (w.high != null || w.temp != null || w.description || w.conditions)) {
          weatherByCity.set(city, w);
        }
      });
      // Emit one cell per stop in the user's trip order
      return (coverStopNames || []).map((name) => {
        const key = (name || '').split(',')[0].trim().toLowerCase();
        const w = weatherByCity.get(key);
        return {
          city: (name || '').split(',')[0].trim(),
          high: w?.high ?? w?.temp ?? null,
          low: w?.low ?? null,
          conditions: w?.description || w?.conditions || w?.summary || '',
        };
      }).filter(cell => cell.city);
    })();

    // Taste profile tagline
    let tastelineHtml = '';
    try {
      const hasTaste = !!(profile?.taste_profile || profile?.travel_pace || profile?.travel_personality);
      if (hasTaste && travelerName) {
        tastelineHtml = `<div class="cover-tagline">Curated for ${travelerName}'s taste profile</div>`;
      }
    } catch {}

    const coverJourneyHtml = coverStopNames.length > 0
      ? coverStopNames.map((name, idx) => {
          const dot = '<span class="journey-dot"></span>';
          const arrow = idx > 0 ? '<span class="journey-arrow">\u2192</span>' : '';
          return `${arrow}<span class="journey-stop">${dot}${name}</span>`;
        }).join('')
      : '';

    // PDF-P5: Cover at-a-glance — Destinations · Dates · Travelers.
    // Renders unconditionally so the cover is never reduced to just logo +
    // title. Complements (doesn't replace) the journey line / map / weather.
    const _atGlanceDestinations = coverStopNames.length > 0
      ? (coverStopNames.length > 3
          ? `${coverStopNames.slice(0, 2).join(', ')} +${coverStopNames.length - 2} more`
          : coverStopNames.join(', '))
      : (itinerary.destination || selectedDestination?.name || '\u2014');
    const _atGlanceDates = (startDateDisplay && endDateDisplay)
      ? `${startDateDisplay} \u2013 ${endDateDisplay}`
      : `${totalDays} day${totalDays === 1 ? '' : 's'}`;
    const _atGlanceTravelerCount = (() => {
      const prefs = userProfile?.preferences || {};
      const adults = Number(prefs.adults ?? prefs.numAdults ?? prefs.travelers ?? prefs.partySize);
      const kids = Number(prefs.children ?? prefs.kids ?? prefs.numChildren ?? 0);
      if (Number.isFinite(adults) && adults > 0) {
        const total = adults + (Number.isFinite(kids) ? kids : 0);
        return `${total} traveler${total === 1 ? '' : 's'}`;
      }
      return travelerName ? 'Solo traveler' : '1 traveler';
    })();
    const coverAtAGlanceHtml = `
      <div class="cover-ataglance">
        <div class="ataglance-cell">
          <div class="ataglance-label">Destinations</div>
          <div class="ataglance-value">${_atGlanceDestinations}</div>
        </div>
        <div class="ataglance-cell">
          <div class="ataglance-label">Dates</div>
          <div class="ataglance-value">${_atGlanceDates}</div>
        </div>
        <div class="ataglance-cell">
          <div class="ataglance-label">Travelers</div>
          <div class="ataglance-value">${_atGlanceTravelerCount}</div>
        </div>
      </div>
    `;

    const coverWeatherHtml = coverWeatherByStop.length > 0 ? `
      <div class="cover-weather">
        ${coverWeatherByStop.map(w => `
          <div class="weather-cell">
            <div class="weather-city">${w.city}</div>
            <div class="weather-temp">${w.high != null ? `${Math.round(w.high * 9 / 5 + 32)}\u00b0` : '--'}${w.low != null ? ` / ${Math.round(w.low * 9 / 5 + 32)}\u00b0` : ''}</div>
            ${w.conditions ? `<div class="weather-cond">${w.conditions}</div>` : ''}
          </div>
        `).join('')}
      </div>
    ` : '';

    // Build HTML content for the itinerary
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${itinerary.destination || selectedDestination.name} - Travel Itinerary</title>
        <link href="https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&family=Cormorant+Garamond:wght@500;600;700&display=swap" rel="stylesheet">
        <style>
          @font-face {
            font-family: 'GulfsDisplay';
            src: url('/GulfsDisplay.ttf') format('truetype');
            font-weight: normal;
            font-style: normal;
          }
        </style>
        <style>
          * { box-sizing: border-box; margin: 0; padding: 0; }
          /* PDF6: reliable emoji rendering across browsers/print engines */
          body, h1, h2, h3, span, div, td, th, li {
            font-family: 'Raleway', -apple-system, 'Segoe UI', 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji', sans-serif;
          }
          body {
            font-family: 'Raleway', -apple-system, 'Apple Color Emoji', 'Segoe UI Emoji', sans-serif;
            color: #1f2937;
            line-height: 1.4;
            padding: 28px 36px;
            max-width: 850px;
            margin: 0 auto;
            background: #FAF8F5;
          }
          .header {
            text-align: center;
            margin-bottom: 32px;
            padding-bottom: 24px;
            border-bottom: 2px solid rgba(167,216,219,0.4);
          }
          .header h1 {
            font-size: 32px;
            color: #313143;
            margin-bottom: 8px;
            font-weight: 700;
          }
          .header .dates {
            color: #6b7280;
            font-size: 16px;
          }
          .day-section {
            margin-bottom: 20px;
            page-break-inside: avoid;
          }
          .day-header {
            background: linear-gradient(135deg, #1d8e85 0%, #166f68 100%);
            color: white;
            padding: 10px 18px;
            border-radius: 14px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 10px;
          }
          .day-header h2 { font-size: 17px; font-weight: 600; }
          .day-header .date { font-size: 13px; opacity: 0.9; }
          .day-header .hotel-badge {
            background: rgba(255,255,255,0.2);
            padding: 3px 9px;
            border-radius: 20px;
            font-size: 11px;
            margin-left: 10px;
          }
          .period-section {
            margin-bottom: 12px;
          }
          .period-divider {
            display: flex;
            align-items: center;
            gap: 10px;
            margin: 10px 0 8px 0;
          }
          .period-divider .line { flex: 1; height: 1px; background: rgba(167,216,219,0.4); }
          .period-divider .label {
            font-size: 11px;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            color: #9ca3af;
            font-weight: 600;
          }
          .timeline-item {
            display: flex;
            gap: 14px;
            padding: 10px 16px;
            margin-bottom: 6px;
            border-radius: 16px;
            border: 1px solid rgba(49,49,67,0.06);
            background: white;
            box-shadow: 0 4px 14px rgba(28,41,53,0.04);
          }
          .timeline-item.meal {
            border-left: 4px solid #CD7A4D;
          }
          .timeline-item.transit {
            background: rgba(167,216,219,0.08);
            border: 1px solid rgba(167,216,219,0.2);
            padding: 6px 16px;
          }
          .tl-time {
            font-size: 12px;
            font-weight: 700;
            color: #1d8e85;
            text-transform: uppercase;
            letter-spacing: 0.05em;
            min-width: 70px;
            flex-shrink: 0;
            padding-top: 2px;
          }
          .tl-time.meal { color: #CD7A4D; }
          .item-content { flex: 1; }
          .item-header {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 4px;
          }
          .item-title { font-size: 15px; font-weight: 800; color: #313143; letter-spacing: -0.02em; }
          .item-meta {
            display: flex;
            flex-wrap: wrap;
            gap: 10px;
            font-size: 11px;
            color: #6b7280;
            margin-bottom: 3px;
          }
          .item-meta span { display: flex; align-items: center; gap: 4px; }
          .item-location { color: #6b7280; font-size: 11px; margin-bottom: 2px; }
          .item-description {
            font-size: 12px;
            color: #4b5563;
            line-height: 1.4;
            margin-top: 4px;
          }
          .price-badge {
            display: inline-block;
            padding: 2px 8px;
            background: rgba(29,142,133,0.1);
            color: #1d8e85;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 700;
          }
          .price-badge.meal {
            background: rgba(205,122,77,0.1);
            color: #CD7A4D;
          }
          .footer {
            margin-top: 20px;
            padding-top: 14px;
            border-top: 2px solid rgba(167,216,219,0.3);
            text-align: center;
            color: #9ca3af;
            font-size: 11px;
            page-break-inside: avoid;
            break-inside: avoid;
          }
          .footer .logo { color: #1d8e85; font-weight: 600; font-size: 13px; margin-bottom: 4px; }
          @media print {
            body { padding: 16px 20px; background: white; }
            /* PDF6: don't force whole .day-section to avoid breaks — that was
               wasting pages on long days. Instead keep day header with its
               first child (period divider + first item) via page-break-after. */
            .day-section { page-break-inside: auto; }
            .timeline-item { break-inside: avoid; page-break-inside: avoid; }
            .footer { page-break-before: avoid; margin-top: 16px; }
            .cover-page { page-break-after: always; break-after: page; }
            .overview-page { page-break-after: always; break-after: page; }
            .concierge-page { page-break-after: always; break-after: page; }
            .day-header-block { page-break-inside: avoid; break-inside: avoid; page-break-after: avoid; break-after: avoid; }
            .period-section { page-break-inside: auto; }
            .period-divider { page-break-after: avoid; break-after: avoid; }
          }
          /* PDF5: serif for premium surfaces */
          .serif {
            font-family: 'Cormorant Garamond', 'Playfair Display', Georgia, 'Times New Roman', serif;
            font-weight: 600;
            letter-spacing: 0.01em;
          }
          /* ── PDF1: Cover page ── */
          .cover-page {
            min-height: 95vh;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            padding: 40px 30px;
            text-align: center;
          }
          .cover-brand {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 6px;
            margin-bottom: 24px;
          }
          .cover-brand .wordmark {
            font-family: 'GulfsDisplay', serif;
            font-size: 34px;
            color: #1d8e85;
            letter-spacing: 0.08em;
          }
          .cover-brand .tagline-sm {
            font-size: 10px;
            color: #9ca3af;
            letter-spacing: 0.2em;
            text-transform: lowercase;
          }
          .cover-title {
            font-size: 48px;
            color: #0f2a2a;
            font-weight: 600;
            line-height: 1.1;
            margin: 16px 0 10px 0;
            letter-spacing: -0.01em;
          }
          .cover-dates {
            font-size: 15px;
            color: #4b5563;
            letter-spacing: 0.05em;
            margin-bottom: 22px;
          }
          .cover-journey {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            align-items: center;
            gap: 6px 10px;
            font-size: 14px;
            color: #1f2937;
            margin: 18px auto 18px auto;
            max-width: 720px;
          }
          .journey-stop {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-weight: 500;
          }
          .journey-dot {
            display: inline-block;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: #CD7A4D;
            box-shadow: 0 0 0 3px rgba(205,122,77,0.15);
          }
          .journey-arrow {
            color: #9ca3af;
            font-size: 14px;
          }
          .cover-map {
            margin: 14px auto 18px auto;
            max-width: 560px;
          }
          .cover-weather {
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            gap: 10px;
            margin: 14px auto 10px auto;
            max-width: 640px;
          }
          .weather-cell {
            border: 1px solid rgba(29,142,133,0.18);
            background: white;
            border-radius: 14px;
            padding: 10px 14px;
            min-width: 110px;
          }
          .weather-city {
            font-size: 11px;
            color: #6b7280;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .weather-temp {
            font-size: 16px;
            color: #1d8e85;
            font-weight: 700;
          }
          .weather-cond {
            font-size: 11px;
            color: #6b7280;
            margin-top: 2px;
            text-transform: capitalize;
          }
          .cover-traveler {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
            margin-top: 24px;
          }
          .cover-avatar {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #1d8e85;
            color: white;
            font-weight: 700;
            font-size: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            letter-spacing: 0;
          }
          .cover-traveler-name {
            font-size: 14px;
            color: #4b5563;
          }
          .cover-tagline {
            font-size: 12px;
            color: #6b7280;
            font-style: italic;
            margin-top: 6px;
            letter-spacing: 0.02em;
          }
          /* PDF-P5: cover at-a-glance (3-column stats — resilient fallback) */
          .cover-ataglance {
            display: flex;
            justify-content: center;
            gap: 0;
            margin: 20px auto 18px auto;
            max-width: 620px;
            flex-wrap: wrap;
          }
          .ataglance-cell {
            min-width: 150px;
            padding: 8px 20px;
            border-left: 2px solid rgba(29,142,133,0.25);
            text-align: left;
          }
          .ataglance-cell:first-child { border-left: none; }
          .ataglance-label {
            font-size: 10px;
            color: #9ca3af;
            text-transform: uppercase;
            letter-spacing: 0.14em;
            font-weight: 700;
            margin-bottom: 4px;
          }
          .ataglance-value {
            font-size: 14px;
            color: #1f2937;
            font-weight: 600;
            line-height: 1.3;
          }
          /* ── PDF2: Overview table ── */
          .overview-page {
            margin-bottom: 36px;
          }
          .overview-title {
            font-size: 24px;
            color: #0f2a2a;
            margin-bottom: 6px;
            text-align: center;
          }
          .overview-sub {
            font-size: 12px;
            color: #6b7280;
            text-align: center;
            letter-spacing: 0.12em;
            text-transform: uppercase;
            margin-bottom: 18px;
          }
          .overview-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 11px;
          }
          .overview-table th {
            text-align: left;
            font-size: 10px;
            text-transform: uppercase;
            letter-spacing: 0.1em;
            color: #9ca3af;
            font-weight: 700;
            padding: 8px 10px;
            border-bottom: 2px solid rgba(29,142,133,0.3);
          }
          .overview-table td {
            padding: 10px 10px;
            border-bottom: 1px solid rgba(29,142,133,0.14);
            vertical-align: top;
            color: #1f2937;
          }
          .overview-table tr.transit-row td {
            background: rgba(205,122,77,0.06);
          }
          .overview-day { font-weight: 700; color: #1d8e85; white-space: nowrap; }
          .overview-city { font-weight: 600; }
          .overview-hotel { color: #4b5563; }
          .overview-theme { color: #6b7280; font-style: italic; }
          .overview-venues { color: #4b5563; }
          /* ── PDF3: Concierge / Key-info ── */
          .concierge-page {
            margin-bottom: 36px;
          }
          .concierge-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
          }
          .info-card {
            background: white;
            border: 1px solid rgba(29,142,133,0.18);
            border-radius: 16px;
            padding: 14px 16px;
            box-shadow: 0 4px 14px rgba(28,41,53,0.04);
          }
          .info-card.full { grid-column: 1 / -1; }
          .info-card-title {
            font-size: 11px;
            color: #1d8e85;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.12em;
            margin-bottom: 6px;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .info-card-body {
            font-size: 12px;
            color: #1f2937;
            line-height: 1.5;
          }
          .info-card-body .muted {
            color: #6b7280;
            font-size: 11px;
          }
          .concierge-warn {
            background: rgba(205,122,77,0.08);
            border: 1px solid rgba(205,122,77,0.25);
            color: #8a4a22;
          }
          .concierge-warn .info-card-title { color: #CD7A4D; }
          /* ── PDF4/5: Day header serif ── */
          .day-header h2 { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 600; font-size: 22px; letter-spacing: 0.01em; }
          .hotel-descriptor {
            margin-top: 4px;
            font-size: 12px;
            color: rgba(255,255,255,0.88);
            font-style: italic;
          }
          .hotel-badge { font-family: 'Cormorant Garamond', Georgia, serif; font-weight: 600; font-size: 13px; }
          /* PDF5: timeline hotel-check-in item uses serif for the hotel name */
          .timeline-item.hotel .item-title,
          .timeline-item.hotel-checkin .item-title {
            font-family: 'Cormorant Garamond', Georgia, serif;
            font-weight: 600;
            font-size: 17px;
            letter-spacing: 0.01em;
          }
        </style>
      </head>
      <body>
        <div class="header" style="display:none">
          <div style="display: flex; justify-content: center; align-items: center; gap: 12px; margin-bottom: 20px;">
            <div style="width: 48px; height: 2px; background: linear-gradient(to right, transparent, #1d8e85);"></div>
            <div style="text-align: center;">
              <img src="data:image/png;base64,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" alt="Arrivo" style="width: 60px; height: auto; margin-bottom: 8px;" />
              <div style="font-family: 'GulfsDisplay', serif; font-size: 36px; color: #1d8e85; letter-spacing: 0.08em;">arrivo</div>
              <div style="font-size: 10px; color: #9ca3af; letter-spacing: 0.2em; text-transform: lowercase; text-align: center;">plan less. explore more.</div>
            </div>
            <div style="width: 48px; height: 2px; background: linear-gradient(to left, transparent, #1d8e85);"></div>
          </div>
          <h1 style="display:none">${itinerary.destination || selectedDestination.name}</h1>
        </div>

        <!-- ── PDF1: Cover page (replaces legacy .header above) ── -->
        <div class="cover-page">
          <div class="cover-brand">
            <!-- Logo image already includes the "arrivo" wordmark + tagline as
                 part of the artwork. Earlier we ALSO rendered a separate text
                 wordmark + tagline below it, producing a stacked-double-brand
                 effect on the PDF cover (user-flagged Apr 29). The img alone is
                 enough for branding. Falls back to text wordmark if the image
                 fails to load (offline reviewers, CSP issues, etc.). -->
            <img src="/arrivo-logo.png"
                 alt="arrivo — plan less. explore more."
                 style="height: 64px; width: auto; display: block; margin-bottom: 6px;"
                 onerror="this.style.display='none'; this.nextElementSibling.style.display='block'; this.nextElementSibling.nextElementSibling.style.display='block';" />
            <div class="wordmark" style="display:none">arrivo</div>
            <div class="tagline-sm" style="display:none">plan less. explore more.</div>
          </div>
          <div>
            <div class="cover-title serif">${itinerary.destination || selectedDestination.name}</div>
            <div class="cover-dates">${coverDateLine}</div>
            ${coverAtAGlanceHtml}
            ${coverJourneyHtml ? `<div class="cover-journey">${coverJourneyHtml}</div>` : ''}
            ${coverMapSvg ? `<div class="cover-map">${coverMapSvg}</div>` : ''}
            ${coverWeatherHtml}
          </div>
          <div>
            ${travelerName ? `
              <div class="cover-traveler">
                <div class="cover-avatar">${travelerInitial}</div>
                <div class="cover-traveler-name">Prepared for <strong>${travelerName}</strong></div>
              </div>
            ` : ''}
            ${tastelineHtml}
          </div>
        </div>

    `;

    // ────────────────────────────────────────────────────────────
    // PDF2: At-a-glance overview table
    // One row per day — Day · Date · City · Hotel · Theme · Key venues
    // Highlights transit days (city changes day-over-day).
    // ────────────────────────────────────────────────────────────
    const overviewRows = (itinerary.days || []).map((day, idx) => {
      if (!day || day._generationFailed) {
        return { idx: idx + 1, date: '', city: '', hotel: '', theme: '', venues: '', transit: false };
      }
      const dateStr = userProfile?.preferences?.startDate
        ? (() => {
            const d = parseLocalDate(userProfile.preferences.startDate);
            d.setDate(d.getDate() + idx);
            return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          })()
        : '';
      const city = (day.baseCity || day.city || '').split(',')[0].trim();
      const prev = idx > 0 ? itinerary.days[idx - 1] : null;
      const prevCity = prev ? ((prev.baseCity || prev.city || '').split(',')[0].trim()) : '';
      const transit = idx > 0 && prevCity && city && prevCity.toLowerCase() !== city.toLowerCase();
      const hotel = day.hotel?.name || '';
      const theme = day.theme || day.title || day.summary || '';
      // Pick 2-3 key venues from schedule
      const scheduleNames = (day.schedule || [])
        .map(a => a?.activity || a?.title)
        .filter(Boolean)
        .slice(0, 3);
      // If no schedule, fall back to meals
      const mealNames = scheduleNames.length === 0
        ? (day.meals || []).map(m => m?.restaurant).filter(Boolean).slice(0, 2)
        : [];
      const venues = [...scheduleNames, ...mealNames].join(', ');
      return { idx: idx + 1, date: dateStr, city, hotel, theme, venues, transit };
    });

    // ────────────────────────────────────────────────────────────
    // PDF3: Concierge / Key-info block — precompute country-level facts
    // ────────────────────────────────────────────────────────────
    // Derive countries from stop names via CITY_TO_COUNTRY → name reverse map
    const CITY_COUNTRY_NAME = {
      'USA': 'United States', 'US': 'United States', 'United States': 'United States',
      'UK': 'United Kingdom', 'United Kingdom': 'United Kingdom',
    };
    // ALL city/country pairs in the trip (not deduped). Used by country-level
    // displays below to group cities by country instead of dropping them.
    // Previously deduped to one-city-per-country which made the concierge
    // page show only "Auckland: English" / "Auckland: 111" for a 4-city
    // NZ trip — losing Wellington/Christchurch/Queenstown.
    const coverStopCityPairs = (() => {
      const pairs = [];
      for (const name of coverStopNames) {
        const parts = name.split(',').map(p => p.trim());
        if (parts.length > 1) {
          pairs.push({ city: parts[0], country: parts[parts.length - 1] });
          continue;
        }
        const mapped = CITY_TO_COUNTRY[name.toLowerCase()] || CITY_TO_COUNTRY[parts[0].toLowerCase()];
        if (mapped) pairs.push({ city: parts[0], country: mapped });
      }
      return pairs;
    })();
    // Deduped country-only list. Used by the once-per-country items (Schengen
    // check, eVisa requirements) where the cities don't matter.
    const coverStopCountries = (() => {
      const seen = new Set();
      return coverStopCityPairs.filter(({ country }) => {
        const k = country.toLowerCase();
        if (seen.has(k)) return false;
        seen.add(k);
        return true;
      });
    })();

    // Currency grouped (e.g., "Park City: USD · Lisbon: EUR")
    const currencyLine = (() => {
      const groups = new Map();
      for (const s of coverStopNames) {
        const curr = getCurrencyForCity(s);
        if (!curr) continue;
        const list = groups.get(curr) || [];
        if (!list.includes(s)) list.push(s);
        groups.set(curr, list);
      }
      if (!groups.size) return '';
      const parts = [];
      for (const [curr, cities] of groups.entries()) {
        parts.push(`<strong>${cities.join(' + ')}</strong>: ${curr}`);
      }
      return parts.join(' \u00b7 ');
    })();

    // Language grouped — simple country-to-primary-language lookup
    const COUNTRY_LANGUAGES_PDF = {
      'united states': 'English', 'usa': 'English', 'us': 'English',
      'canada': 'English/French', 'mexico': 'Spanish', 'cuba': 'Spanish',
      'belize': 'English', 'guatemala': 'Spanish', 'costa rica': 'Spanish',
      'panama': 'Spanish', 'colombia': 'Spanish', 'peru': 'Spanish', 'brazil': 'Portuguese',
      'argentina': 'Spanish', 'chile': 'Spanish',
      'united kingdom': 'English', 'uk': 'English', 'ireland': 'English',
      'france': 'French', 'spain': 'Spanish', 'portugal': 'Portuguese',
      'italy': 'Italian', 'germany': 'German', 'netherlands': 'Dutch',
      'belgium': 'Dutch/French', 'switzerland': 'German/French/Italian',
      'austria': 'German', 'greece': 'Greek', 'czech republic': 'Czech',
      'poland': 'Polish', 'hungary': 'Hungarian', 'sweden': 'Swedish',
      'norway': 'Norwegian', 'denmark': 'Danish', 'finland': 'Finnish',
      'iceland': 'Icelandic', 'turkey': 'Turkish', 'morocco': 'Arabic/French',
      'egypt': 'Arabic', 'japan': 'Japanese', 'china': 'Mandarin',
      'south korea': 'Korean', 'taiwan': 'Mandarin', 'thailand': 'Thai',
      'vietnam': 'Vietnamese', 'singapore': 'English/Mandarin', 'malaysia': 'Malay/English',
      'indonesia': 'Indonesian', 'india': 'Hindi/English', 'uae': 'Arabic/English',
      'australia': 'English', 'new zealand': 'English',
      'south africa': 'English', 'kenya': 'Swahili/English',
    };
    const languageLine = (() => {
      // Iterate ALL pairs (not deduped) so a 4-city NZ trip groups all four
      // under "New Zealand: English" instead of "Auckland: English" only.
      // Label by country since language is country-level.
      const groups = new Map(); // lang -> Set<country>
      for (const { country } of coverStopCityPairs) {
        const lang = COUNTRY_LANGUAGES_PDF[country.toLowerCase()];
        if (!lang) continue;
        if (!groups.has(lang)) groups.set(lang, new Set());
        groups.get(lang).add(country);
      }
      if (!groups.size) return '';
      const parts = [];
      for (const [lang, countries] of groups.entries()) {
        parts.push(`<strong>${Array.from(countries).join(' + ')}</strong>: ${lang}`);
      }
      return parts.join(' \u00b7 ');
    })();

    // Emergency numbers — use imported EMERGENCY_NUMBERS (lowercase keys).
    // Label by country (not city) since emergency # is country-level. Was
    // showing only the first city per country due to the deduped iteration.
    const emergencyLine = (() => {
      const groups = new Map(); // num -> Set<country>
      for (const { country } of coverStopCityPairs) {
        const num = EMERGENCY_NUMBERS?.[country.toLowerCase()];
        if (!num) continue;
        if (!groups.has(num)) groups.set(num, new Set());
        groups.get(num).add(country);
      }
      if (!groups.size) return '';
      const parts = [];
      for (const [num, countries] of groups.entries()) {
        parts.push(`<strong>${Array.from(countries).join(' + ')}</strong>: ${num}`);
      }
      return parts.join(' \u00b7 ');
    })();

    // Altitude warning — check itinerary.days for elevation > 7000ft, or
    // any dayTip containing "altitude"
    const altitudeCities = (() => {
      const flagged = new Set();
      (itinerary.days || []).forEach((d) => {
        const city = (d?.baseCity || '').split(',')[0].trim();
        if (!city) return;
        const elev = d?.elevation || d?.altitude?.feet || d?.altitude?.meters;
        // If meters, >2100m ≈ 7000ft
        if (typeof elev === 'number') {
          const isFeet = elev > 2500; // crude: large values assumed feet
          if ((isFeet && elev > 7000) || (!isFeet && elev > 2100)) flagged.add(city);
        }
        const tips = d?.dayTips || d?.tips || [];
        for (const t of (Array.isArray(tips) ? tips : [])) {
          const str = typeof t === 'string' ? t : (t?.text || t?.tip || '');
          if (/altitude|acclimat/i.test(str)) flagged.add(city);
        }
      });
      // Also static: known high-altitude cities
      const HIGH_ALT = ['la paz', 'cusco', 'quito', 'bogota', 'potosi', 'leh', 'lhasa', 'park city', 'aspen', 'breckenridge', 'vail'];
      for (const s of coverStopNames) {
        if (HIGH_ALT.includes(s.toLowerCase())) flagged.add(s);
      }
      return Array.from(flagged);
    })();

    // Visa / Schengen notes — simple per-country treatment from US passport
    const visaLines = (() => {
      const lines = [];
      const schengenStops = coverStopCountries.filter(s => SCHENGEN_COUNTRIES?.includes(s.country));
      if (schengenStops.length > 0) {
        lines.push(`\u{1F6C2} <strong>Schengen area:</strong> ${schengenStops.map(s => s.country).join(', ')} \u2014 90-day rolling limit across the zone. No internal border checks; carry passport for spot ID checks.`);
      }
      for (const { city, country } of coverStopCountries) {
        if (VISA_REQUIREMENTS_US?.eVisa?.includes(country)) {
          lines.push(`\u{1F4C4} <strong>${country}</strong>: e-Visa required \u2014 apply online before travel.`);
        } else if (VISA_REQUIREMENTS_US?.visaRequired?.includes(country)) {
          lines.push(`\u{1F4C4} <strong>${country}</strong>: Visa required \u2014 apply at embassy in advance.`);
        }
      }
      return lines;
    })();

    // Tipping — static, minimal per-country map
    const TIPPING_GUIDE = {
      'United States': '18\u201322% at sit-down restaurants. $1\u20132/drink at bars.',
      'Canada': '15\u201320% at restaurants. $1\u20132/drink at bars.',
      'Mexico': '10\u201315% at restaurants. Small tips welcomed for porters/drivers.',
      'Belize': '10\u201315% at restaurants. USD accepted.',
      'United Kingdom': '10\u201312.5% if service not already included.',
      'France': 'Service is included; rounding up or 5\u201310% for great service.',
      'Italy': 'Coperto fee usually covers service; round up for good meals.',
      'Spain': 'Not expected; small change for good service.',
      'Portugal': '5\u201310% at restaurants; round up for cafés and taxis.',
      'Germany': '5\u201310%; tell the server the total including tip when paying.',
      'Japan': 'No tipping \u2014 can be considered rude. Service is always included.',
      'South Korea': 'No tipping at restaurants; tips welcome for private guides.',
      'Thailand': 'Round up at casual spots; 10% at upscale restaurants.',
      'UAE': '10\u201315% at restaurants if not in service charge.',
      'Australia': 'Not expected; 10% for exceptional service.',
      'New Zealand': 'Not expected; round up for great service.',
      'Switzerland': 'Service is included; rounding up is customary.',
      'Iceland': 'Not expected; service is included.',
    };
    const tippingLines = (() => {
      const seen = new Set();
      const out = [];
      for (const { country } of coverStopCountries) {
        const guide = TIPPING_GUIDE[country];
        if (guide && !seen.has(country)) {
          seen.add(country);
          out.push(`<strong>${country}</strong>: ${guide}`);
        }
      }
      return out;
    })();

    const hasAnyConcierge = !!(currencyLine || languageLine || emergencyLine || altitudeCities.length || visaLines.length || tippingLines.length);

    const conciergeHtml = hasAnyConcierge ? `
        <div class="concierge-page">
          <div class="overview-title serif">Know Before You Go</div>
          <div class="overview-sub">Your trip concierge</div>
          <div class="concierge-grid">
            ${currencyLine ? `
              <div class="info-card">
                <div class="info-card-title">\u{1F4B1} Currency</div>
                <div class="info-card-body">${currencyLine}</div>
              </div>
            ` : ''}
            ${languageLine ? `
              <div class="info-card">
                <div class="info-card-title">\u{1F4AC} Language</div>
                <div class="info-card-body">${languageLine}</div>
              </div>
            ` : ''}
            ${emergencyLine ? `
              <div class="info-card">
                <div class="info-card-title">\u{1F6A8} Emergency</div>
                <div class="info-card-body">${emergencyLine}</div>
              </div>
            ` : ''}
            ${altitudeCities.length > 0 ? `
              <div class="info-card concierge-warn">
                <div class="info-card-title">\u{1F3D4}\ufe0f Altitude</div>
                <div class="info-card-body">
                  <strong>${altitudeCities.join(', ')}</strong> sits above 7,000 ft.
                  Hydrate, pace yourself the first day, and skip strenuous activity until acclimatized.
                </div>
              </div>
            ` : ''}
            ${visaLines.length > 0 ? `
              <div class="info-card full">
                <div class="info-card-title">\u{1F6C2} Visas & Borders</div>
                <div class="info-card-body">${visaLines.map(l => `<div style="margin-bottom:6px">${l}</div>`).join('')}</div>
              </div>
            ` : ''}
            ${tippingLines.length > 0 ? `
              <div class="info-card full">
                <div class="info-card-title">\u{1F4B5} Tipping</div>
                <div class="info-card-body">${tippingLines.map(l => `<div style="margin-bottom:6px">${l}</div>`).join('')}</div>
              </div>
            ` : ''}
          </div>
        </div>
    ` : '';

    html += `
      <div class="overview-page">
        <div class="overview-title serif">Trip at a Glance</div>
        <div class="overview-sub">Your day-by-day summary</div>
        <table class="overview-table">
          <thead>
            <tr>
              <th style="width:60px">Day</th>
              <th style="width:70px">Date</th>
              <th style="width:120px">City</th>
              <th style="width:150px">Hotel</th>
              <th style="width:120px">Theme</th>
              <th>Key venues</th>
            </tr>
          </thead>
          <tbody>
            ${overviewRows.map(r => `
              <tr class="${r.transit ? 'transit-row' : ''}">
                <td class="overview-day">Day ${r.idx}</td>
                <td>${r.date}</td>
                <td class="overview-city">${r.transit ? '\u2708\ufe0f ' : ''}${r.city || '\u2014'}</td>
                <td class="overview-hotel">${r.hotel || '\u2014'}</td>
                <td class="overview-theme">${r.theme || '\u2014'}</td>
                <td class="overview-venues">${r.venues || '\u2014'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      ${conciergeHtml}
    `;

    // Generate timeline for each day and render
    itinerary.days.forEach((day, dayIndex) => {
      // Skip null/undefined days or days that failed to generate
      if (!day || day._generationFailed) {
        return;
      }

      const dayDate = userProfile?.preferences?.startDate
        ? (() => {
            const d = parseLocalDate(userProfile.preferences.startDate);
            d.setDate(d.getDate() + dayIndex);
            return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
          })()
        : '';

      // Get hotel info for this day
      const hotel = day?.hotel;
      const hotelDisplay = hotel?.name ? hotel.name : '';

      // PDF4: one-line hotel descriptor. Prefer explicit description/tagline;
      // otherwise synthesize from amenity keywords. Never fabricate if we
      // have no signal at all.
      const synthHotelDescriptor = (h) => {
        if (!h?.name) return '';
        const explicit = h.description || h.tagline || h.subtitle || h.shortDescription;
        if (explicit && typeof explicit === 'string' && explicit.length < 120) return explicit.trim();
        // Derive from name + amenities
        const name = (h.name || '').toLowerCase();
        const amen = Array.isArray(h.amenities) ? h.amenities.map(a => String(a).toLowerCase()) : [];
        const text = `${name} ${amen.join(' ')} ${String(h.type || '').toLowerCase()}`;
        // Infer from signal keywords
        if (/ski[- ]?in|ski[- ]?out/i.test(text)) return 'Ski-in/ski-out luxury residence';
        if (/\bresort\b/i.test(name) && /\b(beach|ocean|sea|caye|coast)\b/i.test(text)) return 'Beachfront resort';
        if (/\bresort\b/i.test(name)) return 'Full-service resort';
        if (/\bpalace\b/i.test(name)) return 'Historic palace hotel';
        if (/\bvilla\b/i.test(name)) return 'Private villa stay';
        if (/\bmontage\b|\baman\b|\bfour seasons\b|\bcheval blanc\b|\bbelmond\b|\bmandarin oriental\b/i.test(name)) return 'Luxury flagship property';
        if (/\bboutique\b/i.test(text)) return 'Boutique design hotel';
        if (/\briad\b/i.test(name)) return 'Traditional riad';
        if (/\blodge\b/i.test(name)) return 'Wilderness lodge';
        return '';
      };
      const hotelDescriptor = synthHotelDescriptor(hotel);

      html += `
        <div class="day-section">
          <div class="day-header-block">
          <div class="day-header">
            <div style="display: flex; align-items: center; flex-wrap: wrap; gap: 8px;">
              <h2>Day ${dayIndex + 1}${day.baseCity ? ` \u00b7 ${day.baseCity}` : ''}</h2>
              ${hotelDisplay ? `<span class="hotel-badge">${hotelDisplay}</span>` : ''}
            </div>
            ${dayDate ? `<span class="date">${dayDate}</span>` : ''}
          </div>
          ${hotelDescriptor ? `<div class="hotel-descriptor" style="padding: 4px 20px 12px 20px; font-style: italic; color: #6b7280; font-size: 12px;">${hotelDisplay ? `${hotelDisplay} \u00b7 ` : ''}${hotelDescriptor}</div>` : ''}
          </div>
      `;

      // CRITICAL: Use the SAME createDayTimeline function as the UI to ensure consistency
      // This fixes the bug where PDF and UI showed different content
      const isFirstDay = dayIndex === 0;
      const isLastDayPdf = dayIndex === itinerary.days.length - 1;
      let dayTimeline = [];
      try {
        dayTimeline = createDayTimeline(day, isFirstDay, isLastDayPdf);
      } catch (timelineError) {
        debugError(`❌ PDF: Error creating timeline for Day ${dayIndex + 1}:`, timelineError);
        // Fallback: create minimal timeline from raw data
        const activities = (day?.schedule || []).map((act, idx) => ({
          ...act,
          type: 'activity',
          sortTime: parseTime(act.time) || (idx * 60 + 540)
        }));
        const meals = (day?.meals || []).map((meal, idx) => ({
          ...meal,
          type: 'meal',
          sortTime: parseTime(meal.time) || (idx * 120 + 480)
        }));
        dayTimeline = [...activities, ...meals].sort((a, b) => a.sortTime - b.sortTime);
      }

      // Convert timeline items to PDF format with sortTime for grouping
      const itemsWithTransit = dayTimeline.map(item => {
        const timeMinutes = parseTime(item.time);
        return {
          ...item,
          sortTime: timeMinutes,
          emoji: item.emoji || (item.type === 'meal' ? getMealEmoji(item.restaurant, item.cuisine, item.mealType) : getActivityEmoji(item.activity || item.title, item.description || '')),
          title: item.activity || item.restaurant || item.title || '',
          mealType: item.mealType || item.type
        };
      });

      // Group by period
      const periods = { morning: [], afternoon: [], evening: [] };
      itemsWithTransit.forEach(item => {
        // Use sortTime if available, otherwise parse from time string, fallback to morning
        let hour;
        if (item.sortTime != null && !isNaN(item.sortTime)) {
          hour = Math.floor(item.sortTime / 60);
        } else if (item.time) {
          const [h] = item.time.split(':').map(Number);
          hour = isNaN(h) ? 9 : h; // Default to morning if unparseable
        } else {
          hour = 9; // Default to morning for items with no time info
        }
        if (hour < 12) periods.morning.push(item);
        else if (hour < 17) periods.afternoon.push(item);
        else periods.evening.push(item);
      });

      const periodLabels = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };

      Object.entries(periods).forEach(([period, items]) => {
        if (items.length === 0) return;

        html += `
          <div class="period-section">
            <div class="period-divider">
              <div class="line"></div>
              <span class="label">${periodLabels[period]}</span>
              <div class="line"></div>
            </div>
        `;

        items.forEach(item => {
          const timeDisplay = formatTimeForPdf(item.time);
          const isMealItem = item.type === 'meal';
          const isTransitItem = item.type === 'transit' || item.type === 'return-transit' || item.type === 'airport-transit' || item.type === 'hotel-transit' || item.type === 'station-transit' || item.type === 'port-transit';
          const isHotelItem = item.type === 'hotel' || item.type === 'hotel-checkin' || item.type === 'hotel-checkout';
          const itemClass = isMealItem ? 'meal' : isTransitItem ? 'transit' : isHotelItem ? 'hotel-checkin' : '';

          // Hide duration on arrival/departure flight cards. Shared timeline
          // sets duration: "1.5 hours" for international customs/immigration
          // (used for time-arithmetic). Users misread it as flight duration
          // ("JFK→AKL in 1.5h"). Mobile arrival/departure cards already strip
          // it; mirroring here so the PDF matches.
          const isArrivalDepartureCard = item.type === 'arrival-customs'
            || item.type === 'arrival-domestic'
            || item.type === 'arrival-baggage'
            || item.type === 'departure'
            || item.type === 'departure-logistics'
            || item.isArrivalFlightCard
            || item.isDepartureFlightCard;
          const showDuration = item.duration && !isArrivalDepartureCard;

          // PDF-specific: rewrite the "Dinner · pick a spot" / "Lunch · pick a
          // spot" placeholder labels (shared/createDayTimeline injects these
          // when the orchestrator couldn't fill a missing meal via Google
          // Places). The "pick a spot" suffix is a CTA for the live UI; in
          // print it reads as TODO/unfinished. Substitute a complete,
          // intentional-sounding label instead.
          const isPlaceholderMeal = item.type === 'meal'
            && item._needsReplacement
            && /pick\s+a\s+spot/i.test(item.title || item.activity || '');
          const placeholderCity = (day.baseCity || day.city || '').split(',')[0].trim() || 'your city';
          const renderTitle = isPlaceholderMeal
            ? `${item.mealType ? item.mealType[0].toUpperCase() + item.mealType.slice(1) : 'Meal'} in ${item.location || placeholderCity}`
            : (item.title || item.activity || item.restaurant || '');
          html += `
            <div class="timeline-item ${itemClass}">
              <div class="tl-time ${isMealItem ? 'meal' : ''}">${timeDisplay}</div>
              <div class="item-content">
                <div class="item-header">
                  <span class="item-title">${renderTitle}</span>
                </div>
                <div class="item-meta">
                  ${showDuration ? `<span>${item.duration}</span>` : ''}
                  ${item.priceRange || item.priceTier ? `<span class="price-badge ${isMealItem ? 'meal' : ''}">${formatPriceTier(item.priceRange || item.priceTier)}</span>` : ''}
                  ${isMealItem && item.mealType ? `<span>${item.mealType}</span>` : ''}
                  ${item.cuisine ? `<span>${item.cuisine}</span>` : ''}
                </div>
                ${item.location || item.address ? `<div class="item-location">${item.location || item.address}</div>` : ''}
                ${item.from && item.to ? `<div class="item-location" style="margin-top: 4px;"><a href="https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(item.from)}&destination=${encodeURIComponent(item.to)}&travelmode=${item.travelMode === 'transit' ? 'transit' : item.travelMode === 'walking' ? 'walking' : 'driving'}" target="_blank" style="color: #1d8e85; text-decoration: none; font-size: 11px;">View route on Google Maps</a></div>` : ''}
                ${includeDetails && item.description ? `<div class="item-description">${item.description}</div>` : ''}
                ${includeDetails && item.specialties ? `<div class="item-description"><strong>Known for:</strong> ${item.specialties}</div>` : ''}
                ${includeDetails && item.notes ? `<div class="item-description">${item.notes}</div>` : ''}
              </div>
            </div>
          `;
        });

        html += `</div>`;
      });

      // Add free time suggestions if available.
      // C4: dedupe — QA found Day 1's "Free Time Ideas" listing venues
      // (Utah Olympic Park, High West Distillery) that appear as real
      // schedule items on other days. Strip any suggestion whose
      // activity/label matches a venue name anywhere else in the itinerary.
      if (includeDetails && day.freeTimeSuggestions && day.freeTimeSuggestions.length > 0) {
        const allScheduledVenues = new Set();
        (itinerary?.days || []).forEach((d) => {
          (d?.schedule || []).forEach((a) => {
            const n = (a?.activity || a?.location || '').toLowerCase().trim();
            if (n && n.length > 3) allScheduledVenues.add(n);
          });
          (d?.meals || []).forEach((m) => {
            const n = (m?.restaurant || '').toLowerCase().trim();
            if (n && n.length > 3) allScheduledVenues.add(n);
          });
          if (d?.hotel?.name) allScheduledVenues.add(d.hotel.name.toLowerCase().trim());
        });
        const filteredSuggestions = day.freeTimeSuggestions.filter((s) => {
          const label = (s?.activity || s?.label || '').toLowerCase().trim();
          if (!label) return false;
          // Drop if ANY scheduled venue string includes this label or vice-versa.
          for (const venue of allScheduledVenues) {
            if (venue === label) return false;
            if (venue.includes(label) && label.length >= 5) return false;
            if (label.includes(venue) && venue.length >= 5) return false;
          }
          return true;
        });
        if (filteredSuggestions.length > 0) {
          html += `
            <div style="margin-top: 16px; padding: 12px 16px; background: white; border-radius: 20px; border: 1.5px dashed rgba(24,184,166,0.3);">
              <div style="font-size: 13px; font-weight: 700; color: #1d8e85; margin-bottom: 8px;">Free Time Ideas</div>
              <div style="display: flex; flex-wrap: wrap; gap: 8px;">
                ${filteredSuggestions.map(s => `
                  <span style="display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: white; border-radius: 20px; font-size: 11px; color: #374151; border: 1px solid #e5e7eb;">
                    ${s.activity}${s.description ? ` — ${s.description}` : ''}
                  </span>
                `).join('')}
              </div>
            </div>
          `;
        }
      }

      html += `</div>`;
    });

    html += `
        <div class="footer">
          <div style="display: flex; justify-content: center; align-items: center; gap: 8px; margin-bottom: 6px;">
            <div style="width: 32px; height: 1px; background: linear-gradient(to right, transparent, #1d8e85);"></div>
            <div style="font-family: 'GulfsDisplay', serif; font-size: 18px; color: #1d8e85; letter-spacing: 0.08em;">arrivo</div>
            <div style="width: 32px; height: 1px; background: linear-gradient(to left, transparent, #1d8e85);"></div>
          </div>
          <div style="font-size: 10px; color: #9ca3af; letter-spacing: 0.15em; text-transform: lowercase;">plan less. explore more.</div>
          <div style="font-size: 11px; color: #6b7280; margin-top: 4px;">www.arrivotravel.ai</div>
        </div>
      </body>
      </html>
    `;

    // Print via hidden iframe (avoids Chrome popup blocker that blocks window.open)
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      // Give fonts time to load before printing
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 800);
    } else {
      // Fallback: use an iframe if popup blocker prevents window.open
      debugLog('⚠️ Popup blocked — falling back to iframe print');
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = 'none';
      document.body.appendChild(iframe);
      const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
      if (iframeDoc) {
        iframeDoc.open();
        iframeDoc.write(html);
        iframeDoc.close();
        setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
          } catch { /* cross-origin print blocked in some browsers */ }
          // Clean up iframe after print dialog closes
          setTimeout(() => { try { document.body.removeChild(iframe); } catch {} }, 2000);
        }, 800);
      }
    }

    } catch (pdfError) {
      debugError('❌ PDF generation failed:', pdfError);
      showAlertModal('PDF Error', 'There was an error generating the PDF. Please try again. If the problem persists, try regenerating any failed days first.\n\nTip: If your browser blocks popups, allow popups for this site in your browser settings.');
    }
  };

  // Export itinerary to calendar (ICS format)
  const exportItineraryAsCalendar = () => {
    if (!itinerary || !itinerary.days) {
      showAlertModal('No Itinerary', 'Please generate an itinerary first before exporting.');
      return;
    }

    const startDate = userProfile?.preferences?.startDate;
    if (!startDate) {
      showAlertModal('Dates Required', 'Please set a start date for your trip to export to calendar.');
      return;
    }

    // Helper to format date for ICS (YYYYMMDD format)
    const formatIcsDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}${month}${day}`;
    };

    // Helper to format datetime for ICS (YYYYMMDDTHHMMSS format)
    const formatIcsDateTime = (date, time) => {
      const totalMins = parseTime(time || '09:00');
      const hours = Math.floor(totalMins / 60);
      const minutes = totalMins % 60;
      const dateStr = formatIcsDate(date);
      return `${dateStr}T${String(hours).padStart(2, '0')}${String(minutes).padStart(2, '0')}00`;
    };

    // Helper to escape special characters in ICS
    const escapeIcs = (str) => {
      if (!str) return '';
      return str
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\n/g, '\\n');
    };

    // Generate unique ID for events
    const generateUid = (dayIndex, itemIndex) => {
      return `wander-${Date.now()}-day${dayIndex}-item${itemIndex}@getwander.app`;
    };

    // Parse duration string to minutes (reusable for both activities and meals)
    const parseDuration = (durationStr) => {
      const str = (durationStr || '').toLowerCase();
      let totalMins = 0;
      const hMatch = str.match(/(\d+\.?\d*)\s*(?:hour|hr|h\b)/);
      if (hMatch) totalMins += parseFloat(hMatch[1]) * 60;
      const mMatch = str.match(/(\d+\.?\d*)\s*(?:min|m\b)/);
      if (mMatch) totalMins += parseFloat(mMatch[1]);
      if (totalMins === 0) {
        const fb = str.match(/(\d+\.?\d*)/);
        if (fb) { const v = parseFloat(fb[1]); totalMins = v < 12 ? v * 60 : v; }
        else totalMins = 60;
      }
      return totalMins;
    };

    // Calculate end time from start + duration minutes
    const calcEndTime = (startTime, durationMins) => {
      const startTotalMins = parseTime(startTime);
      const endMinutes = startTotalMins + durationMins;
      const endHours = Math.floor(endMinutes / 60);
      const endMins = endMinutes % 60;
      return `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`;
    };

    // Determine destination timezone for ICS — use IANA timezone directly (DST-aware)
    const destinationCity = itinerary.days?.[0]?.baseCity || itinerary.destination?.split(',')[0] || '';
    const ianaTimezone = getCityIANATimezone(destinationCity) || 'Etc/UTC';

    // Build ICS content
    let icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//arrivo//Travel Itinerary//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      `X-WR-CALNAME:${escapeIcs(itinerary.destination || 'arrivo Trip')}`,
      `X-WR-TIMEZONE:${ianaTimezone}`,
    ];

    let globalItemIndex = 0;

    // Add arrival flight event
    if (arrivalFlight && (arrivalFlight.time || arrivalFlight.flightNumber)) {
      const arrivalDate = parseLocalDate(startDate);
      const arrivalTime = arrivalFlight.time || '12:00';
      const arrivalEndTime = calcEndTime(arrivalTime, 60); // 1 hour for deplaning + customs
      const airportName = itinerary.arrivalAirport?.name || itinerary.arrivalAirport?.code || '';
      const flightDesc = [
        arrivalFlight.flightNumber ? `Flight: ${arrivalFlight.flightNumber}` : '',
        arrivalFlight.airline ? `Airline: ${arrivalFlight.airline}` : '',
        arrivalFlight.from ? `From: ${arrivalFlight.from}` : '',
      ].filter(Boolean).join('\\n');

      icsContent.push(
        'BEGIN:VEVENT',
        `UID:${generateUid('arrival', globalItemIndex++)}`,
        `DTSTAMP:${formatIcsDateTime(new Date(), '00:00')}`,
        `DTSTART:${formatIcsDateTime(arrivalDate, arrivalTime)}`,
        `DTEND:${formatIcsDateTime(arrivalDate, arrivalEndTime)}`,
        `SUMMARY:✈️ Arrival${airportName ? ` at ${escapeIcs(airportName)}` : ''}`,
        airportName ? `LOCATION:${escapeIcs(airportName)}` : '',
        flightDesc ? `DESCRIPTION:${escapeIcs(flightDesc)}` : '',
        'END:VEVENT'
      );
    }

    itinerary.days.forEach((day, dayIndex) => {
      if (!day) return;

      const dayDate = parseLocalDate(startDate);
      dayDate.setDate(dayDate.getDate() + dayIndex);
      const isLastDay = dayIndex === itinerary.days.length - 1;

      let itemIndex = globalItemIndex;

      // Add activities
      if (day.schedule) {
        day.schedule.forEach(item => {
          const durationMinutes = parseDuration(item.duration);
          const startTime = item.time || '09:00';
          const endTime = calcEndTime(startTime, durationMinutes);

          icsContent.push(
            'BEGIN:VEVENT',
            `UID:${generateUid(dayIndex, itemIndex++)}`,
            `DTSTAMP:${formatIcsDateTime(new Date(), '00:00')}`,
            `DTSTART:${formatIcsDateTime(dayDate, startTime)}`,
            `DTEND:${formatIcsDateTime(dayDate, endTime)}`,
            `SUMMARY:${escapeIcs(item.activity || 'Activity')}`,
            item.location ? `LOCATION:${escapeIcs(item.location)}` : '',
            item.description ? `DESCRIPTION:${escapeIcs(item.description)}` : '',
            'END:VEVENT'
          );
        });
      }

      // Add meals (skip airport meals on last day)
      if (day.meals) {
        day.meals.forEach(meal => {
          // Filter out airport meals on last day
          if (isLastDay) {
            const mealName = (meal.restaurant || meal.meal || '').toLowerCase();
            const mealAddress = (meal.address || '').toLowerCase();
            const isAirportMeal = mealName.includes('terminal') || mealName.includes('airport') ||
              mealAddress.includes('terminal') || mealAddress.includes('airport') ||
              mealName.includes('lounge');
            if (isAirportMeal) return;
          }

          const mealDuration = parseDuration(meal.duration || '1 hour');
          const startTime = meal.time || '12:00';
          const endTime = calcEndTime(startTime, mealDuration);

          const mealTitle = meal.restaurant || `${meal.type || 'Meal'}`;
          const mealLocation = meal.address || meal.restaurant || '';
          const mealDescription = [
            meal.cuisine ? `Cuisine: ${meal.cuisine}` : '',
            meal.specialties ? `Known for: ${meal.specialties}` : '',
            meal.notes || ''
          ].filter(Boolean).join('\\n');

          icsContent.push(
            'BEGIN:VEVENT',
            `UID:${generateUid(dayIndex, itemIndex++)}`,
            `DTSTAMP:${formatIcsDateTime(new Date(), '00:00')}`,
            `DTSTART:${formatIcsDateTime(dayDate, startTime)}`,
            `DTEND:${formatIcsDateTime(dayDate, endTime)}`,
            `SUMMARY:🍽️ ${escapeIcs(mealTitle)}`,
            mealLocation ? `LOCATION:${escapeIcs(mealLocation)}` : '',
            mealDescription ? `DESCRIPTION:${escapeIcs(mealDescription)}` : '',
            'END:VEVENT'
          );
        });
      }

      // Add hotel check-in event for Day 1 or hotel change days
      if (day.hotel?.name && (dayIndex === 0 || (dayIndex > 0 && day.hotel?.name !== itinerary.days[dayIndex - 1]?.hotel?.name))) {
        // Derive check-in time: use first activity time minus 1 hour, or 3 PM as default
        const firstItemTime = day.schedule?.[0]?.time || day.meals?.[0]?.time;
        let checkInTime = '15:00'; // default 3 PM
        if (firstItemTime) {
          const firstMins = parseTime(firstItemTime);
          // Set check-in 1 hour before first activity, but not earlier than noon or later than 3 PM
          const checkInMins = Math.max(720, Math.min(firstMins - 60, 900));
          checkInTime = `${String(Math.floor(checkInMins / 60)).padStart(2, '0')}:${String(checkInMins % 60).padStart(2, '0')}`;
        }
        const checkInEnd = calcEndTime(checkInTime, 30); // 30 min for check-in

        icsContent.push(
          'BEGIN:VEVENT',
          `UID:${generateUid(dayIndex, itemIndex++)}`,
          `DTSTAMP:${formatIcsDateTime(new Date(), '00:00')}`,
          `DTSTART:${formatIcsDateTime(dayDate, checkInTime)}`,
          `DTEND:${formatIcsDateTime(dayDate, checkInEnd)}`,
          `SUMMARY:🏨 Check-in: ${escapeIcs(day.hotel.name)}`,
          day.hotel.address ? `LOCATION:${escapeIcs(day.hotel.address)}` : '',
          'DESCRIPTION:Hotel check-in',
          'END:VEVENT'
        );
      }

      globalItemIndex = itemIndex;
    });

    // Add departure flight event
    if (departureFlight && (departureFlight.time || departureFlight.flightNumber)) {
      const lastDayDate = parseLocalDate(startDate);
      lastDayDate.setDate(lastDayDate.getDate() + (itinerary.days.length - 1));
      const depTime = departureFlight.time || '18:00';
      // Check-in 2 hours before departure
      const checkInMins = Math.max(0, parseTime(depTime) - 120);
      const checkInTime = `${String(Math.floor(checkInMins / 60)).padStart(2, '0')}:${String(checkInMins % 60).padStart(2, '0')}`;
      const airportName = itinerary.departureAirport?.name || itinerary.departureAirport?.code || '';
      const flightDesc = [
        departureFlight.flightNumber ? `Flight: ${departureFlight.flightNumber}` : '',
        departureFlight.airline ? `Airline: ${departureFlight.airline}` : '',
        departureFlight.to ? `To: ${departureFlight.to}` : '',
      ].filter(Boolean).join('\\n');

      icsContent.push(
        'BEGIN:VEVENT',
        `UID:${generateUid('departure', globalItemIndex++)}`,
        `DTSTAMP:${formatIcsDateTime(new Date(), '00:00')}`,
        `DTSTART:${formatIcsDateTime(lastDayDate, checkInTime)}`,
        `DTEND:${formatIcsDateTime(lastDayDate, depTime)}`,
        `SUMMARY:✈️ Departure${airportName ? ` from ${escapeIcs(airportName)}` : ''}`,
        airportName ? `LOCATION:${escapeIcs(airportName)}` : '',
        flightDesc ? `DESCRIPTION:${escapeIcs(flightDesc)}` : '',
        'END:VEVENT'
      );
    }

    icsContent.push('END:VCALENDAR');

    // Filter out empty lines and join
    const icsString = icsContent.filter(line => line).join('\r\n');

    // Create and download the file
    const blob = new Blob([icsString], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(itinerary.destination || 'arrivo-trip').replace(/[^a-z0-9]/gi, '-').toLowerCase()}-itinerary.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Export itinerary budget as CSV
  const exportBudgetCSV = () => {
    if (!itinerary || !itinerary.days) {
      showAlertModal('No Itinerary', 'Please generate an itinerary first before exporting.');
      return;
    }

    const rows = [['Day', 'Time', 'Activity', 'Type', 'Estimated Cost', 'Location']];

    itinerary.days.forEach(day => {
      (day.schedule || []).forEach(item => {
        rows.push([
          `Day ${day.day}`,
          item.time || '',
          item.activity || '',
          item.type || 'activity',
          item.estimatedCost || item.priceTier || '',
          item.address || item.location || '',
        ]);
      });

      (day.meals || []).forEach(item => {
        rows.push([
          `Day ${day.day}`,
          item.time || '',
          item.restaurant || item.meal || '',
          item.type || item.mealType || 'meal',
          item.estimatedCost || item.priceRange || '',
          item.address || item.location || '',
        ]);
      });

      // Add hotel
      if (day.hotel?.name) {
        rows.push([
          `Day ${day.day}`,
          'Night',
          day.hotel.name,
          'Hotel',
          day.hotel.estimatedPrice || day.hotel.priceRange || '',
          day.hotel.address || '',
        ]);
      }
    });

    const csv = rows.map(row => row.map(cell => `"${(cell || '').toString().replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const tripTitle = (itinerary.destination || 'arrivo-trip').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    a.download = `${tripTitle}-budget.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Helper functions for showing branded modals
  const showConfirmModal = (options) => {
    return new Promise((resolve) => {
      setConfirmModal({
        isOpen: true,
        title: options.title || 'Confirm',
        message: options.message || '',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'confirm',
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          resolve(true);
        },
        onCancel: () => {
          setConfirmModal(prev => ({ ...prev, isOpen: false }));
          resolve(false);
        }
      });
    });
  };

  const showAlertModal = (title, message) => {
    setConfirmModal({
      isOpen: true,
      title,
      message,
      confirmText: 'OK',
      cancelText: null,
      type: 'alert',
      onConfirm: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
      onCancel: null
    });
  };

  // Timeout wrapper for async operations — rejects after `ms` with custom error
  const withTimeout = (promise, ms, msg = 'Request timed out') =>
    Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error(msg)), ms))]);

  // Lightweight toast notification — auto-dismisses after `duration` ms
  // Types: 'success' (green), 'error' (red), 'info' (blue)
  // Optional `action` param: { label: string, onClick: () => void }
  const showToast = (message, type = 'success', duration = 3500, action = null) => {
    const id = Date.now() + Math.random();
    setToasts(prev => [...prev, { id, message, type, action }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), duration);
  };

  const showUpgradeModal = (context = '') => {
    setUpgradeContext(context);
    setShowUpgradeModalOpen(true);
  };

  // Stripe checkout — redirect user to Stripe payment page
  const handleStripeCheckout = async (interval = 'monthly') => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const headers = await getApiHeaders();
      const res = await fetch(`${BACKEND_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ interval }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        showAlertModal('Error', data.error || 'Could not start checkout. Please try again.');
      }
    } catch (err) {
      console.error('Stripe checkout error:', err);
      showAlertModal('Error', 'Could not connect to billing service. Please try again.');
    }
  };

  // Stripe customer portal — manage existing subscription
  const handleStripePortal = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const headers = await getApiHeaders();
      const res = await fetch(`${BACKEND_URL}/api/billing/create-portal-session`, {
        method: 'POST',
        headers,
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        showAlertModal('Error', data.error || 'Could not open billing portal.');
      }
    } catch (err) {
      console.error('Stripe portal error:', err);
      showAlertModal('Error', 'Could not connect to billing service.');
    }
  };

  // Verify Stripe session after checkout redirect — updates tier as webhook fallback
  useEffect(() => {
    if (stage !== 'checkout-success') return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    const verifySession = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
        const headers = await getApiHeaders();
        const res = await fetch(`${BACKEND_URL}/api/billing/verify-session`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ sessionId }),
        });
        const data = await res.json();
        if (data.tier === 'planner') {
          console.log('[Billing] Session verified — tier updated to planner');
          if (typeof refreshProfile === 'function') refreshProfile();
        }
      } catch (err) {
        console.warn('[Billing] verify-session failed (webhook may handle it):', err.message);
      }
    };
    verifySession();
  }, [stage]); // eslint-disable-line react-hooks/exhaustive-deps

  // Navigate to pricing page
  const goToPricing = () => {
    window.history.pushState({}, '', '/pricing');
    setStage('pricing');
  };

  // Manual save itinerary function
  const handleSaveItinerary = async () => {
    if (!itinerary) { debugLog('💾 Save skipped: no itinerary'); return false; }
    if (!user) { debugLog('💾 Save skipped: no user (not logged in?)'); return false; }
    if (isSavingItinerary) { debugLog('💾 Save skipped: already saving'); return false; }

    // Check if user's tier allows saving itineraries
    if (!hasFeature('canSaveItineraries')) {
      showUpgradeModal();
      return false;
    }

    setIsSavingItinerary(true);
    try {
      // For multi-stop trips, use actual city names instead of "2-Stop Trip"
      const destination = selectedDestination?.stops?.length > 1
        ? selectedDestination.stops.map(s => s.name).join(' & ')
        : (selectedDestination?.name || itinerary.destination || 'Trip');
      const tripDuration = itinerary.days?.length || 0;
      const startDate = userProfile?.preferences?.startDate || null;
      const endDate = userProfile?.preferences?.endDate || null;
      const title = `${destination} - ${tripDuration} days`;

      // Include transport selections (flights, modes) in saved data for persistence
      const itineraryWithTransport = {
        ...itinerary,
        _legTransports: legTransports,
        _hasOvernightOutbound: hasOvernightOutbound,
        _overnightFlightInfo: overnightFlightInfo,
      };

      debugLog('💾 Saving itinerary...', { destination, title, userId: user.id, hasDays: itinerary.days?.length });
      // Wrap saveItinerary with a 15-second timeout to prevent hanging
      const saveResult = await Promise.race([
        saveItinerary(user.id, itineraryWithTransport, title, destination, startDate, endDate),
        new Promise((_, reject) => setTimeout(() => reject(new Error('Save timed out after 15 seconds')), 15000))
      ]);

      if (!saveResult?.id) {
        // Save returned success but no ID — likely an RLS or DB issue
        throw new Error('Save completed but no confirmation received from database. Please try again.');
      }

      setLastSavedAt(new Date());
      setHasUnsavedChanges(false);

      // Update itinerary ID (always update — could be saving modified version)
      setCurrentItineraryId(saveResult.id);
      setCurrentTripId(saveResult.id);
      try { sessionStorage.setItem('wander_current_itinerary_id', saveResult.id); } catch {}
      debugLog('✅ Itinerary saved manually with ID:', saveResult.id);

      // Notify other tabs/devices
      broadcastItineraryChange(saveResult?.updated ? 'UPDATE' : 'INSERT', { destination });

      // Show "Saved!" or "Updated!" on the button temporarily
      const buttonText = saveResult?.updated ? 'Updated!' : 'Saved!';
      setSaveButtonText(buttonText);
      setTimeout(() => setSaveButtonText(null), 2500);

      // Prompt to publish to community after first save (not updates)
      if (!saveResult?.updated) {
        setTimeout(async () => {
          const shouldPublish = await showConfirmModal({
            title: 'Share with the community?',
            message: 'Your itinerary looks great! Publishing lets other travelers discover and get inspired by your trip.',
            confirmText: 'Publish',
            cancelText: 'Maybe later',
            type: 'confirm',
          });
          if (shouldPublish && saveResult.id) {
            try {
              await publishToCommunity(saveResult.id);
              showToast('Published to the community!', 'success');
            } catch (err) {
              showToast('Could not publish — try again from Settings', 'error');
            }
          }
          // Then prompt to rank — but ONLY if the trip is already over.
          // Hope's feedback: "Love this idea, but haven't gone on the trip yet.
          // Can this pop-up after?" — Previously fired 1.5s after save, regardless
          // of whether the user had actually traveled.
          if (features.COMMUNITY_RANKINGS) {
            const tripEndDate = userProfile?.preferences?.endDate
              || (userProfile?.preferences?.startDate && userProfile?.preferences?.duration
                ? new Date(new Date(userProfile.preferences.startDate).getTime() + (userProfile.preferences.duration * 86400000)).toISOString().slice(0, 10)
                : null);
            const tripIsOver = tripEndDate && new Date(tripEndDate) < new Date();
            if (tripIsOver) {
              setTimeout(() => setShowRankPrompt(true), 1500);
            }
          }
        }, 2000);
      }
      return true;
    } catch (error) {
      debugError('Error saving itinerary:', error);
      // Show persistent error toast so user knows save failed
      showToast(`Could not save: ${error.message || 'Unknown error'}. Please try again.`, 'error', 6000);
      setSaveButtonText('Save Failed');
      setTimeout(() => setSaveButtonText(null), 5000);
      return false;
    } finally {
      setIsSavingItinerary(false);
    }
  };

  // Debounced auto-save: persist itinerary modifications after 10 seconds of inactivity
  useEffect(() => {
    if (!hasUnsavedChanges || !itinerary || !user || !isLoggedIn || !hasFeature('canSaveItineraries')) return;
    // Only auto-save if we have a real database ID (not temp)
    if (!currentItineraryId || currentItineraryId.startsWith('temp_')) return;

    const timer = setTimeout(async () => {
      try {
        // For multi-stop trips, use actual city names instead of "2-Stop Trip"
        const destination = selectedDestination?.stops?.length > 1
          ? selectedDestination.stops.map(s => s.name).join(' & ')
          : (selectedDestination?.name || itinerary.destination || 'Trip');
        const tripDuration = itinerary.days?.length || 0;
        const startDate = userProfile?.preferences?.startDate || null;
        const endDate = userProfile?.preferences?.endDate || null;
        const title = `${destination} - ${tripDuration} days`;

        await saveItinerary(user.id, itinerary, title, destination, startDate, endDate);
        setLastSavedAt(new Date());
        setHasUnsavedChanges(false);
        debugLog('✅ Auto-saved itinerary after modification');
      } catch (err) {
        debugError('Auto-save after modification failed:', err);
        // Don't clear hasUnsavedChanges so user knows to save manually
      }
    }, 10000); // 10 second debounce

    return () => clearTimeout(timer);
  }, [hasUnsavedChanges, itinerary, user, isLoggedIn, currentItineraryId]);

  // Warn user before leaving with unsaved changes
  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Load saved itineraries from database
  const handleOpenSavedItineraries = async () => {
    if (!user) {
      showAlertModal('Sign In Required', 'Please sign in to view your saved itineraries.');
      return;
    }

    setShowSavedItineraries(true);
    setIsLoadingSavedItineraries(true);

    try {
      const itineraries = await getSavedItineraries(user.id);
      setSavedItinerariesList(itineraries || []);
    } catch (error) {
      debugError('Error loading saved itineraries:', error);
      showAlertModal('Load Failed', 'Failed to load saved itineraries. Please try again.');
    } finally {
      setIsLoadingSavedItineraries(false);
    }
  };

  // Load a saved itinerary
  const handleLoadItinerary = async (savedItem) => {
    if (!savedItem?.itinerary_data) {
      showAlertModal('Error', 'This itinerary appears to be corrupted.');
      return;
    }

    // Normalize loaded itinerary through unified pipeline
    const loadedData = savedItem.itinerary_data;
    if (loadedData.days?.length > 0) {
      const loadNormResult = normalizeItinerary(loadedData, {
        destination: savedItem.destination || loadedData.destination || '',
      });
      setItinerary(loadNormResult.itinerary);
      debugLog(`📋 Loaded itinerary normalized: ${loadNormResult.dayResults.reduce((sum, r) => sum + r.corrections.length, 0)} corrections`);
    } else {
      setItinerary(loadedData);
    }

    // Set the itinerary ID for modification tracking
    setCurrentItineraryId(savedItem.id);
    setCurrentTripId(savedItem.id);
    try { sessionStorage.setItem('wander_current_itinerary_id', savedItem.id); } catch {}

    // Set destination — also reconstruct stops from itinerary days for multi-city trips
    if (savedItem.destination) {
      // Derive stops from unique baseCities in the itinerary days
      const loadedDays = loadedData.days || [];
      const uniqueCities = [];
      const seenCities = new Set();
      loadedDays.forEach(d => {
        const city = d.baseCity || d.city;
        if (city && !seenCities.has(city)) {
          seenCities.add(city);
          uniqueCities.push({ name: city, stopType: d.stopType || 'city' });
        }
      });
      setSelectedDestination({
        name: savedItem.destination,
        stops: uniqueCities.length > 1 ? uniqueCities : undefined,
      });
    }

    // Load feedback from Supabase
    if (user && savedItem.id) {
      try {
        const feedbackData = await getItineraryFeedback(user.id, savedItem.id);
        if (feedbackData && feedbackData.length > 0) {
          const activityFb = {};
          const mealFb = {};
          const reasons = {};

          feedbackData.forEach(fb => {
            const key = `day${fb.day_index}-${fb.feedback_type === 'activity' ? 'activity' : 'meal'}-${fb.item_index}`;
            if (fb.feedback_type === 'activity') {
              activityFb[key] = fb.rating;
            } else {
              mealFb[key] = fb.rating;
            }
            if (fb.reason) {
              reasons[key] = fb.reason;
            }
          });

          setActivityFeedback(activityFb);
          setMealFeedback(mealFb);
          setFeedbackReasons(reasons);
          debugLog(`✅ Loaded ${feedbackData.length} feedback items from Supabase`);
        }
      } catch (error) {
        debugLog('Failed to load feedback from Supabase:', error);
        // Non-blocking - feedback is optional
      }
    }

    // Reset state that could be stale from a previous trip
    setTransitTimes({});
    transitTimesRef.current = {};
    userHasSetTransportRef.current = false;
    setItineraryChatMessages([]);
    setPendingChanges(null);
    setDayRatings({});
    setFeedbackCount(0);
    setIsProgressiveGenerating(false);
    setTotalItineraryDays(0);
    setCurrentlyGeneratingDay(null);
    setDayGenerationStatus({});
    setDayGenerationErrors({});
    generationInProgressRef.current = false;

    // Infer transportation mode from itinerary data
    const loadedItinerary = savedItem.itinerary_data;
    if (loadedItinerary.arrivalAirport || loadedItinerary.departureAirport) {
      setTransportationMode('fly');
    } else if (loadedItinerary.days?.[0]?.schedule?.some(a =>
      (a.activity || '').toLowerCase().includes('drive') || (a.activity || '').toLowerCase().includes('road trip')
    )) {
      setTransportationMode('drive-own');
    }

    // Restore flight details from saved data
    if (loadedItinerary._legTransports) {
      setLegTransports(loadedItinerary._legTransports);
    }
    if (loadedItinerary._hasOvernightOutbound) {
      setHasOvernightOutbound(true);
      setOvernightFlightInfo(loadedItinerary._overnightFlightInfo || null);
    } else {
      setHasOvernightOutbound(false);
      setOvernightFlightInfo(null);
    }
    // Reset flight details to defaults (will be populated from itinerary display)
    setArrivalFlight({ time: '08:00', flightNumber: '', airline: '', from: '', date: '' });
    setDepartureFlight({ time: '18:00', flightNumber: '', airline: '', to: '', date: '' });

    // Set duration from loaded itinerary
    if (loadedItinerary.days?.length) {
      setUserProfile(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          duration: loadedItinerary.days.length,
          startDate: savedItem.start_date || prev.preferences.startDate,
          endDate: savedItem.end_date || prev.preferences.endDate
        }
      }));
    }

    // Close modal and go to itinerary view — reset all popup states
    setShowSavedItineraries(false);
    setHotelAlternatives(null);
    setShowHotelDetails({});
    setExpandedFlightPrices({});
    setStage('itinerary');
    setCurrentDay(0);
    setWeatherFetchTrigger(t => t + 1); // Ensure weather fetches for loaded itinerary

    debugLog('✅ Loaded saved itinerary:', savedItem.title);
  };

  // Delete a saved itinerary
  const handleDeleteSavedItinerary = async (itineraryId) => {
    if (!user) return;

    const confirmed = await showConfirmModal({ title: 'Delete Itinerary', message: 'Are you sure you want to delete this itinerary? This cannot be undone.', confirmText: 'Delete', type: 'danger' });
    if (!confirmed) return;

    try {
      await deleteItinerary(itineraryId, user.id);
      // Remove from local list
      setSavedItinerariesList(prev => prev.filter(item => item.id !== itineraryId));
      // Notify other tabs/devices
      broadcastItineraryChange('DELETE', { id: itineraryId });
      debugLog('✅ Deleted itinerary:', itineraryId);
    } catch (error) {
      debugError('Error deleting itinerary:', error);
      showAlertModal('Delete Failed', 'Failed to delete itinerary. Please try again.');
    }
  };

  // Rename a saved itinerary. Hope's feedback: "Can I name this myself? Like
  // Game Family Summer Trip 2026, etc." Empty string clears the override and
  // reverts to the auto-generated display.
  const handleRenameSavedItinerary = async (itineraryId, currentName) => {
    if (!user) return;
    // Guard against rename calls with missing IDs — would silently fail at the
    // backend and confuse the user. This shouldn't happen via the UI but the
    // kebab handler doesn't enforce it.
    if (!itineraryId) {
      showAlertModal('Cannot Rename', 'This trip is missing an ID and cannot be renamed.');
      return;
    }
    // window.prompt is the fastest path to ship; design can later replace
    // with a styled modal that matches the rest of the app.
    const newName = window.prompt('Rename this trip', currentName || '');
    if (newName === null) return; // Cancel
    try {
      await updateItineraryTitle(itineraryId, user.id, newName);
      // Optimistic local update
      setSavedItinerariesList(prev => prev.map(item =>
        item.id === itineraryId
          ? { ...item, custom_title: newName.trim() || null }
          : item
      ));
      broadcastItineraryChange('UPDATE', { id: itineraryId });
      showToast(newName.trim() ? 'Trip renamed' : 'Trip name reset', 'success');
    } catch (error) {
      debugError('Error renaming itinerary:', error);
      showAlertModal('Rename Failed', 'Could not rename. Please try again.');
    }
  };

  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', JPY: '¥', CNY: '¥', INR: '₹',
    CHF: 'CHF', SEK: 'kr', NOK: 'kr', DKK: 'kr', PLN: 'zł', CZK: 'Kč', HUF: 'Ft', RON: 'lei',
    SGD: 'S$', HKD: 'HK$', TWD: 'NT$', KRW: '₩', THB: '฿', MYR: 'RM', IDR: 'Rp', PHP: '₱', VND: '₫',
    AED: 'AED', SAR: 'SAR', ILS: '₪', TRY: '₺',
    MXN: 'MX$', BRL: 'R$', ARS: 'ARS', CLP: 'CLP', COP: 'COP', PEN: 'S/',
    ZAR: 'R', EGP: 'E£', MAD: 'MAD', KES: 'KSh',
    NZD: 'NZ$', RUB: '₽', ISK: 'kr', BGN: 'лв', RSD: 'din'
  };
  
  // Get currency based on city — delegates to shared currencyUtils with getCityCountrySync fallback
  const getCurrencyForCity = (city) => {
    // First try the shared implementation (has accent normalization + country fallback)
    const sharedResult = _getCurrencyForCityShared(city);
    if (sharedResult !== 'USD') return sharedResult;
    // Extra fallback: use getCityCountrySync (monolith-specific, uses dynamic cache)
    if (city && typeof getCityCountrySync === 'function') {
      const country = getCityCountrySync(city);
      if (country) {
        // Try shared again with "City, Country" format
        const withCountry = _getCurrencyForCityShared(`${city}, ${country}`);
        if (withCountry !== 'USD') return withCountry;
      }
    }
    return sharedResult;
  };
  
  // Get local currency for the destination (for display in itinerary)
  const getDestinationCurrency = () => {
    if (!selectedDestination?.name && !itinerary?.destination) return 'USD';
    const destination = selectedDestination?.name || itinerary?.destination || '';
    return getCurrencyForCity(destination);
  };
  
  // Static fallback exchange rates from USD — used when live rates are unavailable.
  const STATIC_USD_EXCHANGE_RATES = {
    USD: 1, EUR: 0.92, GBP: 0.79, CAD: 1.36, AUD: 1.53, JPY: 150, CNY: 7.2, INR: 83,
    CHF: 0.88, SEK: 10.5, NOK: 10.8, DKK: 6.9, PLN: 4.0, CZK: 23, HUF: 360, RON: 4.6,
    SGD: 1.34, HKD: 7.8, TWD: 32, KRW: 1330, THB: 35, MYR: 4.7, IDR: 15700, PHP: 56, VND: 24500,
    AED: 3.67, SAR: 3.75, ILS: 3.7, TRY: 32,
    MXN: 17, BRL: 5.0, ARS: 870, CLP: 940, COP: 3950, PEN: 3.7,
    ZAR: 18.5, EGP: 48, MAD: 10, KES: 153,
    NZD: 1.64, RUB: 92, ISK: 137, BGN: 1.8,
  };

  /** Convert a USD amount to destination currency using live rates (or static fallback) */
  const convertUSDToLocal = (usdAmount, currency) => {
    if (!currency || currency === 'USD') return usdAmount;
    const rates = liveExchangeRates || STATIC_USD_EXCHANGE_RATES; // eslint-disable-line no-undef
    const rate = rates[currency];
    if (!rate) return usdAmount; // Unknown currency, show as-is
    return Math.round(usdAmount * rate);
  };

  // Format price in destination's local currency
  // overrideCurrency: pass an explicit currency code (e.g. from Amadeus) — when set, amount is assumed
  //   to ALREADY be in that currency (no conversion needed, just formatting)
  // Hotel price formatting — delegates to the shared `formatDualCurrency`
  // util so web and mobile render identical strings (e.g. "AR$157k · ~$175").
  // Previously this function had an inline implementation that diverged
  // from mobile's format ("~¥40,000 ($320)" vs mobile's "¥40k · ~$267").
  // Using the shared util keeps both platforms in lock-step.
  const formatHotelPrice = (pricePerNight, priceCurrency, baseCity) => {
    if (!pricePerNight) return null;
    return _formatDualCurrencyShared(pricePerNight, {
      amountCurrency: priceCurrency,
      baseCity: baseCity || '',
      preferredCurrency: 'USD',
    }) || formatLocalPrice(pricePerNight, true, priceCurrency || null);
  };

  const formatLocalPrice = (amount, useDestinationCurrency = true, overrideCurrency = null) => {
    // BUG 3 FIX: Sanitize input — handle string amounts with embedded currency symbols
    // LLM sometimes returns "KSh1200" instead of 1200, causing double-prefix like "KShKSh1200"
    let cleanAmount = amount;
    if (typeof amount === 'string') {
      // Strip all non-numeric chars except decimal point and minus
      cleanAmount = parseFloat(amount.replace(/[^0-9.\-]/g, ''));
    }
    // Handle undefined, null, NaN, or invalid amounts
    if (cleanAmount === undefined || cleanAmount === null || isNaN(cleanAmount)) {
      return 'Price TBD';
    }

    const currency = overrideCurrency || (useDestinationCurrency ? getDestinationCurrency() : (userProfile.preferences.currency || 'USD'));
    const symbol = currencySymbols[currency] || '$';

    // Convert from USD to local currency — but only when no overrideCurrency is set
    // (overrideCurrency means the amount is already in the correct currency, e.g. from Amadeus)
    let displayAmount = cleanAmount;
    let usdForHint = null; // non-null when we should append "($XX USD)"
    if (!overrideCurrency && currency !== 'USD') {
      displayAmount = convertUSDToLocal(cleanAmount, currency);
      usdForHint = cleanAmount; // cleanAmount IS the USD value
    } else if (overrideCurrency && currency !== 'USD') {
      // overrideCurrency means the amount is ALREADY in the local currency.
      // Best-effort convert back to USD for the hint (inverse lookup).
      const toUsd = convertUSDToLocal(1, currency); // local-per-USD rate
      if (toUsd && toUsd > 0) usdForHint = cleanAmount / toUsd;
    }

    // Format based on currency conventions — no decimals for whole-number currencies
    const localStr = `${symbol}${Math.round(displayAmount).toLocaleString()}`;

    // Append USD equivalent when we have one, to prevent a misread like
    // "$54,819" for a Costa Rica budget (which is actually ₡54,819 ≈ $110).
    // Skip the hint for very small amounts where the parens would be noise.
    if (usdForHint != null && Math.abs(usdForHint) >= 1) {
      const usdStr = usdForHint >= 100
        ? `$${Math.round(usdForHint).toLocaleString()}`
        : `$${usdForHint.toFixed(2)}`;
      return `${localStr} (${usdStr})`;
    }
    return localStr;
  };
  
  // Match activity text against user interests — returns first matched interest label or null
  const INTEREST_KEYWORDS = {
    'History & Culture': ['history', 'heritage', 'ancient', 'ruins', 'temple', 'church', 'cathedral', 'palace', 'castle', 'monument', 'memorial', 'cultural', 'tradition', 'mosque', 'shrine', 'fortress', 'archaeological', 'historic'],
    'Food & Dining': ['food', 'cuisine', 'culinary', 'cooking class', 'food tour', 'tasting', 'street food', 'gastronomy', 'wine', 'brewery', 'distillery', 'chocolate', 'coffee', 'tea ceremony', 'market food'],
    'Nature & Outdoors': ['nature', 'hike', 'hiking', 'trail', 'park', 'garden', 'botanical', 'waterfall', 'mountain', 'lake', 'river', 'canyon', 'forest', 'jungle', 'wildlife', 'safari', 'beach', 'scenic', 'lookout', 'viewpoint', 'sunrise', 'sunset'],
    'Adventure Sports': ['surf', 'diving', 'snorkel', 'kayak', 'rafting', 'zip line', 'zipline', 'bungee', 'paraglid', 'skydiv', 'climb', 'rock climbing', 'scuba', 'paddleboard', 'cycling', 'mountain bike', 'canyoning', 'adventure'],
    'Art & Museums': ['museum', 'gallery', 'art', 'exhibit', 'installation', 'contemporary', 'sculpture', 'painting', 'mural', 'street art', 'studio'],
    'Shopping': ['shopping', 'market', 'bazaar', 'souk', 'boutique', 'mall', 'antique', 'souvenir', 'flea market', 'artisan'],
    'Nightlife': ['nightlife', 'bar', 'club', 'pub', 'cocktail', 'rooftop', 'live music', 'jazz', 'cabaret', 'show', 'theater', 'theatre', 'concert', 'evening entertainment'],
    'Relaxation': ['spa', 'wellness', 'massage', 'hot spring', 'onsen', 'hammam', 'thermal', 'relax', 'meditation', 'yoga', 'retreat'],
    'Photography': ['photo', 'photography', 'instagram', 'panoramic', 'scenic view', 'viewpoint', 'observation deck', 'lookout'],
  };
  const matchActivityToInterest = (item, userInterests) => {
    if (!userInterests?.length) return null;
    const text = `${item.activity || ''} ${item.description || ''} ${item.title || ''} ${item.type || ''} ${(item.tags || []).join(' ')}`.toLowerCase();
    for (const interest of userInterests) {
      const keywords = INTEREST_KEYWORDS[interest];
      if (keywords && keywords.some(kw => text.includes(kw))) return interest;
    }
    return null;
  };

  // Convert price tier ($, $$, $$$) to local currency symbol — delegates to shared currencyUtils
  const formatPriceTier = (priceTier, currencyCode) => {
    const currency = currencyCode || getDestinationCurrency();
    return _formatPriceTierShared(priceTier, currency);
  };
  
  // Update currency when home city changes
  useEffect(() => {
    if (userProfile.preferences.homeCity) {
      const detectedCurrency = getCurrencyForCity(userProfile.preferences.homeCity);
      if (detectedCurrency !== userProfile.preferences.currency) {
        debugLog(`💱 Auto-setting currency to ${detectedCurrency} based on ${userProfile.preferences.homeCity}`);
        setUserProfile(prev => ({
          ...prev,
          preferences: { ...prev.preferences, currency: detectedCurrency }
        }));
      }
    }
  }, [userProfile.preferences.homeCity]);

  // When homeCity changes AFTER itinerary generation, update departure airport
  // e.g., user changes from NYC to Pinhão — departure airport should change from JFK to OPO
  useEffect(() => {
    const newHomeCity = userProfile.preferences.homeCity;
    if (!newHomeCity || !itinerary?.days?.length || !selectedDestination?.name) return;

    // BUG 2 FIX: Don't overwrite airports during progressive generation — wait until complete
    if (isProgressiveGenerating) return;

    // BUG 2 FIX: Don't overwrite user's explicit airport selection from personalization page
    const userExplicitDeparture = userProfile.preferences.departureAirport;
    if (userExplicitDeparture && itinerary?.departureAirport?.code === userExplicitDeparture) {
      return; // User's selection is already applied
    }

    // Determine the city we're departing FROM (last stop of the trip)
    const lastDay = itinerary.days[itinerary.days.length - 1];
    const departureCity = lastDay?.baseCity || selectedDestination.name;

    // If flying out, re-resolve the departure airport for the destination area (not home city)
    // The departure airport is where you FLY FROM (near the destination), not where you fly TO
    if (effectiveReturnMode === 'fly' && departureCity) {
      // Check if current departure airport makes sense for the destination
      const currentDep = itinerary.departureAirport;
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";

      // Re-fetch transport info for the departure city to get correct airport
      (async () => {
        try {
          const response = await fetch(`${BACKEND_URL}/api/destination-transport-info`, {
            method: 'POST',
            headers: await getApiHeaders(),
            body: JSON.stringify({ destination: departureCity })
          });
          if (response.ok) {
            const data = await response.json();
            if (data.nearestAirport?.code) {
              const newAirport = data.nearestAirport;
              // Only update if it's different from current
              if (!currentDep || currentDep.code !== newAirport.code) {
                debugLog(`✈️ HomeCity changed to ${newHomeCity} — updating departure airport: ${currentDep?.code || 'none'} → ${newAirport.code} (nearest to ${departureCity})`);
                setItinerary(prev => ({
                  ...prev,
                  departureAirport: {
                    code: newAirport.code,
                    name: newAirport.name || `${newAirport.code} Airport`,
                    city: newAirport.city || departureCity
                  }
                }));
                // Also update the user profile departure airport preference
                setUserProfile(prev => ({
                  ...prev,
                  preferences: { ...prev.preferences, departureAirport: newAirport.code }
                }));
              }
            }
          }
        } catch (err) {
          debugError('❌ Failed to re-resolve departure airport after homeCity change:', err);
        }
      })();
    }

    // Also recalculate drive time for the new home city (with country context for small towns)
    if (transportationMode !== 'fly') {
      (async () => {
        const [originCountry, destCountry] = await Promise.all([
          getCityCountry(newHomeCity),
          getCityCountry(selectedDestination.name)
        ]);
        calculateDriveTimeToDestination(newHomeCity, selectedDestination.name, {
          originCountry: originCountry || undefined,
          destCountry: destCountry || undefined
        });
      })();
    }
  }, [userProfile.preferences.homeCity, itinerary?.days?.length, effectiveReturnMode]);

  // Note: All preferences are now saved to Supabase via user_profiles
  // No localStorage needed since users must be logged in

  const formatCurrency = (amount, currency = userProfile.preferences.currency || 'USD') => {
    // Default to USD if currency not found in our rates
    const effectiveCurrency = currencyRates[currency] ? currency : 'USD';
    const rate = currencyRates[effectiveCurrency] || 1;
    const converted = amount * rate;
    const symbol = currencySymbols[effectiveCurrency] || '$';
    
    // Format based on currency conventions (no decimals for high-value currencies)
    if (['JPY', 'KRW', 'VND', 'IDR', 'HUF', 'CLP', 'COP', 'RUB'].includes(effectiveCurrency)) {
      return `${symbol}${Math.round(converted).toLocaleString()}`;
    }
    return `${symbol}${Math.round(converted).toLocaleString()}`;
  };
  
  // Get currency symbol for display
  const getCurrencySymbol = (currency = userProfile.preferences.currency || 'USD') => {
    return currencySymbols[currency] || '$';
  };

  // Flight lookup function — searches by flight number using the backend proxy
  const lookupFlight = async (flightNumber, type) => {
    setLookingUpFlight(true);
    try {
      const cleanedNum = flightNumber.toUpperCase().replace(/\s+/g, '');
      // Parse carrier code (first 2 chars) and flight number (rest)
      const carrierMatch = cleanedNum.match(/^([A-Z0-9]{2})(\d+)$/);
      if (!carrierMatch) {
        showAlertModal('Invalid Format', 'Please enter a flight number like "WS123" or "AC456" (airline code + number).');
        setLookingUpFlight(false);
        return;
      }
      const [, carrierCode, flightNum] = carrierMatch;
      const airlineName = resolveAirlineName(carrierCode, {});

      // Use the trip date if available, otherwise use today
      const tripDate = type === 'arrival'
        ? (userProfile?.preferences?.startDate || new Date().toISOString().split('T')[0])
        : (userProfile?.preferences?.endDate || (() => {
            const start = userProfile?.preferences?.startDate;
            const dur = userProfile?.preferences?.duration;
            if (start && dur) {
              const d = new Date(start + 'T12:00:00');
              d.setDate(d.getDate() + dur - 1);
              return d.toISOString().split('T')[0];
            }
            return new Date().toISOString().split('T')[0];
          })());

      // Use the Amadeus search API through backend proxy to find this flight
      // We search for flights on the route on the given date and look for matching flight number
      const homeAirport = type === 'arrival'
        ? (itinerary?.arrivalAirport?.homeCode || userProfile?.preferences?.homeAirport || '')
        : (itinerary?.departureAirport?.code || itinerary?.arrivalAirport?.code || '');
      const destAirport = type === 'arrival'
        ? (itinerary?.arrivalAirport?.code || '')
        : (itinerary?.departureAirport?.homeCode || userProfile?.preferences?.homeAirport || '');

      const originCode = type === 'arrival' ? homeAirport : destAirport || homeAirport;
      const destinationCode = type === 'arrival' ? destAirport || homeAirport : homeAirport;

      if (!originCode || !destinationCode) {
        // Can't search without airport codes — set what we know manually
        const flightInfo = {
          flightNumber: cleanedNum,
          airline: airlineName || carrierCode,
          from: type === 'arrival' ? (userProfile?.preferences?.homeAirport || '') : (itinerary?.departureAirport?.code || ''),
          to: type === 'arrival' ? (itinerary?.arrivalAirport?.code || '') : (userProfile?.preferences?.homeAirport || ''),
          time: type === 'arrival' ? '08:00' : '18:00',
          date: tripDate,
          timeUnverified: true,
        };
        if (type === 'arrival') setArrivalFlight(flightInfo);
        else setDepartureFlight(flightInfo);
        showToast(`Flight ${cleanedNum} set (${airlineName || carrierCode}). Please update the arrival time.`, 'warning');
        setLookingUpFlight(false);
        return;
      }

      // Try searching for actual flight schedules through Amadeus
      try {
        const { searchFlights } = await import('./lib/amadeusApi');
        const result = await searchFlights({
          origin: originCode,
          destination: destinationCode,
          departureDate: tripDate,
          travelClass: preferredTravelClass || profile?.preferred_cabin_class || 'ECONOMY',
          maxResults: 50,
          includedAirlineCodes: carrierCode,
        });

        // Look for matching flight number in results
        const matchingOffer = result.offers?.find(offer => {
          const segs = offer.itineraries?.[0]?.segments || [];
          return segs.some(seg => {
            const segNum = `${seg.carrier || ''}${seg.flightNumber || ''}`;
            return segNum === cleanedNum;
          });
        });

        if (matchingOffer) {
          const itin = matchingOffer.itineraries[0];
          const segs = itin.segments;
          const firstSeg = segs[0];
          const lastSeg = segs[segs.length - 1];
          const carriers = [...new Set(segs.map(s => s.carrier).filter(Boolean))];
          const resolvedNames = carriers.map(c => resolveAirlineName(c, result.dictionaries || {}));

          const flightInfo = {
            flightNumber: cleanedNum,
            airline: resolvedNames.join(', ') || airlineName || carrierCode,
            from: firstSeg.departure?.airport || originCode,
            to: lastSeg.arrival?.airport || destinationCode,
            time: type === 'arrival'
              ? (parseAmadeusTime(lastSeg.arrival?.time) || '08:00')
              : (parseAmadeusTime(firstSeg.departure?.time) || '18:00'),
            date: parseAmadeusDate(firstSeg.departure?.time) || tripDate,
          };

          if (type === 'arrival') setArrivalFlight(flightInfo);
          else setDepartureFlight(flightInfo);
          showToast(`✈️ ${resolvedNames[0] || airlineName} ${cleanedNum} loaded!`, 'success');
        } else {
          // No matching flight number found — set what we know
          const flightInfo = {
            flightNumber: cleanedNum,
            airline: airlineName || carrierCode,
            from: type === 'arrival' ? originCode : destinationCode,
            to: type === 'arrival' ? destinationCode : originCode,
            time: type === 'arrival' ? '08:00' : '18:00',
            date: tripDate,
            timeUnverified: true,
          };
          if (type === 'arrival') setArrivalFlight(flightInfo);
          else setDepartureFlight(flightInfo);
          showToast(`Flight ${cleanedNum} set. Please update the ${type === 'arrival' ? 'arrival' : 'departure'} time.`, 'warning');
        }
      } catch (searchErr) {
        debugError('Flight search during lookup failed:', searchErr);
        // Fallback: set basic info
        const flightInfo = {
          flightNumber: cleanedNum,
          airline: airlineName || carrierCode,
          from: type === 'arrival' ? originCode : destinationCode,
          to: type === 'arrival' ? destinationCode : originCode,
          time: type === 'arrival' ? '08:00' : '18:00',
          date: tripDate,
          timeUnverified: true,
        };
        if (type === 'arrival') setArrivalFlight(flightInfo);
        else setDepartureFlight(flightInfo);
        showToast(`Flight ${cleanedNum} set (${airlineName || carrierCode}). Please update the time.`, 'warning');
      }

      setLookingUpFlight(false);
    } catch (error) {
      debugError('Flight lookup error:', error);
      showAlertModal('Flight Lookup Failed', 'Could not fetch flight details. Please enter them manually.');
      setLookingUpFlight(false);
    }
  };

  // Calculate transit time based on transport mode
  const getTransitTime = (origin, destination, mode = 'drive') => {
    // Default estimates - can be customized per route
    const timings = {
      'airport-hotel': {
        drive: 30,      // 30 minutes by car/taxi
        transit: 60     // 60 minutes by public transit
      },
      'activity': {
        drive: 15,      // 15 minutes between activities by car
        transit: 25     // 25 minutes by public transit
      }
    };
    
    // Determine route type
    const routeType = (origin === 'Airport' || destination === 'Airport') ? 'airport-hotel' : 'activity';
    
    return timings[routeType][mode] || timings[routeType]['drive'];
  };

  // ============================================
  // TIMING VALIDATION HELPERS
  // ============================================

  // NOTE: parseTimeToMinutes and minutesToTimeString removed — overlap resolution
  // is now handled by normalizeDay Stage 8 in itineraryNormalizer.js

  // Itinerary structure validation — imported from @wander/shared via ./utils/itineraryStructureValidator
  const validateItineraryStructure = (data) => _validateItineraryStructure(data, { logger: debugLog });

  // NOTE: Overlap resolution, address validation, and multi-city reordering are now handled
  // by normalizeDay/normalizeItinerary (Stages 5, 8, and cross-day validation).
  // This function is kept as a pass-through to preserve the downstream variable name (fixedItinerary).
  const validateAndFixItinerary = (itineraryData) => {
    debugLog('🔍 validateAndFixItinerary: data already normalized by itineraryNormalizer pipeline');
    const fixed = JSON.parse(JSON.stringify(itineraryData));

    // Fix duplicate parts in destination name (e.g., "Portland, Oregon, Oregon" → "Portland, Oregon")
    if (fixed.destination) {
      const parts = fixed.destination.split(',').map(p => p.trim());
      const seen = new Set();
      const deduped = parts.filter(p => {
        const lower = p.toLowerCase();
        if (seen.has(lower)) return false;
        seen.add(lower);
        return true;
      });
      fixed.destination = deduped.join(', ');
    }

    return { fixedItinerary: fixed, fixLog: [], warnings: [] };
  };

  // Start time calculator — imported from @wander/shared via ./utils/travelTimeEstimates
  const calculateStartTime = _calculateStartTime;

  // DYNAMIC: Check if flight is domestic (same country as home city)
  // Uses getCityCountrySync for cached lookups, with async version for LLM fallback
  const checkIfDomestic = (destination, homeCity = null) => {
    if (!destination) return false;

    // Get home city from props or user profile
    const home = homeCity || userProfile?.preferences?.homeCity || 'New York';
    const homeCountry = getCityCountrySync(home);

    // Get destination name - could be in different formats
    let destName = destination.name || destination || '';
    if (typeof destName !== 'string') destName = '';

    // For multi-stop trips, check the first stop
    if (destination.isMultiStop && destination.stopNames?.length > 0) {
      destName = destination.stopNames[0];
    }

    // Also check hotels/resorts - extract location hint from name
    const hotelName = destination.hotel || destination.hotelName || '';

    // Try to get country from destination name
    let destCountry = getCityCountrySync(destName);

    // If not found, try the hotel name (might contain location)
    if (!destCountry && hotelName) {
      destCountry = getCityCountrySync(hotelName);
    }

    // If not found, try the first day's baseCity (handles resorts like "Amangiri" → "Canyon Point, Utah")
    if (!destCountry && itinerary?.days?.[0]?.baseCity) {
      destCountry = getCityCountrySync(itinerary.days[0].baseCity);
    }

    // If not found, try the arrival airport city name (e.g., "SLC - Salt Lake City" → "Salt Lake City")
    if (!destCountry && itinerary?.arrivalAirport) {
      const arrAirport = typeof itinerary.arrivalAirport === 'string' ? itinerary.arrivalAirport : (itinerary.arrivalAirport?.name || itinerary.arrivalAirport?.code || '');
      // Extract city name: "SLC - Salt Lake City" → "Salt Lake City", or "Salt Lake City International Airport" → "Salt Lake City"
      const cityFromAirport = arrAirport.replace(/^[A-Z]{2,4}\s*[-–]\s*/, '').replace(/\s*(international|regional|municipal|domestic)?\s*airport\s*$/i, '').trim();
      if (cityFromAirport) {
        destCountry = getCityCountrySync(cityFromAirport);
      }
    }

    // If we have both countries, compare them
    if (homeCountry && destCountry) {
      const isDomestic = homeCountry === destCountry;
      debugLog(`🏠 Domestic check: "${destName}" (${destCountry}) vs "${home}" (${homeCountry}) → ${isDomestic ? 'DOMESTIC' : 'INTERNATIONAL'}`);
      return isDomestic;
    }

    // If we couldn't determine country, default to international (safer for customs/arrival display)
    debugLog(`🏠 Domestic check: "${destName}" - could not determine country, defaulting to INTERNATIONAL`);
    return false;
  };

  // ASYNC version for pre-flight checks - resolves unknown locations via LLM
  const checkIfDomesticAsync = async (destination, homeCity = null) => {
    if (!destination) return false;

    // Get home city from props or user profile
    const home = homeCity || userProfile?.preferences?.homeCity || 'New York';

    // Get destination name
    let destName = destination.name || destination || '';
    if (typeof destName !== 'string') destName = '';

    // For multi-stop trips, check the first stop
    if (destination.isMultiStop && destination.stopNames?.length > 0) {
      destName = destination.stopNames[0];
    }

    // Also check hotels/resorts
    const hotelName = destination.hotel || destination.hotelName || '';

    // Resolve countries via LLM if needed
    const [homeCountry, destCountry, hotelCountry] = await Promise.all([
      getCityCountry(home),
      getCityCountry(destName),
      hotelName ? getCityCountry(hotelName) : Promise.resolve(null)
    ]);

    // Use destination country, or fall back to hotel country
    const finalDestCountry = destCountry || hotelCountry;

    if (homeCountry && finalDestCountry) {
      const isDomestic = homeCountry === finalDestCountry;
      debugLog(`🏠 Async domestic check: "${destName}" (${finalDestCountry}) vs "${home}" (${homeCountry}) → ${isDomestic ? 'DOMESTIC' : 'INTERNATIONAL'}`);
      return isDomestic;
    }

    debugLog(`🏠 Async domestic check: "${destName}" - could not determine country`);
    return false;
  };

  // Thin wrapper: delegates to shared util (same signature)
  const formatDateRange = (startDate, duration) => _formatDateRange(startDate, duration);

  // Thin wrapper: shared util needs explicit startDate param; closure provides it
  const getDateForDay = (dayNumber) => _getDateForDay(userProfile.preferences.startDate, dayNumber);

  useEffect(() => {
    if (showLoadingScreen) {
      const interval = setInterval(() => {
        setFactIndex((prev) => (prev + 1) % shuffledFacts.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [showLoadingScreen, shuffledFacts.length]);

  // Cleanup intervals and timeouts on component unmount
  useEffect(() => {
    return () => {
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
        generationTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    setCurrentFact(shuffledFacts[factIndex]);
  }, [factIndex, shuffledFacts]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  
  // NEW: Auto-scroll for itinerary chat widget
  useEffect(() => {
    // Always scroll to latest chat message first
    itineraryChatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [itineraryChatMessages, pendingChanges]);

  // Prompt bubbles for SINGLE DESTINATION trips
  const singleDestinationBubbles = [
    { icon: Palmtree, text: "Beach Paradise", prompt: "I want a relaxing beach vacation with amazing sunsets" },
    { icon: Utensils, text: "Food & Culture", prompt: "I'm looking for a destination with incredible food and rich culture" },
    { icon: Mountain, text: "Adventure", prompt: "I want an adventurous trip with hiking and outdoor activities" },
    { icon: Heart, text: "Romantic Getaway", prompt: "Planning a romantic trip for two with beautiful scenery" }
  ];

  // Prompt bubbles for MULTI-DESTINATION trips
  const multiDestinationBubbles = [
    { icon: Globe, text: "City Hopping", prompt: "I want to visit multiple cities - Paris, Rome, Barcelona" },
    { icon: Palmtree, text: "City + Beach Combo", prompt: "I want to combine a vibrant city with a relaxing beach destination" },
    { icon: Utensils, text: "Food & Wine Trail", prompt: "I want to explore multiple destinations known for incredible food and wine" },
    { icon: Mountain, text: "Adventure Circuit", prompt: "I want to visit multiple destinations with hiking, nature, and outdoor activities" }
  ];

  // Prompt bubbles for SINGLE HOTEL trips
  const singleHotelBubbles = [
    { icon: Sparkles, text: "Luxury Spa Retreat", prompt: "I want to stay at a luxury spa resort for relaxation and wellness" },
    { icon: Palmtree, text: "Beach Resort", prompt: "I want to stay at an all-inclusive beach resort" },
    { icon: Mountain, text: "Ski Resort", prompt: "I want to stay at a ski resort with slope access" },
    { icon: Gem, text: "Iconic Hotel", prompt: "I want to stay at a famous luxury hotel like Atlantis, Bellagio, or Ritz-Carlton" }
  ];

  // Prompt bubbles for MULTI-PROPERTY trips (resort hopping)
  const multiPropertyBubbles = [
    { icon: Sparkles, text: "Wellness Journey", prompt: "I want to visit multiple spa and wellness resorts for a healing journey" },
    { icon: Palmtree, text: "Island Hopping", prompt: "I want to stay at different beach resorts across multiple islands" },
    { icon: Heart, text: "Honeymoon Tour", prompt: "I want a romantic multi-property trip with different luxury experiences" },
    { icon: Gem, text: "Bucket List Hotels", prompt: "I want to stay at several iconic hotels I've always dreamed about" }
  ];

  // Use the appropriate bubbles based on trip type AND scope
  const promptBubbles = tripType === 'hotel'
    ? (tripScope === 'multi' ? multiPropertyBubbles : singleHotelBubbles)
    : (tripScope === 'multi' ? multiDestinationBubbles : singleDestinationBubbles);

  const quizQuestions = [
    {
      question: "What type of trip are you planning?",
      icon: Compass,
      options: [
        { value: 'destination', label: 'Explore a Destination', icon: MapPin, description: 'City, country, or region to explore' },
        { value: 'resort', label: 'Hotel or Resort Stay', icon: Hotel, description: 'Relaxing at a specific property' }
      ]
    },
    {
      question: "What's your travel vibe?",
      icon: Sparkles,
      options: [
        { value: 'adventure', label: 'Adventure & Exploration', icon: Mountain },
        { value: 'relax', label: 'Relax & Recharge', icon: Palmtree },
        { value: 'culture', label: 'Culture & History', icon: Book },
        { value: 'food', label: 'Food & Culinary', icon: Utensils }
      ]
    },
    {
      question: "What's your ideal climate?",
      icon: ThermometerSun,
      options: [
        { value: 'tropical', label: 'Tropical & Warm', icon: Palmtree },
        { value: 'mediterranean', label: 'Mediterranean', icon: Sunrise },
        { value: 'mountains', label: 'Mountains & Cool', icon: Mountain },
        { value: 'four-seasons', label: 'Four Seasons', icon: Coffee }
      ]
    },
    {
      question: "How do you like to explore?",
      icon: Compass,
      options: [
        { value: 'urban', label: 'City Streets & Urban Life', icon: MapPin },
        { value: 'nature', label: 'Nature & Outdoors', icon: Mountain },
        { value: 'off-beat', label: 'Off the Beaten Path', icon: Compass },
        { value: 'tourist', label: 'Popular Tourist Spots', icon: Camera }
      ]
    },
    {
      question: "What's your budget?",
      icon: Wallet,
      options: [
        { value: 'budget', label: 'Budget-Friendly', icon: DollarSign },
        { value: 'moderate', label: 'Moderate', icon: CreditCard },
        { value: 'luxury', label: 'Luxury & Premium', icon: Gem }
      ]
    },
    {
      question: "How long is your trip?",
      icon: Calendar,
      options: [
        { value: 'weekend', label: '2-3 Days (Weekend Getaway)', icon: Plane },
        { value: 'week', label: '5-7 Days (Perfect Week)', icon: Compass },
        { value: 'extended', label: '10+ Days (Epic Adventure)', icon: Mountain }
      ]
    }
  ];

  // NEW: Effect to check personalization status after profile updates
  useEffect(() => {
    const checkAndGenerate = async () => {
      if (shouldCheckPersonalization && awaitingPersonalization && selectedDestination) {
        const missingInfo = checkMissingInformation();
        debugLog('📋 Checking personalization after state update...');
        debugLog('Missing info:', missingInfo);
        debugLog('Current profile:', userProfile.preferences);
        
        if (missingInfo.length === 0) {
          debugLog('✅ All info collected! Generating itinerary...');
          setShouldCheckPersonalization(false);
          setAwaitingPersonalization(false);
          setPersonalizationQuestionsAsked(false);
          await generateItinerarySmart(selectedDestination);
        } else {
          debugLog('⏳ Still missing:', missingInfo);
          setShouldCheckPersonalization(false);
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: `Got it!\n\nStill need:\n${missingInfo.map(info => `• ${info}`).join('\n')}`
          }]);
        }
        setIsProcessing(false);
      }
    };
    
    checkAndGenerate();
  }, [shouldCheckPersonalization, awaitingPersonalization, selectedDestination]); // Include all dependencies used in the effect

  const checkMissingInformation = () => {
    const missing = [];
    const prefs = userProfile.preferences;
    
    if (!prefs.dietaryRestrictions || prefs.dietaryRestrictions.length === 0) {
      missing.push("Any dietary restrictions? (vegetarian, vegan, allergies, etc.)");
    }
    if (!prefs.cuisinePreferences || prefs.cuisinePreferences.length === 0) {
      missing.push("What types of cuisine do you enjoy?");
    }
    if (!prefs.activityLevel) {
      missing.push("Preferred activity level? (relaxed, moderate, or high-energy)");
    }
    if (!prefs.preferredPace) {
      missing.push("Preferred daily pace? (relaxed, moderate, or packed)");
    }
    
    return missing;
  };

  // Google Maps API key — ONLY used for Maps Embed iframes (requires client-side key)
  // All Places/Directions API calls go through the backend to keep the key server-side
  const GOOGLE_MAPS_EMBED_KEY = process.env.REACT_APP_GOOGLE_PLACES_KEY || process.env.REACT_APP_GOOGLE_ROUTES_API_KEY || '';

  // Validate a place exists using backend proxy (keeps API key server-side)
  const validatePlace = async (placeName, city, type = 'establishment') => {
    try {
      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const session = await getSession();
      const token = session?.access_token;
      const response = await fetch(`${API_BASE}/api/resolve-place`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ query: `${placeName} hotel`, city }),
      });
      const data = await response.json();

      if (data.success) {
        const addressLower = (data.address || '').toLowerCase();
        const cityLower = city.toLowerCase();

        // Check if the address is in the right city
        if (!addressLower.includes(cityLower) && !addressLower.includes(cityLower.split(',')[0])) {
          debugWarn(`⚠️ Found "${data.name}" but it's not in ${city}: ${data.address}`);

          // Retry with more context
          const cityParts = city.split(',').map(p => p.trim());
          const country = cityParts.length > 1 ? cityParts[cityParts.length - 1] : '';
          const retryResponse = await fetch(`${API_BASE}/api/resolve-place`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ query: placeName, city: cityParts[0], country }),
          });
          const retryData = await retryResponse.json();
          if (retryData.success) {
            return {
              isValid: true,
              name: retryData.name,
              address: retryData.address,
              placeId: retryData.placeId,
              rating: retryData.rating,
              isOpen: retryData.businessStatus === 'OPERATIONAL',
              types: retryData.placeTypes
            };
          }
        }
        
        return {
          isValid: true,
          name: data.name,
          address: data.address,
          placeId: data.placeId,
          rating: data.rating,
          isOpen: data.businessStatus === 'OPERATIONAL',
          types: data.placeTypes
        };
      }
      return { isValid: false, name: placeName };
    } catch (error) {
      debugError('Error validating place:', error);
      return { isValid: null, name: placeName, error: error.message };
    }
  };
  
  // NEW: Validate and correct hotel information
  const validateHotel = async (hotelName, city) => {
    const result = await validatePlace(hotelName, city, 'lodging');
    if (result.isValid) {
      debugLog(`✅ Hotel validated: ${result.name} at ${result.address}`);
      return {
        ...result,
        correctedName: result.name,
        correctedAddress: result.address
      };
    } else {
      debugWarn(`⚠️ Hotel not found: ${hotelName} in ${city}`);
      return result;
    }
  };
  
  // NEW: Validate and correct restaurant information  
  const validateRestaurant = async (restaurantName, city) => {
    const result = await validatePlace(restaurantName, city, 'restaurant');
    if (result.isValid) {
      debugLog(`✅ Restaurant validated: ${result.name} at ${result.address}`);
      return {
        ...result,
        correctedName: result.name,
        correctedAddress: result.address
      };
    } else {
      debugWarn(`⚠️ Restaurant not found: ${restaurantName} in ${city}`);
      return result;
    }
  };
  
  // NEW: Validate entire day's locations
  const validateDayLocations = async (dayData, city) => {
    const validations = [];
    
    // Validate hotel
    if (dayData.hotel?.name) {
      const hotelValidation = await validateHotel(dayData.hotel.name, city);
      validations.push({ type: 'hotel', ...hotelValidation });
    }
    
    // Validate restaurants
    if (dayData.meals) {
      for (const meal of dayData.meals) {
        if (meal.restaurant) {
          const restaurantValidation = await validateRestaurant(meal.restaurant, city);
          validations.push({ type: 'restaurant', meal: meal.type, ...restaurantValidation });
        }
      }
    }
    
    // Validate activity locations
    if (dayData.schedule) {
      for (const activity of dayData.schedule) {
        if (activity.location && !activity.location.toLowerCase().includes('hotel')) {
          const locationValidation = await validatePlace(activity.location, city);
          validations.push({ type: 'activity', activity: activity.activity, ...locationValidation });
        }
      }
    }
    
    return validations;
  };
  
  // ============================================
  // ITINERARY GUARDRAILS & VALIDATIONS
  // ============================================
  
  // Validate geographic coherence - all activities on same day should be within reasonable distance
  const validateGeographicCoherence = async (dayData, destination) => {
    const issues = [];
    const locations = [];
    
    // Collect all locations for the day
    if (dayData.hotel?.address) {
      locations.push({ type: 'hotel', name: dayData.hotel.name, address: dayData.hotel.address });
    }
    
    if (dayData.schedule) {
      dayData.schedule.forEach(activity => {
        if (activity.address || activity.location) {
          locations.push({ 
            type: 'activity', 
            name: activity.activity, 
            address: activity.address || `${activity.location}, ${destination}` 
          });
        }
      });
    }
    
    if (dayData.meals) {
      dayData.meals.forEach(meal => {
        if (meal.address || meal.restaurant) {
          locations.push({ 
            type: 'meal', 
            name: meal.restaurant, 
            address: meal.address || `${meal.restaurant}, ${destination}` 
          });
        }
      });
    }
    
    // Check for obviously wrong locations (different continents/regions)
    // These pairs should NEVER appear together on the same day
    const incompatibleRegions = [
      { regions: ['morocco', 'madagascar'], error: 'Morocco and Madagascar are on different parts of Africa (4000+ km apart)' },
      { regions: ['europe', 'asia'], error: 'Europe and Asia locations on same day' },
      { regions: ['north america', 'south america'], error: 'North and South America locations on same day' },
      { regions: ['africa', 'europe'], error: 'Africa and Europe locations on same day' },
      { regions: ['australia', 'asia'], error: 'Australia and Asia locations on same day' },
      { regions: ['japan', 'thailand'], error: 'Japan and Thailand are 4000+ km apart' },
      { regions: ['spain', 'morocco'], error: 'Spain and Morocco - possible but verify ferry/flight planned' },
      { regions: ['new york', 'los angeles'], error: 'NYC and LA are 4000+ km apart' },
      { regions: ['london', 'scotland'], error: 'London and Scotland are 600+ km apart' },
    ];
    
    // Nearby regions that ARE compatible (same day is OK)
    const compatibleRegions = [
      ['argentina', 'chile'], // Patagonia crosses both
      ['switzerland', 'france', 'italy', 'germany', 'austria'], // Alpine region
      ['belgium', 'netherlands', 'luxembourg'], // Benelux
      ['singapore', 'malaysia'], // Close neighbors
      ['hong kong', 'macau'], // Very close
      ['san diego', 'tijuana'], // Border cities
      ['el paso', 'ciudad juarez'], // Border cities
      ['niagara falls'], // US/Canada border
    ];
    
    // Check each location for region markers
    const detectedRegions = new Set();
    
    locations.forEach(loc => {
      const addr = (loc.address || '').toLowerCase();
      
      // Detect regions/countries mentioned
      const regionPatterns = [
        'morocco', 'madagascar', 'france', 'spain', 'italy', 'germany', 'japan', 'thailand',
        'argentina', 'chile', 'peru', 'brazil', 'australia', 'new zealand', 'china', 'india',
        'uk', 'england', 'scotland', 'ireland', 'portugal', 'greece', 'turkey', 'egypt',
        'south africa', 'kenya', 'tanzania', 'iceland', 'norway', 'sweden', 'denmark',
        'new york', 'los angeles', 'london', 'paris', 'tokyo', 'bangkok', 'singapore'
      ];
      
      regionPatterns.forEach(region => {
        if (addr.includes(region)) {
          detectedRegions.add(region);
        }
      });
    });
    
    // Check for incompatible region combinations
    const regionsArray = Array.from(detectedRegions);
    
    for (const { regions, error } of incompatibleRegions) {
      const foundRegions = regions.filter(r => regionsArray.some(detected => detected.includes(r) || r.includes(detected)));
      if (foundRegions.length >= 2) {
        // Check if these are actually compatible (like Argentina/Chile for Patagonia)
        const isCompatible = compatibleRegions.some(compatible => 
          foundRegions.every(fr => compatible.some(c => c.includes(fr) || fr.includes(c)))
        );
        
        if (!isCompatible) {
          issues.push({
            type: 'geographic',
            severity: 'error',
            message: `🗺️ Geographic issue: ${error}. These locations can't be visited on the same day.`,
            locations: locations,
            detectedRegions: regionsArray
          });
        }
      }
    }
    
    // If we detect regions that are far apart but not in our explicit list, warn
    if (regionsArray.length >= 2) {
      const isKnownCompatible = compatibleRegions.some(compatible => 
        regionsArray.every(r => compatible.some(c => c.includes(r) || r.includes(c)))
      );
      
      if (!isKnownCompatible && !issues.length) {
        // Don't warn for same-country different cities - that's usually fine for travel days
        const uniqueCountries = new Set(regionsArray.filter(r => 
          !['new york', 'los angeles', 'london', 'paris', 'tokyo', 'bangkok', 'singapore'].includes(r)
        ));
        
        if (uniqueCountries.size > 1) {
          // This might be OK (like Argentina/Chile) - just log for review
          debugLog(`📍 Day has activities in multiple regions: ${regionsArray.join(', ')} - verify these are geographically close`);
        }
      }
    }
    
    return issues;
  };
  
  // Validate operating hours - uses real Google Places data when available
  const validateOperatingHours = (dayData, dayDate) => {
    const issues = [];
    
    // Get day of week from date (0 = Sunday, 6 = Saturday)
    let dayOfWeek = null;
    if (dayDate) {
      const dateObj = new Date(dayDate);
      if (!isNaN(dateObj.getTime())) {
        dayOfWeek = dateObj.getDay();
      }
    }
    
    const parseTime = (timeStr) => {
      if (!timeStr) return null;
      const match = timeStr.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
      if (!match) return null;
      let hours = parseInt(match[1]);
      const minutes = parseInt(match[2] || '0');
      const period = match[3]?.toUpperCase();
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return hours + minutes / 60;
    };
    
    // Parse Google's time format (e.g., "0900" -> 9.0, "1730" -> 17.5)
    const parseGoogleTime = (timeStr) => {
      if (!timeStr || timeStr.length !== 4) return null;
      const hours = parseInt(timeStr.substring(0, 2));
      const minutes = parseInt(timeStr.substring(2, 4));
      return hours + minutes / 60;
    };
    
    // Check opening hours using Google Places data
    const checkGoogleHours = (item, scheduledTime, itemName) => {
      const openingHours = item.openingHours || item.placeData?.openingHours;
      
      if (!openingHours?.periods || dayOfWeek === null) {
        return null; // No Google data available, will fall back to heuristics
      }
      
      // Find periods for this day of week
      const todayPeriods = openingHours.periods.filter(p => p.open?.day === dayOfWeek);
      
      // Check if closed all day (no periods for this day)
      if (todayPeriods.length === 0) {
        // Double-check: some places have periods array but are closed certain days
        // Also check weekdayText for confirmation
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const weekdayText = openingHours.weekdayText || [];
        const todayText = weekdayText.find(t => t.startsWith(dayNames[dayOfWeek]));
        
        if (todayText?.toLowerCase().includes('closed')) {
          return {
            type: 'closed',
            severity: 'error',
            message: `⚠️ ${itemName} is CLOSED on ${dayNames[dayOfWeek]}`,
            item,
            googleVerified: true
          };
        }
        return null; // Couldn't determine, skip
      }
      
      // Check if scheduled time is within any open period
      for (const period of todayPeriods) {
        const openTime = parseGoogleTime(period.open?.time);
        const closeTime = parseGoogleTime(period.close?.time);
        
        if (openTime !== null && closeTime !== null) {
          // Handle overnight hours (close time is next day)
          const adjustedCloseTime = closeTime < openTime ? closeTime + 24 : closeTime;
          
          if (scheduledTime >= openTime && scheduledTime <= adjustedCloseTime) {
            return null; // Within open hours, no issue
          }
        }
      }
      
      // Scheduled outside of open hours
      const firstPeriod = todayPeriods[0];
      const openTime = parseGoogleTime(firstPeriod.open?.time);
      const closeTime = parseGoogleTime(firstPeriod.close?.time);
      
      const formatHour = (h) => {
        const hour = Math.floor(h) % 12 || 12;
        const ampm = h >= 12 ? 'PM' : 'AM';
        return `${hour}:00 ${ampm}`;
      };
      
      return {
        type: 'operating_hours',
        severity: 'warning',
        message: `${itemName} is open ${formatHour(openTime)} - ${formatHour(closeTime)}, but you're scheduled for ${item.time}`,
        item,
        googleVerified: true
      };
    };
    
    if (dayData.schedule) {
      dayData.schedule.forEach(activity => {
        const time = parseTime(activity.time);
        if (time === null) return;
        
        // First try Google Places data
        const googleIssue = checkGoogleHours(activity, time, activity.activity);
        if (googleIssue) {
          issues.push(googleIssue);
          return; // Found issue with real data, don't add heuristic
        }
        
        // Fall back to heuristic rules if no Google data
        const activityLower = activity.activity?.toLowerCase() || '';
        const locationLower = activity.location?.toLowerCase() || '';
        
        // Museum/gallery hours (typically 9 AM - 6 PM)
        if (activityLower.includes('museum') || activityLower.includes('gallery') || activityLower.includes('exhibition')) {
          if (time < 9 || time > 18) {
            issues.push({
              type: 'operating_hours',
              severity: 'warning',
              message: `${activity.activity} scheduled at ${activity.time} - most museums are open 9 AM - 6 PM`,
              item: activity,
              googleVerified: false
            });
          }
        }
        
        // Temple/religious site hours (typically early morning to evening)
        if (activityLower.includes('temple') || activityLower.includes('church') || activityLower.includes('mosque')) {
          if (time > 20) {
            issues.push({
              type: 'operating_hours',
              severity: 'warning',
              message: `${activity.activity} scheduled at ${activity.time} - religious sites typically close by evening`,
              item: activity,
              googleVerified: false
            });
          }
        }
        
        // Market hours
        if (activityLower.includes('market') || activityLower.includes('bazaar')) {
          if (time < 7 || time > 20) {
            issues.push({
              type: 'operating_hours',
              severity: 'info',
              message: `${activity.activity} scheduled at ${activity.time} - check if market is open at this time`,
              item: activity,
              googleVerified: false
            });
          }
        }
      });
    }
    
    // Validate meal times with Google data
    if (dayData.meals) {
      dayData.meals.forEach(meal => {
        const time = parseTime(meal.time);
        if (time === null) return;
        
        // First try Google Places data for restaurants
        const googleIssue = checkGoogleHours(meal, time, meal.restaurant);
        if (googleIssue) {
          issues.push(googleIssue);
          return;
        }
        
        // Fall back to heuristics
        if (meal.type === 'breakfast' && (time < 6 || time > 11)) {
          issues.push({
            type: 'meal_timing',
            severity: 'warning',
            message: `Breakfast at ${meal.time} is unusual - typical breakfast is 7-10 AM`,
            item: meal,
            googleVerified: false
          });
        }
        
        // In Portugal, Spain, Greece, Italy and other Mediterranean countries, late lunch (1:30-4 PM) is normal
        const destLower = (selectedDestination?.name || itinerary?.destination || '').toLowerCase();
        const lateLunchRegions = ['portugal', 'spain', 'greece', 'italy', 'turkey', 'morocco', 'argentina', 'brazil', 'mexico',
          'lisbon', 'porto', 'ericeira', 'madrid', 'barcelona', 'seville', 'rome', 'florence', 'naples', 'athens', 'istanbul'];
        const isLateLunchCulture = lateLunchRegions.some(r => destLower.includes(r));
        const lunchEndHour = isLateLunchCulture ? 16 : 15; // 4 PM for late-lunch cultures, 3 PM otherwise
        const lunchLabel = isLateLunchCulture ? '12-4 PM' : '12-2 PM';

        if (meal.type === 'lunch' && (time < 11 || time > lunchEndHour)) {
          issues.push({
            type: 'meal_timing',
            severity: 'warning',
            message: `Lunch at ${meal.time} is unusual - typical lunch is ${lunchLabel}`,
            item: meal,
            googleVerified: false
          });
        }
        
        if (meal.type === 'dinner' && (time < 17 || time > 23)) {
          issues.push({
            type: 'meal_timing',
            severity: 'warning',
            message: `Dinner at ${meal.time} is unusual - typical dinner is 6-9 PM`,
            item: meal,
            googleVerified: false
          });
        }
      });
    }
    
    return issues;
  };
  
  // Emergency numbers — imported from @wander/shared via ./utils/emergencyNumbers
  const EMERGENCY_NUMBERS = _EMERGENCY_NUMBERS;
  const getEmergencyNumber = _getEmergencyNumber;

  // Check if trip is domestic (departure and destination in same country)
  const isDomesticTrip = (departureCity, destinationName) => {
    if (!departureCity || !destinationName) return false;
    const depLower = (departureCity || '').toLowerCase();
    const destLower = (destinationName || '').toLowerCase();

    // US cities
    const usCities = ['new york', 'nyc', 'los angeles', 'chicago', 'san francisco', 'miami',
      'boston', 'seattle', 'portland', 'denver', 'austin', 'nashville', 'atlanta', 'dallas',
      'houston', 'philadelphia', 'washington', 'las vegas', 'orlando', 'honolulu', 'anchorage',
      'phoenix', 'san diego', 'detroit', 'minneapolis', 'new orleans', 'savannah', 'charleston'];
    const usStates = ['california', 'oregon', 'washington', 'texas', 'florida', 'hawaii',
      'alaska', 'colorado', 'arizona', 'utah', 'montana', 'maine', 'vermont'];

    const depIsUS = usCities.some(c => depLower.includes(c)) || usStates.some(s => depLower.includes(s)) || depLower.includes('usa') || depLower.includes('united states');
    const destIsUS = usCities.some(c => destLower.includes(c)) || usStates.some(s => destLower.includes(s)) || destLower.includes('usa') || destLower.includes('united states');
    if (depIsUS && destIsUS) return true;

    // UK cities
    const ukCities = ['london', 'manchester', 'edinburgh', 'birmingham', 'glasgow', 'liverpool', 'bristol', 'oxford', 'cambridge', 'bath', 'york'];
    const depIsUK = ukCities.some(c => depLower.includes(c)) || depLower.includes('uk') || depLower.includes('england') || depLower.includes('scotland');
    const destIsUK = ukCities.some(c => destLower.includes(c)) || destLower.includes('uk') || destLower.includes('england') || destLower.includes('scotland');
    if (depIsUK && destIsUK) return true;

    return false;
  };

  // Get destination safety & travel info
  const getDestinationSafetyInfo = async (destination) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          messages: [{
            role: "user",
            content: `Provide travel safety and requirements info for ${destination}. Return ONLY valid JSON:
{
  "visaRequirements": {
    "usaCitizens": "Visa required / Visa on arrival / Visa-free for X days",
    "euCitizens": "...",
    "general": "Brief visa info"
  },
  "healthRequirements": {
    "vaccinations": ["Required or recommended vaccinations"],
    "malariaRisk": true/false,
    "covidRules": "Current COVID entry requirements if any",
    "healthTips": "Any health precautions"
  },
  "safetyInfo": {
    "overallRisk": "low/medium/high",
    "travelAdvisory": "Current advisory level if any",
    "areasToAvoid": ["List of areas to avoid if any"],
    "commonScams": ["Common tourist scams to watch for"],
    "emergencyNumber": "Local emergency number"
  },
  "lgbtqSafety": {
    "legal": "Legal status of LGBTQ+ rights",
    "socialAcceptance": "low/medium/high",
    "advice": "Practical advice for LGBTQ+ travelers"
  },
  "soloFemaleSafety": {
    "rating": "low/medium/high concern",
    "advice": "Specific advice for solo female travelers",
    "dressCode": "Any dress code considerations"
  },
  "weather": {
    "currentSeason": "What season it is now",
    "rainyPeriod": "Months of rainy/monsoon season",
    "bestTimeToVisit": "Recommended months"
  },
  "localHolidays": ["Upcoming major holidays that might affect travel"],
  "politicalStability": "stable/some concerns/unstable"
}`
          }]
        })
      });
      
      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }
      let text = data.content[0].text.trim();
      text = text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

      const result = JSON.parse(text);

      // Strip preamble from LLM-generated travel advisory text
      if (result.safetyInfo?.travelAdvisory) {
        result.safetyInfo.travelAdvisory = result.safetyInfo.travelAdvisory
          .replace(/^Updated to include[^.]*\.\s*/i, '')
          .replace(/^Here is the travel advisory[^.]*\.\s*/i, '')
          .replace(/^Based on current conditions[^.]*\.\s*/i, '')
          .replace(/^The following (is|are) [^.]*\.\s*/i, '')
          .replace(/^This advisory (has been |was )?updated[^.]*\.\s*/i, '')
          .replace(/^Please note[^.]*\.\s*/i, '')
          .replace(/^As of \d{4}[^.]*\.\s*/i, '')
          .trim();
      }

      // Override emergency number with static lookup (AI-generated numbers can be wrong)
      const staticEmergency = getEmergencyNumber(destination);
      if (staticEmergency && result.safetyInfo) {
        result.safetyInfo.emergencyNumber = staticEmergency;
      }

      // Suppress visa/entry info for domestic trips
      const homeCity = userProfile?.preferences?.homeCity || '';
      if (homeCity && isDomesticTrip(homeCity, destination)) {
        result.visaRequirements = null;
        result.healthRequirements = {
          ...result.healthRequirements,
          vaccinations: [],
          covidRules: null,
        };
        debugLog(`🏠 Domestic trip detected (${homeCity} → ${destination}) — suppressing visa/vaccination info`);
      }

      return result;
    } catch (error) {
      debugError('Error fetching safety info:', error);
      return null;
    }
  };
  
  // Validate weather-dependent activities
  const validateWeatherActivities = (dayData, weatherInfo, travelMonth) => {
    const issues = [];
    
    if (!weatherInfo) return issues;
    
    const isRainySeason = weatherInfo.rainyPeriod?.toLowerCase().includes(travelMonth?.toLowerCase());
    
    if (dayData.schedule) {
      dayData.schedule.forEach(activity => {
        const activityLower = activity.activity?.toLowerCase() || '';
        
        // Outdoor activities during rainy season
        if (isRainySeason) {
          if (activityLower.includes('hiking') || activityLower.includes('beach') || 
              activityLower.includes('outdoor') || activityLower.includes('trek') ||
              activityLower.includes('snorkel') || activityLower.includes('diving')) {
            issues.push({
              type: 'weather',
              severity: 'warning',
              message: `${activity.activity} is an outdoor activity during rainy season - have a backup plan`,
              item: activity
            });
          }
        }
      });
    }
    
    return issues;
  };
  
  // Validate reservation requirements
  const validateReservationRequirements = (dayData) => {
    const issues = [];
    
    const needsReservation = [
      { pattern: /michelin|fine dining|starred|tasting menu/i, type: 'restaurant', lead: '1-3 months' },
      { pattern: /anne frank|uffizi|vatican|louvre|sagrada|alhambra|machu picchu|petra/i, type: 'attraction', lead: '2-4 weeks' },
      { pattern: /cooking class|food tour|wine tasting/i, type: 'activity', lead: '1-2 weeks' },
      { pattern: /spa|massage|treatment/i, type: 'service', lead: '1 week' },
      { pattern: /show|theater|concert|performance/i, type: 'entertainment', lead: '2-4 weeks' },
      { pattern: /hot air balloon|helicopter|scenic flight/i, type: 'experience', lead: '1-2 weeks' },
    ];
    
    if (dayData.schedule) {
      dayData.schedule.forEach(activity => {
        const text = `${activity.activity} ${activity.location || ''} ${activity.description || ''}`;
        
        needsReservation.forEach(({ pattern, type, lead }) => {
          if (pattern.test(text)) {
            issues.push({
              type: 'reservation',
              severity: 'info',
              message: `📅 ${activity.activity} typically requires advance booking (${lead} ahead)`,
              item: activity,
              leadTime: lead
            });
          }
        });
      });
    }
    
    if (dayData.meals) {
      dayData.meals.forEach(meal => {
        const text = `${meal.restaurant} ${meal.cuisine || ''} ${meal.specialty || ''}`;
        
        needsReservation.forEach(({ pattern, type, lead }) => {
          if (pattern.test(text)) {
            issues.push({
              type: 'reservation',
              severity: 'info',
              message: `📅 ${meal.restaurant} typically requires reservation (${lead} ahead)`,
              item: meal,
              leadTime: lead
            });
          }
        });
      });
    }
    
    return issues;
  };
  
  // Validate dietary accommodations
  const validateDietaryAccommodation = (dayData, userDietary) => {
    const issues = [];
    
    if (!userDietary || userDietary.length === 0 || userDietary.includes('none')) {
      return issues;
    }
    
    if (dayData.meals) {
      dayData.meals.forEach(meal => {
        const dietaryInfo = (Array.isArray(meal.dietary) ? meal.dietary.join(', ') : (typeof meal.dietary === 'string' ? meal.dietary : '')).toLowerCase();
        const cuisineLower = meal.cuisine?.toLowerCase() || '';
        
        userDietary.forEach(restriction => {
          const restrictionLower = restriction.toLowerCase();
          
          // Check if meal accommodates the restriction
          if (!dietaryInfo.includes(restrictionLower) && !dietaryInfo.includes('available')) {
            // Check for potential issues
            if (restrictionLower === 'vegetarian' || restrictionLower === 'vegan') {
              if (cuisineLower.includes('steakhouse') || cuisineLower.includes('bbq') || cuisineLower.includes('seafood')) {
                issues.push({
                  type: 'dietary',
                  severity: 'warning',
                  message: `${meal.restaurant} (${meal.cuisine}) may have limited ${restriction} options - confirm before visiting`,
                  item: meal
                });
              }
            }
            
            if (restrictionLower === 'halal' || restrictionLower === 'kosher') {
              issues.push({
                type: 'dietary',
                severity: 'info',
                message: `Verify ${meal.restaurant} offers ${restriction} options before visiting`,
                item: meal
              });
            }
          }
        });
      });
    }
    
    return issues;
  };
  
  // Main validation function - runs all checks
  const validateItinerary = async (itineraryData, userPreferences, destination) => {
    const allIssues = [];
    
    debugLog('🔍 Running itinerary validations...');
    
    // Get safety info for destination
    const safetyInfo = await getDestinationSafetyInfo(destination.name);
    
    if (safetyInfo) {
      // Add travel advisory warnings
      if (safetyInfo.safetyInfo?.overallRisk === 'high') {
        allIssues.push({
          type: 'travel_advisory',
          severity: 'error',
          message: `⚠️ High risk destination - ${safetyInfo.safetyInfo.travelAdvisory || 'Check latest travel advisories'}`,
          global: true
        });
      }
      
      // Add visa reminder
      if (safetyInfo.visaRequirements?.general) {
        allIssues.push({
          type: 'visa',
          severity: 'info',
          message: `🛂 Visa: ${safetyInfo.visaRequirements.general}`,
          global: true
        });
      }
      
      // Add health requirements
      if (safetyInfo.healthRequirements?.vaccinations?.length > 0) {
        allIssues.push({
          type: 'health',
          severity: 'info',
          message: `💉 Vaccinations: ${safetyInfo.healthRequirements.vaccinations.join(', ')}`,
          global: true
        });
      }
      
      if (safetyInfo.healthRequirements?.malariaRisk) {
        allIssues.push({
          type: 'health',
          severity: 'warning',
          message: `🦟 Malaria risk area - consult doctor about antimalarials`,
          global: true
        });
      }
      
      // LGBTQ+ safety
      if (safetyInfo.lgbtqSafety?.socialAcceptance === 'low') {
        allIssues.push({
          type: 'lgbtq_safety',
          severity: 'warning',
          message: `🏳️‍🌈 LGBTQ+ advisory: ${safetyInfo.lgbtqSafety.advice}`,
          global: true
        });
      }
      
      // Solo female travel
      if (safetyInfo.soloFemaleSafety?.rating === 'high concern') {
        allIssues.push({
          type: 'solo_female',
          severity: 'warning',
          message: `👩 Solo female travel: ${safetyInfo.soloFemaleSafety.advice}`,
          global: true
        });
      }
      
      // Political stability
      if (safetyInfo.politicalStability === 'unstable') {
        allIssues.push({
          type: 'political',
          severity: 'warning',
          message: `⚠️ Political situation may be unstable - monitor news before travel`,
          global: true
        });
      }
    }
    
    // Validate trip dates
    if (userPreferences?.startDate) {
      const dateValidation = validateTripDates(
        userPreferences.startDate,
        userPreferences.endDate,
        itineraryData.days?.length
      );
      dateValidation.warnings.forEach(warning => {
        allIssues.push({
          type: 'date_warning',
          severity: 'warning',
          message: `📅 ${warning}`,
          global: true
        });
      });
      dateValidation.errors.forEach(error => {
        allIssues.push({
          type: 'date_error',
          severity: 'error',
          message: `📅 ${error}`,
          global: true
        });
      });
    }

    // Validate multi-city trip feasibility
    if (itineraryData.days?.length > 0) {
      const uniqueCities = [...new Set(itineraryData.days.filter(d => d.baseCity).map(d => d.baseCity))];
      if (uniqueCities.length > 1) {
        const stops = uniqueCities.map(city => {
          const daysInCity = itineraryData.days.filter(d => d.baseCity === city).length;
          return { name: city, days: daysInCity };
        });
        const multiCityValidation = validateMultiCityTrip(stops, itineraryData.days.length);
        multiCityValidation.warnings.forEach(warning => {
          allIssues.push({
            type: 'multi_city_warning',
            severity: 'warning',
            message: `🗺️ ${warning}`,
            global: true
          });
        });
        multiCityValidation.errors.forEach(error => {
          allIssues.push({
            type: 'multi_city_error',
            severity: 'error',
            message: `🗺️ ${error}`,
            global: true
          });
        });
      }
    }

    // Validate each day
    for (let i = 0; i < itineraryData.days.length; i++) {
      const dayData = itineraryData.days[i];

      // Calculate the actual date for this day
      const dayDate = userPreferences?.startDate
        ? (() => {
            const start = new Date(userPreferences.startDate);
            start.setDate(start.getDate() + i);
            return start.toISOString().split('T')[0];
          })()
        : null;

      // NEW: Validate day schedule timing and feasibility
      if (dayData.schedule) {
        const scheduleValidation = validateDaySchedule(dayData.schedule);
        scheduleValidation.warnings.forEach(warning => {
          allIssues.push({
            type: 'schedule_warning',
            severity: 'warning',
            message: `⏰ ${warning}`,
            day: i + 1
          });
        });
        scheduleValidation.errors.forEach(error => {
          allIssues.push({
            type: 'schedule_error',
            severity: 'error',
            message: `⏰ ${error}`,
            day: i + 1
          });
        });
      }

      // Start time validation — check if first activity is before user's preferred start time
      // Skip Day 1 (arrival) and last day (departure) as they have special timing
      const preferredStart = userPreferences?.preferredStartTime || '09:00';
      const isRegularDay = i > 0 && i < itineraryData.days.length - 1;
      if (isRegularDay) {
        // Helper: parse time string (12h "1:00 PM" or 24h "13:00") to minutes since midnight
        const timeToMinutes = (timeStr) => {
          if (!timeStr) return 0;
          const upper = timeStr.toUpperCase().trim();
          const isPM = upper.includes('PM');
          const isAM = upper.includes('AM');
          const cleaned = upper.replace(/\s*(AM|PM)\s*/i, '');
          const [hStr, mStr] = cleaned.split(':');
          let h = parseInt(hStr) || 0;
          const m = parseInt(mStr) || 0;
          if (isPM && h < 12) h += 12;
          if (isAM && h === 12) h = 0;
          return h * 60 + m;
        };

        const allDayItems = [
          ...(dayData.schedule || []).map(s => ({ time: s.time, name: s.activity })),
          ...(dayData.meals || []).map(m => ({ time: m.time, name: m.restaurant || m.meal })),
        ].filter(item => item.time).sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));

        if (allDayItems.length > 0) {
          const firstItemTime = allDayItems[0].time;
          const preferredMinutes = timeToMinutes(preferredStart);
          const firstMinutes = timeToMinutes(firstItemTime);
          if (firstMinutes < preferredMinutes - 15) { // Allow 15 min tolerance
            const timeDiff = preferredMinutes - firstMinutes;
            allIssues.push({
              type: 'start_time_violation',
              severity: 'warning',
              message: `"${allDayItems[0].name}" at ${firstItemTime} is ${timeDiff} min before your ${preferredStart} start time`,
              day: i + 1
            });
          }
        }
      }

      // Geographic coherence
      const geoIssues = await validateGeographicCoherence(dayData, destination.name);
      geoIssues.forEach(issue => allIssues.push({ ...issue, day: i + 1 }));

      // Operating hours (with date for day-of-week checking)
      const hoursIssues = validateOperatingHours(dayData, dayDate);
      hoursIssues.forEach(issue => allIssues.push({ ...issue, day: i + 1 }));

      // Reservation requirements
      const reservationIssues = validateReservationRequirements(dayData);
      reservationIssues.forEach(issue => allIssues.push({ ...issue, day: i + 1 }));

      // Dietary validation
      const dietaryIssues = validateDietaryAccommodation(dayData, userPreferences?.dietaryRestrictions);
      dietaryIssues.forEach(issue => allIssues.push({ ...issue, day: i + 1 }));

      // Weather validation
      const travelMonth = userPreferences?.startDate ? new Date(userPreferences.startDate).toLocaleString('default', { month: 'long' }) : null;
      const weatherIssues = validateWeatherActivities(dayData, safetyInfo?.weather, travelMonth);
      weatherIssues.forEach(issue => allIssues.push({ ...issue, day: i + 1 }));
    }
    
    debugLog(`✅ Validation complete: ${allIssues.length} issues found`);
    return { issues: allIssues, safetyInfo };
  };
  
  // NEW: Fetch airports for any destination using AI
  const fetchAirportsForDestination = async (destinationName) => {
    setIsLoadingAirports(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 500,
          messages: [{
            role: "user",
            content: `List the main airports serving ${destinationName} for international travelers. 

Return ONLY a JSON array with 2-5 airports, no other text:
[
  { "code": "XXX", "name": "Airport Name" },
  { "code": "YYY", "name": "Airport Name" }
]

IMPORTANT: 
- Use REAL 3-letter IATA airport codes (e.g., JFK, LAX, LHR, CDG)
- OR Tambo International Airport = JNB (NOT "OR")
- Kruger Mpumalanga International = MQP
- Hoedspruit Eastgate = HDS
- Include the main international airport(s) and any major regional airports tourists would use.`
          }]
        })
      });
      
      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }
      let text = data.content[0].text.trim();

      // Clean up response
      text = text.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

      const airports = JSON.parse(text);

      if (Array.isArray(airports) && airports.length > 0) {
        // Validate and fix common airport code errors
        const correctedAirports = airports.map(airport => {
          let code = airport.code?.toUpperCase();
          
          // Common corrections for AI-generated invalid codes
          const codeCorrections = {
            'OR': 'JNB',      // OR Tambo is JNB
            'ORT': 'JNB',     // OR Tambo abbreviation
            'ORTIA': 'JNB',   // OR Tambo full abbreviation
            'FAOR': 'JNB',    // ICAO code for OR Tambo
            'CPT': 'CPT',     // Cape Town (correct)
            'DUR': 'DUR',     // Durban (correct)
            'SDC': 'LIS',     // AI hallucinated code for Lisbon - should be LIS
            'LSB': 'LIS',     // Another common AI mistake for Lisbon
            'LIS': 'LIS',     // Lisbon (correct)
            'PRT': 'OPO',     // Porto is OPO, not PRT
            'OPO': 'OPO',     // Porto (correct)
            'FAR': 'FAO',     // Faro - common typo
            'FAO': 'FAO',     // Faro (correct)
            'BCN': 'BCN',     // Barcelona (correct)
            'MAD': 'MAD',     // Madrid (correct)
            'ROM': 'FCO',     // Rome is FCO (Fiumicino)
            'FCO': 'FCO',     // Rome Fiumicino (correct)
            'CIA': 'CIA',     // Rome Ciampino (correct)
            'MIL': 'MXP',     // Milan is MXP (Malpensa)
            'MXP': 'MXP',     // Milan Malpensa (correct)
            'LIN': 'LIN',     // Milan Linate (correct)
            'FLR': 'FLR',     // Florence (correct)
          };
          
          if (codeCorrections[code]) {
            code = codeCorrections[code];
          }
          
          // Validate: IATA codes are exactly 3 letters
          if (!code || code.length !== 3 || !/^[A-Z]{3}$/.test(code)) {
            debugWarn(`⚠️ Invalid airport code: ${airport.code}, keeping as-is`);
          }
          
          return { ...airport, code };
        });
        
        debugLog(`✈️ Found ${correctedAirports.length} airports for ${destinationName}:`, correctedAirports);
        setDestinationAirports(correctedAirports);
        setIsLoadingAirports(false);

        // Auto-select first valid airport if user hasn't selected one yet
        if (correctedAirports.length > 0 && correctedAirports[0].code !== '???') {
          const firstAirportCode = correctedAirports[0].code;
          const firstAirport = correctedAirports[0];
          const isMultiStop = selectedDestination?.isMultiStop && selectedDestination?.stopNames?.length > 1;

          setUserProfile(prev => {
            // Only auto-set if not already set
            const needsArrival = !prev.preferences?.arrivalAirport;
            // For multi-stop trips, don't auto-set departure from first stop —
            // the departure airport is fetched separately from the last stop
            const needsDeparture = !isMultiStop && !prev.preferences?.departureAirport;
            if (needsArrival || needsDeparture) {
              debugLog(`✈️ Auto-selecting airport: ${firstAirportCode} (arrival: ${needsArrival}, departure: ${needsDeparture}, multiStop: ${isMultiStop})`);
              return {
                ...prev,
                preferences: {
                  ...prev.preferences,
                  arrivalAirport: needsArrival ? firstAirportCode : prev.preferences?.arrivalAirport,
                  departureAirport: needsDeparture ? firstAirportCode : prev.preferences?.departureAirport
                }
              };
            }
            return prev;
          });

          // Also update itinerary if it has placeholder airports (code === '???' or needsUpdate === true)
          setItinerary(prev => {
            if (!prev) return prev;
            const needsArrivalUpdate = prev.arrivalAirport?.code === '???' || prev.arrivalAirport?.needsUpdate;
            const needsDepartureUpdate = prev.departureAirport?.code === '???' || prev.departureAirport?.needsUpdate;

            if (needsArrivalUpdate || needsDepartureUpdate) {
              debugLog(`✈️ Updating itinerary airports from fetched data: ${firstAirportCode}`);
              return {
                ...prev,
                arrivalAirport: needsArrivalUpdate ? {
                  code: firstAirportCode,
                  name: firstAirport.name || `${firstAirportCode} Airport`,
                  city: prev.arrivalAirport?.city || prev.days?.[0]?.baseCity || prev.destination
                } : prev.arrivalAirport,
                departureAirport: needsDepartureUpdate ? {
                  code: firstAirportCode,
                  name: firstAirport.name || `${firstAirportCode} Airport`,
                  city: prev.departureAirport?.city || prev.days?.[prev.days?.length - 1]?.baseCity || prev.destination
                } : prev.departureAirport
              };
            }
            return prev;
          });
        }

        return correctedAirports;
      }
    } catch (error) {
      debugError('Error fetching airports:', error);
    }
    
    // Fallback: return empty and let AI choose
    setDestinationAirports([{ code: '???', name: 'Let arrivo choose the best airports' }]);
    setIsLoadingAirports(false);
    return [];
  };
  
  // Auto-default "Flying back" when driving to a destination on a different continent from home
  useEffect(() => {
    if (transportationMode !== 'fly' && selectedDestination?.name) {
      const homeCity = (userProfile.preferences?.homeCity || '').toLowerCase();
      const destName = selectedDestination.name.toLowerCase();

      // Use shared top-level continent hint function
      const homeContinent = getContinentHintFromText(homeCity);
      const destContinent = getContinentHintFromText(destName);
      // Also check returnCity — user may have changed homeCity to a local departure point
      // (e.g., homeCity=Pinhão but returnCity=NYC) so we need to detect intercontinental returns
      const returnCity = (userProfile.preferences?.returnCity || '').toLowerCase();
      const returnContinent = returnCity ? getContinentHintFromText(returnCity) : null;

      const isIntercontinental =
        (homeContinent && destContinent && homeContinent !== destContinent) ||
        (returnContinent && destContinent && returnContinent !== destContinent) ||
        // Fallback: if return city is on a different continent from home city, it's intercontinental
        // (handles case where destContinent is unknown, e.g., small unrecognized cities)
        (returnContinent && homeContinent && returnContinent !== homeContinent);

      if (isIntercontinental) {
        // Different continents — auto-set flying back
        if (returnTransportMode !== 'fly') {
          debugLog(`✈️ Auto-setting fly-back: home=${homeCity} (${homeContinent}), return=${returnCity || homeCity} (${returnContinent || homeContinent}), dest=${destName} (${destContinent}) — intercontinental`);
          setReturnTransportMode('fly');
        }
      }
    }
  }, [transportationMode, selectedDestination?.name, userProfile.preferences?.homeCity, userProfile.preferences?.returnCity, returnTransportMode]);

  // Fetch airports when destination changes
  // For hotel/resort trips, resolve hotel names to their geographic locations first
  // e.g., "Amanzoe" → "Porto Heli, Greece" → ATH airport (not JTR/Santorini)
  useEffect(() => {
    if (selectedDestination?.name) {
      // Helper: resolve a stop name to its geographic location for airport lookup
      // Hotels like "Amanzoe" should lookup airports for "Porto Heli, Greece", not "Amanzoe"
      // Country names like "Canada" should use the trip builder's original stop details for context
      const resolveStopForAirport = (stopName) => {
        // First try static hotel location map
        const resolved = extractLocationFromHotelName(stopName);
        if (resolved !== stopName) {
          debugLog(`✈️ Resolved hotel "${stopName}" → "${resolved}" for airport lookup`);
          return resolved;
        }
        // Check if this stop has a resolvedLocation from the trip builder
        const builderStop = tripBuilderStops.find(s => s.name.toLowerCase() === stopName.toLowerCase());
        if (builderStop?.resolvedLocation) {
          debugLog(`✈️ Using trip builder resolved location: "${stopName}" → "${builderStop.resolvedLocation}"`);
          return builderStop.resolvedLocation;
        }
        // Detect bare country names — these are too vague for airport lookup.
        // Check if the stop name matches a known country (single word, no comma).
        const BARE_COUNTRY_NAMES = [
          'canada', 'mexico', 'brazil', 'argentina', 'chile', 'colombia', 'peru', 'ecuador',
          'japan', 'china', 'india', 'thailand', 'vietnam', 'indonesia', 'malaysia', 'philippines',
          'australia', 'zealand', 'france', 'spain', 'italy', 'germany', 'portugal', 'greece',
          'turkey', 'egypt', 'morocco', 'kenya', 'tanzania', 'iceland', 'norway', 'sweden',
          'switzerland', 'austria', 'netherlands', 'ireland', 'scotland', 'england', 'croatia',
          'czech', 'hungary', 'poland', 'romania', 'singapore', 'korea', 'taiwan',
        ];
        const isCountryName = !stopName.includes(',') && BARE_COUNTRY_NAMES.some(c => stopName.toLowerCase().trim() === c);
        if (isCountryName) {
          // Try to find a more specific city from the trip builder stops
          // Look for other stops that include this country name in a "City, Country" format
          const moreSpecific = tripBuilderStops.find(s => {
            const n = (s.resolvedLocation || s.name || '').toLowerCase();
            return n.includes(',') && n.includes(stopName.toLowerCase());
          });
          if (moreSpecific) {
            const city = (moreSpecific.resolvedLocation || moreSpecific.name).split(',')[0].trim();
            debugLog(`✈️ Country name "${stopName}" → resolved to city "${city}" from trip builder`);
            return city;
          }
          // Also check selectedDestination.stops for original stop data
          const origStop = selectedDestination?.stops?.find(s => {
            const sName = (s.resolvedLocation || s.name || '').toLowerCase();
            return sName !== stopName.toLowerCase() && (sName.includes(stopName.toLowerCase()) || stopName.toLowerCase().includes(sName));
          });
          if (origStop?.resolvedLocation) {
            debugLog(`✈️ Country name "${stopName}" → resolved to "${origStop.resolvedLocation}" from original stops`);
            return origStop.resolvedLocation;
          }
          debugLog(`⚠️ Airport lookup using bare country name "${stopName}" — results may be inaccurate`);
        }
        return stopName;
      };

      if (selectedDestination.isMultiStop && selectedDestination.stopNames?.length > 1) {
        // Multi-stop: fetch airports for FIRST stop (arrival) — the auto-select in fetchAirportsForDestination
        // sets arrival airport. Then separately fetch last stop for departure.
        const firstStop = selectedDestination.stopNames[0];
        const lastStop = selectedDestination.stopNames[selectedDestination.stopNames.length - 1];
        const firstStopResolved = resolveStopForAirport(firstStop);
        const lastStopResolved = resolveStopForAirport(lastStop);
        debugLog(`✈️ Multi-stop trip - fetching airports: arrival=${firstStop} (→${firstStopResolved}), departure=${lastStop} (→${lastStopResolved})`);

        // Fetch first stop airports (sets destinationAirports + auto-selects arrival)
        fetchAirportsForDestination(firstStopResolved);

        // Fetch last stop airports separately for departure
        if (firstStop.toLowerCase() !== lastStop.toLowerCase()) {
          (async () => {
            // Shared fallback so every failure path still resolves to SOMETHING
            // instead of leaving `departureAirport` blank and blocking the user.
            // Priority:
            //   1. Already-set preferences.departureAirport (user picked something)
            //   2. preferences.arrivalAirport (sensible same-origin default)
            //   3. Leave unset — caller's downstream code will pick a default
            //      (e.g. round-trip from home airport). We NEVER open a blocking
            //      picker here.
            const applyDepartureFallback = (reason) => {
              setUserProfile(prev => {
                const current = prev.preferences?.departureAirport;
                const arrival = prev.preferences?.arrivalAirport;
                if (current) {
                  debugLog(`✈️ [last-stop-fallback] keeping existing departureAirport=${current} (${reason})`);
                  return prev;
                }
                if (arrival) {
                  debugLog(`✈️ [last-stop-fallback] using arrivalAirport=${arrival} as departure (${reason})`);
                  return {
                    ...prev,
                    preferences: {
                      ...prev.preferences,
                      departureAirport: arrival,
                    },
                  };
                }
                debugWarn(`⚠️ [last-stop-fallback] no departure/arrival set for "${lastStop}" (${reason}) — leaving unset, downstream will default`);
                return prev;
              });
            };

            let data = null;
            let response = null;
            try {
              const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
              response = await fetch(`${BACKEND_URL}/api/destination-transport-info`, {
                method: 'POST',
                headers: await getApiHeaders(),
                body: JSON.stringify({ destination: lastStopResolved })
              });
              if (response.ok) {
                data = await response.json();
              } else {
                debugWarn(`⚠️ Last-stop transport-info non-OK (${response.status}) for "${lastStop}"`);
              }
            } catch (e) {
              debugWarn(`⚠️ Failed to fetch last stop airport for ${lastStop}: ${e.message}`);
            }

            if (data?.nearestAirport?.code) {
              // Extract clean IATA code — LLM sometimes returns garbage like "Multiple major airports (MAD, BCN...)"
              let rawCode = data.nearestAirport.code;
              let cleanCode = rawCode;
              if (rawCode.length > 3) {
                // Try to extract first valid 3-letter IATA code
                const iataMatch = rawCode.match(/\b([A-Z]{3})\b/);
                cleanCode = iataMatch ? iataMatch[1] : rawCode.substring(0, 3).toUpperCase();
                debugLog(`✈️ Cleaned airport code: "${rawCode}" → "${cleanCode}"`);
              }
              data.nearestAirport.code = cleanCode;
              debugLog(`✈️ Last stop (${lastStop} → ${lastStopResolved}) departure airport: ${cleanCode} (${data.nearestAirport.name}) [source=${data.resolverSource || 'unknown'}]`);

              // Store last-stop airports for departure suggestion UI
              const lastAirports = [{ code: cleanCode, name: data.nearestAirport.name }];
              // Add alternative airports if available
              if (data.alternativeAirports?.length) {
                data.alternativeAirports.forEach(alt => {
                  if (alt.code && alt.code !== cleanCode) lastAirports.push(alt);
                });
              }
              setLastStopAirports(lastAirports);

              // Auto-set departure airport from last stop
              setUserProfile(prev => {
                const currentDeparture = prev.preferences?.departureAirport;
                const newDeparture = cleanCode;
                if (!currentDeparture || currentDeparture !== newDeparture) {
                  debugLog(`✈️ Updating departure airport: ${currentDeparture || 'none'} → ${newDeparture} (nearest to last stop: ${lastStop})`);
                  return {
                    ...prev,
                    preferences: {
                      ...prev.preferences,
                      departureAirport: newDeparture
                    }
                  };
                }
                return prev;
              });
            } else {
              // Backend returned but no valid airport (should be rare now that
              // the resolver chain has a haversine ultimate fallback). Use the
              // arrivalAirport as a sensible default — NEVER block the UI with
              // a picker. User requirement: always auto-pick the best airport.
              const reason = !response
                ? 'network failure'
                : !response.ok
                  ? `http ${response.status}`
                  : 'backend returned no airport';
              applyDepartureFallback(reason);
              setLastStopAirports([]);
            }
          })();
        } else {
          setLastStopAirports([]); // Same as first stop, use destinationAirports
        }
      } else {
        // Single destination or single-stop: fetch normally
        let destName = selectedDestination.name;
        if (selectedDestination.isMultiStop && selectedDestination.stopNames?.length > 0) {
          destName = selectedDestination.stopNames[0];
        }
        // Resolve hotel name to location for single-stop too
        destName = resolveStopForAirport(destName);
        fetchAirportsForDestination(destName);
      }
    }
  }, [selectedDestination?.name, selectedDestination?.isMultiStop, selectedDestination?.stopNames]);
  
  // Scroll to top when entering personalization stage
  useEffect(() => {
    if (stage === 'personalization') {
      window.scrollTo({ top: 0, behavior: 'smooth' });

      // Auto-correct tripType based on stop types — now that stops have explicit stopType,
      // we can derive tripType cleanly instead of guessing from hotel name extraction
      if (selectedDestination?.stops?.length > 0) {
        const hasAnyResort = selectedDestination.stops.some(s => (s.stopType === 'hotel' || s.stopType === 'resort'));
        const allResorts = selectedDestination.stops.every(s => (s.stopType === 'hotel' || s.stopType === 'resort'));
        const effectiveTripType = allResorts ? 'hotel' : (hasAnyResort ? 'hotel' : 'destination');
        if (effectiveTripType !== tripType) {
          debugLog(`🔄 Auto-correcting tripType to '${effectiveTripType}' based on stop types`);
          setTripType(effectiveTripType);
        }
      } else if (tripType === 'hotel' && selectedDestination?.name) {
        // Fallback for non-trip-builder flows: check if the name is a recognized hotel
        const destName = selectedDestination.name;
        const resolved = extractLocationFromHotelName(destName);
        const isRecognizedHotel = resolved !== destName;
        if (!isRecognizedHotel) {
          debugLog(`🔄 Auto-correcting tripType from 'hotel' to 'destination' — "${destName}" is not a recognized hotel/resort`);
          setTripType('destination');
        }
      }
    }
  }, [stage]);

  // Fetch LLM flight time AND pre-resolve country for unknown destinations when entering personalization
  useEffect(() => {
    const fetchFlightTimeAndCountry = async () => {
      if (stage === 'personalization' && selectedDestination?.name && userProfile.preferences.homeCity) {
        // For multi-stop trips, use the first stop name for flight time calculation
        const destNameForFlightCalc = selectedDestination.isMultiStop && selectedDestination.stopNames?.length > 0
          ? selectedDestination.stopNames[0]
          : selectedDestination.name;

        // Also get hotel name if available (for resort-centric trips)
        const hotelName = selectedDestination.hotel || selectedDestination.hotelName || '';

        // Pre-resolve countries via LLM in parallel (caches results for checkIfDomestic)
        // This ensures we have country data ready for domestic/international flight detection
        debugLog(`🌍 Pre-resolving countries for: home="${userProfile.preferences.homeCity}", dest="${destNameForFlightCalc}"${hotelName ? `, hotel="${hotelName}"` : ''}`);

        const countryPromises = [
          getCityCountry(userProfile.preferences.homeCity),
          getCityCountry(destNameForFlightCalc)
        ];
        if (hotelName) {
          countryPromises.push(getCityCountry(hotelName));
        }

        // Run country resolution in parallel with flight time lookup
        const [homeCountry, destCountry] = await Promise.all(countryPromises);
        debugLog(`🌍 Resolved: home=${homeCountry}, dest=${destCountry}`);

        // Check if this is an unknown destination (not in our static lookup) for flight time
        if (!isKnownDestination(destNameForFlightCalc)) {
          debugLog(`✈️ Unknown destination "${destNameForFlightCalc}", fetching LLM estimate...`);
          setIsFetchingFlightTime(true);

          const flightHours = await getFlightHours(
            destNameForFlightCalc,
            userProfile.preferences.homeCity
          );

          // Update the selectedDestination with the LLM-estimated flight time
          setSelectedDestination(prev => ({
            ...prev,
            flightHours: flightHours,
            estimatedFlightHours: flightHours
          }));

          setIsFetchingFlightTime(false);
          debugLog(`✈️ Updated flight time for "${destNameForFlightCalc}": ${flightHours} hours`);
        }
      }
    };

    fetchFlightTimeAndCountry();
  }, [stage, selectedDestination?.name, selectedDestination?.isMultiStop, selectedDestination?.stopNames, userProfile.preferences.homeCity]);
  
  // Calculate drive time when entering personalization with a destination
  useEffect(() => {
    const fetchDriveTime = async () => {
      if (stage === 'personalization' && selectedDestination?.name && userProfile.preferences.homeCity) {
        const origin = userProfile.preferences.homeCity;
        // For multi-stop trips, calculate drive time to the FIRST stop (e.g., "Ericeira"), not the country ("Portugal")
        const driveDestination = (selectedDestination.isMultiStop && selectedDestination.stopNames?.length > 0)
          ? selectedDestination.stopNames[0]
          : selectedDestination.name;

        // Skip drive calculation if first stop is in the user's home city (e.g., "The Plaza" in NYC)
        // Check if the first stop's resolved location matches the home city, or if it's a hotel IN the home city
        const originLower = origin.toLowerCase().trim();
        const destLower = driveDestination.toLowerCase().trim();
        const firstStopBuilder = tripBuilderStops?.[0];
        const resolvedLocLower = (firstStopBuilder?.resolvedLocation || '').toLowerCase().trim();
        // Guard against empty strings (includes('') is always true)
        const isFirstStopInHomeCity = (originLower.length > 0 && destLower.length > 0) && (
          destLower.includes(originLower) || originLower.includes(destLower) ||
          (resolvedLocLower.length > 0 && (resolvedLocLower.includes(originLower) || originLower.includes(resolvedLocLower))) ||
          ((firstStopBuilder?.stopType === 'hotel' || firstStopBuilder?.stopType === 'resort') && resolvedLocLower.length > 0 &&
           (resolvedLocLower.startsWith(originLower) || originLower.startsWith(resolvedLocLower.split(',')[0])))
        );

        if (isFirstStopInHomeCity) {
          debugLog(`🏠 First stop "${driveDestination}" is in home city "${origin}" — skipping drive time calculation`);
          setCalculatedDriveTime({ hours: 0, duration: '0 mins', distance: '0 km', isDriveable: false });
          setIsCalculatingDriveTime(false);
          // Don't auto-switch to drive — user should fly to the NEXT destination
          return;
        }

        // Check geography + country context in parallel (dynamic island detection via Haiku)
        // This replaces static keyword lists — Haiku determines if any city is an island
        const [originCountry, destCountry, destGeo] = await Promise.all([
          getCityCountry(origin),
          getCityCountry(driveDestination),
          getDestinationGeography(driveDestination)
        ]);

        // Cache geography result for later use (transit rendering, itinerary generation)
        if (destGeo) {
          const geoLower = driveDestination.toLowerCase().trim();
          dynamicGeographyCache[geoLower] = {
            isIsland: destGeo.isIsland,
            isArchipelago: destGeo.isArchipelago,
            noCarAccess: destGeo.noCarAccess,
            accessMethods: destGeo.accessMethods,
            requiresBoat: destGeo.requiresBoat,
          };
          debugLog(`🗺️ Early geography cache for ${driveDestination}:`, dynamicGeographyCache[geoLower]);
        }

        // Skip drive time calculation for islands — you can't drive across an ocean
        if (destGeo?.isIsland || destGeo?.isArchipelago || destGeo?.noCarAccess || isIslandCity(driveDestination)) {
          debugLog(`🏝️ ${driveDestination} is an island — skipping drive time calculation`);
          setCalculatedDriveTime({ hours: 0, duration: '0 mins', distance: '0 km', isDriveable: false });
          setIsCalculatingDriveTime(false);
          return;
        }

        setIsCalculatingDriveTime(true);
        const result = await calculateDriveTimeToDestination(
          origin,
          driveDestination,
          { originCountry, destCountry }
        );
        setCalculatedDriveTime(result);
        setIsCalculatingDriveTime(false);

        // If it's driveable, suggest driving mode — but only on first calculation for this destination
        // Don't auto-switch if user has already been to personalization (they may have manually chosen fly)
        if (result?.isDriveable && transportationMode === 'fly' && !userHasSetTransportRef.current) {
          setTransportationMode('drive-own');
        }
      }
    };
    
    fetchDriveTime();
  }, [stage, selectedDestination?.name, selectedDestination?.isMultiStop, selectedDestination?.stopNames, userProfile.preferences.homeCity]);

  // Fetch quick-transport recommendation for departure → first stop
  // Used to determine if train/ferry cards should be shown in the transport selector
  useEffect(() => {
    if (stage !== 'personalization') return;
    const origin = userProfile.preferences.homeCity?.trim();
    const destName = (selectedDestination?.isMultiStop && selectedDestination?.stopNames?.length > 0)
      ? selectedDestination.stopNames[0]
      : selectedDestination?.name;
    if (!origin || !destName) return;

    let cancelled = false;
    setIsFetchingQuickTransport(true);
    (async () => {
      try {
        const apiUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/quick-transport`;
        const headers = { 'Content-Type': 'application/json' };
        try {
          const { getSession } = await import('./lib/supabase');
          const session = await getSession();
          if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
        } catch { /* proceed without auth */ }
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers,
          body: JSON.stringify({ fromCity: origin, toCity: destName })
        });
        if (cancelled) return;
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setQuickTransportResult(data);
            debugLog(`🚆 Quick transport for ${origin} → ${destName}: ${data.mode} (${data.duration})`);
            // Auto-switch to drive if quick transport says car/drive (unless user manually set transport)
            if ((data.mode === 'car' || data.mode === 'drive') && transportationMode === 'fly' && !userHasSetTransportRef.current) {
              setTransportationMode('drive-own');
              debugLog(`🚗 Auto-switched transport to drive-own (quick transport: ${data.mode}, ${data.duration})`);
            }
          }
        }
      } catch (err) {
        debugLog('Quick transport fetch error (non-blocking):', err.message);
      }
      if (!cancelled) setIsFetchingQuickTransport(false);
    })();
    return () => { cancelled = true; };
  }, [stage, selectedDestination?.name, selectedDestination?.isMultiStop, selectedDestination?.stopNames, userProfile.preferences.homeCity]);

  // Calculate airport distance from destination using Google Maps API
  // This determines if the selected airport is far from the destination (e.g., MIA for Key West trip)
  const calculateAirportDistance = async (airportCode, destinationName, type = 'arrival') => {
    if (!airportCode || airportCode.length !== 3 || !destinationName) {
      debugLog(`⚠️ Cannot calculate airport distance: invalid params (${airportCode}, ${destinationName})`);
      return null;
    }

    try {
      const origin = `${airportCode} Airport`;
      const destination = destinationName;

      debugLog(`📍 Calculating ${type} airport distance: ${origin} → ${destination}`);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/directions`, {
        method: 'POST',
        headers: await getApiHeaders(),
        body: JSON.stringify({ origin, destination, mode: 'driving' })
      });

      const data = await response.json();

      if (data.routes && data.routes[0]) {
        const leg = data.routes[0].legs[0];
        const durationMinutes = Math.round(leg.duration?.value / 60);
        const isFar = durationMinutes > 90; // Consider "far" if > 90 minutes

        const result = {
          duration: leg.duration?.text,
          durationMinutes,
          isFar,
          airportCode
        };

        debugLog(`✅ ${type} airport distance calculated: ${result.duration} (${durationMinutes} mins, isFar: ${isFar})`);

        setAirportDistances(prev => ({
          ...prev,
          [type]: result
        }));

        return result;
      } else if (data.success && data.duration) {
        // Handle backend response format
        const hoursMatch = data.duration.match(/(\d+)\s*hour/);
        const minsMatch = data.duration.match(/(\d+)\s*min/);
        const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
        const mins = minsMatch ? parseInt(minsMatch[1]) : 0;
        const durationMinutes = hours * 60 + mins;
        const isFar = durationMinutes > 90;

        const result = {
          duration: data.duration,
          durationMinutes,
          isFar,
          airportCode
        };

        debugLog(`✅ ${type} airport distance calculated: ${result.duration} (${durationMinutes} mins, isFar: ${isFar})`);

        setAirportDistances(prev => ({
          ...prev,
          [type]: result
        }));

        return result;
      }
    } catch (error) {
      debugError('Error calculating airport distance:', error);
    }

    return null;
  };

  // Trigger airport distance calculation when airports are selected (in personalization)
  // or when itinerary is generated (to use itinerary's departure airport)
  useEffect(() => {
    const destinationName = selectedDestination?.name;
    if (!destinationName) return;

    // In personalization stage, use user preferences
    if (stage === 'personalization') {
      const arrivalCode = userProfile.preferences.arrivalAirport;
      const departureCode = userProfile.preferences.departureAirport;

      // Calculate arrival airport distance
      if (arrivalCode && arrivalCode.length === 3) {
        calculateAirportDistance(arrivalCode, destinationName, 'arrival');
      } else {
        setAirportDistances(prev => ({ ...prev, arrival: null }));
      }

      // Calculate departure airport distance
      if (departureCode && departureCode.length === 3) {
        calculateAirportDistance(departureCode, destinationName, 'departure');
      } else if (arrivalCode && arrivalCode.length === 3 && !departureCode) {
        // If no departure specified, use arrival airport for departure too
        calculateAirportDistance(arrivalCode, destinationName, 'departure');
      } else {
        setAirportDistances(prev => ({ ...prev, departure: null }));
      }
    }

    // When itinerary is generated, use the itinerary's airports (more reliable)
    if (stage === 'itinerary' && itinerary) {
      const arrivalCode = itinerary.arrivalAirport?.code;
      const departureCode = itinerary.departureAirport?.code;

      debugLog(`📍 Itinerary generated - recalculating airport distances: arrival=${arrivalCode}, departure=${departureCode}`);

      if (arrivalCode && arrivalCode.length === 3) {
        calculateAirportDistance(arrivalCode, destinationName, 'arrival');
      }

      if (departureCode && departureCode.length === 3) {
        // For departure: use the hotel where the user actually sleeps the night before
        // If last day has no hotel (departure day), fall back to second-to-last day's hotel
        const lastDay = itinerary?.days?.[itinerary.days.length - 1];
        const prevLastDay = itinerary?.days?.length > 1 ? itinerary.days[itinerary.days.length - 2] : null;
        const departureHotel = lastDay?.hotel || prevLastDay?.hotel;
        const departureOrigin = departureHotel?.address || departureHotel?.name ||
          (prevLastDay?.baseCity ? `${prevLastDay.baseCity}` : null) ||
          lastDay?.baseCity || destinationName;
        calculateAirportDistance(departureCode, departureOrigin, 'departure');
      } else if (arrivalCode && arrivalCode.length === 3) {
        // If no departure specified, use arrival airport
        const lastDay = itinerary?.days?.[itinerary.days.length - 1];
        const prevLastDay = itinerary?.days?.length > 1 ? itinerary.days[itinerary.days.length - 2] : null;
        const departureHotel = lastDay?.hotel || prevLastDay?.hotel;
        const departureOrigin = departureHotel?.address || departureHotel?.name ||
          (prevLastDay?.baseCity ? `${prevLastDay.baseCity}` : null) ||
          lastDay?.baseCity || destinationName;
        calculateAirportDistance(arrivalCode, departureOrigin, 'departure');
      }
    }
  }, [stage, selectedDestination?.name, userProfile.preferences.arrivalAirport, userProfile.preferences.departureAirport, itinerary?.arrivalAirport?.code, itinerary?.departureAirport?.code]);

  // Calculate drive time from home city to destination using Google Maps API
  // Lightweight geocoder used by the drive-time sanity check. Reuses
  // geocodeCacheRef so repeated calls within a session are free. Returns
  // { lat, lon } (normalized to what sanityCheckDriveHours expects) or null.
  const geocodeForSanityCheck = async (cityName) => {
    if (!cityName) return null;
    const cacheKey = cityName.toLowerCase().trim();
    const cached = geocodeCacheRef.current[cacheKey];
    if (cached && typeof cached.lat === 'number') {
      // cache stores { lat, lng }; sanityCheckDriveHours expects { lat, lon }
      return { lat: cached.lat, lon: cached.lng ?? cached.lon };
    }
    try {
      const firstPart = cityName.split(',')[0].trim();
      const res = await fetch(
        `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(firstPart)}&count=1&language=en`
      );
      if (!res.ok) return null;
      const data = await res.json();
      if (!data.results?.length) return null;
      const { latitude, longitude } = data.results[0];
      geocodeCacheRef.current[cacheKey] = { lat: latitude, lng: longitude };
      return { lat: latitude, lon: longitude };
    } catch {
      return null;
    }
  };

  const calculateDriveTimeToDestination = async (homeCity, destinationName, { originCountry, destCountry } = {}) => {
    if (!homeCity || !destinationName) return null;

    // Normalize accents for consistent cache hits (e.g., "Pinhão" and "Pinhao" → same key)
    const normCity = homeCity.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const normDest = destinationName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const cacheKey = `driveTime:${normCity}:${normDest}`;
    const cached = apiCacheRef.current.get(cacheKey);
    if (cached) {
      debugLog(`📦 [CACHE HIT] Drive time: ${cached.hours} hours`);
      return cached;
    }

    debugLog(`🚗 Calculating drive time: ${homeCity} → ${destinationName}${originCountry ? ` (${originCountry} → ${destCountry})` : ''}`);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/directions`, {
        method: 'POST',
        headers: await getApiHeaders(),
        body: JSON.stringify({
          origin: homeCity,
          destination: destinationName,
          mode: 'driving',
          transitContext: { type: 'trip-start' }, // Home to destination - allow long distances
          // Country context for retry on NO_ROUTE (helps small/ambiguous town names)
          ...(originCountry && { originCountry }),
          ...(destCountry && { destCountry })
        })
      });
      
      const data = await response.json();
      
      if (data.success && (data.duration || data.durationValue)) {
        // Prefer raw seconds from backend (most reliable), fall back to string parsing
        let totalHours;
        if (data.durationValue && data.durationValue > 0) {
          totalHours = Math.round((data.durationValue / 3600) * 10) / 10;
        } else {
          // Parse duration string like "2 hours 30 mins", "39 hr 36 mins", or "4 hours"
          // Backend returns "hr" format, so match both "hr" and "hour(s)"
          const hoursMatch = data.duration.match(/(\d+)\s*(?:hours?|hr)/);
          const minsMatch = data.duration.match(/(\d+)\s*min/);
          const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
          const mins = minsMatch ? parseInt(minsMatch[1]) : 0;
          totalHours = Math.round((hours + mins / 60) * 10) / 10;
        }
        
        debugLog(`✅ Drive time calculated: ${totalHours} hours (${data.duration})`);

        // T2: sanity-check the reported drive time against great-circle distance.
        // Catches cases where Google Directions returns a bogus fast duration
        // because one side geocoded to the wrong location (e.g., Park City →
        // Aspen showing 2h 45m when real is ~7h).
        try {
          const [fromCoords, toCoords] = await Promise.all([
            geocodeForSanityCheck(homeCity),
            geocodeForSanityCheck(destinationName),
          ]);
          const checked = _sanityCheckDriveHours(totalHours, fromCoords, toCoords);
          if (checked.source === 'fallback') {
            debugWarn(
              `⚠️ Drive time ${totalHours}h from "${homeCity}" to "${destinationName}" rejected: ${checked.reason}. Using floor ${checked.hours.toFixed(1)}h.`
            );
            totalHours = Math.round(checked.hours * 10) / 10;
          }
        } catch (sanityErr) {
          debugLog('Drive-time sanity check skipped:', sanityErr?.message);
        }

        const result = {
          hours: totalHours,
          duration: data.duration,
          distance: data.distance,
          // Consider driveable if under 8 hours but NOT if 0 (same city = should fly to next stop)
          isDriveable: totalHours > 0 && totalHours <= 8
        };

        // Cache for 24 hours (drive times don't change much)
        apiCacheRef.current.set(cacheKey, result, 24 * 60 * 60 * 1000);
        return result;
      }
      
      debugLog(`⚠️ Could not calculate drive time:`, data.error || 'no route');
      return null;
    } catch (error) {
      debugError('❌ Error calculating drive time:', error);
      return null;
    }
  };
  
  // Strip LLM-estimated drive times from recommendation message text and replace with placeholder.
  // Returns { strippedMessage, driveDestNames } — the cleaned text and which destinations had drive times.
  // We do this BEFORE showing the message so users never see inaccurate LLM drive times.
  const stripDriveTimesFromMessage = (messageText) => {
    const driveDestNames = [];
    // Match patterns like "~4hr drive", "4hr drive", "~4.5hr drive", "2hr 30min drive", "~4-5hr drive", "30min drive", "45 min drive"
    const driveTimePattern = /~?(?:\d+(?:\.\d+)?\s*(?:-\s*\d+(?:\.\d+)?\s*)?hr(?:\s+\d+\s*min\w*)?\s*drive|\d+\s*min\w*\s*drive)/gi;
    const strippedMessage = messageText.replace(driveTimePattern, (match) => {
      return '⏳ drive';
    });
    return { strippedMessage };
  };

  // Async-validate drive times from LLM recommendations against Google Maps.
  // Now works with stripped messages — INSERTS real Google Maps times where placeholders are.
  // Also handles cases where the LLM time was already shown (backward compat).
  const validateAndCorrectDriveTimes = (extractedDests, originCity, strippedMessage) => {
    // Validate ALL destinations that were identified as drive-distance (driveHours from extraction, or placeholder-flagged)
    const driveDestsToValidate = extractedDests.filter(d => d.driveHours != null || d.drivePlaceholder);
    if (driveDestsToValidate.length === 0) return;

    debugLog(`🗺️ Fetching real drive times for ${driveDestsToValidate.length} destinations from ${originCity}...`);

    // Pre-resolve countries for origin + all destinations (helps Google Maps resolve small towns like "Pinhão")
    const countryResolutionPromise = (async () => {
      try {
        const [originCountry, ...destCountries] = await Promise.all([
          getCityCountry(originCity),
          ...driveDestsToValidate.map(d => getCityCountry(d.name))
        ]);
        debugLog(`🌍 Country context resolved: origin=${originCity}→${originCountry}, dests=${driveDestsToValidate.map((d, i) => `${d.name}→${destCountries[i]}`).join(', ')}`);
        return { originCountry, destCountries };
      } catch (err) {
        debugLog(`⚠️ Country resolution failed, proceeding without country context: ${err.message}`);
        return { originCountry: null, destCountries: driveDestsToValidate.map(() => null) };
      }
    })();

    countryResolutionPromise.then(({ originCountry, destCountries }) => {
      return Promise.allSettled(
        driveDestsToValidate.map(async (dest, idx) => {
          try {
            const result = await calculateDriveTimeToDestination(originCity, dest.name, {
              originCountry: originCountry || undefined,
              destCountry: destCountries[idx] || undefined
            });
            return { dest, result };
          } catch (err) {
            debugError(`❌ Drive time fetch failed for ${dest.name}:`, err);
            return { dest, result: null };
          }
        })
      );
    }).then((results) => {
      const updates = [];
      results.forEach(({ status, value }) => {
        if (status !== 'fulfilled' || !value?.result) return;
        const { dest, result } = value;
        const realHours = result.hours;
        updates.push({ name: dest.name, realHours, isDriveable: result.isDriveable });
        debugLog(`🗺️ Real drive time for ${dest.name}: ${realHours}hr (Google Maps)`);
      });

      if (updates.length > 0) {
        // Update the message text — replace placeholder "⏳ drive" with real times
        setMessages(prev => {
          const updated = [...prev];
          for (let i = updated.length - 1; i >= Math.max(0, updated.length - 5); i--) {
            if (updated[i].role === 'assistant' && updated[i].content === strippedMessage) {
              let correctedContent = updated[i].content;
              const correctedDests = [...(updated[i].destinationOptions || extractedDests)];

              updates.forEach(({ name, realHours }) => {
                const realHoursDisplay = realHours >= 1
                  ? (realHours % 1 === 0 ? `${realHours}hr` : `${Math.floor(realHours)}hr ${Math.round((realHours % 1) * 60)}min`)
                  : `${Math.round(realHours * 60)}min`;

                // Replace placeholder near this destination name
                const destEscaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                // Try placeholder first (new flow)
                const placeholderPattern = new RegExp(
                  `(${destEscaped}[^\\n]{0,80}?)⏳\\s*drive`,
                  'i'
                );
                if (placeholderPattern.test(correctedContent)) {
                  correctedContent = correctedContent.replace(placeholderPattern, (match) => {
                    return match.replace(/⏳\s*drive/i, `${realHoursDisplay} drive`);
                  });
                } else {
                  // Fallback: replace LLM drive time (backward compat / if strip didn't catch it)
                  const driveTimePattern = new RegExp(
                    `(${destEscaped}[^\\n]{0,50}?)~?(\\d+(?:\\.\\d+)?)\\s*(?:-\\s*\\d+(?:\\.\\d+)?\\s*)?hr(?:\\s+\\d+\\s*min)?\\s*drive`,
                    'i'
                  );
                  correctedContent = correctedContent.replace(driveTimePattern, (match) => {
                    return match.replace(/~?\d+(?:\.\d+)?\s*(?:-\s*\d+(?:\.\d+)?\s*)?hr(?:\s+\d+\s*min)?\s*drive/i, `${realHoursDisplay} drive`);
                  });
                }

                const destIdx = correctedDests.findIndex(d => d.name === name);
                if (destIdx !== -1) {
                  correctedDests[destIdx] = { ...correctedDests[destIdx], driveHours: realHours, driveValidated: true, drivePlaceholder: false };
                }
              });

              // Clean up any remaining ⏳ placeholders (destinations that failed validation)
              correctedContent = correctedContent.replace(/⏳\s*drive/gi, 'drive');

              updated[i] = { ...updated[i], content: correctedContent, destinationOptions: correctedDests };
              break;
            }
          }
          return updated;
        });

        // Also update global destinationOptions
        setDestinationOptions(prev => prev.map(d => {
          const update = updates.find(u => u.name === d.name);
          return update ? { ...d, driveHours: update.realHours, driveValidated: true, drivePlaceholder: false } : d;
        }));

        debugLog(`✅ Inserted real drive times for ${updates.length} destination(s)`);
      } else {
        // No Google results — remove placeholders and show "drive" without time
        setMessages(prev => {
          const updated = [...prev];
          for (let i = updated.length - 1; i >= Math.max(0, updated.length - 5); i--) {
            if (updated[i].role === 'assistant' && updated[i].content === strippedMessage) {
              updated[i] = { ...updated[i], content: updated[i].content.replace(/⏳\s*drive/gi, 'drive') };
              break;
            }
          }
          return updated;
        });
        debugLog(`⚠️ No Google Maps results — removed placeholders`);
      }
    }).catch(err => {
      debugError('❌ Drive time validation batch error:', err);
      // On error, clean up placeholders
      setMessages(prev => {
        const updated = [...prev];
        for (let i = updated.length - 1; i >= Math.max(0, updated.length - 5); i--) {
          if (updated[i].role === 'assistant' && updated[i].content?.includes('⏳ drive')) {
            updated[i] = { ...updated[i], content: updated[i].content.replace(/⏳\s*drive/gi, 'drive') };
            break;
          }
        }
        return updated;
      });
    });
  };

  // Calculate travel time between two locations using backend proxy to Google Directions API
  // transitContext provides information about what kind of transit this is:
  // - type: 'same-day' | 'airport-transfer' | 'inter-city' | 'hotel-return'
  // - expectedCity: For same-day transit, what city should both points be in?
  // - fromCity/toCity: For inter-city transit, the expected cities
  const getTravelTime = async (origin, destination, mode = 'driving', transitContext = null) => {
    // Build context if not provided (backward compatibility)
    const context = transitContext || {
      type: 'same-day',
      expectedCity: null
    };
    
    // Include context in cache key
    const cacheKey = apiCacheRef.current.generateKey('transit', origin, destination, mode, context.type);
    const cached = apiCacheRef.current.get(cacheKey);
    if (cached) {
      debugLog(`📦 [CACHE HIT] ${mode}: ${cached.duration}`);
      return cached;
    }
    
    debugLog(`🗺️ [FRONTEND] Fetching ${mode} directions: ${origin} → ${destination} (${context.type})`);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/directions`, {
        method: 'POST',
        headers: await getApiHeaders(),
        body: JSON.stringify({ 
          origin, 
          destination, 
          mode, 
          transitContext: context 
        })
      });
      
      const data = await response.json();
      debugLog(`🗺️ [FRONTEND] Response for ${mode}:`, data);
      
      if (data.success) {
        // Parse duration to minutes for timeline calculations
        let durationMinutes = 15; // default fallback
        if (data.durationValue) {
          // durationValue is in seconds from the backend
          durationMinutes = Math.round(data.durationValue / 60);
        } else if (data.duration) {
          // Parse from duration string like "2 hr 30 mins" or "25 mins"
          const duration = data.duration;
          if (duration.includes('hr') || duration.includes('hour')) {
            const match = duration.match(/(\d+)\s*hr?\s*(\d*)/);
            if (match) durationMinutes = (parseInt(match[1]) || 0) * 60 + (parseInt(match[2]) || 0);
          } else {
            const mins = parseInt(duration);
            if (!isNaN(mins)) durationMinutes = mins;
          }
        }
        debugLog(`✅ [REAL TIME] ${mode}: ${data.duration} (${data.distance}) = ${durationMinutes} mins`);
        const result = {
          duration: data.duration,
          durationMinutes: durationMinutes,
          distance: data.distance,
          mode: mode
        };
        // Cache for 30 minutes
        apiCacheRef.current.set(cacheKey, result, 30 * 60 * 1000);
        return result;
      } else if (data.error === 'NO_ROUTE') {
        // No route available for this mode (e.g., transit not available in remote areas)
        debugLog(`⚠️ [NO ROUTE] ${mode} not available for this route`);

        // For driving in park areas, islands, or remote areas, Google often can't route
        // but driving IS possible - use estimates instead of marking unavailable
        const originLower = origin.toLowerCase();
        const destLower = destination.toLowerCase();

        const isParkArea = originLower.includes('national park') || destLower.includes('national park') ||
                          originLower.includes(' park,') || destLower.includes(' park,') ||
                          originLower.includes(' park ') || destLower.includes(' park ') ||
                          originLower.includes('trail') || destLower.includes('trail') ||
                          originLower.includes('trailhead') || destLower.includes('trailhead');

        // Check for islands and remote destinations where Google may lack routing data
        const islandKeywords = ['island', 'cay', 'key', 'isla', 'isle', 'atoll', 'roatan', 'cozumel', 'aruba',
                                'bahamas', 'maldives', 'bora bora', 'fiji', 'tahiti', 'seychelles', 'mauritius',
                                'zanzibar', 'santorini', 'mykonos', 'capri', 'bermuda', 'cayman', 'turks', 'virgin'];
        const isRemoteArea = islandKeywords.some(keyword =>
          originLower.includes(keyword) || destLower.includes(keyword)
        );

        // For driving in areas where Google lacks data but driving is still possible
        if (mode === 'driving' && (isParkArea || isRemoteArea)) {
          debugLog(`🏝️ [REMOTE AREA] Using driving estimate - Google lacks routing data`);
          return estimateTravelTime(origin, destination, mode);
        }

        // For walking in remote areas, provide an estimate instead of unavailable
        if (mode === 'walking' && isRemoteArea) {
          debugLog(`🏝️ [REMOTE AREA] Using walking estimate - Google lacks routing data`);
          return estimateTravelTime(origin, destination, mode);
        }

        const result = {
          duration: '0 mins', // Signal that this mode is unavailable
          distance: 'N/A',
          mode: mode,
          unavailable: true
        };
        // Cache the "no route" result too
        apiCacheRef.current.set(cacheKey, result, 30 * 60 * 1000);
        return result;
      } else if (data.error === 'DISTANCE_MISMATCH') {
        // Distance doesn't match expected context — the route is way too far for this transport mode in this context
        debugLog(`⚠️ [DISTANCE MISMATCH] ${data.message}`);
        // For walking, if the actual distance is huge (>10km), mark as unavailable rather than estimating
        if (mode === 'walking' && data.actualDistance && data.actualDistance > 10) {
          debugLog(`🚶 Walking marked unavailable — actual distance ${data.actualDistance}km`);
          const result = {
            duration: '0 mins',
            distance: `${data.actualDistance.toFixed(1)} km`,
            mode: mode,
            unavailable: true,
          };
          apiCacheRef.current.set(cacheKey, result, 30 * 60 * 1000);
          return result;
        }
        // For driving/transit with distance mismatch, use estimates
        return estimateTravelTime(origin, destination, mode);
      } else {
        // API returned error or no route - use estimates
        debugLog(`⚠️ [FALLBACK] Using estimates - API returned:`, data.error || 'no route');
        return estimateTravelTime(origin, destination, mode);
      }
    } catch (error) {
      debugError('❌ [ERROR] Getting travel time:', error);
      // Fall back to estimates
      return estimateTravelTime(origin, destination, mode);
    }
  };
  
  // Travel time fallback estimator — imported from @wander/shared via ./utils/travelTimeEstimates
  const estimateTravelTime = _estimateTravelTime;

  // Get multiple transportation options
  const getTransportationOptions = async (origin, destination, transitContext = null) => {
    const modes = ['driving', 'walking', 'transit'];
    const options = await Promise.all(
      modes.map(mode => getTravelTime(origin, destination, mode, transitContext))
    );
    return options.filter(opt => opt !== null);
  };

  // Fetch transit times for a specific route and cache them
  // transitContext: { type: 'same-day' | 'airport-transfer' | 'inter-city' | 'hotel-return', expectedCity?, fromCity?, toCity? }
  const fetchTransitTimes = async (transitKey, origin, destination, transitContext = null) => {
    // Check ref-based cache first (avoids stale closure issues)
    if (transitTimesRef.current[transitKey]) {
      return transitTimesRef.current[transitKey];
    }

    // De-duplicate in-flight requests: if this key is already being fetched, return the existing promise
    if (transitInFlightRef.current[transitKey]) {
      debugLog(`⏳ Transit fetch already in-flight for ${transitKey}, reusing promise`);
      return transitInFlightRef.current[transitKey];
    }

    const fetchPromise = (async () => {
      setLoadingTransit(prev => ({ ...prev, [transitKey]: true }));

      // Hard timeout for the whole fetch — regression fix. Previously if
      // getTravelTime hung (rate limit, slow network, geocoder miss), the
      // loadingTransit state stayed true forever and the UI showed
      // "Calculating..." indefinitely. Now we abandon the fetch after
      // FETCH_TIMEOUT_MS, the loading flag clears, and the renderer falls
      // back to item.duration estimate. The user sees a sensible value
      // instead of a perpetual spinner.
      const FETCH_TIMEOUT_MS = 10000;
      const timeoutGuard = setTimeout(() => {
        if (loadingTransit && loadingTransit[transitKey]) {
          debugLog(`⏱️ Transit fetch timed out for ${transitKey} — clearing loading state`);
          setLoadingTransit(prev => ({ ...prev, [transitKey]: false }));
          delete transitInFlightRef.current[transitKey];
        }
      }, FETCH_TIMEOUT_MS);

      try {
        const contextType = transitContext?.type || 'same-day';
        const tierLimitsTransit = getTierLimits ? getTierLimits() : {};
        const isPlannerOrAbove = tierLimitsTransit.allTransitModes === true;

        // Planner: fetch all 3 modes, auto-select quickest
        // Explorer: fetch only 1 mode (cheapest API cost)
        const primaryMode = contextType === 'inter-city' ? 'driving'
          : contextType === 'airport-transfer' ? 'driving'
          : 'transit';

        const modes = isPlannerOrAbove
          ? ['driving', 'transit', 'walking']
          : [primaryMode];

        const results = {};

        // Per-mode timeout so a single hung mode doesn't block the whole loop
        const PER_MODE_TIMEOUT_MS = 6000;
        const withTimeout = (p, ms) => Promise.race([
          p,
          new Promise((_, reject) => setTimeout(() => reject(new Error('per-mode-timeout')), ms)),
        ]);

        for (const mode of modes) {
          try {
            const result = await withTimeout(getTravelTime(origin, destination, mode, transitContext), PER_MODE_TIMEOUT_MS);
            if (result) {
              results[mode] = result;
            }
          } catch (err) {
            debugLog(`Could not get ${mode} directions${err?.message === 'per-mode-timeout' ? ' (timed out)' : ''}`);
          }
        }

        // If primary mode failed (e.g., no transit available), fallback to driving
        if (Object.keys(results).length === 0 && primaryMode !== 'driving') {
          try {
            const fallback = await getTravelTime(origin, destination, 'driving', transitContext);
            if (fallback) results['driving'] = fallback;
          } catch (err) {
            debugLog('Could not get fallback driving directions');
          }
        }

        // Cache the results in both ref and state
        debugLog(`📦 Caching transit times for ${transitKey}:`, Object.keys(results).length > 0 ? results : 'EMPTY');
        transitTimesRef.current[transitKey] = results; // Update ref immediately (no re-render)
        setTransitTimes(prev => {
          const updated = { ...prev, [transitKey]: results };
          return updated;
        });
        setLoadingTransit(prev => ({ ...prev, [transitKey]: false }));
        clearTimeout(timeoutGuard);
        return results;
      } catch (error) {
        debugError('Error fetching transit times:', error);
        setLoadingTransit(prev => ({ ...prev, [transitKey]: false }));
        clearTimeout(timeoutGuard);
        return null;
      } finally {
        // Clean up in-flight tracker
        delete transitInFlightRef.current[transitKey];
      }
    })();

    // Store the promise so subsequent calls can reuse it
    transitInFlightRef.current[transitKey] = fetchPromise;
    return fetchPromise;
  };

  // Lazy-fetch secondary transit modes (called when user clicks mode toggle on a transit card)
  // This fetches modes not yet cached, avoiding redundant API calls
  const fetchSecondaryTransitModes = async (transitKey, origin, destination, transitContext = null) => {
    const existing = transitTimesRef.current[transitKey] || {};
    const allModes = ['driving', 'transit', 'walking'];
    const missingModes = allModes.filter(m => !existing[m]);

    if (missingModes.length === 0) return existing; // All modes already cached

    debugLog(`🔄 Lazy-fetching secondary modes for ${transitKey}: ${missingModes.join(', ')}`);
    const results = { ...existing };

    await Promise.allSettled(missingModes.map(async (mode) => {
      try {
        const result = await getTravelTime(origin, destination, mode, transitContext);
        if (result) results[mode] = result;
      } catch (err) {
        debugLog(`Could not get ${mode} directions for secondary fetch`);
      }
    }));

    // Update caches
    transitTimesRef.current[transitKey] = results;
    setTransitTimes(prev => ({ ...prev, [transitKey]: results }));
    return results;
  };

  // Auto-calculate transit times when day changes
  // Use a stable fingerprint to avoid re-triggering on itinerary object reference changes
  const currentDayData = itinerary?.days?.[currentDay];

  // ─── Clear weather cache when dates change so we re-fetch ──────────────────
  const weatherDateKey = userProfile?.preferences?.startDate || itinerary?.startDate || '';
  const prevWeatherDateRef = useRef(weatherDateKey);
  useEffect(() => {
    if (prevWeatherDateRef.current && weatherDateKey && prevWeatherDateRef.current !== weatherDateKey) {
      // Dates changed — clear cached weather so it re-fetches
      setItinerary(prev => {
        if (!prev?.days) return prev;
        return { ...prev, days: prev.days.map(d => ({ ...d, weather: undefined })) };
      });
      setWeatherFetchTrigger(t => t + 1);
    }
    prevWeatherDateRef.current = weatherDateKey;
  }, [weatherDateKey]);

  // ─── Fetch weather for itinerary days (with retries) ──────────────────
  //
  // Prefetch strategy: iterate UNIQUE STOPS (not days). Previously we fanned
  // out one request per day, which caused Open-Meteo 429s on longer trips and
  // dropped weather for later stops. Now we coalesce by stop, geocode each
  // stop exactly once, and key results by BOTH the stop's canonical city and
  // each day's baseCity so lookup hits even when baseCity is hotel-contaminated
  // (e.g. "Auberge de la Mer, Cancún").
  const weatherRetryCountRef = useRef(0);
  useEffect(() => {
    if (stage !== 'itinerary' || !itinerary?.days?.length) return;
    const controller = new AbortController();

    const cleanCity = (raw) => {
      if (!raw || typeof raw !== 'string') return '';
      let c = raw;
      if (isHotelOrResort(c)) {
        c = c.replace(/\b(hotel|resort|spa|lodge|inn|villa|château|chateau|palace|grand|auberge)\b/gi, '').trim();
      }
      // Compound destinations like "Hanoi & Ha Long Bay" — take first.
      c = c.split(/\s*[&+]\s*/)[0];
      return c.split(',')[0].trim();
    };

    const fetchDayWeather = async (retryAttempt = 0) => {
      try {
        const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
        let tripStart = itinerary?.startDate || userProfile?.preferences?.startDate;
        if (!tripStart) {
          const fallback = new Date();
          fallback.setDate(fallback.getDate() + 30);
          tripStart = fallback.toISOString().split('T')[0];
        }

        const daysSnapshot = [...itinerary.days];

        // Build the authoritative list of unique stops. Prefer tripBuilderStops
        // (user-curated, canonical) and fall back to unique baseCities from
        // the days array. Each stop entry maps to one or more day indices.
        const stopEntries = new Map(); // canonicalLower → { label, lat?, lng?, dayIndices: [] }

        const pushStop = (label, idx) => {
          const canonical = cleanCity(label);
          if (!canonical) return;
          const key = canonical.toLowerCase();
          let entry = stopEntries.get(key);
          if (!entry) {
            entry = { label: canonical, dayIndices: [] };
            stopEntries.set(key, entry);
          }
          if (typeof idx === 'number' && !entry.dayIndices.includes(idx)) {
            entry.dayIndices.push(idx);
          }
        };

        // Seed with tripBuilderStops so every stop gets covered even if its
        // first day has hotel-contaminated baseCity.
        if (tripBuilderStops?.length) {
          for (const s of tripBuilderStops) {
            pushStop(s.resolvedLocation || s.name, null);
          }
        }

        // Walk days and attach indices to matching stops (match by canonical
        // prefix) or create a new stop entry if no match.
        daysSnapshot.forEach((day, idx) => {
          const dayCity = cleanCity(day.baseCity || day.city || selectedDestination?.name || '');
          if (!dayCity) return;
          const key = dayCity.toLowerCase();
          if (stopEntries.has(key)) {
            const entry = stopEntries.get(key);
            if (!entry.dayIndices.includes(idx)) entry.dayIndices.push(idx);
          } else {
            pushStop(dayCity, idx);
          }
        });

        // Assign day indices to seeded stops that still have none: best-effort
        // match via startsWith on the day's baseCity so e.g. "Cancún" stop
        // picks up days with baseCity "Cancún, Mexico".
        for (const [key, entry] of stopEntries.entries()) {
          if (entry.dayIndices.length) continue;
          daysSnapshot.forEach((day, idx) => {
            const dayCity = cleanCity(day.baseCity || day.city || '').toLowerCase();
            if (dayCity && (dayCity === key || dayCity.startsWith(key) || key.startsWith(dayCity))) {
              if (!entry.dayIndices.includes(idx)) entry.dayIndices.push(idx);
            }
          });
        }

        const iconMap = { 'clear': '☀️', 'partly-cloudy': '⛅', 'cloudy': '☁️', 'fog': '🌫️', 'drizzle': '🌦️', 'rain': '🌧️', 'heavy-rain': '🌧️', 'snow': '❄️', 'heavy-snow': '❄️' };

        // Fetch per unique stop (sequential across stops to stay under
        // Open-Meteo rate limits; parallel across days within one stop is
        // fine because the backend coalesces via inflight dedup + supabase).
        const results = []; // { dayIndex, weather }
        const stopSummary = []; // for debug log
        let fromClimo = 0;
        let fromForecast = 0;

        for (const entry of stopEntries.values()) {
          if (controller.signal.aborted) return;
          if (!entry.dayIndices.length) {
            stopSummary.push(`${entry.label} (no days)`);
            continue;
          }

          // Geocode once per stop. Try Open-Meteo (fast/free/city-focused)
          // first, then fall back to Google Places via backend for
          // resort/hotel-named stops that Open-Meteo can't resolve
          // (e.g. "Hermitage Bay").
          const cacheKey = entry.label.toLowerCase();
          let coords = geocodeCacheRef.current[cacheKey];
          if (!coords) {
            try {
              const geoRes = await fetch(
                `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(entry.label)}&count=1&language=en`,
                { signal: controller.signal }
              );
              if (geoRes.ok) {
                const geoData = await geoRes.json();
                if (geoData.results?.length) {
                  coords = { lat: geoData.results[0].latitude, lng: geoData.results[0].longitude };
                  geocodeCacheRef.current[cacheKey] = coords;
                }
              }
            } catch (e) {
              if (e.name === 'AbortError') return;
            }
          }
          if (!coords) {
            try {
              coords = await _placesFallbackCoords(entry.label, { signal: controller.signal });
              if (coords) geocodeCacheRef.current[cacheKey] = coords;
            } catch { /* non-blocking */ }
          }
          if (!coords) {
            stopSummary.push(`${entry.label} (geocode failed)`);
            continue;
          }

          // One fetch per day of this stop; the backend dedups and a ~30-day
          // trip only ever gets N distinct (lat,lng,date) pairs.
          let stopClimo = 0;
          let stopForecast = 0;
          const dayPromises = entry.dayIndices.map(async (idx) => {
            const day = daysSnapshot[idx];
            if (!day) return null;
            if (day.weather?.high != null) return null;
            const dayDate = new Date(tripStart + 'T12:00:00');
            dayDate.setDate(dayDate.getDate() + idx);
            const dateStr = dayDate.toISOString().split('T')[0];
            const dayId = day.title || `day-${idx}`;

            let weatherData = readClimoCache(coords.lat, coords.lng, dateStr);
            if (weatherData) {
              stopClimo += 1;
            } else {
              try {
                const authHeaders = await getApiHeaders();
                const weatherRes = await fetch(
                  `${API_BASE}/api/weather?lat=${coords.lat}&lng=${coords.lng}&date=${dateStr}`,
                  { signal: controller.signal, headers: authHeaders }
                );
                if (!weatherRes.ok) return null;
                weatherData = await weatherRes.json();
                writeClimoCache(coords.lat, coords.lng, dateStr, weatherData);
                if (weatherData?.source === 'climate') stopClimo += 1;
                else stopForecast += 1;
              } catch (e) {
                if (e.name === 'AbortError') return null;
                return null;
              }
            }
            return { dayIndex: idx, dayId, weather: {
              ...weatherData,
              icon: iconMap[weatherData.icon] || '🌤',
              rainChance: weatherData.precipProbability || 0,
              condition: weatherData.description,
            }};
          });

          const stopResults = (await Promise.all(dayPromises)).filter(Boolean);
          results.push(...stopResults);
          fromClimo += stopClimo;
          fromForecast += stopForecast;
          stopSummary.push(entry.label);
        }

        const successIndices = new Set(results.map(r => r.dayIndex));

        debugLog(`[Weather] Prefetched stops: ${stopSummary.join(', ')} (${fromClimo} from climatology, ${fromForecast} from forecast)`);

        setItinerary(prev => {
          if (!prev?.days) return prev;
          const newDays = [...prev.days];
          results.forEach(({ dayIndex, dayId, weather }) => {
            const matchIdx = newDays.findIndex(d => d.title === dayId) !== -1
              ? newDays.findIndex(d => d.title === dayId)
              : dayIndex;
            if (newDays[matchIdx] && !newDays[matchIdx].weather?.high) {
              newDays[matchIdx] = { ...newDays[matchIdx], weather };
            }
          });
          daysSnapshot.forEach((day, idx) => {
            if (!successIndices.has(idx) && day.weather === undefined && newDays[idx] && !newDays[idx].weather) {
              newDays[idx] = { ...newDays[idx], weather: { _failed: true } };
            }
          });
          return { ...prev, days: newDays };
        });

        // Auto-retry failed days (up to 3 attempts with exponential backoff)
        const failedDays = daysSnapshot.filter((day, idx) => !successIndices.has(idx) && day.weather === undefined);
        if (failedDays.length > 0 && retryAttempt < 3) {
          const delay = Math.min(2000 * Math.pow(2, retryAttempt), 10000);
          debugLog(`[Weather] ${failedDays.length} days failed, retrying in ${delay}ms (attempt ${retryAttempt + 1}/3)`);
          setTimeout(() => {
            if (!controller.signal.aborted) {
              setItinerary(prev => {
                if (!prev?.days) return prev;
                const retryDays = prev.days.map(d =>
                  d.weather?._failed ? { ...d, weather: undefined } : d
                );
                return { ...prev, days: retryDays };
              });
              setWeatherFetchTrigger(t => t + 1);
            }
          }, delay);
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('Itinerary weather fetch failed:', err.message);
          if (retryAttempt < 3) {
            const delay = Math.min(3000 * Math.pow(2, retryAttempt), 15000);
            setTimeout(() => {
              if (!controller.signal.aborted) fetchDayWeather(retryAttempt + 1);
            }, delay);
          }
        }
      }
    };
    fetchDayWeather(0);
    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, itinerary?.days?.length, itinerary?.startDate, userProfile?.preferences?.startDate, itinerary?.destination, weatherFetchTrigger]);

  const transitFingerprint = useMemo(() => {
    if (!currentDayData) return null;
    // Build a stable string from the data that actually matters for transit calculation
    const hotel = currentDayData.hotel?.name || '';
    const activities = (currentDayData.schedule || []).map(a => `${a.time}|${a.location || a.activity}`).join(';');
    const meals = (currentDayData.meals || []).map(m => `${m.time}|${m.restaurant}`).join(';');
    return `${currentDay}:${hotel}:${activities}:${meals}`;
  }, [currentDayData?.hotel?.name, currentDayData?.schedule, currentDayData?.meals, currentDay]);

  useEffect(() => {
    if (itinerary && stage === 'itinerary' && transitFingerprint) {
      // Guard: skip if already calculating for this exact fingerprint
      if (transitCalcGuardRef.current === transitFingerprint) {
        debugLog(`⏭️ Transit calc already running/done for fingerprint, skipping`);
        return;
      }
      transitCalcGuardRef.current = transitFingerprint;

      const calculateDayTransitTimes = async () => {
        const dayData = itinerary.days?.[currentDay];
        if (!dayData) return;
        
        // Get city name for context
        const cityName = selectedDestination?.name || itinerary.destination || '';
        
        // Helper to add city context to location for better Google results
        const addCityContext = (location) => {
          if (!location) return location;
          // If location already has city/country info, don't add
          if (location.includes(',') || location.toLowerCase().includes(cityName.toLowerCase())) {
            return location;
          }
          return `${location}, ${cityName}`;
        };
        
        // Collect all locations for the day (in order)
        const allItems = [];
        
        // Add hotel as first location
        if (dayData.hotel?.name) {
          allItems.push({ 
            location: addCityContext(dayData.hotel.address || dayData.hotel.name), 
            type: 'hotel',
            time: '00:00'
          });
        }
        
        // Add activities
        if (dayData.schedule) {
          dayData.schedule.forEach((item, idx) => {
            // Use the most specific location available
            const specificLocation = item.address || item.location || item.activity;
            if (specificLocation) {
              allItems.push({ 
                location: addCityContext(specificLocation), 
                type: 'activity', 
                idx,
                time: item.time,
                name: item.activity // Keep name for debugging
              });
            }
          });
        }
        
        // Add meals
        if (dayData.meals) {
          dayData.meals.forEach((item, idx) => {
            // Use address first, then restaurant name with city context
            const specificLocation = item.address || `${item.restaurant}, ${cityName}`;
            if (item.restaurant || item.address) {
              allItems.push({ 
                location: addCityContext(specificLocation), 
                type: 'meal', 
                idx,
                time: item.time,
                name: item.restaurant // Keep name for debugging
              });
            }
          });
        }
        
        // Sort by time (use parseTime to correctly handle both 24h and 12h AM/PM formats)
        allItems.sort((a, b) => parseTime(a.time) - parseTime(b.time));
        
        debugLog(`🗺️ Calculating transit times for Day ${currentDay + 1}:`, allItems.map(i => i.location));

        // For Day 1, calculate airport to hotel transit
        debugLog(`  🛫 Day 1 airport check: currentDay=${currentDay}, arrivalAirport=${itinerary.arrivalAirport?.code || 'NOT SET'}, hotel=${dayData.hotel?.name || 'NOT SET'}`);
        if (currentDay === 0 && dayData.hotel) {
          const hotelTransitKey = `day${currentDay}-hotel-transit`;
          // Determine the airport name - use itinerary.arrivalAirport if set, otherwise use destination city
          let airportName;
          let fromCity;
          if (itinerary.arrivalAirport?.code) {
            const arrCityForTransit = safeAirportCity(itinerary.arrivalAirport, dayData.baseCity || cityName);
            airportName = geocodableAirportName(itinerary.arrivalAirport);
            fromCity = arrCityForTransit;
          } else {
            // Fallback: Use the destination city's main airport — B9B: resolve IATA code
            const destCity = dayData.baseCity || cityName;
            const destIata = lookupCityAirportCode(destCity);
            airportName = destIata ? `${destIata} International Airport` : `${destCity} Airport`;
            fromCity = destCity;
            debugLog(`  ⚠️ No arrivalAirport set, using destination city airport: ${airportName}`);
          }
          const hotelLocation = addCityContext(dayData.hotel.address || dayData.hotel.name);
          debugLog(`  Fetching airport to hotel: ${airportName} -> ${hotelLocation}`);
          await fetchTransitTimes(hotelTransitKey, airportName, hotelLocation, {
            type: 'airport-transfer',
            fromCity: fromCity,
            toCity: dayData.baseCity || cityName
          });
        }
        
        // Calculate transit times between consecutive activities (skip hotel at index 0)
        // The timeline creates transit cards with keys like day0-transit-0, day0-transit-1, etc.
        // IMPORTANT: Use a counter that matches the timeline builder's transitCardCounter logic
        // The timeline builder skips transit cards when there's a free time gap (60+ min)
        const activitiesOnly = allItems.slice(1); // Skip hotel at index 0
        let transitCounter = 0; // Match timeline builder's transitCardCounter

        // Helper to check if location is at hotel (must match timeline builder logic)
        // ALIGNED: Pass actual activity/restaurant names like the timeline builder does
        const isAtHotel = (location, activity, restaurant, address) => {
          return isHotelActivityValidator(location, activity || '', restaurant || '', dayData.hotel?.name || '', dayData?.baseCity || '', address || '');
        };

        // Helper to normalize location for comparison (must match timeline builder)
        // ALIGNED: Added ^the\s+ strip to match timeline builder's normalizeLocation
        const normalizeLocation = (loc) => {
          if (!loc) return '';
          return loc.toLowerCase()
            .replace(/^the\s+/i, '')  // Strip leading "The" (e.g., "The Uffizi" vs "Uffizi")
            .replace(/,\s*[a-z]+$/i, '')
            .replace(/\s+/g, ' ')
            .trim();
        };

        // Helper to parse duration strings to minutes (aligned with timeline builder's parseDurationToMinutes)
        const parseDurationMins = (durationStr) => {
          if (!durationStr) return 60; // default 1 hour
          const str = durationStr.toLowerCase();
          let totalMins = 0;
          const hoursMatch = str.match(/(\d+\.?\d*)\s*(?:hour|hr|h\b)/);
          if (hoursMatch) totalMins += parseFloat(hoursMatch[1]) * 60;
          const minsMatch = str.match(/(\d+\.?\d*)\s*(?:min|m\b)/);
          if (minsMatch) totalMins += parseFloat(minsMatch[1]);
          if (totalMins === 0) {
            const fallback = str.match(/(\d+\.?\d*)/);
            if (fallback) {
              const val = parseFloat(fallback[1]);
              totalMins = val < 12 ? val * 60 : val;
            } else {
              totalMins = 60;
            }
          }
          return totalMins;
        };

        for (let i = 0; i < activitiesOnly.length - 1; i++) {
          const currentItem = activitiesOnly[i];
          const nextItem = activitiesOnly[i + 1];

          // Check if BOTH are at hotel (skip just like timeline builder does)
          // ALIGNED: Pass activity/restaurant names to match timeline builder's isHotelActivity calls
          const currentActivity = (currentItem.name || '').toLowerCase();
          const nextActivity = (nextItem.name || '').toLowerCase();
          const currentAtHotel = isAtHotel(currentItem.location, currentActivity, currentItem.name) || isAtHotel(currentItem.name, currentActivity, currentItem.name);
          const nextAtHotel = isAtHotel(nextItem.location, nextActivity, nextItem.name) || isAtHotel(nextItem.name, nextActivity, nextItem.name);
          const sameLocation = normalizeLocation(currentItem.location) === normalizeLocation(nextItem.location);

          if ((currentAtHotel && nextAtHotel) || sameLocation) {
            debugLog(`  ⏭️ Skipping fetch (useEffect): both at hotel or same location`);
            // DON'T increment counter - matches timeline builder behavior
            continue;
          }

          // ALIGNED: Transit is now ALWAYS fetched (no gap-skip).
          // The timeline builder always inserts transit cards regardless of time gaps
          // (see comment at line 11463-11465). Free time is added separately.
          // Previously we skipped fetch when gap >= 60 min, but this caused key desync
          // because the timeline builder always increments its counter.

          const transitKey = `day${currentDay}-transit-${transitCounter}`;
          debugLog(`  Fetching transit-${transitCounter}: ${currentItem.location} -> ${nextItem.location}`);
          await fetchTransitTimes(transitKey, currentItem.location, nextItem.location, {
            type: 'same-day',
            expectedCity: dayData.baseCity || cityName
          });
          transitCounter++; // Always increment to stay in sync with timeline builder
        }
        
        // Calculate return to hotel (from last activity) — SKIP on last day (departure day)
        // On the last day, the traveler heads to the airport, not back to the hotel
        const isLastDayForTransit = currentDay === (Math.max(totalItineraryDays, itinerary.days?.length || 0)) - 1;
        const activitiesOnlyForReturn = allItems.slice(1); // Skip hotel at index 0
        if (!isLastDayForTransit && activitiesOnlyForReturn.length > 0) {
          const lastTransitKey = `day${currentDay}-last-to-hotel`;
          if (!transitTimesRef.current[lastTransitKey]) {
            const lastActivity = activitiesOnlyForReturn[activitiesOnlyForReturn.length - 1];
            const hotelLocation = addCityContext(dayData.hotel?.address || dayData.hotel?.name || 'Hotel');
            debugLog(`  Fetching return: ${lastActivity.location} -> ${hotelLocation}`);
            await fetchTransitTimes(lastTransitKey, lastActivity.location, hotelLocation, {
              type: 'hotel-return',
              expectedCity: dayData.baseCity || cityName
            });
          }
        } else if (isLastDayForTransit) {
          debugLog(`  ⏭️ Skipping last-to-hotel fetch: departure day — heading to airport, not hotel`);
        }

        // Fetch hotel to first activity transit
        if (allItems.length > 1) {  // Need at least hotel + 1 activity
          const hotelToFirstKey = `day${currentDay}-hotel-to-first`;
          const hotelLocation = addCityContext(dayData.hotel?.address || dayData.hotel?.name || 'Hotel');
          const firstActivityLocation = allItems[1]?.location;
          if (firstActivityLocation && firstActivityLocation !== hotelLocation) {
            debugLog(`  🚗 Fetching hotel to first: ${hotelLocation} -> ${firstActivityLocation}`);
            await fetchTransitTimes(hotelToFirstKey, hotelLocation, firstActivityLocation, {
              type: 'same-day',
              expectedCity: dayData.baseCity || cityName
            });
          } else {
            debugLog(`  ⏭️ Skipping hotel to first: no location or same as hotel`);
          }
        } else {
          debugLog(`  ⏭️ Skipping hotel to first: not enough items (${allItems.length})`);
        }

        // For last day, fetch hotel to airport transit
        // Use totalItineraryDays (planned total) to avoid premature airport transit fetching during progressive generation
        // Also check if this is the last generated day (handles cases where fewer days were generated than planned)
        const isLastDay = currentDay === (Math.max(totalItineraryDays, itinerary.days?.length || 0)) - 1 || currentDay === (itinerary.days?.length || 0) - 1;
        if (isLastDay && itinerary.departureAirport) {
          const airportTransitKey = 'last-day-airport-transit';
          if (!transitTimesRef.current[airportTransitKey]) {
            // For departure: use previous day's hotel if current day has no hotel
            // (e.g., user sleeps in Ericeira on Day 2, departs from Porto on Day 3)
            const prevDayDataForAirport = currentDay > 0 ? itinerary.days[currentDay - 1] : null;
            const departureHotel = dayData.hotel || prevDayDataForAirport?.hotel;
            if (departureHotel) {
              // Use the hotel's city context, not the departure day's baseCity
              const hotelCity = departureHotel === dayData.hotel
                ? (dayData.baseCity || cityName)
                : (prevDayDataForAirport?.baseCity || dayData.baseCity || cityName);
              const hotelLocation = `${departureHotel.address || departureHotel.name || 'Hotel'}, ${hotelCity}`;
              const depAirportCity = safeAirportCity(itinerary.departureAirport, hotelCity);
              const airportName = geocodableAirportName(itinerary.departureAirport);
              debugLog(`  Fetching hotel to airport: ${hotelLocation} -> ${airportName}`);
              await fetchTransitTimes(airportTransitKey, hotelLocation, airportName, {
                type: 'airport-transfer',
                fromCity: hotelCity,
                toCity: depAirportCity
              });
            }
          }
        }

        // For inter-city travel days, fetch airport transit times
        const prevDayData = currentDay > 0 ? itinerary.days[currentDay - 1] : null;
        const isInterCityDay = prevDayData &&
          dayData.baseCity && prevDayData.baseCity &&
          dayData.baseCity.toLowerCase() !== prevDayData.baseCity.toLowerCase();

        if (isInterCityDay && prevDayData?.hotel) {
          // Helper: find nearest airport for a city from itinerary.transportInfo
          // transportInfo is keyed by stop name (e.g., "Tórshavn") and contains nearestAirport: { code, name, city }
          // Returns full airport name for accurate Google Maps transit lookup
          const getAirportNameForCity = (city) => {
            if (!city || !itinerary?.transportInfo) return `${city} International Airport`;
            // Try exact match first, then case-insensitive
            const cityLower = city.toLowerCase().trim();
            for (const [stopName, info] of Object.entries(itinerary.transportInfo)) {
              if (stopName.toLowerCase().trim() === cityLower && info?.nearestAirport?.code) {
                // Use geocodableAirportName — never append destination city to IATA code
                return geocodableAirportName(info.nearestAirport);
              }
            }
            // Try partial match (baseCity might be "Tórshavn" but stop is "Faroe Islands")
            for (const [stopName, info] of Object.entries(itinerary.transportInfo)) {
              if (info?.nearestAirport?.city?.toLowerCase().trim() === cityLower && info.nearestAirport.code) {
                return geocodableAirportName(info.nearestAirport);
              }
            }
            return `${city} International Airport`;
          };

          // Transit from previous hotel to departure airport
          const toAirportKey = `day${currentDay}-to-departure-airport`;
          if (!transitTimesRef.current[toAirportKey]) {
            const prevHotelLocation = `${prevDayData.hotel.name}, ${prevDayData.baseCity}`;
            const departureAirportName = getAirportNameForCity(prevDayData.baseCity);
            debugLog(`  Fetching inter-city to airport: ${prevHotelLocation} -> ${departureAirportName}`);
            await fetchTransitTimes(toAirportKey, prevHotelLocation, departureAirportName, {
              type: 'airport-transfer',
              fromCity: prevDayData.baseCity,
              toCity: prevDayData.baseCity
            });
          }

          // Transit from arrival airport to current hotel
          const fromAirportKey = `day${currentDay}-from-arrival-airport`;
          if (!transitTimesRef.current[fromAirportKey] && dayData.hotel) {
            const currentHotelLocation = `${dayData.hotel.name}, ${dayData.baseCity}`;
            const arrivalAirportName = getAirportNameForCity(dayData.baseCity);
            debugLog(`  Fetching inter-city from airport: ${arrivalAirportName} -> ${currentHotelLocation}`);
            await fetchTransitTimes(fromAirportKey, arrivalAirportName, currentHotelLocation, {
              type: 'airport-transfer',
              fromCity: dayData.baseCity,
              toCity: dayData.baseCity
            });
          }
        }
      };

      debugLog(`🚀 Starting transit time calculation for day ${currentDay}`);
      calculateDayTransitTimes().then(() => {
        debugLog(`✅ Transit time calculation completed for day ${currentDay}`);
      }).catch(err => {
        debugError(`❌ Transit time calculation failed for day ${currentDay}:`, err);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transitFingerprint, stage]);

  // Fetch dynamic transport modes for inter-city pairs using Haiku
  // Also pre-resolve city countries/continents into cache for the transport validation guard
  useEffect(() => {
    if (!itinerary?.days || itinerary.days.length < 2) return;

    const fetchDynamicTransportModes = async () => {
      // Collect unique city pairs that need transport mode lookup
      const cityPairs = [];
      const allBaseCities = [];
      for (let i = 1; i < itinerary.days.length; i++) {
        const prevDay = itinerary.days[i - 1];
        const currDay = itinerary.days[i];

        if (!prevDay?.baseCity || !currDay?.baseCity) continue;

        // Collect all unique base cities for pre-resolution
        if (i === 1) allBaseCities.push(prevDay.baseCity);
        allBaseCities.push(currDay.baseCity);

        if (prevDay.baseCity.toLowerCase() === currDay.baseCity.toLowerCase()) continue;

        const pairKey = `${prevDay.baseCity}|${currDay.baseCity}`.toLowerCase();

        // Skip if already fetched or if AI provided transport data
        const hasTransportData = currDay.transportData?.mode ||
          itinerary.interCityTransport?.find(t =>
            t.fromCity?.toLowerCase() === prevDay.baseCity.toLowerCase() &&
            t.toCity?.toLowerCase() === currDay.baseCity.toLowerCase()
          )?.mode;

        if (!dynamicTransportModes[pairKey] && !hasTransportData) {
          cityPairs.push({ from: prevDay.baseCity, to: currDay.baseCity, key: pairKey });
        }
      }

      // Pre-resolve all base cities' countries and continents into cache
      // This populates dynamicCityCountryCache + dynamicCountryContinentCache
      // so the transport validation guard (getCityCountrySync) can use them
      const uniqueBaseCities = [...new Set(allBaseCities)];
      await preResolveCities(uniqueBaseCities);

      // Fetch transport modes for each unique pair
      for (const pair of cityPairs) {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/quick-transport`, {
            method: 'POST',
            headers: await getApiHeaders(),
            body: JSON.stringify({ fromCity: pair.from, toCity: pair.to })
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              debugLog(`🚀 Dynamic transport: ${pair.from} → ${pair.to} = ${data.mode} (${data.duration})`);
              setDynamicTransportModes(prev => ({
                ...prev,
                [pair.key]: data
              }));
              // Populate dynamic timezone cache from transport response
              if (data.timezone) {
                if (data.timezone.from && pair.from) {
                  const fromLower = pair.from.toLowerCase().split(',')[0].trim();
                  if (!CITY_TO_IANA_TIMEZONE[fromLower]) {
                    dynamicTimezoneCache[fromLower] = data.timezone.from;
                    debugLog(`🕐 Cached timezone: ${pair.from} = ${data.timezone.from}`);
                  }
                }
                if (data.timezone.to && pair.to) {
                  const toLower = pair.to.toLowerCase().split(',')[0].trim();
                  if (!CITY_TO_IANA_TIMEZONE[toLower]) {
                    dynamicTimezoneCache[toLower] = data.timezone.to;
                    debugLog(`🕐 Cached timezone: ${pair.to} = ${data.timezone.to}`);
                  }
                }
              }

              // B6: After Haiku determines mode, fetch actual times from Google Directions
              // for driving and transit modes (flights stay as Haiku estimates)
              const haikuMode = data.mode;
              if (haikuMode && haikuMode !== 'flight') {
                const fetchDirectionsForMode = async (googleMode, label) => {
                  const dirKey = `${pair.from}|${pair.to}|${googleMode}`.toLowerCase();
                  try {
                    const dirResp = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/directions`, {
                      method: 'POST',
                      headers: await getApiHeaders(),
                      body: JSON.stringify({
                        origin: `${pair.from} central station`,
                        destination: `${pair.to} central station`,
                        mode: googleMode,
                        transitContext: { type: 'inter-city' }
                      })
                    });
                    if (dirResp.ok) {
                      const dirData = await dirResp.json();
                      if (dirData.success) {
                        debugLog(`📍 Inter-city ${label}: ${pair.from} → ${pair.to} = ${dirData.duration}`);
                        setInterCityDirectionsTimes(prev => ({
                          ...prev,
                          [dirKey]: {
                            duration: dirData.duration,
                            durationMinutes: Math.round((dirData.durationValue || 0) / 60),
                            estimated: dirData.estimated || false
                          }
                        }));
                      }
                    }
                  } catch (dirErr) {
                    debugError(`Failed Google Directions ${label} for ${pair.from} → ${pair.to}:`, dirErr);
                  }
                };

                // Fetch directions for the recommended mode
                if (haikuMode === 'train' || haikuMode === 'ferry') {
                  fetchDirectionsForMode('transit', 'transit');
                }
                if (haikuMode === 'car') {
                  fetchDirectionsForMode('driving', 'driving');
                }
                // Also pre-fetch the alternative mode so toggle shows real times
                if (haikuMode === 'train' || haikuMode === 'ferry') {
                  fetchDirectionsForMode('driving', 'driving');
                } else if (haikuMode === 'car') {
                  fetchDirectionsForMode('transit', 'transit');
                }
              }
            }
          }
        } catch (error) {
          debugError(`Failed to fetch transport for ${pair.from} → ${pair.to}:`, error);
        }
      }
    };

    fetchDynamicTransportModes();
  }, [itinerary?.days?.length, itinerary?.interCityTransport]);

  // Get REAL hotel name using Claude AI
  const getRealHotelName = async (destination, area = 'City Center') => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 200,
          system: `You are a travel expert. Suggest ONE real, CURRENTLY OPERATING hotel name in the specified area. 
CRITICAL: Only suggest hotels that are currently open for business in 2025-2026. Do NOT suggest:
- Hotels that have closed permanently
- Hotels under renovation/closed for refurbishment
- Hotels that have changed names or been demolished
Return ONLY the hotel name, nothing else. No explanation, no quotes, just the name.
Examples of good responses:
- "Marriott Marquis Times Square"
- "The Ritz-Carlton, Central Park"
- "Hampton Inn Downtown"`,
          messages: [{
            role: "user",
            content: `Suggest one real, currently operating hotel in ${destination}, in the ${area} area. Return only the hotel name.`
          }]
        })
      });
      
      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        return `${area} Hotel`; // Fallback on missing content
      }
      let hotelName = data.content[0].text.trim();

      // Clean up response (remove quotes, extra text)
      hotelName = hotelName.replace(/["']/g, '').replace(/^Hotel:\s*/i, '').split('\n')[0];

      return hotelName || `${area} Hotel`;
    } catch (error) {
      debugError('Error fetching real hotel name:', error);
      return `${area} Hotel`; // Fallback
    }
  };

  // Convert price to tier
  const getPriceTier = (price) => {
    if (!price) return '$';
    if (price <= 20) return '$';
    if (price <= 60) return '$$';
    return '$$$';
  };

  const generateGoogleMapsUrl = (location, address) => {
    const query = encodeURIComponent(`${location}, ${address}`);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };

  // Smart link generator based on item type - prioritizes LOCATION over activity name
  const getSmartLink = (item, type, city) => {
    // If we have a placeId from Google Places enrichment, use it for accuracy
    if (item.placeId) {
      return `https://www.google.com/maps/place/?q=place_id:${item.placeId}`;
    }
    
    // Prioritize address/location over activity name for accurate map results
    const location = item.address || item.location || '';
    const name = item.restaurant || item.name || ''; // Only use restaurant name, not activity name
    
    // For activities, use location only (not "Lima Historic Center Walking Tour")
    // For restaurants/hotels, use name + location
    let searchQuery;
    if (type === 'activity') {
      searchQuery = location ? `${location}, ${city}` : `${item.activity}, ${city}`;
    } else {
      searchQuery = location ? (name ? `${name}, ${location}` : `${location}, ${city}`) : `${name}, ${city}`;
    }
    
    const query = encodeURIComponent(searchQuery);
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  };
  
  // Get directions link - from origin (hotel or previous location) to destination
  const getDirectionsLink = (item, city, originAddress) => {
    // If we have a placeId, use it for the destination
    const destinationQuery = item.placeId 
      ? `place_id:${item.placeId}`
      : encodeURIComponent(`${item.address || item.location || item.activity || item.restaurant}, ${city}`);
    
    // If we have an origin (hotel address), include it
    if (originAddress) {
      const originQuery = encodeURIComponent(originAddress);
      return `https://www.google.com/maps/dir/?api=1&origin=${originQuery}&destination=${destinationQuery}`;
    }
    
    // Fallback to just destination (will use user's current location)
    return `https://www.google.com/maps/dir/?api=1&destination=${destinationQuery}`;
  };
  
  // Get Google Flights link — supports one-way, round-trip, and multi-city
  // type: 'oneway' | 'roundtrip' | 'multicity'
  // legs: for multicity, array of { from, to, date } objects
  const getFlightsLink = (fromCity, toCity, date, options = {}) => {
    const { returnDate, type, legs } = options;

    // Multi-city: use Google Flights multi-city URL format
    if (type === 'multicity' && legs?.length > 1) {
      const legParams = legs.map(leg => `${encodeURIComponent(leg.from)}+to+${encodeURIComponent(leg.to)}`).join('+then+');
      const dateParam = legs[0]?.date ? `&date=${legs[0].date}` : '';
      return `https://www.google.com/travel/flights?q=flights+from+${legParams}${dateParam}&tt=multicity`;
    }

    // Round-trip: include return date
    if ((type === 'roundtrip' || returnDate) && returnDate) {
      const formattedDate = date ? `&date=${date}` : '';
      return `https://www.google.com/travel/flights?q=flights+from+${encodeURIComponent(fromCity)}+to+${encodeURIComponent(toCity)}${formattedDate}&return=${returnDate}`;
    }

    // Default: one-way
    const formattedDate = date ? `&date=${date}` : '';
    return `https://www.google.com/travel/flights?q=flights+from+${encodeURIComponent(fromCity)}+to+${encodeURIComponent(toCity)}${formattedDate}`;
  };

  // ─── Shared createDayTimeline bridge ──────────────────────────────────
  // Web historically carried its own ~4300-line copy of createDayTimeline
  // (below). The shared package (@wander/shared) hosts the canonical engine
  // that mobile already consumes. `tryCreateDayTimelineShared` calls the
  // shared engine with a mapped context; on any error it returns `null` so
  // the caller can fall back to the local copy, giving us zero-regression
  // migration. Flag-overridable via `window.__WANDER_USE_SHARED_TIMELINE`
  // for QA.
  const tryCreateDayTimelineShared = (dayData, isFirstDay, isLastDay) => {
    const flag = (typeof window !== 'undefined' && typeof window.__WANDER_USE_SHARED_TIMELINE === 'boolean')
      ? window.__WANDER_USE_SHARED_TIMELINE
      : true; // Default ON — migration commit.
    if (!flag) return null;
    try {
      const ctx = buildWebTimelineContext({
        itinerary,
        selectedDestination,
        userProfile,
        profile,
        arrivalFlight,
        driveDetails,
        transportationMode,
        transitTimes,
        transportModes,
        dynamicTransportModes,
        destinationAirports,
        effectiveReturnMode,
        departureFlight,
        quickTransportResult,
        airportDistances,
        calculatedDriveTime,
        customTransportDesc,
        transitTimesRef,
        validatedFlightDurations,
        selectedHotel,
        accommodationType,
        generatedHotelName,
        customAccommodationName,
        tripType,
        tripBuilderStops,
        hasOvernightOutbound,
        dynamicGeographyCache,
        perLegTransport: legTransports,
        logger: { log: debugLog, warn: debugWarn, error: debugError },
      });
      return createDayTimelineShared(dayData, { isFirstDay, isLastDay, context: ctx });
    } catch (err) {
      debugWarn('[shared createDayTimeline] failed, falling back to local copy:', err);
      return null;
    }
  };

  // NEW: Generate Unsplash image URL for activities/restaurants
  // NEW: Merge activities and meals into chronological timeline
  const createDayTimeline = (dayData, isFirstDay, isLastDay) => {
    const sharedResult = tryCreateDayTimelineShared(dayData, isFirstDay, isLastDay);
    if (sharedResult) return sharedResult;
    // Fallback: run the local copy below. All edits to the local copy
    // remain the reference implementation for now.
    return createDayTimelineLocal(dayData, isFirstDay, isLastDay);
  };

  // Local implementation retained as fallback during migration. This is
  // the source used when __WANDER_USE_SHARED_TIMELINE=false or the shared
  // engine throws. To delete: remove this block + tryCreateDayTimelineShared
  // + rename back to createDayTimeline, after a full release cycle of
  // shared-path QA.
  const createDayTimelineLocal = (dayData, isFirstDay, isLastDay) => {
    let timeline = [];
    let cutoffStartTime = 0;
    let cutoffEndTime = 24 * 60;
    let isOvernightDepartureDay = false;

    // parseDurationToMinutes — using the shared version imported from @wander/shared via itineraryHelpers

    // IMPORTANT: Calculate dayIndex FIRST - needed throughout the function
    // The day number (1-based) is the most reliable identifier since normalization preserves it.
    // Object reference comparison (===) fails on normalized data (deep clones).
    let dayIndex = -1;
    if (itinerary?.days) {
      // Primary: match by day number (unique, reliable after normalization)
      if (dayData?.day != null) {
        dayIndex = itinerary.days.findIndex(d => d.day === dayData.day);
      }
      // Fallback: try object reference (works for non-normalized data)
      if (dayIndex === -1) {
        dayIndex = itinerary.days.findIndex(d => d === dayData);
      }
      // Fallback: match by baseCity + position in that city's sequence
      if (dayIndex === -1) {
        dayIndex = itinerary.days.findIndex(d =>
          d.baseCity === dayData.baseCity &&
          d.day === dayData.day
        );
      }
      if (dayIndex === -1) {
        debugWarn(`⚠️ createDayTimeline: Could not find dayIndex for dayData`, dayData?.baseCity, dayData?.day);
        // Use day number (1-based) to derive 0-based index as last resort
        dayIndex = dayData?.day ? dayData.day - 1 : Math.max(0, isFirstDay ? 0 : 1);
      }
    }

    timelineLog(`📅 createDayTimeline: dayIndex=${dayIndex}, isFirstDay=${isFirstDay}, baseCity=${dayData?.baseCity}`);

    // For inter-city travel days: track separate windows for pre-checkout and post-checkin activities
    let preCheckoutCutoffEnd = null; // Set on inter-city travel days to allow morning activities
    let postCheckinCutoffStart = null; // Set on inter-city travel days
    // D3: Track transport departure/arrival minutes for meal blocking
    let icTransportDepartureMinutes = null;
    let icTransportArrivalMinutes = null;

    // Wrapper for inter-city travel duration parsing with smart fallback
    const parseDurationToMinutesTravel = (duration, fromCity = null, toCity = null) => {
      if (duration) return parseDurationToMinutes(duration);
      // No duration string — try Haiku-cached transport data for this city pair
      if (fromCity && toCity) {
        const pairKey = `${fromCity}|${toCity}`.toLowerCase();
        const dynamicMins = dynamicTransportModes[pairKey]?.durationMinutes;
        if (dynamicMins && dynamicMins > 0) return dynamicMins;
      }
      return 120; // Ultimate fallback if no data source available
    };

    // Get accommodation name for this day — adapts to accommodation type
    const dayHotelName = accommodationType === 'custom' && customAccommodationName
      ? customAccommodationName
      : accommodationType === 'cruise'
      ? 'Cruise Ship'
      : accommodationType === 'campervan'
      ? 'Campsite'
      : (dayData?.hotel?.name || generatedHotelName || 'City Center Hotel');
    // Accommodation-aware labels
    const checkinLabel = accommodationType === 'cruise' ? 'Return to Ship'
      : accommodationType === 'campervan' ? 'Set Up Camp'
      : accommodationType === 'custom' ? `Check in to ${customAccommodationName || 'Accommodation'}`
      : 'Hotel Check-in';
    const checkoutLabel = accommodationType === 'cruise' ? 'Disembark for Day'
      : accommodationType === 'campervan' ? 'Pack Up & Depart'
      : 'Hotel Checkout';
    const returnLabel = accommodationType === 'cruise' ? 'Return to Ship'
      : accommodationType === 'campervan' ? 'Arrive at Campsite'
      : `End of Day at ${dayHotelName}`;

    // Get PREVIOUS day's hotel (for morning activities - user woke up there)
    const prevDayData = !isFirstDay && dayIndex > 0 ? itinerary?.days?.[dayIndex - 1] : null;
    const prevDayHotelName = prevDayData?.hotel?.name || dayHotelName; // Fallback to current if no previous

    // On departure day, if no hotel is set for this day, use previous day's hotel
    // (e.g., user sleeps in Ericeira Day 2 hotel, Day 3 baseCity is Porto but checkout is from Ericeira)
    const departureHotelName = isLastDay && !dayData?.hotel?.name && prevDayData?.hotel?.name
      ? prevDayData.hotel.name
      : dayHotelName;

    // Get city name for adding context to locations (improves Google Maps accuracy)
    const cityContext = dayData?.baseCity || selectedDestination?.name || itinerary?.destination?.split('&')[0]?.trim() || '';

    // Helper to add city context to a location string for better Google Maps results
    const addLocationContext = (location) => {
      if (!location || !cityContext) return location;
      // If location already has city/country info, don't add
      const locLower = location.toLowerCase();
      const cityLower = cityContext.toLowerCase();
      if (locLower.includes(cityLower) || locLower.includes(',')) {
        return location;
      }
      return `${location}, ${cityContext}`;
    };
    
    // Add arrival logistics for Day 1 (consolidated)
    if (isFirstDay) {
      // Check if the first stop is in the user's home city — skip all arrival logistics
      const homeCityPref = userProfile?.preferences?.homeCity || '';
      const baseCityName = dayData?.baseCity || '';
      const firstStopBuilderData = tripBuilderStops?.[0];
      const resolvedLocStr = (firstStopBuilderData?.resolvedLocation || '').toLowerCase().trim();
      const homeLower = homeCityPref.toLowerCase().trim();
      const baseLower = baseCityName.toLowerCase().trim();
      const resolvedFirstPart = (resolvedLocStr.split(',')[0]?.trim()) || '';
      const isHomeCityFirstStop = homeLower && baseLower && (
        baseLower.includes(homeLower) || homeLower.includes(baseLower) ||
        (resolvedLocStr.length > 0 && resolvedLocStr.includes(homeLower)) ||
        (resolvedFirstPart.length > 0 && homeLower.includes(resolvedFirstPart))
      );

      if (isHomeCityFirstStop) {
        // User is already in their home city — no flight/drive arrival logistics needed
        debugLog(`🏠 First stop "${baseCityName}" is home city "${homeCityPref}" — skipping arrival logistics`);
        let hotelDisplay = dayHotelName;
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }
        timeline.push({
          type: 'hotel-checkin',
          time: '14:00',
          activity: 'Hotel Check-in',
          description: `Check in to ${hotelDisplay}`,
          location: hotelDisplay,
          duration: '30 minutes',
          sortTime: 14 * 60
        });
        cutoffStartTime = 14 * 60 + 30;
      } else {
      // Use correct time based on transportation mode
      const startTime = transportationMode === 'fly' ? arrivalFlight.time : driveDetails.departureTime;
      const [hours, minutes] = startTime.split(':').map(Number);
      const arrivalMinutes = hours * 60 + minutes;
      
      // Check transportation mode - driving vs flying
      if (transportationMode === 'fly') {
        // FLYING: Check if this is a domestic or international flight
        const isDomestic = checkIfDomestic(selectedDestination);

        // Get arrival airport info for display (ensure code is uppercase)
        // Fallback chain: itinerary airport → user pref → destinationAirports state → first stop transport info
        const arrivalAirportCode = (
          itinerary?.arrivalAirport?.code ||
          userProfile?.preferences?.arrivalAirport ||
          (destinationAirports?.length > 0 && destinationAirports[0].code !== '???' ? destinationAirports[0].code : '') ||
          ''
        ).toUpperCase();
        const arrivalAirportName = itinerary?.arrivalAirport?.name || safeAirportCity(itinerary?.arrivalAirport) ||
          (destinationAirports?.length > 0 ? destinationAirports[0].name : '') || '';
        // Get home airport for pricing route (home airport → arrival airport)
        // Priority: trip-specific departure city → permanent profile home airport
        const homeAirportsForArr = (() => { const raw = profile?.home_airports; if (Array.isArray(raw)) return raw; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } } return []; })();
        const homeAirportCodeForArr = lookupCityAirportCode(userProfile?.preferences?.homeCity) || homeAirportsForArr?.[0]?.code || '';

        // Add arrival card (different for domestic vs international)
        // Cross-day dedup: when Day 0 (Travel Day) already shows the outbound flight,
        // don't render Day 1 arrival as a flight card — keep as logistics-only to avoid duplication
        const showAsFlightCard = !hasOvernightOutbound;

        if (!isDomestic) {
          // International: Customs + Immigration (1.5 hours)
          const customsEndMinutes = arrivalMinutes + 90;
          const customsEndHours = Math.floor(customsEndMinutes / 60);
          const customsEndMins = customsEndMinutes % 60;

          timeline.push({
            type: 'arrival-customs',
            time: startTime,
            endTime: `${String(customsEndHours).padStart(2, '0')}:${String(customsEndMins).padStart(2, '0')}`,
            activity: arrivalAirportName ? `Arrival at ${arrivalAirportName} & Immigration` : (arrivalAirportCode ? `Arrival at ${arrivalAirportCode} & Immigration` : 'Arrival & Immigration'),
            duration: '1.5 hours',
            description: homeAirportCodeForArr && arrivalAirportCode
              ? `Flight from ${homeAirportCodeForArr} to ${arrivalAirportCode}${arrivalAirportName ? ` (${arrivalAirportName})` : ''} - immigration and customs process`
              : (arrivalAirportCode
                ? `International arrival at ${arrivalAirportCode}${arrivalAirportName ? ` (${arrivalAirportName})` : ''} - immigration and customs process`
                : 'International arrival and immigration process'),
            location: homeAirportCodeForArr && arrivalAirportCode
              ? `${homeAirportCodeForArr} → ${arrivalAirportCode}`
              : (arrivalAirportName ? `${arrivalAirportName}` : (arrivalAirportCode ? `${arrivalAirportCode} Airport` : undefined)),
            sortTime: arrivalMinutes,
            airportCode: arrivalAirportCode || null,
            homeAirportCode: homeAirportCodeForArr || null,
            isArrivalFlightCard: showAsFlightCard
          });
        } else {
          // Domestic: Baggage claim + optional car rental (30-45 min)
          const domesticArrivalMinutes = arrivalMinutes + 30; // 30 min for baggage
          const domesticEndHours = Math.floor(domesticArrivalMinutes / 60);
          const domesticEndMins = domesticArrivalMinutes % 60;

          // Check if destination benefits from car rental (beach, national parks, road trips, etc)
          const destName = selectedDestination?.name?.toLowerCase() || '';
          const needsCar = destName.includes('key') || destName.includes('hawaii') ||
                          destName.includes('park') || destName.includes('beach') ||
                          destName.includes('coast') || destName.includes('route');

          timeline.push({
            type: 'arrival-domestic',
            time: startTime,
            endTime: `${String(domesticEndHours).padStart(2, '0')}:${String(domesticEndMins).padStart(2, '0')}`,
            activity: arrivalAirportName ? `Arrival at ${arrivalAirportName}` : (arrivalAirportCode ? `Arrival at ${arrivalAirportCode}` : 'Baggage Claim'),
            duration: '30 minutes',
            description: homeAirportCodeForArr && arrivalAirportCode
              ? `Flight from ${homeAirportCodeForArr} to ${arrivalAirportCode}. Collect checked bags.${needsCar ? ' Tip: Consider renting a car at the airport for easier exploration.' : ''}`
              : (needsCar
                ? `Collect checked bags${arrivalAirportCode ? ` at ${arrivalAirportCode}` : ''}. Tip: Consider renting a car at the airport for easier exploration.`
                : `Collect checked bags${arrivalAirportCode ? ` at ${arrivalAirportCode}` : ''}`),
            location: homeAirportCodeForArr && arrivalAirportCode
              ? `${homeAirportCodeForArr} → ${arrivalAirportCode}`
              : (arrivalAirportName ? `${arrivalAirportName}` : (arrivalAirportCode ? `${arrivalAirportCode} Airport` : undefined)),
            sortTime: arrivalMinutes,
            airportCode: arrivalAirportCode || null,
            homeAirportCode: homeAirportCodeForArr || null,
            isArrivalFlightCard: showAsFlightCard
          });
        }
        
        const customsTime = isDomestic ? 30 : 90; // 30 min domestic baggage, 90 min international customs

        // Get selected transport mode for hotel transit and use real cached time if available
        const hotelTransitKey = 'day0-hotel-transit';
        const selectedMode = transportModes[hotelTransitKey] || 'driving';
        const cachedHotelTransit = transitTimes[hotelTransitKey];

        timelineLog(`🛫 Airport to Hotel Transit: key=${hotelTransitKey}, hasCachedData=${!!cachedHotelTransit}, mode=${selectedMode}`);
        if (cachedHotelTransit) {
          timelineLog(`   Cached driving: ${cachedHotelTransit.driving?.duration || 'N/A'}`);
          timelineLog(`   Cached transit: ${cachedHotelTransit.transit?.duration || 'N/A'}`);
          timelineLog(`   Cached walking: ${cachedHotelTransit.walking?.duration || 'N/A'}`);
        }

        // Parse real transit time from cache, fallback to estimates
        let transitTime = selectedMode === 'transit' ? 60 : 30; // Default estimates
        if (cachedHotelTransit) {
          const modeData = cachedHotelTransit[selectedMode];
          if (modeData?.duration) {
            // Parse duration string like "45 mins" or "1 hr 20 mins"
            const durationStr = modeData.duration;
            const hoursMatch = durationStr.match(/(\d+)\s*hr/);
            const minsMatch = durationStr.match(/(\d+)\s*min/);
            const hours = hoursMatch ? parseInt(hoursMatch[1]) : 0;
            const mins = minsMatch ? parseInt(minsMatch[1]) : 0;
            transitTime = hours * 60 + mins;
            if (transitTime === 0) transitTime = selectedMode === 'transit' ? 60 : 30; // Fallback if parse fails
          }
        }
        
        // Transit to hotel (starts after arrival + customs time)
        const customsEndMinutes = arrivalMinutes + customsTime;
        const hotelTransitStartMinutes = customsEndMinutes;
        const hotelArrivalMinutes = hotelTransitStartMinutes + transitTime;
        
        // Calculate day offset if time goes past midnight
        let transitDayOffset = 0;
        let arrivalDayOffset = 0;
        
        if (hotelTransitStartMinutes >= 1440) { // 24 hours = 1440 minutes
          transitDayOffset = Math.floor(hotelTransitStartMinutes / 1440);
        }
        if (hotelArrivalMinutes >= 1440) {
          arrivalDayOffset = Math.floor(hotelArrivalMinutes / 1440);
        }
        
        const hotelTransitHours = Math.floor((hotelTransitStartMinutes % 1440) / 60);
        const hotelTransitMins = (hotelTransitStartMinutes % 1440) % 60;
        const hotelArrivalHours = Math.floor((hotelArrivalMinutes % 1440) / 60);
        const hotelArrivalMins = (hotelArrivalMinutes % 1440) % 60;
        
        // Use hotel name from this day's data
        let hotelDisplay = dayHotelName;
        
        // If selectedHotel is a custom name, use it (but this should rarely happen)
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }
        
        // Check if arrival airport is in a different city from the hotel/destination
        // Uses shared DEPARTURE_AIRPORT_CITY_MAP (superset of arrival airports too)
        const airportCode = itinerary?.arrivalAirport?.code || '';
        const resolvedAirportCity = DEPARTURE_AIRPORT_CITY_MAP[airportCode] || safeAirportCity(itinerary?.arrivalAirport) || '';
        const arrivalAirportCity = resolvedAirportCity.toLowerCase();
        const hotelCity = (dayData?.baseCity || selectedDestination?.name || '').toLowerCase();
        const isLongDistanceTransfer = arrivalAirportCity && hotelCity &&
          !arrivalAirportCity.includes(hotelCity) && !hotelCity.includes(arrivalAirportCity.split(',')[0]);

        if (isLongDistanceTransfer) {
          // Long distance transfer from airport to destination (e.g., MIA to Key West, Milan to Lake Como)
          const airportCode = itinerary?.arrivalAirport?.code || 'Airport';
          const destinationName = dayData?.baseCity || selectedDestination?.name || 'destination';
          const destLower = destinationName.toLowerCase();
          const airportLower = arrivalAirportCity.toLowerCase();

          // Determine best transport mode and estimate time for common routes
          let estimatedHours = 2; // Default estimate
          let transportMode = 'car';
          let transportEmoji = '🚗';
          let transportDescription = `Transfer from ${airportCode} airport`;

          // Train-friendly airport to destination routes (European)
          const trainRoutes = {
            'milan-lake como': { hours: 1, mode: 'train' },
            'milan-como': { hours: 1, mode: 'train' },
            'milan-bellagio': { hours: 1.5, mode: 'train' },
            'rome-florence': { hours: 1.5, mode: 'train' },
            'rome-naples': { hours: 1.25, mode: 'train' },
            'paris-versailles': { hours: 0.5, mode: 'train' },
            'paris-champagne': { hours: 1, mode: 'train' },
            'geneva-chamonix': { hours: 1.5, mode: 'train' },
            'zurich-lucerne': { hours: 0.75, mode: 'train' },
            'zurich-interlaken': { hours: 2, mode: 'train' },
            'barcelona-girona': { hours: 0.75, mode: 'train' },
            'london-oxford': { hours: 1, mode: 'train' },
            'london-brighton': { hours: 1, mode: 'train' },
          };

          // Car-preferred routes
          const carRoutes = {
            'miami-key west': { hours: 3.5 },
            'miami-keys': { hours: 3.5 },
            'miami-naples': { hours: 2.5 },
            'miami-fort myers': { hours: 2.5 },
            'los angeles-palm springs': { hours: 2 },
            'los angeles-san diego': { hours: 2 },
            'san francisco-napa': { hours: 1.5 },
            'san francisco-carmel': { hours: 2.5 },
            'denver-aspen': { hours: 3.5 },
            'denver-vail': { hours: 2 },
          };

          // Check for train routes
          const trainRouteKey = Object.keys(trainRoutes).find(key => {
            const [from, to] = key.split('-');
            return airportLower.includes(from) && destLower.includes(to);
          });

          if (trainRouteKey) {
            const route = trainRoutes[trainRouteKey];
            estimatedHours = route.hours;
            transportMode = 'train';
            transportEmoji = '🚆';
            transportDescription = `Train from ${airportCode} to ${destinationName} - fast and scenic!`;
          } else {
            // Check for car routes
            const carRouteKey = Object.keys(carRoutes).find(key => {
              const [from, to] = key.split('-');
              return airportLower.includes(from) && destLower.includes(to);
            });

            if (carRouteKey) {
              estimatedHours = carRoutes[carRouteKey].hours;
            }
            transportDescription = `Scenic drive from ${airportCode} airport. Consider renting a car for this trip!`;
          }

          const transferMinutes = estimatedHours * 60;
          const transferEndMinutes = hotelTransitStartMinutes + transferMinutes;
          const transferEndHours = Math.floor((transferEndMinutes % 1440) / 60);
          const transferEndMins = (transferEndMinutes % 1440) % 60;

          timeline.push({
            type: 'long-distance-transfer',
            time: `${String(hotelTransitHours).padStart(2, '0')}:${String(hotelTransitMins).padStart(2, '0')}`,
            arrivalTime: `${String(transferEndHours).padStart(2, '0')}:${String(transferEndMins).padStart(2, '0')}`,
            activity: `${transportEmoji} ${transportMode === 'train' ? 'Train' : 'Drive'} to ${destinationName}`,
            description: transportDescription,
            location: `${airportCode} → ${destinationName}`,
            from: `${airportCode} Airport, ${arrivalAirportCity}`,
            to: hotelDisplay || destinationName,
            duration: `~${estimatedHours} ${estimatedHours === 1 ? 'hour' : 'hours'}`,
            sortTime: hotelTransitStartMinutes,
            dayOffset: transitDayOffset,
            isLongDistance: true,
            travelMode: transportMode
          });

          // Update check-in time for long distance transfer
          const newCheckinMinutes = transferEndMinutes;
          const newCheckinHours = Math.floor((newCheckinMinutes % 1440) / 60);
          const newCheckinMins = (newCheckinMinutes % 1440) % 60;
          const newCheckinDayOffset = Math.floor(newCheckinMinutes / 1440);

          timeline.push({
            type: 'hotel-checkin',
            time: `${String(newCheckinHours).padStart(2, '0')}:${String(newCheckinMins).padStart(2, '0')}`,
            activity: 'Hotel Check-in',
            description: 'Check in, get settled, freshen up',
            location: hotelDisplay,
            duration: '30 minutes',
            sortTime: newCheckinMinutes,
            dayOffset: newCheckinDayOffset
          });

          cutoffStartTime = newCheckinMinutes + 30;
        } else {
          // Normal short transit to hotel - use same style as Transit to First Stop
          // Prefer airport code format (e.g., "DPS Airport") over full name which may be unreliable
          const airportCode = itinerary?.arrivalAirport?.code;
          const airportCity = itinerary?.arrivalAirport?.city || dayData.baseCity || '';
          const airportFromName = airportCode
            ? `${airportCode} Airport${airportCity ? `, ${airportCity.split(',')[0]}` : ''}`
            : (itinerary?.arrivalAirport?.name || 'Airport');
          const transitKey = `day${dayIndex}-hotel-transit`;

          // Get real duration from cache if available
          let hotelTransitDuration = selectedMode === 'transit' ? '~60 mins' : '~30 mins';
          const cachedTransit = transitTimes[transitKey];
          if (cachedTransit && cachedTransit[selectedMode]?.duration) {
            hotelTransitDuration = cachedTransit[selectedMode].duration;
          }

          timeline.push({
            type: 'transit',
            time: `${String(hotelTransitHours).padStart(2, '0')}:${String(hotelTransitMins).padStart(2, '0')}`,
            arrivalTime: `${String(hotelArrivalHours).padStart(2, '0')}:${String(hotelArrivalMins).padStart(2, '0')}`,
            activity: 'Transit to Hotel',
            from: addLocationContext(airportFromName),
            to: addLocationContext(hotelDisplay),
            duration: hotelTransitDuration,
            selectedMode: selectedMode,
            transitKey: transitKey,
            sortTime: hotelTransitStartMinutes,
            dayOffset: transitDayOffset,
            arrivalDayOffset: arrivalDayOffset
          });

          // Add hotel check-in and settle block for normal transit
          const checkinMinutes = hotelArrivalMinutes;
          const checkinHours = Math.floor((checkinMinutes % 1440) / 60);
          const checkinMins = (checkinMinutes % 1440) % 60;
          const checkinDayOffset = Math.floor(checkinMinutes / 1440);

          timeline.push({
            type: 'hotel-checkin',
            time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
            activity: 'Hotel Check-in',
            description: 'Check in, get settled, freshen up',
            location: hotelDisplay,
            duration: '30 minutes',
            sortTime: checkinMinutes,
            dayOffset: checkinDayOffset
          });

          cutoffStartTime = hotelArrivalMinutes + 30; // Activities start 30 min after check-in
        }

      } else if (transportationMode === 'train' || transportationMode === 'ferry') {
        // TRAIN/FERRY: Transit arrival
        const transitMinutes = quickTransportResult?.durationMinutes || 180;
        const departureMinutes = arrivalMinutes; // departure time from station/port
        const arrivalAtDestMinutes = departureMinutes + transitMinutes;
        const departHours = Math.floor(departureMinutes / 60);
        const departMins = departureMinutes % 60;
        const arriveHours = Math.floor((arrivalAtDestMinutes % 1440) / 60);
        const arriveMins = (arrivalAtDestMinutes % 1440) % 60;
        const homeCity = userProfile.preferences.homeCity || 'Home';
        const emoji = transportationMode === 'train' ? '🚆' : '⛴️';
        const modeLabel = transportationMode === 'train' ? 'Train' : 'Ferry';
        const transitDuration = quickTransportResult?.duration || `${Math.round(transitMinutes / 60)}h`;

        timeline.push({
          type: 'transit-arrival',
          time: `${String(departHours).padStart(2, '0')}:${String(departMins).padStart(2, '0')}`,
          arrivalTime: `${String(arriveHours).padStart(2, '0')}:${String(arriveMins).padStart(2, '0')}`,
          activity: `${emoji} ${modeLabel} to ${selectedDestination?.name || 'Destination'}`,
          duration: transitDuration,
          description: `${modeLabel} from ${homeCity} to ${selectedDestination?.name || 'Destination'} (${transitDuration})`,
          location: `${homeCity} → ${selectedDestination?.name || 'Destination'}`,
          sortTime: departureMinutes
        });

        // Hotel check-in after arrival
        let hotelDisplay = dayHotelName;
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }
        const checkinMinutes = arrivalAtDestMinutes + 15;
        const checkinHours = Math.floor((checkinMinutes % 1440) / 60);
        const checkinMins = (checkinMinutes % 1440) % 60;
        const checkinDayOffset = Math.floor(checkinMinutes / 1440);

        timeline.push({
          type: 'hotel-checkin',
          time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
          activity: 'Hotel Check-in',
          description: `Check in, get settled, freshen up after the ${modeLabel.toLowerCase()}`,
          location: hotelDisplay,
          duration: '30 minutes',
          sortTime: checkinMinutes,
          dayOffset: checkinDayOffset
        });

        cutoffStartTime = checkinMinutes + 30;

      } else if (transportationMode === 'custom') {
        // CUSTOM: User-defined transport
        const customArrivalMinutes = arrivalMinutes; // user sets arrival time directly
        const homeCity = userProfile.preferences.homeCity || 'Home';

        timeline.push({
          type: 'transit-arrival',
          time: `${String(Math.floor(customArrivalMinutes / 60)).padStart(2, '0')}:${String(customArrivalMinutes % 60).padStart(2, '0')}`,
          activity: `🚌 Arrive at ${selectedDestination?.name || 'Destination'}`,
          duration: '',
          description: customTransportDesc || `Travel from ${homeCity} to ${selectedDestination?.name || 'Destination'}`,
          location: selectedDestination?.name || 'Destination',
          sortTime: customArrivalMinutes
        });

        // Hotel check-in after arrival
        let hotelDisplay = dayHotelName;
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }
        const checkinMinutes = customArrivalMinutes + 30;
        const checkinHours = Math.floor((checkinMinutes % 1440) / 60);
        const checkinMins = (checkinMinutes % 1440) % 60;

        timeline.push({
          type: 'hotel-checkin',
          time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
          activity: 'Hotel Check-in',
          description: 'Check in, get settled, freshen up after your journey',
          location: hotelDisplay,
          duration: '30 minutes',
          sortTime: checkinMinutes
        });

        cutoffStartTime = checkinMinutes + 30;

      } else {
        // DRIVING: Road trip arrival
        // Priority: 1) Google Maps calculated time, 2) destination driveHours, 3) estimate from flight hours
        const driveHours = calculatedDriveTime?.hours || selectedDestination?.driveHours || estimateDriveHours(selectedDestination?.flightHours || 3);
        const departureMinutes = arrivalMinutes; // "arrivalTime" is actually departure time for driving
        const arrivalAtDestMinutes = departureMinutes + (driveHours * 60);
        
        // Road trip from home
        const departHours = Math.floor(departureMinutes / 60);
        const departMins = departureMinutes % 60;
        const arriveHours = Math.floor((arrivalAtDestMinutes % 1440) / 60);
        const arriveMins = (arrivalAtDestMinutes % 1440) % 60;
        
        const homeCity = userProfile.preferences.homeCity || 'Home';
        const isRental = transportationMode === 'drive-rental';

        // Detect intercontinental drive — can't drive NYC to Matosinhos!
        const homeContinentCheck = getContinentHintFromText(homeCity);
        const destContinentCheck = getContinentHintFromText(selectedDestination?.name || '');
        const isIntercontinentalDrive = homeContinentCheck && destContinentCheck && homeContinentCheck !== destContinentCheck;

        // Add rental car pickup if applicable
        if (isRental) {
          timeline.push({
            type: 'rental-pickup',
            time: `${String(departHours).padStart(2, '0')}:${String(departMins).padStart(2, '0')}`,
            activity: '🚗 Rental Car Pickup',
            duration: '30 minutes',
            description: `Pick up rental car near ${homeCity}`,
            location: `Rental Location near ${homeCity}`,
            sortTime: departureMinutes
          });
        }
        
        // Road trip activity
        const roadTripStartMinutes = isRental ? departureMinutes + 30 : departureMinutes;
        const roadTripStartHours = Math.floor(roadTripStartMinutes / 60);
        const roadTripStartMins = roadTripStartMinutes % 60;

        // For multi-stop trips, use the first stop name instead of the overall trip name
        const roadTripDestName = selectedDestination?.isMultiStop && selectedDestination?.stopNames?.length > 0
          ? selectedDestination.stopNames[0].split(',')[0].trim()
          : (selectedDestination?.name || 'Destination');

        timeline.push({
          type: 'road-trip',
          time: `${String(roadTripStartHours).padStart(2, '0')}:${String(roadTripStartMins).padStart(2, '0')}`,
          arrivalTime: `${String(arriveHours).padStart(2, '0')}:${String(arriveMins).padStart(2, '0')}`,
          activity: `🚗 Road Trip to ${roadTripDestName}`,
          duration: isIntercontinentalDrive ? '' : `${driveHours} hours`,
          description: isIntercontinentalDrive
            ? `⚠️ Driving from ${homeCity} to ${roadTripDestName} isn't possible — they're on different continents. Update your departure city to a nearby location, or switch to flying.`
            : `Scenic drive from ${homeCity}${isRental ? ' (rental car)' : ''}. Enjoy the journey!`,
          location: `${homeCity} → ${roadTripDestName}`,
          from: homeCity,
          to: roadTripDestName,
          sortTime: roadTripStartMinutes
        });
        
        // Use hotel name from this day's data
        let hotelDisplay = dayHotelName;
        if (selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel)) {
          hotelDisplay = selectedHotel;
        }
        
        // Hotel check-in after arrival
        const checkinMinutes = arrivalAtDestMinutes + 15; // 15 min buffer after arriving
        const checkinHours = Math.floor((checkinMinutes % 1440) / 60);
        const checkinMins = (checkinMinutes % 1440) % 60;
        const checkinDayOffset = Math.floor(checkinMinutes / 1440);
        
        timeline.push({
          type: 'hotel-checkin',
          time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
          activity: 'Hotel Check-in',
          description: 'Check in, get settled, freshen up after the drive',
          location: hotelDisplay,
          duration: '30 minutes',
          sortTime: checkinMinutes,
          dayOffset: checkinDayOffset
        });
        
        cutoffStartTime = checkinMinutes + 30; // Activities start 30 min after check-in
      }
      } // end of !isHomeCityFirstStop else
    }

    // Calculate cutoff for last day - activities should END before checkout
    // This prevents meals/activities from being scheduled after airport transit
    if (isLastDay) {
      // Use effectiveReturnMode (not transportationMode) — departure is based on how user RETURNS home
      const departTime = effectiveReturnMode === 'fly'
        ? (departureFlight.time || '15:00')
        : (driveDetails.returnDepartureTime || '10:00');
      const [hours, minutes] = departTime.split(':').map(Number);
      const departMinutes = hours * 60 + minutes;

      // Use dynamically calculated departure distance if available
      const hasCalculatedDepartureDistance = airportDistances?.departure?.durationMinutes > 0;
      let transitDuration = hasCalculatedDepartureDistance
        ? airportDistances.departure.durationMinutes
        : 45; // Default 45 minutes if not calculated

      // Calculate checkout time (work backwards from flight)
      const isDomesticCutoff = checkIfDomestic(selectedDestination);
      const airportBufferCutoff = isDomesticCutoff ? 120 : 180; // 2hr domestic / 3hr international
      const totalLeadTime = airportBufferCutoff + transitDuration + 30; // airport buffer + transit + checkout
      const checkoutMinutes = departMinutes - totalLeadTime;

      // Cutoff should be checkout time - activities must end before checkout
      // Clamp to 0 (midnight) minimum — very early flights (e.g., 4 AM + long transit)
      // can produce negative values that would incorrectly filter ALL activities
      cutoffEndTime = Math.max(0, checkoutMinutes);
      if (checkoutMinutes < 0) {
        debugWarn(`⚠️ Very early flight produces negative checkout time (${checkoutMinutes} mins) — clamped to midnight`);
      }
      debugLog(`🛫 Last day cutoff: checkout at ${Math.floor(cutoffEndTime/60)}:${String(cutoffEndTime%60).padStart(2,'0')}, cutoffEndTime=${cutoffEndTime}`);
    }
    
    // Check if this is a hotel change day (not first day, hotel is different from previous day)
    // This indicates inter-city travel (e.g., Lima Day 1 → Cusco Day 2)
    // NOTE: dayIndex is calculated at the top of createDayTimeline function

    // Safety check: if day not found in array, skip hotel/city comparisons
    if (dayIndex < 0) {
      debugLog('⚠️ Day not found in itinerary.days array, skipping hotel/city comparison');
    }

    // Debug: Log hotel comparison (only if day found and not first day)
    const prevHotelName = dayIndex > 0 ? itinerary?.days?.[dayIndex - 1]?.hotel?.name : null;
    const currentHotelName = dayData?.hotel?.name;
    if (dayIndex >= 0) {
      debugLog(`🏨 Day ${dayIndex + 1} hotel check: current="${currentHotelName}" vs prev="${prevHotelName}"`);
    }

    // Use fuzzy matching to detect if hotels are actually different
    // This uses the global areSameHotel function which handles:
    // - "Anantara Bazaruto Island Resort" vs "Anantara Bazaruto Island Resort & Spa" (same)
    // - "Four Seasons Hotel" vs "Four Seasons Resort" (same)
    const isHotelChangeDay = dayIndex > 0 &&
      !areSameHotel(prevHotelName, currentHotelName) &&
      prevHotelName && currentHotelName;

    // Also check if baseCity changed (more reliable than hotel name)
    const prevBaseCity = dayIndex > 0 ? itinerary?.days?.[dayIndex - 1]?.baseCity : null;
    const currentBaseCity = dayData?.baseCity;

    // normalizeCityName now imported from shared displayGuardrails
    const baseCityChanged = dayIndex > 0 &&
      dayData?.baseCity && itinerary?.days?.[dayIndex - 1]?.baseCity &&
      normalizeCityName(dayData.baseCity) !== normalizeCityName(itinerary?.days?.[dayIndex - 1]?.baseCity);

    debugLog(`🌆 Day ${dayIndex + 1} city check: current="${currentBaseCity}" vs prev="${prevBaseCity}", changed=${baseCityChanged}`);

    // Flag if this is an inter-city travel day
    // IMPORTANT: Only show inter-city travel when baseCity actually changes
    // Hotel changes within the same city should NOT trigger travel cards
    // NOTE: We now include lastDay IF the baseCity changed - user may travel to a new city
    // and then fly home from there (e.g., Sevilla Day 6 → Barcelona Day 7 → Fly home from BCN)
    //
    // SAFETY NET: If hotels are the SAME (fuzzy match), NEVER show inter-city travel
    // This prevents absurd "checkout→drive→checkin to same hotel" scenarios
    // (e.g., "Tuscany" Day 4 → "Fiesole" Day 5 with same Villa San Michele)
    //
    // FALLBACK: If hotels changed but baseCity didn't, check if we have cached distance data
    // If hotels are > 50km apart, treat as inter-city travel (catches large metro areas)
    const sameHotelAsPrev = dayIndex > 0 && prevHotelName && currentHotelName &&
      areSameHotel(prevHotelName, currentHotelName);

    // Check if server explicitly marked inter-city transport for this day
    const hasServerTransport = itinerary?.interCityTransport?.some(t => t.day === dayIndex + 1);

    let isInterCityTravelDay = baseCityChanged && !isFirstDay && (!sameHotelAsPrev || hasServerTransport);

    if (sameHotelAsPrev && baseCityChanged && !hasServerTransport) {
      debugLog(`🏨 Day ${dayIndex + 1}: Same hotel "${currentHotelName}" as prev day — suppressing inter-city travel despite baseCity change (${prevBaseCity} → ${currentBaseCity})`);
    } else if (sameHotelAsPrev && baseCityChanged && hasServerTransport) {
      debugLog(`🏨 Day ${dayIndex + 1}: Same hotel but server has interCityTransport — showing inter-city travel (${prevBaseCity} → ${currentBaseCity})`);
    }

    // Distance-based fallback for hotel changes within "same city"
    if (!isInterCityTravelDay && isHotelChangeDay && !isFirstDay) {
      const prevHotelName = itinerary?.days?.[dayIndex - 1]?.hotel?.name;
      const currentHotelName = dayData?.hotel?.name;

      // Check if we have pre-fetched distance data between hotels
      // This is populated by the transit time fetching logic
      const hotelDistanceKey = `hotel-${dayIndex - 1}-to-${dayIndex}`;
      const cachedDistance = transitTimes?.[hotelDistanceKey];

      // If distance > 50km (50000m), treat as inter-city even if baseCity is "same"
      // This catches cases like "Miami Beach" hotel to "Key Largo" hotel both tagged as "Florida Keys"
      if (cachedDistance?.distanceMeters > 50000) {
        debugLog(`📏 Hotels are ${Math.round(cachedDistance.distanceMeters / 1000)}km apart - treating as inter-city travel`);
        isInterCityTravelDay = true;
      } else if (prevHotelName && currentHotelName && prevHotelName !== currentHotelName) {
        // No cached distance, but hotels changed - log for debugging
        debugLog(`🏨 Hotel changed (${prevHotelName} → ${currentHotelName}) but baseCity same - no distance data available`);
      }
    }

    debugLog(`📍 Day ${dayIndex + 1}: isHotelChangeDay=${isHotelChangeDay}, baseCityChanged=${baseCityChanged}, isFirstDay=${isFirstDay}, isLastDay=${isLastDay}, isInterCityTravelDay=${isInterCityTravelDay}`);

    // SYSTEMIC FIX: Detect if this is an ARRIVAL day after an overnight flight from the previous day
    // This happens when the previous day had a flight that arrives the next day (arrivalDayOffset > 0)
    // In this case, we need to add arrival/recovery items to TODAY's timeline, not yesterday's
    let isArrivalDayAfterOvernightTravel = false;
    let overnightTravelArrivalData = null;

    if (!isFirstDay && dayIndex > 0) {
      // Check if this day's inter-city transport is an overnight flight arriving today
      // IMPORTANT: interCityTransport.day uses the ARRIVAL day convention (1-indexed)
      // So for Day 5 (dayIndex=4), we look for transport with day === 5 (dayIndex + 1)
      // that has overnight indicators (isOvernight, late departure, or long-haul evening flight)
      const thisDayTransport = itinerary?.interCityTransport?.find(t => t.day === dayIndex + 1);
      const thisDayFlight = itinerary?.flights?.find(f => f.day === dayIndex + 1);
      const thisDayLeg = thisDayTransport || thisDayFlight;

      // Also check the previous convention (t.day === dayIndex) as a fallback
      // Some legacy data may use departure-day tagging
      const prevDayTransport = itinerary?.interCityTransport?.find(t => t.day === dayIndex);
      const prevDayFlight = itinerary?.flights?.find(f => f.day === dayIndex);
      const prevDayLeg = prevDayTransport || prevDayFlight;

      // isExplicitOvernightArrival now imported from shared displayGuardrails
      // Only trusts EXPLICIT overnight markers (isOvernight flag or arrivalDayOffset).

      // For prevDayLeg: use shared overnight detection with full heuristics
      // Fix 7: Replaced local checkOvernightDeparture with shared checkOvernightTransport
      // — now both web and mobile use identical heuristics (including hours >= 10 catch-all)

      // Check prevDayLeg FIRST (more reliable — it's the actual departure from previous day)
      // Only fall back to thisDayLeg if it has explicit overnight markers
      const wasOvernightFromPrevDay = checkOvernightTransport(prevDayLeg, { dynamicGeographyCache, calculateFlightArrival });
      const wasOvernightFromThisDay = isExplicitOvernightArrival(thisDayLeg);
      let overnightLeg = wasOvernightFromPrevDay ? prevDayLeg : (wasOvernightFromThisDay ? thisDayLeg : null);

      // Fallback: if no overnight leg found but cities changed AND it's a long-haul route
      // (different continents), synthesize an overnight arrival entry from the day data.
      // This handles cases where interCityTransport data lacks overnight flags.
      if (!overnightLeg && isInterCityTravelDay) {
        const prevBaseCity = itinerary?.days?.[dayIndex - 1]?.baseCity || '';
        const currBaseCity = dayData?.baseCity || '';
        if (prevBaseCity && currBaseCity && prevBaseCity.toLowerCase() !== currBaseCity.toLowerCase()) {
          const prevCountry = CITY_TO_COUNTRY[prevBaseCity.toLowerCase()] || '';
          const currCountry = CITY_TO_COUNTRY[currBaseCity.toLowerCase()] || '';
          // Check if cross-continental (implies long-haul, likely overnight)
          const prevContinent = prevCountry ? getContinentForCountry(prevCountry) : '';
          const currContinent = currCountry ? getContinentForCountry(currCountry) : '';
          if (prevContinent && currContinent && prevContinent !== currContinent) {
            debugLog(`🌙 Fallback overnight detection: ${prevBaseCity} (${prevContinent}) → ${currBaseCity} (${currContinent}) — cross-continental, treating as overnight arrival`);
            overnightLeg = {
              from: { city: prevBaseCity },
              to: { city: currBaseCity },
              mode: 'flight',
              travelMode: 'flight',
              isOvernight: true,
              arrivalTime: '07:00',
              duration: '',
            };
          }
        }
      }

      if (overnightLeg) {
        isArrivalDayAfterOvernightTravel = true;
        // Determine transport mode from the leg data
        const overnightMode = overnightLeg?.mode || overnightLeg?.travelMode ||
          (overnightLeg?.carrier?.toLowerCase?.()?.includes('train') ? 'train' :
           overnightLeg?.carrier?.toLowerCase?.()?.includes('bus') ? 'bus' : 'flight');
        overnightTravelArrivalData = {
          fromCity: overnightLeg?.from?.city || itinerary?.days?.[dayIndex - 1]?.baseCity || 'Previous City',
          toCity: overnightLeg?.to?.city || dayData?.baseCity || 'Current City',
          arrivalTime: overnightLeg?.arrivalTime || '07:00', // Default early morning for overnight travel
          duration: overnightLeg?.duration || '',
          carrier: overnightLeg?.carrier || '',
          airline: overnightLeg?.airline || '',
          travelMode: overnightMode,
          toAirport: overnightLeg?.to?.airport || (overnightMode === 'flight' ? `${dayData?.baseCity || ''} Airport` : ''),
          toStation: overnightLeg?.to?.station || (overnightMode !== 'flight' ? `${dayData?.baseCity || ''} Station` : '')
        };
        debugLog(`🌅 Day ${dayIndex + 1}: Arrival day after overnight ${overnightMode} from ${overnightTravelArrivalData.fromCity} (transport day=${overnightLeg.day}, source=${wasOvernightFromThisDay ? 'thisDay' : 'prevDay'})`);
      }
    }

    // If this is an arrival day after overnight travel (flight, train, or bus), add arrival timeline items
    if (isArrivalDayAfterOvernightTravel && overnightTravelArrivalData) {
      const arrivalTime = overnightTravelArrivalData.arrivalTime || '07:00';
      const [arrHours, arrMins] = arrivalTime.split(':').map(Number);
      const arrivalMinutes = (arrHours || 7) * 60 + (arrMins || 0);
      const currentCity = overnightTravelArrivalData.toCity;
      const travelMode = overnightTravelArrivalData.travelMode || 'flight';
      const isFlight = travelMode === 'flight';
      const toAirport = overnightTravelArrivalData.toAirport;
      const toStation = overnightTravelArrivalData.toStation;
      const arrivalLocation = isFlight ? toAirport : toStation;
      const travelDuration = overnightTravelArrivalData.duration;
      const carrier = overnightTravelArrivalData.carrier || overnightTravelArrivalData.airline;

      // Mode-specific labels
      const arrivalEmoji = isFlight ? '🛬' : travelMode === 'train' ? '🚆' : '🚌';
      const modeLabel = isFlight ? 'Flight' : travelMode === 'train' ? 'Train' : 'Bus';
      const modeVerb = isFlight ? 'lands' : 'arrives';

      // Add arrival card
      timeline.push({
        type: 'overnight-arrival',
        time: arrivalTime,
        activity: `${arrivalEmoji} Arrival at ${currentCity}`,
        description: `${carrier ? `${carrier} ${modeLabel.toLowerCase()}` : modeLabel} ${modeVerb} after overnight journey${travelDuration ? ` (${travelDuration})` : ''}. Welcome to ${currentCity}!`,
        location: arrivalLocation,
        duration: isFlight ? 'Landing' : 'Arrival',
        sortTime: arrivalMinutes,
        isOvernightArrival: true,
        travelMode: travelMode
      });

      // Add customs/immigration for international arrivals
      // Flights always get customs; ferries/trains get customs if crossing non-Schengen borders
      let postArrivalOffset = 15;
      const arrivalPrevCountry = CITY_TO_COUNTRY[overnightTravelArrivalData.fromCity?.toLowerCase()] || '';
      const arrivalCurrCountry = CITY_TO_COUNTRY[overnightTravelArrivalData.toCity?.toLowerCase()] || '';
      const isInternationalOvernightArrival = arrivalPrevCountry && arrivalCurrCountry
        ? arrivalPrevCountry !== arrivalCurrCountry
        : overnightTravelArrivalData.fromCity?.toLowerCase() !== overnightTravelArrivalData.toCity?.toLowerCase();
      const isSchengenInternal = arrivalPrevCountry && arrivalCurrCountry &&
        SCHENGEN_COUNTRIES?.includes(arrivalPrevCountry) && SCHENGEN_COUNTRIES?.includes(arrivalCurrCountry);
      const needsCustoms = isInternationalOvernightArrival && !isSchengenInternal;

      if (isFlight && needsCustoms) {
        const customsMinutes = arrivalMinutes + 15;
        timeline.push({
          type: 'arrival-customs',
          time: `${String(Math.floor(customsMinutes / 60)).padStart(2, '0')}:${String(customsMinutes % 60).padStart(2, '0')}`,
          activity: '🛂 Customs & Immigration',
          description: `International arrival processing at ${toAirport}. Have passport and any required documents ready.`,
          location: toAirport,
          duration: '45 minutes',
          sortTime: customsMinutes
        });
        postArrivalOffset = 60;
      } else if (!isFlight && needsCustoms) {
        // International ferry/train crossing (e.g., Turkey→Greece, UK→France by ferry)
        const customsMinutes = arrivalMinutes + 10;
        const portOrStation = travelMode === 'ferry' ? `${overnightTravelArrivalData.toCity} Port` : toStation;
        timeline.push({
          type: 'arrival-customs',
          time: `${String(Math.floor(customsMinutes / 60)).padStart(2, '0')}:${String(customsMinutes % 60).padStart(2, '0')}`,
          activity: '🛂 Border Control',
          description: `Passport control at ${portOrStation}. Have documents ready.`,
          location: portOrStation,
          duration: '30 minutes',
          sortTime: customsMinutes
        });
        postArrivalOffset = 45;
      }

      // Add transit to hotel
      const transitMinutes = arrivalMinutes + postArrivalOffset;
      const dayHotelNameForTransit = dayData?.hotel?.name || 'Hotel';
      const transitFrom = isFlight ? toAirport : toStation;
      timeline.push({
        type: isFlight ? 'airport-transit' : 'station-transit',
        time: `${String(Math.floor(transitMinutes / 60)).padStart(2, '0')}:${String(transitMinutes % 60).padStart(2, '0')}`,
        activity: '🚕 Transit to Hotel',
        description: `Transfer from ${transitFrom} to your hotel`,
        location: `${transitFrom} → ${dayHotelNameForTransit}`,
        from: transitFrom,
        to: dayHotelNameForTransit,
        duration: isFlight ? '~45 min' : '~30 min',
        sortTime: transitMinutes,
        transitKey: `day${dayIndex}-from-overnight-arrival`
      });

      // Add check-in
      const transitDuration = isFlight ? 45 : 30;
      const checkinMinutes = transitMinutes + transitDuration;
      timeline.push({
        type: 'hotel-checkin',
        time: `${String(Math.floor(checkinMinutes / 60)).padStart(2, '0')}:${String(checkinMinutes % 60).padStart(2, '0')}`,
        activity: 'Hotel Check-in',
        description: `Check in to ${dayHotelNameForTransit}, get settled after the overnight ${modeLabel.toLowerCase()}`,
        location: dayHotelNameForTransit,
        duration: '30 minutes',
        sortTime: checkinMinutes
      });

      // Add recovery note — timezone-aware for flights
      const recoveryMinutes = checkinMinutes + 30;
      if (isFlight) {
        // Calculate timezone shift for jet lag severity
        const fromOffset = getCityTimezoneOffset(overnightTravelArrivalData.fromCity) || 0;
        const toOffset = getCityTimezoneOffset(currentCity) || 0;
        const tzShiftHours = Math.abs(toOffset - fromOffset);

        let recoveryDuration, recoveryDesc, recoveryCutoff;
        if (tzShiftHours >= 8) {
          // Major jet lag (e.g., NYC→Tokyo, London→Sydney)
          recoveryDuration = '3-4 hours';
          recoveryDesc = `After the overnight flight with a ${tzShiftHours}-hour timezone shift, your body clock will need significant adjustment. Rest, hydrate, and get sunlight to reset your circadian rhythm. Consider a light walk rather than intense sightseeing today.`;
          recoveryCutoff = 180; // 3 hours recovery
        } else if (tzShiftHours >= 4) {
          // Moderate jet lag (e.g., NYC→London, LA→NYC)
          recoveryDuration = '2-3 hours';
          recoveryDesc = `After the overnight flight (${tzShiftHours}-hour timezone change), take time to rest and adjust. Stay hydrated and get some sunlight to help your body clock adapt.`;
          recoveryCutoff = 120; // 2 hours recovery
        } else {
          // Minor or no jet lag
          recoveryDuration = '1-2 hours';
          recoveryDesc = `After the overnight flight, take some time to freshen up and settle in before heading out to explore ${currentCity}.`;
          recoveryCutoff = 90;
        }

        timeline.push({
          type: 'recovery-note',
          time: `${String(Math.floor(recoveryMinutes / 60)).padStart(2, '0')}:${String(recoveryMinutes % 60).padStart(2, '0')}`,
          activity: '💤 Rest & Recovery Time',
          description: recoveryDesc,
          location: dayHotelNameForTransit,
          duration: recoveryDuration,
          sortTime: recoveryMinutes,
          isRecoveryNote: true,
          timezoneShift: tzShiftHours
        });
        cutoffStartTime = recoveryMinutes + recoveryCutoff;
      } else {
        // Trains/buses: no jet lag, shorter recovery
        timeline.push({
          type: 'recovery-note',
          time: `${String(Math.floor(recoveryMinutes / 60)).padStart(2, '0')}:${String(recoveryMinutes % 60).padStart(2, '0')}`,
          activity: '☕ Freshen Up & Settle In',
          description: `After the overnight ${modeLabel.toLowerCase()}, take some time to freshen up and get oriented in ${currentCity}.`,
          location: dayHotelNameForTransit,
          duration: '1 hour',
          sortTime: recoveryMinutes,
          isRecoveryNote: true
        });
        cutoffStartTime = recoveryMinutes + 60;
      }

      preCheckoutCutoffEnd = 0; // No pre-checkout activities (user just arrived)
      postCheckinCutoffStart = checkinMinutes + 30; // Post-checkin activities start after check-in

      debugLog(`🌅 Day ${dayIndex + 1}: Added overnight ${travelMode} arrival items. Activities start after ${Math.floor(cutoffStartTime/60)}:${String(cutoffStartTime%60).padStart(2,'0')}`);

      // Fix 2: Set transport minutes for overnight arrival days so D3 meal blocking works.
      // When isArrivalDayAfterOvernightTravel is true, line ~10340 skips the inter-city block,
      // leaving icTransportDepartureMinutes/icTransportArrivalMinutes null → isMealBlockedByTransport
      // returns false → meals injected during flight. Fix by setting from overnightTravelArrivalData.
      if (overnightTravelArrivalData) {
        const ovrDepTime = overnightTravelArrivalData.departureTime || '00:00';
        const [ovrDepH, ovrDepM] = ovrDepTime.split(':').map(Number);
        icTransportDepartureMinutes = (ovrDepH || 0) * 60 + (ovrDepM || 0);
        const ovrArrTime = overnightTravelArrivalData.arrivalTime || '07:00';
        const [ovrArrH, ovrArrM] = ovrArrTime.split(':').map(Number);
        icTransportArrivalMinutes = (ovrArrH || 0) * 60 + (ovrArrM || 0);
        // For overnight flights, if arrival < departure it means next day — use 1440 for blocking
        if (icTransportArrivalMinutes < icTransportDepartureMinutes) {
          icTransportArrivalMinutes = 1440; // Flight spans entire day
        }
        debugLog(`✈️ Fix 2: Set overnight transport minutes: dep=${icTransportDepartureMinutes}, arr=${icTransportArrivalMinutes}`);
      }
    }

    // Check if there's ALSO a same-day onward transport (separate from the overnight arrival)
    // e.g., arrive NY from overnight flight + afternoon train NY→Boston
    const hasOnwardTransportSameDay = isArrivalDayAfterOvernightTravel && isInterCityTravelDay && (() => {
      const thisDayTransportEntry = itinerary?.interCityTransport?.find(t => t.day === dayIndex + 1);
      // If the transport entry for this day is DIFFERENT from the overnightLeg, it's onward travel
      return thisDayTransportEntry && overnightTravelArrivalData &&
        thisDayTransportEntry.from?.city?.toLowerCase() !== overnightTravelArrivalData.fromCity?.toLowerCase();
    })();

    // Don't render inter-city travel on the last day if the user is flying home —
    // the departure logistics section (below) handles the hotel-to-airport transit directly.
    // Without this, Day 3 shows "Train to Porto" / "Transit to Train Station" which is wrong
    // when the user should just drive from Ericeira hotel to OPO Airport.
    const skipInterCityForDeparture = isLastDay && effectiveReturnMode !== 'drive-own';

    if (!skipInterCityForDeparture && ((isInterCityTravelDay && !isArrivalDayAfterOvernightTravel) || hasOnwardTransportSameDay)) {
      // Render inter-city travel block. Skip when overnight arrival already handled this day
      // UNLESS there's a separate onward transport (e.g., arrive NY, then train to Boston).
      const prevHotel = itinerary?.days?.[dayIndex - 1]?.hotel;
      // BUG 8 FIX: Resolve resort/hotel names to geographic cities EARLY so ALL downstream
      // lookups (country, continent, timezone, airport, island detection) receive real city names
      const rawPrevCity = itinerary?.days?.[dayIndex - 1]?.baseCity ||
                      prevHotel?.address?.split(',').slice(-2, -1)[0]?.trim() ||
                      'Previous City';
      const rawCurrentCity = dayData?.baseCity ||
                         dayData?.hotel?.address?.split(',').slice(-2, -1)[0]?.trim() ||
                         'New City';
      const prevCity = extractLocationFromHotelName(rawPrevCity) !== rawPrevCity
        ? extractLocationFromHotelName(rawPrevCity) : rawPrevCity;
      const currentCity = extractLocationFromHotelName(rawCurrentCity) !== rawCurrentCity
        ? extractLocationFromHotelName(rawCurrentCity) : rawCurrentCity;

      // Day trip detection: if user visits a different city but returns to the same base city next day
      // e.g., Day trip from Kyoto to Nara — no hotel checkout/checkin needed
      const isDayTrip = (() => {
        const prevBaseCity = itinerary?.days?.[dayIndex - 1]?.baseCity?.toLowerCase()?.trim();
        const nextBaseCity = itinerary?.days?.[dayIndex + 1]?.baseCity?.toLowerCase()?.trim();
        const currentBaseCity = dayData?.baseCity?.toLowerCase()?.trim();
        // Day trip = visiting different city but returning to previous city next day
        if (prevBaseCity && nextBaseCity && currentBaseCity &&
            prevBaseCity === nextBaseCity &&
            currentBaseCity !== prevBaseCity) {
          debugLog(`🎯 Day ${dayIndex + 1}: Day trip detected (${prevBaseCity} → ${currentBaseCity} → ${nextBaseCity})`);
          return true;
        }
        return false;
      })();

      // Check if there's travel data in the itinerary (from AI inter-city detection)
      const travelData = itinerary?.interCityTravel?.find(t => t.day === dayIndex + 1);
      // Support both old "flights" array and new "interCityTransport" array
      const transportData = itinerary?.interCityTransport?.find(t => t.day === dayIndex + 1);
      const flightData = itinerary?.flights?.find(f => f.day === dayIndex + 1);
      const legData = transportData || flightData;

      // Dynamic transport lookup via API (async - use for route cards when user clicks)
      // This uses Haiku for fast, accurate transport recommendations
      const getDynamicTransport = async (fromCity, toCity) => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/quick-transport`, {
            method: 'POST',
            headers: await getApiHeaders(),
            body: JSON.stringify({ fromCity, toCity })
          });
          if (response.ok) {
            const data = await response.json();
            if (data.success) {
              return data;
            }
          }
        } catch (error) {
          console.error('Dynamic transport lookup failed:', error);
        }
        return null;
      };

      // getSmartTransportMode — thin wrapper over shared, injecting closure-scoped helpers
      const getSmartTransportMode = (fromCity, toCity) => {
        const mode = _getSmartTransportMode(fromCity, toCity, {
          getCityCountrySync, getContinentForCountry: getContinentForCountry, isIslandCity,
        });
        debugLog(`🚗 Smart transport: ${fromCity} → ${toCity} = ${mode.toUpperCase()}`);
        return mode;
      };

      // Use transport mode from AI-provided data, haiku lookup, or smart fallback
      // BUG 3 FIX: Prefer the mode with shortest travel time when Haiku data is available
      // Haiku's /api/quick-transport gives accurate door-to-door travel time for the optimal mode.
      // If the AI suggested a different mode but Haiku found a faster one, prefer Haiku.
      const getDynamicOrSmartTransport = () => {
        if (flightData) return 'flight';

        // Check if we have a haiku-determined transport mode for this city pair
        const pairKey = `${prevCity}|${currentCity}`.toLowerCase();
        const dynamicData = dynamicTransportModes[pairKey];
        if (dynamicData?.mode) {
          debugLog(`🚀 Using Haiku transport: ${prevCity} → ${currentCity} = ${dynamicData.mode} (${dynamicData.durationMinutes} min)`);
          return dynamicData.mode;
        }

        // Fall back to static rules
        return getSmartTransportMode(prevCity, currentCity);
      };

      // BUG 3 FIX: If both AI and Haiku provide modes, prefer Haiku's choice since it
      // accounts for actual travel time, train availability, and island geography.
      // AI's mode is a guess from itinerary generation; Haiku's is a dedicated transport analysis.
      const aiMode = legData?.mode || travelData?.recommendedMode;
      const pairKeyForMode = `${prevCity}|${currentCity}`.toLowerCase();
      const haikuData = dynamicTransportModes[pairKeyForMode];

      let transportMode;
      if (flightData) {
        transportMode = 'flight';
      } else if (haikuData?.mode && aiMode && haikuData.mode !== aiMode) {
        // Haiku disagrees with AI — prefer Haiku (it has travel time context)
        debugLog(`🔄 Transport override: AI said "${aiMode}" but Haiku says "${haikuData.mode}" (${haikuData.durationMinutes} min) for ${prevCity} → ${currentCity}. Using Haiku.`);
        transportMode = haikuData.mode;
      } else {
        transportMode = aiMode || getDynamicOrSmartTransport();
      }

      // ISSUE 2 FIX: Frontend validation guard — now uses shared validateTransportMode
      // Forces to flight for cross-continental or island routes (except ferry)
      const validatedMode = validateTransportMode(transportMode, prevCity, currentCity, {
        getCityCountrySync, getContinentForCountry, isIslandCity,
      });
      if (validatedMode !== transportMode) {
        debugLog(`⚠️ Transport validation: "${transportMode}" → "${validatedMode}" for ${prevCity} → ${currentCity}`);
        transportMode = validatedMode;
      }

      // Smart flight duration estimate — web wrapper with cache/dynamic layers on top of shared heuristics
      const getSmartFlightDuration = (fromCity, toCity) => {
        const fromLower = fromCity?.toLowerCase().trim();
        const toLower = toCity?.toLowerCase().trim();

        // B4: Priority order — validated > dynamic prefetch > shared heuristics
        // 1. Check dynamically validated cache first (populated by post-gen validation)
        const validatedKey = `${fromLower}-${toLower}`;
        const validatedReverse = `${toLower}-${fromLower}`;
        if (validatedFlightDurations.current[validatedKey]) {
          debugLog(`✈️ Using dynamically validated duration: ${validatedKey} = ${validatedFlightDurations.current[validatedKey]}`);
          return validatedFlightDurations.current[validatedKey];
        }
        if (validatedFlightDurations.current[validatedReverse]) {
          debugLog(`✈️ Using dynamically validated duration (reverse): ${validatedReverse} = ${validatedFlightDurations.current[validatedReverse]}`);
          return validatedFlightDurations.current[validatedReverse];
        }

        // 2. Check dynamic transport modes from Haiku prefetch
        const pairKey = `${fromLower}|${toLower}`;
        const pairKeyReverse = `${toLower}|${fromLower}`;
        const dynamicData = dynamicTransportModes[pairKey] || dynamicTransportModes[pairKeyReverse];
        if (dynamicData?.duration) {
          debugLog(`✈️ Using dynamic prefetch duration: ${fromCity}→${toCity} = ${dynamicData.duration}`);
          return dynamicData.duration;
        }

        // 3. Shared heuristics: static route map → cross-continental → domestic → same-continent
        return estimateFlightDurationHeuristic(fromCity, toCity, { getCityCountrySync, getContinentForCountry });
      };

      const departureTime = legData?.departureTime || flightData?.departureTime || travelData?.options?.[0]?.departureTime || '09:00';

      // BUG 8: Resort name resolution now happens at prevCity/currentCity assignment (line ~10950)
      // so prevCity and currentCity are already resolved to geographic cities here.

      // Use AI-provided duration, dynamic transport data, or smart estimate
      // BUG A FIX: If Haiku overrode the AI's mode (Bug 3 fix), also prefer Haiku's duration
      // since the AI's duration was for a different mode or is unreliable
      const pairKeyForDuration = `${prevCity}|${currentCity}`.toLowerCase();
      const haikuDurationData = dynamicTransportModes[pairKeyForDuration];
      const dynamicDuration = haikuDurationData?.duration;
      const aiDuration = legData?.duration || flightData?.duration || travelData?.options?.find(o => o.mode === transportMode)?.duration;
      // If Haiku overrode the mode, prefer Haiku's duration too
      const haikuOverrodeMode = haikuData?.mode && aiMode && haikuData.mode !== aiMode;
      let travelDuration = haikuOverrodeMode ? (dynamicDuration || aiDuration) : (aiDuration || dynamicDuration);
      if (!travelDuration) {
        if (transportMode === 'flight') {
          travelDuration = getSmartFlightDuration(prevCity, currentCity);
        } else if (transportMode === 'train') {
          travelDuration = '~3h'; // Default for train
        } else {
          travelDuration = '~3h'; // Default for car/bus (was 4h, but most inter-city drives are 2-3h)
        }
      }
      // D3 (backport from shared): reject LLM-produced negative duration strings
      // e.g. "-4h" for a transatlantic with timezone math flipped. Fall back to
      // the smart estimator or blank so downstream code doesn't crunch garbage.
      if (/^\s*-/.test(String(travelDuration)) || /-\s*\d/.test(String(travelDuration))) {
        const fallback = getSmartFlightDuration(prevCity, currentCity);
        travelDuration = fallback || '';
      }
      // Validate flight duration against known routes - LLM often gets duration wrong
      if (transportMode === 'flight' && travelDuration) {
        const parseHours = (str) => {
          const h = str.match(/(\d+(?:\.\d+)?)\s*h/i);
          const m = str.match(/(\d+)\s*m/i);
          return (h ? parseFloat(h[1]) : 0) + (m ? parseInt(m[1]) / 60 : 0);
        };
        // BUG 8: prevCity/currentCity are already resolved (hotel names → real cities) at assignment
        const bestKnown = getSmartFlightDuration(prevCity, currentCity);

        if (bestKnown) {
          const knownHrs = parseHours(bestKnown);
          const currentHrs = parseHours(travelDuration);

          if (!bestKnown.startsWith('~')) {
            // B4: Precise lookup — if LLM is >30% off, use the known duration (was 50%)
            if (knownHrs > 0 && currentHrs > knownHrs * 1.3) {
              debugLog(`✈️ Correcting flight duration ${prevCity}→${currentCity}: ${travelDuration} → ${bestKnown} (lookup override)`);
              travelDuration = bestKnown;
            }
          } else {
            // Cross-continental estimate (starts with ~) — use as MINIMUM floor
            // The AI sometimes says "8h 30m" for a route that's ~12h non-stop (and actually
            // requires connections making it 30-40h). If AI's duration is significantly LESS
            // than the non-stop minimum, the AI is wrong — use the estimate instead.
            // Also flag multi-connection routes where real travel time >> non-stop estimate.
            if (knownHrs > 0 && currentHrs < knownHrs * 0.75) {
              debugLog(`✈️ Correcting underestimated flight ${prevCity}→${currentCity}: ${travelDuration} → ${bestKnown} (below cross-continental minimum)`);
              travelDuration = bestKnown;
            }
          }
        }
      }
      const carrier = legData?.carrier || flightData?.airline || '';
      // REGION_TO_STATION and resolveStation now imported from shared transportConstants
      const fromStation = legData?.from?.station || legData?.from?.airport || resolveStation(prevCity, `${prevCity} Station`);
      const toStation = legData?.to?.station || legData?.to?.airport || resolveStation(currentCity, `${currentCity} Station`);
      // B9B: Resolve IATA codes for airport fallbacks
      const fromIata = lookupCityAirportCode(prevCity);
      const toIata = lookupCityAirportCode(currentCity);
      const fromAirport = legData?.from?.airport || flightData?.from?.airport || (fromIata ? `${fromIata} International Airport` : `${prevCity} Airport`);
      const toAirport = legData?.to?.airport || flightData?.to?.airport || (toIata ? `${toIata} International Airport` : `${currentCity} Airport`);
      // BUG 1 FIX: extractAirportCode and validateAirportCode now imported from shared displayGuardrails
      const fromAirportCode = validateAirportCode(
        extractAirportCode(fromAirport) || extractAirportCode(legData?.from?.airport),
        prevCity || fromAirport
      ) || lookupCityAirportCode(prevCity) || lookupCityAirportCode(fromAirport) || '';
      const toAirportCode = validateAirportCode(
        extractAirportCode(toAirport) || extractAirportCode(legData?.to?.airport),
        currentCity || toAirport
      ) || lookupCityAirportCode(currentCity) || lookupCityAirportCode(toAirport) || '';
      const travelNote = legData?.note || '';
      const connections = legData?.connections || []; // Connection flight data
      const isOvernightFromData = legData?.isOvernight || false;

      const [depHours, depMins] = departureTime.split(':').map(Number);
      const departureMinutes = (depHours || 8) * 60 + (depMins || 0);

      // Clamp cutoff for ANY inter-city departure (flight, train, bus, ferry):
      // No AI activities should show after the user departs
      if (isInterCityTravelDay && departureMinutes > 0) {
        cutoffEndTime = Math.min(cutoffEndTime, departureMinutes);
        isOvernightDepartureDay = true;
        debugLog(`🚀 Inter-city departure (${transportMode}): clamping cutoffEndTime to ${Math.floor(cutoffEndTime/60)}:${String(cutoffEndTime%60).padStart(2,'0')}`);
      }

      // For flights, calculate arrival time with timezone awareness
      // Parse flight duration from string like "14h 30m" or "~2 hours"
      let flightDurationHours = 2; // default
      if (travelDuration) {
        const hourMatch = travelDuration.match(/(\d+(?:\.\d+)?)\s*h/i);
        const minMatch = travelDuration.match(/(\d+)\s*m/i);
        if (hourMatch) {
          flightDurationHours = parseFloat(hourMatch[1]);
          if (minMatch) {
            flightDurationHours += parseInt(minMatch[1]) / 60;
          }
        }
      }

      // Calculate timezone-aware arrival time for flights
      let arrivalTime;
      let arrivalMinutes;
      let arrivalDayOffset = 0;
      let timezoneNote = '';

      // Store timezone difference for jet lag calculations
      let timezoneDiff = 0;

      if (transportMode === 'flight') {
        const flightArrival = calculateFlightArrival(departureTime, flightDurationHours, prevCity, currentCity);
        arrivalTime = flightArrival.localArrivalTime;
        arrivalDayOffset = flightArrival.dayOffset;
        timezoneNote = flightArrival.timezoneNote;
        timezoneDiff = flightArrival.timezoneDiff || 0;

        const [arrHours, arrMins] = arrivalTime.split(':').map(Number);
        arrivalMinutes = (arrHours || 10) * 60 + (arrMins || 0);

        debugLog(`✈️ Flight ${prevCity} → ${currentCity}: depart ${departureTime}, arrive ${arrivalTime} ${arrivalDayOffset !== 0 ? `(+${arrivalDayOffset} day)` : ''} ${timezoneNote}`);
      } else {
        // For non-flight transport (train, bus, car, ferry):
        // 1. Use provided arrival time if available
        // 2. Otherwise CALCULATE from departure + duration (don't use hardcoded fallback!)
        const providedArrival = legData?.arrivalTime || flightData?.arrivalTime;
        if (providedArrival) {
          arrivalTime = providedArrival;
          const [arrHours, arrMins] = arrivalTime.split(':').map(Number);
          arrivalMinutes = (arrHours || 10) * 60 + (arrMins || 0);
        } else {
          // Calculate arrival from departure + duration
          const durationMinutes = Math.round(flightDurationHours * 60); // flightDurationHours is already parsed from travelDuration
          arrivalMinutes = departureMinutes + durationMinutes;
          // Handle day wraparound
          if (arrivalMinutes >= 1440) {
            arrivalDayOffset = Math.floor(arrivalMinutes / 1440);
            arrivalMinutes = arrivalMinutes % 1440;
            debugLog(`⚠️ ${transportMode} travel crosses midnight: +${arrivalDayOffset} day(s)`);
          }
          const arrH = Math.floor(arrivalMinutes / 60);
          const arrM = arrivalMinutes % 60;
          arrivalTime = `${String(arrH).padStart(2, '0')}:${String(arrM).padStart(2, '0')}`;
          debugLog(`🚄 ${transportMode} ${prevCity} → ${currentCity}: depart ${departureTime} + ${travelDuration} = arrive ${arrivalTime}${arrivalDayOffset ? ` (+${arrivalDayOffset} day)` : ''}`);
        }
      }

      // D3: Store transport departure/arrival minutes for meal blocking
      icTransportDepartureMinutes = departureMinutes;
      icTransportArrivalMinutes = arrivalMinutes;

      // Calculate checkout time based on transport mode
      // For flights: derive checkout from transit-to-airport time so the order is always
      // breakfast → checkout → transit to airport → flight
      let checkoutMinutes;
      // Pre-compute flight transit time so checkout can be placed before it
      let precomputedTransitToAirportMinutes = null;
      if (transportMode === 'flight') {
        const toAirportTransitKeyForCheckout = `day${dayIndex}-to-departure-airport`;
        const cachedToAirportForCheckout = transitTimes[toAirportTransitKeyForCheckout] || {};
        const toAirportModeForCheckout = transportModes[toAirportTransitKeyForCheckout] || 'driving';
        const cachedToAirportDataForCheckout = cachedToAirportForCheckout[toAirportModeForCheckout];

        let preEstimatedTransitMins = 45; // Default for city hotels
        if (cachedToAirportDataForCheckout?.durationMinutes) {
          preEstimatedTransitMins = cachedToAirportDataForCheckout.durationMinutes;
          debugLog(`📍 Dynamic checkout buffer: ${prevCity} → airport transit = ${preEstimatedTransitMins} min`);
        } else {
          debugLog(`📍 No cached transit for checkout buffer calc, using default ${preEstimatedTransitMins} min`);
        }
        // Airport buffer: 2.5h for international, 1.5h for domestic (pre-compute for checkout placement)
        const prevCountryForCheckout = CITY_TO_COUNTRY[prevCity?.toLowerCase()] || '';
        const currCountryForCheckout = CITY_TO_COUNTRY[currentCity?.toLowerCase()] || '';
        const isIntlForCheckout = prevCountryForCheckout && currCountryForCheckout
          ? prevCountryForCheckout !== currCountryForCheckout
          : prevCity?.toLowerCase() !== currentCity?.toLowerCase();
        const airportBufferForCheckout = isIntlForCheckout ? 150 : 90;
        precomputedTransitToAirportMinutes = departureMinutes - preEstimatedTransitMins - airportBufferForCheckout;
        // Checkout = 15 min before transit to airport (time to gather luggage and leave)
        checkoutMinutes = precomputedTransitToAirportMinutes - 15;
        debugLog(`📍 Flight checkout timing: transit@${precomputedTransitToAirportMinutes} → checkout@${checkoutMinutes}`);
      } else {
        const checkoutBuffer = 60; // 1 hour before non-flight departure
        checkoutMinutes = departureMinutes - checkoutBuffer;
      }

      // Handle very early departures (checkout would be negative = previous day)
      const isOvernightCheckout = checkoutMinutes < 0;
      if (isOvernightCheckout) {
        checkoutMinutes = Math.max(0, checkoutMinutes); // Clamp to midnight
        debugWarn(`⚠️ Very early departure (${departureTime}) requires late-night checkout`);
      }

      const checkoutHours = Math.floor(checkoutMinutes / 60);
      const checkoutMins = checkoutMinutes % 60;

      // Flag very early checkouts (before 5 AM)
      const isVeryEarlyCheckout = checkoutMinutes < 300 && checkoutMinutes >= 0;

      // Store checkout time for pre-checkout activity filtering
      preCheckoutCutoffEnd = checkoutMinutes;

      const checkoutDescription = isOvernightCheckout
        ? `Very early checkout from ${prevHotel?.name || 'hotel'} - consider airport hotel`
        : isVeryEarlyCheckout
        ? `Early checkout from ${prevHotel?.name || 'hotel'}`
        : `Check out of ${prevHotel?.name || 'hotel'}`;

      // Inject breakfast before checkout on inter-city travel days (if none exists yet)
      // This mirrors the last-day departure logic — ensures breakfast → checkout → transit → flight
      if (!isDayTrip && !isVeryEarlyCheckout && !isOvernightCheckout && checkoutMinutes > 90) {
        const hasBreakfastAlready = timeline.some(item =>
          (item.type === 'meal' && (item.meal || item.activity || '').toLowerCase().includes('breakfast')) ||
          (item.activity || '').toLowerCase().includes('breakfast')
        );
        if (!hasBreakfastAlready) {
          const icBreakfastMinutes = checkoutMinutes - 60;
          if (icBreakfastMinutes >= 300) { // Only if breakfast would be at 5:00 AM or later
            const icBreakfastHours = Math.floor(icBreakfastMinutes / 60);
            const icBreakfastMins = icBreakfastMinutes % 60;
            timeline.push({
              type: 'meal', mealType: 'breakfast', originalType: 'breakfast',
              meal: 'Breakfast',
              time: `${String(icBreakfastHours).padStart(2, '0')}:${String(icBreakfastMins).padStart(2, '0')}`,
              activity: 'Breakfast',
              restaurant: prevHotel?.name ? `${prevHotel.name} Restaurant` : 'Hotel Restaurant',
              description: `Enjoy breakfast before checking out for your ${transportMode === 'flight' ? 'flight' : 'journey'}`,
              duration: '45 minutes',
              location: prevHotel?.name || 'Hotel',
              sortTime: icBreakfastMinutes,
              isDisplayInjected: true,
            });
            debugLog(`🍳 Injected breakfast at ${icBreakfastHours}:${String(icBreakfastMins).padStart(2, '0')} on inter-city travel day (before checkout@${checkoutHours}:${String(checkoutMins).padStart(2, '0')})`);
          }
        }
      }

      // Add checkout from previous hotel (skip for day trips — user keeps the same room)
      if (!isDayTrip) {
        timeline.push({
          type: 'checkout',
          time: `${String(checkoutHours).padStart(2, '0')}:${String(checkoutMins).padStart(2, '0')}`,
          activity: checkoutLabel,
          description: checkoutDescription,
          location: prevHotel?.name || 'Hotel',
          duration: '15 minutes',
          sortTime: checkoutMinutes,
          isVeryEarly: isVeryEarlyCheckout || isOvernightCheckout
        });
      }

      // ISSUE 7 + BUG 15 FIX: Use the actual hotel name in transit labels whenever we have a real name.
      // For resort/hotel trips, we always have the name. For destination trips, use the name
      // if it's a recognizable property (not just generic "Hotel" or "hotel").
      const isResortProperty = tripType === 'hotel' ||
        (dayHotelName && dayHotelName !== 'Hotel' && dayHotelName !== 'hotel' && dayHotelName.length > 3);

      if (transportMode === 'flight') {
        // FLIGHT: Add transit to airport
        // Use cached Google Maps data if available, otherwise default to 45 min
        const toAirportTransitKey = `day${dayIndex}-to-departure-airport`;
        const cachedToAirport = transitTimes[toAirportTransitKey] || {};
        const toAirportMode = transportModes[toAirportTransitKey] || 'driving';
        const cachedToAirportData = cachedToAirport[toAirportMode];

        let estimatedTransitMins = 45; // Default for city hotels
        let transitDurationDisplay = '~45 min';
        let transitDescriptionExtra = '';

        if (cachedToAirportData?.durationMinutes) {
          // Use real Google Maps data
          estimatedTransitMins = cachedToAirportData.durationMinutes;
          transitDurationDisplay = cachedToAirportData.duration || `~${Math.round(estimatedTransitMins / 60 * 10) / 10} hrs`;
          if (estimatedTransitMins > 90) {
            transitDescriptionExtra = `Long drive to ${fromAirport} (${transitDurationDisplay}) - leave early! `;
          }
          debugLog(`📍 Using cached Google Maps transit: ${prevCity} → ${fromAirport} = ${transitDurationDisplay} (${estimatedTransitMins} mins)`);
        } else {
          debugLog(`📍 No cached transit data for ${toAirportTransitKey}, using default ${estimatedTransitMins} min`);
        }

        // Airport buffer: 2.5h for international, 1.5h for domestic
        const prevCountryLookup = CITY_TO_COUNTRY[prevCity?.toLowerCase()] || '';
        const currCountryLookup = CITY_TO_COUNTRY[currentCity?.toLowerCase()] || '';
        const isInternationalDeparture = prevCountryLookup && currCountryLookup
          ? prevCountryLookup !== currCountryLookup
          : prevCity?.toLowerCase() !== currentCity?.toLowerCase(); // different cities = assume international if unknown
        const airportBufferMins = isInternationalDeparture ? 150 : 90;
        const transitToAirportMinutes = departureMinutes - estimatedTransitMins - airportBufferMins;
        const transitToAirportHours = Math.floor(Math.max(0, transitToAirportMinutes) / 60);
        const transitToAirportMins = Math.max(0, transitToAirportMinutes) % 60;

        timeline.push({
          type: 'airport-transit',
          time: `${String(transitToAirportHours).padStart(2, '0')}:${String(transitToAirportMins).padStart(2, '0')}`,
          activity: '🚕 Transit to Airport',
          description: `${transitDescriptionExtra}Head to ${fromAirport} for your flight`,
          location: `${prevHotel?.name || 'Hotel'} → ${fromAirport}`,
          from: prevHotel?.name || 'Hotel',
          to: fromAirport,
          duration: transitDurationDisplay,
          sortTime: Math.max(0, transitToAirportMinutes),
          transitKey: toAirportTransitKey
        });
        
        // Determine if this is an overnight/red-eye flight.
        // D4 (backport from shared): dynamic overnight/long-haul sanity net.
        // Promote to overnight when the flight is clearly multi-hour but the
        // LLM didn't flag it (e.g. Belize → Lisbon, ~10h, same-day arrival),
        // or when it crosses 5+ timezones with ≥6h duration. Also floor the
        // duration to ~8h when a cross-continental flight was reported as
        // under 4h (timezone-math slip).
        let isOvernightFlight = isOvernightFromData || arrivalDayOffset > 0 || (departureMinutes >= 1200); // Departs after 8pm
        const crossesManyTimezones = Math.abs(timezoneDiff) >= 5;
        if (!isOvernightFlight && arrivalDayOffset === 0) {
          const forcedByDuration = flightDurationHours >= 10;
          const forcedByTimezones = crossesManyTimezones && flightDurationHours >= 6;
          if (forcedByDuration || forcedByTimezones) {
            isOvernightFlight = true;
            arrivalDayOffset = 1;
          }
        }
        if (crossesManyTimezones && flightDurationHours > 0 && flightDurationHours < 4) {
          travelDuration = '~8h';
        }
        const isLongHaulFlight = flightDurationHours >= 6 || isOvernightFlight; // 6+ hours or overnight = long-haul

        // cutoffEndTime already clamped above (applies to all transport modes)
        const hasConnections = connections && connections.length > 0;

        // Build connection info string + compute total journey duration
        // (Option A for #3 — flight card must show total travel time, not
        // just the flight leg, when layovers exist).
        let connectionInfo = '';
        let totalLayoverMinutes = 0;
        if (hasConnections) {
          const connectionStops = connections.map(c => `${c.city || c.airport} (${c.layoverDuration || '~2h'})`).join(', ');
          connectionInfo = ` Connection${connections.length > 1 ? 's' : ''}: ${connectionStops}.`;
          totalLayoverMinutes = connections.reduce((sum, c) => {
            const raw = String(c.layoverDuration || c.layover || '');
            const hMatch = raw.match(/(\d+(?:\.\d+)?)\s*h/i);
            const mMatch = raw.match(/(\d+)\s*m(?!\w)/i);
            const h = hMatch ? parseFloat(hMatch[1]) : 0;
            const m = mMatch ? parseInt(mMatch[1], 10) : 0;
            const computed = h * 60 + m;
            return sum + (computed > 0 ? computed : 120);
          }, 0);
        }
        const flyingDurationStr = travelDuration;
        let totalJourneyDurationStr = null;
        if (hasConnections && totalLayoverMinutes > 0 && flightDurationHours > 0) {
          const totalHours = flightDurationHours + totalLayoverMinutes / 60;
          const h = Math.floor(totalHours);
          const m = Math.round((totalHours - h) * 60);
          totalJourneyDurationStr = m > 0 ? `${h}h ${m}m` : `${h}h`;
        }

        // Add flight card with timezone-aware arrival info
        const arrivalInfo = arrivalDayOffset > 0
          ? ` Arrives ${arrivalTime} next day ${timezoneNote}`.trim()
          : arrivalDayOffset < 0
          ? ` Arrives ${arrivalTime} same day ${timezoneNote}`.trim()
          : timezoneNote ? ` Arrives ${arrivalTime} ${timezoneNote}` : ` Arrives ${arrivalTime} local time.`;

        // Add overnight flight indicator and jet lag warning
        const overnightNote = isOvernightFlight ? ' 🌙 Overnight flight.' : '';
        const jetLagNote = isLongHaulFlight && Math.abs(timezoneDiff) >= 5
          ? ` ⚠️ Significant time zone change (${timezoneDiff > 0 ? '+' : ''}${timezoneDiff}hrs) - expect jet lag.`
          : '';

        timeline.push({
          type: 'inter-city-travel',
          time: departureTime,
          activity: `✈️ Flight to ${currentCity}${isOvernightFlight ? ' (Overnight)' : ''}${hasConnections ? ' (1 stop)' : ''}`,
          description: carrier
            ? `${carrier} flight from ${prevCity} to ${currentCity}. ${travelDuration} total.${connectionInfo}${arrivalInfo}${overnightNote}${jetLagNote}`
            : `Flight from ${prevCity} to ${currentCity}. ${travelDuration} total.${connectionInfo}${arrivalInfo}${overnightNote}${jetLagNote}`,
          duration: travelDuration,
          location: `${prevCity} → ${currentCity}`,
          from: fromAirport,
          to: toAirport,
          sortTime: departureMinutes,
          isTravel: true,
          travelMode: 'flight',
          transportData: legData || flightData || null,
          arrivalTime: arrivalTime,
          arrivalDayOffset: arrivalDayOffset,
          fromAirportCode: fromAirportCode,
          toAirportCode: toAirportCode,
          isOvernightFlight: isOvernightFlight,
          isLongHaulFlight: isLongHaulFlight,
          timezoneChange: timezoneDiff,
          connections: connections,
          // Option A layover display (#3)
          flyingDuration: flyingDurationStr,
          totalJourneyDuration: totalJourneyDurationStr,
          totalLayoverMinutes: totalLayoverMinutes || undefined,
        });

        // For overnight flights arriving NEXT DAY, add a brief note on the departure day
        if (isOvernightFlight && arrivalDayOffset > 0) {
          const overnightSortTime = departureMinutes + 1;
          timeline.push({
            type: 'overnight-journey',
            time: arrivalTime || departureTime,
            activity: '✨ Overnight Journey',
            description: `Arriving at ${currentCity} tomorrow after the overnight flight. No hotel tonight — sleeping on the plane!`,
            location: `En route to ${currentCity}`,
            duration: travelDuration,
            sortTime: overnightSortTime,
            isOvernightJourney: true
          });
          // cutoffEndTime already clamped above for all inter-city departures
        }

        // For overnight flights arriving next day, add a "recovery" suggestion
        // IMPORTANT: Only add recovery note if this is NOT an overnight departure (where arrival is tomorrow)
        // The recovery note should appear on the ARRIVAL day, not the departure day
        if (isOvernightFlight && isLongHaulFlight && arrivalDayOffset === 0) {
          // Only add recovery note if we're arriving on the SAME day (not next day)
          // For overnight flights where arrivalDayOffset > 0, the recovery note will be added
          // when we process the ARRIVAL day, not the departure day
          const recoverySortTime = arrivalMinutes < departureMinutes
            ? departureMinutes + 2  // Sort after flight but before customs/transit
            : arrivalMinutes;
          timeline.push({
            type: 'recovery-note',
            time: arrivalTime,
            activity: '💤 Arrival & Recovery Time',
            description: `After a ${travelDuration} ${hasConnections ? 'connecting ' : ''}overnight flight with ${Math.abs(timezoneDiff) >= 5 ? 'significant jet lag' : 'time zone adjustment'}, consider a lighter schedule today. Stay hydrated and get sunlight to adjust faster.`,
            location: currentCity,
            duration: '2-3 hours',
            sortTime: recoverySortTime,
            isRecoveryNote: true
          });
        }

        // Jet lag adjustment note for long-haul same-day arrivals with big timezone changes
        // (e.g. HK→NYC: 16h flight but arrives same day due to -13h timezone shift)
        // These aren't "overnight" but the traveler still needs recovery time
        if (!isOvernightFlight && isLongHaulFlight && arrivalDayOffset <= 0 && Math.abs(timezoneDiff) >= 5) {
          timeline.push({
            type: 'recovery-note',
            time: arrivalTime,
            activity: '⏰ Jet Lag Adjustment',
            description: `After a ${travelDuration} flight with a ${Math.abs(timezoneDiff)}-hour time zone change, your body clock will need adjusting. Stay hydrated and get some sunlight to help reset your circadian rhythm.`,
            location: currentCity,
            duration: '2-3 hours',
            sortTime: arrivalMinutes + 1,
            isRecoveryNote: true
          });
        }
      } else if (transportMode === 'train') {
        // TRAIN: Add transit to station
        const transitToStationMinutes = Math.max(0, departureMinutes - 45); // Less buffer than flight, clamp to 0
        const transitToStationHours = Math.floor(transitToStationMinutes / 60);
        const transitToStationMins = transitToStationMinutes % 60;
        
        timeline.push({
          type: 'station-transit',
          time: `${String(Math.max(0, transitToStationHours)).padStart(2, '0')}:${String(transitToStationMins).padStart(2, '0')}`,
          activity: '🚕 Transit to Train Station',
          description: `Head to ${fromStation} for your train`,
          location: `${prevHotel?.name || 'Hotel'} → ${fromStation}`,
          from: prevHotel?.name || 'Hotel',
          to: fromStation,
          duration: '~30 min',
          sortTime: transitToStationMinutes,
          transitKey: `day${dayIndex}-to-station`
        });
        
        // Add train card
        timeline.push({
          type: 'inter-city-travel',
          time: departureTime,
          activity: `🚆 Train to ${currentCity}`,
          description: carrier
            ? `${carrier} from ${fromStation} to ${toStation}. ${travelDuration}.${travelNote ? ` ${travelNote}` : ''}`
            : `Train from ${prevCity} to ${currentCity}. ${travelDuration}.`,
          duration: travelDuration,
          location: `${fromStation} → ${toStation}`,
          from: fromStation,
          to: toStation,
          sortTime: departureMinutes,
          isTravel: true,
          travelMode: 'train',
          transportData: legData || null,
          arrivalTime: arrivalTime,
          arrivalDayOffset: arrivalDayOffset
        });
        
        // Add transit from station to hotel — ONLY if arriving same day
        // For overnight trains (arrivalDayOffset > 0), this is handled on the arrival day
        if (arrivalDayOffset <= 0) {
          // Use cached Google Maps data if available for station→hotel transit
          const fromStationTransitKey = `day${dayIndex}-from-station`;
          const cachedFromStation = transitTimes[fromStationTransitKey] || {};
          const fromStationMode = transportModes[fromStationTransitKey] || 'driving';
          const cachedFromStationData = cachedFromStation[fromStationMode];

          let stationTransitMins = 30; // Default
          let stationTransitDisplay = '~30 min';
          if (cachedFromStationData?.durationMinutes) {
            stationTransitMins = cachedFromStationData.durationMinutes;
            stationTransitDisplay = cachedFromStationData.duration || `~${stationTransitMins} min`;
          }

          const transitFromStationMinutes = arrivalMinutes + 15;
          const transitFromStationHours = Math.floor(transitFromStationMinutes / 60);
          const transitFromStationMins = transitFromStationMinutes % 60;

          const stationTransitLabel = isResortProperty && dayHotelName && dayHotelName !== 'Hotel'
            ? `🚕 Transit to ${dayHotelName}`
            : '🚕 Transit to Hotel';
          timeline.push({
            type: 'station-transit',
            time: `${String(transitFromStationHours).padStart(2, '0')}:${String(transitFromStationMins).padStart(2, '0')}`,
            activity: stationTransitLabel,
            description: `Transfer from ${toStation} to ${isResortProperty && dayHotelName !== 'Hotel' ? dayHotelName : 'your hotel'}`,
            location: `${toStation} → ${dayHotelName}`,
            from: toStation,
            to: dayHotelName,
            duration: stationTransitDisplay,
            sortTime: transitFromStationMinutes,
            transitKey: fromStationTransitKey
          });
        } else {
          // Overnight train — add journey note on departure day
          timeline.push({
            type: 'overnight-journey',
            time: departureTime,
            activity: '🌙 Overnight Train Journey',
            description: `Arriving at ${currentCity} tomorrow after the overnight train. No hotel tonight — sleeping on the train!`,
            location: `En route to ${currentCity}`,
            duration: travelDuration,
            sortTime: departureMinutes + 1,
            isOvernightJourney: true
          });
        }
      } else if (transportMode === 'ferry') {
        // FERRY: Transit to port + ferry + transit from port
        const ferryFromName = prevHotel?.name
          ? `${prevHotel.name}, ${prevCity}`
          : prevCity;
        const ferryToName = dayData?.hotel?.name
          ? `${dayData.hotel.name}, ${currentCity}`
          : currentCity;

        // Transit to ferry port
        const transitToPortMinutes = Math.max(0, departureMinutes - 60); // 1hr buffer for port
        const transitToPortHours = Math.floor(transitToPortMinutes / 60);
        const transitToPortMins = transitToPortMinutes % 60;

        timeline.push({
          type: 'port-transit',
          time: `${String(transitToPortHours).padStart(2, '0')}:${String(transitToPortMins).padStart(2, '0')}`,
          activity: '🚕 Transit to Ferry Port',
          description: `Head to ${prevCity} port for your ferry to ${currentCity}`,
          location: `${prevHotel?.name || 'Hotel'} → ${prevCity} Port`,
          from: prevHotel?.name || 'Hotel',
          to: `${prevCity} Port`,
          duration: '~30 min',
          sortTime: transitToPortMinutes,
          transitKey: `day${dayIndex}-to-port`
        });

        // Ferry card
        timeline.push({
          type: 'inter-city-travel',
          time: departureTime,
          activity: `⛴️ Ferry to ${currentCity}`,
          description: carrier
            ? `${carrier} from ${prevCity} to ${currentCity}. ${travelDuration}.${travelNote ? ` ${travelNote}` : ''}`
            : `Ferry from ${prevCity} to ${currentCity}. ${travelDuration}. Book in advance during peak season.`,
          duration: travelDuration,
          location: `${prevCity} → ${currentCity}`,
          from: ferryFromName,
          to: ferryToName,
          sortTime: departureMinutes,
          isTravel: true,
          travelMode: 'ferry',
          transportData: legData || null,
          arrivalTime: arrivalTime,
          arrivalDayOffset: arrivalDayOffset,
          transitKey: `day${dayIndex}-inter-city-ferry`
        });

        // Transit from port to hotel — ONLY if arriving same day
        if (arrivalDayOffset <= 0) {
          // Use cached Google Maps data if available for port→hotel transit
          const fromPortTransitKey = `day${dayIndex}-from-port`;
          const cachedFromPort = transitTimes[fromPortTransitKey] || {};
          const fromPortMode = transportModes[fromPortTransitKey] || 'driving';
          const cachedFromPortData = cachedFromPort[fromPortMode];

          let portTransitMins = 30;
          let portTransitDisplay = '~30 min';
          if (cachedFromPortData?.durationMinutes) {
            portTransitMins = cachedFromPortData.durationMinutes;
            portTransitDisplay = cachedFromPortData.duration || `~${portTransitMins} min`;
          }

          const transitFromPortMinutes = arrivalMinutes + 15;
          const transitFromPortHours = Math.floor(transitFromPortMinutes / 60);
          const transitFromPortMins = transitFromPortMinutes % 60;

          const portTransitLabel = isResortProperty && dayHotelName && dayHotelName !== 'Hotel'
            ? `🚕 Transit to ${dayHotelName}`
            : '🚕 Transit to Hotel';
          timeline.push({
            type: 'port-transit',
            time: `${String(transitFromPortHours).padStart(2, '0')}:${String(transitFromPortMins).padStart(2, '0')}`,
            activity: portTransitLabel,
            description: `Transfer from ${currentCity} port to ${isResortProperty && dayHotelName !== 'Hotel' ? dayHotelName : 'your hotel'}`,
            location: `${currentCity} Port → ${dayHotelName}`,
            from: `${currentCity} Port`,
            to: dayHotelName,
            duration: portTransitDisplay,
            sortTime: transitFromPortMinutes,
            transitKey: fromPortTransitKey
          });
        } else {
          // Overnight ferry — add journey note on departure day
          timeline.push({
            type: 'overnight-journey',
            time: departureTime,
            activity: '🌙 Overnight Ferry Journey',
            description: `Arriving at ${currentCity} tomorrow after the overnight ferry. No hotel tonight — sleeping on the ferry!`,
            location: `En route to ${currentCity}`,
            duration: travelDuration,
            sortTime: departureMinutes + 1,
            isOvernightJourney: true
          });
        }
      } else {
        // CAR/BUS: Direct drive or bus journey
        // Determine proper origin/destination names (hotel names or city names)
        const driveFromName = prevHotel?.name
          ? `${prevHotel.name}, ${prevCity}`
          : prevCity;
        const driveToName = dayData?.hotel?.name
          ? `${dayData.hotel.name}, ${currentCity}`
          : currentCity;

        // Determine transport emoji and label based on mode
        const transportLabel = transportMode === 'bus' ? `🚌 Bus to ${currentCity}`
          : `🚗 Drive to ${currentCity}`;
        const transportDescLabel = transportMode === 'bus' ? 'Bus ride'
          : 'Scenic drive';

        timeline.push({
          type: 'inter-city-travel',
          time: departureTime,
          activity: transportLabel,
          description: carrier
            ? `${carrier} from ${prevCity} to ${currentCity}. ${travelDuration}.${travelNote ? ` ${travelNote}` : ''}`
            : `${transportDescLabel} from ${prevCity} to ${currentCity}. ${travelDuration}.`,
          duration: travelDuration,
          location: `${prevCity} → ${currentCity}`,
          from: driveFromName,
          to: driveToName,
          sortTime: departureMinutes,
          isTravel: true,
          travelMode: transportMode,
          transportData: legData || null,
          arrivalTime: arrivalTime,
          arrivalDayOffset: arrivalDayOffset,
          transitKey: `day${dayIndex}-inter-city-drive`
        });

        // For overnight bus/drive journeys, add journey note
        if (arrivalDayOffset > 0) {
          const modeEmoji = transportMode === 'bus' ? '🌙' : '🌙';
          const modeLabel = transportMode === 'bus' ? 'Bus' : 'Drive';
          timeline.push({
            type: 'overnight-journey',
            time: departureTime,
            activity: `${modeEmoji} Overnight ${modeLabel} Journey`,
            description: `Arriving at ${currentCity} tomorrow after the overnight ${modeLabel.toLowerCase()}. No hotel tonight — sleeping on the ${transportMode === 'bus' ? 'bus' : 'road'}!`,
            location: `En route to ${currentCity}`,
            duration: travelDuration,
            sortTime: departureMinutes + 1,
            isOvernightJourney: true
          });
        }
      }
      
      // For flights only: Check if this is an international flight and add customs
      // IMPORTANT: Only add customs/transit on the DEPARTURE day if the flight arrives SAME day.
      // For overnight flights (arrivalDayOffset > 0), customs/transit/check-in are handled
      // on the ARRIVAL day by the overnight arrival processing code (see isArrivalDayAfterOvernightTravel).
      if (transportMode === 'flight' && arrivalDayOffset <= 0) {
        // Check if this is an international flight (different countries)
        // Uses getCityCountrySync which checks both static CITY_TO_COUNTRY table
        // and dynamic Haiku cache (populated by preResolveCities)
        const prevCountry = getCityCountrySync(prevCity);
        const currentCountry = getCityCountrySync(currentCity);

        // Determine if this is an international flight
        // 1. If both countries are known and different, it's international
        // 2. For multi-country itineraries with unknown cities, assume international when cities change
        let isInternationalFlight = prevCountry && currentCountry && prevCountry !== currentCountry;

        // Fallback: Check if itinerary indicates this is a multi-country trip
        // and we couldn't resolve both cities (getCityCountrySync covers all regions via Haiku cache)
        if (!isInternationalFlight && itinerary?.countriesIncluded?.length > 1) {
          // If one or both cities are unresolved but the trip spans multiple countries,
          // treat it as international when cities are clearly different
          if ((!prevCountry || !currentCountry) && prevCity?.toLowerCase() !== currentCity?.toLowerCase()) {
            isInternationalFlight = true;
          }
        }

        // Add customs/immigration for international flights
        // Skip on departure day for overnight flights — arrival cards belong on the next day
        if (isInternationalFlight && arrivalDayOffset <= 0) {
          const customsMinutes = arrivalMinutes + 15; // 15 min after landing
          const customsHours = Math.floor(customsMinutes / 60);
          const customsMins = customsMinutes % 60;

          // Check if this is Schengen-to-Schengen (no passport control)
          const isSchengenToSchengen = prevCountry && currentCountry &&
            SCHENGEN_COUNTRIES.includes(prevCountry) && SCHENGEN_COUNTRIES.includes(currentCountry);

          // Get visa info for destination country
          let visaNote = '';
          if (currentCountry) {
            if (VISA_REQUIREMENTS_US.eVisa.includes(currentCountry)) {
              visaNote = ` ⚠️ E-Visa required for ${currentCountry} - apply online before departure.`;
            } else if (VISA_REQUIREMENTS_US.visaRequired.includes(currentCountry)) {
              visaNote = ` ⚠️ Visa required for ${currentCountry} - apply at embassy before travel.`;
            }
          }

          // Get border crossing note
          let borderNote = '';
          if (isSchengenToSchengen) {
            borderNote = ' No passport control (Schengen area).';
          } else if ((prevCountry === 'UK' && SCHENGEN_COUNTRIES.includes(currentCountry)) ||
                     (currentCountry === 'UK' && SCHENGEN_COUNTRIES.includes(prevCountry))) {
            borderNote = ' Passport control required (UK is outside Schengen).';
          }

          // Get currency info
          const destCurrency = COUNTRY_TO_CURRENCY[currentCountry];
          const currencyNote = destCurrency ? ` 💱 Local currency: ${destCurrency}.` : '';

          // FIX: For timezone changes where arrival time appears "earlier" than departure,
          // we need to ensure post-arrival events sort AFTER the flight departure.
          // This happens when flying west-to-east (e.g., Tokyo 9am -> Dubai 7am due to timezone).
          // The sortTime should be: departure time + small offset to ensure correct order.
          const arrivalSortTimeBase = arrivalMinutes < departureMinutes
            ? departureMinutes + 1  // Arrival appears after departure in timeline
            : arrivalMinutes;
          const customsSortTime = arrivalSortTimeBase + 15;

          // Only show customs card if there's actual passport control
          if (!isSchengenToSchengen) {
            // Add timezone indicator for significant timezone changes
            const tzIndicator = timezoneDiff !== 0 ? ` 🕐 ${currentCity} time` : '';

            timeline.push({
              type: 'arrival-customs',
              time: `${String(customsHours).padStart(2, '0')}:${String(customsMins).padStart(2, '0')}`,
              activity: '🛂 Customs & Immigration',
              description: `International arrival processing at ${toAirport}. Have passport and any required documents ready.${visaNote}${borderNote}${currencyNote}${tzIndicator}`,
              location: toAirport,
              duration: '45 minutes',
              sortTime: customsSortTime,
              visaRequired: VISA_REQUIREMENTS_US.eVisa.includes(currentCountry) || VISA_REQUIREMENTS_US.visaRequired.includes(currentCountry),
              currency: destCurrency,
              isNewTimezone: timezoneDiff !== 0,
              timezoneCity: currentCity
            });
          }
        }

        // Add transit from airport to hotel (skip for overnight — arrival cards on next day)
        if (arrivalDayOffset <= 0) {
        // Allow more time after international flights (customs adds ~45 min)
        const transitFromAirportMinutes = isInternationalFlight
          ? arrivalMinutes + 60  // 1 hour after landing for international (includes customs)
          : arrivalMinutes + 30; // 30 min after landing for domestic
        const transitFromAirportHours = Math.floor(transitFromAirportMinutes / 60);
        const transitFromAirportMins = transitFromAirportMinutes % 60;

        // FIX: Use adjusted sortTime for timezone changes where arrival appears earlier than departure
        const transitSortTimeBase = arrivalMinutes < departureMinutes
          ? departureMinutes + 1
          : arrivalMinutes;
        const transitFromAirportSortTime = isInternationalFlight
          ? transitSortTimeBase + 60
          : transitSortTimeBase + 30;

        // Add timezone indicator for transit if there was a timezone change
        const transitTzNote = timezoneDiff !== 0 ? ` (${currentCity} local time)` : '';

        // Smart transit duration for airport-to-hotel based on destination
        // Use cached Google Maps data for airport-to-hotel transit if available
        const fromAirportTransitKey = `day${dayIndex}-from-arrival-airport`;
        const cachedFromAirport = transitTimes[fromAirportTransitKey] || {};
        const fromAirportMode = transportModes[fromAirportTransitKey] || 'driving';
        const cachedFromAirportData = cachedFromAirport[fromAirportMode];

        let arrivalTransitMins = 45; // Default
        let arrivalTransitDisplay = '~45 min';
        let arrivalTransitDescExtra = '';

        if (cachedFromAirportData?.durationMinutes) {
          arrivalTransitMins = cachedFromAirportData.durationMinutes;
          arrivalTransitDisplay = cachedFromAirportData.duration || `~${Math.round(arrivalTransitMins / 60 * 10) / 10} hrs`;
          if (arrivalTransitMins > 90) {
            arrivalTransitDescExtra = `Long transfer (${arrivalTransitDisplay}) - `;
          }
          debugLog(`📍 Using cached Google Maps transit: ${toAirport} → ${dayHotelName} = ${arrivalTransitDisplay} (${arrivalTransitMins} mins)`);
        } else {
          debugLog(`📍 No cached transit data for ${fromAirportTransitKey}, using default ${arrivalTransitMins} min`);
        }

        // ISSUE 7 FIX: Use resort/property name in transit label for resort trips
        const transitToLabel = isResortProperty && dayHotelName && dayHotelName !== 'Hotel'
          ? `🚕 Transit to ${dayHotelName}`
          : '🚕 Transit to Hotel';
        const arrivalTransitNote = `${arrivalTransitDescExtra}Transfer from ${toAirport} to ${isResortProperty && dayHotelName !== 'Hotel' ? dayHotelName : 'your hotel'}${transitTzNote}`;

        timeline.push({
          type: 'airport-transit',
          time: `${String(transitFromAirportHours).padStart(2, '0')}:${String(transitFromAirportMins).padStart(2, '0')}`,
          activity: transitToLabel,
          description: arrivalTransitNote,
          location: `${toAirport} → ${dayHotelName}`,
          from: toAirport,
          to: dayHotelName,
          duration: arrivalTransitDisplay,
          sortTime: transitFromAirportSortTime,
          transitKey: fromAirportTransitKey,
          isNewTimezone: timezoneDiff !== 0,
          timezoneCity: currentCity
        });
        } // End of arrivalDayOffset <= 0 guard
      } // End of flight block
      
      // Add hotel check-in at new city (for all transport modes)
      // BUT NOT on the last day - user is leaving, not checking in!
      // AND NOT if the flight arrives next day (overnight/red-eye) - check-in happens on arrival day
      // AND NOT if a check-in already exists for this day (prevent duplicates)
      const isOvernightArrival = arrivalDayOffset > 0;
      const hasExistingCheckin = timeline.some(item => item.type === 'hotel-checkin');

      if (!isLastDay && !isOvernightArrival && !hasExistingCheckin) {
        // Use cached Google Maps data for airport-to-hotel transit to compute dynamic check-in time
        let dynamicArrivalTransitMins = 45; // Default
        if (transportMode === 'flight') {
          const fromAirportTransitKeyForCheckin = `day${dayIndex}-from-arrival-airport`;
          const cachedFromAirportForCheckin = transitTimes[fromAirportTransitKeyForCheckin] || {};
          const fromAirportModeForCheckin = transportModes[fromAirportTransitKeyForCheckin] || 'driving';
          const cachedFromAirportDataForCheckin = cachedFromAirportForCheckin[fromAirportModeForCheckin];

          if (cachedFromAirportDataForCheckin?.durationMinutes) {
            dynamicArrivalTransitMins = cachedFromAirportDataForCheckin.durationMinutes;
            debugLog(`📍 Dynamic check-in: ${toAirport} → ${dayHotelName} transit = ${dynamicArrivalTransitMins} min`);
          } else {
            debugLog(`📍 No cached transit for check-in calc, using default ${dynamicArrivalTransitMins} min`);
          }
        }
        // Check-in time: landing + customs (international) + transit + settle-in buffer
        const arrivalCountryPrev = CITY_TO_COUNTRY[prevCity?.toLowerCase()] || '';
        const arrivalCountryCurr = CITY_TO_COUNTRY[currentCity?.toLowerCase()] || '';
        const isInternationalArrival = arrivalCountryPrev && arrivalCountryCurr
          ? arrivalCountryPrev !== arrivalCountryCurr
          : prevCity?.toLowerCase() !== currentCity?.toLowerCase();
        const customsBufferMins = isInternationalArrival ? 60 : 15; // 60 min customs+baggage intl, 15 min domestic
        const checkinMinutes = transportMode === 'flight'
          ? arrivalMinutes + customsBufferMins + dynamicArrivalTransitMins + 30 // customs + transit + settle-in
          : transportMode === 'train'
            ? arrivalMinutes + 45 // 45 min after train arrival (already have station transit)
            : departureMinutes + parseDurationToMinutesTravel(travelDuration, prevCity, currentCity) + 15; // After drive + 15min buffer
        const checkinHours = Math.floor(checkinMinutes / 60);
        const checkinMins = checkinMinutes % 60;

        // FIX: Use adjusted sortTime for timezone changes where arrival appears earlier than departure
        const checkinSortTimeBase = transportMode === 'flight' && arrivalMinutes < departureMinutes
          ? departureMinutes + 1
          : (transportMode === 'flight' ? arrivalMinutes : checkinMinutes);
        const checkinSortTime = transportMode === 'flight'
          ? checkinSortTimeBase + 75
          : checkinMinutes;

        // Add timezone indicator for check-in if there was a timezone change (flights only)
        const checkinTzNote = transportMode === 'flight' && timezoneDiff !== 0 ? ` (${currentCity} local time)` : '';

        if (isDayTrip) {
          // Day trip — no hotel check-in at destination, add "Return" card instead
          const returnTime = Math.max(arrivalMinutes + parseDurationToMinutes(travelDuration || '2h'), 1080); // At least 6pm
          const returnHours = Math.floor((returnTime % 1440) / 60);
          const returnMins = (returnTime % 1440) % 60;
          const prevBaseCity = itinerary?.days?.[dayIndex - 1]?.baseCity || 'base city';
          timeline.push({
            type: 'day-trip-return',
            time: `${String(returnHours).padStart(2, '0')}:${String(returnMins).padStart(2, '0')}`,
            activity: `🔙 Return to ${prevBaseCity}`,
            description: `Head back to ${prevHotel?.name || prevBaseCity} after your day trip to ${currentCity}`,
            location: `${currentCity} → ${prevBaseCity}`,
            duration: travelDuration || '~2h',
            sortTime: returnTime
          });
          postCheckinCutoffStart = null; // Day trip — no separate post-checkin window
        } else {
          timeline.push({
            type: 'hotel-checkin',
            time: `${String(checkinHours).padStart(2, '0')}:${String(checkinMins).padStart(2, '0')}`,
            activity: checkinLabel,
            description: `Check in to ${dayHotelName}, get settled${checkinTzNote}`,
            location: dayHotelName,
            duration: '30 minutes',
            sortTime: checkinSortTime,
            isNewTimezone: transportMode === 'flight' && timezoneDiff !== 0,
            timezoneCity: currentCity
          });
        }

        // Set cutoff so afternoon activities don't start before check-in complete
        // But keep cutoffStartTime at 0 to allow morning activities before checkout
        // BUG 5 FIX: Cap at 22:00 (1320 mins) so late arrivals still have an evening activity window
        if (!isDayTrip) postCheckinCutoffStart = Math.min(checkinMinutes + 30, 1320);
        // Don't override cutoffStartTime - we want to allow pre-checkout activities
        debugLog(`📍 Inter-city Day ${dayIndex + 1}: Check-in at ${checkinHours}:${String(checkinMins).padStart(2, '0')}, postCheckinCutoffStart=${postCheckinCutoffStart} mins, preCheckoutCutoffEnd=${preCheckoutCutoffEnd} mins`);
      } else if (isOvernightArrival) {
        debugLog(`🌙 Day ${dayIndex + 1}: Overnight flight - hotel check-in will be on arrival day (Day ${dayIndex + 2})`);
        // Fix 5: Removed duplicate 'overnight-note' card. The 'overnight-journey' card
        // at line ~11067 (inside the inter-city transport block) already provides this info.
        // Keep only the critical activity-blocking logic below.

        // CRITICAL: Set postCheckinCutoffStart to VERY HIGH so NO activities are allowed after the flight
        // This prevents the LLM-generated activities (which are for the DESTINATION city) from appearing
        // on the DEPARTURE day. The user spends this day in the OLD city until departure, then sleeps on plane.
        postCheckinCutoffStart = 9999; // No post-checkin activities allowed on overnight departure day
        debugLog(`🌙 Day ${dayIndex + 1}: Overnight flight - blocking all post-departure activities (postCheckinCutoffStart=${postCheckinCutoffStart})`);
      }

      // DEFENSIVE: Verify an inter-city-travel card was actually added to the timeline.
      // If not (e.g., AI forgot interCityTransport entry and smart detection failed),
      // inject a fallback transport card so the user isn't confused by a missing flight/train.
      const hasTransportCard = timeline.some(item => item.type === 'inter-city-travel');
      if (!hasTransportCard) {
        debugWarn(`⚠️ Day ${dayIndex + 1}: Inter-city travel day but no transport card found! Adding fallback for ${prevCity} → ${currentCity}`);

        const fallbackMode = getSmartTransportMode(prevCity, currentCity);
        const fallbackDuration = fallbackMode === 'flight'
          ? getSmartFlightDuration(prevCity, currentCity)
          : fallbackMode === 'train' ? '~3h' : '~4h';

        timeline.push({
          type: 'inter-city-travel',
          time: '09:00',
          activity: fallbackMode === 'flight'
            ? `✈️ Flight to ${currentCity}`
            : fallbackMode === 'train'
            ? `🚆 Train to ${currentCity}`
            : `🚗 Drive to ${currentCity}`,
          description: `Travel from ${prevCity} to ${currentCity}. ${fallbackDuration}.`,
          duration: fallbackDuration,
          location: `${prevCity} → ${currentCity}`,
          from: prevCity,
          to: currentCity,
          sortTime: 540, // 9:00 AM
          isTravel: true,
          travelMode: fallbackMode,
          isFallbackTransport: true
        });
      }
    }

    // Debug: Log final cutoff times
    debugLog(`⏰ Day ${dayIndex + 1} cutoff times: start=${cutoffStartTime} (${Math.floor(cutoffStartTime/60)}:${String(cutoffStartTime%60).padStart(2,'0')}), end=${cutoffEndTime} (${Math.floor(cutoffEndTime/60)}:${String(cutoffEndTime%60).padStart(2,'0')})`);

    // Get the current day's city for filtering
    const currentDayCity = dayData?.baseCity?.toLowerCase() || '';
    const prevDayCity = dayIndex > 0 ? (itinerary?.days?.[dayIndex - 1]?.baseCity?.toLowerCase() || '') : '';
    
    // Collect all activities and meals
    const allItems = [];
    
    // Activity types to filter out on Day 1 (we add these ourselves)
    const arrivalActivityPatterns = [
      'arrival', 'immigration', 'customs', 'landing', 'check-in to hotel',
      'hotel check-in', 'transit to hotel', 'transfer to hotel'
    ];
    
    // Activity types to filter out on Last Day (we add these ourselves)
    const departureActivityPatterns = [
      'transit to airport', 'transfer to airport', 'airport departure',
      'hotel checkout', 'check out', 'check-out', 'departure'
    ];
    
    // BUG 4 FIX: Inter-city travel activity patterns — use regex word boundaries
    // to catch "Flight Auckland To Queenstown" where city name is between keywords.
    // Flight/fly patterns use broad .* since flights are always inter-city.
    // Drive/bus/train/transfer/travel patterns keep contiguous match to avoid
    // false positives on local activities like "Drive to the beach".
    const interCityTravelPatterns = [
      /\bflight\b.*\bto\b/i, /\bfly\b.*\bto\b/i,       // Flights: broad match (always inter-city)
      /\bflight\s+from\b/i, /\bfly\s+from\b/i,           // "Flight from X"
      /\btransfer\s+to\b/i, /\btravel\s+to\b/i,          // Contiguous only (avoid local activity FPs)
      /\bdrive\s+to\b/i, /\bdriving\s+to\b/i,            // Driving
      /\bbus\s+to\b/i, /\btrain\s+to\b/i,                // Surface transport
      /\bferry\s+to\b/i, /\bboat\s+to\b/i,               // Water transport (from shared)
      /\bdepart\s+for\b/i, /\bhead\s+to\s+airport\b/i,
      /\broad\s+trip\b/i, /\ben\s+route\b/i, /\bjourney\s+to\b/i, // From shared
    ];
    
    // Helper to check if activity location is in wrong city
    const isWrongCityActivity = (item) => {
      if (!isInterCityTravelDay || !prevDayCity || !currentDayCity) return false;
      
      const location = (item.location || item.address || '').toLowerCase();
      const activity = (item.activity || '').toLowerCase();
      
      // Check if location mentions the previous city (wrong city for this day)
      if (location.includes(prevDayCity) || activity.includes(prevDayCity)) {
        // But make sure it's not just a "Flight FROM prevCity" description
        if (!activity.includes('flight') && !activity.includes('travel')) {
          debugLog(`🚫 Filtering wrong-city activity: ${item.activity} (${prevDayCity} activity on ${currentDayCity} day)`);
          return true;
        }
      }
      
      // Check for common Cappadocia/Göreme locations on non-Cappadocia days
      const cappadociaIndicators = ['göreme', 'goreme', 'cappadocia', 'kapadokya', 'uchisar', 'urgup', 'avanos', 'derinkuyu', 'ihlara'];
      if (!currentDayCity.includes('cappadocia') && !currentDayCity.includes('göreme') && !currentDayCity.includes('goreme')) {
        for (const indicator of cappadociaIndicators) {
          if (location.includes(indicator) || activity.includes(indicator)) {
            debugLog(`🚫 Filtering Cappadocia activity on non-Cappadocia day: ${item.activity}`);
            return true;
          }
        }
      }
      
      return false;
    };

    // NOTE: isUnrealisticDayTrip and isMismatchedCity filters are now handled by normalizeDay (Stages 5-6)

    // Cap activities on travel days to prevent overpacked schedules
    let travelDayActivityCount = 0;
    const TRAVEL_DAY_ACTIVITY_CAP = 4;

    // Dynamic departure day cap based on available hours before cutoff
    let departureDayCap = 2;
    if (isLastDay) {
      const dayStartMin = parseTime(userProfile?.preferences?.preferredStartTime || '09:00') || 540;
      const availableHours = Math.max(0, (cutoffEndTime - dayStartMin) / 60);
      if (availableHours < 2) departureDayCap = 1;
      else if (availableHours < 4) departureDayCap = 2;
      else if (availableHours < 6) departureDayCap = 3;
      else departureDayCap = 4;
    }

    const effectiveActivityCap = isLastDay ? departureDayCap : TRAVEL_DAY_ACTIVITY_CAP;
    const isTravelCapDay = isInterCityTravelDay || isFirstDay || isLastDay;

    // D5: Clean overnight arrival day — strip AI departure logistics, pre-11 AM items, post-9 PM items
    if (isArrivalDayAfterOvernightTravel && dayData.schedule) {
      const overnightCleaned = cleanOvernightArrivalDay(dayData.schedule, dayData.meals, { isOvernightArrival: true });
      dayData.schedule = overnightCleaned.schedule;
      dayData.meals = overnightCleaned.meals;
      debugLog(`🌅 D5: Cleaned overnight arrival day ${dayIndex + 1}: ${overnightCleaned.schedule.length} schedule items, ${overnightCleaned.meals.length} meals remain`);
    }

    if (dayData.schedule) {
      dayData.schedule.forEach((item, index) => {
        // NOTE: AM/PM time correction, suspicious time filtering, and geographic validation
        // are now handled by normalizeDay() before data enters state.
        // This render layer only handles cutoff windows and arrival/departure filtering.
        let itemTime = parseTime(item.time);

        // SAFETY NET: If time falls 1:00-5:59 AM and activity isn't an early-morning one,
        // it's almost certainly a PM time that slipped through normalization
        if (itemTime >= 60 && itemTime < 360) {
          const actLower = (item.activity || '').toLowerCase();
          const earlyKeywords = ['sunrise', 'dawn', 'early morning', 'airport', 'flight', 'departure', 'checkout', 'check-out', 'game drive'];
          const isEarlyMorning = earlyKeywords.some(kw => actLower.includes(kw));
          if (!isEarlyMorning) {
            itemTime += 12 * 60; // Shift to PM
            item.time = `${String(Math.floor(itemTime / 60)).padStart(2, '0')}:${String(itemTime % 60).padStart(2, '0')}`;
            timelineLog(`  ⚠️ AM→PM safety fix: "${item.activity}" at ${item.time} (was ${itemTime - 720} mins)`);
          }
        }

        // For inter-city travel days: allow activities either before checkout OR after check-in
        if (isInterCityTravelDay && preCheckoutCutoffEnd !== null && postCheckinCutoffStart !== null) {
          const isPreCheckoutActivity = itemTime >= cutoffStartTime && itemTime < preCheckoutCutoffEnd;
          const isPostCheckinActivity = itemTime >= postCheckinCutoffStart && itemTime < cutoffEndTime;
          if (!isPreCheckoutActivity && !isPostCheckinActivity) {
            debugLog(`🚫 Filtering activity outside travel day windows: ${item.activity} at ${item.time} (${itemTime} mins)`);
            return;
          }
        } else {
          // Normal cutoff logic for non-travel days
          if (itemTime < cutoffStartTime || itemTime >= cutoffEndTime) return;

          // On last day, also filter activities that would END after checkout
          // This prevents activities from overlapping with airport transit
          if (isLastDay && cutoffEndTime < 24 * 60) {
            // Parse activity duration to calculate end time
            const durationStr = item.duration || '';
            let durationMinutes = 60; // Default 1 hour
            const hourMatch = durationStr.match(/(\d+\.?\d*)\s*(?:h|hour)/i);
            const minMatch = durationStr.match(/(\d+)\s*(?:m|min)/i);
            if (hourMatch) durationMinutes = parseFloat(hourMatch[1]) * 60;
            if (minMatch) durationMinutes += parseInt(minMatch[1]);

            const activityEndTime = itemTime + durationMinutes;
            if (activityEndTime > cutoffEndTime) {
              debugLog(`🚫 Filtering last day activity that would end after checkout: ${item.activity} ends at ${Math.floor(activityEndTime/60)}:${String(activityEndTime%60).padStart(2,'0')} (checkout at ${Math.floor(cutoffEndTime/60)}:${String(cutoffEndTime%60).padStart(2,'0')})`);
              return;
            }
          }
        }

        const activityLower = (item.activity || '').toLowerCase();

        // On Day 1, filter out AI-generated arrival/airport activities
        if (isFirstDay) {
          const isArrivalActivity = arrivalActivityPatterns.some(pattern =>
            activityLower.includes(pattern)
          );
          if (isArrivalActivity) {
            debugLog(`🚫 Filtering out Day 1 arrival activity: ${item.activity}`);
            return; // Skip this activity
          }
        }

        // On Last Day, filter out AI-generated departure activities and filler activities
        if (isLastDay) {
          const isDepartureActivity = departureActivityPatterns.some(pattern =>
            activityLower.includes(pattern)
          );
          if (isDepartureActivity) {
            debugLog(`🚫 Filtering out Last Day departure activity: ${item.activity}`);
            return; // Skip this activity
          }
          // Filter filler activities (pack bags, travel journaling, etc.)
          const FILLER_PATTERNS = [
            'travel journaling', 'pack luggage', 'pack bags', 'packing',
            'rest at hotel', 'relax at hotel', 'hotel rest', 'nap at hotel',
            'free time at hotel', 'settle in', 'freshen up',
          ];
          if (FILLER_PATTERNS.some(p => activityLower.includes(p))) {
            debugLog(`🚫 Filtering out Last Day filler activity: ${item.activity}`);
            return;
          }
        }

        // On inter-city travel days, filter out travel activities (we add our own) and wrong city activities
        if (isInterCityTravelDay) {
          // BUG 4 FIX: Filter out inter-city travel activities using regex matching
          const isTravelActivity = interCityTravelPatterns.some(pattern =>
            pattern.test(activityLower)
          );
          if (isTravelActivity) {
            debugLog(`🚫 Filtering out travel activity: ${item.activity}`);
            return;
          }

          // For pre-checkout activities, skip wrong-city filtering (they're in the OLD city)
          // For post-checkin activities, apply wrong-city filtering (they should be in NEW city)
          if (itemTime >= postCheckinCutoffStart && isWrongCityActivity(item)) {
            return;
          }
        }

        // NOTE: Unrealistic day trip and mismatched city filtering now handled by normalizeDay() Stage 6.

        // Fix time-of-day mislabeling in activity names (e.g., "Morning Run" scheduled at 12:00 PM)
        let correctedActivity = item.activity;
        if (correctedActivity && itemTime !== undefined) {
          const hour = Math.floor(itemTime / 60);
          if (hour >= 12 && /\bmorning\b/i.test(correctedActivity)) {
            correctedActivity = correctedActivity.replace(/\bmorning\b/gi, hour < 17 ? 'Afternoon' : 'Evening');
          } else if (hour < 12 && /\bevening\b/i.test(correctedActivity)) {
            correctedActivity = correctedActivity.replace(/\bevening\b/gi, 'Morning');
          } else if ((hour < 12 || hour >= 17) && /\bafternoon\b/i.test(correctedActivity)) {
            correctedActivity = correctedActivity.replace(/\bafternoon\b/gi, hour < 12 ? 'Morning' : 'Evening');
          }
        }

        // Cap activities on travel/arrival/departure days to prevent overpacked schedules
        if (isTravelCapDay) {
          travelDayActivityCount++;
          if (travelDayActivityCount > effectiveActivityCap) {
            debugLog(`🚫 Capping travel day activity #${travelDayActivityCount}: ${item.activity} (max ${effectiveActivityCap})`);
            return;
          }
        }

        allItems.push({
          ...item,
          activity: correctedActivity || item.activity,
          time: item.time,
          type: 'activity',
          originalIndex: index,
          sortTime: itemTime
        });
      });
    }

    if (dayData.meals) {
      timelineLog(`🍽️ Day ${dayIndex + 1}: Processing ${dayData.meals.length} meals (cutoff: ${cutoffStartTime}-${cutoffEndTime})`);
      dayData.meals.forEach((item, index) => {
        // Times are already normalized by itineraryNormalizer (Stage 1-2: field completeness + time normalization)
        let itemTime = parseTime(item.time);

        // SAFETY NET: If meal time falls 1:00-5:59 AM, fix it
        if (itemTime >= 60 && itemTime < 360) {
          const mealLower = (item.meal || item.type || '').toLowerCase();
          if (mealLower.includes('breakfast')) {
            // Breakfast at 1-5 AM is wrong — clamp to 7:30 AM
            itemTime = 7 * 60 + 30;
            item.time = '07:30';
            timelineLog(`  ⚠️ Breakfast time fix: "${item.restaurant || item.meal}" clamped to ${item.time}`);
          } else {
            itemTime += 12 * 60;
            item.time = `${String(Math.floor(itemTime / 60)).padStart(2, '0')}:${String(itemTime % 60).padStart(2, '0')}`;
            timelineLog(`  ⚠️ AM→PM safety fix (meal): "${item.restaurant || item.meal}" shifted to ${item.time}`);
          }
        }

        const effectiveTime = itemTime || 720; // Fallback to noon if still missing (shouldn't happen after normalization)

        // For inter-city travel days: allow meals either before checkout OR after check-in
        let isInAllowedWindow = false;
        if (isInterCityTravelDay && preCheckoutCutoffEnd !== null && postCheckinCutoffStart !== null) {
          const isPreCheckoutMeal = effectiveTime >= cutoffStartTime && effectiveTime < preCheckoutCutoffEnd;
          const isPostCheckinMeal = effectiveTime >= postCheckinCutoffStart && effectiveTime < cutoffEndTime;
          isInAllowedWindow = isPreCheckoutMeal || isPostCheckinMeal;
          timelineLog(`  Meal ${index}: "${item.meal}" at ${item.time} (${effectiveTime} mins), pre-checkout: ${isPreCheckoutMeal}, post-checkin: ${isPostCheckinMeal}`);
        } else {
          isInAllowedWindow = effectiveTime >= cutoffStartTime && effectiveTime < cutoffEndTime;

          // On last day, also check if meal would END after checkout (meals typically 1 hour)
          if (isLastDay && cutoffEndTime < 24 * 60) {
            const mealEndTime = effectiveTime + 60; // Meals are typically 1 hour
            if (mealEndTime > cutoffEndTime) {
              isInAllowedWindow = false;
              debugLog(`  Meal ${index}: "${item.meal}" at ${item.time} would end after checkout - filtering`);
            }
          }

          timelineLog(`  Meal ${index}: "${item.meal}" at ${item.time} (${effectiveTime} mins), cutoff check: ${isInAllowedWindow}`);
        }

        if (isInAllowedWindow) {
          // In-flight meals, airport meals, and cross-city meals are already filtered by normalizeDay (Stages 6-7)

          // On inter-city travel days, filter out post-checkin meals from wrong city
          // This depends on render-time inter-city transport state so must remain here
          if (isInterCityTravelDay && postCheckinCutoffStart !== null && itemTime >= postCheckinCutoffStart) {
            if (isWrongCityActivity({
              activity: item.restaurant,
              location: item.address || item.restaurant
            })) {
              debugLog(`🚫 Filtering wrong-city meal: ${item.restaurant}`);
              return;
            }
          }

          // Times are already normalized — use item.time directly
          // Preserve meal type: AI schema uses `type: "breakfast"` but timeline needs `type: "meal"`.
          // Store original meal type in MULTIPLE fields so hasMealType() can always detect it.
          const mealTypeLabel = item.meal || item.type || item.mealType || '';
          allItems.push({
            ...item,
            type: 'meal',
            meal: mealTypeLabel, // e.g., "Breakfast", "Lunch", "Dinner" — needed by hasMealType()
            mealType: (item.mealType || item.type || '').toLowerCase(), // lowercase: "breakfast", "lunch", "dinner"
            originalType: (item.type || '').toLowerCase(), // preserve LLM's original type before overwrite
            originalIndex: index,
            sortTime: effectiveTime
          });
        }
      });
    } else {
      debugLog(`⚠️ Day ${dayIndex + 1}: No meals array found in dayData`);
    }
    
    // BUG 5 FIX: If all activities were filtered on an inter-city travel day (late arrival),
    // add a fallback evening-at-hotel activity so the day isn't empty.
    // Skip on departure day (isLastDay) — empty activity list is expected, departure logistics handle it.
    if (allItems.length === 0 && isInterCityTravelDay && !isLastDay && !isOvernightDepartureDay && dayHotelName) {
      const fallbackTime = postCheckinCutoffStart ? Math.max(postCheckinCutoffStart, 1200) : 1200;
      const fallbackHours = Math.floor(fallbackTime / 60);
      const fallbackMins = fallbackTime % 60;
      allItems.push({
        activity: 'Settle In & Explore the Neighborhood',
        description: `Arrive at ${dayHotelName}, freshen up, and take a leisurely walk to explore the area`,
        location: dayHotelName,
        time: `${String(fallbackHours).padStart(2, '0')}:${String(fallbackMins).padStart(2, '0')}`,
        duration: '2 hours',
        type: 'activity',
        originalIndex: 0,
        sortTime: fallbackTime
      });
      debugLog(`⚠️ Day ${dayIndex + 1}: All activities filtered — added fallback evening activity`);
    }

    // timelineSortComparator and TIMELINE_TYPE_PRIORITY now imported from shared displayGuardrails

    // Sort items by time
    allItems.sort(timelineSortComparator);

    // Debug: Log collected items (verbose only — fires 20+ times per render)
    timelineLog(`📋 Day ${dayIndex + 1}: Collected ${allItems.length} items (activities + meals)`);
    allItems.forEach((item, i) => {
      const itemName = item.activity || item.restaurant || item.meal || 'Unknown';
      timelineLog(`  ${i}: ${item.type} - "${itemName}" at ${item.time} (${item.sortTime} mins)`);
    });

    // Add transit from hotel to first activity (for ALL days, not just first day)
    // NOTE: This is the transit FROM hotel TO first activity - needed even after check-in
    // The only exception is if first activity is at the hotel itself (handled below)
    // ALSO skip if inter-city travel already injected a transit-to-hotel card (avoids double transit)
    const hasExistingTransitToHotel = isInterCityTravelDay && timeline.some(
      item => (item.type === 'airport-transit' || item.type === 'station-transit') &&
              (item.activity?.includes('Transit to Hotel') || item.to?.toLowerCase().includes(dayHotelName.toLowerCase()))
    );
    if (allItems.length > 0 && !hasExistingTransitToHotel) {
      const firstItem = allItems[0];
      const firstLocation = firstItem.location || firstItem.restaurant || '';
      const firstActivity = (firstItem.activity || firstItem.meal || '').toLowerCase();
      const firstRestaurant = (firstItem.restaurant || '').toLowerCase();

      // For morning activities, check against PREVIOUS night's hotel (where user slept)
      // For other activities, use current day's hotel
      const firstItemHour = parseInt((firstItem.time || '9:00').split(':')[0]);
      const isMorningActivity = firstItemHour < 12;
      const hotelToCheck = (isMorningActivity && !isFirstDay) ? prevDayHotelName : dayHotelName;
      const hotelLower = hotelToCheck.toLowerCase();

      // Check if first activity is at the hotel (breakfast, hotel restaurant, etc)
      const isAtHotel = (location, activity, restaurant, address) => {
        return isHotelActivityValidator(location, activity, restaurant, hotelToCheck, dayData?.baseCity || '', address || '');
      };

      // Only add transit if first activity isn't at the hotel
      const firstAddress = firstItem.address || firstItem.location || '';
      if (firstLocation && firstLocation !== hotelToCheck && !isAtHotel(firstLocation, firstActivity, firstRestaurant, firstAddress)) {
        const transitKey = `day${dayIndex}-hotel-to-first`;
        const selectedMode = transportModes[transitKey] || 'driving';
        const cachedTransitData = transitTimes[transitKey] || {};

        timelineLog(`🚗 Transit to first activity: key=${transitKey}, hasCachedData=${Object.keys(cachedTransitData).length > 0}, modes=${Object.keys(cachedTransitData).join(',') || 'none'}`);
        if (Object.keys(cachedTransitData).length > 0) {
          timelineLog(`   Cached driving: ${cachedTransitData.driving?.duration || 'N/A'}`);
          timelineLog(`   Cached transit: ${cachedTransitData.transit?.duration || 'N/A'}`);
          timelineLog(`   Cached walking: ${cachedTransitData.walking?.duration || 'N/A'}`);
        }

        // Get actual transit duration from Google Maps API cache, fallback to 30 mins
        const getTransitDurationMinutes = () => {
          const modeData = cachedTransitData[selectedMode] || cachedTransitData.driving || cachedTransitData.transit;
          // SYSTEMIC FIX: Only use API duration if it's a valid positive number
          if (modeData?.durationMinutes && modeData.durationMinutes > 0) {
            return modeData.durationMinutes;
          }
          if (modeData?.duration) {
            // Parse duration string like "25 mins" or "1 hr 10 mins"
            const duration = modeData.duration;
            // Skip "0 mins" which signals route not found
            if (duration === '0 mins' || duration === '0 min' || duration === 'N/A') {
              return 30; // Use default for unavailable routes
            }
            if (duration.includes('hr') || duration.includes('hour')) {
              const match = duration.match(/(\d+)\s*hr?\s*(\d*)/);
              if (match) {
                const parsed = (parseInt(match[1]) || 0) * 60 + (parseInt(match[2]) || 0);
                if (parsed > 0) return parsed;
              }
            }
            const mins = parseInt(duration);
            if (!isNaN(mins) && mins > 0) return mins;
          }
          return 30; // Default fallback
        };

        const transitDurationMins = getTransitDurationMinutes();

        // For first day, use cutoffStartTime (after hotel check-in)
        // For other days, start transit before first activity based on actual duration
        let transitStartMinutes;
        if (isFirstDay) {
          transitStartMinutes = cutoffStartTime;
        } else {
          // Start transit before first activity, using actual duration + 5 min buffer
          transitStartMinutes = firstItem.sortTime - transitDurationMins - 5;
          if (transitStartMinutes < 0) transitStartMinutes = Math.max(0, firstItem.sortTime - 15);
        }

        const transitStartHours = Math.floor(transitStartMinutes / 60);
        const transitStartMins = transitStartMinutes % 60;

        // Use previous night's hotel for morning transit (that's where they slept)
        const transitFromHotel = (isMorningActivity && !isFirstDay) ? prevDayHotelName : dayHotelName;

        // Format duration for display
        const formatDuration = (mins) => {
          if (mins >= 60) {
            const hrs = Math.floor(mins / 60);
            const remainMins = mins % 60;
            return remainMins > 0 ? `${hrs}hr ${remainMins}min` : `${hrs}hr`;
          }
          return `${mins} mins`;
        };

        // SYSTEMIC FIX: Don't display "0 mins" - use formatted duration instead
        const cachedDuration = cachedTransitData[selectedMode]?.duration;
        const displayDuration = (cachedDuration && cachedDuration !== '0 mins' && cachedDuration !== '0 min' && cachedDuration !== 'N/A')
          ? cachedDuration
          : `~${formatDuration(transitDurationMins)}`;

        timeline.push({
          type: 'transit',
          time: `${String(transitStartHours).padStart(2, '0')}:${String(transitStartMins).padStart(2, '0')}`,
          activity: 'Transit',
          from: addLocationContext(transitFromHotel),
          to: addLocationContext(firstLocation),
          duration: displayDuration,
          selectedMode: selectedMode,
          transitKey: transitKey,
          sortTime: transitStartMinutes
        });
      }
    }

    // Add items with transit cards between them
    // IMPORTANT: Use a separate transit counter that matches the useEffect fetch indexing
    // The useEffect fetches transit-0 for first→second activity, transit-1 for second→third, etc.
    // We need to match this indexing exactly for the cached times to display correctly.
    let transitCardCounter = 0;
    debugLog(`🚏 Day ${dayIndex + 1}: Building timeline with ${allItems.length} items`);
    allItems.forEach((item, idx) => {
      // Add the activity/meal
      timeline.push(item);

      // Add transit card to next activity (always, for every item except the last)
      // Skip if current or next item is already a travel/transit type (prevents double transit cards)
      const travelTypes = ['inter-city-travel', 'transit', 'station-transit', 'airport-transit', 'port-transit', 'flight', 'hotel-checkout', 'hotel-checkin', 'overnight-journey', 'overnight-arrival', 'recovery-note', 'arrival-customs'];
      const isCurrentTravel = travelTypes.includes(item.type);
      const nextItem = allItems[idx + 1];
      const isNextTravel = nextItem && travelTypes.includes(nextItem.type);

      debugLog(`  📍 Item ${idx}: ${item.activity || item.restaurant || item.meal} (type=${item.type}), isCurrentTravel=${isCurrentTravel}, hasNext=${!!nextItem}, isNextTravel=${isNextTravel}`);

      if (idx < allItems.length - 1 && !isCurrentTravel && !isNextTravel) {
        const currentLocation = item.location || item.restaurant || item.activity || '';
        const nextLocation = nextItem.location || nextItem.restaurant || nextItem.activity || '';
        const currentActivity = (item.activity || item.meal || '').toLowerCase();
        const nextActivity = (nextItem.activity || nextItem.meal || '').toLowerCase();
        
        // Helper to check if an activity is at the hotel/resort
        const isHotelActivity = (location, activity, restaurant, address) => {
          return isHotelActivityValidator(location, activity, restaurant, dayHotelName, dayData?.baseCity || '', address || '');
        };

        // Skip transit if both activities are at the hotel or same location
        const currentAtHotel = isHotelActivity(currentLocation, currentActivity, item.restaurant, item.address);
        const nextAtHotel = isHotelActivity(nextLocation, nextActivity, nextItem.restaurant, nextItem.address);

        // Normalize location strings for comparison — use ADDRESS for precision, not location name
        // Previously matched by normalized name which caused false positives (e.g., "Shinjuku Gyoen" matched "Shinjuku" hotel)
        const currentAddr = (item.address || '').toLowerCase().trim();
        const nextAddr = (nextItem.address || '').toLowerCase().trim();
        const sameLocation = currentAddr && nextAddr && currentAddr.length > 10 && currentAddr === nextAddr;

        // Skip transit if both activities are at the same hotel/location
        // NOTE: We removed the "sameCity" check because it incorrectly skipped transit
        // between different locations within the same city. For example, "Gornergrat" and
        // "Restaurant Whymper-Stube" are both in Zermatt but require 30+ min travel.
        // Instead, we show transit cards for all location changes and let the real
        // distance data determine the actual travel time.

        // Skip transit when both activities are at the hotel/same location
        // NOTE: Previously had an aggressive "eitherAtHotel" check for resort trips that
        // skipped transit when EITHER activity was at the hotel. This caused false positives
        // when hotel names contain neighborhood words (e.g., "Hotel Fortaleza do Guincho"
        // matched "Praia do Guincho", "Guincho Beach", etc.). Removed in favor of the more
        // precise "both at hotel" check + near-zero transit filter for truly close locations.
        if ((currentAtHotel && nextAtHotel) || sameLocation) {
          debugLog(`  ⏭️ Skipping transit: both at hotel or same location`);
          return;
        }

        // ISSUE 6 FIX: Skip transit cards around yacht/boat/sailing activities
        // These activities ARE the transport themselves (e.g., "Private Yacht to Spetses")
        // Transit before = boarding the yacht (you're already at the port/marina)
        // Transit after = returning from the yacht (you're already back at port/marina or island)
        // NOTE: Must distinguish actual water TRANSPORT ("yacht to Spetses", "boat tour of islands")
        // from venues/landmarks ("dinner at The Yacht Club", "Ferry Building walk")
        const isWaterTransportActivity = (activity) => {
          if (!activity) return false;
          const lower = activity.toLowerCase();
          // Must have a water transport keyword...
          const hasWaterKeyword = /\byacht\b|\bboat\b|\bsailing\b|\bcatamaran\b|\bferry\b|\bspeedboat\b|\bwater\s*taxi\b/.test(lower);
          if (!hasWaterKeyword) return false;
          // ...AND a transport/activity context word (to/from/trip/tour/charter/private/ride/transfer/cruise/excursion)
          // This filters out "The Yacht Club" (restaurant), "Ferry Building" (landmark)
          const hasTransportContext = /\bto\b|\bfrom\b|\btrip\b|\btour\b|\bcharter\b|\bprivate\b|\bride\b|\btransfer\b|\bcruise\b|\bexcursion\b|\baround\b|\bacross\b|\bisland/i.test(lower);
          return hasTransportContext;
        };
        const currentIsWaterTransport = isWaterTransportActivity(currentActivity);
        const nextIsWaterTransport = isWaterTransportActivity(nextActivity);
        if (currentIsWaterTransport || nextIsWaterTransport) {
          debugLog(`  ⏭️ Skipping transit: water transport activity detected (current="${currentActivity}", next="${nextActivity}")`);
          transitCardCounter++;
          return;
        }

        // Skip transit cards around "Drive to X" / "Bus to X" day-trip activities
        // These activities ARE the transport themselves (e.g., "Drive to Sintra" is a 30-min drive).
        // Transit card before it would be redundant (same journey), transit after it is also redundant
        // since the drive activity ends at the destination.
        const isDriveToDayTrip = (activity) => {
          if (!activity) return false;
          const lower = activity.toLowerCase();
          return /^(🚗\s*)?drive\s+to\b/i.test(lower) || /^(🚌\s*)?bus\s+to\b/i.test(lower);
        };
        const currentIsDriveTo = isDriveToDayTrip(currentActivity);
        const nextIsDriveTo = isDriveToDayTrip(nextActivity);
        if (currentIsDriveTo || nextIsDriveTo) {
          debugLog(`  ⏭️ Skipping transit: drive/bus-to day trip activity detected (current="${currentActivity}", next="${nextActivity}")`);
          transitCardCounter++;
          return;
        }

        // BUG 6 FIX: For activities going TO a ferry/boat destination, resolve the transit
        // destination to the ferry terminal instead of the island. The transit card should show
        // "walk to Ferry Terminal" not "drive to Rangitoto Island"
        let resolvedNextLocation = nextLocation;
        let resolvedCurrentLocation = currentLocation;
        const isFerryActivity = (activity) => {
          if (!activity) return false;
          const lower = activity.toLowerCase();
          return /\bferry\b/i.test(lower) && /\bto\b|\bfrom\b|\bcrossing\b|\bride\b/i.test(lower);
        };
        if (isFerryActivity(nextActivity)) {
          // Next activity is a ferry — transit should go to the ferry terminal, not the island
          const ferryCity = dayData?.baseCity || '';
          resolvedNextLocation = ferryCity ? `${ferryCity} Ferry Terminal` : 'Ferry Terminal';
          debugLog(`  🚢 Bug 6: Resolved ferry transit destination: "${nextLocation}" → "${resolvedNextLocation}"`);
        }
        if (isFerryActivity(currentActivity)) {
          // Current activity was a ferry — transit FROM should be from the return terminal
          const ferryCity = dayData?.baseCity || '';
          resolvedCurrentLocation = ferryCity ? `${ferryCity} Ferry Terminal` : 'Ferry Terminal';
          debugLog(`  🚢 Bug 6: Resolved ferry return transit origin: "${currentLocation}" → "${resolvedCurrentLocation}"`);
        }

        // Calculate transit time and end time of current activity
        const currentDurationMins = parseDurationToMinutes(item.duration);

        const transitStartMinutes = item.sortTime + currentDurationMins;

        // NOTE: Previously we skipped transit when gap >= 60 min (assuming free time would be inserted).
        // But this caused missing transit cards — users still need to travel between locations even with free time.
        // Transit cards are now ALWAYS inserted. Free time is added separately in a later pass.
        
        // Check if we have real distance data
        // Use transitCardCounter (not idx) to match useEffect fetch indexing
        const transitKey = `day${dayIndex}-transit-${transitCardCounter}`;
        // Default mode: 'driving' everywhere EXCEPT explicitly car-free islands
        // Uses dynamicGeographyCache (populated by Haiku AI) — noCarAccess=true for places like Santorini, Capri, etc.
        // Large driveable islands (Hawaii, Bali, Sicily) are NOT marked noCarAccess, so they default to driving
        const baseCityLower = dayData?.baseCity?.toLowerCase?.()?.trim?.() || '';
        const geoCache = dynamicGeographyCache[baseCityLower];
        // Only default to walking for explicitly car-free islands (from dynamic Haiku geography cache)
        // Large driveable islands (Hawaii, Bali, Sicily, etc.) will NOT have noCarAccess=true
        // so they correctly default to driving — no hardcoded list needed
        const isExplicitlyCarFree = geoCache?.noCarAccess === true;
        const defaultTransitMode = isExplicitlyCarFree ? 'walking' : 'driving';
        const selectedMode = transportModes[transitKey] || defaultTransitMode;
        const cachedTransitData = transitTimes[transitKey] || {};

        let transitDuration = selectedMode === 'transit' ? 20 : selectedMode === 'walking' ? 15 : 10; // Default
        let distanceInfo = '';

        // First check transitTimes from Google Maps API (most accurate)
        const modeData = cachedTransitData[selectedMode] || cachedTransitData.driving;

        // DEBUG: Log what's in the cached data (verbose only — very high frequency)
        timelineLog(`  🔍 Transit ${transitKey}: mode=${selectedMode}, modeData=`, modeData ? { duration: modeData.duration, durationMinutes: modeData.durationMinutes, distance: modeData.distance } : 'NONE');

        if (modeData?.durationMinutes && modeData.durationMinutes > 0) {
          // Only use API duration if it's a valid positive number
          transitDuration = modeData.durationMinutes;
          if (modeData.distance) distanceInfo = ` • ${modeData.distance}`;
          timelineLog(`  ✅ Using durationMinutes: ${transitDuration}`);
        } else if (modeData?.duration) {
          // Parse duration string like "25 mins" or "1 hr 10 mins"
          const duration = modeData.duration;

          // SYSTEMIC FIX: Skip "0 mins" which signals route not found - keep default instead
          if (duration === '0 mins' || duration === '0 min' || duration === 'N/A') {
            timelineLog(`  ⚠️ Transit ${transitKey}: API returned "${duration}", using default ${transitDuration} mins`);
          } else if (duration.includes('hr') || duration.includes('hour')) {
            const match = duration.match(/(\d+)\s*hr?\s*(\d*)/);
            if (match) {
              const parsedDuration = (parseInt(match[1]) || 0) * 60 + (parseInt(match[2]) || 0);
              if (parsedDuration > 0) transitDuration = parsedDuration;
            }
            if (modeData.distance) distanceInfo = ` • ${modeData.distance}`;
            timelineLog(`  ✅ Parsed hr duration: ${transitDuration}`);
          } else {
            const mins = parseInt(duration);
            if (!isNaN(mins) && mins > 0) transitDuration = mins;
            if (modeData.distance) distanceInfo = ` • ${modeData.distance}`;
            timelineLog(`  ✅ Parsed mins duration: ${transitDuration} from "${duration}"`);
          }
        }
        // Fallback to itinerary.transitData if no Google Maps data
        if (!modeData && itinerary.transitData) {
          // Get location keys - for meals use restaurant, for activities use location
          const fromKey = item.type === 'meal' ? item.restaurant : (item.location || item.restaurant);
          const toKey = nextItem.type === 'meal' ? nextItem.restaurant : (nextItem.location || nextItem.restaurant);

          const realData = findTransitData(itinerary.transitData, fromKey, toKey);
          if (realData) {
            transitDuration = selectedMode === 'transit'
              ? Math.ceil(realData.duration * 1.5) // Transit takes longer
              : selectedMode === 'walking'
              ? Math.ceil(realData.duration * 3) // Walking takes even longer
              : realData.duration;
            distanceInfo = ` • ${realData.distanceText}`;
          }
        }

        // Cap intra-day transit duration at 120 minutes (2 hours)
        // Anything longer is almost certainly a data error (e.g., 599 mins, 762 mins)
        const MAX_INTRA_DAY_TRANSIT = 120;
        if (transitDuration > MAX_INTRA_DAY_TRANSIT) {
          timelineLog(`  ⚠️ Capping transit duration from ${transitDuration}min to ${MAX_INTRA_DAY_TRANSIT}min: ${currentLocation} → ${nextLocation}`);
          transitDuration = MAX_INTRA_DAY_TRANSIT;
        }

        const transitStartHours = Math.floor(transitStartMinutes / 60);
        const transitStartMins = transitStartMinutes % 60;

        // Skip zero-distance or very short transits (same location or < 500m)
        const distanceMeters = (() => {
          const match = distanceInfo.match(/([\d.]+)\s*(km|m)\b/);
          if (!match) return Infinity; // unknown distance, keep it
          const val = parseFloat(match[1]);
          return match[2] === 'km' ? val * 1000 : val;
        })();
        if (distanceMeters < 500 && transitDuration <= 2) {
          timelineLog(`  ⏭️ Skipping near-zero transit: ${currentLocation} → ${nextLocation} (${distanceMeters}m, ${transitDuration}min)`);
          transitCardCounter++;
          return;
        }

        timelineLog(`  ✅ Adding transit card #${transitCardCounter}: ${resolvedCurrentLocation} → ${resolvedNextLocation} (key=${transitKey})`);
        timeline.push({
          type: 'transit',
          time: `${String(transitStartHours).padStart(2, '0')}:${String(transitStartMins).padStart(2, '0')}`,
          activity: 'Transit',
          from: addLocationContext(resolvedCurrentLocation),
          to: addLocationContext(resolvedNextLocation),
          duration: `~${transitDuration} mins${distanceInfo}`,
          selectedMode: selectedMode,
          transitKey: transitKey,
          sortTime: transitStartMinutes
        });

        // Increment counter for next transit card
        transitCardCounter++;
      }
    });
    
    // Add transit back to hotel at end of day (except last day and departure days with overnight flights)
    // On travel days ending with a flight, you're sleeping on the plane — no hotel return
    const hasDepartureFlightTonight = timeline.some(item =>
      (item.type === 'inter-city-travel' || item.type === 'overnight-journey') &&
      (item.isOvernightFlight || item.travelMode === 'flight')
    );
    if (!isLastDay && !hasDepartureFlightTonight && allItems.length > 0) {
      const lastItem = allItems[allItems.length - 1];
      const lastLocation = lastItem.location || lastItem.restaurant || '';
      const lastActivity = (lastItem.activity || lastItem.meal || '').toLowerCase();
      const hotelName = dayHotelName;
      const hotelLower = hotelName.toLowerCase();
      
      // Check if last activity is already at the hotel
      const lastLocLower = lastLocation.toLowerCase();
      const hotelKeyWords = hotelLower.split(/\s+/).filter(w => 
        w.length > 3 && !['hotel', 'suites', 'resort', 'inn', 'lodge', 'spa', 'the', 'and'].includes(w)
      );
      
      const lastActivityAtHotel = lastLocLower.includes(hotelLower) || 
        hotelKeyWords.some(w => lastLocLower.includes(w)) ||
        (lastItem.restaurant && hotelKeyWords.some(w => lastItem.restaurant.toLowerCase().includes(w)));
      
      // Skip return transit if already at hotel
      if (lastActivityAtHotel) {
        // Just add the "Arrive back at hotel" card with adjusted time
        const lastDurationMins = parseDurationToMinutes(lastItem.duration);
        const arrivalMinutes = lastItem.sortTime + lastDurationMins;
        const arrivalHours = Math.floor(arrivalMinutes / 60);
        const arrivalMins = arrivalMinutes % 60;
        
        timeline.push({
          type: 'hotel-return',
          time: `${String(arrivalHours).padStart(2, '0')}:${String(arrivalMins).padStart(2, '0')}`,
          activity: returnLabel,
          location: hotelName,
          description: 'Rest, freshen up, or explore the hotel amenities',
          duration: 'End of day',
          sortTime: arrivalMinutes
        });
      } else {
        // Calculate transit start time
        const lastDurationMins = parseDurationToMinutes(lastItem.duration);

        const transitStartMinutes = lastItem.sortTime + lastDurationMins;
        
        const transitStartHours = Math.floor(transitStartMinutes / 60);
        const transitStartMins = transitStartMinutes % 60;

        const transitKey = `day${dayIndex}-last-to-hotel`;
        const selectedMode = transportModes[transitKey] || 'driving';
        const cachedReturnTransit = transitTimes[transitKey] || {};

        // Get actual transit duration from Google Maps API cache
        let transitDuration = selectedMode === 'transit' ? 25 : selectedMode === 'walking' ? 20 : 15; // Default
        const returnModeData = cachedReturnTransit[selectedMode] || cachedReturnTransit.driving;
        if (returnModeData?.durationMinutes) {
          transitDuration = returnModeData.durationMinutes;
        } else if (returnModeData?.duration) {
          const duration = returnModeData.duration;
          if (duration.includes('hr') || duration.includes('hour')) {
            const match = duration.match(/(\d+)\s*hr?\s*(\d*)/);
            if (match) transitDuration = (parseInt(match[1]) || 0) * 60 + (parseInt(match[2]) || 0);
          } else {
            const mins = parseInt(duration);
            if (!isNaN(mins)) transitDuration = mins;
          }
        }

        // Small-island clamp: on a small island (Ambergris Caye, Caye Caulker,
        // Gili Islands, etc.) return-to-hotel shouldn't take 27 minutes —
        // whole-island traversal is typically <10 min by golf cart. User saw
        // a 27-min "Return to Hotel" in San Pedro on Day 6 in QA. If the
        // day's baseCity is an island and we have no real Google Maps data,
        // clamp to 10 min.
        const dayBaseCity = dayData?.baseCity || '';
        if (dayBaseCity && isIslandCity(dayBaseCity) && transitDuration > 10 && !returnModeData?.durationMinutes) {
          debugLog(`🏝️ Small-island clamp: return-transit ${transitDuration}m → 10m for ${dayBaseCity}`);
          transitDuration = 10;
        }

        // Add transit to hotel card
        timeline.push({
          type: 'return-transit',
          time: `${String(transitStartHours).padStart(2, '0')}:${String(transitStartMins).padStart(2, '0')}`,
          activity: 'Return to Hotel',
          from: addLocationContext(lastLocation || 'Last activity'),
          to: addLocationContext(hotelName),
          duration: returnModeData?.duration || `~${transitDuration} mins`,
          selectedMode: selectedMode,
          transitKey: transitKey,
          sortTime: transitStartMinutes
        });

        // Add "Arrive back at hotel" card
        const arrivalMinutes = transitStartMinutes + transitDuration;
        const arrivalHours = Math.floor(arrivalMinutes / 60);
        const arrivalMins = arrivalMinutes % 60;
        
        timeline.push({
          type: 'hotel-return',
          time: `${String(arrivalHours).padStart(2, '0')}:${String(arrivalMins).padStart(2, '0')}`,
          activity: returnLabel,
          location: hotelName,
          description: 'Rest, freshen up, or explore the hotel amenities',
          duration: 'End of day',
          sortTime: arrivalMinutes
        });
      }
    }
    
    // Add departure logistics for last day
    if (isLastDay) {
      const departTime = effectiveReturnMode === 'fly'
        ? (departureFlight.time || '18:00')
        : (driveDetails.returnDepartureTime || '10:00');
      const [hours, minutes] = departTime.split(':').map(Number);
      const departMinutes = hours * 60 + minutes;

      if (effectiveReturnMode === 'fly') {
        // FLYING: Airport departure logistics
        // Remove any AI-generated checkout/hotel-checkin AND airport departure items to prevent duplicates —
        // departure logistics will handle checkout timing and airport cards properly
        const checkoutPatterns = ['hotel checkout', 'check out', 'check-out', 'hotel check-in', 'hotel checkin', 'transit to hotel', 'return to hotel', 'arrive back at hotel'];
        // Also filter LLM-generated airport activities — the programmatic cards below handle these.
        // Added 'transfer to' to catch "Transfer to Lisbon Airport" / "Transfer to LIS" patterns
        // that slipped past the previous list (seen in Day 10 QA — 8:45pm "Transfer to Lisbon
        // Airport" rendered 10 hours AFTER a 10:15am flight departure).
        const airportPatterns = ['check-in at', 'check in at', 'airport check-in', 'airport checkin', 'departure from', 'depart from', 'head to airport', 'transit to airport', 'drive to airport', 'taxi to airport', 'transfer to airport', 'transfer to lisbon airport', 'transfer to '];
        const checkoutTypes = ['checkout', 'hotel-return', 'return-transit'];
        const beforeFilterCount = timeline.length;
        timeline = timeline.filter(item => {
          const act = (item.activity || '').toLowerCase();
          const desc = (item.description || '').toLowerCase();
          const isCheckoutOrCheckin = checkoutPatterns.some(p => act.includes(p)) || checkoutTypes.includes(item.type);
          // Also catch LLM-generated airport activities (e.g., "Check-In At JFK International Airport",
          // "Transfer to Lisbon Airport", etc.)
          const isAirportActivity = airportPatterns.some(p => act.includes(p)) && (act.includes('airport') || desc.includes('airport') || desc.includes('flight'));

          // Post-flight filter: on a flight-departure day the LLM sometimes
          // schedules lunch/activities AFTER the departure time (Day 10 QA
          // had a 10:15am flight and a 1:30pm lunch card + 8:45pm airport
          // transfer). Drop any non-flight, non-transit item whose time is
          // after the flight's scheduled departure — the user is airborne.
          const itemHHMM = (item.time || '').match(/^(\d{1,2}):(\d{2})/);
          const itemMinutes = itemHHMM ? (parseInt(itemHHMM[1], 10) * 60 + parseInt(itemHHMM[2], 10)) : -1;
          const isFlightItem = item.type === 'inter-city-travel' || item.type === 'flight' || item.travelMode === 'flight';
          const isAirportTransitType = item.type === 'airport-transit' || item.type === 'transit-departure';
          const isPostFlight = itemMinutes >= 0 && itemMinutes > departMinutes && !isFlightItem && !isAirportTransitType;

          if (isCheckoutOrCheckin || isAirportActivity || isPostFlight) {
            debugLog(`  🗑️ Removing item on departure day: "${item.activity}" (type: ${item.type}, reason: ${isPostFlight ? 'post-flight time' : isAirportActivity ? 'airport pattern' : 'checkout/checkin'})`);
          }
          return !isCheckoutOrCheckin && !isAirportActivity && !isPostFlight;
        });
        if (timeline.length < beforeFilterCount) {
          debugLog(`  ✅ Removed ${beforeFilterCount - timeline.length} AI-generated checkout/checkin items from departure day`);
        }

        // Get selected transport mode for airport transit
        const selectedMode = transportModes['last-day-airport'] || 'drive';

        // Check if we have a dynamically calculated departure airport distance
        const hasCalculatedDepartureDistance = airportDistances?.departure?.durationMinutes > 0;

        // SAFETY CHECK: Verify departure airport is NOT the home airport
        // This catches any case where the guardrails in generation/post-processing missed it
        // The departure airport should be near the DESTINATION (where you fly FROM), not home
        const rawHomeAirportsTl = profile?.home_airports;
        const homeAirportsListTl = Array.isArray(rawHomeAirportsTl) ? rawHomeAirportsTl
          : typeof rawHomeAirportsTl === 'string' ? (() => { try { return JSON.parse(rawHomeAirportsTl); } catch { return []; } })()
          : [];
        const homeCodesTl = new Set();
        if (homeAirportsListTl[0]?.code) homeCodesTl.add(homeAirportsListTl[0].code.toUpperCase());
        const homeCityCodeTl = lookupCityAirportCode(userProfile?.preferences?.homeCity);
        if (homeCityCodeTl) homeCodesTl.add(homeCityCodeTl.toUpperCase());

        let effectiveDepartureAirport = itinerary?.departureAirport;
        if (effectiveDepartureAirport?.code && homeCodesTl.has(effectiveDepartureAirport.code.toUpperCase())) {
          // Departure airport is the HOME airport — wrong! Fall back to arrival airport or transport info
          debugLog(`⚠️ Timeline safety: departureAirport (${effectiveDepartureAirport.code}) is home airport! Falling back...`);
          const fallbackAirport = itinerary?.arrivalAirport || null;
          if (fallbackAirport?.code && fallbackAirport.code !== '???' && !homeCodesTl.has(fallbackAirport.code.toUpperCase())) {
            effectiveDepartureAirport = { ...fallbackAirport, city: dayData?.baseCity || fallbackAirport.city };
            debugLog(`  → Using arrival airport as fallback: ${effectiveDepartureAirport.code}`);
          } else {
            // Try transport info for the current city
            const currentCity = dayData?.baseCity || selectedDestination?.name || '';
            const transportInfo = itinerary?.transportInfo?.[currentCity];
            if (transportInfo?.nearestAirport?.code) {
              effectiveDepartureAirport = {
                code: transportInfo.nearestAirport.code,
                name: transportInfo.nearestAirport.name || `${transportInfo.nearestAirport.code} Airport`,
                city: transportInfo.nearestAirport.city || currentCity
              };
              debugLog(`  → Using transport info airport: ${effectiveDepartureAirport.code}`);
            }
          }
        }

        // Check if departure airport is in a different city from the hotel/destination
        // DEPARTURE_AIRPORT_CITY_MAP imported from @wander/shared via ./utils/transportConstants
        const departureCode = effectiveDepartureAirport?.code || '';
        const resolvedDepartureCity = DEPARTURE_AIRPORT_CITY_MAP[departureCode] || safeAirportCity(effectiveDepartureAirport) || '';
        const departureAirportCity = resolvedDepartureCity.toLowerCase();
        const currentHotelCity = (dayData?.baseCity || selectedDestination?.name || '').toLowerCase();
        const isLongDistanceDeparture = hasCalculatedDepartureDistance
          ? airportDistances.departure.isFar
          : (departureAirportCity && currentHotelCity &&
              !departureAirportCity.includes(currentHotelCity) && !currentHotelCity.includes(departureAirportCity.split(',')[0]));

        // Estimate transit duration based on destination type
        // National parks often require long drives to airports
        const destName = (selectedDestination?.name || '').toLowerCase();
        const hotelAddress = (dayHotelName || '').toLowerCase();
        const isNationalParkTrip = destName.includes('national park') || destName.includes('mighty five') ||
                                    destName.includes('utah') || destName.includes('zion') ||
                                    hotelAddress.includes('springdale') || hotelAddress.includes('moab') ||
                                    hotelAddress.includes('bryce') || hotelAddress.includes('kanab');

        // Calculate transit duration - PRIORITY: Use dynamically calculated distance from Google Maps
        let transitDuration;
        let transitDescription = 'Arrive early for check-in and security';

        // Priority 1: Use real-time hotel-to-airport transit data (most accurate — uses actual hotel address)
        const realTimeAirportTransit = transitTimesRef.current['last-day-airport-transit'];
        const hasRealTimeAirportTransit = realTimeAirportTransit?.driving?.durationMinutes > 0;

        if (hasRealTimeAirportTransit) {
          transitDuration = realTimeAirportTransit.driving.durationMinutes;
          if (transitDuration > 90) {
            transitDescription = `Long drive to ${effectiveDepartureAirport?.code || 'airport'} (${realTimeAirportTransit.driving.duration}) - leave early!`;
          }
          debugLog(`📍 Using real-time hotel-to-airport transit: ${realTimeAirportTransit.driving.duration} (${transitDuration} mins)`);
        } else if (hasCalculatedDepartureDistance) {
          // Priority 2: Fallback to calculated airport distance
          transitDuration = airportDistances.departure.durationMinutes;
          if (airportDistances.departure.isFar) {
            transitDescription = `Long drive to ${effectiveDepartureAirport?.code || 'airport'} (${airportDistances.departure.duration}) - leave early!`;
          }
          debugLog(`📍 Using calculated departure distance (fallback): ${airportDistances.departure.duration} (${transitDuration} mins)`);
        } else if (isLongDistanceDeparture) {
          // Fallback: Long distance drive to departure airport (e.g., Key West to MCO)
          let estimatedDriveHours = 4; // Default estimate
          if (destName.includes('key west') || destName.includes('keys')) {
            // Key West to MCO is about 5-6 hours
            if (departureAirportCity.includes('orlando') || effectiveDepartureAirport?.code === 'MCO') {
              estimatedDriveHours = 5.5;
            } else if (departureAirportCity.includes('miami') || effectiveDepartureAirport?.code === 'MIA') {
              estimatedDriveHours = 3.5;
            } else if (departureAirportCity.includes('fort lauderdale') || effectiveDepartureAirport?.code === 'FLL') {
              estimatedDriveHours = 4;
            }
          }
          transitDuration = estimatedDriveHours * 60;
          transitDescription = `Long drive to ${effectiveDepartureAirport?.code || 'airport'} - leave early!`;
        } else if (isNationalParkTrip) {
          transitDuration = selectedMode === 'transit' ? 300 : 240; // 4-5 hours for national parks
          transitDescription = 'Long drive to airport - leave early!';
        } else {
          transitDuration = selectedMode === 'transit' ? 60 : 45; // 45-60 min for cities
        }
        
        // Work backwards from flight time:
        // Flight time - airport buffer - transit time - 30 min checkout
        const isDomesticDeparture = checkIfDomestic(selectedDestination);
        const airportBuffer = isDomesticDeparture ? 120 : 180; // 2hr domestic / 3hr international
        const totalLeadTime = airportBuffer + transitDuration + 30; // airport buffer + transit + checkout
        let checkoutMinutes = departMinutes - totalLeadTime;

        // Handle edge case: checkout time would be negative (previous day)
        // This happens with very early morning flights (e.g., 2:30 AM) + long transit
        // In this case, the checkout is effectively "late night" the previous day
        const isOvernightCheckout = checkoutMinutes < 0;
        if (isOvernightCheckout) {
          // Convert negative to late-night time (e.g., -60 becomes 23:00 previous day)
          // But for display purposes, we'll show it as a very early time and add context
          checkoutMinutes = Math.max(0, checkoutMinutes); // Clamp to midnight minimum
          debugWarn(`⚠️ Very early flight (${departTime}) requires late-night/early morning checkout`);
        }

        const checkoutHours = Math.floor(checkoutMinutes / 60);
        const checkoutMins = checkoutMinutes % 60;

        // Flag very early checkouts (before 5 AM) for display purposes
        const isVeryEarlyCheckout = checkoutMinutes < 300 && checkoutMinutes >= 0;

        // Flag late checkouts (after 2 PM / 14:00 = 840 minutes)
        // Standard checkout is usually 11am-12pm, so anything after 2pm needs late checkout
        const isLateCheckout = checkoutMinutes > 840;

        const checkoutDescription = isOvernightCheckout
          ? 'Very early checkout for red-eye flight - consider staying near airport'
          : isVeryEarlyCheckout
          ? 'Early checkout required for your flight'
          : isLateCheckout
          ? 'Late checkout needed - request in advance or use luggage storage'
          : 'Check out and gather luggage';

        // Inject breakfast if none exists on departure day (AI sometimes omits it)
        const hasBreakfastFly = timeline.some(item =>
          (item.type === 'meal' && (item.meal || item.activity || '').toLowerCase().includes('breakfast')) ||
          (item.activity || '').toLowerCase().includes('breakfast')
        );
        if (!hasBreakfastFly && !isVeryEarlyCheckout && !isOvernightCheckout && checkoutMinutes > 90) {
          const breakfastMinutes = checkoutMinutes - 90;
          const breakfastHours = Math.floor(breakfastMinutes / 60);
          const breakfastMins = breakfastMinutes % 60;
          timeline.push({
            type: 'meal', mealType: 'breakfast', originalType: 'breakfast',
            meal: 'Breakfast',
            time: `${String(breakfastHours).padStart(2, '0')}:${String(breakfastMins).padStart(2, '0')}`,
            activity: 'Breakfast',
            restaurant: dayHotelName ? `${dayHotelName} Restaurant` : 'Hotel Restaurant',
            description: 'Enjoy breakfast before checking out',
            duration: '1 hour',
            location: dayHotelName || 'Hotel',
            sortTime: breakfastMinutes,
            isDisplayInjected: true,
          });
          debugLog(`🍳 Injected breakfast at ${breakfastHours}:${String(breakfastMins).padStart(2, '0')} on departure day (fly)`);
        }

        timeline.push({
          type: 'checkout',
          time: `${String(checkoutHours).padStart(2, '0')}:${String(checkoutMins).padStart(2, '0')}`,
          activity: 'Hotel Checkout',
          description: checkoutDescription,
          duration: '30 minutes',
          sortTime: checkoutMinutes,
          isVeryEarly: isVeryEarlyCheckout || isOvernightCheckout,
          // T7 (web): stamp checkout card with the hotel + city being
          // checked out of, so the detail modal subtitle doesn't fall
          // back to the trip-wide destination list.
          hotelName: dayHotelName || null,
          checkoutCity: dayData?.baseCity || null,
          location: dayHotelName
            ? (dayData?.baseCity ? `${dayHotelName}, ${dayData.baseCity}` : dayHotelName)
            : (dayData?.baseCity || null),
        });

        // Transit to Airport (starts after checkout)
        const transitStartMinutes = checkoutMinutes + 30;
        const transitStartHours = Math.floor(transitStartMinutes / 60);
        const transitStartMins = transitStartMinutes % 60;
        
        const airportTransitKey = 'last-day-airport-transit';
        const airportTransitMode = transportModes[airportTransitKey] || 'driving';
        
        // Format transit duration nicely
        const formatTransitDuration = (mins) => {
          if (mins >= 60) {
            const h = Math.floor(mins / 60);
            const m = mins % 60;
            return m > 0 ? `~${h} hr ${m} mins` : `~${h} hr`;
          }
          return `~${mins} mins`;
        };
        
        // Use appropriate activity name and description based on distance
        const airportCode = effectiveDepartureAirport?.code || 'Airport';

        // For long-distance departures, determine best transport mode
        // European mountain destinations often have good train connections to airports
        let longDistanceMode = 'car';
        let longDistanceModeEmoji = '🚗';
        const hotelCityLower = currentHotelCity.toLowerCase();
        const airportCityLower = departureAirportCity.toLowerCase();

        // BUG B FIX: Detect island locations — you can't drive from islands to mainland airports!
        // Faroe Islands → Copenhagen, Hawaii → mainland US, Bali → Jakarta, etc.
        // Uses shared ISLAND_LOCATION_KEYWORDS constant and isIslandCity() helper
        const isIslandLocation = isIslandCity(currentHotelCity);
        const isIslandBaseCity = isIslandCity(dayData?.baseCity);

        if (isIslandLocation || isIslandBaseCity) {
          // Can't drive from an island — must fly to the departure airport
          longDistanceMode = 'flight';
          longDistanceModeEmoji = '✈️';
          // Override transit description and duration for island flights
          // Typical island-to-mainland connecting flight: 1-3 hours
          transitDuration = 150; // ~2.5h estimated connecting flight
          transitDescription = `Flight to ${effectiveDepartureAirport?.code || 'airport'} - book connecting flight in advance`;
          debugLog(`🏝️ Island departure detected: ${currentHotelCity} is an island, must fly to ${airportCode}`);
        }

        // TRAIN_FRIENDLY_AIRPORT_ROUTES imported from @wander/shared via ./utils/transportConstants

        // Only check train routes if not already determined to be a flight (island)
        if (longDistanceMode !== 'flight') {
          // Priority 1: Use Haiku-determined mode from pre-fetch (most accurate — knows real rail service)
          const depPairKey = `${hotelCityLower}|${airportCityLower}`;
          const dynamicDepTransport = dynamicTransportModes[depPairKey];

          if (dynamicDepTransport?.mode) {
            longDistanceMode = dynamicDepTransport.mode; // 'train', 'car', 'flight', 'ferry'
            if (dynamicDepTransport.mode === 'train') longDistanceModeEmoji = '🚆';
            else if (dynamicDepTransport.mode === 'ferry') longDistanceModeEmoji = '⛴️';
            else if (dynamicDepTransport.mode === 'flight') longDistanceModeEmoji = '✈️';
            // Use real duration from Haiku if available
            if (dynamicDepTransport.durationMinutes) {
              transitDuration = dynamicDepTransport.durationMinutes;
              transitDescription = `${dynamicDepTransport.mode === 'train' ? 'Train' : dynamicDepTransport.mode === 'ferry' ? 'Ferry' : 'Transit'} to ${airportCode} (${dynamicDepTransport.duration})`;
            }
            debugLog(`🚀 Haiku departure transport: ${depPairKey} = ${dynamicDepTransport.mode} (${dynamicDepTransport.duration})`);
          } else {
            // Priority 2: Fallback to shared TRAIN_FRIENDLY_AIRPORT_ROUTES
            const isTrainFriendlyRoute = TRAIN_FRIENDLY_AIRPORT_ROUTES.some(([from, to]) =>
              (hotelCityLower.includes(from) && airportCityLower.includes(to)) ||
              (hotelCityLower.includes(to) && airportCityLower.includes(from))
            );

            if (isTrainFriendlyRoute) {
              longDistanceMode = 'train';
              longDistanceModeEmoji = '🚆';
            }
          }
        }

        const longDistanceModeLabel = longDistanceMode === 'train' ? 'Train'
          : longDistanceMode === 'flight' ? 'Flight' : 'Drive';
        const transitActivityName = isLongDistanceDeparture
          ? `${longDistanceModeEmoji} ${longDistanceModeLabel} to ${airportCode}`
          : 'Transit to Airport';

        // BUG FIX: Recalculate cutoffEndTime after all transit duration refinements
        // (island detection, long-distance routes, national parks, real-time transit data)
        // The initial cutoff at line ~11183 uses a default 45-min transit, but the actual
        // transit duration may be much longer (e.g., 150 min for island connecting flights)
        const refinedTotalLeadTime = airportBuffer + transitDuration + 30;
        const refinedCutoff = Math.max(0, departMinutes - refinedTotalLeadTime);
        if (refinedCutoff !== cutoffEndTime) {
          debugLog(`🔄 Recalculating cutoffEndTime: ${cutoffEndTime} → ${refinedCutoff} (transit refined to ${transitDuration} mins)`);
          cutoffEndTime = refinedCutoff;
        }

        // Use departure hotel (may be previous day's hotel if user travels to a different city to fly out)
        const transitFromHotel = departureHotelName || dayHotelName;
        const transitFromCity = (!dayData?.hotel?.name && prevDayData?.baseCity)
          ? prevDayData.baseCity.toLowerCase()
          : currentHotelCity;

        timeline.push({
          type: isLongDistanceDeparture ? 'long-distance-departure' : 'airport-transit',
          time: `${String(transitStartHours).padStart(2, '0')}:${String(transitStartMins).padStart(2, '0')}`,
          activity: transitActivityName,
          from: `${transitFromHotel}, ${transitFromCity}`,
          to: addLocationContext(`${airportCode} Airport, ${departureAirportCity}`),
          description: transitDescription,
          duration: formatTransitDuration(transitDuration),
          selectedMode: airportTransitMode,
          transitKey: airportTransitKey,
          sortTime: transitStartMinutes,
          isLongDistance: isLongDistanceDeparture,
          travelMode: isLongDistanceDeparture ? longDistanceMode : null,
          fromCity: transitFromCity,
          toCity: departureAirportCity
        });
        
        // Airport Arrival (for context)
        const arrivalAtAirportMinutes = transitStartMinutes + transitDuration;
        const arrivalAtAirportHours = Math.floor(arrivalAtAirportMinutes / 60);
        const arrivalAtAirportMins = arrivalAtAirportMinutes % 60;
        const timeAtAirport = departMinutes - arrivalAtAirportMinutes;
        
        const depAirportCode = effectiveDepartureAirport?.code || '';
        const depAirportName = effectiveDepartureAirport?.name || effectiveDepartureAirport?.city || '';
        const depAirportDisplay = depAirportName || depAirportCode;
        timeline.push({
          type: 'airport-arrival',
          time: `${String(arrivalAtAirportHours).padStart(2, '0')}:${String(arrivalAtAirportMins).padStart(2, '0')}`,
          activity: depAirportDisplay ? `✈️ Check-in at ${depAirportDisplay}` : '✈️ Airport Check-in',
          description: depAirportCode ? `Security, check-in, boarding at ${depAirportCode}` : 'Security, check-in, boarding',
          duration: `${Math.floor(timeAtAirport / 60)}h ${timeAtAirport % 60}m buffer`,
          location: depAirportName ? `${depAirportName}` : (depAirportCode ? `${depAirportCode} Airport` : undefined),
          sortTime: arrivalAtAirportMinutes,
          airportCode: depAirportCode || null
        });

        // Departure Flight — full card with pricing (mirrors inter-city-travel card)
        // Use effectiveDepartureAirport (already home-airport-checked) with fallback chain
        const departureAirportCode = (
          effectiveDepartureAirport?.code ||
          itinerary?.arrivalAirport?.code ||
          ''
        );
        const departureAirportName = effectiveDepartureAirport?.name || effectiveDepartureAirport?.city || '';
        const departureAirportDisplay = departureAirportName || departureAirportCode;
        // Get home airport for pricing route (departure airport → home airport)
        // Priority: trip-specific departure city → permanent profile home airport
        const homeAirportsForDep = (() => { const raw = profile?.home_airports; if (Array.isArray(raw)) return raw; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } } return []; })();
        const homeAirportCodeForDep = lookupCityAirportCode(userProfile?.preferences?.homeCity) || homeAirportsForDep?.[0]?.code || '';
        timeline.push({
          type: 'departure',
          time: departTime,
          activity: departureAirportDisplay ? `✈️ Departure from ${departureAirportDisplay}` : '✈️ Departure Flight',
          description: homeAirportCodeForDep && departureAirportCode
            ? `Flight from ${departureAirportCode} to ${homeAirportCodeForDep}. Safe travels! 🌍`
            : 'Safe travels! 🌍',
          location: departureAirportCode && homeAirportCodeForDep
            ? `${departureAirportCode} → ${homeAirportCodeForDep}`
            : (departureAirportName ? `${departureAirportName}` : (departureAirportCode ? `${departureAirportCode} Airport` : 'Airport')),
          sortTime: departMinutes,
          airportCode: departureAirportCode || null,
          homeAirportCode: homeAirportCodeForDep || null,
          isDepartureFlightCard: true
        });
      } else if (effectiveReturnMode === 'train' || effectiveReturnMode === 'ferry' || effectiveReturnMode === 'custom') {
        // TRAIN/FERRY/CUSTOM: Non-fly, non-drive departure
        const homeCity = userProfile.preferences.homeCity || 'Home';
        const emoji = effectiveReturnMode === 'train' ? '🚆' : effectiveReturnMode === 'ferry' ? '⛴️' : '🚌';
        const modeLabel = effectiveReturnMode === 'train' ? 'Train' : effectiveReturnMode === 'ferry' ? 'Ferry' : 'Departure';
        const transitDuration = quickTransportResult?.duration || '';

        // Remove AI-generated checkout duplicates
        const checkoutPatterns = ['hotel checkout', 'check out', 'check-out'];
        timeline = timeline.filter(item => {
          const act = (item.activity || '').toLowerCase();
          return !checkoutPatterns.some(p => act.includes(p));
        });

        // Checkout 30 min before departure
        const checkoutMinutes = departMinutes - 30;
        const checkoutHours = Math.floor(Math.max(0, checkoutMinutes) / 60);
        const checkoutMins = Math.max(0, checkoutMinutes) % 60;

        // Inject breakfast if none exists
        const hasBreakfast = timeline.some(item =>
          (item.type === 'meal' && (item.meal || item.activity || '').toLowerCase().includes('breakfast')) ||
          (item.activity || '').toLowerCase().includes('breakfast')
        );
        if (!hasBreakfast && checkoutMinutes > 90) {
          const breakfastMinutes = checkoutMinutes - 90;
          timeline.push({
            type: 'meal', mealType: 'breakfast', originalType: 'breakfast',
            meal: 'Breakfast',
            time: `${String(Math.floor(breakfastMinutes / 60)).padStart(2, '0')}:${String(breakfastMinutes % 60).padStart(2, '0')}`,
            activity: 'Breakfast',
            restaurant: dayHotelName ? `${dayHotelName} Restaurant` : 'Hotel Restaurant',
            description: 'Enjoy breakfast before checking out',
            duration: '1 hour',
            location: dayHotelName || 'Hotel',
            sortTime: breakfastMinutes,
            isDisplayInjected: true,
          });
        }

        timeline.push({
          type: 'checkout',
          time: `${String(checkoutHours).padStart(2, '0')}:${String(checkoutMins).padStart(2, '0')}`,
          activity: 'Hotel Checkout',
          description: effectiveReturnMode === 'custom' ? 'Check out and prepare for departure' : `Check out and head to the ${effectiveReturnMode === 'train' ? 'station' : 'port'}`,
          duration: '30 minutes',
          sortTime: Math.max(0, checkoutMinutes),
          // T7 (web)
          hotelName: dayHotelName || null,
          checkoutCity: dayData?.baseCity || null,
          location: dayHotelName
            ? (dayData?.baseCity ? `${dayHotelName}, ${dayData.baseCity}` : dayHotelName)
            : (dayData?.baseCity || null),
        });

        // Departure
        const departHours = Math.floor(departMinutes / 60);
        const departMins = departMinutes % 60;

        timeline.push({
          type: 'transit-departure',
          time: `${String(departHours).padStart(2, '0')}:${String(departMins).padStart(2, '0')}`,
          activity: `${emoji} ${modeLabel} Back to ${homeCity}`,
          duration: transitDuration,
          description: effectiveReturnMode === 'custom'
            ? `${customTransportDesc || 'Departure'} — safe travels! 🏠`
            : `${modeLabel} from ${selectedDestination?.name || 'Destination'} to ${homeCity}${transitDuration ? ` (${transitDuration})` : ''}. Safe travels! 🏠`,
          location: `${selectedDestination?.name || 'Destination'} → ${homeCity}`,
          sortTime: departMinutes
        });

      } else {
        // DRIVING: Road trip home logistics
        // Remove any AI-generated checkout items to prevent duplicates — we add our own below
        const driveCheckoutPatterns = ['hotel checkout', 'check out', 'check-out'];
        const beforeDriveFilterCount = timeline.length;
        timeline = timeline.filter(item => {
          const act = (item.activity || '').toLowerCase();
          const isCheckout = driveCheckoutPatterns.some(p => act.includes(p));
          if (isCheckout) {
            debugLog(`  🗑️ Removing duplicate AI-generated checkout on drive departure day: "${item.activity}"`);
          }
          return !isCheckout;
        });
        if (timeline.length < beforeDriveFilterCount) {
          debugLog(`  ✅ Removed ${beforeDriveFilterCount - timeline.length} AI-generated checkout items from drive departure day`);
        }

        // Priority: 1) Google Maps calculated time, 2) destination driveHours, 3) estimate from flight hours
        const driveHours = calculatedDriveTime?.hours || selectedDestination?.driveHours || estimateDriveHours(selectedDestination?.flightHours || 3);
        const homeCity = userProfile.preferences.homeCity || 'Home';
        const isRental = effectiveReturnMode === 'drive-rental';

        // Work backwards from desired arrival time:
        // departTime is when they want to leave the destination
        // Add drive time to get home arrival
        const homeArrivalMinutes = departMinutes + (driveHours * 60);
        const homeArrivalHours = Math.floor((homeArrivalMinutes % 1440) / 60);
        const homeArrivalMins = (homeArrivalMinutes % 1440) % 60;
        
        // Checkout 30 min before departure
        const checkoutMinutes = departMinutes - 30;
        const checkoutHours = Math.floor(checkoutMinutes / 60);
        const checkoutMins = checkoutMinutes % 60;

        // Inject breakfast if none exists on departure day (AI often skips it for drive-home)
        const hasBreakfast = timeline.some(item =>
          (item.type === 'meal' && (item.meal || item.activity || '').toLowerCase().includes('breakfast')) ||
          (item.activity || '').toLowerCase().includes('breakfast')
        );
        if (!hasBreakfast && checkoutMinutes > 90) {
          // Place breakfast 1.5 hours before checkout
          const breakfastMinutes = checkoutMinutes - 90;
          const breakfastHours = Math.floor(breakfastMinutes / 60);
          const breakfastMins = breakfastMinutes % 60;
          timeline.push({
            type: 'meal', mealType: 'breakfast', originalType: 'breakfast',
            meal: 'Breakfast',
            time: `${String(breakfastHours).padStart(2, '0')}:${String(breakfastMins).padStart(2, '0')}`,
            activity: 'Breakfast',
            restaurant: dayHotelName ? `${dayHotelName} Restaurant` : 'Hotel Restaurant',
            description: 'Enjoy breakfast before checking out',
            duration: '1 hour',
            location: dayHotelName || 'Hotel',
            sortTime: breakfastMinutes,
            isDisplayInjected: true,
          });
          debugLog(`🍳 Injected breakfast at ${breakfastHours}:${String(breakfastMins).padStart(2, '0')} on departure day (drive home)`);
        }

        timeline.push({
          type: 'checkout',
          time: `${String(checkoutHours).padStart(2, '0')}:${String(checkoutMins).padStart(2, '0')}`,
          activity: 'Hotel Checkout',
          description: 'Check out and pack the car',
          duration: '30 minutes',
          sortTime: checkoutMinutes,
          // T7 (web)
          hotelName: dayHotelName || null,
          checkoutCity: dayData?.baseCity || null,
          location: dayHotelName
            ? (dayData?.baseCity ? `${dayHotelName}, ${dayData.baseCity}` : dayHotelName)
            : (dayData?.baseCity || null),
        });
        
        // Road trip home
        const departHours = Math.floor(departMinutes / 60);
        const departMins = departMinutes % 60;
        
        timeline.push({
          type: 'road-trip-return',
          time: `${String(departHours).padStart(2, '0')}:${String(departMins).padStart(2, '0')}`,
          arrivalTime: `${String(homeArrivalHours).padStart(2, '0')}:${String(homeArrivalMins).padStart(2, '0')}`,
          activity: `🚗 Road Trip Back to ${homeCity}`,
          duration: `${driveHours} hours`,
          description: `Drive home${isRental ? ' and return rental car' : ''}. Safe travels! 🏠`,
          location: `${selectedDestination?.name || 'Destination'} → ${homeCity}`,
          sortTime: departMinutes
        });
        
        // Rental car return if applicable
        if (isRental) {
          const returnMinutes = homeArrivalMinutes + 15; // 15 min after arriving home
          const returnHours = Math.floor((returnMinutes % 1440) / 60);
          const returnMins = (returnMinutes % 1440) % 60;
          
          timeline.push({
            type: 'rental-return',
            time: `${String(returnHours).padStart(2, '0')}:${String(returnMins).padStart(2, '0')}`,
            activity: '🚗 Return Rental Car',
            duration: '30 minutes',
            description: `Drop off rental car near ${homeCity}`,
            location: `Rental Location near ${homeCity}`,
            sortTime: returnMinutes
          });
        }
      }
    }

    // Sort by time first (with type tiebreaker)
    timeline.sort(timelineSortComparator);

    // FIX: On last day, resolve overlaps between meals and departure transit
    // If a meal and departure transit/long-distance-departure share the same time slot,
    // move the meal earlier so it finishes before transit starts
    if (isLastDay) {
      const departureTransitItem = timeline.find(t => t.type === 'airport-transit' || t.type === 'long-distance-departure');
      if (departureTransitItem) {
        const transitStartTime = departureTransitItem.sortTime;
        timeline.forEach(item => {
          if (item.type === 'meal' && item.sortTime >= 0) {
            const mealDuration = 60; // Meals are ~1 hour
            const mealEndTime = item.sortTime + mealDuration;
            if (mealEndTime > transitStartTime && item.sortTime < transitStartTime + 60) {
              // Meal overlaps with or runs into departure transit — push meal earlier
              const newMealStart = transitStartTime - mealDuration - 15; // 15 min buffer before transit
              if (newMealStart >= 600) { // Don't push before 10 AM
                debugLog(`🔧 Rescheduling last-day meal to avoid departure overlap: "${item.activity}" ${item.sortTime} mins → ${newMealStart} mins`);
                item.sortTime = newMealStart;
                const newHours = Math.floor(newMealStart / 60);
                const newMins = newMealStart % 60;
                item.time = `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
              } else {
                // Can't fit meal before transit — filter it out
                debugLog(`🚫 Removing last-day meal that overlaps with departure transit: "${item.activity}"`);
                item._remove = true;
              }
            }
          }
        });
        // Remove any meals marked for removal
        const removeCount = timeline.filter(t => t._remove).length;
        if (removeCount > 0) {
          const filtered = timeline.filter(t => !t._remove);
          timeline.length = 0;
          timeline.push(...filtered);
        }
        // Re-sort after adjustments (with type tiebreaker)
        timeline.sort(timelineSortComparator);
      }
    }

    // Detect gaps and insert free time cards
    // Skip free time detection entirely on departure day — any gaps are for
    // packing, checkout prep, or transit buffer, not real free time
    const freeTimeCards = [];
    const MIN_FREE_TIME_GAP = 60; // Minimum 60 minutes to show free time card
    const skipFreeTimeDetection = isLastDay && effectiveReturnMode === 'fly';

    // SYSTEMIC FIX: Track used suggestion labels to avoid showing duplicates across free time slots
    const usedSuggestionLabels = new Set();

    if (skipFreeTimeDetection) {
      debugLog(`⏭️ Skipping free time detection on departure day`);
    }

    for (let i = 0; !skipFreeTimeDetection && i < timeline.length - 1; i++) {
      const currentItem = timeline[i];
      const nextItem = timeline[i + 1];

      // Define travel types
      const travelTypes = ['transit', 'inter-city-travel', 'airport-transit', 'station-transit', 'port-transit', 'airport-arrival', 'departure', 'transit-departure', 'hotel-transit', 'long-distance-transfer', 'long-distance-departure', 'road-trip', 'road-trip-return', 'transit-arrival'];

      // Major travel types - never show free time right before or after these (packing, preparing, or just arrived)
      const majorTravelTypes = ['inter-city-travel', 'airport-transit', 'station-transit', 'port-transit', 'departure', 'transit-departure', 'long-distance-transfer', 'long-distance-departure', 'road-trip', 'road-trip-return', 'airport-arrival', 'transit-arrival', 'hotel-checkin', 'hotel-checkout'];
      if (majorTravelTypes.includes(nextItem.type)) continue;
      // Also skip free time immediately AFTER major travel (you just arrived!)
      if (majorTravelTypes.includes(currentItem.type)) continue;

      // For transit items, calculate when they END (not when they start)
      // This allows us to detect gaps AFTER transit completes
      if (['transit', 'hotel-transit', 'return-transit'].includes(currentItem.type)) {
        // Transit items have duration like "~20 mins" - extract the number
        const transitDurationMatch = currentItem.duration?.match(/(\d+)/);
        if (transitDurationMatch) {
          const transitDuration = parseInt(transitDurationMatch[1]);
          const transitEndMinutes = currentItem.sortTime + transitDuration;
          const nextStartMinutes = nextItem.sortTime;
          const gapMinutes = nextStartMinutes - transitEndMinutes;

          // If there's a significant gap AFTER transit, show free time
          // D1: Cap individual free time cards at 2 hours — but still show something for larger gaps
          const cappedTransitGap = Math.min(gapMinutes, 120);
          if (cappedTransitGap >= MIN_FREE_TIME_GAP && !majorTravelTypes.includes(nextItem.type)) {
            const freeTimeStartHours = Math.floor(transitEndMinutes / 60);
            const freeTimeStartMins = transitEndMinutes % 60;
            const gapHours = Math.floor(cappedTransitGap / 60);
            const gapMins = cappedTransitGap % 60;

            const durationStr = gapHours > 0
              ? (gapMins > 0 ? `${gapHours}hr ${gapMins}min` : `${gapHours}hr`)
              : `${gapMins}min`;

            const baseCity = dayData?.baseCity || cityContext || '';
            const aiSuggestions = dayData?.freeTimeSuggestions;

            // Get the "from" location for distance calculations
            // For transit items, the "to" is where free time starts
            const fromLocation = currentItem.to || currentItem.location || baseCity;
            // The "to" location is the next scheduled activity
            const toLocation = nextItem.location || nextItem.restaurant || nextItem.to || '';

            // Determine time of day for filtering suggestions
            const isBeforeNoon = freeTimeStartHours < 12;
            const isAfternoon = freeTimeStartHours >= 12 && freeTimeStartHours < 17;
            const isEvening = freeTimeStartHours >= 17;

            // Determine current time slot for structured timeOfDay filtering
            const currentTimeSlot = isBeforeNoon ? 'morning' : (isAfternoon ? 'afternoon' : 'evening');

            // Time-inappropriate keywords to filter (fallback for suggestions without timeOfDay)
            const eveningKeywords = ['evening', 'sunset', 'night', 'nightlife', 'cocktail', 'bar hopping', 'dinner spot'];
            const morningKeywords = ['sunrise', 'breakfast spot', 'morning market', 'early'];

            let suggestions;
            if (aiSuggestions && aiSuggestions.length > 0) {
              // Map suggestions and include timeOfDay if available
              // Filter out vague hotel-centric suggestions (e.g., "Family Suite Relaxation", "Hotel Pool Time")
              const VAGUE_SUGGESTION_PATTERN = /^(hotel|suite|room|lobby|relaxation|rest|nap|pack|unpack|settle|freshen|check.?in|check.?out)/i;
              let filtered = aiSuggestions
                .filter(s => s.activity && !VAGUE_SUGGESTION_PATTERN.test(s.activity.trim()))
                .map(s => ({
                  icon: 'map-pin',
                  label: s.activity,
                  description: s.description,
                  timeOfDay: s.timeOfDay || null
                }));
              // If all got filtered, use originals
              if (filtered.length === 0) {
                filtered = aiSuggestions.map(s => ({
                  icon: 'map-pin',
                  label: s.activity,
                  description: s.description,
                  timeOfDay: s.timeOfDay || null
                }));
              }

              // First, filter by timeOfDay field if available (new format)
              // Then fallback to keyword filtering for suggestions without timeOfDay
              filtered = filtered.filter(s => {
                // If suggestion has timeOfDay, use structured filtering
                if (s.timeOfDay) {
                  if (s.timeOfDay === 'any') return true;
                  if (currentTimeSlot === 'morning') return s.timeOfDay === 'morning' || s.timeOfDay === 'any';
                  if (currentTimeSlot === 'afternoon') return s.timeOfDay === 'afternoon' || s.timeOfDay === 'any';
                  if (currentTimeSlot === 'evening') return s.timeOfDay === 'evening' || s.timeOfDay === 'any';
                  return true;
                }

                // Fallback: keyword-based filtering for old format suggestions
                const text = `${s.label} ${s.description}`.toLowerCase();
                if (isBeforeNoon) {
                  return !eveningKeywords.some(kw => text.includes(kw));
                } else if (isAfternoon) {
                  return !eveningKeywords.some(kw => text.includes(kw)) &&
                         !morningKeywords.some(kw => text.includes(kw));
                } else if (isEvening) {
                  return !morningKeywords.some(kw => text.includes(kw));
                }
                return true;
              });

              // Filter out already-used suggestions
              const unusedFiltered = filtered.filter(s => !usedSuggestionLabels.has(s.label));
              if (unusedFiltered.length > 0) {
                suggestions = unusedFiltered;
              } else {
                // If all time-appropriate suggestions are used, fall back to "any" time suggestions
                let fallback = aiSuggestions
                  .map(s => ({ icon: 'map-pin', label: s.activity, description: s.description, timeOfDay: s.timeOfDay }))
                  .filter(s => !usedSuggestionLabels.has(s.label) && (s.timeOfDay === 'any' || !s.timeOfDay));

                suggestions = fallback;
              }
            } else {
              // Fallback: time-appropriate location-specific suggestions
              const nearbyContext = fromLocation ? ` near ${fromLocation.split(',')[0]}` : (baseCity ? ` in ${baseCity}` : '');
              const cityContext = baseCity || 'the area';
              let fallbackSuggestions;
              if (isBeforeNoon) {
                fallbackSuggestions = [
                  { icon: 'coffee', label: `Morning café${nearbyContext}`, description: `Discover a local café in ${cityContext}` },
                  { icon: 'camera', label: `Photo walk in ${cityContext}`, description: 'Morning light is perfect for photos' },
                  { icon: 'shopping-bag', label: `Local market${nearbyContext}`, description: `Browse morning markets and street vendors` }
                ];
              } else if (isAfternoon) {
                fallbackSuggestions = [
                  { icon: 'landmark', label: `Visit a landmark${nearbyContext}`, description: `Explore a nearby attraction in ${cityContext}` },
                  { icon: 'coffee', label: `Afternoon café${nearbyContext}`, description: `Relax at a cozy spot in ${cityContext}` },
                  { icon: 'shopping-bag', label: `Shopping in ${cityContext}`, description: 'Browse local shops and boutiques' }
                ];
              } else {
                fallbackSuggestions = [
                  { icon: 'sunset', label: `Sunset viewpoint${nearbyContext}`, description: `Catch golden hour in ${cityContext}` },
                  { icon: 'wine', label: `Nightlife in ${cityContext}`, description: 'Discover bars and restaurants locals love' },
                  { icon: 'moon', label: `Night walk${nearbyContext}`, description: `Experience ${cityContext} lit up at night` }
                ];
              }
              suggestions = fallbackSuggestions.filter(s => !usedSuggestionLabels.has(s.label));
            }

            // Take up to 3 suggestions and mark them as used
            const selectedSuggestions = suggestions.slice(0, 3);
            selectedSuggestions.forEach(s => usedSuggestionLabels.add(s.label));

            freeTimeCards.push({
              type: 'free-time',
              time: `${String(freeTimeStartHours).padStart(2, '0')}:${String(freeTimeStartMins).padStart(2, '0')}`,
              activity: 'Free Time',
              description: `Time to explore ${baseCity || 'at your own pace'}`,
              duration: durationStr,
              durationMinutes: cappedTransitGap,
              sortTime: transitEndMinutes,
              suggestions: selectedSuggestions,
              baseCity: baseCity,
              fromLocation: fromLocation,
              toLocation: toLocation
            });
          }
        }
        continue; // Still skip for the regular gap detection
      }

      // Don't add free time after airport check-in (airport-arrival) or before departure
      if (currentItem.type === 'airport-arrival' || nextItem.type === 'departure') continue;

      // Calculate end time of current item
      let currentEndMinutes = currentItem.sortTime;

      // For travel items with arrivalTime (road trips, inter-city), use that as end time
      if (travelTypes.includes(currentItem.type) && currentItem.arrivalTime) {
        const [h, m] = currentItem.arrivalTime.split(':').map(Number);
        currentEndMinutes = h * 60 + m;
      } else if (currentItem.type === 'hotel-checkin') {
        // After hotel check-in, add 60 min settling time before considering it "free time"
        currentEndMinutes += 60;
      } else if (currentItem.duration) {
        const durationMatch = currentItem.duration.match(/(\d+(?:\.\d+)?)\s*(hour|hr|min|minute)/i);
        if (durationMatch) {
          const value = parseFloat(durationMatch[1]);
          const unit = durationMatch[2].toLowerCase();
          if (unit.startsWith('hour') || unit === 'hr') {
            currentEndMinutes += value * 60;
          } else {
            currentEndMinutes += value;
          }
        }
      } else {
        // Default duration estimate based on type
        if (currentItem.type === 'activity') currentEndMinutes += 90;
        else if (currentItem.type === 'meal') currentEndMinutes += 60;
        else currentEndMinutes += 30;
      }

      const nextStartMinutes = nextItem.sortTime;
      const gapMinutes = nextStartMinutes - currentEndMinutes;

      // If there's a significant gap, add a free time card
      // D1: Cap individual free time display at 2 hours — but still fill larger gaps
      const cappedGapMinutes = Math.min(gapMinutes, 120);
      if (cappedGapMinutes >= MIN_FREE_TIME_GAP) {
        const freeTimeStartHours = Math.floor(currentEndMinutes / 60);
        const freeTimeStartMins = currentEndMinutes % 60;
        const gapHours = Math.floor(cappedGapMinutes / 60);
        const gapMins = cappedGapMinutes % 60;

        const durationStr = gapHours > 0
          ? (gapMins > 0 ? `${gapHours}hr ${gapMins}min` : `${gapHours}hr`)
          : `${gapMins}min`;

        // Get location-specific suggestions from AI-generated itinerary data
        const baseCity = dayData?.baseCity || cityContext || '';

        // Get the "from" location for distance calculations.
        //
        // Bug fix (2026-05-07): previously the fallback chain was
        //   currentItem.location || currentItem.restaurant || currentItem.to || baseCity
        // For TRANSIT items (e.g., "Transit to Belcanto"), `currentItem.to` is
        // the destination — using it as the FROM location of the free-time
        // gap pointed BOTH "Near" and "Next" at the same place. Lisbon Day 1
        // showed "Near: Belcanto / Next: Belcanto" (the activity AFTER the
        // free time IS Belcanto, but the activity BEFORE it was Parque
        // Eduardo VII). Use `.from` for transit items; activities don't
        // typically have `.to` so the fallback is safe.
        const fromLocation = currentItem.location || currentItem.restaurant || currentItem.from || baseCity;
        // The "to" location is the next scheduled activity
        const toLocation = nextItem.location || nextItem.restaurant || nextItem.to || '';

        // Use AI-generated suggestions from the itinerary if available
        // These are specific real places generated for this exact city/day
        const aiSuggestions = dayData?.freeTimeSuggestions;

        // Determine time of day for filtering suggestions
        const isBeforeNoon = freeTimeStartHours < 12;
        const isAfternoon = freeTimeStartHours >= 12 && freeTimeStartHours < 17;
        const isEvening = freeTimeStartHours >= 17;

        // Determine current time slot for structured timeOfDay filtering
        const currentTimeSlot = isBeforeNoon ? 'morning' : (isAfternoon ? 'afternoon' : 'evening');

        // Time-inappropriate keywords to filter (fallback for suggestions without timeOfDay)
        const eveningKeywords = ['evening', 'sunset', 'night', 'nightlife', 'cocktail', 'bar hopping', 'dinner spot'];
        const morningKeywords = ['sunrise', 'breakfast spot', 'morning market', 'early'];

        let suggestions;
        if (aiSuggestions && aiSuggestions.length > 0) {
          // Convert AI suggestions to the format expected by the UI, including timeOfDay
          let filtered = aiSuggestions.map(s => ({
            emoji: s.emoji || '📍',
            label: s.activity,
            description: s.description,
            timeOfDay: s.timeOfDay || null
          }));

          // First, filter by timeOfDay field if available (new format)
          // Then fallback to keyword filtering for suggestions without timeOfDay
          filtered = filtered.filter(s => {
            // If suggestion has timeOfDay, use structured filtering
            if (s.timeOfDay) {
              if (s.timeOfDay === 'any') return true;
              if (currentTimeSlot === 'morning') return s.timeOfDay === 'morning' || s.timeOfDay === 'any';
              if (currentTimeSlot === 'afternoon') return s.timeOfDay === 'afternoon' || s.timeOfDay === 'any';
              if (currentTimeSlot === 'evening') return s.timeOfDay === 'evening' || s.timeOfDay === 'any';
              return true;
            }

            // Fallback: keyword-based filtering for old format suggestions
            const text = `${s.label} ${s.description}`.toLowerCase();
            if (isBeforeNoon) {
              return !eveningKeywords.some(kw => text.includes(kw));
            } else if (isAfternoon) {
              return !eveningKeywords.some(kw => text.includes(kw)) &&
                     !morningKeywords.some(kw => text.includes(kw));
            } else if (isEvening) {
              return !morningKeywords.some(kw => text.includes(kw));
            }
            return true;
          });

          // Filter out already-used suggestions
          const unusedFiltered = filtered.filter(s => !usedSuggestionLabels.has(s.label));
          if (unusedFiltered.length > 0) {
            suggestions = unusedFiltered;
          } else {
            // If all time-appropriate suggestions are used, fall back to "any" time suggestions
            let fallback = aiSuggestions
              .map(s => ({ emoji: s.emoji || '📍', label: s.activity, description: s.description, timeOfDay: s.timeOfDay }))
              .filter(s => !usedSuggestionLabels.has(s.label) && (s.timeOfDay === 'any' || !s.timeOfDay));
            suggestions = fallback;
          }
        } else {
          // Fallback: time-appropriate location-specific suggestions
          const nearbyCtx = fromLocation ? ` near ${fromLocation.split(',')[0]}` : (baseCity ? ` in ${baseCity}` : '');
          const cityCtx = baseCity || 'the area';
          let fallbackSuggestions;
          if (isBeforeNoon) {
            fallbackSuggestions = [
              { emoji: '☕', label: `Morning café${nearbyCtx}`, description: `Discover a local café in ${cityCtx}` },
              { emoji: '📸', label: `Photo walk in ${cityCtx}`, description: 'Morning light is perfect for photos' },
              { emoji: '🏪', label: `Local market${nearbyCtx}`, description: `Browse morning markets and street vendors` }
            ];
          } else if (isAfternoon) {
            fallbackSuggestions = [
              { emoji: '🏛️', label: `Visit a landmark${nearbyCtx}`, description: `Explore a nearby attraction in ${cityCtx}` },
              { emoji: '☕', label: `Afternoon café${nearbyCtx}`, description: `Relax at a cozy spot in ${cityCtx}` },
              { emoji: '🛍️', label: `Shopping in ${cityCtx}`, description: 'Browse local shops and boutiques' }
            ];
          } else {
            // Evening
            fallbackSuggestions = [
              { emoji: '🌅', label: `Sunset viewpoint${nearbyCtx}`, description: `Catch golden hour in ${cityCtx}` },
              { emoji: '🍷', label: `Nightlife in ${cityCtx}`, description: 'Discover bars and restaurants locals love' },
              { emoji: '🌃', label: `Night walk${nearbyCtx}`, description: `Experience ${cityCtx} lit up at night` }
            ];
          }
          suggestions = fallbackSuggestions.filter(s => !usedSuggestionLabels.has(s.label));
        }

        // Take up to 3 suggestions and mark them as used
        const selectedSuggestions = suggestions.slice(0, 3);
        selectedSuggestions.forEach(s => usedSuggestionLabels.add(s.label));

        freeTimeCards.push({
          type: 'free-time',
          time: `${String(freeTimeStartHours).padStart(2, '0')}:${String(freeTimeStartMins).padStart(2, '0')}`,
          activity: 'Free Time',
          description: `Time to explore ${baseCity || 'at your own pace'}`,
          duration: durationStr,
          durationMinutes: cappedGapMinutes,
          sortTime: currentEndMinutes,
          suggestions: selectedSuggestions,
          baseCity: baseCity,
          fromLocation: fromLocation,
          toLocation: toLocation
        });
      }
    }

    // Add free time cards to timeline and re-sort (with type tiebreaker)
    timeline.push(...freeTimeCards);
    timeline.sort(timelineSortComparator);

    // Remove transit cards that are directly adjacent to free time blocks,
    // but ONLY if no real activity occupies the same time slot.
    // When a user adds an activity via "Ask Arrivo", the activity replaces the free time
    // at the same time. In that case, keep the transit to connect activity → next item.
    const freeTimeAdjacentTransitTypes = ['transit', 'hotel-transit', 'return-transit'];
    // Check if any real activity/meal exists at the same time as a free-time card
    const freeTimeHasActivity = new Set();
    timeline.forEach(item => {
      if (item.type === 'free-time') {
        const hasRealItem = timeline.some(other =>
          other !== item &&
          (other.type === 'activity' || other.type === 'meal') &&
          Math.abs((other.sortTime || 0) - (item.sortTime || 0)) < 30 // within 30 min
        );
        if (hasRealItem) freeTimeHasActivity.add(item.sortTime);
      }
    });

    // First: remove free-time cards that have been replaced by actual activities
    const timelineWithoutReplacedFreeTime = timeline.filter(item => {
      if (item.type === 'free-time' && freeTimeHasActivity.has(item.sortTime)) return false;
      return true;
    });

    // Then: remove transit adjacent to remaining (unoccupied) free-time cards
    const timelineAfterFreeTimeCleanup = timelineWithoutReplacedFreeTime.filter((item, idx, arr) => {
      if (!freeTimeAdjacentTransitTypes.includes(item.type)) return true;
      const prevItem = idx > 0 ? arr[idx - 1] : null;
      const nextItem = idx < arr.length - 1 ? arr[idx + 1] : null;
      if (nextItem?.type === 'free-time') return false;
      if (prevItem?.type === 'free-time') return false;
      return true;
    });
    timeline.length = 0;
    timeline.push(...timelineAfterFreeTimeCleanup);

    // De-duplicate transit cards - check BOTH transitKey AND from/to locations
    // This catches cases where different transit keys (e.g., 'transit-1' vs 'post-freetime-3')
    // represent the same physical transit segment
    const seenTransitKeys = new Set();
    const seenFromToSegments = new Set(); // Track unique from/to combinations
    const transitTypes = ['transit', 'return-transit', 'hotel-transit', 'airport-transit', 'station-transit', 'port-transit', 'inter-city-travel', 'drive'];

    const deduplicatedTimeline = timeline.filter(item => {
      if (transitTypes.includes(item.type)) {
        // First check: transitKey-based deduplication
        if (item.transitKey && seenTransitKeys.has(item.transitKey)) {
          timelineLog(`  🔄 Duplicate transit (key): ${item.transitKey}`);
          return false; // Skip duplicate
        }

        // Second check: from/to segment deduplication (catches different keys for same segment)
        // Normalize location names for comparison
        const fromNorm = (item.from || '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 40);
        const toNorm = (item.to || '').toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 40);
        const segmentKey = `${fromNorm}-->${toNorm}`;

        if (seenFromToSegments.has(segmentKey)) {
          timelineLog(`  🔄 Duplicate transit (segment): ${item.from} → ${item.to} (key: ${item.transitKey})`);
          return false; // Skip duplicate - same from/to already exists
        }

        // Add to tracking sets
        if (item.transitKey) {
          seenTransitKeys.add(item.transitKey);
        }
        seenFromToSegments.add(segmentKey);
      }
      return true;
    });

    // ADDITIONAL: Remove consecutive transit cards (can't have two transits back-to-back)
    // Keep the transit that has valid data OR connects to the next activity
    // IMPORTANT: Don't filter out inter-city-travel cards - they should appear between station-transit cards
    const localTransitTypes = ['transit', 'return-transit', 'hotel-transit', 'airport-transit', 'station-transit', 'port-transit', 'drive'];
    const finalTimeline = deduplicatedTimeline.filter((item, idx, arr) => {
      if (idx === 0) return true;
      const prevItem = arr[idx - 1];
      // Only filter LOCAL transit types (not inter-city-travel which is the main travel between cities)
      const currentIsLocalTransit = localTransitTypes.includes(item.type);
      const prevIsLocalTransit = localTransitTypes.includes(prevItem.type);

      if (currentIsLocalTransit && prevIsLocalTransit) {
        // Two transits back-to-back - decide which to keep

        // Check if either has N/A or invalid data (indicates bad routing)
        const prevHasInvalidData = (prevItem.duration || '').includes('N/A') ||
          prevItem.duration === '~15 mins' && (!prevItem.transitKey || prevItem.transitKey.includes('post-freetime'));
        const currentHasInvalidData = (item.duration || '').includes('N/A') ||
          item.duration === '~15 mins' && (!item.transitKey || item.transitKey.includes('post-freetime'));

        // If prev has invalid data but current doesn't, remove prev (keep current)
        if (prevHasInvalidData && !currentHasInvalidData) {
          timelineLog(`  🔄 Keeping current transit (prev had invalid data): ${item.from} → ${item.to}`);
          return true;
        }

        // If current has invalid data but prev doesn't, remove current
        if (currentHasInvalidData && !prevHasInvalidData) {
          timelineLog(`  🔄 Removing current transit (invalid data): ${item.from} → ${item.to}`);
          return false;
        }

        // Look ahead to find the next non-transit item
        let nextNonTransit = null;
        for (let j = idx + 1; j < arr.length; j++) {
          if (!localTransitTypes.includes(arr[j].type)) {
            nextNonTransit = arr[j];
            break;
          }
        }

        // If this transit's "to" matches the next activity better, keep it
        // Otherwise, skip it (the previous transit will handle it)
        if (nextNonTransit) {
          const nextLocation = (nextNonTransit.location || nextNonTransit.restaurant || '').toLowerCase();
          const currentTo = (item.to || '').toLowerCase();
          const prevTo = (prevItem.to || '').toLowerCase();

          // If current transit goes directly to the next activity and prev doesn't, keep current
          if (nextLocation && currentTo.includes(nextLocation.slice(0, 15)) && !prevTo.includes(nextLocation.slice(0, 15))) {
            return true;
          }
        }

        debugLog(`  🔄 Removing consecutive transit: ${item.from} → ${item.to} (prev was also transit)`);
        return false;
      }
      return true;
    });

    // CLEANUP: Remove any transit/free-time items that appear AFTER the end-of-day hotel return
    // The "Arrive Back at Hotel" or "End of Day at Hotel" should be the last meaningful item
    const endOfDayTypes = ['hotel-return'];
    const endOfDayIdx = finalTimeline.findLastIndex(item => endOfDayTypes.includes(item.type));
    let cleanedTimeline = finalTimeline;
    if (endOfDayIdx >= 0 && endOfDayIdx < finalTimeline.length - 1) {
      const afterEndOfDay = finalTimeline.slice(endOfDayIdx + 1);
      const hasOnlyTransitOrFreeTime = afterEndOfDay.every(item =>
        transitTypes.includes(item.type) || item.type === 'free-time'
      );
      if (hasOnlyTransitOrFreeTime) {
        cleanedTimeline = finalTimeline.slice(0, endOfDayIdx + 1);
        debugLog(`  🧹 Removed ${afterEndOfDay.length} phantom items after end-of-day hotel return`);
      }
    }

    // CHRONOLOGICAL VALIDATION PASS — fix overlapping items and ensure correct order
    const validatedTimeline = (() => {
      const sorted = [...cleanedTimeline].sort(timelineSortComparator);

      // Check for time overlaps and shift later items
      for (let i = 1; i < sorted.length; i++) {
        const prev = sorted[i - 1];
        const curr = sorted[i];
        const prevDuration = parseDurationToMinutes(prev.duration);
        const prevEndMinutes = prev.sortTime + prevDuration;

        // If current item starts before previous item ends, shift it
        // Allow meals to overlap with activities by up to 15 min (parallel scheduling)
        // but fix larger overlaps to prevent double-stacking
        const isMealOverlap = (prev.type === 'meal' || curr.type === 'meal') ||
          (prev.type === 'activity' && curr.type === 'meal') ||
          (prev.type === 'meal' && curr.type === 'activity');
        const mealOverlapThreshold = isMealOverlap ? 15 : 0;

        if (curr.sortTime < prevEndMinutes - mealOverlapThreshold && prevEndMinutes > 0) {
          // Only shift if the gap is significant (> 5 min overlap)
          const overlap = prevEndMinutes - curr.sortTime;
          if (overlap > 5) {
            const newSortTime = prevEndMinutes + 5; // 5 min buffer
            const newHours = Math.floor((newSortTime % 1440) / 60);
            const newMins = (newSortTime % 1440) % 60;
            curr.sortTime = newSortTime;
            curr.time = `${String(newHours).padStart(2, '0')}:${String(newMins).padStart(2, '0')}`;
          }
        }
      }

      return sorted;
    })();

    // C1: MEAL COVERAGE — ensure every day has breakfast, lunch, and dinner
    // AI sometimes omits meals, especially on multi-stop days with lots of transit
    // hasMealType now uses shared helper from displayGuardrails
    const hasThisMealType = (type) => hasMealType(validatedTimeline, type);
    // Don't inject meals on departure days with very early flights (before 7am) or arrival days
    const hasVeryEarlyDeparture = validatedTimeline.some(item =>
      (item.type === 'departure' || item.type === 'transit-departure') && item.sortTime < 420
    );
    const hasLateDeparture = validatedTimeline.some(item =>
      (item.type === 'departure' || item.type === 'transit-departure') && item.sortTime > 1200
    );
    // Find arrival time on this day (airport arrival, checkin, etc.) — don't inject meals BEFORE arrival
    const arrivalEvent = validatedTimeline.find(item =>
      item.type === 'airport-arrival' || item.type === 'transit-arrival' ||
      (item.type === 'arrival' || (item.activity || '').toLowerCase().includes('arrival'))
    );
    const arrivalSortTime = arrivalEvent?.sortTime || 0;
    const isArrivalDay = isFirstDay || !!arrivalEvent;
    const dayCity = dayData?.baseCity || '';
    const dayHotelForMeals = dayData?.hotel?.name || 'Hotel';

    // D2: On travel days, breakfast uses previous day's hotel (user wakes up there)
    const breakfastCtx = getBreakfastHotelContext({
      isInterCityTravelDay: isInterCityTravelDay && !isFirstDay,
      prevHotelName: prevDayHotelName,
      hotelName: dayHotelForMeals,
      prevCity: prevDayData?.baseCity,
      currentCity: dayCity,
    });
    const breakfastHotel = breakfastCtx.hotel;
    const breakfastCity = breakfastCtx.city;

    // D3: Build transport context for meal blocking
    const mealTransportCtx = {
      transportDepartureMinutes: icTransportDepartureMinutes,
      transportArrivalMinutes: icTransportArrivalMinutes,
    };

    // D10: isResortStay now uses shared helper from displayGuardrails
    const isResortStay = isResortStayShared(dayHotelForMeals, accommodationType);

    // Fix 2: Skip ALL meal injection on overnight arrival days.
    // User is in-flight until arrival — display layer shouldn't inject breakfast/lunch/dinner
    // at the destination hotel. AI-provided post-arrival meals (surviving cleanOvernightArrivalDay) are sufficient.
    const skipMealInjectionForOvernightArrival = isArrivalDayAfterOvernightTravel;
    if (skipMealInjectionForOvernightArrival) {
      debugLog(`🍽️ Skipping all meal injection on day ${dayIndex + 1} — overnight arrival day`);
    }

    if (!hasThisMealType('breakfast') && !hasVeryEarlyDeparture && !isFirstDay && !skipMealInjectionForOvernightArrival) {
      // D3: Skip breakfast if blocked by transport (e.g., very early departure flight)
      const breakfastBlocked = isInterCityTravelDay && isMealBlockedByTransport('breakfast', mealTransportCtx);
      if (!breakfastBlocked) {
        // D2: Use correct hotel context for breakfast
        validatedTimeline.push({
          type: 'meal', meal: 'Breakfast', mealType: 'breakfast', originalType: 'breakfast',
          time: '08:30', activity: 'Breakfast',
          restaurant: breakfastHotel !== 'Hotel' ? `${breakfastHotel} Restaurant` : (breakfastCity ? `Breakfast in ${breakfastCity}` : 'Hotel Breakfast'),
          description: breakfastCity ? `Start the day with breakfast in ${breakfastCity}` : 'Start the day with a local breakfast',
          duration: '45 minutes', location: breakfastHotel, sortTime: 510,
          isDisplayInjected: true, _needsReplacement: breakfastHotel === 'Hotel',
        });
        debugLog(`🍳 Injected missing breakfast on day ${dayIndex + 1} (hotel: ${breakfastHotel})`);
      } else {
        debugLog(`🍳 Skipped breakfast on day ${dayIndex + 1} — blocked by transport`);
      }
    }

    // D6: Shift breakfast earlier if it overlaps with next timeline item
    const injectedBreakfast = validatedTimeline.find(i => i.meal === 'Breakfast' && i.sortTime === 510);
    if (injectedBreakfast) {
      const breakfastEnd = 510 + 45; // 08:30 + 45 min = 09:15
      const earliestNonMeal = validatedTimeline
        .filter(i => i.type !== 'meal' && i.sortTime > 0)
        .sort((a, b) => a.sortTime - b.sortTime)[0];
      if (earliestNonMeal && earliestNonMeal.sortTime < breakfastEnd) {
        const newBreakfastStart = Math.max(420, earliestNonMeal.sortTime - 45); // At least 7:00 AM
        const h = Math.floor(newBreakfastStart / 60);
        const m = newBreakfastStart % 60;
        injectedBreakfast.time = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
        injectedBreakfast.sortTime = newBreakfastStart;
        debugLog(`🍳 Shifted breakfast earlier to ${injectedBreakfast.time} to avoid overlap`);
      }
    }

    // Only inject lunch if: not last day, and not before arrival on arrival days
    if (!hasThisMealType('lunch') && !isLastDay && !(isArrivalDay && arrivalSortTime > 720) && !skipMealInjectionForOvernightArrival) {
      // D3: Skip lunch if blocked by transport (e.g., in-flight)
      const lunchBlocked = isInterCityTravelDay && isMealBlockedByTransport('lunch', mealTransportCtx);
      if (!lunchBlocked) {
        // Skip lunch if arrival is after noon (720 = 12:00) — meal would appear before the traveler lands
        const defaultLunchTime = isArrivalDay ? Math.max(780, arrivalSortTime + 60) : 780; // After arrival + 1hr buffer, or 13:00
        // Fix 4: Use collision-aware slot finder instead of hardcoded time
        const lunchSlot = findMealSlot('lunch', validatedTimeline.filter(i => i.sortTime > 0), defaultLunchTime);
        if (lunchSlot !== null) {
          const lunchHours = Math.floor(lunchSlot / 60);
          const lunchMins = lunchSlot % 60;
          validatedTimeline.push({
            type: 'meal', meal: 'Lunch', mealType: 'lunch', originalType: 'lunch',
            time: `${String(lunchHours).padStart(2, '0')}:${String(lunchMins).padStart(2, '0')}`,
            activity: 'Lunch',
            restaurant: isResortStay ? `${dayHotelForMeals} Restaurant` : (dayCity ? `Lunch in ${dayCity}` : 'Local Lunch'),
            description: isResortStay ? `Lunch at ${dayHotelForMeals}` : (dayCity ? `Explore local lunch options in ${dayCity}` : 'Enjoy a local lunch'),
            duration: '1 hour', location: isResortStay ? dayHotelForMeals : dayCity, sortTime: lunchSlot,
            isDisplayInjected: true, _needsReplacement: !isResortStay,
          });
          debugLog(`🍽️ Injected missing lunch on day ${dayIndex + 1} at slot ${lunchSlot} (default was ${defaultLunchTime})`);
        } else {
          debugLog(`🍽️ Skipped lunch on day ${dayIndex + 1} — no collision-free slot found`);
        }
      } else {
        debugLog(`🍽️ Skipped lunch on day ${dayIndex + 1} — blocked by transport`);
      }
    }
    if (!hasThisMealType('dinner') && !hasLateDeparture && !isLastDay && !skipMealInjectionForOvernightArrival) {
      // D3: Skip dinner if blocked by transport
      const dinnerBlocked = isInterCityTravelDay && isMealBlockedByTransport('dinner', mealTransportCtx);
      if (!dinnerBlocked) {
        // Fix 4: Use collision-aware slot finder instead of hardcoded time
        const dinnerSlot = findMealSlot('dinner', validatedTimeline.filter(i => i.sortTime > 0), 1170);
        if (dinnerSlot !== null) {
          const dinnerHours = Math.floor(dinnerSlot / 60);
          const dinnerMins = dinnerSlot % 60;
          validatedTimeline.push({
            type: 'meal', meal: 'Dinner', mealType: 'dinner', originalType: 'dinner',
            time: `${String(dinnerHours).padStart(2, '0')}:${String(dinnerMins).padStart(2, '0')}`,
            activity: 'Dinner',
            restaurant: isResortStay ? `${dayHotelForMeals} Restaurant` : '{TBD: pick a dinner spot}',
            description: isResortStay ? `Dinner at ${dayHotelForMeals}` : '',
            duration: '1 hour 30 minutes', location: isResortStay ? dayHotelForMeals : dayCity, sortTime: dinnerSlot,
            isDisplayInjected: true, _needsReplacement: !isResortStay,
          });
          debugLog(`🍽️ Injected missing dinner on day ${dayIndex + 1} at slot ${dinnerSlot}`);
        } else {
          debugLog(`🍽️ Skipped dinner on day ${dayIndex + 1} — no collision-free slot found`);
        }
      } else {
        debugLog(`🍽️ Skipped dinner on day ${dayIndex + 1} — blocked by transport`);
      }
    }
    // Re-sort if meals were added
    validatedTimeline.sort(timelineSortComparator);

    // MEAL DEDUPLICATION — remove duplicate meal types (e.g., two breakfasts from different injection phases)
    // Keep the first occurrence of each meal type (breakfast/lunch/dinner) and remove later duplicates
    const seenMealTypes = new Set();
    for (let i = validatedTimeline.length - 1; i >= 0; i--) {
      const item = validatedTimeline[i];
      if (item.type !== 'meal') continue;
      const mealKey = (item.meal || item.mealType || item.activity || '').toLowerCase();
      const normalizedType = mealKey.includes('breakfast') ? 'breakfast' :
        mealKey.includes('lunch') ? 'lunch' :
        mealKey.includes('dinner') ? 'dinner' :
        mealKey.includes('brunch') ? 'brunch' : mealKey;
      if (!normalizedType) continue;
      if (seenMealTypes.has(normalizedType)) {
        // Duplicate — remove the display-injected one (prefer AI-generated)
        if (item.isDisplayInjected) {
          debugLog(`🍽️ Dedup: Removed injected duplicate ${normalizedType} on day ${dayIndex + 1}`);
          validatedTimeline.splice(i, 1);
        }
      } else {
        seenMealTypes.add(normalizedType);
      }
    }
    // If duplicates remain after preferring AI-generated, do a second pass keeping earliest
    const seenMealTypes2 = new Set();
    for (let i = 0; i < validatedTimeline.length; i++) {
      const item = validatedTimeline[i];
      if (item.type !== 'meal') continue;
      const mealKey = (item.meal || item.mealType || item.activity || '').toLowerCase();
      const normalizedType = mealKey.includes('breakfast') ? 'breakfast' :
        mealKey.includes('lunch') ? 'lunch' :
        mealKey.includes('dinner') ? 'dinner' :
        mealKey.includes('brunch') ? 'brunch' : mealKey;
      if (!normalizedType) continue;
      if (seenMealTypes2.has(normalizedType)) {
        debugLog(`🍽️ Dedup: Removed extra ${normalizedType} on day ${dayIndex + 1} (keeping earlier)`);
        validatedTimeline.splice(i, 1);
        i--; // Re-check current index
      } else {
        seenMealTypes2.add(normalizedType);
      }
    }

    // Fix 6: Pre-dawn safety cap — remove non-logistics items before 6 AM
    // Belt-and-suspenders with the shared processDayForDisplay orchestrator
    const preDawnCount = validatedTimeline.length;
    const safetyCapped = filterPreDawnItems(validatedTimeline);
    if (safetyCapped.length < preDawnCount) {
      debugLog(`⚠️ Fix 6: Removed ${preDawnCount - safetyCapped.length} pre-dawn items on day ${dayIndex + 1}`);
      validatedTimeline.length = 0;
      validatedTimeline.push(...safetyCapped);
    }

    // B3: LOOK-AHEAD — If the NEXT day has overnight transport arriving,
    // inject departure logistics (checkout, airport transit, flight card) into THIS day's timeline.
    // This fixes the bug where overnight flight departure logistics were shown on the arrival day.
    if (dayIndex >= 0 && itinerary?.days?.[dayIndex + 1]) {
      const nextDayIndex = dayIndex + 1;
      const nextDayTransport = (itinerary.interCityTransport || []).find(t => t.day === nextDayIndex + 1);
      const isNextDayOvernightArrival = nextDayTransport &&
        (nextDayTransport.isOvernight || nextDayTransport.note?.toLowerCase()?.includes('overnight'));

      if (isNextDayOvernightArrival && !isLastDay) {
        const departureTime = nextDayTransport.departureTime || '22:00';
        const [depH, depM] = departureTime.split(':').map(Number);
        const depMinutes = (depH || 22) * 60 + (depM || 0);
        const transportMode = nextDayTransport.mode || 'flight';
        const isFlight = transportMode === 'flight';
        const nextCity = itinerary.days[nextDayIndex]?.baseCity || '';
        const currentCity = dayData?.baseCity || '';

        // Determine airport/station names
        const fromIata = lookupCityAirportCode(currentCity);
        const toIata = lookupCityAirportCode(nextCity);
        const fromAirport = nextDayTransport.from?.airport || (fromIata ? `${fromIata} International Airport` : `${currentCity} Airport`);
        const toAirport = nextDayTransport.to?.airport || (toIata ? `${toIata} International Airport` : `${nextCity} Airport`);
        const carrier = nextDayTransport.carrier || nextDayTransport.airline || '';
        const duration = nextDayTransport.duration || '';

        // Airport transit (2h before departure for flights)
        const transitBuffer = isFlight ? 120 : 60;
        const transitMinutes = depMinutes - transitBuffer;

        if (transitMinutes > 0) {
          // Add "Head to Airport" card
          validatedTimeline.push({
            type: 'airport-transit',
            time: `${String(Math.floor(transitMinutes / 60)).padStart(2, '0')}:${String(transitMinutes % 60).padStart(2, '0')}`,
            activity: `🚕 Head to ${isFlight ? fromAirport : `${currentCity} Station`}`,
            description: `Arrive ${isFlight ? '2 hours' : '1 hour'} before departure for your overnight ${isFlight ? 'flight' : transportMode} to ${nextCity}`,
            location: isFlight ? fromAirport : `${currentCity} Station`,
            duration: `${transitBuffer} minutes`,
            sortTime: transitMinutes,
            isOvernightDeparture: true,
          });

          // Add departure flight/train card
          validatedTimeline.push({
            type: 'inter-city-travel',
            time: departureTime,
            activity: `${isFlight ? '✈️' : '🚆'} Overnight ${isFlight ? 'Flight' : 'Train'} to ${nextCity}`,
            description: `${carrier ? `${carrier} ` : ''}${isFlight ? 'Flight' : 'Train'} departing at ${departureTime}${duration ? ` (${duration})` : ''}. Arriving ${nextCity} tomorrow.`,
            location: `${isFlight ? (fromIata || currentCity) : currentCity} → ${isFlight ? (toIata || nextCity) : nextCity}`,
            duration: duration || 'Overnight',
            sortTime: depMinutes,
            isOvernightDeparture: true,
            fromCity: currentCity,
            toCity: nextCity,
            fromAirportCode: fromIata,
            toAirportCode: toIata,
          });

          debugLog(`✈️ B3: Injected overnight departure logistics into Day ${dayIndex + 1} for ${currentCity}→${nextCity}`);
        }

        // Re-sort after injection
        validatedTimeline.sort(timelineSortComparator);
      }
    }

    debugLog(`🏁 Day ${dayIndex + 1} timeline complete: ${validatedTimeline.length} items (${validatedTimeline.filter(i => transitTypes.includes(i.type)).length} transit cards)`);
    return validatedTimeline;
  };

  // isDayTooPacked wrapper removed 2026-06: its only caller was the
  // "⚠️ Packed Day" header badge, also removed. Shared helper kept in
  // displayGuardrails.js for any future use.


  // Calculate total travel time for a day
  const getTotalTravelTime = (dayData) => {
    if (!itinerary || !itinerary.transitData) return 0;
    
    let totalMinutes = 0;
    const locations = [];
    
    if (dayData.schedule) {
      dayData.schedule.forEach(act => {
        if (act.location) locations.push({ name: act.location, time: act.time });
      });
    }
    
    if (dayData.meals) {
      dayData.meals.forEach(meal => {
        if (meal.restaurant) locations.push({ name: meal.restaurant, time: meal.time });
      });
    }
    
    locations.sort((a, b) => parseTime(a.time) - parseTime(b.time));
    
    for (let i = 0; i < locations.length - 1; i++) {
      const travelData = findTransitData(itinerary.transitData, locations[i].name, locations[i + 1].name);
      if (travelData) {
        totalMinutes += travelData.duration;
      } else {
        totalMinutes += 15; // Default estimate
      }
    }
    
    return totalMinutes;
  };
  
  // Check if any transit leg is too long (>30 min)
  const hasLongTransit = (dayData) => {
    if (!itinerary || !dayData) return false;

    const locations = [];

    if (dayData.schedule && Array.isArray(dayData.schedule)) {
      dayData.schedule.forEach(act => {
        if (act && act.location) locations.push({ name: act.location, time: act.time });
      });
    }

    if (dayData.meals && Array.isArray(dayData.meals)) {
      dayData.meals.forEach(meal => {
        if (meal && meal.restaurant) locations.push({ name: meal.restaurant, time: meal.time });
      });
    }
    
    locations.sort((a, b) => parseTime(a.time) - parseTime(b.time));
    
    for (let i = 0; i < locations.length - 1; i++) {
      const travelData = findTransitData(itinerary?.transitData, locations[i].name, locations[i + 1].name);
      if (travelData && travelData.duration > 30) {
        return true;
      }
    }
    
    return false;
  };

  // Helper: Calculate transit time between locations
  const calculateTransitTime = async (from, to, mode = 'drive') => {
    // Use the real Google Maps getTransitTime function
    try {
      const result = await getTransitTime(from, to, mode);
      if (result && result.duration) {
        return result.duration; // Returns minutes
      }
    } catch (error) {
      debugError('Transit time calculation error:', error);
    }
    
    // Fallback only if API fails
    return mode === 'walk' ? 20 : mode === 'transit' ? 25 : 15;
  };

  // Helper: Parse destination and temporal info
  const parseDestinationInput = (input) => {
    const temporalKeywords = ['in january', 'in february', 'in march', 'in april', 'in may', 'in june',
                              'in july', 'in august', 'in september', 'in october', 'in november', 'in december',
                              'next month', 'next summer', 'next winter', 'next spring', 'next fall'];
    
    let destination = input;
    let timeframe = null;
    
    for (const keyword of temporalKeywords) {
      if (input.toLowerCase().includes(keyword)) {
        destination = input.toLowerCase().replace(keyword, '').trim();
        timeframe = keyword.replace('in ', '').trim();
        break;
      }
    }
    
    return { destination, timeframe };
  };

  // ScrollableMessageContent is defined OUTSIDE this component (above) to prevent
  // React from unmounting/remounting it on every parent re-render (which causes flicker).

  // Gantt Chart Component
  const GanttChart = ({ timeline }) => {
    // Find actual time range from timeline data
    const timeValues = timeline.map(item => parseTime(item.time)).filter(t => !isNaN(t) && t >= 0);
    const durationValues = timeline.map(item => parseDurationToMinutes(item.duration));
    
    const earliestTime = timeValues.length > 0 ? Math.min(...timeValues) : 360; // 6 AM default
    const latestTime = timeValues.length > 0 ? Math.max(...timeValues.map((t, i) => t + durationValues[i])) : 1440; // midnight default
    
    // Round to nearest hour for cleaner display
    const startHour = Math.floor(earliestTime / 60);
    const endHour = Math.ceil(latestTime / 60);
    const totalHours = Math.max(endHour - startHour, 6); // Minimum 6 hours
    
    // Debug: Log timeline data
    debugLog('GanttChart received', timeline.length, 'items');
    debugLog(`Time range: ${startHour}:00 to ${endHour}:00 (${totalHours} hours)`);
    
    // Detect collisions and organize into rows
    const organizeIntoRows = (timeline) => {
      const rows = [];
      const totalMinutes = totalHours * 60;
      const startMinutes = startHour * 60;

      timeline.forEach((item, itemIdx) => {
        const itemStart = parseTime(item.time);

        // Debug: Check if parseTime is working
        if (itemStart === 0 && item.time !== '0:00' && item.time !== '00:00') {
          debugWarn(`Warning: parseTime returned 0 for time "${item.time}" on item:`, item.activity || item.restaurant);
        }

        const durationMins = parseDurationToMinutes(item.duration);
        const itemEnd = itemStart + durationMins;

        // Skip items with invalid times
        if (isNaN(itemStart) || itemStart < 0) {
          debugError(`Skipping item with invalid time:`, item);
          return;
        }

        // Calculate visual end including minimum width enforcement
        // This prevents items from visually overlapping when minimum widths are applied
        const isTransit = item.type === 'transit' || item.type === 'hotel-transit' || item.type === 'airport-transit';
        const minWidthMins = (isTransit ? 4.5 : 3.5) / 100 * totalMinutes;
        const visualEnd = itemStart + Math.max(durationMins, minWidthMins);

        // Find first row where this item doesn't overlap (using visual bounds)
        let placedInRow = false;
        for (let i = 0; i < rows.length; i++) {
          const rowHasOverlap = rows[i].some(existing => {
            return (itemStart < existing.visualEnd && visualEnd > existing.start);
          });

          if (!rowHasOverlap) {
            rows[i].push({ ...item, start: itemStart, end: itemEnd, visualEnd });
            placedInRow = true;
            break;
          }
        }

        // If no suitable row found, create new row
        if (!placedInRow) {
          rows.push([{ ...item, start: itemStart, end: itemEnd, visualEnd }]);
        }
      });

      return rows;
    };
    
    const rows = organizeIntoRows(timeline);
    const rowHeight = 60; // Increased height for better visibility
    
    // Debug: Log organized rows
    debugLog(`Organized into ${rows.length} rows`);
    rows.forEach((row, idx) => {
      debugLog(`Row ${idx}: ${row.length} items`, row.map(i => `${i.activity || i.restaurant} (${i.start}-${i.end}min)`));
    });
    
    return (
      <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg text-gray-800 flex items-center gap-2" style={FONTS.headingSemibold}><BrandClock size={18} /> Daily Timeline</h3>
          <span className="text-sm text-gray-500" style={FONTS.body}>{startHour}:00 - {endHour}:00</span>
        </div>
        
        {/* Time ruler - dynamic based on actual times */}
        <div className="flex border-b border-gray-200 pb-2 mb-4">
          {Array.from({ length: Math.ceil(totalHours / 2) + 1 }, (_, i) => {
            const hour = startHour + (i * 2);
            if (hour > endHour) return null;
            const wrappedHour = ((hour % 24) + 24) % 24; // Handle hours past midnight
            const displayHour = wrappedHour > 12 ? wrappedHour - 12 : (wrappedHour === 0 ? 12 : wrappedHour);
            const period = wrappedHour >= 12 ? 'PM' : 'AM';
            return (
              <div key={i} className="flex-1 text-center text-xs text-gray-600" style={FONTS.body}>
                {displayHour}{period}
              </div>
            );
          })}
        </div>
        
        {/* Show message if no rows */}
        {rows.length === 0 && (
          <div className="text-center text-gray-400 py-8" style={FONTS.body}>
            No timeline items to display
          </div>
        )}
        
        {/* Timeline bars - organized in rows */}
        <div className="relative" style={{ height: `${Math.max(rows.length, 1) * rowHeight}px`, minHeight: '60px' }}>
          {rows.map((row, rowIdx) => (
            <div key={rowIdx} className="absolute w-full" style={{ top: `${rowIdx * rowHeight}px`, height: `${rowHeight - 10}px` }}>
              {row.map((item, idx) => {
                const itemMinutes = item.start;
                const startMinutes = startHour * 60;
                const totalMinutes = totalHours * 60;
                
                const leftPercent = Math.max(0, ((itemMinutes - startMinutes) / totalMinutes) * 100);
                const durationMins = item.end - item.start;
                const rawWidthPercent = Math.min(100 - leftPercent, (durationMins / totalMinutes) * 100);
                // Enforce minimum width so items are visible and text is readable
                // Transit blocks need extra width since "T..." is unreadable
                const isTransitBlock = item.type === 'transit' || item.type === 'hotel-transit' || item.type === 'airport-transit';
                const minWidthPercent = isTransitBlock ? 4.5 : 3.5;
                const widthPercent = Math.min(100 - leftPercent, Math.max(rawWidthPercent, minWidthPercent));

                // Debug: Log positioning
                if (rawWidthPercent < 1) {
                  debugWarn(`Item too narrow to display: ${item.activity || item.restaurant}, width: ${rawWidthPercent}%`);
                }
                
                const colors = {
                  'arrival-customs': 'bg-blue-500',
                  'hotel-transit': 'bg-purple-500',
                  'hotel-checkin': 'bg-purple-600',
                  'activity': 'bg-brand-500',
                  'meal': 'bg-orange-500',
                  'transit': 'bg-gray-400',
                  'checkout': 'bg-purple-500',
                  'airport-transit': 'bg-gray-400',
                  'airport-arrival': 'bg-blue-400',
                  'departure': 'bg-blue-500'
                };
                
                if (widthPercent < 1) return null;
                
                return (
                  <div 
                    key={`${rowIdx}-${idx}`} 
                    className="absolute h-full group"
                    style={{
                      left: `${leftPercent}%`,
                      width: `${widthPercent}%`
                    }}
                  >
                    <div
                      className={`h-full rounded-lg ${colors[item.type] || 'bg-gray-500'} shadow-md hover:shadow-lg transition-all flex items-center px-2 text-white text-xs overflow-hidden cursor-pointer`}
                      style={{ width: 'calc(100% - 2px)', height: '100%', ...FONTS.body }}
                      title={`${item.activity || item.restaurant} - ${formatTimeDisplay(item.time)} (${item.duration})`}
                    >
                      <span className="truncate">{item.activity || item.restaurant}</span>
                    </div>
                    {/* Tooltip */}
                    <div className="absolute left-0 top-full mt-1 bg-gray-800 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none" style={FONTS.body}>
                      {formatTimeDisplay(item.time)} • {item.duration}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };


  const getActivityImage = () => null;
  const getRestaurantImage = () => null;

  // Calculate real travel time between two locations via backend proxy
  const calculateRealTravelTime = async (origin, destination, mode = 'driving') => {
    try {
      const modeMap = { 'drive': 'driving', 'transit': 'transit', 'walk': 'walking' };
      const travelMode = modeMap[mode] || 'driving';

      const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const session = await getSession();
      const token = session?.access_token;
      const response = await fetch(`${API_BASE}/api/directions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ origin, destination, mode: travelMode }),
      });
      const data = await response.json();

      if (data.duration_seconds) {
        return {
          duration: Math.ceil(data.duration_seconds / 60),
          distance: (data.distance_meters / 1000).toFixed(1),
          durationText: data.duration_text,
          distanceText: data.distance_text
        };
      }

      return null;
    } catch (error) {
      debugError('Error calculating travel time:', error);
      return null;
    }
  };

  // Calculate distances for all transit segments in itinerary
  const recalculateAllDistances = async () => {
    if (!itinerary) return;
    
    setShowLoadingScreen(true);
    setLoadingMessage('Calculating real travel times...');
    
    const updatedItinerary = { ...itinerary };
    
    // For each day, calculate distances
    for (let dayIdx = 0; dayIdx < updatedItinerary.days.length; dayIdx++) {
      const day = updatedItinerary.days[dayIdx];
      
      // Collect all locations in order
      const locations = [];
      
      if (day.schedule) {
        day.schedule.forEach(act => {
          if (act.location) locations.push({ name: act.location, time: act.time });
        });
      }
      
      if (day.meals) {
        day.meals.forEach(meal => {
          if (meal.restaurant) locations.push({ name: meal.restaurant, time: meal.time });
        });
      }
      
      // Sort by time
      locations.sort((a, b) => parseTime(a.time) - parseTime(b.time));
      
      // Calculate distances between consecutive locations
      for (let i = 0; i < locations.length - 1; i++) {
        const origin = locations[i].name;
        const destination = locations[i + 1].name;
        
        if (origin !== destination) {
          const travelData = await calculateRealTravelTime(
            `${origin}, ${itinerary.destination}`,
            `${destination}, ${itinerary.destination}`,
            'drive'
          );
          
          if (travelData) {
            // Store in a transit map for reference
            if (!updatedItinerary.transitData) {
              updatedItinerary.transitData = {};
            }
            updatedItinerary.transitData[`${origin}-${destination}`] = travelData;
          }
        }
      }
    }
    
    setItinerary(updatedItinerary);
    setShowLoadingScreen(false);
  };
  
  // Optimize route order by minimizing travel distance
  const optimizeRouteForDay = async (dayIndex) => {
    if (!itinerary?.days?.[dayIndex]) return;

    setLoadingState('optimizing');
    setOptimizingRoute(true);
    setShowLoadingScreen(true);
    setLoadingMessage('Optimizing route order...');

    const day = itinerary.days[dayIndex];
    const activities = [...(day.schedule || [])];
    const meals = [...(day.meals || [])];
    
    if (activities.length + meals.length < 3) {
      showAlertModal('Not Enough Stops', 'Need at least 3 stops to optimize the route.');
      setOptimizingRoute(false);
      setShowLoadingScreen(false);
      return;
    }
    
    // Combine all items with locations
    const items = [];
    activities.forEach((act, idx) => {
      if (act.location) {
        items.push({ ...act, type: 'activity', originalIndex: idx });
      }
    });
    meals.forEach((meal, idx) => {
      if (meal.restaurant) {
        items.push({ ...meal, type: 'meal', originalIndex: idx, location: meal.restaurant });
      }
    });
    
    if (items.length < 3) {
      showAlertModal('Not Enough Locations', 'Need at least 3 stops with known coordinates to optimize the route.');
      setOptimizingRoute(false);
      setShowLoadingScreen(false);
      return;
    }
    
    // Simple greedy algorithm: start with first item, always go to nearest unvisited
    const optimized = [items[0]];
    const remaining = items.slice(1);
    
    while (remaining.length > 0) {
      const current = optimized[optimized.length - 1];
      let nearest = remaining[0];
      let shortestDistance = Infinity;
      
      // Find nearest location to current
      for (const item of remaining) {
        const travelData = await calculateRealTravelTime(
          `${current.location}, ${itinerary.destination}`,
          `${item.location}, ${itinerary.destination}`,
          'drive'
        );
        
        if (travelData && travelData.duration < shortestDistance) {
          shortestDistance = travelData.duration;
          nearest = item;
        }
      }
      
      optimized.push(nearest);
      remaining.splice(remaining.indexOf(nearest), 1);
    }
    
    // Reassign times based on new order
    let currentTime = parseTime(items[0].time); // Keep first item's time
    const newActivities = [];
    const newMeals = [];
    
    for (let i = 0; i < optimized.length; i++) {
      const item = optimized[i];
      const hours = Math.floor(currentTime / 60);
      const minutes = currentTime % 60;
      const newTime = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
      
      const updatedItem = { ...item, time: newTime };
      delete updatedItem.type;
      delete updatedItem.originalIndex;
      
      if (item.type === 'activity') {
        newActivities.push(updatedItem);
      } else {
        delete updatedItem.location;
        newMeals.push(updatedItem);
      }
      
      // Add activity duration + 15 min transit to next
      // Parse compound durations like "2 hours 30 minutes"
      const durationMins = (() => {
        const str = (item.duration || '').toLowerCase();
        let totalMins = 0;
        const hMatch = str.match(/(\d+\.?\d*)\s*(?:hour|hr|h\b)/);
        if (hMatch) totalMins += parseFloat(hMatch[1]) * 60;
        const mMatch = str.match(/(\d+\.?\d*)\s*(?:min|m\b)/);
        if (mMatch) totalMins += parseFloat(mMatch[1]);
        if (totalMins === 0) {
          const fb = str.match(/(\d+\.?\d*)/);
          if (fb) { const v = parseFloat(fb[1]); totalMins = v < 12 ? v * 60 : v; }
          else totalMins = 60;
        }
        return totalMins;
      })();
      currentTime += durationMins + 15; // Activity + transit
    }
    
    // Update itinerary
    const updatedItinerary = { ...itinerary };
    updatedItinerary.days[dayIndex].schedule = newActivities;
    updatedItinerary.days[dayIndex].meals = newMeals;
    // Times changed on existing items — bump _cacheBust so timeline memo rebuilds
    bumpDayCacheBust(updatedItinerary.days[dayIndex]);

    setItinerary(updatedItinerary);
    setOptimizingRoute(false);
    setShowLoadingScreen(false);

    showToast('✨ Route optimized! Activities reordered by proximity.', 'success');
  };

  // EDIT FUNCTIONS
  const updateItemTime = (dayIndex, itemIndex, itemType, newTime) => {
    if (!itinerary?.days?.[dayIndex]) return;

    // Deep-clone the day so React detects all cascaded changes
    // (shallow spread only creates a new top-level ref; inner arrays/objects stay the same)
    const updatedItinerary = { ...itinerary };
    updatedItinerary.days = [...updatedItinerary.days];
    updatedItinerary.days[dayIndex] = bumpDayCacheBust({
      ...updatedItinerary.days[dayIndex],
      schedule: updatedItinerary.days[dayIndex].schedule.map(a => ({ ...a })),
      meals: updatedItinerary.days[dayIndex].meals.map(m => ({ ...m })),
    });
    const day = updatedItinerary.days[dayIndex];

    // Get old time to calculate delta
    let oldTime = 0;
    if (itemType === 'activity') {
      oldTime = parseTime(day.schedule[itemIndex].time);
      day.schedule[itemIndex].time = newTime;
    } else if (itemType === 'meal') {
      oldTime = parseTime(day.meals[itemIndex].time);
      day.meals[itemIndex].time = newTime;
    }

    const newTimeMinutes = parseTime(newTime);
    const timeDelta = newTimeMinutes - oldTime;

    // CASCADE: Shift all subsequent items on this day
    let shiftedCount = 0;
    if (Math.abs(timeDelta) > 0) {
      // Shift activities that come AFTER the edited time
      day.schedule.forEach((activity, idx) => {
        // When editing a meal: shift all activities whose time is after the old meal time
        // When editing an activity: shift only activities with higher index
        const shouldCheck = itemType === 'meal'
          ? true  // All activities are candidates (filtered by time below)
          : idx > itemIndex;
        if (shouldCheck) {
          const activityTime = parseTime(activity.time);
          if (activityTime >= oldTime && !(itemType === 'activity' && idx === itemIndex)) {
            const newActivityTime = activityTime + timeDelta;
            activity.time = formatTimeDisplay(minutesToTime(Math.max(0, newActivityTime)));
            shiftedCount++;
          }
        }
      });

      // Shift meals that come AFTER the edited time
      day.meals.forEach((meal, idx) => {
        // When editing an activity: shift all meals whose time is after old activity time
        // When editing a meal: shift only meals with higher index
        const shouldCheck = itemType === 'activity'
          ? true  // All meals are candidates (filtered by time below)
          : idx > itemIndex;
        if (shouldCheck) {
          const mealTime = parseTime(meal.time);
          if (mealTime >= oldTime && !(itemType === 'meal' && idx === itemIndex)) {
            const newMealTime = mealTime + timeDelta;
            meal.time = formatTimeDisplay(minutesToTime(Math.max(0, newMealTime)));
            shiftedCount++;
          }
        }
      });

      // Track this edit
      setUserEdits(prev => [...prev, {
        type: 'time_change',
        day: dayIndex,
        item: itemType,
        from: formatTimeDisplay(minutesToTime(oldTime)),
        to: newTime,
        cascaded: shiftedCount > 0
      }]);
    }

    // Mark that user has modified the itinerary
    itineraryUserModifiedRef.current = true;
    setHasUnsavedChanges(true);

    setItinerary(updatedItinerary);
    setEditingItem(null);

    // Clear transit cache for this day (times changed affects transit calculations)
    setTransitTimes(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (key.startsWith(`day${dayIndex}-transit-`) ||
            key.startsWith(`day${dayIndex}-last-to-hotel`) ||
            key.startsWith(`day${dayIndex}-hotel-to-first`)) {
          delete updated[key];
          delete transitTimesRef.current[key];
        }
      });
      return updated;
    });
    transitCalcGuardRef.current = null;
    const deltaSign = timeDelta > 0 ? '+' : '';
    const deltaHrs = Math.abs(timeDelta) >= 60 ? `${Math.floor(Math.abs(timeDelta) / 60)}h` : '';
    const deltaMins = Math.abs(timeDelta) % 60 > 0 ? `${Math.abs(timeDelta) % 60}m` : '';
    const deltaStr = `${deltaSign}${deltaHrs}${deltaMins}`;
    showToast(
      shiftedCount > 0
        ? `Time updated — ${shiftedCount} item${shiftedCount > 1 ? 's' : ''} shifted ${deltaStr}`
        : 'Time updated',
      'success',
      3000
    );
  };

  const updateItemDuration = (dayIndex, itemIndex, itemType, newDuration) => {
    if (!itinerary?.days?.[dayIndex]) return;
    const updatedItinerary = { ...itinerary };
    const day = updatedItinerary.days[dayIndex];

    if (itemType === 'activity') {
      day.schedule[itemIndex].duration = newDuration;
    }

    // In-place mutation — bump _cacheBust so timeline memo invalidates
    bumpDayCacheBust(day);
    setItinerary(updatedItinerary);
    setEditingItem(null);
  };

  // ─── Editable cost on any item ──────────────────────────────────────
  const updateItemCost = (dayIndex, itemIndex, itemType, newCost) => {
    if (!itinerary?.days?.[dayIndex]) return;
    const updatedItinerary = { ...itinerary };
    const day = bumpDayCacheBust({ ...updatedItinerary.days[dayIndex] });
    updatedItinerary.days[dayIndex] = day;

    const costVal = newCost === '' ? null : newCost;

    if (itemType === 'activity' && day.schedule?.[itemIndex]) {
      day.schedule = [...day.schedule];
      day.schedule[itemIndex] = { ...day.schedule[itemIndex], _userCost: costVal };
    } else if (itemType === 'meal' && day.meals?.[itemIndex]) {
      day.meals = [...day.meals];
      day.meals[itemIndex] = { ...day.meals[itemIndex], _userCost: costVal };
    } else if (itemType === 'hotel' && day.hotel) {
      day.hotel = { ...day.hotel, _userCost: costVal };
    } else if (itemType === 'transport') {
      if (day.interCityTransport) {
        day.interCityTransport = { ...day.interCityTransport, _userCost: costVal };
      } else if (day.schedule?.[itemIndex]) {
        day.schedule = [...day.schedule];
        day.schedule[itemIndex] = { ...day.schedule[itemIndex], _userCost: costVal };
      }
    }

    itineraryUserModifiedRef.current = true;
    setHasUnsavedChanges(true);
    setItinerary(updatedItinerary);
    showToast('Cost updated', 'success', 2000);
  };

  const deleteItem = async (dayIndex, itemIndex, itemType) => {
    if (!itinerary?.days?.[dayIndex]) return;
    if (!await showConfirmModal({ title: 'Delete Item', message: 'Are you sure you want to delete this item?', confirmText: 'Delete', type: 'danger' })) return;

    const updatedItinerary = { ...itinerary };
    const day = updatedItinerary.days[dayIndex];

    // Store the deleted item for undo
    let deletedItem = null;
    if (itemType === 'activity' && day.schedule[itemIndex]) {
      deletedItem = { ...day.schedule[itemIndex] };
      day.schedule.splice(itemIndex, 1);
    } else if (itemType === 'meal' && day.meals[itemIndex]) {
      deletedItem = { ...day.meals[itemIndex] };
      day.meals.splice(itemIndex, 1);
    }

    lastDeletedRef.current = { dayIndex, itemIndex, itemType, item: deletedItem, timestamp: Date.now() };

    // Bump _cacheBust so timeline memo rebuilds even though schedule/meals.length
    // change already invalidates the key — explicit bump keeps the pattern
    // consistent across all edit handlers.
    bumpDayCacheBust(day);
    setItinerary(updatedItinerary);

    // Show toast with undo action
    const itemName = deletedItem?.activity || deletedItem?.restaurant || deletedItem?.meal || 'Item';
    showToast(`${itemName} deleted`, 'success', 5000, {
      label: 'Undo',
      onClick: () => {
        const restore = lastDeletedRef.current;
        if (!restore || !restore.item) return;
        setItinerary(prev => {
          if (!prev?.days?.[restore.dayIndex]) return prev;
          const restored = { ...prev };
          const restoredDay = restored.days[restore.dayIndex];
          if (restore.itemType === 'activity') {
            restoredDay.schedule.splice(restore.itemIndex, 0, restore.item);
          } else if (restore.itemType === 'meal') {
            restoredDay.meals.splice(restore.itemIndex, 0, restore.item);
          }
          bumpDayCacheBust(restoredDay);
          return restored;
        });
        lastDeletedRef.current = null;
        transitCalcGuardRef.current = null; // Allow transit recalculation
        showToast('Restored!', 'success', 2000);
      }
    });
  };

  const addActivity = (dayIndex, newActivity) => {
    if (!itinerary?.days?.[dayIndex]) return;
    const updatedItinerary = { ...itinerary };
    const day = updatedItinerary.days[dayIndex];

    // Auto-generate whatToDo from description if not provided
    const activityName = (newActivity.activity || '').replace(/^[\p{Emoji}\s]+/u, '').trim();
    const whatToDo = newActivity.whatToDo || (newActivity.description ? [{
      title: activityName || 'Enjoy the experience',
      description: newActivity.description,
      duration: newActivity.duration || '1 hour'
    }] : []);

    // Add to schedule
    day.schedule.push({
      time: newActivity.time,
      duration: newActivity.duration || "1 hour",
      activity: newActivity.activity,
      location: newActivity.location || newActivity.address || `Custom Activity - ${newActivity.activity}`,
      description: newActivity.description || "Custom activity",
      cost: newActivity.cost || 0,
      priceTier: newActivity.priceTier || "$",
      type: newActivity.type || "custom",
      whatToDo
    });

    // Clear transit times for this day so they get recalculated
    setTransitTimes(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (key.startsWith(`day${dayIndex}-`)) {
          delete updated[key];
          delete transitTimesRef.current[key];
        }
      });
      return updated;
    });
    transitCalcGuardRef.current = null; // Allow recalculation

    // Mark that user has modified the itinerary
    itineraryUserModifiedRef.current = true;
    setHasUnsavedChanges(true);

    // schedule.length already changed so the memo key invalidates, but bump
    // _cacheBust too to keep the pattern uniform across edit handlers.
    bumpDayCacheBust(day);
    setItinerary(updatedItinerary);
    setShowAddActivityModal(false);
    setAddActivityPreFill('');
    showToast(`Added to Day ${dayIndex + 1}`, 'success', 3000);
  };

  // Add a new day (blank, explore, or custom) after the specified day index
  const addNewDay = (afterDayIndex, dayType = 'blank', customDescription = '') => {
    if (!itinerary?.days) return;

    const afterDay = itinerary.days[afterDayIndex] || itinerary.days[itinerary.days.length - 1];
    const baseCity = afterDay?.baseCity || afterDay?.city || '';
    const hotel = afterDay?.hotel || {};
    const newDayNum = afterDayIndex + 2; // +1 for 0-index, +1 for "after"

    const dayTemplates = {
      blank: {
        title: `Day in ${baseCity}`,
        description: '',
        schedule: [],
        meals: [],
        dayTips: [],
        freeTimeSlots: [],
      },
      explore: {
        title: `Exploration Day in ${baseCity}`,
        description: 'Dive deeper into the city — visit neighborhoods you haven\'t explored yet.',
        schedule: [
          { time: '10:00 AM', duration: '2 hours', activity: `Explore Local Markets in ${baseCity}`, location: baseCity, description: 'Browse local markets for souvenirs, food, and culture.', type: 'explore', priceTier: '$' },
          { time: '02:00 PM', duration: '2 hours', activity: `Hidden Gems of ${baseCity}`, location: baseCity, description: 'Discover off-the-beaten-path spots the locals love.', type: 'explore', priceTier: '$' },
        ],
        meals: [
          { mealType: 'Breakfast', time: '09:00 AM', restaurant: '{TBD: pick a breakfast spot}', cuisine: '', description: '', priceTier: '$', _needsReplacement: true },
          { mealType: 'Lunch',     time: '01:00 PM', restaurant: '{TBD: pick a lunch spot}',     cuisine: '', description: '', priceTier: '$$', _needsReplacement: true },
          { mealType: 'Dinner',    time: '07:30 PM', restaurant: '{TBD: pick a dinner spot}',    cuisine: '', description: '', priceTier: '$$', _needsReplacement: true },
        ],
        dayTips: [`Explore ${baseCity} like a local — ask at your hotel for neighborhood recommendations.`],
        freeTimeSlots: [],
      },
      custom: {
        title: customDescription ? `${customDescription.slice(0, 60)} in ${baseCity}` : `Custom Day in ${baseCity}`,
        description: customDescription || 'A day built around what you want.',
        schedule: [],
        meals: [
          { mealType: 'Breakfast', time: '09:00 AM', restaurant: '{TBD: pick a breakfast spot}', cuisine: '', description: '', priceTier: '$', _needsReplacement: true },
          { mealType: 'Lunch',     time: '01:00 PM', restaurant: '{TBD: pick a lunch spot}',     cuisine: '', description: '', priceTier: '$$', _needsReplacement: true },
          { mealType: 'Dinner',    time: '07:30 PM', restaurant: '{TBD: pick a dinner spot}',    cuisine: '', description: '', priceTier: '$$', _needsReplacement: true },
        ],
        dayTips: [customDescription || `Your custom day in ${baseCity}.`],
        freeTimeSlots: [],
      },
    };

    const template = dayTemplates[dayType] || dayTemplates.blank;

    const newDay = {
      day: newDayNum,
      baseCity,
      title: template.title,
      description: template.description,
      hotel,
      schedule: template.schedule,
      meals: template.meals,
      dayTips: template.dayTips,
      freeTimeSlots: template.freeTimeSlots,
      _userAdded: true,
      _needsReplacement: dayType !== 'blank', // blank has nothing to replace
    };

    const updatedItinerary = JSON.parse(JSON.stringify(itinerary));
    updatedItinerary.days.splice(afterDayIndex + 1, 0, newDay);
    updatedItinerary.days.forEach((d, i) => { d.day = i + 1; });

    // Clear all transit time cache (day indices shifted)
    setTransitTimes({});
    if (transitTimesRef?.current) transitTimesRef.current = {};
    transitCalcGuardRef.current = null;
    window._timelineCacheKey = null;
    window._timelineCacheResult = null;

    // Recalculate travel structure from actual days (ground truth)
    if (selectedDestination?.stops?.length > 1) {
      const newTotalDays = updatedItinerary.days.length;
      const cityCounts = {};
      updatedItinerary.days.forEach(d => {
        const city = d.baseCity || d.city || '';
        if (city) cityCounts[city] = (cityCounts[city] || 0) + 1;
      });
      const updatedStops = selectedDestination.stops.map(s => {
        const name = typeof s === 'string' ? s : s.name || '';
        const match = Object.keys(cityCounts).find(c => c === name || c.includes(name) || name.includes(c));
        return match ? { ...s, days: cityCounts[match] } : s;
      });
      const newTravelStruct = calculateTravelStructure(updatedStops, newTotalDays);
      setTravelStructure(newTravelStruct);
      setSelectedDestination(prev => ({ ...prev, stops: updatedStops }));
      setTotalItineraryDays(newTotalDays);

      // Patch former travel day — if it was a half-day (departure), fill the gap
      const formerTravelDay = updatedItinerary.days[afterDayIndex];
      if (formerTravelDay) {
        const lastActivity = [...(formerTravelDay.schedule || [])].sort((a, b) => {
          const toMin = (t) => { const m = (t || '').match(/(\d+):(\d+)\s*(AM|PM)?/i); if (!m) return 0; let h = parseInt(m[1]); const min = parseInt(m[2]); if (m[3]?.toUpperCase() === 'PM' && h < 12) h += 12; if (m[3]?.toUpperCase() === 'AM' && h === 12) h = 0; return h * 60 + min; };
          return toMin(b.time) - toMin(a.time);
        })[0];
        const lastMeal = [...(formerTravelDay.meals || [])].sort((a, b) => {
          const toMin = (t) => { const m = (t || '').match(/(\d+):(\d+)\s*(AM|PM)?/i); if (!m) return 0; let h = parseInt(m[1]); const min = parseInt(m[2]); if (m[3]?.toUpperCase() === 'PM' && h < 12) h += 12; if (m[3]?.toUpperCase() === 'AM' && h === 12) h = 0; return h * 60 + min; };
          return toMin(b.time) - toMin(a.time);
        })[0];
        const toMin = (t) => { const m = (t || '').match(/(\d+):(\d+)\s*(AM|PM)?/i); if (!m) return 0; let h = parseInt(m[1]); const min = parseInt(m[2]); if (m[3]?.toUpperCase() === 'PM' && h < 12) h += 12; if (m[3]?.toUpperCase() === 'AM' && h === 12) h = 0; return h * 60 + min; };
        const lastTime = Math.max(toMin(lastActivity?.time), toMin(lastMeal?.time));

        // If last scheduled item is before 4 PM (960 min), the afternoon is empty
        if (lastTime > 0 && lastTime < 960) {
          const gapStart = `${Math.floor((lastTime + 60) / 60) % 12 || 12}:00 PM`;
          formerTravelDay.freeTimeSlots = [
            ...(formerTravelDay.freeTimeSlots || []),
            { start: gapStart, end: '09:00 PM', description: `Free afternoon in ${baseCity} — add activities or ask Arrivo for suggestions.` }
          ];
          // Remove departure flight references since travel moved
          formerTravelDay.schedule = (formerTravelDay.schedule || []).filter(s =>
            !(s.type === 'flight' || s.type === 'departure' || (s.activity || '').toLowerCase().includes('depart'))
          );
          // Add missing meals — departure days often skip lunch/dinner
          const existingMealTypes = (formerTravelDay.meals || []).map(m => (m.mealType || '').toLowerCase());
          if (!existingMealTypes.includes('lunch')) {
            formerTravelDay.meals = [...(formerTravelDay.meals || []),
              { mealType: 'Lunch', time: '01:00 PM', restaurant: '{TBD: pick a lunch spot}', cuisine: '', description: '', priceTier: '$$', _needsReplacement: true }
            ];
          }
          if (!existingMealTypes.includes('dinner')) {
            formerTravelDay.meals = [...(formerTravelDay.meals || []),
              { mealType: 'Dinner', time: '07:30 PM', restaurant: '{TBD: pick a dinner spot}', cuisine: '', description: '', priceTier: '$$', _needsReplacement: true }
            ];
          }
        }
      }
    }

    itineraryUserModifiedRef.current = true;
    setHasUnsavedChanges(true);
    setItinerary(updatedItinerary);
    setCurrentDay(afterDayIndex + 1);
    setShowAddDayModal(false);
    setAddDayCustomText('');
    setAddDayAfterIndex(null);
    const typeLabel = dayType === 'blank' ? 'Blank' : dayType === 'explore' ? 'Exploration' : 'Custom';
    showToast(`Added ${typeLabel} Day after Day ${afterDayIndex + 1}`, 'success', 3000);
  };

  // Generate contextual free time suggestions using AI
  const generateFreeTimeSuggestions = async (slotInfo) => {
    if (!itinerary || !slotInfo) return;

    setLoadingFreeTimeSuggestions(true);
    setFreeTimeSuggestions([]);

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
      const dayIndex = slotInfo.day;
      const dayData = itinerary.days?.[dayIndex];
      const cityName = dayData?.baseCity || selectedDestination?.name || itinerary.destination || 'the area';
      const hotelName = dayData?.hotel?.name || '';

      // Build context about before/after activities
      const slotTimeMinutes = (() => {
        const [h, m] = (slotInfo.time || '12:00').split(':').map(Number);
        return h * 60 + m;
      })();

      // Find activity before and after this free time slot
      const allItems = [
        ...(dayData?.schedule || []).map(s => ({ ...s, itemType: 'activity' })),
        ...(dayData?.meals || []).map(m => ({ ...m, time: m.time, itemType: 'meal', activity: m.restaurant }))
      ].map(item => {
        const [h, m] = (item.time || '12:00').replace(/[^\d:]/g, '').split(':').map(Number);
        return { ...item, timeMinutes: h * 60 + m };
      }).sort((a, b) => a.timeMinutes - b.timeMinutes);

      const beforeItem = allItems.filter(i => i.timeMinutes < slotTimeMinutes).pop();
      const afterItem = allItems.find(i => i.timeMinutes > slotTimeMinutes);

      // Determine time of day
      const slotHours = Math.floor(slotTimeMinutes / 60);
      const timeOfDay = slotHours < 12 ? 'morning' : (slotHours < 17 ? 'afternoon' : 'evening');

      // Build the prompt
      const prompt = `You are a travel assistant. Generate 4 specific, real activity suggestions for free time in ${cityName}.

CONTEXT:
- City: ${cityName}
- Time of day: ${timeOfDay} (${slotInfo.time})
- Duration available: ${slotInfo.duration} (${slotInfo.durationMinutes} minutes)
- Previous activity: ${beforeItem ? `${beforeItem.activity || beforeItem.restaurant} at ${beforeItem.location || beforeItem.address || 'nearby'}` : 'None'}
- Next activity: ${afterItem ? `${afterItem.activity || afterItem.restaurant} at ${afterItem.location || afterItem.address || 'nearby'}` : 'None'}
- From location: ${slotInfo.fromLocation || beforeItem?.location || cityName}
- To location: ${slotInfo.toLocation || afterItem?.location || cityName}
${hotelName ? `- Hotel: ${hotelName}` : ''}

REQUIREMENTS:
1. Suggestions must be REAL, specific places/activities that exist in ${cityName}
2. Must be appropriate for the time of day (${timeOfDay})
3. Must fit within ${slotInfo.durationMinutes} minutes
4. Should be geographically sensible (between previous and next locations if known)
5. Mix of activity types: cafés, viewpoints, quick attractions, local experiences

Return JSON array only, no explanation:
[
  {"emoji": "☕", "activity": "Specific Place Name", "description": "What you'll experience", "duration": "30 min", "location": "Address or area"},
  ...
]`;

      const response = await withTimeout(
        fetch(`${BACKEND_URL}/api/chat`, {
          method: 'POST',
          headers: await getApiHeaders(),
          body: JSON.stringify({
            model: 'claude-haiku-4-5-20251001',
            max_tokens: 800,
            system: 'You are a helpful travel assistant. Return only valid JSON arrays, no markdown or explanation.',
            messages: [{ role: 'user', content: prompt }]
          })
        }),
        10000,
        'AI suggestions timed out'
      );

      if (!response.ok) throw new Error('Failed to get suggestions');

      const data = await response.json();
      const aiResponse = data.content?.[0]?.text || '';

      // Parse the JSON response
      const jsonMatch = aiResponse.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        const suggestions = JSON.parse(jsonMatch[0]);
        // Filter out vague/generic suggestions (e.g., "Family Suite Relaxation", "Hotel Pool Time")
        const VAGUE_PATTERNS = /^(hotel|suite|room|lobby|relaxation|rest|nap|pack|unpack|settle|freshen|check.?in|check.?out)/i;
        const validSuggestions = suggestions.filter(s =>
          s.activity && !VAGUE_PATTERNS.test(s.activity.trim())
        );
        setFreeTimeSuggestions(validSuggestions.length > 0 ? validSuggestions : suggestions);
      }
    } catch (error) {
      debugError('Error generating free time suggestions:', error);
      // Fall back to day-level AI suggestions if available (these are specific real places)
      const dayData = itinerary.days?.[slotInfo.day];
      const dayFreeTime = dayData?.freeTimeSuggestions;
      if (dayFreeTime && dayFreeTime.length > 0) {
        // Filter suggestions by time of day to avoid e.g. nighttime suggestions at 4:30 PM
        const slotHour = Math.floor((() => {
          const [h, m] = (slotInfo.time || '12:00').split(':').map(Number);
          return h * 60 + m;
        })() / 60);
        const slotTimeOfDay = slotHour < 12 ? 'morning' : (slotHour < 17 ? 'afternoon' : 'evening');
        const timeFiltered = dayFreeTime.filter(s => {
          if (!s.timeOfDay || s.timeOfDay === 'any') return true;
          if (slotTimeOfDay === 'morning') return s.timeOfDay === 'morning' || s.timeOfDay === 'any';
          if (slotTimeOfDay === 'afternoon') return s.timeOfDay === 'afternoon' || s.timeOfDay === 'morning' || s.timeOfDay === 'any';
          if (slotTimeOfDay === 'evening') return s.timeOfDay === 'evening' || s.timeOfDay === 'afternoon' || s.timeOfDay === 'any';
          return true;
        });
        const filtered = timeFiltered.length > 0 ? timeFiltered : dayFreeTime;
        // Filter out vague hotel-centric suggestions from AI itinerary
        const VAGUE_PATTERNS_TIER2 = /^(hotel|suite|room|lobby|relaxation|rest|nap|pack|unpack|settle|freshen|check.?in|check.?out)/i;
        const quality = filtered.filter(s => s.activity && !VAGUE_PATTERNS_TIER2.test(s.activity.trim()));
        const usable = quality.length > 0 ? quality : filtered;
        setFreeTimeSuggestions(usable.slice(0, 4).map(s => ({
          emoji: s.emoji || '📍',
          activity: s.activity,
          description: s.description || '',
          duration: s.duration || '30 min',
          location: s.location || dayData?.baseCity || selectedDestination?.name || ''
        })));
      } else {
        // Lazy fetch from /api/free-time-suggestions endpoint
        const cityName = dayData?.baseCity || selectedDestination?.name || 'the area';
        try {
          const slotHour = Math.floor((() => {
            const [h] = (slotInfo.time || '12:00').split(':').map(Number);
            return h;
          })());
          const period = slotHour < 12 ? 'morning' : (slotHour < 17 ? 'afternoon' : 'evening');
          // Cross-day dedup: collect activities already on the itinerary so
          // the recommender doesn't suggest something already scheduled
          // (e.g. suggesting MAAT here when MAAT is on Day 2).
          const existingActivities = (itinerary?.days || [])
            .flatMap(d => (d?.schedule || []).map(s => s.activity || s.name || ''))
            .filter(Boolean);
          const apiSuggestions = await fetchFreeTimeSuggestions(cityName, period, userProfile?.preferences?.travelCompanions, { existingActivities });
          if (apiSuggestions) {
            const periodSuggestions = apiSuggestions[period] || apiSuggestions.afternoon || [];
            setFreeTimeSuggestions(periodSuggestions.slice(0, 4).map(s => ({
              emoji: s.emoji || '📍',
              activity: s.label || s.activity || '',
              description: s.description || '',
              duration: '30 min',
              location: cityName,
            })));
          } else {
            setFreeTimeSuggestions([
              { emoji: '☕', activity: `Local café in ${cityName}`, description: 'Relax with a drink', duration: '30 min' },
              { emoji: '📸', activity: `Explore nearby streets`, description: 'Wander and discover', duration: '45 min' },
              { emoji: '🛍️', activity: `Local shops`, description: 'Browse unique finds', duration: '30 min' }
            ]);
          }
        } catch {
          setFreeTimeSuggestions([
            { emoji: '☕', activity: `Local café in ${cityName}`, description: 'Relax with a drink', duration: '30 min' },
            { emoji: '📸', activity: `Explore nearby streets`, description: 'Wander and discover', duration: '45 min' },
            { emoji: '🛍️', activity: `Local shops`, description: 'Browse unique finds', duration: '30 min' }
          ]);
        }
      }
    } finally {
      setLoadingFreeTimeSuggestions(false);
    }
  };

  // DRAG AND DROP FUNCTIONS
  const handleDragStart = (dayIndex, itemIndex, itemType) => {
    setDraggedItem({ dayIndex, itemIndex, itemType });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (dayIndex, targetIndex, targetType) => {
    if (!draggedItem) return;
    if (!itinerary?.days?.[draggedItem.dayIndex] || !itinerary?.days?.[dayIndex]) return;

    const updatedItinerary = { ...itinerary };
    const sourceDay = updatedItinerary.days[draggedItem.dayIndex];
    const targetDay = updatedItinerary.days[dayIndex];
    
    // Get the item being moved
    let item;
    if (draggedItem.itemType === 'activity') {
      item = sourceDay.schedule[draggedItem.itemIndex];
      sourceDay.schedule.splice(draggedItem.itemIndex, 1);
    } else {
      item = sourceDay.meals[draggedItem.itemIndex];
      sourceDay.meals.splice(draggedItem.itemIndex, 1);
    }
    
    // Insert at target position
    if (targetType === 'activity') {
      targetDay.schedule.splice(targetIndex, 0, item);
    } else {
      targetDay.meals.splice(targetIndex, 0, item);
    }

    // Auto-redistribute times so they stay chronological after reorder
    targetDay.schedule = redistributeTimes(targetDay.schedule);
    targetDay.meals = redistributeTimes(targetDay.meals);
    // If source day is different, also fix its times
    if (draggedItem.dayIndex !== dayIndex) {
      sourceDay.schedule = redistributeTimes(sourceDay.schedule);
      sourceDay.meals = redistributeTimes(sourceDay.meals);
    }

    setItinerary(updatedItinerary);
    setDraggedItem(null);
  };

  // Memoized advisory destination names — prevents TravelAdvisoryWidget from re-fetching on every parent render
  const advisoryDestNames = useMemo(() => {
    return selectedDestination?.isMultiStop
      ? (selectedDestination.stopNames || [])
      : [selectedDestination?.name || ''].filter(Boolean);
  }, [selectedDestination?.isMultiStop, selectedDestination?.stopNames, selectedDestination?.name]);

  // TRAVEL ADVISORY WIDGET — fetches real advisory data for personalization sidebar
  const TravelAdvisoryWidget = React.memo(({ destinationNames }) => {
    // destinationNames: string[] — supports single or multi-city
    const names = Array.isArray(destinationNames) ? destinationNames : destinationNames ? [destinationNames] : [];
    const [advisories, setAdvisories] = useState([]);
    const [loading, setLoading] = useState(false);
    const namesKey = names.join('|');
    useEffect(() => {
      if (!names.length) return;
      setLoading(true);
      import('@wander/shared/utils/travelAdvisory').then(async ({ checkTravelAdvisory }) => {
        try {
          // Get auth token — advisory endpoint requires authentication
          const headers = await getApiHeaders();
          const token = headers['Authorization']?.replace('Bearer ', '') || null;
          const results = await Promise.all(names.map(async (name) => {
            const result = await checkTravelAdvisory(name, process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001', token);
            return { name: name.split(',')[0], ...result };
          }));
          setAdvisories(results);
        } catch (e) { /* silent */ }
        setLoading(false);
      }).catch(() => setLoading(false));
    }, [namesKey]);

    if (!names.length) return null;
    if (loading) return (
      <div className="mb-4 px-3 py-2.5" style={{ background: 'rgba(240,253,250,0.94)', border: '1px solid rgba(13,148,136,0.15)', borderRadius: 12 }}>
        <div className="text-[10px] font-extrabold uppercase mb-1" style={{ color: '#0d9488', letterSpacing: '0.5px' }}>Travel Advisory</div>
        <p className="text-[11px] leading-snug m-0" style={{ color: '#64748b' }}>Checking advisories...</p>
      </div>
    );

    // Find the highest-level advisory across all destinations
    // checkTravelAdvisory returns { results: [{ destination, advisory: { level, levelText, description } }], summary }
    const allAlerts = advisories.flatMap(a => (a.results || [])
      .filter(r => r.advisory && r.advisory.level >= 2)
      .map(r => ({ level: r.advisory.level, message: r.advisory.levelText || r.advisory.description || `Level ${r.advisory.level} advisory`, destName: a.name })));
    const hasAlerts = allAlerts.length > 0;

    if (hasAlerts) {
      const maxLevel = Math.max(...allAlerts.map(a => a.level || 1));
      const bgColor = maxLevel >= 3 ? 'rgba(254,242,242,0.94)' : maxLevel >= 2 ? 'rgba(255,251,235,0.94)' : 'rgba(240,253,250,0.94)';
      const borderColor = maxLevel >= 3 ? 'rgba(239,68,68,0.2)' : maxLevel >= 2 ? 'rgba(245,158,11,0.2)' : 'rgba(13,148,136,0.15)';
      const textColor = maxLevel >= 3 ? '#991b1b' : maxLevel >= 2 ? '#92400e' : '#065f46';
      const labelColor = maxLevel >= 3 ? '#dc2626' : maxLevel >= 2 ? '#d97706' : '#0d9488';
      return (
        <div className="mb-4 px-3 py-2.5" style={{ background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 12 }}>
          <div className="text-[10px] font-extrabold uppercase mb-1" style={{ color: labelColor, letterSpacing: '0.5px' }}>
            Travel Advisory
          </div>
          {allAlerts.filter(a => (a.level || 1) >= 2).map((alert, i) => (
            <p key={i} className="text-[11px] leading-snug m-0 mb-1" style={{ color: textColor }}>
              <strong>{alert.destName}:</strong> {(() => {
                const raw = alert.message || alert.description || '';
                const firstSentence = raw.split(/[.!]\s/)[0];
                return firstSentence.length > 100 ? firstSentence.substring(0, 97) + '...' : firstSentence || `Level ${alert.level} advisory`;
              })()}
            </p>
          ))}
        </div>
      );
    }
    // No advisory or Level 1 — show green "all clear"
    return (
      <div className="mb-4 px-3 py-2.5" style={{ background: 'rgba(240,253,250,0.94)', border: '1px solid rgba(13,148,136,0.15)', borderRadius: 12 }}>
        <div className="text-[10px] font-extrabold uppercase mb-1" style={{ color: '#0d9488', letterSpacing: '0.5px' }}>Travel Advisory</div>
        <p className="text-[11px] leading-snug m-0" style={{ color: '#065f46' }}>
          No significant advisories for {names.join(', ')}. Normal precautions recommended.
        </p>
      </div>
    );
  });

  // SORTABLE CARD WRAPPER for drag-and-drop
  const SortableTimelineCard = React.memo(({ id, children, isDraggable, className = '' }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id, disabled: !isDraggable });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
      ...(isDragging ? {} : { zIndex: 1 })
    };

    if (!isDraggable) {
      return <div className={className}>{typeof children === 'function' ? children({}) : children}</div>;
    }

    return (
      <div 
        ref={setNodeRef} 
        style={style} 
        {...attributes} 
        className={`${className} ${isDragging ? 'dragging' : ''}`}
      >
        {typeof children === 'function' ? children(listeners) : children}
      </div>
    );
  });

  // DND-KIT SORTABLE REORDER HANDLER
  // Handles within-day reorder of activity and meal cards via @dnd-kit/sortable
  const dndSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }, // 8px drag threshold to avoid accidental drags when clicking
    })
  );

  const [activeDragId, setActiveDragId] = useState(null);

  const handleDndDragStart = (event) => {
    setActiveDragId(event.active.id);
  };

  const handleDndDragEnd = (event) => {
    const { active, over } = event;
    setActiveDragId(null);

    if (!active || !over || active.id === over.id) return;
    if (!itinerary?.days?.[currentDay]) return;

    // Parse drag IDs: format is "sortable-{type}-{originalIndex}"
    const parseId = (id) => {
      const match = String(id).match(/^sortable-(activity|meal)-(\d+)$/);
      if (!match) return null;
      return { type: match[1], originalIndex: parseInt(match[2], 10) };
    };

    const activeItem = parseId(active.id);
    const overItem = parseId(over.id);
    if (!activeItem || !overItem) return;

    const updatedItinerary = JSON.parse(JSON.stringify(itinerary));
    const day = updatedItinerary.days[currentDay];

    // Build a unified timeline of activities + meals with their source info
    const unified = [];
    (day.schedule || []).forEach((item, i) => {
      unified.push({ ...item, _srcType: 'activity', _srcIdx: i, _sortTime: parseTime(item.time) || 540 + i * 60 });
    });
    (day.meals || []).forEach((item, i) => {
      unified.push({ ...item, _srcType: 'meal', _srcIdx: i, _sortTime: parseTime(item.time) || 540 + i * 60 });
    });
    unified.sort((a, b) => a._sortTime - b._sortTime);

    // Find indices in unified array
    const activeUnifiedIdx = unified.findIndex(u => u._srcType === activeItem.type && u._srcIdx === activeItem.originalIndex);
    const overUnifiedIdx = unified.findIndex(u => u._srcType === overItem.type && u._srcIdx === overItem.originalIndex);

    if (activeUnifiedIdx === -1 || overUnifiedIdx === -1) return;

    // Reorder in unified
    const [moved] = unified.splice(activeUnifiedIdx, 1);
    unified.splice(overUnifiedIdx, 0, moved);

    // Collect sorted times
    const allTimes = unified.map(u => u._sortTime).sort((a, b) => a - b);

    // Redistribute times to new positions and split back into schedule/meals
    const newSchedule = [];
    const newMeals = [];
    unified.forEach((item, i) => {
      const newTime = minutesToTime(allTimes[i]);
      // Clean up internal tracking fields
      const { _srcType, _srcIdx, _sortTime, ...cleanItem } = item;
      cleanItem.time = newTime;
      if (_srcType === 'activity') {
        newSchedule.push(cleanItem);
      } else {
        newMeals.push(cleanItem);
      }
    });

    day.schedule = newSchedule;
    day.meals = newMeals;

    // Invalidate timeline cache — preferred: bump day._cacheBust (mobile parity).
    // The legacy window._timelineCacheKey reset is kept for belt-and-braces.
    bumpDayCacheBust(day);
    window._timelineCacheKey = null;
    window._timelineCacheResult = null;

    setItinerary(updatedItinerary);

    // Auto-save after reorder
    if (typeof setHasUnsavedChanges === 'function') {
      setHasUnsavedChanges(true);
    }
  };

  // ============================================
  // EXPANDABLE CARD TAB CONTENT COMPONENTS
  // ============================================

  // Overview tab content for activities
  const OverviewTabContent = ({ item }) => {
    const isMeal = item.restaurant || item.type === 'meal';

    if (isMeal) {
      return <RestaurantOverview item={item} />;
    }

    // Get booking info for this activity
    const activityName = item.activity || item.name || '';
    const cacheKey = activityName.toLowerCase().trim();
    const bookingInfo = activityBookingInfo[cacheKey];

    return (
      <div>
        <p className="text-gray-700 leading-relaxed mb-4" style={FONTS.body}>
          {item.description || item.notes || 'No description available.'}
        </p>

        {/* Booking Requirements Alert */}
        {bookingInfo?.loading && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4 flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500"></div>
            <span className="text-sm text-gray-600">Checking booking requirements...</span>
          </div>
        )}
        {bookingInfo?.data && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <span className="text-xl">🎫</span>
              <div className="flex-1">
                <h4 className="font-semibold text-red-800 mb-1">Advance Booking Required</h4>
                <p className="text-sm text-red-700 mb-2">
                  Book {bookingInfo.data.leadTimeDays} {bookingInfo.data.leadTimeDays === 1 ? 'day' : 'days'} ahead
                  {bookingInfo.data.timedEntry && ' • Timed entry tickets'}
                </p>
                {bookingInfo.data.tips && (
                  <div className="text-xs text-red-600 italic mb-2">
                    {Array.isArray(bookingInfo.data.tips)
                      ? bookingInfo.data.tips.map((tip, i) => <p key={i} className="mb-1">{tip}</p>)
                      : <p>{bookingInfo.data.tips}</p>
                    }
                  </div>
                )}
                {bookingInfo.data.peakSeasons && bookingInfo.data.peakSeasons.length > 0 && (
                  <p className="text-xs text-red-600">
                    ⚠️ Busiest: {bookingInfo.data.peakSeasons.join(', ')}
                  </p>
                )}
                {bookingInfo.data.bookingUrl && (
                  <a
                    href={bookingInfo.data.bookingUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    🔗 Book Now
                  </a>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          {item.duration && (
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Duration</span>
              <p className="text-sm font-medium text-gray-900">{item.duration}</p>
            </div>
          )}
          {item.bestTime && (
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Best Time</span>
              <p className="text-sm font-medium text-gray-900">{item.bestTime}</p>
            </div>
          )}
          {(item.cost || item.entryFee) && (
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Entry Fee</span>
              <p className="text-sm font-medium text-gray-900">{(() => {
                const fee = item.cost || item.entryFee;
                const feeStr = String(fee).trim();
                // If already has a currency symbol or non-digit prefix, show as-is
                if (/^[^\d]/.test(feeStr) || /[€£¥₹₩฿₱₫₪₺₽]/.test(feeStr)) return feeStr;
                // Prepend local currency symbol
                const sym = currencySymbols[getDestinationCurrency()] || '$';
                return `${sym}${feeStr}`;
              })()}</p>
            </div>
          )}
          {item.type && (
            <div className="bg-gray-50 rounded-lg p-3">
              <span className="text-xs text-gray-500 uppercase tracking-wide">Type</span>
              <p className="text-sm font-medium text-gray-900 capitalize">{item.type?.replace(/-/g, ' ')}</p>
            </div>
          )}
        </div>

        {/* Transportation/Getting There */}
        {item.transportation && (
          <div className="bg-blue-50 rounded-lg p-3 mb-3">
            <span className="text-xs text-blue-600 uppercase tracking-wide font-medium">Getting There</span>
            <p className="text-sm text-blue-800 mt-1">{item.transportation}</p>
          </div>
        )}

        {/* Quick Tip if available (for legacy string tips) */}
        {item.tips && typeof item.tips === 'string' && (
          <div className="bg-amber-50 rounded-lg p-3">
            <span className="text-xs text-amber-600 uppercase tracking-wide font-medium">Quick Tip</span>
            <p className="text-sm text-amber-800 mt-1">{item.tips}</p>
          </div>
        )}
      </div>
    );
  };

  // Restaurant-specific overview component
  const RestaurantOverview = ({ item }) => (
    <div>
      {/* Rating and Reviews Row */}
      {(item.rating || item.reviewCount) && (
        <div className="flex items-center gap-3 mb-4">
          {item.rating && (
            <div className="flex items-center gap-1.5 bg-amber-50 px-3 py-1.5 rounded-full">
              <span className="text-amber-500">★</span>
              <span className="font-semibold text-amber-700">{item.rating}</span>
            </div>
          )}
          {item.reviewCount && (
            <span className="text-sm text-gray-500">({item.reviewCount})</span>
          )}
          {item.reservationRequired && (
            <span className="px-2 py-1 bg-red-50 text-red-600 text-xs rounded-full font-medium">
              Reservations recommended
            </span>
          )}
        </div>
      )}

      {/* Description */}
      <p className="text-gray-700 leading-relaxed mb-4" style={FONTS.body}>
        {item.description || item.specialty || `${item.cuisine || 'Local'} cuisine at ${item.restaurant || 'this restaurant'}.`}
      </p>

      {/* Must Try Items */}
      {Array.isArray(item.mustTry) && item.mustTry.length > 0 && (
        <div className="mb-4">
          <h4 className="text-xs text-gray-500 uppercase tracking-wide mb-2 font-medium">Must Try</h4>
          <div className="flex flex-wrap gap-2">
            {item.mustTry.map((dish, i) => (
              <span key={i} className="px-3 py-1.5 bg-orange-50 text-orange-700 rounded-full text-sm">
                🍽️ {dish}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Info Grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {item.cuisine && (
          <div className="bg-gray-50 rounded-lg p-3">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Cuisine</span>
            <p className="text-sm font-medium text-gray-900">{item.cuisine}</p>
          </div>
        )}
        {(item.priceRange || item.estimatedCost) && (
          <div className="bg-gray-50 rounded-lg p-3">
            <span className="text-xs text-gray-500 uppercase tracking-wide">Price</span>
            <p className="text-sm font-medium text-gray-900">{item.estimatedCost || item.priceRange}</p>
          </div>
        )}
      </div>

      {/* Dietary Accommodations */}
      {Array.isArray(item.dietary) && item.dietary.length > 0 && (
        <div className="bg-green-50 rounded-lg p-3 mb-3">
          <span className="text-xs text-green-600 uppercase tracking-wide font-medium">Dietary Options</span>
          <div className="flex flex-wrap gap-2 mt-2">
            {/* Dedupe — the LLM occasionally emits duplicates like ["Rf", "Rf"]
                which previously rendered as two identical pills. Mirrors the
                mobile fix (cards.js dietary tag render). */}
            {[...new Set(item.dietary)].map((diet, i) => (
              <span key={i} className="px-2 py-1 bg-white text-green-700 rounded text-xs border border-green-200">
                ✓ {diet}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Quick Tip */}
      {item.tips && typeof item.tips === 'string' && (
        <div className="bg-amber-50 rounded-lg p-3">
          <span className="text-xs text-amber-600 uppercase tracking-wide font-medium">Tip</span>
          <p className="text-sm text-amber-800 mt-1">{item.tips}</p>
        </div>
      )}

      {/* Google Maps & Menu Links */}
      <div className="flex flex-wrap gap-2 mt-4">
        {item.address && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.restaurant + ' ' + item.address)}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
          >
            <MapIcon className="w-4 h-4" /> View on Maps
          </a>
        )}
        {/* View Menu removed — generic Google search wasn't useful */}
      </div>
    </div>
  );

  // What to Do tab content - handles both activities and walking tours
  const WhatToDoTabContent = ({ item }) => {
    const isWalkingTour = item.type === 'walking-tour' || item.activity?.toLowerCase().includes('walk');
    const isMeal = item.restaurant || item.type === 'meal';

    // For meals, show "What to Try" instead
    if (isMeal) {
      return (
        <div className="space-y-3">
          {Array.isArray(item.mustTry) && item.mustTry.length > 0 ? (
            item.mustTry.map((dish, i) => (
              <div key={i} className="flex gap-3 p-3 bg-orange-50 rounded-xl">
                <UtensilsCrossed className="w-6 h-6 text-orange-500 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-900" style={FONTS.headingSemibold}>{dish}</h4>
                  {item.specialty && i === 0 && (
                    <p className="text-sm text-gray-600" style={FONTS.body}>Their signature dish - {item.specialty}</p>
                  )}
                </div>
              </div>
            ))
          ) : item.specialty ? (
            <div className="flex gap-3 p-3 bg-orange-50 rounded-xl">
              <Star className="w-6 h-6 text-amber-500 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-gray-900" style={FONTS.headingSemibold}>Signature Dish</h4>
                <p className="text-sm text-gray-600" style={FONTS.body}>{item.specialty}</p>
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm italic" style={FONTS.body}>
                Ask the chat for menu recommendations!
              </p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setItineraryChatInput(`What should I order at ${item.restaurant}?`);
                  setShowItineraryChat(true);
                }}
                className="mt-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
                style={FONTS.body}
              >
                Get recommendations
              </button>
            </div>
          )}
        </div>
      );
    }

    // For walking tours, show stops with route visualization
    if (isWalkingTour && Array.isArray(item.whatToDo) && item.whatToDo.length > 0) {
      return (
        <div className="space-y-3">
          {/* Route Preview */}
          <div className="bg-gradient-to-r from-blue-50 to-teal-50 rounded-xl p-4 border border-blue-100 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-semibold text-blue-800 flex items-center gap-1.5" style={FONTS.headingSemibold}><MapIcon className="w-4 h-4" /> Route Overview</span>
              {item.duration && <span className="text-sm text-blue-600">{item.duration} walking</span>}
            </div>
            <div className="flex items-center gap-1">
              {item.whatToDo.map((_, i) => (
                <React.Fragment key={i}>
                  <span className="w-7 h-7 bg-[#313143] text-white rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0">
                    {i + 1}
                  </span>
                  {i < item.whatToDo.length - 1 && (
                    <div className="flex-1 h-1 bg-teal-300 rounded"></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Tour Stops */}
          {item.whatToDo.map((stop, i) => (
            <React.Fragment key={i}>
              <div className={`flex gap-3 p-3 rounded-xl transition-colors ${stop.photoSpot ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50 hover:bg-teal-50'}`}>
                <div className="flex flex-col items-center">
                  <span className={`w-8 h-8 ${stop.photoSpot ? 'bg-orange-500' : 'bg-teal-500'} text-white rounded-full flex items-center justify-center text-sm font-bold`}>
                    {stop.photoSpot ? '📸' : i + 1}
                  </span>
                  {i < item.whatToDo.length - 1 && (
                    <div className="w-0.5 flex-1 bg-teal-200 mt-1"></div>
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900" style={FONTS.headingSemibold}>{stop.title}</h4>
                    {stop.duration && (
                      <span className="text-xs text-gray-500">⏱️ {stop.duration}</span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mt-1" style={FONTS.body}>{stop.description}</p>
                  {stop.photoSpot && (
                    <span className="text-xs text-orange-600 mt-2 inline-block">📸 Great photo opportunity!</span>
                  )}
                </div>
              </div>

              {/* Walking segment between stops */}
              {i < item.whatToDo.length - 1 && stop.walkingTime && (
                <div className="flex gap-3 pl-3">
                  <div className="w-8 flex justify-center">
                    <div className="w-0.5 h-6 bg-teal-200"></div>
                  </div>
                  <span className="text-xs text-gray-400 flex items-center gap-1">🚶 {stop.walkingTime}</span>
                </div>
              )}
            </React.Fragment>
          ))}

          {/* Open in Google Maps Button */}
          {item.address && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(item.address)}&travelmode=walking`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="w-full mt-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-colors"
            >
              <MapIcon className="w-4 h-4" /> Open Route in Google Maps
            </a>
          )}
        </div>
      );
    }

    // Standard activities
    const activityCity = currentDayData?.baseCity || selectedDestination?.name || '';
    const cacheKey = `${item.activity}|${activityCity}`;
    const cachedDetails = activityDetailsCache[cacheKey];

    return (
      <div className="space-y-3">
        {Array.isArray(item.whatToDo) && item.whatToDo.length > 0 ? (
          item.whatToDo.map((step, i) => (
            <div key={i} className={`flex gap-3 p-3 rounded-xl ${step.photoSpot ? 'bg-orange-50 border border-orange-200' : 'bg-gray-50'}`}>
              <span className={`w-7 h-7 ${step.photoSpot ? 'bg-orange-500' : 'bg-teal-500'} text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0`}>
                {step.photoSpot ? '📸' : i + 1}
              </span>
              <div>
                <h4 className="font-semibold text-gray-900" style={FONTS.headingSemibold}>{step.title}</h4>
                <p className="text-sm text-gray-600" style={FONTS.body}>{step.description}</p>
                {step.duration && (
                  <span className="text-xs text-gray-500 mt-1 inline-block">⏱️ {step.duration}</span>
                )}
              </div>
            </div>
          ))
        ) : cachedDetails?.whatToDo ? (
          /* Lazy-loaded content from /api/activity-details */
          <div className="space-y-3">
            <div className="flex gap-3 p-3 bg-teal-50 rounded-xl">
              <Lightbulb className="w-6 h-6 text-teal-600 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-700" style={FONTS.body}>{cachedDetails.whatToDo}</p>
                {cachedDetails.bestTime && (
                  <p className="text-xs text-teal-600 mt-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Best time: {cachedDetails.bestTime}</p>
                )}
              </div>
            </div>
            {Array.isArray(cachedDetails.tips) && cachedDetails.tips.length > 0 && (
              <div className="space-y-1.5">
                {cachedDetails.tips.map((tip, i) => (
                  <div key={i} className="flex gap-2 p-2 bg-gray-50 rounded-lg">
                    <span className="text-xs text-teal-500 mt-0.5">✦</span>
                    <p className="text-xs text-gray-600" style={FONTS.body}>{tip}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : cachedDetails?.loading ? (
          <div className="text-center py-6">
            <div className="animate-spin w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-gray-500 text-sm" style={FONTS.body}>Loading tips for {item.activity}...</p>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500 text-sm italic" style={FONTS.body}>
              No specific recommendations yet.
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (item.activity && activityCity) {
                  fetchActivityDetails(item.activity, activityCity, userProfile?.preferences?.travelCompanions);
                } else {
                  setItineraryChatInput(`What should I do at ${item.activity || item.location}?`);
                  setShowItineraryChat(true);
                }
              }}
              className="mt-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
              style={FONTS.body}
            >
              {item.activity && activityCity ? '✨ Get tips' : 'Ask for suggestions'}
            </button>
          </div>
        )}
      </div>
    );
  };

  // Tips tab content - handles both array and string formats
  const TipsTabContent = ({ item }) => {
    // Handle array of tip objects
    if (Array.isArray(item.tips) && item.tips.length > 0) {
      return (
        <div className="space-y-3">
          {item.tips.map((tip, i) => (
            <div
              key={i}
              className={`rounded-xl p-4 border ${
                tip.type === 'warning'
                  ? 'bg-amber-50 border-amber-200'
                  : tip.type === 'info'
                  ? 'bg-blue-50 border-blue-200'
                  : 'bg-green-50 border-green-200'
              }`}
            >
              <h4 className="font-semibold mb-2 flex items-center gap-2" style={FONTS.headingSemibold}>
                <span>{tip.icon || '💡'}</span> {tip.title}
              </h4>
              <p className="text-sm" style={FONTS.body}>{tip.content}</p>
            </div>
          ))}
        </div>
      );
    }

    // Handle single string tip
    if (item.tips && typeof item.tips === 'string') {
      return (
        <div className="space-y-3">
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <h4 className="font-semibold mb-2 flex items-center gap-2 text-amber-800" style={FONTS.headingSemibold}>
              <Lightbulb className="w-4 h-4" /> Pro Tip
            </h4>
            <p className="text-sm text-amber-700" style={FONTS.body}>{item.tips}</p>
          </div>
          <button
            onClick={(e) => {
              e.stopPropagation();
              const name = item.activity || item.restaurant;
              const activityCity = itinerary?.days?.[currentDay - 1]?.city || selectedDestination?.name;
              if (name && activityCity) {
                fetchActivityDetails(name, activityCity, userProfile?.preferences?.travelCompanions);
              } else {
                setItineraryChatInput(`What are more insider tips for ${name}?`);
                setShowItineraryChat(true);
              }
            }}
            className="w-full mt-2 py-2 text-sm text-teal-600 hover:text-teal-700 font-medium border border-teal-200 rounded-lg hover:bg-teal-50 transition-colors"
            style={FONTS.body}
          >
            ✨ Get more tips
          </button>
        </div>
      );
    }

    // No tips available — try loading inline first
    const activityCity = itinerary?.days?.[currentDay - 1]?.city || selectedDestination?.name;
    const cacheKey = `${item.activity || item.restaurant}|${activityCity}`;
    const cachedTips = activityDetailsCache[cacheKey];

    if (cachedTips?.loading) {
      return (
        <div className="text-center py-6">
          <div className="animate-spin w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full mx-auto mb-2"></div>
          <p className="text-gray-500 text-sm" style={FONTS.body}>Loading tips...</p>
        </div>
      );
    }

    if (cachedTips?.tips && Array.isArray(cachedTips.tips) && cachedTips.tips.length > 0) {
      return (
        <div className="space-y-1.5">
          {cachedTips.tips.map((tip, i) => (
            <div key={i} className="flex gap-2 p-2 bg-gray-50 rounded-lg">
              <span className="text-xs text-teal-500 mt-0.5">✦</span>
              <p className="text-xs text-gray-600" style={FONTS.body}>{tip}</p>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="text-center py-6">
        <p className="text-gray-500 text-sm italic" style={FONTS.body}>
          No tips available yet.
        </p>
        <button
          onClick={(e) => {
            e.stopPropagation();
            const name = item.activity || item.restaurant;
            if (name && activityCity) {
              fetchActivityDetails(name, activityCity, userProfile?.preferences?.travelCompanions);
            } else {
              setItineraryChatInput(`What are some insider tips for ${name}?`);
              setShowItineraryChat(true);
            }
          }}
          className="mt-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
          style={FONTS.body}
        >
          {(item.activity || item.restaurant) && activityCity ? '✨ Get tips' : 'Ask for insider tips'}
        </button>
      </div>
    );
  };

  // Fetch real Amadeus flight prices for an inter-city flight card
  // Route key includes cabin class so different cabin searches are cached separately
  const fetchFlightPrice = async (originCode, destCode, departureDate, options = {}) => {
    const { returnDate, isRoundTrip, cabinClass: explicitCabin, airlineFilter } = options;
    const baseRouteKey = `${originCode}-${destCode}`;
    const cabinClass = explicitCabin || flightCabinClass[baseRouteKey] || preferredTravelClass || profile?.preferred_cabin_class || 'ECONOMY';
    const routeKey = `${baseRouteKey}-${cabinClass}`;
    // Skip if already loaded or loading
    if (flightPrices[routeKey]?.data || flightPrices[routeKey]?.loading) return;

    setFlightPrices(prev => ({ ...prev, [routeKey]: { loading: true } }));

    try {
      const { searchFlightsWithFallback } = await import('./lib/amadeusApi');
      const searchParams = {
        origin: originCode,
        destination: destCode,
        departureDate: departureDate || new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0], // 30 days from now if no date
        travelClass: cabinClass,
        maxResults: 10,
        currencyCode: profile?.currency || userProfile?.preferences?.currency || 'USD',
      };

      // Include return date for round-trip searches (non-multi-stop trips)
      if (isRoundTrip && returnDate) {
        searchParams.returnDate = returnDate;
      }

      // Include airline filter if specified
      if (airlineFilter) {
        searchParams.includedAirlineCodes = airlineFilter;
      }

      const result = await withTimeout(searchFlightsWithFallback(searchParams), 15000, 'Flight search timed out');

      // Sort offers by total price (cheapest first), then by duration
      const allOffers = result.offers || [];
      const sortedOffers = [...allOffers].sort((a, b) => {
        // Primary sort: price
        const priceDiff = (a.price || 0) - (b.price || 0);
        if (priceDiff !== 0) return priceDiff;
        // Secondary sort: outbound duration
        const durA = a.itineraries?.[0]?.duration || 'PT999H';
        const durB = b.itineraries?.[0]?.duration || 'PT999H';
        const parseIsoDur = (d) => {
          const m = d.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
          return ((parseInt(m?.[1] || '0') * 60) + parseInt(m?.[2] || '0'));
        };
        return parseIsoDur(durA) - parseIsoDur(durB);
      });

      // Extract unique airlines from all results for the filter dropdown
      const uniqueAirlines = [...new Set(sortedOffers.flatMap(o => o.airlines || []))].filter(Boolean);

      const cheapest = sortedOffers[0] || null;
      const isRT = isRoundTrip && returnDate && cheapest?.itineraries?.length === 2;
      setFlightPrices(prev => ({
        ...prev,
        [routeKey]: {
          loading: false,
          isRoundTrip: isRT,
          data: cheapest ? {
            price: cheapest.price,
            currency: cheapest.currency,
            airlines: cheapest.airlines || [],
            duration: cheapest.itineraries?.[0]?.duration || null,
            segments: cheapest.itineraries?.[0]?.segments || [],
            // For round-trip: store return itinerary too
            returnDuration: isRT ? cheapest.itineraries?.[1]?.duration || null : null,
            returnSegments: isRT ? cheapest.itineraries?.[1]?.segments || [] : [],
            cabin: cabinClass,
            allOffers: sortedOffers.slice(0, 3), // Default: show 3 cheapest
            allOffersExpanded: sortedOffers, // All offers for "More Options"
            dictionaries: result.dictionaries || {}, // Carrier name mappings
            availableAirlines: uniqueAirlines, // For airline filter dropdown
          } : null,
          fallbackUsed: result.fallbackUsed || false,
          originalClass: result.originalClass || null,
          noResults: !cheapest,
        },
      }));
    } catch (err) {
      console.error('Flight price fetch error:', err);
      setFlightPrices(prev => ({
        ...prev,
        [routeKey]: { loading: false, error: err.message },
      }));
    }
  };

  // Fetch real Amadeus hotel prices for a city
  const fetchHotelPrices = async (cityCode, checkIn, checkOut) => {
    const hotelKey = `${cityCode}-${checkIn}`;
    if (hotelPrices[hotelKey]?.data || hotelPrices[hotelKey]?.loading) return;

    setHotelPrices(prev => ({ ...prev, [hotelKey]: { loading: true } }));

    try {
      const { searchHotels } = await import('./lib/amadeusApi');
      const result = await withTimeout(searchHotels({
        cityCode,
        checkIn,
        checkOut,
        adults: 2,
        currencyCode: profile?.currency || userProfile?.preferences?.currency || 'USD',
        maxResults: 5,
      }), 15000, 'Hotel price search timed out');

      setHotelPrices(prev => ({
        ...prev,
        [hotelKey]: {
          loading: false,
          data: result.hotels || [],
          hasRealPricing: result.meta?.hasRealPricing || false,
        },
      }));
    } catch (err) {
      console.error('Hotel price fetch error:', err);
      setHotelPrices(prev => ({
        ...prev,
        [hotelKey]: { loading: false, error: err.message },
      }));
    }
  };

  // Bug #16 web parity: hide the "Change" button on hotel cards when the
  // stop represents a user-pinned named property (Hotel Kosten Aike,
  // Gritti Palace, Ashford Castle, etc.). Stops added via TripBuilder's
  // resort toggle carry `stopType: 'resort'`; we also accept any stop
  // whose name contains common hospitality keywords so
  // "Gritti Palace"-style destinations (added via the generic picker)
  // are treated the same. Mirrors mobile's `isPinnedHotelCity` from
  // PR #208.
  const isPinnedHotelCity = (city) => {
    if (!city) return false;
    const needle = String(city).toLowerCase().trim();
    const HOTEL_NAME_KEYWORDS = /\b(hotel|castle|palace|resort|villa|lodge|inn|chateau|ch[âa]teau|riad|ryokan)\b/i;
    return (tripBuilderStops || []).some((s) => {
      if (!s) return false;
      const stopName = String(s.resolvedLocation || s.name || '').toLowerCase();
      const looksLikeNamedHotel = (s.stopType === 'hotel' || s.stopType === 'resort') || HOTEL_NAME_KEYWORDS.test(stopName);
      if (!looksLikeNamedHotel) return false;
      return stopName.includes(needle) || needle.includes(stopName);
    });
  };

  // Fetch Amadeus hotel alternatives for "Change Hotel" modal
  const fetchHotelAlternatives = async (dayIndex) => {
    const dayData = itinerary?.days?.[dayIndex];
    if (!dayData?.hotel) return;

    setHotelAlternatives({ day: dayIndex, loading: true, options: [], oldHotelName: dayData.hotel.name });

    try {
      const { searchAirports, searchHotels } = await import('./lib/amadeusApi');
      const cityName = dayData.baseCity || selectedDestination?.name || itinerary?.destination?.split(',')[0]?.trim();

      // Try to get the IATA city code (NOT airport code — airport codes return airport-area hotels)
      let cityCode = null;

      // Always search by city name to get the CITY-type code (e.g., "YMQ" for Montreal, not "YUL" airport)
      try {
        const airportResults = await searchAirports(cityName, 10);
        const cityResult = airportResults.find(r => r.type === 'CITY');
        if (cityResult) {
          cityCode = cityResult.iataCode;
        } else if (airportResults.length > 0) {
          // Find a result whose city/name actually matches the search (avoid Shanghai for "Sharm el-Sheikh")
          const cityLower = (cityName || '').toLowerCase();
          const nameMatch = airportResults.find(r =>
            (r.city || '').toLowerCase().includes(cityLower) ||
            (r.name || '').toLowerCase().includes(cityLower) ||
            cityLower.includes((r.city || '').toLowerCase())
          );
          cityCode = nameMatch ? nameMatch.iataCode : airportResults[0].iataCode;
        }
      } catch (searchErr) {
        debugError('Airport/city search failed:', searchErr);
      }

      // Fallback: try itinerary airport codes if search failed
      if (!cityCode) {
        if (itinerary?.arrivalAirport?.code && dayIndex < (itinerary.days?.length || 0) / 2) {
          cityCode = itinerary.arrivalAirport.code;
        } else if (itinerary?.departureAirport?.code) {
          cityCode = itinerary.departureAirport.code;
        }
      }

      if (!cityCode) {
        setHotelAlternatives(prev => prev?.day === dayIndex ? { ...prev, loading: false, error: 'Could not find city code' } : prev);
        return;
      }

      // Calculate check-in/check-out from trip dates
      const tripStart = itinerary?.startDate || userProfile?.preferences?.startDate || new Date().toISOString().split('T')[0];
      const checkInDate = new Date(tripStart + 'T12:00:00');
      checkInDate.setDate(checkInDate.getDate() + dayIndex);
      const checkIn = checkInDate.toISOString().split('T')[0];

      // Find how many consecutive days have the same hotel
      let sameHotelDays = 1;
      for (let d = dayIndex + 1; d < (itinerary.days?.length || 0); d++) {
        if (itinerary.days[d]?.hotel?.name === dayData.hotel.name) sameHotelDays++;
        else break;
      }
      const checkOutDate = new Date(checkInDate);
      checkOutDate.setDate(checkOutDate.getDate() + sameHotelDays);
      const checkOut = checkOutDate.toISOString().split('T')[0];

      const result = await withTimeout(searchHotels({
        cityCode,
        checkIn,
        checkOut,
        adults: 2,
        currencyCode: profile?.currency || userProfile?.preferences?.currency || 'USD',
        maxResults: 15,
      }), 15000, 'Hotel alternatives search timed out');

      // Reset search/filter state when new results arrive
      setHotelSearchFilter('');
      setHotelStarFilter(0);
      setShowManualHotelEntry(false);

      setHotelAlternatives(prev => prev?.day === dayIndex ? {
        ...prev,
        loading: false,
        options: result.hotels || [],
        hasRealPricing: result.meta?.hasRealPricing || false,
        nights: sameHotelDays,
        checkIn,
        checkOut,
        cityName,
      } : prev);
    } catch (err) {
      debugError('Hotel alternatives fetch error:', err);
      setHotelAlternatives(prev => prev?.day === dayIndex ? { ...prev, loading: false, error: err.message } : prev);
    }
  };

  // Fetch room options for a specific hotel (shown when user clicks hotel details)
  const fetchHotelRoomOptions = async (dayIndex) => {
    const dayData = itinerary?.days?.[dayIndex];
    if (!dayData?.hotel) return;

    setHotelRoomOptions({ day: dayIndex, loading: true, rooms: [], hotelName: dayData.hotel.name, error: null });

    try {
      const { searchHotelRoomOptions: searchRooms, searchAirports } = await import('./lib/amadeusApi');
      const cityName = dayData.baseCity || selectedDestination?.name || itinerary?.destination?.split(',')[0]?.trim();

      // Determine city code (prefer CITY type, not airport code)
      let cityCode = null;
      try {
        const airportResults = await searchAirports(cityName, 10);
        const cityResult = airportResults.find(r => r.type === 'CITY');
        if (cityResult) {
          cityCode = cityResult.iataCode;
        } else if (airportResults.length > 0) {
          const cityLower = (cityName || '').toLowerCase();
          const nameMatch = airportResults.find(r =>
            (r.city || '').toLowerCase().includes(cityLower) ||
            (r.name || '').toLowerCase().includes(cityLower) ||
            cityLower.includes((r.city || '').toLowerCase())
          );
          cityCode = nameMatch ? nameMatch.iataCode : airportResults[0].iataCode;
        }
      } catch (searchErr) {
        debugError('Airport/city search failed:', searchErr);
      }
      // Fallback: try itinerary airport codes
      if (!cityCode) {
        if (itinerary?.arrivalAirport?.code && dayIndex < (itinerary.days?.length || 0) / 2) {
          cityCode = itinerary.arrivalAirport.code;
        } else if (itinerary?.departureAirport?.code) {
          cityCode = itinerary.departureAirport.code;
        }
      }

      if (!cityCode) {
        setHotelRoomOptions(prev => prev?.day === dayIndex ? { ...prev, loading: false, error: 'Could not determine city code' } : prev);
        return;
      }

      // Calculate dates
      const tripStart = itinerary?.startDate || userProfile?.preferences?.startDate || new Date().toISOString().split('T')[0];
      const checkInDate = new Date(tripStart + 'T12:00:00');
      checkInDate.setDate(checkInDate.getDate() + dayIndex);
      const checkIn = checkInDate.toISOString().split('T')[0];

      let sameHotelDays = 1;
      for (let d = dayIndex + 1; d < (itinerary.days?.length || 0); d++) {
        if (itinerary.days[d]?.hotel?.name === dayData.hotel.name) sameHotelDays++;
        else break;
      }
      const checkOutDate = new Date(checkInDate);
      checkOutDate.setDate(checkOutDate.getDate() + sameHotelDays);
      const checkOut = checkOutDate.toISOString().split('T')[0];

      const result = await searchRooms({
        hotelName: dayData.hotel.name,
        cityCode,
        checkIn,
        checkOut,
        adults: 2,
        currencyCode: profile?.currency || userProfile?.preferences?.currency || 'USD',
      });

      setHotelRoomOptions(prev => prev?.day === dayIndex ? {
        ...prev,
        loading: false,
        rooms: result.rooms || [],
        hotelId: result.hotelId,
        amadeusHotelName: result.hotelName,
        rating: result.rating,
        hasRealPricing: result.meta?.hasRealPricing || false,
        nights: sameHotelDays,
      } : prev);
    } catch (err) {
      debugError('Hotel room options fetch error:', err);
      setHotelRoomOptions(prev => prev?.day === dayIndex ? { ...prev, loading: false, error: err.message } : prev);
    }
  };

  // Auto-fetch Amadeus hotel data for a specific hotel (used after generation completes)
  const autoFetchAmadeusHotel = async (hotelName, dayIndex) => {
    if (!hotelName || !itinerary?.days?.[dayIndex] || !features.AMADEUS_HOTELS) return;
    if (amadeusHotelFetchedRef.current.has(hotelName)) return;
    amadeusHotelFetchedRef.current.add(hotelName);

    setAmadeusHotelData(prev => ({ ...prev, [hotelName]: { loading: true, rooms: [], error: null } }));

    try {
      const { searchHotelRoomOptions: searchRooms, searchAirports } = await import('./lib/amadeusApi');
      const dayData = itinerary.days[dayIndex];
      const cityName = dayData.baseCity || selectedDestination?.name || itinerary?.destination?.split(',')[0]?.trim();

      // Determine city code — prefer CITY type code (not airport code)
      let cityCode = null;

      // Strategy 1: Search Amadeus for CITY-type code (most accurate for hotel search)
      try {
        const airportResults = await searchAirports(cityName, 10);
        const cityResult = airportResults.find(r => r.type === 'CITY');
        if (cityResult) {
          cityCode = cityResult.iataCode;
        } else if (airportResults.length > 0) {
          const cityLower = (cityName || '').toLowerCase();
          const nameMatch = airportResults.find(r =>
            (r.city || '').toLowerCase().includes(cityLower) ||
            (r.name || '').toLowerCase().includes(cityLower) ||
            cityLower.includes((r.city || '').toLowerCase())
          );
          cityCode = nameMatch ? nameMatch.iataCode : airportResults[0].iataCode;
        }
      } catch (searchErr) {
        debugLog(`🏨 Airport search failed for "${cityName}":`, searchErr.message);
      }

      // Strategy 2: Local lookup as fallback
      if (!cityCode && cityName) {
        const localCode = lookupCityAirportCode(cityName);
        if (localCode) cityCode = localCode;
      }

      // Strategy 3: Use itinerary airport codes as last resort
      if (!cityCode) {
        if (itinerary?.arrivalAirport?.code && dayIndex < (itinerary.days?.length || 0) / 2) {
          cityCode = itinerary.arrivalAirport.code;
        } else if (itinerary?.departureAirport?.code) {
          cityCode = itinerary.departureAirport.code;
        }
      }

      // Strategy 4: Check travelStructure stops
      if (!cityCode && itinerary?.travelStructure?.stops) {
        const stop = itinerary.travelStructure.stops.find(s => dayIndex >= s.startDay && dayIndex <= s.endDay);
        if (stop?.airportCode) cityCode = stop.airportCode;
      }

      if (!cityCode) {
        debugLog(`🏨 No city code found for "${hotelName}" in "${cityName}" — skipping Amadeus fetch`);
        setAmadeusHotelData(prev => ({ ...prev, [hotelName]: { loading: false, rooms: [], error: 'No city code found', attempted: true } }));
        return;
      }

      debugLog(`🏨 Fetching Amadeus rooms for "${hotelName}" in city ${cityCode}...`);

      // Calculate dates — skip Amadeus pricing if no real dates are set
      const tripStart = itinerary?.startDate || userProfile?.preferences?.startDate;
      if (!tripStart) {
        debugLog(`🏨 No trip dates set — skipping Amadeus room search for "${hotelName}" (would get misleading availability)`);
        setAmadeusHotelData(prev => ({ ...prev, [hotelName]: { loading: false, rooms: [], error: null, attempted: false, noDates: true } }));
        return;
      }
      const checkInDate = new Date(tripStart + 'T12:00:00');
      checkInDate.setDate(checkInDate.getDate() + dayIndex);
      const checkIn = checkInDate.toISOString().split('T')[0];
      let sameHotelDays = 1;
      for (let d = dayIndex + 1; d < (itinerary.days?.length || 0); d++) {
        if (itinerary.days[d]?.hotel?.name === hotelName) sameHotelDays++;
        else break;
      }
      const checkOutDate = new Date(checkInDate);
      checkOutDate.setDate(checkOutDate.getDate() + sameHotelDays);
      const checkOut = checkOutDate.toISOString().split('T')[0];

      const result = await searchRooms({
        hotelName,
        cityCode,
        checkIn,
        checkOut,
        adults: 2,
        currencyCode: profile?.currency || userProfile?.preferences?.currency || 'USD',
      });

      const rooms = result.rooms || [];
      const hotelFound = result.meta?.hotelFound || false;
      // Pick the best room: cheapest with a room type
      const bestRoom = rooms.find(r => r.roomType || r.roomDescription) || rooms[0] || null;

      setAmadeusHotelData(prev => ({
        ...prev,
        [hotelName]: {
          loading: false,
          rooms,
          rating: result.rating,
          hotelId: result.hotelId,
          amadeusHotelName: result.hotelName,
          bestRoom,
          hasRealPricing: result.meta?.hasRealPricing || false,
          hotelFound,
          nights: sameHotelDays,
          error: null,
          attempted: true,
        }
      }));

      // Auto-apply Amadeus data to itinerary days with this hotel
      if (bestRoom) {
        // Use the actual currency from the Amadeus response (per-room or meta)
        const amadeusRoomCurrency = bestRoom.currency || result.meta?.currency || null;
        setItinerary(prev => {
          if (!prev?.days) return prev;
          const updated = { ...prev, days: prev.days.map(day => {
            if (day?.hotel?.name === hotelName && !day.hotel.amadeusVerified) {
              return {
                ...day,
                hotel: {
                  ...day.hotel,
                  roomType: bestRoom.roomType || bestRoom.roomDescription || day.hotel.roomType,
                  boardType: bestRoom.boardType || day.hotel.boardType,
                  roomDescription: bestRoom.roomDescription || day.hotel.roomDescription,
                  pricePerNight: bestRoom.pricePerNight || bestRoom.price || day.hotel.pricePerNight,
                  priceCurrency: amadeusRoomCurrency, // Actual currency from Amadeus (e.g. JPY, IDR)
                  rating: result.rating || day.hotel.rating,
                  amadeusVerified: true,
                }
              };
            }
            return day;
          })};
          return updated;
        });
      }

      debugLog(`🏨 Amadeus hotel data fetched for "${hotelName}": ${rooms.length} rooms, hotelFound=${hotelFound}, best=${bestRoom?.roomType || 'N/A'}`);
    } catch (err) {
      debugError(`Amadeus hotel auto-fetch error for "${hotelName}":`, err);
      setAmadeusHotelData(prev => ({ ...prev, [hotelName]: { loading: false, rooms: [], error: err.message, attempted: true } }));
    }
  };

  // Auto-fetch Amadeus hotel data after generation completes
  // Triggers when isProgressiveGenerating transitions from true to false
  // Amadeus auto-fetch: trigger when itinerary is ready with hotel data
  // NOTE: The component is unmounted during the loading/generation screen,
  // so we can't rely on isProgressiveGenerating true→false transitions.
  // Instead, we trigger on mount/update when itinerary has days and generation is complete.
  const amadeusAutoFetchTriggeredRef = useRef(false);
  const prevItineraryIdRef = useRef(null);
  useEffect(() => {
    // Skip if generation is still in progress
    if (isProgressiveGenerating) return;
    // Skip if no itinerary data
    if (!itinerary?.days?.length || itinerary.days.length === 0) return;
    // Skip if feature flag is off
    if (!features.AMADEUS_HOTELS) return;

    // Detect new itinerary: use destination + days count as a fingerprint
    const itineraryFingerprint = `${itinerary.destination || ''}-${itinerary.days.length}-${itinerary.days[0]?.hotel?.name || ''}`;
    const isNewItinerary = itineraryFingerprint !== prevItineraryIdRef.current;

    if (isNewItinerary) {
      // New itinerary detected — reset guards
      amadeusAutoFetchTriggeredRef.current = false;
      amadeusHotelFetchedRef.current.clear();
      prevItineraryIdRef.current = itineraryFingerprint;
    }

    // One-shot guard: only auto-fetch once per itinerary
    if (amadeusAutoFetchTriggeredRef.current) return;

    // Collect unique hotels that haven't been fetched yet
    const uniqueHotels = new Map(); // hotelName → first dayIndex
    itinerary.days.forEach((day, idx) => {
      if (day?.hotel?.name && !uniqueHotels.has(day.hotel.name) && !day.hotel.amadeusVerified) {
        uniqueHotels.set(day.hotel.name, idx);
      }
    });

    if (uniqueHotels.size > 0) {
      amadeusAutoFetchTriggeredRef.current = true;
      debugLog(`🏨 Auto-fetching Amadeus data for ${uniqueHotels.size} hotels:`, Array.from(uniqueHotels.keys()));
      // Stagger requests to avoid overwhelming the API
      let delay = 500;
      uniqueHotels.forEach((dayIdx, hotelName) => {
        setTimeout(() => autoFetchAmadeusHotel(hotelName, dayIdx), delay);
        delay += 1000; // 1 second between requests
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProgressiveGenerating, itinerary?.days?.length, itinerary?.destination]);

  // Handle day regeneration triggered by flight selection (0-indexed dayIndex → 1-indexed dayNumber)
  const handleDayRegeneration = (dayIndex, feedback) => {
    const dayNumber = dayIndex + 1;
    debugLog(`🔄 Flight-triggered regeneration for Day ${dayNumber}: ${feedback}`);
    regenerateSingleDay(dayNumber, feedback);
  };

  // Select an Amadeus flight offer and update arrival/departure flight details
  // type: 'arrival' (Day 1 inbound) or 'departure' (last day outbound)
  // offer: the Amadeus flight offer object from flightPrices[routeKey].data.allOffers
  // Airline utilities — imported from @wander/shared via ./utils/airlineUtils
  const AIRLINE_NAMES = _AIRLINE_NAMES;
  const resolveAirlineName = _resolveAirlineName;

  // Parse time from Amadeus ISO datetime string (e.g., "2024-03-15T12:54:00")
  // Returns HH:MM in 24-hour format — avoids locale-dependent Date parsing
  const parseAmadeusTime = (isoString) => {
    if (!isoString) return null;
    const match = isoString.match(/T(\d{2}:\d{2})/);
    return match ? match[1] : null;
  };

  // Parse date from Amadeus ISO datetime string
  const parseAmadeusDate = (isoString) => {
    if (!isoString) return null;
    const match = isoString.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : null;
  };

  // Format Amadeus ISO datetime to short date like "Mon, Apr 15"
  const formatAmadeusShortDate = (isoString) => {
    if (!isoString) return '';
    const match = isoString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const d = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
    return `${days[d.getDay()]}, ${months[d.getMonth()]} ${parseInt(match[3])}`;
  };

  // Check if arrival is next day compared to departure (+1 indicator)
  const isNextDayArrival = (departureTime, arrivalTime) => {
    if (!departureTime || !arrivalTime) return false;
    const depDate = parseAmadeusDate(departureTime);
    const arrDate = parseAmadeusDate(arrivalTime);
    if (!depDate || !arrDate) return false;
    return arrDate > depDate;
  };

  const selectFlightOffer = async (type, offer, dictionaries = {}) => {
    if (!offer?.itineraries?.[0]?.segments?.length) return;

    // Guard: If generation is still in progress, set the flight details but skip regeneration
    const isStillGenerating = generationInProgressRef.current;

    const itin = offer.itineraries[0];
    const segs = itin.segments;
    const firstSeg = segs[0];
    const lastSeg = segs[segs.length - 1];
    const carrierCodes = [...new Set(segs.map(s => s.carrier).filter(Boolean))];
    const carrierNames = carrierCodes.map(c => resolveAirlineName(c, dictionaries));
    const flightNums = segs.map(s => `${s.carrier || ''}${s.flightNumber || ''}`).filter(Boolean);

    if (type === 'arrival') {
      // For arrival: the ARRIVAL time at destination matters (from last segment)
      const arrivalTime = parseAmadeusTime(lastSeg.arrival?.time) || arrivalFlight.time;
      // Date should be the departure date (when the flight leaves — this is what the user booked)
      const departureDate = parseAmadeusDate(firstSeg.departure?.time) || arrivalFlight.date;

      setArrivalFlight({
        time: arrivalTime,
        flightNumber: flightNums[0] || '',
        airline: carrierNames.join(', ') || '',
        from: firstSeg.departure?.airport || '',
        date: departureDate,
      });

      const airlineName = carrierNames[0] || flightNums[0] || 'selected flight';
      showToast(`Arrival flight set: ${airlineName} arriving ${arrivalTime}`, 'success');
      debugLog(`✈️ Selected arrival flight: ${flightNums[0]} (${carrierNames.join(', ')}) arriving ${arrivalTime}`);

      // Offer to recalculate Day 1 schedule with the new arrival time (skip if generation is in progress)
      if (itinerary?.days?.length > 0 && !isStillGenerating) {
        const recalculate = await showConfirmModal({
          title: 'Update Day 1 Schedule',
          message: `Arrival time set to ${arrivalTime}. Recalculate Day 1 activities to start after your arrival?`,
          confirmText: 'Recalculate',
          cancelText: 'Keep Current',
        });
        if (recalculate) {
          handleDayRegeneration(0, `Traveler arrives at ${arrivalTime} via flight ${flightNums[0] || 'selected flight'}. Recalculate activities to start after arrival + customs/transit time.`);
        }
      } else if (isStillGenerating) {
        showToast('Flight saved. Schedule will use this arrival time for remaining days.', 'info');
      }
    } else {
      // For departure: the DEPARTURE time from origin matters (from first segment)
      const departureTime = parseAmadeusTime(firstSeg.departure?.time) || departureFlight.time;
      const departureDate = parseAmadeusDate(firstSeg.departure?.time) || departureFlight.date;

      setDepartureFlight({
        time: departureTime,
        flightNumber: flightNums[0] || '',
        airline: carrierNames.join(', ') || '',
        to: lastSeg.arrival?.airport || '',
        date: departureDate,
      });

      const airlineName = carrierNames[0] || flightNums[0] || 'selected flight';
      showToast(`Departure flight set: ${airlineName} departing ${departureTime}`, 'success');
      debugLog(`✈️ Selected departure flight: ${flightNums[0]} (${carrierNames.join(', ')}) departing ${departureTime}`);

      // Offer to recalculate last day schedule with the new departure time (skip if generation is in progress)
      if (itinerary?.days?.length > 0 && !isStillGenerating) {
        const lastDayIdx = itinerary.days.length - 1;
        const recalculate = await showConfirmModal({
          title: `Update Day ${lastDayIdx + 1} Schedule`,
          message: `Departure time set to ${departureTime}. Recalculate the last day so activities end in time for your flight?`,
          confirmText: 'Recalculate',
          cancelText: 'Keep Current',
        });
        if (recalculate) {
          handleDayRegeneration(lastDayIdx, `Traveler departs at ${departureTime} via flight ${flightNums[0] || 'selected flight'}. Recalculate so all activities end 3 hours before departure for transit + security.`);
        }
      } else if (isStillGenerating) {
        showToast('Flight saved. Schedule will use this departure time for remaining days.', 'info');
      }
    }
  };

  // Fetch road trip stop recommendations
  const fetchRoadTripStops = async (cardId, location, duration) => {
    const parts = location.split('→').map(s => s.trim());
    if (parts.length !== 2) return;

    const [from, to] = parts;
    setRoadTripStops(prev => ({ ...prev, [cardId]: { loading: true } }));

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/road-trip-stops`, {
        method: 'POST',
        headers: await getApiHeaders(),
        body: JSON.stringify({
          from,
          to,
          preferences: { gas: true, food: true, scenic: true, rest: false },
          tripDuration: duration
        })
      });

      const data = await response.json();
      setRoadTripStops(prev => ({
        ...prev,
        [cardId]: { loading: false, data: data.success ? data : null, error: data.success ? null : data.error }
      }));
    } catch (error) {
      setRoadTripStops(prev => ({
        ...prev,
        [cardId]: { loading: false, error: error.message }
      }));
    }
  };

  // Fetch booking info for an activity when expanded
  const fetchActivityBookingInfo = async (activityName, city, country) => {
    // Normalize the key to avoid duplicates
    const cacheKey = activityName.toLowerCase().trim();

    // Skip if already loaded or loading
    if (activityBookingInfo[cacheKey]?.data || activityBookingInfo[cacheKey]?.loading) {
      return;
    }

    // Check if this activity likely needs booking info
    const needsBookingPatterns = [
      /museum|gallery|palace|castle|cathedral|basilica/i,
      /anne frank|uffizi|vatican|louvre|sagrada|alhambra|machu picchu|petra/i,
      /colosseum|sistine|versailles|prado|rijksmuseum|hermitage/i,
      /tour|tasting|cooking class|spa|massage/i,
      /show|theater|concert|performance|opera|ballet/i,
      /hot air balloon|helicopter|scenic flight/i,
      /zoo|aquarium|theme park|amusement|disney/i,
      /observatory|tower|viewpoint|deck|eye/i,
    ];

    const activityLower = activityName.toLowerCase();
    const shouldFetch = needsBookingPatterns.some(p => p.test(activityLower));

    if (!shouldFetch) {
      // Mark as no booking needed for this activity
      setActivityBookingInfo(prev => ({
        ...prev,
        [cacheKey]: { loading: false, data: null, notNeeded: true }
      }));
      return;
    }

    setActivityBookingInfo(prev => ({ ...prev, [cacheKey]: { loading: true } }));

    try {
      const bookingData = await getAttractionBookingInfo(activityName, city, country);

      setActivityBookingInfo(prev => ({
        ...prev,
        [cacheKey]: {
          loading: false,
          data: bookingData?.requiresAdvanceBooking ? bookingData : null,
          notNeeded: !bookingData?.requiresAdvanceBooking
        }
      }));
    } catch (error) {
      setActivityBookingInfo(prev => ({
        ...prev,
        [cacheKey]: { loading: false, error: error.message }
      }));
    }
  };

  // Lazy-fetch venue info (reviews, cost data, web mentions) on card expand
  const fetchVenueInfo = async (name, city, placeId) => {
    if (!name || !city) return;
    const cacheKey = `${name.toLowerCase().trim()}:${city.toLowerCase().trim()}`;

    // Skip if already loaded or loading
    if (venueInfo[cacheKey]?.data || venueInfo[cacheKey]?.loading) return;

    setVenueInfo(prev => ({ ...prev, [cacheKey]: { loading: true } }));

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      const res = await fetch(`${BACKEND_URL}/api/venue/info`, {
        method: 'POST',
        headers: await getApiHeaders(),
        body: JSON.stringify({ name, city, placeId: placeId || null }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const data = await res.json();
      setVenueInfo(prev => ({ ...prev, [cacheKey]: { loading: false, data } }));
    } catch (err) {
      setVenueInfo(prev => ({ ...prev, [cacheKey]: { loading: false, error: err.message } }));
    }
  };

  // Helper to get cached venue info for a given name + city
  const getVenueData = (name, city) => {
    if (!name || !city) return null;
    const cacheKey = `${name.toLowerCase().trim()}:${city.toLowerCase().trim()}`;
    return venueInfo[cacheKey]?.data || null;
  };

  // Road trip stops display component
  // L4: accepts `targetDayIndex` + `onAdd(stop)` so each card can render an
  // "Add to Day N" button. When targetDayIndex is null the button is hidden
  // (defensive: shouldn't happen in normal itinerary flow).
  const RoadTripStopsDisplay = ({ stops, routeTips, targetDayIndex, onAdd }) => (
    <div className="space-y-3">
      <h4 className="font-semibold text-gray-800" style={FONTS.headingSemibold}>Recommended Stops</h4>
      {stops && stops.map((stop, i) => (
        <div key={i} className="flex gap-3 p-3 bg-gray-50 rounded-xl hover:bg-green-50 transition-colors">
          <div className="flex flex-col items-center">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-white ${
              stop.type === 'scenic' ? 'bg-green-500' :
              stop.type === 'food' ? 'bg-orange-500' :
              stop.type === 'gas' ? 'bg-blue-500' :
              stop.type === 'rest' ? 'bg-purple-500' :
              'bg-teal-500'
            }`}>
              {stop.type === 'scenic' ? '📸' :
               stop.type === 'food' ? '🍔' :
               stop.type === 'gas' ? '⛽' :
               stop.type === 'rest' ? '🛋️' : '🎯'}
            </span>
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h5 className="font-medium text-gray-900" style={FONTS.headingSemibold}>{stop.name}</h5>
              <span className="text-xs text-gray-500">{stop.distanceFromStart}</span>
            </div>
            <p className="text-sm text-gray-600 mt-1" style={FONTS.body}>{stop.description}</p>
            <div className="flex items-center gap-3 mt-2">
              <span className="text-xs text-gray-500">⏱️ {stop.estimatedStopTime}</span>
              {stop.address && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-700"
                  onClick={(e) => e.stopPropagation()}
                >
                  View on Map
                </a>
              )}
              {typeof targetDayIndex === 'number' && onAdd && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onAdd(stop); }}
                  className="ml-auto text-xs px-2.5 py-1 bg-teal-500 hover:bg-teal-600 text-white rounded-full font-semibold transition-colors"
                  style={FONTS.body}
                >
                  + Add to Day {targetDayIndex + 1}
                </button>
              )}
            </div>
            {stop.highlights && stop.highlights.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {stop.highlights.map((h, j) => (
                  <span key={j} className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full text-xs">
                    {h}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}
      {routeTips && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mt-3">
          <p className="text-sm text-blue-800" style={FONTS.body}>
            💡 {routeTips}
          </p>
        </div>
      )}
    </div>
  );

  // L4: Map a road-trip stop to the schedule-item shape expected by addActivity().
  // Stops have .type (scenic/food/gas/rest), .estimatedStopTime (e.g. "30 min"),
  // .description, .address, .name, .highlights. We normalise duration to a string
  // the timeline renderer understands and tag type as 'activity' for non-food stops.
  const addRoadTripStopToDay = (dayIndex, stop) => {
    if (typeof dayIndex !== 'number' || !stop) return;
    const emoji = stop.type === 'scenic' ? '📸' :
      stop.type === 'food' ? '🍽️' :
      stop.type === 'gas' ? '⛽' :
      stop.type === 'rest' ? '🛋️' : '📍';
    // estimatedStopTime is free-form ("30 minutes", "1 hour"); pass through if set.
    const duration = stop.estimatedStopTime || '30 minutes';
    addActivity(dayIndex, {
      activity: `${emoji} ${stop.name}`,
      description: stop.description || '',
      duration,
      location: stop.address || stop.name,
      type: stop.type === 'food' ? 'meal' : 'activity',
      // Leave time blank so addActivity's caller/orchestrator can place it —
      // addActivity already appends to schedule; a downstream sort pass slots it.
      time: '',
      cost: 0,
      priceTier: '$',
    });
  };

  // Helper: extract price tier ($ symbols) and description from text after destination name
  const extractDestMeta = (afterMatch, messageContent, matchEndIndex) => {
    // Extract price tier: look for sequences of $ or currency symbols like €€, £££
    const priceMatch = afterMatch.match(/[•·]\s*([$$€£]{1,4})\s*[•·]/);
    const price = priceMatch ? priceMatch[1] : null;

    // Extract location/city from hotel format: "• Location • $$ per night"
    // The location is the first bullet-separated segment (before price)
    const locationMatch = afterMatch.match(/^\s*[•·]\s*([^•·$€£\n]+?)(?:\s*[•·]|$)/);
    const location = locationMatch ? locationMatch[1].trim().replace(/\s*per\s*night.*/i, '').trim() : null;

    // Extract description: text between end of metadata line and next **N. or end
    const restOfContent = messageContent.substring(matchEndIndex);
    // Get text after the first newline (skip the metadata line) up to next numbered item or double newline
    const descBlock = restOfContent.match(/\n([^*\n][^\n]{10,})/);
    const desc = descBlock ? descBlock[1].trim().replace(/\*\*Best time.*$/i, '').trim() : null;

    return { price, desc, location };
  };

  const extractDestinationsFromMessage = (messageContent) => {
    const destinations = [];

    // Try bold format first: **1. Name** (handles **1. Tokyo & Kyoto** • format)
    // More permissive: capture everything up to the closing ** or bullet point
    let regex = /\*\*(\d+)\.\s+([^*]+?)\*\*(?:\s*•|$)/gm;
    let match;

    while ((match = regex.exec(messageContent)) !== null) {
      const name = match[2].trim();
      const afterMatch = messageContent.substring(match.index + match[0].length, match.index + match[0].length + 500);

      // Check for drive time first (for hotel-first trips)
      // Handle formats like "~4hr drive", "4hr drive", "~4.5hr drive", "2hr 30min drive", "~1hr 15min drive", "30min drive", "45 min drive"
      const driveMatchHr = afterMatch.match(/~?(\d+(?:\.\d+)?)\s*-?\d*\.?\d*\s*hr\s*(?:(\d+)\s*min\w*\s*)?drive/i);
      const driveMatchMinOnly = !driveMatchHr ? afterMatch.match(/~?(\d+)\s*min\w*\s*drive/i) : null;
      const driveMatch = driveMatchHr || driveMatchMinOnly;
      const flightMatch = afterMatch.match(/~?(\d+(?:\.\d+)?)\s*hr\s*flight/i);
      // Fallback to just "Xhr" if no explicit drive/flight
      const genericMatch = !driveMatch && !flightMatch ? afterMatch.match(/~?(\d+(?:\.\d+)?)hr/) : null;

      // Convert hours + optional minutes to decimal hours (e.g., "2hr 30min" → 2.5, "30min" → 0.5)
      const driveHours = driveMatchHr
        ? parseFloat(driveMatchHr[1]) + (driveMatchHr[2] ? parseInt(driveMatchHr[2]) / 60 : 0)
        : driveMatchMinOnly ? parseInt(driveMatchMinOnly[1]) / 60 : null;
      const flightHours = flightMatch ? parseFloat(flightMatch[1]) : (genericMatch ? parseFloat(genericMatch[1]) : null);

      const { price, desc, location } = extractDestMeta(afterMatch, messageContent, match.index + match[0].length);

      destinations.push({
        index: parseInt(match[1]) - 1,
        name: name,
        flightHours: flightHours,
        driveHours: driveHours,
        ...(price && { price }),
        ...(desc && { desc }),
        ...(location && { location })
      });
    }
    
    // Second try: bold format ending just with ** (no bullet required)
    if (destinations.length === 0) {
      regex = /\*\*(\d+)\.\s+([^*\n]+?)\*\*/gm;
      while ((match = regex.exec(messageContent)) !== null) {
        const name = match[2].trim();
        // Skip if this looks like a header/label (contains "Stop" or "Property")
        if (/^(?:stop|property|option)\s*\d*:?\s*/i.test(name)) continue;

        const afterMatch = messageContent.substring(match.index + match[0].length, match.index + match[0].length + 200);
        // Handle formats like "~4hr drive", "4hr drive", "~4-5hr drive", "2hr 30min drive", "30min drive"
        const driveMatchHr = afterMatch.match(/~?(\d+(?:\.\d+)?)\s*-?\d*\.?\d*\s*hr?\s*(?:(\d+)\s*min\w*\s*)?drive/i);
        const driveMatchMinOnly = !driveMatchHr ? afterMatch.match(/~?(\d+)\s*min\w*\s*drive/i) : null;
        const driveMatch = driveMatchHr || driveMatchMinOnly;
        const flightMatch = afterMatch.match(/~?(\d+(?:\.\d+)?)\s*hr?\s*(?:flight|from)/i);
        const genericMatch = !driveMatch && !flightMatch ? afterMatch.match(/~?(\d+(?:\.\d+)?)\s*hr/) : null;

        const driveHours = driveMatchHr
          ? parseFloat(driveMatchHr[1]) + (driveMatchHr[2] ? parseInt(driveMatchHr[2]) / 60 : 0)
          : driveMatchMinOnly ? parseInt(driveMatchMinOnly[1]) / 60 : null;
        const flightHours = flightMatch ? parseFloat(flightMatch[1]) : (genericMatch ? parseFloat(genericMatch[1]) : null);

        const meta2 = extractDestMeta(afterMatch, messageContent, match.index + match[0].length);

        destinations.push({
          index: parseInt(match[1]) - 1,
          name: name,
          flightHours: flightHours,
          driveHours: driveHours,
          ...(meta2.price && { price: meta2.price }),
          ...(meta2.desc && { desc: meta2.desc }),
          ...(meta2.location && { location: meta2.location })
        });
      }
    }

    // Third try: format where number is outside bold: 1. **Name** or 1. **Name, Country**
    if (destinations.length === 0) {
      regex = /(?:^|\n)(\d+)\.\s+\*\*([^*\n]+?)\*\*/gm;
      while ((match = regex.exec(messageContent)) !== null) {
        const name = match[2].trim();
        const afterMatch = messageContent.substring(match.index + match[0].length, match.index + match[0].length + 200);

        const driveMatchHr = afterMatch.match(/(\d+(?:\.\d+)?)\s*hr?\s*drive/i);
        const driveMatchMinOnly = !driveMatchHr ? afterMatch.match(/(\d+)\s*min\w*\s*drive/i) : null;
        const driveMatch = driveMatchHr || driveMatchMinOnly;
        const flightMatch = afterMatch.match(/(\d+(?:\.\d+)?)\s*hr?\s*(?:flight|from)/i);
        const genericMatch = !driveMatch && !flightMatch ? afterMatch.match(/(\d+(?:\.\d+)?)\s*hr/) : null;

        const driveHours = driveMatchHr ? parseFloat(driveMatchHr[1])
          : driveMatchMinOnly ? parseInt(driveMatchMinOnly[1]) / 60 : null;
        const flightHours = flightMatch ? parseFloat(flightMatch[1]) : (genericMatch ? parseFloat(genericMatch[1]) : null);

        const meta3 = extractDestMeta(afterMatch, messageContent, match.index + match[0].length);
        destinations.push({
          index: parseInt(match[1]) - 1,
          name: name,
          flightHours: flightHours,
          driveHours: driveHours,
          ...(meta3.price && { price: meta3.price }),
          ...(meta3.desc && { desc: meta3.desc }),
          ...(meta3.location && { location: meta3.location })
        });
      }
    }

    // Fourth try: plain format: 1. Name • or \n1. Name
    if (destinations.length === 0) {
      regex = /(?:^|\n)(\d+)\.\s+([^•\n]+?)(?:\s+•|\n|$)/gm;
      while ((match = regex.exec(messageContent)) !== null) {
        const name = match[2].trim();
        const afterMatch = messageContent.substring(match.index + match[0].length, match.index + match[0].length + 200);

        // Check for drive time first (for hotel-first trips)
        // Handle formats like "~4hr drive", "4hr drive", "~4-5hr drive", "2hr 30min drive"
        const driveMatch = afterMatch.match(/~?(\d+(?:\.\d+)?)\s*-?\d*\.?\d*\s*hr\s*(?:(\d+)\s*min\w*\s*)?drive/i);
        const flightMatch = afterMatch.match(/~?(\d+(?:\.\d+)?)\s*hr\s*flight/i);
        const genericMatch = !driveMatch && !flightMatch ? afterMatch.match(/~?(\d+(?:\.\d+)?)hr/) : null;

        const driveHours = driveMatch ? parseFloat(driveMatch[1]) + (driveMatch[2] ? parseInt(driveMatch[2]) / 60 : 0) : null;
        const flightHours = flightMatch ? parseFloat(flightMatch[1]) : (genericMatch ? parseFloat(genericMatch[1]) : null);

        const meta4 = extractDestMeta(afterMatch, messageContent, match.index + match[0].length);
        destinations.push({
          index: parseInt(match[1]) - 1,
          name: name,
          flightHours: flightHours,
          driveHours: driveHours,
          ...(meta4.price && { price: meta4.price }),
          ...(meta4.desc && { desc: meta4.desc }),
          ...(meta4.location && { location: meta4.location })
        });
      }
    }

    // Fifth try: unnumbered bold names — **Hotel Name** · Location or **Hotel Name** •
    if (destinations.length === 0) {
      regex = /(?:^|\n)\*\*([^*\n]{3,50}?)\*\*(?:\s*[·•]|\s*\n)/gm;
      let idx = 0;
      while ((match = regex.exec(messageContent)) !== null) {
        const name = match[1].trim();
        // Skip common false positives
        if (/^(?:highlights?|best for|what'?s next|stop|property|option|tip|note)/i.test(name)) continue;
        if (/^\d+$/.test(name)) continue; // skip pure numbers

        const afterMatch = messageContent.substring(match.index + match[0].length, match.index + match[0].length + 300);
        const driveMatchHr = afterMatch.match(/~?(\d+(?:\.\d+)?)\s*-?\d*\.?\d*\s*hr?\s*(?:(\d+)\s*min\w*\s*)?drive/i);
        const driveMatchMinOnly = !driveMatchHr ? afterMatch.match(/~?(\d+)\s*min\w*\s*drive/i) : null;
        const flightMatch = afterMatch.match(/~?(\d+(?:\.\d+)?)\s*hr?\s*(?:flight|from)/i);
        const genericMatch = !driveMatchHr && !driveMatchMinOnly && !flightMatch ? afterMatch.match(/~?(\d+(?:\.\d+)?)\s*hr/) : null;

        const driveHours = driveMatchHr
          ? parseFloat(driveMatchHr[1]) + (driveMatchHr[2] ? parseInt(driveMatchHr[2]) / 60 : 0)
          : driveMatchMinOnly ? parseInt(driveMatchMinOnly[1]) / 60 : null;
        const flightHours = flightMatch ? parseFloat(flightMatch[1]) : (genericMatch ? parseFloat(genericMatch[1]) : null);

        const meta5 = extractDestMeta(afterMatch, messageContent, match.index + match[0].length);
        destinations.push({
          index: idx++,
          name: name,
          flightHours: flightHours,
          driveHours: driveHours,
          ...(meta5.price && { price: meta5.price }),
          ...(meta5.desc && { desc: meta5.desc })
        });
      }
    }

    debugLog('📍 Extracted destinations:', destinations.map(d => d.name));

    // Filter out template placeholders and system prompt leaks
    const cleanedDestinations = destinations.filter(dest => {
      const n = dest.name;
      // Remove placeholders like "[Destination Name]"
      if (/^\[.*\]$/.test(n)) return false;
      if (/^Destination\s*Name$/i.test(n)) return false;
      // Remove if description contains system prompt leaks
      if (dest.desc && /CRITICAL:|MANDATORY|Do NOT|MUST use|format.*EXACT/i.test(dest.desc)) return false;
      // Remove if description is a template placeholder
      if (dest.desc && /Brief compelling description|2-3 sentences max|\[Season\/months\]/i.test(dest.desc)) return false;
      return true;
    });

    // Filter out restricted/forbidden destinations
    const safeDestinations = cleanedDestinations.filter(dest => {
      const safetyCheck = checkDestinationRestriction(dest.name);
      if (!safetyCheck.allowed) {
        debugLog(`🚫 Filtering restricted destination: ${dest.name}`);
        return false;
      }
      return true;
    });

    return safeDestinations;
  };

  const detectDestinationSelection = (message) => {
    const lowerMessage = message.toLowerCase().trim();
    
    // CRITICAL: Exclude questions and "tell me more" patterns
    if (lowerMessage.includes('tell me more') || 
        lowerMessage.includes('more about') || 
        lowerMessage.includes('learn more') ||
        lowerMessage.includes('how about') ||
        lowerMessage.includes('what about') ||
        lowerMessage.endsWith('?')) {
      debugLog('❌ Not a destination selection - this is a question');
      return null;
    }
    
    // CRITICAL: Exclude generic trip requests (no specific destination)
    const genericTripPatterns = [
      /beach\s*(vacation|trip|getaway|paradise)/i,
      /romantic\s*(trip|getaway|vacation)/i,
      /adventure\s*(trip|vacation)/i,
      /food\s*(and|&)\s*culture/i,
      /relaxing\s*(vacation|trip|getaway)/i,
      /amazing\s*sunsets/i,
      /beautiful\s*scenery/i,
      /outdoor\s*activities/i,
      /hiking\s*(trip|adventure)/i,
      /incredible\s*food/i,
      /rich\s*culture/i,
      // Accommodation-focused requests (not destination-focused)
      /luxury\s*(spa|hotel|resort|retreat)/i,
      /\b(spa|hotel|resort)\s*(retreat|getaway|vacation|trip)?$/i,
      /5\s*star\s*(hotel|resort|spa)/i,
      /five\s*star\s*(hotel|resort|spa)/i,
      /boutique\s*(hotel|resort|spa)/i,
    ];
    
    if (genericTripPatterns.some(pattern => pattern.test(lowerMessage))) {
      debugLog('❌ Not a destination selection - this is a generic trip request');
      return null;
    }
    
    // Check for explicit destination confirmation patterns
    const confirmationPatterns = [
      /(?:i want to go to|let's go to|i choose|i pick|i'll take|going to|let's do|book)\s+(.+?)(?:\s+for|\s+with|\s+in|\s+this|\s+next|\s*[!.,]|$)/i,
      /(?:i want|i'd like|we want|we'd like)\s+(?:to visit|to explore|to see|to travel to)\s+(.+?)(?:\s+for|\s+with|\s+in|\s+this|\s+next|\s*[!.,]|$)/i,
      /^(?:yes|yeah|yep|sure|definitely|absolutely)[,!]?\s*(?:let's do\s+)?(.+?)(?:\s+please|\s*[!.]|$)/i,
      /(?:the\s+)?(.+?)\s+(?:sounds? great|sounds? good|sounds? perfect|is perfect|looks? great|looks? good|please)/i,
    ];
    
    // Skip if the message is just a simple confirmation without a destination
    // UNLESS there's exactly one destination in the options (user is confirming that one)
    const simpleConfirmations = ['let\'s go', 'lets go', 'go', 'yes', 'yeah', 'yep', 'sure', 'ok', 'okay', 'proceed', 'continue', 'start', 'begin', 'yes!', 'yes please', 'sure!', 'let\'s do it', 'sounds good', 'sounds great'];
    const isSimpleConfirmation = simpleConfirmations.includes(lowerMessage.replace(/[!.,]/g, '').trim());
    
    if (isSimpleConfirmation) {
      // Check if user is confirming a travel advisory warning
      const lastMsg = messages.filter(m => m.role === 'assistant').pop();
      if (lastMsg?._advisoryConfirmation && lastMsg?._advisoryDestination) {
        debugLog('✅ User confirmed travel advisory — proceeding with generation');
        return { advisoryConfirmed: true, destination: lastMsg._advisoryDestination };
      }

      // CHANGED: Do NOT auto-select destinations on simple confirmations
      // Instead, the chat should show a YES/NO confirmation prompt
      // Return a special flag indicating confirmation was detected but needs user explicit approval

      // Check if there's exactly one destination in options - user is confirming that one
      if (destinationOptions && destinationOptions.length === 1) {
        debugLog('⏸️ Simple confirmation with single destination - needs explicit YES/NO approval');
        // Return special object indicating need for confirmation
        return { needsConfirmation: true, destination: destinationOptions[0], index: 0 };
      }

      // Check if the last AI message mentioned a specific hotel/destination for itinerary creation
      const lastAssistantMessage = messages.filter(m => m.role === 'assistant').pop();
      if (lastAssistantMessage) {
        const content = lastAssistantMessage.content;
        // Look for pattern like "create a wellness itinerary for [Destination]" or "itinerary for [Destination]"
        // Capture everything up to ? ! . , or end of line
        const itineraryMatch = content.match(/(?:create|make|build)[^?]*?itinerary for\s+([A-Za-z][A-Za-z\s&'-]+?)(?:\?|!|\.|,|\s+It|\s+This|$)/i);
        if (itineraryMatch) {
          const mentionedDest = itineraryMatch[1].trim();
          debugLog('⏸️ Simple confirmation for mentioned destination - needs explicit YES/NO approval:', mentionedDest);

          // Check if it matches a destination in options
          if (destinationOptions && destinationOptions.length > 0) {
            for (let dest of destinationOptions) {
              if (dest.name.toLowerCase().includes(mentionedDest.toLowerCase()) ||
                  mentionedDest.toLowerCase().includes(dest.name.toLowerCase().split(',')[0])) {
                return { needsConfirmation: true, destination: dest, index: destinationOptions.indexOf(dest) };
              }
            }
          }

          // Create a dynamic destination from the mentioned name
          return {
            needsConfirmation: true,
            destination: {
              name: mentionedDest,
              country: null,
              flightHours: null,
              priceLevel: null,
              isDynamic: true
            },
            index: 0
          };
        }
      }

      debugLog('❌ Not a destination selection - simple confirmation without clear destination');
      return null;
    }
    
    for (const pattern of confirmationPatterns) {
      const match = lowerMessage.match(pattern);
      if (match) {
        let destinationName = match[1]?.trim();
        
        // Clean up common words - only at the start, and "to/the/a" only when followed by space
        destinationName = destinationName?.replace(/^(yes|yeah|yep|sure|definitely|absolutely)[,!]?\s*/i, '').trim();
        destinationName = destinationName?.replace(/^(to|the|a)\s+/i, '').trim();
        
        // Skip if it's too short or generic
        if (!destinationName || destinationName.length < 3) continue;
        if (['it', 'that', 'this', 'there', 'option', 'one', 'go', 'spa', 'hotel', 'resort', 'vacation', 'trip'].includes(destinationName)) continue;
        
        // Skip if it still looks like a phrase (too many words = probably not just a destination)
        const wordCount = destinationName.split(/\s+/).length;
        if (wordCount > 4) continue;
        
        // Check if this matches a known destination from options
        if (destinationOptions && destinationOptions.length > 0) {
          for (let dest of destinationOptions) {
            const destNameLower = dest.name.toLowerCase();
            const destCountry = dest.country?.toLowerCase() || '';
            
            const check1 = destNameLower.includes(destinationName);
            const check2 = destinationName.includes(destNameLower.split(',')[0]);
            // Only check country if it exists and is not empty
            const check3 = destCountry && destCountry.length > 0 && destCountry.includes(destinationName);
            const check4 = destCountry && destCountry.length > 0 && destinationName.includes(destCountry);
            
            if (check1 || check2 || check3 || check4) {
              debugLog('✅ Destination mentioned from options:', dest.name);
              // CHANGED: Don't auto-proceed - user must click "Create Itinerary"
              return null; // Let the chat continue - user adds via trip builder
            }
          }
        }
        
        // DYNAMIC: Accept any destination name - AI will validate/enrich during itinerary generation
        // Capitalize properly
        const formattedName = destinationName
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ');

        debugLog('✅ Dynamic destination detected:', formattedName);
        // CHANGED: Don't auto-proceed - add to trip builder instead
        // User must click "Create Itinerary" button to proceed
        return null; // Don't auto-select - let the chat continue normally
      }
    }
    
    if (!destinationOptions || destinationOptions.length === 0) {
      return null;
    }
    
    // DISABLED: All auto-destination selection is now disabled
    // User must click "Create Itinerary" button to proceed to personalization
    // The chat will continue to suggest destinations, but won't auto-navigate

    // Old code for number selection (1, 2, 3) - now disabled
    // Old code for explicit selection phrases - now disabled
    // Old code for destination name matching - now disabled

    // Let the chat continue normally - AI will respond to the user's message
    
    return null;
  };

  const generatePackingList = async (destination, itinerary) => {
    try {
      const prefs = userProfile.preferences;
      const duration = prefs.duration || itinerary?.days?.length || 7;

      // Extract activities from itinerary for activity-specific packing
      let itineraryActivities = [];
      if (itinerary?.days) {
        itinerary.days.forEach(day => {
          (day.activities || []).forEach(a => {
            if (a.name) itineraryActivities.push(a.name);
          });
        });
      }
      const activitySummary = itineraryActivities.length > 0
        ? `\nPlanned activities: ${[...new Set(itineraryActivities)].slice(0, 15).join(', ')}`
        : '';

      // Determine season/weather context
      let seasonContext = '';
      if (prefs.startDate) {
        const month = new Date(prefs.startDate).getMonth();
        const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
        seasonContext = `\nTravel month: ${monthNames[month]}. Consider typical weather for ${destination.name} in ${monthNames[month]}.`;
      } else if (prefs.travelMonths && prefs.travelMonths.length > 0) {
        const yearNote = prefs.travelYear && prefs.travelYear !== new Date().getFullYear() ? ` ${prefs.travelYear}` : '';
        seasonContext = prefs.travelMonths.length === 1
          ? `\nTravel month: ${prefs.travelMonths[0]}${yearNote}. Consider typical weather for ${destination.name} in ${prefs.travelMonths[0]}.`
          : `\nFlexible travel window: ${prefs.travelMonths[0]} to ${prefs.travelMonths[1]}${yearNote}. Consider typical weather for ${destination.name} during this period and recommend the better month if relevant.`;
      }

      // Companion context — read from canonical companions array (with legacy fallback)
      const _companionsArr = coerceCompanionsToArray(prefs.companions ?? prefs.travelCompanions);
      let companionContext = '';
      if (_companionsArr.includes('family') || _companionsArr.includes('kids')) {
        companionContext = '\nTraveling with children - include kid-friendly items.';
      } else if (_companionsArr.includes('baby')) {
        companionContext = '\nTraveling with a baby - include baby essentials.';
      } else if (_companionsArr.includes('elderly')) {
        companionContext = '\nTraveling with elderly - include accessibility-friendly items.';
      } else if (_companionsArr.includes('pets')) {
        companionContext = '\nTraveling with pets - include pet essentials.';
      }

      const systemPrompt = `Create a smart packing list for ${destination.name}, ${duration} days.${seasonContext}${activitySummary}${companionContext}
Budget level: ${prefs.budget || 'moderate'}

Return ONLY valid JSON with this exact structure. Each category has items with name and a weather/context tag:
{"categories":[{"name":"Essentials","icon":"passport","items":[{"item":"Passport + copies","tag":"must-have"}]},{"name":"Clothing","icon":"shirt","items":[{"item":"3 lightweight shirts","tag":"warm weather"}]},{"name":"Toiletries & Health","icon":"heart","items":[{"item":"Sunscreen SPF 50","tag":"sun protection"}]},{"name":"Electronics","icon":"zap","items":[{"item":"Universal adapter","tag":"essential"}]},{"name":"Activity Gear","icon":"activity","items":[{"item":"Swimsuit","tag":"beach"}]},{"name":"Comfort & Misc","icon":"star","items":[{"item":"Neck pillow","tag":"long flight"}]}],"tips":["Roll clothes to save space","Check visa requirements"]}

Keep items practical and specific to the destination and weather. Include quantity suggestions. Max 8 items per category. Include 3-4 destination-specific tips.`;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 2000,
          system: systemPrompt,
          messages: [{ role: "user", content: "Create the packing list. Return ONLY JSON, no markdown." }]
        })
      });

      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }
      let packingText = data.content[0].text;
      packingText = packingText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();

      const parsed = JSON.parse(packingText);
      // Track what dates the packing list was generated with
      setPackingListGeneratedWith({
        startDate: userProfile?.preferences?.startDate || null,
        endDate: userProfile?.preferences?.endDate || null
      });

      // Support both old flat format and new categorized format
      if (parsed.categories) {
        setPackingList(parsed);
      } else {
        // Convert old flat format to new format
        const categories = [];
        const iconMap = { essentials: 'passport', clothing: 'shirt', toiletries: 'heart', electronics: 'zap', activities: 'activity', optional: 'star' };
        const nameMap = { essentials: 'Essentials', clothing: 'Clothing', toiletries: 'Toiletries & Health', electronics: 'Electronics', activities: 'Activity Gear', optional: 'Comfort & Misc' };
        Object.entries(parsed).forEach(([key, items]) => {
          if (Array.isArray(items)) {
            categories.push({
              name: nameMap[key] || key,
              icon: iconMap[key] || 'star',
              items: items.map(i => ({ item: i, tag: '' }))
            });
          }
        });
        setPackingList({ categories, tips: [] });
      }
    } catch (error) {
      debugError('Packing list error:', error);
    }
  };

  // Search for real hotels using API
  const searchHotels = async (destination, budget, neighborhood = null) => {
    try {
      debugLog('🏨 Searching for hotels in', destination, 'budget:', budget, 'neighborhood:', neighborhood);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/hotels/search`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          destination,
          budget: budget || 'midrange',
          neighborhood
        })
      });
      
      if (!response.ok) {
        debugWarn('Hotel search failed, will use AI suggestions');
        return null;
      }
      
      const data = await response.json();
      debugLog('✅ Found hotels:', data.hotels?.length, 'via', data.source);
      return data.hotels;
    } catch (error) {
      debugError('Hotel search error:', error);
      return null;
    }
  };

  const generateDetailedItinerary = async (destination) => {
    // PRE-CHECK: Verify user has remaining itinerary quota before starting generation
    // Uses checkUsageLimit (not trackUsageOnBackend) to avoid incrementing before success
    if (user && isLoggedIn && checkUsageLimit) {
      const usageCheck = checkUsageLimit('itinerary');
      if (!usageCheck.allowed) {
        // User has hit their limit - show upgrade prompt
        const tierLimits = getTierLimits ? getTierLimits() : { itinerariesPerQuarter: 5 };
        const isExplorer = tier === 'explorer';

        showAlertModal(
          'Itinerary Limit Reached',
          isExplorer
            ? `You've used all ${tierLimits.itinerariesPerQuarter} free itineraries for this quarter.\n\nUpgrade to arrivo Planner for unlimited itineraries, group planning, booking integration, and permanent trip history!`
            : `You've reached your quarterly itinerary limit (${tierLimits.itinerariesPerQuarter} per quarter).\n\nYour quota will reset at the beginning of next quarter.`
        );
        return; // Stop generation
      }
    }

    // CRITICAL: Calculate duration from dates FIRST, before anything else
    let prefs = { ...userProfile.preferences };
    // Merge trip-level interests from destination highlights (e.g., "surfing, beach")
    if (destination?.highlights?.length > 0) {
      const tripInterests = destination.highlights
        .flatMap(h => h.split(/,\s*/))
        .filter(Boolean);
      if (tripInterests.length > 0 && !(tripInterests.length === 1 && tripInterests[0] === 'Local experiences')) {
        prefs.interests = [...new Set([...(prefs.interests || []), ...tripInterests])];
        debugLog(`🎯 Merged trip interests into prefs: ${prefs.interests.join(', ')}`);
      }
    }
    // Also merge tripContext activities/preferences and likedActivities into interests
    const contextActivitiesDetailed = [
      ...(tripContext?.activities || []),
      ...(tripContext?.preferences || []),
      ...(prefs.likedActivities || [])
    ].filter(Boolean);
    if (contextActivitiesDetailed.length > 0) {
      const existingInterests = prefs.interests || [];
      const mergedInterests = [...new Set([...existingInterests, ...contextActivitiesDetailed])];
      if (mergedInterests.length > existingInterests.length) {
        prefs.interests = mergedInterests;
        debugLog(`🎯 [Detailed] Merged context activities into interests: ${prefs.interests.join(', ')}`);
      }
    }
    // Pass requested places from chat extraction to prefs for itinerary generation
    if (tripContext?.requestedPlaces?.length > 0) {
      prefs.requestedPlaces = tripContext.requestedPlaces;
      debugLog(`📍 [Detailed] Requested places from chat: ${prefs.requestedPlaces.join(', ')}`);
    }
    // Build trip context block from chat for the LLM prompt
    const detailedTripContextLines = [];
    if (tripContext?.occasion) detailedTripContextLines.push(`Trip occasion: ${tripContext.occasion}`);
    if (tripContext?.travelers) detailedTripContextLines.push(`Traveling as: ${tripContext.travelers}`);
    if (tripContext?.month) detailedTripContextLines.push(`Travel month: ${tripContext.month}`);
    const firstUserMsg = messages?.find(m => m.role === 'user' && m.content !== 'loading');
    if (firstUserMsg?.content) {
      detailedTripContextLines.push(`Original request: "${firstUserMsg.content}"`);
    }
    prefs._tripContextBlock = detailedTripContextLines.length > 0
      ? `\n📝 TRIP CONTEXT (from user's chat — use this to understand the trip purpose and tailor activities accordingly):\n${detailedTripContextLines.map(l => `- ${l}`).join('\n')}\n`
      : '';
    if (prefs._tripContextBlock) {
      debugLog(`📝 [Detailed] Trip context block: ${detailedTripContextLines.join(', ')}`);
    }

    if (prefs.startDate && prefs.endDate) {
      const diffDays = calculateDaysBetween(prefs.startDate, prefs.endDate);
      if (diffDays && diffDays > 0) {
        prefs.duration = diffDays;
        debugLog(`📅 Calculated duration from dates: ${diffDays} days (${prefs.startDate} to ${prefs.endDate})`);
        // Also update the state for consistency
        setUserProfile(prev => ({
          ...prev,
          preferences: { ...prev.preferences, duration: diffDays }
        }));
      }
    }
    
    const tripDuration = prefs.duration || 7;
    
    // Calculate timeout before try block so it's available in catch
    // Safari/national park trips need more time due to complex logistics
    const destLower = (destination.name || '').toLowerCase();
    const isComplexTrip = destLower.includes('safari') || destLower.includes('kruger') || 
                          destLower.includes('serengeti') || destLower.includes('national park') ||
                          destLower.includes('game reserve') || tripDuration > 7;
    
    // 5 minutes for complex trips, 4 minutes for 7+ days, 3 minutes for shorter
    const timeoutMs = isComplexTrip ? 300000 : tripDuration >= 7 ? 240000 : 180000;
    
    setShowLoadingScreen(true);
    setLoadingMessage(`Creating your ${tripDuration}-day adventure...`);
    setSelectedDestination(destination);

    // Reset user modification flag for new itinerary generation
    itineraryUserModifiedRef.current = false;

    // Initialize trip plan for multi-city/resort trips
    // Use tripType from destination if available (from LLM extraction), otherwise detect from name
    const newTripPlan = initializeTripPlan(destination.name, tripDuration, {
      transportMode: transportationMode || 'fly',
      tripType: destination.tripType // Pass detected trip type if available
    });
    setTripPlan(newTripPlan);
    debugLog(`📋 Initialized trip plan: type=${newTripPlan.type}, stops=${newTripPlan.stops.length}`);
    
    // Engaging loading messages with destination facts
    // Clear any previous interval before starting a new one
    if (messageIntervalRef.current) {
      clearInterval(messageIntervalRef.current);
      messageIntervalRef.current = null;
    }
    const destinationFacts = {
      'kruger': [
        '🦁 Kruger is home to the Big Five: lion, leopard, rhino, elephant & buffalo',
        '🌍 The park is larger than Israel - nearly 2 million hectares!',
        '🐘 Over 12,000 elephants roam freely in Kruger',
        '🌅 Best game viewing is at dawn and dusk when animals are most active'
      ],
      'safari': [
        '🦁 The Big Five refers to lion, leopard, rhino, elephant & buffalo',
        '📸 Early morning game drives offer the best wildlife photography light',
        '🌙 Night drives reveal a whole different world of nocturnal animals',
        '🐆 Leopards are the most elusive of the Big Five to spot'
      ],
      'paris': [
        '🗼 The Eiffel Tower was only meant to stand for 20 years',
        '🥐 Parisians consume 30 million croissants per day!',
        '🎨 The Louvre would take 100 days to see every piece for 30 seconds',
        '🚇 Paris Metro stations are never more than 500m apart'
      ],
      'tokyo': [
        '🗾 Tokyo has more Michelin stars than any other city',
        '🚄 Shinkansen trains are cleaned in just 7 minutes!',
        '🌸 Over 1,000 cherry trees bloom in Ueno Park each spring',
        '🏯 The Imperial Palace grounds are larger than Central Park'
      ],
      'italy': [
        '🍝 Each region of Italy has its own unique pasta shapes',
        '☕ Italians drink espresso standing at the bar - it costs less!',
        '🏛️ Rome has more ancient obelisks than Egypt',
        '🍕 Naples is the birthplace of pizza - try the original!'
      ],
      'japan': [
        '🗾 Japan has over 6,800 islands but most tourists visit only 4',
        '🍣 Tsukiji was the world\'s largest fish market for 83 years',
        '⛩️ There are over 80,000 Shinto shrines in Japan',
        '🚄 The Shinkansen has never had a fatal accident in 60 years'
      ],
      'default': [
        '✨ Discovering the perfect activities for each day...',
        '🗺️ Finding hidden gems only locals know about...',
        '🍽️ Selecting restaurants that match your taste...',
        '🏨 Choosing hotels in the best neighborhoods...',
        '⏰ Optimizing your schedule to avoid crowds...',
        '📍 Planning efficient routes between attractions...'
      ]
    };
    
    // Get facts for this destination
    const getFactsForDestination = () => {
      const destLower = (destination.name || '').toLowerCase();
      for (const [key, facts] of Object.entries(destinationFacts)) {
        if (key !== 'default' && destLower.includes(key)) {
          return facts;
        }
      }
      return destinationFacts.default;
    };
    
    const facts = getFactsForDestination();
    let factIndex = 0;

    // Show engaging facts while generating (rotate every 6 seconds)
    messageIntervalRef.current = setInterval(() => {
      const currentFact = facts[factIndex % facts.length];
      setLoadingMessage(currentFact);
      factIndex++;
    }, 6000);
    
    try {
      // Hotel search API endpoint not yet implemented - using AI suggestions for now
      // TODO: Re-enable when /api/hotels/search endpoint is added to backend
      // setLoadingMessage(`Finding the best hotels in ${destination.name}...`);
      // const realHotels = await searchHotels(destination.name, prefs.hotelBudget);
      const realHotels = null; // Placeholder until hotel search API is implemented

      // Format hotel suggestions for the prompt
      const hotelSuggestions = realHotels && realHotels.length > 0
        ? `\n\nVERIFIED REAL HOTELS (USE THESE):\n${realHotels.map((h, i) =>
            `${i + 1}. ${h.name}\n   Address: ${h.address}\n   Price: $${h.pricePerNight || h.estimatedPrice || '150'}/night\n   ${h.amenities ? 'Amenities: ' + h.amenities.join(', ') : ''}`
          ).join('\n')}\n\nYou MUST use hotels from this list above. Do NOT make up other hotels.`
        : '\n\nNote: Use well-known hotel chains (Marriott, Hilton, Hyatt, Accor) that you are confident exist.';
      
      // Calculate drive hours - prefer Google Maps calculated time, then extracted driveHours, then estimates
      const getEstimatedDriveHours = () => {
        // First priority: Google Maps calculated drive time
        if (calculatedDriveTime?.hours) {
          debugLog(`🗺️ Using Google Maps drive time: ${calculatedDriveTime.hours} hours`);
          return calculatedDriveTime.hours;
        }
        // Second priority: driveHours extracted from AI response
        if (selectedDestination?.driveHours) {
          return selectedDestination.driveHours;
        }
        // For hotel-first trips to nearby locations, default to 2-3 hours
        if (tripType === 'hotel') {
          const name = selectedDestination?.name?.toLowerCase() || '';
          if (name.includes('hudson') || name.includes('catskill') || name.includes('berkshire') ||
              name.includes('hamptons') || name.includes('mirbeau') || name.includes('mohonk') ||
              name.includes('finger lakes') || name.includes('poconos') || name.includes('woodstock') ||
              name.includes('rhinebeck') || name.includes('saugerties')) {
            return 2; // ~2 hours for nearby NY/NJ/CT/PA locations
          }
        }
        // Otherwise, estimate from flight hours using the helper function
        return Math.round((selectedDestination?.flightHours || 3) * 1.5 + 1.5);
      };
      const estimatedDriveHours = getEstimatedDriveHours();

      // Check destination geography (island detection, altitude, permits, etc.) - don't block on failure
      let geographyInfo = null;
      try {
        setLoadingMessage(`Checking destination details for ${destination.name}...`);
        geographyInfo = await getDestinationGeography(destination.name);
        // Cache geography data for island detection during timeline rendering
        // BUG 11 FIX: Cache under BOTH the destination name AND the resolved city name
        // so lookups work whether using "Soneva Fushi" or "Maldives"
        if (geographyInfo) {
          const destLower = destination.name.toLowerCase().trim();
          const geoData = {
            isIsland: geographyInfo.isIsland,
            isArchipelago: geographyInfo.isArchipelago,
            noCarAccess: geographyInfo.noCarAccess,
            accessMethods: geographyInfo.accessMethods,
            requiresBoat: geographyInfo.requiresBoat,
          };
          dynamicGeographyCache[destLower] = geoData;
          // Also cache under the resolved location name (for resort trips)
          const resolvedName = extractLocationFromHotelName(destination.name);
          if (resolvedName !== destination.name) {
            const resolvedLower = resolvedName.toLowerCase().trim();
            dynamicGeographyCache[resolvedLower] = geoData;
            debugLog(`🗺️ Also cached geography under resolved name: ${resolvedName}`);
          }
          debugLog(`🗺️ Cached geography for ${destination.name}:`, dynamicGeographyCache[destLower]);
        }
        if (geographyInfo?.isIsland || geographyInfo?.isArchipelago) {
          debugLog(`🏝️ Island detected: ${destination.name} - access methods: ${geographyInfo.accessMethods?.join(', ')}`);
        }
        if (geographyInfo?.altitude?.requiresAcclimatization) {
          debugLog(`🏔️ High altitude: ${destination.name} - ${geographyInfo.altitude.meters}m, needs ${geographyInfo.altitude.acclimatizationDays} days acclimatization`);
        }
        if (geographyInfo?.permits?.required) {
          debugLog(`📋 Permits required: ${destination.name} - ${geographyInfo.permits.permitName}, book ${geographyInfo.permits.leadTimeDays} days ahead`);
        }
      } catch (geoError) {
        debugWarn('Geography check failed (continuing):', geoError);
      }

      // Build geography context for the prompt (shared function, single source of truth)
      const geographyContext = geographyInfo
        ? buildGeographyContext({ ...geographyInfo, success: true }, destination.name)
        : '';

      setLoadingMessage(`Creating your perfect ${tripDuration}-day itinerary for ${destination.name}...`);

      // Enrich prefs with arrival/departure timing (same as progressive path)
      const travelTiming = getTravelTimingPrefs();
      prefs.arrivalTime = travelTiming.arrivalTime;
      prefs.departureTime = travelTiming.departureTime;
      prefs.departureAirport = prefs.departureAirport || travelTiming.departureAirport;

      const systemPrompt = buildItinerarySystemPrompt({
        destination: {
          name: destination.name,
          stops: (destination.stops || []).map(s => ({
            name: typeof s === 'object' ? (s.resolvedLocation || s.name) : s,
            days: typeof s === 'object' ? s.days : undefined,
            resolvedLocation: typeof s === 'object' ? s.resolvedLocation : undefined,
            id: typeof s === 'object' ? s.id : undefined,
          })),
          stopNames: destination.stopNames || [destination.name],
          isMultiStop: (destination.stops?.length || 0) > 1 || (destination.stopNames?.length || 0) > 1,
          flightHours: selectedDestination?.flightHours || 0,
        },
        tripDuration,
        tripType,
        prefs: {
          ...prefs,
          budget: prefs.budget || 'moderate',
          preferredPace: prefs.preferredPace || prefs.tripPace || 'moderate',
          homeCity: prefs.homeCity,
          returnCity: prefs.returnCity,
          travelCompanions: companionsToPromptString(prefs.companions ?? prefs.travelCompanions, 'solo'),
          dietaryRestrictions: prefs.dietary || prefs.dietaryRestrictions,
          cuisinePreferences: prefs.cuisinePreferences || prefs.likedCuisines,
          dislikedCuisines: prefs.dislikedCuisines,
          hotelBudget: prefs.hotelBudget || 'midrange',
          preferredHotel: prefs.preferredHotel || '',
          stopHotels: stopHotels,
          preferredStartTime: prefs.preferredStartTime || '09:00',
          preferredEndTime: prefs.preferredEndTime || null,
          startDate: prefs.startDate,
          endDate: prefs.endDate,
          travelPeriod: prefs.travelMonth || prefs.travelPeriod,
          tripStyle: prefs.tripStyle,
          firstTimeVisitor: prefs.firstTimeVisitor,
          accessibilityNeeds: prefs.accessibilityNeeds,
          safetyConsiderations: prefs.safetyConsiderations,
          travelNotes: prefs.travelNotes,
          mustDoActivities: prefs.mustDoActivities,
          interests: prefs.interests,
          requestedPlaces: prefs.requestedPlaces,
          dreamDestinations: prefs.dreamDestinations,
          lovedPlaces: prefs.lovedPlaces,
          avoidPlaces: prefs.avoidPlaces,
          leastFavoriteDestinations: prefs.leastFavoriteDestinations,
          likedItems: prefs.likedActivities || prefs.likedItems,
          dislikedItems: prefs.dislikedActivities || prefs.dislikedItems,
          _tripContextBlock: prefs._tripContextBlock,
        },
        transportationMode,
        effectiveReturnMode,
        arrivalFlight: { time: prefs.arrivalTime },
        departureFlight: { time: prefs.departureTime },
        driveDetails: {
          departureTime: driveDetails.departureTime || '08:00',
          returnDepartureTime: driveDetails.returnDepartureTime || '14:00',
        },
        estimatedDriveHours,
        customTransportDesc,
        quickTransportResult,
        perLegTransport: legTransports,
        geographyContext,
        hotelSuggestions,
        selectedHotelProperty,
        selectedDestination: selectedDestination ? { flightHours: selectedDestination.flightHours } : null,
        itinerary: null,
        formatCurrency,
      });

      debugLog(`📋 System prompt built via buildItinerarySystemPrompt (${systemPrompt.length} chars)`);

      // Calculate tokens needed based on trip length (roughly 1500 tokens per day for detailed itineraries)
      const estimatedTokens = Math.min(64000, Math.max(16000, tripDuration * 1500));
      debugLog(`📊 Trip duration: ${tripDuration} days, requesting ${estimatedTokens} max tokens`);
      
      // Create abort controller for timeout
      const controller = new AbortController();
      // Clear any previous timeout before setting new one
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
      }
      generationTimeoutRef.current = setTimeout(() => controller.abort(), timeoutMs);
      debugLog(`⏱️ Request timeout set to ${timeoutMs/60000} minutes`);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        signal: controller.signal,
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: estimatedTokens,
          system: systemPrompt,
          messages: [{ role: "user", content: "Create the itinerary. Return ONLY the JSON object, nothing else." }]
        })
      });

      // Clear timeout as soon as response is received
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
        generationTimeoutRef.current = null;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`API error (${response.status}): ${errorData.message || 'Failed to generate itinerary'}`);
      }

      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }
      let itineraryText = data.content[0].text;

      debugLog('📄 Raw itinerary response (first 200 chars):', itineraryText.substring(0, 200));
      
      // Aggressive cleaning
      itineraryText = itineraryText
        .replace(/```json\n?/g, '')
        .replace(/```\n?/g, '')
        .replace(/^[^{]*/, '') // Remove anything before first brace
        .replace(/[^}]*$/, '') // Remove anything after last brace
        .trim();
      
      // Check if response looks like markdown instead of JSON
      if ((itineraryText.includes('##') || itineraryText.includes('**Day')) && !itineraryText.startsWith('{')) {
        debugError('❌ Received markdown instead of JSON!');
        throw new Error('AI returned markdown text. Retrying with stricter instructions...');
      }
      
      // Extract the largest JSON object
      const jsonMatches = itineraryText.match(/\{[\s\S]*\}/g);
      if (jsonMatches && jsonMatches.length > 0) {
        // Take the longest match (most complete JSON)
        itineraryText = jsonMatches.reduce((a, b) => a.length > b.length ? a : b);
      }
      
      // Final validation - must start and end with braces
      if (!itineraryText.startsWith('{') || !itineraryText.endsWith('}')) {
        throw new Error('Invalid JSON format - missing braces');
      }
      
      debugLog('🔍 Attempting to parse JSON (length:', itineraryText.length, 'chars)...');
      
      let itineraryData;
      try {
        itineraryData = JSON.parse(itineraryText);
      } catch (parseError) {
        debugError('❌ JSON parse error:', parseError.message);
        throw new Error(`Invalid JSON: ${parseError.message}. Please try again.`);
      }
      
      debugLog('✅ Successfully parsed itinerary JSON!');
      
      // Comprehensive validation with auto-repair
      const validation = validateItineraryStructure(itineraryData);
      
      if (!validation.valid) {
        debugError('❌ Validation errors:', validation.errors);
        throw new Error(`Invalid itinerary: ${validation.errors.join(', ')}`);
      }
      
      if (validation.warnings.length > 0) {
        debugWarn('⚠️ Validation warnings:', validation.warnings);
      }
      
      if (validation.repairs.length > 0) {
        debugLog('🔧 Auto-repairs made:', validation.repairs);
      }
      
      // Use the validated/repaired data
      itineraryData = validation.data;
      
      // Validate and auto-fix timing issues
      const { fixedItinerary, fixLog } = validateAndFixItinerary(itineraryData);
      if (fixLog.length > 0) {
        debugLog(`✅ Auto-fixed ${fixLog.length} timing overlaps`);
      }
      
      // Check for geographic issues (activities in wrong cities)
      let hasGeographicIssues = false;
      fixedItinerary.days.forEach((day, dayIdx) => {
        const baseCity = (day.baseCity || '').toLowerCase();
        if (!baseCity) return;

        const checkLocation = (location, itemType, itemName) => {
          if (!location) return;
          const locLower = location.toLowerCase();

          // Check if location mentions a DIFFERENT city than baseCity
          // Common issue: activities in nearby cities (e.g., Aveiro when baseCity is Porto)
          const nearbyDifferentCities = {
            'porto': ['aveiro', 'braga', 'guimarães', 'guimaraes', 'coimbra', 'viana'],
            'lisbon': ['sintra', 'cascais', 'setúbal', 'setubal', 'évora', 'evora'],
            'barcelona': ['girona', 'tarragona', 'sitges', 'montserrat'],
            'madrid': ['toledo', 'segovia', 'ávila', 'avila'],
            'paris': ['versailles', 'chartres', 'reims', 'fontainebleau'],
            'rome': ['tivoli', 'ostia', 'orvieto', 'frascati'],
            'tokyo': ['yokohama', 'kamakura', 'nikko', 'hakone'],
          };

          // Check if location contains a different city
          const nearbyForBase = nearbyDifferentCities[baseCity] || [];
          for (const nearbyCity of nearbyForBase) {
            if (locLower.includes(nearbyCity) && !locLower.includes(baseCity)) {
              debugWarn(`⚠️ Day ${dayIdx + 1}: ${itemType} "${itemName}" is in ${nearbyCity} but baseCity is ${day.baseCity}`);
              hasGeographicIssues = true;
            }
          }

          // Also check for generic locations (just city name, no specific address)
          if (locLower === baseCity || locLower === `${baseCity}, portugal` || locLower === `${baseCity}, spain` ||
              locLower === `${baseCity}, italy` || locLower === `${baseCity}, france`) {
            debugWarn(`⚠️ Day ${dayIdx + 1}: ${itemType} "${itemName}" has generic location "${location}" - needs specific address`);
          }
        };

        // Check all activities and meals
        day.schedule?.forEach(act => {
          checkLocation(act.location, 'Activity', act.activity);
          checkLocation(act.address, 'Activity address', act.activity);
        });

        day.meals?.forEach(meal => {
          checkLocation(meal.address, 'Meal', meal.restaurant);
        });
      });

      if (hasGeographicIssues) {
        debugWarn('⚠️ This itinerary may have geographic issues. Some activities are in different cities than the day\'s baseCity.');
      }

      // Ensure airports are set from user preferences if LLM didn't return them (fly mode only)
      if (transportationMode === 'fly') {
        const userArrivalCode = userProfile.preferences?.arrivalAirport;
        const userDepartureCode = userProfile.preferences?.departureAirport;
        const firstCity = fixedItinerary.days?.[0]?.baseCity || fixedItinerary.destination;
        const lastCity = fixedItinerary.days?.[fixedItinerary.days.length - 1]?.baseCity || fixedItinerary.destination;

        debugLog(`✈️ Airport fallback check: LLM arrivalAirport=${fixedItinerary.arrivalAirport?.code || 'NOT SET'}, userPref=${userArrivalCode || 'NOT SET'}, destinationAirports=${destinationAirports?.length || 0}`);

        // Set arrivalAirport from user preferences if not in LLM response
        if (!fixedItinerary.arrivalAirport && userArrivalCode) {
          fixedItinerary.arrivalAirport = {
            code: userArrivalCode,
            name: `${userArrivalCode} Airport`,
            city: firstCity
          };
          debugLog(`✈️ Set arrivalAirport from user preferences: ${userArrivalCode}`);
        }

        // Fallback: use first destination airport if neither LLM nor user preferences have an airport
        if (!fixedItinerary.arrivalAirport && destinationAirports?.length > 0 && destinationAirports[0].code !== '???') {
          const firstAirport = destinationAirports[0];
          fixedItinerary.arrivalAirport = {
            code: firstAirport.code,
            name: firstAirport.name || `${firstAirport.code} Airport`,
            city: firstCity
          };
          debugLog(`✈️ Set arrivalAirport from destination airports: ${firstAirport.code}`);
        }

        // Final fallback: if still no airport, try to fetch from backend or use placeholder
        if (!fixedItinerary.arrivalAirport) {
          // Try to determine airport from first city
          debugLog(`⚠️ No airport found, attempting to find airport for: ${firstCity}`);
          // Use a placeholder that will be updated when airports are fetched
          // B9B: Resolve IATA code for placeholder
          const firstCityIata = lookupCityAirportCode(firstCity);
          fixedItinerary.arrivalAirport = {
            code: firstCityIata || '???',
            name: firstCityIata ? `${firstCityIata} International Airport` : `${firstCity} Airport`,
            city: firstCity,
            needsUpdate: !firstCityIata
          };
          debugWarn(`⚠️ arrivalAirport set to placeholder - will need to be updated`);
        }

        // Set departureAirport from user preferences if not in LLM response
        if (!fixedItinerary.departureAirport) {
          const departCode = userDepartureCode || userArrivalCode || fixedItinerary.arrivalAirport?.code;
          if (departCode && departCode !== '???') {
            fixedItinerary.departureAirport = {
              code: departCode,
              name: `${departCode} Airport`,
              city: lastCity
            };
            debugLog(`✈️ Set departureAirport from user preferences: ${departCode}`);
          } else {
            // B9B: Use IATA-resolved placeholder for departure airport too
            const lastCityIata = lookupCityAirportCode(lastCity);
            fixedItinerary.departureAirport = {
              code: lastCityIata || fixedItinerary.arrivalAirport?.code || '???',
              name: lastCityIata ? `${lastCityIata} International Airport` : `${lastCity} Airport`,
              city: lastCity,
              needsUpdate: !lastCityIata && (fixedItinerary.arrivalAirport?.needsUpdate || false)
            };
          }
        }

        // GUARDRAIL: LLM often confuses "departure airport" (where user flies FROM, near destination)
        // with "home airport" (where user flies TO). This is critical for single-batch generation
        // where prev itinerary may not have airports set (unlike progressive path).
        // Always enforce user-selected airports when LLM returned a different code.
        const homeCodes = new Set();
        const rawHomeAirports = profile?.home_airports;
        const homeAirportsList = Array.isArray(rawHomeAirports) ? rawHomeAirports
          : typeof rawHomeAirports === 'string' ? (() => { try { return JSON.parse(rawHomeAirports); } catch { return []; } })()
          : [];
        if (homeAirportsList[0]?.code) homeCodes.add(homeAirportsList[0].code.toUpperCase());
        const homeCityCode = lookupCityAirportCode(userProfile?.preferences?.homeCity);
        if (homeCityCode) homeCodes.add(homeCityCode.toUpperCase());

        // Override arrival airport if LLM set it to home airport (should be near destination)
        if (userArrivalCode && fixedItinerary.arrivalAirport?.code &&
            fixedItinerary.arrivalAirport.code.toUpperCase() !== userArrivalCode.toUpperCase() &&
            homeCodes.has(fixedItinerary.arrivalAirport.code.toUpperCase())) {
          debugLog(`✈️ Guardrail: LLM set arrival to home airport (${fixedItinerary.arrivalAirport.code}), overriding with user preference: ${userArrivalCode}`);
          fixedItinerary.arrivalAirport = {
            code: userArrivalCode,
            name: `${userArrivalCode} Airport`,
            city: firstCity
          };
        }

        // Override departure airport if LLM set it to home airport (should be near last stop)
        if (userDepartureCode && fixedItinerary.departureAirport?.code &&
            fixedItinerary.departureAirport.code.toUpperCase() !== userDepartureCode.toUpperCase() &&
            homeCodes.has(fixedItinerary.departureAirport.code.toUpperCase())) {
          debugLog(`✈️ Guardrail: LLM set departure to home airport (${fixedItinerary.departureAirport.code}), overriding with user preference: ${userDepartureCode}`);
          fixedItinerary.departureAirport = {
            code: userDepartureCode,
            name: `${userDepartureCode} Airport`,
            city: lastCity
          };
        }
      }

      // CRITICAL: Prefer the transport-info airport (KNOWN_AIRPORTS-corrected) over LLM-hallucinated names.
      // The initial itinerary (set before LLM generation) already has the correct airport from
      // the backend's /api/destination-transport-info endpoint which applies KNOWN_AIRPORTS + IATA_CODE_NAMES layers.
      // The LLM often hallucinate wrong airport names (e.g., "Cristóvão Colombo" for Porto instead of "Francisco Sá Carneiro").
      setItinerary(prev => {
        const merged = { ...fixedItinerary };
        // If the previous itinerary (from transport-info) had a valid airport with a real code,
        // use its name over whatever the LLM hallucinated
        if (prev?.arrivalAirport?.code && prev.arrivalAirport.code !== '???') {
          if (merged.arrivalAirport?.code === prev.arrivalAirport.code) {
            // Same airport code but LLM may have wrong name — use transport-info's correct name
            merged.arrivalAirport = { ...merged.arrivalAirport, name: prev.arrivalAirport.name, city: prev.arrivalAirport.city };
            debugLog(`✈️ Preserved transport-info arrival airport name: ${prev.arrivalAirport.name} (LLM had: ${fixedItinerary.arrivalAirport?.name})`);
          } else if (!merged.arrivalAirport || merged.arrivalAirport.code === '???') {
            // LLM didn't provide an airport — use transport-info's
            merged.arrivalAirport = prev.arrivalAirport;
            debugLog(`✈️ Using transport-info arrival airport: ${prev.arrivalAirport.code}`);
          } else {
            // LLM returned a DIFFERENT airport code — prefer transport-info's (user-selected/auto-detected)
            // Transport-info airports come from /api/destination-transport-info with KNOWN_AIRPORTS + IATA validation
            debugLog(`✈️ Overriding LLM arrival airport ${merged.arrivalAirport.code} with transport-info: ${prev.arrivalAirport.code} (LLM may have confused destination airport with home airport)`);
            merged.arrivalAirport = prev.arrivalAirport;
          }
        }
        if (prev?.departureAirport?.code && prev.departureAirport.code !== '???') {
          if (merged.departureAirport?.code === prev.departureAirport.code) {
            merged.departureAirport = { ...merged.departureAirport, name: prev.departureAirport.name, city: prev.departureAirport.city };
            debugLog(`✈️ Preserved transport-info departure airport name: ${prev.departureAirport.name}`);
          } else if (!merged.departureAirport || merged.departureAirport.code === '???') {
            merged.departureAirport = prev.departureAirport;
            debugLog(`✈️ Using transport-info departure airport: ${prev.departureAirport.code}`);
          } else {
            // LLM returned a DIFFERENT airport code — prefer transport-info's (user-selected/auto-detected)
            // e.g., LLM returns JFK (home airport) but user is departing FROM OPO (destination airport)
            debugLog(`✈️ Overriding LLM departure airport ${merged.departureAirport.code} with transport-info: ${prev.departureAirport.code} (LLM likely confused departure-from-destination with home airport)`);
            merged.departureAirport = prev.departureAirport;
          }
        }
        // Also preserve transportInfo and transportationMode from initial setup
        if (prev?.transportInfo && !merged.transportInfo) {
          merged.transportInfo = prev.transportInfo;
        }
        if (prev?.transportationMode && !merged.transportationMode) {
          merged.transportationMode = prev.transportationMode;
        }
        // Sanitize airport .city fields — LLM sometimes returns objects instead of strings
        if (merged.arrivalAirport?.city && typeof merged.arrivalAirport.city !== 'string') {
          debugWarn(`⚠️ arrivalAirport.city was ${typeof merged.arrivalAirport.city}, coercing to string`);
          merged.arrivalAirport.city = merged.arrivalAirport.city.name || merged.arrivalAirport.city.city || JSON.stringify(merged.arrivalAirport.city);
        }
        if (merged.departureAirport?.city && typeof merged.departureAirport.city !== 'string') {
          debugWarn(`⚠️ departureAirport.city was ${typeof merged.departureAirport.city}, coercing to string`);
          merged.departureAirport.city = merged.departureAirport.city.name || merged.departureAirport.city.city || JSON.stringify(merged.departureAirport.city);
        }
        return merged;
      });

      // NEW: Enrich itinerary with Google Places data (in background)
      const enrichItineraryInBackground = async () => {
        try {
          debugLog('🔍 Enriching itinerary with Google Places API...');
          setLoadingMessage('Validating locations...');
          
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/enrich-itinerary`, {
            method: 'POST',
            headers: await getApiHeaders(),
            body: JSON.stringify({ itinerary: fixedItinerary })
          });
          
          const data = await response.json();
          
          if (data.success) {
            debugLog(`✅ Enriched itinerary: ${data.stats.resolvedLocations}/${data.stats.totalLocations} locations validated`);
            
            let enrichedItinerary = data.itinerary;

            // Closure detection + replacement handled by backend pipeline:
            // - replaceClosedVenues() handles CLOSED_PERMANENTLY via Places-verified replacements
            // - validateOpeningHours() flags day-of-week closures using Places data

            // Update itinerary with enriched and fixed data
            // BUT only if user hasn't modified the itinerary (e.g., deleted an activity)
            if (!itineraryUserModifiedRef.current) {
              setItinerary(enrichedItinerary);
            } else {
              debugLog('⚠️ Skipping enrichment update - user has modified itinerary');
            }

            // Log any locations that couldn't be resolved
            const unresolvedCount = data.stats.totalLocations - data.stats.resolvedLocations;
            if (unresolvedCount > 0) {
              debugWarn(`⚠️ ${unresolvedCount} locations could not be validated`);
            }
          } else {
            debugWarn('⚠️ Itinerary enrichment failed:', data.error);
          }
        } catch (error) {
          debugError('Error enriching itinerary:', error);
          // Continue without enrichment - itinerary still works
        }
      };
      
      // Run enrichment in background (don't block UI)
      enrichItineraryInBackground();
      
      // Fetch real hotel name if not already set
      if (!generatedHotelName) {
        try {
          const hotelName = await getRealHotelName(itineraryData.destination, selectedHotel || 'City Center');
          setGeneratedHotelName(hotelName);
        } catch (error) {
          debugError('Error fetching hotel name:', error);
          setGeneratedHotelName('City Center Hotel');
        }
      }
      
      await generatePackingList(destination, itineraryData);
      
      // Clear loading message interval
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }
      
      setShowLoadingScreen(false);
      setStage('itinerary');

      // Track usage and auto-save itinerary for logged-in users
      if (user && isLoggedIn) {
        // Generate a temporary ID for tracking modifications while save is in progress
        // This ensures modifications can be tracked even if save is slow
        const tempItineraryId = `temp_${crypto.randomUUID()}`;
        setCurrentItineraryId(tempItineraryId);
        debugLog('📝 Set temporary itinerary ID for modification tracking:', tempItineraryId);

        // Auto-save itinerary to database
        // Planner/Admin: permanent save. Explorer: temporary save (expires in 24 hours).
        try {
          const startDate = userProfile.preferences?.startDate || null;
          const endDate = userProfile.preferences?.endDate || null;
          const destName = destination.stops?.length > 1
            ? destination.stops.map(s => s.name).join(' & ')
            : destination.name;
          const title = `${destName} - ${tripDuration} days`;

          // Explorer tier: auto-save with 24-hour expiry
          const isExplorerTier = !hasFeature('canSaveItineraries');
          const saveOptions = isExplorerTier
            ? { expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() }
            : {};

          const saveResult = await saveItinerary(user.id, fixedItinerary, title, destName, startDate, endDate, saveOptions);

          if (saveResult?.id) {
            setCurrentItineraryId(saveResult.id);
            setCurrentTripId(saveResult.id);
            setLastSavedAt(new Date());
            debugLog(`✅ Itinerary saved with ID: ${saveResult.id}${isExplorerTier ? ' (expires in 24h)' : ''}`);
          } else {
            debugLog('⚠️ Itinerary save returned no ID - may not have persisted');
            showToast('Itinerary generated but auto-save may have failed. Use the Save button to ensure it\'s saved.', 'warning', 5000);
          }
        } catch (saveError) {
          debugError('Error saving itinerary:', saveError);
          debugLog('⚠️ Keeping temp ID for local modification tracking');
          showToast('Your itinerary was generated but could not be auto-saved. Please save it manually.', 'error', 6000);
        }
      }

      // NEW: Run itinerary validations in background
      const runValidations = async () => {
        try {
          const { issues, safetyInfo } = await validateItinerary(
            fixedItinerary,
            userProfile.preferences,
            destination
          );
          // Merge with existing issues, deduplicating travel_advisory by message
          setValidationIssues(prev => {
            const existingAdvisoryMessages = new Set(prev.filter(i => i.type === 'travel_advisory').map(i => i.message));
            const newNonDupIssues = issues.filter(i =>
              i.type !== 'travel_advisory' || !existingAdvisoryMessages.has(i.message)
            );
            // Keep existing travel advisories, add everything else from validation
            const existingAdvisories = prev.filter(i => i.type === 'travel_advisory');
            const nonAdvisoryNew = newNonDupIssues.filter(i => i.type !== 'travel_advisory');
            const newAdvisories = newNonDupIssues.filter(i => i.type === 'travel_advisory');
            return [...existingAdvisories, ...newAdvisories, ...nonAdvisoryNew];
          });
          setDestinationSafetyInfo(safetyInfo);

          // Check for issues that need regeneration (e.g., activities during transit)
          const issuesNeedingRegeneration = issues.filter(i => i.needsRegeneration);
          if (issuesNeedingRegeneration.length > 0) {
            debugLog(`⚠️ ${issuesNeedingRegeneration.length} issue(s) need regeneration:`,
              issuesNeedingRegeneration.map(i => i.message));

            // Group by day for targeted regeneration
            const daysNeedingRegeneration = [...new Set(issuesNeedingRegeneration.map(i => i.day).filter(Boolean))];

            if (daysNeedingRegeneration.length > 0 && daysNeedingRegeneration.length <= 2) {
              // Only auto-regenerate if 1-2 days affected (avoid full regeneration)
              debugLog(`🔄 Auto-regenerating days: ${daysNeedingRegeneration.join(', ')}`);
              for (const dayNum of daysNeedingRegeneration) {
                const issueDescriptions = issuesNeedingRegeneration
                  .filter(i => i.day === dayNum)
                  .map(i => i.message)
                  .join('; ');
                await regenerateSingleDay(dayNum, `Fix scheduling issues: ${issueDescriptions}`);
              }
            }
          }

          // Show panel automatically if there are warnings, errors, or visa/health info
          const hasImportantIssues = issues.some(i =>
            i.severity === 'error' || i.severity === 'warning' ||
            i.type === 'visa' || i.type === 'health' || i.type === 'travel_advisory'
          );
          if (hasImportantIssues) {
            setShowValidationPanel(true);
          }
        } catch (error) {
          debugError('Validation error:', error);
        }
      };
      runValidations();
      
      // NEW: Initialize itinerary chat with a welcome message
      setItineraryChatMessages([{
        role: 'assistant',
        content: `Your ${destination.name} itinerary is ready! 🎉\n\nI can help you modify anything - just let me know what you'd like to change!`
      }]);
      
    } catch (error) {
      debugError('❌ Itinerary generation error:', error);

      // Clear loading message interval
      if (messageIntervalRef.current) {
        clearInterval(messageIntervalRef.current);
        messageIntervalRef.current = null;
      }

      // Clear generation timeout
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
        generationTimeoutRef.current = null;
      }

      setShowLoadingScreen(false);
      
      // Detect error type and set appropriate message
      let errorTitle = 'Couldn\'t Generate Itinerary';
      let errorText = error.message;
      let canRetry = true;
      
      if (error.name === 'AbortError') {
        errorTitle = 'Request Timed Out';
        errorText = `The request took too long (${Math.round(timeoutMs/60000)} minutes). This can happen with longer trips or slow connections.`;
      } else if (error.message.includes('API error')) {
        errorTitle = 'Server Error';
        errorText = 'Our servers are experiencing issues. Please try again in a moment.';
      } else if (error.message.includes('Invalid JSON')) {
        errorTitle = 'Generation Error';
        errorText = 'arrivo had trouble formatting the itinerary. This usually works on retry.';
      } else if (!navigator.onLine) {
        errorTitle = 'You\'re Offline';
        errorText = 'Please check your internet connection and try again.';
        canRetry = false;
      }
      
      // Get duration for error message
      const duration = userProfile.preferences.duration || 7;
      
      // Show error modal instead of alert
      setErrorInfo({
        title: errorTitle,
        message: errorText,
        canRetry: canRetry,
        suggestions: [
          'Try generating again (it often works on retry)',
          duration > 7 ? 'Try a shorter trip (7 days or less)' : null,
          'Check your internet connection',
          'Simplify your destination (single city instead of multi-city)'
        ].filter(Boolean),
        retryAction: () => {
          setShowErrorModal(false);
          generateItinerarySmart(destination);
        }
      });
      setShowErrorModal(true);
      
      // Show error in chat too
      const errorMessage = {
        role: 'assistant',
        content: `I had trouble creating your ${duration}-day itinerary. ${errorText}\n\nPlease try again - sometimes it just needs another attempt!`
      };
      setMessages([...messages, errorMessage]);
      
      // Reset states so user can try again
      setAwaitingPersonalization(false);
      setPersonalizationQuestionsAsked(false);
      setIsProcessing(false);
      
      // Re-throw so the button handler can also catch it
      throw error;
    }
  };

  // FIXED: handleDestinationSelect now properly tracks personalization phase
  const handleDestinationSelect = async (destination, index) => {
    debugLog('🎯 Destination selected:', destination);

    // Safety check: Verify destination isn't restricted
    const destName = destination.name || destination;
    const destSafetyCheck = checkDestinationRestriction(destName);

    if (!destSafetyCheck.allowed) {
      // Destination is forbidden - don't allow selection
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `I'm sorry, but I cannot help plan a trip to ${destName}.\n\n${destSafetyCheck.message}\n\nWould you like me to suggest some alternative destinations?`
      }]);
      return; // Don't proceed with selection
    }

    // Check citizenship restrictions if passport country is known
    const passportCountryRaw2 = userProfile.preferences?.passportCountry || profile?.passport_country;
    const passportCountries2 = parsePassportCountries(passportCountryRaw2);
    for (const country of passportCountries2) {
      const citizenCheck = checkCitizenshipRestriction(country, destName);
      if (!citizenCheck.allowed) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `⚠️ **Travel Restriction Warning**\n\n${citizenCheck.message}\n\nWould you like me to suggest alternative destinations?`
        }]);
        return; // Don't proceed with selection
      }
    }

    // Reset generation state — previous generation may still be in-flight
    // This ensures "Create my itinerary" works after re-entering personalization
    generationInProgressRef.current = false;
    setIsProgressiveGenerating(false);
    setCurrentlyGeneratingDay(null);

    // Clear stale airport preferences from any previously selected destination
    // so the new destination's airports can be auto-selected fresh
    setUserProfile(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        arrivalAirport: '',
        departureAirport: '',
        letAIChooseAirports: false
      }
    }));
    setDestinationAirports([]);

    // For hotel-first trips, store the selected hotel property
    if (tripType === 'hotel') {
      debugLog('🏨 Hotel-first trip - storing selected hotel:', destination);
      setSelectedHotelProperty({
        name: destination.name,
        location: destination.country || destination.location || null
      });
    }

    // Set the selected destination (normalize capitalization)
    if (destination.name && typeof destination.name === 'string') {
      destination.name = destination.name.replace(/\b\w/g, c => c.toUpperCase());
    }
    if (destination.stops) {
      for (const stop of destination.stops) {
        if (stop.name && typeof stop.name === 'string') {
          stop.name = stop.name.replace(/\b\w/g, c => c.toUpperCase());
        }
      }
    }
    setSelectedDestination(destination);

    // Get safety report for warnings (but allow travel)
    const safetyReport = getDestinationSafetyReport(destName, passportCountryRaw2);
    if (safetyReport.warnings.length > 0) {
      // Store warnings to show during personalization
      destination.safetyWarnings = safetyReport.warnings;
    }

    // Initialize trip plan (duration will be updated during personalization)
    const defaultDuration = userProfile.preferences.duration || 7;
    const newTripPlan = initializeTripPlan(destination.name, defaultDuration, {
      transportMode: transportationMode || 'fly'
    });
    setTripPlan(newTripPlan);
    debugLog(`📋 Initialized trip plan: type=${newTripPlan.type}, stops=${newTripPlan.stops.length}`);

    // Go to personalization stage (instead of staying in chat)
    setStage('personalization');
  };

  const updateUserProfile = (message, conversationHistory) => {
    const lowerMessage = message.toLowerCase();
    
    // Check for "no dietary restrictions" or "none"
    if (lowerMessage.includes('no dietary') || lowerMessage.includes('no restrictions') || 
        (lowerMessage.includes('dietary') && lowerMessage.includes('none')) ||
        (lowerMessage.match(/^none\.?$/))) {
      setUserProfile(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          dietaryRestrictions: ['none']
        }
      }));
    } else if (lowerMessage.includes('vegetarian') || lowerMessage.includes('vegan') || 
        lowerMessage.includes('gluten') || lowerMessage.includes('dairy')) {
      const dietary = [];
      if (lowerMessage.includes('vegetarian')) dietary.push('vegetarian');
      if (lowerMessage.includes('vegan')) dietary.push('vegan');
      if (lowerMessage.includes('gluten')) dietary.push('gluten-free');
      if (lowerMessage.includes('dairy')) dietary.push('dairy-free');
      
      setUserProfile(prev => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          dietaryRestrictions: [...new Set([...prev.preferences.dietaryRestrictions, ...dietary])]
        }
      }));
    }
    
    const cuisines = ['italian', 'french', 'japanese', 'chinese', 'thai', 'indian', 'mexican', 'local', 'seafood', 'spanish', 'tapas'];
    cuisines.forEach(cuisine => {
      if (lowerMessage.includes(cuisine)) {
        setUserProfile(prev => ({
          ...prev,
          preferences: {
            ...prev.preferences,
            cuisinePreferences: [...new Set([...prev.preferences.cuisinePreferences, cuisine])]
          }
        }));
      }
    });
    
    if (lowerMessage.includes('relaxed') || lowerMessage.includes('easy')) {
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, activityLevel: 'low', preferredPace: 'relaxed' }
      }));
    } else if (lowerMessage.includes('active') || lowerMessage.includes('high energy')) {
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, activityLevel: 'high', preferredPace: 'packed' }
      }));
    } else if (lowerMessage.includes('moderate')) {
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, activityLevel: 'moderate', preferredPace: 'moderate' }
      }));
    }
    
    const budgetMatch = message.match(/\$(\d{1,2},?\d{3})/);
    if (budgetMatch) {
      const budget = parseInt(budgetMatch[1].replace(',', ''));
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, budget }
      }));
    }

    // Detect hotel budget tier from keywords in chat
    if (lowerMessage.match(/\b(budget|cheap|backpack|hostel|affordable|economy|inexpensive)\b/)) {
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, hotelBudget: 'budget' }
      }));
    } else if (lowerMessage.match(/\b(luxury|luxurious|upscale|premium|5-star|five star|fancy|splurge|high-end|boutique)\b/)) {
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, hotelBudget: 'luxury' }
      }));
    } else if (lowerMessage.match(/\b(midrange|mid-range|moderate|reasonable|comfortable)\b/)) {
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, hotelBudget: 'midrange' }
      }));
    }

    // Match duration patterns: "10 days", "10-day", "10day", "2 weeks", "5 nights"
    const durationMatch = message.match(/(\d+)[\s-]*(days?|weeks?|nights?)/i);
    if (durationMatch) {
      const unit = durationMatch[2].toLowerCase();
      let duration = parseInt(durationMatch[1]);
      if (unit.startsWith('week')) {
        duration = duration * 7;
      } else if (unit.startsWith('night')) {
        // 5 nights = 6 days (arrive day 1, depart day 6)
        duration = duration + 1;
      }
      // Cap at 14 days
      duration = Math.min(duration, 14);
      debugLog(`📅 Detected trip duration: ${duration} days from "${durationMatch[0]}"`);
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, duration }
      }));
    }
    
    // Detect travel companions / group type
    const companionPatterns = [
      { pattern: /\b(?:my (?:wife|husband|partner|spouse|fianc[eé]+) and I|(?:me and|I and) my (?:wife|husband|partner|spouse|fianc[eé]+)|(?:us two|just (?:the )?two of us|couple(?:'s)?(?:\s+trip)?)|we'?r?e?\s+(?:a\s+)?couple)\b/i, type: 'couple' },
      { pattern: /\b(?:honeymoon|anniversary trip|romantic (?:getaway|trip|vacation))\b/i, type: 'couple' },
      { pattern: /\b(?:solo|(?:just |only )?(?:me|myself)|traveling (?:alone|by myself)|on my own)\b/i, type: 'solo' },
      { pattern: /\b(?:family|(?:my |our )?kids?|children|(?:my |our )?(?:son|daughter)|family (?:trip|vacation|holiday))\b/i, type: 'family' },
      { pattern: /\b(?:friends?|(?:group of|bunch of) (?:friends?|us)|(?:guys?|girls?|gals?) trip|friend group)\b/i, type: 'friends' },
    ];
    for (const { pattern, type } of companionPatterns) {
      if (pattern.test(lowerMessage)) {
        debugLog(`👥 Detected travel companions: ${type}`);
        setUserProfile(prev => ({
          ...prev,
          preferences: { ...prev.preferences, travelCompanions: type }
        }));
        break;
      }
    }

    // Detect trip occasion
    const occasionPatterns = [
      { pattern: /\bhoneymoon\b/i, occasion: 'honeymoon' },
      { pattern: /\banniversary\b/i, occasion: 'anniversary' },
      { pattern: /\bbirthday\b/i, occasion: 'birthday' },
      { pattern: /\bgraduation\b/i, occasion: 'graduation' },
      { pattern: /\bretirement\b/i, occasion: 'retirement' },
      { pattern: /\bbabymoon\b/i, occasion: 'babymoon' },
      { pattern: /\bwedding\b/i, occasion: 'wedding' },
      { pattern: /\bbachelor(?:ette)?\b/i, occasion: 'bachelor' },
    ];
    for (const { pattern, occasion } of occasionPatterns) {
      if (pattern.test(lowerMessage)) {
        debugLog(`🎉 Detected trip occasion: ${occasion}`);
        setUserProfile(prev => ({
          ...prev,
          preferences: { ...prev.preferences, tripOccasion: occasion }
        }));
        break;
      }
    }

    // Detect travel month
    const monthNames = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    const monthMatch = lowerMessage.match(/\b(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)\b/i);
    if (monthMatch) {
      const monthStr = monthMatch[1].toLowerCase();
      const fullMonth = monthNames.find(m => m.startsWith(monthStr.substring(0, 3)));
      if (fullMonth) {
        const capitalizedMonth = fullMonth.charAt(0).toUpperCase() + fullMonth.slice(1);
        debugLog(`📅 Detected travel month: ${capitalizedMonth}`);
        setUserProfile(prev => ({
          ...prev,
          preferences: {
            ...prev.preferences,
            travelMonths: [capitalizedMonth]
          }
        }));
      }
    }

    // REMOVED: Auto-detection of cities from anywhere in message was incorrectly
    // setting homeCity when user mentioned destinations like "I want to visit Paris"
    // Now we only detect homeCity from explicit departure context patterns below
    
    // ENHANCED: Detect home city from multiple patterns
    // Pattern 1 (most specific): "flying from [city]", "traveling from [city]", "departing from [city]"
    let fromCityMatch = message.match(/(?:flying from|traveling from|travelling from|departing from|leaving from)\s+([a-zA-Z][a-zA-Z\s]+?)(?:\s+and|\s+returning|\s+to|,|:|$)/i);

    // Pattern 2: "I'm in/at [city]", "I live in [city]", "I'm based in [city]"
    if (!fromCityMatch) {
      fromCityMatch = message.match(/(?:I'm in|I'm at|I live in|I'm based in|I'm currently in|currently in|based in|located in)\s+([a-zA-Z][a-zA-Z\s]+?)(?:\s+and|\s+within|,|$)/i);
    }

    // Pattern 3 (generic): "from [city]" — must exclude "returning" to avoid capturing "NYC returning"
    if (!fromCityMatch) {
      fromCityMatch = message.match(/from\s+([a-zA-Z][a-zA-Z\s]+?)(?:\s+and|\s+within|\s+returning|\s+to|,|:|$)/i);
    }
    
    // Pattern 4: Starting message: "[City] resident", "I'm a [City] local"
    if (!fromCityMatch) {
      fromCityMatch = message.match(/([a-zA-Z][a-zA-Z\s]+?)\s+(?:resident|local|native)/i);
    }
    
    if (fromCityMatch) {
      let detectedCity = fromCityMatch[1].trim().toLowerCase();

      // Validate against known major cities (must be a real city, not a generic word)
      const knownCities = [
        'new york', 'nyc', 'los angeles', 'la', 'chicago', 'san francisco', 'miami', 'boston',
        'seattle', 'denver', 'atlanta', 'houston', 'dallas', 'phoenix', 'philadelphia',
        'washington', 'washington dc', 'las vegas', 'san diego', 'austin', 'portland', 'honolulu',
        'london', 'manchester', 'birmingham', 'edinburgh', 'glasgow', 'liverpool', 'bristol',
        'paris', 'lyon', 'marseille', 'nice', 'bordeaux',
        'berlin', 'munich', 'frankfurt', 'hamburg', 'cologne',
        'rome', 'milan', 'florence', 'venice', 'naples',
        'madrid', 'barcelona', 'seville', 'valencia',
        'amsterdam', 'rotterdam', 'brussels', 'zurich', 'geneva', 'vienna',
        'toronto', 'vancouver', 'montreal', 'calgary',
        'sydney', 'melbourne', 'brisbane', 'perth',
        'tokyo', 'osaka', 'kyoto', 'seoul', 'singapore', 'hong kong', 'bangkok', 'dubai', 'mumbai', 'delhi',
        'cape town', 'johannesburg', 'cairo', 'nairobi',
        'mexico city', 'sao paulo', 'buenos aires', 'bogota', 'lima', 'santiago'
      ];

      // Check if the detected string matches a known city
      const isKnownCity = knownCities.some(city =>
        detectedCity === city ||
        detectedCity.startsWith(city + ' ') ||
        detectedCity.endsWith(' ' + city)
      );

      if (isKnownCity) {
        // Capitalize properly
        const cityName = detectedCity === 'nyc' ? 'NYC' :
                        detectedCity === 'la' ? 'Los Angeles' :
                        detectedCity === 'washington dc' ? 'Washington DC' :
                        detectedCity.split(' ')
                          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                          .join(' ');
        // Store in tripContext only — do NOT auto-update homeCity.
        // The yellow banner will appear and let the user click "Update From" to confirm.
        setTripContext(prev => ({ ...prev, originCity: cityName }));
        debugLog('🏠 Detected origin city (banner only):', cityName);
      }
    }

    // Also detect return city: "returning to [city]", "coming back to [city]"
    const returnCityMatch = message.match(/(?:returning to|return to|coming back to|flying back to|landing in)\s+([a-zA-Z][a-zA-Z\s]+?)(?:\s+and|,|:|$)/i);
    if (returnCityMatch) {
      let detectedReturn = returnCityMatch[1].trim().toLowerCase();
      const knownReturnCities = [
        'new york', 'nyc', 'los angeles', 'la', 'chicago', 'san francisco', 'miami', 'boston',
        'seattle', 'denver', 'atlanta', 'houston', 'dallas', 'phoenix', 'philadelphia',
        'washington', 'washington dc', 'las vegas', 'san diego', 'austin', 'portland', 'honolulu',
        'london', 'paris', 'berlin', 'rome', 'madrid', 'amsterdam', 'toronto', 'vancouver',
        'sydney', 'melbourne', 'tokyo', 'seoul', 'singapore', 'hong kong', 'bangkok', 'dubai',
        'cape town', 'cairo', 'nairobi', 'mexico city', 'sao paulo', 'buenos aires',
      ];
      const isKnownReturn = knownReturnCities.some(city =>
        detectedReturn === city || detectedReturn.startsWith(city + ' ') || detectedReturn.endsWith(' ' + city)
      );
      if (isKnownReturn) {
        const returnName = detectedReturn === 'nyc' ? 'NYC' :
                          detectedReturn === 'la' ? 'Los Angeles' :
                          detectedReturn.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
        setUserProfile(prev => ({
          ...prev,
          preferences: { ...prev.preferences, returnCity: returnName }
        }));
        debugLog('🏠 Detected return city:', returnName);
      }
    }
  };

  // Helper function to validate if a string is a real destination (not a description)
  const isValidDestinationName = (destination) => {
    if (!destination) return false;
    
    const destLower = destination.toLowerCase();
    
    // These are VALID destination patterns even if they contain descriptive words
    const validDestinationPatterns = [
      'national park', 'kruger', 'serengeti', 'masai mara', 'ngorongoro', 'okavango',
      'yellowstone', 'yosemite', 'grand canyon', 'zion', 'banff', 'patagonia',
      'game reserve', 'safari lodge', 'maldives', 'bora bora', 'santorini', 'amalfi',
      'great barrier reef', 'machu picchu', 'angkor wat', 'petra'
    ];
    
    // If it matches a known valid destination pattern, it's valid
    if (validDestinationPatterns.some(pattern => destLower.includes(pattern))) {
      return true;
    }
    
    const invalidDestinationPatterns = [
      'vacation', 'trip', 'getaway', 'adventure', 'paradise', 'experience',
      'relaxing', 'romantic', 'beautiful', 'amazing', 'perfect', 'dream',
      'scenery', 'sunsets', 'beaches', 'mountains', 'culture', 'food',
      'for two', 'with family', 'solo', 'honeymoon trip', 'beach vacation',
      'coastal', 'tropical', 'exotic', 'destination', 'journey', 'escape',
      'city destination', 'beach destination', 'mountain destination',
      'somewhere', 'anywhere', 'a place', 'location'
    ];
    
    const looksLikeDescription = invalidDestinationPatterns.some(pattern => 
      destLower.includes(pattern)
    ) || destination.split(' ').length > 5; // Allow up to 5 words for places like "Kruger National Park, South Africa"
    
    return !looksLikeDescription;
  };

  // ============================================
  // INTENT-BASED MESSAGE HANDLER
  // New system that uses LLM to understand user intent
  // Replaces brittle regex patterns with dynamic understanding
  // ============================================

  // Feature flag - set to true to enable new intent-based system
  const USE_INTENT_SYSTEM = true;

  // Feature flag - set to true to use new structured extraction system
  const USE_EXTRACTION_SYSTEM = true;

  // ============================================
  // HELPER: Sync trip context to user profile (backward compat)
  // ============================================
  const syncTripContextToProfile = (ctx) => {
    const profileUpdates = {};
    if (ctx.duration) profileUpdates.duration = Math.min(ctx.duration, 14);
    if (ctx.travelers) profileUpdates.travelCompanions = ctx.travelers;
    if (ctx.occasion) profileUpdates.tripOccasion = ctx.occasion;
    if (ctx.month) {
      const cap = ctx.month.charAt(0).toUpperCase() + ctx.month.slice(1).toLowerCase();
      profileUpdates.travelMonths = [cap];
    }
    if (ctx.activities.length > 0) profileUpdates.likedActivities = ctx.activities;

    if (Object.keys(profileUpdates).length > 0) {
      setUserProfile(prev => ({
        ...prev,
        preferences: { ...prev.preferences, ...profileUpdates }
      }));
      debugLog('📋 Synced trip context to profile:', profileUpdates);
    }
  };

  // ============================================
  // HELPER: Build contextual response message for added destinations
  // ============================================
  const buildAddStopsMessage = (addedNames, ctx, entityType) => {
    if (addedNames.length === 0) return null;

    const destText = addedNames.length === 1 ? `**${addedNames[0]}**` : `**${addedNames.join('**, **')}**`;
    const hasContext = ctx.duration || ctx.travelers || ctx.occasion || ctx.month;

    let msgContent;
    if (hasContext) {
      const details = [];
      if (ctx.duration) details.push(`**${ctx.duration} days**`);
      if (ctx.travelers) details.push(`for **${ctx.travelers === 'couple' ? '2 travelers' : ctx.travelers === 'solo' ? '1 traveler' : ctx.travelers}**`);
      if (ctx.month) details.push(`in **${ctx.month}**`);
      const detailStr = details.length > 0 ? ` I've set it up as ${details.join(', ')}.` : '';
      const occasionText = ctx.occasion ? ` ${ctx.occasion}` : '';

      msgContent = ctx.occasion
        ? `${destText} — what a${occasionText === ' anniversary' ? 'n' : ''} great${occasionText} destination!${detailStr}`
        : `${destText} is a great choice!${detailStr}`;
    } else {
      msgContent = addedNames.length === 1
        ? `${destText} is a great choice! I've added it to your trip.`
        : `I've added ${destText} to your trip!`;
    }

    return msgContent;
  };

  // ============================================
  // NEW: Handle message using structured extraction
  // ============================================
  const handleMessageWithExtraction = async (messageToSend, newMessages) => {
    try {
      debugLog('🧠 Using structured extraction...');

      // 1. Extract structured data from message
      const extraction = await extractMessage(
        messageToSend,
        messages.slice(-12),
        tripContext,
        tripType || 'destination'
      );

      debugLog('📦 Extraction result:', extraction);

      // Track requestId for bug report correlation
      if (extraction._requestId) {
        lastRequestIdRef.current = extraction._requestId;
      }

      // 2. Merge extraction into trip context (always, regardless of action)
      const updatedContext = mergeTripContext(tripContext, extraction);
      // Ensure originCity persists: new extraction wins, else keep existing from previous context
      // (mergeTripContext now handles this via `extraction.originCity ?? prevContext.originCity`,
      //  but we keep this as a belt-and-suspenders safeguard)
      if (extraction.originCity) {
        updatedContext.originCity = extraction.originCity;
        debugLog(`📍 Origin city detected: ${extraction.originCity} (used for context only, not added as stop or FROM)`);
        // Auto-apply to homeCity only if not already set — prevents NYC default from leaking.
        // If homeCity is already set, show the "AI detected" banner so the user can manually Update.
        if (!userProfile.preferences?.homeCity) {
          setUserProfile(prev => ({
            ...prev,
            preferences: { ...prev.preferences, homeCity: extraction.originCity }
          }));
          debugLog(`🏠 Auto-set homeCity to ${extraction.originCity} (was empty)`);
        }
      } else if (tripContext.originCity && !updatedContext.originCity) {
        updatedContext.originCity = tripContext.originCity;
        debugLog(`📍 Origin city preserved from previous context: ${tripContext.originCity}`);
      }
      // Handle returnCity for open-jaw trips (e.g., "departing from NYC returning to San Francisco")
      if (extraction.returnCity) {
        setUserProfile(prev => ({
          ...prev,
          preferences: { ...prev.preferences, returnCity: extraction.returnCity }
        }));
        debugLog(`🏠 Return city detected: ${extraction.returnCity}`);
      }
      setTripContext(updatedContext);
      debugLog('🔄 Updated trip context:', updatedContext);

      // 3. Sync metadata to user profile (backward compat)
      syncTripContextToProfile(updatedContext);

      // 4. Determine actions
      // Attach original message so determineActions can detect "give me more" patterns
      extraction._originalMessage = messageToSend;
      const actions = determineActions(extraction, updatedContext);
      debugLog('🎯 Actions to execute:', actions);

      // 5. Execute actions sequentially, tracking context between actions
      let handled = false;
      let justAddedDestinations = []; // Track what was just added for recommendation context
      for (const action of actions) {
        // Pass context about just-added destinations to recommend action
        if (action.type === 'recommend' && justAddedDestinations.length > 0) {
          action._justAddedDestinations = justAddedDestinations;
        }
        const result = await executeExtractionAction(action, extraction, updatedContext, newMessages);
        if (result !== false) handled = true;
        // Track destinations added by add_stops for use by subsequent recommend action
        if (action.type === 'add_stops' && action.destinations) {
          justAddedDestinations = action.destinations;
        }
      }

      return handled;
    } catch (error) {
      debugLog('❌ Extraction handling error:', error);
      return false;
    }
  };

  // ============================================
  // NEW: Execute a single action from structured extraction
  // ============================================
  const executeExtractionAction = async (action, extraction, ctx, newMessages) => {
    // entityType drives the system prompt — "property" gets hotel
    // suggestions, "destination" gets city/region suggestions. Default by
    // tripType, but override based on the user's actual phrasing for
    // THIS message (parity with mobile/screens/ChatScreen.js #5c). Words
    // like "places", "destinations", "cities", "where" force destination
    // mode regardless of trip type — fixes Hope's complaint of getting
    // hotel cards when she asked about "other places in Thailand".
    let entityType = tripType === 'hotel' ? 'property' : 'destination';
    let entityTypePlural = tripType === 'hotel' ? 'properties' : 'destinations';
    const lastUserMsg = newMessages?.slice().reverse().find((m) => m.role === 'user')?.content || '';
    const lower = lastUserMsg.toLowerCase();
    const wantsPlaces = /\b(place|places|destination|destinations|cit(y|ies)|town|towns|spot|spots|where else|what about|other places|elsewhere|another country|other countries|top spots|best spots)\b/.test(lower);
    const wantsHotels = /\b(hotel|hotels|resort|resorts|where to stay|stay at|where do i stay|accommodation|lodging|villa|villas)\b/.test(lower);
    if (wantsPlaces && !wantsHotels) { entityType = 'destination'; entityTypePlural = 'destinations'; }
    if (wantsHotels && !wantsPlaces) { entityType = 'property'; entityTypePlural = 'properties'; }

    switch (action.type) {

      // ============================================
      // ADD STOPS — add specific destinations to trip builder
      // ============================================
      case 'add_stops': {
        // In hotel mode, cities are hotel search contexts — don't add as trip stops
        // Instead, redirect to the recommend flow to show hotel cards.
        // BUT: if a destination IS a specific hotel/resort name (contains hotel keywords),
        // add it directly as a resort stop instead of searching. This handles
        // "The Lodge at Woodloch" being correctly added vs "Paris" triggering hotel search.
        const HOTEL_DEST_SIGNALS = ['hotel', 'resort', 'inn', 'lodge', 'palace', 'villa',
          'manor', 'ranch', 'chateau', 'château', 'mansion', 'retreat', 'cabin', 'cottage', 'suites'];
        // Known luxury hotel/resort brand prefixes — these are NOT city names
        const HOTEL_BRAND_PREFIXES = [
          'aman', 'amangiri', 'amanpuri', 'amankora', 'amanjena', 'amanzoe',
          'soneva', 'one&only', 'one & only', 'oneandonly',
          'six senses', 'four seasons', 'st. regis', 'st regis',
          'ritz carlton', 'ritz-carlton', 'mandarin oriental',
          'rosewood', 'belmond', 'como', 'taj ', 'oberoi',
          'peninsula', 'raffles', 'banyan tree', 'anantara',
          'waldorf astoria', 'park hyatt', 'w hotel',
          'miraval', 'canyon ranch', 'golden door',
          'singita', 'andaz', 'capella', 'alila', 'ayana',
          'bulgari hotel', 'edition hotel', 'nobu hotel',
          'equinox hotel', 'auberge', 'montage',
          'pendry', 'thompson hotel', 'viceroy',
          'faena', 'soho house', 'ace hotel',
        ];
        const isSpecificHotelName = (name) => {
          const lower = name.toLowerCase();
          return HOTEL_DEST_SIGNALS.some(kw => lower.includes(kw)) ||
                 HOTEL_BRAND_PREFIXES.some(brand => lower.startsWith(brand) || lower.includes(brand));
        };

        // BUG-FIX (Test 5 Chrome MCP, May 2026): user-stated order MUST be
        // preserved. Previously this code partitioned destinations into
        // hotels-first, cities-second — meaning "Cape Town, Singita Sabi Sand,
        // Victoria Falls, Maun" came out as "Singita, Cape Town, Vic Falls,
        // Maun" because hotels were always added first. Now we iterate in
        // the user-stated order in the loop below and route each destination
        // based on whether it's a hotel name. We still compute the
        // hotel/city counts for the hotel-mode intercept check (`isMixedTrip`).
        const hotelNameDests = (action.destinations || []).filter(d => isSpecificHotelName(d));
        const cityNameDests = (action.destinations || []).filter(d => !isSpecificHotelName(d));
        const alreadyHandledAsHotel = new Set(); // kept for backward-compat with downstream classifier (always empty after this fix)

        // Use ref to get the CURRENT tripType (avoids stale closure from home page setTimeout)
        const currentTripType = tripTypeRef.current ?? tripType;
        // Only intercept city names for hotel search when in PURE hotel mode —
        // if the extraction also contains hotel/resort names (mixed trip like "Soneva Fushi + Tokyo"),
        // add city destinations as normal trip stops instead of redirecting to hotel search.
        const isMixedTrip = hotelNameDests.length > 0 && cityNameDests.length > 0;
        if (currentTripType === 'hotel' && cityNameDests.length > 0 && !isMixedTrip) {
          const cityNames = cityNameDests;
          debugLog(`🏨 Hotel mode — intercepting add_stops, treating ${JSON.stringify(cityNames)} as hotel search context`);
          pendingHotelCitiesRef.current = cityNames;
          setTripContext(prev => ({
            ...prev,
            hotelSearchLocation: cityNames.join(', '),
            destinations: [...(prev.destinations || []), ...cityNames.map(n => ({ name: n, country: '' }))],
          }));
          // Trigger recommend action inline (since there may be no recommend action in the list)
          const syntheticRecommend = { type: 'recommend', preferences: [], _justAddedDestinations: cityNames };
          return await executeExtractionAction(syntheticRecommend, extraction, { ...ctx, hotelSearchLocation: cityNames.join(', ') }, newMessages);
        }

        const numAddStops = Math.max(1, ctx.destinations.length);
        const baseAddDays = ctx.duration
          ? Math.max(2, Math.floor(ctx.duration / numAddStops))
          : 3;
        const addRemainder = ctx.duration ? ctx.duration - (baseAddDays * numAddStops) : 0;

        const addedNames = [...alreadyHandledAsHotel].map(n =>
          action.destinations.find(d => d.toLowerCase() === n) || n
        ); // include hotel names that were already added above
        let addStopIdx = 0;
        for (const destName of action.destinations) {
          // Skip destinations already handled as hotel names above
          if (alreadyHandledAsHotel.has(destName.toLowerCase())) continue;

          const alreadyAdded = tripBuilderStops.some(stop =>
            stop.name.toLowerCase() === destName.toLowerCase()
          );
          if (!alreadyAdded) {
            // Use per-stop days from extraction if available, fallback to equal division
            const detail = (action.destinationDetails || []).find(d =>
              d.name?.toLowerCase() === destName.toLowerCase()
            );
            const fallbackWithRemainder = addStopIdx < addRemainder ? baseAddDays + 1 : baseAddDays;
            const stopDays = (detail?.days && detail.days > 0) ? detail.days : fallbackWithRemainder;
            addStopIdx++;

            // Bug 2 fix — auto-populate hotel flag dynamically.
            // Three-source classification (priority order):
            //   1. `detail.type` from LLM-tagged recommendation (V2 backend
            //      will populate this; client reads if present).
            //   2. `currentTripType === 'hotel'` chat-mode signal — every
            //      result surfaced in hotel mode is a hotel by construction.
            //   3. `isSpecificHotelName` legacy heuristic as the safety net.
            // Mirrors mobile/src/screens/ChatScreen.js#handleAddDest.
            const detailType = detail?.type;
            const isHotelName = alreadyHandledAsHotel.size > 0
              ? false
              : (detailType === 'hotel'
                  || currentTripType === 'hotel'
                  || isSpecificHotelName(destName));
            addTripBuilderStop({
              name: destName,
              continent: '',
              days: stopDays,
              hotel: ctx.hotelPreference || null,
              stopType: isHotelName ? 'hotel' : 'destination',
              ...(isHotelName ? { stayOnProperty: true } : {}),
            });
            addedNames.push(destName);
          }
        }

        // Only show message if there are no other actions that will show messages
        // (resolve_vague will show its own message that includes the add confirmation)
        const hasVagueAction = determineActions(extraction, ctx).some(a => a.type === 'resolve_vague');
        if (!hasVagueAction) {
          let msgContent = buildAddStopsMessage(addedNames, ctx, entityType);
          if (!msgContent && action.destinations.length > 0) {
            msgContent = action.destinations.length === 1
              ? `**${action.destinations[0]}** is already in your trip!`
              : `Those destinations are already in your trip!`;
          }
          if (msgContent) {
            msgContent += `\n\nYou can see them in the **Trip Builder** on the right. Feel free to:\n- Adjust days for each stop\n- Drag to reorder\n- Add more ${entityTypePlural}\n\nClick **"Create Itinerary"** when you're ready!`;
          }
          if (msgContent) {
            // Include isSmartDetection + destinationOptions so action buttons render
            // (Add to Trip shows "Added", Create Itinerary is actionable)
            const allDestNames = action.destinations.join(' & ');
            setMessages([...newMessages, {
              role: 'assistant',
              content: msgContent,
              isSmartDetection: true,
              extractedDetails: {
                destination: allDestNames,
                duration: ctx.duration || null,
                travelers: ctx.travelers || null,
                occasion: ctx.occasion || null
              },
              destinationOptions: [{
                name: allDestNames,
                description: `${action.destinations.length} ${action.destinations.length === 1 ? 'stop' : 'stops'}`,
                flightHours: null,
                continent: ''
              }]
            }]);
          }
        }
        return true;
      }

      // ============================================
      // RESOLVE VAGUE — resolve vague descriptions into concrete suggestions
      // ============================================
      case 'resolve_vague': {
        // Build context from existing destinations
        const contextDestinations = [...ctx.destinations, ...tripBuilderStops.map(s => s.name)];
        const vagueDescription = action.descriptions.join(', ');

        // Build intro message (may include add confirmation)
        const addedNames = ctx.destinations.filter(d =>
          !tripBuilderStops.some(s => s.name.toLowerCase() === d.toLowerCase()) ||
          tripBuilderStops.length === 0
        );
        let msgContent = buildAddStopsMessage(addedNames.slice(-action.destinations?.length || 0), ctx, entityType) || '';

        if (msgContent) {
          msgContent += `\n\nFor "${vagueDescription}", here are some great options:`;
        } else {
          msgContent = `For "${vagueDescription}", here are some great options:`;
        }

        // Set loading message
        const msgIndex = newMessages.length;
        setMessages([...newMessages, {
          role: 'assistant',
          content: msgContent + '\n\nFinding the best options...',
          vagueDestinationLoading: true
        }]);

        // Resolve via LLM (async)
        (async () => {
          try {
            const resolveResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
              method: "POST",
              headers: await getApiHeaders(),
              body: JSON.stringify({
                model: "claude-haiku-4-5-20251001",
                max_tokens: 300,
                system: `You suggest specific travel destinations. Return ONLY a JSON array of 3 destination names. No explanation.`,
                messages: [{
                  role: "user",
                  content: `The user is planning a trip to ${contextDestinations.join(', ')}.${ctx.occasion ? ` This is a ${ctx.occasion} trip.` : ''}${ctx.activities.length > 0 ? ` They are interested in: ${ctx.activities.join(', ')}.` : ''}${ctx.preferences.length > 0 ? ` They prefer: ${ctx.preferences.join(', ')}.` : ''}${userProfile.preferences?.budget ? ` Budget: ${userProfile.preferences.budget}.` : ''}${userProfile.preferences?.interests?.length > 0 ? ` Interests: ${userProfile.preferences.interests.join(', ')}.` : ''}${userProfile.preferences?.accessibilityNeeds?.length > 0 ? ` Must be accessible for: ${userProfile.preferences.accessibilityNeeds.join(', ')}.` : ''} They also want to visit "${vagueDescription}". Suggest 3 specific, well-known destinations that match "${vagueDescription}" and are geographically sensible given the other stops. Return JSON array of strings, e.g. ["Niseko", "Hakuba", "Nozawa Onsen"].`
                }]
              })
            });

            const resolveData = await resolveResponse.json();
            let suggestions = [];
            if (resolveData?.content?.[0]?.text) {
              let text = resolveData.content[0].text.trim();
              text = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
              const arrMatch = text.match(/\[[\s\S]*\]/);
              if (arrMatch) {
                suggestions = JSON.parse(arrMatch[0]);
              }
            }

            if (suggestions.length > 0) {
              setMessages(prev => prev.map((m, i) => {
                if (i === msgIndex && m.vagueDestinationLoading) {
                  return {
                    ...m,
                    content: msgContent,
                    vagueDestinationLoading: false,
                    vagueDestinationOptions: suggestions,
                    vagueDescription
                  };
                }
                return m;
              }));
            } else {
              setMessages(prev => prev.map((m, i) => {
                if (i === msgIndex && m.vagueDestinationLoading) {
                  return {
                    ...m,
                    content: msgContent + '\n\nTell me a specific destination you have in mind!',
                    vagueDestinationLoading: false
                  };
                }
                return m;
              }));
            }
          } catch (err) {
            debugError('Failed to resolve vague destinations:', err);
            setMessages(prev => prev.map((m, i) => {
              if (i === msgIndex && m.vagueDestinationLoading) {
                return {
                  ...m,
                  content: msgContent + '\n\nWhat specific destination did you have in mind?',
                  vagueDestinationLoading: false
                };
              }
              return m;
            }));
          }
        })();
        return true;
      }

      // ============================================
      // REMOVE STOPS — remove destinations from trip builder
      // ============================================
      case 'remove_stops': {
        const removedNames = [];
        for (const destName of action.targets) {
          const stopToRemove = tripBuilderStops.find(stop =>
            stop.name.toLowerCase().includes(destName.toLowerCase()) ||
            destName.toLowerCase().includes(stop.name.toLowerCase())
          );
          if (stopToRemove) {
            removedNames.push(stopToRemove.name);
            removeTripBuilderStop(stopToRemove.id);
          }
        }
        // Only show removal message if there are no add_stops actions following
        const hasAddAction = determineActions(extraction, ctx).some(a => a.type === 'add_stops');
        if (!hasAddAction) {
          setMessages([...newMessages, {
            role: 'assistant',
            content: removedNames.length > 0
              ? `Removed **${removedNames.join('**, **')}** from your trip.`
              : "I couldn't find that destination in your trip. Check the Trip Builder to see your current stops."
          }]);
        }
        return true;
      }

      // ============================================
      // ADD ALL — add all previously suggested destinations
      // ============================================
      case 'add_all': {
        if (destinationOptions?.length > 0) {
          const addedNames = [];
          for (const dest of destinationOptions) {
            const alreadyAdded = tripBuilderStops.some(stop =>
              stop.name.toLowerCase() === dest.name.toLowerCase()
            );
            if (!alreadyAdded) {
              addTripBuilderStop({
                name: dest.name,
                continent: dest.continent || '',
                days: dest.suggestedDays || 3,
                hotel: null
              });
              addedNames.push(dest.name);
            }
          }
          setMessages([...newMessages, {
            role: 'assistant',
            content: addedNames.length > 0
              ? `Added **${addedNames.join('**, **')}** to your trip! 🎉\n\nCheck the Trip Builder on the right. Click **"Create Itinerary"** when ready!`
              : `All those destinations are already in your trip! Check the Trip Builder on the right.`
          }]);
        } else {
          setMessages([...newMessages, {
            role: 'assistant',
            content: "I don't have any suggested destinations to add right now. Tell me what kind of destination you're looking for!"
          }]);
        }
        return true;
      }

      // ============================================
      // RECOMMEND — generate destination recommendations
      // ============================================
      case 'recommend': {
        debugLog('📋 Generating destination recommendations...');

        const allPreferences = [...new Set([
          ...(action.preferences || []),
          ...(ctx.activities || []),
          ...(ctx.preferences || [])
        ])];
        const preferencesText = allPreferences.length > 0
          ? `Looking for: ${allPreferences.join(', ')}`
          : '';

        // Build accumulated preference summary from ALL user messages in the conversation
        // This ensures criteria like "boutique", "5-star", "in the center" are never lost
        const userMsgs = newMessages.filter(m => m.role === 'user' && typeof m.content === 'string');
        const accumulatedCriteria = [];
        const criteriaKeywords = {
          'boutique': 'boutique/intimate',
          'luxury': 'luxury',
          '5 star': '5-star',
          '5-star': '5-star',
          'five star': '5-star',
          'budget': 'budget-friendly',
          'center': 'city center location',
          'central': 'city center location',
          'downtown': 'city center/downtown',
          'beachfront': 'beachfront',
          'beach': 'beach',
          'pool': 'pool',
          'spa': 'spa',
          'romantic': 'romantic',
          'family': 'family-friendly',
          'adults only': 'adults-only',
          'all inclusive': 'all-inclusive',
          'design': 'design-forward',
          'modern': 'modern',
          'historic': 'historic/heritage',
          'rooftop': 'rooftop',
          'view': 'with views',
          'intimate': 'intimate/small',
          'small': 'small/intimate',
          'exclusive': 'exclusive',
          'hidden gem': 'hidden gem',
          'unique': 'unique/distinctive',
        };
        for (const msg of userMsgs) {
          const lower = msg.content.toLowerCase();
          for (const [keyword, label] of Object.entries(criteriaKeywords)) {
            if (lower.includes(keyword) && !accumulatedCriteria.includes(label)) {
              accumulatedCriteria.push(label);
            }
          }
        }
        // Also capture dissatisfaction signals to understand what the user does NOT want
        const dissatisfactionSignals = [];
        for (const msg of userMsgs) {
          const lower = msg.content.toLowerCase();
          if (lower.includes('not') && lower.includes('enough')) {
            const match = msg.content.match(/not\s+(\w+)\s+enough/i);
            if (match) dissatisfactionSignals.push(`User said previous suggestions were "not ${match[1]} enough"`);
          }
          if (lower.includes('too commercial') || lower.includes('too big') || lower.includes('too large') || lower.includes('too chain')) {
            dissatisfactionSignals.push('User wants to avoid commercial/chain/large properties');
          }
          if (lower.includes('more boutique') || lower.includes('something more')) {
            const match = msg.content.match(/(?:more|something more)\s+(\w+)/i);
            if (match) dissatisfactionSignals.push(`User wants MORE ${match[1]} options than previously shown`);
          }
        }
        const accumulatedCriteriaText = accumulatedCriteria.length > 0
          ? `\n🎯 ACCUMULATED USER CRITERIA (from full conversation — ALL must be satisfied):\n${accumulatedCriteria.map(c => `- ${c}`).join('\n')}${dissatisfactionSignals.length > 0 ? `\n\n⚠️ USER FEEDBACK ON PREVIOUS SUGGESTIONS:\n${dissatisfactionSignals.map(s => `- ${s}`).join('\n')}` : ''}\n`
          : '';

        // Use originCity from extraction for travel time labels + calculations
        // When user says "within 45 mins from Porto", labels AND distances should say "from Porto"
        // CRITICAL: Detect the DISTANCE REFERENCE city from the message separately from departure city.
        // "departing from Pinhão" = departure city, "within 1 hour from Porto" = distance reference city.
        // The distance reference city takes priority for labels and Google Maps calculations.
        const lastUserMsg = newMessages.filter(m => m.role === 'user').pop()?.content || '';
        const distRefMatch = lastUserMsg.match(/(?:within|near|close to|around|less than|under|max).*?(?:from|of)\s+([A-Za-zÀ-ÿ][\w\sÀ-ÿ'.-]{1,30}?)(?:\s*,|\s*\.|\s+(?:and|but|to|for|in|we|i\s|my|the|that)|\s*$)/i);
        const distanceReferenceCity = distRefMatch ? distRefMatch[1].trim() : null;
        // When recommending destinations that complement existing stops, use the LAST existing
        // destination as the reference city for drive/flight times (e.g., if user has Vancouver,
        // Whistler's drive time should be from Vancouver, not from their home city NYC).
        // B9 FIX: existing destinations take priority over homeCity/originCity for drive time origin,
        // because the user will physically be AT those destinations, not at their home city.
        const lastExistingDest = ctx.destinations.length > 0 ? ctx.destinations[ctx.destinations.length - 1] : null;
        const homeCity = distanceReferenceCity || lastExistingDest || ctx.originCity || userProfile.preferences?.homeCity || '';
        // driveOrigin = the city Google Maps should calculate FROM
        const driveOrigin = distanceReferenceCity || lastExistingDest || ctx.originCity || userProfile.preferences?.homeCity || '';
        debugLog(`🏠 Recommend: homeCity=${homeCity}, driveOrigin=${driveOrigin}, distRef=${distanceReferenceCity}, lastExistingDest=${lastExistingDest}, ctx.originCity=${ctx.originCity}, profile.homeCity=${userProfile.preferences?.homeCity}`);
        const currencySymbol = getCurrencySymbol(userProfile.preferences?.currency);
        const hasHomeCity = !!homeCity;
        const flightSuffix = hasHomeCity ? ` • Xhr drive/flight from ${homeCity}` : '';
        const flightSuffixFlight = hasHomeCity ? ` • Xhr flight from ${homeCity}` : '';

        // Build hotel prompt block (computed before the template literal to avoid nesting issues)
        let hotelPromptBlock = '';
        if (tripType === 'hotel') {
          const hotelCities = [];
          // Check ref first (set synchronously in same handler), then ctx (may not be updated yet)
          if (pendingHotelCitiesRef.current.length > 0) {
            pendingHotelCitiesRef.current.forEach(c => { if (!hotelCities.includes(c)) hotelCities.push(c); });
          } else if (ctx.hotelSearchLocation) {
            ctx.hotelSearchLocation.split(',').map(c => c.trim()).filter(Boolean).forEach(c => {
              if (!hotelCities.includes(c)) hotelCities.push(c);
            });
          }
          // Add context destinations as hotel search cities — but skip:
          // 1. Names that look like hotels/resorts (they're properties, not cities)
          // 2. Names already in tripBuilderStops (already on the trip, don't re-search)
          const HOTEL_KEYWORDS_FOR_FILTER = ['hotel', 'resort', 'inn', 'lodge', 'palace', 'villa',
            'manor', 'ranch', 'chateau', 'château', 'mansion', 'retreat', 'cabin', 'cottage', 'suites'];
          const existingStopNames = new Set(tripBuilderStops.map(s => (s.name || '').toLowerCase().trim()));
          (ctx.destinations || []).forEach(d => {
            const name = typeof d === 'string' ? d : d.name;
            if (!name) return;
            const nameLower = name.toLowerCase().trim();
            const looksLikeHotel = HOTEL_KEYWORDS_FOR_FILTER.some(kw => nameLower.includes(kw));
            const alreadyInTrip = existingStopNames.has(nameLower) ||
              [...existingStopNames].some(s => nameLower.includes(s) || s.includes(nameLower));
            if (!hotelCities.includes(name) && !looksLikeHotel && !alreadyInTrip) hotelCities.push(name);
          });

          if (hotelCities.length > 1) {
            const cityBlocks = hotelCities.map((city, ci) => {
              const start = ci * 2 + 1;
              return `### ${city}\n\n**${start}. [Hotel/Resort Name]** • ${city} • ${currencySymbol}${currencySymbol}${currencySymbol} per night\nBrief description (2-3 sentences).\n**Highlights:** Key features\n**Best for:** Type of traveler\n\n**${start + 1}. [Hotel/Resort Name]** • ${city} • ${currencySymbol}${currencySymbol} per night\nDescription\n**Highlights:** Key features\n**Best for:** Type of traveler`;
            }).join('\n\n');
            hotelPromptBlock = `\n🏨 HOTEL-FIRST MODE - The user wants hotels in MULTIPLE cities: ${hotelCities.join(', ')}.\nSuggest exactly 2 specific HOTELS/RESORTS per city. Group them by city.\nNEVER output "[Hotel/Resort Name]" or any bracketed placeholder. Replace ALL with REAL hotel names.\n\n🚨 MANDATORY FORMAT — use this EXACT numbered format grouped by city:\n${cityBlocks}\n`;
          } else {
            hotelPromptBlock = `\n🏨 HOTEL-FIRST MODE - Suggest exactly 4 specific HOTELS/RESORTS that match ALL the user's accumulated criteria above.\nNEVER output "[Destination Name]", "[Hotel/Resort Name]", or any bracketed placeholder. Replace ALL placeholders with REAL hotel names.\n\n🚨 MANDATORY FORMAT — you MUST use this EXACT numbered format. Do NOT deviate. Do NOT use unnumbered headers or plain text names:\n**1. [Hotel/Resort Name]** • [Location] • ${currencySymbol}${currencySymbol}${currencySymbol} per night${flightSuffix}\nBrief description of the property (2-3 sentences).\n**Highlights:** Key features\n**Best for:** Type of traveler\n\n**2. [Hotel/Resort Name]** • [Location] • ${currencySymbol}${currencySymbol} per night${flightSuffix}\nDescription\n**Highlights:** Key features\n**Best for:** Type of traveler\n\n**3. [Hotel/Resort Name]** • [Location] • ${currencySymbol}${currencySymbol}${currencySymbol}${currencySymbol} per night${flightSuffix}\nDescription\n**Highlights:** Key features\n**Best for:** Type of traveler\n\n**4. [Hotel/Resort Name]** • [Location] • ${currencySymbol}${currencySymbol} per night${flightSuffix}\nDescription\n**Highlights:** Key features\n**Best for:** Type of traveler\n`;
          }
        }

        setLoadingState('searching');
        setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);

        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
            method: "POST",
            headers: await getApiHeaders(),
            body: JSON.stringify({
              model: "claude-sonnet-4-20250514",
              max_tokens: 2000,
              system: `You are Arrivo AI, an expert travel agent. ${entityType === 'property' ? 'The user is looking for hotel/resort recommendations.' : 'The user is looking for destination recommendations.'}
${preferencesText ? `\nUser preferences: ${preferencesText}` : ''}
${entityType === 'property' && pendingHotelCitiesRef.current.length > 0
  ? `\nThe user wants to find hotels in: ${pendingHotelCitiesRef.current.join(', ')}. Recommend specific hotels in these cities.`
  : ctx.destinations.length > 0 ? `\nAlready planned stops: ${ctx.destinations.join(', ')}. Suggest destinations that complement these.` : ''}
${ctx.occasion ? `\nTrip occasion: ${ctx.occasion}` : ''}
${buildProfileContext()}
${accumulatedCriteriaText}
Do NOT say "We've added" or "here are destinations to add on". Just present your recommendations directly.
${entityType === 'property' ? hotelPromptBlock : `
🗺️ DESTINATION MODE - Suggest exactly 4 destinations that match the user's criteria.
NEVER output "[Destination Name]" or any bracketed placeholder. Replace ALL placeholders with REAL destination names.

🚨 MANDATORY FORMAT — you MUST use this EXACT numbered format. Do NOT deviate:
**1. [Destination Name]** • ${currencySymbol}${currencySymbol}${flightSuffixFlight}
Brief compelling description (2-3 sentences)
**Best time to visit:** [Season/months] for [reason].

**2. [Destination Name]** • ${currencySymbol}${currencySymbol}${currencySymbol}${flightSuffixFlight}
Description
**Best time to visit:** [Season/months] for [reason].

**3. [Destination Name]** • ${currencySymbol}${flightSuffixFlight}
Description
**Best time to visit:** [Season/months] for [reason].

**4. [Destination Name]** • ${currencySymbol}${currencySymbol}${flightSuffixFlight}
Description
**Best time to visit:** [Season/months] for [reason].
`}
${hasHomeCity ? `
CRITICAL FLIGHT TIME RULES:
- From NYC: Europe 7-9hr, Japan 14-16hr, Southeast Asia 18-22hr, Caribbean 3-5hr
- From LA: Hawaii 5-6hr, Japan 11-13hr, Europe 10-12hr
- Be accurate with flight times!` : `
NOTE: The user's home city is unknown. Do NOT include flight/drive times in the recommendations.`}

🧠 CONTEXT RULES — READ CAREFULLY:
- Review the FULL conversation history. The user may have been refining their criteria across multiple messages.
- If the user previously said "boutique", "5-star", "in the center", etc., ALL subsequent suggestions MUST still match those criteria PLUS any new constraints.
- Accumulate preferences — NEVER forget earlier requirements when the user adds new ones.
- If the user expressed dissatisfaction with previous suggestions (e.g., "not boutique enough", "too commercial"), understand WHY and adjust accordingly.
- CONTEXT SWITCH: If the user changes region/destination entirely (e.g., "actually show me Asia" after discussing Europe), treat it as a fresh search. Drop previous region-specific criteria but keep general preferences (luxury, boutique, budget, etc.).

${shownDestinations.length > 0 ? `
🚫 DO NOT REPEAT — these ${tripType === 'hotel' ? 'hotels' : 'destinations'} were already shown:
${shownDestinations.map(d => `- ${d}`).join('\n')}

You MUST suggest 4 completely NEW ${tripType === 'hotel' ? 'hotels/resorts' : 'destinations'} not in the above list.
If the user has changed regions/destinations entirely, you may ignore this dedup list since they are searching in a new area.
` : ''}

End with: "**What's Next?**\\nTell me which destination interests you and I'll help create a detailed itinerary!"`,
              messages: newMessages.slice(-12).filter(m => m.content && m.content !== 'loading').map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content) }))
            })
          });

          if (!response.ok) {
            const errBody = await response.json().catch(() => ({}));
            throw new Error(errBody.message || `Recommendation API error (${response.status})`);
          }

          const data = await response.json();
          const rawAssistantMessage = data.content?.[0]?.text || "I'd love to help you find the perfect destination! Could you tell me more about what you're looking for?";

          // Extract destinations from the RAW LLM message (before stripping drive times)
          // Always attempt extraction — the parser has 4 fallback patterns and handles varied formats
          let extractedDests = extractDestinationsFromMessage(rawAssistantMessage);

          // Strip LLM drive times and replace with placeholder — we'll insert real Google Maps times async
          const hasDriveDestinations = extractedDests.some(d => d.driveHours != null);
          const { strippedMessage } = hasDriveDestinations
            ? stripDriveTimesFromMessage(rawAssistantMessage)
            : { strippedMessage: rawAssistantMessage };
          const assistantMessage = strippedMessage;

          // Flag drive destinations with placeholder and clear inaccurate LLM driveHours
          if (hasDriveDestinations) {
            extractedDests = extractedDests.map(d => d.driveHours != null
              ? { ...d, drivePlaceholder: true, llmDriveHours: d.driveHours, driveHours: null }
              : d
            );
          }

          if (extractedDests.length > 0) {
            setDestinationOptions(extractedDests);
            const newDestNames = extractedDests.map(d => d.name);
            setShownDestinations(prev => [...prev, ...newDestNames]);
            debugLog('📍 Extracted destinations from recommendations:', newDestNames);
          }

          // If destinations were just added before this recommend action, prepend bridging text
          const justAdded = action._justAddedDestinations || [];
          let finalRecommendMessage = assistantMessage;
          if (justAdded.length > 0 && tripType !== 'hotel') {
            const addedText = justAdded.length === 1
              ? `**${justAdded[0]}**`
              : `**${justAdded.join('**, **')}**`;
            finalRecommendMessage = `We've added ${addedText} to your trip! Based on your interests, here are a few more ${entityTypePlural} you could add on:\n\n${assistantMessage}`;
          }

          // CRITICAL: Set messages, loadingState, and isProcessing together in the same
          // synchronous block so React batches them into a single render.
          // Previously, the await on trackUsageOnBackend split these updates across
          // renders, causing buttons to not appear (isProcessing was still true during
          // the render that had the destination content).
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: finalRecommendMessage,
              // Attach destinations to message so render can detect them even if content format varies
              ...(extractedDests.length > 0 ? { destinationOptions: extractedDests } : {})
            };
            return updated;
          });
          setLoadingState(null);
          setIsProcessing(false);

          // Track usage asynchronously AFTER UI is updated (fire-and-forget)
          if (user?.id && trackUsageOnBackend) {
            trackUsageOnBackend('recommendation').catch(usageErr => {
              debugError('Usage tracking error:', usageErr);
            });
          }

          // ASYNC DRIVE TIME INSERTION (fire-and-forget)
          // Drive times were stripped from the message text (shown as "⏳ drive").
          // Now fetch real Google Maps times from the TRIP ORIGIN (not profile home).
          if (extractedDests.length > 0 && hasDriveDestinations) {
            validateAndCorrectDriveTimes(extractedDests, driveOrigin, assistantMessage);
          }
        } catch (error) {
          debugError('Recommendation error:', error);
          const isLimitError = error.message?.includes('limit');
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: isLimitError
                ? "You've reached a usage limit. Check the **Settings** pane for more information on your plan and limits."
                : "I'm having trouble finding recommendations right now. Could you tell me a specific destination you're interested in?"
            };
            return updated;
          });
        }

        setLoadingState(null);
        setIsProcessing(false);
        return true;
      }

      // ============================================
      // ANSWER QUESTION
      // ============================================
      case 'answer_question': {
        setLoadingState('thinking');
        setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);

        try {
          const tripStopNames = tripBuilderStops.map(s => s.name).join(', ');
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
            method: "POST",
            headers: await getApiHeaders(),
            body: JSON.stringify({
              model: "claude-sonnet-4-20250514",
              max_tokens: 1000,
              system: `You are Arrivo AI, a helpful travel expert. Answer the user's question helpfully and concisely.
${tripStopNames ? `\nThe user is planning a trip to: ${tripStopNames}` : ''}
${ctx.occasion ? `\nTrip type: ${ctx.occasion}` : ''}
${buildProfileContext()}
Always relate back to travel planning when possible. Tailor your answers to the user's profile preferences above (budget, dietary needs, interests, etc.) when relevant.
IMPORTANT: When suggesting or recommending specific destinations/places/towns, format them as a numbered list like:
**1. Place Name** • Brief description
**2. Place Name** • Brief description
This makes it easy for the user to add them to their trip.`,
              messages: newMessages.slice(-12).filter(m => m.content && m.content !== 'loading').map(m => ({
                role: m.role,
                content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content)
              }))
            })
          });

          const data = await response.json();
          const rawAnswer = data.content?.[0]?.text || "I'm not sure about that. Shall we focus on planning your trip?";

          // Check if the answer contains destination recommendations (numbered format)
          // System prompt instructs AI to use numbered format when suggesting destinations
          let answerDests = extractDestinationsFromMessage(rawAnswer);

          // Strip drive times from answer text — show placeholder until Google Maps confirms
          const hasDriveDestsAnswer = answerDests.some(d => d.driveHours != null);
          const { strippedMessage: strippedAnswer } = hasDriveDestsAnswer
            ? stripDriveTimesFromMessage(rawAnswer)
            : { strippedMessage: rawAnswer };
          const answer = strippedAnswer;

          if (hasDriveDestsAnswer) {
            answerDests = answerDests.map(d => d.driveHours != null
              ? { ...d, drivePlaceholder: true, llmDriveHours: d.driveHours, driveHours: null }
              : d
            );
          }

          if (answerDests.length > 0) {
            setDestinationOptions(answerDests);
            setShownDestinations(prev => [...prev, ...answerDests.map(d => d.name)]);
          }

          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: answer,
              ...(answerDests.length > 0 ? { destinationOptions: answerDests } : {})
            };
            return updated;
          });

          // Async drive time insertion for answer destinations
          // Use trip origin or last existing destination for drive times, not profile home
          if (answerDests.length > 0 && hasDriveDestsAnswer) {
            // B9 FIX: existing destinations take priority over originCity for drive time origin
            const lastCtxDest = tripContext?.destinations?.length > 0 ? tripContext.destinations[tripContext.destinations.length - 1] : null;
            const lastStop = tripBuilderStops?.length > 0 ? tripBuilderStops[tripBuilderStops.length - 1].name : null;
            const driveOrigin = lastStop || lastCtxDest || tripContext?.originCity || userProfile.preferences?.homeCity || '';
            validateAndCorrectDriveTimes(answerDests, driveOrigin, answer);
          }
        } catch (error) {
          debugError('Question answering error:', error);
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: "I'm having trouble looking that up. What else can I help you with for your trip?"
            };
            return updated;
          });
        }

        setLoadingState(null);
        setIsProcessing(false);
        return true;
      }

      // ============================================
      // SOCIAL RESPONSE — greeting, farewell, confirmation, rejection
      // ============================================
      case 'social_response': {
        const { sentiment, socialType } = action;

        if (socialType === 'greeting') {
          const greetings = [
            "Hello! 👋 Ready to plan an amazing trip?",
            "Hi there! Where would you like to explore?",
            "Hey! I'm excited to help you plan your next adventure!",
            "Hello! What destination has been on your mind lately?"
          ];
          setMessages([...newMessages, {
            role: 'assistant',
            content: greetings[Math.floor(Math.random() * greetings.length)]
          }]);
          return true;
        }

        if (socialType === 'farewell') {
          const farewells = [
            "You're welcome! Have an amazing trip! ✈️",
            "Happy travels! Come back anytime you need help planning!",
            "Enjoy your adventure! 🌍",
            "Thanks for using arrivo! Safe travels!"
          ];
          setMessages([...newMessages, {
            role: 'assistant',
            content: farewells[Math.floor(Math.random() * farewells.length)]
          }]);
          return true;
        }

        if (sentiment === 'positive') {
          if (tripBuilderStops.length > 0) {
            setMessages([...newMessages, {
              role: 'assistant',
              content: "Great! Your trip is looking good. Click **\"Create Itinerary\"** in the Trip Builder when you're ready, or add more destinations!"
            }]);
          } else {
            setMessages([...newMessages, {
              role: 'assistant',
              content: "Sounds good! What destination are you thinking about?"
            }]);
          }
          return true;
        }

        if (sentiment === 'negative') {
          setMessages([...newMessages, {
            role: 'assistant',
            content: "No problem! Would you like me to show you different options, or do you have something else in mind?"
          }]);
          return true;
        }

        // Neutral social — fall through to conversational
        return false;
      }

      // ============================================
      // MODIFY — falls back to legacy handling for now
      // ============================================
      case 'modify': {
        debugLog('🔧 Modification requested — falling back to legacy handling');
        return false;
      }

      // ============================================
      // CONVERSATIONAL — general chat, off-topic
      // ============================================
      case 'conversational':
      default: {
        setLoadingState('thinking');
        setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);

        try {
          const tripStopNames = tripBuilderStops.map(s => s.name).join(', ');
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
            method: "POST",
            headers: await getApiHeaders(),
            body: JSON.stringify({
              model: "claude-sonnet-4-20250514",
              max_tokens: 300,
              system: `You are Arrivo AI, a friendly travel planning assistant. The user has sent a message that isn't directly about selecting a destination. Acknowledge what they said and gently guide back to travel planning. Be warm and helpful in 2-3 sentences. Use a relevant emoji.
${tripStopNames ? `\nCurrent trip: ${tripStopNames}` : ''}
${ctx.occasion ? `\nTrip occasion: ${ctx.occasion}` : ''}
${buildProfileContext()}
IMPORTANT: If you mention any specific destination/place/town suggestions, format them as a numbered list like:
**1. Place Name** • Brief description
**2. Place Name** • Brief description
This makes it easy for the user to add them to their trip.`,
              messages: newMessages.slice(-12).filter(m => m.content && m.content !== 'loading').map(m => ({
                role: m.role,
                content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content)
              }))
            })
          });

          const data = await response.json();
          const rawResponseText = data.content?.[0]?.text || "I'm here to help you plan your next adventure! What destination sounds exciting to you? 🌍";

          // Check if conversational response contains destination recommendations
          // Uses extractDestinationsFromMessage which now has bold prose fallback
          let convDests = extractDestinationsFromMessage(rawResponseText);

          // Strip drive times from conversational text — show placeholder until Google Maps confirms
          const hasDriveDestsConv = convDests.some(d => d.driveHours != null);
          const { strippedMessage: strippedConv } = hasDriveDestsConv
            ? stripDriveTimesFromMessage(rawResponseText)
            : { strippedMessage: rawResponseText };
          const responseText = strippedConv;

          if (hasDriveDestsConv) {
            convDests = convDests.map(d => d.driveHours != null
              ? { ...d, drivePlaceholder: true, llmDriveHours: d.driveHours, driveHours: null }
              : d
            );
          }

          if (convDests.length > 0) {
            setDestinationOptions(convDests);
            setShownDestinations(prev => [...prev, ...convDests.map(d => d.name)]);
          }

          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: responseText,
              ...(convDests.length > 0 ? { destinationOptions: convDests } : {})
            };
            return updated;
          });

          // Async drive time insertion for conversational destinations
          // B9 FIX: existing destinations take priority over originCity for drive time origin
          if (convDests.length > 0 && hasDriveDestsConv) {
            const lastCtxDest = tripContext?.destinations?.length > 0 ? tripContext.destinations[tripContext.destinations.length - 1] : null;
            const lastStop = tripBuilderStops?.length > 0 ? tripBuilderStops[tripBuilderStops.length - 1].name : null;
            const driveOrigin = lastStop || lastCtxDest || tripContext?.originCity || userProfile.preferences?.homeCity || '';
            validateAndCorrectDriveTimes(convDests, driveOrigin, responseText);
          }
        } catch (error) {
          debugLog('Error generating conversational response:', error);
          setMessages(prev => {
            const updated = [...prev];
            updated[updated.length - 1] = {
              role: 'assistant',
              content: "I'm here to help you plan your next adventure! What destination sounds exciting to you? 🌍"
            };
            return updated;
          });
        }

        setLoadingState(null);
        setIsProcessing(false);
        return true;
      }
    }
  };

  /**
   * Handle user message using LLM-based intent classification
   * Returns true if handled, false to fall back to legacy handling
   */
  const handleMessageWithIntent = async (messageToSend, newMessages) => {
    try {
      debugLog('🧠 Using intent-based message handling...');

      // Build current state for context
      const currentState = {
        selectedDestinations: destinationOptions?.map(d => d.name) || [],
        tripBuilderStops: tripBuilderStops?.map(s => ({ name: s.name, days: s.days })) || [],
        pendingAction: pendingAction || null,
        stage,
        tripType,
        userPreferences: {
          activities: userProfile?.preferences?.likedActivities || [],
          occasion: userProfile?.preferences?.tripOccasion || null,
          travelers: userProfile?.preferences?.travelCompanions || null,
          duration: userProfile?.preferences?.duration || null,
        }
      };

      // Classify the user's intent
      const classifiedIntent = await classifyIntent(
        messageToSend,
        messages.slice(-12), // Last 6 messages for context
        currentState,
        tripType || 'destination'
      );

      debugLog('🎯 Classified intent:', classifiedIntent);

      // Route to get action
      const action = routeIntent(classifiedIntent, currentState);

      debugLog('🚀 Action to execute:', action);

      // Execute the action based on type
      switch (action.action) {

        // ============================================
        // DESTINATION SELECTION
        // ============================================
        case 'select_destination':
        case 'select_multiple_destinations': {
          const destinations = action.destinations || [];
          const vagueItems = action.vagueItems || [];

          if (destinations.length === 0 && vagueItems.length === 0) {
            setMessages([...newMessages, {
              role: 'assistant',
              content: action.message || "I'd love to help you plan a trip! Which destination are you thinking about?"
            }]);
            return true;
          }

          // Apply extracted trip metadata to user profile
          const extractedDuration = action.duration || userProfile?.preferences?.duration || null;
          const extractedTravelers = action.travelers || null;
          const extractedOccasion = action.occasion || null;
          const extractedMonth = action.month || null;
          const extractedPreferences = action.preferences || action.extract?.preferences || null;
          const extractedPace = action.extract?.pace || action.pace || null;

          // Map raw activity keywords → interest pill labels
          const INTEREST_KEYWORD_MAP = {
            'culture': 'History & Culture', 'history': 'History & Culture', 'heritage': 'History & Culture', 'landmarks': 'History & Culture', 'temples': 'History & Culture',
            'food': 'Food & Dining', 'eat': 'Food & Dining', 'eating': 'Food & Dining', 'dining': 'Food & Dining', 'cuisine': 'Food & Dining', 'restaurant': 'Food & Dining', 'restaurants': 'Food & Dining', 'street food': 'Food & Dining', 'foodie': 'Food & Dining', 'culinary': 'Food & Dining', 'cooking': 'Food & Dining',
            'nature': 'Nature & Outdoors', 'outdoors': 'Nature & Outdoors', 'hiking': 'Nature & Outdoors', 'parks': 'Nature & Outdoors', 'mountains': 'Nature & Outdoors',
            'beach': 'Nature & Outdoors', 'beaches': 'Nature & Outdoors', 'swimming': 'Nature & Outdoors', 'ocean': 'Nature & Outdoors', 'waterfall': 'Nature & Outdoors',
            'adventure': 'Adventure Sports', 'surfing': 'Adventure Sports', 'diving': 'Adventure Sports', 'sports': 'Adventure Sports', 'scuba': 'Adventure Sports', 'snorkeling': 'Adventure Sports', 'kayaking': 'Adventure Sports',
            'art': 'Art & Museums', 'museum': 'Art & Museums', 'museums': 'Art & Museums', 'galleries': 'Art & Museums', 'gallery': 'Art & Museums',
            'shopping': 'Shopping', 'markets': 'Shopping', 'boutiques': 'Shopping', 'market': 'Shopping',
            'nightlife': 'Nightlife', 'bars': 'Nightlife', 'clubs': 'Nightlife', 'nightclub': 'Nightlife',
            'relaxation': 'Relaxation', 'spa': 'Relaxation', 'wellness': 'Relaxation', 'wine': 'Relaxation', 'yoga': 'Relaxation',
          };

          if (extractedDuration || extractedTravelers || extractedOccasion || extractedMonth || extractedPreferences || extractedPace) {
            setUserProfile(prev => {
              const profileUpdates = {};
              if (extractedDuration) profileUpdates.duration = Math.min(extractedDuration, 14);
              if (extractedTravelers) profileUpdates.travelCompanions = extractedTravelers;
              if (extractedOccasion) profileUpdates.tripOccasion = extractedOccasion;
              if (extractedMonth) {
                const capitalizedMonth = extractedMonth.charAt(0).toUpperCase() + extractedMonth.slice(1).toLowerCase();
                profileUpdates.travelMonths = [capitalizedMonth];
              }
              if (extractedPreferences && Array.isArray(extractedPreferences)) {
                profileUpdates.likedActivities = extractedPreferences;
                // Also auto-select matching interest pills
                const mappedInterests = [...new Set(
                  extractedPreferences
                    .map(p => INTEREST_KEYWORD_MAP[p.toLowerCase()])
                    .filter(Boolean)
                )];
                if (mappedInterests.length > 0) {
                  const currentInterests = prev?.preferences?.interests || [];
                  profileUpdates.interests = [...new Set([...currentInterests, ...mappedInterests])];
                }
              }
              // Fallback: scan the raw user message for interest keywords if backend extraction missed them
              if (messageToSend) {
                const msgLower = messageToSend.toLowerCase();
                const msgInterests = [];
                Object.entries(INTEREST_KEYWORD_MAP).forEach(([keyword, interest]) => {
                  if (msgLower.includes(keyword) && !msgInterests.includes(interest)) {
                    msgInterests.push(interest);
                  }
                });
                if (msgInterests.length > 0) {
                  const currentInterests = profileUpdates.interests || prev?.preferences?.interests || [];
                  profileUpdates.interests = [...new Set([...currentInterests, ...msgInterests])];
                  debugLog(`🎯 Detected interests from message: ${msgInterests.join(', ')}`);
                }
              }
              // Map extracted pace to preferredPace
              if (extractedPace) {
                const paceNorm = extractedPace.toLowerCase();
                const validPaces = ['relaxed', 'moderate', 'balanced', 'packed', 'adventurous'];
                if (validPaces.includes(paceNorm)) {
                  profileUpdates.preferredPace = paceNorm;
                }
              }
              // Map extracted travelers to companion pills
              // Map chat-extracted traveler phrases → canonical companions array.
              // Each entry is [base, ...modifiers] aligned with COMPANION_VALUES.
              if (extractedTravelers) {
                const travNorm = extractedTravelers.toLowerCase();
                const TRAVELER_TO_COMPANIONS = {
                  'family': ['family'],
                  'family with kids': ['family', 'kids'],
                  'family with teens': ['family', 'teens'],
                  'family with elderly': ['family', 'elderly'],
                  'couple': ['couple'],
                  'couple with kids': ['couple', 'kids'],
                  'solo': ['solo'],
                  'friends': ['friends'],
                  'group': ['group'],
                  'business': ['business'],
                };
                const mappedCompanions = TRAVELER_TO_COMPANIONS[travNorm];
                if (mappedCompanions) {
                  // Replace base type if present (mutually exclusive); merge modifiers.
                  const BASE = ['solo', 'couple', 'family', 'friends', 'group', 'business'];
                  const cur = coerceCompanionsToArray(prev?.preferences?.companions);
                  const incomingBase = mappedCompanions.find(v => BASE.includes(v));
                  const filtered = incomingBase ? cur.filter(c => !BASE.includes(c)) : cur;
                  profileUpdates.companions = [...new Set([...filtered, ...mappedCompanions])];
                }
              }
              debugLog('📋 Applied extracted trip metadata:', profileUpdates);
              return {
                ...prev,
                preferences: { ...prev.preferences, ...profileUpdates }
              };
            });
          }

          // Calculate days per stop — use per-stop days from extraction when available
          const numStops = Math.max(1, destinations.length);
          const baseDaysPerStop = extractedDuration
            ? Math.max(2, Math.floor(extractedDuration / numStops))
            : 3; // Default 3 days if no duration given
          const remainderDays = extractedDuration ? extractedDuration - (baseDaysPerStop * numStops) : 0;

          // Normalize city names: strip trailing country for display consistency
          // "Florence, Italy" → "Florence", "Rome" → "Rome", "Chiang Mai, Thailand" → "Chiang Mai"
          const KNOWN_COUNTRIES = ['Italy', 'Japan', 'France', 'Spain', 'Greece', 'Thailand', 'Vietnam',
            'Portugal', 'Germany', 'UK', 'United Kingdom', 'United States', 'USA', 'Australia',
            'New Zealand', 'Indonesia', 'India', 'China', 'South Korea', 'Morocco', 'Turkey',
            'Egypt', 'Brazil', 'Argentina', 'Mexico', 'Canada', 'Switzerland', 'Austria',
            'Netherlands', 'Belgium', 'Czech Republic', 'Poland', 'Croatia', 'Hungary',
            'Sweden', 'Norway', 'Denmark', 'Finland', 'Iceland', 'Ireland', 'Scotland',
            'UAE', 'Singapore', 'Malaysia', 'Philippines', 'Cambodia', 'Laos', 'Myanmar',
            'Sri Lanka', 'Nepal', 'Peru', 'Colombia', 'Chile', 'Ecuador', 'Costa Rica',
            'Cuba', 'Jamaica', 'Dominican Republic', 'Russia', 'Kenya', 'Tanzania',
            'South Africa', 'Israel'];
          const normalizeStopName = (name) => {
            if (!name) return name;
            const parts = name.split(',').map(s => s.trim());
            if (parts.length > 1) {
              const lastPart = parts[parts.length - 1];
              if (KNOWN_COUNTRIES.includes(lastPart)) {
                return parts.slice(0, -1).join(', ');
              }
            }
            return name;
          };

          // Add concrete destinations to trip builder
          const addedNames = [];
          const addedStopIds = []; // Track IDs for setting leg transports
          const destDetails = action.destinationDetails || [];
          let stopIndex = 0; // Track index for remainder distribution
          for (const destName of destinations) {
            const alreadyAdded = tripBuilderStops.some(stop =>
              stop.name.toLowerCase() === destName.toLowerCase()
            );

            if (!alreadyAdded) {
              // Check if this destination has per-stop day count from extraction
              const detail = destDetails.find(d => d.name?.toLowerCase() === destName.toLowerCase());
              // Distribute remainder days to first stops (e.g. 7 days / 2 stops = 4+3, not 3+3)
              const defaultWithRemainder = stopIndex < remainderDays ? baseDaysPerStop + 1 : baseDaysPerStop;
              const stopDays = (detail?.days && detail.days > 0) ? detail.days : defaultWithRemainder;
              const displayName = normalizeStopName(destName);
              stopIndex++;

              const newStop = addTripBuilderStop({
                name: displayName,
                continent: '',
                days: stopDays,
                hotel: null
              });
              addedNames.push(displayName);
              addedStopIds.push({ id: newStop?.id, name: displayName, transportTo: detail?.transportTo || null });
            }
          }

          // Set user-stated inter-city transport modes from extraction
          // "fly to Orlando, drive to Miami, drive to Key West" → set leg transports with isUserOverride
          if (addedStopIds.length > 0) {
            const allStops = [...tripBuilderStops, ...addedStopIds.filter(s => s.id)];
            const transportModeMap = { fly: 'fly', drive: 'drive', train: 'train', bus: 'bus', ferry: 'ferry' };

            setLegTransports(prev => {
              const updated = { ...prev };
              for (let i = 0; i < addedStopIds.length; i++) {
                const stop = addedStopIds[i];
                if (!stop.transportTo || !stop.id) continue;

                const mappedMode = transportModeMap[stop.transportTo] || stop.transportTo;

                // Find the previous stop (or departure) to build the leg key
                const stopIdx = allStops.findIndex(s => s.id === stop.id);
                let legKey;
                if (stopIdx === 0 || (stopIdx <= tripBuilderStops.length && i === 0)) {
                  // First stop — leg from departure
                  legKey = `dept→${stop.id}`;
                } else if (stopIdx > 0) {
                  const prevStop = allStops[stopIdx - 1];
                  legKey = `${prevStop.id}→${stop.id}`;
                }

                if (legKey) {
                  updated[legKey] = {
                    ...updated[legKey],
                    mode: mappedMode,
                    isUserOverride: true,
                    source: 'user-extraction',
                    isLoading: false,
                  };
                  debugLog(`🚆 User-stated transport: ${legKey} = ${mappedMode} (from chat extraction)`);
                }
              }
              return updated;
            });
          }

          // Build the message — acknowledge all extracted context
          const entityType = tripType === 'hotel' ? 'property' : 'destination';
    const entityTypePlural = tripType === 'hotel' ? 'properties' : 'destinations';
          let msgContent;
          if (addedNames.length > 0) {
            // Build a context-aware response
            const destText = addedNames.length === 1 ? `**${addedNames[0]}**` : `**${addedNames.join('**, **')}**`;
            const occasionText = extractedOccasion ? ` ${extractedOccasion}` : '';
            const hasContext = extractedDuration || extractedTravelers || extractedOccasion || extractedMonth;

            if (hasContext) {
              // Build contextual details string
              const details = [];
              if (extractedDuration) details.push(`**${extractedDuration} days**`);
              if (extractedTravelers) details.push(`for **${extractedTravelers === 'couple' ? '2 travelers' : extractedTravelers === 'solo' ? '1 traveler' : extractedTravelers}**`);
              if (extractedMonth) details.push(`in **${extractedMonth}**`);
              const detailStr = details.length > 0 ? ` I've set it up as ${details.join(', ')}.` : '';

              msgContent = extractedOccasion
                ? `${destText} — what a${occasionText === ' anniversary' ? 'n' : ''} great${occasionText} destination!${detailStr}`
                : `${destText} is a great choice!${detailStr}`;
            } else {
              msgContent = addedNames.length === 1
                ? `${destText} is a great choice! I've added it to your trip.`
                : `I've added ${destText} to your trip!`;
            }
          } else if (destinations.length > 0) {
            msgContent = destinations.length === 1
              ? `**${destinations[0]}** is already in your trip!`
              : `Those destinations are already in your trip!`;
          } else {
            msgContent = '';
          }

          // If there are vague items, resolve them into concrete suggestions
          if (vagueItems.length > 0) {
            // Build context from existing destinations for smarter suggestions
            const contextDestinations = [...addedNames, ...tripBuilderStops.map(s => s.name)];
            const vagueDescription = vagueItems.join(', ');

            if (msgContent) {
              msgContent += `\n\nFor "${vagueDescription}", here are some great options:`;
            } else {
              msgContent = `For "${vagueDescription}", here are some great options:`;
            }

            // Add the message immediately with a loading state
            const msgIndex = newMessages.length;
            setMessages([...newMessages, {
              role: 'assistant',
              content: msgContent + '\n\nFinding the best options...',
              vagueDestinationLoading: true
            }]);

            // Resolve vague destinations via LLM (async, non-blocking)
            (async () => {
              try {
                const resolveResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                  method: "POST",
                  headers: await getApiHeaders(),
                  body: JSON.stringify({
                    model: "claude-haiku-4-5-20251001",
                    max_tokens: 300,
                    system: `You suggest specific travel destinations. Return ONLY a JSON array of 3 destination names. No explanation.`,
                    messages: [{
                      role: "user",
                      content: `The user is planning a trip to ${contextDestinations.join(', ')}.${extractedOccasion ? ` This is a ${extractedOccasion} trip.` : ''}${extractedPreferences?.length > 0 ? ` They are interested in: ${extractedPreferences.join(', ')}.` : ''} They also want to visit "${vagueDescription}". Suggest 3 specific, well-known destinations that match "${vagueDescription}" and are geographically sensible given the other stops. Return JSON array of strings, e.g. ["Niseko", "Hakuba", "Nozawa Onsen"].`
                    }]
                  })
                });

                const resolveData = await resolveResponse.json();
                let suggestions = [];
                if (resolveData?.content?.[0]?.text) {
                  let text = resolveData.content[0].text.trim();
                  text = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
                  const arrMatch = text.match(/\[[\s\S]*\]/);
                  if (arrMatch) {
                    suggestions = JSON.parse(arrMatch[0]);
                  }
                }

                if (suggestions.length > 0) {
                  // Update the message with resolved suggestions as buttons
                  setMessages(prev => prev.map((m, i) => {
                    if (i === msgIndex && m.vagueDestinationLoading) {
                      return {
                        ...m,
                        content: msgContent,
                        vagueDestinationLoading: false,
                        vagueDestinationOptions: suggestions,
                        vagueDescription
                      };
                    }
                    return m;
                  }));
                } else {
                  // Fallback — just remove loading
                  setMessages(prev => prev.map((m, i) => {
                    if (i === msgIndex && m.vagueDestinationLoading) {
                      return {
                        ...m,
                        content: msgContent + '\n\nTell me a specific destination you have in mind!',
                        vagueDestinationLoading: false
                      };
                    }
                    return m;
                  }));
                }
              } catch (err) {
                debugError('Failed to resolve vague destinations:', err);
                setMessages(prev => prev.map((m, i) => {
                  if (i === msgIndex && m.vagueDestinationLoading) {
                    return {
                      ...m,
                      content: msgContent + '\n\nWhat specific destination did you have in mind?',
                      vagueDestinationLoading: false
                    };
                  }
                  return m;
                }));
              }
            })();

            return true;
          }

          // No vague items — standard message
          if (addedNames.length > 0) {
            msgContent += `\n\nYou can see them in the **Trip Builder** on the right. Feel free to:\n- Adjust days for each stop\n- Drag to reorder\n- Add more ${entityTypePlural}\n\nClick **"Create Itinerary"** when you're ready!`;
          } else {
            msgContent += `\n\nCheck the Trip Builder on the right.`;
          }

          setMessages([...newMessages, {
            role: 'assistant',
            content: msgContent
          }]);
          return true;
        }

        // ============================================
        // CORRECTION - User changing their mind (replace one city with another)
        // ============================================
        case 'correct_selection': {
          const toRemove = action.remove;
          const toAdd = action.add;

          // Remove old destination if specified
          if (toRemove) {
            const stopToRemove = tripBuilderStops.find(stop =>
              stop.name.toLowerCase().includes(toRemove.toLowerCase()) ||
              toRemove.toLowerCase().includes(stop.name.toLowerCase())
            );
            if (stopToRemove) {
              removeTripBuilderStop(stopToRemove.id); // Pass the ID, not the index!
              debugLog(`🗑️ Removed "${stopToRemove.name}" from trip builder`);
            }
          }

          // Add new destination
          if (toAdd) {
            const alreadyAdded = tripBuilderStops.some(stop =>
              stop.name.toLowerCase() === toAdd.toLowerCase()
            );

            if (!alreadyAdded) {
              addTripBuilderStop({
                name: toAdd,
                continent: '',
                days: 3,
                hotel: null
              });
            }
          }

          setMessages([...newMessages, {
            role: 'assistant',
            content: action.message || `Got it! I've replaced **${toRemove}** with **${toAdd}**.`
          }]);
          return true;
        }

        // ============================================
        // ADD TO TRIP
        // ============================================
        case 'add_destinations':
        case 'add_all_suggestions': {
          if (action.addAllSuggestions && destinationOptions?.length > 0) {
            // Add all currently suggested destinations
            const addedNames = [];
            for (const dest of destinationOptions) {
              const alreadyAdded = tripBuilderStops.some(stop =>
                stop.name.toLowerCase() === dest.name.toLowerCase()
              );
              if (!alreadyAdded) {
                addTripBuilderStop({
                  name: dest.name,
                  continent: dest.continent || '',
                  days: dest.suggestedDays || 3,
                  hotel: null
                });
                addedNames.push(dest.name);
              }
            }

            setMessages([...newMessages, {
              role: 'assistant',
              content: addedNames.length > 0
                ? `Added **${addedNames.join('**, **')}** to your trip! 🎉\n\nCheck the Trip Builder on the right. Click **"Create Itinerary"** when ready!`
                : `All those destinations are already in your trip! Check the Trip Builder on the right.`
            }]);
            return true;
          }

          // Add specific destinations
          const destinations = action.extract?.destinations || [];
          if (destinations.length > 0) {
            const addedNames = [];
            for (const destName of destinations) {
              const alreadyAdded = tripBuilderStops.some(stop =>
                stop.name.toLowerCase() === destName.toLowerCase()
              );
              if (!alreadyAdded) {
                addTripBuilderStop({ name: destName, continent: '', days: 3, hotel: null });
                addedNames.push(destName);
              }
            }

            setMessages([...newMessages, {
              role: 'assistant',
              content: action.message || `Added **${addedNames.join('**, **')}** to your trip!`
            }]);
            return true;
          }

          setMessages([...newMessages, {
            role: 'assistant',
            content: "Which destination would you like to add to your trip?"
          }]);
          return true;
        }

        // ============================================
        // REMOVE FROM TRIP
        // ============================================
        case 'remove_destinations': {
          const destinations = action.extract?.destinations || [];
          const removedNames = [];

          for (const destName of destinations) {
            const stopToRemove = tripBuilderStops.find(stop =>
              stop.name.toLowerCase().includes(destName.toLowerCase()) ||
              destName.toLowerCase().includes(stop.name.toLowerCase())
            );
            if (stopToRemove) {
              removedNames.push(stopToRemove.name);
              removeTripBuilderStop(stopToRemove.id); // Pass the ID, not the index!
            }
          }

          setMessages([...newMessages, {
            role: 'assistant',
            content: removedNames.length > 0
              ? `Removed **${removedNames.join('**, **')}** from your trip.`
              : "I couldn't find that destination in your trip. Check the Trip Builder to see your current stops."
          }]);
          return true;
        }

        // ============================================
        // RECOMMENDATION REQUEST
        // ============================================
        case 'start_recommendation_flow':
        case 'search_with_preferences': {
          // Generate destination recommendations based on user preferences
          debugLog('📋 Generating destination recommendations...');

          // Merge current action preferences with stored user profile preferences
          const actionPrefs = action.preferences || action.extract?.preferences || [];
          const storedPrefs = userProfile?.preferences?.likedActivities || [];
          const allPreferences = [...new Set([...actionPrefs, ...storedPrefs])];
          const preferencesText = allPreferences.length > 0
            ? `Looking for: ${allPreferences.join(', ')}`
            : '';

          // Build accumulated preference summary from ALL user messages in the conversation
          const userMsgsIntent = newMessages.filter(m => m.role === 'user' && typeof m.content === 'string');
          const accCriteriaIntent = [];
          const criteriaKwIntent = {
            'boutique': 'boutique/intimate', 'luxury': 'luxury', '5 star': '5-star', '5-star': '5-star',
            'five star': '5-star', 'budget': 'budget-friendly', 'center': 'city center location',
            'central': 'city center location', 'downtown': 'city center/downtown', 'beachfront': 'beachfront',
            'beach': 'beach', 'pool': 'pool', 'spa': 'spa', 'romantic': 'romantic',
            'family': 'family-friendly', 'adults only': 'adults-only', 'all inclusive': 'all-inclusive',
            'design': 'design-forward', 'modern': 'modern', 'historic': 'historic/heritage',
            'rooftop': 'rooftop', 'view': 'with views', 'intimate': 'intimate/small',
            'small': 'small/intimate', 'exclusive': 'exclusive', 'hidden gem': 'hidden gem',
            'unique': 'unique/distinctive',
          };
          for (const msg of userMsgsIntent) {
            const lower = msg.content.toLowerCase();
            for (const [kw, label] of Object.entries(criteriaKwIntent)) {
              if (lower.includes(kw) && !accCriteriaIntent.includes(label)) accCriteriaIntent.push(label);
            }
          }
          const dissatIntent = [];
          for (const msg of userMsgsIntent) {
            const lower = msg.content.toLowerCase();
            if (lower.includes('not') && lower.includes('enough')) {
              const m2 = msg.content.match(/not\s+(\w+)\s+enough/i);
              if (m2) dissatIntent.push(`User said previous suggestions were "not ${m2[1]} enough"`);
            }
            if (lower.includes('too commercial') || lower.includes('too big') || lower.includes('too large') || lower.includes('too chain'))
              dissatIntent.push('User wants to avoid commercial/chain/large properties');
            if (lower.includes('more boutique') || lower.includes('something more')) {
              const m3 = msg.content.match(/(?:more|something more)\s+(\w+)/i);
              if (m3) dissatIntent.push(`User wants MORE ${m3[1]} options`);
            }
          }
          const accCriteriaTextIntent = accCriteriaIntent.length > 0
            ? `\n🎯 ACCUMULATED USER CRITERIA (from full conversation — ALL must be satisfied):\n${accCriteriaIntent.map(c => `- ${c}`).join('\n')}${dissatIntent.length > 0 ? `\n\n⚠️ USER FEEDBACK ON PREVIOUS SUGGESTIONS:\n${dissatIntent.map(s => `- ${s}`).join('\n')}` : ''}\n`
            : '';

          // Use originCity from trip context for travel time labels + calculations
          // When user says "from Porto", labels AND distances should say "from Porto"
          // CRITICAL: Detect distance REFERENCE city separately from departure city.
          // "departing from Pinhão" = departure, "within 1 hour from Porto" = distance reference.
          const lastUserMsgIntent = newMessages.filter(m => m.role === 'user').pop()?.content || '';
          const distRefMatchIntent = lastUserMsgIntent.match(/(?:within|near|close to|around|less than|under|max).*?(?:from|of)\s+([A-Za-zÀ-ÿ][\w\sÀ-ÿ'.-]{1,30}?)(?:\s*,|\s*\.|\s+(?:and|but|to|for|in|we|i\s|my|the|that)|\s*$)/i);
          const distRefCityIntent = distRefMatchIntent ? distRefMatchIntent[1].trim() : null;
          // When recommending destinations that complement existing stops, use the LAST existing
          // stop as the reference city (e.g., Whistler drive time from Vancouver, not from home city).
          // B9 FIX: existing destinations take priority over homeCity/originCity for drive time origin,
          // because the user will physically be AT those destinations, not at their home city.
          const lastExistingStop = tripBuilderStops?.length > 0 ? tripBuilderStops[tripBuilderStops.length - 1].name : null;
          const lastCtxDest = tripContext?.destinations?.length > 0 ? tripContext.destinations[tripContext.destinations.length - 1] : null;
          const homeCity = distRefCityIntent || lastExistingStop || lastCtxDest || tripContext?.originCity || userProfile.preferences?.homeCity || '';
          // driveOrigin = city Google Maps should calculate FROM
          const driveOrigin = distRefCityIntent || lastExistingStop || lastCtxDest || tripContext?.originCity || userProfile.preferences?.homeCity || '';
          const currencySymbol = getCurrencySymbol(userProfile.preferences?.currency);
          const hasHomeCityIntent = !!homeCity;
          const flightSuffixIntent = hasHomeCityIntent ? ` • Xhr drive/flight from ${homeCity}` : '';
          const flightSuffixFlightIntent = hasHomeCityIntent ? ` • Xhr flight from ${homeCity}` : '';

          setLoadingState('searching');
          setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);

          try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
              method: "POST",
              headers: await getApiHeaders(),
              body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 2000,
                system: `You are Arrivo AI, an expert travel agent. The user is looking for destination recommendations.
${preferencesText ? `\nUser preferences: ${preferencesText}` : ''}
${tripBuilderStops?.length > 0 ? `\nAlready planned stops: ${tripBuilderStops.map(s => s.name).join(', ')}. Suggest destinations that complement these existing stops (nearby, good travel connections, etc.).` : ''}
${userProfile?.preferences?.tripOccasion ? `\nTrip occasion: ${userProfile.preferences.tripOccasion}` : ''}
${buildProfileContext()}
${accCriteriaTextIntent}
${tripType === 'hotel' ? `
🏨 HOTEL-FIRST MODE - Suggest exactly 4 specific HOTELS/RESORTS that match ALL the user's accumulated criteria above.
NEVER output "[Destination Name]", "[Hotel/Resort Name]", or any bracketed placeholder. Replace ALL placeholders with REAL hotel names.

🚨 MANDATORY FORMAT — you MUST use this EXACT numbered format. Do NOT deviate. Do NOT use unnumbered headers or plain text names:
**1. [Hotel/Resort Name]** • [Location] • ${currencySymbol}${currencySymbol}${currencySymbol} per night${flightSuffixIntent}
Brief description of the property (2-3 sentences).
**Highlights:** Key features
**Best for:** Type of traveler

**2. [Hotel/Resort Name]** • [Location] • ${currencySymbol}${currencySymbol} per night${flightSuffixIntent}
Description
**Highlights:** Key features
**Best for:** Type of traveler

**3. [Hotel/Resort Name]** • [Location] • ${currencySymbol}${currencySymbol}${currencySymbol}${currencySymbol} per night${flightSuffixIntent}
Description
**Highlights:** Key features
**Best for:** Type of traveler

**4. [Hotel/Resort Name]** • [Location] • ${currencySymbol}${currencySymbol} per night${flightSuffixIntent}
Description
**Highlights:** Key features
**Best for:** Type of traveler
` : `
🗺️ DESTINATION MODE - Suggest exactly 4 destinations that match the user's criteria.
NEVER output "[Destination Name]" or any bracketed placeholder. Replace ALL placeholders with REAL destination names.

🚨 MANDATORY FORMAT — you MUST use this EXACT numbered format. Do NOT deviate:
**1. [Destination Name]** • ${currencySymbol}${currencySymbol}${flightSuffixFlightIntent}
Brief compelling description (2-3 sentences)
**Best time to visit:** [Season/months] for [reason].

**2. [Destination Name]** • ${currencySymbol}${currencySymbol}${currencySymbol}${flightSuffixFlightIntent}
Description
**Best time to visit:** [Season/months] for [reason].

**3. [Destination Name]** • ${currencySymbol}${flightSuffixFlightIntent}
Description
**Best time to visit:** [Season/months] for [reason].

**4. [Destination Name]** • ${currencySymbol}${currencySymbol}${flightSuffixFlightIntent}
Description
**Best time to visit:** [Season/months] for [reason].
`}
${hasHomeCityIntent ? `
CRITICAL FLIGHT TIME RULES:
- From NYC: Europe 7-9hr, Japan 14-16hr, Southeast Asia 18-22hr, Caribbean 3-5hr
- From LA: Hawaii 5-6hr, Japan 11-13hr, Europe 10-12hr
- Be accurate with flight times!` : `
NOTE: The user's home city is unknown. Do NOT include flight/drive times in the recommendations.`}

🧠 CONTEXT RULES — READ CAREFULLY:
- Review the FULL conversation history. The user may have been refining their criteria across multiple messages.
- If the user previously said "boutique", "5-star", "in the center", etc., ALL subsequent suggestions MUST still match those criteria PLUS any new constraints.
- Accumulate preferences — NEVER forget earlier requirements when the user adds new ones.
- If the user expressed dissatisfaction with previous suggestions (e.g., "not boutique enough", "too commercial"), understand WHY and adjust accordingly.
- CONTEXT SWITCH: If the user changes region/destination entirely (e.g., "actually show me Asia" after discussing Europe), treat it as a fresh search. Drop previous region-specific criteria but keep general preferences (luxury, boutique, budget, etc.).

${shownDestinations.length > 0 ? `
🚫 DO NOT REPEAT — these ${tripType === 'hotel' ? 'hotels' : 'destinations'} were already shown:
${shownDestinations.map(d => `- ${d}`).join('\n')}

You MUST suggest 4 completely NEW ${tripType === 'hotel' ? 'hotels/resorts' : 'destinations'} not in the above list.
If the user has changed regions/destinations entirely, you may ignore this dedup list since they are searching in a new area.
` : ''}

End with: "**What's Next?**\\nTell me which destination interests you and I'll help create a detailed itinerary!"`,
                messages: newMessages.slice(-12).filter(m => m.content && m.content !== 'loading').map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content) }))
              })
            });

            if (!response.ok) throw new Error('API error');

            const data = await response.json();
            const rawAssistantMessage = data.content?.[0]?.text || "I'd love to help you find the perfect destination! Could you tell me more about what you're looking for?";

            // Extract destinations from the RAW response (before stripping drive times)
            // Always attempt extraction — the parser has 4 fallback patterns and handles varied formats
            let extractedDestsIntent = extractDestinationsFromMessage(rawAssistantMessage);

            // Strip LLM drive times — show placeholder until Google Maps confirms
            const hasDriveDestsIntent = extractedDestsIntent.some(d => d.driveHours != null);
            const { strippedMessage: strippedIntent } = hasDriveDestsIntent
              ? stripDriveTimesFromMessage(rawAssistantMessage)
              : { strippedMessage: rawAssistantMessage };
            const assistantMessage = strippedIntent;

            // Flag drive destinations with placeholder
            if (hasDriveDestsIntent) {
              extractedDestsIntent = extractedDestsIntent.map(d => d.driveHours != null
                ? { ...d, drivePlaceholder: true, llmDriveHours: d.driveHours, driveHours: null }
                : d
              );
            }

            if (extractedDestsIntent.length > 0) {
              setDestinationOptions(extractedDestsIntent);
              const newDestNames = extractedDestsIntent.map(d => d.name);
              setShownDestinations(prev => [...prev, ...newDestNames]);
              debugLog('📍 Extracted destinations from recommendations:', newDestNames);
            }

            // Set messages, loading, and processing state together to ensure
            // React batches them into a single render (buttons need isProcessing=false)
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: assistantMessage,
                ...(extractedDestsIntent.length > 0 ? { destinationOptions: extractedDestsIntent } : {})
              };
              return updated;
            });
            setLoadingState(null);
            setIsProcessing(false);

            // Track usage fire-and-forget AFTER UI is updated
            if (user?.id && trackUsageOnBackend) {
              trackUsageOnBackend('recommendation').then(() => {
                debugLog('✅ Destination recommendation usage tracked');
              }).catch(err => {
                debugLog('⚠️ Failed to track destination recommendation usage:', err);
              });
            }

            // Async drive time insertion — use trip origin for drive times
            if (extractedDestsIntent.length > 0 && hasDriveDestsIntent) {
              validateAndCorrectDriveTimes(extractedDestsIntent, driveOrigin, assistantMessage);
            }

            return true;
          } catch (error) {
            debugLog('❌ Recommendation generation error:', error);
            // Fall back to a helpful message
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: "I'd love to help you find the perfect destination! Could you tell me a bit more about what kind of trip you're looking for? For example:\n\n• **Beach vacation** - Where do you want to relax by the ocean?\n• **Adventure trip** - Mountains, hiking, or outdoor activities?\n• **City exploration** - Culture, food, and nightlife?\n• **Romantic getaway** - Special occasion or couples trip?\n\nJust describe what sounds appealing and I'll suggest some amazing destinations! 🌍"
              };
              return updated;
            });
            setLoadingState(null);
            return true;
          }
        }

        // ============================================
        // CONFIRMATION
        // ============================================
        case 'proceed_to_itinerary': {
          if (tripBuilderStops.length > 0) {
            setMessages([...newMessages, {
              role: 'assistant',
              content: "Great! Let me start creating your itinerary. Click the **Create Itinerary** button in the Trip Builder to begin! 🎉"
            }]);
            return true;
          }
          // Fall through
          setMessages([...newMessages, {
            role: 'assistant',
            content: "Sounds good! What destination would you like to explore?"
          }]);
          return true;
        }

        case 'execute_pending': {
          if (pendingAction?.handler) {
            debugLog('✅ Executing pending action:', pendingAction.type, pendingAction.description);
            setMessages([...newMessages, {
              role: 'assistant',
              content: action.message || `Perfect! ${pendingAction.description ? `I'll ${pendingAction.description.toLowerCase()}.` : "Let's proceed."}`
            }]);
            try {
              await pendingAction.handler();
            } catch (err) {
              debugLog('❌ Pending action failed:', err);
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  role: 'assistant',
                  content: "I ran into an issue making that change. Could you try describing what you'd like differently?"
                };
                return updated;
              });
            }
            setPendingAction(null);
          } else {
            // No pending action — treat as generic confirmation
            debugLog('⚠️ execute_pending called but no pending action stored');
            setMessages([...newMessages, {
              role: 'assistant',
              content: action.message || "Perfect! Let's proceed."
            }]);
          }
          return true;
        }

        // ============================================
        // REJECTION
        // ============================================
        case 'handle_rejection': {
          // Clear any pending action if user rejects
          if (pendingAction) {
            debugLog('🚫 Clearing pending action due to rejection:', pendingAction.type);
            setPendingAction(null);
          }
          setMessages([...newMessages, {
            role: 'assistant',
            content: action.message || "No problem! Would you like different suggestions, or do you have something else in mind?"
          }]);
          return true;
        }

        // ============================================
        // GREETING / FAREWELL
        // ============================================
        case 'greet': {
          setMessages([...newMessages, {
            role: 'assistant',
            content: action.message
          }]);
          return true;
        }

        case 'farewell': {
          setMessages([...newMessages, {
            role: 'assistant',
            content: action.message
          }]);
          return true;
        }

        // ============================================
        // QUESTION - Needs LLM response
        // ============================================
        case 'answer_question': {
          // For questions, we'll use the chat API to generate a helpful response
          setLoadingState('thinking');
          setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);

          const topic = action.topic || 'travel';
          const questionContext = tripBuilderStops.length > 0
            ? `The user is planning a trip to: ${tripBuilderStops.map(s => s.name).join(', ')}.`
            : "The user hasn't selected destinations yet.";

          try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
              method: "POST",
              headers: await getApiHeaders(),
              body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 1500,
                system: `You're a helpful travel assistant for the Arrivo app. ${questionContext}
${buildProfileContext()}
Answer the user's question helpfully and concisely. Tailor your answers to the user's profile preferences (budget, dietary needs, interests, etc.) when relevant. Use markdown formatting with **bold** for emphasis and bullet points for lists. Keep responses under 300 words unless more detail is needed.

If the question is about a specific destination, provide practical travel information.
If the question is off-topic, gently guide them back to travel planning.

Use the conversation history to understand context - the user may be asking follow-up questions about previously discussed topics.`,
                messages: newMessages.slice(-12).filter(m => m.content && m.content !== 'loading').map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content) }))
              })
            });

            const data = await response.json();
            const answerText = data.content?.[0]?.text || "I'm not sure about that. Could you tell me more about what you'd like to know?";

            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: answerText };
              return updated;
            });
          } catch (error) {
            debugLog('Error answering question:', error);
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: "I had trouble looking that up. Could you try asking in a different way?"
              };
              return updated;
            });
          }

          setLoadingState(null);
          return true;
        }

        // ============================================
        // MODIFICATION - Itinerary changes
        // ============================================
        case 'modify_itinerary': {
          // Intent-based modification handler
          // Classifies the user's modification intent and routes to the right handler
          debugLog('📝 Modification request detected via intent system');

          // If no itinerary exists, can't modify
          if (!itinerary || stage !== 'itinerary') {
            setMessages([...newMessages, {
              role: 'assistant',
              content: "I'd be happy to help modify your itinerary, but we haven't created one yet! Let me know which destination you'd like to explore and I'll build one for you."
            }]);
            return true;
          }

          const modTarget = action.target || action.extract?.target || '';
          const modAction = action.modificationAction || action.extract?.action || '';
          const modDetails = action.details || action.extract?.details || '';
          const modMessage = messageToSend;

          // INTENT-BASED MODIFICATION CATEGORIES
          // These map vague user intents to structured modification strategies
          const intentPatterns = [
            {
              pattern: /more\s*(relax|chill|calm|slow|easy|peaceful|laid[\s-]?back)/i,
              intent: 'reduce_intensity',
              systemHint: 'Make the day more relaxed: remove one high-energy activity, add leisure time or a spa/garden visit, push morning starts later, and add buffer time between activities.',
            },
            {
              pattern: /more\s*(food|culinary|eat|dining|restaurant|gastronom|foodie)/i,
              intent: 'add_food_experiences',
              systemHint: 'Add more food experiences: add a food tour, cooking class, or market visit. Replace one non-food activity with a culinary experience. Add a local street food stop.',
            },
            {
              pattern: /too\s*(pack|busy|cramp|much|full|hectic|rush|exhaust)/i,
              intent: 'reduce_activities',
              systemHint: 'Reduce the packed schedule: remove the lowest-priority activity, add 30min buffer between remaining items, and optionally combine nearby activities.',
            },
            {
              pattern: /more\s*(budget|cheap|afford|save|frugal|econom)/i,
              intent: 'reduce_budget',
              systemHint: 'Make it more budget-friendly: swap fine dining for local/street food, suggest free alternatives to paid attractions, recommend cheaper transport options.',
            },
            {
              pattern: /more\s*(adventure|thrill|excit|active|outdoor|hike|sport|adrenaline)/i,
              intent: 'add_adventure',
              systemHint: 'Add more adventure: replace calm activities with outdoor adventures, hiking, water sports, or active experiences appropriate for the destination.',
            },
            {
              pattern: /more\s*(culture|museum|histor|art|local|authentic|tradition)/i,
              intent: 'add_culture',
              systemHint: 'Add more cultural experiences: include museums, historical sites, traditional crafts, local ceremonies, or cultural workshops. Prioritize authentic local experiences over tourist traps.',
            },
            {
              pattern: /off[\s-]*(the)?[\s-]*beaten[\s-]*path|hidden\s*gem|less\s*touris|local\s*secret|undiscovered/i,
              intent: 'hidden_gems',
              systemHint: 'Make it more off-the-beaten-path: replace popular tourist spots with lesser-known local favorites, neighborhood gems, and authentic experiences that most tourists miss.',
            },
            {
              pattern: /more\s*(luxur|upscale|premium|fancy|high[\s-]*end|splurge|indulg)/i,
              intent: 'increase_luxury',
              systemHint: 'Make it more luxurious: upgrade restaurants to fine dining, suggest premium experiences, add spa treatments, recommend upscale venues.',
            },
            {
              pattern: /kid|child|famil|toddler|baby|teen/i,
              intent: 'family_friendly',
              systemHint: 'Make it family-friendly: replace bars/nightlife with kid-friendly activities, add playgrounds/parks, ensure restaurant choices work for children, add rest breaks.',
            },
          ];

          // Check if the message matches a known intent pattern
          const matchedIntent = intentPatterns.find(p => p.pattern.test(modMessage));

          if (matchedIntent) {
            debugLog(`🎯 Matched modification intent: ${matchedIntent.intent}`);

            // Store as pending action — user can confirm or the system auto-applies
            const modHandler = async () => {
              setIsModifyingItinerary(true);
              setItineraryChatMessages(prev => [...prev, { role: 'assistant', content: 'loading' }]);

              try {
                const prefs = userProfile?.preferences || {};
                const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                  method: "POST",
                  headers: await getApiHeaders(),
                  body: JSON.stringify({
                    model: "claude-sonnet-4-20250514",
                    max_tokens: 16000,
                    system: `You are modifying an itinerary for ${selectedDestination?.name || 'a trip'}.

Current itinerary:
${JSON.stringify(itinerary, null, 2)}

MODIFICATION STRATEGY: ${matchedIntent.systemHint}

Return ONLY a JSON object with this structure:
{
  "message": "Brief description of what you changed",
  "changes": [
    { "type": "update_activity"|"add_activity"|"remove_activity"|"update_meal"|"add_meal"|"remove_meal"|"update_hotel"|"regenerate_day", "dayIndex": 0, "target": "name of item to modify", "data": { ...updated fields } }
  ]
}

For "regenerate_day" specifically (used when the user wants a different CITY for the day, e.g. "move Day 4 to Brisbane" on an Australia trip):
{ "type": "regenerate_day", "dayIndex": 3, "data": { "newBaseCity": "Brisbane", "themeHint": "city explorer" }, "reason": "user wants Brisbane instead of Sydney" }

RULES:
- Make 2-4 meaningful changes (not too few, not too many)
- Keep the overall day structure and timing coherent
- Preserve activities the user hasn't mentioned wanting to change
- Match the destination's actual offerings — use real place names
- Budget: ${prefs.budget || 'moderate'}
- Dietary: ${prefs.dietaryRestrictions?.join(', ') || 'None'}`,
                    messages: [{ role: "user", content: `Apply this modification: "${modMessage}"` }]
                  })
                });

                if (!response.ok) throw new Error('Failed to modify');

                const data = await response.json();
                const responseText = data.content?.[0]?.text?.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
                const jsonMatch = responseText?.match(/\{[\s\S]*\}/);
                if (jsonMatch) {
                  const parsed = JSON.parse(jsonMatch[0]);
                  if (parsed.changes && Array.isArray(parsed.changes)) {
                    // Enrich with original data for comparison view
                    const enrichedChanges = parsed.changes.map(change => {
                      const dayIdx = change.dayIndex ?? currentDay;
                      const day = itinerary.days?.[dayIdx];
                      if (!day) return change;

                      if (change.type === 'update_activity' && change.target) {
                        const orig = day.schedule?.find(a => a.activity?.toLowerCase().includes(change.target.toLowerCase()));
                        if (orig) change.original = { ...orig };
                      } else if (change.type === 'update_meal' && change.target) {
                        const orig = day.meals?.find(m => m.restaurant?.toLowerCase().includes(change.target.toLowerCase()));
                        if (orig) change.original = { ...orig };
                      }
                      return change;
                    });

                    setPendingChanges({
                      changes: enrichedChanges,
                      message: parsed.message || `Applied ${matchedIntent.intent.replace(/_/g, ' ')} modifications`,
                      chatMessages: []
                    });
                  }
                }
              } catch (err) {
                debugLog('❌ Intent modification failed:', err);
              } finally {
                setIsModifyingItinerary(false);
                setItineraryChatMessages(prev => prev.filter(m => m.content !== 'loading'));
              }
            };

            // Store as pending action and tell user what we'll do
            setPendingAction({
              type: 'modify_itinerary',
              intent: matchedIntent.intent,
              description: `apply ${matchedIntent.intent.replace(/_/g, ' ')} changes to your itinerary`,
              handler: modHandler,
              createdAt: Date.now(),
            });

            const intentDescriptions = {
              reduce_intensity: "I'll make your itinerary more relaxed — swapping high-energy activities for leisurely ones and adding buffer time.",
              add_food_experiences: "I'll weave in more culinary experiences — food tours, local markets, and authentic dining spots.",
              reduce_activities: "I'll thin out the schedule — removing lower-priority items and adding breathing room.",
              reduce_budget: "I'll find budget-friendly alternatives — local eateries, free attractions, and cheaper transport.",
              add_adventure: "I'll pump up the adventure — adding outdoor activities, active experiences, and thrilling options.",
              add_culture: "I'll deepen the cultural immersion — museums, historical sites, and authentic local experiences.",
              hidden_gems: "I'll swap tourist hotspots for hidden gems — local favorites and lesser-known spots.",
              increase_luxury: "I'll upgrade the experience — fine dining, premium venues, and upscale activities.",
              family_friendly: "I'll make it family-friendly — kid-safe activities, family restaurants, and rest breaks.",
            };

            setMessages([...newMessages, {
              role: 'assistant',
              content: `${intentDescriptions[matchedIntent.intent] || "I'll make those changes."}\n\nShall I go ahead?`
            }]);
            return true;
          }

          // No pattern match — fall back to legacy chat-based modification
          // This handles specific requests like "replace X with Y" or complex modifications
          debugLog('📝 No intent pattern matched — falling back to itinerary chat');
          return false;
        }

        // ============================================
        // CONVERSATIONAL / UNKNOWN
        // ============================================
        case 'conversational_response':
        default: {
          // Generate a conversational response that guides back to travel
          setLoadingState('thinking');
          setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);

          const contextInfo = tripBuilderStops.length > 0
            ? `Current trip: ${tripBuilderStops.map(s => s.name).join(', ')}.`
            : "No destinations selected yet.";

          try {
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
              method: "POST",
              headers: await getApiHeaders(),
              body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 800,
                system: `You're a friendly travel assistant for Arrivo. ${contextInfo}

The user's message didn't clearly indicate a travel intent. Respond conversationally but gently guide them toward travel planning.

Guidelines:
- Be friendly and warm
- Acknowledge what they said
- Smoothly transition to asking about their travel interests
- Keep it brief (2-3 sentences)
- Use a relevant emoji
- Use conversation history for context

Examples of good responses:
- "That's interesting! 😊 Speaking of adventures, is there anywhere in the world you've been dreaming of visiting?"
- "I hear you! 🌍 When you're ready to plan a getaway, I'm here to help. Any destinations on your bucket list?"`,
                messages: newMessages.slice(-12).filter(m => m.content && m.content !== 'loading').map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content) }))
              })
            });

            const data = await response.json();
            const responseText = data.content?.[0]?.text || "I'm here to help you plan an amazing trip! Where would you like to go? 🌍";

            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = { role: 'assistant', content: responseText };
              return updated;
            });
          } catch (error) {
            debugLog('Error generating conversational response:', error);
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: "I'm here to help you plan your next adventure! What destination sounds exciting to you? 🌍"
              };
              return updated;
            });
          }

          setLoadingState(null);
          return true;
        }
      }

    } catch (error) {
      debugLog('❌ Intent handling error:', error);
      // Return false to fall back to legacy handling
      return false;
    }
  };

  // FIXED: handleSend now checks if we're in personalization phase
  const handleSend = async (customInput) => {
    const messageToSend = customInput || input;
    if (!messageToSend.trim() || isProcessing) return;
    if (isButtonDebounced('handleSend', 500)) return; // Prevent rapid submissions

    setInput('');
    setIsProcessing(true);

    const newMessages = [...messages, { role: 'user', content: messageToSend }];
    setMessages(newMessages);

    try {
      updateUserProfile(messageToSend, newMessages);

      // ============================================
      // DURATION-ONLY MESSAGE HANDLING
      // If user has a destination and just typed a duration (e.g., "10 days"),
      // update the sidebar and confirm - don't trigger AI destination generation
      // ============================================
      // Match duration-only messages:
      // "10 days", "10-day", "2 weeks", "5 nights"
      // "I want to go for 10 days", "make it 10 days", "let's do 2 weeks"
      // BUT NOT: "10 days in Paris" (has destination), "plan a 10 day trip" (has intent)
      const durationOnlyMatch = messageToSend.match(/^(?:(?:i\s+)?(?:want\s+to\s+)?(?:go|travel|stay|be\s+there|do|make\s+it|let's\s+do)\s+(?:for\s+)?)?(\d+)[\s-]*(days?|weeks?|nights?)(?:\s+(?:trip|vacation|long))?\.?$/i);
      const hasDestination = selectedDestination || (destinationOptions && destinationOptions.length > 0);

      if (durationOnlyMatch && hasDestination) {
        const unit = durationOnlyMatch[2].toLowerCase();
        let duration = parseInt(durationOnlyMatch[1]);
        if (unit.startsWith('week')) {
          duration = duration * 7;
        } else if (unit.startsWith('night')) {
          duration = duration + 1; // 5 nights = 6 days
        }
        const requested = duration;
        const MAX_TRIP_DAYS = 14;
        duration = Math.min(duration, MAX_TRIP_DAYS);
        const wasCapped = requested > MAX_TRIP_DAYS;

        debugLog(`📅 Duration-only message detected: ${duration} days - updating sidebar only`);

        // Update trip context with duration
        setTripContext(prev => ({ ...prev, duration }));

        // Add confirmation message — surface the cap explicitly when
        // user requested >14 days (previously we silently clamped and
        // the AI then echoed the user's original number, confusing them).
        const confirmMessage = {
          role: 'assistant',
          content: wasCapped
            ? `arrivo currently supports trips up to **${MAX_TRIP_DAYS} days**. I've set yours to ${MAX_TRIP_DAYS} days — for longer trips, plan multiple ${MAX_TRIP_DAYS}-day legs back-to-back.`
            : `Got it! I've updated your trip to **${duration} days**. ${duration >= 10 ? 'That gives us plenty of time to explore!' : ''}`
        };
        setMessages([...newMessages, confirmMessage]);
        setIsProcessing(false);
        return;
      }

      // Reject conversational long-trip requests up-front — same logic
      // as mobile ChatScreen so the user gets a clear cap message instead
      // of the AI generating a 21-day prose itinerary that can't be saved.
      const longTripMatch = messageToSend.match(/(\d+)\s*(?:week|wk)s?\b/i)
        || messageToSend.match(/\b(1[5-9]|[2-9]\d|\d{3,})\s*(?:days?|nights?)\b/i);
      if (longTripMatch) {
        const totalDays = (() => {
          const n = parseInt(longTripMatch[1]);
          return /week|wk/i.test(longTripMatch[0]) ? n * 7 : n;
        })();
        if (totalDays > 14) {
          const capMessage = {
            role: 'assistant',
            content: `arrivo currently supports trips up to **14 days**. For a ${totalDays}-day trip, try planning it in two legs (e.g. days 1-14 and 15-${totalDays}) — I'll help you build the first leg now. What would you like to focus on for the first 14 days?`,
          };
          setMessages([...newMessages, capMessage]);
          setIsProcessing(false);
          return;
        }
      }

      // ============================================
      // STRUCTURED EXTRACTION SYSTEM (new)
      // Falls back to old intent system or legacy if it returns false
      // ============================================
      if (USE_EXTRACTION_SYSTEM && !awaitingPersonalization) {
        debugLog('🧠 Attempting structured extraction...');
        const handled = await handleMessageWithExtraction(messageToSend, newMessages);
        if (handled) {
          debugLog('✅ Message handled by extraction system');
          setIsProcessing(false);
          return;
        }
        debugLog('⚠️ Extraction system returned false, falling back...');
      }

      // ============================================
      // OLD INTENT-BASED HANDLING (fallback)
      // ============================================
      if (USE_INTENT_SYSTEM && !USE_EXTRACTION_SYSTEM && !awaitingPersonalization) {
        debugLog('🧠 Attempting intent-based handling...');
        const handled = await handleMessageWithIntent(messageToSend, newMessages);
        if (handled) {
          debugLog('✅ Message handled by intent system');
          setIsProcessing(false);
          return;
        }
        debugLog('⚠️ Intent system returned false, falling back to legacy handling');
      }

      // ============================================
      // LEGACY HANDLING (fallback)
      // ============================================

      // Check for "tell me more" requests FIRST
      const lowerMessage = messageToSend.toLowerCase();
      if (lowerMessage.includes('tell me more') || 
          lowerMessage.includes('more about') || 
          lowerMessage.includes('learn more')) {
        
        debugLog('🔍 User asking for more info...');
        let targetDestination = null;
        
        if (destinationOptions && destinationOptions.length > 0) {
          for (let dest of destinationOptions) {
            if (lowerMessage.includes(dest.name.toLowerCase())) {
              targetDestination = dest;
              break;
            }
          }
          
          if (targetDestination) {
            debugLog('📍 Found destination:', targetDestination.name);
            
            setLoadingState('learning');
            setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);
            
            const moreInfoSystemPrompt = `You're a travel expert. The user is considering ${targetDestination.name} for their trip. 

Provide detailed, engaging information about:
1. What makes this destination special and unique
2. Best time to visit (weather, seasons, festivals)
3. Top 5 must-see attractions or experiences
4. Local culture, food, and lifestyle highlights  
5. Any important travel tips or insider knowledge

FORMAT REQUIREMENTS:
- Use ## headers with emojis (e.g., "## ⏰ Best Time to Visit", "## 🎯 Top 5 Must-Do", "## 🍛 Culture & Cuisine", "## 💡 Insider Tips")
- Use **bold** for key highlights
- Use numbered lists for the top 5
- Add relevant emojis throughout to make it engaging
- Keep it concise (max 350 words)

End with: "Ready to create your ${targetDestination.name} itinerary? Just let me know! 🎒"`;
            
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
              method: "POST",
              headers: await getApiHeaders(),
              body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 1500,
                system: moreInfoSystemPrompt,
                messages: [{ role: "user", content: `Tell me more about ${targetDestination.name}` }]
              })
            });
            
            const data = await response.json();
            if (!data?.content?.[0]?.text) {
              throw new Error('Invalid API response: missing content');
            }
            const moreInfo = data.content[0].text;

            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1] = {
                role: 'assistant',
                content: moreInfo,
                isLearnMore: true,
              };
              return updated;
            });

            setLoadingState(null);
            setIsProcessing(false);
            return;
          }
        }
        
        setMessages([...newMessages, {
          role: 'assistant',
          content: `Which destination would you like to learn more about? Just let me know and I'll share all the details! 🗺️`
        }]);
        setLoadingState(null);
        setIsProcessing(false);
        return;
      }

      // NEW: Check for multi-city requests like "I want to visit Paris, Rome, Barcelona"
      // This auto-adds cities to the trip builder
      // IMPORTANT: Only match when the user lists specific city/country names, NOT descriptions
      const multiCityPatterns = [
        // "I want to visit Paris, Rome, Barcelona" or "I want to visit Paris & Rome"
        /(?:i\s+)?want\s+to\s+(?:visit|go\s+to|see|explore)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?(?:\s*[,&]\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)+)(?:\s*!+)?$/i,
        // "planning a trip to Paris, Rome, Barcelona"
        /(?:planning|plan)\s+(?:a\s+)?trip\s+to\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?(?:\s*[,&]\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)+)(?:\s*!+)?$/i,
        // "take me to Paris, Rome, Barcelona"
        /(?:take\s+me\s+to|let'?s\s+go\s+to)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?(?:\s*[,&]\s*[A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)+)(?:\s*!+)?$/i,
        // Simple list: "Paris, Rome, Barcelona" or "Paris & Rome & Barcelona" (just city names separated by comma or &)
        // Must have at least 2 cities separated by comma or &
        // Captures the ENTIRE list in group 1 for easier parsing
        /^([A-Za-z]+(?:\s+[A-Za-z]+)?(?:\s*[,&]\s*[A-Za-z]+(?:\s+[A-Za-z]+)?)+)\s*!*$/i,
      ];

      // Words that indicate the user is describing preferences, NOT listing cities
      const preferenceKeywords = [
        'with', 'hiking', 'nature', 'outdoor', 'activities', 'beach', 'beaches',
        'mountain', 'mountains', 'adventure', 'relaxation', 'culture', 'cultural',
        'food', 'cuisine', 'nightlife', 'shopping', 'history', 'historical',
        'romantic', 'family', 'budget', 'luxury', 'warm', 'cold', 'tropical',
        'destinations', 'places', 'somewhere', 'anywhere', 'options'
      ];

      // Check if message contains preference keywords (not a city list)
      const messageWords = messageToSend.toLowerCase().split(/\s+/);
      const hasPreferenceKeywords = preferenceKeywords.some(kw => messageWords.includes(kw));

      if (!hasPreferenceKeywords) {
        // First try phrase-based patterns
        for (const pattern of multiCityPatterns) {
          const match = messageToSend.match(pattern);
          if (match && match[1]) {
            // Extract cities from the match (split by comma, &, and)
            const citiesStr = match[1].trim();
            const { text: protectedCitiesStr, placeholders: compoundPH } = protectCompoundNames(citiesStr);
            const cities = restoreCompoundNames(protectedCitiesStr
              .split(/,\s*|\s*&\s*|\s+and\s+/i), compoundPH)
              .map(c => c.trim())
              .filter(c => {
                // Must be 2+ chars and look like a city name (alphabetic with optional space)
                if (c.length < 2 || !/^[A-Za-z]+(?:\s+[A-Za-z]+)?$/.test(c)) return false;
                // Filter out common non-city words
                const nonCityWords = ['i', 'want', 'to', 'visit', 'go', 'the', 'and', 'or', 'a', 'an', 'my', 'trip'];
                return !nonCityWords.includes(c.toLowerCase());
              });

            if (cities.length >= 2) {
              debugLog('🌍 Multi-city request detected:', cities);

              // Add each city to trip builder
              cities.forEach(city => {
                const formattedCity = city.split(' ')
                  .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                  .join(' ');
                addTripBuilderStop({ name: formattedCity });
              });

              // Show confirmation message with destinationOptions for buttons
              const cityList = cities.map(c => `**${c.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ')}**`).join(', ');
              setMessages([...newMessages, {
                role: 'assistant',
                content: `I've added ${cityList} to your trip! 🗺️\n\nYou can see your stops in the **Trip Builder** on the right. Feel free to:\n- Adjust the number of days for each stop\n- Drag to reorder stops\n- Add more destinations\n\nWhen you're ready, click **"Create Itinerary"** to start planning your multi-city adventure!`,
                destinationOptions: cities.map(c => ({
                  name: c.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '),
                  description: 'Added to trip builder',
                  highlights: []
                }))
              }]);
              setLoadingState(null);
              setIsProcessing(false);
              return;
            }
          }
        }

      }

      // NEW: Check for "add them/all" to add previously suggested destinations
      // This handles: "add them", "add all", "add these", "add all of them", "yes add them"
      const addAllPattern = /^(?:(?:yes|ok|okay|sure|please)\s+)?(?:add|include)\s+(?:them|all|these|those|both|all\s+of\s+(?:them|these|those))\s*!*$/i;
      if (addAllPattern.test(messageToSend) && destinationOptions && destinationOptions.length > 0) {
        debugLog('📍 User wants to add all suggested destinations:', destinationOptions);

        // Add all destination options to trip builder
        const addedNames = [];
        destinationOptions.forEach(dest => {
          // Check if already in trip builder
          const alreadyAdded = tripBuilderStops.some(stop =>
            stop.name.toLowerCase().includes(dest.name.toLowerCase().split(' & ')[0]) ||
            dest.name.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
          );

          if (!alreadyAdded) {
            // Handle multi-destination strings like "Tokyo & Kyoto"
            if (dest.name.includes(' & ') || dest.name.includes(' and ')) {
              const { text: pName, placeholders: cPH } = protectCompoundNames(dest.name);
              const parts = restoreCompoundNames(pName.split(/\s*[&]\s*|\s+and\s+/i), cPH).map(p => p.trim()).filter(Boolean);
              parts.forEach(part => {
                addTripBuilderStop({ name: part, continent: '', days: 3, hotel: null });
                addedNames.push(part);
              });
            } else {
              addTripBuilderStop({ name: dest.name, continent: '', days: dest.suggestedDays || 3, hotel: null });
              addedNames.push(dest.name);
            }
          }
        });

        if (addedNames.length > 0) {
          const entityType = tripType === 'hotel' ? 'properties' : 'destinations';
          setMessages([...newMessages, {
            role: 'assistant',
            content: `Added **${addedNames.join('**, **')}** to your trip! 🎉\n\nYou can see your stops in the **Trip Builder** on the right. Feel free to:\n- Adjust the number of days for each stop\n- Drag to reorder stops\n- Add more ${entityType}\n\nWhen you're ready, click **"Create Itinerary"** to start planning!`
          }]);
        } else {
          setMessages([...newMessages, {
            role: 'assistant',
            content: `All those destinations are already in your trip! Check the Trip Builder on the right. You can add more destinations or click **"Create Itinerary"** when you're ready.`
          }]);
        }
        setLoadingState(null);
        setIsProcessing(false);
        return;
      }

      // NEW: Check for "add [destination]" requests to trip builder
      // This handles cases like "add costa rica", "add bali", "add costa rica!"
      const addDestinationPatterns = [
        /^add\s+(.+?)(?:\s*!+)?$/i,                    // "add costa rica" or "add costa rica!"
        /^(?:i\s+)?want\s+to\s+add\s+(.+?)(?:\s*!+)?$/i,  // "want to add bali" or "i want to add bali"
        /^(?:let'?s|please)\s+add\s+(.+?)(?:\s*!+)?$/i,   // "let's add japan" or "please add japan"
        /^include\s+(.+?)(?:\s*!+)?$/i,               // "include thailand"
      ];

      let addDestinationMatch = null;
      for (const pattern of addDestinationPatterns) {
        const match = lowerMessage.match(pattern);
        if (match && match[1]) {
          addDestinationMatch = match[1].trim();
          break;
        }
      }

      if (addDestinationMatch && addDestinationMatch.length > 1) {
        // Clean up the destination name (remove common suffixes)
        let destName = addDestinationMatch
          .replace(/\s+to\s+(?:my|the|our)\s+(?:trip|itinerary|list).*$/i, '')
          .replace(/\s+too$/i, '')
          .replace(/\s+as\s+well$/i, '')
          .trim();

        // Capitalize properly
        destName = destName.split(' ')
          .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
          .join(' ');

        // Check if it's a valid destination name (not a generic phrase or pronoun)
        const invalidPhrases = [
          'a destination', 'something', 'somewhere', 'a place', 'more', 'another', 'one more',
          // Pronouns that refer to previously mentioned destinations
          'them', 'these', 'those', 'all', 'it', 'that', 'both', 'everything',
          // Common confirmation words that might slip through
          'yes', 'ok', 'okay', 'sure', 'please', 'thanks'
        ];
        const isValidDest = destName.length >= 3 &&
                           !invalidPhrases.some(p => destName.toLowerCase() === p);

        if (isValidDest) {
          debugLog(`📍 User wants to add destination via chat: "${destName}"`);

          // =========================================================
          // SAFETY CHECKS - Block forbidden/restricted destinations
          // =========================================================
          const passportCountryRaw3 = userProfile.preferences?.passportCountry || profile?.passport_country;
          const passportCountries3 = parsePassportCountries(passportCountryRaw3);

          // Check destination-level restrictions (conflict zones, etc.)
          const destSafetyCheck = checkDestinationRestriction(destName);
          if (!destSafetyCheck.allowed) {
            debugLog(`🚫 BLOCKED destination: ${destName} - ${destSafetyCheck.message}`);
            setMessages([...newMessages, {
              role: 'assistant',
              content: `⚠️ I can't add **${destName}** to your trip.\n\n${destSafetyCheck.message}\n\nPlease choose a different destination. Would you like some alternative suggestions?`
            }]);
            setIsProcessing(false);
            return;
          }

          // Check citizenship-based restrictions for each passport country
          for (const country of passportCountries3) {
            const citizenCheck = checkCitizenshipRestriction(country, destName);
            if (!citizenCheck.allowed) {
              debugLog(`🚫 BLOCKED for ${country} passport: ${destName} - ${citizenCheck.message}`);
              setMessages([...newMessages, {
                role: 'assistant',
                content: `⚠️ I can't add **${destName}** to your trip.\n\n${citizenCheck.message}\n\nPlease choose a different destination. Would you like some alternative suggestions?`
              }]);
              setIsProcessing(false);
              return;
            }
          }

          // Build warning message if there are advisories
          let warningNote = '';
          if (destSafetyCheck.level === 'high_risk' || destSafetyCheck.level === 'elevated_risk') {
            warningNote = `\n\n**Travel Advisory:**\n⚠️ ${destSafetyCheck.message}`;
          }
          for (const country of passportCountries3) {
            const citizenCheck = checkCitizenshipRestriction(country, destName);
            if (citizenCheck.level === 'warning') {
              warningNote += `\n⚠️ ${citizenCheck.message}`;
            }
          }

          // Add to trip builder
          addTripBuilderStop({
            name: destName,
            continent: '', // Will be determined later if needed
            days: 3,       // Default days
            hotel: null
          });

          // Show confirmation message
          const entityType = tripType === 'hotel' ? 'resort' : 'destination';
          const entityTypePluralLocal = tripType === 'hotel' ? 'properties' : 'destinations';
          setMessages([...newMessages, {
            role: 'assistant',
            content: `Added **${destName}** to your trip! 🎉\n\nYou can see it in the Trip Builder on the right. Add more ${entityTypePluralLocal} or click **Create Itinerary** when you're ready!${warningNote}`
          }]);

          setIsProcessing(false);
          return;
        }
      }

      // ========================================================================
      // DYNAMIC DESTINATION DETECTION
      // Extracts and validates real destinations from ANY user message
      // Handles: single cities, multiple cities, countries, regions
      // Rejects: pronouns (them, there), generics (beach, mountain), descriptors
      // ========================================================================

      // Function to extract ALL potential destinations from a message
      const extractDestinationsFromUserMessage = (msg) => {
        const originalMessage = msg; // Keep original for context
        let cleaned = msg.trim();

        // First, check if this is a hotel recommendation request (not a specific hotel name)
        // e.g., "luxury hotel in Tokyo" - user wants suggestions, not naming a specific hotel
        if (isHotelRecommendationRequest(originalMessage)) {
          // Extract just the location from the recommendation request
          // Mark as hotelContext so downstream code doesn't add it as a destination stop
          const locationMatch = originalMessage.match(/\b(?:in|near|around|at)\s+([A-Z][a-zA-Z\s]+?)(?:\s*$|\s*[,.]|\s+(?:for|with|during))/i);
          if (locationMatch) {
            const extractedLocation = locationMatch[1].trim();
            const validation = isRealDestination(extractedLocation);
            if (validation && validation.valid) {
              return [{
                input: extractedLocation,
                name: validation.match,
                confidence: validation.confidence,
                type: validation.type,
                needsConfirmation: validation.needsConfirmation,
                hotelContext: true, // Location for hotel search, not a destination to add
              }];
            }
          }
          // If we can't extract a valid location from the recommendation request, return empty
          return [];
        }

        // Remove common prefixes that indicate intent but aren't destinations
        const prefixesToRemove = [
          /^(?:i\s+)?(?:want|would like|'d like|'d love)\s+to\s+(?:go\s+to|visit|see|explore|travel\s+to)?\s*/i,
          /^let'?s\s+(?:go\s+to|do|visit|explore|check\s+out|hit|see)?\s*/i,
          /^(?:take|bring)\s+me\s+to\s*/i,
          /^(?:i'm|we're|i\s+am|we\s+are)\s+(?:going|headed|heading)\s+to\s*/i,
          /^(?:book|plan|schedule)\s+(?:a\s+trip\s+to)?\s*/i,
          /^(?:i\s+)?(?:choose|pick|select)\s*/i,
          /^(?:yes|yeah|yep|ok|okay|sure)[,!]?\s*/i,
          /^(?:going|headed|heading)\s+(?:to|for)\s*/i,
          /^(?:how|what)\s+about\s+(?:visiting\s+)?/i,
          /^(?:thinking\s+(?:of|about))\s+/i,
          /^(?:we\s+should|should\s+we)\s+(?:go\s+to|visit)?\s*/i,
        ];

        for (const pattern of prefixesToRemove) {
          cleaned = cleaned.replace(pattern, '');
        }

        // Remove common suffixes
        const suffixesToRemove = [
          /\s+(?:please|for\s+sure|definitely|sounds?\s+(?:good|great|perfect)|is\s+perfect)\s*[!.]*$/i,
          /\s+for\s+(?:me|us|my\s+trip|our\s+trip|the\s+trip)\s*[!.]*$/i,
          /\s+(?:this|next)\s+(?:week|month|year|summer|winter|spring|fall)\s*[!.]*$/i,
          /\s+(?:would\s+be\s+(?:great|nice|perfect|awesome))\s*[!.]*$/i,
          /[!.?]+$/,
        ];

        for (const pattern of suffixesToRemove) {
          cleaned = cleaned.replace(pattern, '');
        }

        // Remove leftover action words
        cleaned = cleaned.replace(/^(?:go\s+to|visit|explore|see|travel\s+to|then)\s+/i, '');

        // Split by common separators: comma, "and", "&", "+", "then", "or", "/"
        // (protect compound names first). The `+` separator is common in
        // user prompts like "Arenal + Monteverde + Manuel Antonio" and the
        // `/` separator in "Tokyo / Kyoto / Osaka". Without these, the whole
        // concatenated string is treated as ONE destination candidate and
        // usually only the last token validates, silently dropping 2 of 3
        // stops — bug reported as "Costa Rica prompt only registered Manuel
        // Antonio for 2 days" in the skeptical-traveler review.
        const { text: protCleaned, placeholders: cleanedCPH } = protectCompoundNames(cleaned);
        const parts = restoreCompoundNames(protCleaned.split(/\s*(?:,|&|\+|\/|\band\b|\bthen\b|\bor\b)\s*/i), cleanedCPH)
          .map(p => p.trim())
          .filter(p => p.length >= 2);

        // Validate each part as a real destination
        const validDestinations = [];

        for (const part of parts) {
          // Pass original message for context (helps detect recommendation requests)
          const validation = isRealDestination(part, originalMessage);
          if (validation && validation.valid) {
            validDestinations.push({
              input: part,
              name: validation.match,
              confidence: validation.confidence,
              type: validation.type || 'location',
              needsConfirmation: validation.needsConfirmation || false
            });
          }
        }

        return validDestinations;
      };

      // Check for generic/preference indicators that mean user wants suggestions, not to go somewhere specific
      const genericRequestIndicators = [
        'recommend', 'suggest', 'ideas', 'options', 'help me find', 'looking for',
        'what should', 'where should', 'any suggestions', 'give me', 'show me',
        'best places', 'top destinations', 'good spots', 'nice places',
        'want something', 'something in', 'something for'
      ];
      const isGenericRequest = genericRequestIndicators.some(w => lowerMessage.includes(w));

      // ONLY continents should be filtered out when used with vague "something in" language
      // Regions like "Southeast Asia", "Patagonia", "Caribbean" are valid trip destinations!
      const CONTINENT_FILTERS = new Set([
        'europe', 'asia', 'africa', 'oceania', 'antarctica'
        // Note: "North America", "South America", "Central America" are kept because
        // they're rarely used as trip destinations (people say specific countries/regions)
      ]);

      // Check if the message is using continents as filters with vague language
      // "I want something in Asia" = filter (wants suggestions)
      // "7 day trip to Southeast Asia" = destination (specific trip intent)
      const isVagueRegionRequest = /\b(?:something|anywhere|somewhere)\s+(?:in|around)\s+(?:europe|asia|africa|oceania)\b/i.test(lowerMessage) ||
        (/\b(?:in|around)\s+(?:europe|asia|africa|oceania)\b/i.test(lowerMessage) &&
         /\b(?:want|looking for|recommend|suggest)\b/i.test(lowerMessage) &&
         !/\b(?:\d+\s*days?|trip to|visit|go to|travel to|explore)\b/i.test(lowerMessage));

      // Skip destination detection if user is asking for recommendations or using vague continent filters
      if (!isGenericRequest && !hasPreferenceKeywords && !isVagueRegionRequest) {
        let extractedDestinations = extractDestinationsFromUserMessage(messageToSend);

        // Filter out CONTINENT extractions when used as vague filters, not specific destinations
        // e.g., "I want something in Asia" should NOT extract "Asia"
        // BUT "7 day trip to Southeast Asia" SHOULD extract "Southeast Asia" (it's a real destination)
        if (extractedDestinations.length > 0) {
          const originalCount = extractedDestinations.length;
          extractedDestinations = extractedDestinations.filter(dest => {
            const destLower = dest.name.toLowerCase();
            // Only filter out actual continents, not regions like "Southeast Asia", "Patagonia", etc.
            if (CONTINENT_FILTERS.has(destLower)) {
              // Check if the message context suggests this is a vague filter, not a destination
              // "something in Asia" = filter; "trip to Asia" or "visit Asia" = destination intent
              const vagueFilterPattern = /\b(?:something|anywhere|somewhere)\s+(?:in|around)/i;
              const destinationIntent = new RegExp(`\\b(?:trip to|visit|go to|travel to|fly to|explore|\\d+\\s*days?\\s+(?:in|to))\\s+${destLower}\\b`, 'i');

              if (vagueFilterPattern.test(lowerMessage) && !destinationIntent.test(lowerMessage)) {
                debugLog(`🌍 Filtering out continent "${dest.name}" - used as vague filter, not destination`);
                return false;
              }
            }
            return true;
          });

          if (extractedDestinations.length < originalCount) {
            debugLog(`🌍 Filtered ${originalCount - extractedDestinations.length} continent filter(s) from destinations`);
          }
        }

        // Check for ambiguous destinations that need LLM validation
        // This handles edge cases like "I want to go to a nice hotel" where rules might fail
        if (extractedDestinations.length > 0) {
          const firstDest = extractedDestinations[0];

          // Check if this is an ambiguous case (e.g., "nice hotel" -> could be Nice city or generic request)
          if (isAmbiguousDestination(messageToSend, firstDest)) {
            debugLog(`⚠️ Ambiguous destination detected: "${firstDest.name}" - validating with LLM`);

            try {
              const llmResult = await validateDestinationWithLLM(messageToSend, firstDest.name);

              if (llmResult) {
                // LLM says this is a recommendation request, not a specific destination
                if (llmResult.isRecommendationRequest && !llmResult.isSpecificDestination) {
                  debugLog(`🤖 LLM: This is a recommendation request, not a destination`);

                  // Track: rules-based gave false positive, LLM corrected it
                  trackDestinationFeedback(
                    messageToSend,
                    firstDest.name,
                    null, // User actually wanted recommendations
                    'false_positive',
                    true // LLM was used
                  );

                  extractedDestinations = []; // Clear destinations, let it fall through to recommendation flow
                }
                // LLM found a different/corrected destination
                else if (llmResult.extractedDestination && llmResult.extractedDestination !== firstDest.name) {
                  debugLog(`🤖 LLM: Corrected destination to "${llmResult.extractedDestination}"`);

                  // Track: rules-based got wrong destination, LLM corrected it
                  trackDestinationFeedback(
                    messageToSend,
                    firstDest.name,
                    llmResult.extractedDestination,
                    'wrong_destination',
                    true // LLM was used
                  );

                  extractedDestinations = [{
                    input: llmResult.extractedDestination,
                    name: llmResult.extractedDestination,
                    confidence: llmResult.confidence || 'medium',
                    type: llmResult.destinationType || 'location',
                    needsConfirmation: llmResult.confidence === 'low',
                    llmValidated: true
                  }];
                }
                // LLM confirmed the destination but may have updated confidence
                else if (llmResult.isSpecificDestination) {
                  debugLog(`🤖 LLM: Confirmed "${firstDest.name}" as a specific destination`);
                  extractedDestinations[0].confidence = llmResult.confidence || firstDest.confidence;
                  extractedDestinations[0].needsConfirmation = llmResult.confidence === 'low';
                  extractedDestinations[0].llmValidated = true;
                }
              }
            } catch (error) {
              debugLog(`⚠️ LLM validation failed, using rules-based result: ${error.message}`);
              // Continue with rules-based result on error
            }
          }
        }

        // Hotel mode or hotel recommendation requests: don't add cities as destination stops.
        // Pass through to LLM for hotel recommendations instead.
        const isHotelMode = tripType === 'hotel';
        if (extractedDestinations.length > 0 && (extractedDestinations.every(d => d.hotelContext) || isHotelMode)) {
          const locationNames = extractedDestinations.map(d => d.name);
          debugLog(`🏨 Hotel mode — cities ${JSON.stringify(locationNames)} used as context only, not adding as stops`);
          // Store in ref for immediate access by downstream LLM calls (setTripContext is async)
          pendingHotelCitiesRef.current = locationNames;
          // Also store in tripContext for persistence across renders
          setTripContext(prev => ({
            ...prev,
            hotelSearchLocation: locationNames.join(', '),
            destinations: [...(prev.destinations || []), ...extractedDestinations.map(d => ({ name: d.name, country: d.country || '' }))],
          }));
          // Fall through to LLM to handle hotel recommendations
          extractedDestinations = [];
        }

        // Only proceed if we found at least one valid destination
        if (extractedDestinations.length > 0) {
          // Separate destinations that need confirmation from those that don't
          const confirmedDestinations = extractedDestinations.filter(d => !d.needsConfirmation);
          const unconfirmedDestinations = extractedDestinations.filter(d => d.needsConfirmation);

          // If ALL destinations need confirmation, ask the user to verify
          if (confirmedDestinations.length === 0 && unconfirmedDestinations.length > 0) {
            const destNames = unconfirmedDestinations.map(d => `"${d.name}"`).join(', ');
            const isHotel = unconfirmedDestinations.some(d => d.type === 'hotel');

            setMessages([...newMessages, {
              role: 'assistant',
              content: isHotel
                ? `I found ${destNames} but I'm not familiar with ${unconfirmedDestinations.length === 1 ? 'this property' : 'these properties'}. Could you confirm ${unconfirmedDestinations.length === 1 ? "that's" : "those are"} the correct ${unconfirmedDestinations.length === 1 ? 'name' : 'names'}?\n\nIf you're looking for hotel recommendations, just tell me the destination and I can suggest options!`
                : `I found ${destNames} but ${unconfirmedDestinations.length === 1 ? "it's" : "they're"} not in my destination database. Could you double-check the spelling or provide a more well-known nearby city/region?\n\nOr if you'd like me to help you discover destinations, just tell me what type of trip you're looking for!`
            }]);
            setLoadingState(null);
            setIsProcessing(false);
            return;
          }

          // Filter out destinations that are already in trip builder (only for confirmed ones)
          const newDestinations = confirmedDestinations.filter(dest => {
            const alreadyAdded = tripBuilderStops.some(stop =>
              stop.name.toLowerCase() === dest.name.toLowerCase() ||
              stop.name.toLowerCase().includes(dest.name.toLowerCase().split(',')[0].split(' ')[0]) ||
              dest.name.toLowerCase().includes(stop.name.toLowerCase().split(',')[0].split(' ')[0])
            );
            return !alreadyAdded;
          });

          if (newDestinations.length > 0) {
            // =========================================================
            // SAFETY CHECKS - Block forbidden/restricted destinations
            // =========================================================
            const passportCountryRaw4 = userProfile.preferences?.passportCountry || profile?.passport_country;
            const passportCountries4 = parsePassportCountries(passportCountryRaw4);
            const blockedDestinations = [];
            const warningDestinations = [];
            const safeDestinations = [];

            for (const dest of newDestinations) {
              // Check destination-level restrictions (conflict zones, etc.)
              const destSafetyCheck = checkDestinationRestriction(dest.name);
              if (!destSafetyCheck.allowed) {
                debugLog(`🚫 BLOCKED destination: ${dest.name} - ${destSafetyCheck.message}`);
                blockedDestinations.push({ ...dest, reason: destSafetyCheck.message, level: 'forbidden' });
                continue;
              }

              // Check citizenship-based restrictions for each passport country
              let destBlocked = false;
              for (const country of passportCountries4) {
                const citizenCheck = checkCitizenshipRestriction(country, dest.name);
                if (!citizenCheck.allowed) {
                  debugLog(`🚫 BLOCKED for ${country} passport: ${dest.name} - ${citizenCheck.message}`);
                  blockedDestinations.push({ ...dest, reason: citizenCheck.message, level: 'citizenship_banned' });
                  destBlocked = true;
                  break;
                }
                if (citizenCheck.level === 'warning') {
                  debugLog(`⚠️ WARNING for ${country} passport: ${dest.name}`);
                  warningDestinations.push({ ...dest, warning: citizenCheck.message });
                  // Still allow, but with warning - continue to add it
                }
              }
              if (destBlocked) continue;

              // Check for high-risk advisory
              if (destSafetyCheck.level === 'high_risk' || destSafetyCheck.level === 'elevated_risk') {
                warningDestinations.push({ ...dest, warning: destSafetyCheck.message });
              }

              safeDestinations.push(dest);
            }

            // If ALL destinations were blocked, show error message and return
            if (blockedDestinations.length > 0 && safeDestinations.length === 0) {
              const blockMessages = blockedDestinations.map(d => `• **${d.name}**: ${d.reason}`).join('\n');
              setMessages([...newMessages, {
                role: 'assistant',
                content: `⚠️ I can't add ${blockedDestinations.length === 1 ? 'this destination' : 'these destinations'} to your trip:\n\n${blockMessages}\n\nPlease choose a different destination. Would you like some alternative suggestions?`
              }]);
              setLoadingState(null);
              setIsProcessing(false);
              return;
            }

            // Add all safe destinations to trip builder
            const addedNames = [];
            for (const dest of safeDestinations) {
              debugLog(`🌍 Valid destination detected: "${dest.name}" (confidence: ${dest.confidence}, type: ${dest.type})`);
              addTripBuilderStop({
                name: dest.name,
                continent: '',
                days: 3,
                hotel: null
              });
              addedNames.push(dest.name);

              // Track successful destination detection for feedback analysis
              trackDestinationFeedback(
                messageToSend,
                dest.name,
                null, // No correction needed
                'confirmed',
                dest.llmValidated || false
              );
            }

            // Show appropriate confirmation message
            const entityType = tripType === 'hotel' ? 'property' : 'destination';
    const entityTypePlural = tripType === 'hotel' ? 'properties' : 'destinations';
            const isSingle = addedNames.length === 1;
            const destList = addedNames.map(n => `**${n}**`).join(', ');

            // Build warning notes for destinations with advisories
            let warningNote = '';
            if (warningDestinations.length > 0) {
              const warnings = warningDestinations
                .filter(d => addedNames.includes(d.name)) // Only show warnings for destinations we added
                .map(d => `⚠️ **${d.name}**: ${d.warning}`)
                .join('\n');
              if (warnings) {
                warningNote = `\n\n**Travel Advisory:**\n${warnings}`;
              }
            }

            // Note about blocked destinations (if some were added and some blocked)
            let blockedNote = '';
            if (blockedDestinations.length > 0 && addedNames.length > 0) {
              const blocked = blockedDestinations.map(d => `**${d.name}**`).join(', ');
              blockedNote = `\n\n⚠️ I couldn't add ${blocked} due to travel restrictions.`;
            }

            // If there were also unconfirmed destinations, mention them
            const unconfirmedNote = unconfirmedDestinations.length > 0
              ? `\n\n(I wasn't sure about "${unconfirmedDestinations.map(d => d.name).join('", "')}" - let me know if you want to add ${unconfirmedDestinations.length === 1 ? 'it' : 'them'} too!)`
              : '';

            setMessages([...newMessages, {
              role: 'assistant',
              content: isSingle
                ? `${destList} is a great choice! I've added it to your trip. 🎉\n\nYou can:\n- Adjust the days in the **Trip Builder** on the right\n- Add more ${entityTypePlural} by telling me or clicking destinations\n- Click **"Create Itinerary"** when you're ready!\n\nWould you like to add any other destinations, or shall we start planning?${warningNote}${blockedNote}${unconfirmedNote}`
                : `I've added ${destList} to your trip! 🗺️\n\nYou can see them in the **Trip Builder** on the right. Feel free to:\n- Adjust the number of days for each stop\n- Drag to reorder stops\n- Add more ${entityTypePlural}\n\nWhen you're ready, click **"Create Itinerary"** to start planning your adventure!${warningNote}${blockedNote}${unconfirmedNote}`
            }]);
            setLoadingState(null);
            setIsProcessing(false);
            return;
          } else if (confirmedDestinations.length > 0) {
            // All confirmed destinations were already added
            const destList = confirmedDestinations.map(d => `**${d.name}**`).join(', ');
            setMessages([...newMessages, {
              role: 'assistant',
              content: confirmedDestinations.length === 1
                ? `${destList} is already in your trip! 👍\n\nYou can see it in the Trip Builder on the right. Add more destinations or click **"Create Itinerary"** when ready!`
                : `Those destinations are already in your trip! 👍\n\nCheck the Trip Builder on the right. You can add more destinations or click **"Create Itinerary"** when you're ready.`
            }]);
            setLoadingState(null);
            setIsProcessing(false);
            return;
          }
        }
      }

      // Check for general questions (don't trigger destination selection)
      const questionPatterns = [
        'how about', 'what about', 'can you', 'do you', 'is there',
        'are there', 'should i', 'would you recommend',
        'explain', 'describe', 'why', 'when', 'where'
      ];

      const isQuestion = questionPatterns.some(pattern => lowerMessage.includes(pattern)) ||
                         lowerMessage.includes('?');
      
      // FIXED: If we're awaiting personalization and questions were asked, check if we can generate now
      if (awaitingPersonalization && personalizationQuestionsAsked && selectedDestination) {
        debugLog('📋 In personalization phase, updating profile...');
        
        // Trigger the check after state updates complete
        setShouldCheckPersonalization(true);
        return; // Let useEffect handle the rest
      }
      
      // Check for destination selection (only if not a question and not in personalization)
      if (!awaitingPersonalization && !isQuestion) {
        const destinationSelection = detectDestinationSelection(messageToSend);

        // Handle travel advisory confirmation
        if (destinationSelection?.advisoryConfirmed && destinationSelection?.destination) {
          debugLog('🚨 Advisory confirmed, proceeding with generation for:', destinationSelection.destination.name || 'destination');
          // Skip the advisory check this time by marking as confirmed
          destinationSelection.destination._advisoryConfirmed = true;
          await handleDestinationSelect(destinationSelection.destination, 0);
          setIsProcessing(false);
          return;
        }

        if (destinationSelection) {
          // NEW: Check if this needs explicit YES/NO confirmation
          if (destinationSelection.needsConfirmation) {
            // Show confirmation message with YES/NO buttons instead of auto-proceeding
            const destName = destinationSelection.destination?.name || 'your selected destination';
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `Would you like me to create an itinerary for **${destName}**?`,
              confirmationPrompt: true,
              pendingDestination: destinationSelection.destination,
              pendingIndex: destinationSelection.index
            }]);
            setIsProcessing(false);
            return;
          }
          await handleDestinationSelect(destinationSelection.destination, destinationSelection.index);
          setIsProcessing(false);
          return;
        }
        
        // Limited chat-based homeCity: only explicit "change/update/set departure to X" requests
        // Context-based detection (e.g., "wedding in Pinhão") is NOT supported — use UI instead
        const explicitHomeCityPatterns = [
          /(?:change|update|set|switch)\s+(?:my\s+)?(?:home|departure|origin|from)\s+(?:city|airport)?\s*(?:to)\s+([A-Za-zÀ-ÿ\s]+?)(?:\s*$|,|\.)/i,
          /^(?:i'm leaving|i'm departing|i am leaving|i am departing|we're leaving|we are leaving|leaving|departing)\s+from\s+([A-Za-zÀ-ÿ\s]+?)(?:\s*$|,|\.)/i,
        ];

        for (const pattern of explicitHomeCityPatterns) {
          const homeCityMatch = messageToSend.match(pattern);
          if (homeCityMatch) {
            const newHomeCity = homeCityMatch[1].trim();
            const commonWords = ['the', 'a', 'an', 'there', 'here', 'now', 'today', 'tomorrow'];
            if (newHomeCity.length > 2 && !commonWords.includes(newHomeCity.toLowerCase())) {
              debugLog(`🏠 Explicit home city change: ${userProfile.preferences.homeCity} → ${newHomeCity}`);
              const cityName = newHomeCity.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
              setUserProfile(prev => ({
                ...prev,
                preferences: { ...prev.preferences, homeCity: cityName }
              }));
              setShowHomeCityBanner(false);
              setMessages([...newMessages, {
                role: 'assistant',
                content: `Got it! I've updated your departure city to **${cityName}**. Flight times will now be calculated from there. 🏠✈️`
              }]);
              setIsProcessing(false);
              return;
            }
          }
        }

        // Check if previous message was around-the-world planning (asking for more destinations)
        const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
        if (lastAssistantMsg?.isAroundTheWorldPlanning && lastAssistantMsg?.extractedDetails) {
          debugLog('🌍 Around-the-world follow-up detected, processing additional destinations...');
          const previousDetails = lastAssistantMsg.extractedDetails;
          const firstStop = previousDetails.destination;
          const hotelName = previousDetails.hotelName;

          // User is providing additional destinations - extract them
          // Check if they're asking for suggestions or providing specific cities
          const isAskingForSuggestions = /suggest|recommend|ideas|what.*cities|beach|culture|adventure|a few|some/i.test(messageToSend);

          if (isAskingForSuggestions) {
            // User wants suggestions - use LLM to suggest cities that match their criteria
            // AND include their first stop
            try {
              const suggestionResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                method: "POST",
                headers: await getApiHeaders(),
                body: JSON.stringify({
                  model: "claude-sonnet-4-20250514",
                  max_tokens: 1500,
                  messages: [{
                    role: "user",
                    content: `The user is planning an around-the-world trip. Their first stop is ${firstStop}${hotelName ? ` staying at ${hotelName}` : ''}.

They want suggestions for additional destinations with these criteria: "${messageToSend}"

CRITICAL RULES:
1. Each stop MUST be on a DIFFERENT CONTINENT - no two stops can share the same continent
2. ${firstStop} is in Oceania (if Bora Bora/French Polynesia), Asia (if Japan/Thailand/etc.), Europe, etc. - identify its continent and NEVER suggest another destination from that same continent
3. Match their specific criteria: ${messageToSend}
4. Suggest destinations that make logical routing sense for around-the-world travel
5. DO NOT include any self-correction, revisions, or "wait, let me reconsider" text - give your best answer directly

Continent reference:
- Oceania: Australia, New Zealand, Fiji, French Polynesia (Bora Bora), Tahiti, etc.
- Asia: Japan, Thailand, Indonesia, Singapore, Vietnam, India, etc.
- Europe: France, Italy, Spain, UK, Greece, etc.
- Africa: South Africa, Morocco, Kenya, Egypt, etc.
- North America: USA, Canada, Mexico
- South America: Brazil, Argentina, Peru, Chile, etc.

Format your response EXACTLY as:
**Your around-the-world trip so far:**
• **Stop 1:** ${firstStop}${hotelName ? ` (${hotelName})` : ''} - [Continent]

**Suggested additional stops:**

**Stop 2: [City, Country]** - [Continent]
[2-3 sentences about why this fits their criteria]

**Stop 3: [City, Country]** - [Continent]
[2-3 sentences about why this fits their criteria]

**Stop 4: [City, Country]** - [Continent]
[2-3 sentences about why this fits their criteria]

End with: "Would you like to add all of these, select specific ones, or get different suggestions?"`
                  }]
                })
              });

              const suggestionData = await suggestionResponse.json();
              const suggestionText = suggestionData.content[0].text;

              setMessages([...newMessages, {
                role: 'assistant',
                content: suggestionText,
                isAroundTheWorldPlanning: true,
                extractedDetails: previousDetails,
                suggestedStops: true
              }]);
              setIsProcessing(false);
              return;
            } catch (error) {
              debugLog('Error getting around-the-world suggestions:', error);
              // Fall through to normal processing
            }
          } else {
            // User provided specific cities OR wants to add all suggestions
            const cityMatches = messageToSend.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g) || [];
            const additionalCities = cityMatches.filter(c =>
              c.length > 2 &&
              !['The', 'And', 'Also', 'Plus', 'With', 'Yes', 'Add', 'All', 'Those', 'These', 'Both', 'Sounds', 'Great', 'Perfect', 'Let'].includes(c)
            );

            // Check for "add all" or "yes" type responses
            const wantsAllSuggestions = /add all|yes|all of (them|these|those)|sounds (good|great|perfect)|let's do it|add these/i.test(messageToSend);

            if (additionalCities.length > 0 || wantsAllSuggestions) {
              if (wantsAllSuggestions && lastAssistantMsg.suggestedStops) {
                // Extract suggested stops from the previous message
                const suggestedMatches = lastAssistantMsg.content.match(/\*\*Stop \d+: ([^*]+)\*\*/g) || [];
                const suggestedCities = suggestedMatches
                  .map(m => {
                    const match = m.match(/\*\*Stop \d+: ([^*]+)\*\*/);
                    return match ? match[1].split(' - ')[0].trim() : null;
                  })
                  .filter(c => c && c !== firstStop && !c.includes(firstStop.split(',')[0]));
                additionalCities.push(...suggestedCities);
              }

              if (additionalCities.length > 0) {
                // Filter out duplicates - if user included firstStop again, don't duplicate it
                const firstStopLower = firstStop.toLowerCase().split(',')[0].trim();
                const uniqueAdditionalCities = additionalCities.filter(c =>
                  c.toLowerCase().split(',')[0].trim() !== firstStopLower
                );
                const allStops = [firstStop, ...uniqueAdditionalCities].join(' & ');
                const formattedDestination = formatDestinationWithCountries(allStops, tripType === 'hotel');

                setMessages([...newMessages, {
                  role: 'assistant',
                  content: `Your around-the-world adventure is set! 🌍✈️

**Your stops:**
${formattedDestination.split(' & ').map((stop, i) => `${i + 1}. **${stop}**`).join('\n')}
${hotelName ? `\n*Staying at ${hotelName} in ${firstStop}*` : ''}

This is going to be an incredible journey! Ready to create your detailed itinerary?`,
                  isSmartDetection: true,
                  extractedDetails: {
                    ...previousDetails,
                    destination: formattedDestination,
                    tripType: 'around-the-world'
                  },
                  destinationOptions: [{
                    name: formattedDestination,
                    description: `Around the world: ${formattedDestination.split(' & ').length} stops`,
                    flightHours: 20,
                    highlights: ['World travel', 'Multiple continents'],
                    tripType: 'around-the-world'
                  }]
                }]);
                setIsProcessing(false);
                return;
              }
            }
          }
        }

        // Check if previous message was multi-destination planning (asking for more destinations/properties)
        if (lastAssistantMsg?.isMultiDestinationPlanning && lastAssistantMsg?.extractedDetails) {
          debugLog('🗺️ Multi-destination follow-up detected, processing additional stops...');
          const previousDetails = lastAssistantMsg.extractedDetails;
          const firstStop = previousDetails.destination || previousDetails.hotelName;
          const isHotelTrip = tripType === 'hotel';

          // User is providing additional destinations - extract them
          // Check if they're asking for suggestions or providing specific places
          const isAskingForSuggestions = /suggest|recommend|ideas|what|where|a few|some|help me|not sure/i.test(messageToSend);

          if (isAskingForSuggestions) {
            // User wants suggestions - use LLM to suggest destinations/properties
            try {
              const promptContent = isHotelTrip
                ? `The user is planning a multi-property trip. Their first property is ${firstStop}.

They want suggestions for additional properties/resorts with these criteria: "${messageToSend}"

Suggest 2-3 properties/resorts that:
1. Offer different experiences from ${firstStop}
2. Match their criteria (${messageToSend})
3. Would complement their trip (different vibes, locations, or experiences)

Consider various types of multi-property trips:
- Wellness journey (different spa experiences)
- Honeymoon/romantic (beach + city luxury)
- Family resort hopping (different kid-friendly experiences)
- Adventure + relaxation combo

Format your response as:
**Your multi-property trip so far:**
• **Property 1:** ${firstStop}

**Suggested additions:**

**Property 2: [Resort Name]** - [Location]
[2-3 sentences about why this fits and what makes it special]

**Property 3: [Resort Name]** - [Location]
[2-3 sentences about why this fits and what makes it special]

End with: "Would you like to add these, pick specific ones, or get different suggestions?"`
                : `The user is planning a multi-destination trip. Their first stop is ${firstStop}.

They want suggestions for additional destinations with these criteria: "${messageToSend}"

Suggest 2-3 destinations that:
1. Complement ${firstStop} well (logistically or thematically)
2. Match their criteria (${messageToSend})
3. Can be combined in a practical itinerary

Consider various multi-destination trip styles:
- Regional exploration (e.g., Japan rail trip, European city hopping)
- Contrast experiences (e.g., city + beach, culture + relaxation)
- Bucket list combinations (e.g., Machu Picchu + Galapagos)
- Food/wine trails (e.g., Tuscany + Provence)
- Adventure circuits (e.g., Patagonia + Peru)

Format your response as:
**Your multi-destination trip so far:**
• **Stop 1:** ${firstStop}

**Suggested additions:**

**Stop 2: [City/Region, Country]**
[2-3 sentences about why this pairs well with ${firstStop.split(',')[0]}]

**Stop 3: [City/Region, Country]**
[2-3 sentences about why this fits their criteria]

End with: "Would you like to add these, pick specific ones, or get different suggestions?"`;

              const suggestionResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                method: "POST",
                headers: await getApiHeaders(),
                body: JSON.stringify({
                  model: "claude-sonnet-4-20250514",
                  max_tokens: 1500,
                  messages: [{
                    role: "user",
                    content: promptContent
                  }]
                })
              });

              const suggestionData = await suggestionResponse.json();
              const suggestionText = suggestionData.content[0].text;

              setMessages([...newMessages, {
                role: 'assistant',
                content: suggestionText,
                isMultiDestinationPlanning: true,
                extractedDetails: previousDetails,
                suggestedStops: true
              }]);
              setIsProcessing(false);
              return;
            } catch (error) {
              debugLog('Error getting multi-destination suggestions:', error);
              // Fall through to normal processing
            }
          } else {
            // User provided specific places - extract them and combine with first stop
            const placeMatches = messageToSend.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g) || [];
            const additionalPlaces = placeMatches.filter(p =>
              p.length > 2 &&
              !['The', 'And', 'Also', 'Plus', 'With', 'Yes', 'Add', 'All', 'Those', 'These', 'Both'].includes(p)
            );

            // Also check for "add all" or "yes" type responses to suggestions
            const wantsAllSuggestions = /add all|yes|all of (them|these|those)|sounds (good|great|perfect)|let's do it/i.test(messageToSend);

            if (additionalPlaces.length > 0 || wantsAllSuggestions) {
              if (wantsAllSuggestions && lastAssistantMsg.suggestedStops) {
                // They want the suggested stops - extract from previous message
                const suggestedMatches = lastAssistantMsg.content.match(/\*\*(?:Stop|Property) \d+: ([^*]+)\*\*/g) || [];
                const suggestedPlaces = suggestedMatches
                  .map(m => m.replace(/\*\*(?:Stop|Property) \d+: ([^*]+)\*\*/, '$1').trim())
                  .filter(p => p !== firstStop);
                additionalPlaces.push(...suggestedPlaces);
              }

              const allStops = [firstStop, ...additionalPlaces].join(' & ');
              const tripTypeValue = isHotelTrip ? 'resort-hopping' : 'multi-city';

              setMessages([...newMessages, {
                role: 'assistant',
                content: isHotelTrip
                  ? `Your resort-hopping adventure is set! 🏨✨

**Your properties:**
${[firstStop, ...additionalPlaces].map((stop, i) => `${i + 1}. **${stop}**`).join('\n')}

This is going to be an incredible journey! Ready to create your detailed itinerary?`
                  : `Your multi-destination trip is shaping up! 🗺️✈️

**Your stops:**
${[firstStop, ...additionalPlaces].map((stop, i) => `${i + 1}. **${stop}**`).join('\n')}

This looks like an amazing adventure! Ready to create your detailed itinerary?`,
                isSmartDetection: true,
                extractedDetails: {
                  ...previousDetails,
                  destination: allStops,
                  tripType: tripTypeValue
                },
                destinationOptions: [{
                  name: allStops,
                  description: isHotelTrip
                    ? `Resort hopping: ${[firstStop, ...additionalPlaces].length} properties`
                    : `Multi-city: ${[firstStop, ...additionalPlaces].length} destinations`,
                  flightHours: 10,
                  highlights: isHotelTrip ? ['Luxury resorts', 'Multiple properties'] : ['Multiple cities', 'Diverse experiences'],
                  tripType: tripTypeValue
                }]
              }]);
              setIsProcessing(false);
              return;
            }
          }
        }

        // SMART DESTINATION DETECTION using LLM
        // Check if message looks like a trip request
        const tripIndicators = /(?:want to|going to|visit|trip to|travel to|thinking about|planning|heading to|go to)/i;
        
        // Check if this is a GENERIC trip request (no specific destination mentioned)
        // These should skip extraction and go straight to AI for destination suggestions
        const genericTripPatterns = [
          /beach\s*(vacation|trip|getaway|paradise)/i,
          /romantic\s*(trip|getaway|vacation)/i,
          /adventure\s*(trip|vacation)/i,
          /food\s*(and|&)\s*culture/i,
          /relaxing\s*(vacation|trip|getaway)/i,
          /amazing\s*sunsets/i,
          /beautiful\s*scenery/i,
          /outdoor\s*activities/i,
          /hiking\s*(trip|adventure)/i,
          /incredible\s*food/i,
          /rich\s*culture/i,
          // Accommodation-focused requests (not destination-focused)
          /luxury\s*(spa|hotel|resort|retreat)/i,
          /\b(spa|hotel|resort)\s*(retreat|getaway|vacation|trip)?$/i,
          /5\s*star\s*(hotel|resort|spa)/i,
          /five\s*star\s*(hotel|resort|spa)/i,
          /boutique\s*(hotel|resort|spa)/i,
        ];
        
        const isGenericTripRequest = genericTripPatterns.some(pattern => pattern.test(messageToSend));
        
        // Only try extraction if it's NOT a generic request (i.e., might have a real destination)
        if (tripIndicators.test(messageToSend) && !isGenericTripRequest) {
          debugLog('🎯 Detected trip request with potential destination, using LLM for smart extraction...');
          
          try {
            // Use LLM to intelligently extract trip details
            const extractionResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
              method: "POST",
              headers: await getApiHeaders(),
              body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 500,
                messages: [{
                  role: "user",
                  content: `Extract travel details from this message. Return ONLY a JSON object, no other text.

Message: "${messageToSend}"
${tripType === 'hotel' ? '\n⚠️ CONTEXT: User is in HOTEL/RESORT MODE - they selected "Stay at a Hotel/Resort" at the start. ALL stops they mention should be treated as property names, even if they look like city/country names.' : ''}

Extract:
- destination: The SPECIFIC place(s) they want to visit.
  * If user mentions MULTIPLE destinations, join them with " & " (e.g., "Tokyo & Bali", "Italy & Hotel Georges V & The Peninsula London")
  * Return null if they only mention a TYPE of trip without naming a specific place
  * PRESERVE exact names as the user typed them - do NOT modify hotel/resort names
  ${tripType === 'hotel' ? '* In HOTEL MODE: Accept ANY name as a valid destination (user is naming properties/hotels)' : '* Must be real city, country, region, or hotel/resort names'}
- destinationTypes: For EACH destination in the destination field, classify it as "hotel" or "location". Return as comma-separated list matching the order of destinations.
  * Example: destination "Italy & Hotel Georges V & The Peninsula London" → destinationTypes "location, hotel, hotel"
  * Example: destination "Aman Utah & Aman Venice" → destinationTypes "hotel, hotel"
  * Example: destination "Paris & Rome" → destinationTypes "location, location"
  * Use "hotel" for: specific hotel/resort names, branded properties (Four Seasons, Aman, Ritz, Peninsula, etc.), anything with "Hotel", "Resort", "Inn" in name
  * Use "location" for: cities, countries, regions, islands
  ${tripType === 'hotel' ? '* In HOTEL MODE: Default to "hotel" unless it\'s clearly a country/city name' : ''}
- duration: How long (e.g., "weekend", "week", "5 days", or null if not mentioned)
- companion: Who they're traveling with (e.g., "husband", "family", "friends", or null)
- timing: When they want to go (e.g., "January", "spring", "next month", or null)
- interests: What they want to do/see (e.g., "beach", "relaxation", "sunsets", "food", "museums", "adventure", "city exploration in Asia, beach relaxation in Central America", or null)
- homeCity: Where they're DEPARTING FROM / traveling from (e.g., "NYC", "London", or null if not mentioned)
  * CRITICAL: This is their HOME city, NOT a destination city!
  * Look for phrases like "from NYC", "departing from London", "leaving from Chicago", "based in Paris"
  * WRONG: Extracting "Tokyo" as homeCity when user says "trip to Tokyo" or "stop in Tokyo" - that's a DESTINATION
  * If user mentions a city as a STOP or DESTINATION, do NOT put it in homeCity
  * Return null if they don't explicitly mention where they're traveling FROM
- budget: Budget level - "luxury", "moderate", or "budget" (null if not mentioned)
  Luxury signals: "luxury", "5-star", "splurge", "money is no object", "honeymoon", "treat ourselves", "special occasion", "fancy", "high-end"
  Budget signals: "budget", "cheap", "affordable", "backpacking", "broke", "saving money", "hostel", "frugal"
  Moderate signals: "mid-range", "reasonable", "balanced", "not too expensive"
- hotelName: The SPECIFIC hotel, resort, or property name if mentioned (e.g., "Miraval Berkshires", "Four Seasons Bora Bora", "Ritz Carlton", or null)
  * If user says "stay at X" or "book X" or "want to go to X resort", extract the hotel/resort name
  * ONLY extract actual hotel/resort names, not general descriptions
  * If user mentions MULTIPLE hotels/resorts as destinations (e.g., "Aman Utah and Aman Venice"), set hotelName to the FIRST hotel
  * IMPORTANT: Luxury hotel brands include: Aman, Amangiri, Four Seasons, Ritz Carlton, St Regis, Mandarin Oriental, Rosewood, One&Only, Six Senses, Park Hyatt, Belmond, Soneva, etc.
  * If destination looks like hotel names (e.g., "Aman Utah", "Four Seasons Maui"), those ARE hotels and should be reflected in hotelName and tripType
- transportMode: How they want to travel - "drive" or "fly" (null if not mentioned)
  * Drive signals: "driving", "drive", "road trip", "by car", "rent a car"
  * Fly signals: "flying", "fly", "flight", "take a plane"
  * If destination is far (international or cross-country), default to "fly" unless they specifically say driving
  * If destination is within ~5 hours drive and they don't specify, leave as null
- tripType: The type of trip based on what they want - one of: "single", "multi-city", "resort-hopping", "hybrid", "around-the-world" (null if not clear)
  * "around-the-world": They explicitly say "around the world", "world trip", "multiple continents", or want to visit 4+ very distant destinations
  * "multi-city": They mention multiple cities/countries to visit (e.g., "Paris and Rome", "Tokyo, Kyoto, Osaka")
  * "resort-hopping": They mention MULTIPLE luxury resorts/hotels (e.g., "Aman Utah and Aman Venice", "Four Seasons Bali then Soneva Fushi") - MUST be set when destination contains hotel names
  * "hybrid": Mix of cities AND resorts (e.g., "stay at Four Seasons but also visit nearby cities")
  * "single": Just one destination or resort (including single hotel like "Aman Utah")
  * IMPORTANT: If ALL destinations are hotels/resorts (not cities), use "resort-hopping" for multiple or "single" for one
  * If unclear or not specified, return null

🚨 AROUND-THE-WORLD TRIPS - SPECIAL HANDLING:
- If user says "around the world trip" but only mentions ONE specific stop (e.g., "with a stop at Aman Tokyo"):
  * Set tripType: "around-the-world"
  * Set destination to JUST that mentioned stop (e.g., "Aman Tokyo, Japan") - we'll ask for more stops later
  * Set interests to include "around the world trip" so we know they want more destinations
- If user mentions MULTIPLE stops for around-the-world, include ALL of them in destination joined with " & "

CRITICAL: If the user says something like "I want a beach vacation" or "a city destination in asia" without naming a specific place, return destination: null and interests: "beach, relaxation" or "city exploration, Asia". Do NOT use trip descriptions as destination names.

Return ONLY valid JSON like: {"destination":"Paris","destinationTypes":"location","duration":"weekend","companion":"husband","timing":null,"interests":"wedding shopping","homeCity":"NYC","budget":"moderate","hotelName":null,"transportMode":"fly","tripType":"single"}
Or for multiple cities: {"destination":"Kyoto & Puerto Vallarta","destinationTypes":"location, location","duration":"10 days","companion":null,"timing":null,"interests":"city culture, beach relaxation","homeCity":"NYC","budget":"moderate","hotelName":null,"transportMode":"fly","tripType":"multi-city"}
Or for resort-hopping: {"destination":"Aman Utah & Aman Venice","destinationTypes":"hotel, hotel","duration":"10 days","companion":null,"timing":null,"interests":"luxury resorts, relaxation","homeCity":"NYC","budget":"luxury","hotelName":"Aman Utah","transportMode":"fly","tripType":"resort-hopping"}
Or for MIXED trip (cities + hotels): {"destination":"Italy & Hotel Georges V & The Peninsula London","destinationTypes":"location, hotel, hotel","duration":"2 weeks","companion":null,"timing":null,"interests":"culture, luxury stays","homeCity":"NYC","budget":"luxury","hotelName":"Hotel Georges V","transportMode":"fly","tripType":"hybrid"}
Or for around the world: {"destination":"Tokyo & Paris & New York & Cape Town","destinationTypes":"location, location, location, location","duration":"3 weeks","companion":null,"timing":null,"interests":"world exploration, diverse cultures","homeCity":"NYC","budget":"luxury","hotelName":null,"transportMode":"fly","tripType":"around-the-world"}
Or if no specific place: {"destination":null,"destinationTypes":null,"duration":null,"companion":null,"timing":null,"interests":"beach, relaxation, sunsets","homeCity":null,"budget":null,"hotelName":null,"transportMode":null,"tripType":null}`
                }]
              })
            });
            
            const extractionData = await extractionResponse.json();
            const extractionText = extractionData.content[0].text.trim();
            
            // Parse the JSON response
            const jsonMatch = extractionText.match(/\{[\s\S]*\}/);
            if (jsonMatch) {
              const extractedDetails = JSON.parse(jsonMatch[0]);
              debugLog('🎯 LLM extracted details:', extractedDetails);
              
              // Update home city if detected - but validate it's not a destination city!
              if (extractedDetails.homeCity && !userProfile.preferences.homeCity) {
                const homeCityLower = extractedDetails.homeCity.toLowerCase();
                const destLower = (extractedDetails.destination || '').toLowerCase();
                const hotelLower = (extractedDetails.hotelName || '').toLowerCase();

                // Don't set homeCity if it matches the destination or hotel location
                const homeCityIsDestination = destLower.includes(homeCityLower) ||
                                               homeCityLower.includes(destLower.split(',')[0]) ||
                                               hotelLower.includes(homeCityLower);

                if (!homeCityIsDestination) {
                  const cityName = extractedDetails.homeCity.toUpperCase() === 'NYC' ? 'NYC' :
                                  extractedDetails.homeCity.toUpperCase() === 'LA' ? 'Los Angeles' :
                                  extractedDetails.homeCity.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                  setUserProfile(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, homeCity: cityName }
                  }));
                  setShowHomeCityBanner(false);
                  setHomeCityAsked(true);
                } else {
                  debugLog('⚠️ Ignoring homeCity extraction - it matches destination:', extractedDetails.homeCity);
                }
              }
              
              // Count how many details we have (excluding homeCity)
              const detailCount = ['destination', 'duration', 'companion', 'timing', 'interests', 'budget']
                .filter(key => extractedDetails[key]).length;
              
              // Check if this is a famous/specific destination that should skip suggestions
              const isFamousDestination = (dest) => {
                if (!dest) return false;
                const destLower = dest.toLowerCase();
                const famousPlaces = [
                  'kruger', 'serengeti', 'masai mara', 'yellowstone', 'grand canyon', 
                  'machu picchu', 'petra', 'angkor wat', 'taj mahal', 'great wall',
                  'galapagos', 'amazon', 'patagonia', 'maldives', 'bora bora',
                  'santorini', 'amalfi', 'cinque terre', 'swiss alps', 'mont blanc',
                  'northern lights', 'aurora', 'safari', 'napa valley', 'tuscany'
                ];
                return famousPlaces.some(place => destLower.includes(place));
              };
              
              // Check for around-the-world trip with only one stop mentioned
              // In this case, we should ask for more destinations
              const isAroundTheWorldWithOneStop = extractedDetails.tripType === 'around-the-world' &&
                extractedDetails.destination &&
                !extractedDetails.destination.includes('&') &&
                !extractedDetails.destination.includes(',');

              if (isAroundTheWorldWithOneStop) {
                // Ask user for more destinations for their around-the-world trip
                const firstStop = extractedDetails.destination;
                const hotelMention = extractedDetails.hotelName ? ` at **${extractedDetails.hotelName}**` : '';

                const askForMoreStops = `I love that you want to do an around-the-world trip${hotelMention ? ` with a stop${hotelMention} in ${firstStop}` : ` including ${firstStop}`}! 🌍✈️

To plan your epic journey, I need to know your other destinations. An around-the-world trip typically includes **3-5 stops** across different continents.

**Your first stop:** ${firstStop}${hotelMention}

What other cities or regions would you like to visit? For example:
• **Europe:** Paris, Rome, Barcelona, London
• **Asia:** Bali, Singapore, Bangkok
• **Africa:** Cape Town, Marrakech
• **South America:** Rio, Buenos Aires
• **Oceania:** Sydney, Auckland

Just tell me your dream destinations and I'll craft an amazing itinerary! 🗺️`;

                setMessages([...newMessages, {
                  role: 'assistant',
                  content: askForMoreStops,
                  isAroundTheWorldPlanning: true,
                  extractedDetails: extractedDetails
                }]);
                setIsProcessing(false);
                return;
              }

              // Check if user selected "Multi-Destination" but only provided one destination
              const hasOnlyOneDestination = extractedDetails.destination &&
                !extractedDetails.destination.includes('&') &&
                !extractedDetails.destination.includes(' and ');

              if (tripScope === 'multi' && hasOnlyOneDestination) {
                const firstStop = extractedDetails.destination;
                const hotelMention = extractedDetails.hotelName ? ` (staying at **${extractedDetails.hotelName}**)` : '';
                const isHotelTrip = tripType === 'hotel';

                const askForMoreStops = isHotelTrip
                  ? `Great choice starting with **${firstStop}**${hotelMention}! 🏨✨

**Your first property:** ${firstStop}${hotelMention}

Where else would you like to stay? You can:
• Name specific properties you've dreamed about
• Describe what experience you want next (e.g., "beach resort", "mountain lodge", "city hotel")
• Tell me a vibe (e.g., "something more adventurous", "ultra-relaxing spa")
• Ask me to suggest properties that complement ${firstStop.split(',')[0]}

What sounds good for your next stop?`
                  : `Great choice starting with **${firstStop}**${hotelMention}! 🗺️

**Your first stop:** ${firstStop}${hotelMention}

What other places interest you? You can:
• Name specific cities or regions (e.g., "Rome", "the Amalfi Coast", "Kyoto")
• Describe what you're looking for (e.g., "a beach", "great food scene", "hiking")
• Tell me a style (e.g., "keep it in Japan", "add something tropical", "European cities")
• Ask me to suggest destinations that pair well with ${firstStop.split(',')[0]}

What sounds exciting for your next destination?`;

                setMessages([...newMessages, {
                  role: 'assistant',
                  content: askForMoreStops,
                  isMultiDestinationPlanning: true,
                  extractedDetails: extractedDetails
                }]);
                setIsProcessing(false);
                return;
              }

              // Skip directly to itinerary for famous destinations (even with just 1 detail)
              // Or when we have 3+ details for any destination
              const shouldSkipSuggestions = (extractedDetails.destination && isValidDestinationName(extractedDetails.destination)) &&
                (isFamousDestination(extractedDetails.destination) || detailCount >= 3);

              // CRITICAL: Only proceed if we have a VALID destination name (not a description)
              if (shouldSkipSuggestions) {
                // Format destination with country names early (e.g., "Kyoto & Puerto Vallarta" -> "Kyoto, Japan & Puerto Vallarta, Mexico")
                // For hotel trips, preserve hotel names as-is
                // Use destinationTypes from LLM for mixed trips (e.g., "Italy & Hotel Georges V")
                const formattedDestination = formatDestinationWithCountries(
                  extractedDetails.destination,
                  tripType === 'hotel',
                  extractedDetails.destinationTypes
                );

                // We have enough details - show confirmation with 3 options
                const detailsList = [];
                detailsList.push(`**Destination:** ${formattedDestination}`);
                
                // Calculate duration days for preferences
                let durationDays = null;
                if (extractedDetails.duration) {
                  const durLower = extractedDetails.duration.toLowerCase();
                  const durationText = durLower.includes('weekend') ? 'Weekend (2-3 days)' :
                                       durLower.includes('week') && !durLower.includes('weekend') ? '1 week' :
                                       durLower.includes('long weekend') ? 'Long weekend (3-4 days)' :
                                       durLower.includes('few days') ? '3-4 days' :
                                       extractedDetails.duration;
                  detailsList.push(`**Duration:** ${durationText}`);
                  
                  // Convert to days number
                  durationDays = durLower.includes('weekend') ? 3 :
                                 durLower.includes('week') && !durLower.includes('weekend') ? 7 :
                                 durLower.includes('long weekend') ? 4 :
                                 durLower.includes('few days') ? 4 :
                                 parseInt(extractedDetails.duration) || null;
                }
                if (extractedDetails.timing) detailsList.push(`**When:** ${extractedDetails.timing.charAt(0).toUpperCase() + extractedDetails.timing.slice(1)}`);
                if (extractedDetails.companion) detailsList.push(`**Traveling with:** ${extractedDetails.companion.charAt(0).toUpperCase() + extractedDetails.companion.slice(1)}`);
                if (extractedDetails.interests) detailsList.push(`**Interests:** ${extractedDetails.interests}`);
                if (extractedDetails.budget) {
                  const budgetDisplay = extractedDetails.budget === 'luxury' ? 'Luxury 💎' :
                                        extractedDetails.budget === 'budget' ? 'Budget-friendly 💰' :
                                        'Moderate';
                  detailsList.push(`**Budget:** ${budgetDisplay}`);
                }
                
                // Convert timing to month for preferences
                const monthMap = {
                  'january': 'January', 'february': 'February', 'march': 'March', 'april': 'April',
                  'may': 'May', 'june': 'June', 'july': 'July', 'august': 'August',
                  'september': 'September', 'october': 'October', 'november': 'November', 'december': 'December',
                  'spring': 'March', 'summer': 'June', 'fall': 'October', 'autumn': 'October', 'winter': 'December'
                };
                const travelMonth = extractedDetails.timing ? monthMap[extractedDetails.timing.toLowerCase()] : null;
                
                // Convert budget to budget tier for preferences
                const budgetTierMap = {
                  'luxury': '$$$$$',
                  'moderate': '$$$',
                  'budget': '$'
                };
                const budgetTier = extractedDetails.budget ? budgetTierMap[extractedDetails.budget.toLowerCase()] : null;
                
                // If hotel name is specified OR tripType is resort-hopping, set trip type to hotel-first
                const isHotelTrip = extractedDetails.hotelName || extractedDetails.tripType === 'resort-hopping';
                if (isHotelTrip) {
                  setTripType('hotel');
                  // For resort-hopping, use the first hotel from destination if no explicit hotelName
                  const hotelName = extractedDetails.hotelName ||
                    (extractedDetails.destination?.split(/\s*&\s*/)[0]) || null;
                  if (hotelName) {
                    setSelectedHotel({
                      name: hotelName,
                      pricePerNight: 350, // Default, can be updated later
                      amenities: ['Spa', 'Pool', 'Fitness Center']
                    });
                  }
                }
                
                // If transport mode is specified, set it
                if (extractedDetails.transportMode) {
                  setTransportationMode(extractedDetails.transportMode);
                }
                
                // Store extracted details in state AND apply to preferences
                setUserProfile(prev => ({
                  ...prev,
                  preferences: {
                    ...prev.preferences,
                    pendingDestination: extractedDetails,
                    // Pre-fill form fields
                    ...(durationDays && { duration: durationDays }),
                    ...(travelMonth && { travelMonths: [travelMonth], travelPeriod: travelMonth }),
                    ...(extractedDetails.companion && { travelCompanions: extractedDetails.companion }),
                    ...(budgetTier && { budget: budgetTier })
                  }
                }));
                
                // Build confirmation message with all details including hotel and transport
                if (extractedDetails.hotelName) {
                  detailsList.push(`**Hotel:** ${extractedDetails.hotelName} 🏨`);
                }
                if (extractedDetails.transportMode) {
                  const transportDisplay = extractedDetails.transportMode === 'drive' ? 'Driving 🚗' : 'Flying ✈️';
                  detailsList.push(`**Getting there:** ${transportDisplay}`);
                }

                // Display trip type for multi-city or special trips
                if (extractedDetails.tripType && extractedDetails.tripType !== 'single') {
                  const tripTypeDisplay = {
                    'multi-city': 'Multi-City Adventure 🗺️',
                    'resort-hopping': 'Resort Hopping 🏖️',
                    'hybrid': 'City + Resort Combo ✨',
                    'around-the-world': 'Around the World 🌍'
                  };
                  detailsList.push(`**Trip Style:** ${tripTypeDisplay[extractedDetails.tripType] || extractedDetails.tripType}`);
                }

                const confirmationMessage = `${formattedDestination} sounds wonderful! 🌟\n\nHere's what I picked up:\n${detailsList.join('\n')}\n\nWhat would you like to do?`;

                // Create destination object for the buttons
                // Calculate appropriate travel time based on transport mode and destination
                const isFlying = extractedDetails.transportMode !== 'drive' && extractedDetails.transportMode !== 'road-trip';
                const isRoadTrip = extractedDetails.transportMode === 'road-trip';
                // Use async flight estimation with LLM fallback for unknown destinations
                const estimatedFlight = await getFlightHours(
                  extractedDetails.hotelName || extractedDetails.destination,
                  userProfile.preferences.homeCity
                );
                const destination = {
                  name: formattedDestination,
                  description: extractedDetails.hotelName ? `Stay at ${extractedDetails.hotelName}` : `Your personalized trip`,
                  flightHours: isFlying ? estimatedFlight : 0,
                  estimatedFlightHours: estimatedFlight, // Store for fallback
                  driveHours: !isFlying ? estimateDriveHours(estimatedFlight) : 0,
                  highlights: [extractedDetails.interests || 'Local experiences'],
                  tripType: extractedDetails.tripType || 'single', // Store detected trip type
                  isRoadTrip: isRoadTrip, // User explicitly requested a road trip
                  transportMode: extractedDetails.transportMode // Preserve the transport preference
                };
                
                setMessages([...newMessages, { 
                  role: 'assistant', 
                  content: confirmationMessage,
                  isSmartDetection: true,
                  extractedDetails: extractedDetails,
                  destinationOptions: [destination]
                }]);
                setIsProcessing(false);
                return;
              } else if (extractedDetails.destination && isValidDestinationName(extractedDetails.destination)) {
                // We have a destination but need more details - continue to regular chat flow
                // The LLM will ask follow-up questions
                debugLog('🎯 Have destination but need more details, continuing to chat...');
              } else if (extractedDetails.destination && !isValidDestinationName(extractedDetails.destination)) {
                // Invalid destination (looks like a description) - treat as interests and ask for actual destinations
                debugLog('⚠️ Invalid destination detected:', extractedDetails.destination, '- will ask AI for suggestions');
                extractedDetails.interests = extractedDetails.interests 
                  ? `${extractedDetails.interests}, ${extractedDetails.destination}`
                  : extractedDetails.destination;
                extractedDetails.destination = null;
                // Fall through to regular chat flow to get actual destination suggestions
              }
            }
          } catch (extractError) {
            debugError('LLM extraction error:', extractError);
            // Fall through to regular chat handling
          }
        }
        
        // Check if this is a follow-up answer to our questions about a pending destination
        const pendingDest = userProfile.preferences.pendingDestination;
        if (pendingDest && pendingDest.destination) {
          debugLog('🎯 Follow-up for pending destination:', pendingDest.destination);
          
          try {
            // Use LLM to extract additional details from follow-up message
            const followUpResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
              method: "POST",
              headers: await getApiHeaders(),
              body: JSON.stringify({
                model: "claude-sonnet-4-20250514",
                max_tokens: 500,
                messages: [{
                  role: "user",
                  content: `The user is planning a trip to ${pendingDest.destination}. They already told us:
${pendingDest.duration ? `- Duration: ${pendingDest.duration}` : '- Duration: not specified yet'}
${pendingDest.timing ? `- When: ${pendingDest.timing}` : '- When: not specified yet'}
${pendingDest.companion ? `- Traveling with: ${pendingDest.companion}` : '- Traveling with: not specified yet'}
${pendingDest.interests ? `- Interests: ${pendingDest.interests}` : '- Interests: not specified yet'}

Now they said: "${messageToSend}"

Extract any NEW details from their message. Return ONLY a JSON object with the fields that were mentioned (omit fields not mentioned):
{"duration": "...", "timing": "...", "companion": "...", "interests": "..."}`
                }]
              })
            });
            
            const followUpData = await followUpResponse.json();
            const followUpText = followUpData.content[0].text.trim();
            const jsonMatch = followUpText.match(/\{[\s\S]*\}/);
            
            if (jsonMatch) {
              const newDetails = JSON.parse(jsonMatch[0]);
              debugLog('🎯 LLM extracted follow-up details:', newDetails);
              
              // Merge with existing details
              const updatedDetails = { 
                ...pendingDest,
                ...(newDetails.duration && { duration: newDetails.duration }),
                ...(newDetails.timing && { timing: newDetails.timing }),
                ...(newDetails.companion && { companion: newDetails.companion }),
                ...(newDetails.interests && { interests: newDetails.interests })
              };
              
              // VALIDATION: Check if destination is valid (not a description)
              if (updatedDetails.destination && !isValidDestinationName(updatedDetails.destination)) {
                debugLog('⚠️ Invalid destination in follow-up:', updatedDetails.destination);
                // Clear pending and fall through to regular chat for AI suggestions
                setUserProfile(prev => ({
                  ...prev,
                  preferences: { ...prev.preferences, pendingDestination: null }
                }));
                // Don't return - let it fall through to regular chat
              } else {
                // Count details now
                const detailCount = ['destination', 'duration', 'companion', 'timing', 'interests']
                  .filter(key => updatedDetails[key]).length;
                
                if (detailCount >= 3 && isValidDestinationName(updatedDetails.destination)) {
                  // Now we have enough - show confirmation
                  const detailsList = [];
                  detailsList.push(`**Destination:** ${updatedDetails.destination}`);
                  if (updatedDetails.duration) {
                    const durLower = updatedDetails.duration.toLowerCase();
                    const durationText = durLower.includes('weekend') ? 'Weekend (2-3 days)' :
                                         durLower.includes('week') && !durLower.includes('weekend') ? '1 week' :
                                         durLower.includes('long weekend') ? 'Long weekend (3-4 days)' :
                                         durLower.includes('few days') ? '3-4 days' :
                                         updatedDetails.duration;
                    detailsList.push(`**Duration:** ${durationText}`);
                  }
                  if (updatedDetails.timing) detailsList.push(`**When:** ${updatedDetails.timing.charAt(0).toUpperCase() + updatedDetails.timing.slice(1)}`);
                  if (updatedDetails.companion) detailsList.push(`**Traveling with:** ${updatedDetails.companion.charAt(0).toUpperCase() + updatedDetails.companion.slice(1)}`);
                  if (updatedDetails.interests) detailsList.push(`**Interests:** ${updatedDetails.interests}`);
                  
                  // Update state
                  setUserProfile(prev => ({
                    ...prev,
                    preferences: {
                      ...prev.preferences,
                      pendingDestination: updatedDetails
                    }
                  }));
                  
                  const confirmationMessage = `Perfect! Here's your trip:\n${detailsList.join('\n')}\n\nWhat would you like to do?`;
                  
                  // Use async flight estimation with LLM fallback for unknown destinations
                  const estimatedFlight = await getFlightHours(
                    updatedDetails.destination,
                    userProfile.preferences.homeCity
                  );
                  const destination = {
                    name: updatedDetails.destination,
                    description: `Your personalized trip`,
                    flightHours: estimatedFlight,
                    estimatedFlightHours: estimatedFlight,
                    highlights: [updatedDetails.interests || 'Local experiences']
                  };
              
                  setMessages([...newMessages, { 
                    role: 'assistant', 
                    content: confirmationMessage,
                  isSmartDetection: true,
                  extractedDetails: updatedDetails,
                  destinationOptions: [destination]
                }]);
                setIsProcessing(false);
                return;
              } else {
                // Still missing details - ask again
                const missingDetails = [];
                if (!updatedDetails.duration) missingDetails.push('how long');
                if (!updatedDetails.timing) missingDetails.push('when');
                
                setUserProfile(prev => ({
                  ...prev,
                  preferences: {
                    ...prev.preferences,
                    pendingDestination: updatedDetails
                  }
                }));
                
                if (missingDetails.length > 0) {
                  const followUp = `Got it! And ${missingDetails.join(' and ')} are you thinking for ${updatedDetails.destination}?`;
                  setMessages([...newMessages, { role: 'assistant', content: followUp }]);
                  setIsProcessing(false);
                  return;
                }
              }
              } // Close the else block from validation
            }
          } catch (followUpError) {
            debugError('Follow-up extraction error:', followUpError);
            // Fall through to regular chat
          }
        }
      } // end if (!awaitingPersonalization && !isQuestion)

      // Normal chat flow - set loading state based on context
      // Detect intent to set appropriate loading message
      const isOptimizing = /optimiz|reorder|rearrange|route order|best order/i.test(messageToSend);
      const isGenerating = itinerary && /itinerary|schedule|plan/i.test(messageToSend);
      const isUpdating = itinerary && /change|modify|update|replace|swap|add|remove/i.test(messageToSend);

      if (isOptimizing) {
        setLoadingState('optimizing');
      } else if (isUpdating) {
        setLoadingState('updating');
      } else if (isGenerating) {
        setLoadingState('generating');
      } else {
        setLoadingState('searching'); // Default to searching for destinations
      }
      setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);
      
      const profile = userProfile.preferences;
      const profileContext = [];
      
      // CRITICAL: Get the CURRENT home city value - check both state and any recent updates
      // We need to read directly from userProfile state which should be current
      const currentProfile = userProfile.preferences;
      
      // Detect origin city from this message — broader regex that catches any city name
      // Priority: 1) explicit "from {city}" in THIS message, 2) tripContext.originCity (from extraction), 3) profile homeCity
      const originFromMsg = messageToSend.match(/(?:from|near|close to|around|within.*(?:of|from))\s+(?!the\s|a\s|an\s)([A-Za-zÀ-ÿ][\w\sÀ-ÿ'.-]{1,30}?)(?:\s*,|\s*\.(?!\s*[A-ZÀ-ÿ])|\s+(?:and|but|to|for|in|we|i |my|the|that|is|are|within|under|less|max|no more)|\s*$)/i);
      const cityAliases = { 'nyc': 'NYC', 'ny': 'New York', 'la': 'Los Angeles', 'sf': 'San Francisco', 'dc': 'Washington DC' };

      let detectedOriginCity = null;
      if (originFromMsg) {
        const raw = originFromMsg[1].trim();
        detectedOriginCity = cityAliases[raw.toLowerCase()] || raw.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
      }

      // CRITICAL: Lock in origin city for consistent travel times
      // Priority: 1) City explicitly mentioned in THIS message (user's chat always wins)
      //           2) tripContext.originCity (from previous chat extraction)
      //           3) Profile homeCity (sidebar setting)
      //           4) Empty — system will handle missing departure city gracefully
      const homeCity = detectedOriginCity
        || tripContext?.originCity
        || currentProfile.homeCity
        || '';

      const homeCitySource = detectedOriginCity ? 'this message' : tripContext?.originCity ? 'trip context' : currentProfile.homeCity ? 'profile' : 'not set';
      debugLog(`🏠 Using home city for API: ${homeCity} (source: ${homeCitySource}, profile: ${currentProfile.homeCity}, tripContext: ${tripContext?.originCity})`);
      
      // Detect drive time constraints from message (e.g., "1hr drive", "within 2 hours drive")
      const driveHourMatch = messageToSend.match(/(?:within|under|less than|max|maximum|no more than)?\s*(?:a\s+)?(\d+(?:\.\d+)?)\s*(?:hour|hr)s?\s*(?:drive|driving|by car)/i)
        || messageToSend.match(/(\d+(?:\.\d+)?)\s*(?:hour|hr)s?\s*(?:from|of|away)/i);
      const maxDriveHours = driveHourMatch ? parseFloat(driveHourMatch[1]) : null;

      // Detect flight hour constraints from message
      const flightHourMatch = !maxDriveHours ? messageToSend.match(/(?:within|under|less than|max|maximum|no more than)\s*(?:a\s+)?(\d+)\s*(?:hour|hr)/i) : null;
      const maxFlightHours = flightHourMatch ? parseInt(flightHourMatch[1]) : currentProfile.maxFlightHours;

      if (homeCity) profileContext.push(`Home City: ${homeCity} (ALWAYS use this for travel time calculations)`);
      if (maxDriveHours) profileContext.push(`Max Drive Time: ${maxDriveHours} hours from ${homeCity} (ONLY suggest destinations within ${maxDriveHours}hr DRIVE — the user wants to DRIVE, not fly)`);
      if (maxFlightHours && !maxDriveHours) profileContext.push(`Max Flight Time: ${maxFlightHours} hours (ONLY suggest destinations reachable within this time)`);
      if (currentProfile.currency) profileContext.push(`Currency: ${currentProfile.currency}`);
      if (currentProfile.budget) profileContext.push(`Budget: ${formatCurrency(currentProfile.budget)}`);
      if (currentProfile.duration) profileContext.push(`Duration: ${currentProfile.duration} days`);
      if (currentProfile.mustHaves?.length) profileContext.push(`Must-haves: ${currentProfile.mustHaves.join(", ")}`);
      if (currentProfile.dietaryRestrictions?.length) profileContext.push(`Dietary: ${currentProfile.dietaryRestrictions.join(", ")}`);

      // Add passport country for citizenship-based recommendations
      const passportCountryRaw5 = currentProfile.passportCountry || profile?.passport_country;
      const passportCountries5 = parsePassportCountries(passportCountryRaw5);
      if (passportCountries5.length > 0) {
        profileContext.push(`Passport: ${passportCountries5.join(', ')} (avoid destinations with travel restrictions for this passport)`);
      }

      // Add travel companion preference
      const travelCompanion = currentProfile.travelCompanions || profile?.travel_companions;
      if (travelCompanion) profileContext.push(`Traveling: ${travelCompanion}`);

      // Add safety considerations (LGBTQ+, solo female, religious)
      const safetyConsiderations = currentProfile.safetyConsiderations || profile?.safety_considerations;
      if (safetyConsiderations) {
        let safetyList = safetyConsiderations;
        try {
          const parsed = JSON.parse(safetyConsiderations);
          if (Array.isArray(parsed)) safetyList = parsed;
        } catch {}

        if (Array.isArray(safetyList) && safetyList.length > 0) {
          const safetyDescriptions = safetyList.map(s => {
            if (s === 'lgbtq') return 'LGBTQ+ friendly destinations preferred (AVOID countries with anti-LGBTQ laws)';
            if (s === 'solo_female') return 'Solo female traveler - prioritize safe destinations';
            if (s === 'religious') return 'Religious/cultural considerations';
            return s;
          });
          profileContext.push(`Safety Considerations: ${safetyDescriptions.join('; ')}`);
        }
      }

      // Add free-text safety notes
      const safetyNotes = currentProfile.safetyNotes || profile?.safety_notes;
      if (safetyNotes) profileContext.push(`Additional Safety Notes: ${safetyNotes}`);

      // Add trip context from chat extraction (occasion, activities, travelers, etc.)
      // This ensures the LLM knows "post-wedding surf trip for a couple" when recommending destinations
      if (tripContext?.occasion) profileContext.push(`Trip occasion: ${tripContext.occasion}`);
      if (tripContext?.travelers) profileContext.push(`Traveling as: ${tripContext.travelers}`);
      if (tripContext?.month) profileContext.push(`Travel month: ${tripContext.month}`);
      if (tripContext?.activities?.length) profileContext.push(`🎯 Activities requested (HIGH PRIORITY): ${tripContext.activities.join(', ')}`);
      if (tripContext?.preferences?.length) profileContext.push(`Preferences: ${tripContext.preferences.join(', ')}`);
      if (tripContext?.originCity && tripContext.originCity !== homeCity) {
        profileContext.push(`Currently in: ${tripContext.originCity} (user is already there, not at home)`);
      }

      const currencySymbol = getCurrencySymbol(currentProfile.currency);
      
      // Add context about whether destinations were already shown
      // Detect if user is asking for NEW/DIFFERENT destinations
      const wantsNewDestinations = /different|other|new|else|instead|another|more options|alternatives|something else|i want|looking for|prefer|rather/i.test(messageToSend);
      
      const contextNote = destinationOptions && destinationOptions.length > 0 && !wantsNewDestinations
        ? `\n\nNOTE: You already showed these destinations: ${destinationOptions.map(d => d.name).join(', ')}. The user seems to be asking follow-up questions about these, so answer conversationally without showing new destinations.`
        : destinationOptions && destinationOptions.length > 0 && wantsNewDestinations
        ? `\n\nNOTE: You already showed: ${destinationOptions.map(d => d.name).join(', ')}. The user wants DIFFERENT options, so show 4 NEW destinations in the structured format (with **1.**, **2.**, **3.**, **4.**) that match their NEW criteria.`
        : '';
      
      // Add travel constraint note if specified
      const driveConstraintNote = maxDriveHours
        ? `\n\nCRITICAL DRIVE CONSTRAINT: User wants destinations within ${maxDriveHours} hour${maxDriveHours > 1 ? 's' : ''} DRIVE from ${homeCity}. ONLY suggest destinations reachable by car within ${maxDriveHours} hours. Use REALISTIC drive times (check the drive time guidance below). Do NOT suggest destinations that require longer drives or flights.`
        : '';
      const flightConstraintNote = maxFlightHours && !maxDriveHours
        ? `\n\nCRITICAL FLIGHT CONSTRAINT: User wants destinations within ${maxFlightHours} hours flight from ${homeCity}. ONLY suggest destinations that meet this requirement. Do NOT suggest destinations that require longer flights.`
        : '';
      
      // Multi-destination context for system prompt
      const multiDestContext = tripScope === 'multi' ? `
🌍 MULTI-DESTINATION MODE - User wants to visit MULTIPLE places in one trip
The user selected "Multiple Destinations" - they want a multi-city/multi-country trip.

When suggesting destinations:
- Suggest trip COMBINATIONS that work well together (e.g., "Paris & Rome", "Tokyo & Kyoto & Osaka", "Portugal & Spain")
- Consider logical routing and flight connections
- Group destinations by region when it makes sense (e.g., European cities, Southeast Asian countries)
- Explain WHY these destinations pair well together
- You can suggest both:
  1. Cities that are close and easy to combine (Japan rail trip: Tokyo → Kyoto → Osaka)
  2. Diverse experiences across regions (City + Beach: Paris & Amalfi Coast)

Format for multi-destination suggestions:
**1. [City 1] & [City 2]** • ${getCurrencySymbol(userProfile.preferences.currency)}${getCurrencySymbol(userProfile.preferences.currency)} average • Xhr total travel from ${userProfile.preferences.homeCity || 'your city'}
Brief description of why these destinations pair well together.
**Route:** [How to travel between them - train, short flight, etc.]
**Best time:** [Season that works for both]

` : '';

      const systemPrompt = `You are Arrivo AI, an expert travel agent helping users plan their perfect trip.
${multiDestContext}
${tripType === 'hotel' ? `
🏨 HOTEL-FIRST MODE - User wants to find a HOTEL/RESORT, not a destination
The user selected "Stay at a Hotel/Resort" - they want the PROPERTY to be the focus.

Instead of suggesting destinations, suggest SPECIFIC HOTELS/RESORTS:
- For spa requests: Suggest specific spa resorts (Miraval, Canyon Ranch, Cal-a-Vie, Mirbeau, Mohonk Mountain House, etc.)
- For beach resorts: Suggest specific properties (Atlantis Paradise Island, Four Seasons Maui, etc.)
- For ski resorts: Suggest specific ski lodges (St. Regis Deer Valley, Stein Eriksen Lodge, etc.)
- For luxury: Suggest specific iconic hotels (The Plaza NYC, Ritz Paris, Belmond properties, etc.)

IMPORTANT - DISTANCE FORMAT FOR HOTELS:
- For resorts within driving distance (under 6 hours from user's city): Use "Xhr drive from ${homeCity}"
- For far resorts requiring flights: Use "Xhr flight from ${homeCity}"
- ALWAYS check if the hotel is reasonably driveable before suggesting flight times
- BE ACCURATE WITH DRIVE TIMES - think about REAL driving conditions (road quality, terrain, traffic), not simple distance ÷ speed. When unsure, estimate LONGER.
- If ${homeCity} is inland or in a rural/mountain area, ADD extra time to reach the nearest highway or coast (1-2 hours for valley/hill towns).
- Use the STEP-BY-STEP method in the drive time section below to estimate accurately.

REFERENCE DRIVE TIMES FROM NYC (adjust for other origins):
  * Hudson Valley (Mirbeau Inn & Spa, Mohonk Mountain House) = "~2hr drive"
  * Berkshires, MA (Miraval Berkshires, Canyon Ranch Lenox, Blantyre, Cranwell) = "~2.5-3hr drive"
  * Poconos, PA = "~2hr drive"
  * Catskills, NY = "~2-2.5hr drive"
  * Cape Cod, MA (Chatham Bars Inn) = "~4-5hr drive" (NOT 2 hours!)
  * Stowe, Vermont (Stowe Mountain Lodge, Topnotch Resort) = "~5hr drive"
  * Virginia (Salamander Resort, Middleburg) = "~4-5hr drive"
  * Pennsylvania (Nemacolin Woodlands) = "~5-6hr drive"

MIRAVAL LOCATIONS (KNOW THESE!):
  * Miraval Arizona (Tucson) - requires flight (~5hr from NYC)
  * Miraval Austin (Texas) - requires flight (~4hr from NYC)  
  * Miraval Berkshires (Lenox, MA) - DRIVEABLE! (~3hr drive from NYC)

RESORTS REQUIRING FLIGHTS FROM NYC:
  * Miraval Arizona, Canyon Ranch Tucson = "~5hr flight"
  * Atlantis (Bahamas) = "~3hr flight"
  * Any West Coast resort = "~6hr flight"

FORMAT FOR HOTEL SUGGESTIONS:
**1. [Hotel/Resort Name]** • [Location/Region] • ${currencySymbol}${currencySymbol}${currencySymbol} per night • [Xhr drive/flight from ${homeCity}]

Brief description of the property and what makes it special. Mention key amenities (spa, pool, golf, etc.)

**Highlights:** [Key features - spa treatments, dining, activities]
**Best for:** [Type of traveler - couples, wellness seekers, families]

**2. [Hotel/Resort Name]** • [Location/Region] • ${currencySymbol}${currencySymbol} per night • [Xhr drive/flight from ${homeCity}]
...

HOTEL-FIRST RULES:
- Focus on the PROPERTY features, not the destination attractions
- Mention on-site amenities: spa, restaurants, pools, fitness, activities
- Include the location so user knows where the resort is
- ALWAYS include accurate travel time (drive OR flight, whichever is appropriate)
- The hotel IS the experience - itinerary will center on resort amenities
` : `
🗺️ DESTINATION-FIRST MODE - User wants to explore a DESTINATION
`}

**User Profile:**
${profileContext.join('\n')}${contextNote}${driveConstraintNote}${flightConstraintNote}

CRITICAL: User's origin city is ${homeCity}. ALL travel times (flight AND drive) must be calculated from ${homeCity}. Do NOT change or guess a different city.
${homeCitySource === 'this message' ? `NOTE: The user just said they want destinations from ${homeCity} in their latest message — this OVERRIDES any previous home city setting.` : ''}
${maxDriveHours ? `CRITICAL: ONLY suggest destinations reachable within ${maxDriveHours} hours DRIVE from ${homeCity}. Use ACCURATE drive times. The user is DRIVING, not flying.` : ''}
${maxFlightHours && !maxDriveHours ? `CRITICAL: ONLY suggest destinations reachable within ${maxFlightHours} hours from ${homeCity}.` : ''}

Your job:
${tripType === 'hotel' ? `
${pendingHotelCitiesRef.current.length > 1 ? `- The user wants hotels in MULTIPLE cities: ${pendingHotelCitiesRef.current.join(', ')}. Suggest exactly 3 HOTELS/RESORTS per city, grouped by city with ### City headers.` : '- Suggest 4 specific HOTELS/RESORTS that match the user\'s criteria'}
- Focus on property amenities, not destination sightseeing
- When user selects a hotel, the itinerary will be property-centric
` : `
🚨 CRITICAL: ALWAYS SHOW DESTINATIONS IMMEDIATELY
- On ANY trip request (even vague ones like "food and wine" or "somewhere warm"), IMMEDIATELY suggest 4 destinations
- Do NOT ask clarifying questions before showing options - just pick 3 great destinations that fit the vibe
- Do NOT ask "what region?", "what cuisine?", "what type?" - just show destinations
- User can always ask for different options if these don't match
- If user is asking about destinations already shown, answer conversationally
- If user selects a destination, they'll be taken to detailed planning
`}

WHEN TO USE STRUCTURED FORMAT (numbered **1.**, **2.**, **3.**, **4.**) - THIS IS THE DEFAULT:
- First request for destinations${tripType === 'hotel' ? ' or hotels' : ''} (even if vague - just pick great options!)
- User describes what they want (beaches, food, adventure, etc.) - show 4 destinations immediately
- User asks for "different", "other", "new", "more options", "alternatives"
- User provides any travel criteria or preferences
- User says "show me more" or similar
- When in doubt, SHOW DESTINATIONS

ONLY be conversational (no numbered list) when:
- User asks "tell me more about [specific destination${tripType === 'hotel' ? ' or hotel' : ''}]"
- User asks comparison questions like "which is better for couples?"
- User asks clarifying questions about previously shown options

${shownDestinations.length > 0 ? `
CRITICAL - DO NOT REPEAT THESE ${tripType === 'hotel' ? 'HOTELS' : 'DESTINATIONS'} (already shown):
${shownDestinations.map(d => `- ${d}`).join('\n')}

You MUST suggest 4 completely different ${tripType === 'hotel' ? 'hotels/resorts' : 'destinations'} from the above list.
` : ''}

🚨 SAFETY RESTRICTIONS - NEVER SUGGEST THESE DESTINATIONS:
- North Korea, Syria, Yemen, Libya, South Sudan, Somalia, Afghanistan, Central African Republic
- These are either closed to tourism, active war zones, or extremely dangerous
- If user specifically asks about these, explain that we cannot plan trips there for safety reasons
- For high-risk destinations (Venezuela, Haiti, Iraq, Pakistan, Sudan, Mali, Burkina Faso, Niger, Chad), include a travel advisory warning if suggested

🏳️‍🌈 LGBTQ+ SAFETY (if user indicated LGBTQ+ preference):
- AVOID suggesting: Saudi Arabia, Iran, UAE, Qatar, Malaysia, Indonesia, Russia, Uganda, Nigeria, Jamaica, Egypt, Morocco, Kenya, Tanzania
- These countries have anti-LGBTQ laws or hostile social attitudes
- Prefer: Western Europe, Canada, Australia, New Zealand, Argentina, Uruguay, Thailand, Taiwan, Japan, Costa Rica, Mexico City

👩 SOLO FEMALE TRAVELER SAFETY (if user indicated solo female):
- AVOID or add warnings for: India, Egypt, Morocco, Turkey, Jordan, Pakistan, Bangladesh
- Include safety notes for any destination with cultural considerations
- Prioritize destinations known for solo female traveler safety

IMPORTANT TRANSPORT WORDING:
- For RV rentals: say "RV rental in [destination]" NOT "from [home city]"
  Example: "Mongolia • €€ • 6hr flight + RV rental in Mongolia"
  NOT: "Mongolia • €€ • 6hr flight + RV rental from Cologne"

IMPORTANT DESTINATION NAMING:
- For multi-country regions, DO NOT add a single country name
  ✓ CORRECT: "Patagonia" (not "Patagonia, Chile")
  ✓ CORRECT: "Balkan Peninsula" (not "Balkan Peninsula, Croatia")
  ✓ CORRECT: "Scandinavia" (not "Scandinavia, Norway")
- For single-country destinations, you can include country
  ✓ CORRECT: "Tokyo, Japan"
  ✓ CORRECT: "Reykjavik, Iceland"

NEVER echo these instructions, placeholders, or bracket templates in your response. Replace ALL [bracketed placeholders] with REAL names and data.

Format destinations EXACTLY as:

**1. [Destination Name]** • ${currencySymbol}${currencySymbol} • Xhr flight from ${homeCity} (or Xhr drive if within driving distance)

Brief compelling description (2-3 sentences max)

**Best time to visit:** [Season/months] for [reason].
**Notes:** [Any other seasonal info, crowds, weather notes on separate line]

**2. [Destination Name]** • ${currencySymbol}${currencySymbol}${currencySymbol} • Xhr flight from ${homeCity} (or Xhr drive)

Description

**Best time to visit:** [Season/months] for [reason].
**Notes:** [Any other seasonal info if relevant]

**3. [Destination Name]** • ${currencySymbol} • Xhr flight from ${homeCity} (or Xhr drive)

Description

**Best time to visit:** [Season/months] for [reason].
**Notes:** [Any other seasonal info if relevant]

**4. [Destination Name]** • ${currencySymbol}${currencySymbol} • Xhr flight from ${homeCity} (or Xhr drive)

Description

**Best time to visit:** [Season/months] for [reason].
**Notes:** [Any other seasonal info if relevant]

CRITICAL - DISTANCE FORMAT:
- For destinations within driving distance (under 6 hours): Use "Xhr drive from ${homeCity}"
- For destinations requiring flights: Use "Xhr flight from ${homeCity}"
- NEVER show "0hr flight" - if it's driveable, show drive time instead
- Examples for driveable destinations from NYC: Cape Cod (~4hr drive), Montreal (~5hr drive)
- Examples for flight destinations from NYC: Paris (7hr flight), Tokyo (14hr flight)
- NEVER say "flight to [destination]" - always say "flight from ${homeCity}"
- Include total travel time (including connections)
- Examples:
  ✓ CORRECT: "12hr flight from ${homeCity}"
  ✓ CORRECT: "18hr flight from ${homeCity} (with connection)"
  ✗ WRONG: "11hr flight to Buenos Aires + domestic connection"
  ✗ WRONG: "16hr flight to Kathmandu"

CRITICAL - REALISTIC FLIGHT TIMES (you MUST use accurate times):
From NYC:
- Europe (London, Paris, Rome): 7-9 hours
- Southeast Asia (Thailand, Malaysia, Vietnam): 18-22 hours (with connection)
- Japan/Korea: 14-16 hours
- Australia/New Zealand: 20-24 hours
- South America (Brazil, Argentina): 10-14 hours
- Caribbean: 3-5 hours
- Mexico/Central America: 4-6 hours
- Middle East (Dubai, Israel): 11-14 hours
- India: 15-18 hours
- Africa (South Africa, Kenya): 15-20 hours

From London:
- Europe: 1-4 hours
- Southeast Asia: 11-14 hours
- USA East Coast: 7-9 hours
- USA West Coast: 10-12 hours

From Los Angeles:
- Hawaii: 5-6 hours
- Japan/Korea: 11-13 hours
- Southeast Asia: 16-20 hours
- Europe: 10-12 hours
- Australia: 14-16 hours

DO NOT make up short flight times. If unsure, estimate on the LONGER side.

CRITICAL - ACCURATE DRIVE TIMES (DO NOT UNDERESTIMATE!):
- Drive times in suggestions are frequently WRONG. You MUST think carefully about REAL driving conditions.
- DO NOT use simple distance ÷ speed formulas. Road conditions vary enormously by region.
- WHEN UNSURE, estimate on the LONGER side (same principle as flights).
- Think about what Google Maps would ACTUALLY show for that route, not straight-line distance.
- Consider: mountain passes, winding coastal roads, border crossings, urban traffic, road quality.

⚠️ STEP-BY-STEP METHOD FOR ESTIMATING DRIVE TIMES:
1. Identify the ORIGIN city: ${homeCity}
2. Think about WHERE that city is geographically. Is it inland? Coastal? Mountain?
3. For EACH destination, estimate the distance in km (not straight-line — actual road distance).
4. Apply a REALISTIC average speed for that SPECIFIC region and road type:
   * US/European HIGHWAYS (motorway): ~90-110 km/h average
   * European RURAL/mountain roads: ~50-70 km/h average
   * Southeast Asia: ~30-50 km/h
   * India: ~30-40 km/h
   * Africa: ~30-60 km/h
   * Latin America mountain roads: ~40-60 km/h
5. Calculate: distance ÷ average speed = time, then ADD 15-30 min for stops/traffic.
6. SANITY CHECK: Does the result make sense? If you estimated 2hr for a route through mountains, it's probably too short.

⚠️ INLAND ORIGINS ADD SIGNIFICANT TIME:
If the origin city (${homeCity}) is INLAND (not on the coast or a major highway hub), you must ADD the time to reach the nearest highway/coast:
- Small towns in river valleys (e.g., Douro Valley, Tuscany hills, Cotswolds) are typically 1-2 hours from the nearest coastal destination
- Mountain villages add 1-3 hours to reach lowland/coastal destinations
- EXAMPLE: If Porto → Ericeira is ~3.5hr, then Pinhão (Douro Valley, 1.5hr inland from Porto) → Ericeira is ~4.5-5hr
- EXAMPLE: If Florence → Cinque Terre is ~2.5hr, then a Tuscan hill town → Cinque Terre could be ~3.5-4hr

COMMON REFERENCE POINTS (use as baselines, then adjust for actual origin):
  * Porto → Peniche: ~3.5hr (A1 motorway + A8)
  * Porto → Ericeira: ~3.5hr
  * Porto → Nazaré: ~2.5-3hr
  * Porto → Lisbon: ~3hr (A1 motorway)
  * Porto → Matosinhos: ~20min
  * Porto → Viana do Castelo: ~1hr
  * Lisbon → Ericeira: ~45min
  * Lisbon → Peniche: ~1hr
  * Lisbon → Nazaré: ~1.5hr
  * Lisbon → Sagres: ~3.5hr
  * Bangkok → Chiang Mai: ~9hr (NOT 3hr! — 700km of mountain roads)
  * Bali Ubud → Amed: ~2.5hr (NOT 45min! — narrow winding roads)
  * Marrakech → Sahara (Merzouga): ~9hr (NOT 4hr! — Atlas Mountain passes)
  * Lima → Cusco: ~20hr (NOT 6hr! — extreme mountain roads, most people fly)
  * Paris → Bordeaux: ~6hr
  * Barcelona → Madrid: ~6hr
  * Rome → Florence: ~3hr
  * London → Edinburgh: ~7hr

REMEMBER: If ${homeCity} is NOT one of the cities in the reference points above, estimate EXTRA time from ${homeCity} to the nearest reference city, then add that to the baseline.

IMPORTANT - SEASONAL INFO:
Include brief seasonal information for each destination:
- Best season/months to visit and why
- What other seasons are like (weather, crowds, prices)
- IMPORTANT: If destination is in Southern Hemisphere (Patagonia, Australia, New Zealand, South Africa, etc.), 
  clarify that seasons are REVERSED (e.g., "July is winter in Patagonia" or "December is summer in New Zealand")
- Example for Northern Hemisphere: "Best time: June-August for warm weather and long days."
- Example for Southern Hemisphere: "Best time: December-February (summer in Patagonia). Note: June-August is winter here!"

**What's Next?**
${tripType === 'hotel' 
  ? `Tell me which hotel/resort interests you and let's create your perfect getaway itinerary. You can:
• Type the hotel name directly
• Ask: "Tell me more about [hotel]" to learn more before deciding`
  : `Tell me which of these destinations you want to go to and let's create a detailed itinerary. You can:
• Type the destination name directly
• Ask: "Tell me more about [destination]" to learn more before deciding`}`;

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 2000,
          system: systemPrompt,
          messages: newMessages.map(m => ({ role: m.role, content: m.content }))
        })
      });

      if (!response.ok) throw new Error('API error');

      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }
      const rawAssistantMessage = data.content[0].text;

      // Extract destinations from the RAW message (before stripping drive times)
      // Always attempt extraction — the parser has 4 fallback patterns and handles varied formats
      let extractedDestsLegacy = extractDestinationsFromMessage(rawAssistantMessage);

      // Strip LLM drive times — show placeholder until Google Maps confirms
      const hasDriveDestsLegacy = extractedDestsLegacy.some(d => d.driveHours != null);
      const { strippedMessage: strippedLegacy } = hasDriveDestsLegacy
        ? stripDriveTimesFromMessage(rawAssistantMessage)
        : { strippedMessage: rawAssistantMessage };
      const assistantMessage = strippedLegacy;

      // Flag drive destinations with placeholder
      if (hasDriveDestsLegacy) {
        extractedDestsLegacy = extractedDestsLegacy.map(d => d.driveHours != null
          ? { ...d, drivePlaceholder: true, llmDriveHours: d.driveHours, driveHours: null }
          : d
        );
      }

      if (extractedDestsLegacy.length > 0) {
        setDestinationOptions(extractedDestsLegacy);
        // Track all shown destinations to avoid repeats
        const newDestNames = extractedDestsLegacy.map(d => d.name);
        setShownDestinations(prev => [...prev, ...newDestNames]);
        debugLog('📍 Extracted destinations:', newDestNames);
        debugLog('📍 All shown destinations:', [...shownDestinations, ...newDestNames]);
      }

      setMessages([...newMessages, {
        role: 'assistant',
        content: assistantMessage,
        ...(extractedDestsLegacy.length > 0 ? { destinationOptions: extractedDestsLegacy } : {})
      }]);

      setLoadingState(null);
      setIsProcessing(false);

      // B9 FIX: existing destinations take priority over originCity for drive time origin
      if (extractedDestsLegacy.length > 0 && hasDriveDestsLegacy) {
        const lastCtxDest = tripContext?.destinations?.length > 0 ? tripContext.destinations[tripContext.destinations.length - 1] : null;
        const lastStop = tripBuilderStops?.length > 0 ? tripBuilderStops[tripBuilderStops.length - 1].name : null;
        const driveOrigin = lastStop || lastCtxDest || tripContext?.originCity || userProfile.preferences?.homeCity || '';
        validateAndCorrectDriveTimes(extractedDestsLegacy, driveOrigin, assistantMessage);
      }

    } catch (error) {
      debugError('Chat error:', error);
      // Give users a more actionable fallback instead of the generic
      // "Please try again" dead end. Offer: (a) a specific hint about
      // what likely happened, (b) the manual `+ Add stop` escape hatch.
      // Skeptical-traveler review flagged two consecutive "Sorry,
      // something went wrong" attempts on a Maldives honeymoon prompt
      // as unusable — users don't know if it's their prompt, the
      // network, or a real outage.
      const errMsg = error?.message || '';
      const looksLikeNetwork = /network|timeout|fetch|abort|ECONNRESET|503|504|429/i.test(errMsg);
      const hint = looksLikeNetwork
        ? "Looks like a network hiccup. Try again in a moment, or manually add your destination via the **+ Add stop** button on the right."
        : "I had trouble parsing that prompt. Try rephrasing (e.g. just say the destination and days: \"7 days in the Maldives\"), or use the **+ Add stop** button to add your destination manually.";
      setMessages([...newMessages, {
        role: 'assistant',
        content: hint,
      }]);
      setLoadingState(null);
      setIsProcessing(false);
    }
  };

  // Handle toggling lock on an activity/meal
  const handleToggleLock = (day, index, isActivity) => {
    const updatedItinerary = { ...itinerary };
    const dayData = updatedItinerary.days?.[day];
    if (!dayData) return;
    const list = isActivity ? (dayData.schedule || dayData.activities || []) : (dayData.meals || []);
    if (index >= 0 && index < list.length) {
      list[index] = { ...list[index], locked: !list[index].locked };
      setItinerary(updatedItinerary);
    }
  };

  // Handle removing an activity or meal from the itinerary
  const handleRemoveItem = async (day, index, isActivity, itemName) => {
    // Confirm removal
    if (!await showConfirmModal({ title: 'Remove Item', message: `Remove "${itemName}" from your itinerary?`, confirmText: 'Remove', type: 'danger' })) {
      return;
    }

    if (!itinerary?.days?.[day]) return;

    // Mark that user has modified the itinerary (prevents background enrichment from overwriting)
    itineraryUserModifiedRef.current = true;
    setHasUnsavedChanges(true);

    // Deep copy to avoid mutation issues
    const updatedItinerary = JSON.parse(JSON.stringify(itinerary));

    if (isActivity) {
      // Remove the activity at the specified index
      updatedItinerary.days[day].schedule.splice(index, 1);
    } else {
      // Remove the meal at the specified index
      updatedItinerary.days[day].meals.splice(index, 1);
    }

    // Clear transit times for this day so they get recalculated with new activity positions
    setTransitTimes(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(key => {
        if (key.startsWith(`day${day}-transit-`) ||
            key.startsWith(`day${day}-last-to-hotel`) ||
            key.startsWith(`day${day}-hotel-to-first`)) {
          delete updated[key];
          delete transitTimesRef.current[key];
        }
      });
      return updated;
    });
    transitCalcGuardRef.current = null; // Allow recalculation

    // Update the itinerary - the timeline will automatically recalculate
    // showing free time gaps where the removed item was
    setItinerary(updatedItinerary);

    // Clear any feedback for this item
    const feedbackKey = `day${day}-${isActivity ? 'activity' : 'meal'}-${index}`;
    if (isActivity) {
      setActivityFeedback(prev => {
        const updated = { ...prev };
        delete updated[feedbackKey];
        return updated;
      });
    } else {
      setMealFeedback(prev => {
        const updated = { ...prev };
        delete updated[feedbackKey];
        return updated;
      });
    }
  };

  // NEW: Handle feedback on itinerary items (activities and meals)
  const handleFeedback = async (type, day, index, itemName, isActivity, mealType = null) => {
    const feedbackKey = `day${day}-${isActivity ? 'activity' : 'meal'}-${index}`;
    const currentFeedback = isActivity ? activityFeedback[feedbackKey] : mealFeedback[feedbackKey];
    const newFeedback = currentFeedback === type ? null : type; // Toggle off if clicking same button

    // Update feedback state
    if (isActivity) {
      setActivityFeedback(prev => ({
        ...prev,
        [feedbackKey]: newFeedback
      }));
    } else {
      setMealFeedback(prev => ({
        ...prev,
        [feedbackKey]: newFeedback
      }));
    }

    // Update feedback count
    setFeedbackCount(prev => {
      const oldWasDown = currentFeedback === 'down';
      const oldWasUp = currentFeedback === 'up';
      const newIsDown = newFeedback === 'down';
      const newIsUp = newFeedback === 'up';

      return {
        thumbsDown: prev.thumbsDown + (newIsDown ? 1 : 0) - (oldWasDown ? 1 : 0),
        thumbsUp: prev.thumbsUp + (newIsUp ? 1 : 0) - (oldWasUp ? 1 : 0)
      };
    });

    // Save feedback to Supabase (async, don't block UI)
    if (user && isLoggedIn) {
      const dayData = itinerary?.days?.[day];
      const item = isActivity
        ? dayData?.schedule?.[index]
        : dayData?.meals?.[index];

      if (newFeedback) {
        // Save or update feedback
        saveItineraryFeedback({
          userId: user.id,
          itineraryId: currentItineraryId || null,
          feedbackType: isActivity ? 'activity' : 'restaurant',
          dayIndex: day,
          itemIndex: index,
          itemName: itemName,
          itemLocation: item?.location || item?.address || null,
          itemType: isActivity ? item?.type : mealType,
          rating: newFeedback,
          reason: null, // Will be updated if user provides reason
          destination: itinerary?.destination || null
        }).catch(err => debugLog('Failed to save feedback to Supabase:', err));

        // Also save to user profile for long-term AI learning
        // Activities: save the activity type (e.g., "Museums", "Hiking")
        // Meals: save the cuisine type (e.g., "Italian", "Japanese")
        if (isActivity && item?.type) {
          saveUserFeedback(user.id, 'activity', item.type, newFeedback === 'up')
            .catch(err => debugLog('Failed to save activity preference:', err));
        }
        if (isActivity && item?.activity) {
          saveUserFeedback(user.id, 'activity', item.activity, newFeedback === 'up')
            .catch(err => debugLog('Failed to save activity name preference:', err));
        }
        if (!isActivity && item?.cuisine) {
          saveUserFeedback(user.id, 'cuisine', item.cuisine, newFeedback === 'up')
            .catch(err => debugLog('Failed to save cuisine preference:', err));
        }
        if (!isActivity && item?.restaurant) {
          saveUserFeedback(user.id, 'activity', item.restaurant, newFeedback === 'up')
            .catch(err => debugLog('Failed to save restaurant preference:', err));
        }
      } else {
        // Delete feedback (user toggled off)
        deleteItineraryFeedback({
          userId: user.id,
          itineraryId: currentItineraryId || null,
          dayIndex: day,
          itemIndex: index,
          feedbackType: isActivity ? 'activity' : 'restaurant'
        }).catch(err => debugLog('Failed to delete feedback from Supabase:', err));
      }
    }

    // If thumbs down, show choice dialog for scope of feedback
    if (newFeedback === 'down') {
      setFeedbackDialogData({
        type: isActivity ? 'activity' : 'meal',
        day,
        index,
        name: itemName,
        feedbackKey,
        mealType: mealType // Include meal type for "not a full meal" option
      });
      setShowFeedbackDialog(true);
    }

    // If thumbs up, just show banner
    if (newFeedback === 'up') {
      setShowFeedbackBanner(true);
    }
  };

  // ─── Flexible Mode Handlers ─────────────────────
  const handleFlexibleSwap = async (dayIdx, itemIdx, itemName) => {
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const dayData = itinerary?.days?.[dayIdx];
    const city = dayData?.baseCity || selectedDestination?.name || 'the area';
    const schedule = dayData?.schedule || dayData?.items || [];
    const item = schedule[itemIdx];
    const timeSlot = item?.time || '';
    const duration = item?.duration || '1 hour';

    // Open inline swap modal with loading state
    setSwapTarget({ dayIdx, itemIdx, itemName, timeSlot, duration, city });
    setShowSwapModal(true);
    setSwapSuggestions([]);
    setLoadingSwapSuggestions(true);

    try {
      const swapPrompt = `Suggest exactly 4 alternative activities to replace "${itemName}" on Day ${dayIdx + 1} in ${city}. Same time slot (${timeSlot}) and similar duration (~${duration}). Return ONLY a JSON array with no other text: [{"name":"...","description":"one sentence","duration":"...","emoji":"...","category":"..."}]. Make them diverse: 1 popular/iconic, 1 hidden gem, 1 food/drink related, 1 cultural/unique.`;
      const response = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: [{ role: 'user', content: swapPrompt }],
          model: 'claude-haiku-4-5-20251001',
          max_tokens: 1500,
        }),
      });
      const data = await response.json();
      const aiText = data?.content?.[0]?.text || '';
      let suggestions = [];
      try {
        const parsed = JSON.parse(aiText);
        suggestions = Array.isArray(parsed) ? parsed.slice(0, 4) : [];
      } catch {
        // Try to extract JSON array from response
        const match = aiText.match(/\[[\s\S]*\]/);
        if (match) {
          try { suggestions = JSON.parse(match[0]).slice(0, 4); } catch { /* ignore */ }
        }
      }
      setSwapSuggestions(suggestions);
    } catch (err) {
      console.error('Swap suggestions failed:', err);
      setSwapSuggestions([]);
    } finally {
      setLoadingSwapSuggestions(false);
    }
  };

  const handleAcceptSwap = (suggestion) => {
    if (!swapTarget) return;
    const { dayIdx, itemIdx } = swapTarget;
    setItinerary(prev => {
      const updated = JSON.parse(JSON.stringify(prev));
      const day = updated.days[dayIdx];
      const schedule = day.schedule || day.items || [];
      if (schedule[itemIdx]) {
        schedule[itemIdx] = {
          ...schedule[itemIdx],
          activity: suggestion.name,
          description: suggestion.description || schedule[itemIdx].description,
          duration: suggestion.duration || schedule[itemIdx].duration,
          emoji: suggestion.emoji || schedule[itemIdx].emoji,
          source: 'swap',
        };
      }
      return updated;
    });
    setShowSwapModal(false);
    setSwapTarget(null);
    setSwapSuggestions([]);
    setHasUnsavedChanges(true);
    showToast(`Swapped to "${suggestion.name}"!`, 'success');
  };

  const handleRefreshSwapSuggestions = async () => {
    if (!swapTarget) return;
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    setLoadingSwapSuggestions(true);
    setSwapSuggestions([]);
    try {
      const response = await fetch(`${API_BASE}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: `Suggest exactly 4 DIFFERENT alternative activities to replace "${swapTarget.itemName}" on Day ${swapTarget.dayIdx + 1} in ${swapTarget.city}. Same time slot (${swapTarget.timeSlot}) and similar duration (~${swapTarget.duration}). These should be completely different from previous suggestions. Return ONLY a JSON array: [{"name":"...","description":"one sentence","duration":"...","emoji":"...","category":"..."}]. Make them diverse and creative.`,
          model: 'haiku',
          respondAsJson: true,
        }),
      });
      const data = await response.json();
      let suggestions = [];
      try {
        const parsed = JSON.parse(data.response || '[]');
        suggestions = Array.isArray(parsed) ? parsed.slice(0, 4) : [];
      } catch {
        const match = (data.response || '').match(/\[[\s\S]*\]/);
        if (match) {
          try { suggestions = JSON.parse(match[0]).slice(0, 4); } catch { /* ignore */ }
        }
      }
      setSwapSuggestions(suggestions);
    } catch (err) {
      console.error('Swap suggestions refresh failed:', err);
    } finally {
      setLoadingSwapSuggestions(false);
    }
  };

  const handleFlexibleKeep = (dayIdx, itemIdx, isActivity) => {
    const key = `day${dayIdx}-${isActivity ? 'activity' : 'meal'}-${itemIdx}`;
    setKeptActivities(prev => ({ ...prev, [key]: true }));
    showToast('Marked as kept!', 'success', 2000);
  };

  const handleFlexibleRemove = (dayIdx, itemIdx, isActivity, itemName) => {
    handleRemoveItem(dayIdx, itemIdx, isActivity, itemName);
  };

  // ========== MODIFICATION COMPARISON VIEW HANDLERS ==========

  // Apply only the accepted pending changes to the itinerary
  const applyPendingChanges = (acceptedIndices = null) => {
    if (!pendingChanges) return;

    const changesToApply = pendingChanges.changes.filter((_, idx) => {
      if (acceptedIndices) return acceptedIndices.includes(idx);
      return pendingChangeStatuses[idx] !== 'rejected';
    });

    // ─── Analytics: emit a chat_event row capturing the apply outcome ───
    // The chat-time emission left applied_change_count=0 (we didn't know yet
    // what the user would accept). This emission is the post-accept side —
    // tells the dashboard how many of the proposed changes actually stuck.
    // Without this, the intent-dismiss-rate column on the Chat tab is just zeros.
    try {
      const accepted = changesToApply.length;
      const dismissed = pendingChanges.changes.length - accepted;
      const intents = pendingChanges.changes.map(c => c.type).filter(Boolean);
      // Pull perf data from the chat-time snapshot stashed on pendingChanges.
      // This is the SINGLE row for the JSON-action path (no chat-time emit),
      // so latency/model/tokens all live here.
      const perf = pendingChanges._perfSnapshot || {};
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
      (async () => {
        try {
          const headers = await getApiHeaders();
          await fetch(`${backendUrl}/api/analytics/chat-event`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              mode: 'in-itinerary',
              platform: 'web',
              itinerary_id: itinerary?._id || itinerary?.id || null,
              request_message: pendingChanges._userMessage || '',
              response_text: perf.responseText || `applied ${accepted} of ${pendingChanges.changes.length} changes${dismissed > 0 ? ` (${dismissed} dismissed)` : ''}`,
              response_was_conversational: false,
              response_was_no_op: accepted === 0,
              classified_intents: intents,
              applied_change_count: accepted,
              itinerary_day_count: itinerary?.days?.length || null,
              model_used: perf.modelUsed || 'claude-sonnet-4',
              latency_ms: perf.latencyMs || null,
              prompt_tokens: perf.promptTokens || null,
              completion_tokens: perf.completionTokens || null,
              had_error: false,
            }),
          });
        } catch (_) { /* analytics never break UX */ }
      })();
    } catch (_) { /* analytics never break UX */ }

    if (changesToApply.length === 0) {
      setPendingChanges(null);
      setPendingChangeStatuses({});
      return;
    }

    if (!itinerary || !itinerary.days) {
      debugLog('⚠️ Cannot apply changes: itinerary or itinerary.days is null');
      setPendingChanges(null);
      setPendingChangeStatuses({});
      return;
    }

    const updatedItinerary = JSON.parse(JSON.stringify(itinerary));
    const modifiedDays = new Set();
    // Pull regenerate_day changes out — they go through the backend
    // /api/regenerate-day endpoint and are applied async after this fn returns.
    const regenChanges = [];

    // ─── Helper: build a new day object using same templates as addNewDay ───
    // Mirrors mobile/src/screens/ItineraryScreen.js:buildNewDay so chat-inserted
    // days look identical on both platforms.
    const buildNewDay = ({ baseCity, dayType = 'blank', themeHint = '' } = {}) => {
      const _baseCity = baseCity || '';
      const isBlank = dayType === 'blank';
      const isRest  = dayType === 'rest';
      const templates = {
        blank:   { title: '', schedule: [], meals: [], dayTips: [], freeTimeSlots: [] },
        rest: {
          title: themeHint ? `${themeHint} in ${_baseCity}` : `Rest day in ${_baseCity}`,
          schedule: [],
          meals: [
            { mealType: 'Breakfast', time: '09:00 AM', restaurant: '{TBD: pick a breakfast spot}', cuisine: '', priceTier: '$', _needsReplacement: true },
            { mealType: 'Lunch',     time: '01:00 PM', restaurant: '{TBD: pick a lunch spot}',     cuisine: '', priceTier: '$', _needsReplacement: true },
            { mealType: 'Dinner',    time: '07:30 PM', restaurant: '{TBD: pick a dinner spot}',    cuisine: '', priceTier: '$$', _needsReplacement: true },
          ],
          dayTips: [`Take it easy today — recover and explore at your own pace.`],
          freeTimeSlots: [],
        },
        explore: {
          title: themeHint ? `${themeHint} in ${_baseCity}` : `Exploration Day in ${_baseCity}`,
          schedule: [
            { time: '10:00 AM', duration: '2 hours', activity: 'Local Markets',           location: _baseCity, type: 'explore', priceTier: '$' },
            { time: '02:00 PM', duration: '2 hours', activity: `Hidden Gems of ${_baseCity}`, location: _baseCity, type: 'explore', priceTier: '$' },
          ],
          meals: [
            { mealType: 'Breakfast', time: '09:00 AM', restaurant: '{TBD: pick a breakfast spot}', cuisine: '', priceTier: '$', _needsReplacement: true },
            { mealType: 'Lunch',     time: '01:00 PM', restaurant: '{TBD: pick a lunch spot}',     cuisine: '', priceTier: '$', _needsReplacement: true },
            { mealType: 'Dinner',    time: '07:30 PM', restaurant: '{TBD: pick a dinner spot}',    cuisine: '', priceTier: '$$', _needsReplacement: true },
          ],
          dayTips: [`Explore ${_baseCity} like a local.`],
          freeTimeSlots: [],
        },
        travel: {
          title: themeHint || `Travel day to ${_baseCity}`,
          schedule: [],
          meals: [
            { mealType: 'Lunch',  time: '01:00 PM', restaurant: `Lunch in ${_baseCity}`,  cuisine: 'Local', priceTier: '$$', _needsReplacement: true },
            { mealType: 'Dinner', time: '07:30 PM', restaurant: `Dinner in ${_baseCity}`, cuisine: 'Local', priceTier: '$$', _needsReplacement: true },
          ],
          dayTips: [`Travel day. Take it easy and acclimate.`],
          freeTimeSlots: [],
        },
        custom: {
          title: themeHint ? `${themeHint.slice(0, 60)} in ${_baseCity}` : `Custom Day in ${_baseCity}`,
          schedule: [],
          meals: [
            { mealType: 'Breakfast', time: '09:00 AM', restaurant: `Breakfast in ${_baseCity}`, cuisine: 'Local', priceTier: '$' },
            { mealType: 'Lunch',     time: '01:00 PM', restaurant: `Lunch in ${_baseCity}`,     cuisine: 'Local', priceTier: '$$' },
            { mealType: 'Dinner',    time: '07:30 PM', restaurant: `Dinner in ${_baseCity}`,    cuisine: 'Local', priceTier: '$$' },
          ],
          dayTips: [themeHint || `Your custom day in ${_baseCity}.`],
          freeTimeSlots: [],
        },
      };
      const t = templates[dayType] || templates.blank;
      return {
        baseCity: isBlank ? '' : _baseCity,
        city:     isBlank ? '' : _baseCity,
        title: t.title,
        hotel: {},
        schedule: t.schedule,
        meals:    t.meals,
        dayTips:  t.dayTips,
        freeTimeSlots: t.freeTimeSlots,
        _userAdded: true,
        _needsReplacement: !isBlank && !isRest,
      };
    };

    // ─── Pass 1: structural day-list changes (mirror mobile applyAIChanges) ───
    // Process in reverse-index order so inserts/deletes don't invalidate
    // later indices in the same batch.
    const structuralChanges = changesToApply.filter(c =>
      c.type === 'add_day' || c.type === 'remove_day' ||
      c.type === 'extend_trip' || c.type === 'shorten_trip' ||
      c.type === 'update_preferences'
    );
    structuralChanges.sort((a, b) => {
      const ai = a.dayIndex ?? a.data?.afterDayIndex ?? -1;
      const bi = b.dayIndex ?? b.data?.afterDayIndex ?? -1;
      return bi - ai;
    });

    for (const change of structuralChanges) {
      if (change.type === 'add_day') {
        const afterIdx = change.data?.afterDayIndex ?? change.dayIndex ?? currentDay;
        const baseCity = change.data?.baseCity || updatedItinerary.days[afterIdx]?.baseCity || '';
        const newDay = buildNewDay({
          baseCity,
          dayType: change.data?.dayType || 'blank',
          themeHint: change.data?.themeHint || '',
        });
        updatedItinerary.days.splice(afterIdx + 1, 0, newDay);
        if (newDay._needsReplacement && (itinerary?.id || itinerary?._id)) {
          regenChanges.push({
            dayIndex: afterIdx + 1,
            newBaseCity: baseCity,
            themeHint: change.data?.themeHint,
          });
        }
      } else if (change.type === 'remove_day') {
        const dIdx = change.dayIndex;
        if (typeof dIdx === 'number' && dIdx >= 0 && dIdx < updatedItinerary.days.length) {
          updatedItinerary.days.splice(dIdx, 1);
        }
      } else if (change.type === 'extend_trip') {
        const extra = Math.max(1, parseInt(change.data?.extraDays, 10) || 1);
        const lastIdx = updatedItinerary.days.length - 1;
        const baseCity = change.data?.baseCity || updatedItinerary.days[lastIdx]?.baseCity || '';
        for (let i = 0; i < extra; i++) {
          const newDay = buildNewDay({
            baseCity,
            dayType: change.data?.dayType || 'explore',
            themeHint: change.data?.themeHint || '',
          });
          updatedItinerary.days.push(newDay);
          if (newDay._needsReplacement && (itinerary?.id || itinerary?._id)) {
            regenChanges.push({
              dayIndex: updatedItinerary.days.length - 1,
              newBaseCity: baseCity,
              themeHint: change.data?.themeHint,
            });
          }
        }
      } else if (change.type === 'shorten_trip') {
        const removeFrom = change.data?.removeFrom === 'start' ? 'start' : 'end';
        const count = Math.max(1, parseInt(change.data?.count, 10) || 1);
        if (removeFrom === 'end') {
          updatedItinerary.days = updatedItinerary.days.slice(0, Math.max(1, updatedItinerary.days.length - count));
        } else {
          updatedItinerary.days = updatedItinerary.days.slice(Math.min(count, updatedItinerary.days.length - 1));
        }
      } else if (change.type === 'update_preferences') {
        // Fire-and-forget profile update via Supabase. Local state update too.
        const prefDelta = change.data || {};
        const updateRow = {};
        if (Array.isArray(prefDelta.dietaryRestrictions)) updateRow.dietary_restrictions = prefDelta.dietaryRestrictions;
        if (prefDelta.mobility)                            updateRow.mobility            = prefDelta.mobility;
        if (prefDelta.occasion)                            updateRow.occasion            = prefDelta.occasion;
        if (typeof prefDelta.groupSize === 'number')       updateRow.group_size          = prefDelta.groupSize;
        if (prefDelta.groupType)                           updateRow.group_type          = prefDelta.groupType;
        if (typeof prefDelta.budget === 'number')          updateRow.budget              = prefDelta.budget;
        if (prefDelta.pace)                                updateRow.pace                = prefDelta.pace;
        if (Object.keys(updateRow).length && supabaseClient && user?.id) {
          supabaseClient.from('user_profiles').update(updateRow).eq('id', user.id).then(() => {})
            .catch(err => debugLog?.('update_preferences failed:', err?.message));
        }
        // Reflect in local userProfile so subsequent prompts include the new prefs.
        setUserProfile?.(prev => ({
          ...prev,
          preferences: {
            ...(prev?.preferences || {}),
            ...prefDelta,
          },
        }));
      }
    }

    // ─── Pass 2: per-day item changes (existing logic) ───
    const itemChanges = changesToApply.filter(c =>
      c.type !== 'add_day' && c.type !== 'remove_day' &&
      c.type !== 'extend_trip' && c.type !== 'shorten_trip' &&
      c.type !== 'update_preferences'
    );
    for (const change of itemChanges) {
      if (change.dayIndex === undefined || change.dayIndex === null) {
        debugLog(`⚠️ Change missing dayIndex, defaulting to current day ${currentDay}:`, change.type);
      }
      const dayIndex = change.dayIndex ?? currentDay;
      const day = updatedItinerary.days[dayIndex];
      if (!day) {
        debugLog(`⚠️ Skipping change - invalid dayIndex ${dayIndex}:`, change.type);
        continue;
      }

      switch (change.type) {
        case 'update_hotel':
          if (change.data) {
            day.hotel = { ...day.hotel, ...change.data };
            modifiedDays.add(dayIndex);
          }
          break;
        case 'add_activity':
          if (change.data) {
            day.schedule.push(change.data);
            day.schedule.sort((a, b) => parseTime(a.time) - parseTime(b.time));
            modifiedDays.add(dayIndex);
          }
          break;
        case 'remove_activity':
          if (change.target) {
            const idx = day.schedule.findIndex(a =>
              a.activity.toLowerCase().includes(change.target.toLowerCase()) ||
              change.target.toLowerCase().includes(a.activity.toLowerCase())
            );
            if (idx !== -1) { day.schedule.splice(idx, 1); modifiedDays.add(dayIndex); }
          }
          break;
        case 'update_activity':
          if (change.target && change.data) {
            const idx = day.schedule.findIndex(a =>
              a.activity.toLowerCase().includes(change.target.toLowerCase()) ||
              change.target.toLowerCase().includes(a.activity.toLowerCase())
            );
            if (idx !== -1) { day.schedule[idx] = { ...day.schedule[idx], ...change.data }; modifiedDays.add(dayIndex); }
          }
          break;
        case 'add_meal':
          if (change.data) {
            day.meals.push(change.data);
            day.meals.sort((a, b) => parseTime(a.time) - parseTime(b.time));
            modifiedDays.add(dayIndex);
          }
          break;
        case 'remove_meal':
          if (change.target) {
            const idx = day.meals.findIndex(m =>
              m.restaurant.toLowerCase().includes(change.target.toLowerCase()) ||
              change.target.toLowerCase().includes(m.restaurant.toLowerCase())
            );
            if (idx !== -1) { day.meals.splice(idx, 1); modifiedDays.add(dayIndex); }
          }
          break;
        case 'update_meal':
          if (change.target && change.data) {
            const idx = day.meals.findIndex(m =>
              m.restaurant.toLowerCase().includes(change.target.toLowerCase()) ||
              change.target.toLowerCase().includes(m.restaurant.toLowerCase())
            );
            if (idx !== -1) { day.meals[idx] = { ...day.meals[idx], ...change.data }; modifiedDays.add(dayIndex); }
          }
          break;
        case 'regenerate_day':
          // Defer — fire after sync state update completes. This re-plans the
          // entire day in a different city via the backend orchestrator.
          regenChanges.push({
            dayIndex,
            newBaseCity: change.data?.newBaseCity || change.target,
            themeHint: change.data?.themeHint,
          });
          break;
        default: break;
      }
    }

    // Async regenerations — kick these off after the sync state update so
    // the user sees pending state immediately. Each call hits POST
    // /api/regenerate-day which writes to itinerary_data.days[dayNumber - 1]
    // server-side, then we patch local state with the same shape.
    if (regenChanges.length > 0) {
      const itinId = itinerary?.id || itinerary?._id;
      if (!itinId) {
        showAlertModal?.('Save first', 'Save the trip first, then ask to move days to a new city.');
      } else {
        regenChanges.forEach(async (rc) => {
          try {
            const headers = await getApiHeaders();
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/regenerate-day`, {
              method: 'POST',
              headers,
              body: JSON.stringify({
                itineraryId: itinId,
                dayNumber: rc.dayIndex + 1,
                newBaseCity: rc.newBaseCity,
                themeHint: rc.themeHint,
              }),
            });
            const result = await resp.json();
            if (result?.success && result.day) {
              setItinerary(prev => {
                if (!prev) return prev;
                const next = [...prev.days];
                next[rc.dayIndex] = result.day;
                return { ...prev, days: next };
              });
            } else {
              debugLog(`⚠️ regenerate_day failed for Day ${rc.dayIndex + 1}: ${result?.error}`);
            }
          } catch (err) {
            debugLog(`⚠️ regenerate_day error for Day ${rc.dayIndex + 1}: ${err.message}`);
          }
        });
      }
    }

    // ─── DISPLAY-CACHE INVALIDATION ───
    // createDayTimeline() reads from per-day cached fields populated during
    // the orchestrator's normalize step (`_normalized` flag, `injectedItems`
    // pre-computed transits, `_trimmedForPace` pace-trimmed list,
    // `_dailyEstimatedCost`). When chat mutates schedule/meals/hotel WITHOUT
    // clearing these, the renderer keeps using the stale cached layout and
    // the user's chat changes appear invisible. Clear them on every modified
    // day so createDayTimeline recomputes from scratch.
    //
    // Detect structural changes (add_day/remove_day/extend_trip/shorten_trip)
    // — these shift indices, so we need a FULL transit reset (not selective).
    const hadStructuralChange = changesToApply.some(c =>
      c.type === 'add_day' || c.type === 'remove_day' ||
      c.type === 'extend_trip' || c.type === 'shorten_trip'
    );

    // For structural changes we don't have stable dayIndex set — invalidate
    // every day's cached fields. For per-day item changes, only modifiedDays.
    const daysToInvalidate = hadStructuralChange
      ? updatedItinerary.days.map((_, i) => i)
      : [...modifiedDays];

    for (const dIdx of daysToInvalidate) {
      const day = updatedItinerary.days[dIdx];
      if (day) {
        delete day._normalized;
        delete day.injectedItems;
        delete day._trimmedForPace;
        delete day._dailyEstimatedCost;
        delete day._dailyEstimatedCostCurrency;
      }
    }

    // Global timeline memoization cache (web only — keyed by activeDay+counts)
    if (typeof window !== 'undefined') {
      window._timelineCacheKey = null;
      window._timelineCacheResult = null;
    }

    // Clear transit cache. Per-day for item-level changes; full reset for
    // structural changes (since add_day/remove_day shift dayN indices).
    if (hadStructuralChange) {
      setTransitTimes({});
      if (transitTimesRef?.current) transitTimesRef.current = {};
      transitCalcGuardRef.current = null;
    } else if (modifiedDays.size > 0) {
      setTransitTimes(prev => {
        const updated = { ...prev };
        for (const dayIdx of modifiedDays) {
          const dayNum = dayIdx + 1;
          Object.keys(updated).forEach(key => {
            if (key.includes(`day${dayNum}-`) || key.includes(`day${dayIdx}-`)) {
              delete updated[key];
              delete transitTimesRef.current[key];
            }
          });
        }
        return updated;
      });
      transitCalcGuardRef.current = null; // Allow recalculation
    }

    // Resolve any time overlaps created by modifications
    for (const dayIdx of modifiedDays) {
      const dayData = updatedItinerary.days[dayIdx];
      if (dayData) {
        try {
          normalizeDay(dayData, { dayIndex: dayIdx, skipExpensiveOps: true });
        } catch (e) {
          debugLog('Overlap resolution warning:', e.message);
        }
      }
    }

    // Renumber day.day fields after any structural change (add_day / remove_day /
    // extend_trip / shorten_trip can leave gaps or stale day numbers).
    updatedItinerary.days.forEach((d, i) => { d.day = i + 1; });

    setItinerary(updatedItinerary);
    setHasUnsavedChanges?.(true);
    setUserEdits(prev => [...prev, {
      type: 'chat_changes',
      changes: changesToApply.map(c => c.type),
      day: currentDay + 1
    }]);

    const appliedCount = changesToApply.length;
    const rejectedCount = pendingChanges.changes.length - appliedCount;
    let confirmMsg = `Applied ${appliedCount} change${appliedCount > 1 ? 's' : ''}`;
    if (rejectedCount > 0) confirmMsg += `, skipped ${rejectedCount}`;

    setItineraryChatMessages(prev => [
      ...prev,
      { role: 'assistant', content: `✅ ${confirmMsg}.` }
    ]);

    setPendingChanges(null);
    setPendingChangeStatuses({});
  };

  // Handle "Tell me more" - asks AI for more context about a specific change
  const handleTellMeMore = async (changeIndex) => {
    if (!pendingChanges) return;
    const change = pendingChanges.changes[changeIndex];
    if (!change) return;

    const itemName = change.data?.activity || change.data?.restaurant || change.data?.name || change.target || 'this item';
    const changeType = change.type.replace(/_/g, ' ');

    // Add a user message asking for more info
    const askMsg = `Tell me more about this suggestion: ${changeType} - ${itemName}. Why is this a good choice? What makes it special?`;

    setItineraryChatMessages(prev => [
      ...prev,
      { role: 'user', content: askMsg }
    ]);

    // Send to AI for more context
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 500,
          system: `You are a helpful travel assistant. The user wants to know more about a suggested change to their ${itinerary?.destination} itinerary. Give a brief, enthusiastic explanation of why "${itemName}" is a great choice. Include 2-3 specific details about what makes it special (ambiance, signature dishes, unique history, etc.). Keep it to 2-3 sentences.`,
          messages: [{ role: "user", content: askMsg }]
        }),
      });
      const data = await response.json();
      const aiReply = data.content?.[0]?.text || "I don't have more details about this suggestion.";
      setItineraryChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: aiReply }
      ]);
      // Auto-scroll to show the new response
      setTimeout(() => {
        itineraryChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch {
      setItineraryChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Sorry, I couldn't fetch more details right now." }
      ]);
      // Auto-scroll to show the error response
      setTimeout(() => {
        itineraryChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  // Handle "Try another" - asks AI for an alternative when user rejects a suggestion
  const handleTryAnother = async (changeIndex) => {
    if (!pendingChanges) return;
    const change = pendingChanges.changes[changeIndex];
    if (!change) return;

    const itemName = change.data?.activity || change.data?.restaurant || change.data?.name || change.target || 'this item';
    const changeType = change.type.replace(/_/g, ' ');
    const dayNum = change.dayIndex !== undefined ? `Day ${change.dayIndex + 1}` : '';

    const askMsg = `I rejected "${itemName}"${dayNum ? ` on ${dayNum}` : ''}. Can you suggest a different ${changeType} option instead? Something different in style or location.`;

    setItineraryChatMessages(prev => [
      ...prev,
      { role: 'user', content: askMsg }
    ]);

    // Reset this change status
    setPendingChangeStatuses(prev => { const u = {...prev}; delete u[changeIndex]; return u; });

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3001";
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 500,
          system: `You are a helpful travel assistant. The user rejected "${itemName}" as a ${changeType} suggestion for their ${itinerary?.destination} itinerary. Suggest ONE alternative that is different in style, location, or cuisine. Give the name, a brief 1-2 sentence description, and why it's a good alternative. Be enthusiastic but concise.`,
          messages: [{ role: "user", content: askMsg }]
        }),
      });
      const data = await response.json();
      const aiReply = data.content?.[0]?.text || "Let me think of another option for you...";
      setItineraryChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: aiReply }
      ]);
      setTimeout(() => {
        itineraryChatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } catch {
      setItineraryChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: "Sorry, I couldn't generate an alternative right now. Try asking in the chat!" }
      ]);
    }
  };

  // ========== DAY RATING HANDLERS ==========

  // Handle day-level rating: Love it, Needs tweaks, Redo
  const handleDayRating = async (dayIndex, rating) => {
    const currentRating = dayRatings[dayIndex];

    // Toggle off if clicking same rating
    if (currentRating === rating) {
      setDayRatings(prev => {
        const updated = { ...prev };
        delete updated[dayIndex];
        return updated;
      });
      return;
    }

    setDayRatings(prev => ({ ...prev, [dayIndex]: rating }));

    if (rating === 'love') {
      // Batch save positive feedback for all activities and meals in this day
      const dayData = itinerary?.days?.[dayIndex];
      if (dayData && user && isLoggedIn) {
        // Save positive feedback for each activity
        (dayData.schedule || []).forEach((act) => {
          if (act.type) {
            saveUserFeedback(user.id, 'activity', act.type, true)
              .catch(err => debugLog('Failed to save day love activity type:', err));
          }
          if (act.activity) {
            saveUserFeedback(user.id, 'activity', act.activity, true)
              .catch(err => debugLog('Failed to save day love activity name:', err));
          }
        });
        // Save positive feedback for each meal cuisine
        (dayData.meals || []).forEach((meal) => {
          if (meal.cuisine) {
            saveUserFeedback(user.id, 'cuisine', meal.cuisine, true)
              .catch(err => debugLog('Failed to save day love cuisine:', err));
          }
          if (meal.restaurant) {
            saveUserFeedback(user.id, 'activity', meal.restaurant, true)
              .catch(err => debugLog('Failed to save day love restaurant:', err));
          }
        });
      }
    } else if (rating === 'tweaks') {
      // Open chat panel focused on this day
      setShowItineraryChat(true);
      const dayNum = dayIndex + 1;
      const dayData = itinerary?.days?.[dayIndex];
      const location = dayData?.location || itinerary?.destination || '';
      setItineraryChatInput(`I'd like to make some tweaks to Day ${dayNum}${location ? ` in ${location}` : ''}. `);
    } else if (rating === 'redo') {
      // Regenerate the entire day
      const dayNum = dayIndex + 1;
      setRedoingDay(dayIndex);
      try {
        await regenerateSingleDay(dayNum, 'Please generate a completely different day. The user was not happy with the previous suggestions - try different activities, restaurants, and experiences. Keep the same general structure but use entirely fresh recommendations.');
      } catch (err) {
        debugError('Failed to redo day:', err);
      } finally {
        setRedoingDay(null);
      }
    }
  };

  // Handle feedback dialog submission
  const handleFeedbackSubmit = () => {
    // Store the feedback reason
    if (feedbackReason.trim() && feedbackDialogData) {
      setFeedbackReasons(prev => ({
        ...prev,
        [feedbackDialogData.feedbackKey]: feedbackReason.trim()
      }));
      debugLog(`Feedback for ${feedbackDialogData.name}:`, feedbackReason);

      // Update reason in Supabase
      if (user && isLoggedIn) {
        updateFeedbackReason(
          user.id,
          currentItineraryId || null,
          feedbackDialogData.day,
          feedbackDialogData.index,
          feedbackDialogData.type === 'activity' ? 'activity' : 'restaurant',
          feedbackReason.trim()
        ).catch(err => debugLog('Failed to update feedback reason in Supabase:', err));
      }
    }

    // Close dialog
    setShowFeedbackDialog(false);
    setFeedbackReason('');
    setFeedbackDialogData(null);
  };

  // Handle itinerary chat form submission
  const handleItineraryChatSubmit = async (e) => {
    e.preventDefault();
    if (!itineraryChatInput.trim() || isModifyingItinerary) return;

    const userMessage = itineraryChatInput;
    setItineraryChatInput('');
    setIsModifyingItinerary(true);

    const newChatMessages = [
      ...itineraryChatMessages,
      { role: 'user', content: userMessage }
    ];
    setItineraryChatMessages(newChatMessages);

    try {
      // Build context about current day if discussing specific day
      let dayContext = '';
      const dayMatch = userMessage.match(/day (\d+)/i);
      if (dayMatch && itinerary && itinerary.days) {
        const dayNum = parseInt(dayMatch[1]) - 1;
        if (dayNum >= 0 && dayNum < itinerary.days.length) {
          const day = itinerary.days[dayNum];
          dayContext = `\n\nCurrent Day ${dayNum + 1} schedule:\n`;
          
          // Add activities with times and locations
          if (day.schedule) {
            dayContext += 'Activities:\n';
            day.schedule.forEach(act => {
              dayContext += `- ${act.time}: ${act.activity} at ${act.location || 'unknown location'} (${act.duration})\n`;
            });
          }
          
          // Add meals
          if (day.meals) {
            dayContext += '\nMeals:\n';
            day.meals.forEach(meal => {
              dayContext += `- ${meal.time}: ${meal.restaurant} - ${meal.cuisine} (${meal.duration})\n`;
            });
          }
          
          dayContext += `\n⚠️ IMPORTANT: When suggesting new activities:
1. Calculate travel time from the last activity to the new suggestion
2. Ensure there's enough time between activities (minimum 30 min buffer + travel time)
3. Warn if activities are more than 30 minutes apart
4. Consider the logical flow - don't suggest something far away if there's a nearby activity later
5. Check if the day already has 6+ activities (that might be too packed)
6. Ensure new activities fit within available time slots`;
        }
      }
      
      // Send to AI for modifications
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 4000,
          system: `You are helping modify a travel itinerary for ${itinerary.destination}. 

Current complete itinerary: ${JSON.stringify(itinerary, null, 2)}
${dayContext}

When suggesting activities:
- ALWAYS consider travel time between locations
- Warn if suggested activity is far from current activities
- Check if there's enough time in the schedule
- Suggest activities that fit logically with the day's flow
- If a day has 6+ activities, warn it might be too packed

Use the conversation history to understand context from previous messages in this chat.

Respond with helpful, specific suggestions that account for travel logistics.`,
          messages: newChatMessages.slice(-8).filter(m => m.content && m.content !== 'loading').map(m => ({ role: m.role, content: typeof m.content === 'string' ? m.content : JSON.stringify(m.content) }))
        })
      });

      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }
      const assistantResponse = data.content[0].text;

      setItineraryChatMessages(prev => [...prev, { role: 'assistant', content: assistantResponse }]);
    } catch (error) {
      debugError('Chat error:', error);
      setItineraryChatMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I had trouble processing that. Please try again.'
      }]);
    } finally {
      setIsModifyingItinerary(false);
    }
  };

  // NEW: Handle start date changes
  const handleDateChange = (newStartDate) => {
    setUserProfile(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        startDate: newStartDate
      }
    }));
    setShowDatePicker(false);
  };

  // EXISTING: Handle itinerary chat modifications
  const handleItineraryChat = async () => {
    if (!itineraryChatInput.trim() || isModifyingItinerary) return;

    const userMessage = itineraryChatInput;

    // PRE-CHECK: Verify modification limit before processing
    // Only check if we have an itinerary ID and the user is logged in
    if (user && isLoggedIn && currentItineraryId && checkModificationLimit) {
      const modCheck = await checkModificationLimit(currentItineraryId, userMessage);

      if (!modCheck.allowed) {
        // Show the limit message in chat
        setItineraryChatMessages([
          ...itineraryChatMessages,
          { role: 'user', content: userMessage },
          {
            role: 'assistant',
            content: modCheck.upgradePrompt
              ? `${modCheck.message}\n\n[Upgrade to arrivo Planner to continue modifying this itinerary]`
              : modCheck.message
          }
        ]);
        setItineraryChatInput('');
        return; // Stop processing
      }
    }

    setItineraryChatInput('');
    setIsModifyingItinerary(true);

    const newChatMessages = [
      ...itineraryChatMessages,
      { role: 'user', content: userMessage }
    ];
    setItineraryChatMessages(newChatMessages);

    try {
      // Show loading in chat
      setItineraryChatMessages([...newChatMessages, { role: 'assistant', content: 'loading' }]);

      const prefs = userProfile.preferences;
      
      const systemPrompt = `You are a helpful travel assistant modifying an itinerary for ${selectedDestination.name}.

Current itinerary:
${JSON.stringify(itinerary, null, 2)}

User preferences:
- Budget: ${formatCurrency(prefs.budget)}
- Activity level: ${prefs.activityLevel}
- Dietary: ${prefs.dietaryRestrictions?.join(', ') || 'None'}
- Duration: ${prefs.duration} days

IMPORTANT RESPONSE RULES:
1. If the user is ASKING for suggestions or ideas (e.g., "I want more off-the-beaten-path", "what else can I do", "any recommendations"):
   - Respond conversationally with helpful suggestions
   - List alternatives they might enjoy
   - Ask what they'd like to replace
   - Do NOT output JSON

2. If the user is CONFIRMING a change (e.g., "replace X with Y", "change the hotel to Z", "yes, do that", "regenerate"):
   - Return the COMPLETE updated itinerary as valid JSON
   - Make ONLY the requested changes
   - Keep everything else exactly the same
   - Start response with { and end with }

NEVER output raw JSON unless the user is confirming a specific change.
Respond naturally like a helpful travel advisor.`;

      // Detect if this is a confirmation/action request or a question
      const actionKeywords = ['replace', 'change', 'swap', 'update', 'remove', 'add', 'yes', 'do it', 'go ahead', 'regenerate the itinerary', 'make it'];
      const isActionRequest = actionKeywords.some(kw => userMessage.toLowerCase().includes(kw));

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 16000,
          system: systemPrompt,
          messages: [
            // Include recent conversation history for multi-turn context (last 6 messages)
            ...newChatMessages.slice(-7, -1).filter(m => m.role === 'user' || m.role === 'assistant').map(m => ({
              role: m.role,
              content: typeof m.content === 'string' ? m.content : String(m.content)
            })),
            {
              role: "user",
              content: isActionRequest
                ? `User wants to make this change: "${userMessage}". Apply the changes and return the COMPLETE updated itinerary as valid JSON only.`
                : userMessage
            }
          ]
        })
      });

      if (!response.ok) throw new Error('Failed to modify');

      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('Invalid API response: missing content');
      }
      let responseText = data.content[0].text;

      // Try to extract and parse JSON
      try {
        // Clean up the response
        responseText = responseText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
        
        // Try to extract JSON object from response (in case there's extra text)
        let jsonStr = responseText;
        const jsonMatch = responseText.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          jsonStr = jsonMatch[0];
        }
        
        // Try to fix incomplete JSON (common issue with long responses)
        if (jsonStr.includes('"changes"') && !jsonStr.endsWith('}')) {
          // Find the last complete change object
          const lastCompleteChange = jsonStr.lastIndexOf('},');
          if (lastCompleteChange > 0) {
            jsonStr = jsonStr.substring(0, lastCompleteChange + 1) + ']}';
            debugLog('⚠️ Fixed incomplete JSON');
          }
        }
        
        const parsed = JSON.parse(jsonStr);
        
        // Check if this is a "changes" format or a full itinerary
        if (parsed.changes && Array.isArray(parsed.changes)) {
          // Apply changes to existing itinerary
          debugLog('📝 Applying', parsed.changes.length, 'changes');
          
          setShowLoadingScreen(true);
          setLoadingMessage('Updating your itinerary...');

          const updatedItinerary = JSON.parse(JSON.stringify(itinerary)); // Deep clone
          let appliedCount = 0;
          const modifiedDays = new Set(); // Track which days need transit recalculation

          for (const change of parsed.changes) {
            const dayIdx = change.dayIndex;
            if (dayIdx === undefined || !updatedItinerary.days[dayIdx]) {
              debugLog('⚠️ Skipping change - invalid dayIndex:', dayIdx);
              continue;
            }

            const day = updatedItinerary.days[dayIdx];

            switch (change.type) {
              case 'update_activity':
                const actIdx = day.schedule.findIndex(a =>
                  a.activity?.toLowerCase().includes(change.target?.toLowerCase()) ||
                  change.target?.toLowerCase().includes(a.activity?.toLowerCase())
                );
                if (actIdx !== -1 && change.data) {
                  // Check if location changed - if so, mark day for transit recalculation
                  const oldLocation = day.schedule[actIdx].location;
                  day.schedule[actIdx] = { ...day.schedule[actIdx], ...change.data };
                  if (change.data.location && change.data.location !== oldLocation) {
                    modifiedDays.add(dayIdx);
                  }
                  appliedCount++;
                } else {
                  debugLog('⚠️ Could not find activity to update:', change.target);
                }
                break;

              case 'add_activity':
                if (change.data) {
                  day.schedule.push(change.data);
                  modifiedDays.add(dayIdx); // New activity needs transit calculation
                  // Sort by time
                  day.schedule.sort((a, b) => {
                    const timeA = a.time?.replace(/[^\d:]/g, '') || '99:99';
                    const timeB = b.time?.replace(/[^\d:]/g, '') || '99:99';
                    return timeA.localeCompare(timeB);
                  });
                  appliedCount++;
                }
                break;
                
              case 'remove_activity':
                const removeIdx = day.schedule.findIndex(a =>
                  a.activity?.toLowerCase().includes(change.target?.toLowerCase()) ||
                  change.target?.toLowerCase().includes(a.activity?.toLowerCase())
                );
                if (removeIdx !== -1) {
                  day.schedule.splice(removeIdx, 1);
                  modifiedDays.add(dayIdx); // Removed activity affects transit
                  appliedCount++;
                }
                break;
                
              case 'update_meal':
                const mealIdx = day.meals.findIndex(m =>
                  m.restaurant?.toLowerCase().includes(change.target?.toLowerCase()) ||
                  change.target?.toLowerCase().includes(m.restaurant?.toLowerCase())
                );
                if (mealIdx !== -1 && change.data) {
                  // Check if address changed - if so, mark day for transit recalculation
                  const oldAddress = day.meals[mealIdx].address;
                  day.meals[mealIdx] = { ...day.meals[mealIdx], ...change.data };
                  if (change.data.address && change.data.address !== oldAddress) {
                    modifiedDays.add(dayIdx);
                  }
                  appliedCount++;
                }
                break;

              case 'add_meal':
                if (change.data) {
                  day.meals.push(change.data);
                  modifiedDays.add(dayIdx); // New meal needs transit calculation
                  appliedCount++;
                }
                break;

              case 'remove_meal':
                const removeMealIdx = day.meals.findIndex(m =>
                  m.restaurant?.toLowerCase().includes(change.target?.toLowerCase()) ||
                  change.target?.toLowerCase().includes(m.restaurant?.toLowerCase())
                );
                if (removeMealIdx !== -1) {
                  day.meals.splice(removeMealIdx, 1);
                  modifiedDays.add(dayIdx); // Removed meal affects transit
                  appliedCount++;
                }
                break;

              case 'update_hotel':
                if (change.data) {
                  // Check if address changed - if so, mark day for transit recalculation
                  const oldHotelAddress = day.hotel?.address;
                  day.hotel = { ...day.hotel, ...change.data };
                  if (change.data.address && change.data.address !== oldHotelAddress) {
                    modifiedDays.add(dayIdx);
                  }
                  appliedCount++;
                }
                break;
                
              default:
                debugLog('Unknown change type:', change.type);
            }
          }
          
          await new Promise(resolve => setTimeout(resolve, 1500));

          // Clear transit cache for modified days so times get recalculated
          if (modifiedDays.size > 0) {
            debugLog(`🔄 Clearing transit cache for modified days: ${[...modifiedDays].join(', ')}`);
            setTransitTimes(prev => {
              const updated = { ...prev };
              for (const dayIdx of modifiedDays) {
                const dayNum = dayIdx + 1;
                Object.keys(updated).forEach(key => {
                  if (key.includes(`day${dayNum}-`) || key.includes(`day${dayIdx}-`)) {
                    delete updated[key];
                    delete transitTimesRef.current[key];
                  }
                });
              }
              return updated;
            });
            transitCalcGuardRef.current = null;
          }

          setItinerary(updatedItinerary);
          setShowLoadingScreen(false);

          // Track the modification in database (awaited to ensure count is accurate)
          if (user && isLoggedIn && currentItineraryId && trackModification) {
            await trackModification(currentItineraryId, userMessage);
            if (refreshUsage) refreshUsage(); // Update UI usage display
          }

          // Show confirmation with summary
          const confirmMsg = parsed.message || `✅ Done! I've applied ${appliedCount} update${appliedCount > 1 ? 's' : ''} to make your itinerary more off-the-beaten-path.`;
          setItineraryChatMessages([
            ...newChatMessages,
            { role: 'assistant', content: confirmMsg }
          ]);

        } else if (parsed.days && Array.isArray(parsed.days)) {
          // Full itinerary replacement
          setShowLoadingScreen(true);
          setLoadingMessage('Updating your itinerary...');

          await new Promise(resolve => setTimeout(resolve, 2000));

          setItinerary(parsed);
          setShowLoadingScreen(false);

          // Track the modification in database (awaited to ensure count is accurate)
          if (user && isLoggedIn && currentItineraryId && trackModification) {
            await trackModification(currentItineraryId, userMessage);
            if (refreshUsage) refreshUsage(); // Update UI usage display
          }

          setItineraryChatMessages([
            ...newChatMessages,
            { role: 'assistant', content: '✅ Done! Your itinerary has been updated.' }
          ]);
        } else {
          // Unknown JSON format - don't show raw JSON to user
          debugLog('Unknown JSON format:', parsed);
          setItineraryChatMessages([
            ...newChatMessages,
            { role: 'assistant', content: "I've noted your preferences. Let me try to update your itinerary - could you be more specific about which items you'd like to change?" }
          ]);
        }
        
      } catch (parseError) {
        debugLog('JSON parse error:', parseError.message);
        // Check if the response looks like incomplete JSON (contains "changes" but failed to parse)
        if (responseText.includes('"changes"') || responseText.includes('"type"')) {
          // The AI tried to return changes but JSON was malformed/incomplete
          setItineraryChatMessages([
            ...newChatMessages,
            { role: 'assistant', content: "I'm working on updating your itinerary with those changes. The request was quite large - let me try processing it in smaller parts. Could you tell me which day you'd like me to focus on first?" }
          ]);
        } else {
          // Regular conversational response
          setItineraryChatMessages([
            ...newChatMessages,
            { role: 'assistant', content: responseText }
          ]);
        }
      }

      setIsModifyingItinerary(false);

    } catch (error) {
      debugError('Itinerary modification error:', error);
      setItineraryChatMessages([
        ...newChatMessages,
        { role: 'assistant', content: 'Sorry, I had trouble updating that. Can you try rephrasing your request?' }
      ]);
      setIsModifyingItinerary(false);
    }
  };

  const handleQuizAnswer = (answer) => {
    const currentQuestion = quizQuestions[quizStep];
    setQuizAnswers({ ...quizAnswers, [currentQuestion.question]: answer });

    // If this is the first question (trip type), also set the tripType state
    if (quizStep === 0) {
      setTripType(answer === 'resort' ? 'hotel' : 'destination');
    }

    if (quizStep < quizQuestions.length - 1) {
      setQuizStep(quizStep + 1);
    } else {
      const quizSummary = Object.entries(quizAnswers)
        .map(([q, a]) => `${q}: ${a}`)
        .join(', ') + `, ${currentQuestion.question}: ${answer}`;

      const tripTypeForMessage = quizAnswers["What type of trip are you planning?"] || answer;
      const isHotelTrip = tripTypeForMessage === 'resort';

      setMessages([{
        role: 'assistant',
        content: isHotelTrip
          ? `Based on your quiz answers, let me find the perfect hotels and resorts for you! 🏨`
          : `Based on your quiz answers, let me find the perfect destinations for you! 🎯`
      }]);
      setStage('chat');
      // Show the trip builder sidebar immediately when transitioning from quiz to chat
      // so users see the "Your Trip" pane and understand the trip-building flow
      setShowTripBuilder(true);
      handleSend(`I'm looking for a ${isHotelTrip ? 'hotel or resort stay' : 'destination trip'} with these preferences: ${quizSummary}`);
    }
  };

  // Helper function to parse markdown into React elements
  const parseMarkdown = (text) => {
    if (!text) return null;
    
    // Split into lines and process
    return text.split('\n').map((line, lineIndex) => {
      // Handle headers - use Batangas font
      if (line.startsWith('### ')) {
        return <h3 key={lineIndex} className="text-lg mt-3 mb-1" style={FONTS.headingSemibold}>{line.slice(4)}</h3>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={lineIndex} className="text-xl mt-3 mb-1" style={FONTS.headingSemibold}>{line.slice(3)}</h2>;
      }
      if (line.startsWith('# ')) {
        return <h1 key={lineIndex} className="text-2xl mt-3 mb-1" style={FONTS.headingBold}>{line.slice(2)}</h1>;
      }
      
      // Handle bullet points - use Raleway font
      if (line.startsWith('- ') || line.startsWith('• ')) {
        const content = line.slice(2);
        return <p key={lineIndex} className="ml-4 my-1" style={FONTS.body}>• {parseInlineMarkdown(content)}</p>;
      }
      
      // Handle numbered lists - use Raleway font
      const numberedMatch = line.match(/^(\d+)\.\s+(.*)$/);
      if (numberedMatch) {
        return <p key={lineIndex} className="ml-4 my-1" style={FONTS.body}>{numberedMatch[1]}. {parseInlineMarkdown(numberedMatch[2])}</p>;
      }
      
      // Empty lines
      if (line.trim() === '') {
        return <br key={lineIndex} />;
      }
      
      // Regular paragraph with inline formatting - use Raleway font
      return <p key={lineIndex} className="my-1" style={FONTS.body}>{parseInlineMarkdown(line)}</p>;
    });
  };
  
  // Helper to parse inline markdown (bold, italic, etc.)
  const parseInlineMarkdown = (text) => {
    if (!text) return null;
    
    const parts = [];
    let remaining = text;
    let keyIndex = 0;
    
    while (remaining.length > 0) {
      // Bold: **text** - use Batangas semibold for bold text (sub-headers like "Best time to visit:")
      const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/);
      if (boldMatch) {
        if (boldMatch[1]) parts.push(<span key={keyIndex++} style={FONTS.body}>{boldMatch[1]}</span>);
        parts.push(<strong key={keyIndex++} style={FONTS.headingSemibold}>{boldMatch[2]}</strong>);
        remaining = boldMatch[3];
        continue;
      }
      
      // Italic: *text* or _text_
      const italicMatch = remaining.match(/^(.*?)(?:\*(.+?)\*|_(.+?)_)(.*)/);
      if (italicMatch && !italicMatch[1].endsWith('*')) {
        if (italicMatch[1]) parts.push(<span key={keyIndex++} style={FONTS.body}>{italicMatch[1]}</span>);
        parts.push(<em key={keyIndex++} className="italic" style={FONTS.body}>{italicMatch[2] || italicMatch[3]}</em>);
        remaining = italicMatch[4];
        continue;
      }
      
      // Drive time placeholder: "⏳ drive" → pulsing animated placeholder
      const placeholderMatch = remaining.match(/^(.*?)⏳\s*drive(.*)/);
      if (placeholderMatch) {
        if (placeholderMatch[1]) parts.push(<span key={keyIndex++} style={FONTS.body}>{placeholderMatch[1]}</span>);
        parts.push(
          <span key={keyIndex++} className="inline-flex items-center gap-1 text-gray-400 animate-pulse" style={FONTS.body}>
            <span className="inline-block w-3 h-3 border-2 border-gray-300 border-t-emerald-500 rounded-full animate-spin" style={{ animationDuration: '1s' }} />
            drive
          </span>
        );
        remaining = placeholderMatch[2];
        continue;
      }

      // No more markdown, add the rest
      parts.push(<span key={keyIndex++} style={FONTS.body}>{remaining}</span>);
      break;
    }
    
    return parts.length > 0 ? parts : text;
  };

  const renderMessage = (message, index) => {
    if (message.content === 'loading') {
      const loadingMessages = {
        'searching': {
          icon: '🔍',
          text: 'Searching for perfect destinations...',
          subtext: 'Analyzing your preferences'
        },
        'generating': {
          icon: '✨',
          text: 'Creating your itinerary...',
          subtext: 'Building day-by-day plans'
        },
        'updating': {
          icon: '💾',
          text: 'Updating your trip...',
          subtext: 'Saving changes'
        },
        'optimizing': {
          icon: '🎯',
          text: 'Optimizing your route...',
          subtext: 'Finding the best order'
        },
        'learning': {
          icon: '📚',
          text: 'Gathering information...',
          subtext: 'Researching destination details'
        },
        default: {
          icon: '✈️',
          text: 'Thinking...',
          subtext: 'Processing your request'
        }
      };

      const currentLoading = loadingMessages[loadingState] || loadingMessages.default;

      return (
        <div key={index} className="flex gap-3 items-start mb-4">
          <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
          </div>
          <div className="bg-white px-5 py-3.5 max-w-[640px] shadow-sm border border-gray-100" style={{ borderRadius: '6px 22px 22px 22px' }}>
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl animate-bounce">{currentLoading.icon}</span>
                <span className="text-gray-800" style={FONTS.headingSemibold}>{currentLoading.text}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" style={{animationDelay: '0.2s'}}></div>
                  <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse" style={{animationDelay: '0.4s'}}></div>
                </div>
                <span className="text-sm text-gray-500" style={FONTS.body}>{currentLoading.subtext}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const isUser = message.role === 'user';
    
    // Check if this is the last assistant message (only show interactive buttons on last message)
    const lastAssistantIndex = messages.map((m, i) => ({ role: m.role, index: i }))
      .filter(m => m.role === 'assistant')
      .pop()?.index;
    const isLastAssistantMessage = index === lastAssistantIndex;
    
    // Check if this message contains destination recommendations
    // Checks bold format (**1. **2.), plain numbered format (1. 2.), or destinationOptions on message
    // IMPORTANT: Skip detection for "learn more" responses — their numbered lists are experiences, not destinations
    const hasDestinations = !isUser && !message.isLearnMore && (
      (message.content.includes('**1.') && message.content.includes('**2.')) ||
      (message.destinationOptions && message.destinationOptions.length > 0) ||
      ((/(?:^|\n)1\.\s+/.test(message.content)) && (/(?:^|\n)2\.\s+/.test(message.content)))
    );
    
    // Check if this is a smart detection confirmation (user specified destination with details)
    const isSmartDetection = !isUser && message.isSmartDetection === true;
    const extractedDetails = message.extractedDetails;
    const messageDestinations = message.destinationOptions; // Destinations stored in the message itself

    // Effective destinations for button rendering: prefer message-level, fallback to global state,
    // and as last resort try to extract from content if hasDestinations detected a pattern
    let effectiveDestinations = (messageDestinations && messageDestinations.length > 0)
      ? messageDestinations
      : (destinationOptions && destinationOptions.length > 0)
        ? destinationOptions
        : null;

    // Safety net: if content has destination patterns but no extracted data, try on-the-fly extraction
    // This catches cases where extractDestinationsFromMessage() failed during the response handler
    // but the content clearly has numbered destinations
    if (!effectiveDestinations && hasDestinations && !isUser && message.content) {
      const onTheFlyDests = extractDestinationsFromMessage(message.content);
      if (onTheFlyDests && onTheFlyDests.length > 0) {
        effectiveDestinations = onTheFlyDests;
      }
    }
    
    // Check if this is a "Learn More" response (ends with "Ready to create your [destination] itinerary?")
    // Detect via explicit flag (set by tell-me-more handler) or content pattern
    const isLearnMoreResponse = !isUser && (
      message.isLearnMore === true ||
      (!hasDestinations && message.content.includes('Ready to create your') && message.content.includes('itinerary?'))
    );

    // Extract destination name from "Learn More" response if applicable
    let learnMoreDestination = null;
    if (isLearnMoreResponse) {
      const match = message.content.match(/Ready to create your (.+?) itinerary\?/);
      if (match) {
        const destName = match[1];
        // Find matching destination from options
        learnMoreDestination = destinationOptions?.find(d =>
          d.name.toLowerCase() === destName.toLowerCase() ||
          destName.toLowerCase().includes(d.name.toLowerCase())
        );
      }
    }

    // Check if this is a multi-destination suggestions message with selectable stops
    const hasSuggestedStops = !isUser && message.suggestedStops === true;
    const isMultiPlanning = message.isAroundTheWorldPlanning || message.isMultiDestinationPlanning;

    // Extract suggested stops from the message content for individual selection
    let suggestedStopsList = [];
    if (hasSuggestedStops && message.content) {
      const stopMatches = message.content.match(/\*\*(?:Stop|Property) (\d+): ([^*]+)\*\*/g) || [];
      suggestedStopsList = stopMatches.map(m => {
        const match = m.match(/\*\*(?:Stop|Property) (\d+): ([^*]+)\*\*/);
        if (match) {
          const stopNum = parseInt(match[1]);
          const fullName = match[2].trim();
          // Extract just the city/property name (before the dash with continent)
          const name = fullName.split(' - ')[0].trim();
          const continent = fullName.split(' - ')[1]?.trim() || '';
          return { stopNum, name, fullName, continent };
        }
        return null;
      }).filter(Boolean);
    }

    // Only show interactive elements on the last assistant message
    const showInteractiveButtons = isLastAssistantMessage && !isProcessing;
    // Destination cards persist on ALL messages — never hide cards just because a new message is processing.
    // Previously, cards on the last assistant message would flash to raw text when the user typed a new message
    // because isProcessing=true + isLastAssistantMessage=true caused showDestinationCards to be false.
    const showDestinationCards = hasDestinations;

    // Debug: log button rendering conditions for destination messages
    if (hasDestinations && isLastAssistantMessage) {
      debugLog(`🔘 Button render check: showInteractive=${showInteractiveButtons}, isProcessing=${isProcessing}, hasDestinations=${hasDestinations}, isLearnMore=${isLearnMoreResponse}, effectiveDests=${effectiveDestinations?.length || 0}`);
    }

    // Check if this is a deep-dive card message
    const isDeepDive = !isUser && typeof message.content === 'string' && message.content.startsWith('__DEEP_DIVE__');
    let deepDiveData = null;
    if (isDeepDive) {
      try { deepDiveData = JSON.parse(message.content.replace('__DEEP_DIVE__', '')); } catch (e) { /* render as text */ }
    }

    return (
      <div key={index} className="mb-4">
        <div className={`flex gap-3 items-start ${isUser ? 'flex-row-reverse' : ''}`}>
          {!isUser && (
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
            </div>
          )}
          {isUser && (
            <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#313143' }}>
              <span className="text-white text-sm font-extrabold">{(profile?.full_name?.charAt(0)?.toUpperCase() || userProfile?.email?.charAt(0)?.toUpperCase() || 'P')}</span>
            </div>
          )}
          {isDeepDive && deepDiveData ? (
            /* Deep-dive structured card — dark hero + info rows */
            <div className="max-w-[540px] w-full">
              <div className="bg-white border border-gray-200/60 rounded-[22px] overflow-hidden shadow-md">
                {/* Dark hero header with topo overlay */}
                <div className="relative px-5 py-5 overflow-hidden" style={{ background: 'linear-gradient(135deg, #313143, #434360)' }}>
                  <div className="absolute inset-0 pointer-events-none opacity-15" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 200'%3E%3Cpath d='M0 50 Q150 20 300 60 T600 40' fill='none' stroke='%2314b8a6' stroke-width='1.2'/%3E%3Cpath d='M0 100 Q200 70 400 110 T600 90' fill='none' stroke='%2314b8a6' stroke-width='0.8'/%3E%3Cpath d='M0 150 Q150 130 350 160 T600 140' fill='none' stroke='%2314b8a6' stroke-width='1'/%3E%3C/svg%3E")`,
                    backgroundSize: 'cover'
                  }} />
                  <div className="relative z-10 flex justify-between items-start">
                    <div>
                      <h3 className="text-white text-xl font-bold" style={{ fontFamily: 'Georgia, serif' }}>{deepDiveData.destName}</h3>
                      {deepDiveData.desc && <p className="text-white/60 text-xs mt-1 line-clamp-1">{deepDiveData.desc}</p>}
                    </div>
                    {/* Price removed per user request */}
                  </div>
                </div>
                {/* Info rows */}
                <div className="px-5 py-4 space-y-0">
                  {deepDiveData.bestTime && (
                    <div className="flex gap-3 items-start py-3 border-b border-gray-100">
                      <div className="w-8 h-8 rounded-[10px] bg-teal-50 flex items-center justify-center flex-shrink-0">
                        <Sun className="w-4 h-4 text-teal-600" />
                      </div>
                      <div>
                        <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mb-0.5">Best time to visit</div>
                        <div className="text-[13px] text-[#313143] leading-relaxed">{deepDiveData.bestTime}</div>
                      </div>
                    </div>
                  )}
                  {deepDiveData.sampleDay && Array.isArray(deepDiveData.sampleDay) && (
                    <div className="flex gap-3 items-start py-3 border-b border-gray-100">
                      <div className="w-8 h-8 rounded-[10px] bg-teal-50 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-4 h-4 text-teal-600" />
                      </div>
                      <div>
                        <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mb-0.5">Sample day</div>
                        <div className="text-[13px] text-[#313143] leading-relaxed">
                          {deepDiveData.sampleDay.map((item, i) => (
                            <span key={i}>
                              {i > 0 && ' · '}
                              <span className="text-teal-600 font-bold text-[12px]">{item.time}</span> {item.activity}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Budget breakdown removed per user request — no pricing in tell me more */}
                  {deepDiveData.vibe && (
                    <div className="flex gap-3 items-start py-3">
                      <div className="w-8 h-8 rounded-[10px] flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(167,216,219,0.2)' }}>
                        <MapPin className="w-4 h-4 text-cyan-700" />
                      </div>
                      <div>
                        <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mb-0.5">The vibe</div>
                        <div className="text-[13px] text-[#313143] leading-relaxed">{deepDiveData.vibe}</div>
                      </div>
                    </div>
                  )}
                </div>
                {/* Action buttons */}
                <div className="flex gap-2 px-5 py-4 border-t border-gray-100">
                  <button
                    onClick={() => {
                      const destName = deepDiveData.destName;
                      const isAlreadyAdded = tripBuilderStops.some(stop =>
                        stop.name.toLowerCase().includes(destName.toLowerCase().split(' & ')[0]) ||
                        destName.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                      );
                      if (!isAlreadyAdded) {
                        addTripBuilderStop({ name: destName, continent: '', days: 3, hotel: null });
                      }
                    }}
                    disabled={tripBuilderStops.some(stop =>
                      stop.name.toLowerCase().includes(deepDiveData.destName.toLowerCase().split(' & ')[0]) ||
                      deepDiveData.destName.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                    )}
                    className={`px-5 py-2.5 rounded-full text-[13px] font-bold transition-all ${
                      tripBuilderStops.some(stop =>
                        stop.name.toLowerCase().includes(deepDiveData.destName.toLowerCase().split(' & ')[0]) ||
                        deepDiveData.destName.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                      ) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {tripBuilderStops.some(stop =>
                      stop.name.toLowerCase().includes(deepDiveData.destName.toLowerCase().split(' & ')[0]) ||
                      deepDiveData.destName.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                    ) ? 'Added' : `Add ${deepDiveData.destName} to Trip`}
                  </button>
                  <button
                    onClick={() => handleSend('Show me the other destination options')}
                    className="px-5 py-2.5 rounded-full text-[13px] font-bold text-teal-600 border border-teal-200/50 hover:bg-teal-50 transition-all"
                  >
                    Show me the others
                  </button>
                </div>
              </div>
            </div>
          ) : (
          <ScrollableMessageContent
            content={showDestinationCards && effectiveDestinations && effectiveDestinations.length > 0 && !isLearnMoreResponse
              ? (() => {
                  // Strip numbered destination listings when cards are shown (avoid duplicate content)
                  // Handle all format variations:
                  //   **1. Name** •    (number inside bold)
                  //   1. **Name** •    (number outside bold)
                  //   1. Name •        (no bold)
                  //   **Name** •       (unnumbered bold)
                  const stripped = message.content
                    .replace(/\*\*1\.\s[\s\S]*$/, '')       // **1. ... onwards
                    .replace(/(?:^|\n)1\.\s+\*\*[\s\S]*$/, '')  // 1. **... onwards
                    .replace(/(?:^|\n)1\.\s+[A-Z][\s\S]*$/, '') // 1. Name... onwards (plain numbered)
                    .trim();
                  // If stripping removed everything or left very little, show a brief intro
                  return stripped.length > 20 ? stripped : (stripped || "Here are some options I'd recommend:");
                })()
              : message.content}
            isUser={isUser}
            parseMarkdown={parseMarkdown}
          />
          )}
        </div>

        {/* YES/NO Confirmation Prompt - When user needs to explicitly confirm itinerary creation */}
        {showInteractiveButtons && message.confirmationPrompt && message.pendingDestination && (
          <div className="ml-11 mt-4">
            <div className="flex gap-3">
              <button
                onClick={() => {
                  // User confirmed - proceed with itinerary creation
                  handleDestinationSelect(message.pendingDestination, message.pendingIndex || 0);
                }}
                className="px-6 py-2.5 bg-slate-700 text-white rounded-full hover:bg-slate-800 hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 font-medium"
                style={FONTS.body}
              >
                <Check className="w-4 h-4" />
                Yes, Create Itinerary
              </button>
              <button
                onClick={() => {
                  // User declined - add a message and continue conversation
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: `No problem! What would you like to do instead? I can:\n\n• **Explore more destinations** - Tell me what kind of trip you're looking for\n• **Learn more about ${message.pendingDestination?.name || 'this destination'}** - I'll share more details\n• **Adjust your preferences** - Different dates, duration, or travel style\n\nJust let me know! ✈️`
                  }]);
                }}
                className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all flex items-center gap-2 font-medium"
                style={FONTS.body}
              >
                <X className="w-4 h-4" />
                No, Let's Explore More
              </button>
            </div>
          </div>
        )}

        {/* Vague Destination Suggestion Buttons - When AI resolved vague items like "a ski destination" */}
        {showInteractiveButtons && message.vagueDestinationOptions && message.vagueDestinationOptions.length > 0 && (
          <div className="ml-11 mt-3">
            <div className="flex flex-wrap gap-2">
              {message.vagueDestinationOptions.map((suggestion, sIdx) => {
                const alreadyAdded = tripBuilderStops.some(stop =>
                  stop.name.toLowerCase() === suggestion.toLowerCase()
                );
                return (
                  <button
                    key={sIdx}
                    disabled={alreadyAdded}
                    onClick={() => {
                      if (alreadyAdded) return;
                      addTripBuilderStop({
                        name: suggestion,
                        continent: '',
                        days: 3,
                        hotel: null
                      });
                      // Update message to reflect selection
                      setMessages(prev => prev.map((m, i) => {
                        if (i === index) {
                          return {
                            ...m,
                            content: m.content + `\n\nAdded **${suggestion}** to your trip!`,
                            vagueDestinationOptions: m.vagueDestinationOptions.filter((_, j) => j !== sIdx)
                          };
                        }
                        return m;
                      }));
                    }}
                    className={`px-4 py-2 rounded-full flex items-center gap-2 text-sm font-medium transition-all ${
                      alreadyAdded
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-[#313143] text-white hover:bg-[#3d3d52] hover:shadow-lg hover:scale-105'
                    }`}
                    style={FONTS.body}
                  >
                    <MapPin className="w-4 h-4" />
                    {alreadyAdded ? `${suggestion} (added)` : suggestion}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-gray-400 mt-2" style={FONTS.body}>
              Pick one, or type a different destination
            </p>
          </div>
        )}

        {/* Smart Detection Buttons - When user specified a destination with details */}
        {showInteractiveButtons && isSmartDetection && extractedDetails && messageDestinations && messageDestinations.length > 0 && (
          <div className="ml-11 mt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              {/* Add to Trip Button - PRIMARY action */}
              {(() => {
                const dest = messageDestinations[0];
                // Check if destination is already in trip builder
                const rawDest = extractedDetails.destination || dest.name || '';
                // Don't split hotel/resort names on "&" (e.g., "Grand Hotel & Spa")
                const destParts = isHotelOrResort(rawDest)
                  ? [rawDest]
                  : (() => {
                      const { text: destStr, placeholders: destCPH } = protectCompoundNames(rawDest);
                      return restoreCompoundNames(destStr.split(/\s*&\s*|\s+and\s+/i), destCPH).map(p => p.trim()).filter(Boolean);
                    })();
                const allInTrip = destParts.every(part =>
                  tripBuilderStops.some(stop =>
                    stop.name.toLowerCase().includes(part.toLowerCase().split(',')[0]) ||
                    part.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                  )
                );
                const stopCount = destParts.length;

                return (
                  <button
                    onClick={() => {
                      if (isButtonDebounced('add-to-trip-smart')) return;
                      if (allInTrip) return;

                      // Add each destination part as a separate stop
                      // Calculate total days
                      const totalDays = extractedDetails.duration
                        ? (extractedDetails.duration === 'weekend' ? 3 :
                           extractedDetails.duration === 'week' ? 7 :
                           extractedDetails.duration === 'long weekend' ? 4 :
                           extractedDetails.duration === 'few days' ? 4 :
                           parseInt(extractedDetails.duration) || 5)
                        : 5;
                      // Distribute days evenly, giving remainder to first stops
                      const baseDays = Math.max(2, Math.floor(totalDays / destParts.length));
                      const remainder = totalDays - (baseDays * destParts.length);

                      destParts.forEach((partName, partIdx) => {
                        addTripBuilderStop({
                          name: partName,
                          continent: dest.continent || '',
                          days: partIdx < remainder ? baseDays + 1 : baseDays,
                          hotel: null
                        });
                      });

                      // Add confirmation message
                      const destList = destParts.length === 1 ? destParts[0] : destParts.join(' & ');
                      setMessages(prev => [...prev, {
                        role: 'assistant',
                        content: `Added **${destList}** to your trip! 🎉\n\nYou can see ${destParts.length > 1 ? 'them' : 'it'} in the **Trip Builder** on the right. Feel free to:\n- Adjust the number of days for each stop\n- Drag to reorder stops\n- Add more destinations\n\nWhen you're ready, click **"Create Itinerary"** to start planning!`
                      }]);
                    }}
                    disabled={allInTrip}
                    className={`px-5 py-2.5 rounded-full hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 ${
                      allInTrip
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-[#313143] text-white hover:bg-[#3d3d52]'
                    }`}
                    style={FONTS.body}
                  >
                    {allInTrip ? (
                      <>
                        <Check className="w-4 h-4" />
                        Added to Trip
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        Add to Trip{stopCount > 1 ? ` (${stopCount} stops)` : ''}
                      </>
                    )}
                  </button>
                );
              })()}

              {/* Create Itinerary Button - Secondary action */}
              <button
                onClick={() => {
                  if (isButtonDebounced('create-itinerary')) return;
                  const dest = messageDestinations[0];

                  // Calculate all preferences from extracted details
                  const durationDays = extractedDetails.duration
                    ? (extractedDetails.duration === 'weekend' ? 3 :
                       extractedDetails.duration === 'week' ? 7 :
                       extractedDetails.duration === 'long weekend' ? 4 :
                       extractedDetails.duration === 'few days' ? 4 :
                       parseInt(extractedDetails.duration) || 5)
                    : null;

                  // Convert timing to month
                  const monthMap = {
                    'january': 'January', 'february': 'February', 'march': 'March', 'april': 'April',
                    'may': 'May', 'june': 'June', 'july': 'July', 'august': 'August',
                    'september': 'September', 'october': 'October', 'november': 'November', 'december': 'December',
                    'spring': 'March', 'summer': 'June', 'fall': 'October', 'autumn': 'October', 'winter': 'December'
                  };
                  const travelMonth = extractedDetails.timing ? monthMap[extractedDetails.timing.toLowerCase()] : null;

                  // Set ALL preferences at once
                  setUserProfile(prev => ({
                    ...prev,
                    preferences: {
                      ...prev.preferences,
                      ...(durationDays && { duration: durationDays }),
                      ...(travelMonth && { travelMonths: [travelMonth], travelPeriod: travelMonth }),
                      ...(extractedDetails.companion && { travelCompanions: extractedDetails.companion }),
                      pendingDestination: null // Clear pending
                    }
                  }));

                  // Go to personalization
                  handleDestinationSelect(dest, 0);
                }}
                className="px-5 py-2.5 bg-white border-2 border-[#a7d8db] text-[#313143] rounded-full hover:bg-[#f0f9fa] transition-all flex items-center gap-2"
                style={FONTS.body}
              >
                <Sparkles className="w-4 h-4" />
                Create Itinerary
              </button>
              
              {/* Explore Similar Destinations Button */}
              <button
                onClick={async () => {
                  if (isButtonDebounced('explore-similar')) return;
                  setLoadingState('searching');
                  setIsProcessing(true);
                  
                  // Build a description of what they're looking for
                  const tripDescription = [];
                  if (extractedDetails.companion) tripDescription.push(`traveling with ${extractedDetails.companion}`);
                  if (extractedDetails.interests) tripDescription.push(`interested in ${extractedDetails.interests}`);
                  if (extractedDetails.timing) tripDescription.push(`in ${extractedDetails.timing}`);
                  
                  const searchQuery = `Show me ${extractedDetails.destination} and 2 similar destinations for someone ${tripDescription.join(', ')}. Include ${extractedDetails.destination} as option #1.`;
                  
                  const newMessages = [...messages, { role: 'user', content: `Explore similar destinations to ${extractedDetails.destination}` }];
                  setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);
                  
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                      method: "POST",
                      headers: await getApiHeaders(),
                      body: JSON.stringify({
                        model: "claude-sonnet-4-20250514",
                        max_tokens: 2000,
                        system: `You are a travel expert. The user wants to see ${extractedDetails.destination} alongside similar alternatives.
                        
Show exactly 4 destinations in this format:

**1. ${extractedDetails.destination}** • $$ • Xhr flight${userProfile.preferences.homeCity ? ` from ${userProfile.preferences.homeCity}` : ''}

Brief description of why it's great for ${tripDescription.join(', ') || 'this trip'}.

**Best time to visit:** Season/months for [reason].
**Notes:** Any other seasonal info.

**2. [Similar Destination]** • $$ • Xhr flight${userProfile.preferences.homeCity ? ` from ${userProfile.preferences.homeCity}` : ''}

Why this is a great alternative with similar vibes.

**Best time to visit:** Season/months for [reason].
**Notes:** Any other seasonal info.

**3. [Another Similar Destination]** • $$ • Xhr flight${userProfile.preferences.homeCity ? ` from ${userProfile.preferences.homeCity}` : ''}

Why this is another great option.

**Best time to visit:** Season/months for [reason].
**Notes:** Any other seasonal info.

**4. [Another Destination]** • $$ • Xhr flight${userProfile.preferences.homeCity ? ` from ${userProfile.preferences.homeCity}` : ''}

A unique alternative worth considering.

**Best time to visit:** Season/months for [reason].
**Notes:** Any other seasonal info.

IMPORTANT: ${extractedDetails.destination} MUST be option #1. Options 2, 3, and 4 should be alternatives that match: ${tripDescription.join(', ')}.`,
                        messages: [{ role: "user", content: searchQuery }]
                      })
                    });
                    
                    const data = await response.json();
                    if (!data?.content?.[0]?.text) {
                      throw new Error('Invalid API response: missing content');
                    }
                    const assistantMessage = data.content[0].text;

                    // Extract destinations from response
                    const destinations = extractDestinationsFromMessage(assistantMessage);
                    if (destinations.length > 0) {
                      setDestinationOptions(destinations);
                      setShownDestinations(prev => [...prev, ...destinations.map(d => d.name)]);
                    }

                    setMessages([...newMessages, {
                      role: 'assistant',
                      content: assistantMessage,
                      ...(destinations.length > 0 ? { destinationOptions: destinations } : {})
                    }]);
                  } catch (error) {
                    debugError('Error fetching similar destinations:', error);
                    setMessages([...newMessages, { role: 'assistant', content: 'Sorry, I had trouble finding similar destinations. Please try again.' }]);
                  }
                  
                  setLoadingState(null);
                  setIsProcessing(false);
                  
                  // Clear pending destination
                  setUserProfile(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, pendingDestination: null }
                  }));
                }}
                className="px-5 py-2.5 bg-white border-2 border-[#a7d8db] text-[#313143] rounded-full hover:bg-[#f0f9fa] transition-all flex items-center gap-2"
                style={FONTS.body}
              >
                <Compass className="w-4 h-4" />
                Explore Similar Destinations
              </button>
              
              {/* Learn More Button */}
              <button
                onClick={async () => {
                  if (isButtonDebounced('learn-more')) return;
                  setLoadingState('learning');
                  setIsProcessing(true);
                  
                  const newMessages = [...messages, { role: 'user', content: `Tell me more about ${extractedDetails.destination}` }];
                  setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);
                  
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                      method: "POST",
                      headers: await getApiHeaders(),
                      body: JSON.stringify({
                        model: "claude-sonnet-4-20250514",
                        max_tokens: 1500,
                        system: `You are a travel expert. Provide detailed, engaging information about ${extractedDetails.destination}.

Cover:
1. What makes it special and unique
2. Best time to visit (weather, seasons, festivals)${extractedDetails.timing ? ` - specifically mention what ${extractedDetails.timing} is like` : ''}
3. Top 5 must-see attractions or experiences${extractedDetails.interests ? ` - focus on ${extractedDetails.interests}` : ''}
4. Local culture, food, and lifestyle highlights
5. Important travel tips

${extractedDetails.companion ? `Tailor recommendations for someone traveling with their ${extractedDetails.companion}.` : ''}

Keep it engaging but concise (max 350 words). End with: "Ready to plan your ${extractedDetails.destination} trip?"`,
                        messages: [{ role: "user", content: `Tell me more about ${extractedDetails.destination}` }]
                      })
                    });
                    
                    const data = await response.json();
                    if (!data?.content?.[0]?.text) {
                      throw new Error('Invalid API response: missing content');
                    }
                    setMessages([...newMessages, {
                      role: 'assistant',
                      content: data.content[0].text,
                      isSmartDetection: true, // Keep showing buttons
                      extractedDetails: extractedDetails,
                      destinationOptions: messageDestinations // Preserve destinations
                    }]);
                  } catch (error) {
                    debugError('Error fetching destination info:', error);
                    setMessages([...newMessages, { role: 'assistant', content: 'Sorry, I had trouble getting more information. Please try again.' }]);
                  }
                  
                  setLoadingState(null);
                  setIsProcessing(false);
                }}
                className="px-5 py-2.5 bg-white border-2 border-[#a7d8db] text-[#313143] rounded-full hover:bg-[#f0f9fa] transition-all flex items-center gap-2"
                style={FONTS.body}
              >
                <MapPin className="w-4 h-4" />
                Learn More About {extractedDetails.destination}
              </button>
            </div>
          </div>
        )}
        
        {/* Add destination action buttons if this message has destinations */}
        {/* Note: Don't show destination buttons for "Learn More" responses - those have their own buttons */}
        {/* Uses effectiveDestinations: prefers message-level destinationOptions, falls back to global state */}
        {showDestinationCards && !isLearnMoreResponse && effectiveDestinations && effectiveDestinations.length > 0 && (() => {
          const filteredDests = effectiveDestinations.filter(d => {
            if (/^\[.*\]$/.test(d.name) || /^Destination\s*Name$/i.test(d.name)) return false;
            if (d.desc && /CRITICAL:|Do NOT|MANDATORY|Brief compelling description/i.test(d.desc)) return false;
            return true;
          });
          // Group by location if hotels have location labels (multi-city hotel mode)
          const locations = [...new Set(filteredDests.map(d => d.location).filter(Boolean))];
          const isMultiCityHotel = locations.length > 1;
          const groups = isMultiCityHotel
            ? locations.map(loc => ({ city: loc, items: filteredDests.filter(d => d.location === loc) }))
            : [{ city: null, items: filteredDests }];

          return groups.map((group, gi) => (
          <div key={gi} className="ml-11 mt-3">
            {group.city && (
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" style={FONTS.headingSemibold}>
                {titleCaseCity(group.city)}
              </div>
            )}
            <div className="grid gap-3 grid-cols-2">
            {group.items.map((dest, idx) => {
              // Check if this destination is already in the trip builder
              const destNameParts = dest.name.split(/\s*[&+]\s*/).map(p => p.trim().toLowerCase().split(',')[0]);
              const isInTripBuilder = destNameParts.every(part =>
                tripBuilderStops.some(stop =>
                  stop.name.toLowerCase().includes(part) ||
                  part.includes(stop.name.toLowerCase().split(',')[0])
                )
              );
              // Hide card entirely if all destinations are already added
              if (isInTripBuilder) return null;
              const isTopPick = idx === 0;

              // Handler: add destination to trip builder (extracted for cleaner JSX)
              const handleAddDest = () => {
                if (isInTripBuilder) return;
                // Hotel mode: add as hotel stop with location context
                // (Bug 2 rename: 'resort' → 'hotel'.)
                // Use extracted duration from chat (e.g., "paris for 4 days") as fallback
                if (tripType === 'hotel') {
                  addTripBuilderStop({
                    name: dest.name,
                    stopType: 'hotel',
                    stayOnProperty: true,
                    continent: dest.continent || '',
                    days: dest.suggestedDays || tripContext?.duration || userProfile?.preferences?.duration || 3,
                    hotel: dest.name,
                  });
                  return;
                }
                let parts = [];
                const destName = dest.name;
                if (destName.includes('(') && destName.includes(')')) {
                  parts = [destName];
                } else {
                  const separators = /\s*[&]\s*|\s+and\s+/i;
                  const { text: pDest, placeholders: pCPH } = protectCompoundNames(destName);
                  parts = restoreCompoundNames(pDest.split(separators), pCPH).map(p => p.trim()).filter(Boolean);
                }
                // Bug 2 fix — auto-populate hotel flag dynamically when the
                // destination card is itself tagged as a hotel by the LLM
                // (V2 backend will set `dest.type`). Mirrors mobile's
                // 3-source classifier in ChatScreen.handleAddDest.
                const destIsHotel = dest.type === 'hotel'
                  || tripType === 'hotel'
                  || isHotelOrResort(destName);
                if (parts.length > 1) {
                  parts.forEach((partName) => {
                    addTripBuilderStop({ name: partName, continent: dest.continent || '', days: Math.ceil((dest.suggestedDays || 6) / parts.length), hotel: null });
                  });
                } else {
                  addTripBuilderStop({
                    name: dest.name,
                    continent: dest.continent || '',
                    days: dest.suggestedDays || 3,
                    hotel: null,
                    ...(destIsHotel ? { stopType: 'hotel', stayOnProperty: true } : {}),
                  });
                }
              };

              // Handler: learn more — returns structured deep-dive card data
              const handleLearnMore = async () => {
                setLoadingState('learning');
                setIsProcessing(true);
                const newMessages = [...messages, { role: 'user', content: `Tell me more about ${dest.name}` }];
                setMessages(newMessages);
                setMessages([...newMessages, { role: 'assistant', content: 'loading' }]);
                const moreInfoSystemPrompt = `You're a travel expert. The user is considering ${dest.name} for their trip.

Return a JSON object with these exact keys (no markdown, pure JSON):
{
  "bestTime": "When to visit and why (1-2 sentences)",
  "sampleDay": [{"time":"9:00 AM","activity":"Activity name"},{"time":"12:00 PM","activity":"Lunch spot"},...],
  "budget": "Budget breakdown: hotels, meals, activities costs (1-2 sentences)",
  "vibe": "The feel of the destination - culture, food, atmosphere (2-3 sentences)",
  "perDayAvg": "$XX"
}

Keep it concise, evocative, and useful. Use local currency where relevant.`;
                try {
                  const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                    method: "POST",
                    headers: await getApiHeaders(),
                    body: JSON.stringify({
                      model: "claude-sonnet-4-20250514",
                      max_tokens: 1000,
                      system: moreInfoSystemPrompt,
                      messages: [{ role: "user", content: `Tell me more about ${dest.name} as a travel destination. Return JSON only.` }]
                    })
                  });
                  const data = await response.json();
                  if (!data?.content?.[0]?.text) throw new Error('Invalid API response');
                  const rawText = data.content[0].text;
                  // Try to parse as JSON for deep-dive card, fall back to text
                  let deepDiveData = null;
                  try {
                    const jsonMatch = rawText.match(/\{[\s\S]*\}/);
                    if (jsonMatch) deepDiveData = JSON.parse(jsonMatch[0]);
                  } catch (e) { /* fall back to text */ }

                  if (deepDiveData) {
                    // Render as structured deep-dive card
                    const deepDiveContent = `__DEEP_DIVE__${JSON.stringify({ ...deepDiveData, destName: dest.name, price: dest.price, desc: dest.desc })}`;
                    setMessages(prev => {
                      const updated = [...prev];
                      updated[updated.length - 1] = { role: 'assistant', content: deepDiveContent };
                      return updated;
                    });
                  } else {
                    setMessages(prev => {
                      const updated = [...prev];
                      updated[updated.length - 1] = { role: 'assistant', content: rawText };
                      return updated;
                    });
                  }
                } catch (error) {
                  debugError('Error getting more info:', error);
                  setMessages(prev => {
                    const updated = [...prev];
                    updated[updated.length - 1] = { role: 'assistant', content: `Sorry, I had trouble getting more information about ${dest.name}. Please try again!` };
                    return updated;
                  });
                }
                setLoadingState(null);
                setIsProcessing(false);
              };

              // Build travel time label — only show if we have a home city to calculate from
              const hasHomeCity = !!userProfile.preferences?.homeCity;
              const travelLabel = dest.flightHours
                ? `${dest.flightHours}hr from ${toTitleCase(userProfile.preferences.homeCity || 'your city')}`
                : dest.driveHours
                  ? dest.driveHours < 1
                    ? `${Math.round(dest.driveHours * 60)}min drive`
                    : `${dest.driveHours}hr drive`
                  : (dest.drivePlaceholder && hasHomeCity) ? 'Calculating...' : null;

              return (
              <div key={idx} className={`bg-white border rounded-2xl p-4 transition-all hover:shadow-lg hover:-translate-y-0.5 group ${isTopPick ? 'border-teal-300 bg-teal-50/30' : 'border-gray-200 hover:border-teal-300'}`}>
                {/* Top row: name + tags */}
                <div className="flex items-start justify-between mb-1.5">
                  <h4 className="font-extrabold text-[#313143] text-[14px] leading-tight tracking-tight" style={FONTS.heading}>
                    {dest.name}
                  </h4>
                  {/* Top pick badge removed */}
                </div>
                {/* Location + Price + flight metadata row */}
                <div className="flex items-center gap-2.5 mb-2 text-[11px] text-gray-500 font-semibold flex-wrap">
                  {dest.location && (
                    <span className="text-gray-400">
                      {dest.location}
                    </span>
                  )}
                  {dest.price && (
                    <span className="flex items-center gap-1">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2.5"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/></svg>
                      {dest.price}
                    </span>
                  )}
                  {travelLabel && (
                    <span className="flex items-center gap-1">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#a7d8db" strokeWidth="2.5"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                      {travelLabel}
                    </span>
                  )}
                </div>
                {/* Description — truncated to ~150 chars for cleaner cards */}
                <p className="text-[12px] text-gray-600 leading-relaxed mb-2" style={FONTS.body}>
                  {(() => {
                    const desc = dest.desc || 'A stunning destination waiting to be explored.';
                    return desc.length > 160 ? desc.substring(0, 157) + '...' : desc;
                  })()}
                </p>
                {/* Best time hint */}
                {dest.bestTime && (
                  <div className="text-[10px] font-bold text-teal-600 mb-2">Best: {dest.bestTime}</div>
                )}
                {/* Action buttons - mockup style */}
                <div className="flex items-center gap-1.5 mt-2">
                  <button
                    onClick={handleAddDest}
                    disabled={isInTripBuilder}
                    className={`px-3.5 py-1.5 rounded-full text-[11px] font-bold transition-all ${
                      isInTripBuilder
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-teal-600 text-white hover:bg-teal-700'
                    }`}
                  >
                    {isInTripBuilder ? 'Added' : 'Add to Trip'}
                  </button>
                  <button
                    onClick={handleLearnMore}
                    className="px-3.5 py-1.5 rounded-full text-[11px] font-bold text-teal-600 border border-teal-200/50 hover:bg-teal-50 transition-all"
                  >
                    Tell me more
                  </button>
                </div>
              </div>
            );
            })}
            </div>
          </div>
          ));
          })()}

          {/* Show Different Options — after all destination/hotel cards */}
          {showDestinationCards && !isLearnMoreResponse && effectiveDestinations && effectiveDestinations.length > 0 && isLastAssistantMessage && (
          <div className="ml-11 mt-2">
            <div className="pt-2 border-t border-gray-200 mt-2">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    setIsProcessing(true);
                    const newMessages = [...messages, {
                      role: 'user',
                      content: 'Can you suggest some different destinations?'
                    }];
                    setMessages(newMessages);

                    // Trigger a new destination search
                    await handleSend('Can you suggest some different destinations?');
                  }}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all flex items-center gap-2"
                  style={FONTS.body}
                >
                  <RefreshCw className="w-4 h-4" />
                  Show Different Options
                </button>

                <div className="flex items-center gap-2 px-4 py-2 bg-[#f0f9fa] border-2 border-[#a7d8db] text-[#313143] rounded-full" style={FONTS.body}>
                  <MessageCircle className="w-4 h-4" />
                  <span>Type below to refine more!</span>
                </div>
              </div>
            </div>
          </div>
          )}
        
        {/* Multi-destination/Around-the-world suggested stops - individual selection */}
        {showInteractiveButtons && hasSuggestedStops && isMultiPlanning && suggestedStopsList.length > 0 && (() => {
          const previousDetails = message.extractedDetails || {};
          const firstStop = previousDetails.destination || previousDetails.hotelName || '';
          const isAroundTheWorld = message.isAroundTheWorldPlanning;
          const tripTypeLabel = isAroundTheWorld ? 'around-the-world' : (tripType === 'hotel' ? 'resort-hopping' : 'multi-city');

          // Check which stops are currently selected
          const isStopSelected = (stopName) => selectedMultiStops.some(s => s.name === stopName);
          const selectedCount = selectedMultiStops.length;

          return (
            <div className="ml-11 mt-4 space-y-3">
              <p className="text-sm text-gray-600 mb-3" style={FONTS.body}>
                Select the stops you want to include:
              </p>

              {/* First stop (already confirmed) */}
              {firstStop && (
                <div className="flex flex-wrap gap-2 items-center p-3 bg-teal-50 rounded-xl border border-brand-200">
                  <div className="w-6 h-6 bg-[#313143] text-white rounded-full flex items-center justify-center text-sm font-bold">1</div>
                  <span className="font-medium text-teal-800" style={FONTS.body}>{firstStop}</span>
                  <span className="text-xs text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">Your first stop</span>
                  {previousDetails.hotelName && previousDetails.hotelName !== firstStop && (
                    <span className="text-xs text-teal-600">at {previousDetails.hotelName}</span>
                  )}
                </div>
              )}

              {/* Suggested stops - toggleable selection */}
              <div className="space-y-2">
                {suggestedStopsList.map((stop, idx) => {
                  const isSelected = isStopSelected(stop.name);
                  const stopNumber = firstStop ? idx + 2 : idx + 1;

                  return (
                    <div key={idx} className={`flex flex-wrap gap-2 items-center p-3 rounded-xl border-2 transition-all ${
                      isSelected
                        ? 'bg-teal-50 border-teal-400'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}>
                      <button
                        onClick={() => {
                          if (isSelected) {
                            // Remove from selection
                            setSelectedMultiStops(prev => prev.filter(s => s.name !== stop.name));
                          } else {
                            // Add to selection
                            setSelectedMultiStops(prev => [...prev, { name: stop.name, continent: stop.continent }]);
                          }
                        }}
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                          isSelected
                            ? 'bg-[#a7d8db] text-[#313143]'
                            : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                        }`}
                      >
                        {isSelected ? <Check className="w-4 h-4" /> : stopNumber}
                      </button>
                      <span className={`font-medium flex-1 ${isSelected ? 'text-teal-800' : 'text-gray-700'}`} style={FONTS.body}>
                        {stop.name}
                      </span>
                      {stop.continent && (
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          isSelected ? 'bg-teal-100 text-teal-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {stop.continent}
                        </span>
                      )}
                      <button
                        onClick={() => {
                          handleSend(`Tell me more about ${stop.name}`);
                        }}
                        className="px-3 py-1 text-xs bg-white border border-[#a7d8db] text-[#313143] rounded-full hover:bg-[#f0f9fa] transition-all flex items-center gap-1"
                        style={FONTS.body}
                      >
                        <MapPin className="w-3 h-3" />
                        Learn More
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Action buttons */}
              <div className="pt-3 border-t border-gray-200 mt-3 space-y-3">
                {/* Selection summary */}
                {selectedCount > 0 && (
                  <p className="text-sm text-teal-600" style={FONTS.body}>
                    ✓ {selectedCount} stop{selectedCount > 1 ? 's' : ''} selected
                    {firstStop && ` (plus ${firstStop.split(',')[0]})`}
                  </p>
                )}

                <div className="flex flex-wrap gap-2">
                  {/* Add selected stops to Trip Builder */}
                  {selectedCount > 0 && (
                    <button
                      onClick={() => {
                        // Add first stop to trip builder if not already there
                        const firstStopDetails = previousDetails || {};
                        const firstStopAlreadyAdded = tripBuilderStops.some(s =>
                          s.name.toLowerCase() === firstStop?.toLowerCase()
                        );

                        if (firstStop && !firstStopAlreadyAdded) {
                          addTripBuilderStop({
                            name: firstStop,
                            continent: '', // Could extract from firstStop
                            days: 3,
                            hotel: firstStopDetails.hotelName || null
                          });
                        }

                        // Add all selected stops to trip builder (avoid duplicates)
                        selectedMultiStops.forEach(stop => {
                          const alreadyAdded = tripBuilderStops.some(s =>
                            s.name.toLowerCase() === stop.name.toLowerCase()
                          );
                          if (!alreadyAdded) {
                            addTripBuilderStop({
                              name: stop.name,
                              continent: stop.continent || '',
                              days: 3,
                              hotel: null
                            });
                          }
                        });

                        // Show confirmation message
                        setMessages(prev => [...prev, {
                          role: 'assistant',
                          content: `I've added ${selectedCount} stop${selectedCount > 1 ? 's' : ''} to your trip! 🎉

You can see your trip building in the sidebar on the right. From there you can:
• **Drag** stops to reorder them
• **Adjust** the number of days for each stop
• **Remove** any stops you change your mind about
• **Add more** stops if you'd like

When you're happy with your trip, click **"Create Itinerary"** in the sidebar to proceed!`,
                        }]);

                        // Clear selections
                        setSelectedMultiStops([]);
                      }}
                      className="px-5 py-2.5 bg-slate-700 text-white rounded-full hover:bg-slate-800 hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2"
                      style={FONTS.body}
                    >
                      <Plus className="w-4 h-4" />
                      Add to Trip ({firstStop ? selectedCount + 1 : selectedCount} stops)
                    </button>
                  )}

                  {/* Select all button */}
                  <button
                    onClick={() => {
                      if (selectedCount === suggestedStopsList.length) {
                        // Deselect all
                        setSelectedMultiStops([]);
                      } else {
                        // Select all
                        setSelectedMultiStops(suggestedStopsList.map(s => ({ name: s.name, continent: s.continent })));
                      }
                    }}
                    className={`px-4 py-2 rounded-full transition-all flex items-center gap-2 ${
                      selectedCount === suggestedStopsList.length
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                    }`}
                    style={FONTS.body}
                  >
                    <Check className="w-4 h-4" />
                    {selectedCount === suggestedStopsList.length ? 'Deselect All' : 'Select All'}
                  </button>

                  {/* Different suggestions */}
                  <button
                    onClick={() => {
                      setSelectedMultiStops([]);
                      handleSend('Show me different destination suggestions');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all flex items-center gap-2"
                    style={FONTS.body}
                  >
                    <RefreshCw className="w-4 h-4" />
                    Different Suggestions
                  </button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Add buttons for "Learn More" responses */}
        {showInteractiveButtons && isLearnMoreResponse && learnMoreDestination && (
          <div className="ml-11 mt-3 space-y-3">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => {
                  const isAlreadyAdded = tripBuilderStops.some(stop =>
                    stop.name.toLowerCase().includes(learnMoreDestination.name.toLowerCase().split(' & ')[0]) ||
                    learnMoreDestination.name.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                  );
                  if (!isAlreadyAdded) {
                    addTripBuilderStop({ name: learnMoreDestination.name, continent: learnMoreDestination.continent || '', days: learnMoreDestination.suggestedDays || 3, hotel: null });
                  }
                }}
                disabled={tripBuilderStops.some(stop =>
                  stop.name.toLowerCase().includes(learnMoreDestination.name.toLowerCase().split(' & ')[0]) ||
                  learnMoreDestination.name.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                )}
                className={`px-4 py-2 rounded-full hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 ${
                  tripBuilderStops.some(stop =>
                    stop.name.toLowerCase().includes(learnMoreDestination.name.toLowerCase().split(' & ')[0]) ||
                    learnMoreDestination.name.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                  ) ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-teal-600 text-white hover:bg-teal-700'
                }`}
                style={FONTS.body}
              >
                <Check className="w-4 h-4" />
                {tripBuilderStops.some(stop =>
                  stop.name.toLowerCase().includes(learnMoreDestination.name.toLowerCase().split(' & ')[0]) ||
                  learnMoreDestination.name.toLowerCase().includes(stop.name.toLowerCase().split(',')[0])
                ) ? 'Added' : `Add ${learnMoreDestination.name} to Trip`}
              </button>
              
              <button
                onClick={() => {
                  // Find the LAST message with destination options (most recent)
                  let destMessageIndex = -1;
                  for (let i = messages.length - 1; i >= 0; i--) {
                    if (messages[i].role === 'assistant' &&
                        messages[i].content.includes('**1.') &&
                        messages[i].content.includes('**2.') &&
                        (messages[i].content.includes('**3.') || messages[i].content.includes('**4.'))) {
                      destMessageIndex = i;
                      break;
                    }
                  }
                  
                  if (destMessageIndex !== -1) {
                    // Find the message element by its index
                    const chatContainer = document.querySelector('.overflow-y-auto');
                    if (chatContainer) {
                      // Get all message divs
                      const messageElements = chatContainer.querySelectorAll('.mb-4');
                      if (messageElements[destMessageIndex]) {
                        // Scroll to the destination message
                        messageElements[destMessageIndex].scrollIntoView({ 
                          behavior: 'smooth', 
                          block: 'start' 
                        });
                      }
                    }
                  }
                }}
                className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-all flex items-center gap-2"
                style={FONTS.body}
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Latest Options
              </button>
              
              <button
                onClick={async () => {
                  setIsProcessing(true);
                  const newMessages = [...messages, { 
                    role: 'user', 
                    content: 'Can you suggest some different destinations?' 
                  }];
                  setMessages(newMessages);
                  await handleSend('Can you suggest some different destinations?');
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-all flex items-center gap-2"
                style={FONTS.body}
              >
                <RefreshCw className="w-4 h-4" />
                Show Different Options
              </button>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-blue-50 border-2 border-blue-200 text-blue-700 rounded-full" style={FONTS.body}>
                <MessageCircle className="w-4 h-4" />
                <span>Type below to refine more!</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLandingPage = () => {
    const recentTrips = savedItinerariesList.slice(0, 4);
    const userName = user?.user_metadata?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || '';
    const userInitial = (user?.user_metadata?.full_name?.[0] || user?.email?.[0] || 'U').toUpperCase();
    const tripCount = savedItinerariesList.length;
    // Compute unique countries from saved trips
    const uniqueCountries = new Set(savedItinerariesList.map(t => t.destination?.split(',').pop()?.trim()).filter(Boolean)).size;
    const totalDaysPlanned = savedItinerariesList.reduce((sum, t) => sum + (t.itinerary_data?.days?.length || 0), 0);

    const formatTimeAgoWeb = (dateStr) => {
      if (!dateStr) return '';
      const now = new Date();
      const then = new Date(dateStr);
      const mins = Math.floor((now - then) / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hours = Math.floor(mins / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      if (days < 7) return `${days}d ago`;
      return then.toLocaleDateString();
    };

    return (
    <div className="min-h-screen relative" style={{ background: '#F5F5F0' }}>
      {/* Topo background */}
      <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 900' preserveAspectRatio='none'%3E%3Cpath d='M0 120 Q300 60 600 140 T1200 100' fill='none' stroke='%230d9488' stroke-width='1.2'/%3E%3Cpath d='M0 300 Q400 250 700 320 T1200 280' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 520 Q300 480 600 540 T1200 510' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 750 Q250 720 550 770 T1200 740' fill='none' stroke='%23313143' stroke-width='1'/%3E%3C/svg%3E")`, backgroundSize: 'cover', opacity: 0.035 }} />
      {/* Top Bar — matching chat page header */}
      <div className="flex items-center justify-between" style={{
        borderBottom: '1px solid rgba(31,29,59,0.08)',
        background: 'white',
        padding: '14px 24px',
        position: 'relative',
        zIndex: 50,
      }}>
        <div className="flex items-center gap-2.5">
          {/* Paper-plane icon + inline wordmark + tagline. PR c7a108d3 had
             dropped the text JSX based on a comment that the artwork in
             arrivo-icon.png already contained it — but that asset is the
             paper plane only (arrivo-logo.png has the wordmark/tagline,
             but at header sizes the baked-in text is too small to read).
             Restored. */}
          <img src="/arrivo-icon.png" alt="Arrivo" className="h-10 w-auto object-contain" />
          <div className="flex flex-col">
            <h1 className="text-[22px] font-extrabold leading-none" style={{ fontFamily: "'Gulfs Display', 'Batangas', sans-serif", color: '#313143', letterSpacing: '0.05em' }}>
              arrivo
            </h1>
            <span className="text-[10px] font-medium mt-0.5" style={{ fontFamily: "'Batangas', sans-serif", color: '#097066', letterSpacing: '-0.01em' }}>
              plan less. explore more.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <UserMenu onOpenSettings={() => setShowSettings(true)} onOpenSavedItineraries={handleOpenSavedItineraries} onUpgrade={goToPricing} onOpenAnalytics={() => setShowAnalytics(true)} />
        </div>
      </div>

      {/* Responsive grid styles for home page */}
      <style>{`
        .home-app-grid {
          grid-template-columns: 220px 1fr;
        }
        .home-content-grid {
          grid-template-columns: 1fr 300px;
        }
        @media (max-width: 768px) {
          .home-app-grid {
            grid-template-columns: 1fr !important;
          }
          .home-app-grid > .border-r {
            border-right: none !important;
            border-bottom: 1px solid rgba(31,29,59,0.06);
            padding-right: 0;
            padding-bottom: 12px;
            margin-bottom: 12px;
          }
          .home-content-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      {/* Page Content — card container */}
      <div className="max-w-[1480px] mx-auto px-6 pt-4 pb-16 relative z-10">
        <div className="rounded-[28px] border" style={{
          borderColor: 'rgba(31,29,59,0.08)',
          background: 'rgba(255,255,255,0.97)',
          boxShadow: '0 22px 60px rgba(28,41,53,0.07)',
        }}>
          <div className="p-7">
            {/* App Grid: Left Sidebar + Main Content — stacks on narrow screens */}
            <div className="grid gap-6 home-app-grid" style={{ minHeight: '900px' }}>

              {/* ── Left Sidebar ── */}
              <div className="border-r pr-3" style={{ borderColor: 'rgba(31,29,59,0.06)' }}>
                {/* Profile Card */}
                <div className="rounded-[22px] border p-[18px] mb-3.5" style={{
                  borderColor: 'rgba(31,29,59,0.08)',
                  background: 'rgba(255,255,255,0.94)',
                  boxShadow: '0 4px 16px rgba(28,41,53,0.05)',
                }}>
                  <div className="flex items-center gap-3 mb-3.5">
                    <div className="w-[46px] h-[46px] rounded-[14px] flex items-center justify-center text-white font-extrabold text-[17px]" style={{ background: 'linear-gradient(135deg, #0d9488, #2dd4bf)' }}>
                      {userInitial}
                    </div>
                    <div>
                      <div className="text-[15px] font-extrabold" style={{ color: '#1e293b' }}>{userName || 'Wanderer'}</div>
                    </div>
                  </div>
                  <div className="text-xs leading-relaxed mb-3" style={{ color: '#64748b' }}>Exploring the world, one itinerary at a time.</div>

                  {/* Nav Menu */}
                  {[
                    { key: 'home', label: 'Home', icon: <Home className="w-[18px] h-[18px]" />, tab: 'home', sub: null },
                    { key: 'trips', label: 'Trips', icon: <MapPin className="w-[18px] h-[18px]" />, tab: 'trips', sub: null, badge: tripCount },
                    { key: 'explore', label: 'Explore', icon: <Compass className="w-[18px] h-[18px]" />, tab: 'community', sub: 'discover' },
                    { key: 'compare', label: 'Rate & Rank', icon: <BarChart3 className="w-[18px] h-[18px]" />, tab: 'community', sub: 'compare' },
                    { key: 'circle', label: 'Circle', icon: <Users className="w-[18px] h-[18px]" />, tab: 'community', sub: 'circle' },
                    { key: 'you', label: 'You', icon: <User className="w-[18px] h-[18px]" />, tab: 'community', sub: 'you' },
                  ].map(item => {
                    const isActive = item.sub === null
                      ? landingTab === item.tab
                      : (landingTab === item.tab && communitySubTab === item.sub);
                    return (
                      <button
                        key={item.key}
                        onClick={() => {
                          setLandingTab(item.tab);
                          if (item.sub) setCommunitySubTab(item.sub);
                        }}
                        className="w-full flex items-center justify-between gap-2.5 px-3.5 py-2.5 rounded-[14px] text-[13px] transition-all mb-0.5 group/nav"
                        style={{
                          background: isActive ? 'rgba(13,148,136,0.08)' : 'transparent',
                          color: isActive ? '#0d9488' : '#64748b',
                          fontWeight: isActive ? 700 : 500,
                          borderLeft: isActive ? '3px solid #0d9488' : '3px solid transparent',
                          borderRadius: '0 14px 14px 0',
                        }}
                      >
                        <span className="flex items-center gap-2.5">
                          {item.icon}
                          {item.label}
                        </span>
                        {item.badge != null && (
                          <span className="inline-flex items-center justify-center min-w-[20px] h-[20px] px-1 rounded-full text-[10px] font-bold" style={{ background: isActive ? 'rgba(13,148,136,0.12)' : 'rgba(100,116,139,0.1)', color: isActive ? '#0d9488' : '#64748b' }}>{item.badge}</span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Referral Card */}
                <div className="rounded-[22px] border p-[18px]" style={{
                  borderColor: 'rgba(13,148,136,0.15)',
                  background: 'linear-gradient(135deg, rgba(13,148,136,0.06), rgba(45,212,191,0.03))',
                  boxShadow: '0 4px 16px rgba(28,41,53,0.05)',
                }}>
                  <div className="text-[13px] font-extrabold mb-1" style={{ color: '#0d9488' }}>Invite Friends</div>
                  <p className="text-[11px] mb-3" style={{ color: '#64748b' }}>Share arrivo and earn rewards together</p>
                  <button
                    onClick={() => {
                      // Click handler is now SYNCHRONOUS \u2014 referralCode is
                      // pre-fetched on user auth (see useEffect above). PR
                      // #261 added a legacy execCommand fallback when the
                      // modern Clipboard API failed; user still hit "Could
                      // not copy automatically" because BOTH paths needed a
                      // valid user-gesture token, and the prior `await
                      // getReferralCode()` consumed it. Pre-fetching means
                      // we have the code ready and the gesture is fresh.
                      // Web Share API tried first on touch devices (opens
                      // native share sheet \u2014 better UX); falls through to
                      // clipboard on desktop.
                      if (!referralCode) {
                        showToast('Still loading your invite code. Try again in a moment.', 'error');
                        return;
                      }
                      const url = `https://app.arrivotravel.ai/join/${referralCode}`;
                      const msg = `Hey! Sharing an invite to arrivo\u2014an invite-only community-powered travel app. Excited for you to join the community, and make sure to use my link!\n\n${url}`;

                      // Try Web Share API first (mobile-friendly native sheet)
                      if (typeof navigator !== 'undefined' && navigator.share) {
                        navigator.share({ title: 'Join arrivo', text: msg, url }).then(() => {
                          showToast('Invite shared!', 'success');
                        }).catch(err => {
                          // User cancelled the share sheet \u2014 silent. Other
                          // errors fall through to the clipboard path below.
                          if (err?.name !== 'AbortError') {
                            tryClipboard();
                          }
                        });
                        return;
                      }
                      tryClipboard();

                      function tryClipboard() {
                        // Modern API first.
                        if (navigator.clipboard?.writeText) {
                          navigator.clipboard.writeText(msg).then(() => {
                            showToast('Referral link copied!', 'success');
                          }).catch(err => {
                            console.warn('[invite-link] clipboard API failed, trying legacy:', err);
                            legacyCopy();
                          });
                          return;
                        }
                        legacyCopy();
                      }

                      function legacyCopy() {
                        try {
                          const ta = document.createElement('textarea');
                          ta.value = msg;
                          ta.setAttribute('readonly', '');
                          ta.style.position = 'fixed';
                          ta.style.top = '0';
                          ta.style.left = '-9999px';
                          ta.style.opacity = '0';
                          document.body.appendChild(ta);
                          ta.select();
                          ta.setSelectionRange(0, msg.length);
                          const ok = document.execCommand('copy');
                          document.body.removeChild(ta);
                          if (ok) {
                            showToast('Referral link copied!', 'success');
                          } else {
                            // Last-resort: prompt() lets the user OS-level
                            // copy from a selectable text field. Not pretty
                            // but reliable on every browser.
                            window.prompt('Copy your invite link:', url);
                          }
                        } catch (err) {
                          console.error('[invite-link] both clipboard methods failed:', err);
                          window.prompt('Copy your invite link:', url);
                        }
                      }
                    }}
                    disabled={!referralCode}
                    className="w-full py-2 rounded-xl text-[12px] font-bold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-1.5"
                    style={{ background: '#0d9488', color: 'white' }}
                  >
                    <Share2 className="w-3.5 h-3.5" /> Copy Invite Link
                  </button>
                </div>
              </div>

              {/* ── Main Content ── */}
              <div className="min-w-0">
                {/* Header Area with topo contour lines */}
                <div className="relative mb-3 rounded-2xl overflow-hidden px-5 pt-4 pb-5" style={{ background: 'linear-gradient(135deg, rgba(13,148,136,0.06), rgba(13,148,136,0.02))' }}>
                  {/* Topographic contour lines overlay */}
                  <div className="absolute inset-0 pointer-events-none" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 250'%3E%3Cpath d='M0 25 Q150 5 350 35 Q550 65 750 30 T1000 20' fill='none' stroke='%230d9488' stroke-width='1.5'/%3E%3Cpath d='M0 65 Q200 45 400 75 Q600 105 800 65 T1000 60' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 105 Q180 85 380 115 Q580 145 780 105 T1000 100' fill='none' stroke='%230d9488' stroke-width='1.2'/%3E%3Cpath d='M0 145 Q220 130 420 155 Q620 180 820 145 T1000 140' fill='none' stroke='%23313143' stroke-width='0.8'/%3E%3Cpath d='M0 185 Q200 170 400 195 Q600 220 800 185 T1000 180' fill='none' stroke='%230d9488' stroke-width='1'/%3E%3Cpath d='M0 220 Q250 210 450 230 Q650 245 850 220 T1000 215' fill='none' stroke='%23313143' stroke-width='0.7'/%3E%3C/svg%3E")`,
                    backgroundSize: 'cover',
                    opacity: 0.12,
                  }} />
                  <div className="relative z-10">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider mb-2" style={{ background: 'rgba(13,148,136,0.10)', color: '#0d9488' }}>
                      Welcome back
                    </div>
                    <h1 className="text-[32px] font-extrabold leading-none tracking-tight mb-1.5" style={{ fontFamily: "'Gulfs Display', 'Batangas', sans-serif", color: '#1e293b', letterSpacing: '0.05em' }}>your arrivo hub</h1>
                    <p className="text-sm max-w-[540px] mb-3" style={{ color: '#64748b', lineHeight: 1.5 }}>from your first trip to your fiftieth, arrivo connects you with the right destinations, the right itineraries, and the right people.</p>
                    <div className="flex items-center gap-2.5">
                      <button
                        onClick={() => { setStage('chat'); }}
                        className="px-4 py-2 rounded-full text-[13px] font-bold text-white transition-transform hover:-translate-y-0.5"
                        style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', boxShadow: '0 8px 20px rgba(13,148,136,0.2)' }}
                      >
                        Start Planning
                      </button>
                      <button
                        onClick={() => setStage('quiz')}
                        className="px-4 py-2 rounded-full text-[13px] font-bold transition-transform hover:-translate-y-0.5"
                        style={{ background: 'rgba(13,148,136,0.08)', color: '#0d9488', border: '1px solid rgba(13,148,136,0.2)' }}
                      >
                        Take the Quiz
                      </button>
                    </div>
                  </div>
                </div>

                {/* Content driven by sidebar — no tab bar */}

                {/* ═══ HOME TAB — Social-first: planner bar + feed ═══ */}
                {landingTab === 'home' && (
                  <div>
                    {/* Planner Bar */}
                    <div className="flex items-center gap-2.5 p-3 rounded-[22px] border mb-5 flex-wrap" style={{
                      borderColor: 'rgba(31,29,59,0.08)',
                      background: 'rgba(255,255,255,0.96)',
                      boxShadow: '0 4px 16px rgba(28,41,53,0.05)',
                    }}>
                      <Plus className="w-5 h-5 flex-shrink-0" style={{ color: '#0d9488' }} />
                      <input
                        type="text"
                        placeholder='Where do you want to go? Try "10 days in Japan" or "Weekend in Lisbon"'
                        className="flex-1 min-w-[240px] px-3.5 py-3 rounded-[14px] border text-[13px] focus:outline-none"
                        style={{
                          borderColor: 'rgba(31,29,59,0.08)',
                          background: 'white',
                          color: '#1e293b',
                          fontFamily: "'DM Sans', 'Raleway', sans-serif",
                        }}
                        onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #2dd4bf'}
                        onBlur={(e) => e.target.style.boxShadow = 'none'}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            const q = e.target.value.trim();
                            if (/hotel|resort|lodge|spa|villa/i.test(q)) {
                              setTripType('hotel'); setTripScope('single');
                            } else {
                              setTripType('destination'); setTripScope('single');
                            }
                            setInput(q);
                            setShowTripBuilder(true);
                            setStage('chat');
                            setTimeout(() => handleSend(q), 100);
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const inputEl = document.querySelector('[placeholder*="Where do you want to go"]');
                          const q = inputEl?.value?.trim();
                          if (q) {
                            if (/hotel|resort|lodge|spa|villa/i.test(q)) {
                              setTripType('hotel'); setTripScope('single');
                            } else {
                              setTripType('destination'); setTripScope('single');
                            }
                            setInput(q);
                            setShowTripBuilder(true);
                            setStage('chat');
                            setTimeout(() => handleSend(q), 100);
                          } else {
                            setStage('chat');
                          }
                        }}
                        className="px-5 py-3 rounded-full text-[13px] font-bold text-white whitespace-nowrap transition-transform hover:-translate-y-0.5"
                        style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', boxShadow: '0 10px 24px rgba(13,148,136,0.2)' }}
                      >
                        Plan My Trip
                      </button>
                    </div>

                    {/* Side-by-side: Feed | Compare */}
                    <div className="grid gap-6" style={{ gridTemplateColumns: '1fr 1fr' }}>
                      {/* Left: Community Feed */}
                      <div className="min-w-0 space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="text-[13px] font-extrabold" style={{ color: '#1e293b' }}>Community Feed</div>
                          {/* Filter Pills */}
                          <div className="flex gap-1.5">
                            {['All', 'Itineraries', 'Reviews', 'Tips'].map(filter => (
                              <button
                                key={filter}
                                onClick={() => setCommunityFilter(filter)}
                                className="px-3 py-1 rounded-full text-[10px] font-bold transition-all"
                                style={communityFilter === filter
                                  ? { background: '#313143', color: 'white', border: '1.5px solid #313143' }
                                  : { background: 'white', color: '#64748b', border: '1.5px solid #E5E7EB' }
                                }
                              >
                                {filter}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Activity Feed */}
                        <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading feed...</div>}>
                          <CommunityErrorBoundary>
                            <ActivityFeed
                              filter={communityFilter}
                              onOpenPlaceDetail={openPlaceDetail}
                              onViewItinerary={async (id, fetchedData) => {
                                const trip = (savedItinerariesList || []).find(t => t.id === id);
                                if (trip) { handleLoadItinerary(trip); return; }
                                // Try fetching from community API
                                const data = fetchedData || await import('./lib/communityApi').then(m => m.getCommunityItinerary(id));
                                if (data?.itinerary_data) handleLoadItinerary(data);
                                else showToast('Could not load this itinerary', 'error');
                              }}
                              onViewTrip={async (id) => {
                                const trip = (savedItinerariesList || []).find(t => t.id === id);
                                if (trip) { handleLoadItinerary(trip); return; }
                                const data = await import('./lib/communityApi').then(m => m.getCommunityItinerary(id));
                                if (data?.itinerary_data) handleLoadItinerary(data);
                                else showToast('This trip log has no linked itinerary yet', 'info');
                              }}
                              onViewProfile={(authorId) => {
                                try { window.location.hash = `#user/${authorId}`; } catch {}
                                showToast('Profile pages coming soon — author noted', 'info');
                              }}
                              onSaveTip={(tipData) => {
                                // No active trip → suggest creating one for this city
                                showToast(`Saved tip about ${tipData?.city || 'this place'} — open a trip to attach it`, 'success');
                              }}
                              onHideTip={(tipData) => {
                                try {
                                  const stored = JSON.parse(localStorage.getItem('arrivo_hidden_tip_ids') || '[]');
                                  const id = tipData?.id || tipData?.tip || '';
                                  if (id && !stored.includes(id)) {
                                    stored.push(id);
                                    localStorage.setItem('arrivo_hidden_tip_ids', JSON.stringify(stored));
                                  }
                                } catch {}
                                showToast('Tip hidden — won\u2019t show again', 'success');
                              }}
                            />
                          </CommunityErrorBoundary>
                        </React.Suspense>
                      </div>

                      {/* Right: Rate a Place + Recent Trips */}
                      <div className="min-w-0 space-y-4">
                        <div className="text-[13px] font-extrabold" style={{ color: '#1e293b' }}>Rate & Rank</div>
                        <div className="rounded-[22px] p-5" style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                          <p className="text-[12px] mb-3" style={{ color: '#64748b' }}>Search and rate places to build your personal rankings.</p>
                          <button
                            onClick={() => setQuickRateOpen(true)}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors mb-3"
                            style={{ border: '1px solid #E5E7EB' }}
                          >
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(13,148,136,0.1)' }}>
                              <Star className="w-4 h-4" style={{ color: '#0d9488' }} />
                            </div>
                            <div className="text-left">
                              <div className="text-[12px] font-bold" style={{ color: '#313143' }}>Rate a Place</div>
                              <div className="text-[10px]" style={{ color: '#64748b' }}>Search and rate to build your list</div>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-300 ml-auto" />
                          </button>
                          <button
                            onClick={() => { setCommunitySubTab('compare'); }}
                            className="w-full text-center py-2 rounded-lg text-[11px] font-semibold transition-colors hover:bg-teal-50"
                            style={{ color: '#0d9488' }}
                          >
                            View My Rankings →
                          </button>
                          {sidebarReviewPlace && (
                            <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSidebarReviewPlace(null)}>
                              <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                                <React.Suspense fallback={<div className="p-8 text-center text-gray-400">Loading...</div>}>
                                  <PlaceReviewForm
                                    place={sidebarReviewPlace}
                                    onClose={() => setSidebarReviewPlace(null)}
                                    onSuccess={() => {
                                      setSidebarReviewPlace(null);
                                      // Bug #13 web parity: open compare modal after
                                      // a successful review so users can pair-rank
                                      // against existing places. Mirrors mobile's
                                      // PairCompareModal auto-open from PR #207.
                                      setShowCompareModal(true);
                                    }}
                                  />
                                </React.Suspense>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Recent Trips — compact */}
                        {recentTrips.length > 0 && (
                          <div className="rounded-[22px] p-4" style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <div className="text-[10px] font-extrabold uppercase tracking-wider mb-2" style={{ color: '#0d9488', letterSpacing: '1.2px' }}>My Recent Trips</div>
                            <div className="space-y-1.5">
                              {recentTrips.slice(0, 3).map((trip, idx) => (
                                <button
                                  key={trip.id || idx}
                                  onClick={() => handleLoadItinerary(trip)}
                                  className="w-full flex items-center gap-2 p-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                                >
                                  <MapPin className="w-3 h-3 flex-shrink-0" style={{ color: '#0d9488' }} />
                                  <span className="text-[11px] font-semibold text-gray-700 truncate flex-1">{trip.destination || trip.title || 'Untitled'}</span>
                                  <span className="text-[10px] text-gray-400">{trip.itinerary_data?.days?.length || 0}d</span>
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={() => { setLandingTab('trips'); }}
                              className="w-full mt-1.5 py-1.5 rounded-lg text-[10px] font-bold text-teal-600 hover:bg-teal-50 transition-colors"
                            >
                              See all {tripCount} trips →
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ═══ MY TRIPS — own nav item ═══ */}
                {landingTab === 'trips' && (
                  <div>
                    {/* Planner Bar */}
                    <div className="flex items-center gap-2.5 p-3 rounded-[22px] border mb-5 flex-wrap" style={{
                      borderColor: 'rgba(31,29,59,0.08)',
                      background: 'rgba(255,255,255,0.96)',
                      boxShadow: '0 4px 16px rgba(28,41,53,0.05)',
                    }}>
                      <Plus className="w-5 h-5 flex-shrink-0" style={{ color: '#0d9488' }} />
                      <input
                        type="text"
                        placeholder='Where do you want to go? Try "10 days in Japan" or "Weekend in Lisbon"'
                        className="flex-1 min-w-[240px] px-3.5 py-3 rounded-[14px] border text-[13px] focus:outline-none"
                        style={{
                          borderColor: 'rgba(31,29,59,0.08)',
                          background: 'white',
                          color: '#1e293b',
                          fontFamily: "'DM Sans', 'Raleway', sans-serif",
                        }}
                        onFocus={(e) => e.target.style.boxShadow = '0 0 0 2px #2dd4bf'}
                        onBlur={(e) => e.target.style.boxShadow = 'none'}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && e.target.value.trim()) {
                            const q = e.target.value.trim();
                            if (/hotel|resort|lodge|spa|villa/i.test(q)) {
                              setTripType('hotel'); setTripScope('single');
                            } else {
                              setTripType('destination'); setTripScope('single');
                            }
                            setInput(q);
                            setShowTripBuilder(true);
                            setStage('chat');
                            setTimeout(() => handleSend(q), 100);
                          }
                        }}
                      />
                      <button
                        onClick={() => {
                          const inputEl = document.querySelector('[placeholder*="Where do you want to go"]');
                          const q = inputEl?.value?.trim();
                          if (q) {
                            if (/hotel|resort|lodge|spa|villa/i.test(q)) {
                              setTripType('hotel'); setTripScope('single');
                            } else {
                              setTripType('destination'); setTripScope('single');
                            }
                            setInput(q);
                            setShowTripBuilder(true);
                            setStage('chat');
                            setTimeout(() => handleSend(q), 100);
                          } else {
                            setStage('chat');
                          }
                        }}
                        className="px-5 py-3 rounded-full text-[13px] font-bold text-white whitespace-nowrap transition-transform hover:-translate-y-0.5"
                        style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', boxShadow: '0 10px 24px rgba(13,148,136,0.2)' }}
                      >
                        Plan My Trip
                      </button>
                    </div>

                    {/* Trip Cards Grid */}
                    {savedItinerariesList.length > 0 ? (
                      <div className="grid grid-cols-3 gap-3">
                        {savedItinerariesList.map((trip, idx) => {
                          // Hope's feedback: "Can I name this myself? Like - Game Family Summer Trip 2026"
                          // custom_title is the user-set override; falls back to destination → title.
                          const dest = trip.custom_title || trip.destination || trip.title || 'Untitled Trip';
                          const dayCount = trip.itinerary_data?.days?.length || 0;
                          // Generate a stable accent color from destination name for visual differentiation
                          const TRIP_CARD_ACCENTS = ['#0d9488', '#14b8a6', '#1d8e85', '#2dd4bf', '#0f766e', '#10b981', '#059669', '#0d9488'];
                          const destHash = dest.split('').reduce((h, c) => ((h << 5) - h + c.charCodeAt(0)) | 0, 0);
                          const accentColor = TRIP_CARD_ACCENTS[Math.abs(destHash) % TRIP_CARD_ACCENTS.length];
                          // Check if this is a temporary (expiring) trip
                          const isExpiring = !!trip.expires_at;
                          const expiresAt = isExpiring ? new Date(trip.expires_at) : null;
                          const hoursRemaining = isExpiring ? Math.max(0, Math.round((expiresAt - new Date()) / 3600000)) : null;
                          const pillLabel = isExpiring ? `${hoursRemaining}h left` : idx === 0 ? 'Latest' : (trip.is_shared ? 'Shared' : (trip.title ? 'Saved' : 'Draft'));
                          const pillStyle = isExpiring
                            ? { background: 'rgba(205,122,77,0.12)', color: '#CD7A4D' }
                            : idx === 0
                              ? { background: 'rgba(13,148,136,0.1)', color: '#0d9488' }
                              : trip.is_shared
                                ? { background: 'rgba(29,142,133,0.1)', color: '#1d8e85' }
                                : trip.title
                                  ? { background: 'rgba(20,184,166,0.1)', color: '#0f766e' }
                                  : { background: 'rgba(205,122,77,0.12)', color: '#CD7A4D' };
                          return (
                            <div
                              key={trip.id || `trip-${idx}`}
                              className="rounded-[18px] border p-[14px] cursor-pointer transition-all hover:shadow-lg overflow-hidden relative"
                              style={{
                                borderColor: 'rgba(31,29,59,0.08)',
                                background: 'rgba(255,255,255,0.94)',
                                boxShadow: '0 4px 16px rgba(28,41,53,0.05)',
                                borderTop: `3px solid ${accentColor}`,
                              }}
                              onClick={() => handleLoadItinerary(trip)}
                              onMouseEnter={(e) => e.currentTarget.style.borderColor = accentColor}
                              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(31,29,59,0.08)'; e.currentTarget.style.borderTop = `3px solid ${accentColor}`; }}
                            >
                              <div className="flex items-center justify-between gap-2 mb-2.5 flex-wrap">
                                <span className="px-3 py-1 rounded-full text-[11px] font-bold" style={pillStyle}>{pillLabel}</span>
                                {/* Hub trip kebab — Hope reported "the 3 ellipses ... is where I'd think I'd do this,
                                    but doesn't seem to work". Previously the onClick was just stopPropagation() with
                                    no menu attached. Now opens a popover with Delete (and Share lives elsewhere on
                                    the card already). */}
                                <div className="relative">
                                  <button
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-1 -m-1 rounded"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setOpenTripMenuId(openTripMenuId === trip.id ? null : trip.id);
                                    }}
                                    aria-label="Trip options"
                                  >
                                    <MoreVertical className="w-4 h-4" />
                                  </button>
                                  {openTripMenuId === trip.id && (
                                    <>
                                      {/* Click-away backdrop */}
                                      <div
                                        className="fixed inset-0 z-40"
                                        onClick={(e) => { e.stopPropagation(); setOpenTripMenuId(null); }}
                                      />
                                      <div
                                        className="absolute right-0 top-7 z-50 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden min-w-[160px]"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            setOpenTripMenuId(null);
                                            await handleRenameSavedItinerary(trip.id, trip.custom_title);
                                          }}
                                          className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 font-medium border-b border-gray-100"
                                        >
                                          <Pencil className="w-3.5 h-3.5" />
                                          Rename trip
                                        </button>
                                        <button
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            setOpenTripMenuId(null);
                                            await handleDeleteSavedItinerary(trip.id);
                                          }}
                                          className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 font-medium"
                                        >
                                          <Trash2 className="w-3.5 h-3.5" />
                                          Delete trip
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                              <h3 className="text-base font-extrabold leading-tight tracking-tight mb-1" style={{ color: '#1e293b', letterSpacing: '-0.03em' }}>{dest}</h3>
                              <div className="flex items-center gap-2 text-[11px] mb-2.5" style={{ color: '#64748b' }}>
                                <span>{dayCount} day{dayCount !== 1 ? 's' : ''}</span>
                                {trip.updated_at && (
                                  <>
                                    <span style={{ color: '#cbd5e1' }}>-</span>
                                    <span>{formatTimeAgoWeb(trip.updated_at)}</span>
                                  </>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleLoadItinerary(trip)}
                                  className="px-3.5 py-[7px] rounded-full text-xs font-bold text-white transition-colors"
                                  style={{ background: '#0d9488', borderColor: '#0d9488' }}
                                >
                                  {idx === 0 ? 'Continue' : 'View'}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (features.COLLABORATIVE_TRIPS && trip.id) {
                                      setCurrentTripId(trip.id);
                                      setShowShareModal(true);
                                    } else if (navigator.share) {
                                      navigator.share({ title: dest, text: `Check out my ${dest} itinerary on Arrivo!`, url: window.location.origin });
                                    } else {
                                      navigator.clipboard.writeText(window.location.origin);
                                      showToast('Link copied!', 'success');
                                    }
                                  }}
                                  className="px-3.5 py-[7px] rounded-full text-xs font-bold border transition-colors hover:bg-gray-50"
                                  style={{ borderColor: 'rgba(31,29,59,0.08)', color: '#334155', background: 'white' }}
                                >
                                  Share
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      /* Empty state — Quick Start */
                      <div>
                        <div className="text-center py-10 mb-6">
                          <MapPin className="w-12 h-12 mx-auto mb-4" style={{ color: '#cbd5e1' }} />
                          <h3 className="text-lg font-extrabold mb-2" style={{ color: '#334155' }}>No trips yet</h3>
                          <p className="text-sm mb-6" style={{ color: '#94a3b8' }}>Start planning your first adventure above!</p>
                        </div>
                        <p className="text-xs font-extrabold uppercase tracking-wider mb-3" style={{ color: '#94a3b8' }}>Quick Start</p>
                        <div className="grid grid-cols-2 gap-3">
                          {[
                            { icon: Palmtree, text: "Beach Paradise", prompt: "I want a relaxing beach vacation with amazing sunsets" },
                            { icon: Utensils, text: "Food & Culture", prompt: "I'm looking for a destination with incredible food and rich culture" },
                            { icon: Mountain, text: "Adventure", prompt: "I want an adventurous trip with hiking and outdoor activities" },
                            { icon: Heart, text: "Romantic Getaway", prompt: "Planning a romantic trip for two with beautiful scenery" },
                          ].map((item) => (
                            <button
                              key={item.text}
                              onClick={() => { setTripType('destination'); setTripScope('single'); setInput(item.prompt); setStage('chat'); }}
                              className="flex items-center gap-3 px-5 py-4 rounded-[22px] border transition-all hover:shadow-md text-left"
                              style={{ background: 'rgba(255,255,255,0.94)', borderColor: 'rgba(31,29,59,0.08)' }}
                            >
                              <item.icon className="w-5 h-5 flex-shrink-0" style={{ color: '#0d9488' }} />
                              <span className="text-sm font-semibold" style={{ color: '#1e293b' }}>{item.text}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ═══ COMMUNITY TAB ═══ */}
                {landingTab === 'community' && (
                  <div>
                    {/* Community navigation handled by sidebar — no inline tab bar */}

                    {/* ═══ FEED SUB-TAB ═══ */}
                    {communitySubTab === 'feed' && (
                      <div className="grid gap-6 home-content-grid">
                        <div className="min-w-0 space-y-4">
                          {/* Composer */}
                          <div className="flex items-center gap-2.5 px-4 py-3 rounded-[22px]" style={{ background: 'white', border: '1px solid #E5E7EB' }}>
                            <div className="w-[34px] h-[34px] rounded-[10px] flex items-center justify-center text-white text-xs font-extrabold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0d9488, #2dd4bf)' }}>
                              {userInitial}
                            </div>
                            <input
                              type="text"
                              placeholder="Share a tip, rank a place, or ask a question..."
                              className="flex-1 text-[13px] border-0 focus:outline-none bg-transparent"
                              style={{ color: '#313143' }}
                              onKeyDown={(e) => e.key === 'Enter' && showToast('Community posting coming soon!', 'info')}
                            />
                            <div className="flex gap-1.5">
                              <button
                                onClick={() => showToast('Photo upload coming soon!', 'info')}
                                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                                style={{ background: 'rgba(13,148,136,0.06)', color: '#0d9488' }}
                                title="Add photo"
                              >
                                <Camera className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => showToast('Ranking coming soon!', 'info')}
                                className="w-8 h-8 rounded-lg flex items-center justify-center transition-all"
                                style={{ background: 'rgba(205,122,77,0.08)', color: '#CD7A4D' }}
                                title="Rank a place"
                              >
                                <Star className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => showToast('Community posting coming soon!', 'info')}
                                className="px-4 py-1.5 rounded-[10px] text-xs font-extrabold text-white"
                                style={{ background: '#313143' }}
                              >
                                Post
                              </button>
                            </div>
                          </div>

                          {/* Filter Pills */}
                          <div className="flex gap-1.5 flex-wrap">
                            {['All', 'Itineraries', 'Reviews', 'Tips'].map(filter => (
                              <button
                                key={filter}
                                onClick={() => setCommunityFilter(filter)}
                                className="px-3.5 py-1.5 rounded-full text-[11px] font-bold transition-all"
                                style={communityFilter === filter
                                  ? { background: '#313143', color: 'white', border: '1.5px solid #313143' }
                                  : { background: 'white', color: '#64748b', border: '1.5px solid #E5E7EB' }
                                }
                              >
                                {filter}
                              </button>
                            ))}
                          </div>

                          {/* Activity Feed */}
                          <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading feed...</div>}>
                            <CommunityErrorBoundary>
                              <ActivityFeed
                                filter={communityFilter}
                                onOpenPlaceDetail={openPlaceDetail}
                                onViewItinerary={async (id, fetchedData) => {
                                  const trip = (savedItinerariesList || []).find(t => t.id === id);
                                  if (trip) { handleLoadItinerary(trip); return; }
                                  const data = fetchedData || await import('./lib/communityApi').then(m => m.getCommunityItinerary(id));
                                  if (data?.itinerary_data) handleLoadItinerary(data);
                                  else showToast('Could not load this itinerary', 'error');
                                }}
                                onViewTrip={async (id) => {
                                  const trip = (savedItinerariesList || []).find(t => t.id === id);
                                  if (trip) { handleLoadItinerary(trip); return; }
                                  const data = await import('./lib/communityApi').then(m => m.getCommunityItinerary(id));
                                  if (data?.itinerary_data) handleLoadItinerary(data);
                                  else showToast('This trip log has no linked itinerary yet', 'info');
                                }}
                                onViewProfile={(authorId) => {
                                  try { window.location.hash = `#user/${authorId}`; } catch {}
                                  showToast('Profile pages coming soon — author noted', 'info');
                                }}
                                onSaveTip={(tipData) => {
                                  showToast(`Saved tip about ${tipData?.city || 'this place'} — open a trip to attach it`, 'success');
                                }}
                                onHideTip={(tipData) => {
                                  try {
                                    const stored = JSON.parse(localStorage.getItem('arrivo_hidden_tip_ids') || '[]');
                                    const id = tipData?.id || tipData?.tip || '';
                                    if (id && !stored.includes(id)) {
                                      stored.push(id);
                                      localStorage.setItem('arrivo_hidden_tip_ids', JSON.stringify(stored));
                                    }
                                  } catch {}
                                  showToast('Tip hidden — won\u2019t show again', 'success');
                                }}
                              />
                            </CommunityErrorBoundary>
                          </React.Suspense>
                        </div>

                        {/* Feed Sidebar */}
                        <div className="min-w-0 space-y-4">
                          {/* Your Stats */}
                          <div className="rounded-[22px] p-4" style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <div className="text-[10px] font-extrabold uppercase tracking-wider mb-2.5" style={{ color: '#0d9488', letterSpacing: '1.2px' }}>Your Stats</div>
                            <div className="rounded-2xl p-3.5" style={{ background: 'linear-gradient(135deg, rgba(13,148,136,0.04), rgba(45,212,191,0.02))', border: '1px solid rgba(13,148,136,0.15)' }}>
                              <div className="flex text-center">
                                <div className="flex-1">
                                  <div className="text-xl font-black" style={{ color: '#0d9488' }}>{savedItinerariesList?.length || 0}</div>
                                  <div className="text-[9px] font-bold uppercase" style={{ color: '#64748b' }}>Trips</div>
                                </div>
                                <div className="flex-1">
                                  <div className="text-xl font-black" style={{ color: '#0d9488' }}>
                                    {(() => {
                                      const cities = new Set();
                                      (savedItinerariesList || []).forEach(it => {
                                        (it.itinerary_data?.days || []).forEach(d => { const city = d.baseCity || d.city; if (city) cities.add(city); });
                                      });
                                      return cities.size;
                                    })()}
                                  </div>
                                  <div className="text-[9px] font-bold uppercase" style={{ color: '#64748b' }}>Cities</div>
                                </div>
                                <div className="flex-1">
                                  <div className="text-xl font-black" style={{ color: '#0d9488' }}>
                                    {(() => {
                                      const countries = new Set();
                                      (savedItinerariesList || []).forEach(it => {
                                        (it.itinerary_data?.days || []).forEach(d => {
                                          const city = d.baseCity || d.city || '';
                                          const parts = city.split(',');
                                          if (parts.length > 1) countries.add(parts[parts.length - 1].trim());
                                        });
                                      });
                                      return countries.size;
                                    })()}
                                  </div>
                                  <div className="text-[9px] font-bold uppercase" style={{ color: '#64748b' }}>Countries</div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Explore Travel Profile CTA */}
                          <div className="rounded-[22px] p-4" style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                            <div className="flex items-center gap-2.5 mb-3">
                              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-black text-base" style={{ background: 'linear-gradient(135deg, #0d9488, #2dd4bf)' }}>{userInitial}</div>
                              <div>
                                <div className="text-[13px] font-extrabold" style={{ color: '#313143' }}>{user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Wanderer'}</div>
                                <div className="text-[10px]" style={{ color: '#64748b' }}>Member since {new Date(user?.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</div>
                              </div>
                            </div>
                            <button
                              onClick={() => { setLandingTab('community'); setCommunitySubTab('you'); }}
                              className="w-full py-2.5 rounded-xl text-[12px] font-bold border-0 cursor-pointer transition-all hover:opacity-90 flex items-center justify-center gap-2"
                              style={{ background: 'linear-gradient(135deg, rgba(13,148,136,0.08), rgba(45,212,191,0.04))', color: '#0d9488', border: '1.5px solid rgba(13,148,136,0.2)' }}
                            >
                              <Compass className="w-3.5 h-3.5" /> Explore Your Travel Badges
                            </button>
                          </div>

                          {/* Referral */}
                          <React.Suspense fallback={<div className="text-center py-4 text-gray-400 text-xs">Loading referral...</div>}>
                            <CommunityErrorBoundary>
                              <ReferralCard />
                            </CommunityErrorBoundary>
                          </React.Suspense>
                        </div>
                      </div>
                    )}

                    {/* ═══ RANKINGS SUB-TAB ═══ */}
                    {communitySubTab === 'rankings' && (
                      <div className="space-y-4">
                        <button onClick={() => setCommunitySubTab('you')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-1" style={FONTS.body}>
                          <ChevronLeft className="w-4 h-4" /> Back to You
                        </button>
                        {/* Rate & Compare CTA */}
                        <div className="flex justify-end">
                          <button
                            onClick={() => setShowCompareModal(true)}
                            className="px-4 py-2 rounded-xl text-xs font-extrabold text-white transition-all hover:opacity-90"
                            style={{ background: '#313143' }}
                          >
                            <Star className="w-3.5 h-3.5 inline-block mr-1.5" /> Rate & Compare
                          </button>
                        </div>

                        <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading leaderboard...</div>}>
                          <CommunityErrorBoundary>
                            <LeaderboardPage />
                          </CommunityErrorBoundary>
                        </React.Suspense>

                        {/* Shareable Ranked List */}
                        <React.Suspense fallback={<div className="text-center py-4 text-gray-400 text-xs">Loading ranked list...</div>}>
                          <CommunityErrorBoundary>
                            <ShareableRankedList userName={user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Wanderer'} />
                          </CommunityErrorBoundary>
                        </React.Suspense>

                        {/* Compare Modal */}
                        {showCompareModal && (
                          <React.Suspense fallback={null}>
                            <CommunityErrorBoundary>
                              <CompareModal isOpen={showCompareModal} onClose={() => setShowCompareModal(false)} />
                            </CommunityErrorBoundary>
                          </React.Suspense>
                        )}
                      </div>
                    )}

                    {/* ═══ DISCOVER SUB-TAB ═══ */}
                    {communitySubTab === 'discover' && (
                      <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading discover...</div>}>
                        <CommunityErrorBoundary>
                          <DiscoverGrid
                            onViewItinerary={async (id) => {
                              const trip = (savedItinerariesList || []).find(t => t.id === id);
                              if (trip) { handleLoadItinerary(trip); return; }
                              const data = await import('./lib/communityApi').then(m => m.getCommunityItinerary(id));
                              if (data?.itinerary_data) handleLoadItinerary(data);
                              else showToast('This trip log has no linked itinerary yet', 'info');
                            }}
                            onViewProfile={(authorId) => {
                              // Hope's feedback: clicking a tip author should show their profile.
                              // Persist a hash so refreshing the URL keeps you in the same place.
                              try { window.location.hash = `#user/${authorId}`; } catch {}
                              showToast('Profile pages coming soon — author noted', 'info');
                            }}
                            onTipHide={(tip) => {
                              // Persist hidden tip ids in localStorage for now (per-device).
                              // Future: sync to user prefs server-side for cross-device hide.
                              try {
                                const stored = JSON.parse(localStorage.getItem('arrivo_hidden_tip_ids') || '[]');
                                const id = tip?.id || tip?.tip || '';
                                if (id && !stored.includes(id)) {
                                  stored.push(id);
                                  localStorage.setItem('arrivo_hidden_tip_ids', JSON.stringify(stored));
                                }
                              } catch {}
                              showToast('Tip hidden — won\u2019t show again', 'success');
                            }}
                          />
                        </CommunityErrorBoundary>
                      </React.Suspense>
                    )}

                    {/* ═══ CIRCLE SUB-TAB ═══ */}
                    {communitySubTab === 'circle' && (
                      <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading inner circle...</div>}>
                        <CommunityErrorBoundary>
                          <InnerCirclePage currentUserId={user?.id} />
                        </CommunityErrorBoundary>
                      </React.Suspense>
                    )}

                    {/* ═══ MY REVIEWS SUB-TAB ═══ */}
                    {communitySubTab === 'my-reviews' && (() => {
                      const MyReviewsInline = () => {
                        const [reviews, setReviews] = React.useState([]);
                        const [loading, setLoading] = React.useState(true);
                        const [filterCategory, setFilterCategory] = React.useState('all');
                        const [reviewingPlace, setReviewingPlace] = React.useState(null);
                        const [reviewSuccess, setReviewSuccess] = React.useState(false);

                        React.useEffect(() => {
                          (async () => {
                            setLoading(true);
                            const data = await getMyReviews(100);
                            setReviews(data?.reviews || data || []);
                            setLoading(false);
                          })();
                        }, [reviewSuccess]);

                        const SENT = { liked: { emoji: '\u{1F44D}', color: 'text-green-600', bg: 'bg-green-50', label: 'Liked' }, fine: { emoji: '\u{1F610}', color: 'text-yellow-600', bg: 'bg-yellow-50', label: 'Fine' }, disliked: { emoji: '\u{1F44E}', color: 'text-red-600', bg: 'bg-red-50', label: "Didn't like" } };
                        const filtered = filterCategory === 'all' ? reviews : reviews.filter(r => r.place_category === filterCategory);

                        if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full" /></div>;

                        return (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between">
                              <h2 className="text-xl text-gray-800" style={FONTS.headingBold}>My Reviews</h2>
                              <button onClick={() => setReviewingPlace({ name: '', city: '', country: '' })}
                                className="px-4 py-2 rounded-xl text-white text-sm" style={{ ...FONTS.bodyBold, background: '#18B8A6' }}>+ New Review</button>
                            </div>
                            <div className="flex gap-2 overflow-x-auto pb-1">
                              {[{ id: 'all', label: 'All' }, { id: 'dining', label: 'Dining' }, { id: 'hotel', label: 'Hotels' }, { id: 'transport', label: 'Transport' }, { id: 'activity', label: 'Activities' }].map(cat => (
                                <button key={cat.id} onClick={() => setFilterCategory(cat.id)}
                                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${filterCategory === cat.id ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`} style={FONTS.body}>{cat.label}</button>
                              ))}
                            </div>
                            {filtered.length === 0 ? (
                              <div className="text-center py-12">
                                <Star className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm" style={FONTS.body}>No reviews yet</p>
                                <p className="text-xs text-gray-300 mt-1" style={FONTS.body}>Rate places you've visited to build your travel profile</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {filtered.map(review => {
                                  const sentInfo = SENT[review.sentiment];
                                  return (
                                    <div key={review.id} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-shadow">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <p className="text-sm text-gray-800 font-semibold truncate" style={FONTS.headingSemibold}>{review.place_name}</p>
                                          <p className="text-xs text-gray-400" style={FONTS.body}>
                                            {review.city || ''}{review.country ? `, ${review.country}` : ''}{review.visit_date && ` \u00B7 ${new Date(review.visit_date).toLocaleDateString()}`}
                                          </p>
                                          {review.review_text && <p className="text-xs text-gray-600 mt-2 line-clamp-2" style={FONTS.body}>{review.review_text}</p>}
                                          {review.tags?.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-2">
                                              {review.tags.filter(t => t !== 'Been Here').map(tag => (
                                                <span key={tag} className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-xs" style={FONTS.body}>{tag}</span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                        <div className="flex flex-col items-end gap-1 ml-3">
                                          {review.user_rating != null ? (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold" style={{ backgroundColor: (review.user_rating >= 8 ? '#059669' : review.user_rating >= 6 ? '#0d9488' : review.user_rating >= 4 ? '#d97706' : review.user_rating >= 2 ? '#ea580c' : '#dc2626') + '18', color: review.user_rating >= 8 ? '#059669' : review.user_rating >= 6 ? '#0d9488' : review.user_rating >= 4 ? '#d97706' : review.user_rating >= 2 ? '#ea580c' : '#dc2626' }}>
                                              {parseFloat(review.user_rating).toFixed(1)}
                                            </span>
                                          ) : sentInfo ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs ${sentInfo.bg} ${sentInfo.color}`}>{sentInfo.emoji} {sentInfo.label}</span> : null}
                                          {review.price_tier && <span className="text-xs text-gray-400" style={FONTS.body}>{'$'.repeat(review.price_tier)}</span>}
                                        </div>
                                      </div>
                                      {review.photo_urls?.length > 0 && (
                                        <div className="flex gap-1.5 mt-3 overflow-x-auto">
                                          {review.photo_urls.slice(0, 4).map((url, i) => <img key={i} src={url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />)}
                                          {review.photo_urls.length > 4 && <span className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400 flex-shrink-0">+{review.photo_urls.length - 4}</span>}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {reviewingPlace && (
                              <PlaceReviewForm
                                place={reviewingPlace}
                                onClose={() => setReviewingPlace(null)}
                                onSuccess={() => {
                                  setReviewingPlace(null);
                                  setReviewSuccess(s => !s);
                                  // Auto-open pair-compare flow (bug #13 web parity)
                                  setShowCompareModal(true);
                                }}
                              />
                            )}
                          </div>
                        );
                      };
                      return <MyReviewsInline />;
                    })()}

                    {/* ═══ RATE & RANK SUB-TAB ═══ */}
                    {communitySubTab === 'compare' && (() => {
                      const RateAndRankView = () => {
                        const [reviewPlace, setReviewPlace] = React.useState(null);
                        return (
                          <div className="space-y-4">
                            <button onClick={() => setCommunitySubTab('feed')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-1" style={FONTS.body}>
                              <ChevronLeft className="w-4 h-4" /> Back to Feed
                            </button>
                            <h2 className="text-xl text-gray-800" style={FONTS.headingBold}>Rate & Rank</h2>
                            <p className="text-sm text-gray-500" style={FONTS.body}>Search and rate places to build your personal rankings.</p>

                            {/* Rate a Place CTA — opens QuickRateFlow search (Beli-style) */}
                            <button
                              onClick={() => setQuickRateOpen(true)}
                              className="w-full flex items-center gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors"
                              style={{ border: '1px solid #E5E7EB' }}
                            >
                              <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'rgba(13,148,136,0.1)' }}>
                                <Star className="w-5 h-5" style={{ color: '#0d9488' }} />
                              </div>
                              <div className="text-left flex-1">
                                <div className="text-sm font-bold" style={{ color: '#313143' }}>Rate a Place</div>
                                <div className="text-xs" style={{ color: '#64748b' }}>Search for a restaurant, hotel, or activity and rate it</div>
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-300" />
                            </button>

                            {/* Rankings */}
                            <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading rankings...</div>}>
                              <CommunityErrorBoundary>
                                <ShareableRankedList userName={user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Wanderer'} />
                              </CommunityErrorBoundary>
                            </React.Suspense>

                            {/* Review modal */}
                            {reviewPlace && (
                              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setReviewPlace(null)}>
                                <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                                  <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading...</div>}>
                                    <CommunityErrorBoundary>
                                      <PlaceReviewForm
                                        place={reviewPlace}
                                        onClose={() => setReviewPlace(null)}
                                        onSuccess={() => {
                                          setReviewPlace(null);
                                          // Auto-open pair-compare flow (bug #13 web parity)
                                          setShowCompareModal(true);
                                        }}
                                      />
                                    </CommunityErrorBoundary>
                                  </React.Suspense>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      };
                      return <RateAndRankView />;
                    })()}

                    {/* ═══ BOOKMARKS SUB-TAB ═══ */}
                    {communitySubTab === 'bookmarks' && (() => {
                      const BookmarksInline = () => {
                        const [bookmarks, setBookmarksState] = React.useState([]);
                        const [loading, setLoading] = React.useState(true);
                        const [filterCategory, setFilterCategory] = React.useState(null);
                        const [showAddForm, setShowAddForm] = React.useState(false);
                        // Match mobile's add-form fields exactly:
                        // place_name, city, country, place_category, notes.
                        // Default category 'destination' (Dream Trip) since most users
                        // bookmark cities/countries before specific places — same as mobile.
                        const [addForm, setAddForm] = React.useState({ place_name: '', city: '', country: '', place_category: 'destination', notes: '' });

                        const loadBookmarks = React.useCallback(async () => {
                          setLoading(true);
                          const data = await getBookmarks(filterCategory);
                          setBookmarksState(data || []);
                          setLoading(false);
                        }, [filterCategory]);

                        React.useEffect(() => { loadBookmarks(); }, [loadBookmarks]);

                        const handleDelete = async (id) => {
                          await deleteBookmark(id);
                          setBookmarksState(prev => prev.filter(b => b.id !== id));
                        };

                        if (loading) return <div className="flex justify-center py-12"><div className="animate-spin w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full" /></div>;

                        return (
                          <div className="space-y-4">
                            <button onClick={() => setCommunitySubTab('you')} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-1" style={FONTS.body}>
                              <ChevronLeft className="w-4 h-4" /> Back to You
                            </button>
                            <div className="flex items-center justify-between">
                              <h2 className="text-xl text-gray-800" style={FONTS.headingBold}>Bookmarks</h2>
                              <button
                                onClick={() => setShowAddForm(!showAddForm)}
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors hover:bg-teal-50"
                                style={{ color: '#0d9488', border: '1px solid rgba(13,148,136,0.2)' }}
                              >
                                {showAddForm ? 'Cancel' : '+ Add Bookmark'}
                              </button>
                            </div>
                            {showAddForm && (() => {
                              const isDream = addForm.place_category === 'destination';
                              return (
                                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                                  <p className="text-xs text-gray-500" style={FONTS.body}>
                                    {isDream
                                      ? 'Save a city or country you want to visit one day.'
                                      : 'Save a specific place — restaurant, hotel, or activity.'}
                                  </p>
                                  <input
                                    type="text"
                                    placeholder={isDream ? 'Destination (e.g. Tokyo, Patagonia) *' : 'Place name *'}
                                    value={addForm.place_name}
                                    onChange={e => setAddForm(p => ({ ...p, place_name: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-teal-400"
                                  />
                                  {!isDream && (
                                    <div className="grid grid-cols-2 gap-2">
                                      <input type="text" placeholder="City" value={addForm.city}
                                        onChange={e => setAddForm(p => ({ ...p, city: e.target.value }))}
                                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-teal-400" />
                                      <input type="text" placeholder="Country" value={addForm.country}
                                        onChange={e => setAddForm(p => ({ ...p, country: e.target.value }))}
                                        className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-teal-400" />
                                    </div>
                                  )}
                                  <div className="flex gap-2 overflow-x-auto pb-1">
                                    {[
                                      { value: 'destination', label: 'Dream Trip' },
                                      { value: 'activity', label: 'Activity' },
                                      { value: 'dining', label: 'Dining' },
                                      { value: 'hotel', label: 'Hotel' },
                                      { value: 'transport', label: 'Transport' },
                                    ].map(c => (
                                      <button
                                        key={c.value}
                                        type="button"
                                        onClick={() => setAddForm(p => ({ ...p, place_category: c.value }))}
                                        className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${addForm.place_category === c.value ? 'bg-teal-500 text-white border border-teal-500' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}
                                        style={FONTS.body}
                                      >
                                        {c.label}
                                      </button>
                                    ))}
                                  </div>
                                  <input
                                    type="text"
                                    placeholder={isDream ? 'Why this destination? (optional)' : 'Notes (optional)'}
                                    value={addForm.notes}
                                    onChange={e => setAddForm(p => ({ ...p, notes: e.target.value }))}
                                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-teal-400"
                                  />
                                  <button
                                    onClick={async () => {
                                      if (!addForm.place_name.trim()) return;
                                      try {
                                        // createBookmark in lib/communityApi.js expects camelCase
                                        // (placeName/placeCategory). The previous version of this
                                        // form passed snake_case which silently produced undefined
                                        // — explaining why "Add Bookmark" appeared to do nothing.
                                        const res = await createBookmark({
                                          placeName: addForm.place_name.trim(),
                                          city: addForm.city.trim() || null,
                                          country: addForm.country.trim() || null,
                                          placeCategory: addForm.place_category,
                                          notes: addForm.notes.trim() || null,
                                        });
                                        if (res?.error) throw new Error(res.error);
                                        setAddForm({ place_name: '', city: '', country: '', place_category: 'destination', notes: '' });
                                        setShowAddForm(false);
                                        loadBookmarks();
                                        showToast('Bookmark added!', 'success');
                                      } catch (err) { showToast(err.message || 'Failed', 'error'); }
                                    }}
                                    disabled={!addForm.place_name.trim()}
                                    className="w-full py-2 rounded-lg text-sm font-bold text-white transition-colors disabled:opacity-40"
                                    style={{ background: '#0d9488' }}
                                  >
                                    Add Bookmark
                                  </button>
                                </div>
                              );
                            })()}
                            <div className="flex gap-2 overflow-x-auto pb-1">
                              {[{ id: null, label: 'All' }, { id: 'destination', label: 'Dream Trips' }, { id: 'dining', label: 'Dining' }, { id: 'hotel', label: 'Hotels' }, { id: 'transport', label: 'Transport' }, { id: 'activity', label: 'Activities' }].map(cat => (
                                <button key={cat.id || 'all'} onClick={() => setFilterCategory(cat.id)}
                                  className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${filterCategory === cat.id ? 'bg-teal-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`} style={FONTS.body}>{cat.label}</button>
                              ))}
                            </div>
                            {bookmarks.length === 0 ? (
                              <div className="text-center py-12">
                                <Bookmark className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                                <p className="text-gray-500 text-sm" style={FONTS.body}>No bookmarks yet</p>
                                <p className="text-xs text-gray-300 mt-1" style={FONTS.body}>Bookmark places you want to visit later</p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {bookmarks.map(bm => {
                                  const isDreamTrip = bm.place_category === 'destination';
                                  // "Plan This Trip" handler — pre-fills Trip Builder with this
                                  // destination. Mirrors mobile's handlePlanSaved in
                                  // DestinationRecsScreen so the cross-platform UX is identical.
                                  const handlePlanThisTrip = () => {
                                    const destName = [bm.city, bm.country].filter(Boolean).join(', ') || bm.place_name;
                                    // Navigate to home tab and let the existing trip-builder
                                    // open via a custom event the home view listens for.
                                    setLandingTab('home');
                                    setTimeout(() => {
                                      try {
                                        sessionStorage.setItem('wander_prefill_destination', destName);
                                      } catch {}
                                      window.dispatchEvent(new CustomEvent('wander_open_trip_builder', { detail: { destination: destName } }));
                                    }, 100);
                                  };
                                  return (
                                    <div key={bm.id} className={`bg-white rounded-xl border ${isDreamTrip ? 'border-amber-200 bg-amber-50/30' : 'border-gray-100'} p-4 hover:shadow-sm transition-shadow`}>
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3 min-w-0 flex-1">
                                          {isDreamTrip && (
                                            <div className="w-7 h-7 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                                              <Bookmark className="w-3.5 h-3.5 text-amber-700" />
                                            </div>
                                          )}
                                          <div className="min-w-0 flex-1">
                                            <p className="text-sm text-gray-800 truncate" style={FONTS.headingSemibold}>{bm.place_name}</p>
                                            <p className="text-xs text-gray-400" style={FONTS.body}>{bm.city || ''}{bm.country ? `, ${bm.country}` : ''}</p>
                                            {bm.notes && <p className="text-xs text-gray-500 mt-1 line-clamp-1" style={FONTS.body}>{bm.notes}</p>}
                                          </div>
                                        </div>
                                        <button onClick={() => handleDelete(bm.id)}
                                          className="p-2 rounded-full hover:bg-red-50 text-gray-300 hover:text-red-400 transition-colors flex-shrink-0" title="Remove">
                                          <X className="w-4 h-4" />
                                        </button>
                                      </div>
                                      {isDreamTrip && (
                                        <button
                                          onClick={handlePlanThisTrip}
                                          className="mt-3 w-full py-2 rounded-lg text-xs font-bold text-white transition-colors flex items-center justify-center gap-1.5"
                                          style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)' }}
                                        >
                                          <Sparkles className="w-3.5 h-3.5" />
                                          PLAN THIS TRIP
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      };
                      return <BookmarksInline />;
                    })()}

                    {/* ═══ ADMIN SUB-TAB ═══ */}
                    {/* Admin moved to analytics — hidden from community tabs */}
                    {false && communitySubTab === 'admin' && tier === 'super-admin' && (
                      <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading admin...</div>}>
                        <CommunityErrorBoundary>
                          <AdminReviewQueue />
                        </CommunityErrorBoundary>
                      </React.Suspense>
                    )}

                    {/* ═══ YOU SUB-TAB — Condensed Gamification Profile ═══ */}
                    {communitySubTab === 'you' && (() => {
                      const YouProfileTab = () => {
                        const [youReviews, setYouReviews] = React.useState([]);
                        const [youBookmarks, setYouBookmarks] = React.useState([]);
                        // youTripLogs: trip-log entries from the trip_logs table.
                        // Previously the You page only read from saved_itineraries
                        // and youReviews when computing visited cities/countries —
                        // so any place added via "Add Place" (which writes to
                        // trip_logs) was orphaned: createTripLog returned 200
                        // OK, the toast said "added", but the entry never showed
                        // up on the map or in the count. Fixing the gap.
                        const [youTripLogs, setYouTripLogs] = React.useState([]);
                        const [youLoading, setYouLoading] = React.useState(true);
                        const [reviewingPlace, setReviewingPlace] = React.useState(null);
                        const [showTasteProfile, setShowTasteProfile] = React.useState(false);
                        const [showAllAchievements, setShowAllAchievements] = React.useState(false);
                        const [tasteProfileData, setTasteProfileData] = React.useState(null);
                        // Shareable taste card modal — mirrors the mobile entry
                        // point in TasteProfileScreen where tapping opens the
                        // branded share artifact.
                        const [showShareableTasteCard, setShowShareableTasteCard] = React.useState(false);

                        React.useEffect(() => {
                          (async () => {
                            setYouLoading(true);
                            const { getTripLogs } = await import('./lib/communityApi');
                            const [revData, bkData, tlData] = await Promise.all([
                              getMyReviews(100),
                              getBookmarks(),
                              user?.id ? getTripLogs(user.id, 200, 0).catch(() => ({ trip_logs: [] })) : Promise.resolve({ trip_logs: [] }),
                            ]);
                            setYouReviews(revData?.reviews || revData || []);
                            setYouBookmarks(bkData?.bookmarks || bkData || []);
                            setYouTripLogs(tlData?.trip_logs || []);
                            setYouLoading(false);
                            // Fetch taste profile for passport display
                            try {
                              const { getTasteProfile } = await import('./lib/communityApi');
                              const tp = await getTasteProfile(user?.id);
                              if (tp) setTasteProfileData(tp);
                            } catch (_) {}
                          })();
                        }, []);

                        // Compute visited cities + countries ONCE, share between
                        // the Passport (left "World Map") and CommunityTravelMap
                        // (right "Travel Map"). Previously each map had its own
                        // dict + its own input set, producing inconsistent counts
                        // (Passport showed 36, TravelMap showed 39 for the same
                        // user). Now both derive from one source: itineraries +
                        // reviews, resolved via TravelMap's bigger CITY_TO_COUNTRY.
                        const { citySet: visitedCitySet, countrySet: visitedCountrySet } = (() => {
                          const cities = new Set();
                          const countries = new Set();
                          const addCity = (city) => {
                            if (!city || typeof city !== 'string') return;
                            cities.add(city);
                            const code = getCountryCode(city);
                            if (code) {
                              countries.add(code);
                              return;
                            }
                            // Country-name fallback: when getCountryCode fails to
                            // resolve a city → ISO numeric (e.g. user added
                            // "Russia" with no city, or a destination with no
                            // matching CITY_TO_COUNTRY entry), try resolving the
                            // string AS a country name. The map renders by ISO
                            // numeric so we need that shape.
                            const numericFromCountry = COUNTRY_NAME_TO_ISO_NUMERIC[city.toLowerCase().trim()];
                            if (numericFromCountry) {
                              countries.add(numericFromCountry);
                              return;
                            }
                            // Last resort: explicit country suffix after last comma.
                            // ONLY add to set when we resolve to a real ISO numeric;
                            // otherwise we'd pollute the set with sub-national names
                            // (Utah, Florida, "the", "auberge", etc.) which inflate
                            // the count AND never highlight on the world map (no
                            // matching TopoJSON country path).
                            const parts = city.split(',');
                            if (parts.length > 1) {
                              const lastPart = parts[parts.length - 1].trim().toLowerCase();
                              const numericFromSuffix = COUNTRY_NAME_TO_ISO_NUMERIC[lastPart];
                              if (numericFromSuffix) countries.add(numericFromSuffix);
                              // No fallback to raw string — keeps the Set ISO-numeric-only
                            }
                          };
                          (savedItinerariesList || []).forEach(it =>
                            (it.itinerary_data?.days || []).forEach(d => addCity(d.baseCity || d.city))
                          );
                          (youReviews || []).forEach(r => addCity(r.city));
                          // Include trip_logs from the trip_logs table — these are
                          // entries created via "Add Place" on the Travel Map.
                          // Without this, country-only adds (e.g. "Russia") never
                          // light up on the map even though the API call succeeds.
                          (youTripLogs || []).forEach(t => {
                            (Array.isArray(t.destinations) ? t.destinations : []).forEach(addCity);
                          });
                          return { citySet: cities, countrySet: countries };
                        })();
                        const citiesCount = visitedCitySet.size;
                        const countriesCount = visitedCountrySet.size;
                        const topCities = (() => { const cityMap = {}; (savedItinerariesList || []).forEach(it => (it.itinerary_data?.days || []).forEach(d => { const city = d.baseCity || d.city; if (city) cityMap[city] = (cityMap[city] || 0) + 1; })); return Object.entries(cityMap).sort((a,b) => b[1] - a[1]).map(([c]) => c); })();

                        const handleExportSection = async (sectionName) => {
                          showToast(`Exporting ${sectionName}...`, 'info');
                          // Find the section element and export as image
                          try {
                            const html2canvas = (await import('html2canvas')).default;
                            // Find closest parent card by section name
                            const cards = document.querySelectorAll('[data-export-section]');
                            const card = [...cards].find(el => el.dataset.exportSection === sectionName);
                            if (card) {
                              const canvas = await html2canvas(card, { scale: 2, backgroundColor: '#fff', useCORS: true });
                              const link = document.createElement('a');
                              link.download = `arrivo-${sectionName.toLowerCase().replace(/\s/g, '-')}.png`;
                              link.href = canvas.toDataURL('image/png');
                              link.click();
                              showToast(`${sectionName} saved!`, 'success');
                            }
                          } catch (err) {
                            console.error('Export failed:', err);
                            showToast('Export failed', 'error');
                          }
                        };

                        const passportUserName = profile?.display_name || user?.user_metadata?.full_name || user?.email?.split('@')[0];

                        return (
                          <div className="space-y-4">
                            {/* ── Passport (left, source of truth) + Travel Map / Quick Links / Taste Profile (right) ── */}
                            <div className="grid grid-cols-2 gap-4 items-start">
                              {/* Passport — self-contained: stats, achievements, taste profile,
                                  world map, top cities. Previous duplicate Achievements / Taste
                                  Profile cards below were removed; the passport is now the
                                  single source of truth. */}
                              <div className="rounded-[22px] overflow-hidden" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                                <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-xs">Loading...</div>}>
                                  <CommunityErrorBoundary>
                                    <ShareablePassport
                                      userName={passportUserName}
                                      memberSince={new Date(user?.created_at || Date.now()).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                      stats={{ trips: savedItinerariesList?.length || 0, cities: citiesCount, countries: countriesCount, reviews: youReviews.length }}
                                      topCities={topCities}
                                      travelStyle={tasteProfileData?.travel_personality || (profile?.preferences?.pace ? `${profile.preferences.pace} traveler` : null)}
                                      avatarUrl={profile?.avatar_url}
                                      tasteProfile={tasteProfileData}
                                      userId={user?.id}
                                      tripLogs={(savedItinerariesList || []).map(it => ({ ...it, destinations: (it.itinerary_data?.days || []).map(d => d.baseCity || d.city).filter(Boolean) }))}
                                      // Unified country set — passed to both maps so they show consistent counts
                                      visitedCountrySet={visitedCountrySet}
                                    />
                                  </CommunityErrorBoundary>
                                </React.Suspense>
                              </div>

                              {/* Right column: Travel Map on top, Quick Links strip, then Taste Profile card */}
                              <div className="space-y-4">
                                {/* Travel Map */}
                                <div data-export-section="Travel Map" className="rounded-[22px] p-4" style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="text-[10px] font-extrabold uppercase tracking-wider" style={{ color: '#0d9488', letterSpacing: '1.2px' }}>Travel Map</div>
                                    <button onClick={() => handleExportSection('Travel Map')} className="p-1 rounded-lg hover:bg-gray-100 transition-colors" title="Export">
                                      <Share2 className="w-3 h-3 text-gray-400" />
                                    </button>
                                  </div>
                                  {/* Travel Map container — previously wrapped with
                                      aspectRatio: '960/520' + overflow:hidden, which clipped
                                      ~469px of content (the visited destinations chip section
                                      below the SVG, plus extra header). The TravelMap component
                                      manages its own internal SVG sizing (svg w-full + minHeight
                                      280) and its own card chrome, so an outer aspect-ratio box
                                      is unnecessary and harmful — it forced the entire card
                                      (header + stats + map + chips) into the SVG's aspect-ratio
                                      slot. Letting the card render at its natural height
                                      restores the chips and prevents bottom clipping. */}
                                  <div style={{ borderRadius: '14px', overflow: 'hidden' }}>
                                    <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-xs">Loading map...</div>}>
                                      <CommunityErrorBoundary>
                                        <CommunityTravelMap
                                          tripLogs={(savedItinerariesList || []).map(it => ({
                                            ...it,
                                            destinations: (it.itinerary_data?.days || []).map(d => d.baseCity || d.city).filter(Boolean),
                                          }))}
                                          reviews={youReviews}
                                          userName={passportUserName}
                                          isOwnProfile
                                          // Unified country set — passed by parent so this map agrees with the Passport
                                          visitedCountrySet={visitedCountrySet}
                                          // Add Place flow — persists a private trip-log entry so the new
                                          // city shows up in the Travel Map AND lights up the Passport
                                          // world map on next reload. Mirrors mobile's
                                          // TravelMapScreen.handleAddPlace.
                                          onAddPlace={async ({ city, country }) => {
                                            // Accept city-only, country-only, or both. The Travel
                                            // Map is country-level — a user who knows they
                                            // visited Russia but doesn't remember a city should
                                            // still be able to mark it. Country-only stores the
                                            // country as the destination string, which
                                            // getCountryCode() resolves correctly via its
                                            // substring/exact-match fallback in TravelMap.jsx.
                                            const cityClean = (city || '').trim();
                                            const countryClean = (country || '').trim();
                                            if (!cityClean && !countryClean) {
                                              showToast('Enter a city or country', 'error');
                                              return;
                                            }
                                            const query = cityClean && countryClean
                                              ? `${cityClean}, ${countryClean}`
                                              : (cityClean || countryClean);
                                            try {
                                              const res = await createTripLog({
                                                title: `Visit to ${query}`,
                                                destinations: [query],
                                                startDate: null,
                                                endDate: null,
                                                companions: null,
                                                visibility: 'private',
                                              });
                                              if (res?.error) throw new Error(res.error);
                                              showToast(`${query} added to your map!`, 'success');
                                              // Reload BOTH saved_itineraries AND trip_logs so
                                              // the parent's visitedCountrySet picks up the new
                                              // entry on the next render. Previously only
                                              // saved_itineraries was refreshed, but
                                              // createTripLog writes to trip_logs — so the new
                                              // entry never reflected in the count/map. The
                                              // visitedCountrySet computation now reads both
                                              // sources, so we refresh both here.
                                              if (user?.id) {
                                                try {
                                                  const { getTripLogs } = await import('./lib/communityApi');
                                                  const [itineraries, tlData] = await Promise.all([
                                                    getSavedItineraries(user.id).catch(() => []),
                                                    getTripLogs(user.id, 200, 0).catch(() => ({ trip_logs: [] })),
                                                  ]);
                                                  setSavedItinerariesList(itineraries || []);
                                                  setYouTripLogs(tlData?.trip_logs || []);
                                                } catch {}
                                              }
                                            } catch (err) {
                                              console.error('[Add Place] failed:', err);
                                              showToast(err?.message || 'Failed to add place', 'error');
                                            }
                                          }}
                                        />
                                      </CommunityErrorBoundary>
                                    </React.Suspense>
                                  </div>
                                </div>

                                {/* Quick Links: Bookmarks · Rankings · Reviews · Leaderboard.
                                    Moved here from below the travel map so everything actionable
                                    lives on the right side next to the branded passport. */}
                                <div className="grid grid-cols-2 gap-2">
                                  <button
                                    onClick={() => setCommunitySubTab('bookmarks')}
                                    className="rounded-[16px] flex items-center justify-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                    style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                                  >
                                    <Bookmark className="w-3.5 h-3.5" style={{ color: '#0d9488' }} />
                                    <span className="text-[11px] font-semibold" style={{ color: '#313143' }}>Bookmarks</span>
                                    {youBookmarks.length > 0 && <span className="text-[10px] font-bold" style={{ color: '#64748b' }}>{youBookmarks.length}</span>}
                                  </button>
                                  <button
                                    onClick={() => setCommunitySubTab('rankings')}
                                    className="rounded-[16px] flex items-center justify-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                    style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                                  >
                                    <Star className="w-3.5 h-3.5" style={{ color: '#0d9488' }} />
                                    <span className="text-[11px] font-semibold" style={{ color: '#313143' }}>Rankings</span>
                                  </button>
                                  <button
                                    onClick={() => setCommunitySubTab('my-reviews')}
                                    className="rounded-[16px] flex items-center justify-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                    style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                                  >
                                    <MessageSquare className="w-3.5 h-3.5" style={{ color: '#0d9488' }} />
                                    <span className="text-[11px] font-semibold" style={{ color: '#313143' }}>Reviews</span>
                                    {youReviews.length > 0 && <span className="text-[10px] font-bold" style={{ color: '#64748b' }}>{youReviews.length}</span>}
                                  </button>
                                  <button
                                    onClick={() => setCommunitySubTab('leaderboard')}
                                    className="rounded-[16px] flex items-center justify-center gap-2 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                                    style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                                  >
                                    <Trophy className="w-3.5 h-3.5" style={{ color: '#0d9488' }} />
                                    <span className="text-[11px] font-semibold" style={{ color: '#313143' }}>Leaderboard</span>
                                  </button>
                                </div>

                                {/* Share Taste Card — mirrors the mobile entry
                                    point in TasteProfileScreen so web users
                                    have parity when they want to share their
                                    personality card to IG / TikTok. Only shown
                                    when the user has a generated taste profile. */}
                                {tasteProfileData?.personality_title || tasteProfileData?.travel_personality ? (
                                  <button
                                    onClick={() => setShowShareableTasteCard(true)}
                                    className="w-full rounded-[16px] flex items-center justify-center gap-2 px-3 py-2.5 text-white transition-colors"
                                    style={{
                                      background: 'linear-gradient(135deg, #D97706 0%, #B45309 100%)',
                                      boxShadow: '0 2px 6px rgba(217,119,6,0.25)',
                                    }}
                                    title="Share your taste profile card"
                                  >
                                    <Sparkles className="w-3.5 h-3.5" />
                                    <span className="text-[11px] font-bold">Share Taste Card</span>
                                  </button>
                                ) : null}
                              </div>
                            </div>

                            {/* Previous duplicate Achievements + Taste Profile cards removed.
                                The passport on the left already renders both. */}

                            {/* Shareable Taste Card modal — opens from the
                                right-column button above. Mobile parity: the
                                mobile app renders this inline in the Taste
                                Profile screen; on web we gate it behind a
                                modal to avoid duplicating the passport's
                                inline taste section. */}
                            {showShareableTasteCard && tasteProfileData && (
                              <div
                                className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                                onClick={() => setShowShareableTasteCard(false)}
                              >
                                <div
                                  className="bg-white rounded-2xl w-full max-w-md max-h-[92vh] overflow-y-auto p-5"
                                  onClick={e => e.stopPropagation()}
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-1.5">
                                      <Sparkles className="w-4 h-4" style={{ color: '#D97706' }} />
                                      <h3 className="text-sm font-bold text-gray-800">Share Your Taste Card</h3>
                                    </div>
                                    <button
                                      onClick={() => setShowShareableTasteCard(false)}
                                      aria-label="Close"
                                      className="p-1.5 hover:bg-gray-100 rounded-full"
                                    >
                                      <X className="w-4 h-4 text-gray-400" />
                                    </button>
                                  </div>
                                  <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-xs">Loading...</div>}>
                                    <CommunityErrorBoundary>
                                      <ShareableTasteCard
                                        profile={tasteProfileData}
                                        userName={passportUserName}
                                        onClose={() => setShowShareableTasteCard(false)}
                                      />
                                    </CommunityErrorBoundary>
                                  </React.Suspense>
                                </div>
                              </div>
                            )}

                            {/* ── Reviews — compact inline list ── */}
                            {youReviews.length > 0 && (
                              <div className="rounded-[22px] p-4" style={{ background: 'white', border: '1px solid #E5E7EB', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                                <div className="flex items-center justify-between mb-2">
                                  <div className="text-[10px] font-extrabold uppercase tracking-wider" style={{ color: '#0d9488', letterSpacing: '1.2px' }}>Recent Reviews</div>
                                  <span className="text-[11px] font-bold" style={{ color: '#313143' }}>{youReviews.length}</span>
                                </div>
                                <div className="space-y-1 max-h-32 overflow-y-auto">
                                  {youReviews.slice(0, 5).map(r => (
                                    <div key={r.id} className="flex items-center gap-1.5 p-1.5 rounded-lg hover:bg-gray-50">
                                      <span className="text-[11px] font-semibold text-gray-700 truncate flex-1">{r.place_name}</span>
                                      {r.user_rating != null && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: '#0d9488', background: 'rgba(13,148,136,0.1)' }}>{parseFloat(r.user_rating).toFixed(1)}</span>}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Review Place Modal */}
                            {reviewingPlace && (
                              <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setReviewingPlace(null)}>
                                <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
                                  <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading...</div>}>
                                    <CommunityErrorBoundary>
                                      <PlaceReviewForm place={reviewingPlace} onClose={() => setReviewingPlace(null)} onSuccess={async () => {
                                        setReviewingPlace(null);
                                        const revData = await getMyReviews(100);
                                        setYouReviews(revData?.reviews || revData || []);
                                        // Auto-open pair-compare flow (bug #13 web parity)
                                        setShowCompareModal(true);
                                      }} />
                                    </CommunityErrorBoundary>
                                  </React.Suspense>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      };
                      return <YouProfileTab />;
                    })()}
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Sticky bottom bar for mobile web */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm border-t px-4 py-3 flex gap-2" style={{ borderColor: 'rgba(31,29,59,0.08)' }}>
        <button
          onClick={() => recentTrips[0] ? handleLoadItinerary(recentTrips[0]) : setStage('chat')}
          className="flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-opacity hover:opacity-90"
          style={{ backgroundColor: '#0d9488' }}
        >
          {recentTrips[0] ? 'Continue My Trip' : 'Plan My Trip'}
        </button>
        <button
          onClick={() => setStage('quiz')}
          className="px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors hover:bg-gray-50"
          style={{ borderColor: 'rgba(31,29,59,0.08)', color: '#64748b' }}
        >
          <Sparkles className="w-4 h-4 inline-block" style={{ color: '#0d9488' }} />
        </button>
      </div>

    </div>
    );
  };

  const renderQuiz = () => {
    const currentQuestion = quizQuestions[quizStep];
    const Icon = currentQuestion.icon;
    const progress = ((quizStep + 1) / quizQuestions.length) * 100;

    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-8 relative" style={{ background: '#F5F5F0' }}>
        {/* Topo background */}
        <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 900' preserveAspectRatio='none'%3E%3Cpath d='M0 120 Q300 60 600 140 T1200 100' fill='none' stroke='%230d9488' stroke-width='1.2'/%3E%3Cpath d='M0 300 Q400 250 700 320 T1200 280' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 520 Q300 480 600 540 T1200 510' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 750 Q250 720 550 770 T1200 740' fill='none' stroke='%23313143' stroke-width='1'/%3E%3C/svg%3E")`, backgroundSize: 'cover', opacity: 0.035 }} />
        <div className="max-w-2xl w-full relative z-10">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm" style={{ ...FONTS.body, color: '#64748b' }}>
                Question {quizStep + 1} of {quizQuestions.length}
              </span>
              <span className="text-sm" style={{ ...FONTS.body, color: '#64748b' }}>{Math.round(progress)}%</span>
            </div>
            <div className="w-full h-2 rounded-full overflow-hidden" style={{ background: '#E5E7EB' }}>
              <div
                className="h-full transition-all duration-500"
                style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #0d9488, #14b8a6)' }}
              />
            </div>
          </div>

          <div className="rounded-[28px] p-8 mb-6" style={{ background: 'white', border: '1px solid rgba(31,29,59,0.08)', boxShadow: '0 22px 60px rgba(28,41,53,0.07)' }}>
            <div className="flex items-center gap-4 mb-8">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}>
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl" style={{ ...FONTS.headingBold, color: '#313143' }}>{currentQuestion.question}</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {currentQuestion.options.map((option, index) => {
                const OptionIcon = option.icon;
                return (
                  <button
                    key={index}
                    onClick={() => handleQuizAnswer(option.value)}
                    className="group flex items-center gap-4 p-6 bg-gray-50 rounded-2xl hover:bg-[#a7d8db] transition-all duration-300 hover:scale-105 border-2 border-transparent hover:border-[#a7d8db]"
                  >
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <OptionIcon className="w-6 h-6 text-teal-600" />
                    </div>
                    <span className="text-lg text-gray-700 group-hover:text-white transition-colors" style={FONTS.body}>
                      {option.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center justify-between">
            {quizStep > 0 ? (
              <button
                onClick={() => setQuizStep(quizStep - 1)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Previous Question</span>
              </button>
            ) : <div />}
            <button
              onClick={() => { setQuizStep(0); setQuizAnswers({}); setStage('landing'); }}
              className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors text-sm"
            >
              <Home className="w-4 h-4" />
              <span>Home</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderChat = () => (
    <div className="h-screen flex flex-col overflow-hidden bg-white">

      {/* GLOBAL HEADER ALWAYS ON TOP */}
      <div className="chat-header flex-shrink-0 relative z-40" style={{ borderBottom: '1px solid rgba(31,29,59,0.08)', background: 'white', display: 'flex', justifyContent: 'space-between', padding: '14px 24px', alignItems: 'center' }}>
        <div className="flex items-center gap-2.5">
          {/* Paper-plane icon + inline wordmark + tagline. PR c7a108d3 had
             dropped the text JSX based on a comment that the artwork in
             arrivo-icon.png already contained it — but that asset is the
             paper plane only (arrivo-logo.png has the wordmark/tagline,
             but at header sizes the baked-in text is too small to read).
             Restored. */}
          <img src="/arrivo-icon.png" alt="Arrivo" className="h-10 w-auto object-contain" />
          <div className="flex flex-col">
            <h1 className="text-[22px] font-extrabold leading-none" style={{ fontFamily: "'Gulfs Display', 'Batangas', sans-serif", color: '#313143', letterSpacing: '0.05em' }}>
              arrivo
            </h1>
            <span className="text-[10px] font-medium mt-0.5" style={{ fontFamily: "'Batangas', sans-serif", color: '#097066', letterSpacing: '-0.01em' }}>
              plan less. explore more.
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <UserMenu onOpenSettings={() => setShowSettings(true)} onOpenSavedItineraries={handleOpenSavedItineraries} onUpgrade={goToPricing} onOpenAnalytics={() => setShowAnalytics(true)} />
          <button
            onClick={() => resetAllTripState()}
            className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-gray-50"
            style={{ background: '#f8fafc', color: '#64748b' }}
            title="Home"
          >
            <Home className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>

      <div className="chat-shell flex-1 relative bg-white flex overflow-hidden">
        {/* chat-main — flex:1, column layout */}
        <div className="chat-main flex-1 flex flex-col min-w-0">

          {/* Banners — inside chat-main, below header */}
          {isPaymentPastDue && (
            <div className="bg-amber-50 border-b border-amber-300 px-6 py-2.5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-lg">&#9888;&#65039;</span>
                  <div>
                    <span className="text-gray-800 font-semibold text-sm" style={FONTS.body}>Payment failed</span>
                    <span className="text-gray-600 text-sm ml-2" style={FONTS.body}>Update your payment method to restore access.</span>
                  </div>
                </div>
                <button onClick={handleStripePortal} className="px-3 py-1.5 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-xs font-medium whitespace-nowrap" style={FONTS.body}>Update Billing</button>
              </div>
            </div>
          )}

          {showHomeCityBanner && !userProfile.preferences.homeCity && (
            <div className="bg-gradient-to-r from-blue-50 to-emerald-50 border-b border-blue-200 px-6 py-2.5">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-teal-600" />
                  <span className="text-gray-700 text-sm" style={FONTS.body}>Set your departure city:</span>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={userProfile.preferences.homeCity || ''}
                    onChange={(e) => { if (e.target.value) { setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, homeCity: e.target.value } })); setShowHomeCityBanner(false); } }}
                    className="px-2 py-1.5 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none bg-white text-sm"
                    style={FONTS.body}
                  >
                    <option value="">Select city...</option>
                    <optgroup label="North America"><option value="New York">New York</option><option value="Los Angeles">Los Angeles</option><option value="Chicago">Chicago</option><option value="San Francisco">San Francisco</option><option value="Miami">Miami</option><option value="Boston">Boston</option><option value="Seattle">Seattle</option><option value="Toronto">Toronto</option><option value="Vancouver">Vancouver</option></optgroup>
                    <optgroup label="Europe"><option value="London">London</option><option value="Paris">Paris</option><option value="Amsterdam">Amsterdam</option><option value="Berlin">Berlin</option><option value="Madrid">Madrid</option><option value="Rome">Rome</option></optgroup>
                    <optgroup label="Asia Pacific"><option value="Singapore">Singapore</option><option value="Hong Kong">Hong Kong</option><option value="Tokyo">Tokyo</option><option value="Sydney">Sydney</option><option value="Dubai">Dubai</option></optgroup>
                  </select>
                  <input type="text" placeholder="Or type city..." className="px-2 py-1.5 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none w-28 text-sm" style={FONTS.body}
                    onKeyPress={(e) => { if (e.key === 'Enter' && e.target.value.trim()) { setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, homeCity: e.target.value.trim() } })); setShowHomeCityBanner(false); e.target.value = ''; } }}
                  />
                  <button onClick={() => setShowHomeCityBanner(false)} className="text-gray-400 hover:text-gray-600 px-1" title="Dismiss">✕</button>
                </div>
              </div>
            </div>
          )}

          {/* Departing from banner — only when sidebar is hidden */}
          {(tripContext?.originCity || userProfile.preferences.homeCity) && !showTripBuilder && (
            <div className="bg-teal-50 border-b border-teal-200 px-6 py-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-teal-700" style={FONTS.body}>
                  📍 Departing from: <strong style={FONTS.headingSemibold}>{toTitleCase(tripContext?.originCity || userProfile.preferences.homeCity)}</strong>
                </span>
                <button onClick={() => { setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, homeCity: '' } })); setShowHomeCityBanner(true); }} className="text-teal-600 hover:text-teal-800 underline text-xs" style={FONTS.body}>Change</button>
              </div>
            </div>
          )}

          {/* Origin/Destination banners — when sidebar is hidden */}
          {!showTripBuilder && (tripContext?.originCity || tripContext?.destinations?.length > 0) && (
            <div className="bg-amber-50 border-b border-amber-200 px-6 py-2">
              <div className="space-y-1">
                {tripContext?.originCity && userProfile.preferences.homeCity &&
                  tripContext.originCity.toLowerCase() !== userProfile.preferences.homeCity.toLowerCase() && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-amber-700 flex items-center gap-1" style={FONTS.body}><Plane className="w-3.5 h-3.5" /> From: <strong style={FONTS.headingSemibold}>{toTitleCase(tripContext.originCity)}</strong></span>
                    <div className="flex items-center gap-2">
                      <button onClick={() => { const oldHome = userProfile.preferences.homeCity; setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, homeCity: tripContext.originCity, returnCity: prev.preferences.returnCity || oldHome || null } })); showToast(`Updated departure to ${toTitleCase(tripContext.originCity)}`, 'success'); }} className="px-3 py-1 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors text-xs font-medium" style={FONTS.body}>Update From</button>
                      <button onClick={() => setTripContext(prev => ({ ...prev, originCity: null }))} className="text-amber-400 hover:text-amber-600 transition-colors" title="Dismiss">✕</button>
                    </div>
                  </div>
                )}
                {tripContext?.destinations?.length > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-teal-700 flex items-center gap-1" style={FONTS.body}><MapPin className="w-3.5 h-3.5" /> To: <strong style={FONTS.headingSemibold}>{tripContext.destinations.map(d => toTitleCase(d.name || d)).join(', ')}</strong></span>
                    <button className="px-3 py-1 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors text-xs font-medium" style={FONTS.body} onClick={() => showToast(`Confirmed destination`, 'success')}>Update To</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Chat messages area with cream bg + topo */}
          <div className="flex-1 relative px-7 py-8 overflow-y-auto" style={{ background: '#F5F5F0' }}>
        {/* Topographic SVG background overlay */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.04,
            zIndex: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 900' preserveAspectRatio='none'%3E%3Cpath d='M0 120 Q300 60 600 140 T1200 100' fill='none' stroke='%230d9488' stroke-width='1.2'/%3E%3Cpath d='M0 200 Q250 160 550 220 T1200 180' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 300 Q400 250 700 320 T1200 280' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 400 Q350 370 650 420 T1200 390' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 520 Q300 480 600 540 T1200 510' fill='none' stroke='%23313143' stroke-width='1.2'/%3E%3Cpath d='M0 640 Q400 600 750 660 T1200 630' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 750 Q250 720 550 770 T1200 740' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 850 Q350 810 700 870 T1200 840' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3C/svg%3E")`,
            backgroundSize: 'cover',
            backgroundRepeat: 'repeat-y',
          }}
        />
        <div className="w-full overflow-hidden relative" style={{ zIndex: 1 }}>
          {/* STEP 1: Trip Type Selection — only before type is chosen */}
          {messages.length === 0 && !tripType && (
            <div className="text-center mb-8">
                <>
                  <h2 className="text-3xl text-gray-800 mb-4" style={FONTS.headingBold}>
                    What kind of trip are you planning?
                  </h2>
                  <p className="text-gray-600 mb-8">
                    Choose how you'd like to plan your getaway
                  </p>

                  <div className="flex flex-col gap-4 justify-center mb-8 max-w-2xl mx-auto">
                    {/* Row 1: Main options (side by side when neither expanded, full width when one is expanded) */}
                    <div className={`flex flex-col sm:flex-row gap-4 ${tripTypeExpanded ? '' : ''}`}>
                      {/* Destination-first option */}
                      {(!tripTypeExpanded || tripTypeExpanded === 'destination') && (
                        <div className={`${tripTypeExpanded === 'destination' ? 'w-full' : 'flex-1'} transition-all duration-300`}>
                          <button
                            onClick={() => {
                              setTripType('destination');
                              setTripTypeExpanded(null);
                              setShowTripBuilder(true);
                            }}
                            className="w-full p-6 bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border-2 text-left group border-gray-200 hover:border-[#a7d8db] hover:scale-105"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl flex items-center justify-center transition-colors bg-teal-100 group-hover:bg-teal-200">
                                <MapPin className="w-7 h-7 text-teal-600" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-xl text-gray-800" style={FONTS.headingSemibold}>Explore a Destination</h3>
                                <p className="text-gray-600 text-sm mt-1" style={FONTS.body}>
                                  "I want to visit Paris, explore Japan, discover Bali..."
                                </p>
                                <p className="text-teal-600 text-xs mt-2" style={FONTS.body}>
                                  arrivo will build an itinerary around your destination
                                </p>
                              </div>
                            </div>
                          </button>
                        </div>
                      )}

                      {/* Hotel-first option */}
                      {(!tripTypeExpanded || tripTypeExpanded === 'hotel') && (
                        <div className={`${tripTypeExpanded === 'hotel' ? 'w-full' : 'flex-1'} transition-all duration-300`}>
                          <button
                            onClick={() => {
                              setTripType('hotel');
                              setTripScope('single');
                              setTripTypeExpanded(null);
                              setShowTripBuilder(true);
                            }}
                            className="w-full p-6 bg-white rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border-2 text-left group border-gray-200 hover:border-[#a7d8db] hover:scale-105"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl flex items-center justify-center transition-colors bg-teal-100 group-hover:bg-teal-200">
                                <Hotel className="w-7 h-7 text-teal-600" />
                              </div>
                              <div className="flex-1">
                                <h3 className="text-xl text-gray-800" style={FONTS.headingSemibold}>Stay at a Hotel</h3>
                                <p className="text-gray-600 text-sm mt-1" style={FONTS.body}>
                                  "I want to stay at Atlantis, a spa resort, Miraval..."
                                </p>
                                <p className="text-teal-600 text-xs mt-2" style={FONTS.body}>
                                  Itinerary will focus on the property experience
                                </p>
                              </div>
                            </div>
                          </button>

                          {/* One/Multiple sub-step removed — trip builder sidebar handles adding stops */}
                        </div>
                      )}
                    </div>
                  </div>
                </>
            </div>
          )}

          {/* STEP 2: AI greeting bubble — persists after messages arrive */}
          {tripType && (
            <div className="text-left mb-4">
              {/* Back button — only show when no messages yet */}
              {messages.length === 0 && (
                <button
                  onClick={() => {
                    setTripType(null);
                    setTripScope(null);
                    setTripTypeExpanded(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors mb-4 flex items-center gap-1 text-sm"
                  title="Change trip type"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span style={FONTS.body}>Back</span>
                </button>
              )}

              {/* AI greeting as chat bubble */}
              <div className="flex gap-3 items-start mb-6">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                </div>
                <div className="bg-white px-5 py-4 max-w-[540px] shadow-sm" style={{ borderRadius: '6px 22px 22px 22px', border: '1px solid rgba(31,29,59,0.08)' }}>
                  <h3 className="text-[16px] font-extrabold mb-1.5" style={{ color: '#313143', letterSpacing: '-0.02em' }}>
                    Hey! Where are you dreaming of going?
                  </h3>
                  <p className="text-[13px] mb-3.5" style={{ color: '#64748b', margin: 0 }}>
                    Tell me about your ideal trip — a destination, a vibe, or even just "somewhere warm with great food." I'll find the perfect match.
                  </p>
                  {/* Only show prompt pills when no messages yet */}
                  {messages.length === 0 && (
                    <div className="flex flex-wrap gap-2">
                      {promptBubbles.map((bubble, index) => {
                        const Icon = bubble.icon;
                        return (
                          <button
                            key={index}
                            onClick={() => handleSend(bubble.prompt)}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[12px] font-bold bg-white cursor-pointer transition-all hover:border-teal-400 hover:text-teal-600"
                            style={{ border: '1px solid rgba(31,29,59,0.08)', color: '#334155' }}
                          >
                            <Icon className="w-3.5 h-3.5 text-teal-600" />
                            {bubble.text}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {messages.map((message, index) => renderMessage(message, index))}
          
          {/* NEW: Personalization Panel with Buttons */}
          {showPersonalizationPanel && (
            <div className={`bg-white rounded-3xl p-8 shadow-xl border-2 ${tripType === 'hotel' ? 'border-teal-200' : 'border-teal-200'} mb-6`} style={FONTS.body}>
              <h3 className="text-2xl text-gray-800 mb-2" style={FONTS.headingBold}>
                {tripType === 'hotel' ? 'Personalize Your Resort Stay' : 'Personalize Your Trip'}
              </h3>
              {tripType === 'hotel' && selectedHotelProperty && (
                <p className="text-teal-600 mb-4" style={FONTS.body}>
                  Staying at: <span className="font-semibold">{selectedHotelProperty.name}</span>
                  {selectedHotelProperty.location && ` in ${selectedHotelProperty.location}`}
                </p>
              )}
              
              {/* Home City - NEW */}
              <div className="mb-6">
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Departure City
                  <span className="text-sm font-normal text-gray-500 ml-2">Where are you traveling from?</span>
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    placeholder="e.g., New York, London, Tokyo"
                    value={userProfile.preferences.homeCity || ''}
                    onChange={(e) => {
                      setUserProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, homeCity: e.target.value }
                      }));
                    }}
                    className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none text-lg"
                  />
                  {/* Quick city buttons */}
                  <div className="flex gap-2">
                    {['NYC', 'LA', 'London', 'Paris'].map(city => (
                      <button
                        key={city}
                        type="button"
                        onClick={() => {
                          setUserProfile(prev => ({
                            ...prev,
                            preferences: { ...prev.preferences, homeCity: city }
                          }));
                        }}
                        className={BTN.chip(userProfile.preferences.homeCity === city)}
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-1">Flight times and recommendations will be based on this city</p>
              </div>

              {/* Return City - Open Jaw Support */}
              <div className="mb-6">
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>
                  Returning To
                  <span className="text-sm font-normal text-gray-500 ml-2">Where are you traveling back to?</span>
                </label>
                <div className="flex gap-3 items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setUserProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, returnCity: null }
                      }));
                    }}
                    className={BTN.chip(!userProfile.preferences.returnCity)}
                  >
                    Same as departure (Round Trip)
                  </button>
                  <span className="text-gray-400">or</span>
                  <input
                    type="text"
                    placeholder="Different city"
                    value={userProfile.preferences.returnCity || ''}
                    onChange={(e) => {
                      setUserProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, returnCity: e.target.value || null }
                      }));
                    }}
                    className={`flex-1 px-4 py-3 border-2 rounded-lg focus:border-teal-500 focus:outline-none text-lg ${
                      userProfile.preferences.returnCity ? 'border-teal-500' : 'border-gray-200'
                    }`}
                  />
                </div>
                {userProfile.preferences.returnCity && (
                  <p className="text-xs text-teal-600 mt-1 flex items-center gap-1">
                    ✈️ Different return: Departing from <strong>{userProfile.preferences.homeCity || 'Home'}</strong> → returning to <strong>{userProfile.preferences.returnCity}</strong>
                  </p>
                )}
                {!userProfile.preferences.returnCity && (
                  <p className="text-xs text-gray-500 mt-1">Round trip: You'll fly back to {userProfile.preferences.homeCity || 'your departure city'}</p>
                )}
              </div>

              {/* Dietary Restrictions */}
              <div className="mb-6">
                <label className="block text-lg text-gray-700 mb-3" style={FONTS.headingSemibold}>Dietary Restrictions</label>
                <div className="flex flex-wrap gap-2">
                  {['Vegetarian', 'Vegan', 'Pescatarian', 'Gluten-Free', 'Dairy-Free', 'Nut Allergies', 'Shellfish/Fish Allergy', 'Low-Carb/Keto', 'Diabetic Diet', 'Kosher', 'Halal', 'None'].map(diet => (
                    <button
                      key={diet}
                      onClick={() => {
                        const current = userProfile.preferences.dietaryRestrictions || [];
                        const newDietary = current.includes(diet) 
                          ? current.filter(d => d !== diet)
                          : diet === 'None' ? ['None'] : [...current.filter(d => d !== 'None'), diet];
                        setUserProfile(prev => ({
                          ...prev,
                          preferences: { ...prev.preferences, dietaryRestrictions: newDietary }
                        }));
                      }}
                      className={BTN.pill((userProfile.preferences.dietaryRestrictions || []).includes(diet))}
                    >
                      <span style={FONTS.body}>{diet}</span>
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Other (type here)"
                  className="mt-3 w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                  style={FONTS.body}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const current = userProfile.preferences.dietaryRestrictions || [];
                      setUserProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, dietaryRestrictions: [...current, e.target.value.trim()] }
                      }));
                      e.target.value = '';
                    }
                  }}
                />
              </div>

              {/* Activity Level removed — Trip Pace covers this functionality */}


              {/* Flight & Hotel Information */}
              <div className="mb-6">
                <label className="block text-lg text-gray-700 mb-4" style={FONTS.headingSemibold}>Flight & Accommodation Details</label>
                
                {/* Flight Times */}
                <div className="bg-blue-50 rounded-xl p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-3">
                    Don't have flights yet? We'll plan around 8am arrival and 12pm departure
                  </p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-2" style={FONTS.body}>
                        Arrival Time
                      </label>
                      <input
                        type="time"
                        value={arrivalFlight.time}
                        onChange={(e) => setArrivalFlight({ ...arrivalFlight, time: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-2" style={FONTS.body}>
                        Departure Time
                      </label>
                      <input
                        type="time"
                        value={departureFlight.time}
                        onChange={(e) => setDepartureFlight({ ...departureFlight, time: e.target.value })}
                        className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  <div className="text-xs text-blue-700 bg-blue-100 rounded-lg p-2">
                    <strong>International destinations:</strong> We'll automatically add 1 hour for customs & immigration after landing
                  </div>
                </div>
                
                {/* Travel Class Preference — only show when flying */}
                {transportationMode === 'fly' && (
                <div className="bg-blue-50 rounded-xl p-4 mb-4">
                  <label className="block text-sm text-gray-700 mb-3" style={FONTS.body}>
                    Travel Class
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { id: 'ECONOMY', label: 'Economy' },
                      { id: 'PREMIUM_ECONOMY', label: 'Premium Economy' },
                      { id: 'BUSINESS', label: 'Business' },
                      { id: 'FIRST', label: 'First' }
                    ].map(cls => (
                      <button
                        key={cls.id}
                        onClick={() => setPreferredTravelClass(cls.id)}
                        className={BTN.chip(preferredTravelClass === cls.id)}
                      >
                        {cls.label}
                      </button>
                    ))}
                  </div>
                  {(preferredTravelClass === 'BUSINESS' || preferredTravelClass === 'FIRST') && (
                    <p className="text-xs text-blue-600 mt-2">Flight prices and recovery time will be adjusted for premium cabin travel.</p>
                  )}
                </div>
                )}

                {/* Accommodation Type Selector */}
                <div className="bg-purple-50 rounded-xl p-4 mb-4">
                  <label className="block text-sm text-gray-700 mb-3" style={FONTS.body}>
                    Accommodation Type
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {[
                      { id: 'hotel', label: 'Hotel/Resort', desc: 'Traditional hotel or resort stay' },
                      { id: 'cruise', label: 'Cruise/Sailing', desc: 'Ship is your accommodation' },
                      { id: 'campervan', label: 'Campervan/RV', desc: 'Mobile accommodation' },
                      { id: 'custom', label: 'Custom', desc: 'Airbnb, friend\'s place, etc.' }
                    ].map(type => (
                      <button
                        key={type.id}
                        onClick={() => setAccommodationType(type.id)}
                        className={BTN.chip(accommodationType === type.id)}
                        title={type.desc}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                  {accommodationType === 'custom' && (
                    <input
                      type="text"
                      placeholder="e.g., Friend's apartment, Airbnb in Le Marais, Hostel..."
                      value={customAccommodationName}
                      onChange={(e) => setCustomAccommodationName(e.target.value)}
                      className="w-full px-4 py-2 border-2 border-purple-200 rounded-lg focus:border-purple-500 focus:outline-none text-sm mt-2"
                    />
                  )}
                  {accommodationType === 'cruise' && (
                    <p className="text-xs text-purple-600 mt-2">Ship is your hotel. Timeline will show port days with shore excursions and a "return to ship" reminder.</p>
                  )}
                  {accommodationType === 'campervan' && (
                    <p className="text-xs text-purple-600 mt-2">No hotel needed. Timeline will include campsite suggestions and morning/evening routines.</p>
                  )}
                </div>

                {/* Hotel Selection — only show for hotel/custom accommodation types */}
                {(accommodationType === 'hotel' || accommodationType === 'custom') && (
                <div className="bg-teal-50 rounded-xl p-4">
                  <label className="block text-sm text-gray-700 mb-3" style={FONTS.body}>
                    {accommodationType === 'custom' ? `${customAccommodationName || 'Custom'} Details` : 'Hotel Preference'}
                  </label>
                  <p className="text-xs text-gray-600 mb-3">
                    Select your preferred hotel area or let us choose the best location for you
                  </p>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    {['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].map(area => (
                      <button
                        key={area}
                        onClick={() => {
                          setSelectedHotel(area);
                        }}
                        className={BTN.pill(selectedHotel === area)}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                  
                  <input
                    type="text"
                    placeholder="Or enter specific hotel name..."
                    value={selectedHotel && !['City Center', 'Near Airport', 'Beach/Waterfront', 'Tourist District', 'Budget Area', 'Let arrivo Choose'].includes(selectedHotel) ? selectedHotel : ''}
                    onChange={(e) => setSelectedHotel(e.target.value)}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none text-sm"
                  />
                </div>
              )}
              </div>

              {/* Submit Button */}
              <button
                onClick={async () => {
                  // Debounce to prevent double-click
                  if (isButtonDebounced('generate-itinerary-main', 2000)) {
                    debugLog('⚠️ Generate button debounced - ignoring rapid click');
                    return;
                  }

                  // Also check if generation is already in progress
                  if (generationInProgressRef.current) {
                    debugLog('⚠️ Generation already in progress - ignoring click');
                    return;
                  }

                  setShowPersonalizationPanel(false);

                  // Calculate trip duration
                  const prefs = userProfile.preferences;
                  let tripDuration = prefs.duration || 7;
                  if (prefs.startDate && prefs.endDate) {
                    const diffDays = calculateDaysBetween(prefs.startDate, prefs.endDate);
                    if (diffDays && diffDays > 0) tripDuration = diffDays;
                  }

                  // Use smart generation (picks progressive vs single-call based on trip length)
                  await generateItinerarySmart(selectedDestination);
                }}
                disabled={!userProfile.preferences.activityLevel || !userProfile.preferences.preferredPace || stage === 'loading'}
                className={BTN.primary}
              >
                {tripType === 'hotel' ? 'Create My Hotel Itinerary' : 'Generate My Itinerary'}
              </button>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
      </div>
      {/* End chat messages area */}

      {/* Chat input bar — .chat-input-bar */}
      <div className="flex items-center gap-2.5" style={{ padding: '16px 24px', background: 'white', borderTop: '1px solid rgba(31,29,59,0.08)' }}>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && tripType && handleSend()}
            placeholder={!tripType ? "Select an option above to get started..." : "Type your message..."}
            className={`flex-1 px-5 py-3.5 text-sm focus:outline-none ${!tripType ? 'cursor-not-allowed' : ''}`}
            style={{
              ...FONTS.body,
              borderRadius: '22px',
              border: '1.5px solid #e2e8f0',
              background: !tripType ? '#f1f5f9' : '#f8fafc',
              color: '#313143',
              fontSize: '14px',
            }}
            disabled={isProcessing || !tripType}
          />
          <button
            onClick={() => handleSend()}
            disabled={isProcessing || !input.trim() || !tripType}
            className="w-11 h-11 rounded-full flex items-center justify-center text-white disabled:opacity-40 transition-all"
            style={{ background: 'linear-gradient(135deg, #0d9488, #0f766e)', boxShadow: '0 4px 14px rgba(13,148,136,0.2)' }}
          >
            <Send className="w-5 h-5" />
          </button>
      </div>
      </div>
      {/* End chat-main */}

      {/* Trip Builder Sidebar — .chat-sidebar */}
      {showTripBuilder && (
        <div className="w-[300px] flex-shrink-0 border-l flex flex-col" style={{ borderColor: 'rgba(31,29,59,0.08)', background: '#f8fafc' }}>
          {/* sb-header — clean, matching mockup */}
          <div style={{ padding: '16px 20px', background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}>
            <h3 className="text-[15px] font-extrabold text-white" style={{ margin: 0 }}>Trip Builder</h3>
            <span className="text-[11px] font-semibold text-white/70 block mt-0.5" style={FONTS.body}>Building as you chat</span>
          </div>

          {/* Origin/destination badges — below header */}
          <div style={{ padding: '0 20px' }}>
            {/* Origin city mismatch badge — AI detected user is in a different city */}
            {tripContext?.originCity && userProfile.preferences.homeCity &&
              tripContext.originCity.toLowerCase() !== userProfile.preferences.homeCity.toLowerCase() && (
              <div className="mt-2 flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="text-amber-600 flex-1" style={FONTS.body}>
                  💡 Departing from <strong style={FONTS.headingSemibold}>{toTitleCase(tripContext.originCity)}</strong>
                </span>
                <button
                  onClick={() => {
                    // Preserve old homeCity as returnCity (open-jaw) so "To" doesn't visually change
                    const oldHome = userProfile.preferences.homeCity;
                    setUserProfile(prev => ({
                      ...prev,
                      preferences: {
                        ...prev.preferences,
                        homeCity: tripContext.originCity,
                        returnCity: prev.preferences.returnCity || oldHome || null,
                      }
                    }));
                    showToast(`Updated departure to ${toTitleCase(tripContext.originCity)}`, 'success');
                  }}
                  className="px-2 py-1 bg-amber-500 text-white rounded-md hover:bg-amber-600 transition-colors whitespace-nowrap"
                  style={FONTS.body}
                >
                  Update From
                </button>
                <button
                  onClick={() => {
                    // Dismiss by clearing the origin city from trip context
                    setTripContext(prev => ({ ...prev, originCity: null }));
                  }}
                  className="text-amber-400 hover:text-amber-600 transition-colors"
                  title="Dismiss"
                >
                  ✕
                </button>
              </div>
            )}

          </div>

          {/* sb-body — Stops List - Draggable */}
          <div
            className="flex-1 overflow-y-auto space-y-1.5"
            style={{ padding: '20px' }}
            onDragOver={(e) => {
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
            }}
            onDrop={(e) => {
              // Calculate drop position based on mouse Y relative to card positions
              e.preventDefault();
              const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
              if (isNaN(fromIndex)) { setDraggedStopIndex(null); return; }
              const container = e.currentTarget;
              const cards = container.querySelectorAll('[data-stop-index]');
              let toIndex = tripBuilderStops.length - 1; // default: end
              const mouseY = e.clientY;
              for (const card of cards) {
                const rect = card.getBoundingClientRect();
                const cardMidpoint = rect.top + rect.height / 2;
                const cardIndex = parseInt(card.dataset.stopIndex);
                if (mouseY < cardMidpoint) {
                  toIndex = cardIndex;
                  break;
                }
              }
              // Adjust if dragging downward (insertion shifts)
              if (fromIndex < toIndex) toIndex = Math.max(0, toIndex - 1);
              if (fromIndex !== toIndex) {
                reorderTripBuilderStops(fromIndex, toIndex);
              }
              setDraggedStopIndex(null);
            }}
          >
            {/* Departing From Section */}
            <div className="mb-4">
               <div className="text-[9px] font-extrabold uppercase mb-1.5" style={{ color: '#94a3b8', letterSpacing: '0.06em' }}>Departing From</div>
               <label 
                  className="bg-white rounded-lg px-2.5 py-2 flex items-center gap-2 mb-2 group cursor-text focus-within:border-teal-400 transition-colors" 
                  style={{ border: '1px solid rgba(31,29,59,0.15)', boxShadow: '0 2px 8px rgba(28,41,53,0.04)' }}
               >
                 <div className="flex-1 min-w-0">
                   <input
                     key={`dep-${userProfile.preferences.homeCity || tripContext?.originCity || ''}`}
                     type="text"
                     className="w-full text-[12px] font-extrabold text-[#313143] bg-transparent outline-none placeholder-gray-400"
                     placeholder="Enter departure city..."
                     defaultValue={userProfile.preferences.homeCity || tripContext?.originCity || ''}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter') {
                         const val = e.target.value.trim();
                         if (val) {
                           // Silent backend validation — correct or reject without popups
                           validateCityName(val).then(result => {
                             if (result?.valid) {
                               const corrected = result.correctedName || val;
                               setUserProfile(prev => ({...prev, preferences: {...prev.preferences, homeCity: corrected}}));
                               if (corrected.toLowerCase() !== val.toLowerCase()) {
                                 e.target.value = corrected; // Update visible input
                               }
                             } else {
                               // Invalid city — revert to previous value
                               const prev = userProfile.preferences.homeCity || tripContext?.originCity || '';
                               e.target.value = prev;
                               showToast(`"${val}" doesn't appear to be a valid city`, 'error');
                             }
                           }).catch(() => {
                             // Network error — accept the input as-is
                             setUserProfile(prev => ({...prev, preferences: {...prev.preferences, homeCity: val}}));
                           });
                         }
                         e.target.blur();
                       }
                     }}
                     onBlur={(e) => {
                       const val = e.target.value.trim();
                       if (val && val.toLowerCase() !== (userProfile.preferences.homeCity || tripContext?.originCity || '').toLowerCase()) {
                         // Silent backend validation — correct or reject without popups
                         validateCityName(val).then(result => {
                           if (result?.valid) {
                             const corrected = result.correctedName || val;
                             setUserProfile(prev => ({...prev, preferences: {...prev.preferences, homeCity: corrected}}));
                             if (corrected.toLowerCase() !== val.toLowerCase()) {
                               e.target.value = corrected;
                             }
                           } else {
                             const prev = userProfile.preferences.homeCity || tripContext?.originCity || '';
                             e.target.value = prev;
                             showToast(`"${val}" doesn't appear to be a valid city`, 'error');
                           }
                         }).catch(() => {
                           setUserProfile(prev => ({...prev, preferences: {...prev.preferences, homeCity: val}}));
                         });
                       }
                     }}
                   />
                 </div>
                 <Edit2 className="w-3.5 h-3.5 text-gray-400 group-hover:text-teal-600 transition-colors pointer-events-none" />
               </label>

               <div className="text-[9px] font-extrabold uppercase mb-1.5 mt-2.5" style={{ color: '#94a3b8', letterSpacing: '0.06em' }}>Returning To</div>
               <label className="bg-white rounded-lg px-2.5 py-2 flex items-center gap-2 group cursor-text focus-within:border-teal-400 transition-colors" style={{ border: '1px solid rgba(31,29,59,0.15)', boxShadow: '0 2px 8px rgba(28,41,53,0.04)' }}>
                 <div className="flex-1 min-w-0">
                   <input
                     key={`ret-${userProfile.preferences.returnCity || tripContext?.originCity || userProfile.preferences.homeCity || ''}`}
                     type="text"
                     className="w-full text-[12px] font-extrabold text-[#313143] bg-transparent outline-none placeholder-gray-400"
                     placeholder="Enter return city..."
                     defaultValue={userProfile.preferences.returnCity || tripContext?.originCity || userProfile.preferences.homeCity || ''}
                     onKeyDown={(e) => {
                       if (e.key === 'Enter') {
                         const val = e.target.value.trim();
                         if (val) {
                           validateCityName(val).then(result => {
                             if (result?.valid) {
                               const corrected = result.correctedName || val;
                               setUserProfile(prev => ({...prev, preferences: {...prev.preferences, returnCity: corrected}}));
                               if (corrected.toLowerCase() !== val.toLowerCase()) {
                                 e.target.value = corrected;
                               }
                             } else {
                               const prev = userProfile.preferences.returnCity || tripContext?.originCity || userProfile.preferences.homeCity || '';
                               e.target.value = prev;
                               showToast(`"${val}" doesn't appear to be a valid city`, 'error');
                             }
                           }).catch(() => {
                             setUserProfile(prev => ({...prev, preferences: {...prev.preferences, returnCity: val}}));
                           });
                         }
                         e.target.blur();
                       }
                     }}
                     onBlur={(e) => {
                       const val = e.target.value.trim();
                       if (val && val.toLowerCase() !== (userProfile.preferences.returnCity || tripContext?.originCity || userProfile.preferences.homeCity || '').toLowerCase()) {
                         validateCityName(val).then(result => {
                           if (result?.valid) {
                             const corrected = result.correctedName || val;
                             setUserProfile(prev => ({...prev, preferences: {...prev.preferences, returnCity: corrected}}));
                             if (corrected.toLowerCase() !== val.toLowerCase()) {
                               e.target.value = corrected;
                             }
                           } else {
                             const prev = userProfile.preferences.returnCity || tripContext?.originCity || userProfile.preferences.homeCity || '';
                             e.target.value = prev;
                             showToast(`"${val}" doesn't appear to be a valid city`, 'error');
                           }
                         }).catch(() => {
                           setUserProfile(prev => ({...prev, preferences: {...prev.preferences, returnCity: val}}));
                         });
                       }
                     }}
                   />
                 </div>
                 <Edit2 className="w-3.5 h-3.5 text-gray-400 group-hover:text-teal-600 transition-colors pointer-events-none" />
               </label>
            </div>

            {/* sb-label for Destinations */}
            <div className="text-[10px] font-extrabold uppercase mb-2" style={{ color: '#94a3b8', letterSpacing: '0.06em' }}>{tripType === 'hotel' ? 'Properties' : 'Destinations'}</div>

            {/* Empty State */}
            {tripBuilderStops.length === 0 && !addingStopType && (
              <div className="text-center py-8 text-gray-400">
                <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm" style={FONTS.body}>No stops added yet</p>
                <p className="text-xs mt-1">Add {tripType === 'hotel' ? 'properties' : 'destinations'} from the chat or type below</p>
              </div>
            )}

            {tripBuilderStops.map((stop, index) => {
              // Determine leg key for transport connector AFTER this stop
              const nextStop = tripBuilderStops[index + 1];
              const legKey = nextStop ? `${stop.id}→${nextStop.id}` : `${stop.id}→return`;
              const legData = legTransports[legKey] || {};
              const modeDisplay = legData.mode ? getTransportModeDisplay(legData.mode) : null;

              return (
                <React.Fragment key={stop.id}>
                  <div
                    data-stop-index={index}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', index.toString());
                      e.dataTransfer.effectAllowed = 'move';
                      setDraggedStopIndex(index);
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.dataTransfer.dropEffect = 'move';
                      e.currentTarget.classList.add('border-teal-400', 'bg-teal-50');
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove('border-teal-400', 'bg-teal-50');
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      e.currentTarget.classList.remove('border-teal-400', 'bg-teal-50');
                      const fromIndex = parseInt(e.dataTransfer.getData('text/plain'));
                      if (!isNaN(fromIndex) && fromIndex !== index) {
                        reorderTripBuilderStops(fromIndex, index);
                      }
                      setDraggedStopIndex(null);
                    }}
                    onDragEnd={() => setDraggedStopIndex(null)}
                    className={`group relative bg-white rounded-[14px] cursor-grab active:cursor-grabbing transition-all ${
                      draggedStopIndex === index ? 'opacity-50 border-dashed border-gray-300' : ''
                    }`}
                    style={{ border: '1px solid rgba(31,29,59,0.08)', boxShadow: '0 4px 16px rgba(28,41,53,0.05)', padding: '12px 14px' }}
                  >
                    {/* Stack the name on its own row (full-width) so long
                        hotel names like "Four Seasons Resort Maldives at
                        Landaa Giraavaru" don't have to wrap into a narrow
                        column shared with the [Hotel] [N days] [×]
                        controls. Metadata moves to a compact footer row. */}
                    <div className="flex items-start gap-2 min-w-0">
                      {stop.flag && <span className="text-sm flex-shrink-0 mt-0.5">{stop.flag}</span>}
                      <h4 className="text-[13px] font-extrabold text-[#313143] break-words min-w-0 flex-1" title={stop.name}>
                        {stop.name?.split(',')[0]}
                      </h4>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          removeTripBuilderStop(stop.id);
                        }}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all flex-shrink-0 mt-0.5"
                        title="Remove stop"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      {(stop.stopType === 'hotel' || stop.stopType === 'resort') && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">Hotel</span>
                      )}
                      <span className="text-[11px] font-semibold flex items-center" style={{ color: '#64748b' }}>
                        <input
                          type="number"
                          value={stop.days}
                          min="1"
                          max="14"
                          draggable={false}
                          onChange={(e) => {
                            const raw = e.target.value;
                            if (raw === '') { updateTripBuilderStopDays(stop.id, ''); return; }
                            const n = parseInt(raw);
                            if (!isNaN(n)) updateTripBuilderStopDays(stop.id, n);
                          }}
                          onBlur={() => {
                            if (!stop.days || stop.days < 1) updateTripBuilderStopDays(stop.id, 1);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="w-8 text-center bg-transparent text-[11px] font-semibold focus:outline-none focus:bg-white focus:border-teal-400 rounded"
                          style={{ color: '#64748b' }}
                        />
                        <span className="ml-1">days</span>
                      </span>
                    </div>

                    {/* Bug 2 V2 — per-stop "stay on property" toggle moved to
                        Personalization page (Hotel Preferences section). Trip
                        builder stays simple per user feedback. */}
                  </div>

                  {/* removed inter-city transport connectors — transport selection moved to personalization page */}
                </React.Fragment>
              );
            })}

          </div>

          {/* Add Stop — Two branded buttons or expanded input (outside scroll area so always visible) */}
          <div className="flex-shrink-0 pb-3 pt-1 space-y-2" style={{ padding: '0 20px 12px' }}>
            {addingStopType ? (
              <div className={`border-2 rounded-xl p-3 transition-all ${
                addingStopType === 'resort'
                  ? 'border-slate-400 bg-slate-50/70'
                  : 'border-teal-400 bg-teal-50/70'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {addingStopType === 'resort' ? (
                    <div className="w-6 h-6 rounded-lg bg-slate-200/60 flex items-center justify-center">
                      <Hotel className="w-3.5 h-3.5 text-slate-700" />
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-lg bg-teal-200/60 flex items-center justify-center">
                      <MapPin className="w-3.5 h-3.5 text-teal-700" />
                    </div>
                  )}
                  <span className={`text-xs font-medium ${
                    addingStopType === 'resort' ? 'text-slate-700' : 'text-teal-700'
                  }`} style={FONTS.bodyBold}>
                    {addingStopType === 'resort' ? 'Add a Hotel' : 'Add a Destination'}
                  </span>
                </div>
                {/* Real-time restriction warning */}
                {(() => {
                  if (!newStopInput.trim()) return null;
                  const restriction = checkDestinationRestriction(newStopInput.trim());
                  if (restriction.allowed) return null;
                  return (
                    <div className="mb-2 px-3 py-2 bg-red-50 border border-red-200 rounded-xl">
                      <p className="text-xs text-red-600 font-medium" style={FONTS.body}>{restriction.message}</p>
                    </div>
                  );
                })()}
                <input
                  type="text"
                  value={newStopInput}
                  onChange={(e) => setNewStopInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newStopInput.trim()) {
                      addTripBuilderStop({
                        name: newStopInput.trim(),
                        stopType: addingStopType,
                        continent: '',
                        days: 3,
                        hotel: null
                      });
                      setNewStopInput('');
                      setAddingStopType(null);
                    } else if (e.key === 'Escape') {
                      setNewStopInput('');
                      setAddingStopType(null);
                    }
                  }}
                  placeholder={addingStopType === 'resort'
                    ? 'e.g. Four Seasons Bali, Aman Tokyo...'
                    : 'e.g. Paris, Tokyo, Bali...'}
                  className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none ${
                    addingStopType === 'resort'
                      ? 'border-slate-200 focus:border-slate-400'
                      : 'border-teal-200 focus:border-teal-400'
                  }`}
                  style={FONTS.body}
                  autoFocus
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={() => {
                      if (newStopInput.trim()) {
                        addTripBuilderStop({
                          name: newStopInput.trim(),
                          stopType: addingStopType,
                          continent: '',
                          days: 3,
                          hotel: null
                        });
                        setNewStopInput('');
                        setAddingStopType(null);
                      }
                    }}
                    disabled={!newStopInput.trim()}
                    className={`flex-1 py-1.5 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors ${
                      addingStopType === 'resort'
                        ? 'bg-slate-500 hover:bg-slate-600'
                        : 'bg-teal-500 hover:bg-teal-600'
                    }`}
                    style={FONTS.body}
                  >
                    Add
                  </button>
                  <button
                    onClick={() => {
                      setNewStopInput('');
                      setAddingStopType(null);
                    }}
                    className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-sm hover:bg-gray-300 transition-colors"
                    style={FONTS.body}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <button
                  onClick={() => setAddingStopType('destination')}
                  className="w-full rounded-xl p-2 hover:border-teal-400 hover:bg-teal-50/50 transition-all text-center"
                  style={{ border: '1.5px dashed rgba(148,163,184,0.5)', background: 'transparent' }}
                >
                  <span className="text-xs font-bold" style={{ color: '#94a3b8' }}>
                    + Add stop
                  </span>
                </button>
                <button
                  onClick={() => setAddingStopType('resort')}
                  className="w-full rounded-xl p-2 hover:border-slate-400 hover:bg-slate-50/50 transition-all text-center"
                  style={{ border: '1.5px dashed rgba(148,163,184,0.5)', background: 'transparent' }}
                >
                  <span className="text-xs font-bold" style={{ color: '#94a3b8' }}>
                    + Add hotel
                  </span>
                </button>
              </div>
            )}
          </div>


          {/* Trip Summary & Actions */}
          <div className="border-t" style={{ padding: '16px 20px', borderColor: 'rgba(31,29,59,0.06)', background: tripBuilderStops.length > 0 ? '#f8fafc' : 'transparent' }}>
            {tripBuilderStops.length > 0 && (
              <>
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-gray-600" style={FONTS.body}>Total Duration</span>
                  <span className="font-semibold text-gray-800" style={FONTS.headingSemibold}>{getTripBuilderTotalDays()} days</span>
                </div>
                <div className="flex items-center justify-between text-sm mb-4">
                  <span className="text-gray-600" style={FONTS.body}>Route</span>
                  <span className="text-xs text-gray-500 truncate max-w-[180px]" title={getTripBuilderRoute()}>
                    {getTripBuilderRoute()}
                  </span>
                </div>
              </>
            )}

            {/* Create Itinerary */}
            <button
              disabled={tripBuilderStops.length === 0}
              onClick={() => {
                // Prevent 0-stop trip creation
                if (tripBuilderStops.length === 0) return;
                // PERFORMANCE: Transition to personalization immediately for perceived speed
                // Build destination object synchronously
                const isResortFlow = tripType === 'hotel';
                const tripTypeValue = isResortFlow ? 'resort-hopping' : 'multi-city';
                // BUG 10 FIX: Use resolvedLocation (geographic city) when available for transport lookups
                // This ensures transportInfo keys match the resolved city names used elsewhere
                // Use resolvedLocation when available; for bare country names still pending resolution,
                // try to extract a more specific name from the original recommendation context
                const stopNames = tripBuilderStops.map(s => {
                  if (s.resolvedLocation) return s.resolvedLocation;
                  // If name is "City, Country" format, it's already specific enough
                  if (s.name.includes(',')) return s.name;
                  // Check if this looks like a bare country/region name (no comma, generic)
                  // If so, check the original recommendation message for a more specific city
                  const nameLower = s.name.toLowerCase().trim();
                  const COUNTRY_NAMES_QUICK = ['canada','mexico','brazil','argentina','chile','colombia','peru',
                    'japan','china','india','thailand','vietnam','indonesia','malaysia','philippines',
                    'australia','france','spain','italy','germany','portugal','greece','turkey',
                    'egypt','morocco','kenya','iceland','norway','sweden','switzerland','austria',
                    'netherlands','ireland','croatia','hungary','poland','singapore','korea','taiwan'];
                  if (COUNTRY_NAMES_QUICK.some(c => nameLower === c)) {
                    // Try to find a city from the last AI message that mentions this country
                    const lastAiMsg = messages.filter(m => m.role === 'assistant').pop()?.content || '';
                    // Look for "City, Country" pattern in the message
                    const cityCountryMatch = lastAiMsg.match(new RegExp(`([A-Z][a-zA-ZÀ-ÿ\\s]+),\\s*${s.name}`, 'i'));
                    if (cityCountryMatch) {
                      const cityName = cityCountryMatch[1].trim();
                      debugLog(`📍 Resolved bare country "${s.name}" → "${cityName}, ${s.name}" from message context`);
                      return `${cityName}, ${s.name}`;
                    }
                  }
                  return s.name;
                });
                // Display names: resort stops keep original name, destination stops use resolved location
                // This supports mixed trips (e.g., "Paris" destination + "Four Seasons Bali" resort)
                const displayNames = tripBuilderStops.map((s, i) => {
                  if ((s.stopType === 'hotel' || s.stopType === 'resort')) return s.name;
                  return stopNames[i]; // resolved location for destination stops
                });
                const homeCity = userProfile?.preferences?.homeCity || tripContext?.originCity || '';
                const firstStop = stopNames.length > 0 ? stopNames[0] : null;
                const firstStopFlightHours = firstStop ? getFlightHoursSync(firstStop, homeCity) : null;

                const isEurope = ['portugal', 'spain', 'france', 'italy', 'germany', 'uk', 'netherlands', 'greece']
                  .some(c => firstStop?.toLowerCase().includes(c));
                const defaultFlightHours = isEurope ? 8 : 10;

                const dest = {
                  name: displayNames.length === 1 ? displayNames[0] : `${displayNames.length}-Stop Trip`,
                  stops: tripBuilderStops,
                  stopNames: stopNames,       // Resolved locations for transport/airport lookups
                  displayNames: displayNames, // Original resort names for resort flow, same as stopNames for dest flow
                  description: `${tripBuilderStops.length} ${tripBuilderStops.length === 1 ? 'stop' : 'stops'}`,
                  flightHours: firstStopFlightHours || defaultFlightHours,
                  highlights: displayNames.map(s => s.split(',')[0]),
                  tripType: tripTypeValue,
                  isMultiStop: tripBuilderStops.length > 1
                };

                // Safety check before proceeding
                const destName = dest.name;
                const destSafetyCheck = checkDestinationRestriction(destName);
                if (!destSafetyCheck.allowed) {
                  setMessages(prev => [...prev, {
                    role: 'assistant',
                    content: `⚠️ I cannot help plan a trip to ${destName}.\n\n${destSafetyCheck.message}`
                  }]);
                  return;
                }

                // IMMEDIATE TRANSITION: Set stage first for instant feedback
                const totalDays = getTripBuilderTotalDays();
                const defaultDuration = totalDays || userProfile.preferences.duration || 7;

                // Initialize trip plan synchronously
                const newTripPlan = initializeTripPlan(dest.name, defaultDuration, {
                  transportMode: transportationMode || 'fly'
                });

                // Reset generation state to prevent stale guard blocking new generation
                generationInProgressRef.current = false;
                setIsProgressiveGenerating(false);
                setCurrentlyGeneratingDay(null);

                // Batch all state updates together using React's automatic batching
                setSelectedDestination(dest);
                setTripPlan(newTripPlan);
                setUserProfile(prev => {
                  const newPrefs = { ...prev.preferences, duration: totalDays };
                  // Clear stale dates from previously loaded itineraries — they belong to a different trip
                  // If dates exist and don't match the new duration, reset them
                  if (newPrefs.startDate && newPrefs.endDate) {
                    const existingDays = calculateDaysBetween(newPrefs.startDate, newPrefs.endDate);
                    if (existingDays !== totalDays) {
                      debugLog(`📅 Clearing stale dates (${newPrefs.startDate} – ${newPrefs.endDate}, ${existingDays} days) — new trip is ${totalDays} days`);
                      newPrefs.startDate = null;
                      newPrefs.endDate = null;
                    }
                  }
                  return { ...prev, preferences: newPrefs };
                });
                setStage('personalization'); // Transition immediately

                debugLog(`✈️ Quick transition to personalization: ${dest.name}`);
              }}
              className={`w-full py-3 rounded-full text-[13px] font-bold transition-all flex items-center justify-center gap-2 ${
                tripBuilderStops.length === 0
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'text-white hover:shadow-lg'
              }`}
              style={tripBuilderStops.length > 0 ? { background: 'linear-gradient(135deg, #0d9488, #0f766e)', boxShadow: '0 6px 18px rgba(13,148,136,0.2)' } : {}}
            >
              <Sparkles className="w-4 h-4" />
              Create Itinerary
            </button>

            {/* Clear Trip */}
            <button
              onClick={clearTripBuilder}
              className="w-full mt-2 py-2 text-gray-500 hover:text-gray-700 text-sm transition-all"
              style={FONTS.body}
            >
              Clear All
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  );

  // Chat handler for itinerary modifications (accepts optional direct message for programmatic sends)
  const handleItineraryChatSend = async (directMessage) => {
    const userMessage = directMessage || itineraryChatInput;
    if (!userMessage?.trim()) return;
    setItineraryChatInput('');
    const lowerMessage = userMessage.toLowerCase();

    // DETECT "ACCEPT PENDING SUGGESTION" intent
    // When there are pending changes and the user says "yes", "do it", "as suggested", etc.
    // Apply the pending changes instead of making a new API call
    if (pendingChanges && pendingChanges.changes.length > 0) {
      const acceptPatterns = [
        /\b(?:yes|yeah|yep|yup|sure|ok|okay|go\s*ahead|do\s*it|accept|apply|confirm)\b/i,
        /\bas\s*suggested\b/i,
        /\bmake\s*(?:the|that|those)?\s*change/i,
        /\bapply\s*(?:the|that|those|it|them)?\s*(?:change|suggestion|update)?/i,
        /\baccept\s*(?:the|that|those|it|them)?\s*(?:change|suggestion|update)?/i,
        /\bchange\s*(?:to|it\s*to)\s*.+\s*as\s*suggested/i,
        /\bswitch\s*(?:to|it\s*to)\s*.+\s*as\s*suggested/i,
        /\bgo\s*(?:with|for)\s*(?:that|it|the\s*suggestion)/i,
        /\blooks?\s*good/i,
        /\bsounds?\s*good/i,
        /\bperfect\b/i,
        /\blet'?s?\s*do\s*(?:it|that|this)/i,
      ];

      const isAcceptingPending = acceptPatterns.some(p => p.test(lowerMessage));

      if (isAcceptingPending) {
        debugLog('✅ User is accepting pending changes via chat message');
        // Add user message to chat
        const newChatMessages = [
          ...itineraryChatMessages,
          { role: 'user', content: userMessage }
        ];
        setItineraryChatInput('');

        // Apply all pending changes
        applyPendingChanges(pendingChanges.changes.map((_, i) => i));

        // Add confirmation message
        setItineraryChatMessages([
          ...newChatMessages,
          { role: 'assistant', content: `Done! I've applied the ${pendingChanges.changes.length > 1 ? `${pendingChanges.changes.length} suggested changes` : 'suggested change'} to your itinerary. 🎉` }
        ]);
        return;
      }
    }

    // ─── REGEX PRE-EXTRACTION LAYER REMOVED ───
    //
    // The previous ~830-line `majorChangePatterns` regex block + dispatch UI
    // (addCity, removeCity, replaceCity, reorderStops, extendTrip, changeDestination,
    // startOver) used to fire BEFORE the LLM and kick the user out of chat into
    // a major-change UI. Mobile never had this layer and the LLM (GLM-4.7) handles
    // ALL of these gaps cleanly via the typed action vocabulary defined in
    // packages/shared/utils/chatModificationPrompt.js (regenerate_day, add_day,
    // remove_day, extend_trip, shorten_trip, etc.). Deleting this block unifies
    // web + mobile behavior and removes the chat-kickout UX bug.
    //
    // Trip-structural ops are now routed via applyPendingChanges (line ~26200)
    // which mirrors mobile/src/screens/ItineraryScreen.js:applyAIChanges exactly.
    
    // Continue with normal chat flow for non-major changes
    const newChatMessages = [...itineraryChatMessages, { role: 'user', content: userMessage }];
    setItineraryChatMessages(newChatMessages);
    setItineraryChatInput('');
    setIsModifyingItinerary(true);
    
    // Show loading indicator in chat
    setItineraryChatMessages([...newChatMessages, { role: 'assistant', content: 'loading' }]);
    
    try {
      // Build comprehensive context about current day
      const currentDayData = itinerary?.days?.[currentDay];
      if (!currentDayData) {
        setItineraryChatMessages([...newChatMessages, {
          role: 'assistant',
          content: "I couldn't find the current day data. Please try refreshing the itinerary."
        }]);
        setIsModifyingItinerary(false);
        return;
      }
      const currentDayActivities = (currentDayData.schedule || []).map((a, idx) => {
        const feedbackKey = `day${currentDay}-activity-${idx}`;
        const feedback = activityFeedback[feedbackKey];
        const reason = feedbackReasons[feedbackKey];
        return {
          activity: a.activity,
          time: a.time,
          duration: a.duration,
          location: a.location,
          priceTier: a.priceTier,
          userFeedback: feedback ? (feedback === 'up' ? '👍 liked' : `👎 disliked${reason ? `: "${reason}"` : ''}`) : null
        };
      });
      
      const currentDayMeals = (currentDayData.meals || []).map((m, idx) => {
        const feedbackKey = `day${currentDay}-meal-${idx}`;
        const feedback = mealFeedback[feedbackKey];
        const reason = feedbackReasons[feedbackKey];
        return {
          restaurant: m.restaurant,
          time: m.time,
          cuisine: m.cuisine,
          type: m.type,
          priceRange: m.priceRange,
          userFeedback: feedback ? (feedback === 'up' ? '👍 liked' : `👎 disliked${reason ? `: "${reason}"` : ''}`) : null
        };
      });
      
      // Collect all disliked items with reasons across all days
      const dislikedItems = [];
      const likedItems = [];
      
      Object.entries(activityFeedback).forEach(([key, value]) => {
        const match = key.match(/day(\d+)-activity-(\d+)/);
        if (match) {
          const dayIdx = parseInt(match[1]);
          const actIdx = parseInt(match[2]);
          const activity = itinerary.days[dayIdx]?.schedule[actIdx];
          if (activity) {
            if (value === 'down') {
              const reason = feedbackReasons[key];
              dislikedItems.push(`${activity.activity}${reason ? ` (reason: "${reason}")` : ''}`);
            } else if (value === 'up') {
              likedItems.push(activity.activity);
            }
          }
        }
      });
      
      Object.entries(mealFeedback).forEach(([key, value]) => {
        const match = key.match(/day(\d+)-meal-(\d+)/);
        if (match) {
          const dayIdx = parseInt(match[1]);
          const mealIdx = parseInt(match[2]);
          const meal = itinerary.days[dayIdx]?.meals[mealIdx];
          if (meal) {
            if (value === 'down') {
              const reason = feedbackReasons[key];
              dislikedItems.push(`${meal.restaurant}${reason ? ` (reason: "${reason}")` : ''}`);
            } else if (value === 'up') {
              likedItems.push(meal.restaurant);
            }
          }
        }
      });
      
      // System prompt — built from shared @wander/shared/chatModificationPrompt
      // so mobile and web send identical guardrails. The per-day activity/meal
      // arrays above carry userFeedback annotations that the shared builder
      // preserves via currentDayData.schedule/meals.
      // Build itinerary snapshot the shared builder expects (with userFeedback).
      const chatItinerarySnapshot = {
        ...itinerary,
        days: itinerary.days.map((day, idx) => {
          if (idx !== currentDay) return day;
          return {
            ...day,
            schedule: currentDayActivities.map(a => ({
              time: a.time,
              activity: a.activity,
              location: a.location,
              duration: a.duration,
              userFeedback: a.userFeedback,
            })),
            meals: currentDayMeals.map(m => ({
              time: m.time,
              restaurant: m.restaurant,
              cuisine: m.cuisine,
              type: m.type,
              priceRange: m.priceRange,
              userFeedback: m.userFeedback,
            })),
          };
        }),
      };
      const systemPrompt = buildChatModificationSystemPrompt({
        itinerary: chatItinerarySnapshot,
        activeDayIndex: currentDay,
        currentCity: currentDayData.baseCity,
        likedItems,
        dislikedItems,
        tripType,
        destination: itinerary.destination,
        theme: currentDayData.theme,
        budget: formatCurrency(userProfile.preferences.budget),
        dietaryRestrictions: userProfile.preferences.dietaryRestrictions,
        feedbackContextBlock: buildFeedbackContext(userProfile.preferences),
      });

      // Build messages with full conversation history
      // Filter out loading placeholders, empty messages, and ensure alternating roles
      const filteredMessages = newChatMessages
        .filter(m => m.content && m.content !== 'loading' && m.content.trim() !== '')
        .map(m => ({ role: m.role, content: m.content }));

      // Ensure alternating user/assistant roles (Anthropic API requirement)
      // If two consecutive messages have the same role, merge them
      const messagesForAPI = filteredMessages.reduce((acc, msg) => {
        if (acc.length > 0 && acc[acc.length - 1].role === msg.role) {
          acc[acc.length - 1].content += '\n\n' + msg.content;
        } else {
          acc.push({ ...msg });
        }
        return acc;
      }, []);
      
      // Capture chat-event timing so the analytics emit at the bottom of
      // this try block can record latency_ms.
      const _chatStart = Date.now();
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
        method: "POST",
        headers: await getApiHeaders(),
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 4000,
          system: systemPrompt,
          messages: messagesForAPI
        })
      });
      const _chatLatencyMs = Date.now() - _chatStart;

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 429) {
          throw new Error('rate_limit');
        }
        throw new Error(errorData.error || `API error ${response.status}`);
      }

      const data = await response.json();
      if (!data?.content?.[0]?.text) {
        throw new Error('empty_response');
      }
      let aiMessage = data.content[0].text;

      // Try to parse as JSON action
      let actionExecuted = false;
      try {
        // Clean up potential markdown
        const cleanedMessage = aiMessage.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

        // Try to extract JSON — either full message or embedded in text
        let jsonCandidate = null;
        if (cleanedMessage.startsWith('{') && cleanedMessage.endsWith('}')) {
          // Fast path: entire message is JSON
          jsonCandidate = cleanedMessage;
        } else {
          // Fallback: extract embedded JSON from mixed text+JSON response
          const jsonMatch = cleanedMessage.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const candidate = jsonMatch[0];
            // Validate brace balance before parsing
            let openBraces = 0;
            let closeBraces = 0;
            for (const ch of candidate) {
              if (ch === '{') openBraces++;
              if (ch === '}') closeBraces++;
            }
            if (openBraces === closeBraces && openBraces > 0) {
              jsonCandidate = candidate;
              debugLog('📋 Extracted embedded JSON from mixed response');
            }
          }
        }

        // Check for truncated JSON
        if (cleanedMessage.startsWith('{') && !cleanedMessage.endsWith('}') && !jsonCandidate) {
          debugWarn('⚠️ Truncated JSON detected, treating as conversational');
        }

        if (jsonCandidate) {
          const response = JSON.parse(jsonCandidate);
          
          // Check if it's our new changes format
          if (response.changes && Array.isArray(response.changes)) {
            // Enrich changes with original item data for comparison view
            const enrichedChanges = response.changes.map(change => {
              const dayIndex = change.dayIndex ?? currentDay;
              const day = itinerary.days?.[dayIndex];
              if (!day) return { ...change, original: null };

              let original = null;
              if (change.type === 'update_activity' && change.target) {
                const idx = day.schedule.findIndex(a =>
                  a.activity.toLowerCase().includes(change.target.toLowerCase()) ||
                  change.target.toLowerCase().includes(a.activity.toLowerCase())
                );
                if (idx !== -1) original = { ...day.schedule[idx] };
              } else if (change.type === 'update_meal' && change.target) {
                const idx = day.meals.findIndex(m =>
                  m.restaurant.toLowerCase().includes(change.target.toLowerCase()) ||
                  change.target.toLowerCase().includes(m.restaurant.toLowerCase())
                );
                if (idx !== -1) original = { ...day.meals[idx] };
              } else if (change.type === 'update_hotel') {
                original = day.hotel ? { ...day.hotel } : null;
              } else if (change.type === 'remove_activity' && change.target) {
                const idx = day.schedule.findIndex(a =>
                  a.activity.toLowerCase().includes(change.target.toLowerCase()) ||
                  change.target.toLowerCase().includes(a.activity.toLowerCase())
                );
                if (idx !== -1) original = { ...day.schedule[idx] };
              } else if (change.type === 'remove_meal' && change.target) {
                const idx = day.meals.findIndex(m =>
                  m.restaurant.toLowerCase().includes(change.target.toLowerCase()) ||
                  change.target.toLowerCase().includes(m.restaurant.toLowerCase())
                );
                if (idx !== -1) original = { ...day.meals[idx] };
              }

              return { ...change, original, dayIndex };
            });

            // Store as pending changes and show comparison view.
            // _userMessage is the last user input — the post-accept analytics
            // emission re-uses it as the request_message so the row links
            // back to what the user actually asked for.
            // _perfSnapshot stashes chat-time perf data so the SINGLE
            // analytics row written by applyPendingChanges carries latency,
            // model, and token counts. Avoids double-counting (no chat-time
            // emit on the JSON path).
            setPendingChanges({
              changes: enrichedChanges,
              message: response.message,
              chatMessages: newChatMessages,
              _userMessage: messagesForAPI[messagesForAPI.length - 1]?.content || '',
              _perfSnapshot: {
                responseText: aiMessage,
                modelUsed: data?.model || 'claude-sonnet-4',
                latencyMs: _chatLatencyMs,
                promptTokens: data?.usage?.input_tokens || null,
                completionTokens: data?.usage?.output_tokens || null,
              },
            });
            setPendingChangeStatuses({});

            // Show a preview message in chat
            setItineraryChatMessages([
              ...newChatMessages,
              { role: 'assistant', content: `I have ${enrichedChanges.length} suggested change${enrichedChanges.length > 1 ? 's' : ''} ready. Review them below.` }
            ]);
            actionExecuted = true;
          }
        }
      } catch (parseError) {
        // Not valid JSON - that's fine, treat as conversational
        debugLog('Not JSON response (expected for questions):', parseError.message);
        if (aiMessage.includes('"changes"') || aiMessage.includes('"type"')) {
          debugWarn('⚠️ Response contained "changes"/"type" keywords but JSON parsing failed:', parseError.message);
          debugWarn('⚠️ Raw response preview:', aiMessage.substring(0, 200));
        }
      }
      
      // If no action was executed, show the text response
      if (!actionExecuted) {
        setItineraryChatMessages([
          ...newChatMessages,
          { role: 'assistant', content: aiMessage }
        ]);
        // Analytics: conversational reply emit (no JSON action returned).
        // Symmetric with mobile (ItineraryScreen.handleChatSend) — conversational
        // events fire here at chat-time; JSON events fire at apply-time via
        // applyPendingChanges. This keeps the dashboard's intent-dismiss-rate
        // accurate (one row per chat exchange).
        try {
          const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
          const headers = await getApiHeaders();
          fetch(`${backendUrl}/api/analytics/chat-event`, {
            method: 'POST',
            headers,
            body: JSON.stringify({
              mode: 'in-itinerary',
              platform: 'web',
              itinerary_id: itinerary?._id || itinerary?.id || null,
              request_message: messagesForAPI[messagesForAPI.length - 1]?.content || '',
              response_text: aiMessage,
              response_was_conversational: true,
              response_was_no_op: false,
              classified_intents: ['conversational'],
              applied_change_count: 0,
              itinerary_day_count: itinerary?.days?.length || null,
              active_day_index: currentDay,
              current_city: currentDayData?.baseCity || null,
              model_used: data?.model || 'claude-sonnet-4',
              latency_ms: _chatLatencyMs,
              prompt_tokens: data?.usage?.input_tokens || null,
              completion_tokens: data?.usage?.output_tokens || null,
              had_error: false,
            }),
          }).catch(() => {});
        } catch (_) { /* analytics never break UX */ }
      }

    } catch (error) {
      debugError('Chat error:', error);
      let errorMsg = 'Sorry, I encountered an error. Please try again.';
      if (error.message === 'rate_limit') {
        errorMsg = 'You\'ve been chatting a lot! Please wait a minute before sending another message.';
      } else if (error.message === 'empty_response') {
        errorMsg = 'I got an empty response. Please try rephrasing your request.';
      } else if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        errorMsg = 'Connection issue — please check your internet and try again.';
      }
      setItineraryChatMessages([
        ...newChatMessages,
        { role: 'assistant', content: errorMsg }
      ]);
    } finally {
      setIsModifyingItinerary(false);
    }
  };

  // NEW: Enhanced itinerary rendering with date ranges and chat widget
  const renderItinerary = () => {
    if (!itinerary) {
      // No itinerary data — fall back to landing
      setTimeout(() => setStage('landing'), 0);
      return null;
    }
    // If selectedDestination is missing but we have itinerary data, reconstruct it
    if (!selectedDestination && itinerary.destination) {
      setTimeout(() => setSelectedDestination({ name: itinerary.destination, description: '' }), 0);
      return null; // Will re-render on next tick with selectedDestination set
    }
    if (!selectedDestination) {
      // No destination info at all — fall back to landing
      setTimeout(() => setStage('landing'), 0);
      return null;
    }
    if (!itinerary.days || !Array.isArray(itinerary.days) || itinerary.days.length === 0) {
      return (
        <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
          <div className="text-center p-8">
            <p className="text-gray-600 mb-4">No itinerary days available.</p>
            <button
              onClick={() => setStage('destinations')}
              className="px-4 py-2 bg-[#313143] text-white rounded-lg hover:bg-[#3d3d52] transition-colors"
            >
              Start Over
            </button>
          </div>
        </div>
      );
    }

    // Ensure currentDay is within bounds
    const safeCurrentDay = Math.min(Math.max(0, currentDay), itinerary.days.length - 1);
    const currentDayData = itinerary.days[safeCurrentDay];
    
    // Format dates for display using global helper (avoids timezone issues)
    // Recalculate end date from start + actual day count (in case trip was extended/shortened)
    const startDateDisplay = formatDateForDisplay(userProfile.preferences.startDate);
    const endDateDisplay = (() => {
      if (!userProfile.preferences.startDate || !itinerary?.days?.length) {
        return formatDateForDisplay(userProfile.preferences.endDate);
      }
      const start = parseLocalDate(userProfile.preferences.startDate);
      if (!start) return formatDateForDisplay(userProfile.preferences.endDate);
      const end = new Date(start);
      end.setDate(end.getDate() + itinerary.days.length - 1);
      return end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    })();
    
    return (
      <div className="h-screen flex flex-col overflow-hidden" style={{ background: 'white' }}>
        {/* Header — flex-shrink-0 keeps it pinned at top */}
        <div className="chat-header flex-shrink-0 relative z-40" style={{ borderBottom: '1px solid rgba(31,29,59,0.08)', background: 'white' }}>
          <div className="px-6 py-3 pb-0 w-full">
            {/* Row 1: Title, Trip Info, and Actions */}
            <div className="flex items-center justify-between gap-2 h-14">
              
              {/* Left: Brand & Home */}
              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  {/* Paper-plane icon + inline wordmark + tagline (see comment at first occurrence). */}
                  <img src="/arrivo-icon.png" alt="Arrivo" className="h-10 w-auto object-contain" />
                  <div className="flex flex-col">
                    <h1 className="text-[22px] font-extrabold leading-none" style={{ fontFamily: "'Gulfs Display', 'Batangas', sans-serif", color: '#313143', letterSpacing: '0.05em' }}>
                      arrivo
                    </h1>
                    <span className="text-[10px] font-medium mt-0.5" style={{ fontFamily: "'Batangas', sans-serif", color: '#097066', letterSpacing: '-0.01em' }}>
                      plan less. explore more.
                    </span>
                  </div>
                </div>
              </div>

              {/* Center: Title Flex */}
              <div className="flex-1 flex flex-col items-center justify-center min-w-0">
                <div className="flex items-center gap-2">
                  {isEditingTripName ? (
                    <input
                      ref={tripNameInputRef}
                      type="text"
                      value={editedTripName}
                      onChange={(e) => setEditedTripName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        } else if (e.key === 'Escape') {
                          setIsEditingTripName(false);
                          setEditedTripName('');
                        }
                      }}
                      onBlur={() => {
                        const trimmed = editedTripName.trim();
                        if (trimmed && trimmed !== (itinerary.destination || selectedDestination.name)) {
                          setItinerary(prev => ({ ...prev, destination: trimmed }));
                          setSelectedDestination(prev => ({ ...prev, name: trimmed }));
                        }
                        setIsEditingTripName(false);
                        setEditedTripName('');
                      }}
                      className="text-xl text-gray-800 bg-transparent border-b-2 border-teal-500 outline-none w-full min-w-[120px] max-w-[300px]"
                      style={FONTS.headingBold}
                      autoFocus
                    />
                  ) : (
                    <div
                      className="group flex items-center gap-1.5 cursor-pointer"
                      onClick={() => {
                        const currentName = itinerary.destination || selectedDestination.name;
                        setEditedTripName(currentName);
                        setIsEditingTripName(true);
                      }}
                      title="Click to rename trip"
                    >
                      <h1 className="text-xl text-gray-800" style={FONTS.headingBold}>
                        {formatDestinationWithCountries(
                          // For multi-stop trips, derive display name from stops if itinerary.destination is single-city
                          (selectedDestination?.stops?.length > 1 && itinerary.destination && !itinerary.destination.includes('&'))
                            ? selectedDestination.stops.map(s => s.name).join(' & ')
                            : (itinerary.destination || selectedDestination.name),
                          tripType === 'hotel'
                        )}
                      </h1>
                      <Pencil className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                    </div>
                  )}
                  {/* Trip info subtitle — matches mockup */}
                  <p className="text-xs text-gray-500" style={{ marginTop: 2 }}>
                    {(() => {
                      const parts = [];
                      // Route info for multi-stop
                      if (selectedDestination?.stops?.length > 1) {
                        parts.push(selectedDestination.stops.map(s => s.name.split(',')[0]).join(' → '));
                      }
                      // Date range
                      if (startDateDisplay && endDateDisplay) {
                        parts.push(`${startDateDisplay} – ${endDateDisplay}`);
                      }
                      // Duration — use totalItineraryDays if available (avoids showing partial count during progressive generation)
                      const days = totalItineraryDays || itinerary.days?.length || 0;
                      if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
                      // Travelers removed from header per user request
                      return parts.join(' · ');
                    })()}
                  </p>
                </div>
              </div>

              {/* Right: All action icons flush right */}
              <div className="flex items-center gap-1.5 flex-shrink-0" style={{ zIndex: 60, position: 'relative' }}>
                {/* Flexible mode badge */}
                {flexibleMode && (
                  <span className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-100 text-amber-700 border border-amber-200 flex items-center gap-1 whitespace-nowrap">
                    ✨ Flexible
                  </span>
                )}

                {/* Combined Duration + Dates button */}
                {isEditingDuration ? (
                  <div className="flex items-center gap-1 px-2 py-1 bg-white border-2 border-teal-300 rounded-lg">
                    <CalendarDays className="w-4 h-4 text-teal-500" />
                    <input
                      type="number"
                      min="1"
                      max="30"
                      autoFocus
                      defaultValue={totalItineraryDays > itinerary.days.length ? totalItineraryDays : itinerary.days.length}
                      className="w-12 text-sm text-center bg-transparent outline-none"
                      style={FONTS.body}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.target.blur();
                        } else if (e.key === 'Escape') {
                          e.target.dataset.cancelled = 'true';
                          e.target.blur();
                        }
                      }}
                      onBlur={async (e) => {
                        const wasCancelled = e.target.dataset.cancelled === 'true';
                        setIsEditingDuration(false);
                        if (wasCancelled) return;
                        const currentTotalDays = totalItineraryDays > itinerary.days.length ? totalItineraryDays : itinerary.days.length;
                        const numDays = parseInt(e.target.value) || 0;
                        if (numDays > 0 && numDays <= 30 && numDays !== currentTotalDays) {
                          const shouldRegenerate = await showConfirmModal({ title: 'Change Duration', message: `Change trip from ${currentTotalDays} days to ${numDays} days?\n\nThis will regenerate the itinerary.`, confirmText: 'Regenerate' });
                          if (shouldRegenerate) {
                            setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, duration: numDays } }));
                            if (userProfile.preferences.startDate) {
                              const start = new Date(userProfile.preferences.startDate);
                              const end = new Date(start);
                              end.setDate(start.getDate() + numDays - 1);
                              setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, endDate: end.toISOString().split('T')[0] } }));
                            }
                            handleDestinationSelect(selectedDestination, 0);
                          }
                        }
                      }}
                    />
                    <span className="text-sm text-gray-500">days</span>
                  </div>
                ) : (
                <button
                  onClick={() => {
                    setDatePickerValues({
                      startDate: userProfile.preferences.startDate || '',
                      endDate: userProfile.preferences.endDate || ''
                    });
                    setShowDatePicker(true);
                  }}
                  className="w-10 h-10 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg transition-colors flex items-center justify-center border border-teal-200 hover:border-teal-300 relative"
                  title={`${totalItineraryDays > itinerary.days.length ? totalItineraryDays : itinerary.days.length} days${startDateDisplay ? ` · ${startDateDisplay} → ${endDateDisplay}` : userProfile.preferences.travelMonths?.length ? ` · ${userProfile.preferences.travelMonths[0]}` : ''} — Click to change`}
                >
                  <CalendarDays className="w-5 h-5" />
                  <span className="absolute -top-1.5 -right-1.5 bg-teal-600 text-white text-[9px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {totalItineraryDays > itinerary.days.length ? totalItineraryDays : itinerary.days.length}
                  </span>
                </button>
                )}

                {/* Old separate date/month/add-dates buttons removed — merged into combined button above */}

                {/* Route Chain: shows all stops for multi-city, or arrival→departure for single */}
                {(() => {
                  const stopNames = selectedDestination?.stops?.length > 1
                    ? selectedDestination.stops.map(s => s.name)
                    : selectedDestination?.stopNames?.length > 1
                      ? selectedDestination.stopNames
                      : null;

                  // Multi-stop: compact route pill with expandable dropdown
                  if (stopNames && stopNames.length > 1) {
                    const arrCode = itinerary.arrivalAirport?.code;
                    const depCode = itinerary.departureAirport?.code;
                    const routeSummary = arrCode && depCode
                      ? `${arrCode} → ${stopNames.map(s => s.split(',')[0]).join(' → ')} → ${depCode}`
                      : stopNames.map(s => s.split(',')[0]).join(' → ');

                    return (
                      <div className="relative">
                        {/* Transport module button — opens per-leg transport modal directly */}
                        <button
                          onClick={() => setShowFlightModal(true)}
                          className="w-10 h-10 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg transition-colors flex items-center justify-center border border-teal-200 hover:border-teal-300"
                          title="View & edit transportation"
                        >
                          <Plane className="w-5 h-5" />
                        </button>

                        {/* Route overview dropdown (hidden — replaced by transport module modal) */}
                        {false && showRouteOverview && (
                          <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-200 p-4 z-50 min-w-[320px]">
                            <div className="flex items-center justify-between mb-3">
                              <h3 className="text-sm font-semibold text-gray-800" style={FONTS.headingBold}>Route Overview</h3>
                              <button onClick={() => setShowRouteOverview(false)} className="text-gray-400 hover:text-gray-600">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="space-y-0">
                              {/* Arrival */}
                              <button
                                onClick={() => { setFlightModalType('arrival'); setShowFlightModal(true); setShowRouteOverview(false); }}
                                className="w-full flex items-center gap-3 py-2 px-2 hover:bg-blue-50 rounded-lg transition-colors"
                              >
                                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                  <Plane className="w-4 h-4 text-blue-600" />
                                </div>
                                <div className="text-left">
                                  <div className="text-sm font-medium text-gray-800">{arrCode ? `Arrive at ${arrCode}` : 'Arrival'}</div>
                                  <div className="text-xs text-gray-500">{arrivalFlight.time} • Click to edit</div>
                                </div>
                              </button>

                              {/* Each stop with inter-city transport between them */}
                              {stopNames.map((stop, i) => {
                                const transport = itinerary.interCityTransport?.[i];
                                const modeEmoji = transport?.mode === 'flight' ? '✈️' : transport?.mode === 'train' ? '🚆' : transport?.mode === 'ferry' ? '⛴️' : transport?.mode === 'bus' ? '🚌' : '🚗';
                                const stopDays = selectedDestination?.stops?.[i]?.days;
                                // Get airport codes for this segment's flight pricing
                                // transportInfo may be keyed by stop name OR resolved city name
                                const findAirportCode = (stopName) => {
                                  if (!stopName) return null;
                                  // Direct match
                                  const direct = itinerary.transportInfo?.[stopName]?.nearestAirport?.code;
                                  if (direct) return direct;
                                  // Try resolved location from selectedDestination.stops
                                  const stopObj = selectedDestination?.stops?.find(s => s.name === stopName);
                                  if (stopObj?.resolvedLocation) {
                                    const resolved = itinerary.transportInfo?.[stopObj.resolvedLocation]?.nearestAirport?.code;
                                    if (resolved) return resolved;
                                  }
                                  // Try matching by partial key (e.g., "Tokyo" matches "Tokyo, Japan")
                                  if (itinerary.transportInfo) {
                                    for (const [key, val] of Object.entries(itinerary.transportInfo)) {
                                      if (key.toLowerCase().includes(stopName.toLowerCase().split(',')[0]) ||
                                          stopName.toLowerCase().includes(key.toLowerCase().split(',')[0])) {
                                        if (val?.nearestAirport?.code) return val.nearestAirport.code;
                                      }
                                    }
                                  }
                                  return null;
                                };
                                const prevStop = i === 0 ? null : stopNames[i - 1];
                                const fromAirport = i === 0
                                  ? itinerary.arrivalAirport?.code
                                  : findAirportCode(prevStop);
                                const toAirport = findAirportCode(stop);
                                const segmentRouteKey = fromAirport && toAirport ? `${fromAirport}-${toAirport}-ECONOMY` : null;
                                const segmentPrice = segmentRouteKey ? flightPrices[segmentRouteKey] : null;

                                // Auto-fetch flight price for this segment when dropdown opens
                                if (i > 0 && transport?.mode === 'flight' && fromAirport && toAirport && !segmentPrice) {
                                  setTimeout(() => fetchFlightPrice(fromAirport, toAirport, userProfile?.preferences?.startDate), i * 500);
                                }

                                return (
                                  <div key={`route-stop-${i}`}>
                                    {/* Transport connector with flight price */}
                                    {i > 0 && (
                                      <div className="flex items-center gap-3 py-1.5 px-2">
                                        <div className="w-8 flex justify-center">
                                          <div className="w-px h-6 bg-gray-200 relative">
                                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"><ActivityIcon emoji={modeEmoji} size={10} /></div>
                                          </div>
                                        </div>
                                        <div className="flex-1 flex items-center justify-between">
                                          <span className="text-xs text-gray-400">
                                            {fromAirport && toAirport ? `${fromAirport} → ${toAirport}` : ''} {transport?.duration || ''}
                                          </span>
                                          {segmentPrice?.loading && (
                                            <div className="w-3 h-3 border border-teal-400 border-t-transparent rounded-full animate-spin" />
                                          )}
                                          {segmentPrice?.data?.cheapest && (
                                            <span className="text-xs font-semibold text-teal-600">
                                              from ${segmentPrice.data.cheapest.price}
                                            </span>
                                          )}
                                          {segmentPrice?.data && !segmentPrice.data.cheapest && (
                                            <span className="text-[10px] text-gray-400">No flights</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {/* Stop */}
                                    <div className="flex items-center gap-3 py-2 px-2 rounded-lg">
                                      <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center">
                                        <span className="text-sm font-bold text-teal-700">{i + 1}</span>
                                      </div>
                                      <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-800">{stop}</div>
                                        <div className="flex items-center gap-2">
                                          {stopDays && <span className="text-xs text-gray-500">{stopDays} {stopDays === 1 ? 'day' : 'days'}</span>}
                                          {toAirport && <span className="text-[10px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded">{toAirport}</span>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}

                              {/* Departure with flight price */}
                              {(() => {
                                const lastStop = stopNames[stopNames.length - 1];
                                // Try direct key, then resolved location, then partial match
                                const lastAirport = (() => {
                                  const direct = itinerary.transportInfo?.[lastStop]?.nearestAirport?.code;
                                  if (direct) return direct;
                                  const stopObj = selectedDestination?.stops?.find(s => s.name === lastStop);
                                  if (stopObj?.resolvedLocation) {
                                    const r = itinerary.transportInfo?.[stopObj.resolvedLocation]?.nearestAirport?.code;
                                    if (r) return r;
                                  }
                                  if (itinerary.transportInfo) {
                                    for (const [key, val] of Object.entries(itinerary.transportInfo)) {
                                      if (key.toLowerCase().includes(lastStop.toLowerCase().split(',')[0]) ||
                                          lastStop.toLowerCase().includes(key.toLowerCase().split(',')[0])) {
                                        if (val?.nearestAirport?.code) return val.nearestAirport.code;
                                      }
                                    }
                                  }
                                  // Fallback: use arrival airport if there's only one city, NOT departureAirport (which is the HOME airport)
                                  return itinerary.arrivalAirport?.code;
                                })();
                                const depRouteKey = lastAirport && depCode ? `${lastAirport}-${depCode}-ECONOMY` : null;
                                const depPrice = depRouteKey ? flightPrices[depRouteKey] : null;
                                // Auto-fetch departure flight price
                                if (lastAirport && depCode && !depPrice) {
                                  setTimeout(() => fetchFlightPrice(lastAirport, depCode, userProfile?.preferences?.endDate || userProfile?.preferences?.startDate), stopNames.length * 500);
                                }
                                return (
                                  <>
                                    <div className="flex items-center gap-3 py-1.5 px-2">
                                      <div className="w-8 flex justify-center">
                                        <div className="w-px h-6 bg-gray-200 relative">
                                          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"><Plane className="w-2.5 h-2.5 text-gray-400" /></div>
                                        </div>
                                      </div>
                                      <div className="flex-1 flex items-center justify-between">
                                        <span className="text-xs text-gray-400">{lastAirport || ''} → {depCode || ''}</span>
                                        {depPrice?.loading && <div className="w-3 h-3 border border-teal-400 border-t-transparent rounded-full animate-spin" />}
                                        {depPrice?.data?.cheapest && (
                                          <span className="text-xs font-semibold text-teal-600">from ${depPrice.data.cheapest.price}</span>
                                        )}
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => { setFlightModalType('departure'); setShowFlightModal(true); setShowRouteOverview(false); }}
                                      className="w-full flex items-center gap-3 py-2 px-2 hover:bg-blue-50 rounded-lg transition-colors"
                                    >
                                      <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                        <Plane className="w-4 h-4 text-blue-600 rotate-45" />
                                      </div>
                                      <div className="text-left">
                                        <div className="text-sm font-medium text-gray-800">{depCode ? `Depart from ${depCode}` : 'Departure'}</div>
                                        <div className="text-xs text-gray-500">{departureFlight.time} • Click to edit</div>
                                      </div>
                                    </button>
                                  </>
                                );
                              })()}
                            </div>

                            {/* Total estimated flight cost */}
                            {/* Edit Transport button — opens per-leg transport module */}
                            <button
                              onClick={() => { setShowFlightModal(true); setShowRouteOverview(false); }}
                              className="w-full mt-3 py-2 px-3 bg-teal-50 hover:bg-teal-100 text-teal-700 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 border border-teal-200"
                            >
                              <Edit2 className="w-4 h-4" />
                              Edit Transport Modes
                            </button>

                            {(() => {
                              let totalCost = 0;
                              let segCount = 0;
                              let loadingCount = 0;
                              // Sum all segment prices
                              stopNames.forEach((stop, i) => {
                                if (i === 0) return;
                                const prevStop = stopNames[i - 1];
                                const fromA = itinerary.transportInfo?.[prevStop]?.nearestAirport?.code;
                                const toA = itinerary.transportInfo?.[stop]?.nearestAirport?.code;
                                const key = fromA && toA ? `${fromA}-${toA}-ECONOMY` : null;
                                const p = key ? flightPrices[key] : null;
                                if (p?.loading) loadingCount++;
                                if (p?.data?.cheapest?.price) { totalCost += p.data.cheapest.price; segCount++; }
                              });
                              // Add departure segment
                              const lastStop = stopNames[stopNames.length - 1];
                              const lastA = itinerary.transportInfo?.[lastStop]?.nearestAirport?.code;
                              const depKey = lastA && depCode ? `${lastA}-${depCode}-ECONOMY` : null;
                              const depP = depKey ? flightPrices[depKey] : null;
                              if (depP?.loading) loadingCount++;
                              if (depP?.data?.cheapest?.price) { totalCost += depP.data.cheapest.price; segCount++; }

                              if (segCount === 0 && loadingCount === 0) return null;
                              return (
                                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
                                  <span className="text-xs text-gray-500">Est. flights total</span>
                                  {loadingCount > 0 ? (
                                    <div className="flex items-center gap-1.5">
                                      <div className="w-3 h-3 border border-teal-400 border-t-transparent rounded-full animate-spin" />
                                      <span className="text-xs text-gray-400">Loading prices...</span>
                                    </div>
                                  ) : (
                                    <span className="text-sm font-bold text-teal-600">~${totalCost.toLocaleString()}</span>
                                  )}
                                </div>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // Single destination: transportation module icon button
                  return (
                    <button
                      onClick={() => { setFlightModalType('arrival'); setShowFlightModal(true); }}
                      className="w-10 h-10 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg transition-colors flex items-center justify-center border border-teal-200 hover:border-teal-300"
                      title="View & edit transportation details"
                    >
                      {transportationMode === 'fly' ? <Plane className="w-5 h-5" /> : transportationMode === 'train' ? <Train className="w-5 h-5" /> : transportationMode === 'ferry' ? <Ship className="w-5 h-5" /> : transportationMode === 'custom' ? <Clock className="w-5 h-5" /> : <Car className="w-5 h-5" />}
                    </button>
                  );
                })()}

                {/* Budget module button — admin only */}
                {tier === 'super-admin' && itinerary?.days?.length > 0 && (
                  <button
                    onClick={() => setShowBudgetTracker(prev => !prev)}
                    className="w-10 h-10 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg transition-colors flex items-center justify-center border border-teal-200 hover:border-teal-300"
                    title="Budget tracker"
                  >
                    <DollarSign className="w-5 h-5" />
                  </button>
                )}

                {/* Bookmarks shortcut — jump to community bookmarks */}
                {itinerary?.days?.length > 0 && (
                  <button
                    onClick={() => {
                      setLandingTab('community');
                      setCommunitySubTab('bookmarks');
                      setStage('chat');
                    }}
                    className="w-10 h-10 bg-teal-50 hover:bg-teal-100 text-teal-600 rounded-lg transition-colors flex items-center justify-center border border-teal-200 hover:border-teal-300"
                    title="View bookmarks"
                  >
                    <Bookmark className="w-5 h-5" />
                  </button>
                )}

                {/* Share button — hidden when CollaboratorAvatars shows its own */}
                {!(features.COLLABORATIVE_TRIPS && currentTripId) && (
                <button
                  onClick={async () => {
                    if (features.COLLABORATIVE_TRIPS && currentItineraryId) {
                      // Trip is saved but currentTripId not set yet — set it and open collab modal
                      setCurrentTripId(currentItineraryId);
                      setShowShareModal(true);
                    } else if (!features.COLLABORATIVE_TRIPS && currentItineraryId) {
                      setExportType('share'); setShowExportModal(true);
                    } else {
                      // Offer to save first, then share
                      const shouldSave = await showConfirmModal({
                        title: 'Save & Share',
                        message: 'Your itinerary needs to be saved before sharing. Save it now?',
                        confirmText: 'Save & Share',
                        type: 'confirm'
                      });
                      if (shouldSave) {
                        try {
                          const saved = await handleSaveItinerary();
                          if (!saved) {
                            showAlertModal('Save Failed', 'Could not save itinerary. You may need to sign in first.');
                            return;
                          }
                          // After save, handleSaveItinerary sets currentTripId via setCurrentTripId
                          // Use a short timeout to let React state update propagate
                          setTimeout(() => {
                            if (features.COLLABORATIVE_TRIPS) {
                              // currentTripId is now set by handleSaveItinerary
                              setShowShareModal(true);
                            } else {
                              setExportType('share');
                              setShowExportModal(true);
                            }
                          }, 500);
                        } catch (err) {
                          showAlertModal('Save Failed', 'Could not save itinerary. Please try again.');
                        }
                      }
                    }
                  }}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-gray-100 border border-gray-200"
                  style={{ color: '#64748b' }}
                  title="Share itinerary"
                >
                  <Share2 className="w-[16px] h-[16px]" />
                </button>
                )}

                {/* Collaborator Avatars */}
                {features.COLLABORATIVE_TRIPS && currentTripId && (
                  <CollaboratorAvatars
                    tripId={currentTripId}
                    onShareClick={() => setShowShareModal(true)}
                    onActivityLogClick={() => setShowActivityLog(true)}
                  />
                )}

                {/* Home button */}
                <button
                  onClick={async () => {
                    const confirmLeave = await showConfirmModal({ title: 'Return Home', message: 'Return to home? Your current itinerary will be cleared unless saved.', confirmText: 'Yes, Go Home', type: 'confirm' });
                    if (confirmLeave) {
                      resetAllTripState();
                    }
                  }}
                  className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-gray-100 border border-gray-200"
                  style={{ color: '#64748b' }}
                  title="Return to Home"
                >
                  <Home className="w-[16px] h-[16px]" />
                </button>

                {/* Overflow Menu — contains Regenerate, Export, Transport, etc. */}
                <div className="relative">
                  <button
                    onClick={() => setShowHeaderMenu(!showHeaderMenu)}
                    className="w-9 h-9 rounded-xl flex items-center justify-center transition-all hover:bg-gray-100 border border-gray-200"
                    style={{ color: '#64748b' }}
                    title="More options"
                  >
                    <MoreVertical className="w-[16px] h-[16px]" />
                  </button>

                {/* Validation Issues — subtle dot indicator on menu button */}
                {validationIssues.length > 0 && (
                  <span className={`absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white ${
                    validationIssues.some(i => i.severity === 'error') ? 'bg-red-500'
                    : validationIssues.some(i => i.severity === 'warning') ? 'bg-amber-500'
                    : 'bg-teal-500'
                  }`} />
                )}

                {/* Dropdown Menu */}
                {showHeaderMenu && (
                  <>
                    <div className="fixed inset-0 z-[9998]" onClick={() => setShowHeaderMenu(false)} />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-1.5 z-[9999]">
                      {/* Regenerate */}
                      <button
                        data-regenerate-button
                  onClick={async () => {
                    // Collect user feedback and changes with reasons
                    const dislikedItems = [];
                    const likedItems = [];
                    
                    // Collect activity feedback
                    Object.keys(activityFeedback).forEach(key => {
                      const [dayStr, type, idxStr] = key.split('-');
                      const dayIdx = parseInt(dayStr.replace('day', ''));
                      const itemIdx = parseInt(idxStr);
                      
                      if (activityFeedback[key] === 'down') {
                        if (type === 'activity' && itinerary.days[dayIdx]?.schedule[itemIdx]) {
                          const activity = itinerary.days[dayIdx].schedule[itemIdx].activity;
                          const reason = feedbackReasons[key] || 'No reason provided';
                          dislikedItems.push(`${activity} (${reason})`);
                        }
                      } else if (activityFeedback[key] === 'up') {
                        if (type === 'activity' && itinerary.days[dayIdx]?.schedule[itemIdx]) {
                          likedItems.push(itinerary.days[dayIdx].schedule[itemIdx].activity);
                        }
                      }
                    });
                    
                    // Collect meal feedback
                    Object.keys(mealFeedback).forEach(key => {
                      const [dayStr, type, idxStr] = key.split('-');
                      const dayIdx = parseInt(dayStr.replace('day', ''));
                      const itemIdx = parseInt(idxStr);
                      
                      if (mealFeedback[key] === 'down') {
                        if (type === 'meal' && itinerary.days[dayIdx]?.meals[itemIdx]) {
                          const restaurant = itinerary.days[dayIdx].meals[itemIdx].restaurant;
                          const reason = feedbackReasons[key] || 'No reason provided';
                          dislikedItems.push(`${restaurant} (${reason})`);
                        }
                      } else if (mealFeedback[key] === 'up') {
                        if (type === 'meal' && itinerary.days[dayIdx]?.meals[itemIdx]) {
                          likedItems.push(itinerary.days[dayIdx].meals[itemIdx].restaurant);
                        }
                      }
                    });
                    
                    // Also allow regeneration if flights or hotels were changed
                    // OR if any days failed to generate (user needs to be able to retry)
                    const hasFailedDays = itinerary?.days?.some(d => d?._generationFailed || !d);
                    if (dislikedItems.length === 0 && likedItems.length === 0 && userEdits.length === 0 && !flightDetailsChanged && !hotelDetailsChanged && !hasFailedDays) {
                      showAlertModal('No Changes Detected', 'Make some changes first! Use 👍/👎 buttons, edit times, or update flight/hotel details, then regenerate.');
                      return;
                    }
                    
                    // Reset the change flags
                    setFlightDetailsChanged(false);
                    setHotelDetailsChanged(false);
                    
                    // PROGRESSIVE REGENERATION - show days updating in-place (no loading screen)
                    // Use the same dayGenerationStatus system as initial generation
                    const totalDays = itinerary.days.length;

                    // Set all days to 'generating' status initially (queued)
                    const regenStatus = {};
                    for (let i = 1; i <= totalDays; i++) {
                      regenStatus[i] = 'queued';
                    }
                    setDayGenerationStatus(regenStatus);
                    setDayGenerationErrors({});
                    // Clear transit caches for fresh recalculation
                    setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null;

                    // Navigate to Day 1 so user sees progress
                    setCurrentDay(0);

                    // Reset feedback state immediately
                    setActivityFeedback({});
                    setMealFeedback({});
                    setFeedbackReasons({});
                    setFeedbackCount({ thumbsDown: 0, thumbsUp: 0 });
                    setShowFeedbackBanner(false);
                    setUserEdits([]);

                    // Run regeneration async (non-blocking)
                    (async () => {
                      try {
                        const currentHotel = itinerary.days[0]?.hotel;
                        const hotelNeighborhood = currentHotel?.neighborhood || currentHotel?.address?.split(',')[1]?.trim() || 'hotel area';
                        const newDays = [];

                        for (let dayIdx = 0; dayIdx < totalDays; dayIdx++) {
                          const dayNum = dayIdx + 1;
                          setDayGenerationStatus(prev => ({ ...prev, [dayNum]: 'generating' }));
                          // Auto-navigate to the day being generated
                          setCurrentDay(dayIdx);

                          try {
                            // Get feedback specific to this day
                            const dayDislikedItems = dislikedItems.filter(item => {
                              const daySchedule = itinerary.days[dayIdx]?.schedule || [];
                              const dayMeals = itinerary.days[dayIdx]?.meals || [];
                              return daySchedule.some(a => item.includes(a.activity)) ||
                                     dayMeals.some(m => item.includes(m.restaurant));
                            });

                            const dayLikedItems = likedItems.filter(item => {
                              const daySchedule = itinerary.days[dayIdx]?.schedule || [];
                              const dayMeals = itinerary.days[dayIdx]?.meals || [];
                              return daySchedule.some(a => item.includes(a)) ||
                                     dayMeals.some(m => item.includes(m));
                            });

                            const origDayData = itinerary.days[dayIdx];

                            const systemPrompt = `You are regenerating Day ${dayNum} of a ${totalDays}-day trip to ${itinerary.destination}.

CURRENT DAY ${dayNum} DATA (USE AS TEMPLATE):
${JSON.stringify(origDayData, null, 2)}

HOTEL LOCATION:
- Hotel: ${currentHotel?.name || 'Not specified'}
- Address: ${currentHotel?.address || 'Not specified'}
- Neighborhood: ${hotelNeighborhood}
ALL activities must be within 30-60 min of the hotel (day trips 1-2 hrs OK for special attractions).

${dayDislikedItems.length > 0 ? `
DISLIKED ON THIS DAY (REPLACE):
${dayDislikedItems.map((item, i) => `${i + 1}. ${item}`).join('\n')}
Replace these with alternatives that address the concerns.
` : ''}

${dayLikedItems.length > 0 ? `
LIKED ON THIS DAY (KEEP SIMILAR):
${dayLikedItems.map((item, i) => `${i + 1}. ${item}`).join('\n')}
` : ''}

${dislikedItems.length > 0 && dayDislikedItems.length === 0 ? `
GENERAL USER PREFERENCES (from other days):
Disliked: ${dislikedItems.slice(0, 3).join(', ')}
Avoid similar items on this day too.
` : ''}

Return ONLY a JSON object for Day ${dayNum}:
{
  "day": ${dayNum},
  "theme": "...",
  "baseCity": "${origDayData?.baseCity || itinerary.destination}",
  "hotel": ${JSON.stringify(origDayData?.hotel || {})},
  "schedule": [...4-6 activities...],
  "meals": [...3 meals (breakfast, lunch, dinner)...]
}`;

                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                              method: "POST",
                              headers: await getApiHeaders(),
                              body: JSON.stringify({
                                model: "claude-sonnet-4-20250514",
                                max_tokens: 4000,
                                system: systemPrompt,
                                messages: [{
                                  role: "user",
                                  content: `Regenerate Day ${dayNum}. Return ONLY the JSON object.`
                                }]
                              })
                            });

                            const data = await response.json();
                            if (!data?.content?.[0]?.text) {
                              throw new Error(`Failed to regenerate Day ${dayNum}`);
                            }

                            let dayText = data.content[0].text;
                            dayText = dayText.replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();

                            const jsonMatch = dayText.match(/\{[\s\S]*\}/);
                            if (jsonMatch) {
                              dayText = jsonMatch[0];
                            }

                            const newDayData = JSON.parse(dayText);
                            newDayData.day = dayNum;

                            // Preserve the existing hotel data
                            const existingHotel = itinerary.days[dayIdx]?.hotel;
                            if (existingHotel) {
                              newDayData.hotel = {
                                ...existingHotel,
                                ...(newDayData.hotel || {}),
                                name: existingHotel.name,
                                address: existingHotel.address,
                                placeId: existingHotel.placeId,
                                googleValidated: existingHotel.googleValidated
                              };
                            }

                            newDays.push(newDayData);

                            // Update itinerary progressively
                            setItinerary(prev => ({
                              ...prev,
                              days: [...newDays, ...(prev?.days || []).slice(newDays.length)]
                            }));

                            setDayGenerationStatus(prev => ({ ...prev, [dayNum]: 'complete' }));
                          } catch (dayError) {
                            debugError(`❌ Regeneration of Day ${dayNum} failed:`, dayError);
                            // Keep original day data for failed days
                            newDays.push(itinerary.days[dayIdx]);
                            setDayGenerationStatus(prev => ({ ...prev, [dayNum]: 'failed' }));
                            setDayGenerationErrors(prev => ({ ...prev, [dayNum]: dayError.message }));
                          }
                        }

                        // Final update with all regenerated days
                        setItinerary(prev => ({
                          ...prev,
                          days: newDays
                        }));

                        // Clear all generation statuses (show as complete)
                        setDayGenerationStatus({});
                      } catch (error) {
                        debugError('Regeneration error:', error);
                        setDayGenerationStatus({});
                        showAlertModal('Regeneration Failed', 'Failed to regenerate this day. Please try again.');
                      }
                    })();
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-teal-700 hover:bg-teal-50 flex items-center gap-2 font-semibold"
                  style={FONTS.body}
                >
                  <RefreshCw className="w-4 h-4" />
                  Regenerate Itinerary
                </button>

                      <div className="border-t border-gray-100 my-1" />

                      {/* Save */}
                      {user && (
                        <button
                          onClick={() => { handleSaveItinerary(); setShowHeaderMenu(false); }}
                          disabled={isSavingItinerary}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                            saveButtonText === 'Saved!' || saveButtonText === 'Updated!' || (lastSavedAt && !hasUnsavedChanges)
                              ? 'text-teal-600'
                              : hasUnsavedChanges
                              ? 'text-amber-600 font-semibold'
                              : 'text-gray-700'
                          } hover:bg-gray-100`}
                          style={FONTS.body}
                        >
                          <Save className="w-4 h-4" />
                          {isSavingItinerary ? 'Saving...' : saveButtonText || (hasUnsavedChanges ? 'Save Changes' : lastSavedAt ? 'Saved' : 'Save Itinerary')}
                        </button>
                      )}

                      {/* Validation Issues */}
                      {validationIssues.length > 0 && (
                        <button
                          onClick={() => { setShowValidationPanel(!showValidationPanel); setShowHeaderMenu(false); }}
                          className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${
                            validationIssues.some(i => i.severity === 'error') ? 'text-red-600' : 'text-amber-600'
                          } hover:bg-gray-100`}
                          style={FONTS.body}
                        >
                          <AlertCircle className="w-4 h-4" />
                          {validationIssues.length} Alert{validationIssues.length > 1 ? 's' : ''}
                        </button>
                      )}

                      <div className="border-t border-gray-100 my-1" />

                      {/* Export PDF */}
                      <button
                        onClick={() => { setShowPdfExportModal(true); setShowHeaderMenu(false); }}
                        disabled={!itinerary?.days?.some(d => d && !d._generationFailed) || isProgressiveGenerating}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:text-gray-400 disabled:cursor-not-allowed"
                        style={FONTS.body}
                      >
                        <Download className="w-4 h-4" />
                        Export PDF
                      </button>

                      {/* Export Calendar */}
                      <button
                        onClick={() => { setExportType('calendar'); setShowExportModal(true); setShowHeaderMenu(false); }}
                        disabled={!itinerary?.days?.some(d => d && !d._generationFailed) || isProgressiveGenerating}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:text-gray-400 disabled:cursor-not-allowed"
                        style={FONTS.body}
                      >
                        <CalendarDays className="w-4 h-4" />
                        Export to Calendar
                      </button>

                      {/* Packing List */}
                      <button
                        onClick={async () => {
                          setShowHeaderMenu(false);
                          if (packingList) { setShowPackingList(true); }
                          else { setIsGeneratingPackingList(true); setShowPackingList(true); await generatePackingList(selectedDestination, itinerary); setIsGeneratingPackingList(false); }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        style={FONTS.body}
                      >
                        <Package className="w-4 h-4" />
                        Packing List
                      </button>

                      <div className="border-t border-gray-100 my-1" />

                      {/* Report Bug */}
                      <button
                        onClick={() => {
                          setShowHeaderMenu(false);
                          const subject = encodeURIComponent(`Bug Report: ${itinerary.destination || selectedDestination?.name || 'Trip'}`);
                          const body = encodeURIComponent(`Describe the bug:\n\n\nSteps to reproduce:\n\n\nExpected behavior:\n\n\n---\nTrip: ${itinerary.destination || ''}\nDays: ${itinerary.days?.length || 0}\nGenerated: ${new Date().toISOString()}`);
                          window.open(`mailto:bugs@arrivotravel.ai?subject=${subject}&body=${body}`, '_blank');
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                        style={FONTS.body}
                      >
                        <AlertCircle className="w-4 h-4" />
                        Report a Bug
                      </button>

                    </div>
                  </>
                )}

                  {/* OLD Dropdown Menu — DISABLED */}
                  {false && showHeaderMenu && (
                    <>
                      {/* OLD DROPDOWN — DISABLED, replaced by new dropdown above */}
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowHeaderMenu(false)}
                      />
                      <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                        {/* Save */}
                        {user && (
                          <button
                            onClick={() => { handleSaveItinerary(); setShowHeaderMenu(false); }}
                            disabled={isSavingItinerary || saveButtonText}
                            className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 disabled:opacity-50 transition-colors ${
                              saveButtonText === 'Saved!' || saveButtonText === 'Updated!'
                                ? 'text-teal-600 bg-teal-50'
                                : saveButtonText === 'Error!'
                                  ? 'text-red-600 bg-red-50'
                                  : 'text-gray-700 hover:bg-gray-100'
                            }`}
                            style={FONTS.body}
                          >
                            {saveButtonText === 'Saved!' || saveButtonText === 'Updated!' ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            ) : saveButtonText === 'Error!' ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            ) : (
                              <Save className="w-4 h-4" />
                            )}
                            {isSavingItinerary ? 'Saving...' : saveButtonText || (hasUnsavedChanges ? '● Save Changes' : lastSavedAt ? '✓ Saved' : 'Save Itinerary')}
                          </button>
                        )}

                        {/* Export PDF */}
                        {(() => {
                          // BUG 2 FIX: Disable export while progressive generation is still running
                          const hasCompleteDays = itinerary?.days?.some(d => d && !d._generationFailed);
                          const exportDisabled = !hasCompleteDays || isProgressiveGenerating;
                          return (
                            <button
                              onClick={() => { setShowPdfExportModal(true); setShowHeaderMenu(false); }}
                              disabled={exportDisabled}
                              className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${exportDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                              style={FONTS.body}
                            >
                              <Download className="w-4 h-4" />
                              {isProgressiveGenerating ? 'Export PDF (generating...)' : 'Export PDF'}
                            </button>
                          );
                        })()}

                        {/* Export Calendar */}
                        {(() => {
                          // BUG 2 FIX: Disable export while progressive generation is still running
                          const hasCompleteDays = itinerary?.days?.some(d => d && !d._generationFailed);
                          const exportDisabled = !hasCompleteDays || isProgressiveGenerating;
                          return (
                            <button
                              onClick={() => { setExportType('calendar'); setShowExportModal(true); setShowHeaderMenu(false); }}
                              disabled={exportDisabled}
                              className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${exportDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                              style={FONTS.body}
                            >
                              <CalendarDays className="w-4 h-4" />
                              {isProgressiveGenerating ? 'Export Calendar (generating...)' : 'Export to Calendar'}
                            </button>
                          );
                        })()}

                        {/* Export Budget CSV */}
                        {(() => {
                          const hasCompleteDays = itinerary?.days?.some(d => d && !d._generationFailed);
                          const csvDisabled = !hasCompleteDays || isProgressiveGenerating;
                          return (
                            <button
                              onClick={() => { setExportType('csv'); setShowExportModal(true); setShowHeaderMenu(false); }}
                              disabled={csvDisabled}
                              className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${csvDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                              style={FONTS.body}
                            >
                              <Download className="w-4 h-4" />
                              {isProgressiveGenerating ? 'Export Budget (generating...)' : 'Export Budget CSV'}
                            </button>
                          );
                        })()}

                        {/* Share Link */}
                        {(() => {
                          const hasCompleteDays = itinerary?.days?.some(d => d && !d._generationFailed);
                          const shareDisabled = !hasCompleteDays || isProgressiveGenerating || !currentItineraryId;
                          const hasCommunity = features.COMMUNITY_SHARING;
                          return hasCommunity ? (
                            <button
                              onClick={() => { setExportType('share'); setShowExportModal(true); setShowHeaderMenu(false); }}
                              disabled={shareDisabled}
                              className={`w-full px-4 py-2 text-left text-sm flex items-center gap-2 ${shareDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 hover:bg-gray-100'}`}
                              style={FONTS.body}
                            >
                              <Share2 className="w-4 h-4" />
                              {!currentItineraryId ? 'Share Link (save first)' : 'Share Link'}
                            </button>
                          ) : null;
                        })()}

                        {/* Packing List */}
                        <button
                          onClick={async () => {
                            setShowHeaderMenu(false);
                            if (packingList) {
                              setShowPackingList(true);
                            } else {
                              setIsGeneratingPackingList(true);
                              setShowPackingList(true);
                              await generatePackingList(selectedDestination, itinerary);
                              setIsGeneratingPackingList(false);
                            }
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                          style={FONTS.body}
                        >
                          <Package className="w-4 h-4" />
                          Packing List
                        </button>

                        {/* Log This Trip (community feature) */}
                        {features.COMMUNITY_REVIEWS && (
                          <button
                            onClick={() => {
                              setShowHeaderMenu(false);
                              setShowTripLogger(true);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            style={FONTS.body}
                          >
                            <Activity className="w-4 h-4" />
                            Log This Trip
                          </button>
                        )}

                        <div className="border-t border-gray-100 my-1" />

                        {/* Multi-City Trip Editor (if applicable) */}
                        {tripPlan && tripPlan.isEditable && tripPlan.stops && tripPlan.stops.length > 1 && (
                          showAddCityInput ? (
                            <div className="px-4 py-2 space-y-2">
                              <input
                                type="text"
                                autoFocus
                                placeholder="City name..."
                                value={addCityName}
                                onChange={(e) => setAddCityName(e.target.value)}
                                className="w-full px-2 py-1.5 text-sm border border-teal-300 rounded focus:outline-none focus:border-teal-500"
                                style={FONTS.body}
                                onKeyDown={(e) => {
                                  if (e.key === 'Escape') { setShowAddCityInput(false); setAddCityName(''); }
                                }}
                              />
                              <div className="flex items-center gap-2">
                                <label className="text-xs text-gray-500" style={FONTS.body}>Days:</label>
                                <input
                                  type="number"
                                  min="1"
                                  max="14"
                                  value={addCityDays}
                                  onChange={(e) => setAddCityDays(parseInt(e.target.value) || 2)}
                                  className="w-14 px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-teal-500 text-center"
                                  style={FONTS.body}
                                />
                                <button
                                  onClick={async () => {
                                    if (addCityName.trim()) {
                                      const validation = await validateCityName(addCityName.trim());
                                      if (!validation?.valid) {
                                        showToast(validation?.error === 'validation_unavailable' ? 'City validation unavailable — please enter another city' : `"${addCityName.trim()}" doesn't appear to be a valid city`, 'error');
                                        return;
                                      }
                                      const validName = validation.correctedName || addCityName.trim();
                                      addTripStop({ name: validName, type: 'city', days: addCityDays });
                                      const newDestination = [...tripPlan.stops.map(s => s.name), validName].join(' & ');
                                      setSelectedDestination(prev => ({ ...prev, name: newDestination }));
                                      handleDestinationSelect({ ...selectedDestination, name: newDestination }, 0);
                                    }
                                    setShowAddCityInput(false);
                                    setAddCityName('');
                                    setAddCityDays(2);
                                    setShowHeaderMenu(false);
                                  }}
                                  disabled={!addCityName.trim()}
                                  className="px-3 py-1 text-xs bg-[#313143] text-white rounded hover:bg-[#3d3d52] disabled:opacity-50 disabled:cursor-not-allowed"
                                  style={FONTS.body}
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => { setShowAddCityInput(false); setAddCityName(''); }}
                                  className="px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                          <button
                            onClick={() => setShowAddCityInput(true)}
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                            style={FONTS.body}
                          >
                            <Plus className="w-4 h-4" />
                            Add City to Trip
                          </button>
                          )
                        )}

                        {/* Countries info */}
                        {itinerary.countriesIncluded && itinerary.countriesIncluded.length > 1 && (
                          <div className="px-4 py-2 text-xs text-gray-500" style={FONTS.body}>
                            Countries: {itinerary.countriesIncluded.join(', ')}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Row 2: Multi-city stops (if applicable) */}
            {tripPlan && tripPlan.isEditable && tripPlan.stops && tripPlan.stops.length > 1 && (
              <div className="mt-2 flex items-center gap-2 flex-wrap border-t border-gray-100 pt-2">
                <span className="text-xs text-gray-500">Stops:</span>
                {tripPlan.stops.map((stop) => (
                  <div key={stop.id} className="flex items-center gap-1 bg-teal-50 rounded-full px-2.5 py-0.5 text-xs">
                    <span className="font-medium text-teal-700">{stop.name.split(',')[0]}</span>
                    <span className="text-teal-500">({stop.days}d)</span>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        const confirmRemove = await showConfirmModal({ title: 'Remove Stop', message: `Remove ${stop.name.split(',')[0]} from your trip?\n\nThis will regenerate the itinerary.`, confirmText: 'Remove', type: 'danger' });
                        if (confirmRemove) {
                          removeTripStop(stop.id);
                          const newDestination = tripPlan.stops.filter(s => s.id !== stop.id).map(s => s.name).join(' & ');
                          setSelectedDestination(prev => ({ ...prev, name: newDestination }));
                          handleDestinationSelect({ ...selectedDestination, name: newDestination }, 0);
                        }
                      }}
                      className="ml-0.5 text-gray-400 hover:text-red-500 transition-colors"
                      title={`Remove ${stop.name.split(',')[0]}`}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
            
            {/* Validation Panel */}
            {showValidationPanel && validationIssues.length > 0 && (
              <div className="mb-4 bg-white rounded-xl shadow-lg border-2 border-gray-200 overflow-hidden">
                <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
                  <h3 className="text-gray-800 flex items-center gap-2" style={FONTS.headingSemibold}>
                    <Shield className="w-5 h-5 text-teal-600" /> Travel Alerts & Recommendations
                  </h3>
                  <button 
                    onClick={() => setShowValidationPanel(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    ✕
                  </button>
                </div>
                
                <div className="p-4 max-h-96 overflow-y-auto">
                  {/* Global alerts (visa, health, safety) */}
                  {validationIssues.filter(i => i.global).length > 0 && (
                    <div className="mb-4">
                      <h4 className="text-gray-700 mb-2 flex items-center gap-1.5" style={FONTS.headingSemibold}><Globe className="w-4 h-4 text-teal-600" /> Destination Information</h4>
                      <div className="space-y-2">
                        {validationIssues.filter(i => i.global).map((issue, idx) => {
                          // Truncate long advisory messages to first 2 sentences
                          const rawMsg = issue.message || '';
                          const sentences = rawMsg.split(/(?<=[.!])\s+/);
                          const truncated = sentences.length > 2 ? sentences.slice(0, 2).join(' ') : rawMsg;
                          return (
                          <div
                            key={idx}
                            className={`p-3 rounded-lg text-sm ${
                              issue.severity === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
                              issue.severity === 'warning' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' :
                              'bg-blue-50 border border-blue-200 text-blue-800'
                            }`}
                          >
                            {truncated.length > 200 ? truncated.substring(0, 197) + '...' : truncated}
                          </div>);
                        })}
                      </div>
                    </div>
                  )}
                  
                  {/* Day-specific issues */}
                  {validationIssues.filter(i => !i.global).length > 0 && (
                    <div>
                      <h4 className="text-gray-700 mb-2 flex items-center gap-1.5" style={FONTS.headingSemibold}><CalendarDays className="w-4 h-4 text-teal-600" /> Itinerary Suggestions</h4>
                      <div className="space-y-2">
                        {validationIssues.filter(i => !i.global).map((issue, idx) => (
                          <div 
                            key={idx}
                            className={`p-3 rounded-lg text-sm ${
                              issue.severity === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
                              issue.severity === 'warning' ? 'bg-yellow-50 border border-yellow-200 text-yellow-800' :
                              'bg-blue-50 border border-blue-200 text-blue-800'
                            }`}
                            style={FONTS.body}
                          >
                            <span style={FONTS.bodyBold}>Day {issue.day}:</span> {issue.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Safety info summary */}
                  {destinationSafetyInfo && (
                    <div className="mt-4 pt-4 border-t">
                      <h4 className="text-gray-700 mb-2 flex items-center gap-1.5" style={FONTS.headingSemibold}><FileText className="w-4 h-4" /> Quick Reference</h4>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        {destinationSafetyInfo.safetyInfo?.emergencyNumber && (
                          <div className="p-2 bg-gray-50 rounded" style={FONTS.body}>
                            <span className="text-gray-500">Emergency:</span>{' '}
                            <span style={FONTS.bodyBold}>{destinationSafetyInfo.safetyInfo.emergencyNumber}</span>
                          </div>
                        )}
                        {destinationSafetyInfo.weather?.bestTimeToVisit && (
                          <div className="p-2 bg-gray-50 rounded" style={FONTS.body}>
                            <span className="text-gray-500">Best time:</span>{' '}
                            <span style={FONTS.bodyBold}>{destinationSafetyInfo.weather.bestTimeToVisit}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Budget Tracker Modal — admin only, toggled by budget icon button */}
            {itinerary?.days?.length > 0 && tier === 'super-admin' && showBudgetTracker && (
              <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center pt-20 px-4" onClick={(e) => { if (e.target === e.currentTarget) setShowBudgetTracker(false); }}>
                <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[75vh] overflow-y-auto">
                  <div className="sticky top-0 bg-white rounded-t-2xl border-b border-gray-100 px-6 py-4 flex items-center justify-between z-10">
                    <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2" style={FONTS.headingBold}>
                      <DollarSign className="w-5 h-5 text-teal-600" />
                      Budget Tracker
                    </h3>
                    <button onClick={() => setShowBudgetTracker(false)} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>
                  <div className="p-6">
                    <BudgetTracker
                      itinerary={itinerary}
                      budgetPreference={profile?.default_budget || 'moderate'}
                      customBudgetAmount={profile?.custom_budget_amount}
                      totalDays={totalItineraryDays || itinerary?.days?.length}
                      onUpdateCost={updateItemCost}
                    />
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Main Grid — takes remaining height */}
        <div className="itin-shell flex-1 min-h-0">
          {/* Scrollable Main Content */}
          <div className="itin-main h-full">
          {/* Topo SVG Background */}
          <div className="absolute inset-0 pointer-events-none z-0" style={{ opacity: 0.035, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1000 1200' preserveAspectRatio='none'%3E%3Cpath d='M0 80 Q250 40 500 100 T1000 70' fill='none' stroke='%230d9488' stroke-width='1'/%3E%3Cpath d='M0 250 Q300 210 600 270 T1000 240' fill='none' stroke='%23313143' stroke-width='0.8'/%3E%3Cpath d='M0 420 Q200 390 500 440 T1000 410' fill='none' stroke='%230d9488' stroke-width='1'/%3E%3Cpath d='M0 600 Q350 570 650 620 T1000 590' fill='none' stroke='%23313143' stroke-width='0.8'/%3E%3Cpath d='M0 780 Q250 750 550 800 T1000 770' fill='none' stroke='%230d9488' stroke-width='1'/%3E%3Cpath d='M0 950 Q300 920 600 970 T1000 940' fill='none' stroke='%23313143' stroke-width='0.8'/%3E%3Cpath d='M0 1100 Q200 1070 500 1120 T1000 1090' fill='none' stroke='%230d9488' stroke-width='1'/%3E%3C/svg%3E")`, backgroundSize: '100% 600px', backgroundRepeat: 'repeat-y' }} />

          {/* Scroll Progress Bar */}
          {scrollProgress > 0 && (
            <div className="sticky top-0 z-20 h-[3px] w-full bg-gray-100">
              <div
                className="h-full bg-gradient-to-r from-[#1d8e85] to-emerald-400 transition-[width] duration-150 ease-out"
                style={{ width: `${scrollProgress * 100}%` }}
              />
            </div>
          )}

          {/* Collaborative Wishlist — above day tabs */}
          {features.COLLABORATIVE_TRIPS && currentTripId && userTripRole !== 'viewer' && (
            <Wishlist
              tripId={currentTripId}
              userRole={userTripRole}
              currentUserId={user?.id}
              onAddToItinerary={(placeName) => {
                setAddActivityDay(currentDay);
                setAddActivityPreFill(placeName);
                setShowAddActivityModal(true);
              }}
            />
          )}

          {/* Day Tabs - Mockup-matching pill navigation (inside itin-main) */}
          <div className="day-nav relative z-[10] mx-6 mt-0">
              {/* Day 0 Travel Day tab — shown only for overnight outbound flights */}
              {hasOvernightOutbound && overnightFlightInfo && (
                <button
                  onClick={() => setCurrentDay(-1)}
                  className={`day-pill flex items-center gap-1 ${currentDay === -1 ? 'active' : ''}`}
                  title="Travel Day — Overnight flight"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                  Travel
                </button>
              )}
              {(() => {
                // Use totalItineraryDays for progressive generation, otherwise use itinerary.days.length
                const totalDays = isProgressiveGenerating || totalItineraryDays > 0
                  ? Math.max(totalItineraryDays, itinerary.days?.length || 0)
                  : itinerary.days?.length || 0;

                return Array.from({ length: totalDays }, (_, index) => {
                  const dayNum = index + 1;
                  const day = itinerary.days?.[index];
                  const status = dayGenerationStatus[dayNum] || (day?._generationFailed ? 'failed' : day ? 'complete' : 'queued');
                  const hasError = dayGenerationErrors[dayNum] || day?._generationFailed;

                  const prevDay = index > 0 ? itinerary.days[index - 1] : null;
                  const sameCityAsPrev = day && prevDay &&
                    day.baseCity && prevDay.baseCity &&
                    day.baseCity.toLowerCase() === prevDay.baseCity.toLowerCase();
                  const isHotelChangeDay = day && index > 0 && !sameCityAsPrev &&
                    !areSameHotel(day.hotel?.name, prevDay?.hotel?.name) &&
                    day.hotel?.name && prevDay?.hotel?.name;

                  // Arrow pill between days: ONLY for overnight flights.
                  // Travel day = last day of current stop (departure day).
                  // Arrow appears AFTER the departure day = BEFORE the arrival day (first day of next stop).
                  // Check if the PREVIOUS day was a travel day with an overnight flight.
                  const prevDayNum = dayNum - 1;
                  const prevDayTravel = prevDayNum > 0
                    ? travelStructure?.travelDays?.find(td => td.day === prevDayNum)
                    : null;
                  // Determine if the previous day's travel leg is overnight
                  let isOvernightTransit = false;
                  if (prevDayTravel) {
                    try {
                      const flightEst = estimateFlightSchedule(prevDayTravel.from, prevDayTravel.to, {
                        helpers: { getCityCountrySync, getContinentForCountry },
                      });
                      isOvernightTransit = !!(flightEst?.isOvernight);
                    } catch { isOvernightTransit = false; }
                  }
                  const baseCitiesChangedHere = day && index > 0 &&
                    day.baseCity && itinerary.days?.[index - 1]?.baseCity &&
                    day.baseCity.toLowerCase() !== itinerary.days?.[index - 1]?.baseCity?.toLowerCase();
                  // Arrow pill ONLY for overnight flights — regardless of whether travelStructure exists.
                  // When travelStructure is available, use its from/to data.
                  // When it's NOT available (null state), fall back to estimating directly from baseCities.
                  let isCityChangeDay = false;
                  if (travelStructure?.isMultiStop) {
                    isCityChangeDay = isOvernightTransit;
                  } else if (baseCitiesChangedHere) {
                    // No travelStructure — estimate overnight status from consecutive baseCities
                    try {
                      const prevBase = itinerary.days?.[index - 1]?.baseCity;
                      const currBase = day?.baseCity;
                      if (prevBase && currBase) {
                        const fallbackEst = estimateFlightSchedule(prevBase, currBase, {
                          helpers: { getCityCountrySync, getContinentForCountry },
                        });
                        isCityChangeDay = !!(fallbackEst?.isOvernight);
                      }
                    } catch { isCityChangeDay = false; }
                  }

                  // For the pill label, use the arrival city from the previous day's travel info
                  const stopForPill = getStopForDay(dayNum, travelStructure);
                  const resolvedPillLabel = stopForPill?.resolvedLocation?.split(',')[0]?.trim();
                  const cityName = resolvedPillLabel
                    || (prevDayTravel
                      ? (prevDayTravel.to?.split(',')[0]?.trim() || day?.baseCity?.split(',')[0]?.trim() || '')
                      : (day?.baseCity?.split(',')[0]?.trim() || ''));

                  const uniqueCities = new Set(
                    itinerary.days?.filter(d => d?.baseCity).map(d => d.baseCity.toLowerCase()) || []
                  );
                  const isMultiCityTrip = uniqueCities.size > 1;

                  let dateLabel = null;
                  if (userProfile.preferences.startDate) {
                    const startDate = new Date(userProfile.preferences.startDate + 'T00:00:00');
                    const thisDate = new Date(startDate);
                    thisDate.setDate(startDate.getDate() + index);
                    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                    dateLabel = `${dayNames[thisDate.getDay()]} ${monthNames[thisDate.getMonth()]} ${thisDate.getDate()}`;
                  }

                  let buttonClass = '';
                  let statusIndicator = null;

                  if (status === 'generating') {
                    buttonClass = 'opacity-80 border-amber-300';
                    statusIndicator = (
                      <span className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center">
                        <span className="animate-spin w-3 h-3 border-2 border-amber-500 border-t-transparent rounded-full"></span>
                      </span>
                    );
                  } else if (status === 'queued') {
                    buttonClass = 'opacity-60 border-gray-300';
                    statusIndicator = (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-gray-300 rounded-full flex items-center justify-center text-[8px] text-white">⏳</span>
                    );
                  } else if (status === 'failed' || hasError) {
                    buttonClass = 'border-red-300';
                    statusIndicator = (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[10px] text-white">!</span>
                    );
                  } else if (currentDay === index) {
                    buttonClass = 'active';
                  }

                  // Transfer indicator pill before city change days
                  const transferPill = isCityChangeDay ? (
                    <div key={`transfer-${index}`} className="day-pill" style={{ borderColor: 'rgba(13,148,136,0.4)', color: '#0d9488', background: 'rgba(13,148,136,0.05)', cursor: 'default', fontWeight: 700, fontSize: 12 }}>
                      → {cityName}
                    </div>
                  ) : null;

                  const dayButton = (
                    <button
                      key={index}
                      onClick={() => {
                        if (status === 'complete' || day) {
                          setCurrentDay(index);
                          const scrollContainer = dayContentRef.current?.parentElement;
                          if (scrollContainer) scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      onDragOver={(e) => { if (activeDragId && index !== currentDay) e.preventDefault(); }}
                      onDrop={(e) => {
                        if (!activeDragId || index === currentDay || !itinerary?.days?.[currentDay] || !itinerary?.days?.[index]) return;
                        e.preventDefault();
                        const match = String(activeDragId).match(/^sortable-(activity|meal)-(\d+)$/);
                        if (!match) return;
                        const [, dragType, dragIdxStr] = match;
                        const dragIdx = parseInt(dragIdxStr, 10);
                        const updated = JSON.parse(JSON.stringify(itinerary));
                        const srcDay = updated.days[currentDay];
                        const destDay = updated.days[index];
                        const srcArr = dragType === 'activity' ? 'schedule' : 'meals';
                        if (!srcDay[srcArr]?.[dragIdx]) return;
                        const [movedItem] = srcDay[srcArr].splice(dragIdx, 1);
                        if (!destDay[srcArr]) destDay[srcArr] = [];
                        destDay[srcArr].push(movedItem);
                        // Bump _cacheBust on both mutated days (mobile parity)
                        bumpDayCacheBust(srcDay);
                        bumpDayCacheBust(destDay);
                        window._timelineCacheKey = null;
                        window._timelineCacheResult = null;
                        setActiveDragId(null);
                        setItinerary(updated);
                        setCurrentDay(index);
                        setHasUnsavedChanges(true);
                        showToast(`Moved to Day ${index + 1}`, 'success', 2000);
                      }}
                      disabled={status === 'generating' || status === 'queued'}
                      className={`day-pill relative ${buttonClass} ${
                        (status === 'generating' || status === 'queued') ? 'cursor-not-allowed' : ''
                      } ${activeDragId && index !== currentDay ? 'ring-2 ring-teal-300 ring-offset-1 animate-pulse' : ''}`}
                      title={status === 'generating' ? 'Generating...' : status === 'queued' ? 'Waiting...' : hasError ? `Error: ${hasError}` : `${dateLabel || ''} ${day?.baseCity || ''}`}
                    >
                      <span>Day {dayNum}{isMultiCityTrip && cityName && status !== 'queued' ? ` · ${cityName}` : ''}</span>
                      {statusIndicator}
                      {isHotelChangeDay && !isCityChangeDay && !statusIndicator && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-orange-500 rounded-full border border-white" title="Hotel change day"></span>
                      )}
                    </button>
                  );

                  // Return transfer pill + day button for city changes, just day button otherwise
                  return transferPill ? [transferPill, dayButton] : dayButton;
                });
              })()}
              {/* Add Day button — always at end of day pills */}
              {itinerary?.days?.length > 0 && !isProgressiveGenerating && (
                <button
                  onClick={() => setShowAddDayModal(true)}
                  className="day-pill flex items-center justify-center gap-1"
                  style={{ borderColor: 'rgba(13,148,136,0.3)', color: '#0d9488', background: 'rgba(13,148,136,0.04)', minWidth: 36, fontWeight: 700, fontSize: 12 }}
                  title="Add a day to your trip"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

          {/* Day Content */}
          <div ref={dayContentRef} className="relative z-[1] max-w-5xl mx-auto px-6 pt-4 pb-8">
          {/* Day 0 Travel Day Content — Overnight Flight */}
          {currentDay === -1 && hasOvernightOutbound && overnightFlightInfo && (
            <div className="space-y-4">
              {/* Flight Details Card */}
              <div className="rounded-2xl p-6 border border-gray-200" style={{ background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
                <div className="flex items-center gap-2 mb-5">
                  <Plane className="w-4 h-4 text-teal-500" />
                  <span className="text-xs uppercase tracking-wider font-bold text-teal-600" style={FONTS.bodySemibold}>Outbound Flight</span>
                  <span className="ml-auto text-xs text-gray-400" style={FONTS.body}>{overnightFlightInfo.airline} {overnightFlightInfo.flightNumber}</span>
                </div>

                <div className="flex items-start gap-4">
                  {/* Timeline dots */}
                  <div className="flex flex-col items-center pt-1">
                    <div className="w-3 h-3 rounded-full bg-teal-500 border-2 border-teal-200"></div>
                    <div className="w-0.5 h-14 bg-gradient-to-b from-teal-300 to-teal-400 my-1"></div>
                    <div className="w-3 h-3 rounded-full bg-teal-600 border-2 border-teal-200"></div>
                  </div>

                  <div className="flex-1 space-y-4">
                    {/* Departure */}
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-black text-gray-900" style={FONTS.headingBold}>{overnightFlightInfo.departureTime || '--:--'}</span>
                        <span className="text-sm text-gray-400" style={FONTS.body}>
                          {overnightFlightInfo.departureDate ? new Date(overnightFlightInfo.departureDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                        </span>
                      </div>
                      <div className="text-sm text-gray-700" style={FONTS.bodySemibold}>
                        {overnightFlightInfo.from}
                        {overnightFlightInfo.departureTerminal && <span className="text-gray-400 font-normal ml-1.5">Terminal {overnightFlightInfo.departureTerminal}</span>}
                      </div>
                    </div>

                    {/* Flight info */}
                    <div className="flex items-center gap-3">
                      {overnightFlightInfo.duration && (
                        <span className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-lg" style={FONTS.body}>
                          {overnightFlightInfo.duration.replace('PT', '').replace('H', 'h ').replace('M', 'm').trim()}
                        </span>
                      )}
                      {overnightFlightInfo.segments?.length > 1 && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-lg" style={FONTS.body}>
                          {overnightFlightInfo.segments.length - 1} stop{overnightFlightInfo.segments.length > 2 ? 's' : ''}
                        </span>
                      )}
                      {(!overnightFlightInfo.segments || overnightFlightInfo.segments.length <= 1) && (
                        <span className="text-xs text-teal-600 bg-teal-50 px-2 py-1 rounded-lg" style={FONTS.body}>Nonstop</span>
                      )}
                    </div>

                    {/* Arrival */}
                    <div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-2xl font-black text-gray-900" style={FONTS.headingBold}>{overnightFlightInfo.arrivalTime || '--:--'}</span>
                        <span className="text-sm text-gray-400" style={FONTS.body}>
                          {overnightFlightInfo.arrivalDate ? new Date(overnightFlightInfo.arrivalDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : ''}
                        </span>
                        <span className="text-[10px] font-bold text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">+1</span>
                      </div>
                      <div className="text-sm text-gray-700" style={FONTS.bodySemibold}>
                        {overnightFlightInfo.to}
                        {overnightFlightInfo.arrivalTerminal && <span className="text-gray-400 font-normal ml-1.5">Terminal {overnightFlightInfo.arrivalTerminal}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Check if current day is generating, queued, or failed */}
          {currentDay >= 0 && (() => {
            const dayNum = currentDay + 1;
            const status = dayGenerationStatus[dayNum] || (currentDayData?._generationFailed ? 'failed' : currentDayData ? 'complete' : 'queued');
            const errorMsg = dayGenerationErrors[dayNum] || currentDayData?._error;

            // Show placeholder for non-complete days (including failed days with _generationFailed flag)
            if (status === 'generating' || status === 'queued' || status === 'failed' || currentDayData?._generationFailed) {
              return (
                <div className="flex flex-col items-center justify-center py-16 px-8">
                  <div className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 ${
                    status === 'generating' ? 'bg-amber-100' : status === 'failed' ? 'bg-red-100' : 'bg-gray-100'
                  }`}>
                    {status === 'generating' ? (
                      <div className="animate-spin w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full"></div>
                    ) : status === 'failed' ? (
                      <span className="text-4xl">⚠️</span>
                    ) : (
                      <span className="text-4xl">⏳</span>
                    )}
                  </div>
                  <h2 className="text-2xl text-gray-800 mb-2" style={FONTS.headingBold}>
                    {status === 'generating' ? `Generating Day ${dayNum}...` :
                     status === 'failed' ? `Day ${dayNum} Generation Failed` :
                     `Day ${dayNum} Queued`}
                  </h2>
                  <p className="text-gray-500 text-center max-w-md mb-6" style={FONTS.body}>
                    {status === 'generating' ?
                      'Creating personalized activities, restaurants, and experiences for this day...' :
                     status === 'failed' ?
                      (errorMsg || 'There was an error generating this day. You can retry or continue with other days.') :
                      `This day will be generated after Day ${dayNum - 1} is complete.`}
                  </p>
                  {status === 'failed' && (
                    <button
                      onClick={() => retryFailedDay(dayNum)}
                      className="px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors flex items-center gap-2"
                      style={FONTS.bodyBold}
                    >
                      <RefreshCw className="w-4 h-4" />
                      Retry Day {dayNum}
                    </button>
                  )}
                  {status === 'queued' && isProgressiveGenerating && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                      <span className="text-sm" style={FONTS.body}>Generation in progress...</span>
                    </div>
                  )}
                </div>
              );
            }

            // Otherwise show normal day content
            return null;
          })()}

          {/* Normal Day Content - only show if day is complete and not failed */}
          {currentDay >= 0 && currentDayData && !currentDayData._generationFailed && (dayGenerationStatus[currentDay + 1] === 'complete' || !dayGenerationStatus[currentDay + 1]) && (
            <>
          {/* F1 · Must-Do strip — shown on Day 1 only. Curated top-rated
              community places for each city on the trip. Tapping a chip
              opens Place Detail. Future: "+ Add to day" handler. */}
          {safeCurrentDay === 0 && itinerary?.days?.length > 0 && (() => {
            const seen = new Set();
            const cities = (itinerary.days || [])
              .filter(d => d.baseCity && !seen.has(d.baseCity.toLowerCase()) && seen.add(d.baseCity.toLowerCase()))
              .map(d => ({
                name: (d.baseCity || '').split(/[,>]/)[0].trim(),
                country: itinerary?.destination?.split(',').pop()?.trim() || '',
              }));
            if (cities.length === 0) return null;
            return (
              <React.Suspense fallback={null}>
                <CommunityErrorBoundary>
                  <MustDoStrip
                    cities={cities}
                    currentDayIdx={safeCurrentDay}
                    onOpenPlaceDetail={openPlaceDetail}
                  />
                </CommunityErrorBoundary>
              </React.Suspense>
            );
          })()}

          {/* Day Theme Header — Layer-Cake with stats row */}
          {(() => {
            const dayActivities = currentDayData?.schedule || [];
            const dayMeals = currentDayData?.meals || [];
            const allItems = [...dayActivities, ...dayMeals].filter(i => i.time);
            const times = allItems.map(i => parseTime(i.time)).filter(t => t != null && !isNaN(t));
            const firstMin = times.length > 0 ? Math.min(...times) : null;
            const lastMin = times.length > 0 ? Math.max(...times) : null;
            const firstTimeStr = firstMin != null ? formatTimeDisplay(minutesToTime(firstMin)) : '';
            const lastTimeStr = lastMin != null ? formatTimeDisplay(minutesToTime(lastMin)) : '';
            const activityCount = dayActivities.length;
            const mealCount = dayMeals.length;
            // Compute daily cost from priceTier / priceRange
            const estimateCost = (tier) => {
              if (!tier) return 0;
              const clean = typeof tier === 'string' ? tier.replace(/[^$€£¥]/g, '') : '';
              const len = clean.length;
              if (len <= 1) return 15;
              if (len === 2) return 35;
              if (len === 3) return 75;
              return 120;
            };
            const dailyCostUSD = [...dayActivities.map(a => estimateCost(a.priceTier)), ...dayMeals.map(m => estimateCost(m.priceRange))].reduce((s, v) => s + v, 0);
            const dayCurr = getCurrencyForCity(currentDayData?.baseCity || currentDayData?.city || '') || getDestinationCurrency();
            const dailyCost = convertUSDToLocal(dailyCostUSD, dayCurr);
            return (
              <div className="mb-3">
                <div className="flex items-center gap-3 flex-wrap mb-1">
                  <h2 className="text-2xl font-extrabold text-[#313143]" style={{ letterSpacing: '-0.04em' }}>
                    Day {safeCurrentDay + 1} — {currentDayData.theme}
                  </h2>
                  {safeCurrentDay === 0 && (
                    <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-teal-50 text-teal-600 border border-teal-200">Arrival day</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-[13px] text-gray-500" style={FONTS.body}>
                  {(() => {
                    if (!userProfile.preferences.startDate) return null;
                    const start = parseLocalDate(userProfile.preferences.startDate);
                    if (!start) return null;
                    const dayDate = new Date(start);
                    dayDate.setDate(dayDate.getDate() + safeCurrentDay);
                    return <span>{dayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</span>;
                  })()}
                  {/* Judgmental density badges ("Lighter schedule" + "Packed
                      Day") removed 2026-06 per product decision: don't tell
                      the user their day is good/bad — they can see the
                      schedule themselves. Factual counts stay. */}
                  {activityCount > 0 && <span>· {activityCount} {activityCount === 1 ? 'activity' : 'activities'}</span>}
                  {mealCount > 0 && <span>· {mealCount} {mealCount === 1 ? 'meal' : 'meals'}</span>}
                  <span className="text-teal-600 font-semibold">
                    {/* Daily budget pill — show local currency + USD equiv
                        when different. Prevents the "$54,819" Costa Rica
                        misread where a USD `$` prefix was pasted in front
                        of the CRC amount. */}
                    · ~{currencySymbols[dayCurr] || '$'}{Math.round(dailyCost).toLocaleString()}
                    {dayCurr && dayCurr !== 'USD' && dailyCostUSD >= 1 && (
                      <span className="text-gray-500 font-normal"> (~${Math.round(dailyCostUSD).toLocaleString()} USD)</span>
                    )}
                    <span className="text-gray-500 font-normal"> today</span>
                  </span>
                </div>
              </div>
            );
          })()}
          
          {/* Hotel + Controls Row */}
          <div className="flex gap-3 mb-3 items-stretch">
            {/* Departure Card on last day, Hotel Card on other days, nothing if no hotel data on mid-trip day */}
            {/* Use totalItineraryDays (planned total) not itinerary.days.length (generated so far) */}
            {/* Detect overnight departure: next day's base city changes AND inter-city transport has overnight indicator */}
            {(() => {
              const nextDayData = itinerary?.days?.[currentDay + 1];
              const hasOvernightDeparture = nextDayData && nextDayData.baseCity !== currentDayData.baseCity && (() => {
                // Check interCityTransport for overnight departure from this day
                const transport = itinerary?.interCityTransport?.find(t => t.day === currentDay + 1 || t.day === currentDay + 2);
                const flight = itinerary?.flights?.find(f => f.day === currentDay + 1 || f.day === currentDay + 2);
                const leg = transport || flight;
                if (!leg) return false;
                if (leg.isOvernight) return true;
                if (leg.arrivalDayOffset && leg.arrivalDayOffset > 0) return true;
                const depHour = leg.departureTime ? parseInt(leg.departureTime.split(':')[0]) : -1;
                if (depHour >= 20) return true;
                const durationMatch = leg.duration?.match(/(\d+)h/);
                const hours = durationMatch ? parseInt(durationMatch[1]) : 0;
                if (hours >= 8 && depHour >= 14) return true;
                if (depHour >= 0 && hours > 0 && (depHour + hours) >= 24) return true;
                return false;
              })();
              return { hasOvernightDeparture, nextDayData };
            })().hasOvernightDeparture && currentDayData.hotel ? (
              // Overnight departure day — show hotel with overnight journey note
              <div className="flex-1 p-4 bg-indigo-50 rounded-2xl shadow-sm border-2 border-indigo-200 flex flex-col min-h-[120px]">
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Moon className="w-5 h-5 text-indigo-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-bold text-gray-800 truncate" style={FONTS.headingSemibold}>{currentDayData.hotel.name}</h3>
                      <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full flex-shrink-0">Checkout today</span>
                    </div>
                    <p className="text-sm text-indigo-600" style={FONTS.body}>
                      ✨ Overnight journey tonight — no hotel needed
                    </p>
                    {currentDayData.hotel.address && (
                      <p className="text-xs text-gray-500 mt-1 truncate" style={FONTS.body}>{currentDayData.hotel.address}</p>
                    )}
                  </div>
                </div>
              </div>
            ) : currentDay !== (Math.max(totalItineraryDays, itinerary.days?.length || 0)) - 1 && currentDayData.hotel ? (
              <div
                className={`flex-1 bg-white rounded-2xl shadow-sm border-2 border-brand-100 flex flex-col cursor-pointer transition-all ${expandedHotelCards[currentDay] ? 'p-4 min-h-[120px]' : 'p-3'}`}
                onClick={() => setExpandedHotelCards(prev => ({ ...prev, [currentDay]: !prev[currentDay] }))}
              >
                {/* COLLAPSED VIEW: compact single line */}
                {!expandedHotelCards[currentDay] && (
                  <div className="flex items-center gap-3">
                    <ActivityIcon emoji="🏨" size={18} className="flex-shrink-0" />
                    <h3 className="font-bold text-gray-800 truncate flex-1" style={FONTS.headingSemibold}>{currentDayData.hotel.name}</h3>
                    {currentDayData.hotel.pricePerNight && (
                      <span className="text-sm text-brand-600 flex-shrink-0" style={FONTS.bodyBold}>
                        {formatHotelPrice(currentDayData.hotel.pricePerNight, currentDayData.hotel.priceCurrency, currentDayData.baseCity)}/night
                      </span>
                    )}
                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                )}
                {/* EXPANDED VIEW: full details */}
                {expandedHotelCards[currentDay] && (
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Hotel className="w-5 h-5 text-brand-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {/* Hotel name + rating */}
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-bold text-gray-800 truncate" style={FONTS.headingSemibold}>{currentDayData.hotel.name}</h3>
                      {currentDayData.hotel.googleValidated && (
                        <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex-shrink-0" title="Verified with Google Places">
                          ✓
                        </span>
                      )}
                      {currentDayData.hotel.rating && (
                        <span className="text-xs text-yellow-600 flex-shrink-0">{'⭐'.repeat(Math.min(Math.round(currentDayData.hotel.rating), 5))}</span>
                      )}
                      <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 ml-auto" />
                    </div>
                    {/* Address link — use coordinates for map link when available */}
                    <a
                      href={currentDayData.hotel.coordinates
                        ? `https://www.google.com/maps/search/?api=1&query=${currentDayData.hotel.coordinates.lat},${currentDayData.hotel.coordinates.lng}`
                        : getSmartLink({ name: currentDayData.hotel.name, address: currentDayData.hotel.address, placeId: currentDayData.hotel.placeId }, 'hotel', itinerary?.destination?.split(',')[0] || '')}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-gray-600 hover:text-blue-600 mb-2 flex items-center gap-1 group/hotel transition-colors line-clamp-1"
                      style={FONTS.body}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <span className="group-hover/hotel:underline truncate">{currentDayData.hotel.address || currentDayData.hotel.name}</span>
                      <ExternalLink className="w-3 h-3 opacity-0 group-hover/hotel:opacity-100 transition-opacity flex-shrink-0" />
                    </a>
                    {/* Smart pricing: estimated vs real */}
                    <div className="flex items-center gap-2 mt-1">
                      {currentDayData.hotel.pricePerNight ? (
                        <>
                          <span className="text-sm text-brand-600" style={FONTS.bodyBold}>
                            {formatHotelPrice(currentDayData.hotel.pricePerNight, currentDayData.hotel.priceCurrency, currentDayData.baseCity)}/night
                          </span>
                          {!currentDayData.hotel.amadeusVerified && amadeusHotelData[currentDayData.hotel.name]?.loading ? (
                            <span className="text-xs text-blue-400 flex items-center gap-1" style={FONTS.body}>
                              <div className="w-2.5 h-2.5 border border-blue-400 border-t-transparent rounded-full animate-spin" />
                              verifying...
                            </span>
                          ) : !currentDayData.hotel.amadeusVerified ? (
                            <span className="text-xs text-gray-400" style={FONTS.body}>· estimated</span>
                          ) : null}
                        </>
                      ) : (
                        <span className="text-sm text-gray-400" style={FONTS.body}>Price unavailable</span>
                      )}
                    </div>
                    {/* Two-button row: Details + Change */}
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          const willOpen = !showHotelDetails[currentDay];
                          setShowHotelDetails(prev => ({ ...prev, [currentDay]: willOpen }));
                          // Auto-fetch room options when opening details
                          if (willOpen && (!hotelRoomOptions || hotelRoomOptions.day !== currentDay)) {
                            fetchHotelRoomOptions(currentDay);
                          }
                        }}
                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${
                          showHotelDetails[currentDay]
                            ? 'bg-teal-100 text-teal-800 border-teal-300'
                            : 'bg-teal-50 text-teal-700 hover:bg-teal-100 border-teal-200'
                        }`}
                        style={FONTS.body}
                      >
                        <Info className="w-3.5 h-3.5" />
                        {showHotelDetails[currentDay] ? 'Hide Details' : 'Details'}
                      </button>
                      {/* Hide the "Change" hotel affordance when the current
                          day's city corresponds to a user-pinned resort /
                          named property. See isPinnedHotelCity above. */}
                      {!isPinnedHotelCity(itinerary?.days?.[currentDay]?.baseCity || itinerary?.days?.[currentDay]?.city) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            setShowHotelDetails(prev => ({ ...prev, [currentDay]: false }));
                            // Toggle: close if already open, otherwise fetch
                            if (hotelAlternatives?.day === currentDay && !hotelAlternatives?.loading) {
                              setHotelAlternatives(null);
                            } else {
                              fetchHotelAlternatives(currentDay);
                            }
                          }}
                          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-colors border ${
                            hotelAlternatives?.day === currentDay
                              ? 'bg-teal-100 text-teal-800 border-teal-300'
                              : 'bg-gray-50 text-gray-700 hover:bg-gray-100 border-gray-200'
                          }`}
                          style={FONTS.body}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                          Change
                        </button>
                      )}
                    </div>
                    {/* Hotel details modal */}
                    {showHotelDetails[currentDay] && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowHotelDetails(prev => ({ ...prev, [currentDay]: false }))}>
                        {/* Backdrop */}
                        <div className="absolute inset-0 bg-black/50" />
                        {/* Centered panel */}
                        <div
                          className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl animate-in fade-in zoom-in-95 duration-200"
                          style={{ maxHeight: '85vh', overflowY: 'auto' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Close handle */}
                          <div className="flex justify-center py-3 sticky top-0 bg-white rounded-t-3xl z-10">
                            <div className="w-10 h-1 bg-gray-300 rounded-full" />
                          </div>
                          <div className="px-5 pb-6 space-y-4">
                            {/* Hotel name + rating */}
                            <div>
                              <div className="flex items-center gap-2 mb-1">
                                <h2 className="text-xl text-gray-900" style={FONTS.headingBold}>{currentDayData.hotel.name}</h2>
                              </div>
                              <div className="flex items-center gap-2">
                                {currentDayData.hotel.rating && (
                                  <span className="text-sm text-yellow-500">{'⭐'.repeat(Math.min(Math.round(currentDayData.hotel.rating), 5))}</span>
                                )}
                                {currentDayData.hotel.rating && (
                                  <span className="text-xs text-gray-500" style={FONTS.body}>{currentDayData.hotel.rating}-Star</span>
                                )}
                                {currentDayData.hotel.googleValidated && (
                                  <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full" style={FONTS.bodyBold}>✓ Verified</span>
                                )}
                              </div>
                            </div>
                            {/* Price */}
                            {currentDayData.hotel.pricePerNight && (() => {
                              // Calculate actual nights at THIS hotel (consecutive days with same hotel name)
                              const hotelName = currentDayData.hotel.name;
                              let nightsAtHotel = 0;
                              if (itinerary?.days) {
                                for (let d = 0; d < itinerary.days.length; d++) {
                                  if (itinerary.days[d]?.hotel?.name === hotelName) nightsAtHotel++;
                                }
                              }
                              if (nightsAtHotel === 0) nightsAtHotel = 1;
                              const isVerifiedPrice = currentDayData.hotel.amadeusVerified;
                              const totalPrice = currentDayData.hotel.pricePerNight * nightsAtHotel;

                              return (
                              <div className="p-3 bg-teal-50 rounded-xl border border-teal-200">
                                <div>
                                  <span className="text-xl text-teal-700" style={FONTS.headingBold}>
                                    {formatHotelPrice(currentDayData.hotel.pricePerNight, currentDayData.hotel.priceCurrency, currentDayData.baseCity)}
                                  </span>
                                  <span className="text-sm text-gray-500" style={FONTS.body}>/night</span>
                                  {isVerifiedPrice && (
                                    <span className="text-xs text-teal-500 ml-2" style={FONTS.body}>✓ verified</span>
                                  )}
                                  {nightsAtHotel > 1 && (
                                    <p className="text-xs text-gray-500 mt-0.5" style={FONTS.body}>
                                      {isVerifiedPrice ? '' : '~'}{formatHotelPrice(totalPrice, currentDayData.hotel.priceCurrency, currentDayData.baseCity)} total · {nightsAtHotel} night{nightsAtHotel !== 1 ? 's' : ''}
                                    </p>
                                  )}
                                  {!isVerifiedPrice && (
                                    <p className="text-[10px] text-gray-400 mt-1" style={FONTS.body}>Estimated price · use Change Hotel for live pricing</p>
                                  )}
                                </div>
                              </div>
                              );
                            })()}
                            {/* Room info */}
                            {(currentDayData.hotel.roomType || currentDayData.hotel.boardType || currentDayData.hotel.roomDescription) && (
                              <div className="grid grid-cols-2 gap-3">
                                {currentDayData.hotel.roomType && (
                                  <div className="p-3 bg-gray-50 rounded-xl">
                                    <p className="text-xs text-gray-500" style={FONTS.body}>Room Type</p>
                                    <p className="text-sm text-gray-800" style={FONTS.headingSemibold}>{currentDayData.hotel.roomType}</p>
                                  </div>
                                )}
                                {currentDayData.hotel.boardType && (
                                  <div className="p-3 bg-gray-50 rounded-xl">
                                    <p className="text-xs text-gray-500" style={FONTS.body}>Board</p>
                                    <p className="text-sm text-gray-800" style={FONTS.headingSemibold}>
                                      {currentDayData.hotel.boardType === 'ROOM_ONLY' ? 'Room Only' :
                                       currentDayData.hotel.boardType === 'BREAKFAST' ? 'Breakfast Included' :
                                       currentDayData.hotel.boardType === 'HALF_BOARD' ? 'Half Board' :
                                       currentDayData.hotel.boardType === 'FULL_BOARD' ? 'Full Board' :
                                       currentDayData.hotel.boardType === 'ALL_INCLUSIVE' ? 'All Inclusive' :
                                       currentDayData.hotel.boardType}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                            {currentDayData.hotel.roomDescription && (
                              <p className="text-xs text-gray-500" style={FONTS.body}>{currentDayData.hotel.roomDescription}</p>
                            )}
                            {/* Room Options Section */}
                            <div>
                              {!hotelRoomOptions || hotelRoomOptions.day !== currentDay ? (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                  <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                                  <span className="text-xs text-gray-500" style={FONTS.body}>Loading room options...</span>
                                </div>
                              ) : hotelRoomOptions.loading ? (
                                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                                  <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                                  <span className="text-xs text-gray-500" style={FONTS.body}>Loading room options...</span>
                                </div>
                              ) : hotelRoomOptions.rooms?.length > 0 ? (
                                <div className="space-y-2">
                                  <h3 className="text-sm text-gray-900" style={FONTS.headingSemibold}>Room Options</h3>
                                  {hotelRoomOptions.rooms.map((room, idx) => {
                                    const boardLabel = room.boardType === 'ROOM_ONLY' ? 'Room Only' :
                                      room.boardType === 'BREAKFAST' ? 'Breakfast included' :
                                      room.boardType === 'HALF_BOARD' ? 'Half Board' :
                                      room.boardType === 'FULL_BOARD' ? 'Full Board' :
                                      room.boardType === 'ALL_INCLUSIVE' ? 'All Inclusive' :
                                      room.boardType || '';
                                    return (
                                      <div
                                        key={room.offerId || idx}
                                        className={`p-3 rounded-xl border cursor-pointer transition-colors ${
                                          idx === 0 ? 'border-teal-300 bg-teal-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                                        }`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          // Update hotel with selected room option
                                          const updatedItinerary = { ...itinerary };
                                          updatedItinerary.days.forEach((day) => {
                                            if (day.hotel && day.hotel.name === currentDayData.hotel.name) {
                                              day.hotel = {
                                                ...day.hotel,
                                                roomType: room.roomType || room.roomDescription || day.hotel.roomType,
                                                boardType: room.boardType || day.hotel.boardType,
                                                roomDescription: room.roomDescription || day.hotel.roomDescription,
                                                pricePerNight: room.pricePerNight || room.price || day.hotel.pricePerNight,
                                                priceCurrency: room.currency || day.hotel.priceCurrency, // Preserve actual currency
                                                amadeusVerified: true,
                                              };
                                            }
                                          });
                                          setItinerary(updatedItinerary);
                                          showToast(`Selected: ${room.roomType || room.roomDescription || 'Room option'}`, 'success');
                                        }}
                                      >
                                        <div className="flex items-center justify-between">
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-900 truncate" style={FONTS.headingSemibold}>
                                              {room.roomType || room.roomDescription || `Room ${idx + 1}`}
                                            </p>
                                            <div className="flex items-center gap-2 mt-0.5">
                                              {boardLabel && <span className="text-xs text-gray-500" style={FONTS.body}>{boardLabel}</span>}
                                              {room.isFreeCancellation && <span className="text-xs text-green-600" style={FONTS.body}>Free cancellation</span>}
                                              {room.bedType && <span className="text-xs text-gray-400" style={FONTS.body}>{room.bedType}{room.beds > 1 ? ` x${room.beds}` : ''}</span>}
                                            </div>
                                          </div>
                                          {room.pricePerNight ? (
                                            <div className="text-right flex-shrink-0 ml-3">
                                              <span className="text-sm font-bold text-teal-700" style={FONTS.headingBold}>{formatLocalPrice(room.pricePerNight)}</span>
                                              <span className="text-xs text-gray-400">/n</span>
                                            </div>
                                          ) : room.price ? (
                                            <div className="text-right flex-shrink-0 ml-3">
                                              <span className="text-sm font-bold text-teal-700" style={FONTS.headingBold}>{formatLocalPrice(room.price / (hotelRoomOptions.nights || 1))}</span>
                                              <span className="text-xs text-gray-400">/n</span>
                                            </div>
                                          ) : null}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  <p className="text-[10px] text-gray-400 text-center mt-1" style={FONTS.body}>Prices from Amadeus · Tap to select</p>
                                </div>
                              ) : (
                                <div className="p-3 bg-gray-50 rounded-xl">
                                  <p className="text-xs text-gray-500 text-center" style={FONTS.body}>
                                    {hotelRoomOptions.error || 'No room options available for these dates'}
                                  </p>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      fetchHotelRoomOptions(currentDay);
                                    }}
                                    className="mt-2 w-full px-3 py-1.5 text-xs text-teal-600 bg-teal-50 rounded-lg hover:bg-teal-100 transition-colors"
                                    style={FONTS.body}
                                  >
                                    Retry
                                  </button>
                                </div>
                              )}
                            </div>
                            {/* Address */}
                            {currentDayData.hotel.address && (
                              <div className="flex items-start gap-3">
                                <MapPin className="w-5 h-5 text-teal-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm text-gray-700" style={FONTS.body}>{currentDayData.hotel.address}</p>
                                  <a
                                    href={getSmartLink({ name: currentDayData.hotel.name, address: currentDayData.hotel.address, placeId: currentDayData.hotel.placeId }, 'hotel', itinerary?.destination?.split(',')[0] || '')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs text-teal-600 hover:text-teal-800 mt-0.5 inline-block transition-colors"
                                    style={FONTS.body}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    Open in Google Maps →
                                  </a>
                                </div>
                              </div>
                            )}
                            {/* Description */}
                            {currentDayData.hotel.description && (
                              <div className="p-3 bg-teal-50 rounded-xl border border-teal-200">
                                <p className="text-xs text-teal-700" style={FONTS.body}>
                                  <strong>🤖 Why we picked this:</strong> {currentDayData.hotel.description}
                                </p>
                              </div>
                            )}
                            {/* Check-in/out — only show check-in on arrival day or city-change days */}
                            {(() => {
                              const prevDayData = currentDay > 0 ? itinerary?.days?.[currentDay - 1] : null;
                              const isArrivalDay = currentDay === 0 || (prevDayData && prevDayData.city !== currentDayData.city);
                              const showCheckIn = isArrivalDay && currentDayData.hotel.checkIn;
                              const showCheckOut = currentDayData.hotel.checkOut;
                              return (showCheckIn || showCheckOut) ? (
                                <div className="grid grid-cols-2 gap-3">
                                  {showCheckIn && (
                                    <div className="p-3 bg-gray-50 rounded-xl">
                                      <p className="text-xs text-gray-500" style={FONTS.body}>Check-in</p>
                                      <p className="text-sm text-gray-800" style={FONTS.headingBold}>{currentDayData.hotel.checkIn}</p>
                                    </div>
                                  )}
                                  {showCheckOut && (
                                    <div className="p-3 bg-gray-50 rounded-xl">
                                      <p className="text-xs text-gray-500" style={FONTS.body}>Check-out</p>
                                      <p className="text-sm text-gray-800" style={FONTS.headingBold}>{currentDayData.hotel.checkOut}</p>
                                    </div>
                                  )}
                                </div>
                              ) : null;
                            })()}
                            {/* Amenities */}
                            {currentDayData.hotel.amenities && currentDayData.hotel.amenities.length > 0 && (
                              <div>
                                <h3 className="text-base text-gray-900 mb-2" style={FONTS.headingSemibold}>Amenities</h3>
                                <div className="grid grid-cols-2 gap-2">
                                  {currentDayData.hotel.amenities.map((amenity, idx) => (
                                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-600" style={FONTS.body}>
                                      <span className="w-1.5 h-1.5 bg-teal-400 rounded-full flex-shrink-0" />
                                      {amenity}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {/* Action buttons */}
                            <div className="flex gap-3 pt-2">
                              <button
                                onClick={() => {
                                  setShowHotelDetails(prev => ({ ...prev, [currentDay]: false }));
                                  fetchHotelAlternatives(currentDay);
                                }}
                                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 text-sm flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                                style={FONTS.body}
                              >
                                <Pencil className="w-4 h-4" />
                                Change Hotel
                              </button>
                              <a
                                href={getSmartLink({ name: currentDayData.hotel.name, address: currentDayData.hotel.address, placeId: currentDayData.hotel.placeId }, 'hotel', itinerary?.destination?.split(',')[0] || '')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl text-gray-700 text-sm flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                                style={FONTS.body}
                                onClick={(e) => e.stopPropagation()}
                              >
                                <MapPin className="w-4 h-4" />
                                View on Map
                              </a>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Amenities chips (quick preview when details panel is closed) */}
                    {!showHotelDetails[currentDay] && currentDayData.hotel.amenities && currentDayData.hotel.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {currentDayData.hotel.amenities.slice(0, 3).map((amenity, idx) => (
                          <span key={idx} className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                            {amenity}
                          </span>
                        ))}
                      </div>
                    )}
                    {/* Amadeus Hotel Alternatives Modal (shown when Change is clicked) — kept outside expanded guard since it's a fixed overlay */}
                    {hotelAlternatives?.day === currentDay && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setHotelAlternatives(null)}>
                        {/* Backdrop */}
                        <div className="absolute inset-0 bg-black/50" />
                        {/* Modal panel */}
                        <div
                          className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl animate-in fade-in zoom-in-95 duration-200 overflow-hidden"
                          style={{ maxHeight: '85vh', overflowY: 'auto' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Drag handle */}
                          <div className="flex justify-center py-3 sticky top-0 bg-white rounded-t-3xl z-10">
                            <div className="w-10 h-1 bg-gray-300 rounded-full" />
                          </div>
                          {/* Modal header */}
                          <div className="px-5 pb-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <h2 className="text-xl text-gray-900" style={FONTS.headingBold}>Change Hotel</h2>
                                <p className="text-sm text-gray-500 mt-0.5" style={FONTS.body}>
                                  {hotelAlternatives.cityName || currentDayData.baseCity || 'Hotels in this area'}
                                  {hotelAlternatives.nights > 1 ? ` · ${hotelAlternatives.nights} nights` : ''}
                                </p>
                              </div>
                              <button onClick={() => setHotelAlternatives(null)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                <X className="w-5 h-5 text-gray-400" />
                              </button>
                            </div>
                          </div>

                          {/* Search & Filter Controls */}
                          {!hotelAlternatives.loading && hotelAlternatives.options?.length > 0 && (
                            <div className="px-5 pb-3 space-y-2">
                              {/* Search input */}
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  placeholder="Search hotels..."
                                  value={hotelSearchFilter}
                                  onChange={(e) => setHotelSearchFilter(e.target.value)}
                                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400"
                                  style={FONTS.body}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </div>
                              {/* Star rating + distance filters */}
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-xs text-gray-500" style={FONTS.body}>Stars:</span>
                                {[0, 3, 4, 5].map((stars) => (
                                  <button
                                    key={stars}
                                    onClick={(e) => { e.stopPropagation(); setHotelStarFilter(stars); }}
                                    className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
                                      hotelStarFilter === stars
                                        ? 'bg-[#a7d8db] text-[#313143]'
                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                    style={FONTS.body}
                                  >
                                    {stars === 0 ? 'All' : `${stars}★+`}
                                  </button>
                                ))}
                                <div className="flex-1" />
                                <button
                                  onClick={(e) => { e.stopPropagation(); setShowManualHotelEntry(!showManualHotelEntry); }}
                                  className={`px-2.5 py-1 text-xs rounded-lg transition-colors ${
                                    showManualHotelEntry
                                      ? 'bg-amber-500 text-white'
                                      : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                                  }`}
                                  style={FONTS.body}
                                >
                                  {showManualHotelEntry ? '✕ Cancel' : <><Pencil className="w-3.5 h-3.5 inline mr-1" /> Manual Entry</>}
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Manual Hotel/Airbnb Entry Form */}
                          {showManualHotelEntry && (
                            <div className="px-5 pb-4">
                              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 space-y-3">
                                <p className="text-sm font-medium text-amber-800" style={FONTS.headingSemibold}>
                                  🏠 Enter Airbnb, friend's house, or custom accommodation
                                </p>
                                <input
                                  type="text"
                                  placeholder="Name (e.g., Maria's Beach House, Airbnb Loft)"
                                  value={manualHotelName}
                                  onChange={(e) => setManualHotelName(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-amber-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 bg-white"
                                  style={FONTS.body}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <input
                                  type="text"
                                  placeholder="Full address (required)"
                                  value={manualHotelAddress}
                                  onChange={(e) => setManualHotelAddress(e.target.value)}
                                  className="w-full px-3 py-2 text-sm border border-amber-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-400/30 focus:border-amber-400 bg-white"
                                  style={FONTS.body}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <button
                                  disabled={!manualHotelName.trim() || !manualHotelAddress.trim()}
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    if (!manualHotelName.trim() || !manualHotelAddress.trim()) return;
                                    const oldHotelName = currentDayData.hotel.name;
                                    const validatedHotel = {
                                      name: manualHotelName.trim(),
                                      address: manualHotelAddress.trim(),
                                      pricePerNight: null,
                                      roomType: null,
                                      boardType: null,
                                      amenities: [],
                                      neighborhood: null,
                                      description: null,
                                      isManualEntry: true,
                                    };
                                    // Count how many days share this hotel
                                    const matchingDays = itinerary.days.filter(d => d.hotel && d.hotel.name === oldHotelName).length;
                                    let applyToAll = true;
                                    if (matchingDays > 1) {
                                      applyToAll = await showConfirmModal({
                                        title: 'Change Hotel Scope',
                                        message: `"${oldHotelName}" is used for ${matchingDays} nights. Apply change to all ${matchingDays} nights, or just tonight?`,
                                        confirmText: `All ${matchingDays} nights`,
                                        cancelText: 'Just tonight',
                                      });
                                    }

                                    // Update days with proper immutable state update
                                    const updatedItinerary = {
                                      ...itinerary,
                                      days: itinerary.days.map((day, idx) => {
                                        const isMatch = day.hotel && day.hotel.name === oldHotelName;
                                        if (applyToAll ? isMatch : idx === currentDay) {
                                          return { ...day, hotel: { ...validatedHotel } };
                                        }
                                        return day;
                                      })
                                    };
                                    const updatedDays = applyToAll ? matchingDays : 1;
                                    setItinerary(updatedItinerary);
                                    setHotelAlternatives(null);
                                    setShowManualHotelEntry(false);
                                    setManualHotelName('');
                                    setManualHotelAddress('');
                                    showToast(`Accommodation set to "${validatedHotel.name}" for ${updatedDays} night${updatedDays > 1 ? 's' : ''}`, 'success');
                                    // Offer to regenerate activities near the new location
                                    const regenerate = await showConfirmModal({
                                      title: 'Regenerate Activities',
                                      message: `Accommodation changed to "${validatedHotel.name}". Regenerate activities to be near this location?`,
                                      confirmText: 'Regenerate',
                                    });
                                    if (regenerate) {
                                      setShowLoadingScreen(true);
                                      setLoadingMessage('Regenerating day with new location...');
                                      try {
                                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                                          method: "POST",
                                          headers: await getApiHeaders(),
                                          body: JSON.stringify({
                                            model: "claude-sonnet-4-20250514",
                                            max_tokens: 3000,
                                            system: `Regenerate a day's itinerary keeping activities near the accommodation. Return ONLY valid JSON.`,
                                            messages: [{
                                              role: "user",
                                              content: `The user changed their accommodation to "${validatedHotel.name}" at ${validatedHotel.address}. Regenerate Day ${currentDay + 1} activities to be near this location. Keep the same general theme: "${currentDayData.theme}". Return JSON with schedule and meals arrays.`
                                            }]
                                          })
                                        });
                                        const data = await response.json();
                                        if (!data.content?.[0]?.text) throw new Error('Invalid API response');
                                        let text = data.content[0].text.trim().replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
                                        const updatedDay = JSON.parse(text);
                                        const newItinerary = { ...itinerary };
                                        newItinerary.days[currentDay].schedule = updatedDay.schedule;
                                        newItinerary.days[currentDay].meals = updatedDay.meals;
                                        setItinerary(newItinerary);
                                        setShowLoadingScreen(false);
                                        showToast('Day updated with activities near your accommodation!', 'success');
                                      } catch (error) {
                                        debugError('Error regenerating day:', error);
                                        setShowLoadingScreen(false);
                                        setHotelDetailsChanged(true);
                                        showToast('Accommodation updated! Click "Regenerate with arrivo" to update activities.', 'info', 5000);
                                      }
                                    } else {
                                      setHotelDetailsChanged(true);
                                    }
                                  }}
                                  className={`w-full py-2.5 rounded-xl text-sm font-medium transition-all ${
                                    manualHotelName.trim() && manualHotelAddress.trim()
                                      ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm'
                                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  }`}
                                  style={FONTS.headingSemibold}
                                >
                                  Set as Accommodation
                                </button>
                              </div>
                            </div>
                          )}

                          {/* Loading state */}
                          {hotelAlternatives.loading && (
                            <div className="px-5 pb-6 flex items-center gap-3">
                              <div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                              <span className="text-sm text-gray-500" style={FONTS.body}>Searching for hotels...</span>
                            </div>
                          )}

                          {/* Error state */}
                          {hotelAlternatives.error && !hotelAlternatives.loading && (
                            <div className="px-5 pb-6">
                              <p className="text-sm text-red-500" style={FONTS.body}>Could not load hotel options.</p>
                              <p className="text-xs text-gray-400 mt-1" style={FONTS.body}>{hotelAlternatives.error}</p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  fetchHotelAlternatives(currentDay);
                                }}
                                className="mt-3 px-4 py-2 bg-teal-50 text-teal-700 rounded-xl text-sm hover:bg-teal-100 transition-colors"
                                style={FONTS.body}
                              >
                                Retry
                              </button>
                            </div>
                          )}

                        {/* Hotel options list */}
                        {!hotelAlternatives.loading && !hotelAlternatives.error && hotelAlternatives.options?.length > 0 && (
                          <div className="pb-3">
                            {hotelAlternatives.options
                            .filter(hotel => {
                              // Filter by search term
                              if (hotelSearchFilter.trim()) {
                                const q = hotelSearchFilter.toLowerCase().trim();
                                const nameMatch = hotel.name?.toLowerCase().includes(q);
                                const roomMatch = hotel.roomType?.toLowerCase().includes(q);
                                if (!nameMatch && !roomMatch) return false;
                              }
                              // Filter by star rating
                              if (hotelStarFilter > 0 && (!hotel.rating || hotel.rating < hotelStarFilter)) return false;
                              return true;
                            })
                            .map((hotel, idx) => {
                              const isCurrentHotel = hotel.name?.toLowerCase().includes(currentDayData.hotel.name?.toLowerCase()) ||
                                currentDayData.hotel.name?.toLowerCase().includes(hotel.name?.toLowerCase());
                              return (
                                <div
                                  key={hotel.hotelId || idx}
                                  className={`flex items-center gap-3 px-5 py-3.5 cursor-pointer transition-colors border-t border-gray-100 ${
                                    isCurrentHotel ? 'bg-teal-50' : 'hover:bg-white'
                                  }`}
                                  onClick={async () => {
                                    if (isCurrentHotel) return; // Already selected

                                    const oldHotelName = currentDayData.hotel.name;
                                    const newHotelName = hotel.name;

                                    // Validate with Google Places to get address
                                    const city = currentDayData.baseCity || selectedDestination?.name || itinerary?.destination?.split(',')[0];
                                    setShowLoadingScreen(true);
                                    setLoadingMessage('Verifying hotel location...');

                                    let validatedHotel = {
                                      name: newHotelName,
                                      address: null,
                                      placeId: null,
                                      rating: hotel.rating,
                                      pricePerNight: hotel.pricePerNight || hotel.price,
                                      priceCurrency: hotel.currency || null, // Actual Amadeus currency
                                      amadeusVerified: true,
                                      roomType: hotel.roomType,
                                      roomDescription: hotel.roomDescription,
                                      boardType: hotel.boardType,
                                      bookingUrl: hotel.bookingUrl,
                                    };

                                    try {
                                      const validation = await validateHotel(newHotelName, city);
                                      if (validation.isValid) {
                                        validatedHotel.name = validation.correctedName || newHotelName;
                                        validatedHotel.address = validation.correctedAddress;
                                        validatedHotel.placeId = validation.placeId;
                                      }
                                    } catch (err) {
                                      debugError('Hotel validation error:', err);
                                    }

                                    // If no address from Google, construct a readable address from Amadeus data
                                    if (!validatedHotel.address) {
                                      if (hotel.latitude && hotel.longitude) {
                                        // Use hotel name + city as display address, coordinates for Maps link
                                        const city = currentDayData.baseCity || selectedDestination?.name || '';
                                        validatedHotel.address = city ? `${newHotelName}, ${city}` : newHotelName;
                                        validatedHotel.coordinates = { lat: hotel.latitude, lng: hotel.longitude };
                                      }
                                    }

                                    setShowLoadingScreen(false);

                                    // Count how many days share this hotel
                                    const matchingDays = itinerary.days.filter(d => d.hotel && d.hotel.name === oldHotelName).length;
                                    let applyToAll = true;
                                    if (matchingDays > 1) {
                                      applyToAll = await showConfirmModal({
                                        title: 'Change Hotel Scope',
                                        message: `"${oldHotelName}" is used for ${matchingDays} nights. Apply change to all ${matchingDays} nights, or just tonight?`,
                                        confirmText: `All ${matchingDays} nights`,
                                        cancelText: 'Just tonight',
                                      });
                                    }

                                    // Update days with proper immutable state update
                                    const updatedItinerary = {
                                      ...itinerary,
                                      days: itinerary.days.map((day, idx) => {
                                        const isMatch = day.hotel && day.hotel.name === oldHotelName;
                                        if (applyToAll ? isMatch : idx === currentDay) {
                                          return { ...day, hotel: { ...day.hotel, ...validatedHotel } };
                                        }
                                        return day;
                                      })
                                    };
                                    const updatedDays = applyToAll ? matchingDays : 1;
                                    setItinerary(updatedItinerary);
                                    setHotelAlternatives(null);

                                    showToast(`Hotel updated to "${validatedHotel.name}" for ${updatedDays} night${updatedDays > 1 ? 's' : ''}`, 'success');

                                    // Offer to regenerate activities near the new hotel
                                    const regenerate = await showConfirmModal({
                                      title: 'Regenerate Activities',
                                      message: `Hotel changed to "${validatedHotel.name}". Regenerate this day's activities to be near the new hotel?`,
                                      confirmText: 'Regenerate',
                                    });
                                    if (regenerate) {
                                      setShowLoadingScreen(true);
                                      setLoadingMessage('Regenerating day with new hotel location...');
                                      try {
                                        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/chat`, {
                                          method: "POST",
                                          headers: await getApiHeaders(),
                                          body: JSON.stringify({
                                            model: "claude-sonnet-4-20250514",
                                            max_tokens: 3000,
                                            system: `Regenerate a day's itinerary keeping activities near the hotel. Return ONLY valid JSON.`,
                                            messages: [{
                                              role: "user",
                                              content: `The user changed their hotel to "${validatedHotel.name}"${validatedHotel.address ? ` at ${validatedHotel.address}` : ''}. Regenerate Day ${currentDay + 1} activities to be near this hotel. Keep the same general theme: "${currentDayData.theme}". Return JSON with schedule and meals arrays.`
                                            }]
                                          })
                                        });
                                        const data = await response.json();
                                        if (!data.content?.[0]?.text) throw new Error('Invalid API response');
                                        let text = data.content[0].text.trim().replace(/```json\n?/g, "").replace(/```\n?/g, "").trim();
                                        const updatedDay = JSON.parse(text);
                                        const newItinerary = { ...itinerary };
                                        newItinerary.days[currentDay].schedule = updatedDay.schedule;
                                        newItinerary.days[currentDay].meals = updatedDay.meals;
                                        newItinerary.days[currentDay].hotel = { ...newItinerary.days[currentDay].hotel, ...validatedHotel };
                                        setItinerary(newItinerary);
                                        setShowLoadingScreen(false);
                                        showToast('Day updated with activities near your new hotel!', 'success');
                                      } catch (error) {
                                        debugError('Error regenerating day:', error);
                                        setShowLoadingScreen(false);
                                        setHotelDetailsChanged(true);
                                        showToast('Hotel updated! Click "Regenerate with arrivo" to update activities.', 'info', 5000);
                                      }
                                    } else {
                                      setHotelDetailsChanged(true);
                                    }
                                  }}
                                >
                                  {/* Selection indicator */}
                                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                                    isCurrentHotel ? 'bg-teal-500' : 'bg-gray-300'
                                  }`} />
                                  {/* Hotel info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="text-sm font-semibold text-gray-800 truncate" style={FONTS.headingSemibold}>{hotel.name}</span>
                                      {hotel.rating && (
                                        <span className="text-xs text-yellow-500 flex-shrink-0">{'⭐'.repeat(Math.min(hotel.rating, 5))}</span>
                                      )}
                                      {isCurrentHotel && (
                                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-medium flex-shrink-0" style={FONTS.body}>Selected</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-0.5">
                                      {hotel.roomType && (
                                        <span className="text-xs text-gray-500" style={FONTS.body}>{hotel.roomType}</span>
                                      )}
                                      {hotel.roomType && (hotel.boardType || hotel.roomDescription) && (
                                        <span className="text-xs text-gray-300">·</span>
                                      )}
                                      {(hotel.boardType || hotel.roomDescription) && (
                                        <span className="text-xs text-gray-500" style={FONTS.body}>
                                          {hotel.boardType === 'ROOM_ONLY' ? 'Room only' :
                                           hotel.boardType === 'BREAKFAST' ? 'Breakfast included' :
                                           hotel.boardType === 'HALF_BOARD' ? 'Half board' :
                                           hotel.boardType === 'FULL_BOARD' ? 'Full board' :
                                           hotel.boardType === 'ALL_INCLUSIVE' ? 'All inclusive' :
                                           hotel.roomDescription?.slice(0, 30) || hotel.boardType || ''}
                                        </span>
                                      )}
                                      {hotel.distanceKm != null && (
                                        <>
                                          <span className="text-xs text-gray-300">·</span>
                                          <span className="text-xs text-gray-500" style={FONTS.body}>
                                            {hotel.distanceKm < 1 ? `${Math.round(hotel.distanceKm * 1000)}m` : `${hotel.distanceKm.toFixed(1)}km`} from center
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {/* Price */}
                                  {hotel.pricePerNight ? (
                                    <div className="text-right flex-shrink-0">
                                      <span className="text-sm font-bold text-teal-700" style={FONTS.headingBold}>
                                        {formatLocalPrice(hotel.pricePerNight)}
                                      </span>
                                      <span className="text-xs text-gray-400">/n</span>
                                    </div>
                                  ) : hotel.price ? (
                                    <div className="text-right flex-shrink-0">
                                      <span className="text-sm font-bold text-teal-700" style={FONTS.headingBold}>
                                        {formatLocalPrice(hotel.price / (hotelAlternatives.nights || 1))}
                                      </span>
                                      <span className="text-xs text-gray-400">/n</span>
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}

                            {/* No matching results after filtering */}
                            {hotelAlternatives.options.filter(hotel => {
                              if (hotelSearchFilter.trim()) {
                                const q = hotelSearchFilter.toLowerCase().trim();
                                if (!hotel.name?.toLowerCase().includes(q) && !hotel.roomType?.toLowerCase().includes(q)) return false;
                              }
                              if (hotelStarFilter > 0 && (!hotel.rating || hotel.rating < hotelStarFilter)) return false;
                              return true;
                            }).length === 0 && (
                              <div className="px-5 py-4 text-center">
                                <p className="text-sm text-gray-500" style={FONTS.body}>No hotels match your filters</p>
                                {hotelStarFilter > 0 && (
                                  <p className="text-xs text-gray-400 mt-1" style={FONTS.body}>Star ratings may not be available for all hotels in this area</p>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); setHotelSearchFilter(''); setHotelStarFilter(0); }}
                                  className="mt-2 text-xs text-teal-600 hover:text-teal-700"
                                  style={FONTS.body}
                                >
                                  Clear filters
                                </button>
                              </div>
                            )}

                            {/* Footer */}
                            <div className="px-5 pt-3 pb-4 border-t border-gray-100">
                              <p className="text-xs text-gray-400 text-center" style={FONTS.body}>
                                {hotelAlternatives.options.length} hotels from Amadeus · Tap to select · Or use Manual Entry
                              </p>
                            </div>
                          </div>
                        )}

                          {/* No results state */}
                          {!hotelAlternatives.loading && !hotelAlternatives.error && hotelAlternatives.options?.length === 0 && (
                            <div className="px-5 pb-6">
                              <p className="text-sm text-gray-500" style={FONTS.body}>No hotel options found for these dates. Try adjusting your trip dates.</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                )}
              </div>
            ) : currentDay === (Math.max(totalItineraryDays, itinerary.days?.length || 0)) - 1 ? (
              /* Departure Day - Show a simple departure info card instead of hotel */
              <div className="flex-1 p-4 bg-white rounded-2xl shadow-sm border-2 border-orange-100 flex flex-col" style={{ minHeight: '80px' }}>
                <div className="flex items-start gap-3 flex-1">
                  <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-xl">🏠</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800" style={FONTS.headingSemibold}>Departure Day</h3>
                    <p className="text-sm text-gray-600 mt-1" style={FONTS.body}>
                      {effectiveReturnMode === 'fly'
                        ? `Flight to ${userProfile.preferences.returnCity || userProfile.preferences.homeCity || 'home'}${userProfile.preferences.returnCity && userProfile.preferences.returnCity !== userProfile.preferences.homeCity ? ' (different return)' : ''}`
                        : effectiveReturnMode === 'train'
                        ? `Train to ${userProfile.preferences.homeCity || 'home'}`
                        : effectiveReturnMode === 'ferry'
                        ? `Ferry to ${userProfile.preferences.homeCity || 'home'}`
                        : effectiveReturnMode === 'custom'
                        ? `Departure to ${userProfile.preferences.homeCity || 'home'}`
                        : `Drive back to ${userProfile.preferences.homeCity || 'home'}`
                      }
                    </p>
                    <p className="text-sm text-orange-600 mt-2" style={FONTS.body}>
                      {effectiveReturnMode === 'fly'
                        ? `✈️ Departure: ${departureFlight.time}`
                        : effectiveReturnMode === 'train'
                        ? `🚆 Departure: ${driveDetails.returnDepartureTime}`
                        : effectiveReturnMode === 'ferry'
                        ? `⛴️ Departure: ${driveDetails.returnDepartureTime}`
                        : effectiveReturnMode === 'custom'
                        ? `🚌 Departure: ${driveDetails.returnDepartureTime}`
                        : `🚗 Leave hotel: ${driveDetails.returnDepartureTime}`
                      }
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
            
            {/* Right Side - View Controls & Action Buttons */}
            <div className="flex flex-col gap-2 ml-auto" style={{ minWidth: '400px', maxWidth: '400px' }}>
              {/* View Toggle Row - Cards, Timeline, Map */}
              <div className="flex gap-2 flex-1">
                <button
                  onClick={() => {
                    setShowGanttView(false);
                    setShowMapView(false);
                  }}
                  className={`flex-1 px-4 py-4 rounded-2xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    !showGanttView && !showMapView ? 'bg-brand-600 text-white shadow-md' : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-brand-300'
                  }`}
                  style={FONTS.body}
                >
                  <BrandCards size={16} color={!showGanttView && !showMapView ? '#ffffff' : '#1d8e85'} /> Cards
                </button>
                <button
                  onClick={() => {
                    setShowGanttView(true);
                    setShowMapView(false);
                  }}
                  className={`flex-1 px-4 py-4 rounded-2xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    showGanttView && !showMapView ? 'bg-brand-600 text-white shadow-md' : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-brand-300'
                  }`}
                  style={FONTS.body}
                >
                  <BrandClock size={16} color={showGanttView && !showMapView ? '#ffffff' : '#1d8e85'} /> Timeline
                </button>
                <button
                  onClick={() => {
                    setShowGanttView(false);
                    setShowMapView(true);
                  }}
                  className={`flex-1 px-4 py-4 rounded-2xl font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                    showMapView ? 'bg-brand-600 text-white shadow-md' : 'bg-white border-2 border-gray-200 text-gray-600 hover:border-brand-300'
                  }`}
                  style={FONTS.body}
                >
                  <BrandMap size={16} color={showMapView ? '#ffffff' : '#1d8e85'} /> Map
                </button>
              </div>

              {/* Action buttons removed — recalculate times + optimize route were confusing to users */}
            </div>
          </div>
          
          {/* Failed-day banner — inline Regenerate Day button when the LLM
              dropped this specific day (usually a mid-trip Cerebras timeout).
              Avoids forcing the user to regenerate the whole trip just to fix
              one broken day. Calls POST /api/regenerate-day. */}
          {currentDayData?._generationFailed && (
            <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-300 flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-amber-900 font-semibold text-sm">
                  ⚠️ Day {currentDay + 1} didn't finish generating
                </div>
                {currentDayData._error && (
                  <div className="text-xs text-amber-800 mt-1 font-mono truncate">
                    {String(currentDayData._error).slice(0, 120)}
                  </div>
                )}
              </div>
              <button
                type="button"
                disabled={isRegeneratingDay === currentDay + 1 || !itinerary?.id}
                onClick={async () => {
                  if (!itinerary?.id) {
                    showAlertModal('Save the trip first', 'Save your trip, then retry regenerating this day.');
                    return;
                  }
                  setIsRegeneratingDay(currentDay + 1);
                  try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001';
                    const headers = await getApiHeaders();
                    const resp = await fetch(`${backendUrl}/api/regenerate-day`, {
                      method: 'POST',
                      headers,
                      body: JSON.stringify({ itineraryId: itinerary.id, dayNumber: currentDay + 1 }),
                    });
                    const data = await resp.json();
                    if (!data?.success || !data.day) {
                      showAlertModal('Regeneration Failed', data?.error || 'Please try again.');
                      return;
                    }
                    setItinerary(prev => {
                      if (!prev || !Array.isArray(prev.days)) return prev;
                      const newDays = [...prev.days];
                      newDays[currentDay] = { ...newDays[currentDay], ...data.day, _generationFailed: false };
                      return { ...prev, days: newDays };
                    });
                  } catch (err) {
                    showAlertModal('Regeneration Failed', err.message || 'Network error');
                  } finally {
                    setIsRegeneratingDay(null);
                  }
                }}
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isRegeneratingDay === currentDay + 1 ? 'Regenerating…' : 'Regenerate Day'}
              </button>
            </div>
          )}

          {/* Smart Warnings + Weather */}
          <div className="flex gap-2 mb-6 flex-wrap items-center">
            {/* Weather Widget */}
            {currentDayData && (() => {
              // Get coordinates from hotel or first activity with placeData
              const hotelCoords = currentDayData.hotel?.coordinates;
              const firstActCoords = currentDayData.schedule?.find(a => a.placeData?.location)?.placeData?.location;
              const lat = hotelCoords?.lat || firstActCoords?.lat;
              const lng = hotelCoords?.lng || firstActCoords?.lng;
              // Calculate date for this day
              const startDate = itinerary?.startDate || itinerary?.dates?.start;
              let dayDate = null;
              if (startDate) {
                const d = new Date(startDate);
                d.setDate(d.getDate() + currentDay);
                dayDate = d.toISOString().split('T')[0];
              }
              if (lat && lng && dayDate) {
                return (
                  <WeatherWidget
                    lat={lat}
                    lng={lng}
                    date={dayDate}
                    cityName={currentDayData.baseCity}
                  />
                );
              }
              return null;
            })()}
            {/* "⚠️ Packed Day" badge removed 2026-06 per product decision:
                judgmental UI was a self-contradiction risk and added no value
                the user couldn't read off the schedule itself. */}

            {hasLongTransit(currentDayData) && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-xs" style={FONTS.body}>
                🚗 Long Transit (30+ min)
              </span>
            )}
            
            {getTotalTravelTime(currentDayData) > 0 && (
              <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs" style={FONTS.body}>
                ⏱️ {Math.floor(getTotalTravelTime(currentDayData) / 60)}h {getTotalTravelTime(currentDayData) % 60}m travel
              </span>
            )}
          </div>

          {/* Generate full timeline with all logistics — memoized to avoid excessive re-renders */}
          {(() => {
            let fullTimeline = [];
            try {
              // createDayTimeline is expensive (~3800 lines). It was rebuilding 28+ times per generation.
              // The result is now cached via a ref-based memo keyed on day data + transit cache version.
              // `_cacheBust` is bumped by in-place edit handlers (updateItemTime,
              // updateItemCost, deleteItem, etc.) so the memo invalidates even
              // when schedule/meals lengths don't change. Mirrors mobile pattern.
              const cacheKey = `${safeCurrentDay}-${currentDayData?.schedule?.length || 0}-${currentDayData?.meals?.length || 0}-${Object.keys(transitTimes || {}).filter(k => k.startsWith('day' + safeCurrentDay)).length}-${currentDayData?._cacheBust || 0}`;
              if (window._timelineCacheKey === cacheKey && window._timelineCacheResult) {
                fullTimeline = window._timelineCacheResult;
              } else {
                fullTimeline = createDayTimeline(
                  currentDayData,
                  currentDay === 0,
                  currentDay === (Math.max(totalItineraryDays, itinerary.days?.length || 0)) - 1
                );
                window._timelineCacheKey = cacheKey;
                window._timelineCacheResult = fullTimeline;
              }
            } catch (error) {
              debugError('❌ Error in createDayTimeline:', error);
              debugError('Day data:', currentDayData);
              debugError('CurrentDay:', currentDay);
              // Return a minimal timeline to prevent crash - include both activities and meals
              const activities = (currentDayData?.schedule || []).map((act, idx) => ({
                ...act,
                type: 'activity',
                sortTime: parseTime(act.time) || (idx * 60 + 540) // Use actual time or fallback
              }));
              const meals = (currentDayData?.meals || []).map((meal, idx) => ({
                ...meal,
                type: 'meal',
                sortTime: parseTime(meal.time) || (idx * 120 + 480) // Use actual time or fallback (8am, 10am, 12pm...)
              }));
              fullTimeline = [...activities, ...meals].sort((a, b) => a.sortTime - b.sortTime);
            }

            return (
              <>
                {/* Gantt Chart View */}
                {showGanttView && !showMapView && <GanttChart timeline={fullTimeline} />}

                {/* Map View — Interactive Leaflet map with markers and routes */}
                {showMapView && !showGanttView && (
                  <div className="bg-white rounded-xl p-6 shadow-lg mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg text-gray-800 flex items-center gap-2" style={FONTS.headingSemibold}><MapIcon className="w-5 h-5 text-teal-600" /> Day Route Map</h3>
                      <a
                        href={generateMapWithRoute(currentDayData)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg text-sm hover:bg-blue-600 transition-colors"
                        style={FONTS.body}
                      >
                        Open in Google Maps
                      </a>
                    </div>
                    
                    {/* Interactive Leaflet Map */}
                    <div className="w-full h-[500px] rounded-lg overflow-hidden border-2 border-gray-200">
                      <ItineraryMap
                        dayData={currentDayData}
                        transitTimes={transitTimes}
                        selectedCardId={mapSelectedCardId}
                        onMarkerClick={(marker) => {
                          setMapSelectedCardId(marker.id);
                          // Scroll to corresponding card in timeline if it exists
                          const cardEl = document.getElementById(`timeline-card-${marker.id}`);
                          if (cardEl) cardEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }}
                        currentDay={currentDay}
                      />
                    </div>

                    {/* Legacy Google Maps iframe (hidden, kept for fallback) */}
                    <div className="w-full h-0 overflow-hidden" style={{ display: 'none' }}>
                      {(() => {
                        // Check if this is an inter-city travel day
                        const prevDayData = currentDay > 0 ? itinerary.days[currentDay - 1] : null;
                        const isInterCityDay = prevDayData &&
                          currentDayData.baseCity && prevDayData.baseCity &&
                          currentDayData.baseCity.toLowerCase() !== prevDayData.baseCity.toLowerCase();

                        // Check inter-city transport mode
                        const transportData = itinerary?.interCityTransport?.find(t => t.day === currentDay + 1);
                        const transportMode = transportData?.mode || (isInterCityDay ? 'flight' : null);
                        const isFlightDay = transportMode === 'flight';
                        const isTrainDay = transportMode === 'train';
                        const isBusDay = transportMode === 'bus';

                        // For inter-city travel days (any mode), show a place map centered on the new city
                        if (isInterCityDay) {
                          const apiKey = GOOGLE_MAPS_EMBED_KEY;
                          if (!apiKey) {
                            return <div className="flex items-center justify-center h-full bg-gray-100 text-gray-500">Map unavailable — API key not configured</div>;
                          }
                          // More specific query: hotel name + city for better accuracy
                          const placeQuery = currentDayData.hotel?.address ||
                            (currentDayData.hotel?.name && currentDayData.baseCity
                              ? `${currentDayData.hotel.name}, ${currentDayData.baseCity}`
                              : currentDayData.baseCity || itinerary.destination);
                          const placeMapUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(placeQuery)}`;
                          const modeEmoji = isFlightDay ? '✈️' : isTrainDay ? '🚆' : isBusDay ? '🚌' : '🚗';
                          const modeLabel = isFlightDay ? 'Flight' : isTrainDay ? 'Train' : isBusDay ? 'Bus' : 'Travel';
                          return (
                            <>
                              <div className="absolute top-4 left-4 bg-blue-100 text-blue-800 px-3 py-2 rounded-lg shadow-md z-10" style={{ position: 'relative', marginBottom: '10px' }}>
                                <ActivityIcon emoji={modeEmoji} size={14} /> Travel Day: {modeLabel} from {prevDayData.baseCity} to {currentDayData.baseCity}
                              </div>
                              <iframe
                                width="100%"
                                height="100%"
                                frameBorder="0"
                                style={{ border: 0 }}
                                src={placeMapUrl}
                                allowFullScreen
                              />
                            </>
                          );
                        }

                        // Collect all locations for the day's activities and meals
                        const sortedStops = [];

                        // For Day 1 with flight arrival, start from the airport
                        const isFirstDay = currentDay === 0;
                        const isLastDay = currentDay === (Math.max(totalItineraryDays, itinerary.days?.length || 0)) - 1 || currentDay === (itinerary.days?.length || 0) - 1;

                        // Get hotel name/address for filtering (to avoid adding hotel as a waypoint)
                        const hotelName = currentDayData.hotel?.name?.toLowerCase() || '';
                        const hotelAddress = currentDayData.hotel?.address?.toLowerCase() || '';

                        // Helper to check if a location is at the hotel
                        const isAtHotel = (location, activityName, restaurantName, address) => {
                          return isHotelActivityValidator(location, activityName || '', restaurantName || '', currentDayData.hotel?.name || '', currentDayData?.baseCity || '', address || '');
                        };

                        // Determine start point
                        if (isFirstDay && transportationMode === 'fly' && itinerary.arrivalAirport) {
                          sortedStops.push({
                            time: arrivalFlight.time || '08:00',
                            name: `${itinerary.arrivalAirport.code} Airport`,
                            isAirport: true
                          });
                        } else if (currentDayData.hotel) {
                          sortedStops.push({
                            time: '00:00',
                            name: currentDayData.hotel.address || currentDayData.hotel.name,
                            isHotel: true
                          });
                        }

                        // Add activities (check both location and address properties)
                        // Skip activities that are at the hotel (these don't need routing)
                        if (currentDayData.schedule) {
                          currentDayData.schedule.forEach(act => {
                            const location = act.location || act.address;
                            // Fallback: use activity name + base city if no explicit location
                            const fallbackLocation = !location && act.activity && currentDayData.baseCity
                              ? `${act.activity}, ${currentDayData.baseCity}`
                              : null;
                            const mapLocation = location || fallbackLocation;
                            if (mapLocation && !isAtHotel(mapLocation, act.activity, null, act.address || '')) {
                              sortedStops.push({ time: act.time, name: mapLocation, activity: act.activity });
                            }
                          });
                        }

                        // Add meals (check both restaurant and address properties)
                        // Skip meals that are at the hotel
                        if (currentDayData.meals) {
                          currentDayData.meals.forEach(meal => {
                            const location = meal.address || meal.restaurant;
                            // Fallback: use restaurant name + base city if no explicit address
                            const fallbackLocation = !location && meal.restaurant && currentDayData.baseCity
                              ? `${meal.restaurant}, ${currentDayData.baseCity}`
                              : null;
                            const mapLocation = location || fallbackLocation;
                            if (mapLocation && !isAtHotel(mapLocation, null, meal.restaurant, meal.address || '')) {
                              sortedStops.push({ time: meal.time, name: mapLocation, meal: meal.restaurant });
                            }
                          });
                        }

                        // Determine end point
                        if (isLastDay && effectiveReturnMode === 'fly' && itinerary.departureAirport) {
                          sortedStops.push({
                            time: departureFlight.time || '18:00',
                            name: `${itinerary.departureAirport.code} Airport`,
                            isAirport: true
                          });
                        } else if (currentDayData.hotel) {
                          sortedStops.push({
                            time: '23:59',
                            name: currentDayData.hotel.address || currentDayData.hotel.name,
                            isHotel: true
                          });
                        }

                        sortedStops.sort((a, b) => parseTime(a.time) - parseTime(b.time));

                        // Remove duplicate consecutive stops (same location appearing multiple times)
                        const uniqueStops = sortedStops.filter((stop, idx) => {
                          if (idx === 0) return true;
                          const prevStop = sortedStops[idx - 1];
                          // Consider same if names are identical or very similar
                          const currName = stop.name.toLowerCase().trim();
                          const prevName = prevStop.name.toLowerCase().trim();
                          return currName !== prevName;
                        });

                        // Debug logging (only in development)
                        debugLog('Map stops (original):', sortedStops);
                        debugLog('Map stops (unique):', uniqueStops);
                        debugLog('Schedule:', currentDayData.schedule);
                        debugLog('Meals:', currentDayData.meals);

                        if (uniqueStops.length < 2) {
                          // BUG 7 FIX: Show place-mode map centered on hotel for resort/lodge days
                          const hotelQuery = currentDayData.hotel?.address
                            || (currentDayData.hotel?.name && currentDayData.baseCity
                              ? `${currentDayData.hotel.name}, ${currentDayData.baseCity}`
                              : currentDayData.baseCity || itinerary.destination);
                          const apiKey = GOOGLE_MAPS_EMBED_KEY;
                          if (!apiKey || !hotelQuery) {
                            return <div className="flex items-center justify-center h-full bg-gray-100 text-gray-500">
                              Map unavailable
                            </div>;
                          }
                          const placeUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(hotelQuery)}&zoom=14`;
                          return (
                            <iframe
                              title="Hotel Location"
                              width="100%"
                              height="100%"
                              style={{ border: 0, borderRadius: '12px' }}
                              src={placeUrl}
                              allowFullScreen
                            />
                          );
                        }

                        // For hotel-first trips, use the hotel address as the location context
                        // Otherwise use the destination city
                        // Be more specific with context to avoid Google matching wrong locations
                        const baseCity = currentDayData.baseCity || '';
                        const destParts = (itinerary.destination || '').split(',');
                        const country = destParts.length > 1 ? destParts[destParts.length - 1].trim() : '';
                        const region = destParts.length > 0 ? destParts[0].trim() : '';

                        // Build a robust location context - prioritize baseCity, then region, then country
                        let locationContext = '';
                        if (tripType === 'hotel' && currentDayData.hotel?.address) {
                          // Extract city/region from hotel address
                          const hotelParts = currentDayData.hotel.address.split(',');
                          locationContext = hotelParts.slice(-2).join(',').trim();
                        } else if (baseCity) {
                          locationContext = country ? `${baseCity}, ${country}` : baseCity;
                        } else if (region) {
                          locationContext = country ? `${region}, ${country}` : region;
                        } else {
                          locationContext = itinerary.destination || '';
                        }

                        // Helper to add context to a location name if it doesn't already have country info
                        const addContext = (name) => {
                          if (!name) return name;
                          // If already has country-level context (contains comma with country-like text), don't add more
                          if (name.includes(',') && name.split(',').length >= 2) {
                            return name;
                          }
                          return `${name}, ${locationContext}`;
                        };

                        // Build origin and destination with proper context
                        const origin = uniqueStops[0].isAirport
                          ? `${uniqueStops[0].name}, ${itinerary.arrivalAirport?.city || locationContext}`
                          : addContext(uniqueStops[0].name);

                        const lastStop = uniqueStops[uniqueStops.length - 1];
                        const destination = lastStop.isAirport
                          ? `${lastStop.name}, ${itinerary.departureAirport?.city || locationContext}`
                          : addContext(lastStop.name);

                        // Build waypoints (middle stops only)
                        let waypointsParam = '';
                        if (uniqueStops.length > 2) {
                          const middleStops = uniqueStops.slice(1, -1);
                          const waypoints = middleStops.map(stop => addContext(stop.name)).join('|');
                          waypointsParam = `&waypoints=${encodeURIComponent(waypoints)}`;
                        }
                        
                        const apiKey = GOOGLE_MAPS_EMBED_KEY;
                        if (!apiKey) {
                          return <div className="flex items-center justify-center h-full bg-gray-100 text-gray-500">Map unavailable — API key not configured</div>;
                        }

                        // Determine if we should use directions or place mode
                        // If all stops are in the same city, use directions; otherwise fall back to place view centered on the base city
                        const mapBaseCity = baseCity || itinerary.destination || '';
                        const allStopsLocal = uniqueStops.every(stop => {
                          const stopName = (stop.name || '').toLowerCase();
                          const baseCityLower = mapBaseCity.toLowerCase().split(',')[0].trim();
                          // Check if stop location contains city name or doesn't have contradicting city info
                          return !stopName.includes(',') || stopName.includes(baseCityLower) || stop.isHotel || stop.isAirport;
                        });

                        let mapUrl;
                        if (allStopsLocal || uniqueStops.length <= 3) {
                          // Normal directions mode
                          mapUrl = `https://www.google.com/maps/embed/v1/directions?key=${apiKey}&origin=${encodeURIComponent(origin)}&destination=${encodeURIComponent(destination)}${waypointsParam}`;
                        } else {
                          // Locations seem scattered (cross-city issue) — fall back to place mode centered on hotel/city
                          const centerLocation = currentDayData.hotel?.address || currentDayData.hotel?.name || mapBaseCity;
                          mapUrl = `https://www.google.com/maps/embed/v1/place?key=${apiKey}&q=${encodeURIComponent(centerLocation)}&zoom=13`;
                        }

                        return (
                          <iframe
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            style={{ border: 0 }}
                            src={mapUrl}
                            allowFullScreen
                          />
                        );
                      })()}
                    </div>

                    {/* Location List with Numbers */}
                    <div className="mt-4 space-y-2">
                      <h4 className="text-gray-700 mb-2" style={FONTS.headingSemibold}>Route Stops:</h4>
                      {(() => {
                        const stops = [];
                        if (currentDayData.schedule) {
                          currentDayData.schedule.forEach((act, idx) => {
                            if (act.location || act.activity) {
                              stops.push({
                                time: act.time,
                                name: act.location || act.activity,
                                activity: act.activity,
                                type: 'activity',
                                hasCoords: !!(act.placeData?.location?.lat || act.coordinates?.lat),
                              });
                            }
                          });
                        }
                        if (currentDayData.meals) {
                          currentDayData.meals.forEach((meal, idx) => {
                            if (meal.restaurant) {
                              stops.push({
                                time: meal.time,
                                name: meal.restaurant,
                                activity: meal.restaurant,
                                type: 'meal',
                                hasCoords: !!(meal.placeData?.location?.lat || meal.coordinates?.lat),
                              });
                            }
                          });
                        }
                        stops.sort((a, b) => parseTime(a.time) - parseTime(b.time));
                        // Number sequentially (matching the map marker numbers)
                        stops.forEach((stop, i) => { stop.num = i + 1; });
                        
                        return stops.map((stop, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm ${
                              stop.type === 'meal' ? 'bg-orange-500' : 'bg-teal-500'
                            }`} style={FONTS.headingBold}>
                              {stop.num}
                            </div>
                            <div className="flex-1">
                              <p className="text-gray-800" style={FONTS.body}>{stop.name}</p>
                              <p className="text-xs text-gray-500" style={FONTS.body}>{formatTimeDisplay(stop.time)} • {stop.activity}</p>
                            </div>
                            {stop.type === 'meal' && <Utensils className="w-4 h-4 text-orange-500" />}
                            {stop.type === 'activity' && <Activity className="w-4 h-4 text-teal-500" />}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* Beautiful Editable Itinerary View - Default Cards View */}
                {!showGanttView && !showMapView && (
                  <div className="timeline mb-8 relative pr-4">
                    {/* Check for empty day (no activities/meals) */}
                    {(!fullTimeline || fullTimeline.length === 0 || 
                      fullTimeline.every(item => ['hotel-checkin', 'checkout', 'arrival-customs', 'arrival-domestic'].includes(item.type))) && (
                      <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-white rounded-3xl border-2 border-dashed border-gray-200">
                        <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center">
                          <span className="text-4xl">🌴</span>
                        </div>
                        <h3 className="text-2xl text-gray-700 mb-2" style={FONTS.headingBold}>Free Day!</h3>
                        <p className="text-gray-500 mb-6 max-w-md mx-auto" style={FONTS.body}>
                          No activities planned for this day - perfect for spontaneous exploration, relaxation, or following your own path.
                        </p>
                        <div className="flex gap-3 justify-center flex-wrap">
                          <button
                            onClick={() => {
                              setItineraryChatInput('Suggest activities for this free day');
                              setShowItineraryChat(true);
                            }}
                            className="px-5 py-2.5 bg-[#313143] text-white rounded-full hover:bg-[#3d3d52] hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2"
                            style={FONTS.body}
                          >
                            <Sparkles className="w-4 h-4" />
                            Get AI Suggestions
                          </button>
                          <button
                            onClick={() => {
                              setAddActivityDay(currentDay);
                              setShowAddActivityModal(true);
                            }}
                            className="px-5 py-2.5 bg-white border-2 border-[#a7d8db] text-[#313143] rounded-full hover:bg-[#f0f9fa] transition-all flex items-center gap-2"
                            style={FONTS.body}
                          >
                            <Plus className="w-4 h-4" />
                            Add Activity Manually
                          </button>
                        </div>
                        <p className="text-xs text-gray-400 mt-6" style={FONTS.body}>
                          Tip: Use the chat to ask for restaurant recommendations, local experiences, or hidden gems!
                        </p>
                      </div>
                    )}
                    
                    {/* Use fullTimeline which includes arrival logistics */}
                    {fullTimeline && fullTimeline.length > 0 && (
                    (() => {
                      // fullTimeline already has everything sorted and includes arrival cards
                      debugLog('Rendering timeline:', fullTimeline);

                      // Build sortable IDs for drag-and-drop (only activities and meals are draggable)
                      const sortableIds = fullTimeline
                        .filter(item => item.type === 'activity' || item.type === 'meal')
                        .map(item => `sortable-${item.type}-${item.originalIndex ?? 0}`);

                      // Detect time overlaps between consecutive schedule items
                      const overlapIndices = new Set();
                      for (let i = 0; i < fullTimeline.length - 1; i++) {
                        const curr = fullTimeline[i];
                        const next = fullTimeline[i + 1];
                        // Only check activities and meals for overlaps
                        if (!['activity', 'meal'].includes(curr.type) || !['activity', 'meal'].includes(next.type)) continue;
                        const currStart = parseTime(curr.time);
                        const currDur = parseDurationToMinutes(curr.duration) || 60;
                        const currEnd = currStart + currDur;
                        const nextStart = parseTime(next.time);
                        if (currEnd > nextStart + 5) { // 5 min buffer
                          overlapIndices.add(i);
                          overlapIndices.add(i + 1);
                        }
                      }
                      
                      return (
                      <DndContext
                        sensors={dndSensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDndDragStart}
                        onDragEnd={handleDndDragEnd}
                      >
                      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                      {fullTimeline.map((item, idx) => {
                        // Determine item properties based on type
                        const isActivity = item.type === 'activity';
                        const isMeal = item.type === 'meal';
                        const isArrival = item.type === 'arrival-customs' || item.type === 'arrival-domestic' || item.type === 'transit-arrival';
                        const isRoadTrip = item.type === 'road-trip' || item.type === 'road-trip-return';
                        const isRentalPickup = item.type === 'rental-pickup';
                        const isRentalReturn = item.type === 'rental-return';
                        const isHotelTransit = item.type === 'hotel-transit';
                        const isHotelCheckin = item.type === 'hotel-checkin';
                        const isCheckout = item.type === 'checkout';
                        const isAirportTransit = item.type === 'airport-transit';
                        const isStationTransit = item.type === 'station-transit';
                        const isPortTransit = item.type === 'port-transit';
                        const isAirportArrival = item.type === 'airport-arrival' || item.type === 'arrival-customs' || item.type === 'arrival-domestic';
                        const isDeparture = item.type === 'departure' || item.type === 'transit-departure';
                        const isTransit = item.type === 'transit';
                        const isReturnTransit = item.type === 'return-transit';
                        const isHotelReturn = item.type === 'hotel-return';
                        const isInterCityTravel = item.type === 'inter-city-travel';
                        const isLongDistanceTransfer = item.type === 'long-distance-transfer';
                        const isLongDistanceDeparture = item.type === 'long-distance-departure';
                        
                        // Render inter-city travel card (flight/travel between cities)
                        if (isInterCityTravel) {
                          // Extract from/to cities from the inter-city route
                          const fromCityRaw = item.location?.split('→')[0]?.trim() || '';
                          const toCityRaw = item.location?.split('→')[1]?.trim() || '';

                          // Dynamically resolve airport codes to city names using itinerary transport data
                          // Build a reverse lookup (code → city) from the itinerary's own transportInfo
                          const resolveCity = (name) => {
                            if (!name) return '';
                            const upper = name.toUpperCase().trim();
                            // Only try to resolve if it looks like an IATA code (2-4 uppercase letters)
                            if (!/^[A-Z]{2,4}$/.test(upper)) return name;
                            // 1. Check itinerary.transportInfo (keyed by city name, contains nearestAirport.code)
                            if (itinerary?.transportInfo) {
                              for (const [stopName, info] of Object.entries(itinerary.transportInfo)) {
                                if (info?.nearestAirport?.code?.toUpperCase() === upper) {
                                  return info.nearestAirport.city || stopName;
                                }
                                if (info?.alternateAirport?.code?.toUpperCase() === upper) {
                                  return info.alternateAirport.city || stopName;
                                }
                              }
                            }
                            // 2. Check the item's own from/to transport data
                            if (item.transportData?.from?.city && item.from?.toUpperCase().includes(upper)) return item.transportData.from.city;
                            if (item.transportData?.to?.city && item.to?.toUpperCase().includes(upper)) return item.transportData.to.city;
                            // 3. Check itinerary day baseCities — if any day's baseCity matches, it's not an airport code
                            const allBaseCities = itinerary?.days?.map(d => d?.baseCity).filter(Boolean) || [];
                            for (const bc of allBaseCities) {
                              if (bc.toUpperCase() === upper) return bc; // It's actually a city name that happens to be short
                            }
                            // 4. Fallback: return as-is (unknown code stays as code)
                            return name;
                          };
                          const fromCity = resolveCity(fromCityRaw);
                          const toCity = resolveCity(toCityRaw);

                          // Skip rendering if from and to are effectively the same city
                          // (e.g., IST → Istanbul, or Istanbul → Istanbul)
                          if (fromCity && toCity && fromCity.toLowerCase() === toCity.toLowerCase()) {
                            return null;
                          }

                          // Available modes for this route — filter out impossible modes (e.g., drive across oceans, to/from islands, very long drives)
                          const availableModes = (() => {
                            const modes = [];

                            // PRIORITY 1: Use dynamic Haiku transport data if available
                            // This is the most accurate — Haiku knows geography, ferry routes, rail networks etc.
                            const pairKey = `${fromCity}|${toCity}`.toLowerCase();
                            const haikuTransport = dynamicTransportModes[pairKey];
                            if (haikuTransport?.mode) {
                              const haikuMode = haikuTransport.mode;
                              const haikuMins = haikuTransport.durationMinutes || 0;
                              // Check if either city is an island — no car/train to/from islands (same as PRIORITY 2)
                              const fromIsIslandH = isIslandCity(fromCity);
                              const toIsIslandH = isIslandCity(toCity);
                              const involvesIslandH = fromIsIslandH || toIsIslandH;
                              // Only show fly if Haiku recommends it OR drive time is long (>3h)
                              const isShortDrive = haikuMins > 0 && haikuMins <= 180;
                              const fromCountryH = getCityCountrySync(fromCity);
                              const toCountryH = getCityCountrySync(toCity);
                              const sameCountryH = fromCountryH && toCountryH && fromCountryH === toCountryH;
                              if (haikuMode === 'flight' || involvesIslandH || (!sameCountryH && !isShortDrive) || haikuMins > 300) {
                                modes.push({ id: 'flight', emoji: '✈️', label: 'Fly' });
                              }
                              // Only add train/car if NOT an island route — train/car impossible between islands
                              if (!involvesIslandH) {
                                if (haikuMode === 'train' || (haikuMode !== 'flight' && haikuMins > 0 && haikuMins < 480)) {
                                  modes.push({ id: 'train', emoji: '🚆', label: 'Train' });
                                }
                                if (haikuMode === 'car' || (haikuMode !== 'flight' && haikuMins > 0 && haikuMins < 600)) {
                                  modes.push({ id: 'car', emoji: '🚗', label: 'Drive' });
                                }
                              }
                              // Ferry is valid for island routes (but not cross-ocean)
                              if (haikuMode === 'ferry') {
                                modes.push({ id: 'ferry', emoji: '⛴️', label: 'Ferry' });
                              } else if (involvesIslandH) {
                                // Add ferry as option for island routes if not cross-continental
                                const fromContinentH = getContinentForCountry(fromCountryH);
                                const toContinentH = getContinentForCountry(toCountryH);
                                const isCrossOceanH = fromContinentH && toContinentH && fromContinentH !== toContinentH;
                                if (!isCrossOceanH) {
                                  modes.push({ id: 'ferry', emoji: '⛴️', label: 'Ferry' });
                                }
                              }
                              // Ensure at least one mode exists
                              if (modes.length === 0) modes.push({ id: 'flight', emoji: '✈️', label: 'Fly' });
                              return modes;
                            }

                            // PRIORITY 2: Static fallback (used before Haiku responds)
                            let fromContinentIC = getContinentHintFromText(fromCity);
                            let toContinentIC = getContinentHintFromText(toCity);
                            // Fallback: use country→continent lookup when text heuristic fails
                            if (!fromContinentIC) {
                              const fromCountry = getCityCountrySync(fromCity);
                              if (fromCountry) fromContinentIC = getContinentForCountry(fromCountry);
                            }
                            if (!toContinentIC) {
                              const toCountry = getCityCountrySync(toCity);
                              if (toCountry) toContinentIC = getContinentForCountry(toCountry);
                            }
                            const isCrossOceanIC = fromContinentIC && toContinentIC && fromContinentIC !== toContinentIC;
                            const isSameCity = fromCity && toCity && fromCity.toLowerCase() === toCity.toLowerCase();
                            // Check if either city is an island — no car/train to/from islands
                            const fromIsIsland = isIslandCity(fromCity);
                            const toIsIsland = isIslandCity(toCity);
                            const involvesIsland = fromIsIsland || toIsIsland;
                            // Check if drive time is too long (>10h) — parse from item.duration or transportData
                            const durationStr = item.duration || item.transportData?.duration || '';
                            const durationHoursMatch = durationStr.match(/(\d+)\s*h/i);
                            const durationHours = durationHoursMatch ? parseInt(durationHoursMatch[1]) : 0;
                            const isTooFarToDrive = durationHours >= 10;
                            const isTooFarForTrain = durationHours >= 8;
                            // Check if same country with short drive — fly is unrealistic
                            const fromCountryIC = getCityCountrySync(fromCity);
                            const toCountryIC = getCityCountrySync(toCity);
                            const isSameCountryIC = fromCountryIC && toCountryIC && fromCountryIC === toCountryIC;
                            const isShortDriveIC = durationHours > 0 && durationHours <= 3;
                            // Only show fly if: cross-ocean, island, different countries, or long drive (>3h)
                            if (isCrossOceanIC || involvesIsland || (!isSameCountryIC && !isShortDriveIC) || durationHours > 3) {
                              modes.push({ id: 'flight', emoji: '✈️', label: 'Fly' });
                            }
                            // BUG 8 FIX: Add ferry option for island routes
                            if (involvesIsland && !isCrossOceanIC) {
                              modes.push({ id: 'ferry', emoji: '⛴️', label: 'Ferry' });
                            }
                            // Train and Drive only if same continent, not an island, not same city, and not too far
                            if (!isCrossOceanIC && !involvesIsland && !isSameCity && !isTooFarToDrive) {
                              if (!isTooFarForTrain) {
                                modes.push({ id: 'train', emoji: '🚆', label: 'Train' });
                              }
                              modes.push({ id: 'car', emoji: '🚗', label: 'Drive' });
                            }
                            // Ensure at least one mode exists
                            if (modes.length === 0) modes.push({ id: 'car', emoji: '🚗', label: 'Drive' });
                            return modes;
                          })();

                          // Determine the inter-city card key for mode selection state
                          const interCityKey = `day${currentDay}-inter-city-${idx}`;
                          // User-selected mode override, or original AI mode
                          // If AI set 'car' but it's not in availableModes (e.g., too far), fall back to first available
                          const rawMode = interCitySelectedModes[interCityKey] || item.travelMode || 'flight';
                          const activeMode = availableModes.some(m => m.id === rawMode) ? rawMode : (availableModes[0]?.id || 'car');

                          // Get the appropriate emoji and color based on ACTIVE travel mode
                          // BUG 8 FIX: Added ferry support to transport mode styling
                          const travelEmoji = activeMode === 'train' ? '🚆' : activeMode === 'bus' ? '🚌' : activeMode === 'car' ? '🚗' : activeMode === 'ferry' ? '⛴️' : '✈️';
                          const bgGradient = activeMode === 'train' ? 'from-emerald-500 to-teal-600' : activeMode === 'bus' ? 'from-orange-500 to-amber-600' : activeMode === 'car' ? 'from-gray-500 to-slate-600' : activeMode === 'ferry' ? 'from-cyan-500 to-blue-600' : 'from-blue-500 to-indigo-600';
                          const cardGradient = activeMode === 'train' ? 'from-brand-50 to-teal-50' : activeMode === 'bus' ? 'from-orange-50 to-amber-50' : activeMode === 'car' ? 'from-gray-50 to-slate-50' : activeMode === 'ferry' ? 'from-cyan-50 to-blue-50' : 'from-blue-50 to-indigo-50';
                          const borderColor = activeMode === 'train' ? 'border-teal-200' : activeMode === 'bus' ? 'border-orange-200' : activeMode === 'car' ? 'border-gray-200' : activeMode === 'ferry' ? 'border-cyan-200' : 'border-blue-200';
                          const activeModeLabel = activeMode === 'train' ? `Train to ${toCity}`
                            : activeMode === 'car' ? `Drive to ${toCity}`
                            : activeMode === 'bus' ? `Bus to ${toCity}`
                            : activeMode === 'ferry' ? `Ferry to ${toCity}`
                            : stripEmoji(item.activity || ''); // Keep original AI label for flight

                          const interCityCardId = `inter-city-day${currentDay}-${idx}`;
                          const isInterCityExpanded = expandedCards[interCityCardId] || false;

                          return (
                            <div key={`inter-city-${idx}`} className="transit-block group">
                              <div className={`transit-line ${activeMode}`}></div>
                              
                              {/* Compact summary (collapsed) */}
                              {!isInterCityExpanded && (
                                <div
                                  className={`transit-item ${activeMode} cursor-pointer hover:shadow-md transition-all`}
                                  onClick={() => setExpandedCards(prev => ({ ...prev, [interCityCardId]: true }))}
                                >
                                  <div className="flex-1 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      <ActivityIcon emoji={travelEmoji} size={20} />
                                      <div className="flex flex-col">
                                        <span className="text-[14px] font-extrabold text-[#313143]">
                                          {fromCity} → {toCity}
                                        </span>
                                        <div className="flex items-center gap-2 mt-0.5">
                                          {/* Show departure → arrival times with timezone labels + overnight indicator */}
                                          {(() => {
                                            let depTime = item.departureTime;
                                            let arrTime = item.arrivalTime;
                                            let dur = item.duration;
                                            let dayOff = item.arrivalDayOffset || 0;
                                            // Client-side fallback: estimate flight times for older itineraries missing them
                                            if ((!depTime || !arrTime) && (activeMode === 'fly' || activeMode === 'flight') && fromCity && toCity) {
                                              const tzDiffEst = (getCityTimezoneOffset(toCity) || 0) - (getCityTimezoneOffset(fromCity) || 0);
                                              const est = estimateFlightSchedule(fromCity, toCity, { timezoneDiff: tzDiffEst, helpers: { getCityCountrySync, getContinentForCountry } });
                                              if (est) {
                                                depTime = depTime || est.departureTime;
                                                arrTime = arrTime || est.arrivalTime;
                                                dur = dur || est.duration;
                                                dayOff = dayOff || est.arrivalDayOffset || 0;
                                              }
                                            }
                                            if (depTime && arrTime) {
                                              const depTz = getTimezoneAbbr(fromCity);
                                              const arrTz = getTimezoneAbbr(toCity);
                                              const showTz = depTz && arrTz && depTz !== arrTz;
                                              const tzDiff = showTz ? (getCityTimezoneOffset(toCity) - getCityTimezoneOffset(fromCity)) : 0;
                                              if (!dayOff) dayOff = computeArrivalDayOffset(depTime, arrTime, tzDiff, item.durationMinutes);
                                              // Option A (#3) — for flights with layovers, show total journey + flying time
                                              const hasLayover = !!item.totalJourneyDuration && !!item.flyingDuration
                                                && item.totalJourneyDuration !== item.flyingDuration;
                                              const displayDur = hasLayover
                                                ? `${item.totalJourneyDuration} total · ${item.flyingDuration} flying`
                                                : dur;
                                              return (
                                                <span className="text-[11px] font-medium text-gray-500">
                                                  {formatTimeDisplay(depTime)}{showTz ? ` ${depTz}` : ''} → {formatTimeDisplay(arrTime)}{dayOff > 0 ? ' +1d' : ''}{showTz ? ` ${arrTz}` : ''}{displayDur ? ` · ${displayDur}` : ''}
                                                </span>
                                              );
                                            }
                                            // T5 (web): non-flight inter-city transit cards should show
                                            // the departure time alongside the duration.
                                            if (dur) {
                                              const isNonFlight = activeMode !== 'fly' && activeMode !== 'flight';
                                              if (isNonFlight && item.time) {
                                                return (
                                                  <span className="text-[11px] font-medium text-gray-500">
                                                    Depart {formatTimeDisplay(item.time)} · {dur}
                                                  </span>
                                                );
                                              }
                                              return <span className="text-[11px] font-medium text-gray-500">{dur}</span>;
                                            }
                                            return null;
                                          })()}
                                          {item.carrier && (
                                            <span className="text-[10px] font-medium text-gray-400 bg-gray-100 px-1.5 rounded">
                                              {item.carrier}
                                            </span>
                                          )}
                                          <div className="flex items-center gap-1 ml-2">
                                            {availableModes.map(m => (
                                              <span key={m.id} className={`text-xs px-1 rounded ${m.id === activeMode ? 'bg-black/5 text-gray-700' : 'text-gray-400'}`}>
                                                <ActivityIcon emoji={m.emoji} size={12} />
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    {/* Expand icon */}
                                    <ChevronDown className="w-4 h-4 text-gray-400" />
                                  </div>
                                </div>
                              )}

                              {/* Expanded full card */}
                              {isInterCityExpanded && (
                                <div className={`transit-item ${activeMode} flex-col !items-stretch !p-5 relative`}>
                                  {/* Collapse button */}
                                  <button
                                    onClick={() => setExpandedCards(prev => ({ ...prev, [interCityCardId]: false }))}
                                    className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-600 rounded-full hover:bg-black/5 transition-colors z-10"
                                  >
                                    <ChevronUp className="w-4 h-4" />
                                  </button>
                                  {/* Transport Mode Selector */}
                                  <div className="flex items-center gap-1.5 mb-3 -mt-1">
                                    {availableModes.map(mode => (
                                      <button
                                        key={mode.id}
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          setInterCitySelectedModes(prev => ({ ...prev, [interCityKey]: mode.id }));
                                          // On-demand fetch: if Google Directions time not yet cached for this mode, fetch it
                                          const googleMode = mode.id === 'car' ? 'driving' : (mode.id === 'train' || mode.id === 'ferry') ? 'transit' : null;
                                          if (googleMode && fromCity && toCity) {
                                            const dirKey = `${fromCity}|${toCity}|${googleMode}`.toLowerCase();
                                            if (!interCityDirectionsTimes[dirKey]) {
                                              try {
                                                const dirResp = await fetch(`${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/directions`, {
                                                  method: 'POST',
                                                  headers: await getApiHeaders(),
                                                  body: JSON.stringify({
                                                    origin: `${fromCity} central station`,
                                                    destination: `${toCity} central station`,
                                                    mode: googleMode,
                                                    transitContext: { type: 'inter-city' }
                                                  })
                                                });
                                                if (dirResp.ok) {
                                                  const dirData = await dirResp.json();
                                                  if (dirData.success) {
                                                    setInterCityDirectionsTimes(prev => ({
                                                      ...prev,
                                                      [dirKey]: {
                                                        duration: dirData.duration,
                                                        durationMinutes: Math.round((dirData.durationValue || 0) / 60),
                                                        estimated: dirData.estimated || false
                                                      }
                                                    }));
                                                  }
                                                }
                                              } catch (err) { /* silent fallback to Haiku estimate */ }
                                            }
                                          }
                                        }}
                                        className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                                          activeMode === mode.id
                                            ? 'bg-white shadow-sm border border-gray-200 text-gray-800'
                                            : 'bg-transparent text-gray-500 hover:bg-white/50 hover:text-gray-700'
                                        }`}
                                        style={FONTS.body}
                                      >
                                        <ActivityIcon emoji={mode.emoji} size={14} />
                                        <span>{mode.label}</span>
                                      </button>
                                    ))}
                                  </div>

                                  <h3 className="text-xl text-gray-800" style={FONTS.headingBold}>
                                    {/* Always use activeModeLabel — it falls back to item.activity for flights */}
                                    {activeModeLabel}
                                  </h3>
                                  <div className="flex items-center gap-2 mt-2 text-gray-600">
                                    <Clock className="w-4 h-4" />
                                    <span style={FONTS.body}>
                                      {/* Show Google Directions time when available, fall back to Haiku estimate or placeholder */}
                                      {(() => {
                                        const googleModeKey = activeMode === 'car' ? 'driving' : (activeMode === 'train' || activeMode === 'ferry') ? 'transit' : null;
                                        const dirKey = googleModeKey ? `${fromCity}|${toCity}|${googleModeKey}`.toLowerCase() : null;
                                        const googleTime = dirKey ? interCityDirectionsTimes[dirKey] : null;
                                        if (googleTime?.duration) {
                                          return `${googleTime.duration}${googleTime.estimated ? ' (est.)' : ''}`;
                                        }
                                        if (activeMode !== item.travelMode) {
                                          // Check Haiku data for duration when mode matches
                                          const pairKeyDur = `${fromCity}|${toCity}`.toLowerCase();
                                          const haikuDur = dynamicTransportModes[pairKeyDur];
                                          if (haikuDur?.mode === activeMode && haikuDur?.duration) {
                                            return haikuDur.duration;
                                          }
                                          // Show original duration as fallback with mode label, instead of perpetual "Loading..."
                                          return item.duration || `~${activeMode} travel`;
                                        }
                                        return item.duration;
                                      })()}
                                    </span>
                                  </div>
                                  {(item.from || item.location) && (
                                    <div className="flex items-center gap-2 mt-1 text-gray-600">
                                      <MapPin className="w-4 h-4 text-blue-500" />
                                      <span style={FONTS.body}>
                                        {fromCity && toCity ? `${fromCity} → ${toCity}` : item.location}
                                      </span>
                                    </div>
                                  )}
                                  {/* Show mode-appropriate description for non-flight modes (always clean text),
                                      or original AI description for flights */}
                                  {activeMode && activeMode !== 'flight' ? (
                                    <p className="text-gray-600 mt-2" style={FONTS.body}>
                                      {activeMode === 'car' ? `Drive from ${fromCity} to ${toCity}. Check driving conditions and route options before departing.`
                                        : activeMode === 'train' ? `Train from ${fromCity} to ${toCity}. Book tickets in advance for best prices.`
                                        : activeMode === 'ferry' ? `Ferry from ${fromCity} to ${toCity}. Check ferry schedules and book in advance during peak season.`
                                        : activeMode === 'bus' ? `Bus from ${fromCity} to ${toCity}. Book tickets in advance for best prices.`
                                        : `Travel from ${fromCity} to ${toCity}.`}
                                    </p>
                                  ) : item.description ? (
                                    <p className="text-gray-600 mt-2" style={FONTS.body}>{item.description}</p>
                                  ) : null}
                                  {/* Booking Button - changes based on active transport mode */}
                                  {activeMode && (
                                    <div className={`mt-3 pt-3 border-t ${borderColor}`}>
                                      {activeMode === 'flight' && (() => {
                                        const fromCode = item.fromAirportCode || item.from?.match?.(/\(([A-Z]{3})\)/)?.[1] || item.transportData?.from?.airport || lookupCityAirportCode(item.from) || lookupCityAirportCode(item.location?.split('→')?.[0]?.trim()) || '';
                                        const toCode = item.toAirportCode || item.to?.match?.(/\(([A-Z]{3})\)/)?.[1] || item.transportData?.to?.airport || lookupCityAirportCode(item.to) || lookupCityAirportCode(item.location?.split('→')?.[1]?.trim()) || '';
                                        const baseRouteKey = fromCode && toCode ? `${fromCode}-${toCode}` : null;
                                        const cabinForRoute = baseRouteKey ? (flightCabinClass[baseRouteKey] || preferredTravelClass || 'ECONOMY') : 'ECONOMY';
                                        const routeKey = baseRouteKey ? `${baseRouteKey}-${cabinForRoute}` : null;
                                        const airlineFilterForRoute = baseRouteKey ? flightAirlineFilter[baseRouteKey] : null;
                                        const stopsFilterForRoute = baseRouteKey ? flightStopsFilter[baseRouteKey] : null;
                                        const timeFilterForRoute = baseRouteKey ? flightTimeFilter[baseRouteKey] : null;
                                        const priceData = routeKey ? flightPrices[routeKey] : null;
                                        const canFetchPrice = fromCode && toCode && features.AMADEUS_FLIGHTS;

                                        // Auto-fetch price when card renders (no button needed)
                                        if (canFetchPrice && !priceData && !flightPrices[routeKey]?.loading) {
                                          // Schedule fetch outside render cycle
                                          setTimeout(() => fetchFlightPrice(fromCode, toCode, userProfile?.preferences?.startDate, { cabinClass: cabinForRoute }), 0);
                                        }

                                        // Get available airlines from results for the filter dropdown
                                        const availableAirlines = priceData?.data?.availableAirlines || [];
                                        // Filter offers by selected filters (client-side filtering)
                                        const filterOffers = (offers) => {
                                          let filtered = offers;
                                          // Airline filter
                                          if (airlineFilterForRoute) {
                                            filtered = filtered.filter(o => o.airlines?.includes(airlineFilterForRoute) ||
                                              o.itineraries?.[0]?.segments?.some(s => s.carrier === airlineFilterForRoute));
                                          }
                                          // Stops filter
                                          if (stopsFilterForRoute !== null && stopsFilterForRoute !== undefined) {
                                            filtered = filtered.filter(o => {
                                              const segs = o.itineraries?.[0]?.segments || [];
                                              const stops = segs.length - 1;
                                              return stops <= stopsFilterForRoute;
                                            });
                                          }
                                          // Departure time filter
                                          if (timeFilterForRoute) {
                                            filtered = filtered.filter(o => {
                                              const depTime = o.itineraries?.[0]?.segments?.[0]?.departure?.time;
                                              if (!depTime) return true;
                                              const hour = new Date(depTime).getHours();
                                              if (timeFilterForRoute === 'morning') return hour >= 5 && hour < 12;
                                              if (timeFilterForRoute === 'afternoon') return hour >= 12 && hour < 18;
                                              if (timeFilterForRoute === 'evening') return hour >= 18 || hour < 5;
                                              return true;
                                            });
                                          }
                                          return filtered;
                                        };
                                        // Keep legacy name for compatibility
                                        const filterOffersByAirline = filterOffers;

                                        const icFlightExpanded = routeKey ? expandedFlightPrices[routeKey] : false;
                                        const filteredOffers = priceData?.data?.allOffers ? filterOffersByAirline(priceData.data.allOffers) : [];
                                        const icCheapestOffer = filteredOffers[0];
                                        const icCheapestPrice = icCheapestOffer ? new Intl.NumberFormat('en-US', { style: 'currency', currency: icCheapestOffer.currency || priceData.data.currency || 'USD' }).format(icCheapestOffer.price) : (priceData?.data?.price ? `from ${new Intl.NumberFormat('en-US', { style: 'currency', currency: priceData.data.currency || 'USD' }).format(priceData.data.price)}` : null);

                                        return (
                                          <div className="space-y-2">
                                            {/* Collapsed summary bar — click to expand */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (routeKey) setExpandedFlightPrices(prev => ({ ...prev, [routeKey]: !prev[routeKey] }));
                                              }}
                                              className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                                                icFlightExpanded ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                              }`}
                                            >
                                              <div className="flex items-center gap-2">
                                                <span className="text-sm flex items-center gap-1" style={FONTS.body}><Plane className="w-3.5 h-3.5 text-teal-600" /> Flight Options</span>
                                                {priceData?.loading && (
                                                  <div className="w-3.5 h-3.5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                                                )}
                                                {icCheapestPrice && !priceData?.loading && (
                                                  <span className="text-xs text-teal-700 font-medium bg-teal-100 px-2 py-0.5 rounded-full" style={FONTS.bodyBold}>{icCheapestPrice}</span>
                                                )}
                                              </div>
                                              <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${icFlightExpanded ? 'rotate-180' : ''}`} />
                                            </button>

                                            {/* Expanded flight offers */}
                                            {icFlightExpanded && (
                                              <div className="space-y-2">
                                            {/* Cabin Class + Airline Filter Controls */}
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] text-gray-500 uppercase tracking-wide" style={FONTS.body}>Class:</span>
                                                <select
                                                  value={cabinForRoute}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    const newCabin = e.target.value;
                                                    if (baseRouteKey) {
                                                      setFlightCabinClass(prev => ({ ...prev, [baseRouteKey]: newCabin }));
                                                      // New cabin → new routeKey → auto-fetch will trigger on re-render
                                                    }
                                                  }}
                                                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-teal-400 focus:border-teal-400 cursor-pointer"
                                                  style={FONTS.body}
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <option value="ECONOMY">Economy</option>
                                                  <option value="PREMIUM_ECONOMY">Premium Economy</option>
                                                  <option value="BUSINESS">Business</option>
                                                  <option value="FIRST">First</option>
                                                </select>
                                              </div>
                                              {availableAirlines.length > 1 && (
                                                <div className="flex items-center gap-1.5">
                                                  <span className="text-[10px] text-gray-500 uppercase tracking-wide" style={FONTS.body}>Airline:</span>
                                                  <select
                                                    value={airlineFilterForRoute || ''}
                                                    onChange={(e) => {
                                                      e.stopPropagation();
                                                      const val = e.target.value || null;
                                                      if (baseRouteKey) setFlightAirlineFilter(prev => ({ ...prev, [baseRouteKey]: val }));
                                                    }}
                                                    className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-teal-400 focus:border-teal-400 cursor-pointer"
                                                    style={FONTS.body}
                                                    onClick={(e) => e.stopPropagation()}
                                                  >
                                                    <option value="">All Airlines</option>
                                                    {availableAirlines.map(code => (
                                                      <option key={code} value={code}>{resolveAirlineName(code, priceData?.data?.dictionaries) || code}</option>
                                                    ))}
                                                  </select>
                                                </div>
                                              )}
                                              <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] text-gray-500 uppercase tracking-wide" style={FONTS.body}>Stops:</span>
                                                <select
                                                  value={stopsFilterForRoute ?? ''}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    const val = e.target.value === '' ? null : parseInt(e.target.value);
                                                    if (baseRouteKey) setFlightStopsFilter(prev => ({ ...prev, [baseRouteKey]: val }));
                                                  }}
                                                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-teal-400 focus:border-teal-400 cursor-pointer"
                                                  style={FONTS.body}
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <option value="">Any</option>
                                                  <option value="0">Nonstop</option>
                                                  <option value="1">1 stop max</option>
                                                </select>
                                              </div>
                                              <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] text-gray-500 uppercase tracking-wide" style={FONTS.body}>Time:</span>
                                                <select
                                                  value={timeFilterForRoute || ''}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    const val = e.target.value || null;
                                                    if (baseRouteKey) setFlightTimeFilter(prev => ({ ...prev, [baseRouteKey]: val }));
                                                  }}
                                                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-teal-400 focus:border-teal-400 cursor-pointer"
                                                  style={FONTS.body}
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <option value="">Any Time</option>
                                                  <option value="morning">Morning (5a-12p)</option>
                                                  <option value="afternoon">Afternoon (12p-6p)</option>
                                                  <option value="evening">Evening (6p-5a)</option>
                                                </select>
                                              </div>
                                            </div>
                                            {priceData?.loading && (
                                              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl">
                                                <div className="w-3.5 h-3.5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                                                <span className="text-xs text-gray-500" style={FONTS.body}>Loading live prices...</span>
                                              </div>
                                            )}
                                            {/* Flight offers list */}
                                            {filteredOffers.length > 0 && (() => {
                                              const icDicts = priceData.data.dictionaries || {};
                                              const showingAllIc = expandedFlightPrices[`${routeKey}-all`];
                                              const allFilteredExpanded = priceData.data.allOffersExpanded ? filterOffersByAirline(priceData.data.allOffersExpanded) : filteredOffers;
                                              const icOffersToShow = showingAllIc ? allFilteredExpanded : filteredOffers;
                                              const icHasMore = allFilteredExpanded.length > filteredOffers.length;
                                              return (
                                              <div className="space-y-2">
                                                {icOffersToShow.map((offer, offerIdx) => {
                                                  const itin = offer.itineraries?.[0];
                                                  const segs = itin?.segments || [];
                                                  const firstSeg = segs[0];
                                                  const lastSeg = segs[segs.length - 1];
                                                  const stops = segs.length - 1;
                                                  const durMatch = (itin?.duration || '').match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                                                  const durStr = `${durMatch?.[1] || '0'}h ${durMatch?.[2] || '0'}m`;
                                                  const depTime = parseAmadeusTime(firstSeg?.departure?.time) || '';
                                                  const arrTime = parseAmadeusTime(lastSeg?.arrival?.time) || '';
                                                  const icDepDate = formatAmadeusShortDate(firstSeg?.departure?.time);
                                                  const icArrDate = formatAmadeusShortDate(lastSeg?.arrival?.time);
                                                  const icIsNextDay = firstSeg?.departure?.time && lastSeg?.arrival?.time && isNextDayArrival(firstSeg.departure.time, lastSeg.arrival.time);
                                                  const carrierCodes = [...new Set(segs.map(s => s.carrier).filter(Boolean))];
                                                  const carrierNames = carrierCodes.map(c => resolveAirlineName(c, icDicts));
                                                  const flightNums = segs.map(s => `${s.carrier || ''}${s.flightNumber || ''}`).filter(Boolean);
                                                  const currency = offer.currency || priceData.data.currency || 'USD';
                                                  const priceFormatted = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(offer.price);
                                                  // Get cabin class from segment
                                                  const icCabinClass = firstSeg?.cabin || priceData?.data?.cabin || 'Economy';
                                                  const icCabinDisplay = icCabinClass.charAt(0).toUpperCase() + icCabinClass.slice(1).toLowerCase().replace('_', ' ');
                                                  // Build booking link
                                                  const icFlightDate = userProfile?.preferences?.startDate || '';
                                                  const icBookingHref = getFlightsLink(fromCode, toCode, icFlightDate, { type: 'oneway' });

                                                  return (
                                                    <a
                                                      key={offer.id || offerIdx}
                                                      href={icBookingHref}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      className={`block p-3 rounded-xl border transition-all cursor-pointer ${offerIdx === 0 ? 'bg-teal-50 border-teal-200 hover:bg-teal-100' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'}`}
                                                      onClick={(e) => e.stopPropagation()}
                                                    >
                                                      <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                          <span className="text-sm font-bold text-gray-800" style={FONTS.headingBold}>{carrierNames.join(', ') || 'Unknown'}</span>
                                                          {flightNums.length > 0 && <span className="text-xs text-gray-400" style={FONTS.body}>{flightNums[0]}</span>}
                                                        </div>
                                                        <span className={`text-sm font-bold ${offerIdx === 0 ? 'text-teal-700' : 'text-gray-700'}`} style={FONTS.headingBold}>{priceFormatted}</span>
                                                      </div>
                                                      {icDepDate && (
                                                        <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-400" style={FONTS.body}>
                                                          <span>{icDepDate}</span>
                                                          {icArrDate && icArrDate !== icDepDate && (
                                                            <>
                                                              <span>→</span>
                                                              <span>{icArrDate}</span>
                                                            </>
                                                          )}
                                                        </div>
                                                      )}
                                                      <div className="flex items-center justify-between mt-0.5">
                                                        <div className="flex items-center gap-1.5 text-xs text-gray-600" style={FONTS.body}>
                                                          {depTime && <span className="font-medium">{depTime}</span>}
                                                          {depTime && arrTime && <span className="text-gray-400">→</span>}
                                                          {arrTime && (
                                                            <span className="font-medium">
                                                              {arrTime}
                                                              {icIsNextDay && <sup className="text-[9px] text-amber-600 font-bold ml-0.5">+1</sup>}
                                                            </span>
                                                          )}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 text-xs text-gray-500" style={FONTS.body}>
                                                          <span>{durStr}</span>
                                                          <span className="text-gray-300">·</span>
                                                          <span>{stops === 0 ? 'Direct' : `${stops} stop${stops > 1 ? 's' : ''}`}</span>
                                                        </div>
                                                      </div>
                                                      {/* Row 3: Class badge + via stops */}
                                                      <div className="flex items-center gap-1.5 mt-1.5">
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-600 font-medium" style={FONTS.body}>{icCabinDisplay}</span>
                                                        {stops > 0 && (
                                                          <span className="text-xs text-gray-400" style={FONTS.body}>
                                                            {segs.slice(0, -1).map((seg, sIdx) => `${sIdx > 0 ? ', ' : ''}via ${seg.arrival?.airport}`).join('')}
                                                          </span>
                                                        )}
                                                      </div>
                                                    </a>
                                                  );
                                                })}
                                                {icHasMore && !showingAllIc && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setExpandedFlightPrices(prev => ({ ...prev, [`${routeKey}-all`]: true }));
                                                    }}
                                                    className="w-full py-2 text-xs text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg font-medium transition-colors"
                                                    style={FONTS.bodyBold}
                                                  >
                                                    More Options ({(priceData.data.allOffersExpanded?.length || 0) - (priceData.data.allOffers?.length || 0)} more)
                                                  </button>
                                                )}
                                                {priceData.fallbackUsed && (
                                                  <p className="text-xs text-amber-600" style={FONTS.body}>{priceData.originalClass} unavailable · showing Economy</p>
                                                )}
                                              </div>
                                              );
                                            })()}
                                            {/* Fallback single price display */}
                                            {priceData?.data && !priceData?.data?.allOffers?.length && (
                                              <div className="p-2.5 bg-teal-50 rounded-xl border border-teal-200">
                                                <div className="flex items-center gap-2">
                                                  <span className="text-base font-bold text-teal-700" style={FONTS.headingBold}>
                                                    from {new Intl.NumberFormat('en-US', { style: 'currency', currency: priceData.data.currency || 'USD' }).format(priceData.data.price)}
                                                  </span>
                                                  {priceData.data.airlines?.length > 0 && (
                                                    <span className="text-xs text-gray-500" style={FONTS.body}>· {priceData.data.airlines.join(', ')}</span>
                                                  )}
                                                </div>
                                              </div>
                                            )}
                                            {priceData?.noResults && !priceData?.error && (
                                              <p className="text-xs text-gray-400" style={FONTS.body}>No live offers found for this route</p>
                                            )}
                                            {priceData?.error && (
                                              <div className="flex items-center gap-2">
                                                <p className="text-xs text-gray-400" style={FONTS.body}>Live pricing unavailable</p>
                                                <button
                                                  onClick={() => {
                                                    setFlightPrices(prev => { const updated = { ...prev }; delete updated[routeKey]; return updated; });
                                                    setTimeout(() => fetchFlightPrice(fromCode, toCode, userProfile?.preferences?.startDate, { cabinClass: cabinForRoute }), 100);
                                                  }}
                                                  className="text-xs text-teal-600 hover:text-teal-700 font-medium underline"
                                                  style={FONTS.body}
                                                >
                                                  Retry
                                                </button>
                                              </div>
                                            )}

                                            {/* Book Flight link removed per user request */}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                      {activeMode === 'train' && (() => {
                                        const trainFrom = item.from || fromCity || '';
                                        const trainTo = item.to || toCity || '';
                                        // Determine if US domestic route for Amtrak
                                        const fromCountryTrain = getCityCountrySync(fromCity);
                                        const toCountryTrain = getCityCountrySync(toCity);
                                        const isUSDomesticTrain = (fromCountryTrain === 'United States' || fromCountryTrain === 'US') && (toCountryTrain === 'United States' || toCountryTrain === 'US');
                                        const omioUrl = `https://www.omio.com/search?from=${encodeURIComponent(fromCity)}&to=${encodeURIComponent(toCity)}`;
                                        const amtrakUrl = `https://www.amtrak.com/tickets/departure.html`;
                                        const rome2rioUrl = `https://www.rome2rio.com/s/${encodeURIComponent(fromCity)}/${encodeURIComponent(toCity)}`;
                                        return (
                                          <div className="space-y-2">
                                            {trainFrom && trainTo && (
                                              <p className="text-xs text-gray-500" style={FONTS.body}>
                                                🚉 {trainFrom} → {trainTo} {item.duration ? `• ${item.duration}` : ''}
                                              </p>
                                            )}
                                            <div className="flex flex-wrap gap-2">
                                              <a
                                                href={isUSDomesticTrain ? amtrakUrl : omioUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 hover:bg-teal-200 text-teal-700 rounded-lg text-sm font-medium transition-colors"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                🚆 {isUSDomesticTrain ? 'Amtrak' : 'Book on Omio'}
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                              <a
                                                href={rome2rioUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-xs font-medium transition-colors"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                🔍 Rome2Rio
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      {activeMode === 'ferry' && (() => {
                                        const ferrySearchUrl = `https://www.ferryhopper.com/en/search?from=${encodeURIComponent(fromCity)}&to=${encodeURIComponent(toCity)}`;
                                        const directFerriesUrl = `https://www.directferries.com/routes/${encodeURIComponent(fromCity.toLowerCase().replace(/\s+/g, '_'))}_${encodeURIComponent(toCity.toLowerCase().replace(/\s+/g, '_'))}.htm`;
                                        const rome2rioUrl = `https://www.rome2rio.com/s/${encodeURIComponent(fromCity)}/${encodeURIComponent(toCity)}`;
                                        return (
                                          <div className="space-y-3">
                                            <div className="flex flex-wrap gap-2">
                                              <a
                                                href={ferrySearchUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-100 hover:bg-cyan-200 text-cyan-800 rounded-lg text-sm font-medium transition-colors"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                ⛴️ FerryHopper
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                              <a
                                                href={directFerriesUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg text-xs font-medium transition-colors"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                🚢 DirectFerries
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                              <a
                                                href={rome2rioUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-xs font-medium transition-colors"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                🔍 Rome2Rio
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                      {(activeMode === 'car' || activeMode === 'bus') && (() => {
                                        const cardId = `inter-city-${currentDay}-${idx}`;
                                        // Use specific from/to addresses for better Google Maps routing
                                        const mapsOrigin = item.from || item.location?.split('→')[0]?.trim() || '';
                                        const mapsDestination = item.to || item.location?.split('→')[1]?.trim() || '';
                                        const rome2rioUrl = `https://www.rome2rio.com/s/${encodeURIComponent(fromCity)}/${encodeURIComponent(toCity)}`;
                                        return (
                                          <div className="space-y-3">
                                            <div className="flex flex-wrap gap-2">
                                              <a
                                                href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(mapsOrigin)}&destination=${encodeURIComponent(mapsDestination)}&travelmode=driving`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                {activeMode === 'bus' ? '🚌' : '🚗'} View Route
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                              {activeMode === 'bus' && (
                                                <a
                                                  href={rome2rioUrl}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="inline-flex items-center gap-2 px-3 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-lg text-xs font-medium transition-colors"
                                                  style={FONTS.body}
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  🚌 Book on Rome2Rio
                                                  <ExternalLink className="w-3 h-3" />
                                                </a>
                                              )}
                                            </div>

                                            {/* Road Trip Stop Recommendations */}
                                            {!roadTripStops[cardId]?.data && !roadTripStops[cardId]?.loading && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  fetchRoadTripStops(cardId, item.location, item.duration);
                                                }}
                                                className="w-full py-2.5 bg-teal-500 hover:bg-teal-600 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors text-sm"
                                                style={FONTS.body}
                                              >
                                                <Route className="w-4 h-4" /> Get Stop Recommendations
                                              </button>
                                            )}

                                            {roadTripStops[cardId]?.loading && (
                                              <div className="flex items-center justify-center py-4 text-gray-500 text-sm">
                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-500 mr-2"></div>
                                                Finding great stops along your route...
                                              </div>
                                            )}

                                            {roadTripStops[cardId]?.data && (
                                              <RoadTripStopsDisplay
                                                stops={roadTripStops[cardId].data.stops}
                                                routeTips={roadTripStops[cardId].data.routeTips}
                                                targetDayIndex={currentDay}
                                                onAdd={(stop) => addRoadTripStopToDay(currentDay, stop)}
                                              />
                                            )}

                                            {roadTripStops[cardId]?.error && (
                                              <div className="text-center py-3 text-red-500 text-sm">
                                                <p>Failed to load recommendations</p>
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    fetchRoadTripStops(cardId, item.location, item.duration);
                                                  }}
                                                  className="mt-1 text-teal-600 hover:text-teal-700 font-medium"
                                                >
                                                  Try Again
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }

                        // Render transit cards between activities as inline transit selectors
                        if (isTransit || isReturnTransit || isAirportTransit || isStationTransit || isPortTransit) {
                          const transitKey = item.transitKey || `day${currentDay}-transit-${idx}`;
                          const cachedTimes = transitTimes[transitKey] || {};
                          const isLoading = loadingTransit[transitKey];

                          // Debug: Log transit card rendering to help diagnose timing issues
                          if (process.env.NODE_ENV === 'development') {
                            console.log(`🚗 Transit card ${transitKey}: cached=${Object.keys(cachedTimes).length > 0}, loading=${isLoading}, item.duration=${item.duration}`);
                          }

                          // Fetch missing transit data on first render if not cached and not loading
                          // This catches post-freetime and other dynamically generated transit cards
                          if (Object.keys(cachedTimes).length === 0 && !isLoading && item.from && item.to) {
                            // Determine the transit context type
                            const contextType = transitKey.includes('hotel-transit') ? 'airport-transfer' :
                                               transitKey.includes('last-to-hotel') ? 'hotel-return' : 'same-day';
                            const cityContext = currentDayData?.baseCity || selectedDestination?.name || '';

                            // Trigger fetch asynchronously (don't block render)
                            setTimeout(() => {
                              if (!transitTimes[transitKey] && !loadingTransit[transitKey]) {
                                debugLog(`🔄 Auto-fetching missing transit: ${transitKey} (${item.from} → ${item.to})`);
                                fetchTransitTimes(transitKey, item.from, item.to, {
                                  type: contextType,
                                  expectedCity: cityContext
                                });
                              }
                            }, 100);
                          }
                          
                          // Helper to parse distance to meters for comparison
                          const parseDistanceToMeters = (distance) => {
                            if (!distance || distance === 'N/A') return Infinity;
                            const km = parseFloat(distance);
                            if (!isNaN(km)) return km * 1000; // Convert km to meters
                            return Infinity;
                          };
                          
                          // Check if locations are on the same property (very short walk)
                          // This handles resort activities, hotel restaurants, etc.
                          // Use walking TIME (not distance) — 500m can be a meaningful walk in hilly cities
                          const walkingTimeMinutes = parseDurationToMinutes(cachedTimes.walking?.duration, { defaultMinutes: Infinity });
                          const walkingDistance = parseDistanceToMeters(cachedTimes.walking?.distance);

                          // Only skip transit if walking takes under 2 minutes AND distance is under 200m
                          // (truly same-property, e.g., hotel lobby to hotel restaurant)
                          if (walkingTimeMinutes < 2 && walkingDistance < 200 && walkingDistance !== Infinity) {
                            return null; // Don't render transit card - locations are on same property
                          }
                          
                          // Wrapper for parseDurationToMinutes with Infinity default (for transit comparison)
                          const parseDurationForTransit = (duration) => {
                            if (!duration || duration === 'N/A') return Infinity;
                            return parseDurationToMinutes(duration, { defaultMinutes: Infinity });
                          };
                          
                          // Check if this is an island/car-free destination
                          const transitBaseCity = itinerary?.days?.[currentDay]?.baseCity || '';
                          const isIslandTransit = isIslandCity(transitBaseCity) ||
                            (dynamicGeographyCache[transitBaseCity?.toLowerCase?.()?.trim?.()]?.noCarAccess);

                          // Check if this is a same-day intra-city transit (not airport/port/inter-city)
                          const isSameDayTransit = !isAirportTransit && !isPortTransit && !isStationTransit &&
                            !transitKey.includes('inter-city');

                          // Get the fastest available mode
                          const getFastestMode = () => {
                            if (!cachedTimes.driving && !cachedTimes.transit && !cachedTimes.walking) {
                              return isIslandTransit ? 'walking' : 'driving'; // Default before data loads
                            }

                            const modes = ['driving', 'transit', 'walking'];
                            let fastestMode = null;
                            let fastestTime = Infinity;
                            let availableModes = [];

                            modes.forEach(mode => {
                              if (cachedTimes[mode]) {
                                const duration = cachedTimes[mode].duration;
                                // Skip unavailable modes
                                if (duration === '0 mins' || duration === '0 min' || duration === 'N/A' || cachedTimes[mode].unavailable) return;

                                availableModes.push(mode);
                                const mins = parseDurationForTransit(duration);
                                if (mins < fastestTime) {
                                  fastestTime = mins;
                                  fastestMode = mode;
                                }
                              }
                            });

                            // Sanity check: for same-day intra-city transits, if ALL available
                            // modes report >90 minutes, prefer driving with a capped estimate.
                            // This catches cases where small villages (e.g., Grindelwald, Flims)
                            // have unreasonable public transit times (3hrs via hub city).
                            if (isSameDayTransit && fastestTime > 90) {
                              // If driving is available but slow, prefer it over transit
                              const drivingMins = availableModes.includes('driving')
                                ? parseDurationForTransit(cachedTimes.driving?.duration) : Infinity;
                              const walkingMins = availableModes.includes('walking')
                                ? parseDurationForTransit(cachedTimes.walking?.duration) : Infinity;
                              // If walking is under 45 min, prefer walking (short village walk)
                              if (walkingMins <= 45) return 'walking';
                              // If driving is reasonable (under 30 min), prefer driving
                              if (drivingMins <= 30) return 'driving';
                              // Otherwise default to driving — transit >90min for same-city is suspect
                              return isIslandTransit ? 'walking' : 'driving';
                            }

                            // Always prefer driving over walking for distances > 30 min walk
                            if (fastestMode === 'walking' && fastestTime >= 30) {
                              // Try driving first, then transit
                              const alternatives = ['driving', 'transit'].filter(m => availableModes.includes(m));
                              if (alternatives.length > 0) {
                                let altMode = alternatives[0];
                                let altTime = parseDurationForTransit(cachedTimes[alternatives[0]]?.duration);
                                alternatives.forEach(m => {
                                  const t = parseDurationForTransit(cachedTimes[m]?.duration);
                                  if (t < altTime) {
                                    altTime = t;
                                    altMode = m;
                                  }
                                });
                                return altMode;
                              }
                            }

                            // If only walking is available and > 1 hour, default based on destination type
                            if (availableModes.length === 1 && availableModes[0] === 'walking' && fastestTime >= 60) {
                              // On islands, prefer transit over driving (many islands are car-free)
                              return isIslandTransit ? 'transit' : 'driving';
                            }

                            // If walking is the only mode with data but reasonable (< 30 min), use it
                            if (availableModes.length === 1 && availableModes[0] === 'walking') {
                              return 'walking';
                            }

                            return fastestMode || (isIslandTransit ? 'walking' : 'driving');
                          };
                          
                          // Use user-selected mode if set, otherwise use fastest
                          const userSelectedMode = transportModes[transitKey];
                          const selectedMode = userSelectedMode || getFastestMode();
                          
                          // Get emoji for mode. The inline selector only knows
                          // driving/transit/walking; when the post-gen
                          // inferTransitMode repair has set item.travelMode to
                          // a non-driving mode (flight/ferry/seaplane/train/
                          // helicopter), prefer that — it reflects the actual
                          // trip the user is taking, not the routing-API mode
                          // the selector cycles through.
                          const inferredItemMode = (item.travelMode || item.transitMode || item.mode || '').toLowerCase();
                          const getModeEmoji = (mode) => {
                            if (inferredItemMode === 'flight' || inferredItemMode === 'fly') return '✈️';
                            if (inferredItemMode === 'seaplane') return '✈️';
                            if (inferredItemMode === 'helicopter') return '🚁';
                            if (inferredItemMode === 'ferry' || inferredItemMode === 'boat') return '⛴️';
                            if (inferredItemMode === 'train' || inferredItemMode === 'rail') return '🚆';
                            if (inferredItemMode === 'bus') return '🚌';
                            if (mode === 'transit') return '🚇';
                            if (mode === 'walking') return '🚶';
                            return '🚕';
                          };
                          
                          // Get label for transit type
                          const getTransitLabel = () => {
                            // Use the activity text if it's already set (for airport transit cards)
                            // Expanded keyword set so "Flight to Hamilton Island", "Ferry to Devonport",
                            // "Train to Punakha" all surface their actual title rather than the
                            // generic "Travel to next stop" fallback. The post-gen mode-inference
                            // repair sets travelMode for these items, but the label here was
                            // independently coded so we widen the keyword guard too.
                            const TRANSIT_TITLE_KEYWORDS = ['Transit', 'transfer', 'Flight', 'flight', 'Ferry', 'ferry', 'Train', 'train', 'Drive', 'drive', 'Seaplane', 'seaplane', 'Helicopter', 'helicopter', 'Bus', 'bus', 'Departure', 'departure', 'Arrival', 'arrival'];
                            if (item.activity && TRANSIT_TITLE_KEYWORDS.some(k => item.activity.includes(k))) {
                              // Strip emoji and return clean label - include all transport emojis
                              return item.activity.replace(/^[\u{1F680}-\u{1F6FF}\u{1F300}-\u{1F5FF}🚕🚗🚌🚆✈️🚶🛫🛬🚇🏨🧳⛴️🚆🚁🚄🛥️]\s*/gu, '').trim();
                            }
                            if (isAirportTransit) return 'Airport Transfer';
                            if (isPortTransit) return 'Ferry Port Transfer';
                            if (isReturnTransit) return 'Return to Hotel';
                            return 'Travel to next stop';
                          };
                          
                          // Check if duration is over 1 hour
                          const isOverOneHour = (duration) => {
                            if (!duration) return false;
                            if (duration.includes('hour') || duration.includes('hr')) return true;
                            const mins = parseInt(duration);
                            return mins >= 60;
                          };
                          
                          // Get display duration for the selected mode.
                          //
                          // Regression fix: previously this returned 'Calculating...'
                          // whenever isLoading was true. If the /api/directions
                          // fetch hung or silently failed (rate limit, geocoding
                          // miss for venue names like "MAAT" or "LX Factory"),
                          // the card stayed on 'Calculating...' forever even
                          // though createDayTimeline.js had already populated a
                          // reasonable estimate in `item.duration`. Production
                          // test on Lisbon Day 2 hit this on 4/5 transits.
                          //
                          // Now: if we have an estimate, show it immediately.
                          // The async fetch still runs and the real value
                          // replaces the estimate when it arrives. Worst case
                          // (fetch never completes), the user sees a sensible
                          // estimate instead of a perpetual spinner.
                          const getSelectedDuration = () => {
                            // Prefer cached real data when available, regardless of loading state
                            if (cachedTimes[selectedMode]) {
                              const duration = cachedTimes[selectedMode].duration;
                              // If the selected mode has a valid duration, use it
                              if (duration && duration !== '0 mins' && duration !== '0 min' && duration !== 'N/A') {
                                // Sanity cap: same-day intra-city DRIVING/TRANSIT showing >90 min are suspect
                                // (e.g., small villages where public transit routes through a hub city)
                                // Walking >90 min is valid — just show the real time
                                const mins = parseDurationForTransit(duration);
                                if (isSameDayTransit && mins > 90 && selectedMode !== 'walking') {
                                  // Show a reasonable driving estimate instead
                                  if (selectedMode === 'driving') return '~15 min';
                                  return '~15 min';
                                }
                                return duration;
                              }
                            }

                            // Selected mode unavailable - find the best available mode
                            const modePreference = isIslandTransit ? ['walking', 'transit', 'driving'] : ['driving', 'transit', 'walking'];
                            for (const mode of modePreference) {
                              if (cachedTimes[mode]) {
                                const duration = cachedTimes[mode].duration;
                                if (duration && duration !== '0 mins' && duration !== '0 min' && duration !== 'N/A') {
                                  // Same sanity cap for fallback modes
                                  const mins = parseDurationForTransit(duration);
                                  if (isSameDayTransit && mins > 90) continue;
                                  return duration;
                                }
                              }
                            }
                            
                            // No cached data yet - use item.duration if available (calculated from ref),
                            // otherwise show generic estimate scaled from available data.
                            // If still loading AND no fallback, only then show 'Calculating...'.
                            if (item.duration && item.duration !== '~0 mins') return item.duration;
                            if (isAirportTransit || isPortTransit) return '~30 min';
                            if (isLoading) return 'Calculating...';
                            // Scale walking estimate from driving time if available
                            if (selectedMode === 'walking' && cachedTimes.driving) {
                              const driveMins = parseDurationForTransit(cachedTimes.driving.duration);
                              if (driveMins !== Infinity && driveMins > 0) {
                                const walkEstimate = Math.round(driveMins * 4);
                                if (walkEstimate >= 60) return '1hr+';
                                return `~${walkEstimate} min`;
                              }
                            }
                            if (selectedMode === 'transit') return '~25 min';
                            if (selectedMode === 'walking') return '~20 min';
                            return '~15 min';
                          };
                          
                          // Check if a mode is unavailable (0 mins means API couldn't find route)
                          const isModeUnavailable = (mode) => {
                            if (!cachedTimes[mode]) return false;
                            const duration = cachedTimes[mode].duration;
                            return duration === '0 mins' || duration === '0 min';
                          };
                          
                          // Get button duration for each mode
                          const getButtonDuration = (mode) => {
                            // If we have cached data
                            if (cachedTimes[mode]) {
                              const duration = cachedTimes[mode].duration;
                              // Show N/A for unavailable modes (0 mins or explicitly unavailable)
                              if (duration === '0 mins' || duration === '0 min' || cachedTimes[mode].unavailable) {
                                return 'N/A';
                              }
                              // Hide walking times over 1 hour
                              if (mode === 'walking' && isOverOneHour(duration)) {
                                return '1hr+';
                              }
                              // Add ~ prefix if this was an estimated time (not from Google API)
                              if (cachedTimes[mode].estimated) {
                                return `~${duration}`;
                              }
                              return duration;
                            }
                            // No cached data yet - show scaled estimates based on available data
                            // If we know driving time, use it to scale walking estimate (walking ≈ 4x driving)
                            if (mode === 'walking' && cachedTimes.driving) {
                              const driveMins = parseDurationForTransit(cachedTimes.driving.duration);
                              if (driveMins !== Infinity && driveMins > 0) {
                                const walkEstimate = Math.round(driveMins * 4);
                                if (walkEstimate >= 60) return '1hr+';
                                return `~${walkEstimate} min`;
                              }
                            }
                            if (mode === 'driving') return '~15 min';
                            if (mode === 'transit') return '~25 min';
                            if (mode === 'walking') return '~20 min';
                            return null;
                          };
                          
                          // Check if time is estimated (not from Google API)
                          const isTimeEstimated = (mode) => {
                            return cachedTimes[mode]?.estimated === true;
                          };
                          
                          // Check if walking should be disabled (over 1 hour or unavailable)
                          // BUT: Allow walking if it's the ONLY available option
                          const walkingAvailable = cachedTimes.walking && !isModeUnavailable('walking');
                          const drivingAvailable = cachedTimes.driving && !isModeUnavailable('driving');
                          const transitAvailable = cachedTimes.transit && !isModeUnavailable('transit');

                          const isWalkingOnlyOption = walkingAvailable && !drivingAvailable && !transitAvailable;
                          const isWalkingTooFar = walkingAvailable && isOverOneHour(cachedTimes.walking.duration) && !isWalkingOnlyOption;

                          // Hide walk option completely if driving time is over 2 hours (not just disable)
                          const drivingTimeMinutes = parseDurationForTransit(cachedTimes.driving?.duration);
                          // Only hide walk if we have ACTUAL data (not Infinity from missing data)
                          const hideWalkOption = drivingTimeMinutes !== Infinity && drivingTimeMinutes > 120; // Hide walk for drives over 2 hours

                          // FIX: Hide driving option if drive time is absurdly long (over 6 hours)
                          // This indicates the activities are in different cities/countries and driving isn't realistic
                          // Only hide if we have ACTUAL data (not Infinity from missing data)
                          const hideDrivingOption = drivingTimeMinutes !== Infinity && drivingTimeMinutes > 360; // Hide driving for trips over 6 hours

                          // Show info when walking is only option but long
                          const showWalkingOnlyWarning = isWalkingOnlyOption && isOverOneHour(cachedTimes.walking.duration);
                          
                          // V2: Compact transit divider (replaces full card)
                          const cycleModes = ['driving', 'transit', 'walking'].filter(m => !isModeUnavailable(m));
                          const tierLimitsForTransit = getTierLimits ? getTierLimits() : {};
                          const cycleToNextMode = () => {
                            // Feature gate: all transit modes are planner+ only
                            if (tierLimitsForTransit.allTransitModes === false) {
                              // Show upgrade prompt instead of cycling
                              // TODO: wire to upgrade modal
                              return;
                            }
                            // Lazy-fetch secondary transit modes on first user interaction
                            if (item.from && item.to && Object.keys(cachedTimes).length < 3) {
                              fetchSecondaryTransitModes(transitKey, item.from, item.to, item.transitContext || null);
                            }
                            const currentIdx = cycleModes.indexOf(selectedMode);
                            const nextMode = cycleModes[(currentIdx + 1) % cycleModes.length];
                            setTransportModes(prev => ({ ...prev, [transitKey]: nextMode }));
                          };
                          const mapsUrl = item.from && item.to
                            ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(item.from)}&destination=${encodeURIComponent(item.to)}&travelmode=${selectedMode === 'transit' ? 'transit' : selectedMode === 'walking' ? 'walking' : 'driving'}`
                            : null;

                          return (
                            <div key={`transit-${idx}`} className="tl-item group/transit cursor-pointer" onClick={cycleToNextMode}>
                              <div className="tl-dot transit"></div>

                              {/* Transit pill — aligned with activity cards */}
                              <div className="flex items-center gap-0 py-1.5">
                                <button
                                  onClick={(e) => { e.stopPropagation(); cycleToNextMode(); }}
                                  className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold transition-all whitespace-nowrap group-hover/transit:shadow-sm"
                                  style={{ background: 'rgba(167,216,219,0.12)', border: '1px solid rgba(167,216,219,0.25)', color: '#0e7490' }}
                                  title={`${getTransitLabel()} — click to switch mode`}
                                >
                                  <ActivityIcon emoji={getModeEmoji(selectedMode)} size={12} />
                                  <span>{getTransitLabel()}</span>
                                  <span className="text-[10px] font-semibold" style={{ color: '#64748b' }}>{getSelectedDuration()}</span>
                                  {mapsUrl && (
                                    <a href={mapsUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="opacity-0 group-hover/transit:opacity-100 transition-opacity hover:text-cyan-700" style={{ color: '#94a3b8' }} title="View route on Google Maps">
                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"/></svg>
                                    </a>
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        }

                        const feedbackKey = isActivity
                          ? `day${currentDay}-activity-${item.originalIndex ?? idx}`
                          : isMeal
                            ? `day${currentDay}-meal-${item.originalIndex ?? idx}`
                            : null;
                        const feedback = feedbackKey 
                          ? (isActivity ? activityFeedback[feedbackKey] : mealFeedback[feedbackKey])
                          : null;
                        
                        // Determine styling
                        let borderColor, badgeBorder, icon, title;
                        if (isActivity) {
                          borderColor = 'border-l-emerald-500';
                          badgeBorder = 'border-teal-500';
                          // Prefer LLM-generated emoji, fallback to pattern-based
                          icon = item.emoji || getActivityEmoji(item.activity, item.description);
                          // Guard: if activity name is just the city/destination name, use location or derive from description
                          const baseCity = (currentDayData?.baseCity || currentDayData?.city || selectedDestination?.name || '').toLowerCase().split(',')[0].trim();
                          const actName = (item.activity || '').toLowerCase().trim();
                          if (actName && baseCity && (actName === baseCity || actName === baseCity.split(' ')[0]) && item.location) {
                            title = item.location; // e.g., "Musée d'Orsay" instead of "Paris"
                          } else {
                            title = item.activity;
                          }
                        } else if (isMeal) {
                          borderColor = item._needsReplacement ? 'border-l-[#CD7A4D]/60' : 'border-l-[#CD7A4D]';
                          badgeBorder = 'border-[#CD7A4D]';
                          // Prefer LLM-generated emoji, fallback to pattern-based
                          icon = item.emoji || getMealEmoji(item.restaurant, item.cuisine, item.type || item.meal);
                          // Use restaurant name. {TBD: pick a dinner spot} placeholders
                          // (from post-gen safety net) get rendered as "Pick a dinner spot"
                          // so the CTA reads as intended instead of literal "{TBD: ...}".
                          {
                            const _r = item.restaurant || item.meal || 'Meal';
                            const _tbd = typeof _r === 'string' && /^\{TBD:\s*(.+?)\s*\}$/.exec(_r);
                            if (_tbd) {
                              title = _tbd[1].charAt(0).toUpperCase() + _tbd[1].slice(1);
                            } else {
                              title = _r;
                            }
                          }
                        } else if (isArrival) {
                          borderColor = 'border-l-blue-500';
                          badgeBorder = 'border-blue-500';
                          // Use car icon for road trips, plane icon for flights
                          icon = transportationMode === 'fly'
                            ? (item.type === 'arrival-customs' ? '✈️' : '🛬')
                            : transportationMode === 'train' ? '🚆'
                            : transportationMode === 'ferry' ? '⛴️'
                            : transportationMode === 'custom' ? '🚌'
                            : '🚗';
                          title = item.activity;
                        } else if (isRoadTrip) {
                          borderColor = 'border-l-green-500';
                          badgeBorder = 'border-green-500';
                          icon = '🚗';
                          title = item.activity;
                        } else if (isRentalPickup || isRentalReturn) {
                          borderColor = 'border-l-blue-400';
                          badgeBorder = 'border-blue-400';
                          icon = '🚗';
                          title = item.activity;
                        } else if (isHotelTransit) {
                          borderColor = 'border-l-purple-500';
                          badgeBorder = 'border-purple-500';
                          // Update icon based on selected transport mode
                          const hotelTransitKey = `day${currentDay}-hotel-transit`;
                          const hotelTransitMode = transportModes[hotelTransitKey] || 'driving';
                          icon = hotelTransitMode === 'transit' ? '🚇' : hotelTransitMode === 'walking' ? '🚶' : '🚕';
                          title = item.activity;
                        } else if (isHotelCheckin) {
                          borderColor = 'border-l-purple-500';
                          badgeBorder = 'border-purple-500';
                          icon = '🛎️';
                          title = item.activity;
                        } else if (isHotelReturn) {
                          borderColor = 'border-l-purple-400';
                          badgeBorder = 'border-purple-400';
                          icon = '🏨';
                          title = item.activity;
                        } else if (isCheckout) {
                          borderColor = 'border-l-red-400';
                          badgeBorder = 'border-red-400';
                          icon = '🧳';
                          title = item.activity;
                        } else if (isPortTransit) {
                          borderColor = 'border-l-cyan-500';
                          badgeBorder = 'border-cyan-500';
                          icon = '⛴️';
                          title = item.activity;
                        } else if (isAirportTransit || isAirportArrival || isDeparture) {
                          borderColor = 'border-l-blue-600';
                          badgeBorder = 'border-blue-600';
                          // Departure/airport items use effectiveReturnMode (how you go HOME)
                          // All three types (airport-transit, airport-arrival, departure) relate to going home
                          icon = effectiveReturnMode === 'fly' ? '✈️'
                            : effectiveReturnMode === 'train' ? '🚆'
                            : effectiveReturnMode === 'ferry' ? '⛴️'
                            : effectiveReturnMode === 'custom' ? '🚌'
                            : '🚗';
                          title = item.activity;
                        } else if (isLongDistanceTransfer || isLongDistanceDeparture) {
                          borderColor = 'border-l-amber-500';
                          badgeBorder = 'border-amber-500';
                          // Derive icon from item.travelMode (set by createDayTimeline)
                          // OR from item.type/_roadOverride (set by post-gen repair when
                          // a no-roads resort was detected). Fixes the Hayman Island case
                          // where the card always rendered a car emoji regardless of mode.
                          const tm = (item.travelMode || item.type || '').toLowerCase();
                          if (tm === 'flight' || tm === 'fly' || tm === 'air' || tm === 'seaplane' || tm === 'helicopter') {
                            icon = '✈️';
                          } else if (tm === 'ferry' || tm === 'boat' || tm === 'sea' || tm === 'speedboat') {
                            icon = '⛴️';
                          } else if (tm === 'train' || tm === 'rail') {
                            icon = '🚆';
                          } else {
                            icon = '🚗';
                          }
                          title = item.activity;
                        } else {
                          // Default for any other type
                          borderColor = 'border-l-gray-400';
                          badgeBorder = 'border-gray-400';
                          // Prefer LLM-generated emoji, fallback to pattern-based
                          icon = item.emoji || getActivityEmoji(item.activity || item.name, '');
                          title = item.activity || item.name || 'Activity';
                        }
                        
                        // Determine dot color for timeline
                        let dotColor = 'bg-teal-500';
                        if (isActivity) dotColor = 'bg-teal-500';
                        else if (isMeal) dotColor = 'bg-[#CD7A4D]';
                        else if (isHotelCheckin || isHotelTransit || isHotelReturn) dotColor = 'bg-purple-500';
                        else if (isPortTransit) dotColor = 'bg-cyan-500';
                        else if (isArrival || isAirportTransit || isAirportArrival || isDeparture) dotColor = 'bg-blue-500';
                        else if (isCheckout) dotColor = 'bg-red-400';
                        else if (isLongDistanceTransfer || isLongDistanceDeparture) dotColor = 'bg-amber-500';

                        // Check if this is a free time card
                        const isFreeTime = item.type === 'free-time';

                        // Special rendering for free time cards
                        if (isFreeTime) {
                          return (
                            <React.Fragment key={`${item.type}-${idx}`}>
                              {/* Timeline Layout for Free Time */}
                              <div className="tl-item group">
                                <div className="tl-dot free"></div>

                                {/* Right: Compact Free Time Card — collapsed by default, expand on click */}
                                {(() => {
                                  const freeTimeKey = `${currentDay}-${item.time}`;
                                  const isExpanded = expandedFreeTimeSlots.has(freeTimeKey);
                                  return (
                                    <div
                                      onClick={() => {
                                        setExpandedFreeTimeSlots(prev => {
                                          const next = new Set(prev);
                                          if (next.has(freeTimeKey)) next.delete(freeTimeKey);
                                          else next.add(freeTimeKey);
                                          return next;
                                        });
                                      }}
                                      className={`tl-card free-time ${isExpanded ? 'p-5' : 'px-4 py-2.5'}`}
                                    >
                                      <div className="tl-time mb-2">{formatTimeDisplay(item.time)}</div>
                                      {/* Collapsed: single compact line */}
                                      {!isExpanded && (
                                        <div className="flex items-center justify-between gap-3">
                                          <div className="flex items-center gap-2 min-w-0">
                                            <Sparkles className="w-4 h-4 text-[#1d8e85] flex-shrink-0" />
                                            <span className="text-sm font-medium text-gray-800 truncate" style={FONTS.bodySemibold}>Free Time</span>
                                            <span className="text-xs text-gray-400 flex-shrink-0">·</span>
                                            <span className="text-xs text-[#1d8e85] font-medium flex-shrink-0" style={FONTS.bodyBold}>{item.duration}</span>
                                          </div>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const slotInfo = {
                                                day: currentDay, time: item.time, duration: item.duration,
                                                durationMinutes: item.durationMinutes, sortTime: item.sortTime,
                                                fromLocation: item.fromLocation, toLocation: item.toLocation
                                              };
                                              setSelectedFreeTimeSlot(slotInfo);
                                              setShowFreeTimeModal(true);
                                              generateFreeTimeSuggestions(slotInfo);
                                            }}
                                            className="px-3 py-1 rounded-full text-[10px] font-bold transition-all flex items-center gap-1 flex-shrink-0 hover:bg-teal-50"
                                            style={{ background: 'white', color: '#0d9488', border: '1px solid rgba(13,148,136,0.2)' }}
                                          >
                                            <span>Ask arrivo →</span>
                                          </button>
                                        </div>
                                      )}

                                      {/* Expanded: full details */}
                                      {isExpanded && (
                                        <>
                                          <div className="flex items-start justify-between">
                                            <div>
                                              <h3 className="text-xl text-gray-900" style={FONTS.headingSemibold}>Free Time</h3>
                                              <p className="text-sm text-gray-600 mt-1" style={FONTS.body}>{item.description}</p>
                                            </div>
                                            <div className="flex items-center gap-1.5 text-[#1d8e85]">
                                              <Clock className="w-4 h-4" />
                                              <span className="text-sm font-medium" style={FONTS.bodyBold}>{item.duration}</span>
                                            </div>
                                          </div>

                                          {(item.fromLocation || item.toLocation) &&
                                           !(item.fromLocation && item.toLocation && item.fromLocation.split(',')[0] === item.toLocation.split(',')[0]) && (
                                            <div className="mt-3 pt-3 border-t border-teal-100 text-xs text-gray-500 flex items-center gap-3" style={FONTS.body}>
                                              {item.fromLocation && (
                                                <span className="flex items-center gap-1">
                                                  <MapPin className="w-3 h-3 text-teal-500" />
                                                  <span>Near: {item.fromLocation.split(',')[0]}</span>
                                                </span>
                                              )}
                                              {item.fromLocation && item.toLocation && <span>→</span>}
                                              {item.toLocation && (
                                                <span className="flex items-center gap-1">
                                                  <Navigation className="w-3 h-3 text-teal-500" />
                                                  <span>Next: {item.toLocation.split(',')[0]}</span>
                                                </span>
                                              )}
                                            </div>
                                          )}

                                          {/* Suggestions */}
                                          <div className="mt-4">
                                            <p className="text-xs font-bold text-teal-600 uppercase tracking-wide mb-2" style={FONTS.bodyBold}>Suggestions</p>
                                            <div className="flex flex-wrap gap-2">
                                              {item.suggestions?.map((suggestion, sIdx) => (
                                                <button
                                                  key={sIdx}
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    const dayIndex = currentDay;
                                                    const cityName = currentDayData?.baseCity || selectedDestination?.name || 'the area';
                                                    addActivity(dayIndex, {
                                                      time: item.time,
                                                      duration: item.duration,
                                                      activity: suggestion.label,
                                                      location: item.fromLocation || cityName,
                                                      description: suggestion.description || `Explore ${suggestion.label} in ${cityName}`,
                                                      priceTier: '$',
                                                      type: 'custom'
                                                    });
                                                  }}
                                                  className="px-3 py-2 bg-white border border-gray-200 rounded-full text-sm text-gray-700 hover:border-teal-400 hover:bg-teal-50 transition-all flex items-center gap-2"
                                                  style={FONTS.body}
                                                >
                                                  <Sparkles className="w-3.5 h-3.5 text-teal-500" />
                                                  <span>{suggestion.label}</span>
                                                </button>
                                              ))}
                                            </div>

                                            {/* Ask Arrivo Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const slotInfo = {
                                                  day: currentDay, time: item.time, duration: item.duration,
                                                  durationMinutes: item.durationMinutes, sortTime: item.sortTime,
                                                  fromLocation: item.fromLocation, toLocation: item.toLocation
                                                };
                                                setSelectedFreeTimeSlot(slotInfo);
                                                setShowFreeTimeModal(true);
                                                generateFreeTimeSuggestions(slotInfo);
                                                setShowCustomActivityInput(true);
                                              }}
                                              className="mt-3 px-3 py-2 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg text-sm hover:from-teal-600 hover:to-emerald-600 transition-all flex items-center gap-2"
                                              style={FONTS.body}
                                            >
                                              <Sparkles className="w-4 h-4" />
                                              <span>Ask arrivo</span>
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  );
                                })()}
                              </div>
                            </React.Fragment>
                          );
                        }

                        // ─── Special Flight Card (arrival/departure) — styled like mobile ───
                        if (item.isArrivalFlightCard || item.isDepartureFlightCard) {
                          const flightInfo = item.isArrivalFlightCard ? arrivalFlight : departureFlight;
                          const hasFlightDetails = flightInfo?.flightNumber || flightInfo?.airline;
                          const isArrivalCard = item.isArrivalFlightCard;
                          // For arrival: home → destination (JFK → CUN). For departure: destination → home (CUN → JFK).
                          const fromCode = isArrivalCard ? (item.homeAirportCode || flightInfo?.from || '') : (item.airportCode || '');
                          const toCode = isArrivalCard ? (item.airportCode || '') : (item.homeAirportCode || flightInfo?.from || '');
                          const routeLabel = fromCode && toCode ? `${fromCode} → ${toCode}` : item.location || '';

                          return (
                            <React.Fragment key={`flight-${idx}`}>
                              <div className="tl-item group">
                                <div className="tl-dot transit"></div>
                                <div
                                  className="tl-card cursor-pointer overflow-hidden"
                                  style={{
                                    background: isArrivalCard
                                      ? 'rgba(167,216,219,0.08)'
                                      : 'rgba(30,58,95,0.06)',
                                    borderLeft: isArrivalCard
                                      ? '3px solid #a7d8db'
                                      : '3px solid #1e3a5f',
                                    border: isArrivalCard
                                      ? '1px solid rgba(167,216,219,0.25)'
                                      : '1px solid rgba(30,58,95,0.15)',
                                    borderLeftWidth: '3px',
                                    borderLeftColor: isArrivalCard ? '#a7d8db' : '#1e3a5f',
                                    padding: '12px 16px',
                                  }}
                                  onClick={() => {
                                    const cardId = `day${currentDay}-${item.type}-${idx}`;
                                    setExpandedCards(prev => ({ ...prev, [cardId]: !prev[cardId] }));
                                  }}
                                >
                                  <div className="flex items-center gap-2.5">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={isArrivalCard ? '#0e7490' : '#1e3a5f'} strokeWidth="2"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                                    <div className="flex-1 min-w-0">
                                      {hasFlightDetails ? (
                                        <>
                                          <div className="text-[13px] font-extrabold truncate" style={{ color: isArrivalCard ? '#0e7490' : '#1e3a5f' }}>
                                            {flightInfo.flightNumber}{flightInfo.airline ? ` · ${flightInfo.airline}` : ''}
                                          </div>
                                          <div className="text-[11px] mt-0.5" style={{ color: '#64748b' }}>
                                            {routeLabel}{isArrivalCard ? ` · Arrives ${formatTimeDisplay(item.time)}` : ` · Departs ${formatTimeDisplay(item.time)}`}
                                          </div>
                                        </>
                                      ) : (
                                        <>
                                          <div className="text-[13px] font-extrabold truncate" style={{ color: isArrivalCard ? '#0e7490' : '#1e3a5f' }}>
                                            {toTitleCase(stripEmoji(item.activity))}
                                          </div>
                                          {routeLabel && (
                                            <div className="text-[11px] mt-0.5" style={{ color: '#64748b' }}>
                                              {routeLabel}{isArrivalCard ? ` · Arrives ${formatTimeDisplay(item.time)}` : ` · Departs ${formatTimeDisplay(item.time)}`}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Expanded: description + Amadeus flight search */}
                                  {expandedCards[`day${currentDay}-${item.type}-${idx}`] && (
                                    <div className="mt-2.5 pt-2.5" style={{ borderTop: '1px solid rgba(167,216,219,0.25)' }}>
                                      <p className="text-[12px] mb-2" style={{ color: '#64748b', lineHeight: '1.5' }}>{item.description}</p>
                                      {/* Amadeus flight pricing will render here via existing logic */}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        }

                        // Generate sortable ID for this item
                        const sortableId = (isActivity || isMeal) ? `sortable-${item.type}-${item.originalIndex ?? idx}` : null;
                        const isDraggable = !!(isActivity || isMeal);

                        return (
                          <React.Fragment key={`${item.type}-${idx}`}>
                            {/* Timeline Layout — wrapped in SortableTimelineCard for activities/meals */}
                            <SortableTimelineCard id={sortableId || `static-${idx}`} isDraggable={isDraggable} className="tl-item group">
                              {(dragListeners) => {
                                // Generate unique card ID for expand/collapse state
                                const cardId = `day${currentDay}-${item.type}-${idx}`;
                                const isExpanded = expandedCards[cardId] || false;
                                const activeTab = cardActiveTab[cardId] || 'overview';
                                const canExpand = isActivity || isMeal || isRoadTrip || isArrival || isDeparture || isHotelCheckin || isHotelReturn || isLongDistanceTransfer || isLongDistanceDeparture;

                                const isFlexibleItem = flexibleMode && (isActivity || isMeal);
                                const flexKeptKey = `day${currentDay}-${isActivity ? 'activity' : 'meal'}-${item.originalIndex ?? idx}`;
                                const isKept = keptActivities[flexKeptKey];
                                const isLocked = item.locked || item.source === 'must-do';

                                const dotClass = isMeal ? 'meal' : (isArrival || isAirportTransit || isAirportArrival || isDeparture || isHotelTransit || isStationTransit || isPortTransit || isTransit || isReturnTransit || isLongDistanceTransfer || isLongDistanceDeparture) ? 'transit' : '';

                                return (
                                  <>
                                    <div className={`tl-dot ${dotClass}`}></div>
                                    <div
                                      className={`tl-card ${isExpanded ? 'ring-2 ring-teal-200' : ''} ${canExpand ? 'cursor-pointer' : ''} ${isMeal ? 'meal-card' : ''} ${isLocked ? 'locked border-l-[3px] border-l-teal-500' : ''} ${isFlexibleItem ? (isKept ? 'bg-green-50 border-2 border-green-300' : 'bg-amber-50/30 border-2 border-dashed border-amber-300') : ''}`}
                                      onClick={() => {
                                        if (!canExpand) return;
                                        const newExpandedState = !expandedCards[cardId];
                                        setExpandedCards(prev => ({ ...prev, [cardId]: newExpandedState }));
                                        const city = currentDayData?.baseCity || itinerary?.destination?.split(',')[0]?.trim();
                                        if (newExpandedState) {
                                          if (isActivity && item.activity) {
                                            const country = itinerary?.destination?.split(',').slice(-1)[0]?.trim();
                                            fetchActivityBookingInfo(item.activity, city, country);
                                          }
                                          const venueName = item.restaurant || item.activity || item.name;
                                          if (venueName && city) {
                                            fetchVenueInfo(venueName, city, item.placeData?.placeId);
                                          }
                                        }
                                      }}
                                    >
                                      {isDraggable && dragListeners && (
                                        <div className="drag-handle" {...dragListeners}>
                                          <div className="drag-dot-row"><div className="drag-dot"></div><div className="drag-dot"></div></div>
                                          <div className="drag-dot-row"><div className="drag-dot"></div><div className="drag-dot"></div></div>
                                          <div className="drag-dot-row"><div className="drag-dot"></div><div className="drag-dot"></div></div>
                                        </div>
                                      )}
                                      
                                      <div className="tl-time">
                                        {(isActivity || isMeal) && inlineTimeEdit?.day === currentDay && inlineTimeEdit?.index === (item.originalIndex ?? idx) ? (
                                          <div className="relative inline-block w-32 mb-1" onClick={e => e.stopPropagation()}>
                                            <input
                                              type="time"
                                              defaultValue={minutesToTime(parseTime(item.time))}
                                              className="w-full text-sm border border-teal-300 rounded-lg px-2 py-1 bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                              style={FONTS.body}
                                              autoFocus
                                              onBlur={(e) => {
                                                const newTime = e.target.value;
                                                if (newTime && parseTime(newTime) !== parseTime(item.time)) {
                                                  updateItemTime(currentDay, inlineTimeEdit.index, inlineTimeEdit.isActivity ? 'activity' : 'meal', newTime);
                                                }
                                                setInlineTimeEdit(null);
                                              }}
                                              onKeyDown={(e) => {
                                                if (e.key === 'Enter') e.target.blur();
                                                if (e.key === 'Escape') setInlineTimeEdit(null);
                                              }}
                                            />
                                          </div>
                                        ) : (
                                          <span
                                            className={`${isMeal ? 'text-[#CD7A4D]' : ''} ${(isActivity || isMeal) ? 'cursor-pointer hover:underline decoration-dotted underline-offset-2' : ''}`}
                                            onClick={(e) => {
                                              if (isActivity || isMeal) {
                                                e.stopPropagation();
                                                setInlineTimeEdit({ day: currentDay, index: item.originalIndex ?? idx, isActivity, currentTime: item.time });
                                              }
                                            }}
                                            title={(isActivity || isMeal) ? "Click to edit time" : undefined}
                                          >
                                            {formatTimeDisplay(item.time)}
                                          </span>
                                        )}
                                      </div>

                                      <div className="">
                                {/* Flexible Mode Badge */}
                                {isFlexibleItem && !isKept && (
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full" style={FONTS.bodyBold}>Suggestion</span>
                                  </div>
                                )}
                                {isFlexibleItem && isKept && (
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <span className="text-[10px] uppercase tracking-wider font-bold text-green-600 bg-green-100 px-2 py-0.5 rounded-full" style={FONTS.bodyBold}>Confirmed</span>
                                  </div>
                                )}

                                {/* COLLAPSED VIEW: Single-line compact view */}
                                {!isExpanded && canExpand && (
                                  <div className="flex items-center gap-3" onClick={(e) => {
                                    e.stopPropagation();
                                    setExpandedCards(prev => ({ ...prev, [cardId]: true }));
                                    const city = currentDayData?.baseCity || itinerary?.destination?.split(',')[0]?.trim();
                                    if (isActivity && item.activity) {
                                      const country = itinerary?.destination?.split(',').slice(-1)[0]?.trim();
                                      fetchActivityBookingInfo(item.activity, city, country);
                                    }
                                    // Lazy-fetch venue info on expand
                                    const venueName = item.restaurant || item.activity || item.name;
                                    if (venueName && city) {
                                      fetchVenueInfo(venueName, city, item.placeData?.placeId);
                                    }
                                  }}>
                                    <ActivityIcon emoji={icon} size={18} className="flex-shrink-0" />
                                    <h3 className={`text-[14px] font-extrabold truncate flex-1 ${item._needsReplacement ? 'text-gray-400 italic' : 'text-[#313143]'}`} style={{ letterSpacing: '-0.02em' }}>{toTitleCase(stripEmoji(title))}</h3>
                                    {item._needsReplacement && (
                                      <span className="text-[10px] font-bold text-teal-600 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">Tap for picks</span>
                                    )}
                                    {item.time && (
                                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0" style={FONTS.body}>
                                        {formatTimeDisplay(item.time)}
                                      </span>
                                    )}
                                    {isActivity && item.priceTier && (
                                      <span className="text-[10px] font-extrabold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full flex-shrink-0" style={{ fontFamily: '"Segoe UI Symbol", "Noto Sans Thai", "Noto Sans", system-ui, sans-serif' }}>
                                        {formatPriceTier(item.priceTier)}
                                      </span>
                                    )}
                                    {!isActivity && item.priceRange && (
                                      <span className="text-[10px] font-extrabold flex-shrink-0" style={{ color: '#CD7A4D', fontFamily: '"Segoe UI Symbol", "Noto Sans Thai", "Noto Sans", system-ui, sans-serif' }}>{formatPriceTier(item.priceRange)}</span>
                                    )}
                                    {/* Community popularity badge for highly-rated venues */}
                                    {item.placeData?.rating >= 4.5 && (
                                      <span className="text-[10px] text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap" style={FONTS.bodyMedium}>
                                        {item.placeData.rating >= 4.7 ? 'Locals Love' : 'Popular'}
                                      </span>
                                    )}
                                    {isLocked && <Lock className="w-3.5 h-3.5 text-teal-500 flex-shrink-0" />}
                                    <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                  </div>
                                )}

                                {/* EXPANDED VIEW: Full card content */}
                                {(isExpanded || !canExpand) && (
                                <>
                                <div className="flex items-start justify-between gap-4">
                                  <div className="flex-1">
                                    {/* Title */}
                                    <div className="flex items-center gap-3 mb-2">
                                      <ActivityIcon emoji={icon} size={22} />
                                      <h3 className={`text-xl ${item._needsReplacement ? 'text-gray-400 italic' : 'text-gray-900'}`} style={FONTS.headingSemibold}>{toTitleCase(stripEmoji(title))}</h3>
                                      {/* Task 14: Place Detail launcher — opens modal with photos/hours/map/reviews */}
                                      {(() => {
                                        const venueName = item.restaurant || item.activity || item.name;
                                        if (!venueName || item._needsReplacement) return null;
                                        // T7 (web): checkout cards should show the hotel + city they're
                                        // checking out of — NOT the trip-wide destination concat.
                                        // hotelName/checkoutCity are stamped by the engine when the card
                                        // is created; fall back to the day's baseCity for older cards.
                                        let city;
                                        let country;
                                        let displayName;
                                        if (item.type === 'checkout' || item.type === 'hotel-checkout') {
                                          displayName = item.hotelName || stripEmoji(venueName).trim();
                                          city = item.checkoutCity || currentDayData?.baseCity || itinerary?.destination?.split(',')[0]?.trim();
                                          country = itinerary?.destination?.split(',').slice(-1)[0]?.trim();
                                        } else {
                                          displayName = stripEmoji(venueName).trim();
                                          city = currentDayData?.baseCity || itinerary?.destination?.split(',')[0]?.trim();
                                          country = itinerary?.destination?.split(',').slice(-1)[0]?.trim();
                                        }
                                        return (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openPlaceDetail({
                                                placeName: displayName,
                                                city,
                                                country,
                                                placeCategory: isMeal ? 'dining' : 'activity',
                                                placeData: item.placeData,
                                                item,
                                              });
                                            }}
                                            className="text-[10px] font-bold text-teal-600 bg-teal-50 border border-teal-200 hover:bg-teal-100 px-2 py-0.5 rounded-full whitespace-nowrap"
                                            title="View place details"
                                          >
                                            Details
                                          </button>
                                        );
                                      })()}
                                      {item._needsReplacement && (
                                        <span className="text-[10px] font-bold text-teal-600 bg-teal-50 border border-teal-200 px-2 py-0.5 rounded-full whitespace-nowrap">Open Slot</span>
                                      )}
                                      {/* Source badge — hidden on Open Slot to avoid contradicting the empty-state copy.
                                          Hope's feedback: "I'm interpreting this as I have free time for dinner / I can pick
                                          my own. But then the rating at the bottom is making me think there's a
                                          recommendation already populated?" The "Open Slot" + "4.5★" combination was
                                          confusing because the rating came from a previously-suggested restaurant whose
                                          placeData survived even after the slot was reset. Suppress all source/rating
                                          badges when _needsReplacement is true. */}
                                      {item.source && !item._needsReplacement && (
                                        <span className={`text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded-full ${
                                          item.source === 'must-do' ? 'bg-teal-50 text-teal-600 border border-teal-200' :
                                          item.source === 'community' ? 'bg-purple-50 text-purple-600 border border-purple-200' :
                                          'bg-gray-50 text-gray-500 border border-gray-200'
                                        }`} style={FONTS.bodyMedium}>
                                          {item.source === 'must-do' ? 'Must-Do' : item.source === 'community' ? 'Community' : 'arrivo Pick'}
                                        </span>
                                      )}
                                      {/* Community popularity badge — also gated on !_needsReplacement (see above) */}
                                      {item.placeData?.rating >= 4.5 && !item._needsReplacement && (
                                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200" style={FONTS.bodyMedium}>
                                          {item.placeData.rating >= 4.7 ? `Locals Love · ${item.placeData.rating}★` : `Popular · ${item.placeData.rating}★`}
                                        </span>
                                      )}
                                      {/* Lock indicator */}
                                      {isLocked && (
                                        <Lock className="w-4 h-4 text-teal-500 flex-shrink-0" />
                                      )}
                                      {/* Expand/Collapse Chevron */}
                                      {canExpand && (
                                        <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 ml-auto ${isExpanded ? 'rotate-180' : ''}`} />
                                      )}
                                    </div>
                                    {/* Time overlap warning */}
                                    {overlapIndices.has(idx) && (
                                      <div className="flex items-center gap-1.5 mb-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded-lg w-fit">
                                        <span className="text-amber-500 text-sm">&#9888;&#65039;</span>
                                        <span className="text-xs text-amber-700 font-medium" style={FONTS.bodySemibold}>Time overlap</span>
                                      </div>
                                    )}

                                    {/* Details Row */}
                                    <div className="flex flex-wrap items-center gap-4 mb-3">
                                      {isActivity && item.duration && (
                                        <span className="flex items-center gap-1.5 text-sm text-gray-600" style={FONTS.body}>
                                          ⏱️ {item.duration}
                                        </span>
                                      )}
                                      {/* Show duration for meals - editable */}
                                      {isMeal && (
                                        <span className="flex items-center gap-1.5 text-sm text-gray-600" style={FONTS.body}>
                                          ⏱️ {item.duration || '1 hour'}
                                        </span>
                                      )}
                                      {/* Show duration for hotel transit with real time */}
                                      {isHotelTransit && (
                                        <span className="flex items-center gap-1.5 text-sm text-gray-600" style={FONTS.body}>
                                          ⏱️ {(() => {
                                            const hotelTransitKey = `day${currentDay}-hotel-transit`;
                                            let hotelTransitMode = transportModes[hotelTransitKey] || 'driving';
                                            const cached = transitTimes[hotelTransitKey];

                                            // If walking is selected but takes > 1 hour, show driving time instead
                                            if (hotelTransitMode === 'walking' && cached?.walking) {
                                              const walkDuration = cached.walking.duration || '';
                                              if (walkDuration.includes('hr') || walkDuration.includes('hour') || parseInt(walkDuration) >= 60) {
                                                hotelTransitMode = 'driving';
                                              }
                                            }

                                            if (cached && cached[hotelTransitMode]) {
                                              return cached[hotelTransitMode].duration;
                                            }
                                            return item.duration || '~30 mins';
                                          })()}
                                        </span>
                                      )}
                                      {isActivity && item.priceTier && (
                                        <span className="px-2 py-0.5 bg-teal-50 text-teal-600 rounded-full text-[10px] font-extrabold" style={{ fontFamily: '"Segoe UI Symbol", "Noto Sans Thai", "Noto Sans", system-ui, sans-serif' }}>
                                          {formatPriceTier(item.priceTier)}
                                        </span>
                                      )}
                                      {!isActivity && item.cuisine && (
                                        <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold" style={{ background: 'rgba(205,122,77,0.1)', color: '#CD7A4D' }}>
                                          {item.cuisine}
                                        </span>
                                      )}
                                      {!isActivity && item.priceRange && (
                                        <span className="text-[10px] font-extrabold" style={{ color: '#CD7A4D', fontFamily: '"Segoe UI Symbol", "Noto Sans Thai", "Noto Sans", system-ui, sans-serif' }}>{formatPriceTier(item.priceRange)}</span>
                                      )}
                                    </div>

                                    {/* Location/Address - Clickable with Map Link */}
                                    {(item.location || item.address) && (
                                      <div className="flex items-center gap-2 mb-2">
                                        <a
                                          href={getSmartLink(item, isActivity ? 'activity' : 'meal', itinerary?.destination?.split(',')[0] || '')}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm text-gray-600 hover:text-teal-600 flex items-start gap-2 group/link transition-colors"
                                          style={FONTS.body}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                          <span className="group-hover/link:underline">{item.location || item.address}</span>
                                          <ExternalLink className="w-3 h-3 opacity-0 group-hover/link:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
                                        </a>
                                      </div>
                                    )}

                                    {/* Needs Replacement — Ask Arrivo prompt for placeholder meals */}
                                    {isMeal && item._needsReplacement && (
                                      <div className="mb-3 p-3 bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl">
                                        <p className="text-xs text-teal-700 mb-2" style={FONTS.body}>
                                          This is an open meal slot. Tap below for a personalized restaurant pick!
                                        </p>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const mealType = (item.meal || '').toLowerCase();
                                            setFeedbackDialogData({
                                              type: 'meal',
                                              day: currentDay,
                                              index: item.originalIndex ?? idx,
                                              name: item.restaurant || item.meal || 'Meal',
                                              feedbackKey: `day${currentDay}-meal-${item.originalIndex ?? idx}`,
                                              mealType
                                            });
                                            setSelectedFeedbackOption('replace_one');
                                            setShowFeedbackDialog(true);
                                          }}
                                          className="px-3 py-1.5 bg-gradient-to-r from-teal-500 to-emerald-500 text-white rounded-lg text-xs font-bold hover:from-teal-600 hover:to-emerald-600 transition-all flex items-center gap-1.5"
                                          style={FONTS.bodyBold}
                                        >
                                          <Sparkles className="w-3.5 h-3.5" />
                                          Get Restaurant Pick
                                        </button>
                                      </div>
                                    )}

                                    {/* Live Amadeus Flight Pricing (for arrival & departure flight cards) */}
                                    {(item.isArrivalFlightCard || item.isDepartureFlightCard) && item.airportCode && item.airportCode !== '???' && features.AMADEUS_FLIGHTS && (() => {
                                      // If no home airport set, show prompt to add one
                                      if (!item.homeAirportCode) {
                                        return (
                                          <div className="mt-3 pt-3 border-t border-blue-200">
                                            <div className="p-2.5 bg-blue-50 rounded-xl border border-blue-200">
                                              <p className="text-xs text-blue-700" style={FONTS.body}>
                                                ✈️ Add your home airport in <strong>Settings → Travel</strong> to see live flight prices
                                              </p>
                                            </div>
                                            <a
                                              href={getFlightsLink(item.airportCode, '', null)}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="inline-flex items-center gap-2 mt-2 px-4 py-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl text-sm font-medium transition-colors"
                                              style={FONTS.body}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              ✈️ Search Flights
                                              <ExternalLink className="w-3 h-3" />
                                            </a>
                                          </div>
                                        );
                                      }
                                      // Determine route: arrival = home→dest, departure = dest→home
                                      const fromCode = item.isArrivalFlightCard ? item.homeAirportCode : item.airportCode;
                                      const toCode = item.isArrivalFlightCard ? item.airportCode : item.homeAirportCode;
                                      const adBaseRouteKey = `${fromCode}-${toCode}`;
                                      const adCabinForRoute = flightCabinClass[adBaseRouteKey] || preferredTravelClass || 'ECONOMY';
                                      const routeKey = `${adBaseRouteKey}-${adCabinForRoute}`;
                                      const adAirlineFilter = flightAirlineFilter[adBaseRouteKey] || null;
                                      const priceData = flightPrices[routeKey];

                                      // Compute both outbound and return dates for round-trip booking links
                                      const tripStartDate = userProfile?.preferences?.startDate || null;
                                      const tripEndDate = userProfile?.preferences?.endDate || (() => {
                                        const start = userProfile?.preferences?.startDate;
                                        const dur = userProfile?.preferences?.duration;
                                        if (start && dur) {
                                          const d = new Date(start + 'T12:00:00');
                                          d.setDate(d.getDate() + dur - 1);
                                          return d.toISOString().split('T')[0];
                                        }
                                        return null;
                                      })();
                                      // This leg's specific date
                                      const flightDate = item.isArrivalFlightCard ? tripStartDate : tripEndDate;

                                      // For non-multi-stop trips: use round-trip pricing on arrival card
                                      // For departure card on non-multi-stop: reference the arrival's round-trip data
                                      const isMultiStopTrip = selectedDestination?.isMultiStop;
                                      const useRoundTrip = !isMultiStopTrip && item.isArrivalFlightCard && tripStartDate && tripEndDate;

                                      // For departure on non-multi-stop: check if arrival already has round-trip data
                                      const arrivalBaseRouteKey = item.isDepartureFlightCard && !isMultiStopTrip
                                        ? `${item.homeAirportCode}-${item.airportCode}` : null;
                                      const arrivalRouteKey = arrivalBaseRouteKey ? `${arrivalBaseRouteKey}-${flightCabinClass[arrivalBaseRouteKey] || adCabinForRoute}` : null;
                                      const arrivalHasRoundTrip = arrivalRouteKey && flightPrices[arrivalRouteKey]?.isRoundTrip;

                                      // Get available airlines from results for the filter dropdown
                                      const adAvailableAirlines = priceData?.data?.availableAirlines || [];
                                      const adStopsFilter = flightStopsFilter[adBaseRouteKey] ?? null;
                                      const adTimeFilter = flightTimeFilter[adBaseRouteKey] || null;
                                      // Filter offers by all active filters
                                      const adFilterOffers = (offers) => {
                                        let filtered = offers;
                                        if (adAirlineFilter) {
                                          filtered = filtered.filter(o => o.airlines?.includes(adAirlineFilter) ||
                                            o.itineraries?.[0]?.segments?.some(s => s.carrier === adAirlineFilter));
                                        }
                                        if (adStopsFilter !== null) {
                                          filtered = filtered.filter(o => {
                                            const segs = o.itineraries?.[0]?.segments || [];
                                            return (segs.length - 1) <= adStopsFilter;
                                          });
                                        }
                                        if (adTimeFilter) {
                                          filtered = filtered.filter(o => {
                                            const depTime = o.itineraries?.[0]?.segments?.[0]?.departure?.time;
                                            if (!depTime) return true;
                                            const hour = new Date(depTime).getHours();
                                            if (adTimeFilter === 'morning') return hour >= 5 && hour < 12;
                                            if (adTimeFilter === 'afternoon') return hour >= 12 && hour < 18;
                                            if (adTimeFilter === 'evening') return hour >= 18 || hour < 5;
                                            return true;
                                          });
                                        }
                                        return filtered;
                                      };

                                      // Auto-fetch price when card renders
                                      if (!priceData && !flightPrices[routeKey]?.loading) {
                                        if (item.isDepartureFlightCard && arrivalHasRoundTrip) {
                                          // Departure on non-multi-stop: reuse arrival round-trip data (return leg)
                                          const arrData = flightPrices[arrivalRouteKey];
                                          setTimeout(() => {
                                            setFlightPrices(prev => ({
                                              ...prev,
                                              [routeKey]: {
                                                loading: false,
                                                isRoundTrip: true,
                                                isReturnLeg: true,
                                                linkedRouteKey: arrivalRouteKey,
                                                data: arrData?.data ? {
                                                  ...arrData.data,
                                                  // Swap to show return itinerary as primary
                                                  duration: arrData.data.returnDuration || arrData.data.duration,
                                                  segments: arrData.data.returnSegments?.length > 0 ? arrData.data.returnSegments : arrData.data.segments,
                                                } : null,
                                                fallbackUsed: arrData?.fallbackUsed || false,
                                                noResults: arrData?.noResults,
                                              }
                                            }));
                                          }, 0);
                                        } else {
                                          setTimeout(() => fetchFlightPrice(fromCode, toCode, flightDate, {
                                            isRoundTrip: useRoundTrip,
                                            returnDate: useRoundTrip ? tripEndDate : null,
                                            cabinClass: adCabinForRoute,
                                          }), 0);
                                        }
                                      }

                                      const isFlightExpanded = expandedFlightPrices[routeKey];
                                      const adFilteredOffers = priceData?.data?.allOffers ? adFilterOffers(priceData.data.allOffers) : [];
                                      const cheapestOffer = adFilteredOffers[0];
                                      const isRT = priceData?.isRoundTrip;
                                      const priceLabel = isRT ? 'Round-trip' : '';
                                      const cheapestPrice = cheapestOffer ? new Intl.NumberFormat('en-US', { style: 'currency', currency: cheapestOffer.currency || priceData.data.currency || 'USD' }).format(cheapestOffer.price) : (priceData?.data?.price ? `from ${new Intl.NumberFormat('en-US', { style: 'currency', currency: priceData.data.currency || 'USD' }).format(priceData.data.price)}` : null);

                                      return (
                                        <div className="mt-3 pt-3 border-t border-blue-200 space-y-2">
                                          {/* Collapsed summary bar — always visible */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setExpandedFlightPrices(prev => ({ ...prev, [routeKey]: !prev[routeKey] }));
                                            }}
                                            className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                                              isFlightExpanded ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                                            }`}
                                          >
                                            <div className="flex items-center gap-2">
                                              <span className="text-sm flex items-center gap-1" style={FONTS.body}><Plane className="w-3.5 h-3.5 text-teal-600" /> Flight Options</span>
                                              {priceData?.loading && (
                                                <div className="w-3.5 h-3.5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                                              )}
                                              {cheapestPrice && !priceData?.loading && (
                                                <span className="text-xs text-teal-700 font-medium bg-teal-100 px-2 py-0.5 rounded-full" style={FONTS.bodyBold}>
                                                  {cheapestPrice}{isRT ? ' RT' : ''}
                                                </span>
                                              )}
                                            </div>
                                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isFlightExpanded ? 'rotate-180' : ''}`} />
                                          </button>

                                          {/* Expanded flight offers */}
                                          {isFlightExpanded && (
                                            <div className="space-y-2">
                                          {/* Cabin Class + Airline Filter Controls */}
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-[10px] text-gray-500 uppercase tracking-wide" style={FONTS.body}>Class:</span>
                                              <select
                                                value={adCabinForRoute}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  const newCabin = e.target.value;
                                                  if (adBaseRouteKey) {
                                                    setFlightCabinClass(prev => ({ ...prev, [adBaseRouteKey]: newCabin }));
                                                  }
                                                }}
                                                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-teal-400 focus:border-teal-400 cursor-pointer"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <option value="ECONOMY">Economy</option>
                                                <option value="PREMIUM_ECONOMY">Premium Economy</option>
                                                <option value="BUSINESS">Business</option>
                                                <option value="FIRST">First</option>
                                              </select>
                                            </div>
                                            {adAvailableAirlines.length > 1 && (
                                              <div className="flex items-center gap-1.5">
                                                <span className="text-[10px] text-gray-500 uppercase tracking-wide" style={FONTS.body}>Airline:</span>
                                                <select
                                                  value={adAirlineFilter || ''}
                                                  onChange={(e) => {
                                                    e.stopPropagation();
                                                    const val = e.target.value || null;
                                                    if (adBaseRouteKey) setFlightAirlineFilter(prev => ({ ...prev, [adBaseRouteKey]: val }));
                                                  }}
                                                  className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-teal-400 focus:border-teal-400 cursor-pointer"
                                                  style={FONTS.body}
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <option value="">All Airlines</option>
                                                  {adAvailableAirlines.map(code => (
                                                    <option key={code} value={code}>{resolveAirlineName(code, priceData?.data?.dictionaries) || code}</option>
                                                  ))}
                                                </select>
                                              </div>
                                            )}
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-[10px] text-gray-500 uppercase tracking-wide" style={FONTS.body}>Stops:</span>
                                              <select
                                                value={adStopsFilter ?? ''}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  const val = e.target.value === '' ? null : parseInt(e.target.value);
                                                  if (adBaseRouteKey) setFlightStopsFilter(prev => ({ ...prev, [adBaseRouteKey]: val }));
                                                }}
                                                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-teal-400 focus:border-teal-400 cursor-pointer"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <option value="">Any</option>
                                                <option value="0">Nonstop</option>
                                                <option value="1">1 stop max</option>
                                              </select>
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                              <span className="text-[10px] text-gray-500 uppercase tracking-wide" style={FONTS.body}>Time:</span>
                                              <select
                                                value={adTimeFilter || ''}
                                                onChange={(e) => {
                                                  e.stopPropagation();
                                                  const val = e.target.value || null;
                                                  if (adBaseRouteKey) setFlightTimeFilter(prev => ({ ...prev, [adBaseRouteKey]: val }));
                                                }}
                                                className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 bg-white text-gray-700 focus:ring-1 focus:ring-teal-400 focus:border-teal-400 cursor-pointer"
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                <option value="">Any Time</option>
                                                <option value="morning">Morning (5a-12p)</option>
                                                <option value="afternoon">Afternoon (12p-6p)</option>
                                                <option value="evening">Evening (6p-5a)</option>
                                              </select>
                                            </div>
                                          </div>
                                          {priceData?.loading && (
                                            <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-xl">
                                              <div className="w-3.5 h-3.5 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
                                              <span className="text-xs text-gray-500" style={FONTS.body}>Loading live prices...</span>
                                            </div>
                                          )}
                                          {/* Round-trip indicator */}
                                          {isRT && (
                                            <div className="px-3 py-1.5 bg-blue-50 rounded-lg border border-blue-100">
                                              <p className="text-xs text-blue-700" style={FONTS.body}>
                                                {priceData?.isReturnLeg
                                                  ? '↩️ Return leg — prices shown are round-trip totals (booked with outbound)'
                                                  : '✈️ Round-trip prices — includes both outbound and return flights'}
                                              </p>
                                            </div>
                                          )}
                                          {/* Flight offers list */}
                                          {priceData?.data?.allOffers?.length > 0 && (() => {
                                            // Show 3 by default, all if "More Options" clicked
                                            const showingAll = expandedFlightPrices[`${routeKey}-all`];
                                            const adAllFilteredExpanded = priceData.data.allOffersExpanded ? adFilterOffers(priceData.data.allOffersExpanded) : adFilteredOffers;
                                            const offersToShow = showingAll ? adAllFilteredExpanded : adFilteredOffers;
                                            const hasMore = adAllFilteredExpanded.length > adFilteredOffers.length;
                                            const dicts = priceData.data.dictionaries || {};
                                            return (
                                            <div className="space-y-2">
                                              {offersToShow.map((offer, offerIdx) => {
                                                const itin = offer.itineraries?.[0];
                                                const segs = itin?.segments || [];
                                                const firstSeg = segs[0];
                                                const lastSeg = segs[segs.length - 1];
                                                const stops = segs.length - 1;
                                                // Parse ISO 8601 duration (PT8H15M)
                                                const durMatch = (itin?.duration || '').match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
                                                const durH = durMatch?.[1] || '0';
                                                const durM = durMatch?.[2] || '0';
                                                const durStr = `${durH}h ${durM}m`;
                                                // Use direct string parsing for times (avoid locale-dependent Date)
                                                const depTime = parseAmadeusTime(firstSeg?.departure?.time) || '';
                                                const arrTime = parseAmadeusTime(lastSeg?.arrival?.time) || '';
                                                const depDate = formatAmadeusShortDate(firstSeg?.departure?.time);
                                                const arrDate = formatAmadeusShortDate(lastSeg?.arrival?.time);
                                                // Check if arrival is next day (+1)
                                                const isNextDay = firstSeg?.departure?.time && lastSeg?.arrival?.time && isNextDayArrival(firstSeg.departure.time, lastSeg.arrival.time);
                                                // Resolve carrier codes to full names
                                                const carrierCodes = [...new Set(segs.map(s => s.carrier).filter(Boolean))];
                                                const carrierNames = carrierCodes.map(c => resolveAirlineName(c, dicts));
                                                const flightNums = segs.map(s => `${s.carrier || ''}${s.flightNumber || ''}`).filter(Boolean);
                                                const currency = offer.currency || priceData.data.currency || 'USD';
                                                const priceFormatted = new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(offer.price);

                                                // Get cabin class from first segment
                                                const cabinClass = firstSeg?.cabin || priceData?.data?.cabin || 'Economy';
                                                const cabinDisplay = cabinClass.charAt(0).toUpperCase() + cabinClass.slice(1).toLowerCase().replace('_', ' ');
                                                // Build booking link for this specific offer
                                                const offerBookingHref = getFlightsLink(
                                                  item.isArrivalFlightCard ? fromCode : (isMultiStopTrip ? fromCode : item.homeAirportCode),
                                                  item.isArrivalFlightCard ? toCode : (isMultiStopTrip ? toCode : item.airportCode),
                                                  item.isArrivalFlightCard ? tripStartDate : tripEndDate,
                                                  isMultiStopTrip
                                                    ? {
                                                        type: 'multicity',
                                                        legs: [
                                                          { from: item.homeAirportCode, to: item.airportCode, date: tripStartDate },
                                                          { from: itinerary?.departureAirport?.code || item.airportCode, to: item.homeAirportCode, date: tripEndDate }
                                                        ]
                                                      }
                                                    : { returnDate: tripEndDate, type: 'roundtrip' }
                                                );

                                                // Check if this offer is currently selected
                                                const flightType = item.isArrivalFlightCard ? 'arrival' : 'departure';
                                                const currentSelected = flightType === 'arrival' ? arrivalFlight : departureFlight;
                                                const isSelected = currentSelected.flightNumber && currentSelected.flightNumber === flightNums[0] && currentSelected.offerId === (offer.id || offer.offerId);

                                                return (
                                                  <div
                                                    key={offer.id || offerIdx}
                                                    className={`p-3 rounded-xl border transition-all ${
                                                      isSelected
                                                        ? 'bg-teal-100 border-teal-400 ring-1 ring-teal-400'
                                                        : offerIdx === 0 ? 'bg-teal-50 border-teal-200' : 'bg-gray-50 border-gray-200'
                                                    }`}
                                                  >
                                                    {/* Row 1: Airlines + Price */}
                                                    <div className="flex items-center justify-between">
                                                      <div className="flex items-center gap-2">
                                                        <span className="text-sm font-bold text-gray-800" style={FONTS.headingBold}>
                                                          {carrierNames.join(', ') || 'Unknown'}
                                                        </span>
                                                        {flightNums.length > 0 && (
                                                          <span className="text-xs text-gray-400" style={FONTS.body}>{flightNums[0]}</span>
                                                        )}
                                                      </div>
                                                      <div className="flex items-center gap-1.5">
                                                        <span className={`text-sm font-bold ${isSelected ? 'text-teal-800' : offerIdx === 0 ? 'text-teal-700' : 'text-gray-700'}`} style={FONTS.headingBold}>
                                                          {priceFormatted}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    {/* Row 2: Date + Times + Duration + Stops */}
                                                    {depDate && (
                                                      <div className="flex items-center gap-1.5 mt-1 text-[10px] text-gray-400" style={FONTS.body}>
                                                        <span>{depDate}</span>
                                                        {arrDate && arrDate !== depDate && (
                                                          <>
                                                            <span>→</span>
                                                            <span>{arrDate}</span>
                                                          </>
                                                        )}
                                                      </div>
                                                    )}
                                                    <div className="flex items-center justify-between mt-0.5">
                                                      <div className="flex items-center gap-1.5 text-xs text-gray-600" style={FONTS.body}>
                                                        {depTime && <span className="font-medium">{depTime}</span>}
                                                        {depTime && arrTime && <span className="text-gray-400">→</span>}
                                                        {arrTime && (
                                                          <span className="font-medium">
                                                            {arrTime}
                                                            {isNextDay && <sup className="text-[9px] text-amber-600 font-bold ml-0.5">+1</sup>}
                                                          </span>
                                                        )}
                                                      </div>
                                                      <div className="flex items-center gap-1.5 text-xs text-gray-500" style={FONTS.body}>
                                                        <span>{durStr}</span>
                                                        <span className="text-gray-300">·</span>
                                                        <span>{stops === 0 ? 'Direct' : `${stops} stop${stops > 1 ? 's' : ''}`}</span>
                                                      </div>
                                                    </div>
                                                    {/* Row 3: Class badge + via stops */}
                                                    <div className="flex items-center gap-1.5 mt-1.5">
                                                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-600 font-medium" style={FONTS.body}>{cabinDisplay}</span>
                                                      {stops > 0 && (
                                                        <span className="text-xs text-gray-400" style={FONTS.body}>
                                                          {segs.slice(0, -1).map((seg, sIdx) => `${sIdx > 0 ? ', ' : ''}via ${seg.arrival?.airport}`).join('')}
                                                        </span>
                                                      )}
                                                    </div>
                                                    {/* Row 4: Action buttons — Select + Book */}
                                                    <div className="flex items-center gap-2 mt-2.5">
                                                      <button
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          selectFlightOffer(flightType, offer, dicts);
                                                        }}
                                                        className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                                                          isSelected
                                                            ? 'bg-teal-600 text-white'
                                                            : 'bg-[#313143] text-white hover:bg-[#3d3d52]'
                                                        }`}
                                                        style={FONTS.bodyBold}
                                                      >
                                                        {isSelected ? '✓ Selected' : 'Select This Flight'}
                                                      </button>
                                                      <a
                                                        href={offerBookingHref}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                                                        style={FONTS.body}
                                                        onClick={(e) => e.stopPropagation()}
                                                      >
                                                        Book <ExternalLink className="w-3 h-3" />
                                                      </a>
                                                    </div>
                                                  </div>
                                                );
                                              })}
                                              {/* More Options button */}
                                              {hasMore && !showingAll && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedFlightPrices(prev => ({ ...prev, [`${routeKey}-all`]: true }));
                                                  }}
                                                  className="w-full py-2 text-xs text-teal-600 hover:text-teal-700 hover:bg-teal-50 rounded-lg font-medium transition-colors"
                                                  style={FONTS.bodyBold}
                                                >
                                                  More Options ({(priceData.data.allOffersExpanded?.length || 0) - (priceData.data.allOffers?.length || 0)} more)
                                                </button>
                                              )}
                                              {showingAll && hasMore && (
                                                <button
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setExpandedFlightPrices(prev => ({ ...prev, [`${routeKey}-all`]: false }));
                                                  }}
                                                  className="w-full py-2 text-xs text-gray-500 hover:text-gray-600 hover:bg-gray-50 rounded-lg font-medium transition-colors"
                                                  style={FONTS.body}
                                                >
                                                  Show Fewer
                                                </button>
                                              )}
                                              {priceData.fallbackUsed && (
                                                <p className="text-xs text-amber-600" style={FONTS.body}>
                                                  {priceData.originalClass} unavailable · showing Economy
                                                </p>
                                              )}
                                            </div>
                                            );
                                          })()}
                                          {/* Fallback: show single price if allOffers not available */}
                                          {priceData?.data && !priceData?.data?.allOffers?.length && (
                                            <div className="p-2.5 bg-teal-50 rounded-xl border border-teal-200">
                                              <div className="flex items-center gap-2">
                                                <span className="text-base font-bold text-teal-700" style={FONTS.headingBold}>
                                                  from {new Intl.NumberFormat('en-US', { style: 'currency', currency: priceData.data.currency || 'USD' }).format(priceData.data.price)}
                                                </span>
                                                {priceData.data.airlines?.length > 0 && (
                                                  <span className="text-xs text-gray-500" style={FONTS.body}>· {priceData.data.airlines.join(', ')}</span>
                                                )}
                                              </div>
                                              <div className="flex items-center gap-2 mt-1">
                                                <span className="text-xs text-gray-500" style={FONTS.body}>
                                                  {priceData.data.segments?.length > 1 ? `${priceData.data.segments.length - 1} stop` : 'Direct'}
                                                </span>
                                                <span className="text-xs text-gray-400">·</span>
                                                <span className="text-xs text-gray-500" style={FONTS.body}>{priceData.data.cabin || 'Economy'}</span>
                                              </div>
                                            </div>
                                          )}
                                          {priceData?.noResults && !priceData?.error && (
                                            <p className="text-xs text-gray-400" style={FONTS.body}>No live offers found for this route</p>
                                          )}
                                          {priceData?.error && (
                                            <div className="flex items-center gap-2">
                                              <p className="text-xs text-gray-400" style={FONTS.body}>Live pricing unavailable</p>
                                              <button
                                                onClick={() => {
                                                  setFlightPrices(prev => { const updated = { ...prev }; delete updated[routeKey]; return updated; });
                                                  setTimeout(() => fetchFlightPrice(fromCode, toCode, userProfile?.preferences?.startDate, { cabinClass: adCabinForRoute }), 100);
                                                }}
                                                className="text-xs text-teal-600 hover:text-teal-700 font-medium underline"
                                                style={FONTS.body}
                                              >
                                                Retry
                                              </button>
                                            </div>
                                          )}
                                          {/* Booking link — one-way per leg for multi-stop, round-trip search for single destination */}
                                          {(() => {
                                            const isMultiCity = selectedDestination?.isMultiStop;
                                            const isDepart = item.isDepartureFlightCard;
                                            // For multi-city: each leg is a separate one-way search
                                            // For single destination: link to a round-trip search on the comparison site
                                            const label = isMultiCity
                                              ? (isDepart ? '✈️ Search Return Flights' : '✈️ Search Outbound Flights')
                                              : '✈️ Search Flights';
                                            const btnColor = isDepart
                                              ? 'bg-amber-100 hover:bg-amber-200 text-amber-700'
                                              : 'bg-teal-100 hover:bg-teal-200 text-teal-700';
                                            let href;
                                            if (isMultiCity) {
                                              const legs = [{ from: fromCode, to: toCode, date: flightDate }];
                                              href = getFlightsLink(fromCode, toCode, flightDate, { type: 'multicity', legs });
                                            } else {
                                              // Round-trip search on comparison site (both outbound + return)
                                              href = getFlightsLink(fromCode, toCode, tripStartDate, { returnDate: tripEndDate, type: 'roundtrip' });
                                            }
                                            return (
                                              <a
                                                href={href}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`inline-flex items-center gap-2 px-4 py-2 ${btnColor} rounded-xl text-sm font-medium transition-colors`}
                                                style={FONTS.body}
                                                onClick={(e) => e.stopPropagation()}
                                              >
                                                {label}
                                                <ExternalLink className="w-3 h-3" />
                                              </a>
                                            );
                                          })()}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}

                                    {/* Airport Lounge Link (for departures only — lounges used before flights, not on arrival) */}
                                    {isDeparture && item.airportCode && (
                                      <div className="mt-3 pt-3 border-t border-gray-100">
                                        <a
                                          href={`https://www.prioritypass.com/en/loungesearch?airport=${item.airportCode}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-50 to-brand-50 border border-teal-200 hover:border-teal-400 hover:shadow-sm rounded-xl text-sm font-medium text-teal-800 transition-all"
                                          style={FONTS.body}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <span>🛋️</span>
                                          <span>Find a Lounge at {item.airportCode}</span>
                                          <ExternalLink className="w-3 h-3" />
                                        </a>
                                      </div>
                                    )}

                                    {/* Quick Action Buttons - Map, Directions, Website */}
                                    {(isActivity || isMeal) && (item.location || item.address) && (
                                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-gray-100">
                                        <a
                                          href={getSmartLink(item, isActivity ? 'activity' : 'meal', itinerary?.destination?.split(',')[0] || '')}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-3 py-1.5 bg-gray-100 hover:bg-teal-100 hover:text-teal-700 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                                          style={FONTS.body}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <MapIcon className="w-3.5 h-3.5" /> View on Map
                                        </a>
                                        <a
                                          href={getDirectionsLink(item, itinerary?.destination?.split(',')[0] || '', currentDayData?.hotel?.address)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-3 py-1.5 bg-gray-100 hover:bg-blue-100 hover:text-blue-700 rounded-lg text-xs font-medium flex items-center gap-1.5 transition-colors"
                                          style={FONTS.body}
                                          onClick={(e) => e.stopPropagation()}
                                          title={currentDayData?.hotel?.address ? `From: ${currentDayData.hotel.name}` : 'From your location'}
                                        >
                                          🧭 From Hotel
                                        </a>
                                      </div>
                                    )}
                                    
                                    {/* Transport Mode Selection for Hotel Transit */}
                                    {isHotelTransit && (
                                      <div className="mt-3 pt-3 border-t border-gray-100">
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs text-gray-500" style={FONTS.body}>Travel by:</span>
                                          {(() => {
                                            const hotelTransitKey = `day${currentDay}-hotel-transit`;
                                            const hotelTransitMode = transportModes[hotelTransitKey] || 'driving';
                                            const cachedTimes = transitTimes[hotelTransitKey] || {};
                                            
                                            // Check if walking is too long (> 1 hour)
                                            const isWalkingTooLong = cachedTimes.walking && (
                                              cachedTimes.walking.duration?.includes('hr') || 
                                              cachedTimes.walking.duration?.includes('hour') ||
                                              parseInt(cachedTimes.walking.duration) >= 60
                                            );
                                            
                                            return (
                                              <div className="flex gap-1.5">
                                                <button
                                                  onClick={async () => {
                                                    setTransportModes(prev => ({ ...prev, [hotelTransitKey]: 'transit' }));
                                                    if (!cachedTimes.transit && item.location && currentDayData.hotel?.address) {
                                                      await fetchTransitTimes(hotelTransitKey, item.location, currentDayData.hotel.address, {
                                                        type: 'hotel-return',
                                                        expectedCity: currentDayData.baseCity || selectedDestination?.name
                                                      });
                                                    }
                                                  }}
                                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                                                    hotelTransitMode === 'transit'
                                                      ? 'bg-blue-500 text-white shadow-sm'
                                                      : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50'
                                                  }`}
                                                  style={FONTS.body}
                                                >
                                                  🚇 Public Transit
                                                  {cachedTimes.transit && <span className="opacity-75">• {cachedTimes.transit.duration === '0 mins' || cachedTimes.transit.duration === '0 min' ? 'N/A' : cachedTimes.transit.duration}</span>}
                                                </button>
                                                <button
                                                  onClick={async () => {
                                                    setTransportModes(prev => ({ ...prev, [hotelTransitKey]: 'driving' }));
                                                    if (!cachedTimes.driving && item.location && currentDayData.hotel?.address) {
                                                      await fetchTransitTimes(hotelTransitKey, item.location, currentDayData.hotel.address, {
                                                        type: 'hotel-return',
                                                        expectedCity: currentDayData.baseCity || selectedDestination?.name
                                                      });
                                                    }
                                                  }}
                                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                                                    hotelTransitMode === 'driving'
                                                      ? 'bg-gray-700 text-white shadow-sm'
                                                      : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-50'
                                                  }`}
                                                  style={FONTS.body}
                                                >
                                                  🚕 Car
                                                  {cachedTimes.driving && <span className="opacity-75">• {cachedTimes.driving.duration === '0 mins' || cachedTimes.driving.duration === '0 min' ? 'N/A' : cachedTimes.driving.duration}</span>}
                                                </button>
                                                <button
                                                  onClick={async () => {
                                                    if (isWalkingTooLong) return; // Don't allow selection if too long
                                                    setTransportModes(prev => ({ ...prev, [hotelTransitKey]: 'walking' }));
                                                    if (!cachedTimes.walking && item.location && currentDayData.hotel?.address) {
                                                      await fetchTransitTimes(hotelTransitKey, item.location, currentDayData.hotel.address, {
                                                        type: 'hotel-return',
                                                        expectedCity: currentDayData.baseCity || selectedDestination?.name
                                                      });
                                                    }
                                                  }}
                                                  disabled={isWalkingTooLong}
                                                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5 ${
                                                    isWalkingTooLong
                                                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                                      : hotelTransitMode === 'walking'
                                                        ? 'bg-brand-500 text-white shadow-sm'
                                                        : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300 hover:bg-green-50'
                                                  }`}
                                                  style={FONTS.body}
                                                  title={isWalkingTooLong ? 'Walking time exceeds 1 hour' : ''}
                                                >
                                                🚶 Walk
                                                  {cachedTimes.walking && <span className="opacity-75 ml-1">• {isWalkingTooLong ? '1hr+' : cachedTimes.walking.duration}</span>}
                                                </button>
                                              </div>
                                            );
                                          })()}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* Action Buttons - Decluttered */}
                                  {(isActivity || isMeal) && (
                                  <div className="flex flex-col gap-1.5 items-center">
                                    {/* Edit - always visible */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        if (isActivity) {
                                          setEditingActivityIndex(item.originalIndex ?? idx);
                                          setEditedActivity({
                                            activity: item.activity,
                                            time: item.time,
                                            duration: item.duration,
                                            location: item.location || '',
                                            description: item.description || '',
                                            priceTier: item.priceTier || ''
                                          });
                                        } else {
                                          setEditingMealIndex(item.originalIndex ?? idx);
                                          setEditedMeal({
                                            restaurant: item.restaurant,
                                            time: item.time,
                                            cuisine: item.cuisine || '',
                                            address: item.address || '',
                                            notes: item.notes || '',
                                            priceRange: item.priceRange || ''
                                          });
                                        }
                                      }}
                                      className="w-8 h-8 rounded-full bg-teal-50 hover:bg-teal-100 border border-teal-200 flex items-center justify-center transition-all"
                                      title={isActivity ? "Edit Activity" : "Edit Meal"}
                                    >
                                      <Pencil className="w-3.5 h-3.5 text-teal-600" />
                                    </button>

                                    {/* Like/Dislike - show on hover OR if feedback already given */}
                                    <div className={`flex flex-col gap-1.5 transition-all duration-200 ${
                                      feedback ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                    }`}>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleFeedback('up', currentDay, item.originalIndex ?? idx, title, isActivity, isMeal ? item.type : null); }}
                                        className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                                          feedback === 'up' ? 'bg-teal-500 text-white border-teal-500 shadow-md scale-110' : 'bg-white border-gray-200 hover:bg-teal-50 hover:border-teal-300'
                                        }`}
                                        title="Like this"
                                      >
                                        <ThumbsUp className={`w-3.5 h-3.5 ${feedback === 'up' ? 'text-white' : 'text-gray-400'}`} />
                                      </button>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleFeedback('down', currentDay, item.originalIndex ?? idx, title, isActivity, isMeal ? item.type : null); }}
                                        className={`w-8 h-8 rounded-full border flex items-center justify-center transition-all ${
                                          feedback === 'down' ? 'bg-red-400 text-white border-red-400 shadow-md scale-110' : 'bg-white border-gray-200 hover:bg-red-50 hover:border-red-300'
                                        }`}
                                        title="Dislike this"
                                      >
                                        <ThumbsDown className={`w-3.5 h-3.5 ${feedback === 'down' ? 'text-white' : 'text-gray-400'}`} />
                                      </button>
                                    </div>

                                    {/* Overflow menu - show on hover */}
                                    <div className="relative opacity-0 group-hover:opacity-100 transition-all duration-200">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setOverflowMenuOpen(overflowMenuOpen === cardId ? null : cardId);
                                        }}
                                        className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-all text-gray-500"
                                        title="More options"
                                      >
                                        <MoreVertical className="w-4 h-4" />
                                      </button>
                                      {overflowMenuOpen === cardId && (
                                        <>
                                          {/* Backdrop to close menu on outside click */}
                                          <div className="fixed inset-0 z-40" onClick={(e) => { e.stopPropagation(); setOverflowMenuOpen(null); }} />
                                          <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-gray-200 z-50 py-1 min-w-[180px]" onClick={(e) => e.stopPropagation()}>
                                            {/* Lock/Unlock toggle */}
                                            {features.ACTIVITY_CURATION && (isActivity || isMeal) && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOverflowMenuOpen(null);
                                                  handleToggleLock(currentDay, item.originalIndex ?? idx, isActivity);
                                                }}
                                                className="w-full px-4 py-2.5 text-left text-sm text-teal-600 hover:bg-teal-50 flex items-center gap-2 transition-colors"
                                              >
                                                {isLocked ? <Unlock className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                                                {isLocked ? 'Unlock Activity' : 'Lock Activity'}
                                              </button>
                                            )}
                                            {!isLocked && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setOverflowMenuOpen(null);
                                                handleRemoveItem(currentDay, item.originalIndex ?? idx, isActivity, title);
                                              }}
                                              className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                                            >
                                              <Trash2 className="w-4 h-4" />
                                              Remove {isActivity ? 'Activity' : 'Meal'}
                                            </button>
                                            )}
                                            {isActivity && (
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setOverflowMenuOpen(null);
                                                  setAddActivityDay(currentDay);
                                                  setAddActivityInsertIndex((item.originalIndex ?? idx) + 1);
                                                  setShowAddActivityModal(true);
                                                }}
                                                className="w-full px-4 py-2.5 text-left text-sm text-teal-600 hover:bg-teal-50 flex items-center gap-2 transition-colors"
                                              >
                                                <Plus className="w-4 h-4" />
                                                Insert Activity After
                                              </button>
                                            )}
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  )}
                                </div>

                                {/* Flexible Mode Action Bar */}
                                {isFlexibleItem && (
                                  <div className="flex items-center gap-2 mt-3 pt-3 border-t border-dashed border-gray-200" onClick={e => e.stopPropagation()}>
                                    {isKept && (
                                      <span className="text-xs text-green-600 font-medium flex items-center gap-1 mr-auto" style={FONTS.bodySemibold}>
                                        <span>\u2705</span> Kept
                                      </span>
                                    )}
                                    <button
                                      onClick={() => handleFlexibleSwap(currentDay, item.originalIndex ?? idx, title)}
                                      className="px-3 py-1.5 text-xs text-teal-600 hover:text-teal-700 hover:bg-teal-50 font-medium rounded-lg transition-all flex items-center gap-1"
                                      style={FONTS.bodySemibold}
                                    >
                                      🔄 Swap
                                    </button>
                                    <button
                                      onClick={() => handleFlexibleKeep(currentDay, item.originalIndex ?? idx, isActivity)}
                                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1 ${
                                        isKept ? 'text-green-700 bg-green-100' : 'text-green-600 hover:text-green-700 hover:bg-green-50'
                                      }`}
                                      style={FONTS.bodySemibold}
                                    >
                                      👍 Keep
                                    </button>
                                    <button
                                      onClick={() => handleFlexibleRemove(currentDay, item.originalIndex ?? idx, isActivity, title)}
                                      className="px-3 py-1.5 text-xs text-red-500 hover:text-red-600 hover:bg-red-50 font-medium rounded-lg transition-all flex items-center gap-1"
                                      style={FONTS.bodySemibold}
                                    >
                                      👎 Remove
                                    </button>
                                  </div>
                                )}

                                {/* EXPANDABLE CONTENT SECTION */}
                                {isExpanded && canExpand && (
                                  <div className="mt-4 pt-4 border-t border-gray-100" onClick={e => e.stopPropagation()}>
                                    {/* Highlight Pills */}
                                    {item.highlights && item.highlights.length > 0 && (
                                      <div className="flex flex-wrap gap-2 mb-4">
                                        {item.highlights.map((h, hIdx) => (
                                          <span key={hIdx} className="px-3 py-1 bg-teal-50 text-teal-700 rounded-full text-xs font-medium flex items-center gap-1">
                                            <span>✓</span> {h}
                                          </span>
                                        ))}
                                      </div>
                                    )}

                                    {/* Tab Navigation for Activities and Meals */}
                                    {(isActivity || isMeal) && (
                                      <>
                                        <div className="flex border-b border-gray-200 mb-4">
                                          {['overview', 'whatToDo', 'tips'].map(tab => (
                                            <button
                                              key={tab}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setCardActiveTab(prev => ({ ...prev, [cardId]: tab }));
                                                // Auto-trigger lazy fetch when whatToDo tab is clicked and no inline data exists
                                                if (tab === 'whatToDo' && !item.whatToDo?.length) {
                                                  const actCity = currentDayData?.baseCity || selectedDestination?.name || '';
                                                  const actName = item.activity || item.restaurant;
                                                  if (actName && actCity) {
                                                    fetchActivityDetails(actName, actCity, userProfile?.preferences?.travelCompanions);
                                                  }
                                                }
                                              }}
                                              className={`flex-1 py-2.5 text-sm font-medium transition-colors ${
                                                activeTab === tab
                                                  ? 'text-teal-600 border-b-2 border-teal-500'
                                                  : 'text-gray-500 hover:text-gray-700'
                                              }`}
                                              style={FONTS.body}
                                            >
                                              {tab === 'overview' ? 'Overview' : tab === 'whatToDo' ? (isMeal ? 'What to Try' : 'What to Do') : 'Tips'}
                                            </button>
                                          ))}
                                        </div>

                                        {/* Tab Content */}
                                        {activeTab === 'overview' && <OverviewTabContent item={item} />}
                                        {activeTab === 'whatToDo' && <WhatToDoTabContent item={item} />}
                                        {activeTab === 'tips' && <TipsTabContent item={item} />}

                                        {/* Venue Info (lazy-loaded from cache) */}
                                        {(() => {
                                          const venueName = item.restaurant || item.activity || item.name;
                                          const city = currentDayData?.baseCity || '';
                                          const vd = venueName && city ? getVenueData(venueName, city) : null;
                                          const vKey = venueName && city ? `${venueName.toLowerCase().trim()}:${city.toLowerCase().trim()}` : '';
                                          const isLoading = venueInfo[vKey]?.loading;
                                          if (isLoading) return (
                                            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-xs text-gray-400">
                                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-teal-400"></div>
                                              Loading venue details...
                                            </div>
                                          );
                                          if (!vd) return null;
                                          return (
                                            <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                                              {/* Rating + Cost */}
                                              <div className="flex items-center gap-3 flex-wrap">
                                                {vd.googleReviews?.rating && (
                                                  <span className="flex items-center gap-1 text-xs font-medium text-gray-700">
                                                    <span className="text-amber-400">★</span> {vd.googleReviews.rating}
                                                    <span className="text-gray-400">({vd.googleReviews.totalReviews?.toLocaleString()})</span>
                                                  </span>
                                                )}
                                                {vd.costData?.priceTier && (
                                                  <span className="text-xs font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full">
                                                    {vd.costData.priceTier} <span className="text-gray-400 text-[10px]">Google</span>
                                                  </span>
                                                )}
                                                {vd.googleReviews?.businessStatus === 'CLOSED_TEMPORARILY' && (
                                                  <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">⚠️ Temporarily Closed</span>
                                                )}
                                                {vd.googleReviews?.businessStatus === 'CLOSED_PERMANENTLY' && (
                                                  <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded-full">❌ Permanently Closed</span>
                                                )}
                                              </div>
                                              {/* Top review snippet */}
                                              {vd.googleReviews?.reviews?.[0]?.text && (
                                                <p className="text-xs text-gray-500 italic leading-relaxed line-clamp-2 mt-2">
                                                  "{vd.googleReviews.reviews[0].text}" — <span className="not-italic text-gray-400">{vd.googleReviews.reviews[0].author}</span>
                                                </p>
                                              )}
                                            </div>
                                          );
                                        })()}
                                      </>
                                    )}

                                    {/* Road Trip / Long Distance Transfer Expanded Content */}
                                    {(isRoadTrip || isLongDistanceTransfer || isLongDistanceDeparture) && (() => {
                                      // Pick travelmode for the Maps URL: hard-coded 'driving' was
                                      // wrong for ferry/seaplane resort transfers (Hayman Island,
                                      // Maldives etc). Read item.travelMode set by createDayTimeline
                                      // OR the post-gen repair's mode override.
                                      const tmNorm = (() => {
                                        const tm = (item.travelMode || item.type || '').toLowerCase();
                                        if (tm === 'flight' || tm === 'fly' || tm === 'air' || tm === 'seaplane' || tm === 'helicopter') return 'flying';
                                        if (tm === 'transit' || tm === 'train' || tm === 'rail' || tm === 'bus') return 'transit';
                                        if (tm === 'walking' || tm === 'walk') return 'walking';
                                        // ferry/boat/sea: Google Maps doesn't support 'ferry' mode,
                                        // so fall back to 'transit' which often surfaces ferry routes.
                                        if (tm === 'ferry' || tm === 'boat' || tm === 'sea' || tm === 'speedboat') return 'transit';
                                        return 'driving';
                                      })();
                                      return (
                                      <div className="space-y-4">
                                        {/* View Route Button */}
                                        <a
                                          href={`https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(item.location?.split('→')[0]?.trim() || '')}&destination=${encodeURIComponent(item.location?.split('→')[1]?.trim() || '')}&travelmode=${tmNorm}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-colors"
                                          style={FONTS.body}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <MapIcon className="w-4 h-4" /> View Route on Google Maps
                                          <ExternalLink className="w-3 h-3" />
                                        </a>

                                        {/* Road Trip Stops Section */}
                                        <div className="mt-4">
                                          {!roadTripStops[cardId]?.data && !roadTripStops[cardId]?.loading && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                fetchRoadTripStops(cardId, item.location, item.duration);
                                              }}
                                              className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium flex items-center justify-center gap-2 transition-colors"
                                              style={FONTS.body}
                                            >
                                              <Route className="w-4 h-4" /> Get Stop Recommendations
                                            </button>
                                          )}

                                          {roadTripStops[cardId]?.loading && (
                                            <div className="flex items-center justify-center py-6 text-gray-500">
                                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-500 mr-2"></div>
                                              Finding great stops along your route...
                                            </div>
                                          )}

                                          {roadTripStops[cardId]?.data && (
                                            <RoadTripStopsDisplay
                                              stops={roadTripStops[cardId].data.stops}
                                              routeTips={roadTripStops[cardId].data.routeTips}
                                              targetDayIndex={currentDay}
                                              onAdd={(stop) => addRoadTripStopToDay(currentDay, stop)}
                                            />
                                          )}

                                          {roadTripStops[cardId]?.error && (
                                            <div className="text-center py-4 text-red-500">
                                              <p>Failed to load recommendations: {roadTripStops[cardId].error}</p>
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  fetchRoadTripStops(cardId, item.location, item.duration);
                                                }}
                                                className="mt-2 text-sm text-teal-600 hover:text-teal-700 font-medium"
                                              >
                                                Try Again
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      );
                                    })()}
                                  </div>
                                )}
                                </>
                                )}
                              </div>
                            </div>
                          </>
                        );
                      }}
                    </SortableTimelineCard>
                            {/* Break insertion marker — shown between items when insertingBreak is active */}
                            {insertingBreak && idx < fullTimeline.length - 1 && (
                              <div className="flex justify-center my-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const dayData = itinerary.days[currentDay];
                                    if (!dayData) return;
                                    // Calculate time between current item and next item
                                    const currItem = fullTimeline[idx];
                                    const nextItem = fullTimeline[idx + 1];
                                    const currEnd = parseTime(currItem.time) + (parseDurationToMinutes(currItem.duration) || 60);
                                    const nextStart = parseTime(nextItem.time);
                                    const midpoint = Math.round((currEnd + nextStart) / 2);
                                    const breakHour = Math.floor(midpoint / 60) % 24;
                                    const breakMin = midpoint % 60;
                                    const period = breakHour >= 12 ? 'PM' : 'AM';
                                    const displayHour = breakHour > 12 ? breakHour - 12 : breakHour === 0 ? 12 : breakHour;
                                    const breakTime = `${String(displayHour).padStart(2, '0')}:${String(breakMin).padStart(2, '0')} ${period}`;
                                    const breakActivity = {
                                      activity: 'Break',
                                      location: dayData.hotel?.name || dayData.baseCity || '',
                                      time: breakTime,
                                      duration: '30 minutes',
                                      description: 'Coffee break / free time',
                                      priceTier: '$',
                                      type: 'custom',
                                      emoji: '☕'
                                    };
                                    const scheduleIndex = idx + 1; // Insert after current item in schedule
                                    const newSchedule = [...(dayData.schedule || [])];
                                    newSchedule.splice(scheduleIndex, 0, breakActivity);
                                    const newItinerary = {
                                      ...itinerary,
                                      days: itinerary.days.map((day, i) =>
                                        i === currentDay ? { ...day, schedule: newSchedule } : day
                                      )
                                    };
                                    itineraryUserModifiedRef.current = true;
                                    setHasUnsavedChanges(true);
                                    setItinerary(newItinerary);
                                    setInsertingBreak(false);
                                    showToast('Break inserted!', 'success', 2000);
                                  }}
                                  className="px-4 py-1.5 text-xs font-semibold text-amber-700 bg-amber-50 border-2 border-dashed border-amber-300 rounded-full hover:bg-amber-100 hover:border-amber-400 transition-all animate-pulse"
                                  style={FONTS.bodyBold}
                                >
                                  + Insert break here
                                </button>
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}
                      </SortableContext>
                      <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
                        {activeDragId ? (() => {
                          const match = String(activeDragId).match(/^sortable-(activity|meal)-(\d+)$/);
                          if (!match) return null;
                          const dragType = match[1];
                          const dragIdx = parseInt(match[2], 10);
                          const dragItem = dragType === 'activity'
                            ? currentDayData?.schedule?.[dragIdx]
                            : currentDayData?.meals?.[dragIdx];
                          if (!dragItem) return null;
                          return (
                            <div className="tl-card rounded-2xl p-4 bg-white shadow-xl border-2 border-teal-400 opacity-90" style={{ maxWidth: 400 }}>
                              <div className="flex items-center gap-2">
                                <span className="text-teal-600 font-bold text-sm">{dragItem.time}</span>
                                <span className="text-gray-800 font-semibold text-sm truncate">
                                  {dragItem.activity || dragItem.restaurant || dragItem.meal || 'Item'}
                                </span>
                              </div>
                              {dragItem.duration && <span className="text-xs text-gray-400 mt-1 block">{dragItem.duration}</span>}
                            </div>
                          );
                        })() : null}
                      </DragOverlay>
                      </DndContext>
                      );
                    })()
                    )}

                    {/* Add Activity / Add Meal buttons moved to right sidebar Quick Actions */}
                  </div>
                )}
              </>
            );
          })()}

          {/* Return Travel Info Card — shown on the last day */}
          {currentDay === (itinerary.days?.length || 0) - 1 && itinerary.days?.length > 0 && (
            <div className="mt-8 bg-gradient-to-r from-slate-50 to-blue-50 rounded-2xl p-5 border border-slate-200">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center text-lg">🏠</div>
                <div>
                  <h3 className="text-sm font-semibold text-slate-800" style={FONTS.heading}>Journey Home</h3>
                  <p className="text-xs text-slate-500">Safe travels back!</p>
                </div>
              </div>
              <div className="space-y-2">
                {/* Show return transport info — flight, train, or fallback */}
                {(() => {
                  // Find the last leg's transport info
                  const lastLegKey = Object.keys(legTransports).pop();
                  const lastLegData = lastLegKey ? legTransports[lastLegKey] : null;
                  const depFlight = lastLegData?.selectedFlight || (departureFlight?.flightNumber ? departureFlight : null);
                  const returnMode = lastLegData?.mode || 'flight';
                  const _returnModeDisplay = getTransportModeDisplay(returnMode === 'flight' ? 'fly' : returnMode === 'driving' ? 'drive' : returnMode);
                  const modeIcon = _returnModeDisplay.emoji;
                  const modeLabel = returnMode === 'train' ? 'Train' : returnMode === 'bus' ? 'Bus' : returnMode === 'driving' ? 'Drive' : 'Flight';

                  if (depFlight) {
                    return (
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-blue-500">{modeIcon}</span>
                            <span className="font-medium text-slate-700">{depFlight.airline || ''} {depFlight.flightNumber || ''}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                          <span>{depFlight.from || ''}</span>
                          <span>→</span>
                          <span>{depFlight.to || userProfile?.preferences?.homeCity?.split(',')[0] || 'Home'}</span>
                        </div>
                        {(depFlight.departureTime || depFlight.time) && (
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-600 bg-slate-50 rounded px-2 py-1.5">
                            <div className="flex items-center gap-1">
                              <span className="text-slate-400">Departs</span>
                              <span className="font-medium">{depFlight.departureTime || depFlight.time}</span>
                            </div>
                            {depFlight.arrivalTime && (
                              <>
                                <span className="text-slate-300">→</span>
                                <div className="flex items-center gap-1">
                                  <span className="text-slate-400">Arrives</span>
                                  <span className="font-medium">{depFlight.arrivalTime}</span>
                                </div>
                              </>
                            )}
                            {depFlight.duration && (
                              <span className="text-slate-400 ml-auto">{depFlight.duration}</span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  // If transport mode is set (e.g. train) but no selectedFlight, show the mode info
                  if (lastLegData?.mode && lastLegData.mode !== 'flight') {
                    return (
                      <div className="bg-white rounded-lg p-3 border border-slate-100">
                        <div className="flex items-center gap-2 text-sm">
                          <span>{modeIcon}</span>
                          <span className="font-medium text-slate-700">{modeLabel} to {userProfile?.preferences?.homeCity?.split(',')[0] || 'Home'}</span>
                          {lastLegData.duration && <span className="text-slate-400 text-xs ml-auto">{lastLegData.duration}</span>}
                        </div>
                      </div>
                    );
                  }

                  return (
                    <p className="text-sm text-slate-500 italic">
                      No return transport selected yet. Visit the transportation module to search.
                    </p>
                  );
                })()}
              </div>
            </div>
          )}
            </>
          )}
        </div>

        {/* Edit Activity Modal */}
        {editingActivityIndex !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl mb-4" style={FONTS.headingBold}><Pencil className="w-5 h-5 inline mr-2" />Edit Activity</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Activity Name</label>
                  <input
                    type="text"
                    value={editedActivity.activity || ''}
                    onChange={(e) => setEditedActivity({...editedActivity, activity: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Time</label>
                    <input
                      type="time"
                      value={editedActivity.time || ''}
                      onChange={(e) => setEditedActivity({...editedActivity, time: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Duration</label>
                    <input
                      type="text"
                      value={editedActivity.duration || ''}
                      onChange={(e) => setEditedActivity({...editedActivity, duration: e.target.value})}
                      placeholder="2 hours"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Location</label>
                  <input
                    type="text"
                    value={editedActivity.location || ''}
                    onChange={(e) => setEditedActivity({...editedActivity, location: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Description</label>
                  <textarea
                    value={editedActivity.description || ''}
                    onChange={(e) => setEditedActivity({...editedActivity, description: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Price Tier</label>
                  <select
                    value={editedActivity.priceTier || ''}
                    onChange={(e) => setEditedActivity({...editedActivity, priceTier: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select...</option>
                    <option value="$">$ (Budget)</option>
                    <option value="$$">$$ (Moderate)</option>
                    <option value="$$$">$$$ (Expensive)</option>
                    <option value="$$$$">$$$$ (Luxury)</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    if (!itinerary?.days?.[currentDay]?.schedule?.[editingActivityIndex]) return;
                    // Deep copy to avoid mutation issues
                    const updatedItinerary = JSON.parse(JSON.stringify(itinerary));
                    updatedItinerary.days[currentDay].schedule[editingActivityIndex] = {
                      ...updatedItinerary.days[currentDay].schedule[editingActivityIndex],
                      ...editedActivity
                    };
                    // Clear transit times for this day so they get recalculated with new location
                    setTransitTimes(prev => {
                      const updated = { ...prev };
                      Object.keys(updated).forEach(key => {
                        if (key.startsWith(`day${currentDay}-transit-`) ||
                            key.startsWith(`day${currentDay}-last-to-hotel`) ||
                            key.startsWith(`day${currentDay}-hotel-to-first`)) {
                          delete updated[key];
                          delete transitTimesRef.current[key];
                        }
                      });
                      return updated;
                    });
                    transitCalcGuardRef.current = null;
                    setItinerary(updatedItinerary);
                    setEditingActivityIndex(null);
                    setEditedActivity({});
                  }}
                  className="flex-1 px-4 py-2 bg-[#313143] text-white rounded-lg hover:bg-[#3d3d52]"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditingActivityIndex(null);
                    setEditedActivity({});
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Meal Modal */}
        {editingMealIndex !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <h3 className="text-2xl mb-4 flex items-center gap-2" style={FONTS.headingBold}><UtensilsCrossed className="w-6 h-6 text-[#CD7A4D]" /> Edit Meal</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Restaurant Name</label>
                  <input
                    type="text"
                    value={editedMeal.restaurant || ''}
                    onChange={(e) => setEditedMeal({...editedMeal, restaurant: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Time</label>
                    <input
                      type="time"
                      value={editedMeal.time || ''}
                      onChange={(e) => setEditedMeal({...editedMeal, time: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Cuisine</label>
                    <input
                      type="text"
                      value={editedMeal.cuisine || ''}
                      onChange={(e) => setEditedMeal({...editedMeal, cuisine: e.target.value})}
                      placeholder="Italian, Thai, etc."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Address</label>
                  <input
                    type="text"
                    value={editedMeal.address || ''}
                    onChange={(e) => setEditedMeal({...editedMeal, address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Notes</label>
                  <textarea
                    value={editedMeal.notes || ''}
                    onChange={(e) => setEditedMeal({...editedMeal, notes: e.target.value})}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Price Range</label>
                  <select
                    value={editedMeal.priceRange || ''}
                    onChange={(e) => setEditedMeal({...editedMeal, priceRange: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="">Select...</option>
                    <option value="$">$ (Budget)</option>
                    <option value="$$">$$ (Moderate)</option>
                    <option value="$$$">$$$ (Expensive)</option>
                    <option value="$$$$">$$$$ (Luxury)</option>
                  </select>
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    if (!itinerary?.days?.[currentDay]?.meals?.[editingMealIndex]) return;
                    // Deep copy to avoid mutation issues
                    const updatedItinerary = JSON.parse(JSON.stringify(itinerary));
                    updatedItinerary.days[currentDay].meals[editingMealIndex] = {
                      ...updatedItinerary.days[currentDay].meals[editingMealIndex],
                      ...editedMeal
                    };
                    // Clear transit times for this day so they get recalculated with new location
                    setTransitTimes(prev => {
                      const updated = { ...prev };
                      Object.keys(updated).forEach(key => {
                        if (key.startsWith(`day${currentDay}-transit-`) ||
                            key.startsWith(`day${currentDay}-last-to-hotel`) ||
                            key.startsWith(`day${currentDay}-hotel-to-first`)) {
                          delete updated[key];
                          delete transitTimesRef.current[key];
                        }
                      });
                      return updated;
                    });
                    transitCalcGuardRef.current = null;
                    setItinerary(updatedItinerary);
                    setEditingMealIndex(null);
                    setEditedMeal({});
                  }}
                  className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setEditingMealIndex(null);
                    setEditedMeal({});
                  }}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Date Picker Modal */}
        {showDatePicker && (() => {
          const currentDays = totalItineraryDays > (itinerary?.days?.length || 0) ? totalItineraryDays : (itinerary?.days?.length || 0);
          const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
          const selectedMonths = userProfile?.preferences?.travelMonths || [];
          return (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowDatePicker(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-xl flex items-center gap-2" style={FONTS.headingBold}>
                  <CalendarDays className="w-5 h-5 text-teal-600" />
                  Trip Dates
                </h3>
                <button onClick={() => setShowDatePicker(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
              </div>

              <div className="space-y-5">
                {/* Duration */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2" style={FONTS.body}>Duration</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const newDays = Math.max(1, currentDays - 1);
                        setDatePickerValues(prev => {
                          if (prev.startDate) {
                            const end = new Date(prev.startDate);
                            end.setDate(end.getDate() + newDays - 1);
                            return { ...prev, endDate: end.toISOString().split('T')[0], _duration: newDays };
                          }
                          return { ...prev, _duration: newDays };
                        });
                      }}
                      className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-lg transition-colors"
                    >−</button>
                    <div className="flex-1 text-center">
                      <span className="text-2xl font-bold text-gray-800" style={FONTS.headingBold}>{datePickerValues._duration || currentDays}</span>
                      <span className="text-sm text-gray-500 ml-1">days</span>
                    </div>
                    <button
                      onClick={() => {
                        const newDays = Math.min(30, (datePickerValues._duration || currentDays) + 1);
                        setDatePickerValues(prev => {
                          if (prev.startDate) {
                            const end = new Date(prev.startDate);
                            end.setDate(end.getDate() + newDays - 1);
                            return { ...prev, endDate: end.toISOString().split('T')[0], _duration: newDays };
                          }
                          return { ...prev, _duration: newDays };
                        });
                      }}
                      className="w-9 h-9 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 font-bold text-lg transition-colors"
                    >+</button>
                  </div>
                </div>

                {/* Specific Dates (optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2" style={FONTS.body}>
                    Specific Dates <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Start</label>
                      <input
                        type="date"
                        value={datePickerValues.startDate}
                        onChange={(e) => {
                          const newStart = e.target.value;
                          const days = datePickerValues._duration || currentDays;
                          const end = new Date(newStart);
                          end.setDate(end.getDate() + days - 1);
                          setDatePickerValues(prev => ({ ...prev, startDate: newStart, endDate: end.toISOString().split('T')[0] }));
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                        style={FONTS.body}
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">End</label>
                      <input
                        type="date"
                        value={datePickerValues.endDate}
                        min={datePickerValues.startDate || undefined}
                        onChange={(e) => {
                          const newEnd = e.target.value;
                          if (datePickerValues.startDate) {
                            const diffDays = calculateDaysBetween(datePickerValues.startDate, newEnd);
                            if (diffDays > 0) setDatePickerValues(prev => ({ ...prev, endDate: newEnd, _duration: diffDays }));
                          } else {
                            setDatePickerValues(prev => ({ ...prev, endDate: newEnd }));
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400"
                        style={FONTS.body}
                      />
                    </div>
                  </div>
                </div>

                {/* Travel Month (if no specific dates) */}
                {!datePickerValues.startDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-2" style={FONTS.body}>
                      Travel Month <span className="text-gray-400 font-normal">(optional)</span>
                    </label>
                    <div className="flex flex-wrap gap-1.5">
                      {MONTHS.map(month => {
                        const isSelected = selectedMonths.includes(month) || datePickerValues._selectedMonth === month;
                        return (
                          <button
                            key={month}
                            onClick={() => setDatePickerValues(prev => ({ ...prev, _selectedMonth: isSelected ? null : month }))}
                            className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                              isSelected ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {month.slice(0, 3)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Change summary */}
                {(() => {
                  const newDays = datePickerValues._duration || currentDays;
                  const willRegenerate = newDays !== currentDays;
                  if (!willRegenerate && !datePickerValues.startDate) return null;
                  return (
                    <div className={`text-sm px-3 py-2 rounded-lg ${willRegenerate ? 'bg-amber-50 text-amber-700' : 'bg-teal-50 text-teal-700'}`} style={FONTS.body}>
                      {willRegenerate
                        ? <>{newDays} days selected <span className="text-xs">(was {currentDays} days — will regenerate)</span></>
                        : datePickerValues.startDate && datePickerValues.endDate
                          ? <>Dates set: {new Date(datePickerValues.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} → {new Date(datePickerValues.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</>
                          : null
                      }
                    </div>
                  );
                })()}
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowDatePicker(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                  style={FONTS.body}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const newDays = datePickerValues._duration || currentDays;
                    const { startDate: newStart, endDate: newEnd, _selectedMonth } = datePickerValues;
                    const daysChanged = newDays !== currentDays;

                    // Update preferences
                    const prefUpdates = {};
                    if (newStart && newEnd) {
                      prefUpdates.startDate = newStart;
                      prefUpdates.endDate = newEnd;
                    }
                    if (daysChanged) prefUpdates.duration = newDays;
                    if (_selectedMonth) prefUpdates.travelMonths = [_selectedMonth];

                    if (Object.keys(prefUpdates).length > 0) {
                      setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, ...prefUpdates } }));
                      if (daysChanged) {
                        // Regenerate with new duration
                        handleDestinationSelect(selectedDestination, 0);
                      }
                    }
                    setShowDatePicker(false);
                  }}
                  className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium"
                  style={FONTS.body}
                >
                  {(datePickerValues._duration || currentDays) !== currentDays ? 'Save & Regenerate' : 'Save'}
                </button>
              </div>
            </div>
          </div>
          );
        })()}

        {/* Transportation Module Modal — Per-Leg Cards (matches personalization section) */}
        {showFlightModal && (() => {
          // Build legs for the modal — reuse legsList if available, otherwise build from itinerary
          const modalLegs = (() => {
            // If legsList has legs, augment with arrival/return if missing (no homeCity case)
            if (legsList.length > 0) {
              const homeCity = userProfile?.preferences?.homeCity || '';
              if (homeCity) return legsList; // Already has all legs
              // No homeCity — check if arrival/return legs are missing
              const hasArrival = legsList.some(l => l.type === 'departure');
              const hasReturn = legsList.some(l => l.type === 'return');
              if (hasArrival && hasReturn) return legsList;
              const augmented = [...legsList];
              const firstStop = augmented[0]?.from || augmented[0]?.to || '';
              const lastStop = augmented[augmented.length - 1]?.to || augmented[augmented.length - 1]?.from || '';
              if (!hasArrival && firstStop) {
                augmented.unshift({ key: 'arrival', from: 'Home', fromLabel: 'Home', to: firstStop, toLabel: firstStop.split(',')[0].trim(), type: 'departure', isFirst: true, isLast: false, needsHomeCity: true });
              }
              if (!hasReturn && lastStop) {
                augmented.push({ key: 'return', from: lastStop, fromLabel: lastStop.split(',')[0].trim(), to: 'Home', toLabel: 'Home', type: 'return', isFirst: false, isLast: true, needsHomeCity: true });
              }
              return augmented;
            }
            return (() => {
            const legs = [];
            const homeCity = userProfile?.preferences?.homeCity || '';
            const homeCityLabel = homeCity.split(',')[0].trim();
            // Derive first/last stops from itinerary days' baseCities
            const firstStop = itinerary?.destinations?.[0] || itinerary?.days?.[0]?.baseCity || itinerary?.days?.[0]?.city || '';
            const lastStopDay = itinerary?.days?.[itinerary.days.length - 1];
            const lastStop = itinerary?.destinations?.[itinerary.destinations.length - 1] || lastStopDay?.baseCity || lastStopDay?.city || firstStop;
            // Compute travel dates for each leg
            const tripStartDate = itinerary?.startDate || userProfile?.preferences?.startDate || '';
            const totalDays = itinerary?.days?.length || 0;
            const getDateForDayIndex = (dayIdx) => {
              if (!tripStartDate) return null;
              try {
                const d = new Date(tripStartDate + 'T00:00:00');
                d.setDate(d.getDate() + dayIdx);
                return d;
              } catch { return null; }
            };
            const formatLegDate = (date) => {
              if (!date) return '';
              return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
            };
            // Arrival leg — show even without homeCity (label as "Home" placeholder)
            if (firstStop) {
              const fromLabel = homeCity ? homeCityLabel : 'Home';
              const fromCity = homeCity || 'Home';
              const travelDate = getDateForDayIndex(0);
              legs.push({ key: 'arrival', from: fromCity, fromLabel, to: firstStop, toLabel: firstStop.split(',')[0].trim(), type: 'departure', isFirst: true, isLast: false, needsHomeCity: !homeCity, travelDate: formatLegDate(travelDate) });
            }
            // Inter-city legs from itinerary interCityTransport
            if (itinerary?.interCityTransport?.length > 0) {
              itinerary.interCityTransport.forEach((t, i) => {
                // Find the day index when city changes
                const travelDayIdx = (itinerary?.days || []).findIndex((d, di) => di > 0 && (d.baseCity || d.city) === (t.to || ''));
                const travelDate = travelDayIdx >= 0 ? getDateForDayIndex(travelDayIdx) : null;
                legs.push({ key: `inter-${i}`, from: t.from || '', fromLabel: (t.from || '').split(',')[0].trim(), to: t.to || '', toLabel: (t.to || '').split(',')[0].trim(), type: 'inter-city', isFirst: false, isLast: false, travelDate: formatLegDate(travelDate) });
              });
            } else {
              // Derive inter-city legs from itinerary days' baseCities
              const dayCities = (itinerary?.days || []).map(d => d.baseCity || d.city || '').filter(Boolean);
              let prevCity = dayCities[0] || '';
              dayCities.forEach((city, i) => {
                if (city && city !== prevCity) {
                  const travelDate = getDateForDayIndex(i);
                  legs.push({ key: `inter-${legs.length}`, from: prevCity, fromLabel: prevCity.split(',')[0].trim(), to: city, toLabel: city.split(',')[0].trim(), type: 'inter-city', isFirst: false, isLast: false, travelDate: formatLegDate(travelDate) });
                  prevCity = city;
                }
              });
            }
            // Return leg — show even without homeCity
            if (lastStop) {
              const toLabel = homeCity ? homeCityLabel : 'Home';
              const toCity = homeCity || 'Home';
              const travelDate = getDateForDayIndex(totalDays - 1);
              legs.push({ key: 'return', from: lastStop, fromLabel: lastStop.split(',')[0].trim(), to: toCity, toLabel, type: 'return', isFirst: false, isLast: true, needsHomeCity: !homeCity, travelDate: formatLegDate(travelDate) });
            }
            return legs;
          })();
          })();

          return (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-xl" style={FONTS.headingBold}>Getting Around</h3>
                  <p className="text-sm text-gray-500 mt-0.5">How you'll travel between each stop</p>
                </div>
                <button onClick={() => setShowFlightModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-700">Changing transport modes may require regenerating your itinerary to update schedules.</p>
              </div>
              
              {/* Per-Leg Transport Cards */}
              <div className="space-y-3">
                {modalLegs.length === 0 && (
                  <div className="text-center py-6">
                    <Plane className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-medium text-gray-600 mb-1">No transport legs available</p>
                    <p className="text-xs text-gray-400 mb-3">Generate an itinerary first to see transport options.</p>
                  </div>
                )}
                {/* Prompt to set home city if legs exist but homeCity is missing */}
                {modalLegs.some(l => l.needsHomeCity) && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex items-center gap-2">
                    <span className="text-blue-500 text-sm">ℹ️</span>
                    <p className="text-xs text-blue-700 flex-1">Set your home city in settings to search for flights to/from your departure point.</p>
                    <button
                      onClick={() => { setShowFlightModal(false); setShowSettings(true); }}
                      className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors flex-shrink-0"
                    >
                      Settings
                    </button>
                  </div>
                )}
                {modalLegs.map(leg => {
                  const legData = legTransports[leg.key] || {};
                  const isLoading = legData.isLoading;
                  const mode = legData.mode || (leg.isFirst ? transportationMode : leg.isLast ? (returnTransportMode || transportationMode) : 'fly');
                  const driveAvailable = legData.driveAvailable !== false;
                  const flightSearch = legFlightSearch[leg.key];
                  // Prefer real Google Directions time over LLM estimate
                  const driveDirKey = `${leg.from}|${leg.to}|driving`;
                  const googleDriveTime = interCityDirectionsTimes[driveDirKey];
                  const duration = (mode === 'drive' || mode === 'drive-own' || legData.recommendedMode === 'car' || legData.recommendedMode?.startsWith('drive'))
                    ? (googleDriveTime?.duration || legData.duration)
                    : legData.duration;

                  // Build mode options (same logic as personalization)
                  const modeOptions = [];
                  const fromIsIslandLeg = typeof isIslandCity === 'function' && (isIslandCity(leg.from) || isIslandCity(leg.fromLabel));
                  const toIsIslandLeg = typeof isIslandCity === 'function' && (isIslandCity(leg.to) || isIslandCity(leg.toLabel));
                  const involvesIslandLeg = fromIsIslandLeg || toIsIslandLeg;
                  // Compute cross-continent FIRST so we can override drive recommendations
                  const fromCountryDrive = getCityCountrySync(leg.from) || getCityCountrySync(leg.fromLabel);
                  const toCountryDrive = getCityCountrySync(leg.to) || getCityCountrySync(leg.toLabel);
                  const fromContinentDrive = getContinentForCountry(fromCountryDrive) || getContinentHintFromText(leg.from) || getContinentHintFromText(leg.fromLabel);
                  const toContinentDrive = getContinentForCountry(toCountryDrive) || getContinentHintFromText(leg.to) || getContinentHintFromText(leg.toLabel);
                  const isCrossContinent = fromContinentDrive && toContinentDrive && fromContinentDrive !== toContinentDrive;
                  // Override drive recommendation for cross-continent routes
                  // Also override if Google says drive > 10 hours (LLM estimate was wrong)
                  // BUG FIX (Mexico City → Oaxaca): same as personalization view
                  // below — only consider recommendedMode for the auto label, not
                  // the user-selected mode. See comment at the parallel site for
                  // full rationale.
                  const rawRecommended = legData.recommendedMode || 'fly';
                  const driveMinutes = googleDriveTime?.durationMinutes || legData.durationMinutes || 0;
                  const driveTooLong = (rawRecommended === 'car' || rawRecommended?.startsWith('drive')) && driveMinutes > 600;
                  const autoRecommendedMode = (isCrossContinent && (rawRecommended === 'car' || rawRecommended?.startsWith('drive'))) || driveTooLong ? 'fly' : rawRecommended;
                  // Suppress duration when recommendedMode and current mode disagree
                  // (duration was fetched for whichever mode was active and won't
                  // match the recommendation we're about to display).
                  const recommendationMatchesModeModal = rawRecommended === legData.mode
                    || (rawRecommended === 'fly' && legData.mode === 'fly')
                    || (rawRecommended?.startsWith('drive') && legData.mode?.startsWith('drive'));
                  const autoDurationModal = recommendationMatchesModeModal ? duration : null;
                  const autoLabel = isLoading
                    ? 'Analyzing...'
                    : `Recommended: ${autoRecommendedMode === 'fly' ? 'Fly' : autoRecommendedMode === 'train' ? 'Train' : autoRecommendedMode === 'ferry' ? 'Ferry' : autoRecommendedMode?.startsWith('drive') ? 'Drive' : autoRecommendedMode || 'Fly'}${autoDurationModal ? ` (${autoDurationModal})` : ''}`;
                  modeOptions.push({ id: 'auto', icon: 'sparkles', label: 'Auto', sublabel: autoLabel });
                  modeOptions.push({ id: 'fly', icon: 'plane', label: 'Fly' });
                  if (!involvesIslandLeg && !isCrossContinent && driveAvailable && (legData.durationMinutes || 0) < 600) {
                    modeOptions.push({ id: 'drive-own', icon: 'car', label: 'Drive' });
                  }
                  if (!involvesIslandLeg && !isCrossContinent && (legData.recommendedMode === 'train' || legData.reason?.toLowerCase()?.includes('train') || mode === 'train')) {
                    modeOptions.push({ id: 'train', icon: 'train', label: 'Train' });
                  }
                  if (legData.recommendedMode === 'ferry' || legData.reason?.toLowerCase()?.includes('ferry') || mode === 'ferry') {
                    modeOptions.push({ id: 'ferry', icon: 'ship', label: 'Ferry' });
                  }
                  if (involvesIslandLeg && !modeOptions.some(m => m.id === 'ferry') && !isCrossContinent) {
                    modeOptions.push({ id: 'ferry', icon: 'ship', label: 'Ferry' });
                  }

                  // Resolve airports from legTransports, itinerary data, or transportInfo
                  const findLegAirport = (city) => {
                    if (!city || !itinerary?.transportInfo) return null;
                    const cityLower = city.toLowerCase().split(',')[0].trim();
                    for (const [key, val] of Object.entries(itinerary.transportInfo)) {
                      if (key.toLowerCase().includes(cityLower) || cityLower.includes(key.toLowerCase().split(',')[0])) {
                        return val?.nearestAirport || null;
                      }
                    }
                    return null;
                  };
                  const depAirport = legData.departureAirport
                    || (leg.isFirst ? (profile?.home_airports?.[0] || null) : null)
                    || findLegAirport(leg.from);
                  const arrAirport = legData.arrivalAirport
                    || (leg.isFirst ? itinerary?.arrivalAirport : null)
                    || (leg.isLast ? itinerary?.departureAirport : null)
                    || findLegAirport(leg.to);

                  return (
                    <div key={leg.key} className="border-2 border-gray-200 rounded-xl p-4 bg-white hover:border-gray-300 transition-colors">
                      {/* Header: from → to + duration */}
                      <div className="flex items-start justify-between mb-3 gap-2">
                        <div className="flex items-start gap-2 min-w-0 flex-1">
                          <ActivityIcon emoji={mode === 'fly' ? '✈️' : mode === 'train' ? '🚆' : mode === 'ferry' ? '⛴️' : mode?.startsWith('drive') ? '🚗' : '✈️'} size={20} />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-semibold text-gray-800 break-words" style={FONTS.headingSemibold}>
                              {leg.fromLabel} → {leg.toLabel}
                            </p>
                            <p className="text-xs text-gray-400">
                              {leg.isFirst ? 'Getting there' : leg.isLast ? 'Getting home' : 'Inter-city'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {isLoading ? (
                            <div className="animate-spin w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full"></div>
                          ) : duration ? (
                            <span className="text-xs font-medium text-gray-500">{duration}</span>
                          ) : null}
                        </div>
                      </div>

                      {/* Mode selection buttons */}
                      <div className="flex flex-wrap gap-2 mb-3">
                        {modeOptions.map(opt => {
                          const isAutoOpt = opt.id === 'auto';
                          const isSelected = isAutoOpt ? !legData.isUserOverride : (mode === opt.id && legData.isUserOverride);
                          return (
                            <button
                              key={opt.id}
                              onClick={() => {
                                if (isAutoOpt) {
                                  setLegTransports(prev => ({
                                    ...prev,
                                    [leg.key]: { ...(prev[leg.key] || {}), mode: autoRecommendedMode, isUserOverride: false, source: 'auto' },
                                  }));
                                } else {
                                  handleLegModeChange(leg.key, opt.id, leg);
                                }
                              }}
                              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                                isSelected
                                  ? 'bg-teal-600 text-white shadow-sm'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {opt.icon === 'sparkles' ? <Sparkles className="w-3.5 h-3.5" />
                                : opt.icon === 'plane' ? <Plane className="w-3.5 h-3.5" />
                                : opt.icon === 'car' ? <Car className="w-3.5 h-3.5" />
                                : opt.icon === 'train' ? <Train className="w-3.5 h-3.5" />
                                : opt.icon === 'ship' ? <Ship className="w-3.5 h-3.5" />
                                : <ActivityIcon emoji={opt.emoji} size={14} />}
                              <span>{opt.label}</span>
                            </button>
                          );
                        })}
                        <button
                          onClick={() => handleLegModeChange(leg.key, 'custom', leg)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            mode === 'custom' ? 'bg-teal-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          <Pencil className="w-3 h-3 inline mr-1" /> Other
                        </button>
                      </div>

                      {/* Auto recommendation label */}
                      {!legData.isUserOverride && (
                        <p className="text-xs text-teal-600 mb-2 flex items-center gap-1">
                          {isLoading ? (
                            <><span className="animate-spin inline-block w-3 h-3 border border-teal-500 border-t-transparent rounded-full"></span> Analyzing best route...</>
                          ) : (
                            <>{'\u2728'} {autoLabel}</>
                          )}
                        </p>
                      )}

                      {/* Custom transport input */}
                      {mode === 'custom' && (
                        <input
                          type="text"
                          placeholder="e.g., friend is driving us, overnight bus, cruise ship..."
                          value={legData.customDesc || ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[<>]/g, '').slice(0, 100);
                            setLegTransports(prev => ({
                              ...prev,
                              [leg.key]: { ...(prev[leg.key] || {}), customDesc: val, isUserOverride: true, source: 'user' },
                            }));
                          }}
                          className="w-full mb-3 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none"
                          maxLength={100}
                        />
                      )}

                      {/* Airport info — fly legs */}
                      {mode === 'fly' && (depAirport || arrAirport) && (
                        <div className="bg-gray-50 rounded-lg p-3 mb-3 overflow-hidden">
                          {leg.travelDate && (
                            <div className="text-center mb-2">
                              <span className="text-[11px] font-semibold text-gray-500 bg-white px-2.5 py-0.5 rounded-full border border-gray-200">
                                📅 {leg.travelDate}
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 text-xs">
                          <div className="flex-1 text-center min-w-0 overflow-hidden">
                            <span className="font-mono font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded text-sm">
                              {depAirport?.code || '...'}
                            </span>
                            {depAirport?.name && <p className="text-[10px] text-gray-400 mt-1 truncate max-w-full">{depAirport.name}</p>}
                          </div>
                          <span className="text-gray-300 text-lg flex-shrink-0">→</span>
                          <div className="flex-1 text-center min-w-0 overflow-hidden">
                            <span className="font-mono font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded text-sm">
                              {arrAirport?.code || '...'}
                            </span>
                            {arrAirport?.name && <p className="text-[10px] text-gray-400 mt-1 truncate max-w-full">{arrAirport.name}</p>}
                          </div>
                        </div>
                          {/* Alternative airport selects */}
                          {(depAirport?.alternatives?.length > 0 || arrAirport?.alternatives?.length > 0) && (
                            <div className="flex items-center gap-2 mt-2 justify-center">
                              {depAirport?.alternatives?.length > 0 && (
                                <select
                                  value={depAirport?.code || ''}
                                  onChange={(e) => handleLegAirportChange(leg.key, 'departure', e.target.value)}
                                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
                                >
                                  <option value={depAirport?.code}>{depAirport?.code} ({depAirport?.name})</option>
                                  {depAirport.alternatives.map(alt => (
                                    <option key={alt.code} value={alt.code}>{alt.code} ({alt.name})</option>
                                  ))}
                                </select>
                              )}
                              {arrAirport?.alternatives?.length > 0 && (
                                <select
                                  value={arrAirport?.code || ''}
                                  onChange={(e) => handleLegAirportChange(leg.key, 'arrival', e.target.value)}
                                  className="text-xs border border-gray-200 rounded px-2 py-1 bg-white"
                                >
                                  <option value={arrAirport?.code}>{arrAirport?.code} ({arrAirport?.name})</option>
                                  {arrAirport.alternatives.map(alt => (
                                    <option key={alt.code} value={alt.code}>{alt.code} ({alt.name})</option>
                                  ))}
                                </select>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      {/* "Drive further for more routes" — departure side.
                          Progressively enhanced: starts with curated list, upgrades
                          to dynamic OurAirports RPC for the full 4,500-airport set. */}
                      {mode === 'fly' && (
                        <DriveFurtherPicker
                          from={leg.from}
                          currentCode={depAirport?.code}
                          excludeAlternatives={depAirport?.alternatives || []}
                          onSelect={(code) => handleLegAirportChange(leg.key, 'departure', code)}
                        />
                      )}

                      {/* "Drive further for more routes" — ARRIVAL side.
                          Mirror of the departure picker so travelers flying
                          into a small regional airport can consider a larger
                          hub nearby (e.g. into CXR instead of a tiny local
                          strip; into HND/NRT instead of KIX when arriving
                          to Japan). Uses the same DriveFurtherPicker
                          component — pass `leg.to` as the origin city for
                          the radius lookup. */}
                      {mode === 'fly' && (
                        <DriveFurtherPicker
                          from={leg.to}
                          currentCode={arrAirport?.code}
                          excludeAlternatives={arrAirport?.alternatives || []}
                          onSelect={(code) => handleLegAirportChange(leg.key, 'arrival', code)}
                          direction="arrival"
                        />
                      )}

                      {/* Time input — first and last legs */}
                      {(leg.isFirst || leg.isLast) && (
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            {leg.isFirst ? (mode === 'fly' ? '🛬 Arrival time' : '🕐 Departure time') : (mode === 'fly' ? '🛫 Flight departure' : '🕐 Leave at')}
                            {leg.travelDate && <span className="ml-2 text-gray-400 font-normal">({leg.travelDate})</span>}
                          </label>
                          <input
                            type="time"
                            value={legData.time || (leg.isFirst ? '08:00' : '18:00')}
                            onChange={(e) => handleLegTimeChange(leg.key, e.target.value)}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none bg-white"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            {leg.isFirst
                              ? "Don't have flights yet? We'll plan around this time"
                              : "We'll end activities in time for your departure"}
                          </p>
                        </div>
                      )}

                      {/* Flight search — fly legs */}
                      {mode === 'fly' && (
                        <div>
                          {legData.selectedFlight && (() => {
                            const sf = legData.selectedFlight;
                            const segs = sf.segments || [];
                            const firstSeg = segs[0];
                            const lastSeg = segs[segs.length - 1];
                            const depDateStr = firstSeg?.departure?.time ? parseAmadeusDate(firstSeg.departure.time) : null;
                            const arrDateStr = lastSeg?.arrival?.time ? parseAmadeusDate(lastSeg.arrival.time) : null;
                            const depTerminal = firstSeg?.departure?.terminal;
                            const arrTerminal = lastSeg?.arrival?.terminal;
                            const cabin = firstSeg?.cabin;
                            const isOvernightFlight = depDateStr && arrDateStr && arrDateStr > depDateStr;
                            // Layover cities for connecting flights
                            const layoverCities = segs.length > 1
                              ? segs.slice(0, -1).map(s => s.arrival?.airport).filter(Boolean)
                              : [];
                            const formatDuration = (dur) => dur ? dur.replace('PT', '').replace('H', 'h ').replace('M', 'm').trim() : '';

                            return (
                            <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-green-800 flex items-center gap-1.5">
                                  <span className="text-green-600">✓</span>
                                  {sf.airline} {sf.flightNumber}
                                  {cabin && cabin !== 'ECONOMY' && (
                                    <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded ml-1">{cabin}</span>
                                  )}
                                </span>
                                <button
                                  onClick={() => {
                                    setLegTransports(prev => ({
                                      ...prev,
                                      [leg.key]: { ...(prev[leg.key] || {}), selectedFlight: null },
                                    }));
                                    // Clear overnight state if this was the outbound
                                    if (leg?.isFirst) {
                                      setHasOvernightOutbound(false);
                                      setOvernightFlightInfo(null);
                                    }
                                  }}
                                  className="text-xs text-gray-400 hover:text-gray-600"
                                >
                                  ✕ Remove
                                </button>
                              </div>
                              {/* Route with airports, times, dates */}
                              <div className="flex items-center gap-3 text-xs text-green-700">
                                <div>
                                  <div className="flex items-baseline gap-1">
                                    <span className="font-bold text-sm text-green-800">{sf.from}</span>
                                    {depTerminal && <span className="text-[10px] text-green-500">T{depTerminal}</span>}
                                  </div>
                                  <span className="font-medium">{sf.departureTime || ''}</span>
                                  {depDateStr && (
                                    <div className="text-[10px] text-green-500">
                                      {new Date(depDateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                    </div>
                                  )}
                                </div>
                                <div className="flex-1 flex flex-col items-center">
                                  <span className="text-[10px] text-green-400">{formatDuration(sf.duration)}</span>
                                  <div className="w-full flex items-center gap-0.5">
                                    <div className="flex-1 h-px bg-green-300"></div>
                                    {layoverCities.map((city, i) => (
                                      <React.Fragment key={i}>
                                        <span className="text-[9px] text-orange-500 font-medium bg-orange-50 px-1 rounded">{city}</span>
                                        <div className="flex-1 h-px bg-green-300"></div>
                                      </React.Fragment>
                                    ))}
                                    <span className="text-green-400">→</span>
                                  </div>
                                  <span className="text-[10px] text-green-500">
                                    {segs.length > 1 ? `${segs.length - 1} stop` : 'Direct'}
                                  </span>
                                </div>
                                <div className="text-right">
                                  <div className="flex items-baseline gap-1 justify-end">
                                    <span className="font-bold text-sm text-green-800">{sf.to}</span>
                                    {arrTerminal && <span className="text-[10px] text-green-500">T{arrTerminal}</span>}
                                  </div>
                                  <span className="font-medium">{sf.arrivalTime || ''}</span>
                                  {arrDateStr && (
                                    <div className="text-[10px] text-green-500 flex items-center justify-end gap-1">
                                      {new Date(arrDateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                      {isOvernightFlight && <span className="text-purple-600 font-medium">+1</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {/* Price + booking */}
                              <div className="flex items-center justify-between mt-2 pt-2 border-t border-green-100">
                                <span className="text-sm font-semibold text-green-800">
                                  {sf.currency && sf.currency !== 'USD' ? `${sf.currency} ` : '$'}{sf.price}
                                </span>
                                {sf.bookingUrl && (
                                  <a href={sf.bookingUrl} target="_blank" rel="noopener noreferrer"
                                    className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                                    Book →
                                  </a>
                                )}
                                {/* Flight staleness warning */}
                                {sf._fetchedAt && (Date.now() - sf._fetchedAt > 24 * 60 * 60 * 1000) && (
                                  <span className="text-[10px] text-amber-600 flex items-center gap-0.5" title="Price may have changed">
                                    ⚠️ &gt;24h old
                                  </span>
                                )}
                              </div>
                            </div>
                            );
                          })()}
                          {!legData.selectedFlight && !flightSearch?.expanded && (
                            <button
                              onClick={() => searchLegFlights(leg.key, leg)}
                              disabled={flightSearch?.loading}
                              className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                            >
                              {flightSearch?.loading ? (
                                <><div className="animate-spin w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full"></div> Searching flights...</>
                              ) : (
                                <>🔍 Search flights</>
                              )}
                            </button>
                          )}
                          {flightSearch?.expanded && flightSearch?.results?.length > 0 && (
                            <div className="mt-2 space-y-1.5">
                              {/* Flight filters */}
                              <div className="flex gap-1.5 mb-2 flex-wrap">
                                <button
                                  onClick={() => setLegFlightSearch(prev => ({ ...prev, [leg.key]: { ...prev[leg.key], filterNonstop: !prev[leg.key]?.filterNonstop } }))}
                                  className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border transition-colors ${flightSearch.filterNonstop ? 'bg-teal-50 text-teal-700 border-teal-300' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                                >
                                  Non-stop only
                                </button>
                              </div>
                              {(flightSearch.filterNonstop ? flightSearch.results.filter(o => (o.itineraries?.[0]?.segments?.length || 1) === 1) : flightSearch.results).map((offer, idx) => {
                                const itin = offer.itineraries?.[0];
                                const segs = itin?.segments || [];
                                const airlines = (offer.airlines || []).join(', ');
                                const stops = segs.length - 1;
                                return (
                                  <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-100 hover:border-teal-300 transition-colors">
                                    <div className="min-w-0">
                                      <p className="text-xs font-medium text-gray-800 truncate">
                                        {airlines || 'Multiple airlines'} — {itin?.duration?.replace('PT', '').replace('H', 'h ').replace('M', 'm').toLowerCase() || ''}
                                      </p>
                                      <p className="text-xs text-gray-400">
                                        {stops === 0 ? 'Nonstop' : `${stops} stop${stops > 1 ? 's' : ''}`}
                                        {segs[0]?.departure?.airport ? ` · ${segs[0].departure.airport}→${segs[segs.length - 1]?.arrival?.airport}` : ''}
                                      </p>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className="text-sm font-bold text-gray-800">${offer.price}</span>
                                      <button
                                        onClick={() => selectLegFlight(leg.key, offer, flightSearch.dictionaries, leg)}
                                        className="text-xs bg-teal-600 text-white px-2.5 py-1 rounded-lg hover:bg-teal-700 transition-colors"
                                      >
                                        Select
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                              <button
                                onClick={() => setLegFlightSearch(prev => ({ ...prev, [leg.key]: { ...prev[leg.key], expanded: false } }))}
                                className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                              >
                                Collapse
                              </button>
                            </div>
                          )}
                          {flightSearch?.error && (
                            <p className="text-xs text-red-500 mt-1">{flightSearch.error}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Close / Done button */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={() => setShowFlightModal(false)}
                  className="px-6 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 text-sm font-medium"
                >
                  Done
                </button>
              </div>

              {/* Legacy flight mode removed — replaced by per-leg transport cards above */}
              {false && (
                <>
              {/* Home airport info (from settings) */}
              {(() => {
                const homeAirportsList = (() => { const raw = profile?.home_airports; if (Array.isArray(raw)) return raw; if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return []; } } return []; })();
                if (!homeAirportsList || homeAirportsList.length === 0) return null;
                const homeLabel = homeAirportsList.map(a => a.code).join(', ');
                return (
                  <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 mb-4">
                    <p className="text-sm text-gray-600" style={FONTS.body}>
                      {flightModalType === 'arrival' ? '🏠 Flying from:' : '🏠 Returning to:'}
                    </p>
                    <p className="text-sm text-gray-800" style={FONTS.bodyBold}>{homeLabel}</p>
                  </div>
                );
              })()}
              
              {/* Quick Lookup */}
              <div className="bg-blue-50 rounded-lg p-4 mb-4">
                <p className="text-sm text-gray-700 mb-2" style={FONTS.body}>
                  Have a flight number? Auto-fill details:
                </p>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  const flightNum = e.target.flightNumberLookup.value;
                  if (flightNum) {
                    lookupFlight(flightNum, flightModalType);
                  }
                }}>
                  <div className="flex gap-2">
                    <input
                      name="flightNumberLookup"
                      placeholder="e.g., UA1234"
                      className="flex-1 px-3 py-2 border rounded-lg"
                    />
                    <button
                      type="submit"
                      disabled={lookingUpFlight}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                    >
                      {lookingUpFlight ? '...' : '🔍 Lookup'}
                    </button>
                  </div>
                </form>
              </div>

              {/* Manual Entry Form */}
              <form onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const flightData = {
                  flightNumber: formData.get('flightNumber'),
                  airline: formData.get('airline'),
                  time: formData.get('time'),
                  date: formData.get('date')
                };
                
                // Track if destination airport changed
                const oldArrivalAirport = itinerary?.arrivalAirport?.code;
                const oldDepartureAirport = itinerary?.departureAirport?.code;
                const newDestAirport = formData.get('destAirport')?.toUpperCase();
                const newTime = formData.get('time');
                
                if (flightModalType === 'arrival') {
                  flightData.from = formData.get('from');
                  flightData.toAirport = newDestAirport;
                  
                  // Check if arrival TIME changed - recalculate Day 1 schedule
                  const oldTime = arrivalFlight.time;
                  if (newTime && newTime !== oldTime && itinerary?.days?.[0]) {
                    const recalculate = await showConfirmModal({ title: 'Recalculate Schedule', message: `You changed arrival time from ${oldTime || '08:00'} to ${newTime}.\n\nWould you like to recalculate Day 1 activities to start after your arrival?`, confirmText: 'Recalculate' });
                    
                    if (recalculate) {
                      // Calculate new start time (arrival + 2.5 hours for customs/transit/checkin)
                      const [hours, mins] = newTime.split(':').map(Number);
                      const arrivalMins = hours * 60 + mins;
                      const bufferMins = 150; // 2.5 hours buffer
                      const firstActivityMins = arrivalMins + bufferMins;
                      const firstActivityHour = Math.floor(firstActivityMins / 60);
                      const firstActivityMin = firstActivityMins % 60;
                      
                      // Format as 12-hour time
                      const formatTime = (h, m) => {
                        const period = h >= 12 ? 'PM' : 'AM';
                        const hour12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                        return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
                      };
                      
                      // SMART TIMING: Use user's preferred start time
                      // If arrival is very early (before 6 AM), add rest period until preferred start time
                      const preferredStartTime = userProfile?.preferences?.preferredStartTime || '09:00';
                      const [prefHour] = preferredStartTime.split(':').map(Number);
                      const MIN_ACTIVITY_START_HOUR = prefHour; // Use user's preference
                      const MIN_ACTIVITY_START_MINS = MIN_ACTIVITY_START_HOUR * 60;
                      
                      const isEarlyArrival = hours < 6; // Arrived before 6 AM
                      const isLateArrival = hours >= 14; // Arrived after 2 PM
                      
                      // Calculate actual first activity time
                      let actualFirstActivityMins = firstActivityMins;
                      let needsRestPeriod = false;
                      
                      if (isEarlyArrival && firstActivityMins < MIN_ACTIVITY_START_MINS) {
                        // Too early - push activities to preferred start time and add rest
                        actualFirstActivityMins = MIN_ACTIVITY_START_MINS;
                        needsRestPeriod = true;
                      }
                      
                      const newStartTime = formatTime(Math.floor(actualFirstActivityMins / 60), actualFirstActivityMins % 60);
                      
                      // Update Day 1 schedule times
                      const updatedItinerary = { ...itinerary };
                      const day1 = updatedItinerary.days[0];
                      
                      // Build new schedule
                      let newSchedule = [];
                      let currentTime = actualFirstActivityMins;
                      
                      if (day1.schedule) {
                        day1.schedule.forEach((activity, idx) => {
                          if (activity.activity?.toLowerCase().includes('arrival') || 
                              activity.activity?.toLowerCase().includes('immigration')) {
                            // Set arrival activity to match flight time
                            newSchedule.push({ ...activity, time: formatTime(hours, mins) });
                          } else if (activity.activity?.toLowerCase().includes('transit to hotel')) {
                            // Transit starts after immigration (arrival + 1.5 hours)
                            const transitMins = arrivalMins + 90;
                            newSchedule.push({ 
                              ...activity, 
                              time: formatTime(Math.floor(transitMins / 60) % 24, transitMins % 60) 
                            });
                          } else if (activity.activity?.toLowerCase().includes('check-in') ||
                                     activity.activity?.toLowerCase().includes('checkin')) {
                            // Check-in after transit
                            const checkinMins = arrivalMins + 120;
                            newSchedule.push({ 
                              ...activity, 
                              time: formatTime(Math.floor(checkinMins / 60) % 24, checkinMins % 60) 
                            });
                            
                            // Add rest period if early arrival
                            if (needsRestPeriod) {
                              const restEndMins = MIN_ACTIVITY_START_MINS;
                              const checkinEndMins = checkinMins + 30;
                              const restDurationHours = Math.round((restEndMins - checkinEndMins) / 60);
                              
                              if (restDurationHours > 0) {
                                newSchedule.push({
                                  time: formatTime(Math.floor(checkinEndMins / 60) % 24, checkinEndMins % 60),
                                  duration: `${restDurationHours} hours`,
                                  activity: '😴 Rest & Refresh',
                                  location: day1.hotel?.name || 'Hotel',
                                  description: `Rest after early morning arrival. Recover from travel and prepare for the day ahead.`,
                                  type: 'rest',
                                  isAutoGenerated: true
                                });
                              }
                            }
                          } else {
                            // Regular activities - schedule from actual start time
                            const activityTime = formatTime(Math.floor(currentTime / 60) % 24, currentTime % 60);
                            
                            // Parse duration and add to current time
                            const durationMatch = activity.duration?.match(/(\d+\.?\d*)/);
                            const durationHours = durationMatch ? parseFloat(durationMatch[1]) : 1;
                            const durationMins = durationHours * 60;
                            
                            newSchedule.push({ ...activity, time: activityTime });
                            currentTime += durationMins + 30; // Add 30 min buffer between activities
                          }
                        });
                      }
                      
                      day1.schedule = newSchedule;
                      
                      // Update meals too
                      if (day1.meals) {
                        day1.meals = day1.meals.map(meal => {
                          if (meal.type === 'breakfast') {
                            if (isEarlyArrival) {
                              // Early arrival - breakfast at hotel after rest
                              return { ...meal, time: formatTime(8, 0) };
                            } else if (isLateArrival) {
                              // Late arrival - skip breakfast
                              return { ...meal, time: 'Skipped - late arrival', skipped: true };
                            }
                            return meal;
                          } else if (meal.type === 'lunch') {
                            const lunchHour = Math.max(12, Math.floor(actualFirstActivityMins / 60) + 2);
                            return { ...meal, time: formatTime(Math.min(lunchHour, 14), 0) };
                          } else if (meal.type === 'dinner') {
                            return { ...meal, time: formatTime(19, 0) };
                          }
                          return meal;
                        });
                      }
                      
                      setItinerary(updatedItinerary);
                      
                      if (needsRestPeriod) {
                        showToast(`Day 1 schedule updated for early arrival! Activities start at ${newStartTime}.`, 'success', 5000);
                      } else {
                        showToast(`Day 1 schedule updated! First activity starts at ${newStartTime}.`, 'success', 4000);
                      }
                    }
                  }
                  
                  setArrivalFlight({ ...arrivalFlight, ...flightData });

                  // Check if arrival airport changed
                  if (newDestAirport && newDestAirport !== oldArrivalAirport) {
                    setFlightDetailsChanged(true); // Mark that flight details changed

                    // Always update the itinerary's airport immediately so the display reflects the change
                    setItinerary(prev => ({
                      ...prev,
                      arrivalAirport: {
                        ...prev.arrivalAirport,
                        code: newDestAirport,
                      }
                    }));

                    const shouldRegenerate = await showConfirmModal({ title: 'Regenerate Itinerary', message: `You changed the arrival airport from ${oldArrivalAirport || 'unset'} to ${newDestAirport}.\n\nWould you like to regenerate the itinerary to start from ${newDestAirport}?`, confirmText: 'Regenerate' });
                    if (shouldRegenerate) {
                      // Update user preferences and regenerate
                      setUserProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, arrivalAirport: newDestAirport }
                      }));
                      // Trigger regeneration (you may need to call your regenerate function here)
                      setShowFlightModal(false);
                      // Auto-trigger regeneration instead of just alerting
                      setTimeout(() => {
                        document.querySelector('[data-regenerate-button]')?.click();
                      }, 100);
                      return;
                    }
                  }
                } else {
                  flightData.to = formData.get('to');
                  flightData.fromAirport = newDestAirport;
                  
                  // Check if departure TIME changed - recalculate last day
                  const oldTime = departureFlight.time;
                  if (newTime && newTime !== oldTime && itinerary?.days?.length > 0) {
                    const lastDayIdx = itinerary.days.length - 1;
                    const recalculate = await showConfirmModal({ title: 'Recalculate Schedule', message: `You changed departure time from ${oldTime || '18:00'} to ${newTime}.\n\nWould you like to recalculate Day ${lastDayIdx + 1} activities to finish before your departure?`, confirmText: 'Recalculate' });
                    
                    if (recalculate) {
                      // Calculate latest activity end time (departure - 3 hours for checkout/transit/security)
                      const [hours, mins] = newTime.split(':').map(Number);
                      const departureMins = hours * 60 + mins;
                      const bufferMins = 180; // 3 hours buffer before flight
                      const lastActivityEndMins = departureMins - bufferMins;
                      
                      const formatTime = (h, m) => {
                        const period = h >= 12 ? 'PM' : 'AM';
                        const hour12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
                        return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
                      };
                      
                      // Update last day
                      const updatedItinerary = { ...itinerary };
                      const lastDay = updatedItinerary.days[lastDayIdx];
                      
                      // Add/update departure activity
                      const departureActivity = {
                        time: formatTime(Math.floor((departureMins - 120) / 60), (departureMins - 120) % 60),
                        duration: '2 hours',
                        activity: 'Airport Transfer & Departure',
                        location: itinerary.departureAirport?.name || 'Airport',
                        description: 'Check out, transfer to airport, check-in and security',
                        type: 'transit'
                      };
                      
                      // Check if departure activity exists
                      const existingDepartureIdx = lastDay.schedule?.findIndex(a => 
                        a.activity?.toLowerCase().includes('airport') || 
                        a.activity?.toLowerCase().includes('departure')
                      );
                      
                      if (existingDepartureIdx >= 0) {
                        lastDay.schedule[existingDepartureIdx] = departureActivity;
                      } else if (lastDay.schedule) {
                        lastDay.schedule.push(departureActivity);
                      }
                      
                      // Warn if activities run too late
                      const lastRegularActivity = lastDay.schedule?.filter(a => 
                        !a.activity?.toLowerCase().includes('departure') &&
                        !a.activity?.toLowerCase().includes('airport')
                      ).pop();
                      
                      if (lastRegularActivity) {
                        showToast(`Day ${lastDayIdx + 1} updated! Departure transfer at ${departureActivity.time}.`, 'success', 4000);
                      }
                      
                      setItinerary(updatedItinerary);
                    }
                  }
                  
                  setDepartureFlight({ ...departureFlight, ...flightData });

                  // Check if departure airport changed
                  if (newDestAirport && newDestAirport !== oldDepartureAirport) {
                    setFlightDetailsChanged(true); // Mark that flight details changed

                    // Always update the itinerary's airport immediately so the display reflects the change
                    setItinerary(prev => ({
                      ...prev,
                      departureAirport: {
                        ...prev.departureAirport,
                        code: newDestAirport,
                      }
                    }));

                    const shouldRegenerate = await showConfirmModal({ title: 'Regenerate Itinerary', message: `You changed the departure airport from ${oldDepartureAirport || 'unset'} to ${newDestAirport}.\n\nWould you like to regenerate the itinerary to end at ${newDestAirport}?`, confirmText: 'Regenerate' });
                    if (shouldRegenerate) {
                      setUserProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, departureAirport: newDestAirport }
                      }));
                      setShowFlightModal(false);
                      // Auto-trigger regeneration
                      setTimeout(() => {
                        document.querySelector('[data-regenerate-button]')?.click();
                      }, 100);
                      return;
                    }
                  }
                }
                
                setShowFlightModal(false);
              }}>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>
                      Flight Number <span className="text-gray-400">(optional)</span>
                    </label>
                    <input
                      name="flightNumber"
                      value={flightModalType === 'arrival' ? arrivalFlight.flightNumber : departureFlight.flightNumber}
                      onChange={(e) => {
                        if (flightModalType === 'arrival') {
                          setArrivalFlight({ ...arrivalFlight, flightNumber: e.target.value });
                        } else {
                          setDepartureFlight({ ...departureFlight, flightNumber: e.target.value });
                        }
                      }}
                      placeholder="UA1234"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>
                      Airline
                    </label>
                    <input
                      name="airline"
                      value={flightModalType === 'arrival' ? arrivalFlight.airline : departureFlight.airline}
                      onChange={(e) => {
                        if (flightModalType === 'arrival') {
                          setArrivalFlight({ ...arrivalFlight, airline: e.target.value });
                        } else {
                          setDepartureFlight({ ...departureFlight, airline: e.target.value });
                        }
                      }}
                      placeholder="United Airlines"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  {/* Origin/Final Destination (user's home) */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>
                      {flightModalType === 'arrival' ? 'Flying From (Your Home)' : 'Flying To (Your Home)'}
                    </label>
                    <input
                      name={flightModalType === 'arrival' ? 'from' : 'to'}
                      value={flightModalType === 'arrival' ? arrivalFlight.from : departureFlight.to}
                      onChange={(e) => {
                        if (flightModalType === 'arrival') {
                          setArrivalFlight({ ...arrivalFlight, from: e.target.value });
                        } else {
                          setDepartureFlight({ ...departureFlight, to: e.target.value });
                        }
                      }}
                      placeholder="New York (JFK)"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>
                  
                  {/* Destination Airport (in Patagonia etc) */}
                  <div>
                    <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>
                      {flightModalType === 'arrival' 
                        ? '📍 Landing At (Destination Airport)' 
                        : '📍 Departing From (Destination Airport)'}
                    </label>
                    <input
                      name="destAirport"
                      defaultValue={
                        flightModalType === 'arrival' 
                          ? itinerary?.arrivalAirport?.code || ''
                          : itinerary?.departureAirport?.code || ''
                      }
                      placeholder="e.g., PUQ, FTE"
                      maxLength="3"
                      className="w-full px-3 py-2 border rounded-lg uppercase font-mono text-lg"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {flightModalType === 'arrival' 
                        ? 'Airport where you land in your destination'
                        : 'Airport where you depart from at end of trip'}
                    </p>
                    
                    {/* Quick airport buttons */}
                    {itinerary?.destination?.toLowerCase().includes('patagonia') && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {['PUQ', 'FTE', 'USH', 'PMC', 'BRC'].map(code => (
                          <button
                            key={code}
                            type="button"
                            onClick={(e) => {
                              const input = e.target.closest('div').querySelector('input[name="destAirport"]');
                              if (input) input.value = code;
                            }}
                            className="px-2 py-1 text-xs bg-gray-100 hover:bg-teal-100 rounded font-mono"
                          >
                            {code}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>
                        Time
                        {(flightModalType === 'arrival' ? arrivalFlight.timeUnverified : departureFlight.timeUnverified) && (
                          <span className="ml-1 text-amber-600 text-xs font-medium">-- update required</span>
                        )}
                      </label>
                      <input
                        name="time"
                        type="time"
                        value={flightModalType === 'arrival' ? arrivalFlight.time : departureFlight.time}
                        onChange={(e) => {
                          if (flightModalType === 'arrival') {
                            setArrivalFlight({ ...arrivalFlight, time: e.target.value, timeUnverified: false });
                          } else {
                            setDepartureFlight({ ...departureFlight, time: e.target.value, timeUnverified: false });
                          }
                        }}
                        required
                        className={`w-full px-3 py-2 border rounded-lg ${
                          (flightModalType === 'arrival' ? arrivalFlight.timeUnverified : departureFlight.timeUnverified)
                            ? 'border-amber-400 bg-amber-50 ring-2 ring-amber-200'
                            : ''
                        }`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-700 mb-1" style={FONTS.body}>Date</label>
                      <input
                        name="date"
                        type="date"
                        value={flightModalType === 'arrival' ? arrivalFlight.date : departureFlight.date}
                        onChange={(e) => {
                          if (flightModalType === 'arrival') {
                            setArrivalFlight({ ...arrivalFlight, date: e.target.value });
                          } else {
                            setDepartureFlight({ ...departureFlight, date: e.target.value });
                          }
                        }}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowFlightModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                  >
                    Save Flight
                  </button>
                </div>
              </form>
              </>
              )}
            </div>
          </div>
          );
        })()}

        {/* Free Time Activity Modal */}
        {showFreeTimeModal && selectedFreeTimeSlot && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold" style={FONTS.headingBold}>Fill Your Free Time</h3>
                    <p className="text-sm text-cyan-100 mt-1">
                      Day {selectedFreeTimeSlot.day + 1} • {formatTimeDisplay(selectedFreeTimeSlot.time)} • {selectedFreeTimeSlot.duration}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowFreeTimeModal(false);
                      setSelectedFreeTimeSlot(null);
                      setFreeTimeSuggestions([]);
                      setShowMoreFreeTimeSuggestions(false);
                      setShowCustomActivityInput(false);
                      setCustomActivityName('');
                    }}
                    className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 overflow-y-auto max-h-[60vh]">
                {/* Context info about this free time slot */}
                {(selectedFreeTimeSlot.fromLocation || selectedFreeTimeSlot.toLocation) && (
                  <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600" style={FONTS.body}>
                    {selectedFreeTimeSlot.fromLocation && (
                      <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Near: {selectedFreeTimeSlot.fromLocation.split(',')[0]}</span>
                    )}
                    {selectedFreeTimeSlot.fromLocation && selectedFreeTimeSlot.toLocation && <span className="mx-2">→</span>}
                    {selectedFreeTimeSlot.toLocation && (
                      <span className="flex items-center gap-1"><Crosshair className="w-3 h-3" /> Next: {selectedFreeTimeSlot.toLocation.split(',')[0]}</span>
                    )}
                  </div>
                )}

                {/* Dynamic AI-generated suggestions */}
                {(() => {
                  const dayIndex = selectedFreeTimeSlot.day;
                  const dayData = itinerary?.days?.[dayIndex];
                  const cityName = dayData?.baseCity || selectedDestination?.name || 'the area';

                  // Show loading state
                  if (loadingFreeTimeSuggestions) {
                    return (
                      <div className="text-center py-8">
                        <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                        <p className="text-sm text-gray-500" style={FONTS.body}>Finding personalized suggestions...</p>
                        <p className="text-xs text-gray-400 mt-1">Based on your schedule and location</p>
                      </div>
                    );
                  }

                  // Show dynamic suggestions if available
                  if (freeTimeSuggestions && freeTimeSuggestions.length > 0) {
                    return (
                      <>
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Personalized for your {selectedFreeTimeSlot.duration}</p>
                        <div className="space-y-3">
                          {freeTimeSuggestions.map((option, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                addActivity(dayIndex, {
                                  time: selectedFreeTimeSlot.time,
                                  duration: option.duration || selectedFreeTimeSlot.duration,
                                  activity: option.activity,
                                  location: option.location || cityName,
                                  description: option.description,
                                  priceTier: '$$',
                                  type: 'custom'
                                });
                                setShowFreeTimeModal(false);
                                setSelectedFreeTimeSlot(null);
                                setFreeTimeSuggestions([]);
                              }}
                              className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-teal-400 hover:bg-teal-50 transition-all text-left"
                            >
                              <div className="flex-1">
                                <p className="font-semibold text-gray-900" style={FONTS.bodyBold}>{option.activity}</p>
                                <p className="text-sm text-gray-500">{option.description}</p>
                                {option.duration && (
                                  <p className="text-xs text-teal-600 mt-1">~{option.duration}</p>
                                )}
                              </div>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </button>
                          ))}
                        </div>

                        {/* Regenerate button */}
                        <button
                          onClick={() => generateFreeTimeSuggestions(selectedFreeTimeSlot)}
                          className="w-full mt-3 p-2 text-sm text-teal-600 hover:text-teal-700 flex items-center justify-center gap-2"
                        >
                          <RefreshCw className="w-4 h-4" />
                          <span style={FONTS.body}>Get different suggestions</span>
                        </button>
                      </>
                    );
                  }

                  // Fallback quick picks while suggestions load or if API fails
                  return (
                    <>
                      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Quick Picks for {cityName}</p>
                      <div className="space-y-3">
                        {[
                          { emoji: '🏔️', label: 'Panoramic view', description: 'City or landscape overlook', activityName: 'Scenic Viewpoint Visit' },
                          { emoji: '☕', label: 'Local café', description: 'Relax with local coffee', activityName: 'Local Café Experience' },
                          { emoji: '🛍️', label: 'Shopping area', description: 'Browse local shops', activityName: 'Local Shopping & Browsing' },
                          { emoji: '🌳', label: 'Park or garden', description: 'Green space to unwind', activityName: 'Park & Garden Visit' }
                        ].map((option, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              addActivity(dayIndex, {
                                time: selectedFreeTimeSlot.time,
                                duration: selectedFreeTimeSlot.duration,
                                activity: `${option.emoji} ${option.activityName}`,
                                location: `Near ${cityName}`,
                                description: `${option.description}. Use the chat to get a specific recommendation for this area.`,
                                priceTier: option.label.toLowerCase().includes('café') || option.label.toLowerCase().includes('shopping') ? '$$' : 'Free',
                                type: 'custom'
                              });

                              setShowFreeTimeModal(false);
                              setSelectedFreeTimeSlot(null);

                              // Open chat with a suggestion prompt for generic options
                              setItineraryChatInput(`Can you recommend a specific ${option.label.toLowerCase()} near ${cityName} for my ${selectedFreeTimeSlot.duration} of free time at ${formatTimeDisplay(selectedFreeTimeSlot.time)} on Day ${dayIndex + 1}?`);
                              setShowItineraryChat(true);
                            }}
                            className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-teal-400 hover:bg-teal-50 transition-all text-left"
                          >
                            <ActivityIcon emoji={option.emoji} size={28} />
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900" style={FONTS.bodyBold}>{option.label}</p>
                              <p className="text-sm text-gray-500">{option.description}</p>
                            </div>
                            <ChevronRight className="w-5 h-5 text-gray-400" />
                          </button>
                        ))}
                      </div>
                    </>
                  );
                })()}

                {/* More Suggestions Button & Section */}
                {!showMoreFreeTimeSuggestions ? (
                  <button
                    onClick={() => setShowMoreFreeTimeSuggestions(true)}
                    className="w-full mt-4 p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-teal-400 hover:text-teal-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    <span style={FONTS.body}>Show personalized suggestions</span>
                  </button>
                ) : (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">More Activity Ideas</p>
                    <div className="space-y-2">
                      {(() => {
                        const dayIndex = selectedFreeTimeSlot.day;
                        const dayData = itinerary?.days?.[dayIndex];
                        const cityName = dayData?.baseCity || selectedDestination?.name || 'the area';

                        // Additional suggestions based on common traveler interests
                        const moreSuggestions = [
                          { emoji: '🎨', label: 'Local Art & Culture', activity: 'Art Gallery or Street Art Tour', description: 'Discover local artists and cultural spots' },
                          { emoji: '🥘', label: 'Food Experience', activity: 'Local Food Market or Cooking Class', description: 'Taste authentic local flavors' },
                          { emoji: '🏃', label: 'Active Adventure', activity: 'Walking Tour or Bike Rental', description: 'Explore the city actively' },
                          { emoji: '🧘', label: 'Relaxation', activity: 'Spa Visit or Peaceful Garden', description: 'Take time to unwind and recharge' },
                          { emoji: '📸', label: 'Photography Spots', activity: 'Scenic Photo Locations', description: 'Capture memorable views' },
                          { emoji: '🎵', label: 'Live Entertainment', activity: 'Live Music or Performance', description: 'Experience local entertainment' }
                        ];

                        return moreSuggestions.map((option, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              addActivity(dayIndex, {
                                time: selectedFreeTimeSlot.time,
                                duration: selectedFreeTimeSlot.duration,
                                activity: `${option.emoji} ${option.activity}`,
                                location: cityName,
                                description: `${option.description} in ${cityName}`,
                                priceTier: '$$',
                                type: 'custom'
                              });
                              setShowFreeTimeModal(false);
                              setSelectedFreeTimeSlot(null);
                              setShowMoreFreeTimeSuggestions(false);
                            }}
                            className="w-full flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:border-teal-400 hover:bg-teal-50 transition-all text-left"
                          >
                            <ActivityIcon emoji={option.emoji} size={22} />
                            <div className="flex-1">
                              <p className="font-semibold text-gray-900 text-sm" style={FONTS.bodyBold}>{option.label}</p>
                              <p className="text-xs text-gray-500">{option.description}</p>
                            </div>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </button>
                        ));
                      })()}
                    </div>
                  </div>
                )}

                {/* Add Custom Activity Section */}
                {!showCustomActivityInput ? (
                  <button
                    onClick={() => setShowCustomActivityInput(true)}
                    className="w-full mt-4 p-3 border border-gray-200 rounded-xl text-gray-600 hover:border-teal-400 hover:text-teal-600 transition-all flex items-center justify-center gap-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm" style={FONTS.body}>Add custom activity or ask for suggestions</span>
                  </button>
                ) : (
                  <div className="mt-4 p-4 bg-teal-50 border border-brand-200 rounded-xl">
                    <p className="text-xs font-bold text-teal-700 uppercase tracking-wide mb-3">What would you like to do?</p>
                    <div className="space-y-3">
                      <input
                        type="text"
                        value={customActivityName}
                        onChange={(e) => setCustomActivityName(e.target.value)}
                        placeholder="e.g., 'Find a great coffee shop' or 'Ice cream at a local spot'"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-teal-500 text-sm"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && customActivityName.trim()) {
                            // Open chat with the prompt pre-filled
                            const dayIndex = selectedFreeTimeSlot.day;
                            const dayData = itinerary?.days?.[dayIndex];
                            const cityName = dayData?.baseCity || selectedDestination?.name || 'the area';
                            const timeStr = selectedFreeTimeSlot.time;
                            const durationStr = selectedFreeTimeSlot.duration;

                            // Pre-fill the chat with context
                            setItineraryChatInput(`For my free time on Day ${dayIndex + 1} at ${formatTimeDisplay(timeStr)} (${durationStr} available) in ${cityName}: ${customActivityName.trim()}`);
                            setShowItineraryChat(true);
                            setShowFreeTimeModal(false);
                            setSelectedFreeTimeSlot(null);
                            setShowCustomActivityInput(false);
                            setCustomActivityName('');
                          }
                        }}
                      />
                      <div className="flex gap-2">
                        <select
                          value={customActivityDuration}
                          onChange={(e) => setCustomActivityDuration(e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 text-sm"
                        >
                          <option value="15 min">15 min</option>
                          <option value="30 min">30 min</option>
                          <option value="45 min">45 min</option>
                          <option value="1 hour">1 hour</option>
                          <option value="1.5 hours">1.5 hours</option>
                          <option value="2 hours">2 hours</option>
                        </select>
                        <button
                          onClick={() => {
                            if (customActivityName.trim()) {
                              const dayIndex = selectedFreeTimeSlot.day;
                              const dayData = itinerary?.days?.[dayIndex];
                              const cityName = dayData?.baseCity || selectedDestination?.name || 'the area';

                              addActivity(dayIndex, {
                                time: selectedFreeTimeSlot.time,
                                duration: customActivityDuration,
                                activity: customActivityName.trim(),
                                location: cityName,
                                description: `Custom activity added by you`,
                                priceTier: '$',
                                type: 'custom'
                              });
                              setShowFreeTimeModal(false);
                              setSelectedFreeTimeSlot(null);
                              setShowCustomActivityInput(false);
                              setCustomActivityName('');
                              setCustomActivityDuration('30 min');
                              setShowMoreFreeTimeSuggestions(false);
                            }
                          }}
                          disabled={!customActivityName.trim()}
                          className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                            customActivityName.trim()
                              ? 'bg-[#313143] text-white hover:bg-[#3d3d52]'
                              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          Add
                        </button>
                      </div>

                      {/* Ask AI for suggestions button */}
                      {customActivityName.trim() && (
                        <button
                          onClick={() => {
                            const dayIndex = selectedFreeTimeSlot.day;
                            const dayData = itinerary?.days?.[dayIndex];
                            const cityName = dayData?.baseCity || selectedDestination?.name || 'the area';
                            const timeStr = selectedFreeTimeSlot.time;
                            const durationStr = selectedFreeTimeSlot.duration;

                            // Pre-fill the chat with context
                            setItineraryChatInput(`For my free time on Day ${dayIndex + 1} at ${formatTimeDisplay(timeStr)} (${durationStr} available) in ${cityName}: ${customActivityName.trim()}`);
                            setShowItineraryChat(true);
                            setShowFreeTimeModal(false);
                            setSelectedFreeTimeSlot(null);
                            setShowCustomActivityInput(false);
                            setCustomActivityName('');
                          }}
                          className="w-full px-3 py-2 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium text-sm hover:from-teal-600 hover:to-cyan-600 transition-all flex items-center justify-center gap-2"
                        >
                          <Sparkles className="w-4 h-4" />
                          <span>Ask AI for suggestions</span>
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setShowCustomActivityInput(false);
                          setCustomActivityName('');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Ask for something specific */}
                <button
                  onClick={() => {
                    setShowItineraryChat(true);
                    setShowFreeTimeModal(false);
                    setSelectedFreeTimeSlot(null);
                    setShowCustomActivityInput(false);
                    setCustomActivityName('');
                  }}
                  className="w-full mt-3 p-3 text-gray-500 hover:text-teal-600 transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-sm" style={FONTS.body}>Ask for something specific...</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Swap Activity Modal */}
        {showSwapModal && swapTarget && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold" style={FONTS.headingBold}>Swap Activity</h3>
                    <p className="text-sm text-orange-100 mt-1">
                      Replacing "{swapTarget.itemName}" • Day {swapTarget.dayIdx + 1}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowSwapModal(false);
                      setSwapTarget(null);
                      setSwapSuggestions([]);
                    }}
                    className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-5 overflow-y-auto max-h-[60vh]">
                {/* Loading state */}
                {loadingSwapSuggestions && (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-3 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-sm text-gray-500" style={FONTS.body}>Finding alternatives...</p>
                    <p className="text-xs text-gray-400 mt-1">Based on your schedule in {swapTarget.city}</p>
                  </div>
                )}

                {/* Suggestions */}
                {!loadingSwapSuggestions && swapSuggestions.length > 0 && (
                  <>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3">Pick a replacement</p>
                    <div className="space-y-3">
                      {swapSuggestions.map((suggestion, idx) => (
                        <div
                          key={idx}
                          className="p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all cursor-pointer group"
                          onClick={() => handleAcceptSwap(suggestion)}
                        >
                          <div className="flex items-start gap-3">
                            <ActivityIcon emoji={suggestion.emoji || '✨'} size={22} />
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 text-sm" style={FONTS.headingSemibold}>{suggestion.name}</h4>
                              <p className="text-xs text-gray-500 mt-1" style={FONTS.body}>{suggestion.description}</p>
                              <div className="flex items-center gap-2 mt-2">
                                {suggestion.duration && (
                                  <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">⏱ {suggestion.duration}</span>
                                )}
                                {suggestion.category && (
                                  <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">{suggestion.category}</span>
                                )}
                              </div>
                            </div>
                            <span className="text-xs text-orange-500 font-medium opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap mt-1">Use this →</span>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Refresh button */}
                    <button
                      onClick={handleRefreshSwapSuggestions}
                      className="w-full mt-4 p-3 text-orange-600 hover:text-orange-700 transition-all flex items-center justify-center gap-2 border border-orange-200 rounded-xl hover:bg-orange-50"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className="text-sm font-medium" style={FONTS.body}>Show different options</span>
                    </button>
                  </>
                )}

                {/* No suggestions / error state */}
                {!loadingSwapSuggestions && swapSuggestions.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm text-gray-500" style={FONTS.body}>Couldn't generate alternatives right now.</p>
                    <button
                      onClick={handleRefreshSwapSuggestions}
                      className="mt-3 px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}

                {/* Chat fallback */}
                <button
                  onClick={() => {
                    const prompt = `Suggest alternatives for "${swapTarget.itemName}" on Day ${swapTarget.dayIdx + 1} in ${swapTarget.city}. Keep the same time slot and duration.`;
                    setShowItineraryChat(true);
                    handleItineraryChatSend(prompt);
                    setShowSwapModal(false);
                    setSwapTarget(null);
                    setSwapSuggestions([]);
                  }}
                  className="w-full mt-3 p-3 text-gray-500 hover:text-orange-600 transition-all flex items-center justify-center gap-2"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-sm" style={FONTS.body}>Ask for something specific...</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Hotel Modal */}
        {/* Add Activity Modal */}
        {showAddActivityModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-2xl mb-4" style={FONTS.headingBold}>Add Activity</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const newActivity = {
                  activity: formData.get('activity'),
                  location: formData.get('location'),
                  time: formData.get('time'),
                  duration: formData.get('duration'),
                  description: formData.get('description'),
                  priceTier: formData.get('priceTier'),
                  type: 'custom'
                };
                const newItinerary = { ...itinerary };
                if (addActivityInsertIndex !== null) {
                  // Insert at specific position
                  newItinerary.days[addActivityDay].schedule.splice(addActivityInsertIndex, 0, newActivity);
                } else {
                  // Append to end
                  newItinerary.days[addActivityDay].schedule.push(newActivity);
                }

                // Clear transit times for this day so they get recalculated with new activity
                setTransitTimes(prev => {
                  const updated = { ...prev };
                  Object.keys(updated).forEach(key => {
                    if (key.startsWith(`day${addActivityDay}-`)) {
                      delete updated[key];
                      delete transitTimesRef.current[key];
                    }
                  });
                  return updated;
                });
                transitCalcGuardRef.current = null;

                // Mark that user has modified the itinerary
                itineraryUserModifiedRef.current = true;
    setHasUnsavedChanges(true);

                setItinerary(newItinerary);
                setShowAddActivityModal(false);
                setAddActivityInsertIndex(null);
                setAddActivityPreFill('');
              }}>
                <div className="space-y-3">
                  <input name="activity" placeholder="Activity name" required className="w-full px-3 py-2 border rounded-lg" defaultValue={addActivityPreFill} />
                  <input name="location" placeholder="Location" required className="w-full px-3 py-2 border rounded-lg" />
                  <div className="grid grid-cols-2 gap-3">
                    <select name="time" required className="px-3 py-2 border rounded-lg bg-white">
                      <option value="">Select time</option>
                      <option value="06:00 AM">6:00 AM</option>
                      <option value="06:30 AM">6:30 AM</option>
                      <option value="07:00 AM">7:00 AM</option>
                      <option value="07:30 AM">7:30 AM</option>
                      <option value="08:00 AM">8:00 AM</option>
                      <option value="08:30 AM">8:30 AM</option>
                      <option value="09:00 AM">9:00 AM</option>
                      <option value="09:30 AM">9:30 AM</option>
                      <option value="10:00 AM">10:00 AM</option>
                      <option value="10:30 AM">10:30 AM</option>
                      <option value="11:00 AM">11:00 AM</option>
                      <option value="11:30 AM">11:30 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="12:30 PM">12:30 PM</option>
                      <option value="01:00 PM">1:00 PM</option>
                      <option value="01:30 PM">1:30 PM</option>
                      <option value="02:00 PM">2:00 PM</option>
                      <option value="02:30 PM">2:30 PM</option>
                      <option value="03:00 PM">3:00 PM</option>
                      <option value="03:30 PM">3:30 PM</option>
                      <option value="04:00 PM">4:00 PM</option>
                      <option value="04:30 PM">4:30 PM</option>
                      <option value="05:00 PM">5:00 PM</option>
                      <option value="05:30 PM">5:30 PM</option>
                      <option value="06:00 PM">6:00 PM</option>
                      <option value="06:30 PM">6:30 PM</option>
                      <option value="07:00 PM">7:00 PM</option>
                      <option value="07:30 PM">7:30 PM</option>
                      <option value="08:00 PM">8:00 PM</option>
                      <option value="08:30 PM">8:30 PM</option>
                      <option value="09:00 PM">9:00 PM</option>
                      <option value="09:30 PM">9:30 PM</option>
                      <option value="10:00 PM">10:00 PM</option>
                      <option value="10:30 PM">10:30 PM</option>
                      <option value="11:00 PM">11:00 PM</option>
                    </select>
                    <select name="duration" required className="px-3 py-2 border rounded-lg bg-white">
                      <option value="">Duration</option>
                      <option value="30 minutes">30 min</option>
                      <option value="1 hour">1 hour</option>
                      <option value="1.5 hours">1.5 hours</option>
                      <option value="2 hours">2 hours</option>
                      <option value="2.5 hours">2.5 hours</option>
                      <option value="3 hours">3 hours</option>
                      <option value="4 hours">4 hours</option>
                      <option value="Half day">Half day</option>
                      <option value="Full day">Full day</option>
                    </select>
                  </div>
                  <textarea name="description" placeholder="Description" rows="2" className="w-full px-3 py-2 border rounded-lg" />
                  <select name="priceTier" className="w-full px-3 py-2 border rounded-lg">
                    <option value="$">$ Budget</option>
                    <option value="$$">$$ Moderate</option>
                    <option value="$$$">$$$ Premium</option>
                  </select>
                </div>
                <div className="flex gap-3 mt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddActivityModal(false);
                      setAddActivityInsertIndex(null);
                      setAddActivityPreFill('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-[#313143] text-white rounded-lg hover:bg-[#3d3d52]"
                  >
                    Add
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Add Day Modal */}
        {showAddDayModal && itinerary?.days?.length > 0 && (() => {
          const effectiveIdx = addDayAfterIndex !== null ? addDayAfterIndex : (currentDay >= 0 ? currentDay : itinerary.days.length - 1);
          const addCity = itinerary.days[effectiveIdx]?.baseCity || 'your destination';
          return (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
              <h3 className="text-xl mb-1" style={FONTS.headingBold}>Add a Day</h3>

              {/* Day picker */}
              <p className="text-xs text-gray-500 mb-2" style={FONTS.body}>Insert after:</p>
              <div className="flex gap-1.5 flex-wrap mb-4">
                {itinerary.days.map((d, i) => (
                  <button
                    key={i}
                    onClick={() => setAddDayAfterIndex(i)}
                    className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                    style={i === effectiveIdx
                      ? { background: '#18B8A6', color: '#fff', ...FONTS.bodySemibold }
                      : { background: '#f3f4f6', color: '#6b7280', ...FONTS.body }
                    }
                  >
                    Day {d.day}{d.baseCity ? ` · ${d.baseCity.split(/[,>]/)[0].trim().slice(0, 10)}` : ''}
                  </button>
                ))}
              </div>

              {/* Blank + Explore side by side */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  onClick={() => addNewDay(effectiveIdx, 'blank')}
                  className="flex flex-col items-center gap-1.5 p-4 rounded-xl border border-gray-200 hover:border-teal-400 hover:bg-teal-50 transition-all text-center group"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(49,49,67,0.08)' }}>
                    <Plus className="w-5 h-5" style={{ color: '#313143' }} />
                  </div>
                  <span className="text-sm font-bold text-gray-800 group-hover:text-teal-700" style={FONTS.bodySemibold}>Blank Day</span>
                  <span className="text-[10px] text-gray-500">Build it yourself</span>
                </button>
                <button
                  onClick={() => addNewDay(effectiveIdx, 'explore')}
                  className="flex flex-col items-center gap-1.5 p-4 rounded-xl border border-gray-200 hover:border-teal-400 hover:bg-teal-50 transition-all text-center group"
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(24,184,166,0.10)' }}>
                    <Compass className="w-5 h-5" style={{ color: '#18B8A6' }} />
                  </div>
                  <span className="text-sm font-bold text-gray-800 group-hover:text-teal-700" style={FONTS.bodySemibold}>Explore Day</span>
                  <span className="text-[10px] text-gray-500">Markets & hidden gems</span>
                </button>
              </div>

              {/* Custom day — text input */}
              <div className="border border-gray-200 rounded-xl p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(205,122,77,0.10)' }}>
                    <Edit2 className="w-3.5 h-3.5" style={{ color: '#CD7A4D' }} />
                  </div>
                  <span className="text-sm font-bold text-gray-800" style={FONTS.bodySemibold}>Describe your day</span>
                </div>
                <input
                  type="text"
                  value={addDayCustomText}
                  onChange={(e) => setAddDayCustomText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && addDayCustomText.trim()) addNewDay(effectiveIdx, 'custom', addDayCustomText.trim()); }}
                  placeholder="e.g. Wine tasting & vineyard tour"
                  className="w-full text-sm px-3 py-2 rounded-lg border border-gray-200 focus:border-teal-400 focus:ring-1 focus:ring-teal-400 outline-none"
                  style={FONTS.body}
                />
                {addDayCustomText.trim() && (
                  <button
                    onClick={() => addNewDay(effectiveIdx, 'custom', addDayCustomText.trim())}
                    className="w-full mt-2 px-3 py-2 rounded-lg text-white text-sm font-medium transition-colors"
                    style={{ ...FONTS.bodySemibold, background: '#18B8A6' }}
                  >
                    Add Day
                  </button>
                )}
              </div>

              <button
                onClick={() => { setShowAddDayModal(false); setAddDayCustomText(''); setAddDayAfterIndex(null); }}
                className="w-full mt-3 px-4 py-2 border border-gray-300 rounded-xl text-gray-600 hover:bg-gray-50 text-sm font-medium"
              >
                Cancel
              </button>
            </div>
          </div>
          );
        })()}

        {/* Add Meal Modal */}
        {showAddMealModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-2xl mb-4" style={FONTS.headingBold}>Add Meal</h3>
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const mealEntry = {
                  restaurant: formData.get('restaurant') || 'Local Restaurant',
                  cuisine: formData.get('cuisine') || '',
                  time: formData.get('time') || '12:00 PM',
                  duration: '1 hour',
                  description: formData.get('description') || '',
                  priceTier: formData.get('priceTier') || '$$',
                  mealType: formData.get('mealType') || 'lunch',
                  type: 'custom'
                };
                const newItinerary = {
                  ...itinerary,
                  days: itinerary.days.map((day, i) =>
                    i === addActivityDay
                      ? { ...day, meals: [...(day.meals || []), mealEntry] }
                      : day
                  )
                };
                itineraryUserModifiedRef.current = true;
                setHasUnsavedChanges(true);
                setItinerary(newItinerary);
                setShowAddMealModal(false);
                // Clear transit times for recalculation
                setTransitTimes(prev => {
                  const updated = { ...prev };
                  Object.keys(updated).forEach(key => {
                    if (key.startsWith(`day${addActivityDay}-`)) {
                      delete updated[key];
                      delete transitTimesRef.current[key];
                    }
                  });
                  return updated;
                });
                transitCalcGuardRef.current = null;
                showToast('Meal added!', 'success', 2000);
              }}>
                <div className="space-y-3">
                  <input name="restaurant" placeholder="Restaurant name" className="w-full px-3 py-2 border rounded-lg" required />
                  <input name="cuisine" placeholder="Cuisine (e.g., Italian, Japanese...)" className="w-full px-3 py-2 border rounded-lg" />
                  <div className="grid grid-cols-2 gap-3">
                    <select name="mealType" className="px-3 py-2 border rounded-lg">
                      <option value="breakfast">Breakfast</option>
                      <option value="lunch" selected>Lunch</option>
                      <option value="dinner">Dinner</option>
                      <option value="snack">Snack / Café</option>
                    </select>
                    <select name="time" className="px-3 py-2 border rounded-lg">
                      <option value="7:00 AM">7:00 AM</option>
                      <option value="8:00 AM">8:00 AM</option>
                      <option value="9:00 AM">9:00 AM</option>
                      <option value="10:00 AM">10:00 AM</option>
                      <option value="11:00 AM">11:00 AM</option>
                      <option value="12:00 PM">12:00 PM</option>
                      <option value="1:00 PM">1:00 PM</option>
                      <option value="2:00 PM">2:00 PM</option>
                      <option value="3:00 PM">3:00 PM</option>
                      <option value="5:00 PM">5:00 PM</option>
                      <option value="6:00 PM">6:00 PM</option>
                      <option value="7:00 PM">7:00 PM</option>
                      <option value="8:00 PM">8:00 PM</option>
                      <option value="9:00 PM">9:00 PM</option>
                    </select>
                  </div>
                  <textarea name="description" placeholder="Notes (optional)" rows="2" className="w-full px-3 py-2 border rounded-lg" />
                  <select name="priceTier" className="w-full px-3 py-2 border rounded-lg">
                    <option value="$">$ Budget</option>
                    <option value="$$">$$ Moderate</option>
                    <option value="$$$">$$$ Premium</option>
                    <option value="$$$$">$$$$ Fine Dining</option>
                  </select>
                </div>
                <div className="flex gap-3 mt-4">
                  <button type="button" onClick={() => setShowAddMealModal(false)} className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Cancel</button>
                  <button type="submit" className="flex-1 px-4 py-2 bg-[#313143] text-white rounded-lg hover:bg-[#3d3d52]">Add</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Floating Chat Button — removed, chat accessible via sidebar Quick Actions */}
        
        {/* Chat Panel */}
        {showItineraryChat && (
          <div className="bg-white shadow-2xl flex flex-col border-l border-gray-200" style={{position: 'fixed', right: '340px', top: '80px', height: 'calc(100vh - 100px)', width: '384px', zIndex: 50, borderRadius: '22px 0 0 22px'}}>
            {/* Header */}
            <div className="px-5 py-4" style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white text-[14px] font-extrabold flex items-center gap-2" style={FONTS.headingSemibold}><BrandChat size={18} color="#ffffff" /> Ask arrivo</h3>
                  <p className="text-white/70 text-[11px] font-semibold mt-0.5" style={FONTS.body}>
                    Viewing Day {currentDay + 1} of {itinerary?.days?.length || '?'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {itineraryChatMessages.length > 0 && (
                    <button 
                      onClick={async () => {
                        if (await showConfirmModal({ title: 'Clear Chat', message: 'Clear chat history?', confirmText: 'Clear', type: 'danger' })) {
                          setItineraryChatMessages([]);
                        }
                      }}
                      className="text-white/80 hover:text-white hover:bg-white/20 rounded-full p-1.5 transition-colors text-xs"
                      title="Clear chat"
                    >
                      🗑️
                    </button>
                  )}
                  <button 
                    onClick={() => setShowItineraryChat(false)} 
                    className="text-white hover:bg-white/20 rounded-full p-1 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
              {itineraryChatMessages.length === 0 && (
                <div className="text-center text-gray-500 mt-8">
                  <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm" style={FONTS.body}>I can modify your itinerary!</p>
                  <p className="text-xs mt-2" style={FONTS.body}>Try these commands:</p>
                  <div className="mt-3 space-y-2 text-left bg-white rounded-lg p-3 text-xs" style={FONTS.body}>
                    <div className="flex items-start gap-2">
                      <span className="text-brand-600">✓</span>
                      <span>"Remove an activity"</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-brand-600">✓</span>
                      <span>"Add a museum visit"</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-brand-600">✓</span>
                      <span>"Replace lunch with Italian food"</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-brand-600">✓</span>
                      <span>"Find vegetarian restaurants"</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">?</span>
                      <span>"What's the best time to visit?"</span>
                    </div>
                  </div>
                </div>
              )}

              {itineraryChatMessages.map((msg, idx) => (
                <div key={idx} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  {msg.content === 'loading' ? (
                    <div className="bg-white text-gray-800 shadow-sm px-4 py-3 rounded-2xl">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-2 h-2 bg-brand-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                        <span className="text-sm text-gray-500">Thinking...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className={`max-w-[90%] px-4 py-2 rounded-2xl ${
                        msg.role === 'user'
                          ? 'bg-brand-600 text-white'
                          : 'bg-white text-gray-800 shadow-sm'
                      }`}>
                        <div className="text-sm prose prose-sm max-w-none">{parseMarkdown(msg.content)}</div>
                      </div>
                      
                      {/* Regeneration buttons for major changes */}
                      {msg.majorChange && idx === itineraryChatMessages.length - 1 && (
                        <div className="mt-3 flex flex-col gap-2">
                          {/* Special handling for addDestinationType - show individual destination buttons */}
                          {msg.majorChange === 'addDestinationType' && msg.extractedDetails?.suggestions ? (
                            <>
                              <div className="flex flex-wrap gap-2">
                                {msg.extractedDetails.suggestions.map((destination, destIdx) => (
                                  <button
                                    key={destIdx}
                                    onClick={() => {
                                      // Add this specific destination
                                      const params = msg.regenerateParams || {};
                                      const newDestination = `${itinerary.destination} & ${destination}`;

                                      setSelectedDestination({
                                        ...selectedDestination,
                                        name: newDestination,
                                        multiCity: [itinerary.destination, destination]
                                      });

                                      if (params.tripLength) {
                                        setUserProfile(prev => ({
                                          ...prev,
                                          preferences: {
                                            ...prev.preferences,
                                            tripLength: params.tripLength
                                          }
                                        }));
                                      }

                                      generationInProgressRef.current = false;
                                      setIsProgressiveGenerating(false);
                                      setStage('personalization');
                                      setItinerary(null);
                                      setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null; // Clear cached transit times
                                      setItineraryChatMessages([]);
                                    }}
                                    className="px-4 py-2 bg-[#313143] text-white rounded-full hover:bg-[#3d3d52] hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 text-sm font-medium"
                                  >
                                    <MapPin className="w-4 h-4" />
                                    Add {destination}
                                  </button>
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <button
                                  onClick={() => {
                                    // Let user type their own destination
                                    setItineraryChatInput(`Add [destination name] for ${msg.extractedDetails?.additionalDays || 2} days`);
                                  }}
                                  className="px-4 py-2 bg-white border-2 border-teal-300 text-teal-700 rounded-full hover:border-teal-400 transition-all flex items-center gap-2 text-sm"
                                >
                                  <Edit2 className="w-4 h-4" />
                                  Somewhere else...
                                </button>
                                <button
                                  onClick={() => {
                                    setItineraryChatMessages(prev => [
                                      ...prev,
                                      {
                                        role: 'assistant',
                                        content: `No problem! I'll keep your current ${itinerary.days.length}-day ${itinerary.destination} itinerary. Is there anything else I can help you modify within this trip?`
                                      }
                                    ]);
                                  }}
                                  className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-full hover:border-gray-400 transition-all flex items-center gap-2 text-sm"
                                >
                                  Keep Current Itinerary
                                </button>
                              </div>
                            </>
                          ) : (
                            /* Standard buttons for other major changes */
                            <div className="flex flex-wrap gap-2">
                              <button
                                onClick={() => {
                                  // Save current preferences and trigger regeneration
                                  const params = msg.regenerateParams || {};

                                  if (msg.majorChange === 'changeDestination') {
                                    // Go back to destination selection - reset trip type to let user choose again
                                    setStage('chat');
                                    setItinerary(null);
                                    setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null; // Clear cached transit times
                                    setTripType(null); // Reset trip type selection
                                    setSelectedHotelProperty(null); // Reset selected hotel property
                                    setMessages([]);
                                  } else if (msg.majorChange === 'addCity' || msg.majorChange === 'addMultipleCities' || msg.majorChange === 'extendTrip' || msg.majorChange === 'extendAndAddCity') {
                                    // Update destination and trip length, go to personalization
                                    const newDestination = params.destination || (params.additionalCity
                                      ? `${itinerary.destination} & ${params.additionalCity}`
                                      : itinerary.destination);

                                    // If adding a city, update tripScope to multi
                                    if (msg.majorChange === 'addCity' || msg.majorChange === 'addMultipleCities' || msg.majorChange === 'extendAndAddCity') {
                                      setTripScope('multi');
                                    }

                                    setSelectedDestination({
                                      ...selectedDestination,
                                      name: newDestination,
                                      multiCity: params.additionalCities || (params.additionalCity ? [itinerary.destination, params.additionalCity] : null)
                                    });

                                    if (params.tripLength) {
                                      setUserProfile(prev => ({
                                        ...prev,
                                        preferences: {
                                          ...prev.preferences,
                                          tripLength: params.tripLength
                                        }
                                      }));
                                    }

                                    // Go to date selection to confirm/adjust
                                    setStage('personalization');
                                    setItinerary(null);
                                    setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null; // Clear cached transit times

                                  } else if (msg.majorChange === 'removeCity') {
                                    // Remove city from trip - update tripPlan and regenerate
                                    const stopIdToRemove = msg.extractedDetails?.stopId;
                                    const newDestination = params.destination;
                                    const newTripLength = params.tripLength;

                                    // Update tripPlan to remove the stop
                                    if (stopIdToRemove && tripPlan) {
                                      setTripPlan(prev => {
                                        if (!prev) return null;
                                        const updatedStops = prev.stops.filter(s => s.id !== stopIdToRemove);
                                        return {
                                          ...prev,
                                          stops: updatedStops,
                                          totalDays: updatedStops.reduce((sum, s) => sum + s.days, 0)
                                        };
                                      });
                                    }

                                    // Update selectedDestination
                                    setSelectedDestination({
                                      ...selectedDestination,
                                      name: newDestination,
                                      multiCity: newDestination.includes(' & ') ? newDestination.split(' & ') : null
                                    });

                                    // Update trip length in preferences
                                    if (newTripLength) {
                                      setUserProfile(prev => ({
                                        ...prev,
                                        preferences: {
                                          ...prev.preferences,
                                          tripLength: newTripLength
                                        }
                                      }));
                                    }

                                    setStage('personalization');
                                    setItinerary(null);
                                    setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null;

                                  } else if (msg.majorChange === 'reorderStops') {
                                    // Reorder stops - update tripPlan with new order and regenerate
                                    const reorderedNames = msg.extractedDetails?.newOrder || [];
                                    if (tripPlan && reorderedNames.length > 0) {
                                      setTripPlan(prev => {
                                        if (!prev) return null;
                                        // Build new stops array in the specified order
                                        const newStops = [];
                                        for (const name of reorderedNames) {
                                          const existingStop = prev.stops.find(s =>
                                            s.name.toLowerCase() === name.toLowerCase()
                                          );
                                          if (existingStop) {
                                            newStops.push(existingStop);
                                          }
                                        }
                                        // Add any stops not mentioned (shouldn't happen but safety)
                                        for (const stop of prev.stops) {
                                          if (!newStops.some(s => s.id === stop.id)) {
                                            newStops.push(stop);
                                          }
                                        }
                                        return {
                                          ...prev,
                                          stops: newStops.map((s, idx) => ({ ...s, order: idx + 1 }))
                                        };
                                      });
                                    }

                                    const reorderedDestination = reorderedNames.join(' & ');
                                    setSelectedDestination({
                                      ...selectedDestination,
                                      name: reorderedDestination,
                                      multiCity: reorderedNames
                                    });

                                    setStage('personalization');
                                    setItinerary(null);
                                    setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null;

                                  } else if (msg.majorChange === 'replaceCity') {
                                    // Replace city in trip - update tripPlan and regenerate
                                    const stopIdToReplace = msg.extractedDetails?.stopId;
                                    const newCityForReplace = msg.extractedDetails?.newCity;
                                    const newDestination = params.destination;

                                    // Update tripPlan to replace the stop
                                    if (stopIdToReplace && newCityForReplace && tripPlan) {
                                      setTripPlan(prev => {
                                        if (!prev) return null;
                                        const updatedStops = prev.stops.map(s =>
                                          s.id === stopIdToReplace ? { ...s, name: newCityForReplace } : s
                                        );
                                        return {
                                          ...prev,
                                          stops: updatedStops
                                        };
                                      });
                                    }

                                    // Update selectedDestination
                                    setSelectedDestination({
                                      ...selectedDestination,
                                      name: newDestination,
                                      multiCity: newDestination.includes(' & ') ? newDestination.split(' & ') : null
                                    });

                                    setStage('personalization');
                                    setItinerary(null);
                                    setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null;

                                  } else {
                                    // Start over - go to personalization with same destination
                                    setStage('personalization');
                                    setItinerary(null);
                                    setTransitTimes({}); transitTimesRef.current = {}; transitCalcGuardRef.current = null; // Clear cached transit times
                                  }

                                  // Clear chat
                                  setItineraryChatMessages([]);
                                }}
                                className="px-4 py-2 bg-[#313143] text-white rounded-full hover:bg-[#3d3d52] hover:shadow-lg hover:scale-105 transition-all flex items-center gap-2 text-sm font-medium"
                              >
                                <RefreshCw className="w-4 h-4" />
                                {msg.majorChange === 'changeDestination' ? 'Choose New Destination' :
                                 msg.majorChange === 'addCity' ? `Add ${msg.extractedDetails?.newCity} & Regenerate` :
                                 msg.majorChange === 'addMultipleCities' ? `Add ${msg.extractedDetails?.newCity} & Regenerate` :
                                 msg.majorChange === 'extendTrip' ? `Extend to ${msg.extractedDetails?.newDays || msg.regenerateParams?.tripLength || 'More'} Days` :
                                 msg.majorChange === 'extendAndAddCity' ? `${msg.extractedDetails?.newDays} Days + ${msg.extractedDetails?.newCity}` :
                                 msg.majorChange === 'reorderStops' ? `Reorder & Regenerate` :
                                 msg.majorChange === 'removeCity' ? `Remove ${msg.extractedDetails?.cityToRemove?.split(',')[0]} & Regenerate` :
                                 msg.majorChange === 'replaceCity' ? `Replace with ${msg.extractedDetails?.newCity} & Regenerate` :
                                 'Regenerate Itinerary'}
                              </button>
                              <button
                                onClick={() => {
                                  // Keep current itinerary, add a clarification message
                                  setItineraryChatMessages(prev => [
                                    ...prev,
                                    {
                                      role: 'assistant',
                                      content: `No problem! I'll keep your current ${itinerary.days.length}-day ${itinerary.destination} itinerary. Is there anything else I can help you modify within this trip?`
                                    }
                                  ]);
                                }}
                                className="px-4 py-2 bg-white border-2 border-gray-300 text-gray-700 rounded-full hover:border-gray-400 transition-all flex items-center gap-2 text-sm"
                              >
                                Keep Current Itinerary
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
              <div ref={itineraryChatEndRef} />

              {/* Pending Changes Comparison View — below messages for better UX */}
              {pendingChanges && pendingChanges.changes.length > 0 && (
                <div ref={pendingChangesRef} className="bg-white rounded-xl border border-brand-200 shadow-sm overflow-hidden mb-3">
                  <div className="bg-gradient-to-r from-brand-50 to-teal-50 px-4 py-3 border-b border-brand-100">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800 text-sm" style={FONTS.headingBold}>
                        {pendingChanges.changes.length} Suggested Change{pendingChanges.changes.length > 1 ? 's' : ''}
                      </h4>
                      <div className="flex gap-2">
                        {pendingChanges.changes.length > 1 && (
                          <button
                            onClick={() => applyPendingChanges(pendingChanges.changes.map((_, i) => i))}
                            className="px-3 py-1 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 transition-colors font-medium"
                          >
                            Accept All
                          </button>
                        )}
                        <button
                          onClick={() => { setPendingChanges(null); setPendingChangeStatuses({}); }}
                          className="px-3 py-1 bg-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-300 transition-colors font-medium"
                        >
                          Dismiss
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
                    {pendingChanges.changes.map((change, idx) => {
                      const status = pendingChangeStatuses[idx];
                      const isUpdate = change.type.startsWith('update_');
                      const isAdd = change.type.startsWith('add_');
                      const isRemove = change.type.startsWith('remove_');
                      const isActivity = change.type.includes('activity');
                      const isMeal = change.type.includes('meal');
                      const isHotel = change.type.includes('hotel');

                      const originalName = change.original?.activity || change.original?.restaurant || change.original?.name || change.target || '';
                      const newName = change.data?.activity || change.data?.restaurant || change.data?.name || '';
                      const originalAddr = change.original?.address || change.original?.location || '';
                      const newAddr = change.data?.address || change.data?.location || '';
                      const originalPrice = change.original?.priceTier || change.original?.priceRange || '';
                      const newPrice = change.data?.priceTier || change.data?.priceRange || '';
                      const originalCuisine = change.original?.cuisine || '';
                      const newCuisine = change.data?.cuisine || '';
                      const dayLabel = `Day ${(change.dayIndex ?? currentDay) + 1}`;

                      return (
                        <div
                          key={idx}
                          className={`px-4 py-3 transition-colors ${
                            status === 'accepted' ? 'bg-teal-50/50' :
                            status === 'rejected' ? 'bg-red-50/50 opacity-60' : 'bg-white'
                          }`}
                        >
                          {/* Change header */}
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-xs font-medium text-gray-500">{dayLabel}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              isAdd ? 'bg-green-100 text-green-700' :
                              isRemove ? 'bg-red-100 text-red-700' :
                              'bg-blue-100 text-blue-700'
                            }`}>
                              {isAdd ? 'Add' : isRemove ? 'Remove' : 'Update'} {isActivity ? 'Activity' : isMeal ? 'Meal' : 'Hotel'}
                            </span>
                            {change.reason && (
                              <span className="text-xs text-gray-400 italic truncate flex-1">{change.reason}</span>
                            )}
                          </div>

                          {/* Comparison cards */}
                          {isUpdate && change.original ? (
                            <div className="grid grid-cols-2 gap-2">
                              {/* Current */}
                              <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-200">
                                <div className="text-xs text-gray-400 mb-1 font-medium">Current</div>
                                <div className="text-sm font-medium text-gray-700 line-clamp-1">{originalName}</div>
                                {originalCuisine && <div className="text-xs text-gray-500">{originalCuisine}</div>}
                                {originalAddr && <div className="text-xs text-gray-400 mt-0.5 line-clamp-1">{originalAddr}</div>}
                                {originalPrice && <div className="text-xs text-gray-500 mt-0.5">{formatPriceTier(originalPrice)}</div>}
                              </div>
                              {/* Suggested */}
                              <div className="bg-brand-50 rounded-lg p-2.5 border border-brand-200">
                                <div className="text-xs text-brand-600 mb-1 font-medium">Suggested</div>
                                <div className="text-sm font-medium text-gray-800 line-clamp-1">{newName || originalName}</div>
                                {(newCuisine || originalCuisine) && <div className="text-xs text-gray-600">{newCuisine || originalCuisine}</div>}
                                {(newAddr || originalAddr) && <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{newAddr || originalAddr}</div>}
                                {(newPrice || originalPrice) && <div className="text-xs text-gray-600 mt-0.5">{formatPriceTier(newPrice || originalPrice)}</div>}
                              </div>
                            </div>
                          ) : isAdd ? (
                            <div className="bg-brand-50 rounded-lg p-2.5 border border-brand-200">
                              <div className="text-sm font-medium text-gray-800">{newName}</div>
                              {newCuisine && <div className="text-xs text-gray-600">{newCuisine}</div>}
                              {newAddr && <div className="text-xs text-gray-500 mt-0.5">{newAddr}</div>}
                              {change.data?.time && <div className="text-xs text-gray-500 mt-0.5">{change.data.time}</div>}
                            </div>
                          ) : isRemove ? (
                            <div className="bg-red-50 rounded-lg p-2.5 border border-red-200">
                              <div className="text-sm font-medium text-gray-700 line-through">{originalName}</div>
                              {originalAddr && <div className="text-xs text-gray-400 mt-0.5">{originalAddr}</div>}
                            </div>
                          ) : (
                            <div className="bg-brand-50 rounded-lg p-2.5 border border-brand-200">
                              <div className="text-sm font-medium text-gray-800">{newName || change.target}</div>
                            </div>
                          )}

                          {/* Action buttons */}
                          {!status && (
                            <div className="flex gap-2 mt-2">
                              <button
                                onClick={() => {
                                  setPendingChangeStatuses(prev => ({ ...prev, [idx]: 'accepted' }));
                                }}
                                className="flex-1 px-3 py-1.5 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 transition-colors font-medium"
                              >
                                Accept
                              </button>
                              <button
                                onClick={() => {
                                  setPendingChangeStatuses(prev => ({ ...prev, [idx]: 'rejected' }));
                                }}
                                className="flex-1 px-3 py-1.5 bg-gray-200 text-gray-600 text-xs rounded-lg hover:bg-gray-300 transition-colors font-medium"
                              >
                                Reject
                              </button>
                              <button
                                onClick={() => handleTellMeMore(idx)}
                                className="flex-1 px-3 py-1.5 bg-blue-50 text-blue-600 text-xs rounded-lg hover:bg-blue-100 transition-colors font-medium border border-blue-200"
                              >
                                Tell me more
                              </button>
                            </div>
                          )}
                          {status === 'accepted' && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-brand-600 font-medium">
                              <span>✓</span> Accepted
                              <button onClick={() => setPendingChangeStatuses(prev => { const u = {...prev}; delete u[idx]; return u; })} className="ml-auto text-gray-400 hover:text-gray-600 text-xs">Undo</button>
                            </div>
                          )}
                          {status === 'rejected' && (
                            <div className="flex items-center gap-2 mt-2 text-xs">
                              <span className="text-red-500 font-medium">✗ Rejected</span>
                              <button
                                onClick={() => handleTryAnother(idx)}
                                className="text-blue-600 hover:text-blue-700 underline"
                              >
                                Try another
                              </button>
                              <button onClick={() => setPendingChangeStatuses(prev => { const u = {...prev}; delete u[idx]; return u; })} className="ml-auto text-gray-400 hover:text-gray-600">Undo</button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {/* Apply reviewed changes button */}
                  {Object.keys(pendingChangeStatuses).length > 0 && Object.keys(pendingChangeStatuses).length === pendingChanges.changes.length && (
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                      <button
                        onClick={() => {
                          const accepted = Object.entries(pendingChangeStatuses)
                            .filter(([_, s]) => s === 'accepted')
                            .map(([i]) => parseInt(i));
                          applyPendingChanges(accepted);
                        }}
                        className="w-full px-4 py-2 bg-gradient-to-r from-brand-600 to-brand-500 text-white rounded-lg hover:from-brand-700 hover:to-brand-600 transition-colors font-medium text-sm"
                      >
                        Apply {Object.values(pendingChangeStatuses).filter(s => s === 'accepted').length} Accepted Change{Object.values(pendingChangeStatuses).filter(s => s === 'accepted').length !== 1 ? 's' : ''}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-4 border-t bg-white">
              {/* Day scope selector — Hope's feedback: "I'm asking chat to help find a
                  different restaurant I can get to by boat for the last day, but confusing
                  it's saying Day 4 updating (not 7 of 7)." Previously this was a static
                  badge tied to whichever day pill was last clicked, which left users stuck
                  if they wanted to chat about a different day. Now it's a dropdown so users
                  can retarget the chat without leaving the chat panel. */}
              {itinerary?.days && itinerary.days.length > 0 && itinerary.days[currentDay] && (
                <div className="mb-2 inline-flex items-center gap-1.5">
                  <select
                    // Clamp the displayed value to a valid day index. If the
                    // user deletes a day externally and currentDay points past
                    // the new end, the <select> would render an out-of-range
                    // value and React warns. Defensive clamp.
                    value={Math.min(currentDay, itinerary.days.length - 1)}
                    onChange={(e) => setCurrentDay(parseInt(e.target.value, 10))}
                    className="px-3 py-1 bg-teal-50 hover:bg-teal-100 rounded-full text-xs text-teal-700 font-medium border-0 focus:outline-none focus:ring-2 focus:ring-teal-400 cursor-pointer appearance-none pr-7"
                    style={{
                      backgroundImage: 'url("data:image/svg+xml;charset=US-ASCII,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2210%22 height=%2210%22 viewBox=%220 0 10 10%22%3E%3Cpath fill=%22%230f766e%22 d=%22M5 7L1 3h8z%22/%3E%3C/svg%3E")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 8px center',
                      backgroundSize: '10px',
                    }}
                    title="Change which day this chat applies to"
                  >
                    {itinerary.days.map((d, i) => (
                      <option key={i} value={i}>
                        Editing Day {i + 1} of {itinerary.days.length}{d.baseCity ? ` — ${d.baseCity}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Quick-action chips above the textarea. Each chip prefills
                  itineraryChatInput with a context-aware modification
                  query and focuses the textarea — user reviews the
                  prefilled text and hits Send to commit. Mirror of the
                  mobile chip row in the bottom bar (see ItineraryScreen.js
                  st.quickActionChipRow). */}
              {(() => {
                // Web tracks the chat's target day via the `currentDay`
                // state (set by the day-selector <select> a few lines
                // above). Mirror the mobile chip prefill behavior using
                // that index.
                const editingIdx = Math.min(currentDay || 0, (itinerary?.days?.length || 1) - 1);
                const editingDay = itinerary?.days?.[editingIdx];
                const dayCity = editingDay?.baseCity || editingDay?.city || 'this day';
                const nextActivity = (editingDay?.schedule || [])[0];
                const nextMeal = (editingDay?.meals || [])[0];
                const prefill = (text) => {
                  setItineraryChatInput(text);
                  // Focus the textarea so user can review/edit before sending.
                  setTimeout(() => {
                    const ta = document.querySelector('textarea[data-chat-input]');
                    if (ta) {
                      ta.focus();
                      ta.style.height = 'auto';
                      ta.style.height = Math.min(ta.scrollHeight, 120) + 'px';
                    }
                  }, 50);
                };
                return (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    <button
                      type="button"
                      onClick={() => prefill(
                        nextActivity
                          ? `Suggest a different ${nextActivity.activity || 'activity'} in ${dayCity}`
                          : 'Swap an activity on this day'
                      )}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-700 bg-white border border-gray-200 rounded-full hover:border-teal-400 hover:text-teal-700 transition-colors"
                    >
                      <RefreshCw className="w-3 h-3 text-teal-500" />
                      Quick swap
                    </button>
                    <button
                      type="button"
                      onClick={() => prefill(
                        nextMeal
                          ? `Recommend a better ${nextMeal.type || 'restaurant'} in ${dayCity}`
                          : `Suggest a great restaurant in ${dayCity}`
                      )}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-700 bg-white border border-gray-200 rounded-full hover:border-teal-400 hover:text-teal-700 transition-colors"
                    >
                      <UtensilsCrossed className="w-3 h-3 text-teal-500" />
                      Food
                    </button>
                    <button
                      type="button"
                      onClick={() => prefill(
                        `What can I do during free time on day ${editingIdx + 1} in ${dayCity}?`
                      )}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium text-gray-700 bg-white border border-gray-200 rounded-full hover:border-teal-400 hover:text-teal-700 transition-colors"
                    >
                      <Clock className="w-3 h-3 text-teal-500" />
                      Fill gap
                    </button>
                  </div>
                );
              })()}
              <div className="flex gap-2 items-end">
                <div className="relative flex-1">
                  <textarea
                    data-chat-input
                    value={itineraryChatInput}
                    onChange={(e) => {
                      // Hard cap at 500 chars matching mobile. Was unbounded
                      // — backends silently truncated longer messages.
                      const v = e.target.value.slice(0, 500);
                      setItineraryChatInput(v);
                      // Auto-resize: reset height then set to scrollHeight
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleItineraryChatSend();
                        // Reset height after send
                        e.target.style.height = 'auto';
                      }
                    }}
                    placeholder="Ask about your itinerary..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-2xl focus:outline-none focus:border-teal-500 resize-none overflow-hidden"
                    style={{ minHeight: '40px', maxHeight: '120px' }}
                    rows={1}
                    disabled={isModifyingItinerary}
                    maxLength={500}
                  />
                  {/* Char count appears at 80% (400/500) — heads-up before
                      truncation without cluttering the input the rest of
                      the time. Tabular-nums so digits don't shift width as
                      the count climbs. */}
                  {itineraryChatInput.length > 400 && (
                    <span
                      className="absolute right-3 -bottom-4 text-[10px] text-gray-400"
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {itineraryChatInput.length}/500
                    </span>
                  )}
                </div>
                <button
                  onClick={() => handleItineraryChatSend()}
                  disabled={!itineraryChatInput.trim() || isModifyingItinerary}
                  className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white hover:bg-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isModifyingItinerary ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* AI Disclaimer Footer */}
        <div className="py-6 px-4 mt-8" style={{ background: '#FAF8F5' }}>
          <p className="text-xs text-gray-400 text-center max-w-2xl mx-auto" style={FONTS.body}>
            arrivo is AI-powered. Always double-check details and verify the latest travel & safety guidelines before booking.
          </p>
        </div>

        </div>{/* close itin-main */}

        {/* Itinerary Right Sidebar */}
        <div className="itin-sidebar h-full z-30">
          {/* Sidebar Header */}
          <div className="px-5 py-4" style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)' }}>
            <div className="text-[14px] font-extrabold text-white">Day {safeCurrentDay + 1} Details</div>
            <div className="text-[11px] font-semibold text-white/70">
              {(() => {
                if (!userProfile.preferences.startDate) return '';
                const start = parseLocalDate(userProfile.preferences.startDate);
                if (!start) return '';
                const dayDate = new Date(start);
                dayDate.setDate(dayDate.getDate() + safeCurrentDay);
                return dayDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
              })()}
            </div>
          </div>
          <div className="px-4 py-3 space-y-3">

            {/* Curated Insider Tips — keyword-matched to activities, with category tags
                Task 13: place mentions in tips are tappable and open Place Detail. */}
            {(currentDayData?.insiderTips?.length > 0) && (() => {
              const tipPlaces = collectDayPlaces(currentDayData);
              const destCountry = itinerary?.destination?.split(',').slice(-1)[0]?.trim();
              const renderTipWithLinks = (tipText) => {
                const segs = linkTipPlaces(tipText, tipPlaces);
                return segs.map((seg, k) => {
                  if (seg.type === 'text') return <React.Fragment key={k}>{seg.text}</React.Fragment>;
                  const p = seg.place;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPlaceDetail({
                          placeName: p.name,
                          city: currentDayData?.baseCity || itinerary?.destination?.split(',')[0]?.trim(),
                          country: destCountry,
                          placeCategory: p.category,
                          placeData: p.placeData,
                          item: p.item,
                        });
                      }}
                      className="inline underline decoration-dotted underline-offset-2 text-teal-600 hover:text-teal-700 font-semibold cursor-pointer"
                    >
                      {seg.text}
                    </button>
                  );
                });
              };
              return (
                <div className="rounded-2xl p-4 border border-amber-100" style={{ background: 'linear-gradient(135deg, rgba(245,158,11,0.04), rgba(251,191,36,0.02))' }}>
                  <div className="text-[10px] font-extrabold uppercase tracking-wider text-amber-600 mb-3 flex items-center gap-1.5">
                    <Lightbulb className="w-3 h-3" />
                    Insider tips {currentDayData?.baseCity ? <>&nbsp;·&nbsp;{currentDayData.baseCity}</> : ''}
                  </div>
                  {currentDayData.insiderTips.map((tipObj, i) => {
                    const categoryColors = {
                      Taste: 'bg-orange-50 text-orange-600 border-orange-200',
                      Sip: 'bg-amber-50 text-amber-600 border-amber-200',
                      Explore: 'bg-teal-50 text-teal-600 border-teal-200',
                      Culture: 'bg-purple-50 text-purple-600 border-purple-200',
                    };
                    const tagClass = categoryColors[tipObj.categoryLabel] || 'bg-gray-50 text-gray-500 border-gray-200';
                    const tipText = typeof tipObj === 'string' ? tipObj : tipObj.tip;
                    const label = typeof tipObj === 'string' ? null : tipObj.categoryLabel;
                    return (
                      <div key={i} className="flex gap-2 items-start mb-2.5 last:mb-0">
                        <div className="flex-shrink-0 mt-0.5">
                          {label ? (
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold border ${tagClass}`}>
                              {label}
                            </span>
                          ) : (
                            <Lightbulb className="w-3.5 h-3.5 text-amber-500" />
                          )}
                        </div>
                        <div className="text-[12px] text-[#313143] leading-snug">
                          {renderTipWithLinks(tipText || '')}
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Day Tips — general tips from GLM. Task 13: place mentions also link to Place Detail. */}
            {(currentDayData?.dayTips?.length > 0) && (() => {
              const tipPlaces = collectDayPlaces(currentDayData);
              const destCountry = itinerary?.destination?.split(',').slice(-1)[0]?.trim();
              const renderTipWithLinks = (tipText) => {
                const segs = linkTipPlaces(tipText, tipPlaces);
                return segs.map((seg, k) => {
                  if (seg.type === 'text') return <React.Fragment key={k}>{seg.text}</React.Fragment>;
                  const p = seg.place;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPlaceDetail({
                          placeName: p.name,
                          city: currentDayData?.baseCity || itinerary?.destination?.split(',')[0]?.trim(),
                          country: destCountry,
                          placeCategory: p.category,
                          placeData: p.placeData,
                          item: p.item,
                        });
                      }}
                      className="inline underline decoration-dotted underline-offset-2 text-teal-600 hover:text-teal-700 font-semibold cursor-pointer"
                    >
                      {seg.text}
                    </button>
                  );
                });
              };
              return (
                <div className="rounded-2xl p-4 border border-teal-100" style={{ background: 'linear-gradient(135deg, rgba(13,148,136,0.04), rgba(45,212,191,0.02))' }}>
                  <div className="text-[10px] font-extrabold uppercase tracking-wider text-teal-600 mb-3">
                    Tips {currentDayData?.baseCity ? <>&nbsp;·&nbsp;{currentDayData.baseCity}</> : ''}
                  </div>
                  {currentDayData.dayTips.map((tip, i) => (
                    <div key={i} className="flex gap-2 items-start mb-2.5 last:mb-0">
                      <div className="flex-shrink-0 mt-0.5">
                        {i === 0 ? <Lightbulb className="w-3.5 h-3.5 text-teal-600" /> : i === 1 ? <Compass className="w-3.5 h-3.5 text-teal-600" /> : <Crosshair className="w-3.5 h-3.5 text-teal-600" />}
                      </div>
                      <div className="text-[12px] text-[#313143] leading-snug">
                        {renderTipWithLinks(tip || '')}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Weather Widget */}
            <div
              className="rounded-2xl p-4 border cursor-pointer hover:border-teal-200 transition-colors"
              style={{ background: '#F5F5F0', borderColor: 'rgba(0,0,0,0.06)' }}
              onClick={() => {
                // Clear weather for this day and trigger a re-fetch
                if (!currentDayData?.weather?.high) {
                  setItinerary(prev => {
                    if (!prev?.days?.[currentDay]) return prev;
                    const newDays = [...prev.days];
                    newDays[currentDay] = { ...newDays[currentDay], weather: undefined };
                    return { ...prev, days: newDays };
                  });
                  setWeatherFetchTrigger(t => t + 1);
                }
              }}
            >
              <div className="text-[10px] font-extrabold uppercase tracking-wider mb-2" style={{ color: '#0e7490' }}>
                {currentDayData?.city || currentDayData?.baseCity || selectedDestination?.name || ''} weather
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(167,216,219,0.15)' }}>
                  <span className="text-xl">{currentDayData?.weather?.icon || '🌤'}</span>
                </div>
                <div>
                  <div className="text-xl font-extrabold text-[#313143]">
                    {(() => {
                      if (!currentDayData?.weather?.high) return '—';
                      const useCel = (() => { try { return localStorage.getItem('weather-unit') === 'C'; } catch { return false; } })();
                      const conv = (c) => useCel ? c : Math.round(c * 9 / 5 + 32);
                      return `${conv(currentDayData.weather.high)}\u00B0`;
                    })()}
                    <span className="text-sm font-semibold text-gray-400">{(() => {
                      if (!currentDayData?.weather?.low) return '';
                      const useCel = (() => { try { return localStorage.getItem('weather-unit') === 'C'; } catch { return false; } })();
                      const conv = (c) => useCel ? c : Math.round(c * 9 / 5 + 32);
                      return ` / ${conv(currentDayData.weather.low)}\u00B0`;
                    })()}</span>
                  </div>
                  <div className="text-[11px] text-gray-500">
                    {currentDayData?.weather?.description || currentDayData?.weather?.condition || (currentDayData?.weather?.high ? '' : currentDayData?.weather?._failed ? 'Tap to retry' : 'Loading weather...')}
                  </div>
                </div>
              </div>
            </div>

            {/* Budget Tracker — super-admin only */}
            {tier === 'super-admin' && (
            <div className="bg-white border border-gray-200 rounded-2xl p-4">
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-gray-400 mb-1">Day {safeCurrentDay + 1} Budget</div>
              {(() => {
                // Helper: parse cost from numeric or "$$$" text (matches BudgetTracker logic)
                const parseSidebarCost = (val) => {
                  if (!val && val !== 0) return 0;
                  if (typeof val === 'number') return val;
                  const str = String(val).trim();
                  // Support both $ and € currency symbols (LLM sometimes outputs euro signs)
                  if (/^[\$€]{4}$/.test(str)) return 80;
                  if (/^[\$€]{3}$/.test(str)) return 50;
                  if (/^[\$€]{2}$/.test(str)) return 25;
                  if (/^[\$€]{1}$/.test(str)) return 12;
                  const match = str.match(/[\d,.]+/);
                  if (match) {
                    const num = parseFloat(match[0].replace(/,/g, ''));
                    if (!isNaN(num)) return num;
                  }
                  return 0;
                };
                const getItemCost = (item, ...fields) => {
                  if (item?._userCost != null) return parseSidebarCost(item._userCost);
                  for (const f of fields) { if (item?.[f] != null) return parseSidebarCost(item[f]); }
                  return 0;
                };
                // Helper: estimate cost for a single day from its items
                const estimateDayCost = (d) => {
                  const dm = (d.meals || []).reduce((s, m) => s + getItemCost(m, 'cost', 'estimatedCost', 'price', 'priceRange', 'priceTier'), 0);
                  const da = (d.schedule || []).reduce((s, a) => s + getItemCost(a, 'cost', 'estimatedCost', 'price', 'priceRange', 'priceTier'), 0);
                  const dh = d.hotel ? getItemCost(d.hotel, 'pricePerNight', 'estimatedPrice', 'price') : 0;
                  return dm + da + dh;
                };
                // Prefer backend-computed _dailyEstimatedCost (already in local currency, varies per day).
                // Falls back to manual estimation from item `cost` fields.
                // NOTE: _dailyEstimatedCost of 0 means backend didn't compute it — fall through to manual.
                const hasBackendCost = typeof currentDayData?._dailyEstimatedCost === 'number' && currentDayData._dailyEstimatedCost > 0;
                const dayCurrency = currentDayData?._dailyEstimatedCostCurrency
                  || getCurrencyForCity(currentDayData?.baseCity || currentDayData?.city || '')
                  || getDestinationCurrency();
                const daySym = currencySymbols[dayCurrency] || '$';
                const dayTotal = hasBackendCost
                  ? currentDayData._dailyEstimatedCost
                  : estimateDayCost(currentDayData || {});
                // Trip total: convert each day's cost to USD first for cross-currency aggregation,
                // then display the USD total. Without this, IDR+JPY+KES sums are meaningless.
                const tripTotalUSD = (itinerary?.days || []).reduce((sum, d) => {
                  const dCurr = d._dailyEstimatedCostCurrency
                    || getCurrencyForCity(d.baseCity || d.city || '')
                    || getDestinationCurrency();
                  const rawCost = (typeof d._dailyEstimatedCost === 'number' && d._dailyEstimatedCost > 0)
                    ? d._dailyEstimatedCost
                    : estimateDayCost(d);
                  return sum + _approximateToUSD(rawCost, dCurr);
                }, 0);
                const dayTotalUSD = _approximateToUSD(dayTotal, dayCurrency);
                const pct = tripTotalUSD > 0 ? Math.min((dayTotalUSD / tripTotalUSD) * 100 * (itinerary?.days?.length || 1), 100) : 30;
                return (
                  <>
                    <div className="flex justify-between items-baseline">
                      <div className="text-lg font-extrabold text-[#313143]">{daySym}{Math.round(dayTotal).toLocaleString()}</div>
                      <div className="text-[11px] text-gray-500">Trip: ${Math.round(tripTotalUSD).toLocaleString()}</div>
                    </div>
                    <div className="h-1.5 rounded-full bg-gray-100 mt-2 mb-1">
                      <div className="h-full rounded-full bg-teal-600 transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 font-semibold">
                      <span>Day total ({dayCurrency})</span>
                      <span>{itinerary?.days?.length || 0} days</span>
                    </div>
                  </>
                );
              })()}
            </div>
            )}

            {/* Community Tips — hidden until real data is available */}

            {/* Quick Actions */}
            <div className="bg-teal-50 border border-teal-100 rounded-2xl p-4">
              <div className="text-[10px] font-extrabold uppercase tracking-wider text-teal-600 mb-2">Quick actions</div>
              {/* Add Activity & Add Meal — direct modal buttons */}
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => {
                    setAddActivityDay(currentDay);
                    setAddActivityInsertIndex(null);
                    setShowAddActivityModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-bold text-teal-600 bg-white border border-teal-200 rounded-xl hover:bg-teal-100 hover:border-teal-300 transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Activity
                </button>
                <button
                  onClick={() => {
                    setAddActivityDay(currentDay);
                    setShowAddMealModal(true);
                  }}
                  className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] font-bold text-[#CD7A4D] bg-white border border-orange-200 rounded-xl hover:bg-orange-50 hover:border-orange-300 transition-all"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Meal
                </button>
              </div>
              {/* Add Day button */}
              <button
                onClick={() => setShowAddDayModal(true)}
                className="w-full flex items-center justify-center gap-1.5 px-3 py-2 mb-2 text-[12px] font-bold text-purple-600 bg-white border border-purple-200 rounded-xl hover:bg-purple-50 hover:border-purple-300 transition-all"
              >
                <CalendarDays className="w-3.5 h-3.5" />
                Add Day
              </button>
              {/* Chat-based quick actions */}
              {['Regenerate this day', 'Change restaurant', 'Chat with arrivo'].map((action, i) => (
                <button
                  key={i}
                  onClick={() => {
                    if (action === 'Chat with arrivo') {
                      setShowItineraryChat(true);
                    } else {
                      setShowItineraryChat(true);
                      setItineraryChatInput(action === 'Regenerate this day' ? `Regenerate Day ${safeCurrentDay + 1}` : `Suggest a different restaurant for Day ${safeCurrentDay + 1}`);
                    }
                  }}
                  className="w-full text-left px-3 py-2 rounded-xl text-[12px] font-bold bg-white border border-gray-200 text-gray-700 hover:border-teal-300 hover:text-teal-700 transition-all mb-1 last:mb-0"
                >
                  {action}
                </button>
              ))}
            </div>

            {/* Share to Community */}
            {features.COMMUNITY_SHARING && (
            <button
              onClick={() => {
                if (!currentItineraryId) {
                  showToast('Save your itinerary first to share it', 'info');
                  return;
                }
                setExportType('share');
                setShowExportModal(true);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-full text-[11px] font-bold transition-all"
              style={{ color: '#CD7A4D', background: 'rgba(205,122,77,0.08)', border: '1px solid rgba(205,122,77,0.15)' }}
            >
              <Share2 className="w-3.5 h-3.5" />
              Share this itinerary with the community
            </button>
            )}
          </div>
        </div>
        </div>{/* close itin-shell */}
      </div>
    );
  };

  const renderPersonalization = () => {
    if (!selectedDestination) return null;
    
    return (
      <div className="h-screen flex flex-col overflow-hidden" style={{ background: '#F5F5F0' }}>
        {/* Subtle topo background */}
        <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 900' preserveAspectRatio='none'%3E%3Cpath d='M0 120 Q300 60 600 140 T1200 100' fill='none' stroke='%230d9488' stroke-width='1.2'/%3E%3Cpath d='M0 300 Q400 250 700 320 T1200 280' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 520 Q300 480 600 540 T1200 510' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 750 Q250 720 550 770 T1200 740' fill='none' stroke='%23313143' stroke-width='1'/%3E%3C/svg%3E")`, backgroundSize: 'cover', opacity: 0.035 }} />
        {/* Header */}
        <div className="bg-white border-b sticky top-0 z-50" style={{ borderColor: 'rgba(31,29,59,0.08)', boxShadow: '0 2px 12px rgba(28,41,53,0.04)' }}>
          <div className="max-w-[1100px] mx-auto px-6 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              {/* Paper-plane icon + inline wordmark + tagline (see comment at first occurrence). */}
              <img src="/arrivo-icon.png" alt="Arrivo" className="h-10 w-auto object-contain" />
              <div className="flex flex-col">
                <h1 className="text-[22px] font-extrabold leading-none" style={{ fontFamily: "'Gulfs Display', 'Batangas', sans-serif", color: '#313143', letterSpacing: '0.05em' }}>
                  arrivo
                </h1>
                <span className="text-[10px] font-medium mt-0.5" style={{ fontFamily: "'Batangas', sans-serif", color: '#097066', letterSpacing: '-0.01em' }}>
                  plan less. explore more.
                </span>
              </div>
            </div>
            <button
              onClick={() => {
                setStage('chat');
                setSelectedDestination(null);
                setDestinationAirports([]);
                setUserProfile(prev => ({
                  ...prev,
                  preferences: {
                    ...prev.preferences,
                    arrivalAirport: '',
                    departureAirport: '',
                    letAIChooseAirports: false
                  }
                }));
              }}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold transition-all hover:bg-gray-50"
              style={{ border: '1px solid rgba(31,29,59,0.08)', color: '#64748b' }}
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Back to Chat
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto relative z-[1]">
          <div className="max-w-[1100px] mx-auto px-6 py-8">
            {/* 2-Column Layout — stacks on narrow screens */}
            <div className="personalization-grid grid gap-6" style={{ gridTemplateColumns: '1fr 320px' }}>
              {/* Left Column */}
              <div className="flex flex-col gap-5">
                {/* Hero Card — Dark charcoal with topo overlay */}
                <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #313143, #434360)', borderRadius: 22, padding: '28px 32px', color: 'white' }}>
                  <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.12, backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 600 200'%3E%3Cpath d='M0 50 Q150 20 300 60 T600 40' fill='none' stroke='%2314b8a6' stroke-width='1.2'/%3E%3Cpath d='M0 100 Q200 70 400 110 T600 90' fill='none' stroke='%2314b8a6' stroke-width='0.8'/%3E%3Cpath d='M0 150 Q150 130 350 160 T600 140' fill='none' stroke='%2314b8a6' stroke-width='1'/%3E%3C/svg%3E")`, backgroundSize: 'cover' }} />
                  <div className="relative z-[1]">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wide mb-3" style={{ background: 'rgba(45,212,191,0.15)', color: '#2dd4bf', letterSpacing: '0.06em' }}>
                      {tripType === 'hotel' ? 'Your next getaway' : 'Your next adventure'}
                    </div>
                    {selectedDestination.isMultiStop && selectedDestination.stopNames ? (
                      <>
                        <div className="text-[28px] font-extrabold mb-1" style={{ fontFamily: 'Georgia, serif', letterSpacing: '-0.04em' }}>
                          {selectedDestination.stopNames.length}-Stop {tripType === 'hotel' ? 'Journey' : 'Adventure'}
                        </div>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {(selectedDestination.displayNames || selectedDestination.stopNames).map((stop, idx) => (
                            <span key={idx} className="px-2.5 py-1 rounded-full text-[11px] font-bold" style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)' }}>
                              {idx + 1}. {stop}
                            </span>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-[32px] font-extrabold mb-1" style={{ fontFamily: 'Georgia, serif', letterSpacing: '-0.04em' }}>
                        {selectedDestination.name}
                      </div>
                    )}
                    <div className="text-sm mt-1" style={{ opacity: 0.7 }}>
                      {selectedDestination.isMultiStop
                        ? (tripType === 'hotel' ? `Multi-property experience across ${tripBuilderStops.length} hotels` : `${tripBuilderStops.length}-stop adventure across ${tripBuilderStops.map(s => s.name.split(',')[0]).join(', ')}`)
                        : (selectedDestination.description || (tripType === 'hotel' ? `Your hotel getaway in ${selectedDestination.name}` : `Your trip to ${selectedDestination.name}`))}
                    </div>
                  </div>
                </div>

                {/* DATES — Section Card */}
                <div style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, padding: '24px 28px', boxShadow: '0 4px 16px rgba(28,41,53,0.05)' }}>
                  <div className="text-[10px] font-extrabold uppercase tracking-wide mb-1.5" style={{ color: '#94a3b8', letterSpacing: '0.06em' }}>Required</div>
                  <div className="text-lg font-extrabold mb-1" style={{ color: '#1e293b', letterSpacing: '-0.02em' }}>When are you going?</div>
                  <p className="text-[12px] mb-4" style={{ color: '#94a3b8' }}>Set specific dates, or choose a duration + travel month</p>

              {/* Trip Dates & Duration - MANDATORY */}
              <div className="mb-4">
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Arrival Date</label>
                    <input
                      type="date"
                      value={userProfile.preferences.startDate || ''}
                      min={new Date().toISOString().split('T')[0]} // Prevent past dates
                      onChange={(e) => {
                        const newStartDate = e.target.value;
                        let endDate = userProfile.preferences.endDate;
                        let duration = userProfile.preferences.duration;

                        // Validate start date only (not against end date — we'll auto-adjust end date if needed)
                        const startOnlyValidation = validateTripDates(newStartDate, null, null);
                        if (startOnlyValidation.errors.length > 0) {
                          showAlertModal('Invalid Date', startOnlyValidation.errors.join('\n'));
                          return;
                        }

                        // Auto-adjust end date if start date moves past it (instead of blocking)
                        if (endDate && newStartDate > endDate) {
                          const start = parseLocalDate(newStartDate);
                          const end = new Date(start);
                          end.setDate(end.getDate() + (duration || totalItineraryDays || 7) - 1);
                          endDate = end.toISOString().split('T')[0];
                          debugLog(`📅 Auto-adjusted end date to ${endDate} (start moved past previous end)`);
                        }

                        if (newStartDate && endDate) {
                          const diffDays = calculateDaysBetween(newStartDate, endDate);
                          if (diffDays && diffDays > 0) duration = diffDays;
                        }

                        // Auto-calculate endDate from startDate + duration if no end date
                        if (newStartDate && duration && !endDate) {
                          const start = parseLocalDate(newStartDate);
                          const end = new Date(start);
                          end.setDate(end.getDate() + duration - 1);
                          endDate = end.toISOString().split('T')[0];
                        }

                        setUserProfile(prev => ({
                          ...prev,
                          preferences: { ...prev.preferences, startDate: newStartDate, endDate, duration, travelMonths: [], travelPeriod: '' }
                        }));
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-500 mb-1">Departure Date</label>
                    <input
                      type="date"
                      value={userProfile.preferences.endDate || ''}
                      min={userProfile.preferences.startDate || new Date().toISOString().split('T')[0]}
                      onChange={(e) => {
                        const newEndDate = e.target.value;
                        const startDate = userProfile.preferences.startDate;
                        let duration = userProfile.preferences.duration;

                        // Validate end date against start date
                        if (startDate && newEndDate) {
                          const dateValidation = validateTripDates(startDate, newEndDate, duration);
                          if (dateValidation.errors.length > 0) {
                            showAlertModal('Invalid Date', dateValidation.errors.join('\n'));
                            return;
                          }
                          if (dateValidation.warnings.length > 0) {
                            debugWarn('Date warnings:', dateValidation.warnings);
                          }

                          const diffDays = calculateDaysBetween(startDate, newEndDate);
                          if (diffDays && diffDays > 0) {
                            duration = diffDays;
                            debugLog(`📅 Calculated trip duration: ${duration} days`);
                          }
                        }

                        setUserProfile(prev => ({
                          ...prev,
                          preferences: { ...prev.preferences, endDate: newEndDate, duration, travelMonths: [], travelPeriod: '' }
                        }));
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                    />
                  </div>
                </div>

                {/* Show calculated or manual duration */}
                <div className="flex flex-wrap items-center gap-4 mb-4">
                  {userProfile.preferences.startDate && userProfile.preferences.endDate && userProfile.preferences.endDate < userProfile.preferences.startDate ? (
                    <div>
                      <p className="font-medium text-red-600">
                        ❌ End date must be after start date
                      </p>
                    </div>
                  ) : userProfile.preferences.startDate && userProfile.preferences.endDate && userProfile.preferences.duration ? (
                    <div>
                      <p className={`font-medium ${userProfile.preferences.duration > 14 ? 'text-amber-600' : 'text-teal-600'}`}>
                        {userProfile.preferences.duration > 14 ? '⚠️' : '✅'} {userProfile.preferences.duration} day trip
                      </p>
                      {userProfile.preferences.duration > 14 && (
                        <p className="text-xs text-amber-600 mt-1">
                          Max 14 days supported. Please shorten your trip dates.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">Trip duration:</span>
                      <input
                        type="number"
                        min="1"
                        max="14"
                        value={userProfile.preferences.duration || ''}
                        onChange={(e) => {
                          const value = parseInt(e.target.value) || null;
                          // Cap at 14 days
                          const cappedValue = value ? Math.min(value, 14) : null;
                          setUserProfile(prev => {
                            const startDate = prev.preferences.startDate;
                            let endDate = prev.preferences.endDate;
                            // Auto-calculate endDate from startDate + duration
                            if (startDate && cappedValue) {
                              const start = parseLocalDate(startDate);
                              const end = new Date(start);
                              end.setDate(end.getDate() + cappedValue - 1);
                              endDate = end.toISOString().split('T')[0];
                            }
                            return {
                              ...prev,
                              preferences: { ...prev.preferences, duration: cappedValue, ...(endDate ? { endDate } : {}) }
                            };
                          });
                        }}
                        placeholder="# days"
                        className="w-20 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                      />
                      <span className="text-sm text-gray-600">days (max 14)</span>
                    </div>
                  )}
                </div>
                
                {/* Time Period - year selector + clickable months */}
                  <div>
                    <span className="text-sm text-gray-600 block mb-2">
                      Travel month{!userProfile.preferences.startDate ? ' (required)' : ' (optional — dates set)'}:
                    </span>
                    {/* Year selector pills */}
                    <div className="flex gap-2 mb-3">
                      {[new Date().getFullYear(), new Date().getFullYear() + 1].map(year => {
                        const selectedYear = userProfile.preferences.travelYear || new Date().getFullYear();
                        return (
                          <button
                            key={year}
                            onClick={() => {
                              setUserProfile(prev => ({
                                ...prev,
                                preferences: { ...prev.preferences, travelYear: year, travelMonths: [], travelPeriod: '' },
                              }));
                            }}
                            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${
                              selectedYear === year
                                ? 'bg-teal-600 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                            }`}
                          >
                            {year}
                          </button>
                        );
                      })}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((month, index) => {
                        const fullMonth = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'][index];
                        const selectedMonths = userProfile.preferences.travelMonths || [];
                        const selectedYear = userProfile.preferences.travelYear || new Date().getFullYear();
                        const now = new Date();
                        const isPast = selectedYear === now.getFullYear() && index < now.getMonth();
                        const isSelected = selectedMonths.includes(fullMonth);

                        return (
                          <button
                            key={month}
                            disabled={isPast}
                            onClick={() => {
                              if (isPast) return;
                              setUserProfile(prev => {
                                const currentMonths = prev.preferences.travelMonths || [];
                                const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
                                let newMonths;
                                if (isSelected) {
                                  // Deselecting — just remove it
                                  newMonths = currentMonths.filter(m => m !== fullMonth);
                                } else {
                                  // Selecting — enforce max 2 consecutive months
                                  const clickedIdx = monthOrder.indexOf(fullMonth);
                                  if (currentMonths.length === 0) {
                                    // First selection
                                    newMonths = [fullMonth];
                                  } else if (currentMonths.length === 1) {
                                    // Second selection — must be adjacent to the first
                                    const existingIdx = monthOrder.indexOf(currentMonths[0]);
                                    if (Math.abs(clickedIdx - existingIdx) === 1) {
                                      newMonths = [...currentMonths, fullMonth];
                                    } else {
                                      // Not adjacent — replace with just this month
                                      newMonths = [fullMonth];
                                    }
                                  } else {
                                    // Already have 2 — replace with just this month
                                    newMonths = [fullMonth];
                                  }
                                }
                                newMonths.sort((a, b) => monthOrder.indexOf(a) - monthOrder.indexOf(b));

                                // Set travelPeriod for backward compatibility
                                const travelPeriod = newMonths.length > 0
                                  ? newMonths.length === 1
                                    ? newMonths[0]
                                    : `${newMonths[0]}-${newMonths[newMonths.length - 1]}`
                                  : '';

                                return {
                                  ...prev,
                                  preferences: {
                                    ...prev.preferences,
                                    travelMonths: newMonths,
                                    travelPeriod: travelPeriod,
                                    // Clear specific dates when months are selected (mutually exclusive)
                                    ...(newMonths.length > 0 ? { startDate: '', endDate: '' } : {})
                                  }
                                };
                              });
                            }}
                            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                              isPast
                                ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                : isSelected
                                  ? 'bg-[#a7d8db] text-[#313143] shadow-md'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {month}
                          </button>
                        );
                      })}
                    </div>
                    {(userProfile.preferences.travelMonths?.length > 0) && (
                      <p className="text-xs text-teal-600 mt-2">
                        ✓ Traveling in: {userProfile.preferences.travelMonths.join(', ')}{userProfile.preferences.travelYear && userProfile.preferences.travelYear !== new Date().getFullYear() ? ` ${userProfile.preferences.travelYear}` : ''}
                      </p>
                    )}
                  </div>
              </div>

                </div>

                {/* GETTING AROUND — Section Card */}
                {tripBuilderStops.length > 0 && (
                <div style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, padding: '24px 28px', boxShadow: '0 4px 16px rgba(28,41,53,0.05)' }}>
                  <div className="text-lg font-extrabold mb-1" style={{ color: '#1e293b', letterSpacing: '-0.02em' }}>Getting Around</div>
                  <p className="text-[13px] mb-4" style={{ color: '#64748b' }}>How you'll travel between each stop</p>
                {/* Empty-state nudge — Hope's feedback: "did I miss somewhere I can
                    choose how I like to travel between destinations?" When there's
                    only one stop, the card was rendering an empty body. Now we
                    show an actionable hint so users know where the picker lives
                    once they add a second destination. */}
                {legsList.length === 0 && (
                  <div className="rounded-xl border border-dashed border-gray-200 px-4 py-3 text-[13px] text-gray-500 flex items-center gap-2.5" style={FONTS.body}>
                    <Plane className="w-4 h-4 text-teal-400" />
                    <span>Add another destination above to pick a mode (fly, train, ferry, drive) for each leg.</span>
                  </div>
                )}
                <div className="space-y-3">
                  {legsList.map(leg => {
                    const legData = legTransports[leg.key] || {};
                    const isLoading = legData.isLoading;
                    const mode = legData.mode || 'fly';
                    const duration = legData.duration;
                    const driveAvailable = legData.driveAvailable !== false;
                    const flightSearch = legFlightSearch[leg.key];

                    // Determine which mode buttons to show
                    const modeOptions = [];
                    // Check if either city is an island — no car/train between islands
                    // Check BOTH from/to (resolvedLocation) AND fromLabel/toLabel (display name)
                    // because resolvedLocation might be e.g. "Thira, Greece" instead of "Santorini"
                    const fromIsIslandLeg = isIslandCity(leg.from) || isIslandCity(leg.fromLabel);
                    const toIsIslandLeg = isIslandCity(leg.to) || isIslandCity(leg.toLabel);
                    const involvesIslandLeg = fromIsIslandLeg || toIsIslandLeg;
                    // Cross-continent check FIRST — needed to override drive recommendations
                    const fromCountryDrive = getCityCountrySync(leg.from) || getCityCountrySync(leg.fromLabel);
                    const toCountryDrive = getCityCountrySync(leg.to) || getCityCountrySync(leg.toLabel);
                    const fromContinentDrive = getContinentForCountry(fromCountryDrive) || getContinentHintFromText(leg.from) || getContinentHintFromText(leg.fromLabel);
                    const toContinentDrive = getContinentForCountry(toCountryDrive) || getContinentHintFromText(leg.to) || getContinentHintFromText(leg.toLabel);
                    const isCrossContinent = fromContinentDrive && toContinentDrive && fromContinentDrive !== toContinentDrive;
                    // Auto option — override drive for cross-continent routes
                    //
                    // BUG FIX (Mexico City → Oaxaca, May 2026): the previous fallback
                    //   `legData.recommendedMode || legData.mode || 'fly'`
                    // would fall through to legData.mode when recommendedMode wasn't
                    // set. That's wrong: legData.mode reflects the user's CURRENT
                    // selection (after they may have clicked Drive then Auto), not
                    // the system's RECOMMENDATION. The result was the auto label
                    // saying "Recommended: Drive (1h 15m)" when the cached recommendation
                    // was actually flight at 1h 15m — the system was just echoing
                    // the user's last-stuck mode and pretending it was a recommendation.
                    //
                    // Fix: only consider recommendedMode for the auto label. If we
                    // don't have a recommendation yet (loading / fetch failed),
                    // default to 'fly' rather than echoing legData.mode.
                    const rawRecommendedPers = legData.recommendedMode || 'fly';
                    // Also: if mode and recommendedMode disagree on the leg, the
                    // duration shown was fetched for whichever mode was active at
                    // fetch time. When recommendedMode='fly' but legData.mode='drive',
                    // showing the flight duration next to "Drive" is misleading.
                    // Suppress the duration in that case.
                    const recommendationMatchesMode = rawRecommendedPers === legData.mode
                      || (rawRecommendedPers === 'fly' && legData.mode === 'fly')
                      || (rawRecommendedPers?.startsWith('drive') && legData.mode?.startsWith('drive'));
                    const autoDuration = recommendationMatchesMode ? duration : null;
                    const autoRecommendedMode = (isCrossContinent && (rawRecommendedPers === 'car' || rawRecommendedPers?.startsWith('drive'))) ? 'fly' : rawRecommendedPers;
                    const autoLabel = isLoading
                      ? 'Analyzing...'
                      : `Recommended: ${autoRecommendedMode === 'fly' ? 'Fly' : autoRecommendedMode === 'train' ? 'Train' : autoRecommendedMode === 'ferry' ? 'Ferry' : autoRecommendedMode?.startsWith('drive') ? 'Drive' : autoRecommendedMode || 'Fly'}${autoDuration ? ` (${autoDuration})` : ''}`;
                    modeOptions.push({ id: 'auto', icon: 'sparkles', label: 'Auto', sublabel: autoLabel });
                    // Always show fly (unless truly same city)
                    modeOptions.push({ id: 'fly', icon: 'plane', label: 'Fly' });
                    // Show drive if available and reasonable (< 10hr) AND not island/cross-continent
                    if (!involvesIslandLeg && !isCrossContinent && driveAvailable && (legData.durationMinutes || 0) < 600) {
                      modeOptions.push({ id: 'drive-own', icon: 'car', label: 'Drive' });
                    }
                    // Show train/ferry if recommended by quick-transport (use recommendedMode so it persists after user switches)
                    // Train: only if NOT an island route AND not cross-continent
                    if (!involvesIslandLeg && !isCrossContinent && (legData.recommendedMode === 'train' || legData.reason?.toLowerCase()?.includes('train') || mode === 'train')) {
                      modeOptions.push({ id: 'train', icon: 'train', label: 'Train' });
                    }
                    if (legData.recommendedMode === 'ferry' || legData.reason?.toLowerCase()?.includes('ferry') || mode === 'ferry') {
                      modeOptions.push({ id: 'ferry', icon: 'ship', label: 'Ferry' });
                    }
                    // For island routes, always add ferry option if same continent and not already added
                    if (involvesIslandLeg && !modeOptions.some(m => m.id === 'ferry')) {
                      const fromCountryLeg = getCityCountrySync(leg.from);
                      const toCountryLeg = getCityCountrySync(leg.to);
                      const fromContLeg = getContinentForCountry(fromCountryLeg);
                      const toContLeg = getContinentForCountry(toCountryLeg);
                      const isCrossOceanLeg = fromContLeg && toContLeg && fromContLeg !== toContLeg;
                      if (!isCrossOceanLeg) {
                        modeOptions.push({ id: 'ferry', icon: 'ship', label: 'Ferry' });
                      }
                    }

                    // Airport info for fly legs
                    const depAirport = legData.departureAirport;
                    const arrAirport = legData.arrivalAirport;

                    return (
                      <div key={leg.key} className="border-2 border-gray-200 rounded-xl p-4 bg-white hover:border-gray-300 transition-colors">
                        {/* Header: from → to + duration */}
                        <div className="flex items-start justify-between mb-3 gap-2">
                          <div className="flex items-start gap-2 min-w-0 flex-1">
                            <ActivityIcon emoji={mode === 'fly' ? '✈️' : mode === 'train' ? '🚆' : mode === 'ferry' ? '⛴️' : mode?.startsWith('drive') ? '🚗' : '✈️'} size={20} />
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-gray-800 break-words" style={FONTS.headingSemibold}>
                                {leg.fromLabel} → {leg.toLabel}
                              </p>
                              <p className="text-xs text-gray-400">
                                {leg.isFirst ? 'Getting there' : leg.isLast ? 'Getting home' : 'Inter-city'}
                              </p>
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0">
                            {isLoading ? (
                              <div className="animate-spin w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full"></div>
                            ) : duration ? (
                              <span className="text-xs font-medium text-gray-500">{duration}</span>
                            ) : null}
                          </div>
                        </div>

                        {/* Mode selection buttons */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {modeOptions.map(opt => {
                            const isAutoOpt = opt.id === 'auto';
                            const isSelected = isAutoOpt ? !legData.isUserOverride : (mode === opt.id && legData.isUserOverride);
                            return (
                              <button
                                key={opt.id}
                                onClick={() => {
                                  if (isAutoOpt) {
                                    // Auto: clear user override, revert to recommended
                                    setLegTransports(prev => ({
                                      ...prev,
                                      [leg.key]: { ...(prev[leg.key] || {}), mode: autoRecommendedMode, isUserOverride: false, source: 'auto' },
                                    }));
                                  } else {
                                    handleLegModeChange(leg.key, opt.id, leg);
                                  }
                                }}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all flex items-center gap-1.5 ${
                                  isSelected
                                    ? 'bg-teal-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {opt.icon === 'sparkles' ? <Sparkles className="w-3.5 h-3.5" />
                                  : opt.icon === 'plane' ? <Plane className="w-3.5 h-3.5" />
                                  : opt.icon === 'car' ? <Car className="w-3.5 h-3.5" />
                                  : opt.icon === 'train' ? <Train className="w-3.5 h-3.5" />
                                  : opt.icon === 'ship' ? <Ship className="w-3.5 h-3.5" />
                                  : <ActivityIcon emoji={opt.emoji} size={14} />}
                                <span>{opt.label}</span>
                              </button>
                            );
                          })}
                          {/* Custom option */}
                          <button
                            onClick={() => handleLegModeChange(leg.key, 'custom', leg)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              mode === 'custom'
                                ? 'bg-teal-600 text-white shadow-sm'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <Pencil className="w-3 h-3 inline mr-1" /> Other
                          </button>
                        </div>
                        {/* Auto recommendation label */}
                        {!legData.isUserOverride && (
                          <p className="text-xs text-teal-600 mb-2 flex items-center gap-1">
                            {isLoading ? (
                              <><span className="animate-spin inline-block w-3 h-3 border border-teal-500 border-t-transparent rounded-full"></span> Analyzing best route...</>
                            ) : (
                              <>{'\u2728'} {autoLabel}</>
                            )}
                          </p>
                        )}

                        {/* Custom transport text input */}
                        {mode === 'custom' && (
                          <input
                            type="text"
                            placeholder="e.g., friend is driving us, overnight bus, cruise ship..."
                            value={legData.customDesc || ''}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[<>]/g, '').slice(0, 100);
                              setLegTransports(prev => ({
                                ...prev,
                                [leg.key]: { ...(prev[leg.key] || {}), customDesc: val, isUserOverride: true, source: 'user' },
                              }));
                            }}
                            className="w-full mb-3 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none"
                            maxLength={100}
                          />
                        )}

                        {/* Airport selection — only for fly legs */}
                        {mode === 'fly' && (depAirport || arrAirport) && (
                          <div className="flex items-center gap-2 mb-3 text-xs flex-wrap">
                            {leg.travelDate && <span className="text-[11px] font-semibold text-gray-500">📅 {leg.travelDate} ·</span>}
                            <span className="text-gray-400">Airports:</span>
                            {/* Departure airport chip */}
                            <span className="font-mono font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                              {depAirport?.code || '...'}
                            </span>
                            {depAirport?.alternatives?.length > 0 && (
                              <select
                                value={depAirport?.code || ''}
                                onChange={(e) => handleLegAirportChange(leg.key, 'departure', e.target.value)}
                                className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white"
                              >
                                <option value={depAirport?.code}>{depAirport?.code} ({depAirport?.name})</option>
                                {depAirport.alternatives.map(alt => (
                                  <option key={alt.code} value={alt.code}>{alt.code} ({alt.name})</option>
                                ))}
                              </select>
                            )}
                            <span className="text-gray-300">→</span>
                            {/* Arrival airport chip */}
                            <span className="font-mono font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                              {arrAirport?.code || '...'}
                            </span>
                            {arrAirport?.alternatives?.length > 0 && (
                              <select
                                value={arrAirport?.code || ''}
                                onChange={(e) => handleLegAirportChange(leg.key, 'arrival', e.target.value)}
                                className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white"
                              >
                                <option value={arrAirport?.code}>{arrAirport?.code} ({arrAirport?.name})</option>
                                {arrAirport.alternatives.map(alt => (
                                  <option key={alt.code} value={alt.code}>{alt.code} ({alt.name})</option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}

                        {/* "Drive further for more routes" — expose major-hub airports
                            within a 300mi drive radius of the leg origin (leg.from). Useful
                            when the recommended departure airport is a tiny regional field
                            and the user would rather drive to a bigger hub. Uses the dynamic
                            airports_within_radius RPC (4,500-airport master) so cities
                            outside MULTI_AIRPORT_CITIES — Tokyo NRT/HND, Newcastle NTL —
                            now surface alternatives automatically. */}
                        {mode === 'fly' && (
                          <DriveFurtherPicker
                            from={leg.from}
                            currentCode={depAirport?.code}
                            excludeAlternatives={depAirport?.alternatives || []}
                            onSelect={(code) => handleLegAirportChange(leg.key, 'departure', code)}
                          />
                        )}

                        {/* Mirror of the above for the ARRIVAL side — lets
                            travelers fly into a different/larger hub near
                            leg.to and drive from there. Same component with
                            direction="arrival" swaps the copy accordingly. */}
                        {mode === 'fly' && (
                          <DriveFurtherPicker
                            from={leg.to}
                            currentCode={arrAirport?.code}
                            excludeAlternatives={arrAirport?.alternatives || []}
                            onSelect={(code) => handleLegAirportChange(leg.key, 'arrival', code)}
                            direction="arrival"
                          />
                        )}

                        {/* Time input — all legs get a departure/arrival time picker */}
                        <div className="mb-3">
                          <label className="block text-xs font-medium text-gray-500 mb-1">
                            {leg.isFirst ? (mode === 'fly' ? '🛬 Arrival time' : '🕐 Departure time') : (mode === 'fly' ? '🛫 Flight departure' : '🕐 Leave at')}
                            {leg.travelDate && <span className="ml-2 text-gray-400 font-normal">({leg.travelDate})</span>}
                          </label>
                          <input
                            type="time"
                            value={legData.time || (leg.isFirst ? '08:00' : leg.isLast ? '18:00' : '10:00')}
                            onChange={(e) => handleLegTimeChange(leg.key, e.target.value)}
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none bg-white"
                          />
                          <p className="text-xs text-gray-400 mt-1">
                            {leg.isFirst
                              ? "Don't have flights yet? We'll plan around this time"
                              : leg.isLast
                                ? "We'll end activities in time for your departure"
                                : "Set when you'd like to travel between cities"}
                          </p>
                        </div>

                        {/* Flight search — only for fly legs */}
                        {mode === 'fly' && (
                          <div>
                            {/* Selected flight display */}
                            {legData.selectedFlight && (() => {
                              const sf = legData.selectedFlight;
                              const segs = sf.segments || [];
                              const firstSeg = segs[0];
                              const lastSeg = segs[segs.length - 1];
                              const depDateStr = firstSeg?.departure?.time ? parseAmadeusDate(firstSeg.departure.time) : null;
                              const arrDateStr = lastSeg?.arrival?.time ? parseAmadeusDate(lastSeg.arrival.time) : null;
                              const depTerminal = firstSeg?.departure?.terminal;
                              const arrTerminal = lastSeg?.arrival?.terminal;
                              const cabin = firstSeg?.cabin;
                              const isOvernightFlight = depDateStr && arrDateStr && arrDateStr > depDateStr;
                              const layoverCities = segs.length > 1 ? segs.slice(0, -1).map(s => s.arrival?.airport).filter(Boolean) : [];
                              const formatDuration = (dur) => dur ? dur.replace('PT', '').replace('H', 'h ').replace('M', 'm').trim() : '';

                              return (
                              <div className="mb-2 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-semibold text-green-800 flex items-center gap-1.5">
                                    <span className="text-green-600">✓</span>
                                    {sf.airline} {sf.flightNumber}
                                    {cabin && cabin !== 'ECONOMY' && (
                                      <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded ml-1">{cabin}</span>
                                    )}
                                  </span>
                                  <button onClick={() => setLegTransports(prev => ({ ...prev, [leg.key]: { ...(prev[leg.key] || {}), selectedFlight: null } }))}
                                    className="text-xs text-gray-400 hover:text-gray-600">✕ Remove</button>
                                </div>
                                <div className="flex items-center gap-3 text-xs text-green-700">
                                  <div>
                                    <div className="flex items-baseline gap-1">
                                      <span className="font-bold text-sm text-green-800">{sf.from}</span>
                                      {depTerminal && <span className="text-[10px] text-green-500">T{depTerminal}</span>}
                                    </div>
                                    <span className="font-medium">{sf.departureTime || ''}</span>
                                    {depDateStr && <div className="text-[10px] text-green-500">{new Date(depDateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</div>}
                                  </div>
                                  <div className="flex-1 flex flex-col items-center">
                                    <span className="text-[10px] text-green-400">{formatDuration(sf.duration)}</span>
                                    <div className="w-full flex items-center gap-0.5">
                                      <div className="flex-1 h-px bg-green-300"></div>
                                      {layoverCities.map((city, i) => (
                                        <React.Fragment key={i}>
                                          <span className="text-[9px] text-orange-500 font-medium bg-orange-50 px-1 rounded">{city}</span>
                                          <div className="flex-1 h-px bg-green-300"></div>
                                        </React.Fragment>
                                      ))}
                                      <span className="text-green-400">→</span>
                                    </div>
                                    <span className="text-[10px] text-green-500">{segs.length > 1 ? `${segs.length - 1} stop` : 'Direct'}</span>
                                  </div>
                                  <div className="text-right">
                                    <div className="flex items-baseline gap-1 justify-end">
                                      <span className="font-bold text-sm text-green-800">{sf.to}</span>
                                      {arrTerminal && <span className="text-[10px] text-green-500">T{arrTerminal}</span>}
                                    </div>
                                    <span className="font-medium">{sf.arrivalTime || ''}</span>
                                    {arrDateStr && <div className="text-[10px] text-green-500 flex items-center justify-end gap-1">{new Date(arrDateStr + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}{isOvernightFlight && <span className="text-purple-600 font-medium">+1</span>}</div>}
                                  </div>
                                </div>
                                <div className="flex items-center justify-between mt-2 pt-2 border-t border-green-100">
                                  <span className="text-sm font-semibold text-green-800">{sf.currency && sf.currency !== 'USD' ? `${sf.currency} ` : '$'}{sf.price}</span>
                                  {sf.bookingUrl && <a href={sf.bookingUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-700 font-medium">Book →</a>}
                                  {sf._fetchedAt && (Date.now() - sf._fetchedAt > 24 * 60 * 60 * 1000) && (
                                    <span className="text-[10px] text-amber-600 flex items-center gap-0.5" title="Price may have changed">⚠️ &gt;24h old</span>
                                  )}
                                </div>
                              </div>
                              );
                            })()}

                            {/* Search button */}
                            {!legData.selectedFlight && !flightSearch?.expanded && (
                              <button
                                onClick={() => searchLegFlights(leg.key, leg)}
                                disabled={flightSearch?.loading}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                              >
                                {flightSearch?.loading ? (
                                  <>
                                    <div className="animate-spin w-3 h-3 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                                    Searching flights...
                                  </>
                                ) : (
                                  <>🔍 Search flights</>
                                )}
                              </button>
                            )}

                            {/* Flight search results */}
                            {flightSearch?.expanded && flightSearch?.results?.length > 0 && (
                              <div className="mt-2 space-y-1.5">
                                {flightSearch.results.map((offer, idx) => {
                                  const itin = offer.itineraries?.[0];
                                  const segs = itin?.segments || [];
                                  const airlines = (offer.airlines || []).join(', ');
                                  const stops = segs.length - 1;
                                  return (
                                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-100 hover:border-teal-300 transition-colors">
                                      <div className="min-w-0">
                                        <p className="text-xs font-medium text-gray-800 truncate">
                                          {airlines || 'Multiple airlines'} — {itin?.duration?.replace('PT', '').replace('H', 'h ').replace('M', 'm').toLowerCase() || ''}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                          {stops === 0 ? 'Nonstop' : `${stops} stop${stops > 1 ? 's' : ''}`}
                                          {segs[0]?.departure?.airport ? ` · ${segs[0].departure.airport}→${segs[segs.length - 1]?.arrival?.airport}` : ''}
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2 flex-shrink-0">
                                        <span className="text-sm font-bold text-gray-800">${offer.price}</span>
                                        <button
                                          onClick={() => selectLegFlight(leg.key, offer, flightSearch.dictionaries, leg)}
                                          className="text-xs bg-teal-600 text-white px-2.5 py-1 rounded-lg hover:bg-teal-700 transition-colors"
                                        >
                                          Select
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                                <button
                                  onClick={() => setLegFlightSearch(prev => ({ ...prev, [leg.key]: { ...prev[leg.key], expanded: false } }))}
                                  className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                                >
                                  Collapse
                                </button>
                              </div>
                            )}

                            {/* Error */}
                            {flightSearch?.error && (
                              <p className="text-xs text-red-500 mt-1">{flightSearch.error}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* HOTEL PREFERENCES — Section Card.
                  Two stop kinds, two control sets:
                  - hotel/resort stops: show "stay focus" toggle (Mostly at the
                    hotel vs Explore nearby) — drives orchestrator's per-day
                    tripType branch via stayOnProperty.
                  - destination stops: show suggest/booked/skip + budget pills. */}
              {tripBuilderStops.length > 0 && (
                <div style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, padding: '24px 28px', boxShadow: '0 4px 16px rgba(28,41,53,0.05)' }}>
                  <div className="text-lg font-extrabold mb-1" style={{ color: '#1e293b', letterSpacing: '-0.02em' }}>Hotel Preferences</div>
                  <p className="text-[13px] mb-4" style={{ color: '#64748b' }}>Set focus, budget, and preferences for each stop</p>
                  <div className="space-y-3">
                    {tripBuilderStops.map((stop) => {
                      const isHotelStop = stop.stopType === 'hotel' || stop.stopType === 'resort';
                      // Per-stop "stay on property" toggle for hotel/resort
                      // stops. Default true (=== 'Mostly at the hotel') keeps
                      // legacy behavior. Toggle to false to plan exploration.
                      if (isHotelStop) {
                        const stayOnProperty = stop.stayOnProperty !== false;
                        return (
                          <div key={stop.id} className={BTN.card(false)}>
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-2">
                                <div className="flex items-center justify-center" style={{ width: 22, height: 22, borderRadius: 7, background: 'rgba(13,148,136,0.1)' }}>
                                  <MapPin className="w-3 h-3 text-teal-600" />
                                </div>
                                <span className="font-semibold text-gray-800" style={FONTS.headingSemibold}>
                                  {(stop.name || '').split(',')[0]}
                                </span>
                                <span className="text-xs text-gray-400">{stop.days}d</span>
                              </div>
                            </div>
                            <p className="text-[11px] mb-2" style={{ color: '#64748b' }}>How should we plan your days here?</p>
                            <div className="flex gap-2">
                              <button
                                onClick={() => setTripBuilderStops(prev => prev.map(s => s.id === stop.id ? { ...s, stayOnProperty: true } : s))}
                                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                  stayOnProperty
                                    ? 'bg-teal-100 text-teal-700 border-2 border-teal-400'
                                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                                }`}
                                title="Plan activities at the hotel"
                              >
                                Mostly at the hotel
                              </button>
                              <button
                                onClick={() => setTripBuilderStops(prev => prev.map(s => s.id === stop.id ? { ...s, stayOnProperty: false } : s))}
                                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                  !stayOnProperty
                                    ? 'bg-teal-100 text-teal-700 border-2 border-teal-400'
                                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                                }`}
                                title="Plan activities exploring the surrounding area"
                              >
                                Explore nearby
                              </button>
                            </div>
                          </div>
                        );
                      }
                      const stopData = stopHotels[stop.id] || {};
                      const effectiveBudget = stopData.budget || userProfile.preferences.hotelBudget || 'midrange';
                      const isOverridden = stopData.isOverridden;
                      return (
                        <div key={stop.id} className={BTN.card(false)}>
                          {/* Header: stop icon + name */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center justify-center" style={{ width: 22, height: 22, borderRadius: 7, background: 'rgba(13,148,136,0.1)' }}>
                                <MapPin className="w-3 h-3 text-teal-600" />
                              </div>
                              <span className="font-semibold text-gray-800" style={FONTS.headingSemibold}>
                                {(stop.name || '').split(',')[0]}
                              </span>
                              <span className="text-xs text-gray-400">{stop.days}d</span>
                            </div>
                            {isOverridden && (
                              <button onClick={() => handleResetStopHotel(stop.id)}
                                className="text-xs text-gray-400 hover:text-gray-600">Reset</button>
                            )}
                          </div>
                          {/* Hotel Mode Selector */}
                          <div className="flex gap-2 mb-4">
                            {[
                              { mode: 'suggest', label: 'Suggest Hotel' },
                              { mode: 'booked', label: 'Already Booked' },
                              { mode: 'skip', label: 'Skip Hotel' },
                            ].map(opt => (
                              <button
                                key={opt.mode}
                                onClick={() => updateStopHotelMode(stop.id, opt.mode)}
                                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                                  (stopData.mode || 'suggest') === opt.mode
                                    ? 'bg-teal-100 text-teal-700 border-2 border-teal-400'
                                    : 'bg-gray-50 text-gray-600 border border-gray-200 hover:bg-gray-100'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>

                          {/* Conditional: Already booked input */}
                          {(stopData.mode === 'booked') && (
                            <input type="text" placeholder="Hotel name or address..."
                              value={stopData.bookedHotel || ''}
                              onChange={(e) => updateStopBookedHotel(stop.id, e.target.value)}
                              className="w-full px-3 py-2 border rounded-lg text-sm mb-2 focus:border-teal-500 focus:outline-none"
                              style={FONTS.body}
                            />
                          )}

                          {/* Conditional: Skip shows neighborhood recommendation text */}
                          {(stopData.mode === 'skip') && (
                            <p className="text-xs text-gray-500 italic mb-2">arrivo will recommend a neighborhood instead of a specific hotel.</p>
                          )}

                          {/* Budget tier buttons (only shown in suggest mode) */}
                          {(!stopData.mode || stopData.mode === 'suggest') && (
                            <>
                              <div className="grid grid-cols-3 gap-2 mb-3">
                                {[
                                  { label: 'Budget', desc: '<$100', value: 'budget' },
                                  { label: 'Mid-Range', desc: '$100\u2013350', value: 'midrange' },
                                  { label: 'Luxury', desc: '$350+', value: 'luxury' },
                                ].map(opt => (
                                  <button key={opt.value}
                                    onClick={() => handleStopHotelBudget(stop.id, opt.value)}
                                    className={BTN.card(effectiveBudget === opt.value)}>
                                    <div className="font-semibold text-xs" style={FONTS.headingSemibold}>{opt.label}</div>
                                    <div className="text-xs opacity-70">{opt.desc}</div>
                                  </button>
                                ))}
                              </div>
                              {/* Optional specific hotel input */}
                              <input type="text"
                                placeholder="Specific hotel? e.g., The Ritz Paris"
                                value={stopData.preferredHotel || ''}
                                onChange={(e) => handleStopPreferredHotel(stop.id, e.target.value)}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-teal-500 focus:outline-none"
                                style={FONTS.body}
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Note: pure-hotel trips now render the per-stop stay-focus toggle
                  inside the Hotel Preferences card above. The "Not applicable"
                  placeholder is no longer needed. */}

              {/* TRAVEL STYLE — Section Card */}
              <div style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, padding: '24px 28px', boxShadow: '0 4px 16px rgba(28,41,53,0.05)' }}>
                <div className="text-lg font-extrabold mb-4" style={{ color: '#1e293b', letterSpacing: '-0.02em' }}>Travel Style</div>

              {/* Dietary Restrictions */}
              <div className="mb-6">
                <label className="block text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: '#64748b', letterSpacing: '0.04em' }}>Dietary Restrictions</label>
                <div className="flex flex-wrap gap-2">
                  {['Vegetarian', 'Vegan', 'Pescatarian', 'Gluten-Free', 'Dairy-Free', 'Nut Allergies', 'Shellfish/Fish Allergy', 'Low-Carb/Keto', 'Diabetic Diet', 'Kosher', 'Halal', 'None'].map(diet => (
                    <button
                      key={diet}
                      onClick={() => {
                        const current = userProfile.preferences.dietaryRestrictions || [];
                        const newDietary = current.includes(diet)
                          ? current.filter(d => d !== diet)
                          : diet === 'None' ? ['None'] : [...current.filter(d => d !== 'None'), diet];
                        setUserProfile(prev => ({
                          ...prev,
                          preferences: { ...prev.preferences, dietaryRestrictions: newDietary }
                        }));
                      }}
                      className={BTN.pill((userProfile.preferences.dietaryRestrictions || []).includes(diet))}
                    >
                      <span style={FONTS.body}>{diet}</span>
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  placeholder="Other (type here)"
                  className="mt-3 w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-teal-500 focus:outline-none"
                  style={FONTS.body}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const current = userProfile.preferences.dietaryRestrictions || [];
                      setUserProfile(prev => ({
                        ...prev,
                        preferences: { ...prev.preferences, dietaryRestrictions: [...current, e.target.value.trim()] }
                      }));
                      e.target.value = '';
                    }
                  }}
                />
              </div>

              {/* Trip Pace */}
              <div className="mb-6">
                <label className="block text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: '#64748b', letterSpacing: '0.04em' }}>Trip Pace</label>

                <div className="space-y-4">
                  {/* Pace Options — uses CANONICAL_PACE_OPTIONS from onboardingSchema
                      (3 tiers: relaxed/moderate/packed). Previously had 4 hardcoded
                      options including 'easy' and 'balanced' that the orchestrator
                      didn't recognize, silently dropping the user's pace choice. */}
                  <div className="flex gap-3 flex-wrap">
                    {CANONICAL_PACE_OPTIONS.map(pace => {
                      const perDay = pace.value === 'relaxed' ? '2-3 activities/day'
                        : pace.value === 'moderate' ? '4-5 activities/day'
                        : '6+ activities/day';
                      const selected = userProfile.preferences.preferredPace === pace.value
                        || userProfile.preferences.pace === pace.value
                        || (!userProfile.preferences.preferredPace && !userProfile.preferences.pace
                            && userProfile.preferences.tripPace === pace.value);
                      return (
                        <button
                          key={pace.value}
                          onClick={() => {
                            // Write canonical `pace` (orchestrator reads this) AND
                            // legacy `preferredPace` + `tripPace` for backward compat
                            // with screens that still read those.
                            setUserProfile(prev => ({
                              ...prev,
                              preferences: {
                                ...prev.preferences,
                                pace: pace.value,
                                preferredPace: pace.value,
                                tripPace: pace.value,
                              },
                            }));
                          }}
                          className={`flex-1 min-w-[120px] ${BTN.card(selected)}`}
                        >
                          <div className="font-bold" style={FONTS.headingSemibold}>{pace.label}</div>
                          <div className="text-xs opacity-80 mt-1">{perDay}</div>
                        </button>
                      );
                    })}
                  </div>

                </div>
              </div>

              {/* Companion Profiles — uses CANONICAL_COMPANION_OPTIONS shared with
                  mobile + canonical schema. Base types (solo/couple/family/friends/
                  group/business) are mutually exclusive; modifiers (kids/teens/
                  elderly/wheelchair/pets) stack additively. */}
              <div className="mb-6">
                <label className="block text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: '#64748b', letterSpacing: '0.04em' }}>Companion Type</label>
                <p className="text-xs text-gray-500 mb-3">Pick a base type plus any modifiers</p>

                <div className="grid grid-cols-2 gap-3">
                  {CANONICAL_COMPANION_OPTIONS.map(companion => {
                    const current = Array.isArray(userProfile.preferences.companions)
                      ? userProfile.preferences.companions
                      : (userProfile.preferences.companions ? [userProfile.preferences.companions] : []);
                    const isSelected = current.includes(companion.value);
                    const baseValues = CANONICAL_COMPANION_OPTIONS.filter(o => o.base).map(o => o.value);
                    return (
                      <button
                        key={companion.value}
                        onClick={() => {
                          setUserProfile(prev => {
                            const cur = Array.isArray(prev.preferences.companions)
                              ? prev.preferences.companions
                              : (prev.preferences.companions ? [prev.preferences.companions] : []);
                            let updated;
                            if (isSelected) {
                              updated = cur.filter(c => c !== companion.value);
                            } else if (companion.base) {
                              // Replace any existing base type with this one
                              updated = [...cur.filter(c => !baseValues.includes(c)), companion.value];
                            } else {
                              updated = [...cur, companion.value];
                            }
                            return {
                              ...prev,
                              preferences: { ...prev.preferences, companions: updated }
                            };
                          });
                        }}
                        className={`text-left ${BTN.card(isSelected)}`}
                      >
                        <div className="font-bold text-sm" style={FONTS.headingSemibold}>
                          {companion.icon ? `${companion.icon} ` : ''}{companion.label}
                        </div>
                        <div className="text-xs opacity-80 mt-1">{companion.description || ''}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              </div>

              {/* DAILY SCHEDULE — Section Card */}
              <div style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, padding: '24px 28px', boxShadow: '0 4px 16px rgba(28,41,53,0.05)' }}>
                <div className="text-lg font-extrabold mb-4" style={{ color: '#1e293b', letterSpacing: '-0.02em' }}>Daily Schedule</div>

              {/* Preferred Activity Start Time */}
              <div className="mb-6">
                <label className="block text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: '#64748b', letterSpacing: '0.04em' }}>Preferred Start Time</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: '07:00', label: '7:00 AM' },
                    { value: '08:00', label: '8:00 AM' },
                    { value: '09:00', label: '9:00 AM' },
                    { value: '10:00', label: '10:00 AM' },
                  ].map(time => (
                    <button
                      key={time.value}
                      onClick={() => {
                        setUserProfile(prev => ({
                          ...prev,
                          preferences: { ...prev.preferences, preferredStartTime: time.value }
                        }));
                      }}
                      className={`text-left ${BTN.card(userProfile.preferences.preferredStartTime === time.value)}`}
                    >
                      <div className="font-bold text-sm" style={FONTS.headingSemibold}>{time.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* End of Day */}
              <div className="mb-6">
                <label className="block text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: '#64748b', letterSpacing: '0.04em' }}>End of Day</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: '18:00', label: '6:00 PM' },
                    { value: '20:00', label: '8:00 PM' },
                    { value: '22:00', label: '10:00 PM' },
                    { value: '00:00', label: 'Midnight' },
                  ].map(time => (
                    <button
                      key={time.value}
                      onClick={() => {
                        setUserProfile(prev => ({
                          ...prev,
                          preferences: { ...prev.preferences, preferredEndTime: time.value }
                        }));
                      }}
                      className={`text-left ${BTN.card(userProfile.preferences.preferredEndTime === time.value)}`}
                    >
                      <div className="font-bold text-sm" style={FONTS.headingSemibold}>{time.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              </div>

              {/* INTERESTS — Section Card */}
              <div style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, padding: '24px 28px', boxShadow: '0 4px 16px rgba(28,41,53,0.05)' }}>
                <div className="text-lg font-extrabold mb-4" style={{ color: '#1e293b', letterSpacing: '-0.02em' }}>What interests you most?</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'History & Culture', desc: 'Landmarks & heritage' },
                    { label: 'Food & Dining', desc: 'Local cuisine & restaurants' },
                    { label: 'Nature & Outdoors', desc: 'Parks, hikes & scenery' },
                    { label: 'Adventure Sports', desc: 'Surfing, diving & more' },
                    { label: 'Art & Museums', desc: 'Galleries & exhibitions' },
                    { label: 'Shopping', desc: 'Markets & boutiques' },
                    { label: 'Nightlife', desc: 'Bars, clubs & shows' },
                    { label: 'Relaxation', desc: 'Spas & wellness' },
                    { label: 'Photography', desc: 'Scenic photo spots' },
                  ].map(interest => (
                    <button
                      key={interest.label}
                      onClick={() => {
                        const current = userProfile.preferences.interests || [];
                        const newInterests = current.includes(interest.label)
                          ? current.filter(i => i !== interest.label)
                          : [...current, interest.label];
                        setUserProfile(prev => ({
                          ...prev,
                          preferences: { ...prev.preferences, interests: newInterests }
                        }));
                      }}
                      className={`text-left ${BTN.card((userProfile.preferences.interests || []).includes(interest.label))}`}
                    >
                      <div className="font-bold text-sm" style={FONTS.headingSemibold}>{interest.label}</div>
                      <div className="text-xs opacity-80 mt-1">{interest.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* MUST-DO ACTIVITIES — Section Card */}
              {features.ACTIVITY_CURATION && (
              <div style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, padding: '24px 28px', boxShadow: '0 4px 16px rgba(28,41,53,0.05)' }}>
                <div className="text-lg font-extrabold mb-1" style={{ color: '#1e293b', letterSpacing: '-0.02em' }}>Must-Do Activities</div>
                <p className="text-[13px] mb-4" style={{ color: '#64748b' }}>
                  Add specific places, restaurants, or experiences you don't want to miss
                </p>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    placeholder="e.g., Vatican Museums, dinner at Da Enzo..."
                    value={mustDoInput}
                    onChange={(e) => setMustDoInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && mustDoInput.trim()) {
                        setUserProfile(prev => ({
                          ...prev,
                          preferences: {
                            ...prev.preferences,
                            mustDoActivities: [...(prev.preferences.mustDoActivities || []), { text: mustDoInput.trim() }]
                          }
                        }));
                        setMustDoInput('');
                      }
                    }}
                    className="flex-1 px-4 py-2.5 border-2 border-gray-200 rounded-xl text-sm focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
                    style={FONTS.body}
                  />
                  <button
                    onClick={() => {
                      if (mustDoInput.trim()) {
                        setUserProfile(prev => ({
                          ...prev,
                          preferences: {
                            ...prev.preferences,
                            mustDoActivities: [...(prev.preferences.mustDoActivities || []), { text: mustDoInput.trim() }]
                          }
                        }));
                        setMustDoInput('');
                      }
                    }}
                    disabled={!mustDoInput.trim()}
                    className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${mustDoInput.trim() ? 'bg-teal-500 text-white hover:bg-teal-600' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
                    style={FONTS.bodyMedium}
                  >
                    + Add
                  </button>
                </div>
                {(userProfile.preferences.mustDoActivities || []).length > 0 && (
                  <div className="flex flex-col gap-2 mb-2">
                    {userProfile.preferences.mustDoActivities.map((item, idx) => (
                      <div
                        key={idx}
                        className="inline-flex items-center gap-2 px-3 py-2 bg-teal-50 border border-teal-200 rounded-xl text-xs text-teal-700"
                        style={FONTS.bodyMedium}
                      >
                        <span className="flex-1">{item.text}</span>
                        {/* Optional day constraint */}
                        <select
                          value={item.dayConstraint || ''}
                          onChange={(e) => {
                            const newDay = e.target.value ? parseInt(e.target.value) : undefined;
                            setUserProfile(prev => ({
                              ...prev,
                              preferences: {
                                ...prev.preferences,
                                mustDoActivities: prev.preferences.mustDoActivities.map((a, i) =>
                                  i === idx ? { ...a, dayConstraint: newDay } : a
                                )
                              }
                            }));
                          }}
                          className="w-[70px] px-1.5 py-1 text-xs border border-teal-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none"
                          title="Assign to a specific day"
                        >
                          <option value="">Day</option>
                          {Array.from({ length: userProfile.preferences.duration || 7 }, (_, i) => (
                            <option key={i + 1} value={i + 1}>Day {i + 1}</option>
                          ))}
                        </select>
                        {/* Optional time constraint */}
                        <input
                          type="time"
                          value={item.timeConstraint || ''}
                          onChange={(e) => {
                            const newTime = e.target.value;
                            setUserProfile(prev => ({
                              ...prev,
                              preferences: {
                                ...prev.preferences,
                                mustDoActivities: prev.preferences.mustDoActivities.map((a, i) =>
                                  i === idx ? { ...a, timeConstraint: newTime || undefined } : a
                                )
                              }
                            }));
                          }}
                          placeholder="Time"
                          className="w-[90px] px-2 py-1 text-xs border border-teal-200 rounded-lg bg-white focus:border-teal-400 focus:outline-none"
                          title="Set a specific time (e.g., reservation at 8pm)"
                        />
                        <button
                          onClick={() => setUserProfile(prev => ({
                            ...prev,
                            preferences: {
                              ...prev.preferences,
                              mustDoActivities: prev.preferences.mustDoActivities.filter((_, i) => i !== idx)
                            }
                          }))}
                          className="ml-1 text-teal-400 hover:text-teal-600 transition-colors"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                {(userProfile.preferences.mustDoActivities || []).length >= 15 && (
                  <p className="text-xs text-amber-600" style={FONTS.body}>Maximum 15 must-do items recommended for best results</p>
                )}
                {/* Phase 3 placeholder: Import from Link */}
                <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-2">
                  <button
                    onClick={async () => {
                      const bm = await getBookmarks();
                      if (!bm || bm.length === 0) { showToast('No bookmarks saved yet', 'info'); return; }
                      const existing = new Set((userProfile.preferences.mustDoActivities || []).map(a => a.text.toLowerCase()));
                      const newItems = bm.filter(b => !existing.has(b.place_name.toLowerCase())).map(b => ({ text: `${b.place_name}${b.city ? ` (${b.city})` : ''}` }));
                      if (newItems.length === 0) { showToast('All bookmarks already added', 'info'); return; }
                      setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, mustDoActivities: [...(prev.preferences.mustDoActivities || []), ...newItems] } }));
                      showToast(`Added ${newItems.length} bookmarked place${newItems.length > 1 ? 's' : ''}`, 'success');
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-xs text-teal-600 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                    style={FONTS.bodyMedium}
                  >
                    <Bookmark className="w-3.5 h-3.5" />
                    From Bookmarks
                  </button>
                  <button
                    onClick={() => setShowBrowseCommunityTrips(true)}
                    className="flex items-center gap-2 px-3 py-2 text-xs text-teal-600 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors"
                    style={FONTS.bodyMedium}
                  >
                    <Compass className="w-3.5 h-3.5" />
                    Browse Community Trips
                  </button>
                </div>
              </div>
              )}

              {/* Browse Community Trips Modal */}
              <BrowseCommunityTripsModal
                isOpen={showBrowseCommunityTrips}
                onClose={() => setShowBrowseCommunityTrips(false)}
                destination={selectedDestination?.name || selectedDestination?.stopNames?.[0] || ''}
                destinations={selectedDestination?.stopNames || (selectedDestination?.name ? [selectedDestination.name] : [])}
                onAddActivity={(activityName) => {
                  setUserProfile(prev => ({
                    ...prev,
                    preferences: {
                      ...prev.preferences,
                      mustDoActivities: [...(prev.preferences.mustDoActivities || []), { text: activityName }]
                    }
                  }));
                  showToast(`Added "${activityName}" to must-do list`, 'success', 2000);
                }}
              />

              {/* TRAVEL NOTES — Section Card */}
              <div style={{ background: 'rgba(255,255,255,0.94)', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, padding: '24px 28px', boxShadow: '0 4px 16px rgba(28,41,53,0.05)' }}>
                <div className="text-lg font-extrabold mb-1" style={{ color: '#1e293b', letterSpacing: '-0.02em' }}>Travel Notes</div>
                <textarea
                  placeholder="Any things to avoid, accessibility needs, or special requests..."
                  value={userProfile.preferences.travelNotes || ''}
                  onChange={(e) => setUserProfile(prev => ({
                    ...prev,
                    preferences: { ...prev.preferences, travelNotes: e.target.value }
                  }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm resize-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
                  style={FONTS.body}
                  rows={3}
                />
              </div>

              {/* PLANNING STYLE — Removed: user requested removal of flexible vs structured concept */}

              </div>
              {/* end left column */}

              {/* Right Sidebar — Sticky Trip Summary */}
              <div style={{ alignSelf: 'stretch' }}>
              <div className="personalization-sidebar flex flex-col" style={{ position: 'sticky', top: 80, maxHeight: 'calc(100vh - 100px)', background: 'white', border: '1px solid rgba(31,29,59,0.08)', borderRadius: 22, boxShadow: '0 4px 16px rgba(28,41,53,0.05)', overflow: 'hidden' }}>
                <div className="overflow-y-auto flex-1">
                  {/* Summary Header */}
                  <div className="px-5 py-4" style={{ background: 'linear-gradient(135deg, #0d9488, #14b8a6)', color: 'white' }}>
                    <h3 className="text-[15px] font-extrabold">
                      {selectedDestination.isMultiStop ? `${selectedDestination.stopNames?.length || 2}-Stop Trip` : selectedDestination.name}
                    </h3>
                    <span className="text-[11px] font-semibold block mt-0.5" style={{ opacity: 0.7 }}>
                      {userProfile.preferences.startDate && userProfile.preferences.endDate
                        ? `${new Date(userProfile.preferences.startDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(userProfile.preferences.endDate + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
                        : 'Set dates below'}
                      {userProfile.preferences.duration ? ` · ${userProfile.preferences.duration} days` : ''}
                    </span>
                    {selectedDestination.description && (
                      <p className="text-[10px] mt-1.5 leading-relaxed" style={{ opacity: 0.65 }}>
                        {selectedDestination.description.length > 120
                          ? selectedDestination.description.substring(0, 120) + '...'
                          : selectedDestination.description}
                      </p>
                    )}
                  </div>
                  {/* Summary Body */}
                  <div className="p-5">
                    {/* Quick Facts */}
                    <div className="flex flex-col gap-2 mb-4">
                      <div className="flex items-center gap-2.5 py-2" style={{ borderBottom: '1px solid rgba(31,29,59,0.06)' }}>
                        <div className="flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 10, background: 'rgba(13,148,136,0.08)' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2"><path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"/></svg>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#94a3b8', letterSpacing: '0.04em' }}>Weather</div>
                          <div className="text-[11px] font-bold" style={{ color: '#1e293b' }}>
                            {(() => {
                              if (personalizationWeather) {
                                const isCelsius = (() => { try { return localStorage.getItem('weather-unit') === 'C'; } catch { return false; } })();
                                // Open-Meteo returns Celsius — convert to F when user wants Fahrenheit
                                const toF = (c) => c !== null && c !== undefined ? Math.round(c * 9 / 5 + 32) : null;
                                const temp = (c) => isCelsius ? c : toF(c);
                                const formatWeather = (w) => {
                                  const highLow = w.high !== null && w.low !== null ? `${temp(w.high)}°/${temp(w.low)}°` : '';
                                  const rain = w.precipProbability > 30 ? ` ${w.precipProbability}%☔` : '';
                                  const source = w.source === 'climate' ? ' avg' : '';
                                  return `${highLow}${rain}${source}`;
                                };
                                // Multi-city: render each city's weather as a flex-wrap pill
                                // so all stops stay visible in the narrow sidebar instead of
                                // being clipped by overflow:hidden when joined into a single
                                // long " · " separated line. Cap at 5 to protect the layout;
                                // overflow scrolls horizontally on mobile widths.
                                if (Array.isArray(personalizationWeather)) {
                                  const cap = personalizationWeather.slice(0, 5);
                                  return (
                                    <div className="flex flex-wrap gap-x-2 gap-y-0.5 items-center">
                                      {cap.map((w, i) => (
                                        <span key={`${w.city}-${i}`} className="whitespace-nowrap">
                                          <span className="font-semibold" style={{ color: '#64748b' }}>{w.city}:</span>{' '}{formatWeather(w)}
                                        </span>
                                      ))}
                                      <button
                                        onClick={(e) => { e.stopPropagation(); const next = !isCelsius; try { localStorage.setItem('weather-unit', next ? 'C' : 'F'); } catch {} setPersonalizationWeather(prev => prev ? (Array.isArray(prev) ? [...prev] : {...prev}) : prev); }}
                                        className="text-[10px] font-bold text-gray-400 hover:text-teal-600 cursor-pointer"
                                        title={isCelsius ? 'Switch to Fahrenheit' : 'Switch to Celsius'}
                                      >
                                        {isCelsius ? '°C' : '°F'}
                                      </button>
                                    </div>
                                  );
                                }
                                // Single city
                                const w = personalizationWeather;
                                const desc = w.description || '';
                                return <>{`${formatWeather(w)}${desc ? ` · ${desc}` : ''}`}<button onClick={(e) => { e.stopPropagation(); const next = !isCelsius; try { localStorage.setItem('weather-unit', next ? 'C' : 'F'); } catch {} setPersonalizationWeather(prev => prev ? {...prev} : prev); }} className="ml-1 text-[10px] font-bold text-gray-400 hover:text-teal-600 cursor-pointer" title={isCelsius ? 'Switch to Fahrenheit' : 'Switch to Celsius'}>{isCelsius ? '°C' : '°F'}</button></>;
                              }
                              const destName = selectedDestination?.isMultiStop
                                ? (selectedDestination.stopNames?.[0] || '')
                                : (selectedDestination?.name || '');
                              const dateStr = userProfile.preferences.startDate;
                              const travelMonths = userProfile.preferences.travelMonths || [];
                              if (destName && (dateStr || travelMonths.length > 0)) return 'Loading weather...';
                              if (destName) return 'Set dates for weather forecast';
                              return 'Select destination';
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 py-2" style={{ borderBottom: '1px solid rgba(31,29,59,0.06)' }}>
                        <div className="flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 10, background: 'rgba(20,184,166,0.1)' }}>
                          <DollarSign className="w-3.5 h-3.5" style={{ color: '#14b8a6' }} />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#94a3b8', letterSpacing: '0.04em' }}>Currency</div>
                          <div className="text-[11px] font-bold" style={{ color: '#1e293b' }}>
                            {(() => {
                              // Get all stop names from any source — prefer LIVE tripBuilderStops
                              // because resolvedLocation updates asynchronously for hotel/resort names
                              // (e.g., "Hôtel de Crillon" → "Paris, France" via AI lookup)
                              const allStopNames = tripBuilderStops?.length > 1
                                ? tripBuilderStops.map(s => s.resolvedLocation || s.name)
                                : selectedDestination?.stopNames?.length > 1
                                  ? selectedDestination.stopNames
                                  : selectedDestination?.stops?.length > 1
                                    ? selectedDestination.stops.map(s => s.resolvedLocation || s.name || s)
                                    // Fallback: parse from name if it contains "&" (e.g., "Barcelona & Rome") — but not hotel names
                                    : selectedDestination?.name?.includes('&') && !isHotelOrResort(selectedDestination.name)
                                      ? selectedDestination.name.split('&').map(s => s.trim()).filter(Boolean)
                                      : null;
                              if (allStopNames && allStopNames.length > 1) {
                                // B2 fix: multi-city should show every stop, grouped by currency.
                                // If all stops share a currency: "Park City + Aspen: USD".
                                // If they differ: "Park City: USD · Lisbon: EUR".
                                const groups = new Map();
                                allStopNames.forEach((name) => {
                                  const curr = getCurrencyForCity(name);
                                  if (!curr) return;
                                  const cityShort = (name || '').split(',')[0].trim();
                                  const list = groups.get(curr) || [];
                                  if (!list.includes(cityShort)) list.push(cityShort);
                                  groups.set(curr, list);
                                });
                                const parts = [];
                                for (const [curr, cities] of groups.entries()) {
                                  parts.push(`${cities.join(' + ')}: ${curr}`);
                                }
                                return parts.length > 0 ? parts.join(' · ') : 'Currency info available after generation';
                              }
                              // Try resolved location first (e.g., "Rudi Dadens 1, Switzerland"), then destination name
                              const resolvedLoc = tripBuilderStops?.[0]?.resolvedLocation;
                              const destCity = resolvedLoc || selectedDestination?.name || '';
                              const curr = destCity ? getCurrencyForCity(destCity) : null;
                              return curr && curr !== 'USD' ? `${curr} (local)` : curr === 'USD' ? 'USD' : 'Currency info available after generation';
                            })()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2.5 py-2">
                        <div className="flex items-center justify-center flex-shrink-0" style={{ width: 28, height: 28, borderRadius: 10, background: 'rgba(13,148,136,0.08)' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wide" style={{ color: '#94a3b8', letterSpacing: '0.04em' }}>Language</div>
                          <div className="text-[11px] font-bold" style={{ color: '#1e293b' }}>
                            {(() => {
                              // Prefer LIVE tripBuilderStops — resolvedLocation updates async for hotel names
                              const allStops = tripBuilderStops?.length > 1
                                ? tripBuilderStops.map(s => s.resolvedLocation || s.name)
                                : selectedDestination?.stopNames?.length > 1
                                  ? selectedDestination.stopNames
                                  : selectedDestination?.stops?.length > 1
                                    ? selectedDestination.stops.map(s => s.resolvedLocation || s.name || s)
                                    // Fallback: parse from name if it contains "&" — but not hotel names
                                    : selectedDestination?.name?.includes('&') && !isHotelOrResort(selectedDestination.name)
                                      ? selectedDestination.name.split('&').map(s => s.trim()).filter(Boolean)
                                      // Use resolved location if available (e.g., "Rudi Dadens 1, Switzerland")
                                      : (tripBuilderStops?.[0]?.resolvedLocation || selectedDestination?.name) ? [tripBuilderStops?.[0]?.resolvedLocation || selectedDestination?.name] : [];
                              if (!allStops.length) return 'Select destination';
                              // Dynamic language lookup: country-keyed map covers all countries.
                              // Extracts country from "City, Country" format or matches country names in the string.
                              const COUNTRY_LANGUAGES = {
                                'afghanistan': 'Dari/Pashto', 'albania': 'Albanian', 'algeria': 'Arabic/French', 'andorra': 'Catalan',
                                'angola': 'Portuguese', 'antigua': 'English', 'argentina': 'Spanish', 'armenia': 'Armenian',
                                'australia': 'English', 'austria': 'German', 'azerbaijan': 'Azerbaijani',
                                'bahamas': 'English', 'bahrain': 'Arabic', 'bangladesh': 'Bengali', 'barbados': 'English',
                                'belarus': 'Belarusian/Russian', 'belgium': 'Dutch/French', 'belize': 'English/Spanish', 'benin': 'French',
                                'bhutan': 'Dzongkha', 'bolivia': 'Spanish', 'bosnia': 'Bosnian', 'botswana': 'English/Setswana',
                                'brazil': 'Portuguese', 'brunei': 'Malay', 'bulgaria': 'Bulgarian', 'burkina faso': 'French',
                                'burundi': 'Kirundi/French', 'cambodia': 'Khmer', 'cameroon': 'French/English', 'canada': 'English/French',
                                'cape verde': 'Portuguese', 'central african': 'French', 'chad': 'French/Arabic', 'chile': 'Spanish',
                                'china': 'Mandarin', 'colombia': 'Spanish', 'comoros': 'Comorian/French/Arabic', 'congo': 'French',
                                'costa rica': 'Spanish', 'croatia': 'Croatian', 'cuba': 'Spanish', 'cyprus': 'Greek/Turkish',
                                'czech republic': 'Czech', 'czechia': 'Czech', 'denmark': 'Danish',
                                'djibouti': 'French/Arabic', 'dominica': 'English', 'dominican republic': 'Spanish',
                                'ecuador': 'Spanish', 'egypt': 'Arabic', 'el salvador': 'Spanish', 'england': 'English',
                                'equatorial guinea': 'Spanish/French', 'eritrea': 'Tigrinya/Arabic', 'estonia': 'Estonian',
                                'eswatini': 'Swazi/English', 'ethiopia': 'Amharic', 'fiji': 'English/Fijian', 'finland': 'Finnish',
                                'france': 'French', 'gabon': 'French', 'gambia': 'English', 'georgia': 'Georgian',
                                'germany': 'German', 'ghana': 'English', 'greece': 'Greek', 'grenada': 'English',
                                'guatemala': 'Spanish', 'guinea': 'French', 'guyana': 'English', 'haiti': 'French/Creole',
                                'honduras': 'Spanish', 'hong kong': 'Cantonese/English', 'hungary': 'Hungarian',
                                'iceland': 'Icelandic', 'india': 'Hindi/English', 'indonesia': 'Indonesian', 'iran': 'Persian',
                                'iraq': 'Arabic/Kurdish', 'ireland': 'English/Irish', 'israel': 'Hebrew', 'italy': 'Italian',
                                'ivory coast': 'French', 'jamaica': 'English', 'japan': 'Japanese', 'jordan': 'Arabic',
                                'kazakhstan': 'Kazakh/Russian', 'kenya': 'Swahili/English', 'kiribati': 'English/Gilbertese',
                                'kuwait': 'Arabic', 'kyrgyzstan': 'Kyrgyz/Russian', 'laos': 'Lao', 'latvia': 'Latvian',
                                'lebanon': 'Arabic/French', 'lesotho': 'Sesotho/English', 'liberia': 'English', 'libya': 'Arabic',
                                'liechtenstein': 'German', 'lithuania': 'Lithuanian', 'luxembourg': 'Luxembourgish/French/German',
                                'macau': 'Cantonese/Portuguese', 'madagascar': 'Malagasy/French', 'malawi': 'Chichewa/English',
                                'malaysia': 'Malay/English', 'maldives': 'Dhivehi/English', 'mali': 'French', 'malta': 'Maltese/English',
                                'mauritania': 'Arabic', 'mauritius': 'English/French/Creole', 'mexico': 'Spanish',
                                'micronesia': 'English', 'moldova': 'Romanian', 'monaco': 'French', 'mongolia': 'Mongolian',
                                'montenegro': 'Montenegrin', 'morocco': 'Arabic/French', 'mozambique': 'Portuguese',
                                'myanmar': 'Burmese', 'namibia': 'English', 'nauru': 'Nauruan/English', 'nepal': 'Nepali',
                                'netherlands': 'Dutch', 'new zealand': 'English/Māori', 'nicaragua': 'Spanish', 'niger': 'French',
                                'nigeria': 'English', 'north korea': 'Korean', 'north macedonia': 'Macedonian',
                                'norway': 'Norwegian', 'oman': 'Arabic', 'pakistan': 'Urdu/English', 'palau': 'Palauan/English',
                                'palestine': 'Arabic', 'panama': 'Spanish', 'papua new guinea': 'English/Tok Pisin',
                                'paraguay': 'Spanish/Guarani', 'peru': 'Spanish', 'philippines': 'Filipino/English',
                                'poland': 'Polish', 'portugal': 'Portuguese', 'qatar': 'Arabic', 'romania': 'Romanian',
                                'russia': 'Russian', 'rwanda': 'Kinyarwanda/French/English',
                                'samoa': 'Samoan/English', 'san marino': 'Italian', 'saudi arabia': 'Arabic',
                                'scotland': 'English', 'senegal': 'French', 'serbia': 'Serbian', 'seychelles': 'French/English/Creole',
                                'sierra leone': 'English', 'singapore': 'English/Mandarin/Malay/Tamil',
                                'slovakia': 'Slovak', 'slovenia': 'Slovenian', 'solomon islands': 'English',
                                'somalia': 'Somali/Arabic', 'south africa': 'English/Afrikaans/Zulu', 'south korea': 'Korean',
                                'south sudan': 'English', 'spain': 'Spanish', 'sri lanka': 'Sinhala/Tamil',
                                'sudan': 'Arabic/English', 'suriname': 'Dutch', 'sweden': 'Swedish', 'switzerland': 'German/French/Italian',
                                'syria': 'Arabic', 'taiwan': 'Mandarin', 'tajikistan': 'Tajik', 'tanzania': 'Swahili/English',
                                'thailand': 'Thai', 'timor-leste': 'Portuguese/Tetum', 'togo': 'French', 'tonga': 'Tongan/English',
                                'trinidad': 'English', 'tunisia': 'Arabic/French', 'turkey': 'Turkish', 'türkiye': 'Turkish',
                                'turkmenistan': 'Turkmen', 'tuvalu': 'Tuvaluan/English', 'uganda': 'English/Swahili',
                                'ukraine': 'Ukrainian', 'united arab emirates': 'Arabic/English', 'uae': 'Arabic/English',
                                'united kingdom': 'English', 'uk': 'English', 'united states': 'English', 'usa': 'English', 'us': 'English',
                                'uruguay': 'Spanish', 'uzbekistan': 'Uzbek', 'vanuatu': 'Bislama/English/French',
                                'vatican': 'Italian/Latin', 'venezuela': 'Spanish', 'vietnam': 'Vietnamese',
                                'wales': 'English/Welsh', 'yemen': 'Arabic', 'zambia': 'English', 'zimbabwe': 'English/Shona',
                              };
                              // Dynamic lookup: try country from "City, Country" format, then currency-based country inference
                              const getLang = (destination) => {
                                const lower = destination.toLowerCase().trim();
                                // 1. Try the part after comma (usually country): "Barcelona, Spain" → "spain"
                                const parts = lower.split(',').map(p => p.trim());
                                for (const part of parts) {
                                  if (COUNTRY_LANGUAGES[part]) return COUNTRY_LANGUAGES[part];
                                }
                                // 2. Fuzzy match: check if any country name appears in the destination string
                                for (const [country, lang] of Object.entries(COUNTRY_LANGUAGES)) {
                                  if (lower.includes(country)) return lang;
                                }
                                // 3. Check if the city name itself IS a country (e.g., "Singapore", "Monaco")
                                const cityOnly = parts[0];
                                if (COUNTRY_LANGUAGES[cityOnly]) return COUNTRY_LANGUAGES[cityOnly];
                                // 3.5. Use CITY_TO_COUNTRY lookup (handles "Rome" → "Italy" → "Italian")
                                const mappedCountry = CITY_TO_COUNTRY[cityOnly];
                                if (mappedCountry && COUNTRY_LANGUAGES[mappedCountry.toLowerCase()]) return COUNTRY_LANGUAGES[mappedCountry.toLowerCase()];
                                // 4. Infer country from currency (dynamic bridge: getCurrencyForCity → currency → country)
                                // This leverages the existing currency database to resolve bare city names
                                const currency = getCurrencyForCity(destination);
                                if (currency) {
                                  const CURRENCY_TO_COUNTRY = {
                                    'JPY': 'japan', 'CNY': 'china', 'KRW': 'south korea', 'TWD': 'taiwan',
                                    'THB': 'thailand', 'VND': 'vietnam', 'IDR': 'indonesia', 'MYR': 'malaysia',
                                    'PHP': 'philippines', 'SGD': 'singapore', 'HKD': 'hong kong', 'INR': 'india',
                                    'LKR': 'sri lanka', 'NPR': 'nepal', 'BDT': 'bangladesh', 'PKR': 'pakistan',
                                    'AED': 'united arab emirates', 'SAR': 'saudi arabia', 'ILS': 'israel',
                                    'TRY': 'turkey', 'EGP': 'egypt', 'MAD': 'morocco', 'KES': 'kenya',
                                    'ZAR': 'south africa', 'NGN': 'nigeria', 'GHS': 'ghana', 'TZS': 'tanzania',
                                    'BRL': 'brazil', 'MXN': 'mexico', 'ARS': 'argentina', 'CLP': 'chile',
                                    'COP': 'colombia', 'PEN': 'peru', 'CRC': 'costa rica',
                                    'EUR': 'france', 'GBP': 'united kingdom', 'CHF': 'switzerland',
                                    'SEK': 'sweden', 'NOK': 'norway', 'DKK': 'denmark', 'ISK': 'iceland',
                                    'PLN': 'poland', 'CZK': 'czech republic', 'HUF': 'hungary', 'RON': 'romania',
                                    'BGN': 'bulgaria', 'HRK': 'croatia', 'RUB': 'russia',
                                    'AUD': 'australia', 'NZD': 'new zealand', 'FJD': 'fiji',
                                    'CAD': 'canada', 'JMD': 'jamaica', 'TTD': 'trinidad',
                                    'KHR': 'cambodia', 'LAK': 'laos', 'MMK': 'myanmar', 'MNT': 'mongolia',
                                    'MVR': 'maldives', 'GEL': 'georgia', 'AMD': 'armenia', 'AZN': 'azerbaijan',
                                    'JOD': 'jordan', 'BHD': 'bahrain', 'KWD': 'kuwait', 'OMR': 'oman', 'QAR': 'qatar',
                                  };
                                  const country = CURRENCY_TO_COUNTRY[currency];
                                  if (country && COUNTRY_LANGUAGES[country]) return COUNTRY_LANGUAGES[country];
                                }
                                return null;
                              };
                              if (allStops.length > 1) {
                                // L2: group by language. Collapse to a single label
                                // ("English") when every stop shares the same language;
                                // show per-stop ("Aspen: English · Paris: French") only
                                // when they differ. This matches B2's currency pattern
                                // but without the noisy "City + City: Lang" when they all match.
                                const groups = new Map();
                                allStops.forEach((name) => {
                                  const lang = getLang(name);
                                  if (!lang) return;
                                  const cityShort = (name || '').split(',')[0].trim();
                                  const list = groups.get(lang) || [];
                                  if (!list.includes(cityShort)) list.push(cityShort);
                                  groups.set(lang, list);
                                });
                                if (groups.size === 0) return 'Tap to add language preferences for dining & shopping tips';
                                if (groups.size === 1) {
                                  const [onlyLang] = groups.keys();
                                  return onlyLang === 'English' ? 'English spoken' : `${onlyLang} spoken locally`;
                                }
                                const parts = [];
                                for (const [lang, cities] of groups.entries()) {
                                  parts.push(`${cities.join(' + ')}: ${lang}`);
                                }
                                return parts.join(' · ');
                              }
                              const lang = getLang(allStops[0]);
                              if (lang) return lang === 'English' ? 'English spoken' : `${lang} spoken locally`;
                              // Hope's feedback: "Is there a setting to see the local language? Not clear."
                              // Old fallback was just the title 'Local language info' — that's also what we
                              // were showing in the body, leaving the card with no actual content. Fall back to
                              // an actionable hint so users know what the card *would* show.
                              return 'Tap to add language preferences for dining & shopping tips';
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Travel Advisory removed from personalization — shown post-generation in sidebar */}

                  </div>
                </div>
                {/* Generate Button — pinned at bottom of sidebar, outside scroll area */}
                <div className="flex-shrink-0 px-5 py-4" style={{ borderTop: '1px solid rgba(31,29,59,0.08)' }}>
                      <button
                        onClick={async () => {
                          const prefs = userProfile.preferences;
                          let duration = prefs.duration;
                          if (prefs.startDate && prefs.endDate) {
                            const start = new Date(prefs.startDate);
                            const end = new Date(prefs.endDate);
                            const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
                            if (diffDays > 0) {
                              duration = diffDays;
                              setUserProfile(prev => ({ ...prev, preferences: { ...prev.preferences, duration: diffDays } }));
                            }
                          }
                          if (!duration || duration <= 0) {
                            showAlertModal('Duration Required', 'Please specify your trip duration.\n\nYou can either:\n• Set specific dates, or\n• Enter number of days');
                            return;
                          }
                          if (duration > 14) {
                            showAlertModal('Trip Too Long', `We can currently generate itineraries up to 14 days.\n\nYour trip is ${duration} days. Please shorten your dates or consider splitting into two trips.`);
                            return;
                          }
                          try {
                            await generateItinerarySmart(selectedDestination);
                          } catch (error) {
                            debugError('Error generating itinerary:', error);
                            showAlertModal('Generation Failed', `Failed to generate itinerary: ${error.message}\n\nPlease try again.`);
                            setShowLoadingScreen(false);
                          }
                        }}
                        disabled={!userProfile.preferences.duration || userProfile.preferences.duration > 14}
                        className="w-full text-[15px] font-extrabold transition-all"
                        style={{
                          padding: '14px 24px',
                          borderRadius: 14,
                          border: 'none',
                          background: (!userProfile.preferences.duration || userProfile.preferences.duration > 14) ? '#9ca3af' : 'linear-gradient(135deg, #0d9488, #0f766e)',
                          color: 'white',
                          cursor: (!userProfile.preferences.duration || userProfile.preferences.duration > 14) ? 'not-allowed' : 'pointer',
                          boxShadow: (!userProfile.preferences.duration || userProfile.preferences.duration > 14) ? 'none' : '0 10px 24px rgba(13,148,136,0.25)',
                        }}
                      >
                        Generate My Itinerary
                      </button>
                      <p className="text-[11px] text-center mt-2" style={{ color: '#94a3b8' }}>
                        {!userProfile.preferences.duration
                          ? '⚠️ Set trip dates or duration first'
                          : userProfile.preferences.duration > 14
                            ? '⚠️ Please reduce to 14 days or less'
                            : `${userProfile.preferences.duration} day itinerary will be created`}
                      </p>
                </div>
              </div>
              </div>
              {/* end right sidebar */}

            </div>
            {/* end 2-column grid */}
          </div>
        </div>
      </div>
    );
  };

  const renderLoadingScreen = () => (
    <div className="fixed inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-blue-500 flex flex-col items-center justify-center z-50 overflow-hidden">
      {/* Animated Flying Paper Planes - Smooth continuous wave motion */}
      <style>
        {`
          @keyframes smoothFly1 {
            0% {
              transform: translateX(-80px) translateY(0px);
            }
            100% {
              transform: translateX(calc(100vw + 80px)) translateY(0px);
            }
          }
          @keyframes wave1 {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-25px) rotate(-3deg); }
            50% { transform: translateY(0px) rotate(0deg); }
            75% { transform: translateY(25px) rotate(3deg); }
          }
          @keyframes wave2 {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(20px) rotate(2deg); }
            50% { transform: translateY(0px) rotate(0deg); }
            75% { transform: translateY(-20px) rotate(-2deg); }
          }
          @keyframes wave3 {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            25% { transform: translateY(-15px) rotate(-2deg); }
            50% { transform: translateY(0px) rotate(0deg); }
            75% { transform: translateY(15px) rotate(2deg); }
          }
          .plane-container-1 {
            animation: smoothFly1 8s linear infinite;
          }
          .plane-container-2 {
            animation: smoothFly1 10s linear infinite;
            animation-delay: -3s;
          }
          .plane-container-3 {
            animation: smoothFly1 12s linear infinite;
            animation-delay: -7s;
          }
          .plane-wave-1 {
            animation: wave1 2s ease-in-out infinite;
          }
          .plane-wave-2 {
            animation: wave2 2.5s ease-in-out infinite;
          }
          .plane-wave-3 {
            animation: wave3 3s ease-in-out infinite;
          }
        `}
      </style>
      
      {/* Flying planes - smooth wave motion left to right */}
      <div className="absolute top-16 left-0 w-full h-48 pointer-events-none">
        {/* Main plane - largest, fastest */}
        <div className="plane-container-1 absolute" style={{ top: '60px' }}>
          <div className="plane-wave-1">
            <img src="/arrivo-icon.png" alt="" className="w-10 h-10 opacity-80 brightness-0 invert drop-shadow-lg" />
          </div>
        </div>
        {/* Second plane - medium, offset wave */}
        <div className="plane-container-2 absolute" style={{ top: '30px' }}>
          <div className="plane-wave-2">
            <img src="/arrivo-icon.png" alt="" className="w-8 h-8 opacity-50 brightness-0 invert" />
          </div>
        </div>
        {/* Third plane - smallest, slowest */}
        <div className="plane-container-3 absolute" style={{ top: '100px' }}>
          <div className="plane-wave-3">
            <img src="/arrivo-icon.png" alt="" className="w-6 h-6 opacity-30 brightness-0 invert" />
          </div>
        </div>
      </div>
      
      <div className="text-center">
        {/* Arrivo Logo */}
        <div className="mb-6">
          <img src="/arrivo-icon.png" alt="Arrivo" className="h-28 w-auto object-contain mx-auto drop-shadow-2xl" />
          <h2 className="text-5xl font-semibold text-white mt-3 leading-none" style={{ fontFamily: "'Gulfs Display', 'Batangas', sans-serif", letterSpacing: '0.05em' }}>arrivo</h2>
          <p className="text-xs font-bold text-white/70 mt-1.5" style={{ fontFamily: "'Raleway', sans-serif", letterSpacing: '-0.01em' }}>plan less. explore more.</p>
        </div>
        
        {/* Loading Status */}
        <div className="mb-10">
          <p className="text-xl text-white/90" style={FONTS.body}>{loadingMessage}</p>
          
          {/* Animated dots */}
          <div className="flex justify-center gap-2 mt-4">
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>
        
        {/* Did You Know Card */}
        <div className="bg-white/15 backdrop-blur-md rounded-3xl p-8 max-w-lg mx-4 border border-white/20">
          <p className="text-sm text-white/70 uppercase tracking-widest mb-3" style={FONTS.headingSemibold}>Did you know?</p>
          <p className="text-xl text-white leading-relaxed" style={FONTS.body}>{currentFact}</p>
        </div>
      </div>

      {/* Bottom landscape with rolling hills */}
      <div className="absolute bottom-0 left-0 w-full h-32 overflow-hidden">
        {/* Rolling hills animation styles */}
        <style>
          {`
            @keyframes rollHills1 {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @keyframes rollHills2 {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            @keyframes rollHills3 {
              0% { transform: translateX(0); }
              100% { transform: translateX(-50%); }
            }
            .hills-back {
              animation: rollHills1 40s linear infinite;
            }
            .hills-mid {
              animation: rollHills2 30s linear infinite;
            }
            .hills-front {
              animation: rollHills3 25s linear infinite;
            }
          `}
        </style>

        {/* Back hill layer - slowest, darker */}
        <div className="hills-back absolute bottom-0 w-[200%] h-full">
          <svg className="w-full h-full" viewBox="0 0 2880 128" preserveAspectRatio="none">
            <path
              d="M0,128 L0,80 Q180,30 360,70 T720,50 T1080,75 T1440,60 T1800,70 T2160,50 T2520,75 T2880,60 L2880,128 Z"
              fill="rgba(13, 148, 136, 0.6)"
            />
          </svg>
        </div>

        {/* Middle hill layer */}
        <div className="hills-mid absolute bottom-0 w-[200%] h-full">
          <svg className="w-full h-full" viewBox="0 0 2880 128" preserveAspectRatio="none">
            <path
              d="M0,128 L0,90 Q240,50 480,85 T960,65 T1440,80 T1920,65 T2400,85 T2880,70 L2880,128 Z"
              fill="rgba(20, 184, 166, 0.5)"
            />
          </svg>
        </div>

        {/* Front hill layer - fastest, lightest */}
        <div className="hills-front absolute bottom-0 w-[200%] h-full">
          <svg className="w-full h-full" viewBox="0 0 2880 128" preserveAspectRatio="none">
            <path
              d="M0,128 L0,100 Q300,70 600,95 T1200,85 T1800,95 T2400,85 T2880,95 L2880,128 Z"
              fill="rgba(45, 212, 191, 0.4)"
            />
            {/* Trees scattered on the front hills - layered pine tree style */}
            <g>
              {/* Tree cluster 1 - Layered pine trees */}
              <g transform="translate(150, 95)">
                {/* Tree 1 - tall */}
                <path d="M0,0 L12,-35 L24,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-8 L12,-38 L22,-8 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M4,-16 L12,-42 L20,-16 Z" fill="rgba(6, 95, 70, 0.6)" />
                {/* Tree 2 - medium */}
                <path d="M30,0 L40,-28 L50,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M32,-6 L40,-30 L48,-6 Z" fill="rgba(6, 95, 70, 0.65)" />
                {/* Tree 3 - small */}
                <path d="M-15,0 L-8,-22 L-1,0 Z" fill="rgba(6, 95, 70, 0.7)" />
              </g>
              {/* Tree cluster 2 */}
              <g transform="translate(450, 90)">
                <path d="M0,0 L10,-30 L20,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-7 L10,-33 L18,-7 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M4,-14 L10,-38 L16,-14 Z" fill="rgba(6, 95, 70, 0.6)" />
                <path d="M25,0 L35,-35 L45,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M27,-8 L35,-38 L43,-8 Z" fill="rgba(6, 95, 70, 0.65)" />
                <path d="M29,-16 L35,-42 L41,-16 Z" fill="rgba(6, 95, 70, 0.55)" />
              </g>
              {/* Tree cluster 3 */}
              <g transform="translate(750, 92)">
                <path d="M0,0 L12,-32 L24,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-8 L12,-36 L22,-8 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M18,0 L28,-26 L38,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M20,-6 L28,-28 L36,-6 Z" fill="rgba(6, 95, 70, 0.65)" />
              </g>
              {/* Tree cluster 4 */}
              <g transform="translate(1050, 88)">
                <path d="M0,0 L10,-28 L20,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-6 L10,-30 L18,-6 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M15,0 L27,-36 L39,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M17,-8 L27,-40 L37,-8 Z" fill="rgba(6, 95, 70, 0.65)" />
                <path d="M19,-16 L27,-44 L35,-16 Z" fill="rgba(6, 95, 70, 0.55)" />
                <path d="M42,0 L50,-24 L58,0 Z" fill="rgba(6, 95, 70, 0.7)" />
              </g>
              {/* Tree cluster 5 */}
              <g transform="translate(1350, 90)">
                <path d="M0,0 L12,-34 L24,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-8 L12,-38 L22,-8 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M4,-16 L12,-42 L20,-16 Z" fill="rgba(6, 95, 70, 0.6)" />
                <path d="M28,0 L38,-28 L48,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M30,-6 L38,-30 L46,-6 Z" fill="rgba(6, 95, 70, 0.65)" />
              </g>
              {/* Tree cluster 6 */}
              <g transform="translate(1650, 85)">
                <path d="M0,0 L10,-30 L20,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-7 L10,-33 L18,-7 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M4,-14 L10,-36 L16,-14 Z" fill="rgba(6, 95, 70, 0.6)" />
                <path d="M22,0 L34,-38 L46,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M24,-9 L34,-42 L44,-9 Z" fill="rgba(6, 95, 70, 0.65)" />
                <path d="M26,-18 L34,-46 L42,-18 Z" fill="rgba(6, 95, 70, 0.55)" />
                <path d="M50,0 L58,-24 L66,0 Z" fill="rgba(6, 95, 70, 0.7)" />
              </g>
              {/* Tree cluster 7 */}
              <g transform="translate(1950, 88)">
                <path d="M0,0 L12,-32 L24,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-8 L12,-36 L22,-8 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M4,-16 L12,-40 L20,-16 Z" fill="rgba(6, 95, 70, 0.6)" />
                <path d="M28,0 L38,-28 L48,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M30,-6 L38,-30 L46,-6 Z" fill="rgba(6, 95, 70, 0.65)" />
              </g>
              {/* Tree cluster 8 - repeat for seamless loop */}
              <g transform="translate(2250, 92)">
                <path d="M0,0 L12,-35 L24,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-8 L12,-38 L22,-8 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M4,-16 L12,-42 L20,-16 Z" fill="rgba(6, 95, 70, 0.6)" />
                <path d="M30,0 L40,-28 L50,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M32,-6 L40,-30 L48,-6 Z" fill="rgba(6, 95, 70, 0.65)" />
                <path d="M-15,0 L-8,-22 L-1,0 Z" fill="rgba(6, 95, 70, 0.7)" />
              </g>
              {/* Tree cluster 9 */}
              <g transform="translate(2550, 90)">
                <path d="M0,0 L10,-30 L20,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-7 L10,-33 L18,-7 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M4,-14 L10,-38 L16,-14 Z" fill="rgba(6, 95, 70, 0.6)" />
                <path d="M25,0 L35,-35 L45,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M27,-8 L35,-38 L43,-8 Z" fill="rgba(6, 95, 70, 0.65)" />
                <path d="M29,-16 L35,-42 L41,-16 Z" fill="rgba(6, 95, 70, 0.55)" />
              </g>
              {/* Tree cluster 10 */}
              <g transform="translate(2850, 88)">
                <path d="M0,0 L12,-32 L24,0 Z" fill="rgba(6, 95, 70, 0.8)" />
                <path d="M2,-8 L12,-36 L22,-8 Z" fill="rgba(6, 95, 70, 0.7)" />
                <path d="M18,0 L28,-26 L38,0 Z" fill="rgba(6, 95, 70, 0.75)" />
                <path d="M20,-6 L28,-28 L36,-6 Z" fill="rgba(6, 95, 70, 0.65)" />
              </g>
            </g>
          </svg>
        </div>
      </div>
    </div>
  );

  // Show full-page login if user is not authenticated
  // Exception: checkout-success page should render even if session was lost during Stripe redirect
  if (!isLoggedIn && !authLoading && stage !== 'checkout-success') {
    return <FullPageLogin referralCode={referralCodeFromUrl} />;
  }

  // Show loading state while checking auth (but let checkout-success through)
  if (authLoading && stage !== 'checkout-success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F5F0]">
        <div className="text-center">
          <img src="/arrivo-icon.png" alt="Arrivo" className="w-16 h-16 mx-auto mb-4 animate-pulse object-contain" />
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Mobile browser → native app interstitial. Renders above all content
          on mobile viewports; desktop and ?noredirect=1 are unaffected. */}
      <MobileAppBanner />

      {showLoadingScreen && renderLoadingScreen()}

      {stage === 'landing' && renderLandingPage()}
      {stage === 'quiz' && renderQuiz()}
      {stage === 'chat' && renderChat()}
      {stage === 'personalization' && renderPersonalization()}
      {stage === 'itinerary' && renderItinerary()}

      {/* Pricing Page */}
      {stage === 'pricing' && (
        <div className="min-h-screen relative" style={{ background: '#F5F5F0' }}>
          {/* Topo bg */}
          <div className="fixed inset-0 pointer-events-none z-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1200 900' preserveAspectRatio='none'%3E%3Cpath d='M0 120 Q300 60 600 140 T1200 100' fill='none' stroke='%230d9488' stroke-width='1.2'/%3E%3Cpath d='M0 300 Q400 250 700 320 T1200 280' fill='none' stroke='%23313143' stroke-width='1'/%3E%3Cpath d='M0 520 Q300 480 600 540 T1200 510' fill='none' stroke='%230d9488' stroke-width='0.8'/%3E%3Cpath d='M0 750 Q250 720 550 770 T1200 740' fill='none' stroke='%23313143' stroke-width='1'/%3E%3C/svg%3E")`, backgroundSize: 'cover', opacity: 0.035 }} />
          {/* Header */}
          <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200/50 px-6 py-3 flex items-center justify-between">
            <button
              onClick={() => { window.history.pushState({}, '', '/'); setStage('landing'); }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors text-sm"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <UserMenu onOpenSettings={() => setShowSettings(true)} onOpenSavedItineraries={handleOpenSavedItineraries} onUpgrade={goToPricing} onOpenAnalytics={() => setShowAnalytics(true)} />
          </div>

          <div className="max-w-4xl mx-auto px-6 py-12">
            <div className="text-center mb-10">
              <Sparkles className="w-10 h-10 text-teal-500 mx-auto mb-3" />
              <h1 className="text-3xl font-bold text-gray-900" style={FONTS.headingBold}>Choose Your Plan</h1>
              <p className="text-gray-500 mt-2" style={FONTS.body}>Plan less. Explore more.</p>
            </div>

            {/* Billing interval toggle */}
            <div className="flex items-center justify-center gap-3 mb-8">
              <button
                onClick={() => setBillingInterval?.('monthly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  (billingInterval || 'monthly') === 'monthly' ? 'bg-[#313143] text-white' : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingInterval?.('yearly')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  (billingInterval || 'monthly') === 'yearly' ? 'bg-[#313143] text-white' : 'bg-white text-gray-600 border border-gray-200'
                }`}
              >
                Yearly <span className="text-xs text-emerald-500 font-semibold ml-1">Save 17%</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {/* Explorer — Free */}
              <div className="bg-white rounded-2xl border border-gray-200 p-7 shadow-sm">
                <div className="mb-5">
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-600">EXPLORER</span>
                  <div className="text-3xl font-bold text-gray-900 mt-3">Free</div>
                  <p className="text-sm text-gray-500 mt-1">For casual travelers</p>
                </div>
                <div className="space-y-3 text-sm mb-6">
                  {['5 itineraries per quarter', '10 destination recommendations', '15 modifications per itinerary', 'Save itineraries (30-day history)', 'PDF export (watermarked)', 'All transit modes'].map((t, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      <span className="text-gray-700">{t}</span>
                    </div>
                  ))}
                  {['Group planning', 'Booking integration', 'Complex multi-day modifications'].map((t, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-gray-300 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      <span className="text-gray-400">{t}</span>
                    </div>
                  ))}
                </div>
                {profile?.tier === 'explorer' || !profile?.tier ? (
                  <div className="py-2.5 text-center text-sm text-gray-500 border border-gray-200 rounded-xl">Current plan</div>
                ) : null}
              </div>

              {/* Planner — Paid */}
              <div className="bg-white rounded-2xl border-2 border-teal-500 p-7 shadow-lg relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-[#313143] text-white shadow-sm">RECOMMENDED</span>
                </div>
                <div className="mb-5">
                  <span className="px-3 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-700">PLANNER</span>
                  <div className="text-3xl font-bold text-gray-900 mt-3">
                    {(billingInterval || 'monthly') === 'yearly' ? '$120' : '$12'}
                    <span className="text-sm font-normal text-gray-500">
                      {(billingInterval || 'monthly') === 'yearly' ? '/year' : '/mo'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1">For serious travelers</p>
                </div>
                <div className="space-y-3 text-sm mb-6">
                  {[
                    <><strong>Unlimited</strong> itineraries</>,
                    <><strong>Unlimited</strong> modifications</>,
                    <><strong>Forever</strong> trip history</>,
                    'Save & sync across devices',
                    'PDF export (unwatermarked)',
                    'Group planning (up to 10 collaborators)',
                    'Booking integration + points optimization',
                    'Complex multi-day modifications',
                    'Priority response queue',
                  ].map((t, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      <span className="text-gray-700">{t}</span>
                    </div>
                  ))}
                </div>
                {profile?.tier === 'planner' || profile?.tier === 'admin' ? (
                  <button
                    onClick={handleStripePortal}
                    className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all font-semibold text-sm"
                  >
                    Manage Subscription
                  </button>
                ) : (
                  <button
                    onClick={() => handleStripeCheckout(billingInterval || 'monthly')}
                    className="w-full py-3 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] transition-all font-semibold text-sm shadow-lg"
                  >
                    Upgrade to Planner
                  </button>
                )}
              </div>
            </div>

            <div className="mt-8 text-center text-sm text-gray-400">
              Cancel anytime. No long-term commitment.
            </div>
          </div>
        </div>
      )}

      {/* Checkout Success */}
      {stage === 'checkout-success' && (
        <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2" style={FONTS.headingBold}>Welcome to Planner!</h1>
            <p className="text-gray-500 mb-6" style={FONTS.body}>Your upgrade is active. Here's what you've unlocked:</p>
            <div className="text-left space-y-2 mb-8 bg-teal-50 rounded-xl p-4">
              {['Unlimited itineraries', 'Unlimited modifications', 'Forever trip history', 'Save & sync itineraries', 'PDF export (unwatermarked)', 'Group planning + booking integration'].map((t, i) => (
                <div key={i} className="flex items-center gap-2 text-sm text-teal-700">
                  <svg className="w-4 h-4 text-teal-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  {t}
                </div>
              ))}
            </div>
            <button
              onClick={() => {
                // Clear checkout query params and refresh profile
                window.history.pushState({}, '', '/');
                if (typeof refreshProfile === 'function') refreshProfile();
                setStage('landing');
              }}
              className="w-full py-3 bg-[#313143] text-white rounded-xl hover:bg-[#3d3d52] transition-all font-semibold text-sm"
            >
              Plan My Trip
            </button>
          </div>
        </div>
      )}

      {/* Community — lazy-loaded, error-isolated from planner */}
      {stage === 'community' && (
        <CommunityErrorBoundary>
          <React.Suspense fallback={
            <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
              <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }>
            <CommunityPage currentUserId={user?.id} userEmail={user?.email} onBack={() => setStage('landing')} onOpenInPlanner={handleLoadItinerary} />
          </React.Suspense>
        </CommunityErrorBoundary>
      )}

      {/* Shared Trip Viewer — public, no auth required */}
      {stage === 'shared-trip' && sharedTripHash && (
        <CommunityErrorBoundary>
          <React.Suspense fallback={
            <div className="min-h-screen bg-[#F5F5F0] flex items-center justify-center">
              <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin" />
            </div>
          }>
            <SharedTripViewer
              shareHash={sharedTripHash}
              onGoHome={() => {
                window.history.pushState({}, '', '/');
                setStage('landing');
              }}
              onSignIn={() => {
                // Store the share hash so we can return after SSO redirect.
                // Google OAuth redirects to origin root, losing the /trip/:hash URL.
                try { sessionStorage.setItem('wander_pending_share', sharedTripHash); } catch (e) {}
                // Navigate to landing page where SSO button lives
                window.history.pushState({}, '', '/');
                setStage('landing');
              }}
            />
          </React.Suspense>
        </CommunityErrorBoundary>
      )}

      {/* Collaborative Trip Invite Landing */}
      {features.COLLABORATIVE_TRIPS && stage === 'invite-landing' && inviteToken && (
        <InviteLanding
          token={inviteToken}
          onJoinSuccess={(tripId) => {
            setCurrentTripId(tripId);
            setStage('itinerary');
            window.history.replaceState({}, document.title, '/');
          }}
        />
      )}

      {/* Feedback Banner - Shows when user gives thumbs up */}
      {showFeedbackBanner && stage === 'itinerary' && feedbackCount.thumbsUp > 0 && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
          <div className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 max-w-2xl">
            <div className="flex-1">
              <p className="font-semibold text-lg mb-1">
                Great choice! 👍
              </p>
              <p className="text-sm text-white/90">
                We'll remember you like this type of {feedbackCount.thumbsUp === 1 ? 'experience' : 'experiences'}
              </p>
            </div>
            <button
              onClick={() => setShowFeedbackBanner(false)}
              className="text-white hover:bg-white/20 rounded-full p-2 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}


      {/* Feedback Dialog - Choose scope of feedback */}
      {showFeedbackDialog && feedbackDialogData && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">
                  What would you like to change?
                </h3>
                <p className="text-sm text-gray-600">
                  You disliked "{feedbackDialogData.name}"
                </p>
              </div>
              <button
                onClick={() => {
                  setShowFeedbackDialog(false);
                  setFeedbackReason('');
                  setFeedbackDialogData(null);
                  setSelectedFeedbackOption(null);
                  // Also undo the thumbs down
                  const key = feedbackDialogData.feedbackKey;
                  if (feedbackDialogData.type === 'activity') {
                    setActivityFeedback(prev => {
                      const newFeedback = { ...prev };
                      delete newFeedback[key];
                      return newFeedback;
                    });
                  } else {
                    setMealFeedback(prev => {
                      const newFeedback = { ...prev };
                      delete newFeedback[key];
                      return newFeedback;
                    });
                  }
                  setFeedbackCount(prev => ({ ...prev, thumbsDown: Math.max(0, prev.thumbsDown - 1) }));
                  // Clean up the orphaned Supabase record
                  if (user && isLoggedIn) {
                    deleteItineraryFeedback({
                      userId: user.id,
                      itineraryId: currentItineraryId || null,
                      dayIndex: feedbackDialogData.day,
                      itemIndex: feedbackDialogData.index,
                      feedbackType: feedbackDialogData.type === 'activity' ? 'activity' : 'restaurant'
                    }).catch(err => debugLog('Failed to clean up feedback:', err));
                  }
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Options - Selection based (click to select, not to open chat) */}
            <div className="space-y-3">
              {/* Option 1: Just this item */}
              <button
                onClick={() => setSelectedFeedbackOption(selectedFeedbackOption === 'replace_one' ? null : 'replace_one')}
                className={`w-full p-4 border-2 rounded-2xl transition-all text-left group ${
                  selectedFeedbackOption === 'replace_one'
                    ? 'bg-teal-50 border-teal-400 ring-2 ring-teal-200'
                    : 'bg-white hover:bg-teal-50 border-gray-200 hover:border-teal-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                    selectedFeedbackOption === 'replace_one' ? 'bg-teal-100' : 'bg-teal-50 group-hover:bg-teal-100'
                  }`}>
                    <RefreshCw className={`w-5 h-5 ${selectedFeedbackOption === 'replace_one' ? 'text-teal-600' : 'text-teal-500'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900" style={FONTS.headingSemibold}>Replace just this {feedbackDialogData.type === 'activity' ? 'activity' : 'restaurant'}</p>
                    <p className="text-sm text-gray-500" style={FONTS.body}>Find a different option for this time slot</p>
                  </div>
                  {selectedFeedbackOption === 'replace_one' && (
                    <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </button>

              {/* Option 1.5: Not a full meal (only for meals, not activities) */}
              {feedbackDialogData.type === 'meal' && feedbackDialogData.mealType && ['lunch', 'dinner'].includes(feedbackDialogData.mealType) && (
                <button
                  onClick={() => setSelectedFeedbackOption(selectedFeedbackOption === 'not_full_meal' ? null : 'not_full_meal')}
                  className={`w-full p-4 border-2 rounded-2xl transition-all text-left group ${
                    selectedFeedbackOption === 'not_full_meal'
                      ? 'bg-amber-50 border-amber-400 ring-2 ring-amber-200'
                      : 'bg-white hover:bg-amber-50 border-gray-200 hover:border-amber-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                      selectedFeedbackOption === 'not_full_meal' ? 'bg-amber-100' : 'bg-amber-50 group-hover:bg-amber-100'
                    }`}>
                      <UtensilsCrossed className={`w-5 h-5 ${selectedFeedbackOption === 'not_full_meal' ? 'text-amber-600' : 'text-amber-500'}`} />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900" style={FONTS.headingSemibold}>Not a full meal</p>
                      <p className="text-sm text-gray-500" style={FONTS.body}>This is a cafe/dessert spot - I want a proper restaurant</p>
                    </div>
                    {selectedFeedbackOption === 'not_full_meal' && (
                      <div className="w-6 h-6 bg-amber-500 rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              )}

              {/* Option 2: Apply to whole itinerary */}
              <button
                onClick={() => setSelectedFeedbackOption(selectedFeedbackOption === 'replace_all' ? null : 'replace_all')}
                className={`w-full p-4 border-2 rounded-2xl transition-all text-left group ${
                  selectedFeedbackOption === 'replace_all'
                    ? 'bg-emerald-50 border-emerald-400 ring-2 ring-emerald-200'
                    : 'bg-white hover:bg-emerald-50 border-gray-200 hover:border-emerald-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                    selectedFeedbackOption === 'replace_all' ? 'bg-emerald-100' : 'bg-emerald-50 group-hover:bg-emerald-100'
                  }`}>
                    <Sparkles className={`w-5 h-5 ${selectedFeedbackOption === 'replace_all' ? 'text-emerald-600' : 'text-emerald-500'}`} />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900" style={FONTS.headingSemibold}>Update entire itinerary</p>
                    <p className="text-sm text-gray-500" style={FONTS.body}>Apply this preference to all {feedbackDialogData.type === 'activity' ? 'activities' : 'restaurants'} (e.g., "less touristy", "more casual")</p>
                  </div>
                  {selectedFeedbackOption === 'replace_all' && (
                    <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
              </button>
            </div>

            {/* Feedback text input */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <label className="block text-sm font-semibold text-gray-700 mb-2" style={FONTS.bodySemibold}>
                {selectedFeedbackOption ? 'Tell us what you\'d prefer' : 'Or tell us what you want'}
              </label>
              <textarea
                value={feedbackReason}
                onChange={(e) => setFeedbackReason(e.target.value)}
                placeholder={feedbackDialogData.type === 'activity'
                  ? "e.g., I want something more local, less touristy, outdoor..."
                  : "e.g., I want seafood, something cheaper, a rooftop, vegetarian-friendly..."}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-teal-500 focus:outline-none transition-colors resize-none text-sm"
                style={FONTS.body}
                rows={2}
              />
              <p className="text-xs text-gray-400 mt-2" style={FONTS.body}>
                {selectedFeedbackOption
                  ? 'arrivo will use your preferences to find a better match'
                  : 'Describe what you\'d like — arrivo will find the perfect match'}
              </p>
              <div className="flex gap-2 mt-3">
                {(feedbackReason.trim() || selectedFeedbackOption) && (
                  <button
                    onClick={() => {
                      // Store the feedback reason and selection
                      if (feedbackReason.trim() || selectedFeedbackOption) {
                        setFeedbackReasons(prev => ({
                          ...prev,
                          [feedbackDialogData.feedbackKey]: feedbackReason.trim() || selectedFeedbackOption
                        }));
                      }
                      // Close dialog without opening chat
                      setShowFeedbackDialog(false);
                      setFeedbackReason('');
                      setFeedbackDialogData(null);
                      setSelectedFeedbackOption(null);
                    }}
                    className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors text-sm"
                  >
                    Save for Later
                  </button>
                )}
                <button
                  onClick={async () => {
                    // Build reason string based on selection for storage
                    let reasonToStore = feedbackReason.trim();
                    if (!reasonToStore && selectedFeedbackOption) {
                      if (selectedFeedbackOption === 'replace_one') {
                        reasonToStore = 'Replace with something better';
                      } else if (selectedFeedbackOption === 'not_full_meal') {
                        reasonToStore = 'Not a full meal - cafe/dessert venue';
                      } else if (selectedFeedbackOption === 'replace_all') {
                        reasonToStore = 'Update all similar items in itinerary';
                      }
                    }

                    // Store the feedback reason if provided
                    if (reasonToStore) {
                      setFeedbackReasons(prev => ({
                        ...prev,
                        [feedbackDialogData.feedbackKey]: reasonToStore
                      }));
                    }

                    // Handle "replace_one" or "not_full_meal" - replace just this single item
                    if (selectedFeedbackOption === 'replace_one' || selectedFeedbackOption === 'not_full_meal') {
                      const { day, index, type, name } = feedbackDialogData;
                      const dayData = itinerary?.days?.[day];
                      const baseCity = dayData?.baseCity || itinerary?.destination;
                      const hotelLocation = dayData?.hotel?.address || itinerary?.days?.[0]?.hotel?.address;

                      // Close dialog first and show loading
                      setShowFeedbackDialog(false);
                      setFeedbackReason('');
                      setFeedbackDialogData(null);
                      setSelectedFeedbackOption(null);

                      setShowLoadingScreen(true);
                      setLoadingMessage(`Finding a better ${type === 'activity' ? 'activity' : 'restaurant'}...`);

                      try {
                        const problem = selectedFeedbackOption === 'not_full_meal'
                          ? 'This is a cafe/dessert spot - need a proper full-service restaurant'
                          : reasonToStore || 'User wants a different option';

                        const existingActivities = [
                          ...(dayData?.schedule || []).map(a => a.activity),
                          ...(dayData?.meals || []).map(m => m.restaurant)
                        ].filter(Boolean);

                        const timeSlot = type === 'activity'
                          ? dayData?.schedule?.[index]?.time
                          : dayData?.meals?.[index]?.time;

                        const replacement = await regenerateActivity(
                          name,
                          problem,
                          baseCity,
                          hotelLocation,
                          dayData?.theme,
                          existingActivities,
                          timeSlot,
                          type === 'activity' ? 'activity' : 'meal'
                        );

                        if (replacement && !replacement._error) {
                          debugLog(`✅ Got replacement: "${replacement.activity || replacement.restaurant}"`);

                          // Apply the replacement to the itinerary
                          setItinerary(prev => {
                            const updated = { ...prev };
                            updated.days = [...prev.days];
                            updated.days[day] = { ...prev.days[day] };

                            if (type === 'activity' && updated.days[day].schedule?.[index]) {
                              updated.days[day].schedule = [...prev.days[day].schedule];
                              updated.days[day].schedule[index] = {
                                ...prev.days[day].schedule[index],
                                activity: replacement.activity,
                                location: replacement.location || prev.days[day].schedule[index].location,
                                address: replacement.address || prev.days[day].schedule[index].address,
                                description: replacement.description || prev.days[day].schedule[index].description,
                                duration: replacement.duration || prev.days[day].schedule[index].duration,
                                emoji: replacement.emoji || prev.days[day].schedule[index].emoji,
                                _wasReplaced: true,
                                _originalActivity: name
                              };
                            } else if (type === 'meal' && updated.days[day].meals?.[index]) {
                              updated.days[day].meals = [...prev.days[day].meals];
                              updated.days[day].meals[index] = {
                                ...prev.days[day].meals[index],
                                restaurant: replacement.restaurant || replacement.activity,
                                address: replacement.address || prev.days[day].meals[index].address,
                                cuisine: replacement.cuisine || prev.days[day].meals[index].cuisine,
                                mustTry: replacement.mustTry || prev.days[day].meals[index].mustTry,
                                _wasReplaced: true,
                                _originalRestaurant: name
                              };
                            }

                            return updated;
                          });

                          // Clear the feedback for this item
                          if (type === 'activity') {
                            setActivityFeedback(prev => {
                              const updated = { ...prev };
                              delete updated[feedbackDialogData.feedbackKey];
                              return updated;
                            });
                          } else {
                            setMealFeedback(prev => {
                              const updated = { ...prev };
                              delete updated[feedbackDialogData.feedbackKey];
                              return updated;
                            });
                          }
                          setFeedbackCount(prev => ({ ...prev, thumbsDown: Math.max(0, prev.thumbsDown - 1) }));

                          setShowLoadingScreen(false);
                          // Show success notification
                          showToast(`Replaced "${name}" with "${replacement.activity || replacement.restaurant}"`, 'success');
                        } else {
                          setShowLoadingScreen(false);
                          const errorDetail = replacement?._error || 'Could not find a suitable replacement.';
                          showAlertModal('No Replacement Found', `${errorDetail} Try using "Update entire itinerary" instead.`);
                        }
                      } catch (error) {
                        debugLog('❌ Single item replacement error:', error);
                        setShowLoadingScreen(false);
                        showAlertModal('Replacement Failed', 'Please try again.');
                      }

                      return;
                    }

                    // Close dialog
                    setShowFeedbackDialog(false);
                    setFeedbackReason('');
                    setFeedbackDialogData(null);
                    setSelectedFeedbackOption(null);

                    // For "replace_all" - trigger full regeneration
                    setTimeout(() => {
                      const regenerateBtn = document.querySelector('[data-regenerate-button]');
                      if (regenerateBtn) {
                        regenerateBtn.click();
                      }
                    }, 100);
                  }}
                  className={`${(feedbackReason.trim() || selectedFeedbackOption) ? 'flex-1' : 'w-full'} py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl font-semibold transition-all text-sm flex items-center justify-center gap-2 shadow-sm`}
                  style={FONTS.bodySemibold}
                >
                  <RefreshCw className="w-4 h-4" />
                  {selectedFeedbackOption === 'replace_one' || selectedFeedbackOption === 'not_full_meal' ? 'Replace This Item' : feedbackReason.trim() && !selectedFeedbackOption ? 'Find What I Want' : 'Regenerate Itinerary'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 animate-fade-in">
            {/* Header */}
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <span className="text-2xl">⚠️</span>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900" style={FONTS.headingBold}>
                  {errorInfo.title || 'Something Went Wrong'}
                </h3>
                <p className="text-gray-600 mt-1" style={FONTS.body}>
                  {errorInfo.message || 'An unexpected error occurred.'}
                </p>
              </div>
              <button
                onClick={() => setShowErrorModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Suggestions */}
            {errorInfo.suggestions && errorInfo.suggestions.length > 0 && (
              <div className="bg-gray-50 rounded-xl p-4 mb-4">
                <p className="text-sm font-medium text-gray-700 mb-2" style={FONTS.bodyBold}>Things to try:</p>
                <ul className="space-y-1.5">
                  {errorInfo.suggestions.map((suggestion, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-600" style={FONTS.body}>
                      <span className="text-teal-500 mt-0.5">•</span>
                      {suggestion}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Action Buttons */}
            <div className="flex gap-3">
              {errorInfo.canRetry && errorInfo.retryAction && (
                <button
                  onClick={errorInfo.retryAction}
                  className="flex-1 py-3 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                  style={FONTS.body}
                >
                  <RefreshCw className="w-4 h-4" />
                  Try Again
                </button>
              )}
              <button
                onClick={() => {
                  setShowErrorModal(false);
                  setStage('personalization');
                }}
                className={`${errorInfo.canRetry ? 'flex-1' : 'w-full'} py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all`}
                style={FONTS.body}
              >
                Modify Settings
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Offline Banner */}
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white py-2 px-4 z-50 flex items-center justify-center gap-2 shadow-lg">
          <span className="text-lg">📡</span>
          <span className="font-medium" style={FONTS.body}>You're offline. Some features may not work.</span>
          <button
            onClick={() => window.location.reload()}
            className="ml-4 px-3 py-1 bg-amber-600 hover:bg-amber-700 rounded-lg text-sm font-medium transition-colors"
            style={FONTS.body}
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Vercel Analytics */}
      <Analytics />

      {/* Auth Modals */}
      <LoginModal />
      <PersonalizationModal />
      <SettingsModal isOpen={showSettings} onClose={() => setShowSettings(false)} />

      {/* Place Detail modal (Task 14) — opened from insider-tip links and activity/meal names */}
      <PlaceDetailModal
        open={!!placeDetailModal}
        onClose={closePlaceDetail}
        placeName={placeDetailModal?.placeName}
        city={placeDetailModal?.city}
        country={placeDetailModal?.country}
        placeCategory={placeDetailModal?.placeCategory}
        placeData={placeDetailModal?.placeData}
        item={placeDetailModal?.item}
        onRate={(payload) => {
          // Close place detail, then open the review form pre-seeded with
          // the place context so the user lands directly in the rating form.
          closePlaceDetail();
          setSidebarReviewPlace({
            place_name: payload.placeName || '',
            city: payload.city || '',
            country: payload.country || '',
            place_id: payload.placeData?.placeId || payload.placeData?.place_id || null,
          });
        }}
      />

      {/* Root-level CompareModal — mounted here so the bug #13 web parity
          auto-open works from ANY surface (home feed, insider tips, place
          detail, must-do chips, etc.). Previously the modal only lived
          inside the community rankings tab, which meant
          setShowCompareModal(true) was a no-op when the user rated from
          elsewhere. `focalItem` is set when the modal is opened from a
          fresh PlaceReviewForm.onSuccess so the comparisons anchor on the
          newly-rated place (Beli-style binary-search insertion) instead
          of just shuffling random pairs of existing items — user-flagged:
          "i added sofitel leyenda and then it opened this module but it
          didnt automatically include sofitel?? so how do we know where
          to place sofitel". */}
      {showCompareModal && (
        <React.Suspense fallback={null}>
          <CommunityErrorBoundary>
            <CompareModal
              isOpen={showCompareModal}
              onClose={() => { setShowCompareModal(false); setCompareFocalItem(null); }}
              focalItem={compareFocalItem}
            />
          </CommunityErrorBoundary>
        </React.Suspense>
      )}

      {/* Root-level PlaceReviewForm modal. The sidebar also renders one
          scoped to its own state, but we need a root-level copy so the
          "Rate this place" CTA inside PlaceDetailModal (which is reachable
          from the home feed, insider tips, must-do chips, etc.) can open
          the review form regardless of which screen the user is on. */}
      {sidebarReviewPlace && (
        <div
          className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4"
          onClick={() => setSidebarReviewPlace(null)}
        >
          <div
            className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <React.Suspense fallback={<div className="p-8 text-center text-gray-400">Loading…</div>}>
              <PlaceReviewForm
                place={sidebarReviewPlace}
                onClose={() => setSidebarReviewPlace(null)}
                onSuccess={() => {
                  // Capture the place name BEFORE clearing the form state,
                  // so CompareModal opens already anchored on it.
                  const justRatedName = sidebarReviewPlace?.name || null;
                  setSidebarReviewPlace(null);
                  setCompareFocalItem(justRatedName);
                  setShowCompareModal(true);
                }}
              />
            </React.Suspense>
          </div>
        </div>
      )}

      {/* Rate & Rank → Beli-style search → Review → Compare flow.
          Previously: Rate CTA → search → select → Place Detail modal →
          "Rate this place" button → PlaceReviewForm → CompareModal.
          That's 5 taps to record a single rating.
          Now: Rate CTA → search → select → PlaceReviewForm → CompareModal.
          The place-detail stop is skipped because the user already knows
          which place they want to rate — they just searched for it.
          Detail view is still reachable from the feed / insider tips /
          must-do chips, just not from this particular flow. */}
      {quickRateOpen && (
        <React.Suspense fallback={<div className="fixed inset-0 z-50 bg-black/20 flex items-center justify-center"><div className="text-white">Loading…</div></div>}>
          <QuickRateFlow
            onClose={() => setQuickRateOpen(false)}
            onSelectPlace={(place) => {
              setQuickRateOpen(false);
              // Open the review form directly with the selected place
              // pre-filled. onSuccess fires setShowCompareModal(true)
              // (see sidebarReviewPlace render site above) so the full
              // user path is: search → rate → compare.
              setSidebarReviewPlace({
                name: place.name,
                city: place.city,
                country: place.country,
                placeCategory: place.category || 'activity',
                placeId: place.placeId || null,
                ...(place.placeData || {}),
              });
            }}
          />
        </React.Suspense>
      )}

      {/* Collaboration Modals */}
      {features.COLLABORATIVE_TRIPS && showShareModal && currentTripId && (
        <ShareModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          tripId={currentTripId}
          tripTitle={selectedDestination?.name || 'My Trip'}
          destination={selectedDestination?.name || ''}
        />
      )}
      {features.COLLABORATIVE_TRIPS && showActivityLog && currentTripId && (
        <ActivityLog
          isOpen={showActivityLog}
          onClose={() => setShowActivityLog(false)}
          tripId={currentTripId}
        />
      )}

      {/* Admin Analytics Dashboard + Review Queue */}
      {showAnalytics && profile?.tier === 'super-admin' && (
        <React.Suspense fallback={<div className="fixed inset-0 z-50 bg-gray-50 flex items-center justify-center">Loading analytics...</div>}>
          <AdminAnalytics onClose={() => setShowAnalytics(false)} />
          {/* Admin Review Queue — embedded below analytics */}
          <div className="fixed inset-0 z-[51] pointer-events-none">
            <div className="absolute bottom-4 right-4 pointer-events-auto">
              <button
                onClick={() => setShowAdminQueue(!showAdminQueue)}
                className="px-4 py-2.5 rounded-xl bg-teal-500 text-white text-sm font-bold shadow-lg hover:bg-teal-600 transition-colors flex items-center gap-2"
                style={FONTS.bodySemibold}
              >
                <Shield className="w-4 h-4" /> Review Queue
              </button>
            </div>
            {showAdminQueue && (
              <div className="absolute inset-4 top-16 bg-white rounded-2xl shadow-2xl overflow-y-auto pointer-events-auto border border-gray-200" style={{ zIndex: 52 }}>
                <div className="flex items-center justify-between p-4 border-b border-gray-100 sticky top-0 bg-white z-10">
                  <h3 className="text-lg font-bold text-gray-800" style={FONTS.headingBold}>Admin Review Queue</h3>
                  <button onClick={() => setShowAdminQueue(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
                </div>
                <div className="p-4">
                  <React.Suspense fallback={<div className="text-center py-8 text-gray-400 text-sm">Loading...</div>}>
                    <CommunityErrorBoundary>
                      <AdminReviewQueue />
                    </CommunityErrorBoundary>
                  </React.Suspense>
                </div>
              </div>
            )}
          </div>
        </React.Suspense>
      )}

      {/* Saved Itineraries Modal */}
      {showSavedItineraries && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSavedItineraries(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-800" style={FONTS.headingBold}>
                  Saved Itineraries
                </h2>
                <button
                  onClick={() => setShowSavedItineraries(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>
            </div>

            {/* Search & Sort */}
            {!isLoadingSavedItineraries && savedItinerariesList.length > 0 && (
              <div className="px-6 pt-4 pb-2 flex gap-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search trips..."
                    value={savedItinerarySearch || ''}
                    onChange={(e) => setSavedItinerarySearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-teal-500"
                    style={FONTS.body}
                  />
                </div>
                <select
                  value={savedItinerarySort || 'newest'}
                  onChange={(e) => setSavedItinerarySort(e.target.value)}
                  className="px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:ring-2 focus:ring-emerald-500"
                  style={FONTS.body}
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="destination">Destination A-Z</option>
                </select>
              </div>
            )}

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {isLoadingSavedItineraries ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full mb-4"></div>
                  <p className="text-gray-500" style={FONTS.body}>Loading your trips...</p>
                </div>
              ) : savedItinerariesList.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <MapPin className="w-8 h-8 text-gray-400" />
                  </div>
                  <p className="text-gray-600 font-medium mb-2" style={FONTS.body}>No saved itineraries yet</p>
                  <p className="text-gray-400 text-sm text-center" style={FONTS.body}>
                    Generate an itinerary and click Save to keep it for later
                  </p>
                </div>
              ) : (() => {
                // Filter and sort
                const searchTerm = (savedItinerarySearch || '').toLowerCase().trim();
                const filtered = savedItinerariesList.filter(item => {
                  if (!searchTerm) return true;
                  const title = (item.title || '').toLowerCase();
                  const dest = (item.destination || '').toLowerCase();
                  return title.includes(searchTerm) || dest.includes(searchTerm);
                });

                const sorted = [...filtered].sort((a, b) => {
                  switch (savedItinerarySort) {
                    case 'oldest':
                      return new Date(a.updated_at) - new Date(b.updated_at);
                    case 'destination':
                      return (a.destination || '').localeCompare(b.destination || '');
                    case 'newest':
                    default:
                      return new Date(b.updated_at) - new Date(a.updated_at);
                  }
                });

                if (sorted.length === 0 && searchTerm) {
                  return (
                    <div className="flex flex-col items-center justify-center py-8">
                      <p className="text-gray-500 text-sm" style={FONTS.body}>No trips matching "{savedItinerarySearch}"</p>
                    </div>
                  );
                }

                return (
                <div className="space-y-3">
                  {sorted.map((item) => (
                    <div
                      key={item.id}
                      className="p-4 bg-gray-50 hover:bg-teal-50 rounded-xl border border-gray-200 hover:border-teal-200 transition-colors cursor-pointer group"
                      onClick={() => handleLoadItinerary(item)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-800 truncate" style={FONTS.headingSemibold}>
                            {item.title || item.destination || 'Untitled Trip'}
                          </h3>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500" style={FONTS.body}>
                            {item.destination && (
                              <span className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" />
                                {item.destination}
                              </span>
                            )}
                            {item.start_date && item.end_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(item.start_date).toLocaleDateString()} - {new Date(item.end_date).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                          {item.expires_at ? (
                            <p className="text-xs text-amber-600 mt-2 font-medium" style={FONTS.body}>
                              Expires in {Math.max(0, Math.round((new Date(item.expires_at) - new Date()) / 3600000))}h — upgrade to keep permanently
                            </p>
                          ) : (
                            <p className="text-xs text-gray-400 mt-2" style={FONTS.body}>
                              Last updated: {new Date(item.updated_at).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSavedItinerary(item.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                            title="Delete itinerary"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-teal-500" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Packing List Modal */}
      {showPackingList && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowPackingList(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 pb-4 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <Package className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900" style={FONTS.heading}>Packing List</h2>
                    <p className="text-sm text-gray-500" style={FONTS.body}>
                      {selectedDestination?.name} {userProfile.preferences?.duration ? `• ${userProfile.preferences.duration} days` : ''}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {packingList?.categories && (
                    <button
                      onClick={() => {
                        const lines = [`Packing List — ${selectedDestination?.name || 'Trip'}\n`];
                        packingList.categories.forEach(cat => {
                          lines.push(`\n${cat.name}`);
                          (cat.items || []).forEach(item => {
                            lines.push(`  ${item.packed ? '\u2611' : '\u2610'} ${item.item}`);
                          });
                        });
                        if (packingList.tips?.length) {
                          lines.push('\nTips');
                          packingList.tips.forEach(t => lines.push(`  \u2022 ${t}`));
                        }
                        const text = lines.join('\n');
                        if (navigator.share) {
                          navigator.share({ text }).catch(() => {});
                        } else {
                          navigator.clipboard.writeText(text).then(() => {
                            showToast('Packing list copied!', 'success', 2000);
                          }).catch(() => {});
                        }
                      }}
                      className="p-2 hover:bg-teal-50 rounded-lg transition-colors"
                      title="Share packing list"
                    >
                      <Share2 className="w-5 h-5 text-teal-500" />
                    </button>
                  )}
                  <button
                    onClick={() => setShowPackingList(false)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
              {/* Progress bar */}
              {packingList?.categories && (() => {
                const totalItems = packingList.categories.reduce((sum, cat) => sum + cat.items.length, 0);
                const checkedCount = Object.values(checkedPackingItems).filter(Boolean).length;
                const pct = totalItems > 0 ? Math.round((checkedCount / totalItems) * 100) : 0;
                return (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-gray-500 mb-1" style={FONTS.body}>
                      <span>{checkedCount} of {totalItems} items packed</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Stale date nudge — show when dates changed since packing list was generated */}
            {packingList && packingListGeneratedWith && (
              (packingListGeneratedWith.startDate !== (userProfile?.preferences?.startDate || null) ||
               packingListGeneratedWith.endDate !== (userProfile?.preferences?.endDate || null)) && (
                <div className="mx-6 mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm text-amber-800">
                    <span>📅</span>
                    <span style={FONTS.body}>Your travel dates changed — packing list may be outdated for the season.</span>
                  </div>
                  <button
                    onClick={async () => {
                      setIsGeneratingPackingList(true);
                      setCheckedPackingItems({});
                      await generatePackingList(selectedDestination, itinerary);
                      setIsGeneratingPackingList(false);
                    }}
                    className="text-xs font-semibold text-amber-700 hover:text-amber-900 whitespace-nowrap px-2 py-1 rounded-lg hover:bg-amber-100 transition-colors"
                    style={FONTS.body}
                  >
                    Update
                  </button>
                </div>
              )
            )}

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {isGeneratingPackingList ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full mb-4"></div>
                  <p className="text-gray-600 font-medium" style={FONTS.body}>Creating your packing list...</p>
                  <p className="text-gray-400 text-sm mt-1" style={FONTS.body}>Checking weather & activities</p>
                </div>
              ) : packingList?.categories ? (
                <div className="space-y-5">
                  {packingList.categories.map((category, catIdx) => {
                    const catIcons = {
                      passport: '🛂', shirt: '👕', heart: '💊', zap: '⚡', activity: '🎒', star: '✨'
                    };
                    return (
                      <div key={catIdx}>
                        <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2" style={FONTS.heading}>
                          <span>{catIcons[category.icon] || '📦'}</span>
                          {category.name}
                          <span className="text-xs text-gray-400 font-normal">
                            ({category.items.filter((_, i) => checkedPackingItems[`${catIdx}-${i}`]).length}/{category.items.length})
                          </span>
                        </h3>
                        <div className="space-y-1">
                          {category.items.map((item, itemIdx) => {
                            const key = `${catIdx}-${itemIdx}`;
                            const isChecked = checkedPackingItems[key];
                            return (
                              <label
                                key={itemIdx}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                                  isChecked ? 'bg-teal-50' : 'hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked || false}
                                  onChange={() => setCheckedPackingItems(prev => ({
                                    ...prev,
                                    [key]: !prev[key]
                                  }))}
                                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-emerald-500"
                                />
                                <span
                                  className={`flex-1 text-sm ${isChecked ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                                  style={FONTS.body}
                                >
                                  {item.item}
                                </span>
                                {item.tag && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 whitespace-nowrap">
                                    {item.tag}
                                  </span>
                                )}
                              </label>
                            );
                          })}
                          {/* User-added custom items — Hope's feedback: "Can I add my
                              own items? E.g., extra batteries for Nana's hearing aids".
                              Same checkbox UI; X button removes the custom item. */}
                          {(customPackingItems[catIdx] || []).map((customItem, customIdx) => {
                            const key = `${catIdx}-custom-${customIdx}`;
                            const isChecked = checkedPackingItems[key];
                            return (
                              <div
                                key={key}
                                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors group ${
                                  isChecked ? 'bg-teal-50' : 'hover:bg-gray-50'
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked || false}
                                  onChange={() => setCheckedPackingItems(prev => ({
                                    ...prev,
                                    [key]: !prev[key]
                                  }))}
                                  className="w-4 h-4 rounded border-gray-300 text-teal-600 focus:ring-emerald-500"
                                />
                                <span
                                  className={`flex-1 text-sm ${isChecked ? 'text-gray-400 line-through' : 'text-gray-700'}`}
                                  style={FONTS.body}
                                >
                                  {customItem.item}
                                </span>
                                <span className="text-[10px] px-2 py-0.5 rounded-full bg-teal-100 text-teal-600 whitespace-nowrap">
                                  yours
                                </span>
                                <button
                                  onClick={() => {
                                    setCustomPackingItems(prev => ({
                                      ...prev,
                                      [catIdx]: (prev[catIdx] || []).filter((_, i) => i !== customIdx),
                                    }));
                                    setCheckedPackingItems(prev => {
                                      const next = { ...prev };
                                      delete next[key];
                                      return next;
                                    });
                                  }}
                                  className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all"
                                  title="Remove custom item"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            );
                          })}
                          {/* Add custom item input */}
                          <div className="flex items-center gap-2 px-3 py-1 mt-1">
                            <Plus className="w-3.5 h-3.5 text-gray-400" />
                            <input
                              type="text"
                              value={customItemInputs[catIdx] || ''}
                              onChange={(e) => setCustomItemInputs(prev => ({ ...prev, [catIdx]: e.target.value }))}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const value = (customItemInputs[catIdx] || '').trim();
                                  if (!value) return;
                                  setCustomPackingItems(prev => ({
                                    ...prev,
                                    [catIdx]: [...(prev[catIdx] || []), { item: value, custom: true }],
                                  }));
                                  setCustomItemInputs(prev => ({ ...prev, [catIdx]: '' }));
                                }
                              }}
                              placeholder="Add your own item — press Enter"
                              className="flex-1 text-sm bg-transparent border-0 focus:outline-none text-gray-600 placeholder-gray-300"
                              style={FONTS.body}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  {/* Tips section */}
                  {packingList.tips && packingList.tips.length > 0 && (
                    <div className="mt-4 p-4 bg-amber-50 rounded-xl border border-amber-100">
                      <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2" style={FONTS.heading}>
                        <Lightbulb className="w-4 h-4" /> Packing Tips
                      </h3>
                      <ul className="space-y-1">
                        {packingList.tips.map((tip, i) => (
                          <li key={i} className="text-sm text-amber-700 flex items-start gap-2" style={FONTS.body}>
                            <span className="text-amber-400 mt-1">•</span>
                            {tip}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  <p className="text-gray-500" style={FONTS.body}>No packing list available</p>
                  <button
                    onClick={async () => {
                      setIsGeneratingPackingList(true);
                      await generatePackingList(selectedDestination, itinerary);
                      setIsGeneratingPackingList(false);
                    }}
                    className="mt-3 px-4 py-2 bg-[#313143] text-white rounded-lg text-sm hover:bg-[#3d3d52] transition-colors"
                    style={FONTS.body}
                  >
                    Generate Packing List
                  </button>
                </div>
              )}
            </div>

            {/* Footer */}
            {packingList?.categories && (
              <div className="p-4 border-t border-gray-100 flex items-center justify-between">
                <button
                  onClick={() => setCheckedPackingItems({})}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  style={FONTS.body}
                >
                  Reset all
                </button>
                <button
                  onClick={async () => {
                    setIsGeneratingPackingList(true);
                    setCheckedPackingItems({});
                    await generatePackingList(selectedDestination, itinerary);
                    setIsGeneratingPackingList(false);
                  }}
                  className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1 transition-colors"
                  style={FONTS.body}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  Regenerate
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Trip Logger Modal (community feature) */}
      {showTripLogger && (
        <React.Suspense fallback={null}>
          <TripLogger
            itinerary={itinerary}
            onSave={async (tripData) => {
              const result = await createTripLog(tripData);
              if (!result.error) {
                setShowTripLogger(false);
              }
              return result;
            }}
            onClose={() => setShowTripLogger(false)}
            onReviewPlace={(place) => {
              // Could open PlaceReviewForm in future
              console.log('Review place:', place);
            }}
          />
        </React.Suspense>
      )}

      {/* Confirmation/Alert Modal (replaces browser dialogs) */}
      {/* Upgrade Modal - Tier Comparison */}
      {showUpgradeModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowUpgradeModalOpen(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-teal-600 px-6 py-5 text-center relative">
              <button
                onClick={() => setShowUpgradeModalOpen(false)}
                className="absolute right-4 top-4 text-white/70 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
              <Sparkles className="w-8 h-8 text-white mx-auto mb-2" />
              <h2 className="text-xl font-bold text-white" style={FONTS.headingBold}>Upgrade Your Travel Planning</h2>
              {upgradeContext && (
                <p className="text-teal-100 text-sm mt-1" style={FONTS.body}>{upgradeContext}</p>
              )}
            </div>

            {/* Tier Comparison */}
            <div className="p-6">
              <div className="grid grid-cols-2 gap-4">
                {/* Explorer Column */}
                <div className="border border-gray-200 rounded-xl p-5">
                  <div className="text-center mb-4">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-700">
                      EXPLORER
                    </span>
                    <div className="text-2xl font-bold text-gray-900 mt-2">Free</div>
                    <p className="text-sm text-gray-500 mt-1">For casual travelers</p>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </span>
                      <span className="text-gray-700">5 itineraries per quarter</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </span>
                      <span className="text-gray-700">10 destination recommendations</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </span>
                      <span className="text-gray-700">15 modifications per itinerary</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </span>
                      <span className="text-gray-700">Save itineraries (30-day history)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-teal-500 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      </span>
                      <span className="text-gray-700">PDF export (watermarked)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-300 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </span>
                      <span className="text-gray-400">Group planning</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-300 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </span>
                      <span className="text-gray-400">Booking integration</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-gray-300 mt-0.5">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </span>
                      <span className="text-gray-400">Complex multi-day modifications</span>
                    </div>
                  </div>
                  {profile?.tier === 'explorer' && (
                    <div className="mt-4 py-2 text-center text-sm text-gray-500 border border-gray-200 rounded-lg">
                      Current plan
                    </div>
                  )}
                </div>

                {/* Planner Column */}
                <div className="border-2 border-teal-500 rounded-xl p-5 relative bg-teal-50/30">
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-[#313143] text-white">
                      RECOMMENDED
                    </span>
                  </div>
                  <div className="text-center mb-4">
                    <span className="px-3 py-1 text-xs font-semibold rounded-full bg-teal-100 text-teal-700">
                      PLANNER
                    </span>
                    <div className="text-2xl font-bold text-gray-900 mt-2">$12<span className="text-sm font-normal text-gray-500">/mo</span></div>
                    <p className="text-sm text-gray-500 mt-1">For serious travelers</p>
                  </div>
                  <div className="space-y-3 text-sm">
                    {[
                      { text: <><strong>Unlimited</strong> itineraries</> },
                      { text: <><strong>Unlimited</strong> modifications</> },
                      { text: <><strong>Forever</strong> trip history</> },
                      { text: 'Save & sync across devices' },
                      { text: 'PDF export (unwatermarked)' },
                      { text: 'Group planning (up to 10 collaborators)' },
                      { text: 'Booking integration + points optimization' },
                      { text: 'Complex multi-day modifications' },
                      { text: 'Priority response queue' },
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <span className="text-teal-500 mt-0.5">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                        </span>
                        <span className="text-gray-700">{item.text}</span>
                      </div>
                    ))}
                  </div>
                  {profile?.tier === 'planner' ? (
                    <div className="mt-4 py-2 text-center text-sm text-teal-600 border border-teal-300 rounded-lg bg-teal-50">
                      Current plan
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setShowUpgradeModalOpen(false);
                        debugLog('User wants to upgrade - redirect to billing');
                        if (process.env.REACT_APP_STRIPE_ENABLED === 'true') {
                          handleStripeCheckout('monthly');
                        } else {
                          showAlertModal('Coming Soon!', 'Planner subscriptions will be available soon. Thanks for your interest!');
                        }
                      }}
                      className="mt-4 w-full py-3 bg-[#313143] text-white rounded-lg hover:bg-[#3d3d52] transition-all font-semibold text-sm"
                    >
                      Upgrade to Planner
                    </button>
                  )}
                </div>
              </div>

              {/* Complex modifications detail */}
              <div className="mt-5 p-4 bg-gray-50 rounded-xl">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">What are complex modifications?</h4>
                <p className="text-xs text-gray-500">
                  Add extra days, extend your trip, add new cities, reoptimize for budget, plan side trips, and more.
                  Explorer users can swap activities, adjust times, and make basic changes.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post-save ranking prompt — Beli-style engagement */}
      {showRankPrompt && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[65] animate-in slide-in-from-bottom fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 px-6 py-4 flex items-center gap-4 max-w-lg">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #1d8e85, #14b8a6)' }}>
              <Star className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-[#1a1a2e]">How was this trip?</div>
              <div className="text-xs text-gray-500 mt-0.5">Rank your destinations to get better recommendations</div>
            </div>
            <button
              onClick={() => { setShowRankPrompt(false); setStage('community'); }}
              className="px-4 py-2 text-xs font-semibold text-white rounded-lg whitespace-nowrap"
              style={{ background: 'linear-gradient(135deg, #1d8e85, #14b8a6)' }}
            >
              Rank Now
            </button>
            <button onClick={() => setShowRankPrompt(false)} className="text-gray-300 hover:text-gray-500 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Toast Notifications — auto-dismissing, stacking from top-right */}
      {toasts.length > 0 && (
        <div className="fixed top-4 right-4 z-[70] flex flex-col gap-2 pointer-events-none">
          {toasts.map(toast => (
            <div
              key={toast.id}
              className={`pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg border backdrop-blur-sm animate-in slide-in-from-right fade-in duration-300 max-w-md ${
                toast.type === 'error' ? 'bg-red-50 border-red-200 text-red-800' :
                toast.type === 'info' ? 'bg-blue-50 border-blue-200 text-blue-800' :
                'bg-teal-50 border-teal-200 text-teal-800'
              }`}
              style={FONTS.body}
            >
              {toast.type === 'error' ? <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" /> :
               toast.type === 'info' ? <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0" /> :
               <Check className="w-5 h-5 text-teal-500 flex-shrink-0" />}
              <span className="text-sm font-medium">{toast.message}</span>
              {toast.action && (
                <button
                  onClick={() => {
                    toast.action.onClick();
                    setToasts(prev => prev.filter(t => t.id !== toast.id));
                  }}
                  className="ml-1 px-2.5 py-1 text-xs font-bold rounded-lg bg-teal-600 text-white hover:bg-teal-700 transition-colors flex-shrink-0"
                  style={FONTS.bodyBold}
                >
                  {toast.action.label}
                </button>
              )}
              <button
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                className={`ml-2 flex-shrink-0 ${
                  toast.type === 'error' ? 'text-red-400 hover:text-red-600' :
                  toast.type === 'info' ? 'text-blue-400 hover:text-blue-600' :
                  'text-emerald-400 hover:text-teal-600'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={confirmModal.onCancel || confirmModal.onConfirm}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            {/* Icon based on type */}
            <div className="flex justify-center mb-4">
              {confirmModal.type === 'upgrade' ? (
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-white" />
                </div>
              ) : confirmModal.type === 'alert' ? (
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertCircle className="w-8 h-8 text-amber-500" />
                </div>
              ) : (
                <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-8 h-8 text-teal-500" />
                </div>
              )}
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-center text-gray-900 mb-2" style={FONTS.headingBold}>
              {confirmModal.title}
            </h3>

            {/* Message with markdown-like formatting */}
            <div className="text-center text-gray-600 mb-6 whitespace-pre-line" style={FONTS.body}>
              {confirmModal.message.split('\n').map((line, i) => (
                <p key={i} className={line.startsWith('•') ? 'text-left ml-4' : ''}>
                  {line.startsWith('•') ? (
                    <span className="flex items-start gap-2">
                      <span className="text-teal-500">•</span>
                      <span>{line.slice(1).trim()}</span>
                    </span>
                  ) : line}
                </p>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              {confirmModal.cancelText && (
                <button
                  onClick={confirmModal.onCancel}
                  className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all"
                  style={FONTS.body}
                >
                  {confirmModal.cancelText}
                </button>
              )}
              <button
                onClick={confirmModal.onConfirm}
                className={`flex-1 py-3 rounded-xl font-semibold transition-all ${
                  confirmModal.type === 'upgrade'
                    ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600'
                    : 'bg-teal-500 hover:bg-teal-600 text-white'
                }`}
                style={FONTS.body}
              >
                {confirmModal.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal - PDF, Calendar, or Share Link */}
      {showExportModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowExportModal(false)}
          />
          {/* Modal */}
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-full flex items-center justify-center">
                {exportType === 'share' ? <Share2 className="w-8 h-8 text-white" /> : <Download className="w-8 h-8 text-white" />}
              </div>
            </div>

            {/* Title */}
            <h3 className="text-xl font-bold text-center text-gray-900 mb-2" style={FONTS.headingBold}>
              {exportType === 'share' ? 'Share Itinerary' : 'Export Itinerary'}
            </h3>

            {/* Description */}
            <p className="text-center text-gray-600 mb-6" style={FONTS.body}>
              {exportType === 'share'
                ? `Share your ${itinerary?.destination || 'travel'} itinerary with a link`
                : `Export your ${itinerary?.destination || 'travel'} itinerary`}
            </p>

            {/* Export Type Selection */}
            {(() => {
              const showShareTab = features.COMMUNITY_SHARING;
              const shareDisabled = !currentItineraryId;
              const colCount = showShareTab ? 4 : 3;
              return (
                <div className={`grid grid-cols-${colCount} gap-3 mb-6`} style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
                  <button
                    onClick={() => setExportType('pdf')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      exportType === 'pdf'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex justify-center mb-2"><FileText className="w-6 h-6 text-gray-600" /></div>
                    <div className="font-semibold text-gray-900" style={FONTS.bodySemibold}>PDF</div>
                    <p className="text-xs text-gray-500 mt-1" style={FONTS.body}>Printable document</p>
                  </button>
                  <button
                    onClick={() => setExportType('csv')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      exportType === 'csv'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex justify-center mb-2"><BarChart3 className="w-6 h-6 text-gray-600" /></div>
                    <div className="font-semibold text-gray-900" style={FONTS.bodySemibold}>Budget</div>
                    <p className="text-xs text-gray-500 mt-1" style={FONTS.body}>CSV spreadsheet</p>
                  </button>
                  <button
                    onClick={() => setExportType('calendar')}
                    disabled={tier === 'explorer'}
                    className={`p-4 rounded-xl border-2 transition-all relative ${
                      tier === 'explorer'
                        ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                        : exportType === 'calendar'
                        ? 'border-teal-500 bg-teal-50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    {tier === 'explorer' && (
                      <div className="absolute -top-2 -right-2 bg-purple-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">
                        PLANNER
                      </div>
                    )}
                    <div className="flex justify-center mb-2"><CalendarDays className="w-6 h-6 text-gray-600" /></div>
                    <div className="font-semibold text-gray-900" style={FONTS.bodySemibold}>Calendar</div>
                    <p className="text-xs text-gray-500 mt-1" style={FONTS.body}>
                      {tier === 'explorer' ? 'Upgrade required' : 'Add to calendar'}
                    </p>
                  </button>
                  {showShareTab && (
                    <button
                      onClick={() => setExportType('share')}
                      disabled={shareDisabled}
                      className={`p-4 rounded-xl border-2 transition-all relative ${
                        shareDisabled
                          ? 'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'
                          : exportType === 'share'
                          ? 'border-teal-500 bg-teal-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}
                    >
                      {shareDisabled && (
                        <div className="absolute -top-2 -right-2 bg-teal-500 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold">
                          {!currentItineraryId ? 'SAVE' : 'BETA'}
                        </div>
                      )}
                      <div className="flex justify-center mb-2"><Share2 className="w-6 h-6 text-gray-600" /></div>
                      <div className="font-semibold text-gray-900" style={FONTS.bodySemibold}>Share</div>
                      <p className="text-xs text-gray-500 mt-1" style={FONTS.body}>
                        {shareDisabled ? (!currentItineraryId ? 'Save first' : 'Community beta') : 'Shareable link'}
                      </p>
                    </button>
                  )}
                </div>
              );
            })()}

            {/* PDF Options */}
            {exportType === 'pdf' && (
              <div className="space-y-4 mb-6">
                <label className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                  <input
                    type="checkbox"
                    checked={pdfIncludeDetails}
                    onChange={(e) => setPdfIncludeDetails(e.target.checked)}
                    className="w-5 h-5 rounded border-gray-300 text-teal-500 focus:ring-emerald-500"
                  />
                  <div>
                    <span className="font-medium text-gray-900" style={FONTS.bodySemibold}>Include detailed descriptions</span>
                    <p className="text-sm text-gray-500" style={FONTS.body}>
                      Add activity descriptions, restaurant specialties, and notes
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Calendar Options/Info */}
            {exportType === 'calendar' && tier !== 'explorer' && (
              <div className="mb-6">
                {!userProfile?.preferences?.startDate ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-amber-700" style={FONTS.bodySemibold}>
                      <AlertCircle className="w-4 h-4" />
                      <span>Start date required</span>
                    </div>
                    <p className="text-sm text-amber-600 mt-1" style={FONTS.body}>
                      Please set a start date for your trip to export to calendar.
                    </p>
                  </div>
                ) : (
                  <div className="bg-blue-50 rounded-xl p-4">
                    <div className="flex items-center gap-2 text-blue-700" style={FONTS.bodySemibold}>
                      <Calendar className="w-4 h-4" />
                      <span>Calendar Export</span>
                    </div>
                    <p className="text-sm text-blue-600 mt-1" style={FONTS.body}>
                      Downloads an .ics file you can import into Google Calendar, Apple Calendar, or Outlook.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* CSV Budget Info */}
            {exportType === 'csv' && (
              <div className="mb-6">
                <div className="bg-emerald-50 rounded-xl p-4">
                  <div className="flex items-center gap-2 text-emerald-700" style={FONTS.bodySemibold}>
                    <BarChart3 className="w-4 h-4" />
                    <span>Budget Export</span>
                  </div>
                  <p className="text-sm text-emerald-600 mt-1" style={FONTS.body}>
                    Downloads a CSV file with all activities, meals, and hotels with estimated costs. Open in Excel, Google Sheets, or Numbers.
                  </p>
                </div>
              </div>
            )}

            {/* Share Link Content */}
            {exportType === 'share' && (
              <div className="mb-6">
                <ErrorBoundarySimple fallbackMessage="Share feature couldn't load. Please try refreshing the page.">
                  <React.Suspense fallback={<div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" /></div>}>
                    <ShareLinkModal
                      itineraryId={currentItineraryId}
                      itineraryTitle={itinerary?.destination || 'My Trip'}
                    />
                  </React.Suspense>
                </ErrorBoundarySimple>
              </div>
            )}

            {/* Summary info — only for PDF, Calendar, and CSV */}
            {exportType !== 'share' && (
              <div className={`rounded-xl p-4 mb-6 ${exportType === 'pdf' ? 'bg-teal-50' : exportType === 'csv' ? 'bg-emerald-50' : 'bg-blue-50'}`}>
                <div className={`flex items-center gap-2 ${exportType === 'pdf' ? 'text-teal-700' : exportType === 'csv' ? 'text-emerald-700' : 'text-blue-700'}`} style={FONTS.bodySemibold}>
                  <span>{exportType === 'pdf' ? <FileText className="w-4 h-4" /> : exportType === 'csv' ? <BarChart3 className="w-4 h-4" /> : <CalendarDays className="w-4 h-4" />}</span>
                  <span>{exportType === 'pdf' ? 'PDF Summary' : exportType === 'csv' ? 'Budget Summary' : 'Calendar Summary'}</span>
                </div>
                <p className={`text-sm mt-1 ${exportType === 'pdf' ? 'text-teal-600' : exportType === 'csv' ? 'text-emerald-600' : 'text-blue-600'}`} style={FONTS.body}>
                  {exportType === 'pdf'
                    ? `${itinerary?.days?.length || 0} days • Timeline format • ${pdfIncludeDetails ? 'With details' : 'Compact view'}`
                    : exportType === 'csv'
                    ? `${itinerary?.days?.length || 0} days • ${(itinerary?.days?.reduce((acc, day) => acc + (day?.schedule?.length || 0) + (day?.meals?.length || 0), 0) || 0)} items • Includes hotels`
                    : `${itinerary?.days?.length || 0} days • ${(itinerary?.days?.reduce((acc, day) => acc + (day?.schedule?.length || 0) + (day?.meals?.length || 0), 0) || 0)} events`
                  }
                </p>
              </div>
            )}

            {/* Buttons — Cancel always shown, Export button only for PDF/Calendar */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowExportModal(false)}
                className={`${exportType === 'share' ? 'w-full' : 'flex-1'} py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all`}
                style={FONTS.body}
              >
                {exportType === 'share' ? 'Close' : 'Cancel'}
              </button>
              {exportType !== 'share' && (
                <button
                  onClick={() => {
                    if (exportType === 'pdf') {
                      exportItineraryAsPDF(pdfIncludeDetails);
                    } else if (exportType === 'csv') {
                      exportBudgetCSV();
                    } else {
                      exportItineraryAsCalendar();
                    }
                    setShowExportModal(false);
                  }}
                  disabled={exportType === 'calendar' && !userProfile?.preferences?.startDate}
                  className={`flex-1 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                    exportType === 'calendar' && !userProfile?.preferences?.startDate
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-[#313143] text-white hover:bg-[#3d3d52]'
                  }`}
                  style={FONTS.body}
                >
                  <Download className="w-4 h-4" />
                  {exportType === 'pdf' ? 'Export PDF' : exportType === 'csv' ? 'Download CSV' : 'Download .ics'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Skeleton Trip Builder Modal */}
      {showSkeletonBuilder && (
        <React.Suspense fallback={null}>
          <CommunityErrorBoundary>
            <SkeletonTripBuilder onClose={() => setShowSkeletonBuilder(false)} />
          </CommunityErrorBoundary>
        </React.Suspense>
      )}

      {/* Trip Importer Modal */}
      {showTripImporter && (
        <React.Suspense fallback={null}>
          <CommunityErrorBoundary>
            <TripImporter onClose={() => setShowTripImporter(false)} />
          </CommunityErrorBoundary>
        </React.Suspense>
      )}

    </div>
  );
};

export default WanderAppComplete;
